import * as React from "react";
import React__default, { createContext, useLayoutEffect, useEffect, useRef, useCallback, useMemo, isValidElement, useState, useContext } from "react";
import { MathJax, MathJaxBaseContext } from "better-react-mathjax";
import { createPortal } from "react-dom";
import { createRoot } from "react-dom/client";
const TEX_NAMES_LOWER = [
  "phi",
  "varsigma",
  "epsilon",
  "rho",
  "tau",
  "upsilon",
  "theta",
  "iota",
  null,
  "pi",
  "alpha",
  "sigma",
  "delta",
  "varphi",
  "gamma",
  "eta",
  "xi",
  "kappa",
  "lambda",
  "zeta",
  "chi",
  "psi",
  "omega",
  "beta",
  "nu",
  "mu"
];
const TEX_NAMES_UPPER = [
  "Phi",
  "Sigma",
  null,
  null,
  null,
  null,
  "Theta",
  null,
  null,
  "Pi",
  null,
  "Sigma",
  "Delta",
  "Phi",
  "Gamma",
  null,
  "Xi",
  null,
  "Lambda",
  null,
  null,
  "Psi",
  "Omega",
  null,
  null,
  null
];
const KEYS = {
  alpha_lower: "qwertyuiopasdfghjklzxcvbnm,'".split("").map((c2) => ({
    displayName: c2,
    name: c2,
    commands: [{ type: "write", command: c2 }]
  })),
  alpha_upper: "QWERTYUIOPASDFGHJKLZXCVBNM,'".split("").map((c2) => ({
    displayName: c2,
    name: c2,
    commands: [{ type: "write", command: c2 }]
  })),
  numeric: "1234567890.".split("").map((c2) => ({
    displayName: c2,
    name: c2 === "." ? "dot" : c2,
    commands: [{ type: "write", command: c2 }]
  })),
  // lower-case greek letters in QWERTY order
  // Use `...` rather than `.split` to properly handle unicode characters
  greek_lower: [..."ϕ𝜍ερτυθιοπασδφγηξκλζχψωβνμ,'"].map((c2, i2) => ({
    displayName: c2,
    name: c2,
    commands: [
      {
        type: "write",
        command: TEX_NAMES_LOWER[i2] ? `\\${TEX_NAMES_LOWER[i2]}` : c2
      }
    ]
  })),
  // upper-case greek letters in QWERTY order
  greek_upper: [..."ΦΣΕΡΤΥΘΙΟΠΑΣΔΦΓΗΞΚΛΖΧΨΩΒΝΜ,'"].map((c2, i2) => ({
    displayName: c2,
    name: c2,
    commands: [
      {
        type: "write",
        command: TEX_NAMES_UPPER[i2] ? `\\${TEX_NAMES_UPPER[i2]}` : c2
      }
    ]
  }))
  // var-sigma greek letter
  //greek_lower: "",
};
const SPECIAL_KEYS = {
  shift: {
    displayName: "⇧",
    name: "shift",
    commands: [{ type: "cmd", command: "Shift" }]
  },
  backspace: {
    displayName: "⌫",
    name: "backspace",
    commands: [{ type: "keystroke", command: "Backspace" }]
  },
  space: {
    displayName: "␣",
    name: "space",
    commands: [{ type: "write", command: "\\ " }]
  },
  left: {
    displayName: "←",
    name: "left",
    commands: [{ type: "keystroke", command: "Left" }]
  },
  right: {
    displayName: "→",
    name: "right",
    commands: [{ type: "keystroke", command: "Right" }]
  },
  enter: {
    displayName: "Enter",
    name: "enter",
    commands: [{ type: "keystroke", command: "Enter" }]
  }
};
const OPERATOR_KEYS = {
  times: {
    displayName: "×",
    name: "times",
    commands: [{ type: "write", command: "\\times" }]
  },
  divide: {
    displayName: "÷",
    name: "divide",
    commands: [{ type: "cmd", command: "/" }]
  },
  plus: {
    displayName: "+",
    name: "plus",
    commands: [{ type: "write", command: "+" }]
  },
  minus: {
    displayName: "−",
    name: "minus",
    commands: [{ type: "write", command: "-" }]
  },
  equals: {
    displayName: "=",
    name: "equals",
    commands: [{ type: "write", command: "=" }]
  }
};
const VAR_KEYS = {
  x: {
    // displayName: "x",
    displayName: " x",
    name: "x",
    commands: [{ type: "write", command: "x" }]
  },
  y: {
    // displayName: "y",
    displayName: " y",
    name: "y",
    commands: [{ type: "write", command: "y" }]
  },
  pi: {
    // displayName: "π",
    displayName: "\\pi",
    name: "pi",
    commands: [{ type: "write", command: "\\pi" }]
  },
  e: {
    // displayName: "e",
    displayName: " e",
    name: "e",
    commands: [{ type: "write", command: "e" }]
  },
  square: {
    // displayName: "a²",
    displayName: "a^2",
    name: "square",
    commands: [
      { type: "type", command: "^2" },
      { type: "keystroke", command: "Right" }
    ]
  },
  exponent: {
    // displayName: "aᵇ",
    displayName: "b^a",
    name: "exponent",
    commands: [{ type: "cmd", command: "^" }]
  },
  sqrt: {
    // displayName: "√a",
    displayName: "\\sqrt{a}",
    name: "sqrt",
    commands: [{ type: "type", command: "sqrt" }]
  },
  abs: {
    displayName: "|a|",
    name: "abs",
    commands: [
      { type: "cmd", command: "|" },
      { type: "cmd", command: "|" },
      { type: "keystroke", command: "Left" }
    ]
  },
  lt: {
    displayName: "<",
    name: "lt",
    commands: [{ type: "write", command: "<" }]
  },
  gt: {
    displayName: ">",
    name: "gt",
    commands: [{ type: "write", command: ">" }]
  },
  leq: {
    displayName: "≤",
    name: "leq",
    commands: [{ type: "type", command: "<=" }]
  },
  geq: {
    displayName: "≥",
    name: "geq",
    commands: [{ type: "type", command: ">=" }]
  },
  comma: {
    displayName: ",",
    name: "comma",
    commands: [{ type: "write", command: "," }]
  },
  leftParen: {
    displayName: "(",
    name: "leftParen",
    commands: [{ type: "cmd", command: "(" }]
  },
  rightParen: {
    displayName: ")",
    name: "rightParen",
    commands: [{ type: "cmd", command: ")" }]
  }
};
const SYMBOL_KEYS = {
  openBrace: {
    displayName: "\\{",
    name: "openBrace",
    commands: [{ type: "cmd", command: "{" }]
  },
  closeBrace: {
    displayName: "\\}",
    name: "closeBrace",
    commands: [{ type: "cmd", command: "}" }]
  },
  comma: {
    displayName: ",",
    name: "comma",
    commands: [{ type: "write", command: "," }]
  },
  colon: {
    displayName: ":",
    name: "colon",
    commands: [{ type: "write", command: ":" }]
  },
  vert: {
    displayName: "|",
    name: "vert",
    commands: [{ type: "write", command: "\\vert" }]
  },
  subset: {
    displayName: "⊂",
    name: "subset",
    commands: [{ type: "write", command: "\\subset" }]
  },
  subseteq: {
    displayName: "⊆",
    name: "subseteq",
    commands: [{ type: "write", command: "\\subseteq" }]
  },
  neq: {
    displayName: "≠",
    name: "neq",
    commands: [{ type: "write", command: "\\neq" }]
  },
  elementOf: {
    displayName: "∈",
    name: "elementOf",
    commands: [{ type: "write", command: "\\in" }]
  },
  infty: {
    displayName: "∞",
    name: "infinity",
    commands: [{ type: "write", command: "\\infty" }]
  },
  openParen: {
    displayName: "(",
    name: "openParen",
    commands: [{ type: "cmd", command: "(" }]
  },
  closeParen: {
    displayName: ")",
    name: "closeParen",
    commands: [{ type: "cmd", command: ")" }]
  },
  openSquareBracket: {
    displayName: "[",
    name: "openSquareBracket",
    commands: [{ type: "cmd", command: "[" }]
  },
  closeSquareBracket: {
    displayName: "]",
    name: "closeSquareBracket",
    commands: [{ type: "cmd", command: "]" }]
  },
  emptySet: {
    displayName: "∅",
    name: "emptySet",
    commands: [{ type: "write", command: "\\emptyset" }]
  }
};
const OTHER_SYMBOLS = {
  vec: {
    displayName: "a⃗",
    name: "vec",
    commands: [
      { type: "write", command: "\\vec{}" },
      { type: "keystroke", command: "Left" }
    ]
  },
  langle: {
    displayName: "⟨",
    name: "langle",
    commands: [{ type: "cmd", command: "\\langle" }]
  },
  rangle: {
    displayName: "⟩",
    name: "rangle",
    commands: [{ type: "cmd", command: "\\rangle" }]
  },
  cdot: {
    displayName: "⋅",
    name: "cdot",
    commands: [{ type: "write", command: "\\cdot" }]
  },
  times: {
    displayName: "×",
    name: "times",
    commands: [{ type: "write", command: "\\times" }]
  },
  conj: {
    displayName: "ā",
    name: "conj",
    commands: [{ type: "cmd", command: "\\overline" }]
  },
  perp: {
    displayName: "⊥",
    name: "perp",
    commands: [{ type: "write", command: "\\perp" }]
  },
  parallel: {
    displayName: "∥",
    name: "parallel",
    commands: [{ type: "write", command: "\\parallel" }]
  },
  angle: {
    displayName: "∠",
    name: "angle",
    commands: [{ type: "write", command: "\\angle" }]
  },
  degree: {
    displayName: "a°",
    name: "degree",
    commands: [{ type: "write", command: "\\degree" }]
  },
  exists: {
    displayName: "∃",
    name: "exists",
    commands: [{ type: "write", command: "\\exists" }]
  },
  forall: {
    displayName: "∀",
    name: "forall",
    commands: [{ type: "write", command: "\\forall" }]
  },
  percent: {
    displayName: "\\%",
    name: "percent",
    commands: [{ type: "write", command: "%" }]
  },
  dollar: {
    displayName: "$",
    name: "dollar",
    commands: [{ type: "write", command: "$" }]
  },
  subscript: {
    displayName: "bₐ",
    name: "subscript",
    commands: [{ type: "cmd", command: "_" }]
  }
};
const TRIG_KEYS = {
  sin: {
    // displayName: "sin",
    displayName: "\\sin",
    name: "sin",
    commands: [{ type: "type", command: "sin(" }]
  },
  cos: {
    // displayName: "cos",
    displayName: "\\cos",
    name: "cos",
    commands: [{ type: "type", command: "cos(" }]
  },
  tan: {
    // displayName: "tan",
    displayName: "\\tan",
    name: "tan",
    commands: [{ type: "type", command: "tan(" }]
  },
  arcsin: {
    // displayName: "sin⁻¹",
    displayName: "\\sin^{-1}",
    name: "arcsin",
    commands: [
      { type: "write", command: "sin^{-1}" },
      { type: "type", command: "(" }
    ]
  },
  arccos: {
    // displayName: "cos⁻¹",
    displayName: "\\cos^{-1}",
    name: "arccos",
    commands: [
      { type: "write", command: "cos^{-1}" },
      { type: "type", command: "(" }
    ]
  },
  arctan: {
    // displayName: "tan⁻¹",
    displayName: "\\tan^{-1}",
    name: "arctan",
    commands: [
      { type: "write", command: "tan^{-1}" },
      { type: "type", command: "(" }
    ]
  },
  ln: {
    // displayName: "ln",
    displayName: "\\ln",
    name: "ln",
    commands: [{ type: "type", command: "ln(" }]
  },
  logBaseA: {
    // displayName: "logₐ",
    displayName: "\\log_{b}",
    name: "logBaseA",
    commands: [
      { type: "write", command: "log_{}" },
      { type: "keystroke", command: "Left" }
    ]
  },
  lg: {
    // displayName: "log",
    displayName: "\\log",
    name: "lg",
    commands: [{ type: "write", command: "log_{10}" }]
  },
  exp: {
    // displayName: "eᵃ",
    displayName: "e^a",
    name: "exp",
    commands: [
      { type: "write", command: "e^{}" },
      { type: "keystroke", command: "Left" }
    ]
  },
  tenToTheA: {
    // displayName: "10ᵃ",
    displayName: "10^a",
    name: "tenToTheA",
    commands: [
      { type: "write", command: "10^{}" },
      { type: "keystroke", command: "Left" }
    ]
  },
  athRootOfB: {
    // displayName: "ᵃ√b",
    displayName: "\\sqrt[a]{b}",
    name: "athRootOfB",
    commands: [
      { type: "write", command: "\\sqrt[]{}" },
      { type: "keystroke", command: "Left" },
      { type: "keystroke", command: "Left" }
    ]
  }
};
const FUNCTION_KEYS = {
  deriv: {
    // displayName: "d/dx",
    displayName: "\\frac{d}{dx}",
    name: "deriv",
    commands: [{ type: "write", command: "\\frac{d}{dx}" }]
  },
  int: {
    // displayName: "∫",
    displayName: "\\int_a^b",
    name: "int",
    commands: [
      { type: "write", command: "\\int_{}^{}" },
      { type: "keystroke", command: "Left" },
      { type: "keystroke", command: "Left" }
    ]
  },
  nPr: {
    displayName: "nPr",
    name: "nPr",
    commands: [{ type: "type", command: "nPr(" }]
  },
  nCr: {
    displayName: "nCr",
    name: "nCr",
    commands: [{ type: "type", command: "nCr(" }]
  },
  factorial: {
    displayName: "n!",
    name: "factorial",
    commands: [{ type: "write", command: "!" }]
  },
  floor: {
    displayName: "⌊a⌋",
    name: "floor",
    commands: [
      { type: "write", command: "\\lfloor" },
      { type: "write", command: "\\rfloor" },
      { type: "keystroke", command: "Left" }
    ]
  },
  ceil: {
    displayName: "⌈a⌉",
    name: "ceil",
    commands: [
      { type: "write", command: "\\lceil" },
      { type: "write", command: "\\rceil" },
      { type: "keystroke", command: "Left" }
    ]
  }
};
for (const key of [
  ...Object.values(SYMBOL_KEYS),
  ...Object.values(OTHER_SYMBOLS),
  ...Object.values(FUNCTION_KEYS),
  ...Object.values(TRIG_KEYS),
  ...Object.values(OPERATOR_KEYS),
  ...Object.values(VAR_KEYS)
]) {
  key.isMath = true;
}
const BackspaceIcon = /* @__PURE__ */ React__default.createElement(
  "svg",
  {
    "aria-hidden": "true",
    focusable: "false",
    "data-prefix": "fas",
    "data-icon": "delete-left",
    className: "svg-inline--fa fa-delete-left ",
    role: "img",
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 576 512"
  },
  /* @__PURE__ */ React__default.createElement(
    "path",
    {
      fill: "currentColor",
      d: "M576 128c0-35.3-28.7-64-64-64H205.3c-17 0-33.3 6.7-45.3 18.7L9.4 233.4c-6 6-9.4 14.1-9.4 22.6s3.4 16.6 9.4 22.6L160 429.3c12 12 28.3 18.7 45.3 18.7H512c35.3 0 64-28.7 64-64V128zM271 175c9.4-9.4 24.6-9.4 33.9 0l47 47 47-47c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9l-47 47 47 47c9.4 9.4 9.4 24.6 0 33.9s-24.6 9.4-33.9 0l-47-47-47 47c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9l47-47-47-47c-9.4-9.4-9.4-24.6 0-33.9z"
    }
  )
);
function Keyboard({
  style,
  onClick
}) {
  switch (style) {
    case "alpha_lower":
      return /* @__PURE__ */ React__default.createElement("div", { className: "virtual-keyboard alpha" }, /* @__PURE__ */ React__default.createElement(Qwerty, { type: "lower", onClick }));
    case "alpha_upper":
      return /* @__PURE__ */ React__default.createElement("div", { className: "virtual-keyboard alpha" }, /* @__PURE__ */ React__default.createElement(Qwerty, { type: "upper", onClick }));
    case "numeric":
      return /* @__PURE__ */ React__default.createElement("div", { className: "virtual-keyboard numeric" }, /* @__PURE__ */ React__default.createElement(Numeric, { onClick }));
    case "greek_lower":
      return /* @__PURE__ */ React__default.createElement("div", { className: "virtual-keyboard greek" }, /* @__PURE__ */ React__default.createElement(Greek, { type: "lower", onClick }));
    case "greek_upper":
      return /* @__PURE__ */ React__default.createElement("div", { className: "virtual-keyboard greek" }, /* @__PURE__ */ React__default.createElement(Greek, { type: "upper", onClick }));
    case "symbol":
      return /* @__PURE__ */ React__default.createElement("div", { className: "virtual-keyboard symbol" }, /* @__PURE__ */ React__default.createElement(Symbol$1, { onClick }));
    case "function":
      return /* @__PURE__ */ React__default.createElement("div", { className: "virtual-keyboard composite" }, /* @__PURE__ */ React__default.createElement(Composite, { onClick }));
  }
}
function Key({
  keyInfo,
  onClick
}) {
  let content = keyInfo.displayName;
  if (keyInfo.isMath) {
    content = /* @__PURE__ */ React__default.createElement("div", { style: { pointerEvents: "none" } }, /* @__PURE__ */ React__default.createElement(MathJax, null, "\\(" + content + "\\)"));
  }
  if (keyInfo.name === "backspace") {
    content = BackspaceIcon;
  }
  return /* @__PURE__ */ React__default.createElement(
    "button",
    {
      className: `key key-${keyInfo.name} ${keyInfo.isMath ? "math" : ""}`,
      onClick: (e2) => {
        e2.stopPropagation();
        onClick(keyInfo.commands);
      },
      title: `Type ${keyInfo.name}`
    },
    content
  );
}
function Qwerty({
  type,
  onClick
}) {
  const keys2 = type === "upper" ? KEYS.alpha_upper : KEYS.alpha_lower;
  const row1 = keys2.slice(0, 10);
  const row2 = keys2.slice(10, 19);
  const row3 = [
    SPECIAL_KEYS.shift,
    ...keys2.slice(19, 26),
    SPECIAL_KEYS.backspace
  ];
  const row4 = [
    ...keys2.slice(26, 29),
    SPECIAL_KEYS.space,
    SPECIAL_KEYS.left,
    SPECIAL_KEYS.right,
    SPECIAL_KEYS.enter
  ];
  return /* @__PURE__ */ React__default.createElement(React__default.Fragment, null, [row1, row2, row3, row4].map((row, i2) => /* @__PURE__ */ React__default.createElement("div", { key: i2, className: "row" }, row.map((key) => /* @__PURE__ */ React__default.createElement(Key, { keyInfo: key, onClick, key: key.name })))));
}
function Numeric({ onClick }) {
  const row1 = [
    ...KEYS.numeric.slice(0, 3),
    OPERATOR_KEYS.times,
    OPERATOR_KEYS.divide
  ];
  const row2 = [
    ...KEYS.numeric.slice(3, 6),
    OPERATOR_KEYS.plus,
    OPERATOR_KEYS.minus
  ];
  const row3 = [
    ...KEYS.numeric.slice(6, 9),
    OPERATOR_KEYS.equals,
    SPECIAL_KEYS.backspace
  ];
  const row4 = [
    ...KEYS.numeric.slice(9),
    SPECIAL_KEYS.left,
    SPECIAL_KEYS.right,
    SPECIAL_KEYS.enter
  ];
  return /* @__PURE__ */ React__default.createElement(React__default.Fragment, null, /* @__PURE__ */ React__default.createElement("div", { className: "keyboard-region" }, Object.values(VAR_KEYS).map((key) => /* @__PURE__ */ React__default.createElement(Key, { keyInfo: key, onClick, key: key.name }))), /* @__PURE__ */ React__default.createElement("div", { className: "sub-keyboard" }, [row1, row2, row3, row4].map((row, i2) => /* @__PURE__ */ React__default.createElement("div", { key: i2, className: "row" }, row.map((key) => /* @__PURE__ */ React__default.createElement(
    Key,
    {
      keyInfo: key,
      onClick,
      key: key.name
    }
  ))))));
}
function Symbol$1({ onClick }) {
  return /* @__PURE__ */ React__default.createElement(React__default.Fragment, null, /* @__PURE__ */ React__default.createElement("div", { className: "keyboard-region" }, Object.values(SYMBOL_KEYS).map((key) => /* @__PURE__ */ React__default.createElement(Key, { keyInfo: key, onClick, key: key.name }))), /* @__PURE__ */ React__default.createElement("div", { className: "keyboard-region" }, Object.values(OTHER_SYMBOLS).map((key) => /* @__PURE__ */ React__default.createElement(Key, { keyInfo: key, onClick, key: key.name })), [
    SPECIAL_KEYS.left,
    SPECIAL_KEYS.right,
    SPECIAL_KEYS.backspace,
    SPECIAL_KEYS.enter
  ].map((key) => /* @__PURE__ */ React__default.createElement(Key, { keyInfo: key, onClick, key: key.name }))));
}
function Greek({
  type,
  onClick
}) {
  const keys2 = type === "upper" ? KEYS.greek_upper : KEYS.greek_lower;
  const row1 = keys2.slice(0, 10);
  const row2 = keys2.slice(10, 19);
  const row3 = [
    SPECIAL_KEYS.shift,
    ...keys2.slice(19, 26),
    SPECIAL_KEYS.backspace
  ];
  const row4 = [
    ...keys2.slice(26, 29),
    SPECIAL_KEYS.space,
    SPECIAL_KEYS.left,
    SPECIAL_KEYS.right,
    SPECIAL_KEYS.enter
  ];
  return /* @__PURE__ */ React__default.createElement(React__default.Fragment, null, [row1, row2, row3, row4].map((row, i2) => /* @__PURE__ */ React__default.createElement("div", { key: i2, className: "row" }, row.map((key) => /* @__PURE__ */ React__default.createElement(Key, { keyInfo: key, onClick, key: key.name })))));
}
function Composite({ onClick }) {
  return /* @__PURE__ */ React__default.createElement(React__default.Fragment, null, /* @__PURE__ */ React__default.createElement("div", { className: "keyboard-region" }, Object.values(TRIG_KEYS).map((key) => /* @__PURE__ */ React__default.createElement(Key, { keyInfo: key, onClick, key: key.name }))), /* @__PURE__ */ React__default.createElement("div", { className: "keyboard-region" }, Object.values(FUNCTION_KEYS).map((key) => /* @__PURE__ */ React__default.createElement(Key, { keyInfo: key, onClick, key: key.name })), [
    SPECIAL_KEYS.backspace,
    SPECIAL_KEYS.left,
    SPECIAL_KEYS.right,
    SPECIAL_KEYS.enter
  ].map((key) => /* @__PURE__ */ React__default.createElement(Key, { keyInfo: key, onClick, key: key.name }))));
}
const FocusableContext = createContext(true);
function flatten2DArray(array) {
  const flattened = [];
  for (const row of array) flattened.push(...row);
  return flattened;
}
function reverseArray(array) {
  return array.slice().reverse();
}
function noop$1(..._2) {
}
function hasOwnProperty$2(object2, prop) {
  if (typeof Object.hasOwn === "function") return Object.hasOwn(object2, prop);
  return Object.prototype.hasOwnProperty.call(object2, prop);
}
function chain$1(...fns) {
  return (...args) => {
    for (const fn of fns) if (typeof fn === "function") fn(...args);
  };
}
function omit$1(object2, keys2) {
  const result2 = { ...object2 };
  for (const key of keys2) if (hasOwnProperty$2(result2, key)) delete result2[key];
  return result2;
}
function pick(object2, paths) {
  const result2 = {};
  for (const key of paths) if (hasOwnProperty$2(object2, key)) result2[key] = object2[key];
  return result2;
}
function identity$1(value) {
  return value;
}
function invariant(condition, message) {
  if (condition) return;
  if (typeof message !== "string") throw new Error("Invariant failed");
  throw new Error(message);
}
function getKeys(obj) {
  return Object.keys(obj);
}
function disabledFromProps(props) {
  return props.disabled || props["aria-disabled"] === true || props["aria-disabled"] === "true";
}
function removeUndefinedValues(obj) {
  const result2 = {};
  for (const key in obj) if (obj[key] !== void 0) result2[key] = obj[key];
  return result2;
}
function defaultValue(...values2) {
  for (const value of values2) if (value !== void 0) return value;
}
const canUseDOM = checkIsBrowser();
function checkIsBrowser() {
  return typeof window !== "undefined" && !!window.document?.createElement;
}
function getDocument(node) {
  if (!node) return document;
  if ("self" in node) return node.document;
  return node.ownerDocument || document;
}
function getActiveElement(node, activeDescendant = false) {
  const { activeElement } = getDocument(node);
  if (!activeElement?.nodeName) return null;
  if (isFrame(activeElement) && activeElement.contentDocument?.body) return getActiveElement(activeElement.contentDocument.body, activeDescendant);
  if (activeDescendant) {
    const id = activeElement.getAttribute("aria-activedescendant");
    if (id) {
      const element = getDocument(activeElement).getElementById(id);
      if (element) return element;
    }
  }
  return activeElement;
}
function contains$1(parent, child) {
  return parent === child || parent.contains(child);
}
function isElement$1(target) {
  return target?.nodeType === 1;
}
function isNode$1(target) {
  return typeof target?.nodeType === "number";
}
function isFrame(element) {
  return element.tagName === "IFRAME";
}
function isButton(element) {
  const tagName = element.tagName.toLowerCase();
  if (tagName === "button") return true;
  if (tagName === "input" && element.type) return buttonInputTypes.indexOf(element.type) !== -1;
  return false;
}
const buttonInputTypes = [
  "button",
  "color",
  "file",
  "image",
  "reset",
  "submit"
];
function isVisible(element) {
  if (typeof element.checkVisibility === "function") return element.checkVisibility();
  const htmlElement = element;
  return htmlElement.offsetWidth > 0 || htmlElement.offsetHeight > 0 || element.getClientRects().length > 0;
}
function isTextField(element) {
  try {
    if (element.tagName === "TEXTAREA") return true;
    if (element.tagName !== "INPUT") return false;
    return element.selectionStart !== null;
  } catch (_error) {
    return false;
  }
}
function isTextbox(element) {
  return element.isContentEditable || isTextField(element);
}
function getTextboxValue(element) {
  if (isTextField(element)) return element.value;
  if (element.isContentEditable) {
    const range2 = getDocument(element).createRange();
    range2.selectNodeContents(element);
    return range2.toString();
  }
  return "";
}
function getTextboxSelection(element) {
  let start = 0;
  let end = 0;
  if (isTextField(element)) {
    start = element.selectionStart || 0;
    end = element.selectionEnd || 0;
  } else if (element.isContentEditable) {
    const selection = getDocument(element).getSelection();
    if (selection?.rangeCount && selection.anchorNode && contains$1(element, selection.anchorNode) && selection.focusNode && contains$1(element, selection.focusNode)) {
      const range2 = selection.getRangeAt(0);
      const nextRange = range2.cloneRange();
      nextRange.selectNodeContents(element);
      nextRange.setEnd(range2.startContainer, range2.startOffset);
      start = nextRange.toString().length;
      nextRange.setEnd(range2.endContainer, range2.endOffset);
      end = nextRange.toString().length;
    }
  }
  return {
    start,
    end
  };
}
function getScrollingElement(element) {
  if (!element) return null;
  const isScrollableOverflow = (overflow) => {
    if (overflow === "auto") return true;
    if (overflow === "scroll") return true;
    return false;
  };
  if (element.clientHeight && element.scrollHeight > element.clientHeight) {
    const { overflowY } = getComputedStyle(element);
    if (isScrollableOverflow(overflowY)) return element;
  } else if (element.clientWidth && element.scrollWidth > element.clientWidth) {
    const { overflowX } = getComputedStyle(element);
    if (isScrollableOverflow(overflowX)) return element;
  }
  const doc = getDocument(element);
  return getScrollingElement(element.parentElement) || doc.scrollingElement || doc.body;
}
function sortBasedOnDOMPosition(items, getElement) {
  const pairs2 = items.map((item, index) => [index, item]);
  let isOrderDifferent = false;
  pairs2.sort(([indexA, a2], [indexB, b2]) => {
    const elementA = getElement(a2);
    const elementB = getElement(b2);
    if (elementA === elementB) return 0;
    if (!elementA || !elementB) return 0;
    if (isElementPreceding(elementA, elementB)) {
      if (indexA > indexB) isOrderDifferent = true;
      return -1;
    }
    if (indexA < indexB) isOrderDifferent = true;
    return 1;
  });
  if (isOrderDifferent) return pairs2.map(([_2, item]) => item);
  return items;
}
function isElementPreceding(a2, b2) {
  return Boolean(b2.compareDocumentPosition(a2) & Node.DOCUMENT_POSITION_PRECEDING);
}
function isApple() {
  if (!canUseDOM) return false;
  return /mac|iphone|ipad|ipod/i.test(navigator.platform);
}
function isSafari() {
  return canUseDOM && isApple() && /apple/i.test(navigator.vendor);
}
function isFirefox() {
  return canUseDOM && /firefox\//i.test(navigator.userAgent);
}
function isPortalEvent(event) {
  const { currentTarget, target } = event;
  if (!currentTarget) return false;
  if (!isNode$1(target)) return true;
  return !contains$1(currentTarget, target);
}
function isSelfTarget(event) {
  return event.target === event.currentTarget;
}
function fireBlurEvent(element, eventInit) {
  const event = new FocusEvent("blur", eventInit);
  const defaultAllowed = element.dispatchEvent(event);
  const bubbleInit = {
    ...eventInit,
    bubbles: true
  };
  element.dispatchEvent(new FocusEvent("focusout", bubbleInit));
  return defaultAllowed;
}
function fireKeyboardEvent(element, type, eventInit) {
  const event = new KeyboardEvent(type, eventInit);
  return element.dispatchEvent(event);
}
function fireClickEvent(element, eventInit) {
  const event = new MouseEvent("click", eventInit);
  return element.dispatchEvent(event);
}
function isFocusEventOutside(event, container) {
  const containerElement = event.currentTarget;
  const relatedTarget = event.relatedTarget;
  return !isNode$1(relatedTarget) || !contains$1(containerElement, relatedTarget);
}
function queueBeforeEvent(element, type, callback, timeout) {
  const createTimer = (callback2) => {
    const timerId = requestAnimationFrame(callback2);
    return () => cancelAnimationFrame(timerId);
  };
  const cancelTimer = createTimer(() => {
    element.removeEventListener(type, callSync, true);
    callback();
  });
  const callSync = () => {
    cancelTimer();
    callback();
  };
  element.addEventListener(type, callSync, {
    once: true,
    capture: true
  });
  return () => {
    cancelTimer();
    element.removeEventListener(type, callSync, true);
  };
}
function addGlobalEventListener(type, listener, options, scope = window) {
  const children = [];
  try {
    scope.document.addEventListener(type, listener, options);
    for (const frame of Array.from(scope.frames)) children.push(addGlobalEventListener(type, listener, options, frame));
  } catch {
  }
  const removeEventListener = () => {
    try {
      scope.document.removeEventListener(type, listener, options);
    } catch {
    }
    for (const remove of children) remove();
  };
  return removeEventListener;
}
const selector = "input:not([type='hidden']):not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], button:not([disabled]), [tabindex], summary, iframe, object, embed, area[href], audio[controls], video[controls], [contenteditable]:not([contenteditable='false'])";
function isFocusable(element) {
  if (!element.matches(selector)) return false;
  if (!isVisible(element)) return false;
  if (element.closest("[inert]")) return false;
  return true;
}
function hasFocus(element) {
  const activeElement = getActiveElement(element);
  if (!activeElement) return false;
  if (activeElement === element) return true;
  const activeDescendant = activeElement.getAttribute("aria-activedescendant");
  if (!activeDescendant) return false;
  return activeDescendant === element.id;
}
function focusIntoView(element, options) {
  if (!("scrollIntoView" in element)) element.focus();
  else {
    element.focus({ preventScroll: true });
    element.scrollIntoView({
      block: "nearest",
      inline: "nearest",
      ...options
    });
  }
}
function createUndoCallback(callback) {
  return async () => {
    const redo = await callback?.();
    return createUndoCallback(async () => {
      await redo?.();
      return callback;
    });
  };
}
createUndoManager();
function createUndoManager({ limit = 100 } = {}) {
  const undoStack = [];
  let redoStack = [];
  let currentGroup = null;
  const canUndo = () => undoStack.length > 0;
  const canRedo = () => redoStack.length > 0;
  const undo = async () => {
    if (!canUndo()) return;
    currentGroup = null;
    redoStack.push(await undoStack.pop()?.());
  };
  const redo = async () => {
    if (!canRedo()) return;
    currentGroup = null;
    undoStack.push(await redoStack.pop()?.());
  };
  const execute = async (callback, group2) => {
    if (!callback) return;
    const sameGroup = group2 === currentGroup;
    currentGroup = group2 ?? null;
    const nextIndex = sameGroup ? Math.max(0, undoStack.length - 1) : undoStack.length;
    const undoCallback = await callback();
    if (!undoCallback) return;
    redoStack = [];
    const currentUndo = undoStack[nextIndex];
    undoStack[nextIndex] = createUndoCallback(async () => {
      await undoCallback?.();
      const currentRedo = await currentUndo?.();
      return async () => {
        await currentRedo?.();
        await callback?.();
      };
    });
    while (undoStack.length > limit) undoStack.shift();
  };
  return {
    canUndo,
    canRedo,
    undo,
    redo,
    execute
  };
}
function getDefaultExportFromCjs$1(x2) {
  return x2 && x2.__esModule && Object.prototype.hasOwnProperty.call(x2, "default") ? x2["default"] : x2;
}
var jsxRuntime = { exports: {} };
var reactJsxRuntime_production = {};
var hasRequiredReactJsxRuntime_production;
function requireReactJsxRuntime_production() {
  if (hasRequiredReactJsxRuntime_production) return reactJsxRuntime_production;
  hasRequiredReactJsxRuntime_production = 1;
  var REACT_ELEMENT_TYPE = /* @__PURE__ */ Symbol.for("react.transitional.element"), REACT_FRAGMENT_TYPE = /* @__PURE__ */ Symbol.for("react.fragment");
  function jsxProd(type, config2, maybeKey) {
    var key = null;
    void 0 !== maybeKey && (key = "" + maybeKey);
    void 0 !== config2.key && (key = "" + config2.key);
    if ("key" in config2) {
      maybeKey = {};
      for (var propName in config2)
        "key" !== propName && (maybeKey[propName] = config2[propName]);
    } else maybeKey = config2;
    config2 = maybeKey.ref;
    return {
      $$typeof: REACT_ELEMENT_TYPE,
      type,
      key,
      ref: void 0 !== config2 ? config2 : null,
      props: maybeKey
    };
  }
  reactJsxRuntime_production.Fragment = REACT_FRAGMENT_TYPE;
  reactJsxRuntime_production.jsx = jsxProd;
  reactJsxRuntime_production.jsxs = jsxProd;
  return reactJsxRuntime_production;
}
var reactJsxRuntime_development = {};
var hasRequiredReactJsxRuntime_development;
function requireReactJsxRuntime_development() {
  if (hasRequiredReactJsxRuntime_development) return reactJsxRuntime_development;
  hasRequiredReactJsxRuntime_development = 1;
  "production" !== process.env.NODE_ENV && (function() {
    function getComponentNameFromType(type) {
      if (null == type) return null;
      if ("function" === typeof type)
        return type.$$typeof === REACT_CLIENT_REFERENCE ? null : type.displayName || type.name || null;
      if ("string" === typeof type) return type;
      switch (type) {
        case REACT_FRAGMENT_TYPE:
          return "Fragment";
        case REACT_PROFILER_TYPE:
          return "Profiler";
        case REACT_STRICT_MODE_TYPE:
          return "StrictMode";
        case REACT_SUSPENSE_TYPE:
          return "Suspense";
        case REACT_SUSPENSE_LIST_TYPE:
          return "SuspenseList";
        case REACT_ACTIVITY_TYPE:
          return "Activity";
      }
      if ("object" === typeof type)
        switch ("number" === typeof type.tag && console.error(
          "Received an unexpected object in getComponentNameFromType(). This is likely a bug in React. Please file an issue."
        ), type.$$typeof) {
          case REACT_PORTAL_TYPE:
            return "Portal";
          case REACT_CONTEXT_TYPE:
            return type.displayName || "Context";
          case REACT_CONSUMER_TYPE:
            return (type._context.displayName || "Context") + ".Consumer";
          case REACT_FORWARD_REF_TYPE:
            var innerType = type.render;
            type = type.displayName;
            type || (type = innerType.displayName || innerType.name || "", type = "" !== type ? "ForwardRef(" + type + ")" : "ForwardRef");
            return type;
          case REACT_MEMO_TYPE:
            return innerType = type.displayName || null, null !== innerType ? innerType : getComponentNameFromType(type.type) || "Memo";
          case REACT_LAZY_TYPE:
            innerType = type._payload;
            type = type._init;
            try {
              return getComponentNameFromType(type(innerType));
            } catch (x2) {
            }
        }
      return null;
    }
    function testStringCoercion(value) {
      return "" + value;
    }
    function checkKeyStringCoercion(value) {
      try {
        testStringCoercion(value);
        var JSCompiler_inline_result = false;
      } catch (e2) {
        JSCompiler_inline_result = true;
      }
      if (JSCompiler_inline_result) {
        JSCompiler_inline_result = console;
        var JSCompiler_temp_const = JSCompiler_inline_result.error;
        var JSCompiler_inline_result$jscomp$0 = "function" === typeof Symbol && Symbol.toStringTag && value[Symbol.toStringTag] || value.constructor.name || "Object";
        JSCompiler_temp_const.call(
          JSCompiler_inline_result,
          "The provided key is an unsupported type %s. This value must be coerced to a string before using it here.",
          JSCompiler_inline_result$jscomp$0
        );
        return testStringCoercion(value);
      }
    }
    function getTaskName(type) {
      if (type === REACT_FRAGMENT_TYPE) return "<>";
      if ("object" === typeof type && null !== type && type.$$typeof === REACT_LAZY_TYPE)
        return "<...>";
      try {
        var name2 = getComponentNameFromType(type);
        return name2 ? "<" + name2 + ">" : "<...>";
      } catch (x2) {
        return "<...>";
      }
    }
    function getOwner() {
      var dispatcher = ReactSharedInternals.A;
      return null === dispatcher ? null : dispatcher.getOwner();
    }
    function UnknownOwner() {
      return Error("react-stack-top-frame");
    }
    function hasValidKey(config2) {
      if (hasOwnProperty2.call(config2, "key")) {
        var getter = Object.getOwnPropertyDescriptor(config2, "key").get;
        if (getter && getter.isReactWarning) return false;
      }
      return void 0 !== config2.key;
    }
    function defineKeyPropWarningGetter(props, displayName) {
      function warnAboutAccessingKey() {
        specialPropKeyWarningShown || (specialPropKeyWarningShown = true, console.error(
          "%s: `key` is not a prop. Trying to access it will result in `undefined` being returned. If you need to access the same value within the child component, you should pass it as a different prop. (https://react.dev/link/special-props)",
          displayName
        ));
      }
      warnAboutAccessingKey.isReactWarning = true;
      Object.defineProperty(props, "key", {
        get: warnAboutAccessingKey,
        configurable: true
      });
    }
    function elementRefGetterWithDeprecationWarning() {
      var componentName = getComponentNameFromType(this.type);
      didWarnAboutElementRef[componentName] || (didWarnAboutElementRef[componentName] = true, console.error(
        "Accessing element.ref was removed in React 19. ref is now a regular prop. It will be removed from the JSX Element type in a future release."
      ));
      componentName = this.props.ref;
      return void 0 !== componentName ? componentName : null;
    }
    function ReactElement(type, key, props, owner, debugStack, debugTask) {
      var refProp = props.ref;
      type = {
        $$typeof: REACT_ELEMENT_TYPE,
        type,
        key,
        props,
        _owner: owner
      };
      null !== (void 0 !== refProp ? refProp : null) ? Object.defineProperty(type, "ref", {
        enumerable: false,
        get: elementRefGetterWithDeprecationWarning
      }) : Object.defineProperty(type, "ref", { enumerable: false, value: null });
      type._store = {};
      Object.defineProperty(type._store, "validated", {
        configurable: false,
        enumerable: false,
        writable: true,
        value: 0
      });
      Object.defineProperty(type, "_debugInfo", {
        configurable: false,
        enumerable: false,
        writable: true,
        value: null
      });
      Object.defineProperty(type, "_debugStack", {
        configurable: false,
        enumerable: false,
        writable: true,
        value: debugStack
      });
      Object.defineProperty(type, "_debugTask", {
        configurable: false,
        enumerable: false,
        writable: true,
        value: debugTask
      });
      Object.freeze && (Object.freeze(type.props), Object.freeze(type));
      return type;
    }
    function jsxDEVImpl(type, config2, maybeKey, isStaticChildren, debugStack, debugTask) {
      var children = config2.children;
      if (void 0 !== children)
        if (isStaticChildren)
          if (isArrayImpl(children)) {
            for (isStaticChildren = 0; isStaticChildren < children.length; isStaticChildren++)
              validateChildKeys(children[isStaticChildren]);
            Object.freeze && Object.freeze(children);
          } else
            console.error(
              "React.jsx: Static children should always be an array. You are likely explicitly calling React.jsxs or React.jsxDEV. Use the Babel transform instead."
            );
        else validateChildKeys(children);
      if (hasOwnProperty2.call(config2, "key")) {
        children = getComponentNameFromType(type);
        var keys2 = Object.keys(config2).filter(function(k2) {
          return "key" !== k2;
        });
        isStaticChildren = 0 < keys2.length ? "{key: someKey, " + keys2.join(": ..., ") + ": ...}" : "{key: someKey}";
        didWarnAboutKeySpread[children + isStaticChildren] || (keys2 = 0 < keys2.length ? "{" + keys2.join(": ..., ") + ": ...}" : "{}", console.error(
          'A props object containing a "key" prop is being spread into JSX:\n  let props = %s;\n  <%s {...props} />\nReact keys must be passed directly to JSX without using spread:\n  let props = %s;\n  <%s key={someKey} {...props} />',
          isStaticChildren,
          children,
          keys2,
          children
        ), didWarnAboutKeySpread[children + isStaticChildren] = true);
      }
      children = null;
      void 0 !== maybeKey && (checkKeyStringCoercion(maybeKey), children = "" + maybeKey);
      hasValidKey(config2) && (checkKeyStringCoercion(config2.key), children = "" + config2.key);
      if ("key" in config2) {
        maybeKey = {};
        for (var propName in config2)
          "key" !== propName && (maybeKey[propName] = config2[propName]);
      } else maybeKey = config2;
      children && defineKeyPropWarningGetter(
        maybeKey,
        "function" === typeof type ? type.displayName || type.name || "Unknown" : type
      );
      return ReactElement(
        type,
        children,
        maybeKey,
        getOwner(),
        debugStack,
        debugTask
      );
    }
    function validateChildKeys(node) {
      isValidElement2(node) ? node._store && (node._store.validated = 1) : "object" === typeof node && null !== node && node.$$typeof === REACT_LAZY_TYPE && ("fulfilled" === node._payload.status ? isValidElement2(node._payload.value) && node._payload.value._store && (node._payload.value._store.validated = 1) : node._store && (node._store.validated = 1));
    }
    function isValidElement2(object2) {
      return "object" === typeof object2 && null !== object2 && object2.$$typeof === REACT_ELEMENT_TYPE;
    }
    var React2 = React__default, REACT_ELEMENT_TYPE = /* @__PURE__ */ Symbol.for("react.transitional.element"), REACT_PORTAL_TYPE = /* @__PURE__ */ Symbol.for("react.portal"), REACT_FRAGMENT_TYPE = /* @__PURE__ */ Symbol.for("react.fragment"), REACT_STRICT_MODE_TYPE = /* @__PURE__ */ Symbol.for("react.strict_mode"), REACT_PROFILER_TYPE = /* @__PURE__ */ Symbol.for("react.profiler"), REACT_CONSUMER_TYPE = /* @__PURE__ */ Symbol.for("react.consumer"), REACT_CONTEXT_TYPE = /* @__PURE__ */ Symbol.for("react.context"), REACT_FORWARD_REF_TYPE = /* @__PURE__ */ Symbol.for("react.forward_ref"), REACT_SUSPENSE_TYPE = /* @__PURE__ */ Symbol.for("react.suspense"), REACT_SUSPENSE_LIST_TYPE = /* @__PURE__ */ Symbol.for("react.suspense_list"), REACT_MEMO_TYPE = /* @__PURE__ */ Symbol.for("react.memo"), REACT_LAZY_TYPE = /* @__PURE__ */ Symbol.for("react.lazy"), REACT_ACTIVITY_TYPE = /* @__PURE__ */ Symbol.for("react.activity"), REACT_CLIENT_REFERENCE = /* @__PURE__ */ Symbol.for("react.client.reference"), ReactSharedInternals = React2.__CLIENT_INTERNALS_DO_NOT_USE_OR_WARN_USERS_THEY_CANNOT_UPGRADE, hasOwnProperty2 = Object.prototype.hasOwnProperty, isArrayImpl = Array.isArray, createTask = console.createTask ? console.createTask : function() {
      return null;
    };
    React2 = {
      react_stack_bottom_frame: function(callStackForError) {
        return callStackForError();
      }
    };
    var specialPropKeyWarningShown;
    var didWarnAboutElementRef = {};
    var unknownOwnerDebugStack = React2.react_stack_bottom_frame.bind(
      React2,
      UnknownOwner
    )();
    var unknownOwnerDebugTask = createTask(getTaskName(UnknownOwner));
    var didWarnAboutKeySpread = {};
    reactJsxRuntime_development.Fragment = REACT_FRAGMENT_TYPE;
    reactJsxRuntime_development.jsx = function(type, config2, maybeKey) {
      var trackActualOwner = 1e4 > ReactSharedInternals.recentlyCreatedOwnerStacks++;
      return jsxDEVImpl(
        type,
        config2,
        maybeKey,
        false,
        trackActualOwner ? Error("react-stack-top-frame") : unknownOwnerDebugStack,
        trackActualOwner ? createTask(getTaskName(type)) : unknownOwnerDebugTask
      );
    };
    reactJsxRuntime_development.jsxs = function(type, config2, maybeKey) {
      var trackActualOwner = 1e4 > ReactSharedInternals.recentlyCreatedOwnerStacks++;
      return jsxDEVImpl(
        type,
        config2,
        maybeKey,
        true,
        trackActualOwner ? Error("react-stack-top-frame") : unknownOwnerDebugStack,
        trackActualOwner ? createTask(getTaskName(type)) : unknownOwnerDebugTask
      );
    };
  })();
  return reactJsxRuntime_development;
}
var hasRequiredJsxRuntime;
function requireJsxRuntime() {
  if (hasRequiredJsxRuntime) return jsxRuntime.exports;
  hasRequiredJsxRuntime = 1;
  if (process.env.NODE_ENV === "production") {
    jsxRuntime.exports = requireReactJsxRuntime_production();
  } else {
    jsxRuntime.exports = requireReactJsxRuntime_development();
  }
  return jsxRuntime.exports;
}
var jsxRuntimeExports = requireJsxRuntime();
function setRef(ref, value) {
  if (typeof ref === "function") {
    const cleanup = ref(value);
    if (typeof cleanup === "function") return cleanup;
  } else if (ref) ref.current = value;
}
function isValidElementWithRef(element) {
  if (!element) return false;
  if (!isValidElement(element)) return false;
  if ("ref" in element.props) return true;
  if ("ref" in element) return true;
  return false;
}
function getRefProperty(element) {
  if (!isValidElementWithRef(element)) return null;
  return { ...element.props }.ref || element.ref;
}
function mergeProps(base, overrides) {
  const props = { ...base };
  for (const key in overrides) {
    if (!hasOwnProperty$2(overrides, key)) continue;
    if (key === "className") {
      const prop = "className";
      const baseClass = base[prop];
      const overrideClass = overrides[prop];
      if (baseClass && overrideClass) props[prop] = `${baseClass} ${overrideClass}`;
      else props[prop] = overrideClass || baseClass;
      continue;
    }
    if (key === "style") {
      const prop = "style";
      props[prop] = base[prop] ? {
        ...base[prop],
        ...overrides[prop]
      } : overrides[prop];
      continue;
    }
    const overrideValue = overrides[key];
    if (key.startsWith("on")) {
      if (typeof overrideValue !== "function") continue;
      const baseValue = base[key];
      if (typeof baseValue === "function") {
        props[key] = (...args) => {
          overrideValue(...args);
          baseValue(...args);
        };
        continue;
      }
    }
    props[key] = overrideValue;
  }
  return props;
}
const _React = { ...React };
const useReactId = _React.useId;
const useReactInsertionEffect = _React.useInsertionEffect;
const useSafeLayoutEffect = canUseDOM ? useLayoutEffect : useEffect;
function useLiveRef(value) {
  const ref = useRef(value);
  useSafeLayoutEffect(() => {
    ref.current = value;
  });
  return ref;
}
function useEvent(callback) {
  const ref = useRef(() => {
    throw new Error("Cannot call an event handler while rendering.");
  });
  if (useReactInsertionEffect) useReactInsertionEffect(() => {
    ref.current = callback;
  });
  else ref.current = callback;
  return useCallback((...args) => ref.current?.(...args), []);
}
function useTransactionState(callback) {
  const [state, setState] = useState(null);
  useSafeLayoutEffect(() => {
    if (state == null) return;
    if (!callback) return;
    let prevState = null;
    callback((prev) => {
      prevState = prev;
      return state;
    });
    return () => {
      callback(prevState);
    };
  }, [state, callback]);
  return [state, setState];
}
function useMergeRefs(...refs) {
  return useMemo(() => {
    if (!refs.some(Boolean)) return;
    return (value) => {
      const refEffects = [];
      for (const ref of refs) {
        if (!ref) continue;
        const cleanup = setRef(ref, value);
        refEffects.push({
          ref,
          cleanup: typeof cleanup === "function" ? cleanup : void 0
        });
      }
      if (!refEffects.some((effect) => effect.cleanup)) return;
      return () => {
        for (const { ref, cleanup } of refEffects) if (cleanup) cleanup();
        else setRef(ref, null);
      };
    };
  }, refs);
}
function useId(defaultId) {
  if (useReactId) {
    const reactId = useReactId();
    if (defaultId) return defaultId;
    return reactId;
  }
  const [id, setId] = useState(defaultId);
  useSafeLayoutEffect(() => {
    if (defaultId || id) return;
    setId(`id-${Math.random().toString(36).slice(2, 8)}`);
  }, [defaultId, id]);
  return defaultId || id;
}
function useTagName(refOrElement, type) {
  const stringOrUndefined = (type2) => {
    return;
  };
  const [tagName, setTagName] = useState(() => stringOrUndefined());
  useSafeLayoutEffect(() => {
    setTagName((refOrElement && "current" in refOrElement ? refOrElement.current : refOrElement)?.tagName.toLowerCase() || stringOrUndefined());
  }, [refOrElement, type]);
  return tagName;
}
function useUpdateEffect(effect, deps) {
  const mounted = useRef(false);
  useEffect(() => {
    if (mounted.current) return effect();
    mounted.current = true;
  }, deps);
  useEffect(() => () => {
    mounted.current = false;
  }, []);
}
function useBooleanEvent(booleanOrCallback) {
  return useEvent(typeof booleanOrCallback === "function" ? booleanOrCallback : () => booleanOrCallback);
}
function useWrapElement(props, callback, deps = []) {
  const wrapElement = useCallback((element) => {
    if (props.wrapElement) element = props.wrapElement(element);
    return callback(element);
  }, [...deps, props.wrapElement]);
  return {
    ...props,
    wrapElement
  };
}
function useMetadataProps(props, key, value) {
  const parent = props.onLoadedMetadataCapture;
  const onLoadedMetadataCapture = useMemo(() => {
    return Object.assign(() => {
    }, parent, ...value !== void 0 ? [{ [key]: value }] : []);
  }, [
    parent,
    key,
    value
  ]);
  return [parent?.[key], { onLoadedMetadataCapture }];
}
function forwardRef(render) {
  const Role = React.forwardRef((props, ref) => render({
    ...props,
    ref
  }));
  Role.displayName = render.displayName || render.name;
  return Role;
}
function memo(Component, propsAreEqual) {
  return React.memo(Component, propsAreEqual);
}
function createElement(Type, props) {
  const { wrapElement, render, ...rest2 } = props;
  const mergedRef = useMergeRefs(props.ref, getRefProperty(render));
  let element;
  if (React.isValidElement(render)) {
    const renderProps = {
      ...render.props,
      ref: mergedRef
    };
    element = React.cloneElement(render, mergeProps(rest2, renderProps));
  } else if (render) element = render(rest2);
  else element = /* @__PURE__ */ jsxRuntimeExports.jsx(Type, { ...rest2 });
  if (wrapElement) return wrapElement(element);
  return element;
}
function createHook(useProps) {
  const useRole = (props = {}) => {
    return useProps(props);
  };
  useRole.displayName = useProps.name;
  return useRole;
}
function createStoreContext(providers = [], scopedProviders = []) {
  const context = React.createContext(void 0);
  const scopedContext = React.createContext(void 0);
  const useContext2 = () => React.useContext(context);
  const useScopedContext = (onlyScoped = false) => {
    const scoped = React.useContext(scopedContext);
    const store = useContext2();
    if (onlyScoped) return scoped;
    return scoped || store;
  };
  const useProviderContext = () => {
    const scoped = React.useContext(scopedContext);
    const store = useContext2();
    if (scoped && scoped === store) return;
    return store;
  };
  const ContextProvider = (props) => {
    return providers.reduceRight((children, Provider) => /* @__PURE__ */ jsxRuntimeExports.jsx(Provider, {
      ...props,
      children
    }), /* @__PURE__ */ jsxRuntimeExports.jsx(context.Provider, { ...props }));
  };
  const ScopedContextProvider = (props) => {
    return /* @__PURE__ */ jsxRuntimeExports.jsx(ContextProvider, {
      ...props,
      children: scopedProviders.reduceRight((children, Provider) => /* @__PURE__ */ jsxRuntimeExports.jsx(Provider, {
        ...props,
        children
      }), /* @__PURE__ */ jsxRuntimeExports.jsx(scopedContext.Provider, { ...props }))
    });
  };
  return {
    context,
    scopedContext,
    useContext: useContext2,
    useScopedContext,
    useProviderContext,
    ContextProvider,
    ScopedContextProvider
  };
}
const TagName$6 = "div";
const accessibleWhenDisabledSymbol = /* @__PURE__ */ Symbol("accessibleWhenDisabled");
const isSafariBrowser = isSafari();
const alwaysFocusVisibleInputTypes = [
  "text",
  "search",
  "url",
  "tel",
  "email",
  "password",
  "number",
  "date",
  "month",
  "week",
  "time",
  "datetime",
  "datetime-local"
];
function isAlwaysFocusVisible(element) {
  const { tagName, readOnly, type } = element;
  if (tagName === "TEXTAREA" && !readOnly) return true;
  if (tagName === "SELECT" && !readOnly) return true;
  if (tagName === "INPUT" && !readOnly) return alwaysFocusVisibleInputTypes.includes(type);
  if (element.isContentEditable) return true;
  if (element.getAttribute("role") === "combobox" && element.dataset.name) return true;
  return false;
}
function isNativeTabbable(tagName) {
  if (!tagName) return true;
  return tagName === "button" || tagName === "summary" || tagName === "input" || tagName === "select" || tagName === "textarea" || tagName === "a";
}
function supportsDisabledAttribute(tagName) {
  if (!tagName) return true;
  return tagName === "button" || tagName === "input" || tagName === "select" || tagName === "textarea";
}
function isNativeSubmitControl(element) {
  if (element.tagName === "BUTTON") {
    const { type } = element;
    return type === "submit";
  }
  if (element.tagName === "INPUT") {
    const { type } = element;
    return type === "submit" || type === "image";
  }
  return false;
}
function getTabIndex({ focusable, trulyDisabled, nativeTabbable, supportsDisabled, safariTabIndex, tabIndexProp }) {
  if (!focusable) return tabIndexProp;
  if (trulyDisabled) {
    if (nativeTabbable && !supportsDisabled) return -1;
    return;
  }
  if (nativeTabbable) {
    if (safariTabIndex && tabIndexProp == null) return 0;
    return tabIndexProp;
  }
  return tabIndexProp ?? 0;
}
function useDisableEvent(onEvent, disabled) {
  return useEvent((event) => {
    onEvent?.(event);
    if (event.defaultPrevented) return;
    if (disabled) {
      event.stopPropagation();
      event.preventDefault();
    }
  });
}
let hasInstalledGlobalEventListeners = false;
let isKeyboardModality = true;
function onGlobalMouseDown(event) {
  const target = event.target;
  if (isElement$1(target) && !target.hasAttribute("data-focus-visible")) isKeyboardModality = false;
}
function onGlobalKeyDown(event) {
  if (event.metaKey) return;
  if (event.ctrlKey) return;
  if (event.altKey) return;
  isKeyboardModality = true;
}
const useFocusable = createHook(function useFocusable2({ focusable = true, accessibleWhenDisabled, autoFocus, onFocusVisible, ...props }) {
  const ref = useRef(null);
  const [parentAccessibleWhenDisabled, metadataProps] = useMetadataProps(props, accessibleWhenDisabledSymbol, accessibleWhenDisabled);
  accessibleWhenDisabled ??= parentAccessibleWhenDisabled;
  useEffect(() => {
    if (!focusable) return;
    if (hasInstalledGlobalEventListeners) return;
    addGlobalEventListener("mousedown", onGlobalMouseDown, true);
    addGlobalEventListener("keydown", onGlobalKeyDown, true);
    hasInstalledGlobalEventListeners = true;
  }, [focusable]);
  const disabled = focusable && disabledFromProps(props);
  const trulyDisabled = disabled && !accessibleWhenDisabled;
  const [focusVisible, setFocusVisible] = useState(false);
  const focusVisibleRef = useRef(false);
  const nativeSubmitObserverCleanupRef = useRef(null);
  const cleanupFocusVisible = useEvent((element) => {
    nativeSubmitObserverCleanupRef.current?.();
    nativeSubmitObserverCleanupRef.current = null;
    focusVisibleRef.current = false;
    element?.removeAttribute("data-focus-visible");
  });
  useEffect(() => {
    if (!focusable) return;
    if (!trulyDisabled) return;
    cleanupFocusVisible(ref.current);
    if (focusVisible) setFocusVisible(false);
  }, [
    focusable,
    trulyDisabled,
    focusVisible,
    cleanupFocusVisible
  ]);
  useEffect(() => {
    if (!focusable) return;
    if (!focusVisible) return;
    const element = ref.current;
    if (!element) return;
    if (typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(() => {
      if (!isFocusable(element)) {
        focusVisibleRef.current = false;
        setFocusVisible(false);
      }
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [focusable, focusVisible]);
  useEffect(() => {
    return () => nativeSubmitObserverCleanupRef.current?.();
  }, []);
  const onKeyPressCapture = useDisableEvent(props.onKeyPressCapture, disabled);
  const onMouseDownCapture = useDisableEvent(props.onMouseDownCapture, disabled);
  const onClickCapture = useDisableEvent(props.onClickCapture, disabled);
  const handleFocusVisible = (event, currentTarget) => {
    if (currentTarget) event.currentTarget = currentTarget;
    if (!focusable) return;
    const element = event.currentTarget;
    if (!element) return;
    if (!hasFocus(element)) return;
    onFocusVisible?.(event);
    if (event.defaultPrevented) return;
    element.dataset.focusVisible = "true";
    focusVisibleRef.current = true;
    if (isNativeSubmitControl(element)) {
      nativeSubmitObserverCleanupRef.current?.();
      nativeSubmitObserverCleanupRef.current = null;
      if (typeof IntersectionObserver !== "undefined") {
        const observer = new IntersectionObserver(() => {
          if (isFocusable(element)) return;
          cleanupFocusVisible(element);
        });
        observer.observe(element);
        nativeSubmitObserverCleanupRef.current = () => observer.disconnect();
      }
      return;
    }
    setFocusVisible(true);
  };
  const onKeyDownCaptureProp = props.onKeyDownCapture;
  const onKeyDownCapture = useEvent((event) => {
    onKeyDownCaptureProp?.(event);
    if (event.defaultPrevented) return;
    if (!focusable) return;
    if (focusVisible) return;
    if (focusVisibleRef.current) return;
    if (event.metaKey) return;
    if (event.altKey) return;
    if (event.ctrlKey) return;
    if (!isSelfTarget(event)) return;
    const element = event.currentTarget;
    const applyFocusVisible = () => handleFocusVisible(event, element);
    queueBeforeEvent(element, "focusout", applyFocusVisible);
  });
  const onFocusCaptureProp = props.onFocusCapture;
  const onFocusCapture = useEvent((event) => {
    onFocusCaptureProp?.(event);
    if (event.defaultPrevented) return;
    if (!focusable) return;
    if (!isSelfTarget(event)) {
      setFocusVisible(false);
      return;
    }
    const element = event.currentTarget;
    const applyFocusVisible = () => handleFocusVisible(event, element);
    if (isKeyboardModality || isAlwaysFocusVisible(event.target)) queueBeforeEvent(event.target, "focusout", applyFocusVisible);
    else setFocusVisible(false);
  });
  const onBlurProp = props.onBlur;
  const onBlur = useEvent((event) => {
    onBlurProp?.(event);
    if (!focusable) return;
    if (!isFocusEventOutside(event)) return;
    cleanupFocusVisible(event.currentTarget);
    setFocusVisible(false);
  });
  const autoFocusOnShow = useContext(FocusableContext);
  const autoFocusRef = useEvent((element) => {
    if (!focusable) return;
    if (!autoFocus) return;
    if (!element) return;
    if (!autoFocusOnShow) return;
    queueMicrotask(() => {
      if (hasFocus(element)) return;
      if (!isFocusable(element)) return;
      element.focus();
    });
  });
  const tagName = useTagName(ref);
  const nativeTabbable = focusable && isNativeTabbable(tagName);
  const supportsDisabled = focusable && supportsDisabledAttribute(tagName);
  const [safariTabIndex, setSafariTabIndex] = useState(false);
  if (isSafariBrowser) useEffect(() => {
    if (!focusable) return;
    const element = ref.current;
    if (!element) return;
    const { type } = element;
    const isNativeCheckboxOrRadio = element.tagName === "INPUT" && (type === "checkbox" || type === "radio");
    setSafariTabIndex(isButton(element) || isNativeCheckboxOrRadio);
  }, [focusable]);
  const styleProp = props.style;
  const style = useMemo(() => {
    if (trulyDisabled) return {
      pointerEvents: "none",
      ...styleProp
    };
    return styleProp;
  }, [trulyDisabled, styleProp]);
  props = {
    "data-focus-visible": focusable && focusVisible || void 0,
    "data-autofocus": autoFocus || void 0,
    "aria-disabled": disabled || void 0,
    ...props,
    ...metadataProps,
    ref: useMergeRefs(ref, autoFocusRef, props.ref),
    style,
    tabIndex: getTabIndex({
      focusable,
      trulyDisabled,
      nativeTabbable,
      supportsDisabled,
      safariTabIndex,
      tabIndexProp: props.tabIndex
    }),
    disabled: supportsDisabled && trulyDisabled ? true : void 0,
    contentEditable: disabled ? void 0 : props.contentEditable,
    onKeyPressCapture,
    onClickCapture,
    onMouseDownCapture,
    onKeyDownCapture,
    onFocusCapture,
    onBlur
  };
  return removeUndefinedValues(props);
});
forwardRef(function Focusable(props) {
  return createElement(TagName$6, useFocusable(props));
});
const TagName$5 = "button";
function isNativeClick(event) {
  if (!event.isTrusted) return false;
  const element = event.currentTarget;
  if (event.key === "Enter") return isButton(element) || element.tagName === "SUMMARY" || element.tagName === "A";
  if (event.key === " ") return isButton(element) || element.tagName === "SUMMARY" || element.tagName === "INPUT" || element.tagName === "SELECT";
  return false;
}
const symbol = /* @__PURE__ */ Symbol("command");
const useCommand = createHook(function useCommand2({ clickOnEnter = true, clickOnSpace = true, ...props }) {
  const ref = useRef(null);
  const [isNativeButton, setIsNativeButton] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    setIsNativeButton(isButton(ref.current));
  }, []);
  const [active, setActive] = useState(false);
  const activeRef = useRef(false);
  const disabled = disabledFromProps(props);
  const [isDuplicate, metadataProps] = useMetadataProps(props, symbol, true);
  useSafeLayoutEffect(() => {
    if (!disabled) return;
    activeRef.current = false;
    setActive(false);
  }, [disabled]);
  const onKeyDownProp = props.onKeyDown;
  const onKeyDown = useEvent((event) => {
    onKeyDownProp?.(event);
    const element = event.currentTarget;
    if (event.defaultPrevented) return;
    if (isDuplicate) return;
    if (disabled) return;
    if (!isSelfTarget(event)) return;
    if (isTextField(element)) return;
    if (element.isContentEditable) return;
    const isEnter = clickOnEnter && event.key === "Enter";
    const isSpace = clickOnSpace && event.key === " ";
    const shouldPreventEnter = event.key === "Enter" && !clickOnEnter;
    const shouldPreventSpace = event.key === " " && !clickOnSpace;
    if (shouldPreventEnter || shouldPreventSpace) {
      event.preventDefault();
      return;
    }
    if (isEnter || isSpace) {
      const nativeClick = isNativeClick(event);
      if (isEnter) {
        if (!nativeClick) {
          event.preventDefault();
          const { view, ...eventInit } = event;
          const click = () => fireClickEvent(element, eventInit);
          if (isFirefox()) queueBeforeEvent(element, "keyup", click);
          else queueMicrotask(click);
        }
      } else if (isSpace) {
        activeRef.current = true;
        if (!nativeClick) {
          event.preventDefault();
          setActive(true);
        }
      }
    }
  });
  const onKeyUpProp = props.onKeyUp;
  const onKeyUp = useEvent((event) => {
    onKeyUpProp?.(event);
    if (isDuplicate) return;
    const isSpace = clickOnSpace && event.key === " ";
    if (!activeRef.current || !isSpace) return;
    const nativeClick = isNativeClick(event);
    activeRef.current = false;
    if (!nativeClick) setActive(false);
    if (event.defaultPrevented) return;
    if (!isSelfTarget(event)) return;
    if (disabled) return;
    if (event.metaKey) return;
    if (nativeClick) return;
    event.preventDefault();
    const element = event.currentTarget;
    const { view, ...eventInit } = event;
    queueMicrotask(() => fireClickEvent(element, eventInit));
  });
  const onBlurProp = props.onBlur;
  const onBlur = useEvent((event) => {
    onBlurProp?.(event);
    if (!activeRef.current) return;
    activeRef.current = false;
    setActive(false);
  });
  props = {
    "data-active": active || void 0,
    type: isNativeButton ? "button" : void 0,
    ...metadataProps,
    ...props,
    ref: useMergeRefs(ref, props.ref),
    onKeyDown,
    onKeyUp,
    onBlur
  };
  props = useFocusable(props);
  return props;
});
forwardRef(function Command(props) {
  return createElement(TagName$5, useCommand(props));
});
const ctx$7 = createStoreContext();
const useCollectionContext = ctx$7.useContext;
ctx$7.useScopedContext;
ctx$7.useProviderContext;
const CollectionContextProvider = ctx$7.ContextProvider;
const CollectionScopedContextProvider = ctx$7.ScopedContextProvider;
const TagName$4 = "div";
const useCollectionItem = createHook(function useCollectionItem2({ store, shouldRegisterItem = true, getItem = identity$1, element, ...props }) {
  const context = useCollectionContext();
  store = store || context;
  const id = useId(props.id);
  const ref = useRef(element);
  useEffect(() => {
    const element2 = ref.current;
    if (!id) return;
    if (!element2) return;
    if (!shouldRegisterItem) return;
    const item = getItem({
      id,
      element: element2
    });
    return store?.renderItem(item);
  }, [
    id,
    shouldRegisterItem,
    getItem,
    store
  ]);
  props = {
    ...props,
    ref: useMergeRefs(ref, props.ref)
  };
  return removeUndefinedValues(props);
});
forwardRef(function CollectionItem(props) {
  return createElement(TagName$4, useCollectionItem(props));
});
const ctx$6 = createStoreContext([CollectionContextProvider], [CollectionScopedContextProvider]);
const useCompositeScopedContext = ctx$6.useScopedContext;
const useCompositeProviderContext = ctx$6.useProviderContext;
const CompositeContextProvider = ctx$6.ContextProvider;
const CompositeScopedContextProvider = ctx$6.ScopedContextProvider;
const CompositeItemContext = createContext(void 0);
const CompositeRowContext = createContext(void 0);
function getInternal(store, key) {
  const internals = store.__unstableInternals;
  invariant(internals, "Invalid store");
  return internals[key];
}
function hasUpdatedKey(keys2, updatedKey) {
  if (!keys2) return true;
  for (const currentKey of keys2) if (updatedKey instanceof Set) {
    if (updatedKey.has(currentKey)) return true;
  } else if (currentKey === updatedKey) return true;
  return false;
}
function isSameValue(value, other) {
  return value === other || value !== value && other !== other;
}
function getCleanupPrevState(prevState, state, stateBeforeCleanup, updatedKey) {
  let cleanupPrevState;
  for (const key of getKeys(state)) {
    if (isSameValue(state[key], stateBeforeCleanup[key])) continue;
    if (updatedKey !== void 0 && hasUpdatedKey([key], updatedKey)) continue;
    cleanupPrevState ??= { ...prevState };
    cleanupPrevState[key] = state[key];
  }
  return cleanupPrevState;
}
const MAX_REPAIR_PASSES = 100;
function addKeyedListener(map2, keys2, listener) {
  if (!keys2) return;
  for (const key of keys2) {
    let listeners = map2.get(key);
    if (!listeners) {
      listeners = /* @__PURE__ */ new Set();
      map2.set(key, listeners);
    }
    listeners.add(listener);
  }
}
function deleteKeyedListener(map2, keys2, listener) {
  if (!map2) return;
  if (!keys2) return;
  for (const key of keys2) {
    const listeners = map2.get(key);
    if (!listeners) continue;
    listeners.delete(listener);
    if (!listeners.size) map2.delete(key);
  }
}
function getFastPathNotifiedListeners(frame) {
  const notifiedListeners = /* @__PURE__ */ new Set();
  const currentListener = frame.currentListener;
  if (!currentListener) return notifiedListeners;
  for (const listener of frame.keyedListeners) {
    notifiedListeners.add(listener);
    if (listener === currentListener) return notifiedListeners;
  }
  notifiedListeners.clear();
  notifiedListeners.add(currentListener);
  return notifiedListeners;
}
function preserveFastPathNotifiedListeners(frame) {
  frame.notifiedListeners ??= getFastPathNotifiedListeners(frame);
}
function hasFastPathPassedListener(frame, listener) {
  if (!frame.currentListener) return false;
  let foundCurrentKeyedListener = false;
  for (const currentListener of frame.keyedListeners) {
    if (currentListener === frame.currentListener) {
      foundCurrentKeyedListener = true;
      continue;
    }
    if (!foundCurrentKeyedListener) continue;
    if (currentListener === listener) return false;
  }
  let foundListener = false;
  for (const currentListener of frame.group.listeners) {
    if (currentListener === frame.currentListener) return foundListener;
    if (currentListener === listener) foundListener = true;
  }
  return false;
}
function preserveFastPathFrames(fastPathFrames, group2, listener) {
  for (const frame of fastPathFrames) {
    if (frame.group !== group2) continue;
    if (frame.recovering) continue;
    if (listener && !frame.keyedListeners.has(listener)) continue;
    preserveFastPathNotifiedListeners(frame);
    for (const currentListener of frame.group.listeners) {
      if (currentListener === frame.currentListener) break;
      if (!hasFastPathPassedListener(frame, currentListener)) continue;
      frame.notifiedListeners?.add(currentListener);
    }
  }
}
function preserveFastPathPassedListeners(fastPathFrames, group2, listener) {
  for (const frame of fastPathFrames) {
    if (frame.group !== group2) continue;
    if (frame.recovering) continue;
    if (!hasFastPathPassedListener(frame, listener)) continue;
    preserveFastPathNotifiedListeners(frame);
    frame.notifiedListeners?.add(listener);
  }
}
function preserveFastPathPassedKeyedListeners({ fastPathFrames, group: group2, keys: keys2, listener }) {
  const wasRegistered = group2.listeners.has(listener);
  for (const frame of fastPathFrames) {
    if (frame.group !== group2) continue;
    if (frame.recovering) continue;
    if (!keys2.includes(frame.updatedKey)) continue;
    if (hasFastPathPassedListener(frame, listener)) {
      preserveFastPathNotifiedListeners(frame);
      frame.notifiedListeners?.add(listener);
    } else if (wasRegistered) {
      preserveFastPathNotifiedListeners(frame);
      frame.recoverToLive = true;
    }
  }
}
function clearFastPathNotifiedListener(fastPathFrames, group2, listener) {
  for (const frame of fastPathFrames) {
    if (frame.group !== group2) continue;
    frame.notifiedListeners?.delete(listener);
  }
}
function addFastPathKeyedListener({ fastPathFrames, group: group2, keys: keys2, listener }) {
  for (const frame of fastPathFrames) {
    if (frame.group !== group2) continue;
    if (frame.recovering) continue;
    if (!keys2.includes(frame.updatedKey)) continue;
    frame.keyedListeners.add(listener);
  }
}
function runPendingCleanup(group2, listener) {
  if (!group2.disposables.size) return;
  const cleanup = group2.disposables.get(listener);
  if (!cleanup) return;
  group2.disposables.delete(listener);
  cleanup();
}
function setListenerCleanup(group2, listener, cleanup) {
  const currentCleanup = group2.disposables.get(listener);
  if (!currentCleanup) {
    group2.disposables.set(listener, cleanup);
    return;
  }
  group2.disposables.set(listener, () => {
    currentCleanup();
    cleanup();
  });
}
function notifyStoreListener(group2, listener, state, prevState, getState, updatedKey) {
  if (group2.suspendCounts?.has(listener)) return;
  const { disposables } = group2;
  const cleanup = disposables.size ? disposables.get(listener) : void 0;
  if (cleanup) {
    disposables.delete(listener);
    const stateBeforeCleanup = state;
    cleanup();
    state = getState?.() ?? state;
    if (state !== stateBeforeCleanup) prevState = getCleanupPrevState(prevState, state, stateBeforeCleanup, updatedKey) ?? prevState;
  }
  const result2 = listener(state, prevState);
  if (result2) setListenerCleanup(group2, listener, result2);
}
function runLiveListeners({ group: group2, getState, prevState, updatedKey, notifiedListeners }) {
  const allKeysListeners = group2.allKeysListeners;
  for (const listener of group2.listeners) {
    if (notifiedListeners?.has(listener)) continue;
    if (!allKeysListeners?.has(listener)) {
      if (!hasUpdatedKey(group2.listenerKeys.get(listener), updatedKey)) continue;
    }
    notifiedListeners?.add(listener);
    notifyStoreListener(group2, listener, getState(), prevState, getState, updatedKey);
  }
}
function createStore(initialState, ...stores) {
  let state = initialState;
  let prevStateBatch = state;
  let destroy = noop$1;
  let batchPending = false;
  let inDispatch = false;
  let updatedKeys = /* @__PURE__ */ new Set();
  const instances = /* @__PURE__ */ new Set();
  const setups = /* @__PURE__ */ new Set();
  const syncListenerGroup = {
    listeners: /* @__PURE__ */ new Set(),
    disposables: /* @__PURE__ */ new Map(),
    listenerKeys: /* @__PURE__ */ new WeakMap()
  };
  const batchListenerGroup = {
    listeners: /* @__PURE__ */ new Set(),
    disposables: /* @__PURE__ */ new Map(),
    listenerKeys: /* @__PURE__ */ new WeakMap()
  };
  const storeSetup = (callback) => {
    setups.add(callback);
    return () => setups.delete(callback);
  };
  const storeInit = () => {
    const initializedInstances = instances.size;
    const instance = /* @__PURE__ */ Symbol();
    instances.add(instance);
    const maybeDestroy = () => {
      if (!instances.delete(instance)) return;
      if (instances.size) return;
      destroy();
    };
    if (initializedInstances) return maybeDestroy;
    const stateKeys = getKeys(state);
    const desyncs = [];
    for (const store of stores) {
      const storeState = store?.getState?.();
      if (!storeState) continue;
      const keys2 = stateKeys.filter((key) => hasOwnProperty$2(storeState, key));
      if (!keys2.length) continue;
      if (stores.length === 1 || keys2.length === stateKeys.length) {
        for (const key of keys2) desyncs.push(sync(store, [key], (state2) => {
          setState(key, state2[key], true);
        }));
        continue;
      }
      desyncs.push(subscribe(store, keys2, (state2, prevState) => {
        for (const key of keys2) {
          if (state2[key] === prevState[key]) continue;
          setState(key, state2[key], true);
        }
      }));
      for (const key of keys2) {
        const liveState = store?.getState?.();
        if (!liveState) continue;
        setState(key, liveState[key], true);
      }
    }
    const teardowns = [];
    for (const setup2 of setups) teardowns.push(setup2());
    const cleanups = stores.map(init);
    destroy = chain$1(...desyncs, ...teardowns, ...cleanups);
    return maybeDestroy;
  };
  const deleteListenerIndexes = (group2, listener, keys2) => {
    if (keys2 === void 0) return;
    if (keys2) deleteKeyedListener(group2.listenersByKey, keys2, listener);
    else group2.allKeysListeners?.delete(listener);
  };
  const fastPathFrames = [];
  const registerListener = (keys2, listener, group2 = syncListenerGroup) => {
    const listenerKeysValue = keys2 ? [...keys2] : null;
    const wasRegistered = group2.listeners.has(listener);
    if (!wasRegistered) clearFastPathNotifiedListener(fastPathFrames, group2, listener);
    if (!listenerKeysValue) {
      if (wasRegistered) preserveFastPathFrames(fastPathFrames, group2);
      preserveFastPathPassedListeners(fastPathFrames, group2, listener);
    } else preserveFastPathPassedKeyedListeners({
      fastPathFrames,
      group: group2,
      keys: listenerKeysValue,
      listener
    });
    if (wasRegistered) {
      preserveFastPathFrames(fastPathFrames, group2, listener);
      deleteListenerIndexes(group2, listener, group2.listenerKeys.get(listener));
    }
    group2.listeners.add(listener);
    if (listenerKeysValue) {
      group2.listenersByKey ??= /* @__PURE__ */ new Map();
      addKeyedListener(group2.listenersByKey, listenerKeysValue, listener);
      addFastPathKeyedListener({
        fastPathFrames,
        group: group2,
        keys: listenerKeysValue,
        listener
      });
    } else {
      group2.allKeysListeners ??= /* @__PURE__ */ new Set();
      group2.allKeysListeners.add(listener);
    }
    group2.listenerKeys.set(listener, listenerKeysValue);
    return () => {
      const cleanup = group2.disposables.get(listener);
      group2.disposables.delete(listener);
      preserveFastPathFrames(fastPathFrames, group2, listener);
      const currentKeys = group2.listenerKeys.get(listener);
      deleteListenerIndexes(group2, listener, listenerKeysValue);
      if (currentKeys !== listenerKeysValue) deleteListenerIndexes(group2, listener, currentKeys);
      group2.listenerKeys.delete(listener);
      group2.listeners.delete(listener);
      cleanup?.();
    };
  };
  const storeSubscribe = (keys2, listener) => registerListener(keys2, listener);
  const runInitialListener = (group2, listener, prevState) => {
    const shouldSuspend = group2.listeners.has(listener);
    if (shouldSuspend) {
      group2.suspendCounts ??= /* @__PURE__ */ new Map();
      const count = group2.suspendCounts.get(listener) ?? 0;
      group2.suspendCounts.set(listener, count + 1);
    }
    let cleanupPrevState;
    try {
      const stateBeforeCleanups = state;
      runPendingCleanup(group2, listener);
      if (state !== stateBeforeCleanups) cleanupPrevState = getCleanupPrevState(prevState, state, stateBeforeCleanups);
      const cleanup = listener(state, cleanupPrevState ?? prevState);
      if (cleanup) setListenerCleanup(group2, listener, cleanup);
    } finally {
      if (shouldSuspend) {
        const suspendCounts = group2.suspendCounts;
        const count = suspendCounts?.get(listener);
        if (count && count > 1) suspendCounts?.set(listener, count - 1);
        else suspendCounts?.delete(listener);
        if (!suspendCounts?.size) delete group2.suspendCounts;
      }
    }
  };
  const storeSync = (keys2, listener) => {
    runInitialListener(syncListenerGroup, listener, state);
    return registerListener(keys2, listener);
  };
  const storeBatch = (keys2, listener) => {
    if (!batchListenerGroup.listeners.size && !inDispatch) prevStateBatch = state;
    runInitialListener(batchListenerGroup, listener, prevStateBatch);
    return registerListener(keys2, listener, batchListenerGroup);
  };
  const storePick = (keys2) => createStore(pick(state, keys2), finalStore);
  const storeOmit = (keys2) => createStore(omit$1(state, keys2), finalStore);
  const getState = () => state;
  const runListeners = (group2, prevState, updatedKey) => {
    if (!(updatedKey instanceof Set) && !group2.allKeysListeners?.size) {
      const keyedListeners = group2.listenersByKey?.get(updatedKey);
      if (!keyedListeners) return;
      const frame = {
        group: group2,
        keyedListeners,
        updatedKey,
        currentListener: null
      };
      fastPathFrames.push(frame);
      try {
        for (const listener of keyedListeners) {
          if (frame.notifiedListeners?.has(listener)) continue;
          frame.currentListener = listener;
          frame.notifiedListeners?.add(listener);
          notifyStoreListener(group2, listener, state, prevState, getState, updatedKey);
          if (!group2.allKeysListeners?.size && !frame.recoverToLive) continue;
          const notifiedListeners = frame.notifiedListeners ?? getFastPathNotifiedListeners(frame);
          frame.notifiedListeners = notifiedListeners;
          frame.recovering = true;
          runLiveListeners({
            group: group2,
            getState,
            prevState,
            updatedKey,
            notifiedListeners
          });
          return;
        }
      } finally {
        fastPathFrames.pop();
      }
      return;
    }
    runLiveListeners({
      group: group2,
      getState,
      prevState,
      updatedKey
    });
  };
  const setState = (key, value, fromStores = false) => {
    if (!hasOwnProperty$2(state, key)) return;
    const currentValue = state[key];
    const nextValue = typeof value === "function" ? value(currentValue) : value;
    if (isSameValue(nextValue, currentValue)) return;
    const wasInDispatch = inDispatch;
    inDispatch = true;
    const prevState = state;
    const nextState = {
      ...state,
      [key]: nextValue
    };
    state = nextState;
    let superseded = false;
    try {
      if (!fromStores && stores.length) {
        for (const store of stores) {
          store?.setState?.(key, nextValue);
          if (isSameValue(state[key], nextValue)) continue;
          superseded = true;
          break;
        }
        if (superseded) {
          let pass = 0;
          for (; pass < MAX_REPAIR_PASSES; pass += 1) {
            let changed = false;
            for (const store of stores) {
              const previousValue = state[key];
              store?.setState?.(key, previousValue);
              if (!isSameValue(state[key], previousValue)) changed = true;
            }
            if (!changed) break;
          }
          if (process.env.NODE_ENV !== "production" && pass === MAX_REPAIR_PASSES) console.warn("Parent stores did not converge after a superseded fan-out; a parent listener may be rewriting this key in a cycle.");
        }
      }
      if (!superseded) runListeners(syncListenerGroup, state === nextState ? prevState : {
        ...state,
        [key]: prevState[key]
      }, key);
    } finally {
      inDispatch = wasInDispatch;
    }
    if (!batchListenerGroup.listeners.size) {
      if (!inDispatch) prevStateBatch = state;
      return;
    }
    updatedKeys.add(key);
    if (batchPending) return;
    batchPending = true;
    queueMicrotask(() => {
      batchPending = false;
      const snapshot = state;
      const updatedKeysSnapshot = updatedKeys;
      updatedKeys = /* @__PURE__ */ new Set();
      const prevStateBatchBefore = prevStateBatch;
      runListeners(batchListenerGroup, prevStateBatchBefore, updatedKeysSnapshot);
      if (prevStateBatch === prevStateBatchBefore) prevStateBatch = snapshot;
    });
  };
  const finalStore = {
    getState,
    setState,
    __unstableInternals: {
      setup: storeSetup,
      init: storeInit,
      subscribe: storeSubscribe,
      sync: storeSync,
      batch: storeBatch,
      pick: storePick,
      omit: storeOmit
    }
  };
  return finalStore;
}
function setup(store, ...args) {
  if (!store) return;
  return getInternal(store, "setup")(...args);
}
function init(store, ...args) {
  if (!store) return;
  return getInternal(store, "init")(...args);
}
function subscribe(store, ...args) {
  if (!store) return;
  return getInternal(store, "subscribe")(...args);
}
function sync(store, ...args) {
  if (!store) return;
  return getInternal(store, "sync")(...args);
}
function batch(store, ...args) {
  if (!store) return;
  return getInternal(store, "batch")(...args);
}
function omit(store, ...args) {
  if (!store) return;
  return getInternal(store, "omit")(...args);
}
function mergeStore(...stores) {
  const initialState = {};
  for (const store2 of stores) {
    const nextState = store2?.getState?.();
    if (nextState) Object.assign(initialState, nextState);
  }
  const store = createStore(initialState, ...stores);
  return Object.assign({}, ...stores, store);
}
function throwOnConflictingProps(props, store) {
  if (process.env.NODE_ENV === "production") return;
  if (!store) return;
  const defaultKeys = Object.entries(props).filter(([key, value]) => key.startsWith("default") && value !== void 0).map(([key]) => {
    const stateKey = key.replace("default", "");
    return `${stateKey[0]?.toLowerCase() || ""}${stateKey.slice(1)}`;
  });
  if (!defaultKeys.length) return;
  const storeState = store.getState();
  if (!defaultKeys.filter((key) => hasOwnProperty$2(storeState, key)).length) return;
  throw new Error(`Passing a store prop in conjunction with a default state is not supported.

const store = useSelectStore();
<SelectProvider store={store} defaultValue="Apple" />
                ^             ^

Instead, pass the default state to the topmost store:

const store = useSelectStore({ defaultValue: "Apple" });
<SelectProvider store={store} />

See https://github.com/ariakit/ariakit/pull/2745 for more details.

If there's a particular need for this, please submit a feature request at https://github.com/ariakit/ariakit
`);
}
function getCommonParent(items) {
  const firstItem = items.find((item) => !!item.element);
  const lastElement = [...items].reverse().find((item) => !!item.element)?.element;
  let parentElement = firstItem?.element?.parentElement;
  if (!lastElement) return getDocument(parentElement).body;
  while (parentElement) {
    if (parentElement.contains(lastElement)) return parentElement;
    parentElement = parentElement.parentElement;
  }
  return getDocument(parentElement).body;
}
function getPrivateStore(store) {
  return store?.__unstablePrivateStore;
}
function createCollectionStore(props = {}) {
  throwOnConflictingProps(props, props.store);
  const syncState = props.store?.getState();
  const items = defaultValue(props.items, syncState?.items, props.defaultItems, []);
  const itemsMap = new Map(items.map((item) => [item.id, item]));
  const initialState = {
    items,
    renderedItems: defaultValue(syncState?.renderedItems, [])
  };
  const syncPrivateStore = getPrivateStore(props.store);
  const privateStore = createStore({
    items,
    renderedItems: initialState.renderedItems
  }, syncPrivateStore);
  const collection = createStore(initialState, props.store);
  const sortItems = (renderedItems) => {
    const sortedItems = sortBasedOnDOMPosition(renderedItems, (i2) => i2.element);
    privateStore.setState("renderedItems", sortedItems);
    collection.setState("renderedItems", sortedItems);
  };
  setup(collection, () => init(privateStore));
  setup(privateStore, () => {
    return batch(privateStore, ["items"], (state) => {
      collection.setState("items", state.items);
    });
  });
  setup(privateStore, () => {
    return batch(privateStore, ["renderedItems"], (state) => {
      let firstRun = true;
      let raf = requestAnimationFrame(() => {
        const { renderedItems } = collection.getState();
        if (state.renderedItems === renderedItems) return;
        sortItems(state.renderedItems);
      });
      if (typeof IntersectionObserver !== "function") return () => cancelAnimationFrame(raf);
      const ioCallback = () => {
        if (firstRun) {
          firstRun = false;
          return;
        }
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => sortItems(state.renderedItems));
      };
      const root2 = getCommonParent(state.renderedItems);
      const observer = new IntersectionObserver(ioCallback, { root: root2 });
      for (const item of state.renderedItems) {
        if (!item.element) continue;
        observer.observe(item.element);
      }
      return () => {
        cancelAnimationFrame(raf);
        observer.disconnect();
      };
    });
  });
  const mergeItem = (item, setItems, canDeleteFromMap = false) => {
    let prevItem;
    setItems((items2) => {
      const index = items2.findIndex(({ id }) => id === item.id);
      const nextItems = items2.slice();
      if (index !== -1) {
        prevItem = items2[index];
        const nextItem = {
          ...prevItem,
          ...item
        };
        nextItems[index] = nextItem;
        itemsMap.set(item.id, nextItem);
      } else {
        nextItems.push(item);
        itemsMap.set(item.id, item);
      }
      return nextItems;
    });
    const unmergeItem = () => {
      setItems((items2) => {
        if (!prevItem) {
          if (canDeleteFromMap) itemsMap.delete(item.id);
          return items2.filter(({ id }) => id !== item.id);
        }
        const index = items2.findIndex(({ id }) => id === item.id);
        if (index === -1) return items2;
        const nextItems = items2.slice();
        nextItems[index] = prevItem;
        itemsMap.set(item.id, prevItem);
        return nextItems;
      });
    };
    return unmergeItem;
  };
  const registerItem = (item) => mergeItem(item, (getItems) => privateStore.setState("items", getItems), true);
  return {
    ...collection,
    registerItem,
    renderItem: (item) => chain$1(registerItem(item), mergeItem(item, (getItems) => privateStore.setState("renderedItems", getItems))),
    item: (id) => {
      if (!id) return null;
      let item = itemsMap.get(id);
      if (!item) {
        const { items: items2 } = privateStore.getState();
        item = items2.find((item2) => item2.id === id);
        if (item) itemsMap.set(id, item);
      }
      return item || null;
    },
    __unstablePrivateStore: privateStore
  };
}
const NULL_ITEM = { id: null };
function findFirstEnabledItem$1(items, excludeId) {
  return items.find((item) => {
    if (excludeId) return !item.disabled && item.id !== excludeId;
    return !item.disabled;
  });
}
function getEnabledItems(items, excludeId) {
  return items.filter((item) => {
    if (excludeId) return !item.disabled && item.id !== excludeId;
    return !item.disabled;
  });
}
function getItemsInRow(items, rowId) {
  return items.filter((item) => item.rowId === rowId);
}
function findEnabledItemId({ items, fromIndex, step, rowId, excludeId }) {
  for (let i2 = fromIndex; i2 >= 0 && i2 < items.length; i2 += step) {
    const item = items[i2];
    if (!item) continue;
    if (item.rowId !== rowId) continue;
    if (item.disabled) continue;
    if (excludeId != null && item.id === excludeId) continue;
    return item.id;
  }
}
function flipItems(items, activeId, shouldInsertNullItem = false) {
  const index = items.findIndex((item) => item.id === activeId);
  return [
    ...items.slice(index + 1),
    ...shouldInsertNullItem ? [NULL_ITEM] : [],
    ...items.slice(0, index)
  ];
}
function groupItemsByRows$1(items) {
  const rows = [];
  for (const item of items) {
    const row = rows.find((currentRow) => currentRow[0]?.rowId === item.rowId);
    if (row) row.push(item);
    else rows.push([item]);
  }
  return rows;
}
function getMaxRowLength(array) {
  let maxLength = 0;
  for (const { length } of array) if (length > maxLength) maxLength = length;
  return maxLength;
}
function createEmptyItem(rowId) {
  return {
    id: "__EMPTY_ITEM__",
    disabled: true,
    rowId
  };
}
function normalizeRows(rows, activeId, focusShift) {
  const maxLength = getMaxRowLength(rows);
  for (const row of rows) for (let i2 = 0; i2 < maxLength; i2 += 1) {
    const item = row[i2];
    if (!item || focusShift && item.disabled) {
      const previousItem = i2 === 0 && focusShift ? findFirstEnabledItem$1(row) : row[i2 - 1];
      row[i2] = previousItem && activeId !== previousItem.id && focusShift ? previousItem : createEmptyItem(previousItem?.rowId);
    }
  }
  return rows;
}
function verticalizeItems(items) {
  const rows = groupItemsByRows$1(items);
  const maxLength = getMaxRowLength(rows);
  const verticalized = [];
  for (let i2 = 0; i2 < maxLength; i2 += 1) for (const row of rows) {
    const item = row[i2];
    if (item) verticalized.push({
      ...item,
      rowId: item.rowId ? `${i2}` : void 0
    });
  }
  return verticalized;
}
function createCompositeStore(props = {}) {
  const syncState = props.store?.getState();
  const collection = createCollectionStore(props);
  const activeId = defaultValue(props.activeId, syncState?.activeId, props.defaultActiveId);
  const composite = createStore({
    ...collection.getState(),
    id: defaultValue(props.id, syncState?.id) ?? `id-${Math.random().toString(36).slice(2, 8)}`,
    activeId,
    baseElement: defaultValue(syncState?.baseElement, null),
    includesBaseElement: defaultValue(props.includesBaseElement, syncState?.includesBaseElement, activeId === null),
    moves: defaultValue(syncState?.moves, 0),
    orientation: defaultValue(props.orientation, syncState?.orientation, "both"),
    rtl: defaultValue(props.rtl, syncState?.rtl, false),
    virtualFocus: defaultValue(props.virtualFocus, syncState?.virtualFocus, false),
    focusLoop: defaultValue(props.focusLoop, syncState?.focusLoop, false),
    focusWrap: defaultValue(props.focusWrap, syncState?.focusWrap, false),
    focusShift: defaultValue(props.focusShift, syncState?.focusShift, false)
  }, collection, props.store);
  setup(composite, () => sync(composite, ["renderedItems", "activeId"], (state) => {
    composite.setState("activeId", (activeId2) => {
      if (activeId2 !== void 0) return activeId2;
      return findFirstEnabledItem$1(state.renderedItems)?.id;
    });
  }));
  const getNextId = (direction = "next", options = {}) => {
    const defaultState = composite.getState();
    const { skip = 0, activeId: activeId2 = defaultState.activeId, focusShift = defaultState.focusShift, focusLoop = defaultState.focusLoop, focusWrap = defaultState.focusWrap, includesBaseElement = defaultState.includesBaseElement, renderedItems = defaultState.renderedItems, rtl = defaultState.rtl } = options;
    const isVerticalDirection = direction === "up" || direction === "down";
    const isNextDirection = direction === "next" || direction === "down";
    const canReverse = isNextDirection ? rtl && !isVerticalDirection : !rtl || isVerticalDirection;
    const canShift = focusShift && !skip;
    if (!skip && !focusWrap && !includesBaseElement && activeId2 != null) {
      if (!isVerticalDirection ? true : !canShift && !renderedItems.some((item) => item.rowId != null)) {
        const activeIndex2 = renderedItems.findIndex((item) => item.id === activeId2);
        const activeItem2 = renderedItems[activeIndex2];
        if (activeItem2) {
          const step = canReverse ? -1 : 1;
          const nextId = findEnabledItemId({
            items: renderedItems,
            fromIndex: activeIndex2 + step,
            step,
            rowId: activeItem2.rowId,
            excludeId: activeId2
          });
          if (nextId !== void 0) return nextId;
          if (!(focusLoop && (isVerticalDirection ? focusLoop !== "horizontal" : focusLoop !== "vertical"))) return void 0;
          return findEnabledItemId({
            items: renderedItems,
            fromIndex: step === 1 ? 0 : renderedItems.length - 1,
            step,
            rowId: activeItem2.rowId,
            excludeId: activeId2
          });
        }
      }
    }
    let items = !isVerticalDirection ? renderedItems : flatten2DArray(normalizeRows(groupItemsByRows$1(renderedItems), activeId2, canShift));
    items = canReverse ? reverseArray(items) : items;
    items = isVerticalDirection ? verticalizeItems(items) : items;
    if (activeId2 == null) return findFirstEnabledItem$1(items)?.id;
    const activeItem = items.find((item) => item.id === activeId2);
    if (!activeItem) return findFirstEnabledItem$1(items)?.id;
    const isGrid2 = items.some((item) => item.rowId);
    const activeIndex = items.indexOf(activeItem);
    const nextItems = items.slice(activeIndex + 1);
    const nextItemsInRow = getItemsInRow(nextItems, activeItem.rowId);
    if (skip) {
      const nextEnabledItemsInRow = getEnabledItems(nextItemsInRow, activeId2);
      return (nextEnabledItemsInRow.slice(skip)[0] || nextEnabledItemsInRow[nextEnabledItemsInRow.length - 1])?.id;
    }
    const canLoop = focusLoop && (isVerticalDirection ? focusLoop !== "horizontal" : focusLoop !== "vertical");
    const canWrap = isGrid2 && focusWrap && (isVerticalDirection ? focusWrap !== "horizontal" : focusWrap !== "vertical");
    const hasNullItem = isNextDirection ? (!isGrid2 || isVerticalDirection) && canLoop && includesBaseElement : isVerticalDirection ? includesBaseElement : false;
    if (canLoop) return findFirstEnabledItem$1(flipItems(canWrap && !hasNullItem ? items : getItemsInRow(items, activeItem.rowId), activeId2, hasNullItem), activeId2)?.id;
    if (canWrap) {
      const nextItem2 = findFirstEnabledItem$1(hasNullItem ? nextItemsInRow : nextItems, activeId2);
      return hasNullItem ? nextItem2?.id || null : nextItem2?.id;
    }
    const nextItem = findFirstEnabledItem$1(nextItemsInRow, activeId2);
    if (!nextItem && hasNullItem) return null;
    return nextItem?.id;
  };
  const getNextIdFromOptions = (direction, options) => {
    if (typeof options === "number") return getNextId(direction, { skip: options });
    return getNextId(direction, options);
  };
  return {
    ...collection,
    ...composite,
    setBaseElement: (element) => composite.setState("baseElement", element),
    setActiveId: (id) => composite.setState("activeId", id),
    move: (id) => {
      if (id === void 0) return;
      composite.setState("activeId", id);
      composite.setState("moves", (moves) => moves + 1);
    },
    first: () => findFirstEnabledItem$1(composite.getState().renderedItems)?.id,
    last: () => findFirstEnabledItem$1(reverseArray(composite.getState().renderedItems))?.id,
    next: (options) => getNextIdFromOptions("next", options),
    previous: (options) => getNextIdFromOptions("previous", options),
    down: (options) => getNextIdFromOptions("down", options),
    up: (options) => getNextIdFromOptions("up", options)
  };
}
const findFirstEnabledItem = findFirstEnabledItem$1;
const groupItemsByRows = groupItemsByRows$1;
function getEnabledItem(store, id) {
  if (!id) return null;
  return store.item(id) || null;
}
function selectTextField(element, collapseToEnd = false) {
  if (isTextField(element)) element.setSelectionRange(collapseToEnd ? element.value.length : 0, element.value.length);
  else if (element.isContentEditable) {
    const selection = getDocument(element).getSelection();
    selection?.selectAllChildren(element);
    if (collapseToEnd) selection?.collapseToEnd();
  }
}
const FOCUS_SILENTLY = /* @__PURE__ */ Symbol("FOCUS_SILENTLY");
function focusSilently(element) {
  element[FOCUS_SILENTLY] = true;
  element.focus({ preventScroll: true });
}
function silentlyFocused(element) {
  const isSilentlyFocused = element[FOCUS_SILENTLY];
  delete element[FOCUS_SILENTLY];
  return isSilentlyFocused;
}
function isItem(store, element, exclude) {
  if (!element) return false;
  if (element === exclude) return false;
  const item = store.item(element.id);
  if (!item) return false;
  return true;
}
var shim = { exports: {} };
var useSyncExternalStoreShim_production = {};
var hasRequiredUseSyncExternalStoreShim_production;
function requireUseSyncExternalStoreShim_production() {
  if (hasRequiredUseSyncExternalStoreShim_production) return useSyncExternalStoreShim_production;
  hasRequiredUseSyncExternalStoreShim_production = 1;
  var React2 = React__default;
  function is(x2, y2) {
    return x2 === y2 && (0 !== x2 || 1 / x2 === 1 / y2) || x2 !== x2 && y2 !== y2;
  }
  var objectIs = "function" === typeof Object.is ? Object.is : is, useState2 = React2.useState, useEffect2 = React2.useEffect, useLayoutEffect2 = React2.useLayoutEffect, useDebugValue = React2.useDebugValue;
  function useSyncExternalStore$2(subscribe2, getSnapshot) {
    var value = getSnapshot(), _useState = useState2({ inst: { value, getSnapshot } }), inst = _useState[0].inst, forceUpdate = _useState[1];
    useLayoutEffect2(
      function() {
        inst.value = value;
        inst.getSnapshot = getSnapshot;
        checkIfSnapshotChanged(inst) && forceUpdate({ inst });
      },
      [subscribe2, value, getSnapshot]
    );
    useEffect2(
      function() {
        checkIfSnapshotChanged(inst) && forceUpdate({ inst });
        return subscribe2(function() {
          checkIfSnapshotChanged(inst) && forceUpdate({ inst });
        });
      },
      [subscribe2]
    );
    useDebugValue(value);
    return value;
  }
  function checkIfSnapshotChanged(inst) {
    var latestGetSnapshot = inst.getSnapshot;
    inst = inst.value;
    try {
      var nextValue = latestGetSnapshot();
      return !objectIs(inst, nextValue);
    } catch (error) {
      return true;
    }
  }
  function useSyncExternalStore$1(subscribe2, getSnapshot) {
    return getSnapshot();
  }
  var shim2 = "undefined" === typeof window || "undefined" === typeof window.document || "undefined" === typeof window.document.createElement ? useSyncExternalStore$1 : useSyncExternalStore$2;
  useSyncExternalStoreShim_production.useSyncExternalStore = void 0 !== React2.useSyncExternalStore ? React2.useSyncExternalStore : shim2;
  return useSyncExternalStoreShim_production;
}
var useSyncExternalStoreShim_development = {};
var hasRequiredUseSyncExternalStoreShim_development;
function requireUseSyncExternalStoreShim_development() {
  if (hasRequiredUseSyncExternalStoreShim_development) return useSyncExternalStoreShim_development;
  hasRequiredUseSyncExternalStoreShim_development = 1;
  "production" !== process.env.NODE_ENV && (function() {
    function is(x2, y2) {
      return x2 === y2 && (0 !== x2 || 1 / x2 === 1 / y2) || x2 !== x2 && y2 !== y2;
    }
    function useSyncExternalStore$2(subscribe2, getSnapshot) {
      didWarnOld18Alpha || void 0 === React2.startTransition || (didWarnOld18Alpha = true, console.error(
        "You are using an outdated, pre-release alpha of React 18 that does not support useSyncExternalStore. The use-sync-external-store shim will not work correctly. Upgrade to a newer pre-release."
      ));
      var value = getSnapshot();
      if (!didWarnUncachedGetSnapshot) {
        var cachedValue = getSnapshot();
        objectIs(value, cachedValue) || (console.error(
          "The result of getSnapshot should be cached to avoid an infinite loop"
        ), didWarnUncachedGetSnapshot = true);
      }
      cachedValue = useState2({
        inst: { value, getSnapshot }
      });
      var inst = cachedValue[0].inst, forceUpdate = cachedValue[1];
      useLayoutEffect2(
        function() {
          inst.value = value;
          inst.getSnapshot = getSnapshot;
          checkIfSnapshotChanged(inst) && forceUpdate({ inst });
        },
        [subscribe2, value, getSnapshot]
      );
      useEffect2(
        function() {
          checkIfSnapshotChanged(inst) && forceUpdate({ inst });
          return subscribe2(function() {
            checkIfSnapshotChanged(inst) && forceUpdate({ inst });
          });
        },
        [subscribe2]
      );
      useDebugValue(value);
      return value;
    }
    function checkIfSnapshotChanged(inst) {
      var latestGetSnapshot = inst.getSnapshot;
      inst = inst.value;
      try {
        var nextValue = latestGetSnapshot();
        return !objectIs(inst, nextValue);
      } catch (error) {
        return true;
      }
    }
    function useSyncExternalStore$1(subscribe2, getSnapshot) {
      return getSnapshot();
    }
    "undefined" !== typeof __REACT_DEVTOOLS_GLOBAL_HOOK__ && "function" === typeof __REACT_DEVTOOLS_GLOBAL_HOOK__.registerInternalModuleStart && __REACT_DEVTOOLS_GLOBAL_HOOK__.registerInternalModuleStart(Error());
    var React2 = React__default, objectIs = "function" === typeof Object.is ? Object.is : is, useState2 = React2.useState, useEffect2 = React2.useEffect, useLayoutEffect2 = React2.useLayoutEffect, useDebugValue = React2.useDebugValue, didWarnOld18Alpha = false, didWarnUncachedGetSnapshot = false, shim2 = "undefined" === typeof window || "undefined" === typeof window.document || "undefined" === typeof window.document.createElement ? useSyncExternalStore$1 : useSyncExternalStore$2;
    useSyncExternalStoreShim_development.useSyncExternalStore = void 0 !== React2.useSyncExternalStore ? React2.useSyncExternalStore : shim2;
    "undefined" !== typeof __REACT_DEVTOOLS_GLOBAL_HOOK__ && "function" === typeof __REACT_DEVTOOLS_GLOBAL_HOOK__.registerInternalModuleStop && __REACT_DEVTOOLS_GLOBAL_HOOK__.registerInternalModuleStop(Error());
  })();
  return useSyncExternalStoreShim_development;
}
var hasRequiredShim;
function requireShim() {
  if (hasRequiredShim) return shim.exports;
  hasRequiredShim = 1;
  if (process.env.NODE_ENV === "production") {
    shim.exports = requireUseSyncExternalStoreShim_production();
  } else {
    shim.exports = requireUseSyncExternalStoreShim_development();
  }
  return shim.exports;
}
var shimExports = requireShim();
const noopSubscribe = () => () => {
};
function useStoreState(store, keyOrSelector = identity$1) {
  const storeSubscribe = React.useCallback((callback) => {
    if (!store) return noopSubscribe();
    return subscribe(store, null, callback);
  }, [store]);
  const getSnapshot = () => {
    const key = typeof keyOrSelector === "string" ? keyOrSelector : null;
    const selector2 = typeof keyOrSelector === "function" ? keyOrSelector : null;
    const state = store?.getState();
    if (selector2) return selector2(state);
    if (!state) return;
    if (!key) return;
    if (!hasOwnProperty$2(state, key)) return;
    return state[key];
  };
  return shimExports.useSyncExternalStore(storeSubscribe, getSnapshot, getSnapshot);
}
function useStoreStateObject(store, object2) {
  const objRef = React.useRef({});
  const storeSubscribe = React.useCallback((callback) => {
    if (!store) return noopSubscribe();
    return subscribe(store, null, callback);
  }, [store]);
  const getSnapshot = () => {
    const state = store?.getState();
    let updated = false;
    const obj = objRef.current;
    for (const prop in object2) {
      const keyOrSelector = object2[prop];
      if (typeof keyOrSelector === "function") {
        const value = keyOrSelector(state);
        if (!Object.is(value, obj[prop])) {
          obj[prop] = value;
          updated = true;
        }
      }
      if (typeof keyOrSelector === "string") {
        if (!state) continue;
        if (!hasOwnProperty$2(state, keyOrSelector)) continue;
        const value = state[keyOrSelector];
        if (!Object.is(value, obj[prop])) {
          obj[prop] = value;
          updated = true;
        }
      }
    }
    if (updated) objRef.current = { ...obj };
    return objRef.current;
  };
  return shimExports.useSyncExternalStore(storeSubscribe, getSnapshot, getSnapshot);
}
function useStoreProps(store, props, key, setKey) {
  const value = hasOwnProperty$2(props, key) ? props[key] : void 0;
  const propsRef = useLiveRef({
    value,
    setValue: setKey ? props[setKey] : void 0
  });
  useSafeLayoutEffect(() => {
    return sync(store, [key], (state, prev) => {
      const { value: value2, setValue } = propsRef.current;
      if (!setValue) return;
      if (state[key] === prev[key]) return;
      if (state[key] === value2) return;
      setValue(state[key]);
    });
  }, [store, key]);
  useSafeLayoutEffect(() => {
    if (value === void 0) return;
    store.setState(key, value);
    return batch(store, [key], () => {
      if (value === void 0) return;
      store.setState(key, value);
    });
  });
}
function useStore(createStore2, props) {
  const [store, setStore] = React.useState(() => createStore2(props));
  useSafeLayoutEffect(() => init(store), [store]);
  const useState2 = React.useCallback((keyOrSelector) => useStoreState(store, keyOrSelector), [store]);
  return [React.useMemo(() => ({
    ...store,
    useState: useState2
  }), [store, useState2]), useEvent(() => {
    setStore((store2) => createStore2({
      ...props,
      ...store2.getState()
    }));
  })];
}
const TagName$3 = "button";
function isEditableElement(element) {
  if (isTextbox(element)) return true;
  return element.tagName === "INPUT" && !isButton(element);
}
function getNextPageOffset(scrollingElement, pageUp = false) {
  const height = scrollingElement.clientHeight;
  const { top } = scrollingElement.getBoundingClientRect();
  const pageSize = Math.max(height * 0.875, height - 40) * 1.5;
  const pageOffset = pageUp ? height - pageSize + top : pageSize + top;
  if (scrollingElement.tagName === "HTML") return pageOffset + scrollingElement.scrollTop;
  return pageOffset;
}
function getItemOffset(itemElement, pageUp = false) {
  const { top } = itemElement.getBoundingClientRect();
  if (pageUp) return top + itemElement.clientHeight;
  return top;
}
function findNextPageItemId(element, store, next, pageUp = false) {
  if (!store) return;
  if (!next) return;
  const { renderedItems } = store.getState();
  const scrollingElement = getScrollingElement(element);
  if (!scrollingElement) return;
  const nextPageOffset = getNextPageOffset(scrollingElement, pageUp);
  let id;
  let prevDifference;
  for (let i2 = 0; i2 < renderedItems.length; i2 += 1) {
    const previousId = id;
    id = next(i2);
    if (!id) break;
    if (id === previousId) continue;
    const itemElement = getEnabledItem(store, id)?.element;
    if (!itemElement) continue;
    const difference = getItemOffset(itemElement, pageUp) - nextPageOffset;
    const absDifference = Math.abs(difference);
    if (pageUp && difference <= 0 || !pageUp && difference >= 0) {
      if (prevDifference !== void 0 && prevDifference < absDifference) id = previousId;
      break;
    }
    prevDifference = absDifference;
  }
  return id;
}
function targetIsAnotherItem(event, store) {
  if (isSelfTarget(event)) return false;
  return isItem(store, event.target);
}
const useCompositeItem = createHook(function useCompositeItem2({ store, rowId: rowIdProp, preventScrollOnKeyDown = false, moveOnKeyPress = true, tabbable = false, getItem: getItemProp, "aria-setsize": ariaSetSizeProp, "aria-posinset": ariaPosInSetProp, ...props }) {
  const context = useCompositeScopedContext();
  store = store || context;
  const id = useId(props.id);
  const ref = useRef(null);
  const row = useContext(CompositeRowContext);
  const trulyDisabled = disabledFromProps(props) && !props.accessibleWhenDisabled;
  const shouldRegisterItem = props.shouldRegisterItem;
  const getRowId = (state) => {
    if (rowIdProp) return rowIdProp;
    if (!state) return;
    if (!row?.baseElement) return;
    if (row.baseElement !== state.baseElement) return;
    return row.id;
  };
  const { rowId, baseElement, isActiveItem, ariaSetSize, ariaPosInSet, isTabbable } = useStoreStateObject(store, {
    rowId: getRowId,
    baseElement(state) {
      return state?.baseElement || void 0;
    },
    isActiveItem(state) {
      return !!state && state.activeId === id;
    },
    ariaSetSize(state) {
      if (ariaSetSizeProp != null) return ariaSetSizeProp;
      if (!state) return;
      if (!row?.ariaSetSize) return;
      if (row.baseElement !== state.baseElement) return;
      return row.ariaSetSize;
    },
    ariaPosInSet(state) {
      if (ariaPosInSetProp != null) return ariaPosInSetProp;
      if (!state) return;
      if (!row?.ariaPosInSet) return;
      if (row.baseElement !== state.baseElement) return;
      const rowId2 = getRowId(state);
      const itemsInRow = state.renderedItems.filter((item) => item.rowId === rowId2);
      return row.ariaPosInSet + itemsInRow.findIndex((item) => item.id === id);
    },
    isTabbable(state) {
      if (!state?.renderedItems.length) return true;
      if (state.virtualFocus) return false;
      if (tabbable) return true;
      if (state.activeId === null) return false;
      const item = store?.item(state.activeId);
      if (item?.disabled) return true;
      if (!item?.element) return true;
      return state.activeId === id;
    }
  });
  const getItem = useCallback((item) => {
    const nextItem = {
      ...item,
      id: id || item.id,
      rowId,
      disabled: trulyDisabled,
      children: item.element?.textContent
    };
    if (getItemProp) return getItemProp(nextItem);
    return nextItem;
  }, [
    id,
    rowId,
    trulyDisabled,
    getItemProp
  ]);
  const onFocusProp = props.onFocus;
  const hasFocusedComposite = useRef(false);
  const cancelScheduledFocusRedirectRef = useRef(null);
  const onFocus = useEvent((event) => {
    onFocusProp?.(event);
    if (event.defaultPrevented) return;
    if (isPortalEvent(event)) return;
    if (!id) return;
    if (!store) return;
    if (targetIsAnotherItem(event, store)) return;
    const { virtualFocus, baseElement: baseElement2 } = store.getState();
    store.setActiveId(id);
    if (isTextbox(event.currentTarget)) selectTextField(event.currentTarget);
    if (!virtualFocus) return;
    if (!isSelfTarget(event)) return;
    if (isEditableElement(event.currentTarget)) return;
    const redirectFocusToBaseElement = (currentTarget2, relatedTarget2, baseElement3) => {
      if (isSafari() && currentTarget2.hasAttribute("data-autofocus")) currentTarget2.scrollIntoView({
        block: "nearest",
        inline: "nearest"
      });
      hasFocusedComposite.current = true;
      if (relatedTarget2 === baseElement3 || isItem(store, relatedTarget2)) focusSilently(baseElement3);
      else baseElement3.focus();
    };
    if (baseElement2?.isConnected) {
      redirectFocusToBaseElement(event.currentTarget, event.relatedTarget, baseElement2);
      return;
    }
    if (shouldRegisterItem === false) return;
    const { currentTarget, relatedTarget } = event;
    const cancelScheduledFocusRedirect = () => {
      cancelScheduledFocusRedirectRef.current?.();
      cancelScheduledFocusRedirectRef.current = null;
    };
    cancelScheduledFocusRedirect();
    cancelScheduledFocusRedirectRef.current = subscribe(store, null, () => {
      if (getActiveElement(currentTarget) !== currentTarget) {
        cancelScheduledFocusRedirect();
        return;
      }
      const state = store.getState();
      const nextBaseElement = state.baseElement;
      if (!nextBaseElement?.isConnected) return;
      cancelScheduledFocusRedirect();
      if (!state.virtualFocus) return;
      redirectFocusToBaseElement(currentTarget, relatedTarget, nextBaseElement);
    });
  });
  const onBlurCaptureProp = props.onBlurCapture;
  const onBlurCapture = useEvent((event) => {
    onBlurCaptureProp?.(event);
    if (event.defaultPrevented) return;
    if (store?.getState()?.virtualFocus && hasFocusedComposite.current) {
      hasFocusedComposite.current = false;
      event.preventDefault();
      event.stopPropagation();
    }
  });
  const onKeyDownProp = props.onKeyDown;
  const preventScrollOnKeyDownProp = useBooleanEvent(preventScrollOnKeyDown);
  const moveOnKeyPressProp = useBooleanEvent(moveOnKeyPress);
  const onKeyDown = useEvent((event) => {
    onKeyDownProp?.(event);
    if (event.defaultPrevented) return;
    if (!isSelfTarget(event)) return;
    if (!store) return;
    const { currentTarget } = event;
    const state = store.getState();
    const isGrid2 = !!store.item(id)?.rowId;
    const isVertical = state.orientation !== "horizontal";
    const isHorizontal = state.orientation !== "vertical";
    const canHomeEnd = () => {
      if (isGrid2) return true;
      if (isHorizontal) return true;
      if (!state.baseElement) return true;
      if (!isTextField(state.baseElement)) return true;
      return false;
    };
    const action = {
      ArrowUp: (isGrid2 || isVertical) && store.up,
      ArrowRight: (isGrid2 || isHorizontal) && store.next,
      ArrowDown: (isGrid2 || isVertical) && store.down,
      ArrowLeft: (isGrid2 || isHorizontal) && store.previous,
      Home: () => {
        if (!canHomeEnd()) return;
        if (!isGrid2 || event.ctrlKey) return store?.first();
        return store?.previous(-1);
      },
      End: () => {
        if (!canHomeEnd()) return;
        if (!isGrid2 || event.ctrlKey) return store?.last();
        return store?.next(-1);
      },
      PageUp: () => {
        return findNextPageItemId(currentTarget, store, store?.up, true);
      },
      PageDown: () => {
        return findNextPageItemId(currentTarget, store, store?.down);
      }
    }[event.key];
    if (action) {
      if (isTextbox(currentTarget)) {
        const selection = getTextboxSelection(currentTarget);
        const isLeft = isHorizontal && event.key === "ArrowLeft";
        const isRight = isHorizontal && event.key === "ArrowRight";
        const isUp = isVertical && event.key === "ArrowUp";
        const isDown = isVertical && event.key === "ArrowDown";
        if (isRight || isDown) {
          const { length: valueLength } = getTextboxValue(currentTarget);
          if (selection.end !== valueLength) return;
        } else if ((isLeft || isUp) && selection.start !== 0) return;
      }
      const nextId = action();
      if (preventScrollOnKeyDownProp(event) || nextId !== void 0) {
        if (!moveOnKeyPressProp(event)) return;
        event.preventDefault();
        store.move(nextId);
      }
    }
  });
  const providerValue = useMemo(() => ({
    id,
    baseElement
  }), [id, baseElement]);
  props = useWrapElement(props, (element) => /* @__PURE__ */ jsxRuntimeExports.jsx(CompositeItemContext.Provider, {
    value: providerValue,
    children: element
  }), [providerValue]);
  props = {
    "data-active-item": isActiveItem || void 0,
    ...props,
    id,
    ref: useMergeRefs(ref, props.ref),
    tabIndex: isTabbable ? props.tabIndex : -1,
    onFocus,
    onBlurCapture,
    onKeyDown
  };
  props = useCommand(props);
  props = useCollectionItem({
    store,
    ...props,
    getItem,
    shouldRegisterItem: id ? shouldRegisterItem : false
  });
  return removeUndefinedValues({
    ...props,
    "aria-setsize": ariaSetSize,
    "aria-posinset": ariaPosInSet
  });
});
const CompositeItem = memo(forwardRef(function CompositeItem2(props) {
  return createElement(TagName$3, useCompositeItem(props));
}));
const ctx$5 = createStoreContext([CompositeContextProvider], [CompositeScopedContextProvider]);
const useTabScopedContext = ctx$5.useScopedContext;
const useTabProviderContext = ctx$5.useProviderContext;
const TabContextProvider = ctx$5.ContextProvider;
const TabScopedContextProvider = ctx$5.ScopedContextProvider;
const TagName$2 = "button";
const useTab = createHook(function useTab2({ store, getItem: getItemProp, ...props }) {
  const context = useTabScopedContext();
  store = store || context;
  invariant(store, process.env.NODE_ENV !== "production" && "Tab must be wrapped in a TabList component.");
  const defaultId = useId();
  const id = props.id || defaultId;
  const dimmed = disabledFromProps(props);
  const getItem = useCallback((item) => {
    const nextItem = {
      ...item,
      dimmed
    };
    if (getItemProp) return getItemProp(nextItem);
    return nextItem;
  }, [dimmed, getItemProp]);
  const onClickProp = props.onClick;
  const onClick = useEvent((event) => {
    onClickProp?.(event);
    if (event.defaultPrevented) return;
    store?.setSelectedId(id);
  });
  const panelId = useStoreState(store.panels, () => store.panel(id)?.id);
  const shouldRegisterItem = defaultId ? props.shouldRegisterItem : false;
  const isActive = useStoreState(store, (state) => !!id && state.activeId === id);
  const selected = useStoreState(store, (state) => !!id && state.selectedId === id);
  const hasActiveItem = useStoreState(store, (state) => !!store.item(state.activeId));
  const canRegisterComposedItem = isActive || selected && !hasActiveItem;
  const accessibleWhenDisabled = selected || (props.accessibleWhenDisabled ?? true);
  if (useStoreState(store.combobox || store.composite, "virtualFocus")) props = {
    ...props,
    tabIndex: -1
  };
  props = {
    role: "tab",
    "aria-selected": selected,
    "aria-controls": panelId || void 0,
    ...props,
    id,
    onClick
  };
  if (store.composite) {
    const defaultProps = {
      id,
      accessibleWhenDisabled,
      store: store.composite,
      shouldRegisterItem: canRegisterComposedItem && shouldRegisterItem,
      rowId: props.rowId,
      render: props.render
    };
    props = {
      ...props,
      render: /* @__PURE__ */ jsxRuntimeExports.jsx(CompositeItem, {
        ...defaultProps,
        render: store.combobox && store.composite !== store.combobox ? /* @__PURE__ */ jsxRuntimeExports.jsx(CompositeItem, {
          ...defaultProps,
          store: store.combobox
        }) : defaultProps.render
      })
    };
  }
  props = useCompositeItem({
    store,
    ...props,
    accessibleWhenDisabled,
    getItem,
    shouldRegisterItem
  });
  return props;
});
const Tab = memo(forwardRef(function Tab2(props) {
  return createElement(TagName$2, useTab(props));
}));
const TagName$1 = "div";
function isGrid(items) {
  return items.some((item) => !!item.rowId);
}
function isPrintableKey(event) {
  const target = event.target;
  if (target && !isTextField(target)) return false;
  return event.key.length === 1 && !event.ctrlKey && !event.metaKey;
}
function isModifierKey(event) {
  return event.key === "Shift" || event.key === "Control" || event.key === "Alt" || event.key === "Meta";
}
function useKeyboardEventProxy(store, onKeyboardEvent, previousElementRef) {
  return useEvent((event) => {
    onKeyboardEvent?.(event);
    if (event.defaultPrevented) return;
    if (event.isPropagationStopped()) return;
    if (!isSelfTarget(event)) return;
    if (isModifierKey(event)) return;
    if (isPrintableKey(event)) return;
    const activeElement = getEnabledItem(store, store.getState().activeId)?.element;
    if (!activeElement) return;
    const { view, ...eventInit } = event;
    if (activeElement !== previousElementRef?.current) activeElement.focus();
    if (!fireKeyboardEvent(activeElement, event.type, eventInit)) event.preventDefault();
    if (event.currentTarget.contains(activeElement)) event.stopPropagation();
  });
}
function findFirstEnabledItemInTheLastRow(items) {
  return findFirstEnabledItem(flatten2DArray(reverseArray(groupItemsByRows(items))));
}
function withBaseScrollPreserved(store, callback) {
  const { virtualFocus, baseElement } = store.getState();
  if (!virtualFocus || !baseElement || !isTextField(baseElement)) {
    callback();
    return;
  }
  const savedScrollLeft = baseElement.scrollLeft;
  const savedScrollTop = baseElement.scrollTop;
  callback();
  baseElement.scrollLeft = savedScrollLeft;
  baseElement.scrollTop = savedScrollTop;
}
function useScheduleFocus(store) {
  const [scheduled, setScheduled] = useState(false);
  const schedule = useCallback(() => setScheduled(true), []);
  const activeItem = useStoreState(store, (state) => scheduled ? getEnabledItem(store, state.activeId) : null);
  useEffect(() => {
    const activeElement = activeItem?.element;
    if (!scheduled) return;
    if (!activeElement) return;
    setScheduled(false);
    withBaseScrollPreserved(store, () => {
      activeElement.focus({ preventScroll: true });
    });
  }, [
    store,
    activeItem,
    scheduled
  ]);
  return schedule;
}
const CompositeFocusOnMove = memo(function CompositeFocusOnMove2({ store, focusOnMove, previousElementRef }) {
  const moves = useStoreState(store, "moves");
  const baseElement = useStoreState(store, "baseElement");
  useEffect(() => {
    if (!moves) return;
    if (!focusOnMove) return;
    const { activeId } = store.getState();
    const itemElement = getEnabledItem(store, activeId)?.element;
    if (!itemElement) return;
    withBaseScrollPreserved(store, () => focusIntoView(itemElement));
  }, [
    store,
    moves,
    focusOnMove
  ]);
  useSafeLayoutEffect(() => {
    if (!moves) return;
    if (!baseElement) return;
    const { activeId } = store.getState();
    if (!(activeId === null)) return;
    const previousElement = previousElementRef.current;
    previousElementRef.current = null;
    if (previousElement) fireBlurEvent(previousElement, { relatedTarget: baseElement });
    if (!hasFocus(baseElement)) baseElement.focus();
  }, [
    store,
    moves,
    baseElement
  ]);
  return null;
});
const useComposite = createHook(function useComposite2({ store, composite = true, focusOnMove = composite, moveOnKeyPress = true, ...props }) {
  const context = useCompositeProviderContext();
  store = store || context;
  invariant(store, process.env.NODE_ENV !== "production" && "Composite must receive a `store` prop or be wrapped in a CompositeProvider component.");
  const ref = useRef(null);
  const previousElementRef = useRef(null);
  const scheduleFocus = useScheduleFocus(store);
  const [, setBaseElement] = useTransactionState(composite ? store.setBaseElement : null);
  const virtualFocus = useStoreState(store, "virtualFocus");
  const activeId = useStoreState(store, (state) => state.virtualFocus ? state.activeId : null);
  useSafeLayoutEffect(() => {
    if (!store) return;
    if (!composite) return;
    if (!virtualFocus) return;
    const previousElement = previousElementRef.current;
    previousElementRef.current = null;
    if (!previousElement) return;
    const relatedTarget = getEnabledItem(store, activeId)?.element || getActiveElement(previousElement);
    if (relatedTarget === previousElement) return;
    fireBlurEvent(previousElement, { relatedTarget });
  }, [
    store,
    activeId,
    virtualFocus,
    composite
  ]);
  const onKeyDownCapture = useKeyboardEventProxy(store, props.onKeyDownCapture, previousElementRef);
  const onKeyUpCapture = useKeyboardEventProxy(store, props.onKeyUpCapture, previousElementRef);
  const onFocusCaptureProp = props.onFocusCapture;
  const onFocusCapture = useEvent((event) => {
    onFocusCaptureProp?.(event);
    if (event.defaultPrevented) return;
    if (!store) return;
    const { virtualFocus: virtualFocus2 } = store.getState();
    if (!virtualFocus2) return;
    const previousActiveElement = event.relatedTarget;
    const isSilentlyFocused = silentlyFocused(event.currentTarget);
    if (isSelfTarget(event) && isSilentlyFocused) {
      event.stopPropagation();
      previousElementRef.current = previousActiveElement;
    }
  });
  const onFocusProp = props.onFocus;
  const onFocus = useEvent((event) => {
    onFocusProp?.(event);
    if (event.defaultPrevented) return;
    if (!composite) return;
    if (!store) return;
    const { relatedTarget } = event;
    const { virtualFocus: virtualFocus2 } = store.getState();
    if (virtualFocus2) {
      if (isSelfTarget(event) && !isItem(store, relatedTarget)) queueMicrotask(scheduleFocus);
    } else if (isSelfTarget(event)) store.setActiveId(null);
  });
  const onBlurCaptureProp = props.onBlurCapture;
  const onBlurCapture = useEvent((event) => {
    onBlurCaptureProp?.(event);
    if (event.defaultPrevented) return;
    if (!store) return;
    const { virtualFocus: virtualFocus2, activeId: activeId2 } = store.getState();
    if (!virtualFocus2) return;
    const activeElement = getEnabledItem(store, activeId2)?.element;
    const nextActiveElement = event.relatedTarget;
    const nextActiveElementIsItem = isItem(store, nextActiveElement);
    const previousElement = previousElementRef.current;
    previousElementRef.current = null;
    if (isSelfTarget(event) && nextActiveElementIsItem) {
      if (nextActiveElement === activeElement) {
        if (previousElement && previousElement !== nextActiveElement) fireBlurEvent(previousElement, event);
      } else if (activeElement) fireBlurEvent(activeElement, event);
      else if (previousElement) fireBlurEvent(previousElement, event);
      event.stopPropagation();
    } else if (!isItem(store, event.target) && activeElement) fireBlurEvent(activeElement, event);
  });
  const onKeyDownProp = props.onKeyDown;
  const moveOnKeyPressProp = useBooleanEvent(moveOnKeyPress);
  const onKeyDown = useEvent((event) => {
    onKeyDownProp?.(event);
    if (event.nativeEvent.isComposing) return;
    if (event.defaultPrevented) return;
    if (!store) return;
    if (!isSelfTarget(event)) return;
    const { orientation, renderedItems, activeId: activeId2, rtl } = store.getState();
    if (getEnabledItem(store, activeId2)?.element?.isConnected) return;
    const isVertical = orientation !== "horizontal";
    const isHorizontal = orientation !== "vertical";
    const grid = isGrid(renderedItems);
    if ((event.key === "ArrowLeft" || event.key === "ArrowRight" || event.key === "Home" || event.key === "End") && isTextField(event.currentTarget)) return;
    const up = () => {
      if (grid) return findFirstEnabledItemInTheLastRow(renderedItems)?.id;
      return store?.last();
    };
    const action = {
      ArrowUp: (grid || isVertical) && up,
      ArrowRight: (grid || isHorizontal) && (rtl ? store.last : store.first),
      ArrowDown: (grid || isVertical) && store.first,
      ArrowLeft: (grid || isHorizontal) && (rtl ? store.first : store.last),
      Home: store.first,
      End: store.last,
      PageUp: store.first,
      PageDown: store.last
    }[event.key];
    if (action) {
      const id = action();
      if (id !== void 0) {
        if (!moveOnKeyPressProp(event)) return;
        event.preventDefault();
        store.move(id);
      }
    }
  });
  props = useWrapElement(props, (element) => /* @__PURE__ */ jsxRuntimeExports.jsxs(CompositeScopedContextProvider, {
    value: store,
    children: [element, composite && /* @__PURE__ */ jsxRuntimeExports.jsx(CompositeFocusOnMove, {
      store,
      focusOnMove,
      previousElementRef
    })]
  }), [
    store,
    composite,
    focusOnMove
  ]);
  props = {
    "aria-activedescendant": useStoreState(store, (state) => {
      if (!store) return;
      if (!composite) return;
      if (!state.virtualFocus) return;
      return getEnabledItem(store, state.activeId)?.id;
    }),
    ...props,
    ref: useMergeRefs(ref, setBaseElement, props.ref),
    onKeyDownCapture,
    onKeyUpCapture,
    onFocusCapture,
    onFocus,
    onBlurCapture,
    onKeyDown
  };
  props = useFocusable({
    focusable: useStoreState(store, (state) => composite && (state.virtualFocus || state.activeId === null)),
    ...props
  });
  return props;
});
forwardRef(function Composite2(props) {
  return createElement(TagName$1, useComposite(props));
});
const TagName = "div";
const useTabList = createHook(function useTabList2({ store, ...props }) {
  const context = useTabProviderContext();
  store = store || context;
  invariant(store, process.env.NODE_ENV !== "production" && "TabList must receive a `store` prop or be wrapped in a TabProvider component.");
  const orientation = useStoreState(store, (state) => state.orientation === "both" ? void 0 : state.orientation);
  props = useWrapElement(props, (element) => /* @__PURE__ */ jsxRuntimeExports.jsx(TabScopedContextProvider, {
    value: store,
    children: element
  }), [store]);
  if (store.composite) props = {
    focusable: false,
    ...props
  };
  props = {
    role: "tablist",
    "aria-orientation": orientation,
    ...props
  };
  props = useComposite({
    store,
    ...props
  });
  return props;
});
const TabList = forwardRef(function TabList2(props) {
  return createElement(TagName, useTabList(props));
});
const ctx$4 = createStoreContext();
ctx$4.useContext;
ctx$4.useScopedContext;
ctx$4.useProviderContext;
const DisclosureContextProvider = ctx$4.ContextProvider;
const DisclosureScopedContextProvider = ctx$4.ScopedContextProvider;
const ctx$3 = createStoreContext([DisclosureContextProvider], [DisclosureScopedContextProvider]);
ctx$3.useContext;
ctx$3.useScopedContext;
ctx$3.useProviderContext;
const DialogContextProvider = ctx$3.ContextProvider;
const DialogScopedContextProvider = ctx$3.ScopedContextProvider;
createContext(void 0);
createContext(void 0);
const ctx$2 = createStoreContext([DialogContextProvider], [DialogScopedContextProvider]);
const PopoverContextProvider = ctx$2.ContextProvider;
const PopoverScopedContextProvider = ctx$2.ScopedContextProvider;
createContext(void 0);
const ctx$1 = createStoreContext([PopoverContextProvider, CompositeContextProvider], [PopoverScopedContextProvider, CompositeScopedContextProvider]);
const useComboboxContext = ctx$1.useContext;
createContext(void 0);
createContext(false);
function useCollectionStoreProps(store, update, props) {
  useUpdateEffect(update, [props.store]);
  useStoreProps(store, props, "items", "setItems");
  return store;
}
function useCompositeStoreProps(store, update, props) {
  store = useCollectionStoreProps(store, update, props);
  useStoreProps(store, props, "activeId", "setActiveId");
  useStoreProps(store, props, "includesBaseElement");
  useStoreProps(store, props, "virtualFocus");
  useStoreProps(store, props, "orientation");
  useStoreProps(store, props, "rtl");
  useStoreProps(store, props, "focusLoop");
  useStoreProps(store, props, "focusWrap");
  useStoreProps(store, props, "focusShift");
  return store;
}
const ctx = createStoreContext([PopoverContextProvider, CompositeContextProvider], [PopoverScopedContextProvider, CompositeScopedContextProvider]);
const useSelectContext = ctx.useContext;
createContext(false);
createContext(null);
function getFocusedTab(items) {
  const activeElement = items[0]?.element?.ownerDocument.activeElement;
  if (!activeElement) return;
  return items.find((item) => item.element === activeElement);
}
function getTabById(items, id) {
  if (id == null) return;
  return items.find((item) => item.id === id);
}
function isEnabledTab(item) {
  if (!item) return false;
  if (item.disabled) return false;
  if (item.dimmed) return false;
  return true;
}
function createPanelsStore() {
  const panels = createCollectionStore();
  const panelsByTabId = /* @__PURE__ */ new Map();
  sync(panels, ["items"], (state) => {
    panelsByTabId.clear();
    for (const panel of state.items) {
      const { tabId } = panel;
      if (tabId == null) continue;
      if (panelsByTabId.has(tabId)) continue;
      panelsByTabId.set(tabId, panel);
    }
  });
  return {
    panels,
    panel: (tabId) => {
      if (tabId == null) return null;
      return panelsByTabId.get(tabId) || null;
    }
  };
}
function createTabStore({ composite: parentComposite, combobox, ...props } = {}) {
  const independentKeys = [
    "items",
    "renderedItems",
    "moves",
    "orientation",
    "virtualFocus",
    "includesBaseElement",
    "baseElement",
    "focusLoop",
    "focusShift",
    "focusWrap"
  ];
  const store = mergeStore(props.store, omit(parentComposite, independentKeys), omit(combobox, independentKeys));
  const syncState = store?.getState();
  const composite = createCompositeStore({
    ...props,
    store,
    includesBaseElement: defaultValue(props.includesBaseElement, syncState?.includesBaseElement, false),
    orientation: defaultValue(props.orientation, syncState?.orientation, "horizontal"),
    focusLoop: defaultValue(props.focusLoop, syncState?.focusLoop, true)
  });
  const { panels, panel } = createPanelsStore();
  const tab = createStore({
    ...composite.getState(),
    selectedId: defaultValue(props.selectedId, syncState?.selectedId, props.defaultSelectedId),
    selectOnMove: defaultValue(props.selectOnMove, syncState?.selectOnMove, true)
  }, composite, store);
  setup(tab, () => sync(tab, ["moves"], () => {
    const { activeId, selectOnMove } = tab.getState();
    if (!selectOnMove) return;
    if (!activeId) return;
    const tabItem = composite.item(activeId);
    if (!isEnabledTab(tabItem)) return;
    tab.setState("selectedId", tabItem.id);
  }));
  let pendingRestore = false;
  let restoredSelectedId;
  setup(tab, () => {
    pendingRestore = false;
    return batch(tab, ["selectedId"], (state, prev) => {
      if (pendingRestore) {
        pendingRestore = false;
        if (state.selectedId === restoredSelectedId) return;
      }
      if (parentComposite && state.selectedId === prev.selectedId) return;
      const { activeId, renderedItems } = tab.getState();
      const focusedTab = getFocusedTab(renderedItems);
      const selectedTab = getTabById(renderedItems, state.selectedId);
      if (focusedTab && isEnabledTab(selectedTab) && activeId !== selectedTab.id) {
        composite.move(selectedTab.id);
        return;
      }
      tab.setState("activeId", state.selectedId);
    });
  });
  setup(tab, () => sync(tab, ["selectedId", "renderedItems"], (state) => {
    if (state.selectedId !== void 0) return;
    const { activeId, renderedItems } = tab.getState();
    const tabItem = composite.item(activeId);
    if (isEnabledTab(tabItem)) tab.setState("selectedId", tabItem.id);
    else {
      const tabItem2 = renderedItems.find(isEnabledTab);
      tab.setState("selectedId", tabItem2?.id);
    }
  }));
  setup(tab, () => sync(tab, ["renderedItems"], (state) => {
    const tabs = state.renderedItems;
    if (!tabs.length) return;
    return sync(panels, ["renderedItems"], (state2) => {
      const items = state2.renderedItems;
      if (!items.some((panel2) => !panel2.tabId)) return;
      items.forEach((panel2, i2) => {
        if (panel2.tabId) return;
        const tabItem = tabs[i2];
        if (!tabItem) return;
        panels.renderItem({
          ...panel2,
          tabId: tabItem.id
        });
      });
    });
  }));
  let selectedIdFromSelectedValue = null;
  setup(tab, () => {
    const backupSelectedId = () => {
      selectedIdFromSelectedValue = tab.getState().selectedId;
    };
    const restoreSelectedId = () => {
      const { selectedId } = tab.getState();
      if (selectedId === selectedIdFromSelectedValue) return;
      pendingRestore = true;
      restoredSelectedId = selectedIdFromSelectedValue;
      tab.setState("selectedId", selectedIdFromSelectedValue);
    };
    if (parentComposite && "setSelectElement" in parentComposite) return chain$1(sync(parentComposite, ["value"], backupSelectedId), sync(parentComposite, ["mounted"], restoreSelectedId));
    if (!combobox) return;
    return chain$1(sync(combobox, ["selectedValue"], backupSelectedId), sync(combobox, ["mounted"], restoreSelectedId));
  });
  return {
    ...composite,
    ...tab,
    panels,
    panel,
    setSelectedId: (id) => tab.setState("selectedId", id),
    select: (id) => {
      tab.setState("selectedId", id);
      composite.move(id);
    }
  };
}
function useTabStoreProps(store, update, props) {
  useUpdateEffect(update, [props.composite, props.combobox]);
  store = useCompositeStoreProps(store, update, props);
  useStoreProps(store, props, "selectedId", "setSelectedId");
  useStoreProps(store, props, "selectOnMove");
  const [panels, updatePanels] = useStore(() => store.panels, {});
  useUpdateEffect(updatePanels, [store, updatePanels]);
  return Object.assign(useMemo(() => ({
    ...store,
    panels
  }), [store, panels]), {
    composite: props.composite,
    combobox: props.combobox
  });
}
function useTabStore(props = {}) {
  const combobox = useComboboxContext();
  const composite = useSelectContext() || combobox;
  props = {
    ...props,
    composite: props.composite !== void 0 ? props.composite : composite,
    combobox: props.combobox !== void 0 ? props.combobox : combobox
  };
  const [store, update] = useStore(createTabStore, props);
  return useTabStoreProps(store, update, props);
}
function TabProvider(props = {}) {
  return /* @__PURE__ */ jsxRuntimeExports.jsx(TabContextProvider, {
    value: useTabStore(props),
    children: props.children
  });
}
function ManagedKeyboard({ onClick }) {
  const [style, setStyle] = React__default.useState("numeric");
  const store = useTabStore();
  const storeState = store.useState();
  React__default.useEffect(() => {
    if (!storeState.activeId) {
      return;
    }
    if (style !== storeState.activeId) {
      if (["alpha_lower", "alpha_upper"].includes(storeState.activeId) && ["alpha_lower", "alpha_upper"].includes(style)) {
        return;
      }
      if (["greek_lower", "greek_upper"].includes(storeState.activeId) && ["greek_lower", "greek_upper"].includes(style)) {
        return;
      }
      setStyle(storeState.activeId);
    }
  }, [storeState, style]);
  const wrappedOnClick = React__default.useCallback(
    (e2) => {
      const firstPress = e2[0];
      if (!firstPress) {
        return;
      }
      if (firstPress.type === "cmd" && firstPress.command === "Shift") {
        switch (style) {
          case "alpha_lower":
            setStyle("alpha_upper");
            break;
          case "alpha_upper":
            setStyle("alpha_lower");
            break;
          case "greek_lower":
            setStyle("greek_upper");
            break;
          case "greek_upper":
            setStyle("greek_lower");
            break;
        }
        return;
      }
      onClick(e2);
    },
    [style]
  );
  return /* @__PURE__ */ React__default.createElement(TabProvider, null, /* @__PURE__ */ React__default.createElement(TabList, { className: "virtual-keyboard-tab-list" }, /* @__PURE__ */ React__default.createElement(Tab, { store, id: "numeric" }, "123"), /* @__PURE__ */ React__default.createElement(Tab, { store, id: "function" }, "f(x)"), /* @__PURE__ */ React__default.createElement(Tab, { store, id: "alpha_lower" }, "ABC"), /* @__PURE__ */ React__default.createElement(Tab, { store, id: "greek_lower" }, "αβγ"), /* @__PURE__ */ React__default.createElement(Tab, { store, id: "symbol" }, "$%∞")), /* @__PURE__ */ React__default.createElement(Keyboard, { style, onClick: wrappedOnClick }));
}
var classnames = { exports: {} };
var hasRequiredClassnames;
function requireClassnames() {
  if (hasRequiredClassnames) return classnames.exports;
  hasRequiredClassnames = 1;
  (function(module) {
    (function() {
      var hasOwn = {}.hasOwnProperty;
      function classNames2() {
        var classes = "";
        for (var i2 = 0; i2 < arguments.length; i2++) {
          var arg = arguments[i2];
          if (arg) {
            classes = appendClass(classes, parseValue(arg));
          }
        }
        return classes;
      }
      function parseValue(arg) {
        if (typeof arg === "string" || typeof arg === "number") {
          return arg;
        }
        if (typeof arg !== "object") {
          return "";
        }
        if (Array.isArray(arg)) {
          return classNames2.apply(null, arg);
        }
        if (arg.toString !== Object.prototype.toString && !arg.toString.toString().includes("[native code]")) {
          return arg.toString();
        }
        var classes = "";
        for (var key in arg) {
          if (hasOwn.call(arg, key) && arg[key]) {
            classes = appendClass(classes, key);
          }
        }
        return classes;
      }
      function appendClass(value, newClass) {
        if (!newClass) {
          return value;
        }
        if (value) {
          return value + " " + newClass;
        }
        return value + newClass;
      }
      if (module.exports) {
        classNames2.default = classNames2;
        module.exports = classNames2;
      } else {
        window.classNames = classNames2;
      }
    })();
  })(classnames);
  return classnames.exports;
}
var classnamesExports = requireClassnames();
const classNames = /* @__PURE__ */ getDefaultExportFromCjs$1(classnamesExports);
const untranslated = (key, _args, fallback) => fallback ?? key;
const VIRTUAL_KEYBOARD_TRAY_ID = "virtual-keyboard-tray";
function getVirtualKeyboardTrayElement() {
  return document.getElementById(VIRTUAL_KEYBOARD_TRAY_ID);
}
function isInVirtualKeyboardTray(node) {
  return node instanceof Node && (getVirtualKeyboardTrayElement()?.contains(node) ?? false);
}
const KeyboardIcon = () => /* @__PURE__ */ React__default.createElement(
  "svg",
  {
    stroke: "currentColor",
    fill: "currentColor",
    strokeWidth: "0",
    viewBox: "0 0 576 512",
    "aria-hidden": "true",
    focusable: "false",
    height: "1em",
    width: "1em",
    xmlns: "http://www.w3.org/2000/svg"
  },
  /* @__PURE__ */ React__default.createElement("path", { d: "M528 448H48c-26.51 0-48-21.49-48-48V112c0-26.51 21.49-48 48-48h480c26.51 0 48 21.49 48 48v288c0 26.51-21.49 48-48 48zM128 180v-40c0-6.627-5.373-12-12-12H76c-6.627 0-12 5.373-12 12v40c0 6.627 5.373 12 12 12h40c6.627 0 12-5.373 12-12zm96 0v-40c0-6.627-5.373-12-12-12h-40c-6.627 0-12 5.373-12 12v40c0 6.627 5.373 12 12 12h40c6.627 0 12-5.373 12-12zm96 0v-40c0-6.627-5.373-12-12-12h-40c-6.627 0-12 5.373-12 12v40c0 6.627 5.373 12 12 12h40c6.627 0 12-5.373 12-12zm96 0v-40c0-6.627-5.373-12-12-12h-40c-6.627 0-12 5.373-12 12v40c0 6.627 5.373 12 12 12h40c6.627 0 12-5.373 12-12zm96 0v-40c0-6.627-5.373-12-12-12h-40c-6.627 0-12 5.373-12 12v40c0 6.627 5.373 12 12 12h40c6.627 0 12-5.373 12-12zm-336 96v-40c0-6.627-5.373-12-12-12h-40c-6.627 0-12 5.373-12 12v40c0 6.627 5.373 12 12 12h40c6.627 0 12-5.373 12-12zm96 0v-40c0-6.627-5.373-12-12-12h-40c-6.627 0-12 5.373-12 12v40c0 6.627 5.373 12 12 12h40c6.627 0 12-5.373 12-12zm96 0v-40c0-6.627-5.373-12-12-12h-40c-6.627 0-12 5.373-12 12v40c0 6.627 5.373 12 12 12h40c6.627 0 12-5.373 12-12zm96 0v-40c0-6.627-5.373-12-12-12h-40c-6.627 0-12 5.373-12 12v40c0 6.627 5.373 12 12 12h40c6.627 0 12-5.373 12-12zm-336 96v-40c0-6.627-5.373-12-12-12H76c-6.627 0-12 5.373-12 12v40c0 6.627 5.373 12 12 12h40c6.627 0 12-5.373 12-12zm288 0v-40c0-6.627-5.373-12-12-12H172c-6.627 0-12 5.373-12 12v40c0 6.627 5.373 12 12 12h232c6.627 0 12-5.373 12-12zm96 0v-40c0-6.627-5.373-12-12-12h-40c-6.627 0-12 5.373-12 12v40c0 6.627 5.373 12 12 12h40c6.627 0 12-5.373 12-12z" })
);
function KeyboardTray({
  onClick,
  open,
  onOpenChange,
  theme,
  translate,
  direction
}) {
  const t4 = translate ?? untranslated;
  const openLabel = t4("keyboard-open", void 0, "Open Keyboard");
  const closeLabel = t4("keyboard-close", void 0, "Close Keyboard");
  const containerRef = React__default.useRef(null);
  React__default.useLayoutEffect(() => {
    if (open && containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, [open]);
  return createPortal(
    /* @__PURE__ */ React__default.createElement(
      "div",
      {
        id: VIRTUAL_KEYBOARD_TRAY_ID,
        "data-theme": theme,
        dir: direction,
        className: classNames({ open }),
        onPointerDown: (e2) => e2.preventDefault(),
        onMouseDown: (e2) => e2.preventDefault()
      },
      /* @__PURE__ */ React__default.createElement(
        "button",
        {
          className: "open-keyboard-button",
          onClick: () => onOpenChange(!open),
          title: open ? closeLabel : openLabel,
          "aria-label": open ? closeLabel : openLabel
        },
        /* @__PURE__ */ React__default.createElement(KeyboardIcon, null)
      ),
      /* @__PURE__ */ React__default.createElement("div", { className: "keyboard-container", ref: containerRef }, /* @__PURE__ */ React__default.createElement(
        "button",
        {
          className: "close-keyboard-button",
          onClick: () => onOpenChange(false),
          title: closeLabel,
          "aria-label": closeLabel
        },
        "×"
      ), /* @__PURE__ */ React__default.createElement(ManagedKeyboard, { onClick }))
    ),
    document.body
  );
}
const DEFAULT_MATHJAX_SRC = "https://cdn.jsdelivr.net/npm/mathjax@4.1.3/tex-mml-chtml.js";
const GLOBAL_PROMISE_KEY = "__doenetMathJaxPromise";
const DOENET_MATHJAX_SCRIPT_ATTR = "data-doenet-mathjax";
const MATHJAX_SRC_PATTERN = /mathjax|tex-(chtml|mml|svg)|mml-chtml/i;
const DEFAULT_TIMEOUT_MS = 3e4;
function isMathJaxEngine(candidate) {
  if (!candidate || typeof candidate !== "object") {
    return false;
  }
  const startup = candidate.startup;
  return !!startup && (typeof startup === "object" || typeof startup === "function") && typeof startup.promise?.then === "function";
}
function findMathJaxScript() {
  const scripts = document.querySelectorAll("script[src]");
  for (const script of scripts) {
    if (script.hasAttribute(DOENET_MATHJAX_SCRIPT_ATTR)) {
      continue;
    }
    if (MATHJAX_SRC_PATTERN.test(script.src)) {
      return script;
    }
  }
  return null;
}
function waitForExistingMathJax(timeoutMs) {
  return new Promise((resolve, reject2) => {
    const deadline = Date.now() + timeoutMs;
    function poll() {
      const mathJax = window.MathJax;
      if (isMathJaxEngine(mathJax)) {
        resolve(mathJax);
        return;
      }
      if (Date.now() > deadline) {
        reject2(
          new Error(
            "DoenetViewer: timed out waiting for a host-provided MathJax"
          )
        );
        return;
      }
      window.setTimeout(poll, 50);
    }
    poll();
  });
}
function injectMathJax(src, config2, { force = false } = {}) {
  return new Promise((resolve, reject2) => {
    if (config2 && (force || window.MathJax == null)) {
      window.MathJax = config2;
    }
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.src = src;
    script.async = true;
    script.setAttribute(DOENET_MATHJAX_SCRIPT_ATTR, "true");
    script.addEventListener("load", () => {
      resolve(window.MathJax);
    });
    script.addEventListener("error", () => {
      reject2(
        new Error(`DoenetViewer: failed to load MathJax from ${src}`)
      );
    });
    document.head.appendChild(script);
  });
}
function createMathJaxPromise(options) {
  const {
    config: config2,
    src = DEFAULT_MATHJAX_SRC,
    useExistingMathJax = false,
    timeoutMs = DEFAULT_TIMEOUT_MS
  } = options;
  if (isMathJaxEngine(window.MathJax)) {
    return Promise.resolve(window.MathJax);
  }
  if (useExistingMathJax || findMathJaxScript()) {
    return waitForExistingMathJax(timeoutMs).catch((reason) => {
      console.warn(
        "DoenetViewer: a host-provided MathJax did not become usable in time; falling back to loading Doenet's own copy.",
        reason
      );
      return injectMathJax(src, config2, { force: true });
    });
  }
  return injectMathJax(src, config2);
}
function loadMathJax(options = {}) {
  if (typeof window === "undefined") {
    return Promise.reject(
      new Error("MathJax can only be loaded in a browser environment")
    );
  }
  const cached = window[GLOBAL_PROMISE_KEY];
  if (cached) {
    return cached;
  }
  const promise = createMathJaxPromise(options);
  window[GLOBAL_PROMISE_KEY] = promise;
  promise.catch(() => {
    const store = window;
    if (store[GLOBAL_PROMISE_KEY] === promise) {
      delete store[GLOBAL_PROMISE_KEY];
    }
  });
  return promise;
}
function MathJaxContext({
  config: config2,
  src,
  useExistingMathJax,
  timeoutMs,
  version: version2 = 4,
  children
}) {
  const value = useMemo(() => {
    const promise = loadMathJax({
      config: config2,
      src,
      useExistingMathJax,
      timeoutMs
    });
    return { version: version2, promise };
  }, [config2, src, useExistingMathJax, timeoutMs, version2]);
  return /* @__PURE__ */ React__default.createElement(MathJaxBaseContext.Provider, { value }, children);
}
var commonjsGlobal = typeof globalThis !== "undefined" ? globalThis : typeof window !== "undefined" ? window : typeof global !== "undefined" ? global : typeof self !== "undefined" ? self : {};
function getDefaultExportFromCjs(x2) {
  return x2 && x2.__esModule && Object.prototype.hasOwnProperty.call(x2, "default") ? x2["default"] : x2;
}
var colorName$1;
var hasRequiredColorName;
function requireColorName() {
  if (hasRequiredColorName) return colorName$1;
  hasRequiredColorName = 1;
  colorName$1 = {
    "aliceblue": [240, 248, 255],
    "antiquewhite": [250, 235, 215],
    "aqua": [0, 255, 255],
    "aquamarine": [127, 255, 212],
    "azure": [240, 255, 255],
    "beige": [245, 245, 220],
    "bisque": [255, 228, 196],
    "black": [0, 0, 0],
    "blanchedalmond": [255, 235, 205],
    "blue": [0, 0, 255],
    "blueviolet": [138, 43, 226],
    "brown": [165, 42, 42],
    "burlywood": [222, 184, 135],
    "cadetblue": [95, 158, 160],
    "chartreuse": [127, 255, 0],
    "chocolate": [210, 105, 30],
    "coral": [255, 127, 80],
    "cornflowerblue": [100, 149, 237],
    "cornsilk": [255, 248, 220],
    "crimson": [220, 20, 60],
    "cyan": [0, 255, 255],
    "darkblue": [0, 0, 139],
    "darkcyan": [0, 139, 139],
    "darkgoldenrod": [184, 134, 11],
    "darkgray": [169, 169, 169],
    "darkgreen": [0, 100, 0],
    "darkgrey": [169, 169, 169],
    "darkkhaki": [189, 183, 107],
    "darkmagenta": [139, 0, 139],
    "darkolivegreen": [85, 107, 47],
    "darkorange": [255, 140, 0],
    "darkorchid": [153, 50, 204],
    "darkred": [139, 0, 0],
    "darksalmon": [233, 150, 122],
    "darkseagreen": [143, 188, 143],
    "darkslateblue": [72, 61, 139],
    "darkslategray": [47, 79, 79],
    "darkslategrey": [47, 79, 79],
    "darkturquoise": [0, 206, 209],
    "darkviolet": [148, 0, 211],
    "deeppink": [255, 20, 147],
    "deepskyblue": [0, 191, 255],
    "dimgray": [105, 105, 105],
    "dimgrey": [105, 105, 105],
    "dodgerblue": [30, 144, 255],
    "firebrick": [178, 34, 34],
    "floralwhite": [255, 250, 240],
    "forestgreen": [34, 139, 34],
    "fuchsia": [255, 0, 255],
    "gainsboro": [220, 220, 220],
    "ghostwhite": [248, 248, 255],
    "gold": [255, 215, 0],
    "goldenrod": [218, 165, 32],
    "gray": [128, 128, 128],
    "green": [0, 128, 0],
    "greenyellow": [173, 255, 47],
    "grey": [128, 128, 128],
    "honeydew": [240, 255, 240],
    "hotpink": [255, 105, 180],
    "indianred": [205, 92, 92],
    "indigo": [75, 0, 130],
    "ivory": [255, 255, 240],
    "khaki": [240, 230, 140],
    "lavender": [230, 230, 250],
    "lavenderblush": [255, 240, 245],
    "lawngreen": [124, 252, 0],
    "lemonchiffon": [255, 250, 205],
    "lightblue": [173, 216, 230],
    "lightcoral": [240, 128, 128],
    "lightcyan": [224, 255, 255],
    "lightgoldenrodyellow": [250, 250, 210],
    "lightgray": [211, 211, 211],
    "lightgreen": [144, 238, 144],
    "lightgrey": [211, 211, 211],
    "lightpink": [255, 182, 193],
    "lightsalmon": [255, 160, 122],
    "lightseagreen": [32, 178, 170],
    "lightskyblue": [135, 206, 250],
    "lightslategray": [119, 136, 153],
    "lightslategrey": [119, 136, 153],
    "lightsteelblue": [176, 196, 222],
    "lightyellow": [255, 255, 224],
    "lime": [0, 255, 0],
    "limegreen": [50, 205, 50],
    "linen": [250, 240, 230],
    "magenta": [255, 0, 255],
    "maroon": [128, 0, 0],
    "mediumaquamarine": [102, 205, 170],
    "mediumblue": [0, 0, 205],
    "mediumorchid": [186, 85, 211],
    "mediumpurple": [147, 112, 219],
    "mediumseagreen": [60, 179, 113],
    "mediumslateblue": [123, 104, 238],
    "mediumspringgreen": [0, 250, 154],
    "mediumturquoise": [72, 209, 204],
    "mediumvioletred": [199, 21, 133],
    "midnightblue": [25, 25, 112],
    "mintcream": [245, 255, 250],
    "mistyrose": [255, 228, 225],
    "moccasin": [255, 228, 181],
    "navajowhite": [255, 222, 173],
    "navy": [0, 0, 128],
    "oldlace": [253, 245, 230],
    "olive": [128, 128, 0],
    "olivedrab": [107, 142, 35],
    "orange": [255, 165, 0],
    "orangered": [255, 69, 0],
    "orchid": [218, 112, 214],
    "palegoldenrod": [238, 232, 170],
    "palegreen": [152, 251, 152],
    "paleturquoise": [175, 238, 238],
    "palevioletred": [219, 112, 147],
    "papayawhip": [255, 239, 213],
    "peachpuff": [255, 218, 185],
    "peru": [205, 133, 63],
    "pink": [255, 192, 203],
    "plum": [221, 160, 221],
    "powderblue": [176, 224, 230],
    "purple": [128, 0, 128],
    "rebeccapurple": [102, 51, 153],
    "red": [255, 0, 0],
    "rosybrown": [188, 143, 143],
    "royalblue": [65, 105, 225],
    "saddlebrown": [139, 69, 19],
    "salmon": [250, 128, 114],
    "sandybrown": [244, 164, 96],
    "seagreen": [46, 139, 87],
    "seashell": [255, 245, 238],
    "sienna": [160, 82, 45],
    "silver": [192, 192, 192],
    "skyblue": [135, 206, 235],
    "slateblue": [106, 90, 205],
    "slategray": [112, 128, 144],
    "slategrey": [112, 128, 144],
    "snow": [255, 250, 250],
    "springgreen": [0, 255, 127],
    "steelblue": [70, 130, 180],
    "tan": [210, 180, 140],
    "teal": [0, 128, 128],
    "thistle": [216, 191, 216],
    "tomato": [255, 99, 71],
    "turquoise": [64, 224, 208],
    "violet": [238, 130, 238],
    "wheat": [245, 222, 179],
    "white": [255, 255, 255],
    "whitesmoke": [245, 245, 245],
    "yellow": [255, 255, 0],
    "yellowgreen": [154, 205, 50]
  };
  return colorName$1;
}
var colorNameExports = requireColorName();
const colorName = /* @__PURE__ */ getDefaultExportFromCjs(colorNameExports);
var r$2 = { grad: 0.9, turn: 360, rad: 360 / (2 * Math.PI) }, t$3 = function(r2) {
  return "string" == typeof r2 ? r2.length > 0 : "number" == typeof r2;
}, n$2 = function(r2, t22, n2) {
  return void 0 === t22 && (t22 = 0), void 0 === n2 && (n2 = Math.pow(10, t22)), Math.round(n2 * r2) / n2 + 0;
}, e$2 = function(r2, t22, n2) {
  return void 0 === t22 && (t22 = 0), void 0 === n2 && (n2 = 1), r2 > n2 ? n2 : r2 > t22 ? r2 : t22;
}, u$2 = function(r2) {
  return (r2 = isFinite(r2) ? r2 % 360 : 0) > 0 ? r2 : r2 + 360;
}, a$2 = function(r2) {
  return { r: e$2(r2.r, 0, 255), g: e$2(r2.g, 0, 255), b: e$2(r2.b, 0, 255), a: e$2(r2.a) };
}, o$3 = function(r2) {
  return { r: n$2(r2.r), g: n$2(r2.g), b: n$2(r2.b), a: n$2(r2.a, 3) };
}, i$2 = /^#([0-9a-f]{3,8})$/i, s$1 = function(r2) {
  var t22 = r2.toString(16);
  return t22.length < 2 ? "0" + t22 : t22;
}, h$2 = function(r2) {
  var t22 = r2.r, n2 = r2.g, e2 = r2.b, u2 = r2.a, a2 = Math.max(t22, n2, e2), o2 = a2 - Math.min(t22, n2, e2), i2 = o2 ? a2 === t22 ? (n2 - e2) / o2 : a2 === n2 ? 2 + (e2 - t22) / o2 : 4 + (t22 - n2) / o2 : 0;
  return { h: 60 * (i2 < 0 ? i2 + 6 : i2), s: a2 ? o2 / a2 * 100 : 0, v: a2 / 255 * 100, a: u2 };
}, b$2 = function(r2) {
  var t22 = r2.h, n2 = r2.s, e2 = r2.v, u2 = r2.a;
  t22 = t22 / 360 * 6, n2 /= 100, e2 /= 100;
  var a2 = Math.floor(t22), o2 = e2 * (1 - n2), i2 = e2 * (1 - (t22 - a2) * n2), s2 = e2 * (1 - (1 - t22 + a2) * n2), h2 = a2 % 6;
  return { r: 255 * [e2, i2, o2, o2, s2, e2][h2], g: 255 * [s2, e2, e2, i2, o2, o2][h2], b: 255 * [o2, o2, s2, e2, e2, i2][h2], a: u2 };
}, g = function(r2) {
  return { h: u$2(r2.h), s: e$2(r2.s, 0, 100), l: e$2(r2.l, 0, 100), a: e$2(r2.a) };
}, d$1 = function(r2) {
  return { h: n$2(r2.h), s: n$2(r2.s), l: n$2(r2.l), a: n$2(r2.a, 3) };
}, f$1 = function(r2) {
  return b$2((n2 = (t22 = r2).s, { h: t22.h, s: (n2 *= ((e2 = t22.l) < 50 ? e2 : 100 - e2) / 100) > 0 ? 2 * n2 / (e2 + n2) * 100 : 0, v: e2 + n2, a: t22.a }));
  var t22, n2, e2;
}, c$1 = function(r2) {
  return { h: (t22 = h$2(r2)).h, s: (u2 = (200 - (n2 = t22.s)) * (e2 = t22.v) / 100) > 0 && u2 < 200 ? n2 * e2 / 100 / (u2 <= 100 ? u2 : 200 - u2) * 100 : 0, l: u2 / 2, a: t22.a };
  var t22, n2, e2, u2;
}, l$2 = /^hsla?\(\s*([+-]?\d*\.?\d+)(deg|rad|grad|turn)?\s*,\s*([+-]?\d*\.?\d+)%\s*,\s*([+-]?\d*\.?\d+)%\s*(?:,\s*([+-]?\d*\.?\d+)(%)?\s*)?\)$/i, p$2 = /^hsla?\(\s*([+-]?\d*\.?\d+)(deg|rad|grad|turn)?\s+([+-]?\d*\.?\d+)%\s+([+-]?\d*\.?\d+)%\s*(?:\/\s*([+-]?\d*\.?\d+)(%)?\s*)?\)$/i, v$1 = /^rgba?\(\s*([+-]?\d*\.?\d+)(%)?\s*,\s*([+-]?\d*\.?\d+)(%)?\s*,\s*([+-]?\d*\.?\d+)(%)?\s*(?:,\s*([+-]?\d*\.?\d+)(%)?\s*)?\)$/i, m = /^rgba?\(\s*([+-]?\d*\.?\d+)(%)?\s+([+-]?\d*\.?\d+)(%)?\s+([+-]?\d*\.?\d+)(%)?\s*(?:\/\s*([+-]?\d*\.?\d+)(%)?\s*)?\)$/i, y$1 = { string: [[function(r2) {
  var t22 = i$2.exec(r2);
  return t22 ? (r2 = t22[1]).length <= 4 ? { r: parseInt(r2[0] + r2[0], 16), g: parseInt(r2[1] + r2[1], 16), b: parseInt(r2[2] + r2[2], 16), a: 4 === r2.length ? n$2(parseInt(r2[3] + r2[3], 16) / 255, 2) : 1 } : 6 === r2.length || 8 === r2.length ? { r: parseInt(r2.substr(0, 2), 16), g: parseInt(r2.substr(2, 2), 16), b: parseInt(r2.substr(4, 2), 16), a: 8 === r2.length ? n$2(parseInt(r2.substr(6, 2), 16) / 255, 2) : 1 } : null : null;
}, "hex"], [function(r2) {
  var t22 = v$1.exec(r2) || m.exec(r2);
  return t22 ? t22[2] !== t22[4] || t22[4] !== t22[6] ? null : a$2({ r: Number(t22[1]) / (t22[2] ? 100 / 255 : 1), g: Number(t22[3]) / (t22[4] ? 100 / 255 : 1), b: Number(t22[5]) / (t22[6] ? 100 / 255 : 1), a: void 0 === t22[7] ? 1 : Number(t22[7]) / (t22[8] ? 100 : 1) }) : null;
}, "rgb"], [function(t22) {
  var n2 = l$2.exec(t22) || p$2.exec(t22);
  if (!n2) return null;
  var e2, u2, a2 = g({ h: (e2 = n2[1], u2 = n2[2], void 0 === u2 && (u2 = "deg"), Number(e2) * (r$2[u2] || 1)), s: Number(n2[3]), l: Number(n2[4]), a: void 0 === n2[5] ? 1 : Number(n2[5]) / (n2[6] ? 100 : 1) });
  return f$1(a2);
}, "hsl"]], object: [[function(r2) {
  var n2 = r2.r, e2 = r2.g, u2 = r2.b, o2 = r2.a, i2 = void 0 === o2 ? 1 : o2;
  return t$3(n2) && t$3(e2) && t$3(u2) ? a$2({ r: Number(n2), g: Number(e2), b: Number(u2), a: Number(i2) }) : null;
}, "rgb"], [function(r2) {
  var n2 = r2.h, e2 = r2.s, u2 = r2.l, a2 = r2.a, o2 = void 0 === a2 ? 1 : a2;
  if (!t$3(n2) || !t$3(e2) || !t$3(u2)) return null;
  var i2 = g({ h: Number(n2), s: Number(e2), l: Number(u2), a: Number(o2) });
  return f$1(i2);
}, "hsl"], [function(r2) {
  var n2 = r2.h, a2 = r2.s, o2 = r2.v, i2 = r2.a, s2 = void 0 === i2 ? 1 : i2;
  if (!t$3(n2) || !t$3(a2) || !t$3(o2)) return null;
  var h2 = (function(r3) {
    return { h: u$2(r3.h), s: e$2(r3.s, 0, 100), v: e$2(r3.v, 0, 100), a: e$2(r3.a) };
  })({ h: Number(n2), s: Number(a2), v: Number(o2), a: Number(s2) });
  return b$2(h2);
}, "hsv"]] }, N = function(r2, t22) {
  for (var n2 = 0; n2 < t22.length; n2++) {
    var e2 = t22[n2][0](r2);
    if (e2) return [e2, t22[n2][1]];
  }
  return [null, void 0];
}, x$1 = function(r2) {
  return "string" == typeof r2 ? N(r2.trim(), y$1.string) : "object" == typeof r2 && null !== r2 ? N(r2, y$1.object) : [null, void 0];
}, M$2 = function(r2, t22) {
  var n2 = c$1(r2);
  return { h: n2.h, s: e$2(n2.s + 100 * t22, 0, 100), l: n2.l, a: n2.a };
}, H = function(r2) {
  return (299 * r2.r + 587 * r2.g + 114 * r2.b) / 1e3 / 255;
}, $ = function(r2, t22) {
  var n2 = c$1(r2);
  return { h: n2.h, s: n2.s, l: e$2(n2.l + 100 * t22, 0, 100), a: n2.a };
}, j = (function() {
  function r2(r3) {
    this.parsed = x$1(r3)[0], this.rgba = this.parsed || { r: 0, g: 0, b: 0, a: 1 };
  }
  return r2.prototype.isValid = function() {
    return null !== this.parsed;
  }, r2.prototype.brightness = function() {
    return n$2(H(this.rgba), 2);
  }, r2.prototype.isDark = function() {
    return H(this.rgba) < 0.5;
  }, r2.prototype.isLight = function() {
    return H(this.rgba) >= 0.5;
  }, r2.prototype.toHex = function() {
    return r3 = o$3(this.rgba), t22 = r3.r, e2 = r3.g, u2 = r3.b, i2 = (a2 = r3.a) < 1 ? s$1(n$2(255 * a2)) : "", "#" + s$1(t22) + s$1(e2) + s$1(u2) + i2;
    var r3, t22, e2, u2, a2, i2;
  }, r2.prototype.toRgb = function() {
    return o$3(this.rgba);
  }, r2.prototype.toRgbString = function() {
    return r3 = o$3(this.rgba), t22 = r3.r, n2 = r3.g, e2 = r3.b, (u2 = r3.a) < 1 ? "rgba(" + t22 + ", " + n2 + ", " + e2 + ", " + u2 + ")" : "rgb(" + t22 + ", " + n2 + ", " + e2 + ")";
    var r3, t22, n2, e2, u2;
  }, r2.prototype.toHsl = function() {
    return d$1(c$1(this.rgba));
  }, r2.prototype.toHslString = function() {
    return r3 = d$1(c$1(this.rgba)), t22 = r3.h, n2 = r3.s, e2 = r3.l, (u2 = r3.a) < 1 ? "hsla(" + t22 + ", " + n2 + "%, " + e2 + "%, " + u2 + ")" : "hsl(" + t22 + ", " + n2 + "%, " + e2 + "%)";
    var r3, t22, n2, e2, u2;
  }, r2.prototype.toHsv = function() {
    return r3 = h$2(this.rgba), { h: n$2(r3.h), s: n$2(r3.s), v: n$2(r3.v), a: n$2(r3.a, 3) };
    var r3;
  }, r2.prototype.invert = function() {
    return w$1({ r: 255 - (r3 = this.rgba).r, g: 255 - r3.g, b: 255 - r3.b, a: r3.a });
    var r3;
  }, r2.prototype.saturate = function(r3) {
    return void 0 === r3 && (r3 = 0.1), w$1(M$2(this.rgba, r3));
  }, r2.prototype.desaturate = function(r3) {
    return void 0 === r3 && (r3 = 0.1), w$1(M$2(this.rgba, -r3));
  }, r2.prototype.grayscale = function() {
    return w$1(M$2(this.rgba, -1));
  }, r2.prototype.lighten = function(r3) {
    return void 0 === r3 && (r3 = 0.1), w$1($(this.rgba, r3));
  }, r2.prototype.darken = function(r3) {
    return void 0 === r3 && (r3 = 0.1), w$1($(this.rgba, -r3));
  }, r2.prototype.rotate = function(r3) {
    return void 0 === r3 && (r3 = 15), this.hue(this.hue() + r3);
  }, r2.prototype.alpha = function(r3) {
    return "number" == typeof r3 ? w$1({ r: (t22 = this.rgba).r, g: t22.g, b: t22.b, a: r3 }) : n$2(this.rgba.a, 3);
    var t22;
  }, r2.prototype.hue = function(r3) {
    var t22 = c$1(this.rgba);
    return "number" == typeof r3 ? w$1({ h: r3, s: t22.s, l: t22.l, a: t22.a }) : n$2(t22.h);
  }, r2.prototype.isEqual = function(r3) {
    return this.toHex() === w$1(r3).toHex();
  }, r2;
})(), w$1 = function(r2) {
  return r2 instanceof j ? r2 : new j(r2);
}, S = [], k = function(r2) {
  r2.forEach(function(r3) {
    S.indexOf(r3) < 0 && (r3(j, y$1), S.push(r3));
  });
};
var a$1 = function(a2) {
  return "string" == typeof a2 ? a2.length > 0 : "number" == typeof a2;
}, t$2 = function(a2, t22, o2) {
  return void 0 === t22 && (t22 = 0), void 0 === o2 && (o2 = Math.pow(10, t22)), Math.round(o2 * a2) / o2 + 0;
}, o$2 = function(a2, t22, o2) {
  return void 0 === t22 && (t22 = 0), void 0 === o2 && (o2 = 1), a2 > o2 ? o2 : a2 > t22 ? a2 : t22;
}, r$1 = function(a2) {
  var t22 = a2 / 255;
  return t22 < 0.04045 ? t22 / 12.92 : Math.pow((t22 + 0.055) / 1.055, 2.4);
}, h$1 = function(a2) {
  return 255 * (a2 > 31308e-7 ? 1.055 * Math.pow(a2, 1 / 2.4) - 0.055 : 12.92 * a2);
}, n$1 = 96.422, p$1 = 100, M$1 = 82.521, u$1 = function(a2) {
  var t22, r2, n2 = { x: 0.9555766 * (t22 = a2).x + -0.0230393 * t22.y + 0.0631636 * t22.z, y: -0.0282895 * t22.x + 1.0099416 * t22.y + 0.0210077 * t22.z, z: 0.0122982 * t22.x + -0.020483 * t22.y + 1.3299098 * t22.z };
  return r2 = { r: h$1(0.032404542 * n2.x - 0.015371385 * n2.y - 4985314e-9 * n2.z), g: h$1(-969266e-8 * n2.x + 0.018760108 * n2.y + 41556e-8 * n2.z), b: h$1(556434e-9 * n2.x - 2040259e-9 * n2.y + 0.010572252 * n2.z), a: a2.a }, { r: o$2(r2.r, 0, 255), g: o$2(r2.g, 0, 255), b: o$2(r2.b, 0, 255), a: o$2(r2.a) };
}, e$1 = function(a2) {
  var t22 = r$1(a2.r), h2 = r$1(a2.g), u2 = r$1(a2.b);
  return (function(a3) {
    return { x: o$2(a3.x, 0, n$1), y: o$2(a3.y, 0, p$1), z: o$2(a3.z, 0, M$1), a: o$2(a3.a) };
  })((function(a3) {
    return { x: 1.0478112 * a3.x + 0.0228866 * a3.y + -0.050127 * a3.z, y: 0.0295424 * a3.x + 0.9904844 * a3.y + -0.0170491 * a3.z, z: -92345e-7 * a3.x + 0.0150436 * a3.y + 0.7521316 * a3.z, a: a3.a };
  })({ x: 100 * (0.4124564 * t22 + 0.3575761 * h2 + 0.1804375 * u2), y: 100 * (0.2126729 * t22 + 0.7151522 * h2 + 0.072175 * u2), z: 100 * (0.0193339 * t22 + 0.119192 * h2 + 0.9503041 * u2), a: a2.a }));
}, w = 216 / 24389, b$1 = 24389 / 27, i$1 = function(t22) {
  var r2 = t22.l, h2 = t22.a, n2 = t22.b, p2 = t22.alpha, M2 = void 0 === p2 ? 1 : p2;
  if (!a$1(r2) || !a$1(h2) || !a$1(n2)) return null;
  var u2 = (function(a2) {
    return { l: o$2(a2.l, 0, 400), a: a2.a, b: a2.b, alpha: o$2(a2.alpha) };
  })({ l: Number(r2), a: Number(h2), b: Number(n2), alpha: Number(M2) });
  return l$1(u2);
}, l$1 = function(a2) {
  var t22 = (a2.l + 16) / 116, o2 = a2.a / 500 + t22, r2 = t22 - a2.b / 200;
  return u$1({ x: (Math.pow(o2, 3) > w ? Math.pow(o2, 3) : (116 * o2 - 16) / b$1) * n$1, y: (a2.l > 8 ? Math.pow((a2.l + 16) / 116, 3) : a2.l / b$1) * p$1, z: (Math.pow(r2, 3) > w ? Math.pow(r2, 3) : (116 * r2 - 16) / b$1) * M$1, a: a2.alpha });
};
function labPlugin(a2, r2) {
  a2.prototype.toLab = function() {
    return o2 = e$1(this.rgba), h2 = o2.y / p$1, u2 = o2.z / M$1, r3 = (r3 = o2.x / n$1) > w ? Math.cbrt(r3) : (b$1 * r3 + 16) / 116, a3 = { l: 116 * (h2 = h2 > w ? Math.cbrt(h2) : (b$1 * h2 + 16) / 116) - 16, a: 500 * (r3 - h2), b: 200 * (h2 - (u2 = u2 > w ? Math.cbrt(u2) : (b$1 * u2 + 16) / 116)), alpha: o2.a }, { l: t$2(a3.l, 2), a: t$2(a3.a, 2), b: t$2(a3.b, 2), alpha: t$2(a3.alpha, 3) };
    var a3, o2, r3, h2, u2;
  }, a2.prototype.delta = function(r3) {
    void 0 === r3 && (r3 = "#FFF");
    var h2 = r3 instanceof a2 ? r3 : new a2(r3), n2 = (function(a3, t22) {
      var o2 = a3.l, r4 = a3.a, h3 = a3.b, n3 = t22.l, p2 = t22.a, M2 = t22.b, u2 = 180 / Math.PI, e2 = Math.PI / 180, w2 = Math.pow(Math.pow(r4, 2) + Math.pow(h3, 2), 0.5), b2 = Math.pow(Math.pow(p2, 2) + Math.pow(M2, 2), 0.5), i2 = (o2 + n3) / 2, l2 = Math.pow((w2 + b2) / 2, 7), c2 = 0.5 * (1 - Math.pow(l2 / (l2 + Math.pow(25, 7)), 0.5)), f2 = r4 * (1 + c2), y2 = p2 * (1 + c2), v2 = Math.pow(Math.pow(f2, 2) + Math.pow(h3, 2), 0.5), x2 = Math.pow(Math.pow(y2, 2) + Math.pow(M2, 2), 0.5), z = (v2 + x2) / 2, s2 = 0 === f2 && 0 === h3 ? 0 : Math.atan2(h3, f2) * u2, d2 = 0 === y2 && 0 === M2 ? 0 : Math.atan2(M2, y2) * u2;
      s2 < 0 && (s2 += 360), d2 < 0 && (d2 += 360);
      var g2 = d2 - s2, m2 = Math.abs(d2 - s2);
      m2 > 180 && d2 <= s2 ? g2 += 360 : m2 > 180 && d2 > s2 && (g2 -= 360);
      var N2 = s2 + d2;
      m2 <= 180 ? N2 /= 2 : N2 = (s2 + d2 < 360 ? N2 + 360 : N2 - 360) / 2;
      var F = 1 - 0.17 * Math.cos(e2 * (N2 - 30)) + 0.24 * Math.cos(2 * e2 * N2) + 0.32 * Math.cos(e2 * (3 * N2 + 6)) - 0.2 * Math.cos(e2 * (4 * N2 - 63)), L = n3 - o2, I = x2 - v2, P2 = 2 * Math.sin(e2 * g2 / 2) * Math.pow(v2 * x2, 0.5), j2 = 1 + 0.015 * Math.pow(i2 - 50, 2) / Math.pow(20 + Math.pow(i2 - 50, 2), 0.5), k2 = 1 + 0.045 * z, q = 1 + 0.015 * z * F, A2 = 30 * Math.exp(-1 * Math.pow((N2 - 275) / 25, 2)), B2 = -2 * Math.pow(l2 / (l2 + Math.pow(25, 7)), 0.5) * Math.sin(2 * e2 * A2);
      return Math.pow(Math.pow(L / 1 / j2, 2) + Math.pow(I / 1 / k2, 2) + Math.pow(P2 / 1 / q, 2) + B2 * I * P2 / (1 * k2 * 1 * q), 0.5);
    })(this.toLab(), h2.toLab()) / 100;
    return o$2(t$2(n2, 3));
  }, r2.object.push([i$1, "lab"]);
}
var r = { grad: 0.9, turn: 360, rad: 360 / (2 * Math.PI) }, t$1 = function(r2) {
  return "string" == typeof r2 ? r2.length > 0 : "number" == typeof r2;
}, a = function(r2, t22, a2) {
  return void 0 === t22 && (t22 = 0), void 0 === a2 && (a2 = Math.pow(10, t22)), Math.round(a2 * r2) / a2 + 0;
}, n = function(r2, t22, a2) {
  return void 0 === t22 && (t22 = 0), void 0 === a2 && (a2 = 1), r2 > a2 ? a2 : r2 > t22 ? r2 : t22;
}, u = function(r2) {
  var t22 = r2 / 255;
  return t22 < 0.04045 ? t22 / 12.92 : Math.pow((t22 + 0.055) / 1.055, 2.4);
}, h = function(r2) {
  return 255 * (r2 > 31308e-7 ? 1.055 * Math.pow(r2, 1 / 2.4) - 0.055 : 12.92 * r2);
}, o$1 = 96.422, e$3 = 100, c = 82.521, i = function(r2) {
  var t22, a2, u2 = { x: 0.9555766 * (t22 = r2).x + -0.0230393 * t22.y + 0.0631636 * t22.z, y: -0.0282895 * t22.x + 1.0099416 * t22.y + 0.0210077 * t22.z, z: 0.0122982 * t22.x + -0.020483 * t22.y + 1.3299098 * t22.z };
  return a2 = { r: h(0.032404542 * u2.x - 0.015371385 * u2.y - 4985314e-9 * u2.z), g: h(-969266e-8 * u2.x + 0.018760108 * u2.y + 41556e-8 * u2.z), b: h(556434e-9 * u2.x - 2040259e-9 * u2.y + 0.010572252 * u2.z), a: r2.a }, { r: n(a2.r, 0, 255), g: n(a2.g, 0, 255), b: n(a2.b, 0, 255), a: n(a2.a) };
}, l = function(r2) {
  var t22 = u(r2.r), a2 = u(r2.g), h2 = u(r2.b);
  return (function(r3) {
    return { x: n(r3.x, 0, o$1), y: n(r3.y, 0, e$3), z: n(r3.z, 0, c), a: n(r3.a) };
  })((function(r3) {
    return { x: 1.0478112 * r3.x + 0.0228866 * r3.y + -0.050127 * r3.z, y: 0.0295424 * r3.x + 0.9904844 * r3.y + -0.0170491 * r3.z, z: -92345e-7 * r3.x + 0.0150436 * r3.y + 0.7521316 * r3.z, a: r3.a };
  })({ x: 100 * (0.4124564 * t22 + 0.3575761 * a2 + 0.1804375 * h2), y: 100 * (0.2126729 * t22 + 0.7151522 * a2 + 0.072175 * h2), z: 100 * (0.0193339 * t22 + 0.119192 * a2 + 0.9503041 * h2), a: r2.a }));
}, f$2 = 216 / 24389, b = 24389 / 27, d = function(r2) {
  return { l: n(r2.l, 0, 100), c: r2.c, h: (t22 = r2.h, (t22 = isFinite(t22) ? t22 % 360 : 0) > 0 ? t22 : t22 + 360), a: r2.a };
  var t22;
}, p = function(r2) {
  return { l: a(r2.l, 2), c: a(r2.c, 2), h: a(r2.h, 2), a: a(r2.a, 3) };
}, v = function(r2) {
  var a2 = r2.l, n2 = r2.c, u2 = r2.h, h2 = r2.a, o2 = void 0 === h2 ? 1 : h2;
  if (!t$1(a2) || !t$1(n2) || !t$1(u2)) return null;
  var e2 = d({ l: Number(a2), c: Number(n2), h: Number(u2), a: Number(o2) });
  return M(e2);
}, y = function(r2) {
  var t22 = (function(r3) {
    var t32 = l(r3), a2 = t32.x / o$1, n3 = t32.y / e$3, u3 = t32.z / c;
    return a2 = a2 > f$2 ? Math.cbrt(a2) : (b * a2 + 16) / 116, { l: 116 * (n3 = n3 > f$2 ? Math.cbrt(n3) : (b * n3 + 16) / 116) - 16, a: 500 * (a2 - n3), b: 200 * (n3 - (u3 = u3 > f$2 ? Math.cbrt(u3) : (b * u3 + 16) / 116)), alpha: t32.a };
  })(r2), n2 = a(t22.a, 3), u2 = a(t22.b, 3), h2 = Math.atan2(u2, n2) / Math.PI * 180;
  return { l: t22.l, c: Math.sqrt(n2 * n2 + u2 * u2), h: h2 < 0 ? h2 + 360 : h2, a: t22.alpha };
}, M = function(r2) {
  return t22 = { l: r2.l, a: r2.c * Math.cos(r2.h * Math.PI / 180), b: r2.c * Math.sin(r2.h * Math.PI / 180), alpha: r2.a }, n2 = t22.a / 500 + (a2 = (t22.l + 16) / 116), u2 = a2 - t22.b / 200, i({ x: (Math.pow(n2, 3) > f$2 ? Math.pow(n2, 3) : (116 * n2 - 16) / b) * o$1, y: (t22.l > 8 ? Math.pow((t22.l + 16) / 116, 3) : t22.l / b) * e$3, z: (Math.pow(u2, 3) > f$2 ? Math.pow(u2, 3) : (116 * u2 - 16) / b) * c, a: t22.alpha });
  var t22, a2, n2, u2;
}, x = /^lch\(\s*([+-]?\d*\.?\d+)%\s+([+-]?\d*\.?\d+)\s+([+-]?\d*\.?\d+)(deg|rad|grad|turn)?\s*(?:\/\s*([+-]?\d*\.?\d+)(%)?\s*)?\)$/i, s = function(t22) {
  var a2 = x.exec(t22);
  if (!a2) return null;
  var n2, u2, h2 = d({ l: Number(a2[1]), c: Number(a2[2]), h: (n2 = a2[3], u2 = a2[4], void 0 === u2 && (u2 = "deg"), Number(n2) * (r[u2] || 1)), a: void 0 === a2[5] ? 1 : Number(a2[5]) / (a2[6] ? 100 : 1) });
  return M(h2);
};
function lchPlugin(r2, t22) {
  r2.prototype.toLch = function() {
    return p(y(this.rgba));
  }, r2.prototype.toLchString = function() {
    return r3 = p(y(this.rgba)), t32 = r3.l, a2 = r3.c, n2 = r3.h, (u2 = r3.a) < 1 ? "lch(" + t32 + "% " + a2 + " " + n2 + " / " + u2 + ")" : "lch(" + t32 + "% " + a2 + " " + n2 + ")";
    var r3, t32, a2, n2, u2;
  }, t22.string.push([s, "lch"]), t22.object.push([v, "lch"]);
}
k([labPlugin, lchPlugin]);
const canonicalColorHexByKey = {
  black: "#000000",
  white: "#ffffff",
  gray: "#808080",
  red: "#ff0000",
  orange: "#ffa500",
  yellow: "#ffff00",
  green: "#008000",
  cyan: "#00ffff",
  blue: "#0000ff",
  purple: "#800080",
  pink: "#ffc0cb",
  brown: "#a52a2a"
};
const canonicalAnchorDefinitions = Object.fromEntries(
  Object.entries(canonicalColorHexByKey).map(([canonical, hex]) => [
    canonical,
    { hex, canonical }
  ])
);
const extraAnchorDefinitions = {
  silver: { hex: "#c0c0c0", canonical: "gray" },
  grayDark: { hex: "#4d4d4d", canonical: "gray" },
  grayCustom: { hex: "#757575", canonical: "gray" },
  grayLight: { hex: "#c8c8c8", canonical: "gray" },
  grayLighter: { hex: "#dcdcdc", canonical: "gray" },
  maroon: { hex: "#800000", canonical: "red" },
  redLight: { hex: "#f08080", canonical: "red" },
  redCustom: { hex: "#D4042D", canonical: "red" },
  redDark: { hex: "#8b0000", canonical: "red" },
  crimson: { hex: "#dc143c", canonical: "red" },
  wine: { hex: "#993355", canonical: "red" },
  orangeLight: { hex: "#ffa07a", canonical: "orange" },
  orangeCustom: { hex: "#F19143", canonical: "orange" },
  orangePresetAccessible: { hex: "#a6510c", canonical: "orange" },
  orangeDark: { hex: "#ff8c00", canonical: "orange" },
  yellowLight: { hex: "#ffffe0", canonical: "yellow" },
  yellowDark: { hex: "#bdb76b", canonical: "yellow" },
  yellowgreen: { hex: "#9acd32", canonical: "green" },
  olive: { hex: "#808000", canonical: "green" },
  lime: { hex: "#00ff00", canonical: "green" },
  greenLight: { hex: "#90ee90", canonical: "green" },
  greenCustom: { hex: "#2ca02c", canonical: "green" },
  greenDark: { hex: "#006400", canonical: "green" },
  teal: { hex: "#008080", canonical: "green" },
  aqua: { hex: "#00ffff", canonical: "cyan" },
  cyanLight: { hex: "#e0ffff", canonical: "cyan" },
  cyanMid: { hex: "#25b0c0", canonical: "cyan" },
  cyanDark: { hex: "#008b8b", canonical: "cyan" },
  navy: { hex: "#000080", canonical: "blue" },
  blueMid: { hex: "#4477aa", canonical: "blue" },
  blueSky: { hex: "#56b4e9", canonical: "blue" },
  blueCustom: { hex: "#648FFF", canonical: "blue" },
  bluePresetAccessible: { hex: "#1f5dff", canonical: "blue" },
  blueLight: { hex: "#add8e6", canonical: "blue" },
  blueDark: { hex: "#00008b", canonical: "blue" },
  fuchsia: { hex: "#ff00ff", canonical: "purple" },
  purpleCustom: { hex: "#644CD6", canonical: "purple" },
  purpleMuted: { hex: "#9c5686", canonical: "purple" },
  purpleLight: { hex: "#dda0dd", canonical: "purple" },
  purpleDark: { hex: "#8b008b", canonical: "purple" },
  hotpink: { hex: "#ff69b4", canonical: "pink" },
  pinkDark: { hex: "#ff1493", canonical: "pink" },
  brownLight: { hex: "#deb887", canonical: "brown" },
  brownDark: { hex: "#8b4513", canonical: "brown" }
};
const anchorDefinitions = {
  ...canonicalAnchorDefinitions,
  ...extraAnchorDefinitions
};
Object.values(anchorDefinitions).map((definition) => ({
  color: w$1(definition.hex),
  canonical: definition.canonical
}));
/* @__PURE__ */ new Set([
  ...Object.keys(colorName),
  "transparent",
  "currentcolor"
]);
const DEFAULT_STYLE_VALUES = {
  lineOpacity: 0.7,
  lineWidth: 4,
  lineWidthWord: "thick",
  lineStyle: "solid",
  lineStyleWord: "",
  markerOpacity: 0.7,
  markerStyle: "circle",
  markerStyleWord: "point",
  markerSize: 5,
  markerFilled: true,
  fillOpacity: 0.3,
  fillPatternOpacity: 1,
  fillStyle: "solid",
  fillStyleWord: "",
  lineColor: "#1f5dff",
  lineColorDarkMode: "#648FFF",
  markerColor: "#1f5dff",
  markerColorDarkMode: "#648FFF",
  fillColor: "#1f5dff",
  fillColorDarkMode: "#648FFF",
  textColor: "black",
  textColorDarkMode: "white",
  highContrastColor: "#2963FF",
  highContrastColorDarkMode: "#92B4FF"
};
const defaultPalette = {
  name: "default",
  description: "The standard Doenet styles: blue, red, orange, purple, black, and gray.",
  styles: {
    1: { ...DEFAULT_STYLE_VALUES },
    2: {
      lineColor: "#D4042D",
      lineColorDarkMode: "#F1466A",
      lineOpacity: 0.7,
      lineWidth: 2,
      lineWidthWord: "",
      lineStyle: "solid",
      lineStyleWord: "",
      markerColor: "#D4042D",
      markerColorDarkMode: "#F1466A",
      markerOpacity: 0.7,
      markerStyle: "square",
      markerStyleWord: "square",
      markerSize: 5,
      fillColor: "#D4042D",
      fillColorDarkMode: "#F1466A",
      fillOpacity: 0.3,
      textColor: "#D4042D",
      textColorDarkMode: "#FF7A7A",
      highContrastColor: "#D4042D",
      highContrastColorDarkMode: "#FF7A7A"
    },
    3: {
      lineColor: "#a6510c",
      lineColorDarkMode: "#F19143",
      lineOpacity: 0.7,
      lineWidth: 3,
      lineWidthWord: "",
      lineStyle: "solid",
      lineStyleWord: "",
      markerColor: "#a6510c",
      markerColorDarkMode: "#F19143",
      markerOpacity: 0.7,
      markerStyle: "triangle",
      markerStyleWord: "triangle",
      markerSize: 5,
      fillColor: "#a6510c",
      fillColorDarkMode: "#F19143",
      fillOpacity: 0.3,
      textColor: "#BE5A0E",
      textColorDarkMode: "#FFA94D",
      highContrastColor: "#BE5A0E",
      highContrastColorDarkMode: "#FFA94D"
    },
    4: {
      lineColor: "#644CD6",
      lineColorDarkMode: "#9F8FE8",
      lineColorWordDarkMode: "purple",
      lineOpacity: 0.7,
      lineWidth: 2,
      lineWidthWord: "",
      lineStyle: "solid",
      lineStyleWord: "",
      markerColor: "#644CD6",
      markerColorDarkMode: "#9F8FE8",
      markerColorWordDarkMode: "purple",
      markerOpacity: 0.7,
      markerStyle: "diamond",
      markerStyleWord: "diamond",
      markerSize: 5,
      fillColor: "#644CD6",
      fillColorDarkMode: "#9F8FE8",
      fillColorWordDarkMode: "purple",
      fillOpacity: 0.3,
      textColor: "#644CD6",
      textColorDarkMode: "#B0A4EE",
      highContrastColor: "#644CD6",
      highContrastColorDarkMode: "#B0A4EE"
    },
    5: {
      lineColor: "black",
      lineColorDarkMode: "white",
      lineOpacity: 1,
      lineWidth: 1,
      lineWidthWord: "thin",
      lineStyle: "solid",
      lineStyleWord: "",
      markerColor: "black",
      markerColorDarkMode: "white",
      markerOpacity: 1,
      markerStyle: "circle",
      markerStyleWord: "point",
      markerSize: 5,
      fillColor: "black",
      fillColorDarkMode: "white",
      fillOpacity: 0.7,
      textColor: "black",
      textColorDarkMode: "white",
      highContrastColor: "black",
      highContrastColorDarkMode: "white"
    },
    6: {
      lineColor: "#636363",
      lineColorDarkMode: "#CCCCCC",
      lineOpacity: 0.7,
      lineWidth: 1,
      lineWidthWord: "thin",
      lineStyle: "dotted",
      lineStyleWord: "dotted",
      markerColor: "#636363",
      markerColorDarkMode: "#CCCCCC",
      markerOpacity: 0.7,
      markerStyle: "circle",
      markerStyleWord: "point",
      markerSize: 5,
      fillColor: "#636363",
      fillColorDarkMode: "#CCCCCC",
      fillOpacity: 0.3,
      textColor: "#757575",
      textColorDarkMode: "#B0B0B0",
      highContrastColor: "#757575",
      highContrastColorDarkMode: "#B0B0B0"
    }
  }
};
const okabeItoPalette = {
  name: "okabeIto",
  description: "Colorblind-friendly blues, oranges, greens, and purples adapted from the Okabe-Ito palette, with varied marker shapes and line styles.",
  styles: {
    1: {
      // blue (canonical #0072b2)
      lineColor: "#0072b2",
      markerColor: "#0072b2",
      fillColor: "#0072b2",
      highContrastColor: "#0072b2",
      lineColorDarkMode: "#0072b2",
      markerColorDarkMode: "#0072b2",
      fillColorDarkMode: "#0072b2",
      lineWidth: 4,
      markerStyle: "circle"
    },
    2: {
      // vermillion (canonical #d55e00, darkened for text contrast)
      lineColor: "#c15400",
      markerColor: "#c15400",
      fillColor: "#c15400",
      textColor: "#c15400",
      highContrastColor: "#c15400",
      lineColorDarkMode: "#d55e00",
      markerColorDarkMode: "#d55e00",
      fillColorDarkMode: "#d55e00",
      textColorDarkMode: "#d55e00",
      lineWidth: 2,
      markerStyle: "square"
    },
    3: {
      // bluish green (canonical #009e73)
      lineColor: "#009e73",
      markerColor: "#009e73",
      fillColor: "#009e73",
      textColor: "#008561",
      highContrastColor: "#008561",
      lineColorDarkMode: "#009e73",
      markerColorDarkMode: "#009e73",
      fillColorDarkMode: "#009e73",
      textColorDarkMode: "#009e73",
      lineWidth: 3,
      markerStyle: "triangle"
    },
    4: {
      // reddish purple (canonical #cc79a7)
      lineColor: "#cc79a7",
      markerColor: "#cc79a7",
      fillColor: "#cc79a7",
      textColor: "#a35f85",
      highContrastColor: "#a35f85",
      lineColorWord: "purple",
      markerColorWord: "purple",
      fillColorWord: "purple",
      lineColorDarkMode: "#cc79a7",
      markerColorDarkMode: "#cc79a7",
      fillColorDarkMode: "#cc79a7",
      textColorDarkMode: "#cc79a7",
      lineColorWordDarkMode: "purple",
      markerColorWordDarkMode: "purple",
      fillColorWordDarkMode: "purple",
      textColorWordDarkMode: "purple",
      highContrastColorWordDarkMode: "purple",
      lineWidth: 2,
      markerStyle: "diamond"
    },
    5: {
      // black (canonical #000000; inverted to white in dark mode)
      lineColor: "#000000",
      markerColor: "#000000",
      fillColor: "#000000",
      textColor: "#000000",
      highContrastColor: "#000000",
      lineColorDarkMode: "#ffffff",
      markerColorDarkMode: "#ffffff",
      fillColorDarkMode: "#ffffff",
      textColorDarkMode: "#ffffff",
      lineWidth: 2,
      markerStyle: "triangleDown"
    },
    6: {
      // yellow (canonical #f0e442, darkened to olive in light mode)
      lineColor: "#5a5513",
      markerColor: "#5a5513",
      fillColor: "#5a5513",
      textColor: "#5a5513",
      highContrastColor: "#5a5513",
      lineColorWord: "olive",
      markerColorWord: "olive",
      fillColorWord: "olive",
      textColorWord: "olive",
      highContrastColorWord: "olive",
      lineColorDarkMode: "#f0e442",
      markerColorDarkMode: "#f0e442",
      fillColorDarkMode: "#f0e442",
      textColorDarkMode: "#f0e442",
      lineWidth: 2,
      markerStyle: "triangleLeft"
    },
    7: {
      // sky blue (canonical #56b4e9, darkened for light mode)
      lineColor: "#499bca",
      markerColor: "#499bca",
      fillColor: "#499bca",
      textColor: "#397ba1",
      highContrastColor: "#397ba1",
      lineColorWord: "skyblue",
      markerColorWord: "skyblue",
      fillColorWord: "skyblue",
      textColorWord: "skyblue",
      highContrastColorWord: "skyblue",
      lineColorDarkMode: "#56b4e9",
      markerColorDarkMode: "#56b4e9",
      fillColorDarkMode: "#56b4e9",
      textColorDarkMode: "#56b4e9",
      lineColorWordDarkMode: "skyblue",
      markerColorWordDarkMode: "skyblue",
      fillColorWordDarkMode: "skyblue",
      textColorWordDarkMode: "skyblue",
      highContrastColorWordDarkMode: "skyblue",
      lineWidth: 3,
      lineStyle: "dashed",
      markerStyle: "triangleRight"
    },
    8: {
      // orange (canonical #e69f00, darkened for light mode)
      lineColor: "#c58700",
      markerColor: "#c58700",
      fillColor: "#c58700",
      textColor: "#9d6b00",
      highContrastColor: "#9d6b00",
      lineColorWord: "goldenrod",
      markerColorWord: "goldenrod",
      fillColorWord: "goldenrod",
      textColorWord: "goldenrod",
      highContrastColorWord: "goldenrod",
      lineColorDarkMode: "#e69f00",
      markerColorDarkMode: "#e69f00",
      fillColorDarkMode: "#e69f00",
      textColorDarkMode: "#e69f00",
      lineColorWordDarkMode: "goldenrod",
      markerColorWordDarkMode: "goldenrod",
      fillColorWordDarkMode: "goldenrod",
      textColorWordDarkMode: "goldenrod",
      highContrastColorWordDarkMode: "goldenrod",
      lineWidth: 1,
      lineStyle: "dotted",
      markerStyle: "circle"
    }
  }
};
const tolBrightPalette = {
  name: "tolBright",
  description: "Colorblind-friendly bright blues, reds, greens, and purples from Paul Tol's bright palette, with varied marker shapes and line styles.",
  styles: {
    1: {
      lineColor: "#4477aa",
      markerColor: "#4477aa",
      fillColor: "#4477aa",
      highContrastColor: "#4477aa",
      lineColorDarkMode: "#4477aa",
      markerColorDarkMode: "#4477aa",
      fillColorDarkMode: "#4477aa",
      lineWidth: 4,
      markerStyle: "circle"
    },
    2: {
      lineColor: "#ee6677",
      markerColor: "#ee6677",
      fillColor: "#ee6677",
      textColor: "#bf515e",
      highContrastColor: "#bf515e",
      lineColorDarkMode: "#ee6677",
      markerColorDarkMode: "#ee6677",
      fillColorDarkMode: "#ee6677",
      textColorDarkMode: "#ee6677",
      lineWidth: 2,
      markerStyle: "square"
    },
    3: {
      lineColor: "#228833",
      markerColor: "#228833",
      fillColor: "#228833",
      textColor: "#228833",
      highContrastColor: "#228833",
      lineColorDarkMode: "#228833",
      markerColorDarkMode: "#228833",
      fillColorDarkMode: "#228833",
      textColorDarkMode: "#3d8e47",
      lineWidth: 3,
      markerStyle: "triangle"
    },
    4: {
      lineColor: "#a29534",
      markerColor: "#a29534",
      fillColor: "#a29534",
      textColor: "#817628",
      highContrastColor: "#817628",
      lineColorWord: "olive",
      markerColorWord: "olive",
      fillColorWord: "olive",
      textColorWord: "olive",
      highContrastColorWord: "olive",
      lineColorDarkMode: "#ccbb44",
      markerColorDarkMode: "#ccbb44",
      fillColorDarkMode: "#ccbb44",
      textColorDarkMode: "#ccbb44",
      lineWidth: 2,
      markerStyle: "diamond"
    },
    5: {
      lineColor: "#4d9db8",
      markerColor: "#4d9db8",
      fillColor: "#4d9db8",
      textColor: "#3c7d94",
      highContrastColor: "#3c7d94",
      textColorWord: "cyan",
      highContrastColorWord: "cyan",
      lineColorDarkMode: "#66ccee",
      markerColorDarkMode: "#66ccee",
      fillColorDarkMode: "#66ccee",
      textColorDarkMode: "#66ccee",
      lineColorWordDarkMode: "cyan",
      markerColorWordDarkMode: "cyan",
      fillColorWordDarkMode: "cyan",
      textColorWordDarkMode: "cyan",
      highContrastColorWordDarkMode: "cyan",
      lineWidth: 2,
      markerStyle: "triangleDown"
    },
    6: {
      lineColor: "#aa3377",
      markerColor: "#aa3377",
      fillColor: "#aa3377",
      textColor: "#aa3377",
      highContrastColor: "#aa3377",
      lineColorDarkMode: "#aa3377",
      markerColorDarkMode: "#aa3377",
      fillColorDarkMode: "#aa3377",
      textColorDarkMode: "#b5648c",
      lineWidth: 2,
      markerStyle: "triangleLeft"
    },
    7: {
      lineColor: "#939393",
      markerColor: "#939393",
      fillColor: "#939393",
      textColor: "#757575",
      highContrastColor: "#757575",
      lineColorDarkMode: "#bbbbbb",
      markerColorDarkMode: "#bbbbbb",
      fillColorDarkMode: "#bbbbbb",
      textColorDarkMode: "#bbbbbb",
      lineWidth: 3,
      lineStyle: "dashed",
      markerStyle: "triangleRight"
    }
  }
};
const tolMutedPalette = {
  name: "tolMuted",
  description: "Nine colorblind-friendly muted styles from Paul Tol's muted palette, with varied marker shapes and line styles.",
  styles: {
    1: {
      lineColor: "#332288",
      markerColor: "#332288",
      fillColor: "#332288",
      highContrastColor: "#332288",
      lineColorWord: "indigo",
      markerColorWord: "indigo",
      fillColorWord: "indigo",
      highContrastColorWord: "indigo",
      lineColorDarkMode: "#8a86e8",
      markerColorDarkMode: "#8a86e8",
      fillColorDarkMode: "#8a86e8",
      lineColorWordDarkMode: "indigo",
      markerColorWordDarkMode: "indigo",
      fillColorWordDarkMode: "indigo",
      highContrastColorWordDarkMode: "indigo",
      lineWidth: 4,
      markerStyle: "circle"
    },
    2: {
      lineColor: "#669ab5",
      markerColor: "#669ab5",
      fillColor: "#669ab5",
      textColor: "#507a90",
      highContrastColor: "#507a90",
      lineColorDarkMode: "#88ccee",
      markerColorDarkMode: "#88ccee",
      fillColorDarkMode: "#88ccee",
      textColorDarkMode: "#88ccee",
      lineWidth: 2,
      markerStyle: "square"
    },
    3: {
      lineColor: "#41a393",
      markerColor: "#41a393",
      fillColor: "#41a393",
      textColor: "#328175",
      highContrastColor: "#328175",
      lineColorWord: "teal",
      markerColorWord: "teal",
      fillColorWord: "teal",
      textColorWord: "teal",
      highContrastColorWord: "teal",
      lineColorDarkMode: "#44aa99",
      markerColorDarkMode: "#44aa99",
      fillColorDarkMode: "#44aa99",
      textColorDarkMode: "#44aa99",
      lineColorWordDarkMode: "teal",
      markerColorWordDarkMode: "teal",
      fillColorWordDarkMode: "teal",
      textColorWordDarkMode: "teal",
      highContrastColorWordDarkMode: "teal",
      lineWidth: 3,
      markerStyle: "triangle"
    },
    4: {
      lineColor: "#117733",
      markerColor: "#117733",
      fillColor: "#117733",
      textColor: "#117733",
      highContrastColor: "#117733",
      lineColorDarkMode: "#117733",
      markerColorDarkMode: "#117733",
      fillColorDarkMode: "#117733",
      textColorDarkMode: "#548a5f",
      lineWidth: 2,
      markerStyle: "diamond"
    },
    5: {
      lineColor: "#989833",
      markerColor: "#989833",
      fillColor: "#989833",
      textColor: "#787827",
      highContrastColor: "#787827",
      lineColorWord: "olive",
      markerColorWord: "olive",
      fillColorWord: "olive",
      textColorWord: "olive",
      highContrastColorWord: "olive",
      lineColorDarkMode: "#999933",
      markerColorDarkMode: "#999933",
      fillColorDarkMode: "#999933",
      textColorDarkMode: "#999933",
      lineColorWordDarkMode: "olive",
      markerColorWordDarkMode: "olive",
      fillColorWordDarkMode: "olive",
      textColorWordDarkMode: "olive",
      highContrastColorWordDarkMode: "olive",
      lineWidth: 2,
      markerStyle: "triangleDown"
    },
    6: {
      lineColor: "#867c46",
      markerColor: "#867c46",
      fillColor: "#867c46",
      textColor: "#7f7542",
      highContrastColor: "#7f7542",
      lineColorWord: "tan",
      markerColorWord: "tan",
      fillColorWord: "tan",
      textColorWord: "tan",
      highContrastColorWord: "tan",
      lineColorDarkMode: "#ddcc77",
      markerColorDarkMode: "#ddcc77",
      fillColorDarkMode: "#ddcc77",
      textColorDarkMode: "#ddcc77",
      lineColorWordDarkMode: "tan",
      markerColorWordDarkMode: "tan",
      fillColorWordDarkMode: "tan",
      textColorWordDarkMode: "tan",
      highContrastColorWordDarkMode: "tan",
      lineWidth: 2,
      markerStyle: "triangleLeft"
    },
    7: {
      lineColor: "#cc6677",
      markerColor: "#cc6677",
      fillColor: "#cc6677",
      textColor: "#b35968",
      highContrastColor: "#b35968",
      lineColorWord: "pink",
      markerColorWord: "pink",
      fillColorWord: "pink",
      textColorWord: "pink",
      highContrastColorWord: "pink",
      lineColorDarkMode: "#cc6677",
      markerColorDarkMode: "#cc6677",
      fillColorDarkMode: "#cc6677",
      textColorDarkMode: "#cc6677",
      lineColorWordDarkMode: "pink",
      markerColorWordDarkMode: "pink",
      fillColorWordDarkMode: "pink",
      textColorWordDarkMode: "pink",
      highContrastColorWordDarkMode: "pink",
      lineWidth: 3,
      lineStyle: "dashed",
      markerStyle: "triangleRight"
    },
    8: {
      lineColor: "#882255",
      markerColor: "#882255",
      fillColor: "#882255",
      textColor: "#882255",
      highContrastColor: "#882255",
      lineColorWord: "maroon",
      markerColorWord: "maroon",
      fillColorWord: "maroon",
      textColorWord: "maroon",
      highContrastColorWord: "maroon",
      lineColorDarkMode: "#924b69",
      markerColorDarkMode: "#924b69",
      fillColorDarkMode: "#924b69",
      textColorDarkMode: "#a17083",
      lineColorWordDarkMode: "maroon",
      markerColorWordDarkMode: "maroon",
      fillColorWordDarkMode: "maroon",
      textColorWordDarkMode: "maroon",
      highContrastColorWordDarkMode: "maroon",
      lineWidth: 1,
      lineStyle: "dotted",
      markerStyle: "circle"
    },
    9: {
      lineColor: "#aa4499",
      markerColor: "#aa4499",
      fillColor: "#aa4499",
      textColor: "#aa4499",
      highContrastColor: "#aa4499",
      lineColorDarkMode: "#aa4499",
      markerColorDarkMode: "#aa4499",
      fillColorDarkMode: "#aa4499",
      textColorDarkMode: "#b262a3",
      lineWidth: 2,
      lineStyle: "dashed",
      markerStyle: "square"
    }
  }
};
const tolHighContrastPalette = {
  name: "tolHighContrast",
  description: "Four maximum-distinction styles from Paul Tol's high-contrast palette, distinguishable even in grayscale.",
  styles: {
    1: {
      lineColor: "#004488",
      markerColor: "#004488",
      fillColor: "#004488",
      highContrastColor: "#004488",
      lineColorDarkMode: "#496194",
      markerColorDarkMode: "#496194",
      fillColorDarkMode: "#496194",
      lineWidth: 4,
      markerStyle: "circle"
    },
    2: {
      lineColor: "#b88d29",
      markerColor: "#b88d29",
      fillColor: "#b88d29",
      textColor: "#92701f",
      highContrastColor: "#92701f",
      lineColorWord: "gold",
      markerColorWord: "gold",
      fillColorWord: "gold",
      textColorWord: "gold",
      highContrastColorWord: "gold",
      lineColorDarkMode: "#ddaa33",
      markerColorDarkMode: "#ddaa33",
      fillColorDarkMode: "#ddaa33",
      textColorDarkMode: "#ddaa33",
      lineColorWordDarkMode: "gold",
      markerColorWordDarkMode: "gold",
      fillColorWordDarkMode: "gold",
      textColorWordDarkMode: "gold",
      highContrastColorWordDarkMode: "gold",
      lineWidth: 2,
      markerStyle: "square"
    },
    3: {
      lineColor: "#bb5566",
      markerColor: "#bb5566",
      fillColor: "#bb5566",
      textColor: "#bb5566",
      highContrastColor: "#bb5566",
      lineColorDarkMode: "#c06976",
      markerColorDarkMode: "#c06976",
      fillColorDarkMode: "#c06976",
      textColorDarkMode: "#c06976",
      lineWidth: 3,
      markerStyle: "triangle"
    },
    4: {
      lineColor: "#000000",
      markerColor: "#000000",
      fillColor: "#000000",
      textColor: "#000000",
      highContrastColor: "#000000",
      lineColorDarkMode: "#ffffff",
      markerColorDarkMode: "#ffffff",
      fillColorDarkMode: "#ffffff",
      textColorDarkMode: "#ffffff",
      lineWidth: 2,
      markerStyle: "diamond"
    }
  }
};
const ibmPalette = {
  name: "ibm",
  description: "Five colorblind-friendly styles from the IBM Design Library accessible palette, with varied marker shapes.",
  styles: {
    1: {
      lineColor: "#648fff",
      markerColor: "#648fff",
      fillColor: "#648fff",
      highContrastColor: "#4f71cb",
      lineColorDarkMode: "#648fff",
      markerColorDarkMode: "#648fff",
      fillColorDarkMode: "#648fff",
      lineWidth: 4,
      markerStyle: "circle"
    },
    2: {
      lineColor: "#6a53d5",
      markerColor: "#6a53d5",
      fillColor: "#6a53d5",
      textColor: "#6a53d5",
      highContrastColor: "#6a53d5",
      lineColorDarkMode: "#785ef0",
      markerColorDarkMode: "#785ef0",
      fillColorDarkMode: "#785ef0",
      textColorDarkMode: "#7f69f0",
      lineWidth: 2,
      markerStyle: "square"
    },
    3: {
      lineColor: "#dc267f",
      markerColor: "#dc267f",
      fillColor: "#dc267f",
      textColor: "#dc267f",
      highContrastColor: "#dc267f",
      lineColorWord: "magenta",
      markerColorWord: "magenta",
      fillColorWord: "magenta",
      textColorWord: "magenta",
      highContrastColorWord: "magenta",
      lineColorDarkMode: "#dc267f",
      markerColorDarkMode: "#dc267f",
      fillColorDarkMode: "#dc267f",
      textColorDarkMode: "#dd4186",
      lineColorWordDarkMode: "magenta",
      markerColorWordDarkMode: "magenta",
      fillColorWordDarkMode: "magenta",
      textColorWordDarkMode: "magenta",
      highContrastColorWordDarkMode: "magenta",
      lineWidth: 3,
      markerStyle: "triangle"
    },
    4: {
      lineColor: "#fe6100",
      markerColor: "#fe6100",
      fillColor: "#fe6100",
      textColor: "#cb4b00",
      highContrastColor: "#cb4b00",
      lineColorDarkMode: "#fe6100",
      markerColorDarkMode: "#fe6100",
      fillColorDarkMode: "#fe6100",
      textColorDarkMode: "#fe6100",
      lineWidth: 2,
      markerStyle: "diamond"
    },
    5: {
      lineColor: "#865a00",
      markerColor: "#865a00",
      fillColor: "#865a00",
      textColor: "#865a00",
      highContrastColor: "#865a00",
      lineColorWord: "gold",
      markerColorWord: "gold",
      fillColorWord: "gold",
      textColorWord: "gold",
      highContrastColorWord: "gold",
      lineColorDarkMode: "#ffb000",
      markerColorDarkMode: "#ffb000",
      fillColorDarkMode: "#ffb000",
      textColorDarkMode: "#ffb000",
      lineColorWordDarkMode: "gold",
      markerColorWordDarkMode: "gold",
      fillColorWordDarkMode: "gold",
      textColorWordDarkMode: "gold",
      highContrastColorWordDarkMode: "gold",
      lineWidth: 2,
      markerStyle: "triangleDown"
    }
  }
};
const grayscalePalette = {
  name: "grayscale",
  description: "Four grays with maximum luminance separation, for readers who distinguish styles by lightness alone.",
  styles: {
    1: {
      lineColor: "#000000",
      markerColor: "#000000",
      fillColor: "#000000",
      highContrastColor: "#000000",
      lineColorDarkMode: "#ffffff",
      markerColorDarkMode: "#ffffff",
      fillColorDarkMode: "#ffffff",
      highContrastColorDarkMode: "#ffffff",
      lineWidth: 4,
      markerStyle: "circle"
    },
    2: {
      lineColor: "#323232",
      markerColor: "#323232",
      fillColor: "#323232",
      textColor: "#323232",
      highContrastColor: "#323232",
      lineColorWord: "dark gray",
      markerColorWord: "dark gray",
      fillColorWord: "dark gray",
      textColorWord: "dark gray",
      highContrastColorWord: "dark gray",
      lineColorDarkMode: "#c7c7c7",
      markerColorDarkMode: "#c7c7c7",
      fillColorDarkMode: "#c7c7c7",
      textColorDarkMode: "#c7c7c7",
      highContrastColorDarkMode: "#c7c7c7",
      lineColorWordDarkMode: "light gray",
      markerColorWordDarkMode: "light gray",
      fillColorWordDarkMode: "light gray",
      textColorWordDarkMode: "light gray",
      highContrastColorWordDarkMode: "light gray",
      lineWidth: 2,
      markerStyle: "square"
    },
    3: {
      lineColor: "#616161",
      markerColor: "#616161",
      fillColor: "#616161",
      textColor: "#616161",
      highContrastColor: "#616161",
      lineColorDarkMode: "#929292",
      markerColorDarkMode: "#929292",
      fillColorDarkMode: "#929292",
      textColorDarkMode: "#929292",
      highContrastColorDarkMode: "#929292",
      lineWidth: 3,
      markerStyle: "triangle"
    },
    4: {
      lineColor: "#939393",
      markerColor: "#939393",
      fillColor: "#939393",
      textColor: "#767676",
      highContrastColor: "#767676",
      lineColorWord: "light gray",
      markerColorWord: "light gray",
      fillColorWord: "light gray",
      textColorWord: "light gray",
      highContrastColorWord: "light gray",
      lineColorDarkMode: "#626262",
      markerColorDarkMode: "#626262",
      fillColorDarkMode: "#626262",
      textColorDarkMode: "#7d7d7d",
      highContrastColorDarkMode: "#7d7d7d",
      lineColorWordDarkMode: "dark gray",
      markerColorWordDarkMode: "dark gray",
      fillColorWordDarkMode: "dark gray",
      textColorWordDarkMode: "dark gray",
      highContrastColorWordDarkMode: "dark gray",
      lineWidth: 2,
      markerStyle: "diamond"
    }
  }
};
const categoricalPalette = {
  name: "categorical",
  description: "Ten maximally varied hues adapted from the Category10 sequence, for documents needing many obviously distinct styles.",
  styles: {
    1: {
      lineColor: "#1f77b4",
      markerColor: "#1f77b4",
      fillColor: "#1f77b4",
      highContrastColor: "#1f77b4",
      lineColorDarkMode: "#5ba3dd",
      markerColorDarkMode: "#5ba3dd",
      fillColorDarkMode: "#5ba3dd",
      lineWidth: 4,
      markerStyle: "circle"
    },
    2: {
      lineColor: "#e7730c",
      markerColor: "#e7730c",
      fillColor: "#e7730c",
      textColor: "#b95b07",
      highContrastColor: "#b95b07",
      lineColorDarkMode: "#ff9d3c",
      markerColorDarkMode: "#ff9d3c",
      fillColorDarkMode: "#ff9d3c",
      textColorDarkMode: "#ff9d3c",
      lineWidth: 2,
      markerStyle: "square"
    },
    3: {
      lineColor: "#217a21",
      markerColor: "#217a21",
      fillColor: "#217a21",
      textColor: "#217a21",
      highContrastColor: "#217a21",
      lineColorDarkMode: "#2ca02c",
      markerColorDarkMode: "#2ca02c",
      fillColorDarkMode: "#2ca02c",
      textColorDarkMode: "#2ca02c",
      lineWidth: 3,
      markerStyle: "triangle"
    },
    4: {
      lineColor: "#d62728",
      markerColor: "#d62728",
      fillColor: "#d62728",
      textColor: "#d62728",
      highContrastColor: "#d62728",
      lineColorDarkMode: "#e2494a",
      markerColorDarkMode: "#e2494a",
      fillColorDarkMode: "#e2494a",
      textColorDarkMode: "#e2494a",
      lineWidth: 2,
      markerStyle: "diamond"
    },
    5: {
      lineColor: "#9467bd",
      markerColor: "#9467bd",
      fillColor: "#9467bd",
      textColor: "#8e62b5",
      highContrastColor: "#8e62b5",
      lineColorDarkMode: "#b48fd6",
      markerColorDarkMode: "#b48fd6",
      fillColorDarkMode: "#b48fd6",
      textColorDarkMode: "#b48fd6",
      lineWidth: 2,
      markerStyle: "triangleDown"
    },
    6: {
      lineColor: "#7a4a41",
      markerColor: "#7a4a41",
      fillColor: "#7a4a41",
      textColor: "#7a4a41",
      highContrastColor: "#7a4a41",
      lineColorDarkMode: "#a9705f",
      markerColorDarkMode: "#a9705f",
      fillColorDarkMode: "#a9705f",
      textColorDarkMode: "#a9705f",
      lineColorWordDarkMode: "brown",
      markerColorWordDarkMode: "brown",
      fillColorWordDarkMode: "brown",
      textColorWordDarkMode: "brown",
      highContrastColorWordDarkMode: "brown",
      lineWidth: 2,
      markerStyle: "triangleLeft"
    },
    7: {
      lineColor: "#d670b7",
      markerColor: "#d670b7",
      fillColor: "#d670b7",
      textColor: "#ab5892",
      highContrastColor: "#ab5892",
      textColorWord: "pink",
      highContrastColorWord: "pink",
      lineColorDarkMode: "#e377c2",
      markerColorDarkMode: "#e377c2",
      fillColorDarkMode: "#e377c2",
      textColorDarkMode: "#e377c2",
      lineWidth: 3,
      lineStyle: "dashed",
      markerStyle: "triangleRight"
    },
    8: {
      lineColor: "#7f7f7f",
      markerColor: "#7f7f7f",
      fillColor: "#7f7f7f",
      textColor: "#757575",
      highContrastColor: "#757575",
      lineColorDarkMode: "#a8a8a8",
      markerColorDarkMode: "#a8a8a8",
      fillColorDarkMode: "#a8a8a8",
      textColorDarkMode: "#a8a8a8",
      lineWidth: 1,
      lineStyle: "dotted",
      markerStyle: "circle"
    },
    9: {
      lineColor: "#6e6e12",
      markerColor: "#6e6e12",
      fillColor: "#6e6e12",
      textColor: "#6e6e12",
      highContrastColor: "#6e6e12",
      lineColorWord: "olive",
      markerColorWord: "olive",
      fillColorWord: "olive",
      textColorWord: "olive",
      highContrastColorWord: "olive",
      lineColorDarkMode: "#bcbd22",
      markerColorDarkMode: "#bcbd22",
      fillColorDarkMode: "#bcbd22",
      textColorDarkMode: "#bcbd22",
      lineColorWordDarkMode: "olive",
      markerColorWordDarkMode: "olive",
      fillColorWordDarkMode: "olive",
      textColorWordDarkMode: "olive",
      highContrastColorWordDarkMode: "olive",
      lineWidth: 2,
      lineStyle: "dashed",
      markerStyle: "square"
    },
    10: {
      lineColor: "#12a2b1",
      markerColor: "#12a2b1",
      fillColor: "#12a2b1",
      textColor: "#0c818e",
      highContrastColor: "#0c818e",
      textColorWord: "cyan",
      highContrastColorWord: "cyan",
      lineColorDarkMode: "#17becf",
      markerColorDarkMode: "#17becf",
      fillColorDarkMode: "#17becf",
      textColorDarkMode: "#17becf",
      lineWidth: 2,
      lineStyle: "dotted",
      markerStyle: "diamond"
    }
  }
};
const grumpyNarwhalPalette = {
  name: "grumpyNarwhal",
  description: "Six saturated hues — burnt orange, forest green, deep pink, arctic teal, gold, and purple — going neon in dark mode.",
  styles: {
    1: {
      lineColor: "#b34700",
      markerColor: "#b34700",
      fillColor: "#b34700",
      highContrastColor: "#b34700",
      lineColorDarkMode: "#ff5f00",
      markerColorDarkMode: "#ff5f00",
      fillColorDarkMode: "#ff5f00",
      lineColorWordDarkMode: "orange",
      markerColorWordDarkMode: "orange",
      fillColorWordDarkMode: "orange",
      lineWidth: 4,
      markerStyle: "circle"
    },
    2: {
      lineColor: "#1b7a1b",
      markerColor: "#1b7a1b",
      fillColor: "#1b7a1b",
      textColor: "#1b7a1b",
      highContrastColor: "#1b7a1b",
      lineColorDarkMode: "#39ff14",
      markerColorDarkMode: "#39ff14",
      fillColorDarkMode: "#39ff14",
      textColorDarkMode: "#39ff14",
      lineWidth: 2,
      markerStyle: "square"
    },
    3: {
      lineColor: "#c20e67",
      markerColor: "#c20e67",
      fillColor: "#c20e67",
      textColor: "#c20e67",
      highContrastColor: "#c20e67",
      lineColorWord: "pink",
      markerColorWord: "pink",
      fillColorWord: "pink",
      textColorWord: "pink",
      highContrastColorWord: "pink",
      lineColorDarkMode: "#ff1493",
      markerColorDarkMode: "#ff1493",
      fillColorDarkMode: "#ff1493",
      textColorDarkMode: "#ff1493",
      lineWidth: 3,
      markerStyle: "triangle"
    },
    4: {
      lineColor: "#007a8c",
      markerColor: "#007a8c",
      fillColor: "#007a8c",
      textColor: "#007a8c",
      highContrastColor: "#007a8c",
      lineColorWord: "teal",
      markerColorWord: "teal",
      fillColorWord: "teal",
      textColorWord: "teal",
      highContrastColorWord: "teal",
      lineColorDarkMode: "#00e5ff",
      markerColorDarkMode: "#00e5ff",
      fillColorDarkMode: "#00e5ff",
      textColorDarkMode: "#00e5ff",
      lineWidth: 2,
      markerStyle: "diamond"
    },
    5: {
      lineColor: "#8a6d00",
      markerColor: "#8a6d00",
      fillColor: "#8a6d00",
      textColor: "#8a6d00",
      highContrastColor: "#8a6d00",
      lineColorWord: "gold",
      markerColorWord: "gold",
      fillColorWord: "gold",
      textColorWord: "gold",
      highContrastColorWord: "gold",
      lineColorDarkMode: "#ffea00",
      markerColorDarkMode: "#ffea00",
      fillColorDarkMode: "#ffea00",
      textColorDarkMode: "#ffea00",
      lineWidth: 2,
      markerStyle: "triangleDown"
    },
    6: {
      // Contributed as the same purple in both modes; on Doenet's
      // lighter dark canvas that lands at 4.07:1, so the dark text
      // variant is lightened to clear 4.5:1 while lines and markers
      // keep the contributed color.
      lineColor: "#b026ff",
      markerColor: "#b026ff",
      fillColor: "#b026ff",
      textColor: "#b026ff",
      highContrastColor: "#b026ff",
      lineColorDarkMode: "#b026ff",
      markerColorDarkMode: "#b026ff",
      fillColorDarkMode: "#b026ff",
      textColorDarkMode: "#b445ff",
      highContrastColorDarkMode: "#b445ff",
      lineWidth: 2,
      markerStyle: "triangleLeft"
    }
  }
};
function deepFreezePalette(palette) {
  for (const styleDef of Object.values(palette.styles)) {
    for (const value of Object.values(styleDef)) {
      if (typeof value === "object" && value !== null) {
        Object.freeze(value);
      }
    }
    Object.freeze(styleDef);
  }
  Object.freeze(palette.styles);
  return Object.freeze(palette);
}
function registerByKey(palette) {
  return [palette.name.toLowerCase(), deepFreezePalette(palette)];
}
const STYLE_PALETTES = Object.assign(
  /* @__PURE__ */ Object.create(null),
  Object.fromEntries(
    [
      defaultPalette,
      okabeItoPalette,
      tolBrightPalette,
      tolMutedPalette,
      tolHighContrastPalette,
      ibmPalette,
      grayscalePalette,
      categoricalPalette,
      grumpyNarwhalPalette
    ].map(registerByKey)
  )
);
Object.freeze(STYLE_PALETTES);
defaultPalette.name.toLowerCase();
Object.freeze(
  Object.keys(STYLE_PALETTES)
);
var o = function(o2) {
  var t22 = o2 / 255;
  return t22 < 0.04045 ? t22 / 12.92 : Math.pow((t22 + 0.055) / 1.055, 2.4);
}, t = function(t22) {
  return 0.2126 * o(t22.r) + 0.7152 * o(t22.g) + 0.0722 * o(t22.b);
};
function a11yPlugin(o2) {
  o2.prototype.luminance = function() {
    return o3 = t(this.rgba), void 0 === (r2 = 2) && (r2 = 0), void 0 === n2 && (n2 = Math.pow(10, r2)), Math.round(n2 * o3) / n2 + 0;
    var o3, r2, n2;
  }, o2.prototype.contrast = function(r2) {
    void 0 === r2 && (r2 = "#FFF");
    var n2, a2, i2, e2, v2, u2, d2, c2 = r2 instanceof o2 ? r2 : new o2(r2);
    return e2 = this.rgba, v2 = c2.toRgb(), u2 = t(e2), d2 = t(v2), n2 = u2 > d2 ? (u2 + 0.05) / (d2 + 0.05) : (d2 + 0.05) / (u2 + 0.05), void 0 === (a2 = 2) && (a2 = 0), void 0 === i2 && (i2 = Math.pow(10, a2)), Math.floor(i2 * n2) / i2 + 0;
  }, o2.prototype.isReadable = function(o3, t22) {
    return void 0 === o3 && (o3 = "#FFF"), void 0 === t22 && (t22 = {}), this.contrast(o3) >= (e2 = void 0 === (i2 = (r2 = t22).size) ? "normal" : i2, "AAA" === (a2 = void 0 === (n2 = r2.level) ? "AA" : n2) && "normal" === e2 ? 7 : "AA" === a2 && "large" === e2 ? 3 : 4.5);
    var r2, n2, a2, i2, e2;
  };
}
function namesPlugin(e2, f2) {
  var a2 = { white: "#ffffff", bisque: "#ffe4c4", blue: "#0000ff", cadetblue: "#5f9ea0", chartreuse: "#7fff00", chocolate: "#d2691e", coral: "#ff7f50", antiquewhite: "#faebd7", aqua: "#00ffff", azure: "#f0ffff", whitesmoke: "#f5f5f5", papayawhip: "#ffefd5", plum: "#dda0dd", blanchedalmond: "#ffebcd", black: "#000000", gold: "#ffd700", goldenrod: "#daa520", gainsboro: "#dcdcdc", cornsilk: "#fff8dc", cornflowerblue: "#6495ed", burlywood: "#deb887", aquamarine: "#7fffd4", beige: "#f5f5dc", crimson: "#dc143c", cyan: "#00ffff", darkblue: "#00008b", darkcyan: "#008b8b", darkgoldenrod: "#b8860b", darkkhaki: "#bdb76b", darkgray: "#a9a9a9", darkgreen: "#006400", darkgrey: "#a9a9a9", peachpuff: "#ffdab9", darkmagenta: "#8b008b", darkred: "#8b0000", darkorchid: "#9932cc", darkorange: "#ff8c00", darkslateblue: "#483d8b", gray: "#808080", darkslategray: "#2f4f4f", darkslategrey: "#2f4f4f", deeppink: "#ff1493", deepskyblue: "#00bfff", wheat: "#f5deb3", firebrick: "#b22222", floralwhite: "#fffaf0", ghostwhite: "#f8f8ff", darkviolet: "#9400d3", magenta: "#ff00ff", green: "#008000", dodgerblue: "#1e90ff", grey: "#808080", honeydew: "#f0fff0", hotpink: "#ff69b4", blueviolet: "#8a2be2", forestgreen: "#228b22", lawngreen: "#7cfc00", indianred: "#cd5c5c", indigo: "#4b0082", fuchsia: "#ff00ff", brown: "#a52a2a", maroon: "#800000", mediumblue: "#0000cd", lightcoral: "#f08080", darkturquoise: "#00ced1", lightcyan: "#e0ffff", ivory: "#fffff0", lightyellow: "#ffffe0", lightsalmon: "#ffa07a", lightseagreen: "#20b2aa", linen: "#faf0e6", mediumaquamarine: "#66cdaa", lemonchiffon: "#fffacd", lime: "#00ff00", khaki: "#f0e68c", mediumseagreen: "#3cb371", limegreen: "#32cd32", mediumspringgreen: "#00fa9a", lightskyblue: "#87cefa", lightblue: "#add8e6", midnightblue: "#191970", lightpink: "#ffb6c1", mistyrose: "#ffe4e1", moccasin: "#ffe4b5", mintcream: "#f5fffa", lightslategray: "#778899", lightslategrey: "#778899", navajowhite: "#ffdead", navy: "#000080", mediumvioletred: "#c71585", powderblue: "#b0e0e6", palegoldenrod: "#eee8aa", oldlace: "#fdf5e6", paleturquoise: "#afeeee", mediumturquoise: "#48d1cc", mediumorchid: "#ba55d3", rebeccapurple: "#663399", lightsteelblue: "#b0c4de", mediumslateblue: "#7b68ee", thistle: "#d8bfd8", tan: "#d2b48c", orchid: "#da70d6", mediumpurple: "#9370db", purple: "#800080", pink: "#ffc0cb", skyblue: "#87ceeb", springgreen: "#00ff7f", palegreen: "#98fb98", red: "#ff0000", yellow: "#ffff00", slateblue: "#6a5acd", lavenderblush: "#fff0f5", peru: "#cd853f", palevioletred: "#db7093", violet: "#ee82ee", teal: "#008080", slategray: "#708090", slategrey: "#708090", aliceblue: "#f0f8ff", darkseagreen: "#8fbc8f", darkolivegreen: "#556b2f", greenyellow: "#adff2f", seagreen: "#2e8b57", seashell: "#fff5ee", tomato: "#ff6347", silver: "#c0c0c0", sienna: "#a0522d", lavender: "#e6e6fa", lightgreen: "#90ee90", orange: "#ffa500", orangered: "#ff4500", steelblue: "#4682b4", royalblue: "#4169e1", turquoise: "#40e0d0", yellowgreen: "#9acd32", salmon: "#fa8072", saddlebrown: "#8b4513", sandybrown: "#f4a460", rosybrown: "#bc8f8f", darksalmon: "#e9967a", lightgoldenrodyellow: "#fafad2", snow: "#fffafa", lightgrey: "#d3d3d3", lightgray: "#d3d3d3", dimgray: "#696969", dimgrey: "#696969", olivedrab: "#6b8e23", olive: "#808000" }, r2 = {};
  for (var d2 in a2) r2[a2[d2]] = d2;
  var l2 = {};
  e2.prototype.toName = function(f3) {
    if (!(this.rgba.a || this.rgba.r || this.rgba.g || this.rgba.b)) return "transparent";
    var d3, i2, n2 = r2[this.toHex()];
    if (n2) return n2;
    if (null == f3 ? void 0 : f3.closest) {
      var o2 = this.toRgb(), t22 = 1 / 0, b2 = "black";
      if (!l2.length) for (var c2 in a2) l2[c2] = new e2(a2[c2]).toRgb();
      for (var g2 in a2) {
        var u2 = (d3 = o2, i2 = l2[g2], Math.pow(d3.r - i2.r, 2) + Math.pow(d3.g - i2.g, 2) + Math.pow(d3.b - i2.b, 2));
        u2 < t22 && (t22 = u2, b2 = g2);
      }
      return b2;
    }
  };
  f2.string.push([function(f3) {
    var r3 = f3.toLowerCase(), d3 = "transparent" === r3 ? "#0000" : a2[r3];
    return d3 ? new e2(d3).toRgb() : null;
  }, "name"]);
}
k([a11yPlugin, namesPlugin]);
let styleAttributes = {
  lineColor: {
    componentType: "text",
    description: "Color used for line strokes (light mode)."
  },
  lineColorWord: {
    componentType: "text",
    description: "Human-readable name of the line color (light mode)."
  },
  lineColorDarkMode: {
    componentType: "text",
    description: "Color used for line strokes (dark mode)."
  },
  lineColorWordDarkMode: {
    componentType: "text",
    description: "Human-readable name of the line color (dark mode)."
  },
  lineOpacity: {
    componentType: "number",
    description: "Opacity of line strokes, 0 to 1."
  },
  lineWidth: {
    componentType: "number",
    description: "Stroke width for lines, in pixels."
  },
  lineWidthWord: {
    componentType: "text",
    description: "Human-readable name of the line width."
  },
  lineStyle: {
    componentType: "text",
    description: "Stroke style for lines.",
    toLowerCase: true,
    validValues: [
      { value: "solid", description: "Continuous, unbroken stroke." },
      {
        value: "dashed",
        description: "Stroke composed of evenly-spaced dashes."
      },
      {
        value: "dotted",
        description: "Stroke composed of evenly-spaced dots."
      }
    ]
  },
  lineStyleWord: {
    componentType: "text",
    description: "Human-readable name of the line style."
  },
  markerColor: {
    componentType: "text",
    description: "Color used for markers/points (light mode)."
  },
  markerColorWord: {
    componentType: "text",
    description: "Human-readable name of the marker color (light mode)."
  },
  markerColorDarkMode: {
    componentType: "text",
    description: "Color used for markers/points (dark mode)."
  },
  markerColorWordDarkMode: {
    componentType: "text",
    description: "Human-readable name of the marker color (dark mode)."
  },
  markerOpacity: {
    componentType: "number",
    description: "Opacity of markers/points, 0 to 1."
  },
  markerStyle: {
    componentType: "text",
    description: "Marker shape.",
    toLowerCase: true,
    validValues: [
      { value: "circle", description: "Circular marker." },
      { value: "square", description: "Square marker." },
      {
        value: "triangle",
        description: 'Triangular marker pointing up (alias for "triangleUp").'
      },
      {
        value: "triangleUp",
        description: "Triangular marker pointing up."
      },
      {
        value: "triangleDown",
        description: "Triangular marker pointing down."
      },
      {
        value: "triangleLeft",
        description: "Triangular marker pointing left."
      },
      {
        value: "triangleRight",
        description: "Triangular marker pointing right."
      },
      { value: "diamond", description: "Diamond-shaped marker." },
      { value: "cross", description: "Cross-shaped marker (×)." },
      { value: "plus", description: "Plus-shaped marker (+)." }
    ]
  },
  markerStyleWord: {
    componentType: "text",
    description: "Human-readable name of the marker style."
  },
  markerSize: {
    componentType: "number",
    description: "Marker size in pixels."
  },
  markerFilled: {
    componentType: "boolean",
    description: "Whether the marker is rendered filled (true) or open (false). Has no effect when markerStyle is cross or plus, which have no interior."
  },
  fillColor: {
    componentType: "text",
    description: "Fill color used inside closed shapes (light mode)."
  },
  fillColorWord: {
    componentType: "text",
    description: "Human-readable name of the fill color (light mode)."
  },
  fillColorDarkMode: {
    componentType: "text",
    description: "Fill color used inside closed shapes (dark mode)."
  },
  fillColorWordDarkMode: {
    componentType: "text",
    description: "Human-readable name of the fill color (dark mode)."
  },
  fillOpacity: {
    componentType: "number",
    description: "Opacity of solid fills, 0 to 1."
  },
  fillPatternOpacity: {
    componentType: "number",
    description: "Opacity of pattern fills (non-solid fillStyle), 0 to 1."
  },
  fillStyle: {
    componentType: "text",
    description: "Fill pattern used inside closed shapes.",
    toLowerCase: true,
    validValues: [
      { value: "solid", description: "Solid fill." },
      {
        value: "horizontal",
        description: "Horizontal line pattern."
      },
      {
        value: "vertical",
        description: "Vertical line pattern."
      },
      {
        value: "diagonal",
        description: "Diagonal line pattern (/)."
      },
      {
        value: "backDiagonal",
        description: "Back-diagonal line pattern (\\)."
      },
      {
        value: "dots",
        description: "Dots pattern."
      },
      {
        value: "diamonds",
        description: "Filled diamonds pattern."
      }
    ]
  },
  fillStyleWord: {
    componentType: "text",
    description: "Human-readable name of the fill style."
  },
  textColor: {
    componentType: "text",
    description: "Text color (light mode)."
  },
  textColorWord: {
    componentType: "text",
    description: "Human-readable name of the text color (light mode)."
  },
  textColorDarkMode: {
    componentType: "text",
    description: "Text color (dark mode)."
  },
  textColorWordDarkMode: {
    componentType: "text",
    description: "Human-readable name of the text color (dark mode)."
  },
  highContrastColor: {
    componentType: "text",
    description: "High-contrast accent color (light mode)."
  },
  highContrastColorWord: {
    componentType: "text",
    description: "Human-readable name of the high-contrast accent color (light mode)."
  },
  highContrastColorDarkMode: {
    componentType: "text",
    description: "High-contrast accent color (dark mode)."
  },
  highContrastColorWordDarkMode: {
    componentType: "text",
    description: "Human-readable name of the high-contrast accent color (dark mode)."
  },
  backgroundColor: {
    componentType: "text",
    description: "Background color (light mode)."
  },
  backgroundColorWord: {
    componentType: "text",
    description: "Human-readable name of the background color (light mode)."
  },
  backgroundColorDarkMode: {
    componentType: "text",
    description: "Background color (dark mode)."
  },
  backgroundColorWordDarkMode: {
    componentType: "text",
    description: "Human-readable name of the background color (dark mode)."
  }
};
styleAttributes.markerStyle.validValues.filter(
  (entry) => entry.value !== "cross" && entry.value !== "plus"
);
const MARKER_OVERRIDE_KEYS = [
  "markerStyle",
  "markerSize",
  "markerFilled"
];
const LINE_OVERRIDE_KEYS = [
  "lineStyle",
  "lineWidth"
];
const FILL_OVERRIDE_KEYS = [
  "fillOpacity",
  "fillPatternOpacity",
  "fillStyle"
];
const markerOverrideAttributes = Object.fromEntries(
  MARKER_OVERRIDE_KEYS.map((key) => [key, styleAttributes[key]])
);
const lineOverrideAttributes = Object.fromEntries(
  LINE_OVERRIDE_KEYS.map((key) => [key, styleAttributes[key]])
);
const fillOverrideAttributes = Object.fromEntries(
  FILL_OVERRIDE_KEYS.map((key) => [key, styleAttributes[key]])
);
({
  ...markerOverrideAttributes,
  ...lineOverrideAttributes,
  ...fillOverrideAttributes
});
const STYLE_OVERRIDE_CATEGORIES = {
  marker: markerOverrideAttributes,
  line: lineOverrideAttributes,
  fill: fillOverrideAttributes
};
for (const [category, group2] of Object.entries(STYLE_OVERRIDE_CATEGORIES)) {
  for (const [key, spec] of Object.entries(group2)) {
    if (key.toLowerCase().includes("color")) {
      throw new Error(
        `Style override category "${category}" contains color-related key "${key}"; colors stay <styleDefinition>-only so per-styleNumber WCAG contrast diagnostics remain authoritative. If this is intentional, also reconcile the lowercase asymmetry in returnSelectedStyleStateVariableDefinition.`
      );
    }
    if (key.endsWith("Opacity") && key !== "fillOpacity" && key !== "fillPatternOpacity") {
      throw new Error(
        `Style override category "${category}" contains contrast-feeding opacity key "${key}"; lineOpacity and markerOpacity feed the WCAG contrast diagnostic as an opacityMultiplier on the foreground alpha, so they stay <styleDefinition>-only alongside colors. Only fillOpacity and fillPatternOpacity are contrast-irrelevant and overridable.`
      );
    }
    if (spec.componentType === "text" && !spec.toLowerCase) {
      throw new Error(
        `Style override key "${key}" in category "${category}" is text-typed but missing toLowerCase: true. Add toLowerCase: true to its styleAttributes entry, or move it out of the override surface.`
      );
    }
  }
}
Object.entries(styleAttributes).filter(([, spec]) => spec.toLowerCase).map(([key]) => key);
var __create = Object.create, __defProp = Object.defineProperty, __getOwnPropDesc = Object.getOwnPropertyDescriptor, __getOwnPropNames = Object.getOwnPropertyNames, __getProtoOf = Object.getPrototypeOf, __hasOwnProp = Object.prototype.hasOwnProperty, __esmMin = (t4, n2) => () => (t4 && (n2 = t4(t4 = 0)), n2), __commonJSMin = (t4, n2) => () => (n2 || (t4((n2 = { exports: {} }).exports, n2), t4 = null), n2.exports), __exportAll = (t4, n2) => {
  let r2 = {};
  for (var i2 in t4) __defProp(r2, i2, { get: t4[i2], enumerable: true });
  return __defProp(r2, Symbol.toStringTag, { value: `Module` }), r2;
}, __copyProps = (t4, n2, r2, i2) => {
  if (n2 && typeof n2 == `object` || typeof n2 == `function`) for (var a2 = __getOwnPropNames(n2), o2 = 0, s2 = a2.length, c2; o2 < s2; o2++) c2 = a2[o2], !__hasOwnProp.call(t4, c2) && c2 !== r2 && __defProp(t4, c2, { get: ((t5) => n2[t5]).bind(null, c2), enumerable: !(i2 = __getOwnPropDesc(n2, c2)) || i2.enumerable });
  return t4;
}, __toESM = (t4, n2, r2) => (r2 = t4 == null ? {} : __create(__getProtoOf(t4)), __copyProps(__defProp(r2, `default`, { value: t4, enumerable: true }), t4)), __toCommonJS = (t4) => __hasOwnProp.call(t4, `module.exports`) ? t4[`module.exports`] : __copyProps(__defProp({}, `__esModule`, { value: true }), t4), get_tree = function(t4) {
  if (t4 != null) return t4.tree === void 0 ? t4 : t4.tree;
};
function* subsets(t4, n2) {
  var r2 = t4.length;
  if (n2 === void 0 && (n2 = r2), n2 !== 0) {
    for (let n3 = 0; n3 < r2; n3++) yield [t4[n3]];
    if (n2 !== 1) for (let i2 = 0; i2 < r2; i2++) {
      let r3 = subsets(t4.slice(i2 + 1), n2 - 1);
      for (let n3 of r3) yield [t4[i2]].concat(n3);
    }
  }
}
var is_associative$1 = { "+": true, "*": true, and: true, or: true, union: true, intersect: true };
function flatten$5(t4) {
  var n2 = get_tree(t4);
  if (!Array.isArray(n2)) return n2;
  var r2 = n2[0], i2 = n2.slice(1);
  if (i2 = i2.map(function(t5, n3) {
    return flatten$5(t5);
  }), is_associative$1[r2]) {
    for (var a2 = [], o2 = 0; o2 < i2.length; o2++) Array.isArray(i2[o2]) && i2[o2][0] === r2 && i2[o2].length > 2 ? a2 = a2.concat(i2[o2].slice(1)) : a2.push(i2[o2]);
    i2 = a2;
  }
  return [r2].concat(i2);
}
var unflattenRight = function(t4) {
  var n2 = get_tree(t4);
  if (!Array.isArray(n2)) return n2;
  var r2 = n2[0], i2 = n2.slice(1);
  if (i2 = i2.map(function(t5, n3) {
    return unflattenRight(t5);
  }), i2.length > 2 && is_associative$1[r2]) {
    for (var a2 = [r2, i2[0], void 0], o2 = a2, s2 = 1; s2 < i2.length - 1; s2++) o2[2] = [r2, i2[s2], void 0], o2 = o2[2];
    return o2[2] = i2[i2.length - 1], a2;
  }
  return [r2].concat(i2);
}, unflattenLeft = function(t4) {
  var n2 = get_tree(t4);
  if (!Array.isArray(n2)) return n2;
  var r2 = n2[0], i2 = n2.slice(1);
  if (i2 = i2.map(function(t5, n3) {
    return unflattenLeft(t5);
  }), i2.length > 2 && is_associative$1[r2]) {
    for (var a2 = [r2, void 0, i2[i2.length - 1]], o2 = a2, s2 = i2.length - 2; s2 > 0; s2--) o2[1] = [r2, void 0, i2[s2]], o2 = o2[1];
    return o2[1] = i2[0], a2;
  }
  return [r2].concat(i2);
}, allChildren = function(t4) {
  if (!Array.isArray(t4)) return [];
  var n2 = t4[0], r2 = t4.slice(1);
  return is_associative$1[n2] ? r2.reduce(function(t5, r3) {
    return Array.isArray(r3) && r3[0] === n2 ? t5.concat(allChildren(r3)) : t5.concat([r3]);
  }, []) : r2;
};
function _extends() {
  return _extends = Object.assign ? Object.assign.bind() : function(t4) {
    for (var n2 = 1; n2 < arguments.length; n2++) {
      var r2 = arguments[n2];
      for (var i2 in r2) ({}).hasOwnProperty.call(r2, i2) && (t4[i2] = r2[i2]);
    }
    return t4;
  }, _extends.apply(null, arguments);
}
var DEFAULT_CONFIG = { relTol: 1e-12, absTol: 1e-15, matrix: `Matrix`, number: `number`, numberFallback: `number`, precision: 64, predictable: false, randomSeed: null, legacySubset: false };
function getSafeProperty(t4, n2) {
  if (isSafeObjectProperty(t4, n2) || isSafeArrayProperty(t4, n2)) return t4[n2];
  throw isSafeMethod(t4, n2) ? Error(`Cannot access method "${n2}" as a property`) : t4 == null ? TypeError(`Cannot access property "${n2}": object is ${t4}`) : Error(`No access to property "` + n2 + `"`);
}
function setSafeProperty(t4, n2, r2) {
  if (isSafeObjectProperty(t4, n2) || isSafeArrayProperty(t4, n2)) return t4[n2] = r2, r2;
  throw Error(`No access to property "${n2}"`);
}
function isSafeObjectProperty(t4, n2) {
  return isPlainObject(t4) ? !(n2 in Object.prototype) : false;
}
function isSafeArrayProperty(t4, n2) {
  return Array.isArray(t4) ? typeof n2 == `number` || typeof n2 == `string` && isInteger$1(n2) || n2 === `length` : false;
}
function isInteger$1(t4) {
  return /^\d+$/.test(t4);
}
function getSafeMethod(t4, n2) {
  if (!isSafeMethod(t4, n2)) throw Error(`No access to method "` + n2 + `"`);
  return t4[n2];
}
function isSafeMethod(t4, n2) {
  return t4 == null || typeof t4[n2] != `function` || hasOwnProperty$1(t4, n2) && Object.getPrototypeOf && n2 in Object.getPrototypeOf(t4) ? false : safeNativeMethods.has(n2) ? true : !(n2 in Object.prototype || n2 in Function.prototype);
}
function isPlainObject(t4) {
  return typeof t4 == `object` && t4 && t4.constructor === Object;
}
var safeNativeMethods = /* @__PURE__ */ new Set([`toString`, `valueOf`, `toLocaleString`]), ObjectWrappingMap = class {
  constructor(t4) {
    this.wrappedObject = t4, this[Symbol.iterator] = this.entries;
  }
  keys() {
    return Object.keys(this.wrappedObject).filter((t4) => this.has(t4)).values();
  }
  get(t4) {
    return getSafeProperty(this.wrappedObject, t4);
  }
  set(t4, n2) {
    return setSafeProperty(this.wrappedObject, t4, n2), this;
  }
  has(t4) {
    return isSafeObjectProperty(this.wrappedObject, t4) && t4 in this.wrappedObject;
  }
  entries() {
    return mapIterator(this.keys(), (t4) => [t4, this.get(t4)]);
  }
  forEach(t4) {
    for (var n2 of this.keys()) t4(this.get(n2), n2, this);
  }
  delete(t4) {
    isSafeObjectProperty(this.wrappedObject, t4) && delete this.wrappedObject[t4];
  }
  clear() {
    for (var t4 of this.keys()) this.delete(t4);
  }
  get size() {
    return Object.keys(this.wrappedObject).length;
  }
}, PartitionedMap = class {
  constructor(t4, n2, r2) {
    this.a = t4, this.b = n2, this.bKeys = r2, this[Symbol.iterator] = this.entries;
  }
  get(t4) {
    return this.bKeys.has(t4) ? this.b.get(t4) : this.a.get(t4);
  }
  set(t4, n2) {
    return this.bKeys.has(t4) ? this.b.set(t4, n2) : this.a.set(t4, n2), this;
  }
  has(t4) {
    return this.b.has(t4) || this.a.has(t4);
  }
  keys() {
    return (/* @__PURE__ */ new Set([...this.a.keys(), ...this.b.keys()]))[Symbol.iterator]();
  }
  entries() {
    return mapIterator(this.keys(), (t4) => [t4, this.get(t4)]);
  }
  forEach(t4) {
    for (var n2 of this.keys()) t4(this.get(n2), n2, this);
  }
  delete(t4) {
    return this.bKeys.has(t4) ? this.b.delete(t4) : this.a.delete(t4);
  }
  clear() {
    this.a.clear(), this.b.clear();
  }
  get size() {
    return [...this.keys()].length;
  }
};
function mapIterator(t4, n2) {
  return { next: () => {
    var r2 = t4.next();
    return r2.done ? r2 : { value: n2(r2.value), done: false };
  } };
}
function createEmptyMap() {
  return /* @__PURE__ */ new Map();
}
function createMap$1(t4) {
  if (!t4) return createEmptyMap();
  if (isMap(t4)) return t4;
  if (isObject$1(t4)) return new ObjectWrappingMap(t4);
  throw Error(`createMap can create maps from objects or Maps`);
}
function toObject(t4) {
  if (t4 instanceof ObjectWrappingMap) return t4.wrappedObject;
  var n2 = {};
  for (var r2 of t4.keys()) setSafeProperty(n2, r2, t4.get(r2));
  return n2;
}
function isNumber(t4) {
  return typeof t4 == `number`;
}
function isBigNumber(t4) {
  return !t4 || typeof t4 != `object` || typeof t4.constructor != `function` ? false : t4.isBigNumber === true && typeof t4.constructor.prototype == `object` && t4.constructor.prototype.isBigNumber === true || typeof t4.constructor.isDecimal == `function` && t4.constructor.isDecimal(t4) === true;
}
function isBigInt(t4) {
  return typeof t4 == `bigint`;
}
function isComplex(t4) {
  return t4 && typeof t4 == `object` && Object.getPrototypeOf(t4).isComplex === true || false;
}
function isFraction(t4) {
  return t4 && typeof t4 == `object` && Object.getPrototypeOf(t4).isFraction === true || false;
}
function isUnit(t4) {
  return t4 && t4.constructor.prototype.isUnit === true || false;
}
function isString(t4) {
  return typeof t4 == `string`;
}
var isArray = Array.isArray;
function isMatrix(t4) {
  return t4 && t4.constructor.prototype.isMatrix === true || false;
}
function isCollection(t4) {
  return Array.isArray(t4) || isMatrix(t4);
}
function isDenseMatrix(t4) {
  return t4 && t4.isDenseMatrix && t4.constructor.prototype.isMatrix === true || false;
}
function isSparseMatrix(t4) {
  return t4 && t4.isSparseMatrix && t4.constructor.prototype.isMatrix === true || false;
}
function isRange(t4) {
  return t4 && t4.constructor.prototype.isRange === true || false;
}
function isIndex(t4) {
  return t4 && t4.constructor.prototype.isIndex === true || false;
}
function isBoolean$1(t4) {
  return typeof t4 == `boolean`;
}
function isResultSet(t4) {
  return t4 && t4.constructor.prototype.isResultSet === true || false;
}
function isHelp(t4) {
  return t4 && t4.constructor.prototype.isHelp === true || false;
}
function isFunction$1(t4) {
  return typeof t4 == `function`;
}
function isDate(t4) {
  return t4 instanceof Date;
}
function isRegExp(t4) {
  return t4 instanceof RegExp;
}
function isObject$1(t4) {
  return !!(t4 && typeof t4 == `object` && t4.constructor === Object && !isComplex(t4) && !isFraction(t4));
}
function isMap(t4) {
  return t4 ? t4 instanceof Map || t4 instanceof ObjectWrappingMap || typeof t4.set == `function` && typeof t4.get == `function` && typeof t4.keys == `function` && typeof t4.has == `function` : false;
}
function isPartitionedMap(t4) {
  return isMap(t4) && isMap(t4.a) && isMap(t4.b);
}
function isObjectWrappingMap(t4) {
  return isMap(t4) && isObject$1(t4.wrappedObject);
}
function isNull$1(t4) {
  return t4 === null;
}
function isUndefined$1(t4) {
  return t4 === void 0;
}
function isAccessorNode(t4) {
  return t4 && t4.isAccessorNode === true && t4.constructor.prototype.isNode === true || false;
}
function isArrayNode(t4) {
  return t4 && t4.isArrayNode === true && t4.constructor.prototype.isNode === true || false;
}
function isAssignmentNode(t4) {
  return t4 && t4.isAssignmentNode === true && t4.constructor.prototype.isNode === true || false;
}
function isBlockNode(t4) {
  return t4 && t4.isBlockNode === true && t4.constructor.prototype.isNode === true || false;
}
function isConditionalNode(t4) {
  return t4 && t4.isConditionalNode === true && t4.constructor.prototype.isNode === true || false;
}
function isConstantNode(t4) {
  return t4 && t4.isConstantNode === true && t4.constructor.prototype.isNode === true || false;
}
function rule2Node(t4) {
  return isConstantNode(t4) || isOperatorNode(t4) && t4.args.length === 1 && isConstantNode(t4.args[0]) && `-+~`.includes(t4.op);
}
function isFunctionAssignmentNode(t4) {
  return t4 && t4.isFunctionAssignmentNode === true && t4.constructor.prototype.isNode === true || false;
}
function isFunctionNode(t4) {
  return t4 && t4.isFunctionNode === true && t4.constructor.prototype.isNode === true || false;
}
function isIndexNode(t4) {
  return t4 && t4.isIndexNode === true && t4.constructor.prototype.isNode === true || false;
}
function isNode(t4) {
  return t4 && t4.isNode === true && t4.constructor.prototype.isNode === true || false;
}
function isObjectNode(t4) {
  return t4 && t4.isObjectNode === true && t4.constructor.prototype.isNode === true || false;
}
function isOperatorNode(t4) {
  return t4 && t4.isOperatorNode === true && t4.constructor.prototype.isNode === true || false;
}
function isParenthesisNode(t4) {
  return t4 && t4.isParenthesisNode === true && t4.constructor.prototype.isNode === true || false;
}
function isRangeNode(t4) {
  return t4 && t4.isRangeNode === true && t4.constructor.prototype.isNode === true || false;
}
function isRelationalNode(t4) {
  return t4 && t4.isRelationalNode === true && t4.constructor.prototype.isNode === true || false;
}
function isSymbolNode(t4) {
  return t4 && t4.isSymbolNode === true && t4.constructor.prototype.isNode === true || false;
}
function isChain(t4) {
  return t4 && t4.constructor.prototype.isChain === true || false;
}
function typeOf(t4) {
  var n2 = typeof t4;
  return n2 === `object` ? t4 === null ? `null` : isBigNumber(t4) ? `BigNumber` : t4.constructor && t4.constructor.name ? t4.constructor.name : `Object` : n2;
}
function clone$3(t4) {
  var n2 = typeof t4;
  if (n2 === `number` || n2 === `bigint` || n2 === `string` || n2 === `boolean` || t4 == null) return t4;
  if (typeof t4.clone == `function`) return t4.clone();
  if (Array.isArray(t4)) return t4.map(function(t5) {
    return clone$3(t5);
  });
  if (t4 instanceof Date) return new Date(t4.valueOf());
  if (isBigNumber(t4)) return t4;
  if (isObject$1(t4)) return mapObject$1(t4, clone$3);
  if (n2 === `function`) return t4;
  throw TypeError(`Cannot clone: unknown type of value (value: ${t4})`);
}
function mapObject$1(t4, n2) {
  var r2 = {};
  for (var i2 in t4) hasOwnProperty$1(t4, i2) && (r2[i2] = n2(t4[i2]));
  return r2;
}
function extend$1(t4, n2) {
  for (var r2 in n2) hasOwnProperty$1(n2, r2) && (t4[r2] = n2[r2]);
  return t4;
}
function deepExtend(t4, n2) {
  if (Array.isArray(n2)) throw TypeError(`Arrays are not supported by deepExtend`);
  for (var r2 in n2) if (hasOwnProperty$1(n2, r2) && !(r2 in Object.prototype) && !(r2 in Function.prototype)) if (n2[r2] && n2[r2].constructor === Object) t4[r2] === void 0 && (t4[r2] = {}), t4[r2] && t4[r2].constructor === Object ? deepExtend(t4[r2], n2[r2]) : t4[r2] = n2[r2];
  else if (Array.isArray(n2[r2])) throw TypeError(`Arrays are not supported by deepExtend`);
  else t4[r2] = n2[r2];
  return t4;
}
function deepStrictEqual(t4, n2) {
  var r2, i2, a2;
  if (Array.isArray(t4)) {
    if (!Array.isArray(n2) || t4.length !== n2.length) return false;
    for (i2 = 0, a2 = t4.length; i2 < a2; i2++) if (!deepStrictEqual(t4[i2], n2[i2])) return false;
    return true;
  } else if (typeof t4 == `function`) return t4 === n2;
  else if (t4 instanceof Object) {
    if (Array.isArray(n2) || !(n2 instanceof Object)) return false;
    for (r2 in t4) if (!(r2 in n2) || !deepStrictEqual(t4[r2], n2[r2])) return false;
    for (r2 in n2) if (!(r2 in t4)) return false;
    return true;
  } else return t4 === n2;
}
function deepFlatten(t4) {
  var n2 = {};
  return _deepFlatten(t4, n2), n2;
}
function _deepFlatten(t4, n2) {
  for (var r2 in t4) if (hasOwnProperty$1(t4, r2)) {
    var i2 = t4[r2];
    typeof i2 == `object` && i2 ? _deepFlatten(i2, n2) : n2[r2] = i2;
  }
}
function lazy(t4, n2, r2) {
  var i2 = true, a2;
  Object.defineProperty(t4, n2, { get: function() {
    return i2 &&= (a2 = r2(), false), a2;
  }, set: function(t5) {
    a2 = t5, i2 = false;
  }, configurable: true, enumerable: true });
}
function hasOwnProperty$1(t4, n2) {
  return t4 && Object.hasOwnProperty.call(t4, n2);
}
function isLegacyFactory(t4) {
  return t4 && typeof t4.factory == `function`;
}
function pickShallow(t4, n2) {
  for (var r2 = {}, i2 = 0; i2 < n2.length; i2++) {
    var a2 = n2[i2], o2 = t4[a2];
    o2 !== void 0 && (r2[a2] = o2);
  }
  return r2;
}
var MATRIX_OPTIONS = [`Matrix`, `Array`], NUMBER_OPTIONS = [`number`, `BigNumber`, `bigint`, `Fraction`];
function configFactory(t4, n2) {
  function r2(i2) {
    if (i2) {
      if (i2.epsilon !== void 0) {
        console.warn(`Warning: The configuration option "epsilon" is deprecated. Use "relTol" and "absTol" instead.`);
        var a2 = clone$3(i2);
        return a2.relTol = i2.epsilon, a2.absTol = i2.epsilon * 1e-3, delete a2.epsilon, r2(a2);
      }
      i2.legacySubset === true && console.warn(`Warning: The configuration option "legacySubset" is for compatibility only and might be deprecated in the future.`);
      var o2 = clone$3(t4);
      validateOption(i2, `matrix`, MATRIX_OPTIONS), validateOption(i2, `number`, NUMBER_OPTIONS), deepExtend(t4, i2);
      var s2 = clone$3(t4);
      return n2(`config`, s2, o2, clone$3(i2)), s2;
    } else return clone$3(t4);
  }
  return r2.MATRIX_OPTIONS = MATRIX_OPTIONS, r2.NUMBER_OPTIONS = NUMBER_OPTIONS, Object.keys(DEFAULT_CONFIG).forEach((n3) => {
    Object.defineProperty(r2, n3, { get: () => t4[n3], enumerable: true, configurable: true });
  }), r2;
}
function validateOption(t4, n2, r2) {
  t4[n2] !== void 0 && !r2.includes(t4[n2]) && console.warn(`Warning: Unknown value "` + t4[n2] + `" for configuration option "` + n2 + `". Available options: ` + r2.map((t5) => JSON.stringify(t5)).join(`, `) + `.`);
}
var require_typed_function = __commonJSMin(((t4, n2) => {
  (function(r2, i2) {
    typeof t4 == `object` && n2 !== void 0 ? n2.exports = i2() : typeof define == `function` && define.amd ? define(i2) : (r2 = typeof globalThis < `u` ? globalThis : r2 || self, r2[`'typed'`] = i2());
  })(t4, (function() {
    function t5() {
      return true;
    }
    function n3() {
      return false;
    }
    function r2() {
    }
    let i2 = `Argument is not a typed-function.`;
    function a2() {
      function o2(t6) {
        return typeof t6 == `object` && !!t6 && t6.constructor === Object;
      }
      let s2 = [{ name: `number`, test: function(t6) {
        return typeof t6 == `number`;
      } }, { name: `string`, test: function(t6) {
        return typeof t6 == `string`;
      } }, { name: `boolean`, test: function(t6) {
        return typeof t6 == `boolean`;
      } }, { name: `Function`, test: function(t6) {
        return typeof t6 == `function`;
      } }, { name: `Array`, test: Array.isArray }, { name: `Date`, test: function(t6) {
        return t6 instanceof Date;
      } }, { name: `RegExp`, test: function(t6) {
        return t6 instanceof RegExp;
      } }, { name: `Object`, test: o2 }, { name: `null`, test: function(t6) {
        return t6 === null;
      } }, { name: `undefined`, test: function(t6) {
        return t6 === void 0;
      } }], c2 = { name: `any`, test: t5, isAny: true }, l2, u2, d2 = 0, p2 = { createCount: 0 };
      function m2(t6) {
        let n4 = l2.get(t6);
        if (n4) return n4;
        let r3 = `Unknown type "` + t6 + `"`, i3 = t6.toLowerCase(), a3;
        for (a3 of u2) if (a3.toLowerCase() === i3) {
          r3 += `. Did you mean "` + a3 + `" ?`;
          break;
        }
        throw TypeError(r3);
      }
      function h2(t6) {
        let n4 = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : `any`, r3 = n4 ? m2(n4).index : u2.length, i3 = [];
        for (let n5 = 0; n5 < t6.length; ++n5) {
          if (!t6[n5] || typeof t6[n5].name != `string` || typeof t6[n5].test != `function`) throw TypeError(`Object with properties {name: string, test: function} expected`);
          let a4 = t6[n5].name;
          if (l2.has(a4)) throw TypeError(`Duplicate type name "` + a4 + `"`);
          i3.push(a4), l2.set(a4, { name: a4, test: t6[n5].test, isAny: t6[n5].isAny, index: r3 + n5, conversionsTo: [] });
        }
        let a3 = u2.slice(r3);
        u2 = u2.slice(0, r3).concat(i3).concat(a3);
        for (let t7 = r3 + i3.length; t7 < u2.length; ++t7) l2.get(u2[t7]).index = t7;
      }
      function g2() {
        l2 = /* @__PURE__ */ new Map(), u2 = [], d2 = 0, h2([c2], false);
      }
      g2(), h2(s2);
      function v2() {
        let t6;
        for (t6 of u2) l2.get(t6).conversionsTo = [];
        d2 = 0;
      }
      function y2(t6) {
        let n4 = u2.filter((n5) => {
          let r3 = l2.get(n5);
          return !r3.isAny && r3.test(t6);
        });
        return n4.length ? n4 : [`any`];
      }
      function b2(t6) {
        return t6 && typeof t6 == `function` && `_typedFunctionData` in t6;
      }
      function x2(t6, n4, r3) {
        if (!b2(t6)) throw TypeError(i2);
        let a3 = r3 && r3.exact, o3 = M2(Array.isArray(n4) ? n4.join(`,`) : n4), s3 = E(o3);
        if (!a3 || s3 in t6.signatures) {
          let n5 = t6._typedFunctionData.signatureMap.get(s3);
          if (n5) return n5;
        }
        let c3 = o3.length, l3;
        if (a3) {
          l3 = [];
          let n5;
          for (n5 in t6.signatures) l3.push(t6._typedFunctionData.signatureMap.get(n5));
        } else l3 = t6._typedFunctionData.signatures;
        for (let t7 = 0; t7 < c3; ++t7) {
          let n5 = o3[t7], r4 = [], i3;
          for (i3 of l3) {
            let a4 = L(i3.params, t7);
            if (!(!a4 || n5.restParam && !a4.restParam)) {
              if (!a4.hasAny) {
                let t8 = j2(a4);
                if (n5.types.some((n6) => !t8.has(n6.name))) continue;
              }
              r4.push(i3);
            }
          }
          if (l3 = r4, l3.length === 0) break;
        }
        let u3;
        for (u3 of l3) if (u3.params.length <= c3) return u3;
        throw TypeError(`Signature not found (signature: ` + (t6.name || `unnamed`) + `(` + E(o3, `, `) + `))`);
      }
      function S2(t6, n4, r3) {
        return x2(t6, n4, r3).implementation;
      }
      function w2(t6, n4) {
        let r3 = m2(n4);
        if (r3.test(t6)) return t6;
        let i3 = r3.conversionsTo;
        if (i3.length === 0) throw Error(`There are no conversions to ` + n4 + ` defined.`);
        for (let n5 = 0; n5 < i3.length; n5++) if (m2(i3[n5].from).test(t6)) return i3[n5].convert(t6);
        throw Error(`Cannot convert ` + t6 + ` to ` + n4);
      }
      function E(t6) {
        let n4 = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : `,`;
        return t6.map((t7) => t7.name).join(n4);
      }
      function D(t6) {
        let n4 = t6.indexOf(`...`) === 0, r3 = (n4 ? t6.length > 3 ? t6.slice(3) : `any` : t6).split(`|`).map((t7) => m2(t7.trim())), i3 = false, a3 = n4 ? `...` : ``;
        return { types: r3.map(function(t7) {
          return i3 = t7.isAny || i3, a3 += t7.name + `|`, { name: t7.name, typeIndex: t7.index, test: t7.test, isAny: t7.isAny, conversion: null, conversionIndex: -1 };
        }), name: a3.slice(0, -1), hasAny: i3, hasConversion: false, restParam: n4 };
      }
      function O(t6) {
        let n4 = q(t6.types.map((t7) => t7.name)), r3 = t6.hasAny, i3 = t6.name, a3 = n4.map(function(t7) {
          let n5 = m2(t7.from);
          return r3 = n5.isAny || r3, i3 += `|` + t7.from, { name: t7.from, typeIndex: n5.index, test: n5.test, isAny: n5.isAny, conversion: t7, conversionIndex: t7.index };
        });
        return { types: t6.types.concat(a3), name: i3, hasAny: r3, hasConversion: a3.length > 0, restParam: t6.restParam };
      }
      function j2(t6) {
        return t6.typeSet || (t6.typeSet = /* @__PURE__ */ new Set(), t6.types.forEach((n4) => t6.typeSet.add(n4.name))), t6.typeSet;
      }
      function M2(t6) {
        let n4 = [];
        if (typeof t6 != `string`) throw TypeError(`Signatures must be strings`);
        let r3 = t6.trim();
        if (r3 === ``) return n4;
        let i3 = r3.split(`,`);
        for (let t7 = 0; t7 < i3.length; ++t7) {
          let r4 = D(i3[t7].trim());
          if (r4.restParam && t7 !== i3.length - 1) throw SyntaxError(`Unexpected rest parameter "` + i3[t7] + `": only allowed for the last parameter`);
          if (r4.types.length === 0) return null;
          n4.push(r4);
        }
        return n4;
      }
      function N2(t6) {
        let n4 = se(t6);
        return n4 ? n4.restParam : false;
      }
      function F(n4) {
        if (!n4 || n4.types.length === 0) return t5;
        if (n4.types.length === 1) return m2(n4.types[0].name).test;
        if (n4.types.length === 2) {
          let t6 = m2(n4.types[0].name).test, r3 = m2(n4.types[1].name).test;
          return function(n5) {
            return t6(n5) || r3(n5);
          };
        } else {
          let t6 = n4.types.map(function(t7) {
            return m2(t7.name).test;
          });
          return function(n5) {
            for (let r3 = 0; r3 < t6.length; r3++) if (t6[r3](n5)) return true;
            return false;
          };
        }
      }
      function I(t6) {
        let n4, r3, i3;
        if (N2(t6)) {
          n4 = $2(t6).map(F);
          let r4 = n4.length, i4 = F(se(t6)), a3 = function(t7) {
            for (let n5 = r4; n5 < t7.length; n5++) if (!i4(t7[n5])) return false;
            return true;
          };
          return function(t7) {
            for (let r5 = 0; r5 < n4.length; r5++) if (!n4[r5](t7[r5])) return false;
            return a3(t7) && t7.length >= r4 + 1;
          };
        } else if (t6.length === 0) return function(t7) {
          return t7.length === 0;
        };
        else if (t6.length === 1) return r3 = F(t6[0]), function(t7) {
          return r3(t7[0]) && t7.length === 1;
        };
        else if (t6.length === 2) return r3 = F(t6[0]), i3 = F(t6[1]), function(t7) {
          return r3(t7[0]) && i3(t7[1]) && t7.length === 2;
        };
        else return n4 = t6.map(F), function(t7) {
          for (let r4 = 0; r4 < n4.length; r4++) if (!n4[r4](t7[r4])) return false;
          return t7.length === n4.length;
        };
      }
      function L(t6, n4) {
        return n4 < t6.length ? t6[n4] : N2(t6) ? se(t6) : null;
      }
      function R(t6, n4) {
        let r3 = L(t6, n4);
        return r3 ? j2(r3) : /* @__PURE__ */ new Set();
      }
      function z(t6) {
        return t6.conversion === null || t6.conversion === void 0;
      }
      function V(t6, n4) {
        let r3 = /* @__PURE__ */ new Set();
        return t6.forEach((t7) => {
          let i3 = R(t7.params, n4), a3;
          for (a3 of i3) r3.add(a3);
        }), r3.has(`any`) ? [`any`] : Array.from(r3);
      }
      function H2(t6, n4, r3) {
        let i3, a3, o3 = t6 || `unnamed`, s3 = r3, c3;
        for (c3 = 0; c3 < n4.length; c3++) {
          let t7 = [];
          if (s3.forEach((r4) => {
            let i4 = F(L(r4.params, c3));
            (c3 < r4.params.length || N2(r4.params)) && i4(n4[c3]) && t7.push(r4);
          }), t7.length === 0) {
            if (a3 = V(s3, c3), a3.length > 0) {
              let t8 = y2(n4[c3]);
              return i3 = TypeError(`Unexpected type of argument in function ` + o3 + ` (expected: ` + a3.join(` or `) + `, actual: ` + t8.join(` | `) + `, index: ` + c3 + `)`), i3.data = { category: `wrongType`, fn: o3, index: c3, actual: t8, expected: a3 }, i3;
            }
          } else s3 = t7;
        }
        let l3 = s3.map(function(t7) {
          return N2(t7.params) ? 1 / 0 : t7.params.length;
        });
        if (n4.length < Math.min.apply(null, l3)) return a3 = V(s3, c3), i3 = TypeError(`Too few arguments in function ` + o3 + ` (expected: ` + a3.join(` or `) + `, index: ` + n4.length + `)`), i3.data = { category: `tooFewArgs`, fn: o3, index: n4.length, expected: a3 }, i3;
        let u3 = Math.max.apply(null, l3);
        if (n4.length > u3) return i3 = TypeError(`Too many arguments in function ` + o3 + ` (expected: ` + u3 + `, actual: ` + n4.length + `)`), i3.data = { category: `tooManyArgs`, fn: o3, index: n4.length, expectedLength: u3 }, i3;
        let d3 = [];
        for (let t7 = 0; t7 < n4.length; ++t7) d3.push(y2(n4[t7]).join(`|`));
        return i3 = TypeError(`Arguments of type "` + d3.join(`, `) + `" do not match any of the defined signatures of function ` + o3 + `.`), i3.data = { category: `mismatch`, actual: d3 }, i3;
      }
      function U(t6) {
        let n4 = u2.length + 1;
        for (let r3 = 0; r3 < t6.types.length; r3++) z(t6.types[r3]) && (n4 = Math.min(n4, t6.types[r3].typeIndex));
        return n4;
      }
      function W(t6) {
        let n4 = d2 + 1;
        for (let r3 = 0; r3 < t6.types.length; r3++) z(t6.types[r3]) || (n4 = Math.min(n4, t6.types[r3].conversionIndex));
        return n4;
      }
      function G(t6, n4) {
        if (t6.hasAny) {
          if (!n4.hasAny) return 1;
        } else if (n4.hasAny) return -1;
        if (t6.restParam) {
          if (!n4.restParam) return 1;
        } else if (n4.restParam) return -1;
        if (t6.hasConversion) {
          if (!n4.hasConversion) return 1;
        } else if (n4.hasConversion) return -1;
        let r3 = U(t6) - U(n4);
        if (r3 < 0) return -1;
        if (r3 > 0) return 1;
        let i3 = W(t6) - W(n4);
        return i3 < 0 ? -1 : +(i3 > 0);
      }
      function K(t6, n4) {
        let r3 = t6.params, i3 = n4.params, a3 = se(r3), o3 = se(i3), s3 = N2(r3), c3 = N2(i3);
        if (s3 && a3.hasAny) {
          if (!c3 || !o3.hasAny) return 1;
        } else if (c3 && o3.hasAny) return -1;
        let l3 = 0, u3 = 0, d3;
        for (d3 of r3) d3.hasAny && ++l3, d3.hasConversion && ++u3;
        let p3 = 0, m3 = 0;
        for (d3 of i3) d3.hasAny && ++p3, d3.hasConversion && ++m3;
        if (l3 !== p3) return l3 - p3;
        if (s3 && a3.hasConversion) {
          if (!c3 || !o3.hasConversion) return 1;
        } else if (c3 && o3.hasConversion) return -1;
        if (u3 !== m3) return u3 - m3;
        if (s3) {
          if (!c3) return 1;
        } else if (c3) return -1;
        let h3 = (r3.length - i3.length) * (s3 ? -1 : 1);
        if (h3 !== 0) return h3;
        let g3 = [], v3 = 0;
        for (let t7 = 0; t7 < r3.length; ++t7) {
          let n5 = G(r3[t7], i3[t7]);
          g3.push(n5), v3 += n5;
        }
        if (v3 !== 0) return v3;
        let y3;
        for (y3 of g3) if (y3 !== 0) return y3;
        return 0;
      }
      function q(t6) {
        if (t6.length === 0) return [];
        let n4 = t6.map(m2);
        t6.length > 1 && n4.sort((t7, n5) => t7.index - n5.index);
        let r3 = n4[0].conversionsTo;
        if (t6.length === 1) return r3;
        r3 = r3.concat([]);
        let i3 = new Set(t6);
        for (let t7 = 1; t7 < n4.length; ++t7) {
          let a3;
          for (a3 of n4[t7].conversionsTo) i3.has(a3.from) || (r3.push(a3), i3.add(a3.from));
        }
        return r3;
      }
      function J(t6, n4) {
        let r3 = n4;
        if (t6.some((t7) => t7.hasConversion)) {
          let i4 = N2(t6), a3 = t6.map(ee);
          r3 = function() {
            let t7 = [], r4 = i4 ? arguments.length - 1 : arguments.length;
            for (let n5 = 0; n5 < r4; n5++) t7[n5] = a3[n5](arguments[n5]);
            return i4 && (t7[r4] = arguments[r4].map(a3[r4])), n4.apply(this, t7);
          };
        }
        let i3 = r3;
        if (N2(t6)) {
          let n5 = t6.length - 1;
          i3 = function() {
            return r3.apply(this, ce(arguments, 0, n5).concat([ce(arguments, n5)]));
          };
        }
        return i3;
      }
      function ee(t6) {
        let n4, r3, i3, a3, o3 = [], s3 = [];
        switch (t6.types.forEach(function(t7) {
          t7.conversion && (o3.push(m2(t7.conversion.from).test), s3.push(t7.conversion.convert));
        }), s3.length) {
          case 0:
            return function(t7) {
              return t7;
            };
          case 1:
            return n4 = o3[0], i3 = s3[0], function(t7) {
              return n4(t7) ? i3(t7) : t7;
            };
          case 2:
            return n4 = o3[0], r3 = o3[1], i3 = s3[0], a3 = s3[1], function(t7) {
              return n4(t7) ? i3(t7) : r3(t7) ? a3(t7) : t7;
            };
          default:
            return function(t7) {
              for (let n5 = 0; n5 < s3.length; n5++) if (o3[n5](t7)) return s3[n5](t7);
              return t7;
            };
        }
      }
      function Y(t6) {
        function n4(t7, r3, i3) {
          if (r3 < t7.length) {
            let a3 = t7[r3], o3 = [];
            if (a3.restParam) {
              let t8 = a3.types.filter(z);
              t8.length < a3.types.length && o3.push({ types: t8, name: `...` + t8.map((t9) => t9.name).join(`|`), hasAny: t8.some((t9) => t9.isAny), hasConversion: false, restParam: true }), o3.push(a3);
            } else o3 = a3.types.map(function(t8) {
              return { types: [t8], name: t8.name, hasAny: t8.isAny, hasConversion: t8.conversion, restParam: false };
            });
            return ue(o3, function(a4) {
              return n4(t7, r3 + 1, i3.concat([a4]));
            });
          } else return [i3];
        }
        return n4(t6, 0, []);
      }
      function te(t6, n4) {
        let r3 = Math.max(t6.length, n4.length);
        for (let i4 = 0; i4 < r3; i4++) {
          let r4 = R(t6, i4), a4 = R(n4, i4), o4 = false, s4;
          for (s4 of a4) if (r4.has(s4)) {
            o4 = true;
            break;
          }
          if (!o4) return false;
        }
        let i3 = t6.length, a3 = n4.length, o3 = N2(t6), s3 = N2(n4);
        return o3 ? s3 ? i3 === a3 : a3 >= i3 : s3 ? i3 >= a3 : i3 === a3;
      }
      function ne(t6) {
        return t6.map((t7) => he(t7) ? pe(t7.referToSelf.callback) : me(t7) ? fe(t7.referTo.references, t7.referTo.callback) : t7);
      }
      function X(t6, n4, r3) {
        let i3 = [], a3;
        for (a3 of t6) {
          let t7 = r3[a3];
          if (typeof t7 != `number`) throw TypeError(`No definition for referenced signature "` + a3 + `"`);
          if (t7 = n4[t7], typeof t7 != `function`) return false;
          i3.push(t7);
        }
        return i3;
      }
      function ie(t6, n4, r3) {
        let i3 = ne(t6), a3 = Array(i3.length).fill(false), o3 = true;
        for (; o3; ) {
          o3 = false;
          let t7 = true;
          for (let s3 = 0; s3 < i3.length; ++s3) {
            if (a3[s3]) continue;
            let c3 = i3[s3];
            if (he(c3)) i3[s3] = c3.referToSelf.callback(r3), i3[s3].referToSelf = c3.referToSelf, a3[s3] = true, t7 = false;
            else if (me(c3)) {
              let r4 = X(c3.referTo.references, i3, n4);
              r4 ? (i3[s3] = c3.referTo.callback.apply(this, r4), i3[s3].referTo = c3.referTo, a3[s3] = true, t7 = false) : o3 = true;
            }
          }
          if (t7 && o3) throw SyntaxError(`Circular reference detected in resolving typed.referTo`);
        }
        return i3;
      }
      function ae(t6) {
        let n4 = /\bthis(\(|\.signatures\b)/;
        Object.keys(t6).forEach((r3) => {
          let i3 = t6[r3];
          if (n4.test(i3.toString())) throw SyntaxError("Using `this` to self-reference a function is deprecated since typed-function@3. Use typed.referTo and typed.referToSelf instead.");
        });
      }
      function Z(t6, i3) {
        if (p2.createCount++, Object.keys(i3).length === 0) throw SyntaxError(`No signatures provided`);
        p2.warnAgainstDeprecatedThis && ae(i3);
        let a3 = [], o3 = [], s3 = {}, c3 = [], l3;
        for (l3 in i3) {
          if (!Object.prototype.hasOwnProperty.call(i3, l3)) continue;
          let t7 = M2(l3);
          if (!t7) continue;
          a3.forEach(function(n5) {
            if (te(n5, t7)) throw TypeError(`Conflicting signatures "` + E(n5) + `" and "` + E(t7) + `".`);
          }), a3.push(t7);
          let n4 = o3.length;
          o3.push(i3[l3]);
          let r3 = t7.map(O), u4;
          for (u4 of Y(r3)) {
            let t8 = E(u4);
            c3.push({ params: u4, name: t8, fn: n4 }), u4.every((t9) => !t9.hasConversion) && (s3[t8] = n4);
          }
        }
        c3.sort(K);
        let u3 = ie(o3, s3, ye2), d3;
        for (d3 in s3) Object.prototype.hasOwnProperty.call(s3, d3) && (s3[d3] = u3[s3[d3]]);
        let m3 = [], h3 = /* @__PURE__ */ new Map();
        for (d3 of c3) h3.has(d3.name) || (d3.fn = u3[d3.fn], m3.push(d3), h3.set(d3.name, d3));
        let g3 = m3[0] && m3[0].params.length <= 2 && !N2(m3[0].params), v3 = m3[1] && m3[1].params.length <= 2 && !N2(m3[1].params), y3 = m3[2] && m3[2].params.length <= 2 && !N2(m3[2].params), b3 = m3[3] && m3[3].params.length <= 2 && !N2(m3[3].params), x3 = m3[4] && m3[4].params.length <= 2 && !N2(m3[4].params), S3 = m3[5] && m3[5].params.length <= 2 && !N2(m3[5].params), w3 = g3 && v3 && y3 && b3 && x3 && S3;
        for (let t7 = 0; t7 < m3.length; ++t7) m3[t7].test = I(m3[t7].params);
        let D2 = g3 ? F(m3[0].params[0]) : n3, j3 = v3 ? F(m3[1].params[0]) : n3, L2 = y3 ? F(m3[2].params[0]) : n3, R2 = b3 ? F(m3[3].params[0]) : n3, z2 = x3 ? F(m3[4].params[0]) : n3, V2 = S3 ? F(m3[5].params[0]) : n3, H3 = g3 ? F(m3[0].params[1]) : n3, U2 = v3 ? F(m3[1].params[1]) : n3, W2 = y3 ? F(m3[2].params[1]) : n3, G2 = b3 ? F(m3[3].params[1]) : n3, q2 = x3 ? F(m3[4].params[1]) : n3, ee2 = S3 ? F(m3[5].params[1]) : n3;
        for (let t7 = 0; t7 < m3.length; ++t7) m3[t7].implementation = J(m3[t7].params, m3[t7].fn);
        let ne2 = g3 ? m3[0].implementation : r2, X2 = v3 ? m3[1].implementation : r2, Z2 = y3 ? m3[2].implementation : r2, oe2 = b3 ? m3[3].implementation : r2, $3 = x3 ? m3[4].implementation : r2, se2 = S3 ? m3[5].implementation : r2, ce2 = g3 ? m3[0].params.length : -1, le2 = v3 ? m3[1].params.length : -1, ue2 = y3 ? m3[2].params.length : -1, de2 = b3 ? m3[3].params.length : -1, fe2 = x3 ? m3[4].params.length : -1, pe2 = S3 ? m3[5].params.length : -1, me2 = w3 ? 6 : 0, he2 = m3.length, ge2 = m3.map((t7) => t7.test), _e2 = m3.map((t7) => t7.implementation), ve2 = function() {
          for (let t7 = me2; t7 < he2; t7++) if (ge2[t7](arguments)) return _e2[t7].apply(this, arguments);
          return p2.onMismatch(t6, arguments, m3);
        };
        function ye2(t7, n4) {
          return arguments.length === ce2 && D2(t7) && H3(n4) ? ne2.apply(this, arguments) : arguments.length === le2 && j3(t7) && U2(n4) ? X2.apply(this, arguments) : arguments.length === ue2 && L2(t7) && W2(n4) ? Z2.apply(this, arguments) : arguments.length === de2 && R2(t7) && G2(n4) ? oe2.apply(this, arguments) : arguments.length === fe2 && z2(t7) && q2(n4) ? $3.apply(this, arguments) : arguments.length === pe2 && V2(t7) && ee2(n4) ? se2.apply(this, arguments) : ve2.apply(this, arguments);
        }
        try {
          Object.defineProperty(ye2, "name", { value: t6 });
        } catch {
        }
        return ye2.signatures = s3, ye2._typedFunctionData = { signatures: m3, signatureMap: h3 }, ye2;
      }
      function oe(t6, n4, r3) {
        throw H2(t6, n4, r3);
      }
      function $2(t6) {
        return ce(t6, 0, t6.length - 1);
      }
      function se(t6) {
        return t6[t6.length - 1];
      }
      function ce(t6, n4, r3) {
        return Array.prototype.slice.call(t6, n4, r3);
      }
      function le(t6, n4) {
        for (let r3 = 0; r3 < t6.length; r3++) if (n4(t6[r3])) return t6[r3];
      }
      function ue(t6, n4) {
        return Array.prototype.concat.apply([], t6.map(n4));
      }
      function de() {
        let t6 = $2(arguments).map((t7) => E(M2(t7))), n4 = se(arguments);
        if (typeof n4 != `function`) throw TypeError(`Callback function expected as last argument`);
        return fe(t6, n4);
      }
      function fe(t6, n4) {
        return { referTo: { references: t6, callback: n4 } };
      }
      function pe(t6) {
        if (typeof t6 != `function`) throw TypeError(`Callback function expected as first argument`);
        return { referToSelf: { callback: t6 } };
      }
      function me(t6) {
        return t6 && typeof t6.referTo == `object` && Array.isArray(t6.referTo.references) && typeof t6.referTo.callback == `function`;
      }
      function he(t6) {
        return t6 && typeof t6.referToSelf == `object` && typeof t6.referToSelf.callback == `function`;
      }
      function ge(t6, n4) {
        if (!t6) return n4;
        if (n4 && n4 !== t6) {
          let r3 = Error(`Function names do not match (expected: ` + t6 + `, actual: ` + n4 + `)`);
          throw r3.data = { actual: n4, expected: t6 }, r3;
        }
        return t6;
      }
      function _e(t6) {
        let n4;
        for (let r3 in t6) Object.prototype.hasOwnProperty.call(t6, r3) && (b2(t6[r3]) || typeof t6[r3].signature == `string`) && (n4 = ge(n4, t6[r3].name));
        return n4;
      }
      function ve(t6, n4) {
        let r3;
        for (r3 in n4) if (Object.prototype.hasOwnProperty.call(n4, r3)) {
          if (r3 in t6 && n4[r3] !== t6[r3]) {
            let i3 = Error(`Signature "` + r3 + `" is defined twice`);
            throw i3.data = { signature: r3, sourceFunction: n4[r3], destFunction: t6[r3] }, i3;
          }
          t6[r3] = n4[r3];
        }
      }
      let ye = p2;
      p2 = function(t6) {
        let n4 = typeof t6 == `string`, r3 = +!!n4, i3 = n4 ? t6 : ``, a3 = {};
        for (let t7 = r3; t7 < arguments.length; ++t7) {
          let r4 = arguments[t7], s3 = {}, c3;
          if (typeof r4 == `function` ? (c3 = r4.name, typeof r4.signature == `string` ? s3[r4.signature] = r4 : b2(r4) && (s3 = r4.signatures)) : o2(r4) && (s3 = r4, n4 || (c3 = _e(r4))), Object.keys(s3).length === 0) {
            let n5 = TypeError(`Argument to 'typed' at index ` + t7 + ` is not a (typed) function, nor an object with signatures as keys and functions as values.`);
            throw n5.data = { index: t7, argument: r4 }, n5;
          }
          n4 || (i3 = ge(i3, c3)), ve(a3, s3);
        }
        return Z(i3 || ``, a3);
      }, p2.create = a2, p2.createCount = ye.createCount, p2.onMismatch = oe, p2.throwMismatchError = oe, p2.createError = H2, p2.clear = g2, p2.clearConversions = v2, p2.addTypes = h2, p2._findType = m2, p2.referTo = de, p2.referToSelf = pe, p2.convert = w2, p2.findSignature = x2, p2.find = S2, p2.isTypedFunction = b2, p2.warnAgainstDeprecatedThis = true, p2.addType = function(t6, n4) {
        let r3 = `any`;
        n4 !== false && l2.has(`Object`) && (r3 = `Object`), p2.addTypes([t6], r3);
      };
      function be(t6) {
        if (!t6 || typeof t6.from != `string` || typeof t6.to != `string` || typeof t6.convert != `function`) throw TypeError(`Object with properties {from: string, to: string, convert: function} expected`);
        if (t6.to === t6.from) throw SyntaxError(`Illegal to define conversion from "` + t6.from + `" to itself.`);
      }
      return p2.addConversion = function(t6) {
        let n4 = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : { override: false };
        be(t6);
        let r3 = m2(t6.to), i3 = r3.conversionsTo.find((n5) => n5.from === t6.from);
        if (i3) if (n4 && n4.override) p2.removeConversion({ from: i3.from, to: t6.to, convert: i3.convert });
        else throw Error(`There is already a conversion from "` + t6.from + `" to "` + r3.name + `"`);
        r3.conversionsTo.push({ from: t6.from, convert: t6.convert, index: d2++ });
      }, p2.addConversions = function(t6, n4) {
        t6.forEach((t7) => p2.addConversion(t7, n4));
      }, p2.removeConversion = function(t6) {
        be(t6);
        let n4 = m2(t6.to), r3 = le(n4.conversionsTo, (n5) => n5.from === t6.from);
        if (!r3) throw Error(`Attempt to remove nonexistent conversion from ` + t6.from + ` to ` + t6.to);
        if (r3.convert !== t6.convert) throw Error(`Conversion to remove does not match existing conversion`);
        let i3 = n4.conversionsTo.indexOf(r3);
        n4.conversionsTo.splice(i3, 1);
      }, p2.resolve = function(t6, n4) {
        if (!b2(t6)) throw TypeError(i2);
        let r3 = t6._typedFunctionData.signatures;
        for (let t7 = 0; t7 < r3.length; ++t7) if (r3[t7].test(n4)) return r3[t7];
        return null;
      }, p2;
    }
    return a2();
  }));
}));
function factory(t4, n2, r2, i2) {
  function a2(i3) {
    var a3 = pickShallow(i3, n2.map(stripOptionalNotation));
    return assertDependencies(t4, n2, i3), r2(a3);
  }
  return a2.isFactory = true, a2.fn = t4, a2.dependencies = n2.slice().sort(), i2 && (a2.meta = i2), a2;
}
function isFactory(t4) {
  return typeof t4 == `function` && typeof t4.fn == `string` && Array.isArray(t4.dependencies);
}
function assertDependencies(t4, n2, r2) {
  if (!n2.filter((t5) => !isOptionalDependency(t5)).every((t5) => r2[t5] !== void 0)) {
    var i2 = n2.filter((t5) => r2[t5] === void 0);
    throw Error(`Cannot create function "${t4}", some dependencies are missing: ${i2.map((t5) => `"${t5}"`).join(`, `)}.`);
  }
}
function isOptionalDependency(t4) {
  return t4 && t4[0] === `?`;
}
function stripOptionalNotation(t4) {
  return t4 && t4[0] === `?` ? t4.slice(1) : t4;
}
function isInteger(t4) {
  return typeof t4 == `boolean` ? true : Number.isFinite(t4) ? t4 === Math.round(t4) : false;
}
function safeNumberType(t4, n2) {
  if (n2.number === `bigint`) try {
    BigInt(t4);
  } catch {
    return n2.numberFallback;
  }
  return n2.number;
}
var sign$2 = Math.sign || function(t4) {
  return t4 > 0 ? 1 : t4 < 0 ? -1 : 0;
}, log2$1 = Math.log2 || function(t4) {
  return Math.log(t4) / Math.LN2;
}, log10$2 = Math.log10 || function(t4) {
  return Math.log(t4) / Math.LN10;
}, log1p = Math.log1p || function(t4) {
  return Math.log(t4 + 1);
}, cbrt$1 = Math.cbrt || function(t4) {
  if (t4 === 0) return t4;
  var n2 = t4 < 0, r2;
  return n2 && (t4 = -t4), Number.isFinite(t4) ? (r2 = Math.exp(Math.log(t4) / 3), r2 = (t4 / (r2 * r2) + 2 * r2) / 3) : r2 = t4, n2 ? -r2 : r2;
}, expm1 = Math.expm1 || function(t4) {
  return t4 >= 2e-4 || t4 <= -2e-4 ? Math.exp(t4) - 1 : t4 + t4 * t4 / 2 + t4 * t4 * t4 / 6;
};
function formatNumberToBase(t4, n2, r2) {
  var i2 = { 2: `0b`, 8: `0o`, 16: `0x` }[n2], a2 = ``;
  if (r2) {
    if (r2 < 1) throw Error(`size must be in greater than 0`);
    if (!isInteger(r2)) throw Error(`size must be an integer`);
    if (t4 > 2 ** (r2 - 1) - 1 || t4 < -(2 ** (r2 - 1))) throw Error(`Value must be in range [-2^${r2 - 1}, 2^${r2 - 1}-1]`);
    if (!isInteger(t4)) throw Error(`Value must be an integer`);
    t4 < 0 && (t4 += 2 ** r2), a2 = `i${r2}`;
  }
  var o2 = ``;
  return t4 < 0 && (t4 = -t4, o2 = `-`), `${o2}${i2}${t4.toString(n2)}${a2}`;
}
function format$2(t4, n2) {
  if (typeof n2 == `function`) return n2(t4);
  if (t4 === 1 / 0) return `Infinity`;
  if (t4 === -1 / 0) return `-Infinity`;
  if (isNaN(t4)) return `NaN`;
  var { notation: r2, precision: i2, wordSize: a2 } = normalizeFormatOptions(n2);
  switch (r2) {
    case `fixed`:
      return toFixed$2(t4, i2);
    case `exponential`:
      return toExponential$1(t4, i2);
    case `engineering`:
      return toEngineering$1(t4, i2);
    case `bin`:
      return formatNumberToBase(t4, 2, a2);
    case `oct`:
      return formatNumberToBase(t4, 8, a2);
    case `hex`:
      return formatNumberToBase(t4, 16, a2);
    case `auto`:
      return toPrecision(t4, i2, n2).replace(/((\.\d*?)(0+))($|e)/, function() {
        var t5 = arguments[2], n3 = arguments[4];
        return t5 === `.` ? n3 : t5 + n3;
      });
    default:
      throw Error(`Unknown notation "` + r2 + `". Choose "auto", "exponential", "fixed", "bin", "oct", or "hex.`);
  }
}
function normalizeFormatOptions(t4) {
  var n2 = `auto`, r2, i2;
  if (t4 !== void 0) if (isNumber(t4)) r2 = t4;
  else if (isBigNumber(t4)) r2 = t4.toNumber();
  else if (isObject$1(t4)) t4.precision !== void 0 && (r2 = _toNumberOrThrow(t4.precision, () => {
    throw Error(`Option "precision" must be a number or BigNumber`);
  })), t4.wordSize !== void 0 && (i2 = _toNumberOrThrow(t4.wordSize, () => {
    throw Error(`Option "wordSize" must be a number or BigNumber`);
  })), t4.notation && (n2 = t4.notation);
  else throw Error(`Unsupported type of options, number, BigNumber, or object expected`);
  return { notation: n2, precision: r2, wordSize: i2 };
}
function splitNumber(t4) {
  var n2 = String(t4).toLowerCase().match(/^(-?)(\d+\.?\d*)(e([+-]?\d+))?$/);
  if (!n2) throw SyntaxError(`Invalid number ` + t4);
  var r2 = n2[1], i2 = n2[2], a2 = parseFloat(n2[4] || `0`), o2 = i2.indexOf(`.`);
  a2 += o2 === -1 ? i2.length - 1 : o2 - 1;
  var s2 = i2.replace(`.`, ``).replace(/^0*/, function(t5) {
    return a2 -= t5.length, ``;
  }).replace(/0*$/, ``).split(``).map(function(t5) {
    return parseInt(t5);
  });
  return s2.length === 0 && (s2.push(0), a2++), { sign: r2, coefficients: s2, exponent: a2 };
}
function toEngineering$1(t4, n2) {
  if (isNaN(t4) || !Number.isFinite(t4)) return String(t4);
  var r2 = roundDigits(splitNumber(t4), n2), i2 = r2.exponent, a2 = r2.coefficients, o2 = i2 % 3 == 0 ? i2 : i2 < 0 ? i2 - 3 - i2 % 3 : i2 - i2 % 3;
  if (isNumber(n2)) for (; n2 > a2.length || i2 - o2 + 1 > a2.length; ) a2.push(0);
  else for (var s2 = Math.abs(i2 - o2) - (a2.length - 1), c2 = 0; c2 < s2; c2++) a2.push(0);
  for (var l2 = Math.abs(i2 - o2), u2 = 1; l2 > 0; ) u2++, l2--;
  var d2 = a2.slice(u2).join(``), p2 = isNumber(n2) && d2.length || d2.match(/[1-9]/) ? `.` + d2 : ``, m2 = a2.slice(0, u2).join(``) + p2 + `e` + (i2 >= 0 ? `+` : ``) + o2.toString();
  return r2.sign + m2;
}
function toFixed$2(t4, n2) {
  if (isNaN(t4) || !Number.isFinite(t4)) return String(t4);
  var r2 = splitNumber(t4), i2 = typeof n2 == `number` ? roundDigits(r2, r2.exponent + 1 + n2) : r2, a2 = i2.coefficients, o2 = i2.exponent + 1, s2 = o2 + (n2 || 0);
  return a2.length < s2 && (a2 = a2.concat(zeros(s2 - a2.length))), o2 < 0 && (a2 = zeros(-o2 + 1).concat(a2), o2 = 1), o2 < a2.length && a2.splice(o2, 0, o2 === 0 ? `0.` : `.`), i2.sign + a2.join(``);
}
function toExponential$1(t4, n2) {
  if (isNaN(t4) || !Number.isFinite(Number(t4))) return String(t4);
  var r2 = splitNumber(t4), i2 = n2 ? roundDigits(r2, n2) : r2, a2 = i2.coefficients, o2 = i2.exponent;
  a2.length < n2 && (a2 = a2.concat(zeros(n2 - a2.length)));
  var s2 = a2.shift();
  return i2.sign + s2 + (a2.length > 0 ? `.` + a2.join(``) : ``) + `e` + (o2 >= 0 ? `+` : ``) + o2;
}
function toPrecision(t4, n2, r2) {
  if (isNaN(t4) || !Number.isFinite(t4)) return String(t4);
  var i2 = _toNumberOrDefault$1(r2?.lowerExp, -3), a2 = _toNumberOrDefault$1(r2?.upperExp, 5), o2 = splitNumber(t4), s2 = n2 ? roundDigits(o2, n2) : o2;
  if (s2.exponent < i2 || s2.exponent >= a2) return toExponential$1(t4, n2);
  var c2 = s2.coefficients, l2 = s2.exponent;
  c2.length < n2 && (c2 = c2.concat(zeros(n2 - c2.length))), c2 = c2.concat(zeros(l2 - c2.length + 1 + (c2.length < n2 ? n2 - c2.length : 0))), c2 = zeros(-l2).concat(c2);
  var u2 = l2 > 0 ? l2 : 0;
  return u2 < c2.length - 1 && c2.splice(u2 + 1, 0, `.`), s2.sign + c2.join(``);
}
function roundDigits(t4, n2) {
  for (var r2 = { sign: t4.sign, coefficients: t4.coefficients, exponent: t4.exponent }, i2 = r2.coefficients; n2 <= 0; ) i2.unshift(0), r2.exponent++, n2++;
  if (i2.length > n2 && i2.splice(n2, i2.length - n2)[0] >= 5) {
    var a2 = n2 - 1;
    for (i2[a2]++; i2[a2] === 10; ) i2.pop(), a2 === 0 && (i2.unshift(0), r2.exponent++, a2++), a2--, i2[a2]++;
  }
  return r2;
}
function zeros(t4) {
  for (var n2 = [], r2 = 0; r2 < t4; r2++) n2.push(0);
  return n2;
}
function digits(t4) {
  return t4.toExponential().replace(/e.*$/, ``).replace(/^0\.?0*|\./, ``).length;
}
function nearlyEqual$1(t4, n2) {
  var r2 = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : 1e-8, i2 = arguments.length > 3 && arguments[3] !== void 0 ? arguments[3] : 0;
  if (r2 <= 0) throw Error(`Relative tolerance must be greater than 0`);
  if (i2 < 0) throw Error(`Absolute tolerance must be at least 0`);
  return isNaN(t4) || isNaN(n2) ? false : !Number.isFinite(t4) || !Number.isFinite(n2) ? t4 === n2 : t4 === n2 ? true : Math.abs(t4 - n2) <= Math.max(r2 * Math.max(Math.abs(t4), Math.abs(n2)), i2);
}
var acosh$2 = Math.acosh || function(t4) {
  return Math.log(Math.sqrt(t4 * t4 - 1) + t4);
}, asinh$2 = Math.asinh || function(t4) {
  return Math.log(Math.sqrt(t4 * t4 + 1) + t4);
}, atanh$2 = Math.atanh || function(t4) {
  return Math.log((1 + t4) / (1 - t4)) / 2;
}, cosh$3 = Math.cosh || function(t4) {
  return (Math.exp(t4) + Math.exp(-t4)) / 2;
}, sinh$3 = Math.sinh || function(t4) {
  return (Math.exp(t4) - Math.exp(-t4)) / 2;
}, tanh$2 = Math.tanh || function(t4) {
  var n2 = Math.exp(2 * t4);
  return (n2 - 1) / (n2 + 1);
};
function copysign(t4, n2) {
  return true ^ (n2 > 0 ? true : n2 < 0 ? false : 1 / n2 == 1 / 0) ? -t4 : t4;
}
function _toNumberOrThrow(t4, n2) {
  if (isNumber(t4)) return t4;
  if (isBigNumber(t4)) return t4.toNumber();
  n2();
}
function _toNumberOrDefault$1(t4, n2) {
  return isNumber(t4) ? t4 : isBigNumber(t4) ? t4.toNumber() : n2;
}
var import_typed_function = __toESM(require_typed_function()), _createTyped2 = function() {
  return _createTyped2 = import_typed_function.default.create, import_typed_function.default;
}, dependencies$322 = [`?BigNumber`, `?Complex`, `?DenseMatrix`, `?Fraction`], createTyped = factory(`typed`, dependencies$322, function(t4) {
  var { BigNumber: n2, Complex: r2, DenseMatrix: i2, Fraction: a2 } = t4, o2 = _createTyped2();
  return o2.clear(), o2.addTypes([{ name: `number`, test: isNumber }, { name: `Complex`, test: isComplex }, { name: `BigNumber`, test: isBigNumber }, { name: `bigint`, test: isBigInt }, { name: `Fraction`, test: isFraction }, { name: `Unit`, test: isUnit }, { name: `identifier`, test: (t5) => isString && /^(?:[A-Za-z\xAA\xB5\xBA\xC0-\xD6\xD8-\xF6\xF8-\u02C1\u02C6-\u02D1\u02E0-\u02E4\u02EC\u02EE\u0370-\u0374\u0376\u0377\u037A-\u037D\u037F\u0386\u0388-\u038A\u038C\u038E-\u03A1\u03A3-\u03F5\u03F7-\u0481\u048A-\u052F\u0531-\u0556\u0559\u0560-\u0588\u05D0-\u05EA\u05EF-\u05F2\u0620-\u064A\u066E\u066F\u0671-\u06D3\u06D5\u06E5\u06E6\u06EE\u06EF\u06FA-\u06FC\u06FF\u0710\u0712-\u072F\u074D-\u07A5\u07B1\u07CA-\u07EA\u07F4\u07F5\u07FA\u0800-\u0815\u081A\u0824\u0828\u0840-\u0858\u0860-\u086A\u0870-\u0887\u0889-\u088F\u08A0-\u08C9\u0904-\u0939\u093D\u0950\u0958-\u0961\u0971-\u0980\u0985-\u098C\u098F\u0990\u0993-\u09A8\u09AA-\u09B0\u09B2\u09B6-\u09B9\u09BD\u09CE\u09DC\u09DD\u09DF-\u09E1\u09F0\u09F1\u09FC\u0A05-\u0A0A\u0A0F\u0A10\u0A13-\u0A28\u0A2A-\u0A30\u0A32\u0A33\u0A35\u0A36\u0A38\u0A39\u0A59-\u0A5C\u0A5E\u0A72-\u0A74\u0A85-\u0A8D\u0A8F-\u0A91\u0A93-\u0AA8\u0AAA-\u0AB0\u0AB2\u0AB3\u0AB5-\u0AB9\u0ABD\u0AD0\u0AE0\u0AE1\u0AF9\u0B05-\u0B0C\u0B0F\u0B10\u0B13-\u0B28\u0B2A-\u0B30\u0B32\u0B33\u0B35-\u0B39\u0B3D\u0B5C\u0B5D\u0B5F-\u0B61\u0B71\u0B83\u0B85-\u0B8A\u0B8E-\u0B90\u0B92-\u0B95\u0B99\u0B9A\u0B9C\u0B9E\u0B9F\u0BA3\u0BA4\u0BA8-\u0BAA\u0BAE-\u0BB9\u0BD0\u0C05-\u0C0C\u0C0E-\u0C10\u0C12-\u0C28\u0C2A-\u0C39\u0C3D\u0C58-\u0C5A\u0C5C\u0C5D\u0C60\u0C61\u0C80\u0C85-\u0C8C\u0C8E-\u0C90\u0C92-\u0CA8\u0CAA-\u0CB3\u0CB5-\u0CB9\u0CBD\u0CDC-\u0CDE\u0CE0\u0CE1\u0CF1\u0CF2\u0D04-\u0D0C\u0D0E-\u0D10\u0D12-\u0D3A\u0D3D\u0D4E\u0D54-\u0D56\u0D5F-\u0D61\u0D7A-\u0D7F\u0D85-\u0D96\u0D9A-\u0DB1\u0DB3-\u0DBB\u0DBD\u0DC0-\u0DC6\u0E01-\u0E30\u0E32\u0E33\u0E40-\u0E46\u0E81\u0E82\u0E84\u0E86-\u0E8A\u0E8C-\u0EA3\u0EA5\u0EA7-\u0EB0\u0EB2\u0EB3\u0EBD\u0EC0-\u0EC4\u0EC6\u0EDC-\u0EDF\u0F00\u0F40-\u0F47\u0F49-\u0F6C\u0F88-\u0F8C\u1000-\u102A\u103F\u1050-\u1055\u105A-\u105D\u1061\u1065\u1066\u106E-\u1070\u1075-\u1081\u108E\u10A0-\u10C5\u10C7\u10CD\u10D0-\u10FA\u10FC-\u1248\u124A-\u124D\u1250-\u1256\u1258\u125A-\u125D\u1260-\u1288\u128A-\u128D\u1290-\u12B0\u12B2-\u12B5\u12B8-\u12BE\u12C0\u12C2-\u12C5\u12C8-\u12D6\u12D8-\u1310\u1312-\u1315\u1318-\u135A\u1380-\u138F\u13A0-\u13F5\u13F8-\u13FD\u1401-\u166C\u166F-\u167F\u1681-\u169A\u16A0-\u16EA\u16F1-\u16F8\u1700-\u1711\u171F-\u1731\u1740-\u1751\u1760-\u176C\u176E-\u1770\u1780-\u17B3\u17D7\u17DC\u1820-\u1878\u1880-\u1884\u1887-\u18A8\u18AA\u18B0-\u18F5\u1900-\u191E\u1950-\u196D\u1970-\u1974\u1980-\u19AB\u19B0-\u19C9\u1A00-\u1A16\u1A20-\u1A54\u1AA7\u1B05-\u1B33\u1B45-\u1B4C\u1B83-\u1BA0\u1BAE\u1BAF\u1BBA-\u1BE5\u1C00-\u1C23\u1C4D-\u1C4F\u1C5A-\u1C7D\u1C80-\u1C8A\u1C90-\u1CBA\u1CBD-\u1CBF\u1CE9-\u1CEC\u1CEE-\u1CF3\u1CF5\u1CF6\u1CFA\u1D00-\u1DBF\u1E00-\u1F15\u1F18-\u1F1D\u1F20-\u1F45\u1F48-\u1F4D\u1F50-\u1F57\u1F59\u1F5B\u1F5D\u1F5F-\u1F7D\u1F80-\u1FB4\u1FB6-\u1FBC\u1FBE\u1FC2-\u1FC4\u1FC6-\u1FCC\u1FD0-\u1FD3\u1FD6-\u1FDB\u1FE0-\u1FEC\u1FF2-\u1FF4\u1FF6-\u1FFC\u2071\u207F\u2090-\u209C\u2102\u2107\u210A-\u2113\u2115\u2119-\u211D\u2124\u2126\u2128\u212A-\u212D\u212F-\u2139\u213C-\u213F\u2145-\u2149\u214E\u2183\u2184\u2C00-\u2CE4\u2CEB-\u2CEE\u2CF2\u2CF3\u2D00-\u2D25\u2D27\u2D2D\u2D30-\u2D67\u2D6F\u2D80-\u2D96\u2DA0-\u2DA6\u2DA8-\u2DAE\u2DB0-\u2DB6\u2DB8-\u2DBE\u2DC0-\u2DC6\u2DC8-\u2DCE\u2DD0-\u2DD6\u2DD8-\u2DDE\u2E2F\u3005\u3006\u3031-\u3035\u303B\u303C\u3041-\u3096\u309D-\u309F\u30A1-\u30FA\u30FC-\u30FF\u3105-\u312F\u3131-\u318E\u31A0-\u31BF\u31F0-\u31FF\u3400-\u4DBF\u4E00-\uA48C\uA4D0-\uA4FD\uA500-\uA60C\uA610-\uA61F\uA62A\uA62B\uA640-\uA66E\uA67F-\uA69D\uA6A0-\uA6E5\uA717-\uA71F\uA722-\uA788\uA78B-\uA7DC\uA7F1-\uA801\uA803-\uA805\uA807-\uA80A\uA80C-\uA822\uA840-\uA873\uA882-\uA8B3\uA8F2-\uA8F7\uA8FB\uA8FD\uA8FE\uA90A-\uA925\uA930-\uA946\uA960-\uA97C\uA984-\uA9B2\uA9CF\uA9E0-\uA9E4\uA9E6-\uA9EF\uA9FA-\uA9FE\uAA00-\uAA28\uAA40-\uAA42\uAA44-\uAA4B\uAA60-\uAA76\uAA7A\uAA7E-\uAAAF\uAAB1\uAAB5\uAAB6\uAAB9-\uAABD\uAAC0\uAAC2\uAADB-\uAADD\uAAE0-\uAAEA\uAAF2-\uAAF4\uAB01-\uAB06\uAB09-\uAB0E\uAB11-\uAB16\uAB20-\uAB26\uAB28-\uAB2E\uAB30-\uAB5A\uAB5C-\uAB69\uAB70-\uABE2\uAC00-\uD7A3\uD7B0-\uD7C6\uD7CB-\uD7FB\uF900-\uFA6D\uFA70-\uFAD9\uFB00-\uFB06\uFB13-\uFB17\uFB1D\uFB1F-\uFB28\uFB2A-\uFB36\uFB38-\uFB3C\uFB3E\uFB40\uFB41\uFB43\uFB44\uFB46-\uFBB1\uFBD3-\uFD3D\uFD50-\uFD8F\uFD92-\uFDC7\uFDF0-\uFDFB\uFE70-\uFE74\uFE76-\uFEFC\uFF21-\uFF3A\uFF41-\uFF5A\uFF66-\uFFBE\uFFC2-\uFFC7\uFFCA-\uFFCF\uFFD2-\uFFD7\uFFDA-\uFFDC]|\uD800[\uDC00-\uDC0B\uDC0D-\uDC26\uDC28-\uDC3A\uDC3C\uDC3D\uDC3F-\uDC4D\uDC50-\uDC5D\uDC80-\uDCFA\uDE80-\uDE9C\uDEA0-\uDED0\uDF00-\uDF1F\uDF2D-\uDF40\uDF42-\uDF49\uDF50-\uDF75\uDF80-\uDF9D\uDFA0-\uDFC3\uDFC8-\uDFCF]|\uD801[\uDC00-\uDC9D\uDCB0-\uDCD3\uDCD8-\uDCFB\uDD00-\uDD27\uDD30-\uDD63\uDD70-\uDD7A\uDD7C-\uDD8A\uDD8C-\uDD92\uDD94\uDD95\uDD97-\uDDA1\uDDA3-\uDDB1\uDDB3-\uDDB9\uDDBB\uDDBC\uDDC0-\uDDF3\uDE00-\uDF36\uDF40-\uDF55\uDF60-\uDF67\uDF80-\uDF85\uDF87-\uDFB0\uDFB2-\uDFBA]|\uD802[\uDC00-\uDC05\uDC08\uDC0A-\uDC35\uDC37\uDC38\uDC3C\uDC3F-\uDC55\uDC60-\uDC76\uDC80-\uDC9E\uDCE0-\uDCF2\uDCF4\uDCF5\uDD00-\uDD15\uDD20-\uDD39\uDD40-\uDD59\uDD80-\uDDB7\uDDBE\uDDBF\uDE00\uDE10-\uDE13\uDE15-\uDE17\uDE19-\uDE35\uDE60-\uDE7C\uDE80-\uDE9C\uDEC0-\uDEC7\uDEC9-\uDEE4\uDF00-\uDF35\uDF40-\uDF55\uDF60-\uDF72\uDF80-\uDF91]|\uD803[\uDC00-\uDC48\uDC80-\uDCB2\uDCC0-\uDCF2\uDD00-\uDD23\uDD4A-\uDD65\uDD6F-\uDD85\uDE80-\uDEA9\uDEB0\uDEB1\uDEC2-\uDEC7\uDF00-\uDF1C\uDF27\uDF30-\uDF45\uDF70-\uDF81\uDFB0-\uDFC4\uDFE0-\uDFF6]|\uD804[\uDC03-\uDC37\uDC71\uDC72\uDC75\uDC83-\uDCAF\uDCD0-\uDCE8\uDD03-\uDD26\uDD44\uDD47\uDD50-\uDD72\uDD76\uDD83-\uDDB2\uDDC1-\uDDC4\uDDDA\uDDDC\uDE00-\uDE11\uDE13-\uDE2B\uDE3F\uDE40\uDE80-\uDE86\uDE88\uDE8A-\uDE8D\uDE8F-\uDE9D\uDE9F-\uDEA8\uDEB0-\uDEDE\uDF05-\uDF0C\uDF0F\uDF10\uDF13-\uDF28\uDF2A-\uDF30\uDF32\uDF33\uDF35-\uDF39\uDF3D\uDF50\uDF5D-\uDF61\uDF80-\uDF89\uDF8B\uDF8E\uDF90-\uDFB5\uDFB7\uDFD1\uDFD3]|\uD805[\uDC00-\uDC34\uDC47-\uDC4A\uDC5F-\uDC61\uDC80-\uDCAF\uDCC4\uDCC5\uDCC7\uDD80-\uDDAE\uDDD8-\uDDDB\uDE00-\uDE2F\uDE44\uDE80-\uDEAA\uDEB8\uDF00-\uDF1A\uDF40-\uDF46]|\uD806[\uDC00-\uDC2B\uDCA0-\uDCDF\uDCFF-\uDD06\uDD09\uDD0C-\uDD13\uDD15\uDD16\uDD18-\uDD2F\uDD3F\uDD41\uDDA0-\uDDA7\uDDAA-\uDDD0\uDDE1\uDDE3\uDE00\uDE0B-\uDE32\uDE3A\uDE50\uDE5C-\uDE89\uDE9D\uDEB0-\uDEF8\uDFC0-\uDFE0]|\uD807[\uDC00-\uDC08\uDC0A-\uDC2E\uDC40\uDC72-\uDC8F\uDD00-\uDD06\uDD08\uDD09\uDD0B-\uDD30\uDD46\uDD60-\uDD65\uDD67\uDD68\uDD6A-\uDD89\uDD98\uDDB0-\uDDDB\uDEE0-\uDEF2\uDF02\uDF04-\uDF10\uDF12-\uDF33\uDFB0]|\uD808[\uDC00-\uDF99]|\uD809[\uDC80-\uDD43]|\uD80B[\uDF90-\uDFF0]|[\uD80C\uD80E\uD80F\uD81C-\uD822\uD840-\uD868\uD86A-\uD86D\uD86F-\uD872\uD874-\uD879\uD880-\uD883\uD885-\uD88C][\uDC00-\uDFFF]|\uD80D[\uDC00-\uDC2F\uDC41-\uDC46\uDC60-\uDFFF]|\uD810[\uDC00-\uDFFA]|\uD811[\uDC00-\uDE46]|\uD818[\uDD00-\uDD1D]|\uD81A[\uDC00-\uDE38\uDE40-\uDE5E\uDE70-\uDEBE\uDED0-\uDEED\uDF00-\uDF2F\uDF40-\uDF43\uDF63-\uDF77\uDF7D-\uDF8F]|\uD81B[\uDD40-\uDD6C\uDE40-\uDE7F\uDEA0-\uDEB8\uDEBB-\uDED3\uDF00-\uDF4A\uDF50\uDF93-\uDF9F\uDFE0\uDFE1\uDFE3\uDFF2\uDFF3]|\uD823[\uDC00-\uDCD5\uDCFF-\uDD1E\uDD80-\uDDF2]|\uD82B[\uDFF0-\uDFF3\uDFF5-\uDFFB\uDFFD\uDFFE]|\uD82C[\uDC00-\uDD22\uDD32\uDD50-\uDD52\uDD55\uDD64-\uDD67\uDD70-\uDEFB]|\uD82F[\uDC00-\uDC6A\uDC70-\uDC7C\uDC80-\uDC88\uDC90-\uDC99]|\uD835[\uDC00-\uDC54\uDC56-\uDC9C\uDC9E\uDC9F\uDCA2\uDCA5\uDCA6\uDCA9-\uDCAC\uDCAE-\uDCB9\uDCBB\uDCBD-\uDCC3\uDCC5-\uDD05\uDD07-\uDD0A\uDD0D-\uDD14\uDD16-\uDD1C\uDD1E-\uDD39\uDD3B-\uDD3E\uDD40-\uDD44\uDD46\uDD4A-\uDD50\uDD52-\uDEA5\uDEA8-\uDEC0\uDEC2-\uDEDA\uDEDC-\uDEFA\uDEFC-\uDF14\uDF16-\uDF34\uDF36-\uDF4E\uDF50-\uDF6E\uDF70-\uDF88\uDF8A-\uDFA8\uDFAA-\uDFC2\uDFC4-\uDFCB]|\uD837[\uDF00-\uDF1E\uDF25-\uDF2A]|\uD838[\uDC30-\uDC6D\uDD00-\uDD2C\uDD37-\uDD3D\uDD4E\uDE90-\uDEAD\uDEC0-\uDEEB]|\uD839[\uDCD0-\uDCEB\uDDD0-\uDDED\uDDF0\uDEC0-\uDEDE\uDEE0-\uDEE2\uDEE4\uDEE5\uDEE7-\uDEED\uDEF0-\uDEF4\uDEFE\uDEFF\uDFE0-\uDFE6\uDFE8-\uDFEB\uDFED\uDFEE\uDFF0-\uDFFE]|\uD83A[\uDC00-\uDCC4\uDD00-\uDD43\uDD4B]|\uD83B[\uDE00-\uDE03\uDE05-\uDE1F\uDE21\uDE22\uDE24\uDE27\uDE29-\uDE32\uDE34-\uDE37\uDE39\uDE3B\uDE42\uDE47\uDE49\uDE4B\uDE4D-\uDE4F\uDE51\uDE52\uDE54\uDE57\uDE59\uDE5B\uDE5D\uDE5F\uDE61\uDE62\uDE64\uDE67-\uDE6A\uDE6C-\uDE72\uDE74-\uDE77\uDE79-\uDE7C\uDE7E\uDE80-\uDE89\uDE8B-\uDE9B\uDEA1-\uDEA3\uDEA5-\uDEA9\uDEAB-\uDEBB]|\uD869[\uDC00-\uDEDF\uDF00-\uDFFF]|\uD86E[\uDC00-\uDC1D\uDC20-\uDFFF]|\uD873[\uDC00-\uDEAD\uDEB0-\uDFFF]|\uD87A[\uDC00-\uDFE0\uDFF0-\uDFFF]|\uD87B[\uDC00-\uDE5D]|\uD87E[\uDC00-\uDE1D]|\uD884[\uDC00-\uDF4A\uDF50-\uDFFF]|\uD88D[\uDC00-\uDC79])(?:[0-9A-Za-z\xAA\xB5\xBA\xC0-\xD6\xD8-\xF6\xF8-\u02C1\u02C6-\u02D1\u02E0-\u02E4\u02EC\u02EE\u0370-\u0374\u0376\u0377\u037A-\u037D\u037F\u0386\u0388-\u038A\u038C\u038E-\u03A1\u03A3-\u03F5\u03F7-\u0481\u048A-\u052F\u0531-\u0556\u0559\u0560-\u0588\u05D0-\u05EA\u05EF-\u05F2\u0620-\u064A\u066E\u066F\u0671-\u06D3\u06D5\u06E5\u06E6\u06EE\u06EF\u06FA-\u06FC\u06FF\u0710\u0712-\u072F\u074D-\u07A5\u07B1\u07CA-\u07EA\u07F4\u07F5\u07FA\u0800-\u0815\u081A\u0824\u0828\u0840-\u0858\u0860-\u086A\u0870-\u0887\u0889-\u088F\u08A0-\u08C9\u0904-\u0939\u093D\u0950\u0958-\u0961\u0971-\u0980\u0985-\u098C\u098F\u0990\u0993-\u09A8\u09AA-\u09B0\u09B2\u09B6-\u09B9\u09BD\u09CE\u09DC\u09DD\u09DF-\u09E1\u09F0\u09F1\u09FC\u0A05-\u0A0A\u0A0F\u0A10\u0A13-\u0A28\u0A2A-\u0A30\u0A32\u0A33\u0A35\u0A36\u0A38\u0A39\u0A59-\u0A5C\u0A5E\u0A72-\u0A74\u0A85-\u0A8D\u0A8F-\u0A91\u0A93-\u0AA8\u0AAA-\u0AB0\u0AB2\u0AB3\u0AB5-\u0AB9\u0ABD\u0AD0\u0AE0\u0AE1\u0AF9\u0B05-\u0B0C\u0B0F\u0B10\u0B13-\u0B28\u0B2A-\u0B30\u0B32\u0B33\u0B35-\u0B39\u0B3D\u0B5C\u0B5D\u0B5F-\u0B61\u0B71\u0B83\u0B85-\u0B8A\u0B8E-\u0B90\u0B92-\u0B95\u0B99\u0B9A\u0B9C\u0B9E\u0B9F\u0BA3\u0BA4\u0BA8-\u0BAA\u0BAE-\u0BB9\u0BD0\u0C05-\u0C0C\u0C0E-\u0C10\u0C12-\u0C28\u0C2A-\u0C39\u0C3D\u0C58-\u0C5A\u0C5C\u0C5D\u0C60\u0C61\u0C80\u0C85-\u0C8C\u0C8E-\u0C90\u0C92-\u0CA8\u0CAA-\u0CB3\u0CB5-\u0CB9\u0CBD\u0CDC-\u0CDE\u0CE0\u0CE1\u0CF1\u0CF2\u0D04-\u0D0C\u0D0E-\u0D10\u0D12-\u0D3A\u0D3D\u0D4E\u0D54-\u0D56\u0D5F-\u0D61\u0D7A-\u0D7F\u0D85-\u0D96\u0D9A-\u0DB1\u0DB3-\u0DBB\u0DBD\u0DC0-\u0DC6\u0E01-\u0E30\u0E32\u0E33\u0E40-\u0E46\u0E81\u0E82\u0E84\u0E86-\u0E8A\u0E8C-\u0EA3\u0EA5\u0EA7-\u0EB0\u0EB2\u0EB3\u0EBD\u0EC0-\u0EC4\u0EC6\u0EDC-\u0EDF\u0F00\u0F40-\u0F47\u0F49-\u0F6C\u0F88-\u0F8C\u1000-\u102A\u103F\u1050-\u1055\u105A-\u105D\u1061\u1065\u1066\u106E-\u1070\u1075-\u1081\u108E\u10A0-\u10C5\u10C7\u10CD\u10D0-\u10FA\u10FC-\u1248\u124A-\u124D\u1250-\u1256\u1258\u125A-\u125D\u1260-\u1288\u128A-\u128D\u1290-\u12B0\u12B2-\u12B5\u12B8-\u12BE\u12C0\u12C2-\u12C5\u12C8-\u12D6\u12D8-\u1310\u1312-\u1315\u1318-\u135A\u1380-\u138F\u13A0-\u13F5\u13F8-\u13FD\u1401-\u166C\u166F-\u167F\u1681-\u169A\u16A0-\u16EA\u16F1-\u16F8\u1700-\u1711\u171F-\u1731\u1740-\u1751\u1760-\u176C\u176E-\u1770\u1780-\u17B3\u17D7\u17DC\u1820-\u1878\u1880-\u1884\u1887-\u18A8\u18AA\u18B0-\u18F5\u1900-\u191E\u1950-\u196D\u1970-\u1974\u1980-\u19AB\u19B0-\u19C9\u1A00-\u1A16\u1A20-\u1A54\u1AA7\u1B05-\u1B33\u1B45-\u1B4C\u1B83-\u1BA0\u1BAE\u1BAF\u1BBA-\u1BE5\u1C00-\u1C23\u1C4D-\u1C4F\u1C5A-\u1C7D\u1C80-\u1C8A\u1C90-\u1CBA\u1CBD-\u1CBF\u1CE9-\u1CEC\u1CEE-\u1CF3\u1CF5\u1CF6\u1CFA\u1D00-\u1DBF\u1E00-\u1F15\u1F18-\u1F1D\u1F20-\u1F45\u1F48-\u1F4D\u1F50-\u1F57\u1F59\u1F5B\u1F5D\u1F5F-\u1F7D\u1F80-\u1FB4\u1FB6-\u1FBC\u1FBE\u1FC2-\u1FC4\u1FC6-\u1FCC\u1FD0-\u1FD3\u1FD6-\u1FDB\u1FE0-\u1FEC\u1FF2-\u1FF4\u1FF6-\u1FFC\u2071\u207F\u2090-\u209C\u2102\u2107\u210A-\u2113\u2115\u2119-\u211D\u2124\u2126\u2128\u212A-\u212D\u212F-\u2139\u213C-\u213F\u2145-\u2149\u214E\u2183\u2184\u2C00-\u2CE4\u2CEB-\u2CEE\u2CF2\u2CF3\u2D00-\u2D25\u2D27\u2D2D\u2D30-\u2D67\u2D6F\u2D80-\u2D96\u2DA0-\u2DA6\u2DA8-\u2DAE\u2DB0-\u2DB6\u2DB8-\u2DBE\u2DC0-\u2DC6\u2DC8-\u2DCE\u2DD0-\u2DD6\u2DD8-\u2DDE\u2E2F\u3005\u3006\u3031-\u3035\u303B\u303C\u3041-\u3096\u309D-\u309F\u30A1-\u30FA\u30FC-\u30FF\u3105-\u312F\u3131-\u318E\u31A0-\u31BF\u31F0-\u31FF\u3400-\u4DBF\u4E00-\uA48C\uA4D0-\uA4FD\uA500-\uA60C\uA610-\uA61F\uA62A\uA62B\uA640-\uA66E\uA67F-\uA69D\uA6A0-\uA6E5\uA717-\uA71F\uA722-\uA788\uA78B-\uA7DC\uA7F1-\uA801\uA803-\uA805\uA807-\uA80A\uA80C-\uA822\uA840-\uA873\uA882-\uA8B3\uA8F2-\uA8F7\uA8FB\uA8FD\uA8FE\uA90A-\uA925\uA930-\uA946\uA960-\uA97C\uA984-\uA9B2\uA9CF\uA9E0-\uA9E4\uA9E6-\uA9EF\uA9FA-\uA9FE\uAA00-\uAA28\uAA40-\uAA42\uAA44-\uAA4B\uAA60-\uAA76\uAA7A\uAA7E-\uAAAF\uAAB1\uAAB5\uAAB6\uAAB9-\uAABD\uAAC0\uAAC2\uAADB-\uAADD\uAAE0-\uAAEA\uAAF2-\uAAF4\uAB01-\uAB06\uAB09-\uAB0E\uAB11-\uAB16\uAB20-\uAB26\uAB28-\uAB2E\uAB30-\uAB5A\uAB5C-\uAB69\uAB70-\uABE2\uAC00-\uD7A3\uD7B0-\uD7C6\uD7CB-\uD7FB\uF900-\uFA6D\uFA70-\uFAD9\uFB00-\uFB06\uFB13-\uFB17\uFB1D\uFB1F-\uFB28\uFB2A-\uFB36\uFB38-\uFB3C\uFB3E\uFB40\uFB41\uFB43\uFB44\uFB46-\uFBB1\uFBD3-\uFD3D\uFD50-\uFD8F\uFD92-\uFDC7\uFDF0-\uFDFB\uFE70-\uFE74\uFE76-\uFEFC\uFF21-\uFF3A\uFF41-\uFF5A\uFF66-\uFFBE\uFFC2-\uFFC7\uFFCA-\uFFCF\uFFD2-\uFFD7\uFFDA-\uFFDC]|\uD800[\uDC00-\uDC0B\uDC0D-\uDC26\uDC28-\uDC3A\uDC3C\uDC3D\uDC3F-\uDC4D\uDC50-\uDC5D\uDC80-\uDCFA\uDE80-\uDE9C\uDEA0-\uDED0\uDF00-\uDF1F\uDF2D-\uDF40\uDF42-\uDF49\uDF50-\uDF75\uDF80-\uDF9D\uDFA0-\uDFC3\uDFC8-\uDFCF]|\uD801[\uDC00-\uDC9D\uDCB0-\uDCD3\uDCD8-\uDCFB\uDD00-\uDD27\uDD30-\uDD63\uDD70-\uDD7A\uDD7C-\uDD8A\uDD8C-\uDD92\uDD94\uDD95\uDD97-\uDDA1\uDDA3-\uDDB1\uDDB3-\uDDB9\uDDBB\uDDBC\uDDC0-\uDDF3\uDE00-\uDF36\uDF40-\uDF55\uDF60-\uDF67\uDF80-\uDF85\uDF87-\uDFB0\uDFB2-\uDFBA]|\uD802[\uDC00-\uDC05\uDC08\uDC0A-\uDC35\uDC37\uDC38\uDC3C\uDC3F-\uDC55\uDC60-\uDC76\uDC80-\uDC9E\uDCE0-\uDCF2\uDCF4\uDCF5\uDD00-\uDD15\uDD20-\uDD39\uDD40-\uDD59\uDD80-\uDDB7\uDDBE\uDDBF\uDE00\uDE10-\uDE13\uDE15-\uDE17\uDE19-\uDE35\uDE60-\uDE7C\uDE80-\uDE9C\uDEC0-\uDEC7\uDEC9-\uDEE4\uDF00-\uDF35\uDF40-\uDF55\uDF60-\uDF72\uDF80-\uDF91]|\uD803[\uDC00-\uDC48\uDC80-\uDCB2\uDCC0-\uDCF2\uDD00-\uDD23\uDD4A-\uDD65\uDD6F-\uDD85\uDE80-\uDEA9\uDEB0\uDEB1\uDEC2-\uDEC7\uDF00-\uDF1C\uDF27\uDF30-\uDF45\uDF70-\uDF81\uDFB0-\uDFC4\uDFE0-\uDFF6]|\uD804[\uDC03-\uDC37\uDC71\uDC72\uDC75\uDC83-\uDCAF\uDCD0-\uDCE8\uDD03-\uDD26\uDD44\uDD47\uDD50-\uDD72\uDD76\uDD83-\uDDB2\uDDC1-\uDDC4\uDDDA\uDDDC\uDE00-\uDE11\uDE13-\uDE2B\uDE3F\uDE40\uDE80-\uDE86\uDE88\uDE8A-\uDE8D\uDE8F-\uDE9D\uDE9F-\uDEA8\uDEB0-\uDEDE\uDF05-\uDF0C\uDF0F\uDF10\uDF13-\uDF28\uDF2A-\uDF30\uDF32\uDF33\uDF35-\uDF39\uDF3D\uDF50\uDF5D-\uDF61\uDF80-\uDF89\uDF8B\uDF8E\uDF90-\uDFB5\uDFB7\uDFD1\uDFD3]|\uD805[\uDC00-\uDC34\uDC47-\uDC4A\uDC5F-\uDC61\uDC80-\uDCAF\uDCC4\uDCC5\uDCC7\uDD80-\uDDAE\uDDD8-\uDDDB\uDE00-\uDE2F\uDE44\uDE80-\uDEAA\uDEB8\uDF00-\uDF1A\uDF40-\uDF46]|\uD806[\uDC00-\uDC2B\uDCA0-\uDCDF\uDCFF-\uDD06\uDD09\uDD0C-\uDD13\uDD15\uDD16\uDD18-\uDD2F\uDD3F\uDD41\uDDA0-\uDDA7\uDDAA-\uDDD0\uDDE1\uDDE3\uDE00\uDE0B-\uDE32\uDE3A\uDE50\uDE5C-\uDE89\uDE9D\uDEB0-\uDEF8\uDFC0-\uDFE0]|\uD807[\uDC00-\uDC08\uDC0A-\uDC2E\uDC40\uDC72-\uDC8F\uDD00-\uDD06\uDD08\uDD09\uDD0B-\uDD30\uDD46\uDD60-\uDD65\uDD67\uDD68\uDD6A-\uDD89\uDD98\uDDB0-\uDDDB\uDEE0-\uDEF2\uDF02\uDF04-\uDF10\uDF12-\uDF33\uDFB0]|\uD808[\uDC00-\uDF99]|\uD809[\uDC80-\uDD43]|\uD80B[\uDF90-\uDFF0]|[\uD80C\uD80E\uD80F\uD81C-\uD822\uD840-\uD868\uD86A-\uD86D\uD86F-\uD872\uD874-\uD879\uD880-\uD883\uD885-\uD88C][\uDC00-\uDFFF]|\uD80D[\uDC00-\uDC2F\uDC41-\uDC46\uDC60-\uDFFF]|\uD810[\uDC00-\uDFFA]|\uD811[\uDC00-\uDE46]|\uD818[\uDD00-\uDD1D]|\uD81A[\uDC00-\uDE38\uDE40-\uDE5E\uDE70-\uDEBE\uDED0-\uDEED\uDF00-\uDF2F\uDF40-\uDF43\uDF63-\uDF77\uDF7D-\uDF8F]|\uD81B[\uDD40-\uDD6C\uDE40-\uDE7F\uDEA0-\uDEB8\uDEBB-\uDED3\uDF00-\uDF4A\uDF50\uDF93-\uDF9F\uDFE0\uDFE1\uDFE3\uDFF2\uDFF3]|\uD823[\uDC00-\uDCD5\uDCFF-\uDD1E\uDD80-\uDDF2]|\uD82B[\uDFF0-\uDFF3\uDFF5-\uDFFB\uDFFD\uDFFE]|\uD82C[\uDC00-\uDD22\uDD32\uDD50-\uDD52\uDD55\uDD64-\uDD67\uDD70-\uDEFB]|\uD82F[\uDC00-\uDC6A\uDC70-\uDC7C\uDC80-\uDC88\uDC90-\uDC99]|\uD835[\uDC00-\uDC54\uDC56-\uDC9C\uDC9E\uDC9F\uDCA2\uDCA5\uDCA6\uDCA9-\uDCAC\uDCAE-\uDCB9\uDCBB\uDCBD-\uDCC3\uDCC5-\uDD05\uDD07-\uDD0A\uDD0D-\uDD14\uDD16-\uDD1C\uDD1E-\uDD39\uDD3B-\uDD3E\uDD40-\uDD44\uDD46\uDD4A-\uDD50\uDD52-\uDEA5\uDEA8-\uDEC0\uDEC2-\uDEDA\uDEDC-\uDEFA\uDEFC-\uDF14\uDF16-\uDF34\uDF36-\uDF4E\uDF50-\uDF6E\uDF70-\uDF88\uDF8A-\uDFA8\uDFAA-\uDFC2\uDFC4-\uDFCB]|\uD837[\uDF00-\uDF1E\uDF25-\uDF2A]|\uD838[\uDC30-\uDC6D\uDD00-\uDD2C\uDD37-\uDD3D\uDD4E\uDE90-\uDEAD\uDEC0-\uDEEB]|\uD839[\uDCD0-\uDCEB\uDDD0-\uDDED\uDDF0\uDEC0-\uDEDE\uDEE0-\uDEE2\uDEE4\uDEE5\uDEE7-\uDEED\uDEF0-\uDEF4\uDEFE\uDEFF\uDFE0-\uDFE6\uDFE8-\uDFEB\uDFED\uDFEE\uDFF0-\uDFFE]|\uD83A[\uDC00-\uDCC4\uDD00-\uDD43\uDD4B]|\uD83B[\uDE00-\uDE03\uDE05-\uDE1F\uDE21\uDE22\uDE24\uDE27\uDE29-\uDE32\uDE34-\uDE37\uDE39\uDE3B\uDE42\uDE47\uDE49\uDE4B\uDE4D-\uDE4F\uDE51\uDE52\uDE54\uDE57\uDE59\uDE5B\uDE5D\uDE5F\uDE61\uDE62\uDE64\uDE67-\uDE6A\uDE6C-\uDE72\uDE74-\uDE77\uDE79-\uDE7C\uDE7E\uDE80-\uDE89\uDE8B-\uDE9B\uDEA1-\uDEA3\uDEA5-\uDEA9\uDEAB-\uDEBB]|\uD869[\uDC00-\uDEDF\uDF00-\uDFFF]|\uD86E[\uDC00-\uDC1D\uDC20-\uDFFF]|\uD873[\uDC00-\uDEAD\uDEB0-\uDFFF]|\uD87A[\uDC00-\uDFE0\uDFF0-\uDFFF]|\uD87B[\uDC00-\uDE5D]|\uD87E[\uDC00-\uDE1D]|\uD884[\uDC00-\uDF4A\uDF50-\uDFFF]|\uD88D[\uDC00-\uDC79])*$/.test(t5) }, { name: `string`, test: isString }, { name: `Chain`, test: isChain }, { name: `Array`, test: isArray }, { name: `Matrix`, test: isMatrix }, { name: `DenseMatrix`, test: isDenseMatrix }, { name: `SparseMatrix`, test: isSparseMatrix }, { name: `Range`, test: isRange }, { name: `Index`, test: isIndex }, { name: `boolean`, test: isBoolean$1 }, { name: `ResultSet`, test: isResultSet }, { name: `Help`, test: isHelp }, { name: `function`, test: isFunction$1 }, { name: `Date`, test: isDate }, { name: `RegExp`, test: isRegExp }, { name: `null`, test: isNull$1 }, { name: `undefined`, test: isUndefined$1 }, { name: `AccessorNode`, test: isAccessorNode }, { name: `ArrayNode`, test: isArrayNode }, { name: `AssignmentNode`, test: isAssignmentNode }, { name: `BlockNode`, test: isBlockNode }, { name: `ConditionalNode`, test: isConditionalNode }, { name: `ConstantNode`, test: isConstantNode }, { name: `FunctionNode`, test: isFunctionNode }, { name: `FunctionAssignmentNode`, test: isFunctionAssignmentNode }, { name: `IndexNode`, test: isIndexNode }, { name: `Node`, test: isNode }, { name: `ObjectNode`, test: isObjectNode }, { name: `OperatorNode`, test: isOperatorNode }, { name: `ParenthesisNode`, test: isParenthesisNode }, { name: `RangeNode`, test: isRangeNode }, { name: `RelationalNode`, test: isRelationalNode }, { name: `SymbolNode`, test: isSymbolNode }, { name: `Map`, test: isMap }, { name: `Object`, test: isObject$1 }]), o2.addConversions([{ from: `number`, to: `BigNumber`, convert: function(t5) {
    if (n2 || throwNoBignumber(t5), digits(t5) > 15) throw TypeError(`Cannot implicitly convert a number with >15 significant digits to BigNumber (value: ` + t5 + `). Use function bignumber(x) to convert to BigNumber.`);
    return new n2(t5);
  } }, { from: `number`, to: `Complex`, convert: function(t5) {
    return r2 || throwNoComplex(t5), new r2(t5, 0);
  } }, { from: `BigNumber`, to: `Complex`, convert: function(t5) {
    return r2 || throwNoComplex(t5), new r2(t5.toNumber(), 0);
  } }, { from: `bigint`, to: `number`, convert: function(t5) {
    if (t5 > 2 ** 53 - 1) throw TypeError(`Cannot implicitly convert bigint to number: value exceeds the max safe integer value (value: ` + t5 + `)`);
    return Number(t5);
  } }, { from: `bigint`, to: `BigNumber`, convert: function(t5) {
    return n2 || throwNoBignumber(t5), new n2(t5.toString());
  } }, { from: `bigint`, to: `Fraction`, convert: function(t5) {
    return a2 || throwNoFraction(t5), new a2(t5);
  } }, { from: `Fraction`, to: `BigNumber`, convert: function(t5) {
    throw TypeError(`Cannot implicitly convert a Fraction to BigNumber or vice versa. Use function bignumber(x) to convert to BigNumber or fraction(x) to convert to Fraction.`);
  } }, { from: `Fraction`, to: `Complex`, convert: function(t5) {
    return r2 || throwNoComplex(t5), new r2(t5.valueOf(), 0);
  } }, { from: `number`, to: `Fraction`, convert: function(t5) {
    a2 || throwNoFraction(t5);
    var n3 = new a2(t5);
    if (n3.valueOf() !== t5) throw TypeError(`Cannot implicitly convert a number to a Fraction when there will be a loss of precision (value: ` + t5 + `). Use function fraction(x) to convert to Fraction.`);
    return n3;
  } }, { from: `string`, to: `number`, convert: function(t5) {
    var n3 = Number(t5);
    if (isNaN(n3)) throw Error(`Cannot convert "` + t5 + `" to a number`);
    return n3;
  } }, { from: `string`, to: `BigNumber`, convert: function(t5) {
    n2 || throwNoBignumber(t5);
    try {
      return new n2(t5);
    } catch {
      throw Error(`Cannot convert "` + t5 + `" to BigNumber`);
    }
  } }, { from: `string`, to: `bigint`, convert: function(t5) {
    try {
      return BigInt(t5);
    } catch {
      throw Error(`Cannot convert "` + t5 + `" to BigInt`);
    }
  } }, { from: `string`, to: `Fraction`, convert: function(t5) {
    a2 || throwNoFraction(t5);
    try {
      return new a2(t5);
    } catch {
      throw Error(`Cannot convert "` + t5 + `" to Fraction`);
    }
  } }, { from: `string`, to: `Complex`, convert: function(t5) {
    r2 || throwNoComplex(t5);
    try {
      return new r2(t5);
    } catch {
      throw Error(`Cannot convert "` + t5 + `" to Complex`);
    }
  } }, { from: `boolean`, to: `number`, convert: function(t5) {
    return +t5;
  } }, { from: `boolean`, to: `BigNumber`, convert: function(t5) {
    return n2 || throwNoBignumber(t5), new n2(+t5);
  } }, { from: `boolean`, to: `bigint`, convert: function(t5) {
    return BigInt(+t5);
  } }, { from: `boolean`, to: `Fraction`, convert: function(t5) {
    return a2 || throwNoFraction(t5), new a2(+t5);
  } }, { from: `boolean`, to: `string`, convert: function(t5) {
    return String(t5);
  } }, { from: `Array`, to: `Matrix`, convert: function(t5) {
    return i2 || throwNoMatrix(), new i2(t5);
  } }, { from: `Matrix`, to: `Array`, convert: function(t5) {
    return t5.valueOf();
  } }]), o2.onMismatch = (t5, n3, r3) => {
    var i3 = o2.createError(t5, n3, r3);
    if ([`wrongType`, `mismatch`].includes(i3.data.category) && n3.length === 1 && isCollection(n3[0]) && r3.some((t6) => !t6.params.includes(`,`))) {
      var a3 = TypeError(`Function '${t5}' doesn't apply to matrices. To call it elementwise on a matrix 'M', try 'map(M, ${t5})'.`);
      throw a3.data = i3.data, a3;
    }
    throw i3;
  }, o2.onMismatch = (t5, n3, r3) => {
    var i3 = o2.createError(t5, n3, r3);
    if ([`wrongType`, `mismatch`].includes(i3.data.category) && n3.length === 1 && isCollection(n3[0]) && r3.some((t6) => !t6.params.includes(`,`))) {
      var a3 = TypeError(`Function '${t5}' doesn't apply to matrices. To call it elementwise on a matrix 'M', try 'map(M, ${t5})'.`);
      throw a3.data = i3.data, a3;
    }
    throw i3;
  }, o2;
});
function throwNoBignumber(t4) {
  throw Error(`Cannot convert value ${t4} into a BigNumber: no class 'BigNumber' provided`);
}
function throwNoComplex(t4) {
  throw Error(`Cannot convert value ${t4} into a Complex number: no class 'Complex' provided`);
}
function throwNoMatrix() {
  throw Error(`Cannot convert array into a Matrix: no class 'DenseMatrix' provided`);
}
function throwNoFraction(t4) {
  throw Error(`Cannot convert value ${t4} into a Fraction, no class 'Fraction' provided.`);
}
var name$321 = `ResultSet`, dependencies$321 = [], createResultSet = factory(name$321, dependencies$321, () => {
  function t4(n2) {
    if (!(this instanceof t4)) throw SyntaxError(`Constructor must be called with the new operator`);
    this.entries = n2 || [];
  }
  return t4.prototype.type = `ResultSet`, t4.prototype.isResultSet = true, t4.prototype.valueOf = function() {
    return this.entries;
  }, t4.prototype.toString = function() {
    return `[` + this.entries.map(String).join(`, `) + `]`;
  }, t4.prototype.toJSON = function() {
    return { mathjs: `ResultSet`, entries: this.entries };
  }, t4.fromJSON = function(n2) {
    return new t4(n2.entries);
  }, t4;
}, { isClass: true }), EXP_LIMIT = 9e15, MAX_DIGITS = 1e9, NUMERALS = `0123456789abcdef`, LN10 = `2.3025850929940456840179914546843642076011014886287729760333279009675726096773524802359972050895982983419677840422862486334095254650828067566662873690987816894829072083255546808437998948262331985283935053089653777326288461633662222876982198867465436674744042432743651550489343149393914796194044002221051017141748003688084012647080685567743216228355220114804663715659121373450747856947683463616792101806445070648000277502684916746550586856935673420670581136429224554405758925724208241314695689016758940256776311356919292033376587141660230105703089634572075440370847469940168269282808481184289314848524948644871927809676271275775397027668605952496716674183485704422507197965004714951050492214776567636938662976979522110718264549734772662425709429322582798502585509785265383207606726317164309505995087807523710333101197857547331541421808427543863591778117054309827482385045648019095610299291824318237525357709750539565187697510374970888692180205189339507238539205144634197265287286965110862571492198849978748873771345686209167058`, PI = `3.1415926535897932384626433832795028841971693993751058209749445923078164062862089986280348253421170679821480865132823066470938446095505822317253594081284811174502841027019385211055596446229489549303819644288109756659334461284756482337867831652712019091456485669234603486104543266482133936072602491412737245870066063155881748815209209628292540917153643678925903600113305305488204665213841469519415116094330572703657595919530921861173819326117931051185480744623799627495673518857527248912279381830119491298336733624406566430860213949463952247371907021798609437027705392171762931767523846748184676694051320005681271452635608277857713427577896091736371787214684409012249534301465495853710507922796892589235420199561121290219608640344181598136297747713099605187072113499999983729780499510597317328160963185950244594553469083026425223082533446850352619311881710100031378387528865875332083814206171776691473035982534904287554687311595628638823537875937519577818577805321712268066130019278766111959092164201989380952572010654858632789`, DEFAULTS = { precision: 20, rounding: 4, modulo: 1, toExpNeg: -7, toExpPos: 21, minE: -EXP_LIMIT, maxE: EXP_LIMIT, crypto: false }, inexact, quadrant, external = true, decimalError = `[DecimalError] `, invalidArgument = decimalError + `Invalid argument: `, precisionLimitExceeded = decimalError + `Precision limit exceeded`, cryptoUnavailable = decimalError + `crypto unavailable`, tag = `[object Decimal]`, mathfloor = Math.floor, mathpow = Math.pow, isBinary = /^0b([01]+(\.[01]*)?|\.[01]+)(p[+-]?\d+)?$/i, isHex = /^0x([0-9a-f]+(\.[0-9a-f]*)?|\.[0-9a-f]+)(p[+-]?\d+)?$/i, isOctal = /^0o([0-7]+(\.[0-7]*)?|\.[0-7]+)(p[+-]?\d+)?$/i, isDecimal = /^(\d+(\.\d*)?|\.\d+)(e[+-]?\d+)?$/i, BASE = 1e7, LOG_BASE = 7, MAX_SAFE_INTEGER = 9007199254740991, LN10_PRECISION = LN10.length - 1, PI_PRECISION = PI.length - 1, P$3 = { toStringTag: tag };
P$3.absoluteValue = P$3.abs = function() {
  var t4 = new this.constructor(this);
  return t4.s < 0 && (t4.s = 1), finalise(t4);
}, P$3.ceil = function() {
  return finalise(new this.constructor(this), this.e + 1, 2);
}, P$3.clampedTo = P$3.clamp = function(t4, n2) {
  var r2, i2 = this, a2 = i2.constructor;
  if (t4 = new a2(t4), n2 = new a2(n2), !t4.s || !n2.s) return new a2(NaN);
  if (t4.gt(n2)) throw Error(invalidArgument + n2);
  return r2 = i2.cmp(t4), r2 < 0 ? t4 : i2.cmp(n2) > 0 ? n2 : new a2(i2);
}, P$3.comparedTo = P$3.cmp = function(t4) {
  var n2, r2, i2, a2, o2 = this, s2 = o2.d, c2 = (t4 = new o2.constructor(t4)).d, l2 = o2.s, u2 = t4.s;
  if (!s2 || !c2) return !l2 || !u2 ? NaN : l2 === u2 ? s2 === c2 ? 0 : !s2 ^ l2 < 0 ? 1 : -1 : l2;
  if (!s2[0] || !c2[0]) return s2[0] ? l2 : c2[0] ? -u2 : 0;
  if (l2 !== u2) return l2;
  if (o2.e !== t4.e) return o2.e > t4.e ^ l2 < 0 ? 1 : -1;
  for (i2 = s2.length, a2 = c2.length, n2 = 0, r2 = i2 < a2 ? i2 : a2; n2 < r2; ++n2) if (s2[n2] !== c2[n2]) return s2[n2] > c2[n2] ^ l2 < 0 ? 1 : -1;
  return i2 === a2 ? 0 : i2 > a2 ^ l2 < 0 ? 1 : -1;
}, P$3.cosine = P$3.cos = function() {
  var t4, n2, r2 = this, i2 = r2.constructor;
  return r2.d ? r2.d[0] ? (t4 = i2.precision, n2 = i2.rounding, i2.precision = t4 + Math.max(r2.e, r2.sd()) + LOG_BASE, i2.rounding = 1, r2 = cosine(i2, toLessThanHalfPi(i2, r2)), i2.precision = t4, i2.rounding = n2, finalise(quadrant == 2 || quadrant == 3 ? r2.neg() : r2, t4, n2, true)) : new i2(1) : new i2(NaN);
}, P$3.cubeRoot = P$3.cbrt = function() {
  var t4, n2, r2, i2, a2, o2, s2, c2, l2, u2, d2 = this, p2 = d2.constructor;
  if (!d2.isFinite() || d2.isZero()) return new p2(d2);
  for (external = false, o2 = d2.s * mathpow(d2.s * d2, 1 / 3), !o2 || Math.abs(o2) == 1 / 0 ? (r2 = digitsToString(d2.d), t4 = d2.e, (o2 = (t4 - r2.length + 1) % 3) && (r2 += o2 == 1 || o2 == -2 ? `0` : `00`), o2 = mathpow(r2, 1 / 3), t4 = mathfloor((t4 + 1) / 3) - (t4 % 3 == (t4 < 0 ? -1 : 2)), o2 == 1 / 0 ? r2 = `5e` + t4 : (r2 = o2.toExponential(), r2 = r2.slice(0, r2.indexOf(`e`) + 1) + t4), i2 = new p2(r2), i2.s = d2.s) : i2 = new p2(o2.toString()), s2 = (t4 = p2.precision) + 3; ; ) if (c2 = i2, l2 = c2.times(c2).times(c2), u2 = l2.plus(d2), i2 = divide$1(u2.plus(d2).times(c2), u2.plus(l2), s2 + 2, 1), digitsToString(c2.d).slice(0, s2) === (r2 = digitsToString(i2.d)).slice(0, s2)) if (r2 = r2.slice(s2 - 3, s2 + 1), r2 == `9999` || !a2 && r2 == `4999`) {
    if (!a2 && (finalise(c2, t4 + 1, 0), c2.times(c2).times(c2).eq(d2))) {
      i2 = c2;
      break;
    }
    s2 += 4, a2 = 1;
  } else {
    (!+r2 || !+r2.slice(1) && r2.charAt(0) == `5`) && (finalise(i2, t4 + 1, 1), n2 = !i2.times(i2).times(i2).eq(d2));
    break;
  }
  return external = true, finalise(i2, t4, p2.rounding, n2);
}, P$3.decimalPlaces = P$3.dp = function() {
  var t4, n2 = this.d, r2 = NaN;
  if (n2) {
    if (t4 = n2.length - 1, r2 = (t4 - mathfloor(this.e / LOG_BASE)) * LOG_BASE, t4 = n2[t4], t4) for (; t4 % 10 == 0; t4 /= 10) r2--;
    r2 < 0 && (r2 = 0);
  }
  return r2;
}, P$3.dividedBy = P$3.div = function(t4) {
  return divide$1(this, new this.constructor(t4));
}, P$3.dividedToIntegerBy = P$3.divToInt = function(t4) {
  var n2 = this, r2 = n2.constructor;
  return finalise(divide$1(n2, new r2(t4), 0, 1, 1), r2.precision, r2.rounding);
}, P$3.equals = P$3.eq = function(t4) {
  return this.cmp(t4) === 0;
}, P$3.floor = function() {
  return finalise(new this.constructor(this), this.e + 1, 3);
}, P$3.greaterThan = P$3.gt = function(t4) {
  return this.cmp(t4) > 0;
}, P$3.greaterThanOrEqualTo = P$3.gte = function(t4) {
  var n2 = this.cmp(t4);
  return n2 == 1 || n2 === 0;
}, P$3.hyperbolicCosine = P$3.cosh = function() {
  var t4, n2, r2, i2, a2, o2 = this, s2 = o2.constructor, c2 = new s2(1);
  if (!o2.isFinite()) return new s2(o2.s ? 1 / 0 : NaN);
  if (o2.isZero()) return c2;
  r2 = s2.precision, i2 = s2.rounding, s2.precision = r2 + Math.max(o2.e, o2.sd()) + 4, s2.rounding = 1, a2 = o2.d.length, a2 < 32 ? (t4 = Math.ceil(a2 / 3), n2 = (1 / tinyPow(4, t4)).toString()) : (t4 = 16, n2 = `2.3283064365386962890625e-10`), o2 = taylorSeries(s2, 1, o2.times(n2), new s2(1), true);
  for (var l2, u2 = t4, d2 = new s2(8); u2--; ) l2 = o2.times(o2), o2 = c2.minus(l2.times(d2.minus(l2.times(d2))));
  return finalise(o2, s2.precision = r2, s2.rounding = i2, true);
}, P$3.hyperbolicSine = P$3.sinh = function() {
  var t4, n2, r2, i2, a2 = this, o2 = a2.constructor;
  if (!a2.isFinite() || a2.isZero()) return new o2(a2);
  if (n2 = o2.precision, r2 = o2.rounding, o2.precision = n2 + Math.max(a2.e, a2.sd()) + 4, o2.rounding = 1, i2 = a2.d.length, i2 < 3) a2 = taylorSeries(o2, 2, a2, a2, true);
  else {
    t4 = 1.4 * Math.sqrt(i2), t4 = t4 > 16 ? 16 : t4 | 0, a2 = a2.times(1 / tinyPow(5, t4)), a2 = taylorSeries(o2, 2, a2, a2, true);
    for (var s2, c2 = new o2(5), l2 = new o2(16), u2 = new o2(20); t4--; ) s2 = a2.times(a2), a2 = a2.times(c2.plus(s2.times(l2.times(s2).plus(u2))));
  }
  return o2.precision = n2, o2.rounding = r2, finalise(a2, n2, r2, true);
}, P$3.hyperbolicTangent = P$3.tanh = function() {
  var t4, n2, r2 = this, i2 = r2.constructor;
  return r2.isFinite() ? r2.isZero() ? new i2(r2) : (t4 = i2.precision, n2 = i2.rounding, i2.precision = t4 + 7, i2.rounding = 1, divide$1(r2.sinh(), r2.cosh(), i2.precision = t4, i2.rounding = n2)) : new i2(r2.s);
}, P$3.inverseCosine = P$3.acos = function() {
  var t4, n2 = this, r2 = n2.constructor, i2 = n2.abs().cmp(1), a2 = r2.precision, o2 = r2.rounding;
  return i2 === -1 ? n2.isZero() ? getPi(r2, a2 + 4, o2).times(0.5) : (r2.precision = a2 + 6, r2.rounding = 1, n2 = n2.asin(), t4 = getPi(r2, a2 + 4, o2).times(0.5), r2.precision = a2, r2.rounding = o2, t4.minus(n2)) : i2 === 0 ? n2.isNeg() ? getPi(r2, a2, o2) : new r2(0) : new r2(NaN);
}, P$3.inverseHyperbolicCosine = P$3.acosh = function() {
  var t4, n2, r2 = this, i2 = r2.constructor;
  return r2.lte(1) ? new i2(r2.eq(1) ? 0 : NaN) : r2.isFinite() ? (t4 = i2.precision, n2 = i2.rounding, i2.precision = t4 + Math.max(Math.abs(r2.e), r2.sd()) + 4, i2.rounding = 1, external = false, r2 = r2.times(r2).minus(1).sqrt().plus(r2), external = true, i2.precision = t4, i2.rounding = n2, r2.ln()) : new i2(r2);
}, P$3.inverseHyperbolicSine = P$3.asinh = function() {
  var t4, n2, r2 = this, i2 = r2.constructor;
  return !r2.isFinite() || r2.isZero() ? new i2(r2) : (t4 = i2.precision, n2 = i2.rounding, i2.precision = t4 + 2 * Math.max(Math.abs(r2.e), r2.sd()) + 6, i2.rounding = 1, external = false, r2 = r2.times(r2).plus(1).sqrt().plus(r2), external = true, i2.precision = t4, i2.rounding = n2, r2.ln());
}, P$3.inverseHyperbolicTangent = P$3.atanh = function() {
  var t4, n2, r2, i2, a2 = this, o2 = a2.constructor;
  return a2.isFinite() ? a2.e >= 0 ? new o2(a2.abs().eq(1) ? a2.s / 0 : a2.isZero() ? a2 : NaN) : (t4 = o2.precision, n2 = o2.rounding, i2 = a2.sd(), Math.max(i2, t4) < 2 * -a2.e - 1 ? finalise(new o2(a2), t4, n2, true) : (o2.precision = r2 = i2 - a2.e, a2 = divide$1(a2.plus(1), new o2(1).minus(a2), r2 + t4, 1), o2.precision = t4 + 4, o2.rounding = 1, a2 = a2.ln(), o2.precision = t4, o2.rounding = n2, a2.times(0.5))) : new o2(NaN);
}, P$3.inverseSine = P$3.asin = function() {
  var t4, n2, r2, i2, a2 = this, o2 = a2.constructor;
  return a2.isZero() ? new o2(a2) : (n2 = a2.abs().cmp(1), r2 = o2.precision, i2 = o2.rounding, n2 === -1 ? (o2.precision = r2 + 6, o2.rounding = 1, a2 = a2.div(new o2(1).minus(a2.times(a2)).sqrt().plus(1)).atan(), o2.precision = r2, o2.rounding = i2, a2.times(2)) : n2 === 0 ? (t4 = getPi(o2, r2 + 4, i2).times(0.5), t4.s = a2.s, t4) : new o2(NaN));
}, P$3.inverseTangent = P$3.atan = function() {
  var t4, n2, r2, i2, a2, o2, s2, c2, l2, u2 = this, d2 = u2.constructor, p2 = d2.precision, m2 = d2.rounding;
  if (!u2.isFinite()) {
    if (!u2.s) return new d2(NaN);
    if (p2 + 4 <= PI_PRECISION) return s2 = getPi(d2, p2 + 4, m2).times(0.5), s2.s = u2.s, s2;
  } else if (u2.isZero()) return new d2(u2);
  else if (u2.abs().eq(1) && p2 + 4 <= PI_PRECISION) return s2 = getPi(d2, p2 + 4, m2).times(0.25), s2.s = u2.s, s2;
  for (d2.precision = c2 = p2 + 10, d2.rounding = 1, r2 = Math.min(28, c2 / LOG_BASE + 2 | 0), t4 = r2; t4; --t4) u2 = u2.div(u2.times(u2).plus(1).sqrt().plus(1));
  for (external = false, n2 = Math.ceil(c2 / LOG_BASE), i2 = 1, l2 = u2.times(u2), s2 = new d2(u2), a2 = u2; t4 !== -1; ) if (a2 = a2.times(l2), o2 = s2.minus(a2.div(i2 += 2)), a2 = a2.times(l2), s2 = o2.plus(a2.div(i2 += 2)), s2.d[n2] !== void 0) for (t4 = n2; s2.d[t4] === o2.d[t4] && t4--; ) ;
  return r2 && (s2 = s2.times(2 << r2 - 1)), external = true, finalise(s2, d2.precision = p2, d2.rounding = m2, true);
}, P$3.isFinite = function() {
  return !!this.d;
}, P$3.isInteger = P$3.isInt = function() {
  return !!this.d && mathfloor(this.e / LOG_BASE) > this.d.length - 2;
}, P$3.isNaN = function() {
  return !this.s;
}, P$3.isNegative = P$3.isNeg = function() {
  return this.s < 0;
}, P$3.isPositive = P$3.isPos = function() {
  return this.s > 0;
}, P$3.isZero = function() {
  return !!this.d && this.d[0] === 0;
}, P$3.lessThan = P$3.lt = function(t4) {
  return this.cmp(t4) < 0;
}, P$3.lessThanOrEqualTo = P$3.lte = function(t4) {
  return this.cmp(t4) < 1;
}, P$3.logarithm = P$3.log = function(t4) {
  var n2, r2, i2, a2, o2, s2, c2, l2, u2 = this, d2 = u2.constructor, p2 = d2.precision, m2 = d2.rounding, h2 = 5;
  if (t4 == null) t4 = new d2(10), n2 = true;
  else {
    if (t4 = new d2(t4), r2 = t4.d, t4.s < 0 || !r2 || !r2[0] || t4.eq(1)) return new d2(NaN);
    n2 = t4.eq(10);
  }
  if (r2 = u2.d, u2.s < 0 || !r2 || !r2[0] || u2.eq(1)) return new d2(r2 && !r2[0] ? -1 / 0 : u2.s == 1 ? r2 ? 0 : 1 / 0 : NaN);
  if (n2) if (r2.length > 1) o2 = true;
  else {
    for (a2 = r2[0]; a2 % 10 == 0; ) a2 /= 10;
    o2 = a2 !== 1;
  }
  if (external = false, c2 = p2 + h2, s2 = naturalLogarithm(u2, c2), i2 = n2 ? getLn10(d2, c2 + 10) : naturalLogarithm(t4, c2), l2 = divide$1(s2, i2, c2, 1), checkRoundingDigits(l2.d, a2 = p2, m2)) do
    if (c2 += 10, s2 = naturalLogarithm(u2, c2), i2 = n2 ? getLn10(d2, c2 + 10) : naturalLogarithm(t4, c2), l2 = divide$1(s2, i2, c2, 1), !o2) {
      +digitsToString(l2.d).slice(a2 + 1, a2 + 15) + 1 == 1e14 && (l2 = finalise(l2, p2 + 1, 0));
      break;
    }
  while (checkRoundingDigits(l2.d, a2 += 10, m2));
  return external = true, finalise(l2, p2, m2);
}, P$3.minus = P$3.sub = function(t4) {
  var n2, r2, i2, a2, o2, s2, c2, l2, u2, d2, p2, m2, h2 = this, g2 = h2.constructor;
  if (t4 = new g2(t4), !h2.d || !t4.d) return !h2.s || !t4.s ? t4 = new g2(NaN) : h2.d ? t4.s = -t4.s : t4 = new g2(t4.d || h2.s !== t4.s ? h2 : NaN), t4;
  if (h2.s != t4.s) return t4.s = -t4.s, h2.plus(t4);
  if (u2 = h2.d, m2 = t4.d, c2 = g2.precision, l2 = g2.rounding, !u2[0] || !m2[0]) {
    if (m2[0]) t4.s = -t4.s;
    else if (u2[0]) t4 = new g2(h2);
    else return new g2(l2 === 3 ? -0 : 0);
    return external ? finalise(t4, c2, l2) : t4;
  }
  if (r2 = mathfloor(t4.e / LOG_BASE), d2 = mathfloor(h2.e / LOG_BASE), u2 = u2.slice(), o2 = d2 - r2, o2) {
    for (p2 = o2 < 0, p2 ? (n2 = u2, o2 = -o2, s2 = m2.length) : (n2 = m2, r2 = d2, s2 = u2.length), i2 = Math.max(Math.ceil(c2 / LOG_BASE), s2) + 2, o2 > i2 && (o2 = i2, n2.length = 1), n2.reverse(), i2 = o2; i2--; ) n2.push(0);
    n2.reverse();
  } else {
    for (i2 = u2.length, s2 = m2.length, p2 = i2 < s2, p2 && (s2 = i2), i2 = 0; i2 < s2; i2++) if (u2[i2] != m2[i2]) {
      p2 = u2[i2] < m2[i2];
      break;
    }
    o2 = 0;
  }
  for (p2 && (n2 = u2, u2 = m2, m2 = n2, t4.s = -t4.s), s2 = u2.length, i2 = m2.length - s2; i2 > 0; --i2) u2[s2++] = 0;
  for (i2 = m2.length; i2 > o2; ) {
    if (u2[--i2] < m2[i2]) {
      for (a2 = i2; a2 && u2[--a2] === 0; ) u2[a2] = BASE - 1;
      --u2[a2], u2[i2] += BASE;
    }
    u2[i2] -= m2[i2];
  }
  for (; u2[--s2] === 0; ) u2.pop();
  for (; u2[0] === 0; u2.shift()) --r2;
  return u2[0] ? (t4.d = u2, t4.e = getBase10Exponent(u2, r2), external ? finalise(t4, c2, l2) : t4) : new g2(l2 === 3 ? -0 : 0);
}, P$3.modulo = P$3.mod = function(t4) {
  var n2, r2 = this, i2 = r2.constructor;
  return t4 = new i2(t4), !r2.d || !t4.s || t4.d && !t4.d[0] ? new i2(NaN) : !t4.d || r2.d && !r2.d[0] ? finalise(new i2(r2), i2.precision, i2.rounding) : (external = false, i2.modulo == 9 ? (n2 = divide$1(r2, t4.abs(), 0, 3, 1), n2.s *= t4.s) : n2 = divide$1(r2, t4, 0, i2.modulo, 1), n2 = n2.times(t4), external = true, r2.minus(n2));
}, P$3.naturalExponential = P$3.exp = function() {
  return naturalExponential(this);
}, P$3.naturalLogarithm = P$3.ln = function() {
  return naturalLogarithm(this);
}, P$3.negated = P$3.neg = function() {
  var t4 = new this.constructor(this);
  return t4.s = -t4.s, finalise(t4);
}, P$3.plus = P$3.add = function(t4) {
  var n2, r2, i2, a2, o2, s2, c2, l2, u2, d2, p2 = this, m2 = p2.constructor;
  if (t4 = new m2(t4), !p2.d || !t4.d) return !p2.s || !t4.s ? t4 = new m2(NaN) : p2.d || (t4 = new m2(t4.d || p2.s === t4.s ? p2 : NaN)), t4;
  if (p2.s != t4.s) return t4.s = -t4.s, p2.minus(t4);
  if (u2 = p2.d, d2 = t4.d, c2 = m2.precision, l2 = m2.rounding, !u2[0] || !d2[0]) return d2[0] || (t4 = new m2(p2)), external ? finalise(t4, c2, l2) : t4;
  if (o2 = mathfloor(p2.e / LOG_BASE), i2 = mathfloor(t4.e / LOG_BASE), u2 = u2.slice(), a2 = o2 - i2, a2) {
    for (a2 < 0 ? (r2 = u2, a2 = -a2, s2 = d2.length) : (r2 = d2, i2 = o2, s2 = u2.length), o2 = Math.ceil(c2 / LOG_BASE), s2 = o2 > s2 ? o2 + 1 : s2 + 1, a2 > s2 && (a2 = s2, r2.length = 1), r2.reverse(); a2--; ) r2.push(0);
    r2.reverse();
  }
  for (s2 = u2.length, a2 = d2.length, s2 - a2 < 0 && (a2 = s2, r2 = d2, d2 = u2, u2 = r2), n2 = 0; a2; ) n2 = (u2[--a2] = u2[a2] + d2[a2] + n2) / BASE | 0, u2[a2] %= BASE;
  for (n2 && (u2.unshift(n2), ++i2), s2 = u2.length; u2[--s2] == 0; ) u2.pop();
  return t4.d = u2, t4.e = getBase10Exponent(u2, i2), external ? finalise(t4, c2, l2) : t4;
}, P$3.precision = P$3.sd = function(t4) {
  var n2, r2 = this;
  if (t4 !== void 0 && t4 !== !!t4 && t4 !== 1 && t4 !== 0) throw Error(invalidArgument + t4);
  return r2.d ? (n2 = getPrecision(r2.d), t4 && r2.e + 1 > n2 && (n2 = r2.e + 1)) : n2 = NaN, n2;
}, P$3.round = function() {
  var t4 = this, n2 = t4.constructor;
  return finalise(new n2(t4), t4.e + 1, n2.rounding);
}, P$3.sine = P$3.sin = function() {
  var t4, n2, r2 = this, i2 = r2.constructor;
  return r2.isFinite() ? r2.isZero() ? new i2(r2) : (t4 = i2.precision, n2 = i2.rounding, i2.precision = t4 + Math.max(r2.e, r2.sd()) + LOG_BASE, i2.rounding = 1, r2 = sine(i2, toLessThanHalfPi(i2, r2)), i2.precision = t4, i2.rounding = n2, finalise(quadrant > 2 ? r2.neg() : r2, t4, n2, true)) : new i2(NaN);
}, P$3.squareRoot = P$3.sqrt = function() {
  var t4, n2, r2, i2, a2, o2, s2 = this, c2 = s2.d, l2 = s2.e, u2 = s2.s, d2 = s2.constructor;
  if (u2 !== 1 || !c2 || !c2[0]) return new d2(!u2 || u2 < 0 && (!c2 || c2[0]) ? NaN : c2 ? s2 : 1 / 0);
  for (external = false, u2 = Math.sqrt(+s2), u2 == 0 || u2 == 1 / 0 ? (n2 = digitsToString(c2), (n2.length + l2) % 2 == 0 && (n2 += `0`), u2 = Math.sqrt(n2), l2 = mathfloor((l2 + 1) / 2) - (l2 < 0 || l2 % 2), u2 == 1 / 0 ? n2 = `5e` + l2 : (n2 = u2.toExponential(), n2 = n2.slice(0, n2.indexOf(`e`) + 1) + l2), i2 = new d2(n2)) : i2 = new d2(u2.toString()), r2 = (l2 = d2.precision) + 3; ; ) if (o2 = i2, i2 = o2.plus(divide$1(s2, o2, r2 + 2, 1)).times(0.5), digitsToString(o2.d).slice(0, r2) === (n2 = digitsToString(i2.d)).slice(0, r2)) if (n2 = n2.slice(r2 - 3, r2 + 1), n2 == `9999` || !a2 && n2 == `4999`) {
    if (!a2 && (finalise(o2, l2 + 1, 0), o2.times(o2).eq(s2))) {
      i2 = o2;
      break;
    }
    r2 += 4, a2 = 1;
  } else {
    (!+n2 || !+n2.slice(1) && n2.charAt(0) == `5`) && (finalise(i2, l2 + 1, 1), t4 = !i2.times(i2).eq(s2));
    break;
  }
  return external = true, finalise(i2, l2, d2.rounding, t4);
}, P$3.tangent = P$3.tan = function() {
  var t4, n2, r2 = this, i2 = r2.constructor;
  return r2.isFinite() ? r2.isZero() ? new i2(r2) : (t4 = i2.precision, n2 = i2.rounding, i2.precision = t4 + 10, i2.rounding = 1, r2 = r2.sin(), r2.s = 1, r2 = divide$1(r2, new i2(1).minus(r2.times(r2)).sqrt(), t4 + 10, 0), i2.precision = t4, i2.rounding = n2, finalise(quadrant == 2 || quadrant == 4 ? r2.neg() : r2, t4, n2, true)) : new i2(NaN);
}, P$3.times = P$3.mul = function(t4) {
  var n2, r2, i2, a2, o2, s2, c2, l2, u2, d2 = this, p2 = d2.constructor, m2 = d2.d, h2 = (t4 = new p2(t4)).d;
  if (t4.s *= d2.s, !m2 || !m2[0] || !h2 || !h2[0]) return new p2(!t4.s || m2 && !m2[0] && !h2 || h2 && !h2[0] && !m2 ? NaN : !m2 || !h2 ? t4.s / 0 : t4.s * 0);
  for (r2 = mathfloor(d2.e / LOG_BASE) + mathfloor(t4.e / LOG_BASE), l2 = m2.length, u2 = h2.length, l2 < u2 && (o2 = m2, m2 = h2, h2 = o2, s2 = l2, l2 = u2, u2 = s2), o2 = [], s2 = l2 + u2, i2 = s2; i2--; ) o2.push(0);
  for (i2 = u2; --i2 >= 0; ) {
    for (n2 = 0, a2 = l2 + i2; a2 > i2; ) c2 = o2[a2] + h2[i2] * m2[a2 - i2 - 1] + n2, o2[a2--] = c2 % BASE | 0, n2 = c2 / BASE | 0;
    o2[a2] = (o2[a2] + n2) % BASE | 0;
  }
  for (; !o2[--s2]; ) o2.pop();
  return n2 ? ++r2 : o2.shift(), t4.d = o2, t4.e = getBase10Exponent(o2, r2), external ? finalise(t4, p2.precision, p2.rounding) : t4;
}, P$3.toBinary = function(t4, n2) {
  return toStringBinary(this, 2, t4, n2);
}, P$3.toDecimalPlaces = P$3.toDP = function(t4, n2) {
  var r2 = this, i2 = r2.constructor;
  return r2 = new i2(r2), t4 === void 0 ? r2 : (checkInt32(t4, 0, MAX_DIGITS), n2 === void 0 ? n2 = i2.rounding : checkInt32(n2, 0, 8), finalise(r2, t4 + r2.e + 1, n2));
}, P$3.toExponential = function(t4, n2) {
  var r2, i2 = this, a2 = i2.constructor;
  return t4 === void 0 ? r2 = finiteToString(i2, true) : (checkInt32(t4, 0, MAX_DIGITS), n2 === void 0 ? n2 = a2.rounding : checkInt32(n2, 0, 8), i2 = finalise(new a2(i2), t4 + 1, n2), r2 = finiteToString(i2, true, t4 + 1)), i2.isNeg() && !i2.isZero() ? `-` + r2 : r2;
}, P$3.toFixed = function(t4, n2) {
  var r2, i2, a2 = this, o2 = a2.constructor;
  return t4 === void 0 ? r2 = finiteToString(a2) : (checkInt32(t4, 0, MAX_DIGITS), n2 === void 0 ? n2 = o2.rounding : checkInt32(n2, 0, 8), i2 = finalise(new o2(a2), t4 + a2.e + 1, n2), r2 = finiteToString(i2, false, t4 + i2.e + 1)), a2.isNeg() && !a2.isZero() ? `-` + r2 : r2;
}, P$3.toFraction = function(t4) {
  var n2, r2, i2, a2, o2, s2, c2, l2, u2, d2, p2, m2, h2 = this, g2 = h2.d, v2 = h2.constructor;
  if (!g2) return new v2(h2);
  if (u2 = r2 = new v2(1), i2 = l2 = new v2(0), n2 = new v2(i2), o2 = n2.e = getPrecision(g2) - h2.e - 1, s2 = o2 % LOG_BASE, n2.d[0] = mathpow(10, s2 < 0 ? LOG_BASE + s2 : s2), t4 == null) t4 = o2 > 0 ? n2 : u2;
  else {
    if (c2 = new v2(t4), !c2.isInt() || c2.lt(u2)) throw Error(invalidArgument + c2);
    t4 = c2.gt(n2) ? o2 > 0 ? n2 : u2 : c2;
  }
  for (external = false, c2 = new v2(digitsToString(g2)), d2 = v2.precision, v2.precision = o2 = g2.length * LOG_BASE * 2; p2 = divide$1(c2, n2, 0, 1, 1), a2 = r2.plus(p2.times(i2)), a2.cmp(t4) != 1; ) r2 = i2, i2 = a2, a2 = u2, u2 = l2.plus(p2.times(a2)), l2 = a2, a2 = n2, n2 = c2.minus(p2.times(a2)), c2 = a2;
  return a2 = divide$1(t4.minus(r2), i2, 0, 1, 1), l2 = l2.plus(a2.times(u2)), r2 = r2.plus(a2.times(i2)), l2.s = u2.s = h2.s, m2 = divide$1(u2, i2, o2, 1).minus(h2).abs().cmp(divide$1(l2, r2, o2, 1).minus(h2).abs()) < 1 ? [u2, i2] : [l2, r2], v2.precision = d2, external = true, m2;
}, P$3.toHexadecimal = P$3.toHex = function(t4, n2) {
  return toStringBinary(this, 16, t4, n2);
}, P$3.toNearest = function(t4, n2) {
  var r2 = this, i2 = r2.constructor;
  if (r2 = new i2(r2), t4 == null) {
    if (!r2.d) return r2;
    t4 = new i2(1), n2 = i2.rounding;
  } else {
    if (t4 = new i2(t4), n2 === void 0 ? n2 = i2.rounding : checkInt32(n2, 0, 8), !r2.d) return t4.s ? r2 : t4;
    if (!t4.d) return t4.s &&= r2.s, t4;
  }
  return t4.d[0] ? (external = false, r2 = divide$1(r2, t4, 0, n2, 1).times(t4), external = true, finalise(r2)) : (t4.s = r2.s, r2 = t4), r2;
}, P$3.toNumber = function() {
  return +this;
}, P$3.toOctal = function(t4, n2) {
  return toStringBinary(this, 8, t4, n2);
}, P$3.toPower = P$3.pow = function(t4) {
  var n2, r2, i2, a2, o2, s2, c2 = this, l2 = c2.constructor, u2 = +(t4 = new l2(t4));
  if (!c2.d || !t4.d || !c2.d[0] || !t4.d[0]) return new l2(mathpow(+c2, u2));
  if (c2 = new l2(c2), c2.eq(1)) return c2;
  if (i2 = l2.precision, o2 = l2.rounding, t4.eq(1)) return finalise(c2, i2, o2);
  if (n2 = mathfloor(t4.e / LOG_BASE), n2 >= t4.d.length - 1 && (r2 = u2 < 0 ? -u2 : u2) <= MAX_SAFE_INTEGER) return a2 = intPow(l2, c2, r2, i2), t4.s < 0 ? new l2(1).div(a2) : finalise(a2, i2, o2);
  if (s2 = c2.s, s2 < 0) {
    if (n2 < t4.d.length - 1) return new l2(NaN);
    if (t4.d[n2] & 1 || (s2 = 1), c2.e == 0 && c2.d[0] == 1 && c2.d.length == 1) return c2.s = s2, c2;
  }
  return r2 = mathpow(+c2, u2), n2 = r2 == 0 || !isFinite(r2) ? mathfloor(u2 * (Math.log(`0.` + digitsToString(c2.d)) / Math.LN10 + c2.e + 1)) : new l2(r2 + ``).e, n2 > l2.maxE + 1 || n2 < l2.minE - 1 ? new l2(n2 > 0 ? s2 / 0 : 0) : (external = false, l2.rounding = c2.s = 1, r2 = Math.min(12, (n2 + ``).length), a2 = naturalExponential(t4.times(naturalLogarithm(c2, i2 + r2)), i2), a2.d && (a2 = finalise(a2, i2 + 5, 1), checkRoundingDigits(a2.d, i2, o2) && (n2 = i2 + 10, a2 = finalise(naturalExponential(t4.times(naturalLogarithm(c2, n2 + r2)), n2), n2 + 5, 1), +digitsToString(a2.d).slice(i2 + 1, i2 + 15) + 1 == 1e14 && (a2 = finalise(a2, i2 + 1, 0)))), a2.s = s2, external = true, l2.rounding = o2, finalise(a2, i2, o2));
}, P$3.toPrecision = function(t4, n2) {
  var r2, i2 = this, a2 = i2.constructor;
  return t4 === void 0 ? r2 = finiteToString(i2, i2.e <= a2.toExpNeg || i2.e >= a2.toExpPos) : (checkInt32(t4, 1, MAX_DIGITS), n2 === void 0 ? n2 = a2.rounding : checkInt32(n2, 0, 8), i2 = finalise(new a2(i2), t4, n2), r2 = finiteToString(i2, t4 <= i2.e || i2.e <= a2.toExpNeg, t4)), i2.isNeg() && !i2.isZero() ? `-` + r2 : r2;
}, P$3.toSignificantDigits = P$3.toSD = function(t4, n2) {
  var r2 = this, i2 = r2.constructor;
  return t4 === void 0 ? (t4 = i2.precision, n2 = i2.rounding) : (checkInt32(t4, 1, MAX_DIGITS), n2 === void 0 ? n2 = i2.rounding : checkInt32(n2, 0, 8)), finalise(new i2(r2), t4, n2);
}, P$3.toString = function() {
  var t4 = this, n2 = t4.constructor, r2 = finiteToString(t4, t4.e <= n2.toExpNeg || t4.e >= n2.toExpPos);
  return t4.isNeg() && !t4.isZero() ? `-` + r2 : r2;
}, P$3.truncated = P$3.trunc = function() {
  return finalise(new this.constructor(this), this.e + 1, 1);
}, P$3.valueOf = P$3.toJSON = function() {
  var t4 = this, n2 = t4.constructor, r2 = finiteToString(t4, t4.e <= n2.toExpNeg || t4.e >= n2.toExpPos);
  return t4.isNeg() ? `-` + r2 : r2;
};
function digitsToString(t4) {
  var n2, r2, i2, a2 = t4.length - 1, o2 = ``, s2 = t4[0];
  if (a2 > 0) {
    for (o2 += s2, n2 = 1; n2 < a2; n2++) i2 = t4[n2] + ``, r2 = LOG_BASE - i2.length, r2 && (o2 += getZeroString(r2)), o2 += i2;
    s2 = t4[n2], i2 = s2 + ``, r2 = LOG_BASE - i2.length, r2 && (o2 += getZeroString(r2));
  } else if (s2 === 0) return `0`;
  for (; s2 % 10 == 0; ) s2 /= 10;
  return o2 + s2;
}
function checkInt32(t4, n2, r2) {
  if (t4 !== ~~t4 || t4 < n2 || t4 > r2) throw Error(invalidArgument + t4);
}
function checkRoundingDigits(t4, n2, r2, i2) {
  var a2, o2, s2, c2;
  for (o2 = t4[0]; o2 >= 10; o2 /= 10) --n2;
  return --n2 < 0 ? (n2 += LOG_BASE, a2 = 0) : (a2 = Math.ceil((n2 + 1) / LOG_BASE), n2 %= LOG_BASE), o2 = mathpow(10, LOG_BASE - n2), c2 = t4[a2] % o2 | 0, i2 == null ? n2 < 3 ? (n2 == 0 ? c2 = c2 / 100 | 0 : n2 == 1 && (c2 = c2 / 10 | 0), s2 = r2 < 4 && c2 == 99999 || r2 > 3 && c2 == 49999 || c2 == 5e4 || c2 == 0) : s2 = (r2 < 4 && c2 + 1 == o2 || r2 > 3 && c2 + 1 == o2 / 2) && (t4[a2 + 1] / o2 / 100 | 0) == mathpow(10, n2 - 2) - 1 || (c2 == o2 / 2 || c2 == 0) && (t4[a2 + 1] / o2 / 100 | 0) == 0 : n2 < 4 ? (n2 == 0 ? c2 = c2 / 1e3 | 0 : n2 == 1 ? c2 = c2 / 100 | 0 : n2 == 2 && (c2 = c2 / 10 | 0), s2 = (i2 || r2 < 4) && c2 == 9999 || !i2 && r2 > 3 && c2 == 4999) : s2 = ((i2 || r2 < 4) && c2 + 1 == o2 || !i2 && r2 > 3 && c2 + 1 == o2 / 2) && (t4[a2 + 1] / o2 / 1e3 | 0) == mathpow(10, n2 - 3) - 1, s2;
}
function convertBase(t4, n2, r2) {
  for (var i2, a2 = [0], o2, s2 = 0, c2 = t4.length; s2 < c2; ) {
    for (o2 = a2.length; o2--; ) a2[o2] *= n2;
    for (a2[0] += NUMERALS.indexOf(t4.charAt(s2++)), i2 = 0; i2 < a2.length; i2++) a2[i2] > r2 - 1 && (a2[i2 + 1] === void 0 && (a2[i2 + 1] = 0), a2[i2 + 1] += a2[i2] / r2 | 0, a2[i2] %= r2);
  }
  return a2.reverse();
}
function cosine(t4, n2) {
  var r2, i2, a2;
  if (n2.isZero()) return n2;
  i2 = n2.d.length, i2 < 32 ? (r2 = Math.ceil(i2 / 3), a2 = (1 / tinyPow(4, r2)).toString()) : (r2 = 16, a2 = `2.3283064365386962890625e-10`), t4.precision += r2, n2 = taylorSeries(t4, 1, n2.times(a2), new t4(1));
  for (var o2 = r2; o2--; ) {
    var s2 = n2.times(n2);
    n2 = s2.times(s2).minus(s2).times(8).plus(1);
  }
  return t4.precision -= r2, n2;
}
var divide$1 = /* @__PURE__ */ (function() {
  function t4(t5, n3, r3) {
    var i2, a2 = 0, o2 = t5.length;
    for (t5 = t5.slice(); o2--; ) i2 = t5[o2] * n3 + a2, t5[o2] = i2 % r3 | 0, a2 = i2 / r3 | 0;
    return a2 && t5.unshift(a2), t5;
  }
  function n2(t5, n3, r3, i2) {
    var a2, o2;
    if (r3 != i2) o2 = r3 > i2 ? 1 : -1;
    else for (a2 = o2 = 0; a2 < r3; a2++) if (t5[a2] != n3[a2]) {
      o2 = t5[a2] > n3[a2] ? 1 : -1;
      break;
    }
    return o2;
  }
  function r2(t5, n3, r3, i2) {
    for (var a2 = 0; r3--; ) t5[r3] -= a2, a2 = +(t5[r3] < n3[r3]), t5[r3] = a2 * i2 + t5[r3] - n3[r3];
    for (; !t5[0] && t5.length > 1; ) t5.shift();
  }
  return function(i2, a2, o2, s2, c2, l2) {
    var u2, d2, p2, m2, h2, g2, v2, y2, b2, x2, S2, w2, E, D, O, j2, M2, N2, F, I, L = i2.constructor, R = i2.s == a2.s ? 1 : -1, z = i2.d, V = a2.d;
    if (!z || !z[0] || !V || !V[0]) return new L(!i2.s || !a2.s || (z ? V && z[0] == V[0] : !V) ? NaN : z && z[0] == 0 || !V ? R * 0 : R / 0);
    for (l2 ? (h2 = 1, d2 = i2.e - a2.e) : (l2 = BASE, h2 = LOG_BASE, d2 = mathfloor(i2.e / h2) - mathfloor(a2.e / h2)), F = V.length, M2 = z.length, b2 = new L(R), x2 = b2.d = [], p2 = 0; V[p2] == (z[p2] || 0); p2++) ;
    if (V[p2] > (z[p2] || 0) && d2--, o2 == null ? (D = o2 = L.precision, s2 = L.rounding) : D = c2 ? o2 + (i2.e - a2.e) + 1 : o2, D < 0) x2.push(1), g2 = true;
    else {
      if (D = D / h2 + 2 | 0, p2 = 0, F == 1) {
        for (m2 = 0, V = V[0], D++; (p2 < M2 || m2) && D--; p2++) O = m2 * l2 + (z[p2] || 0), x2[p2] = O / V | 0, m2 = O % V | 0;
        g2 = m2 || p2 < M2;
      } else {
        for (m2 = l2 / (V[0] + 1) | 0, m2 > 1 && (V = t4(V, m2, l2), z = t4(z, m2, l2), F = V.length, M2 = z.length), j2 = F, S2 = z.slice(0, F), w2 = S2.length; w2 < F; ) S2[w2++] = 0;
        I = V.slice(), I.unshift(0), N2 = V[0], V[1] >= l2 / 2 && ++N2;
        do
          m2 = 0, u2 = n2(V, S2, F, w2), u2 < 0 ? (E = S2[0], F != w2 && (E = E * l2 + (S2[1] || 0)), m2 = E / N2 | 0, m2 > 1 ? (m2 >= l2 && (m2 = l2 - 1), v2 = t4(V, m2, l2), y2 = v2.length, w2 = S2.length, u2 = n2(v2, S2, y2, w2), u2 == 1 && (m2--, r2(v2, F < y2 ? I : V, y2, l2))) : (m2 == 0 && (u2 = m2 = 1), v2 = V.slice()), y2 = v2.length, y2 < w2 && v2.unshift(0), r2(S2, v2, w2, l2), u2 == -1 && (w2 = S2.length, u2 = n2(V, S2, F, w2), u2 < 1 && (m2++, r2(S2, F < w2 ? I : V, w2, l2))), w2 = S2.length) : u2 === 0 && (m2++, S2 = [0]), x2[p2++] = m2, u2 && S2[0] ? S2[w2++] = z[j2] || 0 : (S2 = [z[j2]], w2 = 1);
        while ((j2++ < M2 || S2[0] !== void 0) && D--);
        g2 = S2[0] !== void 0;
      }
      x2[0] || x2.shift();
    }
    if (h2 == 1) b2.e = d2, inexact = g2;
    else {
      for (p2 = 1, m2 = x2[0]; m2 >= 10; m2 /= 10) p2++;
      b2.e = p2 + d2 * h2 - 1, finalise(b2, c2 ? o2 + b2.e + 1 : o2, s2, g2);
    }
    return b2;
  };
})();
function finalise(t4, n2, r2, i2) {
  var a2, o2, s2, c2, l2, u2, d2, p2, m2, h2 = t4.constructor;
  out: if (n2 != null) {
    if (p2 = t4.d, !p2) return t4;
    for (a2 = 1, c2 = p2[0]; c2 >= 10; c2 /= 10) a2++;
    if (o2 = n2 - a2, o2 < 0) o2 += LOG_BASE, s2 = n2, d2 = p2[m2 = 0], l2 = d2 / mathpow(10, a2 - s2 - 1) % 10 | 0;
    else if (m2 = Math.ceil((o2 + 1) / LOG_BASE), c2 = p2.length, m2 >= c2) if (i2) {
      for (; c2++ <= m2; ) p2.push(0);
      d2 = l2 = 0, a2 = 1, o2 %= LOG_BASE, s2 = o2 - LOG_BASE + 1;
    } else break out;
    else {
      for (d2 = c2 = p2[m2], a2 = 1; c2 >= 10; c2 /= 10) a2++;
      o2 %= LOG_BASE, s2 = o2 - LOG_BASE + a2, l2 = s2 < 0 ? 0 : d2 / mathpow(10, a2 - s2 - 1) % 10 | 0;
    }
    if (i2 = i2 || n2 < 0 || p2[m2 + 1] !== void 0 || (s2 < 0 ? d2 : d2 % mathpow(10, a2 - s2 - 1)), u2 = r2 < 4 ? (l2 || i2) && (r2 == 0 || r2 == (t4.s < 0 ? 3 : 2)) : l2 > 5 || l2 == 5 && (r2 == 4 || i2 || r2 == 6 && (o2 > 0 ? s2 > 0 ? d2 / mathpow(10, a2 - s2) : 0 : p2[m2 - 1]) % 10 & 1 || r2 == (t4.s < 0 ? 8 : 7)), n2 < 1 || !p2[0]) return p2.length = 0, u2 ? (n2 -= t4.e + 1, p2[0] = mathpow(10, (LOG_BASE - n2 % LOG_BASE) % LOG_BASE), t4.e = -n2 || 0) : p2[0] = t4.e = 0, t4;
    if (o2 == 0 ? (p2.length = m2, c2 = 1, m2--) : (p2.length = m2 + 1, c2 = mathpow(10, LOG_BASE - o2), p2[m2] = s2 > 0 ? (d2 / mathpow(10, a2 - s2) % mathpow(10, s2) | 0) * c2 : 0), u2) for (; ; ) if (m2 == 0) {
      for (o2 = 1, s2 = p2[0]; s2 >= 10; s2 /= 10) o2++;
      for (s2 = p2[0] += c2, c2 = 1; s2 >= 10; s2 /= 10) c2++;
      o2 != c2 && (t4.e++, p2[0] == BASE && (p2[0] = 1));
      break;
    } else {
      if (p2[m2] += c2, p2[m2] != BASE) break;
      p2[m2--] = 0, c2 = 1;
    }
    for (o2 = p2.length; p2[--o2] === 0; ) p2.pop();
  }
  return external && (t4.e > h2.maxE ? (t4.d = null, t4.e = NaN) : t4.e < h2.minE && (t4.e = 0, t4.d = [0])), t4;
}
function finiteToString(t4, n2, r2) {
  if (!t4.isFinite()) return nonFiniteToString(t4);
  var i2, a2 = t4.e, o2 = digitsToString(t4.d), s2 = o2.length;
  return n2 ? (r2 && (i2 = r2 - s2) > 0 ? o2 = o2.charAt(0) + `.` + o2.slice(1) + getZeroString(i2) : s2 > 1 && (o2 = o2.charAt(0) + `.` + o2.slice(1)), o2 = o2 + (t4.e < 0 ? `e` : `e+`) + t4.e) : a2 < 0 ? (o2 = `0.` + getZeroString(-a2 - 1) + o2, r2 && (i2 = r2 - s2) > 0 && (o2 += getZeroString(i2))) : a2 >= s2 ? (o2 += getZeroString(a2 + 1 - s2), r2 && (i2 = r2 - a2 - 1) > 0 && (o2 = o2 + `.` + getZeroString(i2))) : ((i2 = a2 + 1) < s2 && (o2 = o2.slice(0, i2) + `.` + o2.slice(i2)), r2 && (i2 = r2 - s2) > 0 && (a2 + 1 === s2 && (o2 += `.`), o2 += getZeroString(i2))), o2;
}
function getBase10Exponent(t4, n2) {
  var r2 = t4[0];
  for (n2 *= LOG_BASE; r2 >= 10; r2 /= 10) n2++;
  return n2;
}
function getLn10(t4, n2, r2) {
  if (n2 > LN10_PRECISION) throw external = true, r2 && (t4.precision = r2), Error(precisionLimitExceeded);
  return finalise(new t4(LN10), n2, 1, true);
}
function getPi(t4, n2, r2) {
  if (n2 > PI_PRECISION) throw Error(precisionLimitExceeded);
  return finalise(new t4(PI), n2, r2, true);
}
function getPrecision(t4) {
  var n2 = t4.length - 1, r2 = n2 * LOG_BASE + 1;
  if (n2 = t4[n2], n2) {
    for (; n2 % 10 == 0; n2 /= 10) r2--;
    for (n2 = t4[0]; n2 >= 10; n2 /= 10) r2++;
  }
  return r2;
}
function getZeroString(t4) {
  for (var n2 = ``; t4--; ) n2 += `0`;
  return n2;
}
function intPow(t4, n2, r2, i2) {
  var a2, o2 = new t4(1), s2 = Math.ceil(i2 / LOG_BASE + 4);
  for (external = false; ; ) {
    if (r2 % 2 && (o2 = o2.times(n2), truncate(o2.d, s2) && (a2 = true)), r2 = mathfloor(r2 / 2), r2 === 0) {
      r2 = o2.d.length - 1, a2 && o2.d[r2] === 0 && ++o2.d[r2];
      break;
    }
    n2 = n2.times(n2), truncate(n2.d, s2);
  }
  return external = true, o2;
}
function isOdd(t4) {
  return t4.d[t4.d.length - 1] & 1;
}
function maxOrMin(t4, n2, r2) {
  for (var i2, a2 = new t4(n2[0]), o2 = 0; ++o2 < n2.length; ) if (i2 = new t4(n2[o2]), i2.s) a2[r2](i2) && (a2 = i2);
  else {
    a2 = i2;
    break;
  }
  return a2;
}
function naturalExponential(t4, n2) {
  var r2, i2, a2, o2, s2, c2, l2, u2 = 0, d2 = 0, p2 = 0, m2 = t4.constructor, h2 = m2.rounding, g2 = m2.precision;
  if (!t4.d || !t4.d[0] || t4.e > 17) return new m2(t4.d ? t4.d[0] ? t4.s < 0 ? 0 : 1 / 0 : 1 : t4.s ? t4.s < 0 ? 0 : t4 : NaN);
  for (n2 == null ? (external = false, l2 = g2) : l2 = n2, c2 = new m2(0.03125); t4.e > -2; ) t4 = t4.times(c2), p2 += 5;
  for (i2 = Math.log(mathpow(2, p2)) / Math.LN10 * 2 + 5 | 0, l2 += i2, r2 = o2 = s2 = new m2(1), m2.precision = l2; ; ) {
    if (o2 = finalise(o2.times(t4), l2, 1), r2 = r2.times(++d2), c2 = s2.plus(divide$1(o2, r2, l2, 1)), digitsToString(c2.d).slice(0, l2) === digitsToString(s2.d).slice(0, l2)) {
      for (a2 = p2; a2--; ) s2 = finalise(s2.times(s2), l2, 1);
      if (n2 == null) if (u2 < 3 && checkRoundingDigits(s2.d, l2 - i2, h2, u2)) m2.precision = l2 += 10, r2 = o2 = c2 = new m2(1), d2 = 0, u2++;
      else return finalise(s2, m2.precision = g2, h2, external = true);
      else return m2.precision = g2, s2;
    }
    s2 = c2;
  }
}
function naturalLogarithm(t4, n2) {
  var r2, i2, a2, o2, s2, c2, l2, u2, d2, p2, m2, h2 = 1, g2 = 10, v2 = t4, y2 = v2.d, b2 = v2.constructor, x2 = b2.rounding, S2 = b2.precision;
  if (v2.s < 0 || !y2 || !y2[0] || !v2.e && y2[0] == 1 && y2.length == 1) return new b2(y2 && !y2[0] ? -1 / 0 : v2.s == 1 ? y2 ? 0 : v2 : NaN);
  if (n2 == null ? (external = false, d2 = S2) : d2 = n2, b2.precision = d2 += g2, r2 = digitsToString(y2), i2 = r2.charAt(0), Math.abs(o2 = v2.e) < 15e14) {
    for (; i2 < 7 && i2 != 1 || i2 == 1 && r2.charAt(1) > 3; ) v2 = v2.times(t4), r2 = digitsToString(v2.d), i2 = r2.charAt(0), h2++;
    o2 = v2.e, i2 > 1 ? (v2 = new b2(`0.` + r2), o2++) : v2 = new b2(i2 + `.` + r2.slice(1));
  } else return u2 = getLn10(b2, d2 + 2, S2).times(o2 + ``), v2 = naturalLogarithm(new b2(i2 + `.` + r2.slice(1)), d2 - g2).plus(u2), b2.precision = S2, n2 == null ? finalise(v2, S2, x2, external = true) : v2;
  for (p2 = v2, l2 = s2 = v2 = divide$1(v2.minus(1), v2.plus(1), d2, 1), m2 = finalise(v2.times(v2), d2, 1), a2 = 3; ; ) {
    if (s2 = finalise(s2.times(m2), d2, 1), u2 = l2.plus(divide$1(s2, new b2(a2), d2, 1)), digitsToString(u2.d).slice(0, d2) === digitsToString(l2.d).slice(0, d2)) if (l2 = l2.times(2), o2 !== 0 && (l2 = l2.plus(getLn10(b2, d2 + 2, S2).times(o2 + ``))), l2 = divide$1(l2, new b2(h2), d2, 1), n2 == null) if (checkRoundingDigits(l2.d, d2 - g2, x2, c2)) b2.precision = d2 += g2, u2 = s2 = v2 = divide$1(p2.minus(1), p2.plus(1), d2, 1), m2 = finalise(v2.times(v2), d2, 1), a2 = c2 = 1;
    else return finalise(l2, b2.precision = S2, x2, external = true);
    else return b2.precision = S2, l2;
    l2 = u2, a2 += 2;
  }
}
function nonFiniteToString(t4) {
  return String(t4.s * t4.s / 0);
}
function parseDecimal(t4, n2) {
  var r2, i2, a2;
  for ((r2 = n2.indexOf(`.`)) > -1 && (n2 = n2.replace(`.`, ``)), (i2 = n2.search(/e/i)) > 0 ? (r2 < 0 && (r2 = i2), r2 += +n2.slice(i2 + 1), n2 = n2.substring(0, i2)) : r2 < 0 && (r2 = n2.length), i2 = 0; n2.charCodeAt(i2) === 48; i2++) ;
  for (a2 = n2.length; n2.charCodeAt(a2 - 1) === 48; --a2) ;
  if (n2 = n2.slice(i2, a2), n2) {
    if (a2 -= i2, t4.e = r2 = r2 - i2 - 1, t4.d = [], i2 = (r2 + 1) % LOG_BASE, r2 < 0 && (i2 += LOG_BASE), i2 < a2) {
      for (i2 && t4.d.push(+n2.slice(0, i2)), a2 -= LOG_BASE; i2 < a2; ) t4.d.push(+n2.slice(i2, i2 += LOG_BASE));
      n2 = n2.slice(i2), i2 = LOG_BASE - n2.length;
    } else i2 -= a2;
    for (; i2--; ) n2 += `0`;
    t4.d.push(+n2), external && (t4.e > t4.constructor.maxE ? (t4.d = null, t4.e = NaN) : t4.e < t4.constructor.minE && (t4.e = 0, t4.d = [0]));
  } else t4.e = 0, t4.d = [0];
  return t4;
}
function parseOther(t4, n2) {
  var r2, i2, a2, o2, s2, c2, l2, u2, d2;
  if (n2.indexOf(`_`) > -1) {
    if (n2 = n2.replace(/(\d)_(?=\d)/g, `$1`), isDecimal.test(n2)) return parseDecimal(t4, n2);
  } else if (n2 === `Infinity` || n2 === `NaN`) return +n2 || (t4.s = NaN), t4.e = NaN, t4.d = null, t4;
  if (isHex.test(n2)) r2 = 16, n2 = n2.toLowerCase();
  else if (isBinary.test(n2)) r2 = 2;
  else if (isOctal.test(n2)) r2 = 8;
  else throw Error(invalidArgument + n2);
  for (o2 = n2.search(/p/i), o2 > 0 ? (l2 = +n2.slice(o2 + 1), n2 = n2.substring(2, o2)) : n2 = n2.slice(2), o2 = n2.indexOf(`.`), s2 = o2 >= 0, i2 = t4.constructor, s2 && (n2 = n2.replace(`.`, ``), c2 = n2.length, o2 = c2 - o2, a2 = intPow(i2, new i2(r2), o2, o2 * 2)), u2 = convertBase(n2, r2, BASE), d2 = u2.length - 1, o2 = d2; u2[o2] === 0; --o2) u2.pop();
  return o2 < 0 ? new i2(t4.s * 0) : (t4.e = getBase10Exponent(u2, d2), t4.d = u2, external = false, s2 && (t4 = divide$1(t4, a2, c2 * 4)), l2 && (t4 = t4.times(Math.abs(l2) < 54 ? mathpow(2, l2) : Decimal.pow(2, l2))), external = true, t4);
}
function sine(t4, n2) {
  var r2, i2 = n2.d.length;
  if (i2 < 3) return n2.isZero() ? n2 : taylorSeries(t4, 2, n2, n2);
  r2 = 1.4 * Math.sqrt(i2), r2 = r2 > 16 ? 16 : r2 | 0, n2 = n2.times(1 / tinyPow(5, r2)), n2 = taylorSeries(t4, 2, n2, n2);
  for (var a2, o2 = new t4(5), s2 = new t4(16), c2 = new t4(20); r2--; ) a2 = n2.times(n2), n2 = n2.times(o2.plus(a2.times(s2.times(a2).minus(c2))));
  return n2;
}
function taylorSeries(t4, n2, r2, i2, a2) {
  var o2, s2, c2, l2, d2 = t4.precision, p2 = Math.ceil(d2 / LOG_BASE);
  for (external = false, l2 = r2.times(r2), c2 = new t4(i2); ; ) {
    if (s2 = divide$1(c2.times(l2), new t4(n2++ * n2++), d2, 1), c2 = a2 ? i2.plus(s2) : i2.minus(s2), i2 = divide$1(s2.times(l2), new t4(n2++ * n2++), d2, 1), s2 = c2.plus(i2), s2.d[p2] !== void 0) {
      for (o2 = p2; s2.d[o2] === c2.d[o2] && o2--; ) ;
      if (o2 == -1) break;
    }
    o2 = c2, c2 = i2, i2 = s2, s2 = o2;
  }
  return external = true, s2.d.length = p2 + 1, s2;
}
function tinyPow(t4, n2) {
  for (var r2 = t4; --n2; ) r2 *= t4;
  return r2;
}
function toLessThanHalfPi(t4, n2) {
  var r2, i2 = n2.s < 0, a2 = getPi(t4, t4.precision, 1), o2 = a2.times(0.5);
  if (n2 = n2.abs(), n2.lte(o2)) return quadrant = i2 ? 4 : 1, n2;
  if (r2 = n2.divToInt(a2), r2.isZero()) quadrant = i2 ? 3 : 2;
  else {
    if (n2 = n2.minus(r2.times(a2)), n2.lte(o2)) return quadrant = isOdd(r2) ? i2 ? 2 : 3 : i2 ? 4 : 1, n2;
    quadrant = isOdd(r2) ? i2 ? 1 : 4 : i2 ? 3 : 2;
  }
  return n2.minus(a2).abs();
}
function toStringBinary(t4, n2, r2, i2) {
  var a2, o2, s2, c2, l2, u2, d2, p2, m2, h2 = t4.constructor, g2 = r2 !== void 0;
  if (g2 ? (checkInt32(r2, 1, MAX_DIGITS), i2 === void 0 ? i2 = h2.rounding : checkInt32(i2, 0, 8)) : (r2 = h2.precision, i2 = h2.rounding), !t4.isFinite()) d2 = nonFiniteToString(t4);
  else {
    for (d2 = finiteToString(t4), s2 = d2.indexOf(`.`), g2 ? (a2 = 2, n2 == 16 ? r2 = r2 * 4 - 3 : n2 == 8 && (r2 = r2 * 3 - 2)) : a2 = n2, s2 >= 0 && (d2 = d2.replace(`.`, ``), m2 = new h2(1), m2.e = d2.length - s2, m2.d = convertBase(finiteToString(m2), 10, a2), m2.e = m2.d.length), p2 = convertBase(d2, 10, a2), o2 = l2 = p2.length; p2[--l2] == 0; ) p2.pop();
    if (!p2[0]) d2 = g2 ? `0p+0` : `0`;
    else {
      if (s2 < 0 ? o2-- : (t4 = new h2(t4), t4.d = p2, t4.e = o2, t4 = divide$1(t4, m2, r2, i2, 0, a2), p2 = t4.d, o2 = t4.e, u2 = inexact), s2 = p2[r2], c2 = a2 / 2, u2 ||= p2[r2 + 1] !== void 0, u2 = i2 < 4 ? (s2 !== void 0 || u2) && (i2 === 0 || i2 === (t4.s < 0 ? 3 : 2)) : s2 > c2 || s2 === c2 && (i2 === 4 || u2 || i2 === 6 && p2[r2 - 1] & 1 || i2 === (t4.s < 0 ? 8 : 7)), p2.length = r2, u2) for (; ++p2[--r2] > a2 - 1; ) p2[r2] = 0, r2 || (++o2, p2.unshift(1));
      for (l2 = p2.length; !p2[l2 - 1]; --l2) ;
      for (s2 = 0, d2 = ``; s2 < l2; s2++) d2 += NUMERALS.charAt(p2[s2]);
      if (g2) {
        if (l2 > 1) if (n2 == 16 || n2 == 8) {
          for (s2 = n2 == 16 ? 4 : 3, --l2; l2 % s2; l2++) d2 += `0`;
          for (p2 = convertBase(d2, a2, n2), l2 = p2.length; !p2[l2 - 1]; --l2) ;
          for (s2 = 1, d2 = `1.`; s2 < l2; s2++) d2 += NUMERALS.charAt(p2[s2]);
        } else d2 = d2.charAt(0) + `.` + d2.slice(1);
        d2 = d2 + (o2 < 0 ? `p` : `p+`) + o2;
      } else if (o2 < 0) {
        for (; ++o2; ) d2 = `0` + d2;
        d2 = `0.` + d2;
      } else if (++o2 > l2) for (o2 -= l2; o2--; ) d2 += `0`;
      else o2 < l2 && (d2 = d2.slice(0, o2) + `.` + d2.slice(o2));
    }
    d2 = (n2 == 16 ? `0x` : n2 == 2 ? `0b` : n2 == 8 ? `0o` : ``) + d2;
  }
  return t4.s < 0 ? `-` + d2 : d2;
}
function truncate(t4, n2) {
  if (t4.length > n2) return t4.length = n2, true;
}
function abs$1(t4) {
  return new this(t4).abs();
}
function acos$1(t4) {
  return new this(t4).acos();
}
function acosh$1(t4) {
  return new this(t4).acosh();
}
function add$1(t4, n2) {
  return new this(t4).plus(n2);
}
function asin$1(t4) {
  return new this(t4).asin();
}
function asinh$1(t4) {
  return new this(t4).asinh();
}
function atan$1(t4) {
  return new this(t4).atan();
}
function atanh$1(t4) {
  return new this(t4).atanh();
}
function atan2$1(t4, n2) {
  t4 = new this(t4), n2 = new this(n2);
  var r2, i2 = this.precision, a2 = this.rounding, o2 = i2 + 4;
  return !t4.s || !n2.s ? r2 = new this(NaN) : !t4.d && !n2.d ? (r2 = getPi(this, o2, 1).times(n2.s > 0 ? 0.25 : 0.75), r2.s = t4.s) : !n2.d || t4.isZero() ? (r2 = n2.s < 0 ? getPi(this, i2, a2) : new this(0), r2.s = t4.s) : !t4.d || n2.isZero() ? (r2 = getPi(this, o2, 1).times(0.5), r2.s = t4.s) : n2.s < 0 ? (this.precision = o2, this.rounding = 1, r2 = this.atan(divide$1(t4, n2, o2, 1)), n2 = getPi(this, o2, 1), this.precision = i2, this.rounding = a2, r2 = t4.s < 0 ? r2.minus(n2) : r2.plus(n2)) : r2 = this.atan(divide$1(t4, n2, o2, 1)), r2;
}
function cbrt(t4) {
  return new this(t4).cbrt();
}
function ceil(t4) {
  return finalise(t4 = new this(t4), t4.e + 1, 2);
}
function clamp(t4, n2, r2) {
  return new this(t4).clamp(n2, r2);
}
function config(t4) {
  if (!t4 || typeof t4 != `object`) throw Error(decimalError + `Object expected`);
  var n2, r2, i2, a2 = t4.defaults === true, o2 = [`precision`, 1, MAX_DIGITS, `rounding`, 0, 8, `toExpNeg`, -EXP_LIMIT, 0, `toExpPos`, 0, EXP_LIMIT, `maxE`, 0, EXP_LIMIT, `minE`, -EXP_LIMIT, 0, `modulo`, 0, 9];
  for (n2 = 0; n2 < o2.length; n2 += 3) if (r2 = o2[n2], a2 && (this[r2] = DEFAULTS[r2]), (i2 = t4[r2]) !== void 0) if (mathfloor(i2) === i2 && i2 >= o2[n2 + 1] && i2 <= o2[n2 + 2]) this[r2] = i2;
  else throw Error(invalidArgument + r2 + `: ` + i2);
  if (r2 = `crypto`, a2 && (this[r2] = DEFAULTS[r2]), (i2 = t4[r2]) !== void 0) if (i2 === true || i2 === false || i2 === 0 || i2 === 1) if (i2) if (typeof crypto < `u` && crypto && (crypto.getRandomValues || crypto.randomBytes)) this[r2] = true;
  else throw Error(cryptoUnavailable);
  else this[r2] = false;
  else throw Error(invalidArgument + r2 + `: ` + i2);
  return this;
}
function cos$1(t4) {
  return new this(t4).cos();
}
function cosh$2(t4) {
  return new this(t4).cosh();
}
function clone$2(t4) {
  var n2, r2, i2;
  function a2(t5) {
    var n3, r3, i3, o2 = this;
    if (!(o2 instanceof a2)) return new a2(t5);
    if (o2.constructor = a2, isDecimalInstance(t5)) {
      o2.s = t5.s, external ? !t5.d || t5.e > a2.maxE ? (o2.e = NaN, o2.d = null) : t5.e < a2.minE ? (o2.e = 0, o2.d = [0]) : (o2.e = t5.e, o2.d = t5.d.slice()) : (o2.e = t5.e, o2.d = t5.d ? t5.d.slice() : t5.d);
      return;
    }
    if (i3 = typeof t5, i3 === `number`) {
      if (t5 === 0) {
        o2.s = 1 / t5 < 0 ? -1 : 1, o2.e = 0, o2.d = [0];
        return;
      }
      if (t5 < 0 ? (t5 = -t5, o2.s = -1) : o2.s = 1, t5 === ~~t5 && t5 < 1e7) {
        for (n3 = 0, r3 = t5; r3 >= 10; r3 /= 10) n3++;
        external ? n3 > a2.maxE ? (o2.e = NaN, o2.d = null) : n3 < a2.minE ? (o2.e = 0, o2.d = [0]) : (o2.e = n3, o2.d = [t5]) : (o2.e = n3, o2.d = [t5]);
        return;
      } else if (t5 * 0 != 0) {
        t5 || (o2.s = NaN), o2.e = NaN, o2.d = null;
        return;
      }
      return parseDecimal(o2, t5.toString());
    } else if (i3 !== `string`) throw Error(invalidArgument + t5);
    return (r3 = t5.charCodeAt(0)) === 45 ? (t5 = t5.slice(1), o2.s = -1) : (r3 === 43 && (t5 = t5.slice(1)), o2.s = 1), isDecimal.test(t5) ? parseDecimal(o2, t5) : parseOther(o2, t5);
  }
  if (a2.prototype = P$3, a2.ROUND_UP = 0, a2.ROUND_DOWN = 1, a2.ROUND_CEIL = 2, a2.ROUND_FLOOR = 3, a2.ROUND_HALF_UP = 4, a2.ROUND_HALF_DOWN = 5, a2.ROUND_HALF_EVEN = 6, a2.ROUND_HALF_CEIL = 7, a2.ROUND_HALF_FLOOR = 8, a2.EUCLID = 9, a2.config = a2.set = config, a2.clone = clone$2, a2.isDecimal = isDecimalInstance, a2.abs = abs$1, a2.acos = acos$1, a2.acosh = acosh$1, a2.add = add$1, a2.asin = asin$1, a2.asinh = asinh$1, a2.atan = atan$1, a2.atanh = atanh$1, a2.atan2 = atan2$1, a2.cbrt = cbrt, a2.ceil = ceil, a2.clamp = clamp, a2.cos = cos$1, a2.cosh = cosh$2, a2.div = div, a2.exp = exp$1, a2.floor = floor, a2.hypot = hypot$1, a2.ln = ln, a2.log = log$1, a2.log10 = log10$1, a2.log2 = log2, a2.max = max$1, a2.min = min$1, a2.mod = mod$1, a2.mul = mul, a2.pow = pow$1, a2.random = random$1, a2.round = round, a2.sign = sign$1, a2.sin = sin$1, a2.sinh = sinh$2, a2.sqrt = sqrt$1, a2.sub = sub, a2.sum = sum, a2.tan = tan$1, a2.tanh = tanh$1, a2.trunc = trunc$1, t4 === void 0 && (t4 = {}), t4 && t4.defaults !== true) for (i2 = [`precision`, `rounding`, `toExpNeg`, `toExpPos`, `maxE`, `minE`, `modulo`, `crypto`], n2 = 0; n2 < i2.length; ) t4.hasOwnProperty(r2 = i2[n2++]) || (t4[r2] = this[r2]);
  return a2.config(t4), a2;
}
function div(t4, n2) {
  return new this(t4).div(n2);
}
function exp$1(t4) {
  return new this(t4).exp();
}
function floor(t4) {
  return finalise(t4 = new this(t4), t4.e + 1, 3);
}
function hypot$1() {
  var t4, n2, r2 = new this(0);
  for (external = false, t4 = 0; t4 < arguments.length; ) if (n2 = new this(arguments[t4++]), n2.d) r2.d && (r2 = r2.plus(n2.times(n2)));
  else {
    if (n2.s) return external = true, new this(1 / 0);
    r2 = n2;
  }
  return external = true, r2.sqrt();
}
function isDecimalInstance(t4) {
  return t4 instanceof Decimal || t4 && t4.toStringTag === tag || false;
}
function ln(t4) {
  return new this(t4).ln();
}
function log$1(t4, n2) {
  return new this(t4).log(n2);
}
function log2(t4) {
  return new this(t4).log(2);
}
function log10$1(t4) {
  return new this(t4).log(10);
}
function max$1() {
  return maxOrMin(this, arguments, `lt`);
}
function min$1() {
  return maxOrMin(this, arguments, `gt`);
}
function mod$1(t4, n2) {
  return new this(t4).mod(n2);
}
function mul(t4, n2) {
  return new this(t4).mul(n2);
}
function pow$1(t4, n2) {
  return new this(t4).pow(n2);
}
function random$1(t4) {
  var n2, r2, i2, a2, o2 = 0, s2 = new this(1), c2 = [];
  if (t4 === void 0 ? t4 = this.precision : checkInt32(t4, 1, MAX_DIGITS), i2 = Math.ceil(t4 / LOG_BASE), !this.crypto) for (; o2 < i2; ) c2[o2++] = Math.random() * 1e7 | 0;
  else if (crypto.getRandomValues) for (n2 = crypto.getRandomValues(new Uint32Array(i2)); o2 < i2; ) a2 = n2[o2], a2 >= 429e7 ? n2[o2] = crypto.getRandomValues(new Uint32Array(1))[0] : c2[o2++] = a2 % 1e7;
  else if (crypto.randomBytes) {
    for (n2 = crypto.randomBytes(i2 *= 4); o2 < i2; ) a2 = n2[o2] + (n2[o2 + 1] << 8) + (n2[o2 + 2] << 16) + ((n2[o2 + 3] & 127) << 24), a2 >= 214e7 ? crypto.randomBytes(4).copy(n2, o2) : (c2.push(a2 % 1e7), o2 += 4);
    o2 = i2 / 4;
  } else throw Error(cryptoUnavailable);
  for (i2 = c2[--o2], t4 %= LOG_BASE, i2 && t4 && (a2 = mathpow(10, LOG_BASE - t4), c2[o2] = (i2 / a2 | 0) * a2); c2[o2] === 0; o2--) c2.pop();
  if (o2 < 0) r2 = 0, c2 = [0];
  else {
    for (r2 = -1; c2[0] === 0; r2 -= LOG_BASE) c2.shift();
    for (i2 = 1, a2 = c2[0]; a2 >= 10; a2 /= 10) i2++;
    i2 < LOG_BASE && (r2 -= LOG_BASE - i2);
  }
  return s2.e = r2, s2.d = c2, s2;
}
function round(t4) {
  return finalise(t4 = new this(t4), t4.e + 1, this.rounding);
}
function sign$1(t4) {
  return t4 = new this(t4), t4.d ? t4.d[0] ? t4.s : 0 * t4.s : t4.s || NaN;
}
function sin$1(t4) {
  return new this(t4).sin();
}
function sinh$2(t4) {
  return new this(t4).sinh();
}
function sqrt$1(t4) {
  return new this(t4).sqrt();
}
function sub(t4, n2) {
  return new this(t4).sub(n2);
}
function sum() {
  var t4 = 0, n2 = arguments, r2 = new this(n2[t4]);
  for (external = false; r2.s && ++t4 < n2.length; ) r2 = r2.plus(n2[t4]);
  return external = true, finalise(r2, this.precision, this.rounding);
}
function tan$1(t4) {
  return new this(t4).tan();
}
function tanh$1(t4) {
  return new this(t4).tanh();
}
function trunc$1(t4) {
  return finalise(t4 = new this(t4), t4.e + 1, 1);
}
P$3[/* @__PURE__ */ Symbol.for(`nodejs.util.inspect.custom`)] = P$3.toString, P$3[Symbol.toStringTag] = `Decimal`;
var Decimal = P$3.constructor = clone$2(DEFAULTS);
LN10 = new Decimal(LN10), PI = new Decimal(PI);
var name$320 = `BigNumber`, dependencies$320 = [`?on`, `config`], createBigNumberClass = factory(name$320, dependencies$320, (t4) => {
  var { on: n2, config: r2 } = t4, i2 = Decimal.clone({ precision: r2.precision, modulo: Decimal.EUCLID });
  return i2.prototype = Object.create(i2.prototype), i2.prototype.type = `BigNumber`, i2.prototype.isBigNumber = true, i2.prototype.toJSON = function() {
    return { mathjs: `BigNumber`, value: this.toString() };
  }, i2.fromJSON = function(t5) {
    return new i2(t5.value);
  }, n2 && n2(`config`, function(t5, n3) {
    t5.precision !== n3.precision && i2.config({ precision: t5.precision });
  }), i2;
}, { isClass: true }), cosh$1 = Math.cosh || function(t4) {
  return Math.abs(t4) < 1e-9 ? 1 - t4 : (Math.exp(t4) + Math.exp(-t4)) * 0.5;
}, sinh$1 = Math.sinh || function(t4) {
  return Math.abs(t4) < 1e-9 ? t4 : (Math.exp(t4) - Math.exp(-t4)) * 0.5;
}, cosm1 = function(t4) {
  let n2 = Math.PI / 4;
  if (-n2 > t4 || t4 > n2) return Math.cos(t4) - 1;
  let r2 = t4 * t4;
  return r2 * (r2 * (r2 * (r2 * (r2 * (r2 * (r2 * (r2 / 20922789888e3 - 1 / 87178291200) + 1 / 479001600) - 1 / 3628800) + 1 / 40320) - 1 / 720) + 1 / 24) - 1 / 2);
}, hypot = function(t4, n2) {
  return t4 = Math.abs(t4), n2 = Math.abs(n2), t4 < n2 && ([t4, n2] = [n2, t4]), t4 < 1e8 ? Math.sqrt(t4 * t4 + n2 * n2) : (n2 /= t4, t4 * Math.sqrt(1 + n2 * n2));
}, parser_exit = function() {
  throw SyntaxError(`Invalid Param`);
};
function logHypot(t4, n2) {
  let r2 = Math.abs(t4), i2 = Math.abs(n2);
  return t4 === 0 ? Math.log(i2) : n2 === 0 ? Math.log(r2) : r2 < 3e3 && i2 < 3e3 ? Math.log(t4 * t4 + n2 * n2) * 0.5 : (t4 *= 0.5, n2 *= 0.5, 0.5 * Math.log(t4 * t4 + n2 * n2) + Math.LN2);
}
var P$2 = { re: 0, im: 0 }, parse$1 = function(t4, n2) {
  let r2 = P$2;
  if (t4 == null) r2.re = r2.im = 0;
  else if (n2 !== void 0) r2.re = t4, r2.im = n2;
  else switch (typeof t4) {
    case `object`:
      if (`im` in t4 && `re` in t4) r2.re = t4.re, r2.im = t4.im;
      else if (`abs` in t4 && `arg` in t4) {
        if (!isFinite(t4.abs) && isFinite(t4.arg)) return Complex.INFINITY;
        r2.re = t4.abs * Math.cos(t4.arg), r2.im = t4.abs * Math.sin(t4.arg);
      } else if (`r` in t4 && `phi` in t4) {
        if (!isFinite(t4.r) && isFinite(t4.phi)) return Complex.INFINITY;
        r2.re = t4.r * Math.cos(t4.phi), r2.im = t4.r * Math.sin(t4.phi);
      } else t4.length === 2 ? (r2.re = t4[0], r2.im = t4[1]) : parser_exit();
      break;
    case `string`:
      r2.im = r2.re = 0;
      let n3 = t4.replace(/_/g, ``).match(/\d+\.?\d*e[+-]?\d+|\d+\.?\d*|\.\d+|./g), i2 = 1, a2 = 0;
      n3 === null && parser_exit();
      for (let t5 = 0; t5 < n3.length; t5++) {
        let o2 = n3[t5];
        o2 === ` ` || o2 === `	` || o2 === `
` || (o2 === `+` ? i2++ : o2 === `-` ? a2++ : o2 === `i` || o2 === `I` ? (i2 + a2 === 0 && parser_exit(), n3[t5 + 1] !== ` ` && !isNaN(n3[t5 + 1]) ? (r2.im += parseFloat((a2 % 2 ? `-` : ``) + n3[t5 + 1]), t5++) : r2.im += parseFloat((a2 % 2 ? `-` : ``) + `1`), i2 = a2 = 0) : ((i2 + a2 === 0 || isNaN(o2)) && parser_exit(), n3[t5 + 1] === `i` || n3[t5 + 1] === `I` ? (r2.im += parseFloat((a2 % 2 ? `-` : ``) + o2), t5++) : r2.re += parseFloat((a2 % 2 ? `-` : ``) + o2), i2 = a2 = 0));
      }
      i2 + a2 > 0 && parser_exit();
      break;
    case `number`:
      r2.im = 0, r2.re = t4;
      break;
    default:
      parser_exit();
  }
  return isNaN(r2.re) || isNaN(r2.im), r2;
};
function Complex(t4, n2) {
  if (!(this instanceof Complex)) return new Complex(t4, n2);
  let r2 = parse$1(t4, n2);
  this.re = r2.re, this.im = r2.im;
}
Complex.prototype = { re: 0, im: 0, sign: function() {
  let t4 = hypot(this.re, this.im);
  return new Complex(this.re / t4, this.im / t4);
}, add: function(t4, n2) {
  let r2 = parse$1(t4, n2), i2 = this.isInfinite(), a2 = !(isFinite(r2.re) && isFinite(r2.im));
  return i2 || a2 ? i2 && a2 ? Complex.NAN : Complex.INFINITY : new Complex(this.re + r2.re, this.im + r2.im);
}, sub: function(t4, n2) {
  let r2 = parse$1(t4, n2), i2 = this.isInfinite(), a2 = !(isFinite(r2.re) && isFinite(r2.im));
  return i2 || a2 ? i2 && a2 ? Complex.NAN : Complex.INFINITY : new Complex(this.re - r2.re, this.im - r2.im);
}, mul: function(t4, n2) {
  let r2 = parse$1(t4, n2), i2 = this.isInfinite(), a2 = !(isFinite(r2.re) && isFinite(r2.im)), o2 = this.re === 0 && this.im === 0, s2 = r2.re === 0 && r2.im === 0;
  return i2 && s2 || a2 && o2 ? Complex.NAN : i2 || a2 ? Complex.INFINITY : r2.im === 0 && this.im === 0 ? new Complex(this.re * r2.re, 0) : new Complex(this.re * r2.re - this.im * r2.im, this.re * r2.im + this.im * r2.re);
}, div: function(t4, n2) {
  let r2 = parse$1(t4, n2), i2 = this.isInfinite(), a2 = !(isFinite(r2.re) && isFinite(r2.im)), o2 = this.re === 0 && this.im === 0, s2 = r2.re === 0 && r2.im === 0;
  if (o2 && s2 || i2 && a2) return Complex.NAN;
  if (s2 || i2) return Complex.INFINITY;
  if (o2 || a2) return Complex.ZERO;
  if (r2.im === 0) return new Complex(this.re / r2.re, this.im / r2.re);
  if (Math.abs(r2.re) < Math.abs(r2.im)) {
    let t5 = r2.re / r2.im, n3 = r2.re * t5 + r2.im;
    return new Complex((this.re * t5 + this.im) / n3, (this.im * t5 - this.re) / n3);
  } else {
    let t5 = r2.im / r2.re, n3 = r2.im * t5 + r2.re;
    return new Complex((this.re + this.im * t5) / n3, (this.im - this.re * t5) / n3);
  }
}, pow: function(t4, n2) {
  let r2 = parse$1(t4, n2), i2 = this.re === 0 && this.im === 0;
  if (r2.re === 0 && r2.im === 0) return Complex.ONE;
  if (r2.im === 0) {
    if (this.im === 0 && this.re > 0) return new Complex(this.re ** +r2.re, 0);
    if (this.re === 0) switch ((r2.re % 4 + 4) % 4) {
      case 0:
        return new Complex(this.im ** +r2.re, 0);
      case 1:
        return new Complex(0, this.im ** +r2.re);
      case 2:
        return new Complex(-(this.im ** +r2.re), 0);
      case 3:
        return new Complex(0, -(this.im ** +r2.re));
    }
  }
  if (i2 && r2.re > 0) return Complex.ZERO;
  let a2 = Math.atan2(this.im, this.re), o2 = logHypot(this.re, this.im), s2 = Math.exp(r2.re * o2 - r2.im * a2), c2 = r2.im * o2 + r2.re * a2;
  return new Complex(s2 * Math.cos(c2), s2 * Math.sin(c2));
}, sqrt: function() {
  let t4 = this.re, n2 = this.im;
  if (n2 === 0) return t4 >= 0 ? new Complex(Math.sqrt(t4), 0) : new Complex(0, Math.sqrt(-t4));
  let r2 = hypot(t4, n2), i2 = Math.sqrt(0.5 * (r2 + Math.abs(t4))), a2 = Math.abs(n2) / (2 * i2);
  return t4 >= 0 ? new Complex(i2, n2 < 0 ? -a2 : a2) : new Complex(a2, n2 < 0 ? -i2 : i2);
}, exp: function() {
  let t4 = Math.exp(this.re);
  return this.im === 0 ? new Complex(t4, 0) : new Complex(t4 * Math.cos(this.im), t4 * Math.sin(this.im));
}, expm1: function() {
  let t4 = this.re, n2 = this.im;
  return new Complex(Math.expm1(t4) * Math.cos(n2) + cosm1(n2), Math.exp(t4) * Math.sin(n2));
}, log: function() {
  let t4 = this.re, n2 = this.im;
  return n2 === 0 && t4 > 0 ? new Complex(Math.log(t4), 0) : new Complex(logHypot(t4, n2), Math.atan2(n2, t4));
}, abs: function() {
  return hypot(this.re, this.im);
}, arg: function() {
  return Math.atan2(this.im, this.re);
}, sin: function() {
  let t4 = this.re, n2 = this.im;
  return new Complex(Math.sin(t4) * cosh$1(n2), Math.cos(t4) * sinh$1(n2));
}, cos: function() {
  let t4 = this.re, n2 = this.im;
  return new Complex(Math.cos(t4) * cosh$1(n2), -Math.sin(t4) * sinh$1(n2));
}, tan: function() {
  let t4 = 2 * this.re, n2 = 2 * this.im, r2 = Math.cos(t4) + cosh$1(n2);
  return new Complex(Math.sin(t4) / r2, sinh$1(n2) / r2);
}, cot: function() {
  let t4 = 2 * this.re, n2 = 2 * this.im, r2 = Math.cos(t4) - cosh$1(n2);
  return new Complex(-Math.sin(t4) / r2, sinh$1(n2) / r2);
}, sec: function() {
  let t4 = this.re, n2 = this.im, r2 = 0.5 * cosh$1(2 * n2) + 0.5 * Math.cos(2 * t4);
  return new Complex(Math.cos(t4) * cosh$1(n2) / r2, Math.sin(t4) * sinh$1(n2) / r2);
}, csc: function() {
  let t4 = this.re, n2 = this.im, r2 = 0.5 * cosh$1(2 * n2) - 0.5 * Math.cos(2 * t4);
  return new Complex(Math.sin(t4) * cosh$1(n2) / r2, -Math.cos(t4) * sinh$1(n2) / r2);
}, asin: function() {
  let t4 = this.re, n2 = this.im, r2 = new Complex(n2 * n2 - t4 * t4 + 1, -2 * t4 * n2).sqrt(), i2 = new Complex(r2.re - n2, r2.im + t4).log();
  return new Complex(i2.im, -i2.re);
}, acos: function() {
  let t4 = this.re, n2 = this.im, r2 = new Complex(n2 * n2 - t4 * t4 + 1, -2 * t4 * n2).sqrt(), i2 = new Complex(r2.re - n2, r2.im + t4).log();
  return new Complex(Math.PI / 2 - i2.im, i2.re);
}, atan: function() {
  let t4 = this.re, n2 = this.im;
  if (t4 === 0) {
    if (n2 === 1) return new Complex(0, 1 / 0);
    if (n2 === -1) return new Complex(0, -1 / 0);
  }
  let r2 = t4 * t4 + (1 - n2) * (1 - n2), i2 = new Complex((1 - n2 * n2 - t4 * t4) / r2, -2 * t4 / r2).log();
  return new Complex(-0.5 * i2.im, 0.5 * i2.re);
}, acot: function() {
  let t4 = this.re, n2 = this.im;
  if (n2 === 0) return new Complex(Math.atan2(1, t4), 0);
  let r2 = t4 * t4 + n2 * n2;
  return r2 === 0 ? new Complex(t4 === 0 ? 0 : t4 / 0, n2 === 0 ? 0 : -n2 / 0).atan() : new Complex(t4 / r2, -n2 / r2).atan();
}, asec: function() {
  let t4 = this.re, n2 = this.im;
  if (t4 === 0 && n2 === 0) return new Complex(0, 1 / 0);
  let r2 = t4 * t4 + n2 * n2;
  return r2 === 0 ? new Complex(t4 === 0 ? 0 : t4 / 0, n2 === 0 ? 0 : -n2 / 0).acos() : new Complex(t4 / r2, -n2 / r2).acos();
}, acsc: function() {
  let t4 = this.re, n2 = this.im;
  if (t4 === 0 && n2 === 0) return new Complex(Math.PI / 2, 1 / 0);
  let r2 = t4 * t4 + n2 * n2;
  return r2 === 0 ? new Complex(t4 === 0 ? 0 : t4 / 0, n2 === 0 ? 0 : -n2 / 0).asin() : new Complex(t4 / r2, -n2 / r2).asin();
}, sinh: function() {
  let t4 = this.re, n2 = this.im;
  return new Complex(sinh$1(t4) * Math.cos(n2), cosh$1(t4) * Math.sin(n2));
}, cosh: function() {
  let t4 = this.re, n2 = this.im;
  return new Complex(cosh$1(t4) * Math.cos(n2), sinh$1(t4) * Math.sin(n2));
}, tanh: function() {
  let t4 = 2 * this.re, n2 = 2 * this.im, r2 = cosh$1(t4) + Math.cos(n2);
  return new Complex(sinh$1(t4) / r2, Math.sin(n2) / r2);
}, coth: function() {
  let t4 = 2 * this.re, n2 = 2 * this.im, r2 = cosh$1(t4) - Math.cos(n2);
  return new Complex(sinh$1(t4) / r2, -Math.sin(n2) / r2);
}, csch: function() {
  let t4 = this.re, n2 = this.im, r2 = Math.cos(2 * n2) - cosh$1(2 * t4);
  return new Complex(-2 * sinh$1(t4) * Math.cos(n2) / r2, 2 * cosh$1(t4) * Math.sin(n2) / r2);
}, sech: function() {
  let t4 = this.re, n2 = this.im, r2 = Math.cos(2 * n2) + cosh$1(2 * t4);
  return new Complex(2 * cosh$1(t4) * Math.cos(n2) / r2, -2 * sinh$1(t4) * Math.sin(n2) / r2);
}, asinh: function() {
  let t4 = this.im;
  this.im = -this.re, this.re = t4;
  let n2 = this.asin();
  return this.re = -this.im, this.im = t4, t4 = n2.re, n2.re = -n2.im, n2.im = t4, n2;
}, acosh: function() {
  let t4 = this.acos();
  if (t4.im <= 0) {
    let n2 = t4.re;
    t4.re = -t4.im, t4.im = n2;
  } else {
    let n2 = t4.im;
    t4.im = -t4.re, t4.re = n2;
  }
  return t4;
}, atanh: function() {
  let t4 = this.re, n2 = this.im, r2 = t4 > 1 && n2 === 0, i2 = 1 - t4, a2 = 1 + t4, o2 = i2 * i2 + n2 * n2, s2 = o2 === 0 ? new Complex(t4 === -1 ? 0 : t4 / 0, n2 === 0 ? 0 : n2 / 0) : new Complex((a2 * i2 - n2 * n2) / o2, (n2 * i2 + a2 * n2) / o2), c2 = s2.re;
  return s2.re = logHypot(s2.re, s2.im) / 2, s2.im = Math.atan2(s2.im, c2) / 2, r2 && (s2.im = -s2.im), s2;
}, acoth: function() {
  let t4 = this.re, n2 = this.im;
  if (t4 === 0 && n2 === 0) return new Complex(0, Math.PI / 2);
  let r2 = t4 * t4 + n2 * n2;
  return r2 === 0 ? new Complex(t4 === 0 ? 0 : t4 / 0, n2 === 0 ? 0 : -n2 / 0).atanh() : new Complex(t4 / r2, -n2 / r2).atanh();
}, acsch: function() {
  let t4 = this.re, n2 = this.im;
  if (n2 === 0) return new Complex(t4 === 0 ? 1 / 0 : Math.log(t4 + Math.sqrt(t4 * t4 + 1)), 0);
  let r2 = t4 * t4 + n2 * n2;
  return r2 === 0 ? new Complex(t4 === 0 ? 0 : t4 / 0, n2 === 0 ? 0 : -n2 / 0).asinh() : new Complex(t4 / r2, -n2 / r2).asinh();
}, asech: function() {
  let t4 = this.re, n2 = this.im;
  if (this.isZero()) return Complex.INFINITY;
  let r2 = t4 * t4 + n2 * n2;
  return r2 === 0 ? new Complex(t4 === 0 ? 0 : t4 / 0, n2 === 0 ? 0 : -n2 / 0).acosh() : new Complex(t4 / r2, -n2 / r2).acosh();
}, inverse: function() {
  if (this.isZero()) return Complex.INFINITY;
  if (this.isInfinite()) return Complex.ZERO;
  let t4 = this.re, n2 = this.im, r2 = t4 * t4 + n2 * n2;
  return new Complex(t4 / r2, -n2 / r2);
}, conjugate: function() {
  return new Complex(this.re, -this.im);
}, neg: function() {
  return new Complex(-this.re, -this.im);
}, ceil: function(t4) {
  return t4 = 10 ** (t4 || 0), new Complex(Math.ceil(this.re * t4) / t4, Math.ceil(this.im * t4) / t4);
}, floor: function(t4) {
  return t4 = 10 ** (t4 || 0), new Complex(Math.floor(this.re * t4) / t4, Math.floor(this.im * t4) / t4);
}, round: function(t4) {
  return t4 = 10 ** (t4 || 0), new Complex(Math.round(this.re * t4) / t4, Math.round(this.im * t4) / t4);
}, equals: function(t4, n2) {
  let r2 = parse$1(t4, n2);
  return Math.abs(r2.re - this.re) <= Complex.EPSILON && Math.abs(r2.im - this.im) <= Complex.EPSILON;
}, clone: function() {
  return new Complex(this.re, this.im);
}, toString: function() {
  let t4 = this.re, n2 = this.im, r2 = ``;
  return this.isNaN() ? `NaN` : this.isInfinite() ? `Infinity` : (Math.abs(t4) < Complex.EPSILON && (t4 = 0), Math.abs(n2) < Complex.EPSILON && (n2 = 0), n2 === 0 ? r2 + t4 : (t4 === 0 ? n2 < 0 && (n2 = -n2, r2 += `-`) : (r2 += t4, r2 += ` `, n2 < 0 ? (n2 = -n2, r2 += `-`) : r2 += `+`, r2 += ` `), n2 !== 1 && (r2 += n2), r2 + `i`));
}, toVector: function() {
  return [this.re, this.im];
}, valueOf: function() {
  return this.im === 0 ? this.re : null;
}, isNaN: function() {
  return isNaN(this.re) || isNaN(this.im);
}, isZero: function() {
  return this.im === 0 && this.re === 0;
}, isFinite: function() {
  return isFinite(this.re) && isFinite(this.im);
}, isInfinite: function() {
  return !this.isFinite();
} }, Complex.ZERO = new Complex(0, 0), Complex.ONE = new Complex(1, 0), Complex.I = new Complex(0, 1), Complex.PI = new Complex(Math.PI, 0), Complex.E = new Complex(Math.E, 0), Complex.INFINITY = new Complex(1 / 0, 1 / 0), Complex.NAN = new Complex(NaN, NaN), Complex.EPSILON = 1e-15;
var name$319 = `Complex`, dependencies$319 = [], createComplexClass = factory(name$319, dependencies$319, () => (Object.defineProperty(Complex, "name", { value: `Complex` }), Complex.prototype.constructor = Complex, Complex.prototype.type = `Complex`, Complex.prototype.isComplex = true, Complex.prototype.toJSON = function() {
  return { mathjs: `Complex`, re: this.re, im: this.im };
}, Complex.prototype.toPolar = function() {
  return { r: this.abs(), phi: this.arg() };
}, Complex.prototype.format = function(t4) {
  var n2 = ``, r2 = this.im, i2 = this.re, a2 = format$2(this.re, t4), o2 = format$2(this.im, t4), s2 = isNumber(t4) ? t4 : t4 ? t4.precision : null;
  if (s2 !== null) {
    var c2 = 10 ** -s2;
    Math.abs(i2 / r2) < c2 && (i2 = 0), Math.abs(r2 / i2) < c2 && (r2 = 0);
  }
  return n2 = r2 === 0 ? a2 : i2 === 0 ? r2 === 1 ? `i` : r2 === -1 ? `-i` : o2 + `i` : r2 < 0 ? r2 === -1 ? a2 + ` - i` : a2 + ` - ` + o2.substring(1) + `i` : r2 === 1 ? a2 + ` + i` : a2 + ` + ` + o2 + `i`, n2;
}, Complex.fromPolar = function(t4) {
  switch (arguments.length) {
    case 1:
      var n2 = arguments[0];
      if (typeof n2 == `object`) return Complex(n2);
      throw TypeError(`Input has to be an object with r and phi keys.`);
    case 2:
      var r2 = arguments[0], i2 = arguments[1];
      if (isNumber(r2)) {
        if (isUnit(i2) && i2.hasBase(`ANGLE`) && (i2 = i2.toNumber(`rad`)), isNumber(i2)) return new Complex({ r: r2, phi: i2 });
        throw TypeError(`Phi is not a number nor an angle unit.`);
      } else throw TypeError(`Radius r is not a number.`);
    default:
      throw SyntaxError(`Wrong number of arguments in function fromPolar`);
  }
}, Complex.prototype.valueOf = Complex.prototype.toString, Complex.fromJSON = function(t4) {
  return new Complex(t4);
}, Complex.compare = function(t4, n2) {
  return t4.re > n2.re ? 1 : t4.re < n2.re ? -1 : t4.im > n2.im ? 1 : t4.im < n2.im ? -1 : 0;
}, Complex), { isClass: true });
typeof BigInt > `u` && (BigInt = function(t4) {
  if (isNaN(t4)) throw Error(``);
  return t4;
});
var C_ZERO = BigInt(0), C_ONE = BigInt(1), C_TWO = BigInt(2), C_FIVE = BigInt(5), C_TEN = BigInt(10), MAX_CYCLE_LEN = 2e3, P$1 = { s: C_ONE, n: C_ZERO, d: C_ONE };
function assign(t4, n2) {
  try {
    t4 = BigInt(t4);
  } catch {
    throw InvalidParameter();
  }
  return t4 * n2;
}
function trunc(t4) {
  return typeof t4 == `bigint` ? t4 : Math.floor(t4);
}
function newFraction(t4, n2) {
  if (n2 === C_ZERO) throw DivisionByZero();
  let r2 = Object.create(Fraction.prototype);
  r2.s = t4 < C_ZERO ? -C_ONE : C_ONE, t4 = t4 < C_ZERO ? -t4 : t4;
  let i2 = gcd$1(t4, n2);
  return r2.n = t4 / i2, r2.d = n2 / i2, r2;
}
function factorize(t4) {
  let n2 = {}, r2 = t4, i2 = C_TWO, a2 = C_FIVE - C_ONE;
  for (; a2 <= r2; ) {
    for (; r2 % i2 === C_ZERO; ) r2 /= i2, n2[i2] = (n2[i2] || C_ZERO) + C_ONE;
    a2 += C_ONE + C_TWO * i2++;
  }
  return r2 === t4 ? n2[t4] = (n2[t4] || C_ZERO) + C_ONE : r2 > 1 && (n2[r2] = (n2[r2] || C_ZERO) + C_ONE), n2;
}
var parse = function(t4, n2) {
  let r2 = C_ZERO, i2 = C_ONE, a2 = C_ONE;
  if (t4 != null) if (n2 !== void 0) {
    if (typeof t4 == `bigint`) r2 = t4;
    else if (isNaN(t4)) throw InvalidParameter();
    else if (t4 % 1 != 0) throw NonIntegerParameter();
    else r2 = BigInt(t4);
    if (typeof n2 == `bigint`) i2 = n2;
    else if (isNaN(n2)) throw InvalidParameter();
    else if (n2 % 1 != 0) throw NonIntegerParameter();
    else i2 = BigInt(n2);
    a2 = r2 * i2;
  } else if (typeof t4 == `object`) {
    if (`d` in t4 && `n` in t4) r2 = BigInt(t4.n), i2 = BigInt(t4.d), `s` in t4 && (r2 *= BigInt(t4.s));
    else if (0 in t4) r2 = BigInt(t4[0]), 1 in t4 && (i2 = BigInt(t4[1]));
    else if (typeof t4 == `bigint`) r2 = t4;
    else throw InvalidParameter();
    a2 = r2 * i2;
  } else if (typeof t4 == `number`) {
    if (isNaN(t4)) throw InvalidParameter();
    if (t4 < 0 && (a2 = -C_ONE, t4 = -t4), t4 % 1 == 0) r2 = BigInt(t4);
    else {
      let n3 = 1, a3 = 0, o2 = 1, s2 = 1, c2 = 1, l2 = 1e7;
      for (t4 >= 1 && (n3 = 10 ** Math.floor(1 + Math.log10(t4)), t4 /= n3); o2 <= l2 && c2 <= l2; ) {
        let n4 = (a3 + s2) / (o2 + c2);
        if (t4 === n4) {
          o2 + c2 <= l2 ? (r2 = a3 + s2, i2 = o2 + c2) : c2 > o2 ? (r2 = s2, i2 = c2) : (r2 = a3, i2 = o2);
          break;
        } else t4 > n4 ? (a3 += s2, o2 += c2) : (s2 += a3, c2 += o2), o2 > l2 ? (r2 = s2, i2 = c2) : (r2 = a3, i2 = o2);
      }
      r2 = BigInt(r2) * BigInt(n3), i2 = BigInt(i2);
    }
  } else if (typeof t4 == `string`) {
    let n3 = 0, o2 = C_ZERO, s2 = C_ZERO, c2 = C_ZERO, l2 = C_ONE, u2 = C_ONE, d2 = t4.replace(/_/g, ``).match(/\d+|./g);
    if (d2 === null) throw InvalidParameter();
    if (d2[n3] === `-` ? (a2 = -C_ONE, n3++) : d2[n3] === `+` && n3++, d2.length === n3 + 1 ? s2 = assign(d2[n3++], a2) : d2[n3 + 1] === `.` || d2[n3] === `.` ? (d2[n3] !== `.` && (o2 = assign(d2[n3++], a2)), n3++, (n3 + 1 === d2.length || d2[n3 + 1] === `(` && d2[n3 + 3] === `)` || d2[n3 + 1] === `'` && d2[n3 + 3] === `'`) && (s2 = assign(d2[n3], a2), l2 = C_TEN ** BigInt(d2[n3].length), n3++), (d2[n3] === `(` && d2[n3 + 2] === `)` || d2[n3] === `'` && d2[n3 + 2] === `'`) && (c2 = assign(d2[n3 + 1], a2), u2 = C_TEN ** BigInt(d2[n3 + 1].length) - C_ONE, n3 += 3)) : d2[n3 + 1] === `/` || d2[n3 + 1] === `:` ? (s2 = assign(d2[n3], a2), l2 = assign(d2[n3 + 2], C_ONE), n3 += 3) : d2[n3 + 3] === `/` && d2[n3 + 1] === ` ` && (o2 = assign(d2[n3], a2), s2 = assign(d2[n3 + 2], a2), l2 = assign(d2[n3 + 4], C_ONE), n3 += 5), d2.length <= n3) i2 = l2 * u2, a2 = r2 = c2 + i2 * o2 + u2 * s2;
    else throw InvalidParameter();
  } else if (typeof t4 == `bigint`) r2 = t4, a2 = t4, i2 = C_ONE;
  else throw InvalidParameter();
  if (i2 === C_ZERO) throw DivisionByZero();
  P$1.s = a2 < C_ZERO ? -C_ONE : C_ONE, P$1.n = r2 < C_ZERO ? -r2 : r2, P$1.d = i2 < C_ZERO ? -i2 : i2;
};
function modpow(t4, n2, r2) {
  let i2 = C_ONE;
  for (; n2 > C_ZERO; t4 = t4 * t4 % r2, n2 >>= C_ONE) n2 & C_ONE && (i2 = i2 * t4 % r2);
  return i2;
}
function cycleLen(t4, n2) {
  for (; n2 % C_TWO === C_ZERO; n2 /= C_TWO) ;
  for (; n2 % C_FIVE === C_ZERO; n2 /= C_FIVE) ;
  if (n2 === C_ONE) return C_ZERO;
  let r2 = C_TEN % n2, i2 = 1;
  for (; r2 !== C_ONE; i2++) if (r2 = r2 * C_TEN % n2, i2 > MAX_CYCLE_LEN) return C_ZERO;
  return BigInt(i2);
}
function cycleStart(t4, n2, r2) {
  let i2 = C_ONE, a2 = modpow(C_TEN, r2, n2);
  for (let t5 = 0; t5 < 300; t5++) {
    if (i2 === a2) return BigInt(t5);
    i2 = i2 * C_TEN % n2, a2 = a2 * C_TEN % n2;
  }
  return 0;
}
function gcd$1(t4, n2) {
  if (!t4) return n2;
  if (!n2) return t4;
  for (; ; ) {
    if (t4 %= n2, !t4) return n2;
    if (n2 %= t4, !n2) return t4;
  }
}
function Fraction(t4, n2) {
  if (parse(t4, n2), this instanceof Fraction) t4 = gcd$1(P$1.d, P$1.n), this.s = P$1.s, this.n = P$1.n / t4, this.d = P$1.d / t4;
  else return newFraction(P$1.s * P$1.n, P$1.d);
}
var DivisionByZero = function() {
  return Error(`Division by Zero`);
}, InvalidParameter = function() {
  return Error(`Invalid argument`);
}, NonIntegerParameter = function() {
  return Error(`Parameters must be integer`);
};
Fraction.prototype = { s: C_ONE, n: C_ZERO, d: C_ONE, abs: function() {
  return newFraction(this.n, this.d);
}, neg: function() {
  return newFraction(-this.s * this.n, this.d);
}, add: function(t4, n2) {
  return parse(t4, n2), newFraction(this.s * this.n * P$1.d + P$1.s * this.d * P$1.n, this.d * P$1.d);
}, sub: function(t4, n2) {
  return parse(t4, n2), newFraction(this.s * this.n * P$1.d - P$1.s * this.d * P$1.n, this.d * P$1.d);
}, mul: function(t4, n2) {
  return parse(t4, n2), newFraction(this.s * P$1.s * this.n * P$1.n, this.d * P$1.d);
}, div: function(t4, n2) {
  return parse(t4, n2), newFraction(this.s * P$1.s * this.n * P$1.d, this.d * P$1.n);
}, clone: function() {
  return newFraction(this.s * this.n, this.d);
}, mod: function(t4, n2) {
  if (t4 === void 0) return newFraction(this.s * this.n % this.d, C_ONE);
  if (parse(t4, n2), C_ZERO === P$1.n * this.d) throw DivisionByZero();
  return newFraction(this.s * (P$1.d * this.n) % (P$1.n * this.d), P$1.d * this.d);
}, gcd: function(t4, n2) {
  return parse(t4, n2), newFraction(gcd$1(P$1.n, this.n) * gcd$1(P$1.d, this.d), P$1.d * this.d);
}, lcm: function(t4, n2) {
  return parse(t4, n2), P$1.n === C_ZERO && this.n === C_ZERO ? newFraction(C_ZERO, C_ONE) : newFraction(P$1.n * this.n, gcd$1(P$1.n, this.n) * gcd$1(P$1.d, this.d));
}, inverse: function() {
  return newFraction(this.s * this.d, this.n);
}, pow: function(t4, n2) {
  if (parse(t4, n2), P$1.d === C_ONE) return P$1.s < C_ZERO ? newFraction((this.s * this.d) ** P$1.n, this.n ** P$1.n) : newFraction((this.s * this.n) ** P$1.n, this.d ** P$1.n);
  if (this.s < C_ZERO) return null;
  let r2 = factorize(this.n), i2 = factorize(this.d), a2 = C_ONE, o2 = C_ONE;
  for (let t5 in r2) if (t5 !== `1`) {
    if (t5 === `0`) {
      a2 = C_ZERO;
      break;
    }
    if (r2[t5] *= P$1.n, r2[t5] % P$1.d === C_ZERO) r2[t5] /= P$1.d;
    else return null;
    a2 *= BigInt(t5) ** r2[t5];
  }
  for (let t5 in i2) if (t5 !== `1`) {
    if (i2[t5] *= P$1.n, i2[t5] % P$1.d === C_ZERO) i2[t5] /= P$1.d;
    else return null;
    o2 *= BigInt(t5) ** i2[t5];
  }
  return P$1.s < C_ZERO ? newFraction(o2, a2) : newFraction(a2, o2);
}, log: function(t4, n2) {
  if (parse(t4, n2), this.s <= C_ZERO || P$1.s <= C_ZERO) return null;
  let r2 = {}, i2 = factorize(P$1.n), a2 = factorize(P$1.d), o2 = factorize(this.n), s2 = factorize(this.d);
  for (let t5 in a2) i2[t5] = (i2[t5] || C_ZERO) - a2[t5];
  for (let t5 in s2) o2[t5] = (o2[t5] || C_ZERO) - s2[t5];
  for (let t5 in i2) t5 !== `1` && (r2[t5] = true);
  for (let t5 in o2) t5 !== `1` && (r2[t5] = true);
  let c2 = null, l2 = null;
  for (let t5 in r2) {
    let n3 = i2[t5] || C_ZERO, r3 = o2[t5] || C_ZERO;
    if (n3 === C_ZERO) {
      if (r3 !== C_ZERO) return null;
      continue;
    }
    let a3 = r3, s3 = n3, u2 = gcd$1(a3, s3);
    if (a3 /= u2, s3 /= u2, c2 === null && l2 === null) c2 = a3, l2 = s3;
    else if (a3 * l2 !== c2 * s3) return null;
  }
  return c2 !== null && l2 !== null ? newFraction(c2, l2) : null;
}, equals: function(t4, n2) {
  return parse(t4, n2), this.s * this.n * P$1.d === P$1.s * P$1.n * this.d;
}, lt: function(t4, n2) {
  return parse(t4, n2), this.s * this.n * P$1.d < P$1.s * P$1.n * this.d;
}, lte: function(t4, n2) {
  return parse(t4, n2), this.s * this.n * P$1.d <= P$1.s * P$1.n * this.d;
}, gt: function(t4, n2) {
  return parse(t4, n2), this.s * this.n * P$1.d > P$1.s * P$1.n * this.d;
}, gte: function(t4, n2) {
  return parse(t4, n2), this.s * this.n * P$1.d >= P$1.s * P$1.n * this.d;
}, compare: function(t4, n2) {
  parse(t4, n2);
  let r2 = this.s * this.n * P$1.d - P$1.s * P$1.n * this.d;
  return (C_ZERO < r2) - (r2 < C_ZERO);
}, ceil: function(t4) {
  return t4 = C_TEN ** BigInt(t4 || 0), newFraction(trunc(this.s * t4 * this.n / this.d) + (t4 * this.n % this.d > C_ZERO && this.s >= C_ZERO ? C_ONE : C_ZERO), t4);
}, floor: function(t4) {
  return t4 = C_TEN ** BigInt(t4 || 0), newFraction(trunc(this.s * t4 * this.n / this.d) - (t4 * this.n % this.d > C_ZERO && this.s < C_ZERO ? C_ONE : C_ZERO), t4);
}, round: function(t4) {
  return t4 = C_TEN ** BigInt(t4 || 0), newFraction(trunc(this.s * t4 * this.n / this.d) + this.s * ((this.s >= C_ZERO ? C_ONE : C_ZERO) + C_TWO * (t4 * this.n % this.d) > this.d ? C_ONE : C_ZERO), t4);
}, roundTo: function(t4, n2) {
  parse(t4, n2);
  let r2 = this.n * P$1.d, i2 = this.d * P$1.n, a2 = r2 % i2, o2 = trunc(r2 / i2);
  return a2 + a2 >= i2 && o2++, newFraction(this.s * o2 * P$1.n, P$1.d);
}, divisible: function(t4, n2) {
  return parse(t4, n2), !(!(P$1.n * this.d) || this.n * P$1.d % (P$1.n * this.d));
}, valueOf: function() {
  return Number(this.s * this.n) / Number(this.d);
}, toString: function(t4) {
  let n2 = this.n, r2 = this.d;
  t4 ||= 15;
  let i2 = cycleLen(n2, r2), a2 = cycleStart(n2, r2, i2), o2 = this.s < C_ZERO ? `-` : ``;
  if (o2 += trunc(n2 / r2), n2 %= r2, n2 *= C_TEN, n2 && (o2 += `.`), i2) {
    for (let t5 = a2; t5--; ) o2 += trunc(n2 / r2), n2 %= r2, n2 *= C_TEN;
    o2 += `(`;
    for (let t5 = i2; t5--; ) o2 += trunc(n2 / r2), n2 %= r2, n2 *= C_TEN;
    o2 += `)`;
  } else for (let i3 = t4; n2 && i3--; ) o2 += trunc(n2 / r2), n2 %= r2, n2 *= C_TEN;
  return o2;
}, toFraction: function(t4) {
  let n2 = this.n, r2 = this.d, i2 = this.s < C_ZERO ? `-` : ``;
  if (r2 === C_ONE) i2 += n2;
  else {
    let a2 = trunc(n2 / r2);
    t4 && a2 > C_ZERO && (i2 += a2, i2 += ` `, n2 %= r2), i2 += n2, i2 += `/`, i2 += r2;
  }
  return i2;
}, toLatex: function(t4) {
  let n2 = this.n, r2 = this.d, i2 = this.s < C_ZERO ? `-` : ``;
  if (r2 === C_ONE) i2 += n2;
  else {
    let a2 = trunc(n2 / r2);
    t4 && a2 > C_ZERO && (i2 += a2, n2 %= r2), i2 += `\\frac{`, i2 += n2, i2 += `}{`, i2 += r2, i2 += `}`;
  }
  return i2;
}, toContinued: function() {
  let t4 = this.n, n2 = this.d, r2 = [];
  do {
    r2.push(trunc(t4 / n2));
    let i2 = t4 % n2;
    t4 = n2, n2 = i2;
  } while (t4 !== C_ONE);
  return r2;
}, simplify: function(t4) {
  let n2 = BigInt(1 / (t4 || 1e-3) | 0), r2 = this.abs(), i2 = r2.toContinued();
  for (let t5 = 1; t5 < i2.length; t5++) {
    let a2 = newFraction(i2[t5 - 1], C_ONE);
    for (let n3 = t5 - 2; n3 >= 0; n3--) a2 = a2.inverse().add(i2[n3]);
    let o2 = a2.sub(r2);
    if (o2.n * n2 < o2.d) return a2.mul(this.s);
  }
  return this;
} };
var name$318 = `Fraction`, dependencies$318 = [], createFractionClass = factory(name$318, dependencies$318, () => (Object.defineProperty(Fraction, "name", { value: `Fraction` }), Fraction.prototype.constructor = Fraction, Fraction.prototype.type = `Fraction`, Fraction.prototype.isFraction = true, Fraction.prototype.toJSON = function() {
  return { mathjs: `Fraction`, n: String(this.s * this.n), d: String(this.d) };
}, Fraction.fromJSON = function(t4) {
  return new Fraction(t4);
}, Fraction), { isClass: true }), name$317 = `Range`, dependencies$317 = [], createRangeClass = factory(name$317, dependencies$317, () => {
  function t4(n2, r2, i2) {
    if (!(this instanceof t4)) throw SyntaxError(`Constructor must be called with the new operator`);
    var a2 = n2 != null, o2 = r2 != null, s2 = i2 != null;
    if (a2) {
      if (isBigNumber(n2)) n2 = n2.toNumber();
      else if (typeof n2 != `number` && !isBigInt(n2)) throw TypeError(`Parameter start must be a number or bigint`);
    }
    if (o2) {
      if (isBigNumber(r2)) r2 = r2.toNumber();
      else if (typeof r2 != `number` && !isBigInt(r2)) throw TypeError(`Parameter end must be a number or bigint`);
    }
    if (s2) {
      if (isBigNumber(i2)) i2 = i2.toNumber();
      else if (typeof i2 != `number` && !isBigInt(i2)) throw TypeError(`Parameter step must be a number or bigint`);
    }
    if (this.start = a2 ? parseFloat(n2) : 0, this.end = o2 ? parseFloat(r2) : 0, this.step = s2 ? parseFloat(i2) : 1, s2 && nearlyEqual$1(this.step, 0)) throw Error(`Step must not be zero`);
  }
  return t4.prototype.type = `Range`, t4.prototype.isRange = true, t4.parse = function(n2) {
    if (typeof n2 != `string`) return null;
    var r2 = n2.split(`:`).map(function(t5) {
      return parseFloat(t5);
    });
    if (r2.some(function(t5) {
      return isNaN(t5);
    })) return null;
    switch (r2.length) {
      case 2:
        return new t4(r2[0], r2[1]);
      case 3:
        return new t4(r2[0], r2[2], r2[1]);
      default:
        return null;
    }
  }, t4.prototype.clone = function() {
    return new t4(this.start, this.end, this.step);
  }, t4.prototype.size = function() {
    var t5 = 0, n2 = this.start, r2 = this.step, i2 = this.end - n2;
    return sign$2(r2) === sign$2(i2) ? t5 = Math.ceil(i2 / r2) : i2 === 0 && (t5 = 0), isNaN(t5) && (t5 = 0), [t5];
  }, t4.prototype.min = function() {
    var t5 = this.size()[0];
    if (t5 > 0) return this.step > 0 ? this.start : this.start + (t5 - 1) * this.step;
  }, t4.prototype.max = function() {
    var t5 = this.size()[0];
    if (t5 > 0) return this.step > 0 ? this.start + (t5 - 1) * this.step : this.start;
  }, t4.prototype.forEach = function(t5) {
    var n2 = this.start, r2 = this.step, i2 = this.end, a2 = 0;
    if (r2 > 0) for (; n2 < i2; ) t5(n2, [a2], this), n2 += r2, a2++;
    else if (r2 < 0) for (; n2 > i2; ) t5(n2, [a2], this), n2 += r2, a2++;
  }, t4.prototype.map = function(t5) {
    var n2 = [];
    return this.forEach(function(r2, i2, a2) {
      n2[i2[0]] = t5(r2, i2, a2);
    }), n2;
  }, t4.prototype.toArray = function() {
    var t5 = [];
    return this.forEach(function(n2, r2) {
      t5[r2[0]] = n2;
    }), t5;
  }, t4.prototype.valueOf = function() {
    return this.toArray();
  }, t4.prototype.format = function(t5) {
    var n2 = format$2(this.start, t5);
    return this.step !== 1 && (n2 += `:` + format$2(this.step, t5)), n2 += `:` + format$2(this.end, t5), n2;
  }, t4.prototype.toString = function() {
    return this.format();
  }, t4.prototype.toJSON = function() {
    return { mathjs: `Range`, start: this.start, end: this.end, step: this.step };
  }, t4.fromJSON = function(n2) {
    return new t4(n2.start, n2.end, n2.step);
  }, t4;
}, { isClass: true }), name$316 = `Matrix`, dependencies$316 = [], createMatrixClass = factory(name$316, dependencies$316, () => {
  function t4() {
    if (!(this instanceof t4)) throw SyntaxError(`Constructor must be called with the new operator`);
  }
  return t4.prototype.type = `Matrix`, t4.prototype.isMatrix = true, t4.prototype.storage = function() {
    throw Error(`Cannot invoke storage on a Matrix interface`);
  }, t4.prototype.datatype = function() {
    throw Error(`Cannot invoke datatype on a Matrix interface`);
  }, t4.prototype.create = function(t5, n2) {
    throw Error(`Cannot invoke create on a Matrix interface`);
  }, t4.prototype.subset = function(t5, n2, r2) {
    throw Error(`Cannot invoke subset on a Matrix interface`);
  }, t4.prototype.get = function(t5) {
    throw Error(`Cannot invoke get on a Matrix interface`);
  }, t4.prototype.set = function(t5, n2, r2) {
    throw Error(`Cannot invoke set on a Matrix interface`);
  }, t4.prototype.resize = function(t5, n2) {
    throw Error(`Cannot invoke resize on a Matrix interface`);
  }, t4.prototype.reshape = function(t5, n2) {
    throw Error(`Cannot invoke reshape on a Matrix interface`);
  }, t4.prototype.clone = function() {
    throw Error(`Cannot invoke clone on a Matrix interface`);
  }, t4.prototype.size = function() {
    throw Error(`Cannot invoke size on a Matrix interface`);
  }, t4.prototype.map = function(t5, n2) {
    throw Error(`Cannot invoke map on a Matrix interface`);
  }, t4.prototype.forEach = function(t5) {
    throw Error(`Cannot invoke forEach on a Matrix interface`);
  }, t4.prototype[Symbol.iterator] = function() {
    throw Error(`Cannot iterate a Matrix interface`);
  }, t4.prototype.toArray = function() {
    throw Error(`Cannot invoke toArray on a Matrix interface`);
  }, t4.prototype.valueOf = function() {
    throw Error(`Cannot invoke valueOf on a Matrix interface`);
  }, t4.prototype.format = function(t5) {
    throw Error(`Cannot invoke format on a Matrix interface`);
  }, t4.prototype.toString = function() {
    throw Error(`Cannot invoke toString on a Matrix interface`);
  }, t4;
}, { isClass: true });
function formatBigNumberToBase(t4, n2, r2) {
  var i2 = t4.constructor, a2 = new i2(2), o2 = ``;
  if (r2) {
    if (r2 < 1) throw Error(`size must be in greater than 0`);
    if (!isInteger(r2)) throw Error(`size must be an integer`);
    if (t4.greaterThan(a2.pow(r2 - 1).sub(1)) || t4.lessThan(a2.pow(r2 - 1).mul(-1))) throw Error(`Value must be in range [-2^${r2 - 1}, 2^${r2 - 1}-1]`);
    if (!t4.isInteger()) throw Error(`Value must be an integer`);
    t4.lessThan(0) && (t4 = t4.add(a2.pow(r2))), o2 = `i${r2}`;
  }
  switch (n2) {
    case 2:
      return `${t4.toBinary()}${o2}`;
    case 8:
      return `${t4.toOctal()}${o2}`;
    case 16:
      return `${t4.toHexadecimal()}${o2}`;
    default:
      throw Error(`Base ${n2} not supported `);
  }
}
function format$1(t4, n2) {
  if (typeof n2 == `function`) return n2(t4);
  if (!t4.isFinite()) return t4.isNaN() ? `NaN` : t4.gt(0) ? `Infinity` : `-Infinity`;
  var { notation: r2, precision: i2, wordSize: a2 } = normalizeFormatOptions(n2);
  switch (r2) {
    case `fixed`:
      return toFixed$1(t4, i2);
    case `exponential`:
      return toExponential(t4, i2);
    case `engineering`:
      return toEngineering(t4, i2);
    case `bin`:
      return formatBigNumberToBase(t4, 2, a2);
    case `oct`:
      return formatBigNumberToBase(t4, 8, a2);
    case `hex`:
      return formatBigNumberToBase(t4, 16, a2);
    case `auto`:
      var o2 = _toNumberOrDefault(n2?.lowerExp, -3), s2 = _toNumberOrDefault(n2?.upperExp, 5);
      if (t4.isZero()) return `0`;
      var c2, l2 = t4.toSignificantDigits(i2), u2 = l2.e;
      return c2 = u2 >= o2 && u2 < s2 ? l2.toFixed() : toExponential(t4, i2), c2.replace(/((\.\d*?)(0+))($|e)/, function() {
        var t5 = arguments[2], n3 = arguments[4];
        return t5 === `.` ? n3 : t5 + n3;
      });
    default:
      throw Error(`Unknown notation "` + r2 + `". Choose "auto", "exponential", "fixed", "bin", "oct", or "hex.`);
  }
}
function toEngineering(t4, n2) {
  var r2 = t4.e, i2 = r2 % 3 == 0 ? r2 : r2 < 0 ? r2 - 3 - r2 % 3 : r2 - r2 % 3, a2 = t4.mul(10 ** -i2).toPrecision(n2);
  if (a2.includes(`e`)) {
    var o2 = t4.constructor;
    a2 = new o2(a2).toFixed();
  }
  return a2 + `e` + (r2 >= 0 ? `+` : ``) + i2.toString();
}
function toExponential(t4, n2) {
  return n2 === void 0 ? t4.toExponential() : t4.toExponential(n2 - 1);
}
function toFixed$1(t4, n2) {
  return t4.toFixed(n2);
}
function _toNumberOrDefault(t4, n2) {
  return isNumber(t4) ? t4 : isBigNumber(t4) ? t4.toNumber() : n2;
}
function endsWith(t4, n2) {
  var r2 = t4.length - n2.length, i2 = t4.length;
  return t4.substring(r2, i2) === n2;
}
function format(t4, n2) {
  var r2 = _format(t4, n2);
  return n2 && typeof n2 == `object` && `truncate` in n2 && r2.length > n2.truncate ? r2.substring(0, n2.truncate - 3) + `...` : r2;
}
function _format(t4, n2) {
  return typeof t4 == `number` ? format$2(t4, n2) : isBigNumber(t4) ? format$1(t4, n2) : looksLikeFraction(t4) ? !n2 || n2.fraction !== `decimal` ? `${t4.s * t4.n}/${t4.d}` : t4.toString() : Array.isArray(t4) ? formatArray(t4, n2) : isString(t4) ? stringify(t4) : typeof t4 == `function` ? t4.syntax ? String(t4.syntax) : `function` : t4 && typeof t4 == `object` ? typeof t4.format == `function` ? t4.format(n2) : t4 && t4.toString(n2) !== {}.toString() ? t4.toString(n2) : `{` + Object.keys(t4).map((r2) => stringify(r2) + `: ` + format(t4[r2], n2)).join(`, `) + `}` : String(t4);
}
function stringify(t4) {
  for (var n2 = String(t4), r2 = ``, i2 = 0; i2 < n2.length; ) {
    var a2 = n2.charAt(i2);
    r2 += a2 in controlCharacters ? controlCharacters[a2] : a2, i2++;
  }
  return `"` + r2 + `"`;
}
var controlCharacters = { '"': `\\"`, "\\": `\\\\`, "\b": `\\b`, "\f": `\\f`, "\n": `\\n`, "\r": `\\r`, "	": `\\t` };
function escape(t4) {
  var n2 = String(t4);
  return n2 = n2.replace(/&/g, `&amp;`).replace(/"/g, `&quot;`).replace(/'/g, `&#39;`).replace(/</g, `&lt;`).replace(/>/g, `&gt;`), n2;
}
function formatArray(t4, n2) {
  if (Array.isArray(t4)) {
    for (var r2 = `[`, i2 = t4.length, a2 = 0; a2 < i2; a2++) a2 !== 0 && (r2 += `, `), r2 += formatArray(t4[a2], n2);
    return r2 += `]`, r2;
  } else return format(t4, n2);
}
function looksLikeFraction(t4) {
  return t4 && typeof t4 == `object` && typeof t4.s == `bigint` && typeof t4.n == `bigint` && typeof t4.d == `bigint` || false;
}
function compareText(t4, n2) {
  if (!isString(t4)) throw TypeError(`Unexpected type of argument in function compareText (expected: string or Array or Matrix, actual: ` + typeOf(t4) + `, index: 0)`);
  if (!isString(n2)) throw TypeError(`Unexpected type of argument in function compareText (expected: string or Array or Matrix, actual: ` + typeOf(n2) + `, index: 1)`);
  return t4 === n2 ? 0 : t4 > n2 ? 1 : -1;
}
function DimensionError(t4, n2, r2) {
  if (!(this instanceof DimensionError)) throw SyntaxError(`Constructor must be called with the new operator`);
  this.actual = t4, this.expected = n2, this.relation = r2, this.message = `Dimension mismatch (` + (Array.isArray(t4) ? `[` + t4.join(`, `) + `]` : t4) + ` ` + (this.relation || `!=`) + ` ` + (Array.isArray(n2) ? `[` + n2.join(`, `) + `]` : n2) + `)`, this.stack = Error().stack;
}
DimensionError.prototype = RangeError(), DimensionError.prototype.constructor = RangeError, DimensionError.prototype.name = `DimensionError`, DimensionError.prototype.isDimensionError = true;
function IndexError(t4, n2, r2) {
  if (!(this instanceof IndexError)) throw SyntaxError(`Constructor must be called with the new operator`);
  this.index = t4, arguments.length < 3 ? (this.min = 0, this.max = n2) : (this.min = n2, this.max = r2), this.min !== void 0 && this.index < this.min ? this.message = `Index out of range (` + this.index + ` < ` + this.min + `)` : this.max !== void 0 && this.index >= this.max ? this.message = `Index out of range (` + this.index + ` > ` + (this.max - 1) + `)` : this.message = `Index out of range (` + this.index + `)`, this.stack = Error().stack;
}
IndexError.prototype = RangeError(), IndexError.prototype.constructor = RangeError, IndexError.prototype.name = `IndexError`, IndexError.prototype.isIndexError = true;
function arraySize(t4) {
  for (var n2 = []; Array.isArray(t4); ) n2.push(t4.length), t4 = t4[0];
  return n2;
}
function _validate(t4, n2, r2) {
  var i2, a2 = t4.length;
  if (a2 !== n2[r2]) throw new DimensionError(a2, n2[r2]);
  if (r2 < n2.length - 1) {
    var o2 = r2 + 1;
    for (i2 = 0; i2 < a2; i2++) {
      var s2 = t4[i2];
      if (!Array.isArray(s2)) throw new DimensionError(n2.length - 1, n2.length, `<`);
      _validate(t4[i2], n2, o2);
    }
  } else for (i2 = 0; i2 < a2; i2++) if (Array.isArray(t4[i2])) throw new DimensionError(n2.length + 1, n2.length, `>`);
}
function validate(t4, n2) {
  if (n2.length === 0) {
    if (Array.isArray(t4)) throw new DimensionError(t4.length, 0);
  } else _validate(t4, n2, 0);
}
function validateIndexSourceSize(t4, n2) {
  var r2 = t4.isMatrix ? t4._size : arraySize(t4);
  n2._sourceSize.forEach((t5, n3) => {
    if (t5 !== null && t5 !== r2[n3]) throw new DimensionError(t5, r2[n3]);
  });
}
function validateIndex(t4, n2) {
  if (t4 !== void 0) {
    if (!isNumber(t4) || !isInteger(t4)) throw TypeError(`Index must be an integer (value: ` + t4 + `)`);
    if (t4 < 0 || typeof n2 == `number` && t4 >= n2) throw new IndexError(t4, n2);
  }
}
function isEmptyIndex(t4) {
  for (var n2 = 0; n2 < t4._dimensions.length; ++n2) {
    var r2 = t4._dimensions[n2];
    if (r2._data && isArray(r2._data)) {
      if (r2._size[0] === 0) return true;
    } else if (r2.isRange) {
      if (r2.start === r2.end) return true;
    } else if (isString(r2) && r2.length === 0) return true;
  }
  return false;
}
function resize(t4, n2, r2) {
  if (!Array.isArray(n2)) throw TypeError(`Array expected`);
  if (n2.length === 0) throw Error(`Resizing to scalar is not supported`);
  return n2.forEach(function(t5) {
    if (!isNumber(t5) || !isInteger(t5) || t5 < 0) throw TypeError(`Invalid size, must contain positive integers (size: ` + format(n2) + `)`);
  }), (isNumber(t4) || isBigNumber(t4)) && (t4 = [t4]), _resize(t4, n2, 0, r2 === void 0 ? 0 : r2), t4;
}
function _resize(t4, n2, r2, i2) {
  var a2, o2, s2 = t4.length, c2 = n2[r2], l2 = Math.min(s2, c2);
  if (t4.length = c2, r2 < n2.length - 1) {
    var u2 = r2 + 1;
    for (a2 = 0; a2 < l2; a2++) o2 = t4[a2], Array.isArray(o2) || (o2 = [o2], t4[a2] = o2), _resize(o2, n2, u2, i2);
    for (a2 = l2; a2 < c2; a2++) o2 = [], t4[a2] = o2, _resize(o2, n2, u2, i2);
  } else {
    for (a2 = 0; a2 < l2; a2++) for (; Array.isArray(t4[a2]); ) t4[a2] = t4[a2][0];
    for (a2 = l2; a2 < c2; a2++) t4[a2] = i2;
  }
}
function reshape(t4, n2) {
  var r2 = flatten$4(t4, true), i2 = r2.length;
  if (!Array.isArray(t4) || !Array.isArray(n2)) throw TypeError(`Array expected`);
  if (n2.length === 0) throw new DimensionError(0, i2, `!=`);
  n2 = processSizesWildcard(n2, i2);
  var a2 = product$1(n2);
  if (i2 !== a2) throw new DimensionError(a2, i2, `!=`);
  try {
    return _reshape(r2, n2);
  } catch (t5) {
    throw t5 instanceof DimensionError ? new DimensionError(a2, i2, `!=`) : t5;
  }
}
function processSizesWildcard(t4, n2) {
  var r2 = product$1(t4), i2 = t4.slice(), a2 = -1, o2 = t4.indexOf(a2);
  if (t4.indexOf(a2, o2 + 1) >= 0) throw Error(`More than one wildcard in sizes`);
  var s2 = o2 >= 0, c2 = n2 % r2 === 0;
  if (s2) if (c2) i2[o2] = -n2 / r2;
  else throw Error(`Could not replace wildcard, since ` + n2 + ` is no multiple of ` + -r2);
  return i2;
}
function product$1(t4) {
  return t4.reduce((t5, n2) => t5 * n2, 1);
}
function _reshape(t4, n2) {
  for (var r2 = t4, i2, a2 = n2.length - 1; a2 > 0; a2--) {
    var o2 = n2[a2];
    i2 = [];
    for (var s2 = r2.length / o2, c2 = 0; c2 < s2; c2++) i2.push(r2.slice(c2 * o2, (c2 + 1) * o2));
    r2 = i2;
  }
  return r2;
}
function squeeze(t4, n2) {
  for (var r2 = arraySize(t4); Array.isArray(t4) && t4.length === 1; ) t4 = t4[0], r2.shift();
  for (var i2 = r2.length; r2[i2 - 1] === 1; ) i2--;
  return i2 < r2.length && (t4 = _squeeze(t4, i2, 0), r2.length = i2), t4;
}
function _squeeze(t4, n2, r2) {
  var i2, a2;
  if (r2 < n2) {
    var o2 = r2 + 1;
    for (i2 = 0, a2 = t4.length; i2 < a2; i2++) t4[i2] = _squeeze(t4[i2], n2, o2);
  } else for (; Array.isArray(t4); ) t4 = t4[0];
  return t4;
}
function unsqueeze(t4, n2, r2, i2) {
  var a2 = i2 || arraySize(t4);
  if (r2) for (var o2 = 0; o2 < r2; o2++) t4 = [t4], a2.unshift(1);
  for (t4 = _unsqueeze(t4, n2, 0); a2.length < n2; ) a2.push(1);
  return t4;
}
function _unsqueeze(t4, n2, r2) {
  var i2, a2;
  if (Array.isArray(t4)) {
    var o2 = r2 + 1;
    for (i2 = 0, a2 = t4.length; i2 < a2; i2++) t4[i2] = _unsqueeze(t4[i2], n2, o2);
  } else for (var s2 = r2; s2 < n2; s2++) t4 = [t4];
  return t4;
}
function flatten$4(t4) {
  var n2 = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : false;
  if (!Array.isArray(t4)) return t4;
  if (typeof n2 != `boolean`) throw TypeError(`Boolean expected for second argument of flatten`);
  var r2 = [];
  return n2 ? a2(t4) : i2(t4), r2;
  function i2(t5) {
    for (var n3 = 0; n3 < t5.length; n3++) {
      var a3 = t5[n3];
      Array.isArray(a3) ? i2(a3) : r2.push(a3);
    }
  }
  function a2(t5) {
    if (Array.isArray(t5[0])) for (var n3 = 0; n3 < t5.length; n3++) a2(t5[n3]);
    else for (var i3 = 0; i3 < t5.length; i3++) r2.push(t5[i3]);
  }
}
function map$1(t4, n2) {
  return Array.prototype.map.call(t4, n2);
}
function forEach(t4, n2) {
  Array.prototype.forEach.call(t4, n2);
}
function filter$1(t4, n2) {
  if (arraySize(t4).length !== 1) throw Error(`Only one dimensional matrices supported`);
  return Array.prototype.filter.call(t4, n2);
}
function filterRegExp(t4, n2) {
  if (arraySize(t4).length !== 1) throw Error(`Only one dimensional matrices supported`);
  return Array.prototype.filter.call(t4, (t5) => n2.test(t5));
}
function join(t4, n2) {
  return Array.prototype.join.call(t4, n2);
}
function identify(t4) {
  if (!Array.isArray(t4)) throw TypeError(`Array input expected`);
  if (t4.length === 0) return t4;
  var n2 = [], r2 = 0;
  n2[0] = { value: t4[0], identifier: 0 };
  for (var i2 = 1; i2 < t4.length; i2++) t4[i2] === t4[i2 - 1] ? r2++ : r2 = 0, n2.push({ value: t4[i2], identifier: r2 });
  return n2;
}
function generalize(t4) {
  if (!Array.isArray(t4)) throw TypeError(`Array input expected`);
  if (t4.length === 0) return t4;
  for (var n2 = [], r2 = 0; r2 < t4.length; r2++) n2.push(t4[r2].value);
  return n2;
}
function getArrayDataType(t4, n2) {
  for (var r2, i2 = 0, a2 = 0; a2 < t4.length; a2++) {
    var o2 = t4[a2], s2 = Array.isArray(o2);
    if (a2 === 0 && s2 && (i2 = o2.length), s2 && o2.length !== i2) return;
    var c2 = s2 ? getArrayDataType(o2, n2) : n2(o2);
    if (r2 === void 0) r2 = c2;
    else if (r2 !== c2) return `mixed`;
  }
  return r2;
}
function concatRecursive(t4, n2, r2, i2) {
  if (i2 < r2) {
    if (t4.length !== n2.length) throw new DimensionError(t4.length, n2.length);
    for (var a2 = [], o2 = 0; o2 < t4.length; o2++) a2[o2] = concatRecursive(t4[o2], n2[o2], r2, i2 + 1);
    return a2;
  } else return t4.concat(n2);
}
function concat() {
  var t4 = Array.prototype.slice.call(arguments, 0, -1), n2 = Array.prototype.slice.call(arguments, -1);
  if (t4.length === 1) return t4[0];
  if (t4.length > 1) return t4.slice(1).reduce(function(t5, r2) {
    return concatRecursive(t5, r2, n2, 0);
  }, t4[0]);
  throw Error(`Wrong number of arguments in function concat`);
}
function broadcastSizes() {
  for (var t4 = [...arguments], n2 = t4.map((t5) => t5.length), r2 = Math.max(...n2), i2 = Array(r2).fill(null), a2 = 0; a2 < t4.length; a2++) for (var o2 = t4[a2], s2 = n2[a2], c2 = 0; c2 < s2; c2++) {
    var l2 = r2 - s2 + c2;
    o2[c2] > i2[l2] && (i2[l2] = o2[c2]);
  }
  for (var u2 = 0; u2 < t4.length; u2++) checkBroadcastingRules(t4[u2], i2);
  return i2;
}
function checkBroadcastingRules(t4, n2) {
  for (var r2 = n2.length, i2 = t4.length, a2 = 0; a2 < i2; a2++) {
    var o2 = r2 - i2 + a2;
    if (t4[a2] < n2[o2] && t4[a2] > 1 || t4[a2] > n2[o2]) throw Error(`shape mismatch: mismatch is found in arg with shape (${t4}) not possible to broadcast dimension ${i2} with size ${t4[a2]} to size ${n2[o2]}`);
  }
}
function broadcastTo(t4, n2) {
  var r2 = arraySize(t4);
  if (deepStrictEqual(r2, n2)) return t4;
  checkBroadcastingRules(r2, n2);
  var i2 = broadcastSizes(r2, n2), a2 = i2.length, o2 = [...Array(a2 - r2.length).fill(1), ...r2], s2 = clone$1(t4);
  r2.length < a2 && (s2 = reshape(s2, o2), r2 = arraySize(s2));
  for (var c2 = 0; c2 < a2; c2++) r2[c2] < i2[c2] && (s2 = stretch(s2, i2[c2], c2), r2 = arraySize(s2));
  return s2;
}
function stretch(t4, n2, r2) {
  return concat(...Array(n2).fill(t4), r2);
}
function get$1(t4, n2) {
  if (!Array.isArray(t4)) throw Error(`Array expected`);
  if (!Array.isArray(n2)) throw Error(`Array expected for index`);
  var r2 = arraySize(t4);
  if (n2.length !== r2.length) throw new DimensionError(n2.length, r2.length);
  for (var i2 = 0; i2 < n2.length; i2++) validateIndex(n2[i2], r2[i2]);
  return n2.reduce((t5, n3) => t5[n3], t4);
}
function deepMap$1(t4, n2) {
  var r2 = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : false;
  if (t4.length === 0) return [];
  if (r2) return o2(t4);
  var i2 = [];
  return a2(t4, 0);
  function a2(r3, o3) {
    if (Array.isArray(r3)) {
      for (var s2 = r3.length, c2 = Array(s2), l2 = 0; l2 < s2; l2++) i2[o3] = l2, c2[l2] = a2(r3[l2], o3 + 1);
      return c2;
    } else return n2(r3, i2.slice(0, o3), t4);
  }
  function o2(t5) {
    if (Array.isArray(t5)) {
      for (var r3 = t5.length, i3 = Array(r3), a3 = 0; a3 < r3; a3++) i3[a3] = o2(t5[a3]);
      return i3;
    } else return n2(t5);
  }
}
function deepForEach$1(t4, n2) {
  var r2 = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : false;
  if (t4.length === 0) return;
  if (r2) {
    o2(t4);
    return;
  }
  var i2 = [];
  a2(t4, 0);
  function a2(r3, o3) {
    if (Array.isArray(r3)) for (var s2 = r3.length, c2 = 0; c2 < s2; c2++) i2[o3] = c2, a2(r3[c2], o3 + 1);
    else n2(r3, i2.slice(0, o3), t4);
  }
  function o2(t5) {
    if (Array.isArray(t5)) for (var r3 = t5.length, i3 = 0; i3 < r3; i3++) o2(t5[i3]);
    else n2(t5);
  }
}
function clone$1(t4) {
  return _extends([], t4);
}
function optimizeCallback(t4, n2, r2, i2) {
  if (import_typed_function.default.isTypedFunction(t4)) {
    var a2;
    if (i2) a2 = 1;
    else {
      var o2 = n2.isMatrix ? n2.size() : arraySize(n2);
      if (!o2.length || o2[o2.length - 1] === 0) return { isUnary: i2, fn: t4 };
      var s2 = o2.map(() => 0);
      a2 = _findNumberOfArgumentsTyped(t4, n2.isMatrix ? n2.get(s2) : get$1(n2, s2), s2, n2);
    }
    var c2;
    if (n2.isMatrix && n2.dataType !== `mixed` && n2.dataType !== void 0) {
      var l2 = _findSingleSignatureWithArity(t4, a2);
      c2 = l2 === void 0 ? t4 : l2;
    } else c2 = t4;
    return a2 >= 1 && a2 <= 3 ? { isUnary: a2 === 1, fn: function() {
      return _tryFunctionWithArgs(c2, [...arguments].slice(0, a2), r2, t4.name);
    } } : { isUnary: false, fn: function() {
      return _tryFunctionWithArgs(c2, [...arguments], r2, t4.name);
    } };
  }
  return i2 === void 0 ? { isUnary: _findIfCallbackIsUnary(t4), fn: t4 } : { isUnary: i2, fn: t4 };
}
function _findSingleSignatureWithArity(t4, n2) {
  var r2 = [];
  if (Object.entries(t4.signatures).forEach((t5) => {
    var [i2, a2] = t5;
    i2.split(`,`).length === n2 && r2.push(a2);
  }), r2.length === 1) return r2[0];
}
function _findIfCallbackIsUnary(t4) {
  if (t4.length !== 1) return false;
  var n2 = t4.toString();
  if (/arguments/.test(n2)) return false;
  var r2 = n2.match(/\(.*?\)/);
  return !/\.\.\./.test(r2);
}
function _findNumberOfArgumentsTyped(t4, n2, r2, i2) {
  for (var a2 = [n2, r2, i2], o2 = 3; o2 > 0; o2--) {
    var s2 = a2.slice(0, o2);
    if (import_typed_function.default.resolve(t4, s2) !== null) return o2;
  }
}
function _tryFunctionWithArgs(t4, n2, r2, i2) {
  try {
    return t4(...n2);
  } catch (t5) {
    _createCallbackError(t5, n2, r2, i2);
  }
}
function _createCallbackError(t4, n2, r2, i2) {
  if (t4 instanceof TypeError && t4.data?.category === `wrongType`) {
    var o2 = [];
    throw o2.push(`value: ${typeOf(n2[0])}`), n2.length >= 2 && o2.push(`index: ${typeOf(n2[1])}`), n2.length >= 3 && o2.push(`array: ${typeOf(n2[2])}`), TypeError(`Function ${r2} cannot apply callback arguments ${i2}(${o2.join(`, `)}) at index ${JSON.stringify(n2[1])}`);
  } else throw TypeError(`Function ${r2} cannot apply callback arguments to function ${i2}: ${t4.message}`);
}
var name$315 = `DenseMatrix`, dependencies$315 = [`Matrix`, `config`], createDenseMatrixClass = factory(name$315, dependencies$315, (t4) => {
  var { Matrix: n2, config: r2 } = t4;
  function i2(t5, n3) {
    if (!(this instanceof i2)) throw SyntaxError(`Constructor must be called with the new operator`);
    if (n3 && !isString(n3)) throw Error(`Invalid datatype: ` + n3);
    if (isMatrix(t5)) t5.type === `DenseMatrix` ? (this._data = clone$3(t5._data), this._size = clone$3(t5._size), this._datatype = n3 || t5._datatype) : (this._data = t5.toArray(), this._size = t5.size(), this._datatype = n3 || t5._datatype);
    else if (t5 && isArray(t5.data) && isArray(t5.size)) this._data = t5.data, this._size = t5.size, validate(this._data, this._size), this._datatype = n3 || t5.datatype;
    else if (isArray(t5)) this._data = d2(t5), this._size = arraySize(this._data), validate(this._data, this._size), this._datatype = n3;
    else if (t5) throw TypeError(`Unsupported type of data (` + typeOf(t5) + `)`);
    else this._data = [], this._size = [0], this._datatype = n3;
  }
  i2.prototype = new n2(), i2.prototype.createDenseMatrix = function(t5, n3) {
    return new i2(t5, n3);
  }, Object.defineProperty(i2, "name", { value: `DenseMatrix` }), i2.prototype.constructor = i2, i2.prototype.type = `DenseMatrix`, i2.prototype.isDenseMatrix = true, i2.prototype.getDataType = function() {
    return getArrayDataType(this._data, typeOf);
  }, i2.prototype.storage = function() {
    return `dense`;
  }, i2.prototype.datatype = function() {
    return this._datatype;
  }, i2.prototype.create = function(t5, n3) {
    return new i2(t5, n3);
  }, i2.prototype.subset = function(t5, n3, r3) {
    switch (arguments.length) {
      case 1:
        return a2(this, t5);
      case 2:
      case 3:
        return s2(this, t5, n3, r3);
      default:
        throw SyntaxError(`Wrong number of arguments`);
    }
  }, i2.prototype.get = function(t5) {
    return get$1(this._data, t5);
  }, i2.prototype.set = function(t5, n3, r3) {
    if (!isArray(t5)) throw TypeError(`Array expected`);
    if (t5.length < this._size.length) throw new DimensionError(t5.length, this._size.length, `<`);
    var i3, a3, o3, s3 = t5.map(function(t6) {
      return t6 + 1;
    });
    u2(this, s3, r3);
    var c3 = this._data;
    for (i3 = 0, a3 = t5.length - 1; i3 < a3; i3++) o3 = t5[i3], validateIndex(o3, c3.length), c3 = c3[o3];
    return o3 = t5[t5.length - 1], validateIndex(o3, c3.length), c3[o3] = n3, this;
  };
  function a2(t5, n3) {
    if (!isIndex(n3)) throw TypeError(`Invalid index`);
    if (r2.legacySubset ? n3.size().every((t6) => t6 === 1) : n3.isScalar()) return t5.get(n3.min());
    var a3 = n3.size();
    if (a3.length !== t5._size.length) throw new DimensionError(a3.length, t5._size.length);
    for (var s3 = n3.min(), c3 = n3.max(), l3 = 0, u3 = t5._size.length; l3 < u3; l3++) validateIndex(s3[l3], t5._size[l3]), validateIndex(c3[l3], t5._size[l3]);
    var d3 = new i2(), p2 = o2(t5._data, n3);
    return d3._size = p2.size, d3._datatype = t5._datatype, d3._data = p2.data, r2.legacySubset ? d3.reshape(n3.size()) : d3;
  }
  function o2(t5, n3) {
    var r3 = n3.size().length - 1, i3 = Array(r3);
    return { data: a3(t5), size: i3.filter((t6) => t6 !== null) };
    function a3(t6) {
      var o3 = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : 0, s3 = n3.dimension(o3);
      function c3(t7, n4) {
        return isNumber(t7) ? n4(t7) : t7.map(n4).valueOf();
      }
      return isNumber(s3) ? i3[o3] = null : i3[o3] = s3.size()[0], o3 < r3 ? c3(s3, (n4) => (validateIndex(n4, t6.length), a3(t6[n4], o3 + 1))) : c3(s3, (n4) => (validateIndex(n4, t6.length), t6[n4]));
    }
  }
  function s2(t5, n3, r3, i3) {
    if (!n3 || n3.isIndex !== true) throw TypeError(`Invalid index`);
    var a3 = n3.size(), o3 = n3.isScalar(), s3;
    if (isMatrix(r3) ? (s3 = r3.size(), r3 = r3.valueOf()) : s3 = arraySize(r3), o3) {
      if (s3.length !== 0) throw TypeError(`Scalar expected`);
      t5.set(n3.min(), r3, i3);
    } else {
      if (!deepStrictEqual(s3, a3)) {
        if (s3.length === 0) r3 = broadcastTo([r3], a3);
        else try {
          r3 = broadcastTo(r3, a3);
        } catch {
        }
        s3 = arraySize(r3);
      }
      if (a3.length < t5._size.length) throw new DimensionError(a3.length, t5._size.length, `<`);
      if (s3.length < a3.length) {
        for (var l3 = 0, d3 = 0; a3[l3] === 1 && s3[l3] === 1; ) l3++;
        for (; a3[l3] === 1; ) d3++, l3++;
        r3 = unsqueeze(r3, a3.length, d3, s3);
      }
      if (!deepStrictEqual(a3, s3)) throw new DimensionError(a3, s3, `>`);
      u2(t5, n3.max().map(function(t6) {
        return t6 + 1;
      }), i3), c2(t5._data, n3, r3);
    }
    return t5;
  }
  function c2(t5, n3, r3) {
    var i3 = n3.size().length - 1;
    a3(t5, r3);
    function a3(t6, r4) {
      var o3 = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : 0, s3 = n3.dimension(o3), c3 = (n4, i4) => {
        validateIndex(n4, t6.length), a3(t6[n4], r4[i4[0]], o3 + 1);
      }, l3 = (n4, i4) => {
        validateIndex(n4, t6.length), t6[n4] = r4[i4[0]];
      };
      o3 < i3 ? isNumber(s3) ? c3(s3, [0]) : s3.forEach(c3) : isNumber(s3) ? l3(s3, [0]) : s3.forEach(l3);
    }
  }
  i2.prototype.resize = function(t5, n3, r3) {
    if (!isCollection(t5)) throw TypeError(`Array or Matrix expected`);
    var i3 = t5.valueOf().map((t6) => Array.isArray(t6) && t6.length === 1 ? t6[0] : t6);
    return l2(r3 ? this.clone() : this, i3, n3);
  };
  function l2(t5, n3, r3) {
    if (n3.length === 0) {
      for (var i3 = t5._data; isArray(i3); ) i3 = i3[0];
      return i3;
    }
    return t5._size = n3.slice(0), t5._data = resize(t5._data, t5._size, r3), t5;
  }
  i2.prototype.reshape = function(t5, n3) {
    var r3 = n3 ? this.clone() : this;
    return r3._data = reshape(r3._data, t5), r3._size = processSizesWildcard(t5, r3._size.reduce((t6, n4) => t6 * n4)), r3;
  };
  function u2(t5, n3, r3) {
    for (var i3 = t5._size.slice(0), a3 = false; i3.length < n3.length; ) i3.push(0), a3 = true;
    for (var o3 = 0, s3 = n3.length; o3 < s3; o3++) n3[o3] > i3[o3] && (i3[o3] = n3[o3], a3 = true);
    a3 && l2(t5, i3, r3);
  }
  i2.prototype.clone = function() {
    return new i2({ data: clone$3(this._data), size: clone$3(this._size), datatype: this._datatype });
  }, i2.prototype.size = function() {
    return this._size.slice(0);
  }, i2.prototype.map = function(t5) {
    var n3 = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : false, r3 = this, i3 = r3._size.length - 1;
    if (i3 < 0) return r3.clone();
    var a3 = optimizeCallback(t5, r3, `map`, n3), o3 = a3.fn, s3 = r3.create(void 0, r3._datatype);
    if (s3._size = r3._size, n3 || a3.isUnary) return s3._data = m2(r3._data), s3;
    if (i3 === 0) {
      for (var c3 = r3.valueOf(), l3 = Array(c3.length), u3 = 0; u3 < c3.length; u3++) l3[u3] = o3(c3[u3], [u3], r3);
      return s3._data = l3, s3;
    }
    var d3 = [];
    return s3._data = p2(r3._data), s3;
    function p2(t6) {
      var n4 = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : 0, a4 = Array(t6.length);
      if (n4 < i3) for (var s4 = 0; s4 < t6.length; s4++) d3[n4] = s4, a4[s4] = p2(t6[s4], n4 + 1);
      else for (var c4 = 0; c4 < t6.length; c4++) d3[n4] = c4, a4[c4] = o3(t6[c4], d3.slice(), r3);
      return a4;
    }
    function m2(t6) {
      var n4 = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : 0, r4 = Array(t6.length);
      if (n4 < i3) for (var a4 = 0; a4 < t6.length; a4++) r4[a4] = m2(t6[a4], n4 + 1);
      else for (var s4 = 0; s4 < t6.length; s4++) r4[s4] = o3(t6[s4]);
      return r4;
    }
  }, i2.prototype.forEach = function(t5) {
    var n3 = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : false, r3 = this, i3 = r3._size.length - 1;
    if (i3 < 0) return;
    var a3 = optimizeCallback(t5, r3, `map`, n3), o3 = a3.fn;
    if (n3 || a3.isUnary) {
      u3(r3._data);
      return;
    }
    if (i3 === 0) {
      for (var s3 = 0; s3 < r3._data.length; s3++) o3(r3._data[s3], [s3], r3);
      return;
    }
    var c3 = [];
    l3(r3._data);
    function l3(t6) {
      var n4 = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : 0;
      if (n4 < i3) for (var a4 = 0; a4 < t6.length; a4++) c3[n4] = a4, l3(t6[a4], n4 + 1);
      else for (var s4 = 0; s4 < t6.length; s4++) c3[n4] = s4, o3(t6[s4], c3.slice(), r3);
    }
    function u3(t6) {
      var n4 = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : 0;
      if (n4 < i3) for (var r4 = 0; r4 < t6.length; r4++) u3(t6[r4], n4 + 1);
      else for (var a4 = 0; a4 < t6.length; a4++) o3(t6[a4]);
    }
  }, i2.prototype[Symbol.iterator] = function* () {
    var t5 = this._size.length - 1;
    if (!(t5 < 0)) {
      if (t5 === 0) {
        for (var n3 = 0; n3 < this._data.length; n3++) yield { value: this._data[n3], index: [n3] };
        return;
      }
      for (var r3 = Array(t5 + 1).fill(0), i3 = this._size.reduce((t6, n4) => t6 * n4, 1), a3 = 0; a3 < i3; a3++) {
        for (var o3 = this._data, s3 = 0; s3 < t5; s3++) o3 = o3[r3[s3]];
        yield { value: o3[r3[t5]], index: r3.slice() };
        for (var c3 = t5; c3 >= 0 && (r3[c3]++, !(r3[c3] < this._size[c3])); c3--) r3[c3] = 0;
      }
    }
  }, i2.prototype.rows = function() {
    var t5 = [];
    if (this.size().length !== 2) throw TypeError(`Rows can only be returned for a 2D matrix.`);
    for (var n3 of this._data) t5.push(new i2([n3], this._datatype));
    return t5;
  }, i2.prototype.columns = function() {
    var t5 = this, n3 = [], r3 = this.size();
    if (r3.length !== 2) throw TypeError(`Rows can only be returned for a 2D matrix.`);
    for (var a3 = this._data, o3 = function(r4) {
      var o4 = a3.map((t6) => [t6[r4]]);
      n3.push(new i2(o4, t5._datatype));
    }, s3 = 0; s3 < r3[1]; s3++) o3(s3);
    return n3;
  }, i2.prototype.toArray = function() {
    return clone$3(this._data);
  }, i2.prototype.valueOf = function() {
    return this._data;
  }, i2.prototype.format = function(t5) {
    return format(this._data, t5);
  }, i2.prototype.toString = function() {
    return format(this._data);
  }, i2.prototype.toJSON = function() {
    return { mathjs: `DenseMatrix`, data: this._data, size: this._size, datatype: this._datatype };
  }, i2.prototype.diagonal = function(t5) {
    if (t5) {
      if (isBigNumber(t5) && (t5 = t5.toNumber()), !isNumber(t5) || !isInteger(t5)) throw TypeError(`The parameter k must be an integer number`);
    } else t5 = 0;
    for (var n3 = t5 > 0 ? t5 : 0, r3 = t5 < 0 ? -t5 : 0, a3 = this._size[0], o3 = this._size[1], s3 = Math.min(a3 - r3, o3 - n3), c3 = [], l3 = 0; l3 < s3; l3++) c3[l3] = this._data[l3 + r3][l3 + n3];
    return new i2({ data: c3, size: [s3], datatype: this._datatype });
  }, i2.diagonal = function(t5, n3, r3, a3) {
    if (!isArray(t5)) throw TypeError(`Array expected, size parameter`);
    if (t5.length !== 2) throw Error(`Only two dimensions matrix are supported`);
    if (t5 = t5.map(function(t6) {
      if (isBigNumber(t6) && (t6 = t6.toNumber()), !isNumber(t6) || !isInteger(t6) || t6 < 1) throw Error(`Size values must be positive integers`);
      return t6;
    }), r3) {
      if (isBigNumber(r3) && (r3 = r3.toNumber()), !isNumber(r3) || !isInteger(r3)) throw TypeError(`The parameter k must be an integer number`);
    } else r3 = 0;
    var o3 = r3 > 0 ? r3 : 0, s3 = r3 < 0 ? -r3 : 0, c3 = t5[0], l3 = t5[1], u3 = Math.min(c3 - s3, l3 - o3), d3;
    if (isArray(n3)) {
      if (n3.length !== u3) throw Error(`Invalid value array length`);
      d3 = function(t6) {
        return n3[t6];
      };
    } else if (isMatrix(n3)) {
      var p2 = n3.size();
      if (p2.length !== 1 || p2[0] !== u3) throw Error(`Invalid matrix length`);
      d3 = function(t6) {
        return n3.get([t6]);
      };
    } else d3 = function() {
      return n3;
    };
    a3 ||= isBigNumber(d3(0)) ? d3(0).mul(0) : 0;
    var m2 = [];
    if (t5.length > 0) {
      m2 = resize(m2, t5, a3);
      for (var h2 = 0; h2 < u3; h2++) m2[h2 + s3][h2 + o3] = d3(h2);
    }
    return new i2({ data: m2, size: [c3, l3] });
  }, i2.fromJSON = function(t5) {
    return new i2(t5);
  }, i2.prototype.swapRows = function(t5, n3) {
    if (!isNumber(t5) || !isInteger(t5) || !isNumber(n3) || !isInteger(n3)) throw Error(`Row index must be positive integers`);
    if (this._size.length !== 2) throw Error(`Only two dimensional matrix is supported`);
    return validateIndex(t5, this._size[0]), validateIndex(n3, this._size[0]), i2._swapRows(t5, n3, this._data), this;
  }, i2._swapRows = function(t5, n3, r3) {
    var i3 = r3[t5];
    r3[t5] = r3[n3], r3[n3] = i3;
  };
  function d2(t5) {
    return isMatrix(t5) ? d2(t5.valueOf()) : isArray(t5) ? t5.map(d2) : t5;
  }
  return i2;
}, { isClass: true }), name$314 = `clone`, dependencies$314 = [`typed`], createClone = factory(name$314, dependencies$314, (t4) => {
  var { typed: n2 } = t4;
  return n2(name$314, { any: clone$3 });
});
function _switch$1(t4) {
  var n2 = t4.length, r2 = t4[0].length, i2, a2, o2 = [];
  for (a2 = 0; a2 < r2; a2++) {
    var s2 = [];
    for (i2 = 0; i2 < n2; i2++) s2.push(t4[i2][a2]);
    o2.push(s2);
  }
  return o2;
}
function containsCollections(t4) {
  for (var n2 = 0; n2 < t4.length; n2++) if (isCollection(t4[n2])) return true;
  return false;
}
function deepForEach(t4, n2) {
  isMatrix(t4) ? t4.forEach((t5) => n2(t5), false, true) : deepForEach$1(t4, n2, true);
}
function deepMap(t4, n2, r2) {
  if (!r2) return isMatrix(t4) ? t4.map((t5) => n2(t5), false, true) : deepMap$1(t4, n2, true);
  var i2 = (t5) => t5 === 0 ? t5 : n2(t5);
  return isMatrix(t4) ? t4.map((t5) => i2(t5), false, true) : deepMap$1(t4, i2, true);
}
function reduce$1(t4, n2, r2) {
  var i2 = Array.isArray(t4) ? arraySize(t4) : t4.size();
  if (n2 < 0 || n2 >= i2.length) throw new IndexError(n2, i2.length);
  return isMatrix(t4) ? t4.create(_reduce(t4.valueOf(), n2, r2), t4.datatype()) : _reduce(t4, n2, r2);
}
function _reduce(t4, n2, r2) {
  var i2, a2, o2, s2;
  if (n2 <= 0) if (Array.isArray(t4[0])) {
    for (s2 = _switch$1(t4), a2 = [], i2 = 0; i2 < s2.length; i2++) a2[i2] = _reduce(s2[i2], n2 - 1, r2);
    return a2;
  } else {
    for (o2 = t4[0], i2 = 1; i2 < t4.length; i2++) o2 = r2(o2, t4[i2]);
    return o2;
  }
  else {
    for (a2 = [], i2 = 0; i2 < t4.length; i2++) a2[i2] = _reduce(t4[i2], n2 - 1, r2);
    return a2;
  }
}
function scatter(t4, n2, r2, i2, a2, o2, s2, c2, l2, u2, d2) {
  var p2 = t4._values, m2 = t4._index, h2 = t4._ptr, g2, v2, y2, b2;
  if (i2) for (v2 = h2[n2], y2 = h2[n2 + 1], g2 = v2; g2 < y2; g2++) b2 = m2[g2], r2[b2] === o2 ? (i2[b2] = c2(i2[b2], p2[g2]), a2[b2] = o2) : (r2[b2] = o2, s2.push(b2), i2[b2] = p2[g2]);
  else for (v2 = h2[n2], y2 = h2[n2 + 1], g2 = v2; g2 < y2; g2++) b2 = m2[g2], r2[b2] === o2 ? a2[b2] = o2 : (r2[b2] = o2, s2.push(b2));
}
var name$313 = `isInteger`, dependencies$313 = [`typed`, `equal`], createIsInteger = factory(name$313, dependencies$313, (t4) => {
  var { typed: n2, equal: r2 } = t4;
  return n2(name$313, { number: (t5) => Number.isFinite(t5) ? r2(t5, Math.round(t5)) : false, BigNumber: (t5) => t5.isFinite() ? r2(t5.round(), t5) : false, bigint: (t5) => true, Fraction: (t5) => t5.d === 1n, "Array | Matrix": n2.referToSelf((t5) => (n3) => deepMap(n3, t5)) });
}), n1$4 = `number`, n2$3 = `number, number`;
function absNumber(t4) {
  return Math.abs(t4);
}
absNumber.signature = n1$4;
function addNumber(t4, n2) {
  return t4 + n2;
}
addNumber.signature = n2$3;
function subtractNumber(t4, n2) {
  return t4 - n2;
}
subtractNumber.signature = n2$3;
function multiplyNumber(t4, n2) {
  return t4 * n2;
}
multiplyNumber.signature = n2$3;
function unaryMinusNumber(t4) {
  return -t4;
}
unaryMinusNumber.signature = n1$4;
function unaryPlusNumber(t4) {
  return t4;
}
unaryPlusNumber.signature = n1$4;
function cbrtNumber(t4) {
  return cbrt$1(t4);
}
cbrtNumber.signature = n1$4;
function cubeNumber(t4) {
  return t4 * t4 * t4;
}
cubeNumber.signature = n1$4;
function expNumber(t4) {
  return Math.exp(t4);
}
expNumber.signature = n1$4;
function expm1Number(t4) {
  return expm1(t4);
}
expm1Number.signature = n1$4;
function lcmNumber(t4, n2) {
  if (!isInteger(t4) || !isInteger(n2)) throw Error(`Parameters in function lcm must be integer numbers`);
  if (t4 === 0 || n2 === 0) return 0;
  for (var r2, i2 = t4 * n2; n2 !== 0; ) r2 = n2, n2 = t4 % r2, t4 = r2;
  return Math.abs(i2 / t4);
}
lcmNumber.signature = n2$3;
function logNumber(t4, n2) {
  return n2 ? Math.log(t4) / Math.log(n2) : Math.log(t4);
}
function log10Number(t4) {
  return log10$2(t4);
}
log10Number.signature = n1$4;
function log2Number(t4) {
  return log2$1(t4);
}
log2Number.signature = n1$4;
function nthRootNumber(t4) {
  var n2 = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : 2, r2 = n2 < 0;
  if (r2 && (n2 = -n2), n2 === 0) throw Error(`Root must be non-zero`);
  if (t4 < 0 && Math.abs(n2) % 2 != 1) throw Error(`Root must be odd when a is negative.`);
  if (t4 === 0) return r2 ? 1 / 0 : 0;
  if (!isFinite(t4)) return r2 ? 0 : t4;
  var i2 = Math.abs(t4) ** (1 / n2);
  return i2 = t4 < 0 ? -i2 : i2, r2 ? 1 / i2 : i2;
}
function signNumber(t4) {
  return sign$2(t4);
}
signNumber.signature = n1$4;
function squareNumber(t4) {
  return t4 * t4;
}
squareNumber.signature = n1$4;
function xgcdNumber(t4, n2) {
  var r2, i2, a2, o2 = 0, s2 = 1, c2 = 1, l2 = 0;
  if (!isInteger(t4) || !isInteger(n2)) throw Error(`Parameters in function xgcd must be integer numbers`);
  for (; n2; ) i2 = Math.floor(t4 / n2), a2 = t4 - i2 * n2, r2 = o2, o2 = s2 - i2 * o2, s2 = r2, r2 = c2, c2 = l2 - i2 * c2, l2 = r2, t4 = n2, n2 = a2;
  return t4 < 0 ? [-t4, -s2, -l2] : [t4, t4 ? s2 : 0, l2];
}
xgcdNumber.signature = n2$3;
function powNumber(t4, n2) {
  return t4 * t4 < 1 && n2 === 1 / 0 || t4 * t4 > 1 && n2 === -1 / 0 ? 0 : t4 ** +n2;
}
powNumber.signature = n2$3;
function roundNumber(t4) {
  var n2 = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : 0;
  if (!isInteger(n2) || n2 < 0 || n2 > 15) throw Error(`Number of decimals in function round must be an integer from 0 to 15 inclusive`);
  return parseFloat(toFixed$2(t4, n2));
}
var n1$3 = `number`, n2$2 = `number, number`;
function bitAndNumber(t4, n2) {
  if (!isInteger(t4) || !isInteger(n2)) throw Error(`Integers expected in function bitAnd`);
  return t4 & n2;
}
bitAndNumber.signature = n2$2;
function bitNotNumber(t4) {
  if (!isInteger(t4)) throw Error(`Integer expected in function bitNot`);
  return ~t4;
}
bitNotNumber.signature = n1$3;
function bitOrNumber(t4, n2) {
  if (!isInteger(t4) || !isInteger(n2)) throw Error(`Integers expected in function bitOr`);
  return t4 | n2;
}
bitOrNumber.signature = n2$2;
function bitXorNumber(t4, n2) {
  if (!isInteger(t4) || !isInteger(n2)) throw Error(`Integers expected in function bitXor`);
  return t4 ^ n2;
}
bitXorNumber.signature = n2$2;
function leftShiftNumber(t4, n2) {
  if (!isInteger(t4) || !isInteger(n2)) throw Error(`Integers expected in function leftShift`);
  return t4 << n2;
}
leftShiftNumber.signature = n2$2;
function rightArithShiftNumber(t4, n2) {
  if (!isInteger(t4) || !isInteger(n2)) throw Error(`Integers expected in function rightArithShift`);
  return t4 >> n2;
}
rightArithShiftNumber.signature = n2$2;
function rightLogShiftNumber(t4, n2) {
  if (!isInteger(t4) || !isInteger(n2)) throw Error(`Integers expected in function rightLogShift`);
  return t4 >>> n2;
}
rightLogShiftNumber.signature = n2$2;
function product(t4, n2) {
  if (n2 < t4) return 1;
  if (n2 === t4) return n2;
  var r2 = n2 + t4 >> 1;
  return product(t4, r2) * product(r2 + 1, n2);
}
function combinationsNumber(t4, n2) {
  if (!isInteger(t4) || t4 < 0 || !isInteger(n2) || n2 < 0) throw TypeError(`Positive integer value expected in function combinations`);
  if (n2 > t4) throw TypeError(`k must be less than or equal to n`);
  for (var r2 = t4 - n2, i2 = 1, a2 = n2 < r2 ? r2 + 1 : n2 + 1, o2 = 2, s2 = n2 < r2 ? n2 : r2, c2 = a2; c2 <= t4; ++c2) for (i2 *= c2; o2 <= s2 && i2 % o2 === 0; ) i2 /= o2, ++o2;
  return o2 <= s2 && (i2 /= product(o2, s2)), i2;
}
combinationsNumber.signature = `number, number`;
var pi = Math.PI, tau = 2 * Math.PI, e = Math.E, phi = 1.618033988749895, n1$2 = `number`, n2$1 = `number, number`;
function notNumber(t4) {
  return !t4;
}
notNumber.signature = n1$2;
function orNumber(t4, n2) {
  return !!(t4 || n2);
}
orNumber.signature = n2$1;
function xorNumber(t4, n2) {
  return !!t4 != !!n2;
}
xorNumber.signature = n2$1;
function andNumber(t4, n2) {
  return !!(t4 && n2);
}
andNumber.signature = n2$1;
function gammaNumber(t4) {
  var n2;
  if (isInteger(t4)) return t4 <= 0 ? Number.isFinite(t4) ? 1 / 0 : NaN : t4 > 171 ? 1 / 0 : product(1, t4 - 1);
  if (t4 < 0.5) return Math.PI / (Math.sin(Math.PI * t4) * gammaNumber(1 - t4));
  if (t4 >= 171.35) return 1 / 0;
  if (t4 > 85) {
    var r2 = t4 * t4, i2 = r2 * t4, a2 = i2 * t4, o2 = a2 * t4;
    return Math.sqrt(2 * Math.PI / t4) * (t4 / Math.E) ** +t4 * (1 + 1 / (12 * t4) + 1 / (288 * r2) - 139 / (51840 * i2) - 571 / (2488320 * a2) + 163879 / (209018880 * o2) + 5246819 / (75246796800 * o2 * t4));
  }
  --t4, n2 = gammaP[0];
  for (var s2 = 1; s2 < gammaP.length; ++s2) n2 += gammaP[s2] / (t4 + s2);
  var c2 = t4 + gammaG + 0.5;
  return Math.sqrt(2 * Math.PI) * c2 ** +(t4 + 0.5) * Math.exp(-c2) * n2;
}
gammaNumber.signature = `number`;
var gammaG = 4.7421875, gammaP = [0.9999999999999971, 57.15623566586292, -59.59796035547549, 14.136097974741746, -0.4919138160976202, 3399464998481189e-20, 4652362892704858e-20, -9837447530487956e-20, 1580887032249125e-19, -21026444172410488e-20, 21743961811521265e-20, -1643181065367639e-19, 8441822398385275e-20, -26190838401581408e-21, 36899182659531625e-22], lnSqrt2PI = 0.9189385332046728, lgammaSeries = [1.000000000190015, 76.18009172947146, -86.50532032941678, 24.01409824083091, -1.231739572450155, 0.001208650973866179, -5395239384953e-18];
function lgammaNumber(t4) {
  if (t4 < 0) return NaN;
  if (t4 === 0) return 1 / 0;
  if (!Number.isFinite(t4)) return t4;
  if (t4 < 0.5) return Math.log(Math.PI / Math.sin(Math.PI * t4)) - lgammaNumber(1 - t4);
  --t4;
  for (var n2 = t4 + 5 + 0.5, r2 = lgammaSeries[0], i2 = 6; i2 >= 1; i2--) r2 += lgammaSeries[i2] / (t4 + i2);
  return lnSqrt2PI + (t4 + 0.5) * Math.log(n2) - n2 + Math.log(r2);
}
lgammaNumber.signature = `number`;
var n1$1 = `number`;
function acoshNumber(t4) {
  return acosh$2(t4);
}
acoshNumber.signature = n1$1;
function acotNumber(t4) {
  return Math.atan(1 / t4);
}
acotNumber.signature = n1$1;
function acothNumber(t4) {
  return Number.isFinite(t4) ? (Math.log((t4 + 1) / t4) + Math.log(t4 / (t4 - 1))) / 2 : 0;
}
acothNumber.signature = n1$1;
function acscNumber(t4) {
  return Math.asin(1 / t4);
}
acscNumber.signature = n1$1;
function acschNumber(t4) {
  var n2 = 1 / t4;
  return Math.log(n2 + Math.sqrt(n2 * n2 + 1));
}
acschNumber.signature = n1$1;
function asecNumber(t4) {
  return Math.acos(1 / t4);
}
asecNumber.signature = n1$1;
function asechNumber(t4) {
  var n2 = 1 / t4, r2 = Math.sqrt(n2 * n2 - 1);
  return Math.log(r2 + n2);
}
asechNumber.signature = n1$1;
function asinhNumber(t4) {
  return asinh$2(t4);
}
asinhNumber.signature = n1$1;
function atanhNumber(t4) {
  return atanh$2(t4);
}
atanhNumber.signature = n1$1;
function cotNumber(t4) {
  return 1 / Math.tan(t4);
}
cotNumber.signature = n1$1;
function cothNumber(t4) {
  var n2 = Math.exp(2 * t4);
  return (n2 + 1) / (n2 - 1);
}
cothNumber.signature = n1$1;
function cscNumber(t4) {
  return 1 / Math.sin(t4);
}
cscNumber.signature = n1$1;
function cschNumber(t4) {
  return t4 === 0 ? 1 / 0 : Math.abs(2 / (Math.exp(t4) - Math.exp(-t4))) * sign$2(t4);
}
cschNumber.signature = n1$1;
function secNumber(t4) {
  return 1 / Math.cos(t4);
}
secNumber.signature = n1$1;
function sechNumber(t4) {
  return 2 / (Math.exp(t4) + Math.exp(-t4));
}
sechNumber.signature = n1$1;
function sinhNumber(t4) {
  return sinh$3(t4);
}
sinhNumber.signature = n1$1;
var n1 = `number`;
function isNegativeNumber(t4) {
  return t4 < 0;
}
isNegativeNumber.signature = n1;
function isPositiveNumber(t4) {
  return t4 > 0;
}
isPositiveNumber.signature = n1;
function isNaNNumber(t4) {
  return Number.isNaN(t4);
}
isNaNNumber.signature = n1;
function nearlyEqual(t4, n2) {
  var r2 = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : 1e-9, i2 = arguments.length > 3 && arguments[3] !== void 0 ? arguments[3] : 0;
  if (r2 <= 0) throw Error(`Relative tolerance must be greater than 0`);
  if (i2 < 0) throw Error(`Absolute tolerance must be at least 0`);
  return t4.isNaN() || n2.isNaN() ? false : !t4.isFinite() || !n2.isFinite() ? t4.eq(n2) : t4.eq(n2) ? true : t4.minus(n2).abs().lte(t4.constructor.max(t4.constructor.max(t4.abs(), n2.abs()).mul(r2), i2));
}
var name$312 = `isNegative`, dependencies$312 = [`typed`, `config`], createIsNegative = factory(name$312, dependencies$312, (t4) => {
  var { typed: n2, config: r2 } = t4;
  return n2(name$312, { number: (t5) => nearlyEqual$1(t5, 0, r2.relTol, r2.absTol) ? false : isNegativeNumber(t5), BigNumber: (t5) => nearlyEqual(t5, new t5.constructor(0), r2.relTol, r2.absTol) ? false : t5.isNeg() && !t5.isZero() && !t5.isNaN(), bigint: (t5) => t5 < 0n, Fraction: (t5) => t5.s < 0n, Unit: n2.referToSelf((t5) => (r3) => n2.find(t5, r3.valueType())(r3.value)), "Array | Matrix": n2.referToSelf((t5) => (n3) => deepMap(n3, t5)) });
}), name$311 = `isNumeric`, dependencies$311 = [`typed`], createIsNumeric = factory(name$311, dependencies$311, (t4) => {
  var { typed: n2 } = t4;
  return n2(name$311, { "number | BigNumber | bigint | Fraction | boolean": () => true, "Complex | Unit | string | null | undefined | Node": () => false, "Array | Matrix": n2.referToSelf((t5) => (n3) => deepMap(n3, t5)) });
}), name$310 = `hasNumericValue`, dependencies$310 = [`typed`, `isNumeric`], createHasNumericValue = factory(name$310, dependencies$310, (t4) => {
  var { typed: n2, isNumeric: r2 } = t4;
  return n2(name$310, { boolean: () => true, string: function(t5) {
    return t5.trim().length > 0 && !isNaN(Number(t5));
  }, any: function(t5) {
    return r2(t5);
  } });
}), name$309 = `isPositive`, dependencies$309 = [`typed`, `config`], createIsPositive = factory(name$309, dependencies$309, (t4) => {
  var { typed: n2, config: r2 } = t4;
  return n2(name$309, { number: (t5) => nearlyEqual$1(t5, 0, r2.relTol, r2.absTol) ? false : isPositiveNumber(t5), BigNumber: (t5) => nearlyEqual(t5, new t5.constructor(0), r2.relTol, r2.absTol) ? false : !t5.isNeg() && !t5.isZero() && !t5.isNaN(), bigint: (t5) => t5 > 0n, Fraction: (t5) => t5.s > 0n && t5.n > 0n, Unit: n2.referToSelf((t5) => (r3) => n2.find(t5, r3.valueType())(r3.value)), "Array | Matrix": n2.referToSelf((t5) => (n3) => deepMap(n3, t5)) });
}), name$308 = `isZero`, dependencies$308 = [`typed`, `equalScalar`], createIsZero = factory(name$308, dependencies$308, (t4) => {
  var { typed: n2, equalScalar: r2 } = t4;
  return n2(name$308, { "number | BigNumber | Complex | Fraction": (t5) => r2(t5, 0), bigint: (t5) => t5 === 0n, Unit: n2.referToSelf((t5) => (r3) => n2.find(t5, r3.valueType())(r3.value)), "Array | Matrix": n2.referToSelf((t5) => (n3) => deepMap(n3, t5)) });
}), name$307 = `isNaN`, dependencies$307 = [`typed`], createIsNaN = factory(name$307, dependencies$307, (t4) => {
  var { typed: n2 } = t4;
  return n2(name$307, { number: isNaNNumber, BigNumber: function(t5) {
    return t5.isNaN();
  }, bigint: function(t5) {
    return false;
  }, Fraction: function(t5) {
    return false;
  }, Complex: function(t5) {
    return t5.isNaN();
  }, Unit: function(t5) {
    return Number.isNaN(t5.value);
  }, "Array | Matrix": n2.referToSelf((t5) => (n3) => deepMap(n3, t5)) });
}), name$306 = `isBounded`, dependencies$306 = [`typed`], createIsBounded = factory(name$306, dependencies$306, (t4) => {
  var { typed: n2 } = t4;
  return n2(name$306, { number: (t5) => Number.isFinite(t5), "BigNumber | Complex": (t5) => t5.isFinite(), "bigint | Fraction": () => true, "null | undefined": () => false, Unit: n2.referToSelf((t5) => (n3) => t5(n3.value)), "Array | Matrix": n2.referToSelf((t5) => (n3) => (Array.isArray(n3) || (n3 = n3.valueOf()), n3.every((n4) => t5(n4)))) });
}), name$305 = `isFinite`, dependencies$305 = [`typed`, `isBounded`, `map`], createIsFinite = factory(name$305, dependencies$305, (t4) => {
  var { typed: n2, isBounded: r2, map: i2 } = t4;
  return n2(name$305, { "Array | Matrix": (t5) => i2(t5, r2), any: (t5) => r2(t5) });
}), name$304 = `typeOf`, dependencies$304 = [`typed`], createTypeOf = factory(name$304, dependencies$304, (t4) => {
  var { typed: n2 } = t4;
  return n2(name$304, { any: typeOf });
});
function complexEquals(t4, n2, r2, i2) {
  return nearlyEqual$1(t4.re, n2.re, r2, i2) && nearlyEqual$1(t4.im, n2.im, r2, i2);
}
var createCompareUnits = factory(`compareUnits`, [`typed`], (t4) => {
  var { typed: n2 } = t4;
  return { "Unit, Unit": n2.referToSelf((t5) => (r2, i2) => {
    if (!r2.equalBase(i2)) throw Error(`Cannot compare units with different base`);
    return n2.find(t5, [r2.valueType(), i2.valueType()])(r2.value, i2.value);
  }) };
}), name$303 = `equalScalar`, dependencies$303 = [`typed`, `config`], createEqualScalar = factory(name$303, dependencies$303, (t4) => {
  var { typed: n2, config: r2 } = t4;
  return n2(name$303, { "boolean, boolean": function(t5, n3) {
    return t5 === n3;
  }, "number, number": function(t5, n3) {
    return nearlyEqual$1(t5, n3, r2.relTol, r2.absTol);
  }, "BigNumber, BigNumber": function(t5, n3) {
    return t5.eq(n3) || nearlyEqual(t5, n3, r2.relTol, r2.absTol);
  }, "bigint, bigint": function(t5, n3) {
    return t5 === n3;
  }, "Fraction, Fraction": function(t5, n3) {
    return t5.equals(n3);
  }, "Complex, Complex": function(t5, n3) {
    return complexEquals(t5, n3, r2.relTol, r2.absTol);
  } }, createCompareUnits({ typed: n2 }));
});
factory(name$303, [`typed`, `config`], (t4) => {
  var { typed: n2, config: r2 } = t4;
  return n2(name$303, { "number, number": function(t5, n3) {
    return nearlyEqual$1(t5, n3, r2.relTol, r2.absTol);
  } });
});
var name$302 = `SparseMatrix`, dependencies$302 = [`typed`, `equalScalar`, `Matrix`], createSparseMatrixClass = factory(name$302, dependencies$302, (t4) => {
  var { typed: n2, equalScalar: r2, Matrix: i2 } = t4;
  function a2(t5, n3) {
    if (!(this instanceof a2)) throw SyntaxError(`Constructor must be called with the new operator`);
    if (n3 && !isString(n3)) throw Error(`Invalid datatype: ` + n3);
    if (isMatrix(t5)) o2(this, t5, n3);
    else if (t5 && isArray(t5.index) && isArray(t5.ptr) && isArray(t5.size)) this._values = t5.values, this._index = t5.index, this._ptr = t5.ptr, this._size = t5.size, this._datatype = n3 || t5.datatype;
    else if (isArray(t5)) s2(this, t5, n3);
    else if (t5) throw TypeError(`Unsupported type of data (` + typeOf(t5) + `)`);
    else this._values = [], this._index = [], this._ptr = [0], this._size = [0, 0], this._datatype = n3;
  }
  function o2(t5, n3, r3) {
    n3.type === `SparseMatrix` ? (t5._values = n3._values ? clone$3(n3._values) : void 0, t5._index = clone$3(n3._index), t5._ptr = clone$3(n3._ptr), t5._size = clone$3(n3._size), t5._datatype = r3 || n3._datatype) : s2(t5, n3.valueOf(), r3 || n3._datatype);
  }
  function s2(t5, i3, a3) {
    t5._values = [], t5._index = [], t5._ptr = [], t5._datatype = a3;
    var o3 = i3.length, s3 = 0, c3 = r2, l3 = 0;
    if (isString(a3) && (c3 = n2.find(r2, [a3, a3]) || r2, l3 = n2.convert(0, a3)), o3 > 0) {
      var u3 = 0;
      do {
        t5._ptr.push(t5._index.length);
        for (var d3 = 0; d3 < o3; d3++) {
          var p3 = i3[d3];
          if (isArray(p3)) {
            if (u3 === 0 && s3 < p3.length && (s3 = p3.length), u3 < p3.length) {
              var m3 = p3[u3];
              c3(m3, l3) || (t5._values.push(m3), t5._index.push(d3));
            }
          } else u3 === 0 && s3 < 1 && (s3 = 1), c3(p3, l3) || (t5._values.push(p3), t5._index.push(d3));
        }
        u3++;
      } while (u3 < s3);
    }
    for (; t5._ptr.length <= s3; ) t5._ptr.push(t5._index.length);
    t5._size = [o3, s3];
  }
  a2.prototype = new i2(), a2.prototype.createSparseMatrix = function(t5, n3) {
    return new a2(t5, n3);
  }, Object.defineProperty(a2, "name", { value: `SparseMatrix` }), a2.prototype.constructor = a2, a2.prototype.type = `SparseMatrix`, a2.prototype.isSparseMatrix = true, a2.prototype.getDataType = function() {
    return getArrayDataType(this._values, typeOf);
  }, a2.prototype.storage = function() {
    return `sparse`;
  }, a2.prototype.datatype = function() {
    return this._datatype;
  }, a2.prototype.create = function(t5, n3) {
    return new a2(t5, n3);
  }, a2.prototype.density = function() {
    var t5 = this._size[0], n3 = this._size[1];
    return t5 !== 0 && n3 !== 0 ? this._index.length / (t5 * n3) : 0;
  }, a2.prototype.subset = function(t5, n3, r3) {
    if (!this._values) throw Error(`Cannot invoke subset on a Pattern only matrix`);
    switch (arguments.length) {
      case 1:
        return c2(this, t5);
      case 2:
      case 3:
        return l2(this, t5, n3, r3);
      default:
        throw SyntaxError(`Wrong number of arguments`);
    }
  };
  function c2(t5, n3) {
    if (!isIndex(n3)) throw TypeError(`Invalid index`);
    if (n3.isScalar()) return t5.get(n3.min());
    var r3 = n3.size();
    if (r3.length !== t5._size.length) throw new DimensionError(r3.length, t5._size.length);
    var i3, o3, s3, c3, l3 = n3.min(), u3 = n3.max();
    for (i3 = 0, o3 = t5._size.length; i3 < o3; i3++) validateIndex(l3[i3], t5._size[i3]), validateIndex(u3[i3], t5._size[i3]);
    var d3 = t5._values, p3 = t5._index, m3 = t5._ptr, h3 = n3.dimension(0), g3 = n3.dimension(1), v2 = [], y2 = [];
    function b2(t6, n4) {
      y2[t6] = n4[0], v2[t6] = true;
    }
    Number.isInteger(h3) ? b2(h3, [0]) : h3.forEach(b2);
    var x2 = d3 ? [] : void 0, S2 = [], w2 = [];
    function E(t6) {
      for (w2.push(S2.length), s3 = m3[t6], c3 = m3[t6 + 1]; s3 < c3; s3++) i3 = p3[s3], v2[i3] === true && (S2.push(y2[i3]), x2 && x2.push(d3[s3]));
    }
    return Number.isInteger(g3) ? E(g3) : g3.forEach(E), w2.push(S2.length), new a2({ values: x2, index: S2, ptr: w2, size: r3, datatype: t5._datatype });
  }
  function l2(t5, n3, r3, i3) {
    if (!n3 || n3.isIndex !== true) throw TypeError(`Invalid index`);
    var a3 = n3.size(), o3 = n3.isScalar(), s3;
    if (isMatrix(r3) ? (s3 = r3.size(), r3 = r3.toArray()) : s3 = arraySize(r3), o3) {
      if (s3.length !== 0) throw TypeError(`Scalar expected`);
      t5.set(n3.min(), r3, i3);
    } else {
      if (a3.length !== 1 && a3.length !== 2) throw new DimensionError(a3.length, t5._size.length, `<`);
      if (s3.length < a3.length) {
        for (var c3 = 0, l3 = 0; a3[c3] === 1 && s3[c3] === 1; ) c3++;
        for (; a3[c3] === 1; ) l3++, c3++;
        r3 = unsqueeze(r3, a3.length, l3, s3);
      }
      if (!deepStrictEqual(a3, s3)) throw new DimensionError(a3, s3, `>`);
      if (a3.length === 1) p3(n3.dimension(0), (n4, a4) => {
        validateIndex(n4), t5.set([n4, 0], r3[a4[0]], i3);
      });
      else {
        var u3 = n3.dimension(0), d3 = n3.dimension(1);
        p3(u3, (n4, a4) => {
          validateIndex(n4), p3(d3, (o4, s4) => {
            validateIndex(o4), t5.set([n4, o4], r3[a4[0]][s4[0]], i3);
          });
        });
      }
    }
    return t5;
    function p3(t6, n4) {
      isNumber(t6) ? n4(t6, [0]) : t6.forEach(n4);
    }
  }
  a2.prototype.get = function(t5) {
    if (!isArray(t5)) throw TypeError(`Array expected`);
    if (t5.length !== this._size.length) throw new DimensionError(t5.length, this._size.length);
    if (!this._values) throw Error(`Cannot invoke get on a Pattern only matrix`);
    var n3 = t5[0], r3 = t5[1];
    validateIndex(n3, this._size[0]), validateIndex(r3, this._size[1]);
    var i3 = u2(n3, this._ptr[r3], this._ptr[r3 + 1], this._index);
    return i3 < this._ptr[r3 + 1] && this._index[i3] === n3 ? this._values[i3] : 0;
  }, a2.prototype.set = function(t5, i3, a3) {
    if (!isArray(t5)) throw TypeError(`Array expected`);
    if (t5.length !== this._size.length) throw new DimensionError(t5.length, this._size.length);
    if (!this._values) throw Error(`Cannot invoke set on a Pattern only matrix`);
    var o3 = t5[0], s3 = t5[1], c3 = this._size[0], l3 = this._size[1], h3 = r2, g3 = 0;
    isString(this._datatype) && (h3 = n2.find(r2, [this._datatype, this._datatype]) || r2, g3 = n2.convert(0, this._datatype)), (o3 > c3 - 1 || s3 > l3 - 1) && (m2(this, Math.max(o3 + 1, c3), Math.max(s3 + 1, l3), a3), c3 = this._size[0], l3 = this._size[1]), validateIndex(o3, c3), validateIndex(s3, l3);
    var v2 = u2(o3, this._ptr[s3], this._ptr[s3 + 1], this._index);
    return v2 < this._ptr[s3 + 1] && this._index[v2] === o3 ? h3(i3, g3) ? d2(v2, s3, this._values, this._index, this._ptr) : this._values[v2] = i3 : h3(i3, g3) || p2(v2, o3, s3, i3, this._values, this._index, this._ptr), this;
  };
  function u2(t5, n3, r3, i3) {
    if (r3 - n3 === 0) return r3;
    for (var a3 = n3; a3 < r3; a3++) if (i3[a3] === t5) return a3;
    return n3;
  }
  function d2(t5, n3, r3, i3, a3) {
    r3.splice(t5, 1), i3.splice(t5, 1);
    for (var o3 = n3 + 1; o3 < a3.length; o3++) a3[o3]--;
  }
  function p2(t5, n3, r3, i3, a3, o3, s3) {
    a3.splice(t5, 0, i3), o3.splice(t5, 0, n3);
    for (var c3 = r3 + 1; c3 < s3.length; c3++) s3[c3]++;
  }
  a2.prototype.resize = function(t5, n3, r3) {
    if (!isCollection(t5)) throw TypeError(`Array or Matrix expected`);
    var i3 = t5.valueOf().map((t6) => Array.isArray(t6) && t6.length === 1 ? t6[0] : t6);
    if (i3.length !== 2) throw Error(`Only two dimensions matrix are supported`);
    return i3.forEach(function(t6) {
      if (!isNumber(t6) || !isInteger(t6) || t6 < 0) throw TypeError(`Invalid size, must contain positive integers (size: ` + format(i3) + `)`);
    }), m2(r3 ? this.clone() : this, i3[0], i3[1], n3);
  };
  function m2(t5, i3, a3, o3) {
    var s3 = o3 || 0, c3 = r2, l3 = 0;
    isString(t5._datatype) && (c3 = n2.find(r2, [t5._datatype, t5._datatype]) || r2, l3 = n2.convert(0, t5._datatype), s3 = n2.convert(s3, t5._datatype));
    var u3 = !c3(s3, l3), d3 = t5._size[0], p3 = t5._size[1], m3, h3, g3;
    if (a3 > p3) {
      for (h3 = p3; h3 < a3; h3++) if (t5._ptr[h3] = t5._values.length, u3) for (m3 = 0; m3 < d3; m3++) t5._values.push(s3), t5._index.push(m3);
      t5._ptr[a3] = t5._values.length;
    } else a3 < p3 && (t5._ptr.splice(a3 + 1, p3 - a3), t5._values.splice(t5._ptr[a3], t5._values.length), t5._index.splice(t5._ptr[a3], t5._index.length));
    if (p3 = a3, i3 > d3) {
      if (u3) {
        var v2 = 0;
        for (h3 = 0; h3 < p3; h3++) {
          t5._ptr[h3] = t5._ptr[h3] + v2, g3 = t5._ptr[h3 + 1] + v2;
          var y2 = 0;
          for (m3 = d3; m3 < i3; m3++, y2++) t5._values.splice(g3 + y2, 0, s3), t5._index.splice(g3 + y2, 0, m3), v2++;
        }
        t5._ptr[p3] = t5._values.length;
      }
    } else if (i3 < d3) {
      var b2 = 0;
      for (h3 = 0; h3 < p3; h3++) {
        t5._ptr[h3] = t5._ptr[h3] - b2;
        var x2 = t5._ptr[h3], S2 = t5._ptr[h3 + 1] - b2;
        for (g3 = x2; g3 < S2; g3++) m3 = t5._index[g3], m3 > i3 - 1 && (t5._values.splice(g3, 1), t5._index.splice(g3, 1), b2++);
      }
      t5._ptr[h3] = t5._values.length;
    }
    return t5._size[0] = i3, t5._size[1] = a3, t5;
  }
  a2.prototype.reshape = function(t5, n3) {
    if (!isArray(t5)) throw TypeError(`Array expected`);
    if (t5.length !== 2) throw Error(`Sparse matrices can only be reshaped in two dimensions`);
    t5.forEach(function(n4) {
      if (!isNumber(n4) || !isInteger(n4) || n4 <= -2 || n4 === 0) throw TypeError(`Invalid size, must contain positive integers or -1 (size: ` + format(t5) + `)`);
    });
    var r3 = this._size[0] * this._size[1];
    if (t5 = processSizesWildcard(t5, r3), r3 !== t5[0] * t5[1]) throw Error(`Reshaping sparse matrix will result in the wrong number of elements`);
    var i3 = n3 ? this.clone() : this;
    if (this._size[0] === t5[0] && this._size[1] === t5[1]) return i3;
    for (var a3 = [], o3 = 0; o3 < i3._ptr.length; o3++) for (var s3 = 0; s3 < i3._ptr[o3 + 1] - i3._ptr[o3]; s3++) a3.push(o3);
    for (var c3 = i3._values.slice(), l3 = i3._index.slice(), d3 = 0; d3 < i3._index.length; d3++) {
      var m3 = l3[d3], h3 = a3[d3], g3 = m3 * i3._size[1] + h3;
      a3[d3] = g3 % t5[1], l3[d3] = Math.floor(g3 / t5[1]);
    }
    i3._values.length = 0, i3._index.length = 0, i3._ptr.length = t5[1] + 1, i3._size = t5.slice();
    for (var v2 = 0; v2 < i3._ptr.length; v2++) i3._ptr[v2] = 0;
    for (var y2 = 0; y2 < c3.length; y2++) {
      var b2 = l3[y2], x2 = a3[y2], S2 = c3[y2];
      p2(u2(b2, i3._ptr[x2], i3._ptr[x2 + 1], i3._index), b2, x2, S2, i3._values, i3._index, i3._ptr);
    }
    return i3;
  }, a2.prototype.clone = function() {
    return new a2({ values: this._values ? clone$3(this._values) : void 0, index: clone$3(this._index), ptr: clone$3(this._ptr), size: clone$3(this._size), datatype: this._datatype });
  }, a2.prototype.size = function() {
    return this._size.slice(0);
  }, a2.prototype.map = function(t5, n3) {
    if (!this._values) throw Error(`Cannot invoke map on a Pattern only matrix`);
    var r3 = this, i3 = this._size[0], a3 = this._size[1], o3 = optimizeCallback(t5, r3, `map`);
    return h2(this, 0, i3 - 1, 0, a3 - 1, function(t6, n4, i4) {
      return o3.fn(t6, [n4, i4], r3);
    }, n3);
  };
  function h2(t5, i3, o3, s3, c3, l3, u3) {
    var d3 = [], p3 = [], m3 = [], h3 = r2, g3 = 0;
    isString(t5._datatype) && (h3 = n2.find(r2, [t5._datatype, t5._datatype]) || r2, g3 = n2.convert(0, t5._datatype));
    for (var v2 = function(t6, n3, r3) {
      var i4 = l3(t6, n3, r3);
      h3(i4, g3) || (d3.push(i4), p3.push(n3));
    }, y2 = s3; y2 <= c3; y2++) {
      m3.push(d3.length);
      var b2 = t5._ptr[y2], x2 = t5._ptr[y2 + 1];
      if (u3) for (var S2 = b2; S2 < x2; S2++) {
        var w2 = t5._index[S2];
        w2 >= i3 && w2 <= o3 && v2(t5._values[S2], w2 - i3, y2 - s3);
      }
      else {
        for (var E = {}, D = b2; D < x2; D++) {
          var O = t5._index[D];
          E[O] = t5._values[D];
        }
        for (var j2 = i3; j2 <= o3; j2++) v2(j2 in E ? E[j2] : 0, j2 - i3, y2 - s3);
      }
    }
    return m3.push(d3.length), new a2({ values: d3, index: p3, ptr: m3, size: [o3 - i3 + 1, c3 - s3 + 1] });
  }
  a2.prototype.forEach = function(t5, n3) {
    if (!this._values) throw Error(`Cannot invoke forEach on a Pattern only matrix`);
    for (var r3 = this, i3 = this._size[0], a3 = this._size[1], o3 = optimizeCallback(t5, r3, `forEach`), s3 = 0; s3 < a3; s3++) {
      var c3 = this._ptr[s3], l3 = this._ptr[s3 + 1];
      if (n3) for (var u3 = c3; u3 < l3; u3++) {
        var d3 = this._index[u3];
        o3.fn(this._values[u3], [d3, s3], r3);
      }
      else {
        for (var p3 = {}, m3 = c3; m3 < l3; m3++) {
          var h3 = this._index[m3];
          p3[h3] = this._values[m3];
        }
        for (var g3 = 0; g3 < i3; g3++) {
          var v2 = g3 in p3 ? p3[g3] : 0;
          o3.fn(v2, [g3, s3], r3);
        }
      }
    }
  }, a2.prototype[Symbol.iterator] = function* () {
    if (!this._values) throw Error(`Cannot iterate a Pattern only matrix`);
    for (var t5 = this._size[1], n3 = 0; n3 < t5; n3++) for (var r3 = this._ptr[n3], i3 = this._ptr[n3 + 1], a3 = r3; a3 < i3; a3++) {
      var o3 = this._index[a3];
      yield { value: this._values[a3], index: [o3, n3] };
    }
  }, a2.prototype.toArray = function() {
    return g2(this._values, this._index, this._ptr, this._size, true);
  }, a2.prototype.valueOf = function() {
    return g2(this._values, this._index, this._ptr, this._size, false);
  };
  function g2(t5, n3, r3, i3, a3) {
    var o3 = i3[0], s3 = i3[1], c3 = [], l3, u3;
    for (l3 = 0; l3 < o3; l3++) for (c3[l3] = [], u3 = 0; u3 < s3; u3++) c3[l3][u3] = 0;
    for (u3 = 0; u3 < s3; u3++) for (var d3 = r3[u3], p3 = r3[u3 + 1], m3 = d3; m3 < p3; m3++) l3 = n3[m3], c3[l3][u3] = t5 ? a3 ? clone$3(t5[m3]) : t5[m3] : 1;
    return c3;
  }
  return a2.prototype.format = function(t5) {
    for (var n3 = this._size[0], r3 = this._size[1], i3 = this.density(), a3 = `Sparse Matrix [` + format(n3, t5) + ` x ` + format(r3, t5) + `] density: ` + format(i3, t5) + `
`, o3 = 0; o3 < r3; o3++) for (var s3 = this._ptr[o3], c3 = this._ptr[o3 + 1], l3 = s3; l3 < c3; l3++) {
      var u3 = this._index[l3];
      a3 += `
    (` + format(u3, t5) + `, ` + format(o3, t5) + `) ==> ` + (this._values ? format(this._values[l3], t5) : `X`);
    }
    return a3;
  }, a2.prototype.toString = function() {
    return format(this.toArray());
  }, a2.prototype.toJSON = function() {
    return { mathjs: `SparseMatrix`, values: this._values, index: this._index, ptr: this._ptr, size: this._size, datatype: this._datatype };
  }, a2.prototype.diagonal = function(t5) {
    if (t5) {
      if (isBigNumber(t5) && (t5 = t5.toNumber()), !isNumber(t5) || !isInteger(t5)) throw TypeError(`The parameter k must be an integer number`);
    } else t5 = 0;
    var n3 = t5 > 0 ? t5 : 0, r3 = t5 < 0 ? -t5 : 0, i3 = this._size[0], o3 = this._size[1], s3 = Math.min(i3 - r3, o3 - n3), c3 = [], l3 = [], u3 = [];
    u3[0] = 0;
    for (var d3 = n3; d3 < o3 && c3.length < s3; d3++) for (var p3 = this._ptr[d3], m3 = this._ptr[d3 + 1], h3 = p3; h3 < m3; h3++) {
      var g3 = this._index[h3];
      if (g3 === d3 - n3 + r3) {
        c3.push(this._values[h3]), l3[c3.length - 1] = g3 - r3;
        break;
      }
    }
    return u3.push(c3.length), new a2({ values: c3, index: l3, ptr: u3, size: [s3, 1] });
  }, a2.fromJSON = function(t5) {
    return new a2(t5);
  }, a2.diagonal = function(t5, i3, o3, s3, c3) {
    if (!isArray(t5)) throw TypeError(`Array expected, size parameter`);
    if (t5.length !== 2) throw Error(`Only two dimensions matrix are supported`);
    if (t5 = t5.map(function(t6) {
      if (isBigNumber(t6) && (t6 = t6.toNumber()), !isNumber(t6) || !isInteger(t6) || t6 < 1) throw Error(`Size values must be positive integers`);
      return t6;
    }), o3) {
      if (isBigNumber(o3) && (o3 = o3.toNumber()), !isNumber(o3) || !isInteger(o3)) throw TypeError(`The parameter k must be an integer number`);
    } else o3 = 0;
    var l3 = r2, u3 = 0;
    isString(c3) && (l3 = n2.find(r2, [c3, c3]) || r2, u3 = n2.convert(0, c3));
    var d3 = o3 > 0 ? o3 : 0, p3 = o3 < 0 ? -o3 : 0, m3 = t5[0], h3 = t5[1], g3 = Math.min(m3 - p3, h3 - d3), v2;
    if (isArray(i3)) {
      if (i3.length !== g3) throw Error(`Invalid value array length`);
      v2 = function(t6) {
        return i3[t6];
      };
    } else if (isMatrix(i3)) {
      var y2 = i3.size();
      if (y2.length !== 1 || y2[0] !== g3) throw Error(`Invalid matrix length`);
      v2 = function(t6) {
        return i3.get([t6]);
      };
    } else v2 = function() {
      return i3;
    };
    for (var b2 = [], x2 = [], S2 = [], w2 = 0; w2 < h3; w2++) {
      S2.push(b2.length);
      var E = w2 - d3;
      if (E >= 0 && E < g3) {
        var D = v2(E);
        l3(D, u3) || (x2.push(E + p3), b2.push(D));
      }
    }
    return S2.push(b2.length), new a2({ values: b2, index: x2, ptr: S2, size: [m3, h3] });
  }, a2.prototype.swapRows = function(t5, n3) {
    if (!isNumber(t5) || !isInteger(t5) || !isNumber(n3) || !isInteger(n3)) throw Error(`Row index must be positive integers`);
    if (this._size.length !== 2) throw Error(`Only two dimensional matrix is supported`);
    return validateIndex(t5, this._size[0]), validateIndex(n3, this._size[0]), a2._swapRows(t5, n3, this._size[1], this._values, this._index, this._ptr), this;
  }, a2._forEachRow = function(t5, n3, r3, i3, a3) {
    for (var o3 = i3[t5], s3 = i3[t5 + 1], c3 = o3; c3 < s3; c3++) a3(r3[c3], n3[c3]);
  }, a2._swapRows = function(t5, n3, r3, i3, a3, o3) {
    for (var s3 = 0; s3 < r3; s3++) {
      var c3 = o3[s3], l3 = o3[s3 + 1], d3 = u2(t5, c3, l3, a3), p3 = u2(n3, c3, l3, a3);
      if (d3 < l3 && p3 < l3 && a3[d3] === t5 && a3[p3] === n3) {
        if (i3) {
          var m3 = i3[d3];
          i3[d3] = i3[p3], i3[p3] = m3;
        }
        continue;
      }
      if (d3 < l3 && a3[d3] === t5 && (p3 >= l3 || a3[p3] !== n3)) {
        var h3 = i3 ? i3[d3] : void 0;
        a3.splice(p3, 0, n3), i3 && i3.splice(p3, 0, h3), a3.splice(p3 <= d3 ? d3 + 1 : d3, 1), i3 && i3.splice(p3 <= d3 ? d3 + 1 : d3, 1);
        continue;
      }
      if (p3 < l3 && a3[p3] === n3 && (d3 >= l3 || a3[d3] !== t5)) {
        var g3 = i3 ? i3[p3] : void 0;
        a3.splice(d3, 0, t5), i3 && i3.splice(d3, 0, g3), a3.splice(d3 <= p3 ? p3 + 1 : p3, 1), i3 && i3.splice(d3 <= p3 ? p3 + 1 : p3, 1);
      }
    }
  }, a2;
}, { isClass: true }), name$301 = `number`, dependencies$301 = [`typed`];
function getNonDecimalNumberParts(t4) {
  var n2 = t4.match(/(0[box])([0-9a-fA-F]*)\.([0-9a-fA-F]*)/);
  return n2 ? { input: t4, radix: { "0b": 2, "0o": 8, "0x": 16 }[n2[1]], integerPart: n2[2], fractionalPart: n2[3] } : null;
}
function makeNumberFromNonDecimalParts(t4) {
  for (var n2 = parseInt(t4.integerPart, t4.radix), r2 = 0, i2 = 0; i2 < t4.fractionalPart.length; i2++) {
    var a2 = parseInt(t4.fractionalPart[i2], t4.radix);
    r2 += a2 / t4.radix ** +(i2 + 1);
  }
  var o2 = n2 + r2;
  if (isNaN(o2)) throw SyntaxError(`String "` + t4.input + `" is not a valid number`);
  return o2;
}
var createNumber = factory(name$301, dependencies$301, (t4) => {
  var { typed: n2 } = t4, r2 = n2(`number`, { "": function() {
    return 0;
  }, number: function(t5) {
    return t5;
  }, string: function(t5) {
    if (t5 === `NaN`) return NaN;
    var n3 = getNonDecimalNumberParts(t5);
    if (n3) return makeNumberFromNonDecimalParts(n3);
    var r3 = 0, i2 = t5.match(/(0[box][0-9a-fA-F]*)i([0-9]*)/);
    i2 && (r3 = Number(i2[2]), t5 = i2[1]);
    var a2 = Number(t5);
    if (isNaN(a2)) throw SyntaxError(`String "` + t5 + `" is not a valid number`);
    if (i2) {
      if (a2 > 2 ** r3 - 1) throw SyntaxError(`String "${t5}" is out of range`);
      a2 >= 2 ** (r3 - 1) && (a2 -= 2 ** r3);
    }
    return a2;
  }, BigNumber: function(t5) {
    return t5.toNumber();
  }, bigint: function(t5) {
    return Number(t5);
  }, Fraction: function(t5) {
    return t5.valueOf();
  }, Unit: n2.referToSelf((t5) => (n3) => {
    var r3 = n3.clone();
    return r3.value = t5(n3.value), r3;
  }), null: function(t5) {
    return 0;
  }, "Unit, string | Unit": function(t5, n3) {
    return t5.toNumber(n3);
  }, "Array | Matrix": n2.referToSelf((t5) => (n3) => deepMap(n3, t5)) });
  return r2.fromJSON = function(t5) {
    return parseFloat(t5.value);
  }, r2;
}), name$300 = `bigint`, dependencies$300 = [`typed`], createBigint = factory(name$300, dependencies$300, (t4) => {
  var { typed: n2 } = t4, r2 = n2(`bigint`, { "": function() {
    return 0n;
  }, bigint: function(t5) {
    return t5;
  }, number: function(t5) {
    return BigInt(t5.toFixed());
  }, BigNumber: function(t5) {
    return BigInt(t5.round().toString());
  }, Fraction: function(t5) {
    return BigInt(t5.valueOf().toFixed());
  }, "string | boolean": function(t5) {
    return BigInt(t5);
  }, null: function(t5) {
    return 0n;
  }, "Array | Matrix": n2.referToSelf((t5) => (n3) => deepMap(n3, t5)) });
  return r2.fromJSON = function(t5) {
    return BigInt(t5.value);
  }, r2;
}), name$299 = `string`, dependencies$299 = [`typed`], createString = factory(name$299, dependencies$299, (t4) => {
  var { typed: n2 } = t4;
  return n2(name$299, { "": function() {
    return ``;
  }, number: format$2, null: function(t5) {
    return `null`;
  }, boolean: function(t5) {
    return t5 + ``;
  }, string: function(t5) {
    return t5;
  }, "Array | Matrix": n2.referToSelf((t5) => (n3) => deepMap(n3, t5)), any: function(t5) {
    return String(t5);
  } });
}), name$298 = `boolean`, dependencies$298 = [`typed`], createBoolean = factory(name$298, dependencies$298, (t4) => {
  var { typed: n2 } = t4;
  return n2(name$298, { "": function() {
    return false;
  }, boolean: function(t5) {
    return t5;
  }, number: function(t5) {
    return !!t5;
  }, null: function(t5) {
    return false;
  }, BigNumber: function(t5) {
    return !t5.isZero();
  }, string: function(t5) {
    var n3 = t5.toLowerCase();
    if (n3 === `true`) return true;
    if (n3 === `false`) return false;
    var r2 = Number(t5);
    if (t5 !== `` && !isNaN(r2)) return !!r2;
    throw Error(`Cannot convert "` + t5 + `" to a boolean`);
  }, "Array | Matrix": n2.referToSelf((t5) => (n3) => deepMap(n3, t5)) });
}), name$297 = `bignumber`, dependencies$297 = [`typed`, `BigNumber`], createBignumber = factory(name$297, dependencies$297, (t4) => {
  var { typed: n2, BigNumber: r2 } = t4;
  return n2(`bignumber`, { "": function() {
    return new r2(0);
  }, number: function(t5) {
    return new r2(t5 + ``);
  }, string: function(t5) {
    var n3 = t5.match(/(0[box][0-9a-fA-F]*)i([0-9]*)/);
    if (n3) {
      var i2 = n3[2], a2 = r2(n3[1]), o2 = new r2(2).pow(Number(i2));
      if (a2.gt(o2.sub(1))) throw SyntaxError(`String "${t5}" is out of range`);
      var s2 = new r2(2).pow(Number(i2) - 1);
      return a2.gte(s2) ? a2.sub(o2) : a2;
    }
    return new r2(t5);
  }, BigNumber: function(t5) {
    return t5;
  }, bigint: function(t5) {
    return new r2(t5.toString());
  }, Unit: n2.referToSelf((t5) => (n3) => {
    var r3 = n3.clone();
    return r3.value = t5(n3.value), r3;
  }), Fraction: function(t5) {
    return new r2(String(t5.n)).div(String(t5.d)).times(String(t5.s));
  }, null: function(t5) {
    return new r2(0);
  }, "Array | Matrix": n2.referToSelf((t5) => (n3) => deepMap(n3, t5)) });
}), name$296 = `complex`, dependencies$296 = [`typed`, `Complex`], createComplex = factory(name$296, dependencies$296, (t4) => {
  var { typed: n2, Complex: r2 } = t4;
  return n2(`complex`, { "": function() {
    return r2.ZERO;
  }, number: function(t5) {
    return new r2(t5, 0);
  }, "number, number": function(t5, n3) {
    return new r2(t5, n3);
  }, "BigNumber, BigNumber": function(t5, n3) {
    return new r2(t5.toNumber(), n3.toNumber());
  }, Fraction: function(t5) {
    return new r2(t5.valueOf(), 0);
  }, Complex: function(t5) {
    return t5.clone();
  }, string: function(t5) {
    return r2(t5);
  }, null: function(t5) {
    return r2(0);
  }, Object: function(t5) {
    if (`re` in t5 && `im` in t5) return new r2(t5.re, t5.im);
    if (`r` in t5 && `phi` in t5 || `abs` in t5 && `arg` in t5) return new r2(t5);
    throw Error(`Expected object with properties (re and im) or (r and phi) or (abs and arg)`);
  }, "Array | Matrix": n2.referToSelf((t5) => (n3) => deepMap(n3, t5)) });
}), name$295 = `fraction`, dependencies$295 = [`typed`, `Fraction`], createFraction = factory(name$295, dependencies$295, (t4) => {
  var { typed: n2, Fraction: r2 } = t4;
  return n2(`fraction`, { number: function(t5) {
    if (!Number.isFinite(t5) || isNaN(t5)) throw Error(t5 + ` cannot be represented as a fraction`);
    return new r2(t5);
  }, string: function(t5) {
    return new r2(t5);
  }, "number, number": function(t5, n3) {
    return new r2(t5, n3);
  }, "bigint, bigint": function(t5, n3) {
    return new r2(t5, n3);
  }, null: function(t5) {
    return new r2(0);
  }, BigNumber: function(t5) {
    return new r2(t5.toString());
  }, bigint: function(t5) {
    return new r2(t5.toString());
  }, Fraction: function(t5) {
    return t5;
  }, Unit: n2.referToSelf((t5) => (n3) => {
    var r3 = n3.clone();
    return r3.value = t5(n3.value), r3;
  }), Object: function(t5) {
    return new r2(t5);
  }, "Array | Matrix": n2.referToSelf((t5) => (n3) => deepMap(n3, t5)) });
}), name$294 = `matrix`, dependencies$294 = [`typed`, `Matrix`, `DenseMatrix`, `SparseMatrix`], createMatrix = factory(name$294, dependencies$294, (t4) => {
  var { typed: n2, Matrix: r2, DenseMatrix: i2, SparseMatrix: a2 } = t4;
  return n2(name$294, { "": function() {
    return o2([]);
  }, string: function(t5) {
    return o2([], t5);
  }, "string, string": function(t5, n3) {
    return o2([], t5, n3);
  }, Array: function(t5) {
    return o2(t5);
  }, Matrix: function(t5) {
    return o2(t5, t5.storage());
  }, "Array | Matrix, string": o2, "Array | Matrix, string, string": o2 });
  function o2(t5, n3, r3) {
    if (n3 === `dense` || n3 === "default" || n3 === void 0) return new i2(t5, r3);
    if (n3 === `sparse`) return new a2(t5, r3);
    throw TypeError(`Unknown matrix type ` + JSON.stringify(n3) + `.`);
  }
}), name$293 = `matrixFromFunction`, dependencies$293 = [`typed`, `matrix`, `isZero`], createMatrixFromFunction = factory(name$293, dependencies$293, (t4) => {
  var { typed: n2, matrix: r2, isZero: i2 } = t4;
  return n2(name$293, { "Array | Matrix, function, string, string": function(t5, n3, r3, i3) {
    return a2(t5, n3, r3, i3);
  }, "Array | Matrix, function, string": function(t5, n3, r3) {
    return a2(t5, n3, r3);
  }, "Matrix, function": function(t5, n3) {
    return a2(t5, n3, `dense`);
  }, "Array, function": function(t5, n3) {
    return a2(t5, n3, `dense`).toArray();
  }, "Array | Matrix, string, function": function(t5, n3, r3) {
    return a2(t5, r3, n3);
  }, "Array | Matrix, string, string, function": function(t5, n3, r3, i3) {
    return a2(t5, i3, n3, r3);
  } });
  function a2(t5, n3, a3, o2) {
    var s2 = o2 === void 0 ? r2(a3) : r2(a3, o2);
    return s2.resize(t5), s2.forEach(function(t6, r3) {
      var a4 = n3(r3);
      i2(a4) || s2.set(r3, a4);
    }), s2;
  }
}), name$292 = `matrixFromRows`, dependencies$292 = [`typed`, `matrix`, `flatten`, `size`], createMatrixFromRows = factory(name$292, dependencies$292, (t4) => {
  var { typed: n2, matrix: r2, flatten: i2, size: a2 } = t4;
  return n2(name$292, { "...Array": function(t5) {
    return o2(t5);
  }, "...Matrix": function(t5) {
    return r2(o2(t5.map((t6) => t6.toArray())));
  } });
  function o2(t5) {
    if (t5.length === 0) throw TypeError(`At least one row is needed to construct a matrix.`);
    var n3 = s2(t5[0]), r3 = [];
    for (var a3 of t5) {
      var o3 = s2(a3);
      if (o3 !== n3) throw TypeError(`The vectors had different length: ` + (n3 | 0) + ` ≠ ` + (o3 | 0));
      r3.push(i2(a3));
    }
    return r3;
  }
  function s2(t5) {
    var n3 = a2(t5);
    if (n3.length === 1) return n3[0];
    if (n3.length === 2) {
      if (n3[0] === 1) return n3[1];
      if (n3[1] === 1) return n3[0];
      throw TypeError(`At least one of the arguments is not a vector.`);
    } else throw TypeError(`Only one- or two-dimensional vectors are supported.`);
  }
}), name$291 = `matrixFromColumns`, dependencies$291 = [`typed`, `matrix`, `flatten`, `size`], createMatrixFromColumns = factory(name$291, dependencies$291, (t4) => {
  var { typed: n2, matrix: r2, flatten: i2, size: a2 } = t4;
  return n2(name$291, { "...Array": function(t5) {
    return o2(t5);
  }, "...Matrix": function(t5) {
    return r2(o2(t5.map((t6) => t6.toArray())));
  } });
  function o2(t5) {
    if (t5.length === 0) throw TypeError(`At least one column is needed to construct a matrix.`);
    for (var n3 = s2(t5[0]), r3 = [], a3 = 0; a3 < n3; a3++) r3[a3] = [];
    for (var o3 of t5) {
      var c2 = s2(o3);
      if (c2 !== n3) throw TypeError(`The vectors had different length: ` + (n3 | 0) + ` ≠ ` + (c2 | 0));
      for (var l2 = i2(o3), u2 = 0; u2 < n3; u2++) r3[u2].push(l2[u2]);
    }
    return r3;
  }
  function s2(t5) {
    var n3 = a2(t5);
    if (n3.length === 1) return n3[0];
    if (n3.length === 2) {
      if (n3[0] === 1) return n3[1];
      if (n3[1] === 1) return n3[0];
      throw TypeError(`At least one of the arguments is not a vector.`);
    } else throw TypeError(`Only one- or two-dimensional vectors are supported.`);
  }
}), name$290 = `splitUnit`, dependencies$290 = [`typed`], createSplitUnit = factory(name$290, dependencies$290, (t4) => {
  var { typed: n2 } = t4;
  return n2(name$290, { "Unit, Array": function(t5, n3) {
    return t5.splitUnit(n3);
  } });
}), name$289 = `unaryMinus`, dependencies$289 = [`typed`], createUnaryMinus = factory(name$289, dependencies$289, (t4) => {
  var { typed: n2 } = t4;
  return n2(name$289, { number: unaryMinusNumber, "Complex | BigNumber | Fraction": (t5) => t5.neg(), bigint: (t5) => -t5, Unit: n2.referToSelf((t5) => (r2) => {
    var i2 = r2.clone();
    return i2.value = n2.find(t5, i2.valueType())(r2.value), i2;
  }), "Array | Matrix": n2.referToSelf((t5) => (n3) => deepMap(n3, t5, true)) });
}), name$288 = `unaryPlus`, dependencies$288 = [`typed`, `config`, `numeric`], createUnaryPlus = factory(name$288, dependencies$288, (t4) => {
  var { typed: n2, config: r2, numeric: i2 } = t4;
  return n2(name$288, { number: unaryPlusNumber, Complex: function(t5) {
    return t5;
  }, BigNumber: function(t5) {
    return t5;
  }, bigint: function(t5) {
    return t5;
  }, Fraction: function(t5) {
    return t5;
  }, Unit: function(t5) {
    return t5.clone();
  }, "Array | Matrix": n2.referToSelf((t5) => (n3) => deepMap(n3, t5, true)), boolean: function(t5) {
    return i2(+!!t5, r2.number);
  }, string: function(t5) {
    return i2(t5, safeNumberType(t5, r2));
  } });
}), name$287 = `abs`, dependencies$287 = [`typed`], createAbs = factory(name$287, dependencies$287, (t4) => {
  var { typed: n2 } = t4;
  return n2(name$287, { number: absNumber, "Complex | BigNumber | Fraction | Unit": (t5) => t5.abs(), bigint: (t5) => t5 < 0n ? -t5 : t5, "Array | Matrix": n2.referToSelf((t5) => (n3) => deepMap(n3, t5, true)) });
}), name$286 = `mapSlices`, dependencies$286 = [`typed`, `isInteger`], createMapSlices = factory(name$286, dependencies$286, (t4) => {
  var { typed: n2, isInteger: r2 } = t4;
  return n2(name$286, { "Array | Matrix, number | BigNumber, function": function(t5, n3, i2) {
    if (!r2(n3)) throw TypeError(`Integer number expected for dimension`);
    var a2 = Array.isArray(t5) ? arraySize(t5) : t5.size();
    if (n3 < 0 || n3 >= a2.length) throw new IndexError(n3, a2.length);
    return isMatrix(t5) ? t5.create(_mapSlices(t5.valueOf(), n3, i2), t5.datatype()) : _mapSlices(t5, n3, i2);
  } });
}, { formerly: `apply` });
function _mapSlices(t4, n2, r2) {
  var i2, a2, o2;
  if (n2 <= 0) if (Array.isArray(t4[0])) {
    for (o2 = _switch(t4), a2 = [], i2 = 0; i2 < o2.length; i2++) a2[i2] = _mapSlices(o2[i2], n2 - 1, r2);
    return a2;
  } else return r2(t4);
  else {
    for (a2 = [], i2 = 0; i2 < t4.length; i2++) a2[i2] = _mapSlices(t4[i2], n2 - 1, r2);
    return a2;
  }
}
function _switch(t4) {
  var n2 = t4.length, r2 = t4[0].length, i2, a2, o2 = [];
  for (a2 = 0; a2 < r2; a2++) {
    var s2 = [];
    for (i2 = 0; i2 < n2; i2++) s2.push(t4[i2][a2]);
    o2.push(s2);
  }
  return o2;
}
var name$285 = `addScalar`, dependencies$285 = [`typed`], createAddScalar = factory(name$285, dependencies$285, (t4) => {
  var { typed: n2 } = t4;
  return n2(name$285, { "number, number": addNumber, "Complex, Complex": function(t5, n3) {
    return t5.add(n3);
  }, "BigNumber, BigNumber": function(t5, n3) {
    return t5.plus(n3);
  }, "bigint, bigint": function(t5, n3) {
    return t5 + n3;
  }, "Fraction, Fraction": function(t5, n3) {
    return t5.add(n3);
  }, "Unit, Unit": n2.referToSelf((t5) => (r2, i2) => {
    if (r2.value === null || r2.value === void 0) throw Error(`Parameter x contains a unit with undefined value`);
    if (i2.value === null || i2.value === void 0) throw Error(`Parameter y contains a unit with undefined value`);
    if (!r2.equalBase(i2)) throw Error(`Units do not match`);
    var a2 = r2.clone();
    return a2.value = n2.find(t5, [a2.valueType(), i2.valueType()])(a2.value, i2.value), a2.fixPrefix = false, a2;
  }) });
}), name$284 = `subtractScalar`, dependencies$284 = [`typed`], createSubtractScalar = factory(name$284, dependencies$284, (t4) => {
  var { typed: n2 } = t4;
  return n2(name$284, { "number, number": subtractNumber, "Complex, Complex": function(t5, n3) {
    return t5.sub(n3);
  }, "BigNumber, BigNumber": function(t5, n3) {
    return t5.minus(n3);
  }, "bigint, bigint": function(t5, n3) {
    return t5 - n3;
  }, "Fraction, Fraction": function(t5, n3) {
    return t5.sub(n3);
  }, "Unit, Unit": n2.referToSelf((t5) => (r2, i2) => {
    if (r2.value === null || r2.value === void 0) throw Error(`Parameter x contains a unit with undefined value`);
    if (i2.value === null || i2.value === void 0) throw Error(`Parameter y contains a unit with undefined value`);
    if (!r2.equalBase(i2)) throw Error(`Units do not match`);
    var a2 = r2.clone();
    return a2.value = n2.find(t5, [a2.valueType(), i2.valueType()])(a2.value, i2.value), a2.fixPrefix = false, a2;
  }) });
}), name$283 = `cbrt`, dependencies$283 = [`config`, `typed`, `isNegative`, `unaryMinus`, `matrix`, `Complex`, `BigNumber`, `Fraction`], createCbrt = factory(name$283, dependencies$283, (t4) => {
  var { config: n2, typed: r2, isNegative: i2, unaryMinus: a2, matrix: o2, Complex: s2, BigNumber: c2, Fraction: l2 } = t4;
  return r2(name$283, { number: cbrtNumber, Complex: u2, "Complex, boolean": u2, BigNumber: function(t5) {
    return t5.cbrt();
  }, Unit: d2 });
  function u2(t5, r3) {
    var i3 = t5.arg() / 3, a3 = t5.abs(), c3 = new s2(cbrtNumber(a3), 0).mul(new s2(0, i3).exp());
    if (r3) {
      var l3 = [c3, new s2(cbrtNumber(a3), 0).mul(new s2(0, i3 + Math.PI * 2 / 3).exp()), new s2(cbrtNumber(a3), 0).mul(new s2(0, i3 - Math.PI * 2 / 3).exp())];
      return n2.matrix === `Array` ? l3 : o2(l3);
    } else return c3;
  }
  function d2(t5) {
    if (t5.value && isComplex(t5.value)) {
      var n3 = t5.clone();
      return n3.value = 1, n3 = n3.pow(1 / 3), n3.value = u2(t5.value), n3;
    } else {
      var r3 = i2(t5.value);
      r3 && (t5.value = a2(t5.value));
      var o3 = isBigNumber(t5.value) ? new c2(1).div(3) : isFraction(t5.value) ? new l2(1, 3) : 1 / 3, s3 = t5.pow(o3);
      return r3 && (s3.value = a2(s3.value)), s3;
    }
  }
}), name$282 = `matAlgo11xS0s`, dependencies$282 = [`typed`, `equalScalar`], createMatAlgo11xS0s = factory(name$282, dependencies$282, (t4) => {
  var { typed: n2, equalScalar: r2 } = t4;
  return function(t5, i2, a2, o2) {
    var s2 = t5._values, c2 = t5._index, l2 = t5._ptr, u2 = t5._size, d2 = t5._datatype;
    if (!s2) throw Error(`Cannot perform operation on Pattern Sparse Matrix and Scalar value`);
    var p2 = u2[0], m2 = u2[1], h2, g2 = r2, v2 = 0, y2 = a2;
    typeof d2 == `string` && (h2 = d2, g2 = n2.find(r2, [h2, h2]), v2 = n2.convert(0, h2), i2 = n2.convert(i2, h2), y2 = n2.find(a2, [h2, h2]));
    for (var b2 = [], x2 = [], S2 = [], w2 = 0; w2 < m2; w2++) {
      S2[w2] = x2.length;
      for (var E = l2[w2], D = l2[w2 + 1], O = E; O < D; O++) {
        var j2 = c2[O], M2 = o2 ? y2(i2, s2[O]) : y2(s2[O], i2);
        g2(M2, v2) || (x2.push(j2), b2.push(M2));
      }
    }
    return S2[m2] = x2.length, t5.createSparseMatrix({ values: b2, index: x2, ptr: S2, size: [p2, m2], datatype: h2 });
  };
}), name$281 = `matAlgo12xSfs`, dependencies$281 = [`typed`, `DenseMatrix`], createMatAlgo12xSfs = factory(name$281, dependencies$281, (t4) => {
  var { typed: n2, DenseMatrix: r2 } = t4;
  return function(t5, i2, a2, o2) {
    var s2 = t5._values, c2 = t5._index, l2 = t5._ptr, u2 = t5._size, d2 = t5._datatype;
    if (!s2) throw Error(`Cannot perform operation on Pattern Sparse Matrix and Scalar value`);
    var p2 = u2[0], m2 = u2[1], h2, g2 = a2;
    typeof d2 == `string` && (h2 = d2, i2 = n2.convert(i2, h2), g2 = n2.find(a2, [h2, h2]));
    for (var v2 = [], y2 = [], b2 = [], x2 = 0; x2 < m2; x2++) {
      for (var S2 = x2 + 1, w2 = l2[x2], E = l2[x2 + 1], D = w2; D < E; D++) {
        var O = c2[D];
        y2[O] = s2[D], b2[O] = S2;
      }
      for (var j2 = 0; j2 < p2; j2++) x2 === 0 && (v2[j2] = []), b2[j2] === S2 ? v2[j2][x2] = o2 ? g2(i2, y2[j2]) : g2(y2[j2], i2) : v2[j2][x2] = o2 ? g2(i2, 0) : g2(0, i2);
    }
    return new r2({ data: v2, size: [p2, m2], datatype: h2 });
  };
}), name$280 = `matAlgo14xDs`, dependencies$280 = [`typed`], createMatAlgo14xDs = factory(name$280, dependencies$280, (t4) => {
  var { typed: n2 } = t4;
  return function(t5, i2, a2, o2) {
    var s2 = t5._data, c2 = t5._size, l2 = t5._datatype, u2, d2 = a2;
    typeof l2 == `string` && (u2 = l2, i2 = n2.convert(i2, u2), d2 = n2.find(a2, [u2, u2]));
    var p2 = c2.length > 0 ? r2(d2, 0, c2, c2[0], s2, i2, o2) : [];
    return t5.createDenseMatrix({ data: p2, size: clone$3(c2), datatype: u2 });
  };
  function r2(t5, n3, i2, a2, o2, s2, c2) {
    var l2 = [];
    if (n3 === i2.length - 1) for (var u2 = 0; u2 < a2; u2++) l2[u2] = c2 ? t5(s2, o2[u2]) : t5(o2[u2], s2);
    else for (var d2 = 0; d2 < a2; d2++) l2[d2] = r2(t5, n3 + 1, i2, i2[n3 + 1], o2[d2], s2, c2);
    return l2;
  }
}), name$279 = `ceil`, dependencies$279 = [`typed`, `config`, `round`, `matrix`, `equalScalar`, `zeros`, `DenseMatrix`], bigTen$1 = new Decimal(10), createCeilNumber = factory(name$279, [`typed`, `config`, `round`], (t4) => {
  var { typed: n2, config: r2, round: i2 } = t4;
  function a2(t5) {
    var n3 = Math.ceil(t5), a3 = i2(t5);
    return n3 === a3 ? n3 : nearlyEqual$1(t5, a3, r2.relTol, r2.absTol) && !nearlyEqual$1(t5, n3, r2.relTol, r2.absTol) ? a3 : n3;
  }
  return n2(name$279, { number: a2, "number, number": function(t5, n3) {
    if (!isInteger(n3)) throw RangeError(`number of decimals in function ceil must be an integer`);
    if (n3 < 0 || n3 > 15) throw RangeError(`number of decimals in ceil number must be in range 0-15`);
    var r3 = 10 ** n3;
    return a2(t5 * r3) / r3;
  } });
}), createCeil = factory(name$279, dependencies$279, (t4) => {
  var { typed: n2, config: r2, round: i2, matrix: a2, equalScalar: o2, zeros: s2, DenseMatrix: c2 } = t4, l2 = createMatAlgo11xS0s({ typed: n2, equalScalar: o2 }), u2 = createMatAlgo12xSfs({ typed: n2, DenseMatrix: c2 }), d2 = createMatAlgo14xDs({ typed: n2 }), p2 = createCeilNumber({ typed: n2, config: r2, round: i2 });
  function m2(t5) {
    var n3 = (t6, n4) => nearlyEqual(t6, n4, r2.relTol, r2.absTol), a3 = t5.ceil(), o3 = i2(t5);
    return a3.eq(o3) ? a3 : n3(t5, o3) && !n3(t5, a3) ? o3 : a3;
  }
  return n2(`ceil`, { number: p2.signatures.number, "number,number": p2.signatures[`number,number`], Complex: function(t5) {
    return t5.ceil();
  }, "Complex, number": function(t5, n3) {
    return t5.ceil(n3);
  }, "Complex, BigNumber": function(t5, n3) {
    return t5.ceil(n3.toNumber());
  }, BigNumber: m2, "BigNumber, BigNumber": function(t5, n3) {
    var r3 = bigTen$1.pow(n3);
    return m2(t5.mul(r3)).div(r3);
  }, bigint: (t5) => t5, "bigint, number": (t5, n3) => t5, "bigint, BigNumber": (t5, n3) => t5, Fraction: function(t5) {
    return t5.ceil();
  }, "Fraction, number": function(t5, n3) {
    return t5.ceil(n3);
  }, "Fraction, BigNumber": function(t5, n3) {
    return t5.ceil(n3.toNumber());
  }, "Unit, number, Unit": n2.referToSelf((t5) => function(n3, r3, i3) {
    var a3 = n3.toNumeric(i3);
    return i3.multiply(t5(a3, r3));
  }), "Unit, BigNumber, Unit": n2.referToSelf((t5) => (n3, r3, i3) => t5(n3, r3.toNumber(), i3)), "Array | Matrix, number | BigNumber, Unit": n2.referToSelf((t5) => (n3, r3, i3) => deepMap(n3, (n4) => t5(n4, r3, i3), true)), "Array | Matrix | Unit, Unit": n2.referToSelf((t5) => (n3, r3) => t5(n3, 0, r3)), "Array | Matrix": n2.referToSelf((t5) => (n3) => deepMap(n3, t5, true)), "Array, number | BigNumber": n2.referToSelf((t5) => (n3, r3) => deepMap(n3, (n4) => t5(n4, r3), true)), "SparseMatrix, number | BigNumber": n2.referToSelf((t5) => (n3, r3) => l2(n3, r3, t5, false)), "DenseMatrix, number | BigNumber": n2.referToSelf((t5) => (n3, r3) => d2(n3, r3, t5, false)), "number | Complex | Fraction | BigNumber, Array": n2.referToSelf((t5) => (n3, r3) => d2(a2(r3), n3, t5, true).valueOf()), "number | Complex | Fraction | BigNumber, Matrix": n2.referToSelf((t5) => (n3, r3) => o2(n3, 0) ? s2(r3.size(), r3.storage()) : r3.storage() === `dense` ? d2(r3, n3, t5, true) : u2(r3, n3, t5, true)) });
}), name$278 = `cube`, dependencies$278 = [`typed`], createCube = factory(name$278, dependencies$278, (t4) => {
  var { typed: n2 } = t4;
  return n2(name$278, { number: cubeNumber, Complex: function(t5) {
    return t5.mul(t5).mul(t5);
  }, BigNumber: function(t5) {
    return t5.times(t5).times(t5);
  }, bigint: function(t5) {
    return t5 * t5 * t5;
  }, Fraction: function(t5) {
    return t5.pow(3);
  }, Unit: function(t5) {
    return t5.pow(3);
  } });
}), name$277 = `exp`, dependencies$277 = [`typed`], createExp = factory(name$277, dependencies$277, (t4) => {
  var { typed: n2 } = t4;
  return n2(name$277, { number: expNumber, Complex: function(t5) {
    return t5.exp();
  }, BigNumber: function(t5) {
    return t5.exp();
  } });
}), name$276 = `expm1`, dependencies$276 = [`typed`, `Complex`], createExpm1 = factory(name$276, dependencies$276, (t4) => {
  var { typed: n2, Complex: r2 } = t4;
  return n2(name$276, { number: expm1Number, Complex: function(t5) {
    var n3 = Math.exp(t5.re);
    return new r2(n3 * Math.cos(t5.im) - 1, n3 * Math.sin(t5.im));
  }, BigNumber: function(t5) {
    return t5.exp().minus(1);
  } });
}), name$275 = `fix`, dependencies$275 = [`typed`, `Complex`, `matrix`, `ceil`, `floor`, `equalScalar`, `zeros`, `DenseMatrix`], createFixNumber = factory(name$275, [`typed`, `ceil`, `floor`], (t4) => {
  var { typed: n2, ceil: r2, floor: i2 } = t4;
  return n2(name$275, { number: function(t5) {
    return t5 > 0 ? i2(t5) : r2(t5);
  }, "number, number": function(t5, n3) {
    return t5 > 0 ? i2(t5, n3) : r2(t5, n3);
  } });
}), createFix = factory(name$275, dependencies$275, (t4) => {
  var { typed: n2, Complex: r2, matrix: i2, ceil: a2, floor: o2, equalScalar: s2, zeros: c2, DenseMatrix: l2 } = t4, u2 = createMatAlgo12xSfs({ typed: n2, DenseMatrix: l2 }), d2 = createMatAlgo14xDs({ typed: n2 }), p2 = createFixNumber({ typed: n2, ceil: a2, floor: o2 });
  return n2(`fix`, { number: p2.signatures.number, "number, number | BigNumber": p2.signatures[`number,number`], Complex: function(t5) {
    return new r2(t5.re > 0 ? Math.floor(t5.re) : Math.ceil(t5.re), t5.im > 0 ? Math.floor(t5.im) : Math.ceil(t5.im));
  }, "Complex, number": function(t5, n3) {
    return new r2(t5.re > 0 ? o2(t5.re, n3) : a2(t5.re, n3), t5.im > 0 ? o2(t5.im, n3) : a2(t5.im, n3));
  }, "Complex, BigNumber": function(t5, n3) {
    var i3 = n3.toNumber();
    return new r2(t5.re > 0 ? o2(t5.re, i3) : a2(t5.re, i3), t5.im > 0 ? o2(t5.im, i3) : a2(t5.im, i3));
  }, BigNumber: function(t5) {
    return t5.isNegative() ? a2(t5) : o2(t5);
  }, "BigNumber, number | BigNumber": function(t5, n3) {
    return t5.isNegative() ? a2(t5, n3) : o2(t5, n3);
  }, bigint: (t5) => t5, "bigint, number": (t5, n3) => t5, "bigint, BigNumber": (t5, n3) => t5, Fraction: function(t5) {
    return t5.s < 0n ? t5.ceil() : t5.floor();
  }, "Fraction, number | BigNumber": function(t5, n3) {
    return t5.s < 0n ? a2(t5, n3) : o2(t5, n3);
  }, "Unit, number, Unit": n2.referToSelf((t5) => function(n3, r3, i3) {
    var a3 = n3.toNumeric(i3);
    return i3.multiply(t5(a3, r3));
  }), "Unit, BigNumber, Unit": n2.referToSelf((t5) => (n3, r3, i3) => t5(n3, r3.toNumber(), i3)), "Array | Matrix, number | BigNumber, Unit": n2.referToSelf((t5) => (n3, r3, i3) => deepMap(n3, (n4) => t5(n4, r3, i3), true)), "Array | Matrix | Unit, Unit": n2.referToSelf((t5) => (n3, r3) => t5(n3, 0, r3)), "Array | Matrix": n2.referToSelf((t5) => (n3) => deepMap(n3, t5, true)), "Array | Matrix, number | BigNumber": n2.referToSelf((t5) => (n3, r3) => deepMap(n3, (n4) => t5(n4, r3), true)), "number | Complex | Fraction | BigNumber, Array": n2.referToSelf((t5) => (n3, r3) => d2(i2(r3), n3, t5, true).valueOf()), "number | Complex | Fraction | BigNumber, Matrix": n2.referToSelf((t5) => (n3, r3) => s2(n3, 0) ? c2(r3.size(), r3.storage()) : r3.storage() === `dense` ? d2(r3, n3, t5, true) : u2(r3, n3, t5, true)) });
}), name$274 = `floor`, dependencies$274 = [`typed`, `config`, `round`, `matrix`, `equalScalar`, `zeros`, `DenseMatrix`], bigTen = new Decimal(10), createFloorNumber = factory(name$274, [`typed`, `config`, `round`], (t4) => {
  var { typed: n2, config: r2, round: i2 } = t4;
  function a2(t5) {
    var n3 = Math.floor(t5), a3 = i2(t5);
    return n3 === a3 ? n3 : nearlyEqual$1(t5, a3, r2.relTol, r2.absTol) && !nearlyEqual$1(t5, n3, r2.relTol, r2.absTol) ? a3 : n3;
  }
  return n2(name$274, { number: a2, "number, number": function(t5, n3) {
    if (!isInteger(n3)) throw RangeError(`number of decimals in function floor must be an integer`);
    if (n3 < 0 || n3 > 15) throw RangeError(`number of decimals in floor number must be in range 0 - 15`);
    var r3 = 10 ** n3;
    return a2(t5 * r3) / r3;
  } });
}), createFloor = factory(name$274, dependencies$274, (t4) => {
  var { typed: n2, config: r2, round: i2, matrix: a2, equalScalar: o2, zeros: s2, DenseMatrix: c2 } = t4, l2 = createMatAlgo11xS0s({ typed: n2, equalScalar: o2 }), u2 = createMatAlgo12xSfs({ typed: n2, DenseMatrix: c2 }), d2 = createMatAlgo14xDs({ typed: n2 }), p2 = createFloorNumber({ typed: n2, config: r2, round: i2 });
  function m2(t5) {
    var n3 = (t6, n4) => nearlyEqual(t6, n4, r2.relTol, r2.absTol), a3 = t5.floor(), o3 = i2(t5);
    return a3.eq(o3) ? a3 : n3(t5, o3) && !n3(t5, a3) ? o3 : a3;
  }
  return n2(`floor`, { number: p2.signatures.number, "number,number": p2.signatures[`number,number`], Complex: function(t5) {
    return t5.floor();
  }, "Complex, number": function(t5, n3) {
    return t5.floor(n3);
  }, "Complex, BigNumber": function(t5, n3) {
    return t5.floor(n3.toNumber());
  }, BigNumber: m2, "BigNumber, BigNumber": function(t5, n3) {
    var r3 = bigTen.pow(n3);
    return m2(t5.mul(r3)).div(r3);
  }, bigint: (t5) => t5, "bigint, number": (t5, n3) => t5, "bigint, BigNumber": (t5, n3) => t5, Fraction: function(t5) {
    return t5.floor();
  }, "Fraction, number": function(t5, n3) {
    return t5.floor(n3);
  }, "Fraction, BigNumber": function(t5, n3) {
    return t5.floor(n3.toNumber());
  }, "Unit, number, Unit": n2.referToSelf((t5) => function(n3, r3, i3) {
    var a3 = n3.toNumeric(i3);
    return i3.multiply(t5(a3, r3));
  }), "Unit, BigNumber, Unit": n2.referToSelf((t5) => (n3, r3, i3) => t5(n3, r3.toNumber(), i3)), "Array | Matrix, number | BigNumber, Unit": n2.referToSelf((t5) => (n3, r3, i3) => deepMap(n3, (n4) => t5(n4, r3, i3), true)), "Array | Matrix | Unit, Unit": n2.referToSelf((t5) => (n3, r3) => t5(n3, 0, r3)), "Array | Matrix": n2.referToSelf((t5) => (n3) => deepMap(n3, t5, true)), "Array, number | BigNumber": n2.referToSelf((t5) => (n3, r3) => deepMap(n3, (n4) => t5(n4, r3), true)), "SparseMatrix, number | BigNumber": n2.referToSelf((t5) => (n3, r3) => l2(n3, r3, t5, false)), "DenseMatrix, number | BigNumber": n2.referToSelf((t5) => (n3, r3) => d2(n3, r3, t5, false)), "number | Complex | Fraction | BigNumber, Array": n2.referToSelf((t5) => (n3, r3) => d2(a2(r3), n3, t5, true).valueOf()), "number | Complex | Fraction | BigNumber, Matrix": n2.referToSelf((t5) => (n3, r3) => o2(n3, 0) ? s2(r3.size(), r3.storage()) : r3.storage() === `dense` ? d2(r3, n3, t5, true) : u2(r3, n3, t5, true)) });
}), name$273 = `matAlgo02xDS0`, dependencies$273 = [`typed`, `equalScalar`], createMatAlgo02xDS0 = factory(name$273, dependencies$273, (t4) => {
  var { typed: n2, equalScalar: r2 } = t4;
  return function(t5, i2, a2, o2) {
    var s2 = t5._data, c2 = t5._size, l2 = t5._datatype || t5.getDataType(), u2 = i2._values, d2 = i2._index, p2 = i2._ptr, m2 = i2._size, h2 = i2._datatype || i2._data === void 0 ? i2._datatype : i2.getDataType();
    if (c2.length !== m2.length) throw new DimensionError(c2.length, m2.length);
    if (c2[0] !== m2[0] || c2[1] !== m2[1]) throw RangeError(`Dimension mismatch. Matrix A (` + c2 + `) must match Matrix B (` + m2 + `)`);
    if (!u2) throw Error(`Cannot perform operation on Dense Matrix and Pattern Sparse Matrix`);
    var g2 = c2[0], v2 = c2[1], y2, b2 = r2, x2 = 0, S2 = a2;
    typeof l2 == `string` && l2 === h2 && l2 !== `mixed` && (y2 = l2, b2 = n2.find(r2, [y2, y2]), x2 = n2.convert(0, y2), S2 = n2.find(a2, [y2, y2]));
    for (var w2 = [], E = [], D = [], O = 0; O < v2; O++) {
      D[O] = E.length;
      for (var j2 = p2[O], M2 = p2[O + 1], N2 = j2; N2 < M2; N2++) {
        var F = d2[N2], I = o2 ? S2(u2[N2], s2[F][O]) : S2(s2[F][O], u2[N2]);
        b2(I, x2) || (E.push(F), w2.push(I));
      }
    }
    return D[v2] = E.length, i2.createSparseMatrix({ values: w2, index: E, ptr: D, size: [g2, v2], datatype: l2 === t5._datatype && h2 === i2._datatype ? y2 : void 0 });
  };
}), name$272 = `matAlgo03xDSf`, dependencies$272 = [`typed`], createMatAlgo03xDSf = factory(name$272, dependencies$272, (t4) => {
  var { typed: n2 } = t4;
  return function(t5, r2, i2, a2) {
    var o2 = t5._data, s2 = t5._size, c2 = t5._datatype || t5.getDataType(), l2 = r2._values, u2 = r2._index, d2 = r2._ptr, p2 = r2._size, m2 = r2._datatype || r2._data === void 0 ? r2._datatype : r2.getDataType();
    if (s2.length !== p2.length) throw new DimensionError(s2.length, p2.length);
    if (s2[0] !== p2[0] || s2[1] !== p2[1]) throw RangeError(`Dimension mismatch. Matrix A (` + s2 + `) must match Matrix B (` + p2 + `)`);
    if (!l2) throw Error(`Cannot perform operation on Dense Matrix and Pattern Sparse Matrix`);
    var h2 = s2[0], g2 = s2[1], v2, y2 = 0, b2 = i2;
    typeof c2 == `string` && c2 === m2 && c2 !== `mixed` && (v2 = c2, y2 = n2.convert(0, v2), b2 = n2.find(i2, [v2, v2]));
    for (var x2 = [], S2 = 0; S2 < h2; S2++) x2[S2] = [];
    for (var w2 = [], E = [], D = 0; D < g2; D++) {
      for (var O = D + 1, j2 = d2[D], M2 = d2[D + 1], N2 = j2; N2 < M2; N2++) {
        var F = u2[N2];
        w2[F] = a2 ? b2(l2[N2], o2[F][D]) : b2(o2[F][D], l2[N2]), E[F] = O;
      }
      for (var I = 0; I < h2; I++) E[I] === O ? x2[I][D] = w2[I] : x2[I][D] = a2 ? b2(y2, o2[I][D]) : b2(o2[I][D], y2);
    }
    return t5.createDenseMatrix({ data: x2, size: [h2, g2], datatype: c2 === t5._datatype && m2 === r2._datatype ? v2 : void 0 });
  };
}), name$271 = `matAlgo05xSfSf`, dependencies$271 = [`typed`, `equalScalar`], createMatAlgo05xSfSf = factory(name$271, dependencies$271, (t4) => {
  var { typed: n2, equalScalar: r2 } = t4;
  return function(t5, i2, a2) {
    var o2 = t5._values, s2 = t5._index, c2 = t5._ptr, l2 = t5._size, u2 = t5._datatype || t5._data === void 0 ? t5._datatype : t5.getDataType(), d2 = i2._values, p2 = i2._index, m2 = i2._ptr, h2 = i2._size, g2 = i2._datatype || i2._data === void 0 ? i2._datatype : i2.getDataType();
    if (l2.length !== h2.length) throw new DimensionError(l2.length, h2.length);
    if (l2[0] !== h2[0] || l2[1] !== h2[1]) throw RangeError(`Dimension mismatch. Matrix A (` + l2 + `) must match Matrix B (` + h2 + `)`);
    var v2 = l2[0], y2 = l2[1], b2, x2 = r2, S2 = 0, w2 = a2;
    typeof u2 == `string` && u2 === g2 && u2 !== `mixed` && (b2 = u2, x2 = n2.find(r2, [b2, b2]), S2 = n2.convert(0, b2), w2 = n2.find(a2, [b2, b2]));
    var E = o2 && d2 ? [] : void 0, D = [], O = [], j2 = E ? [] : void 0, M2 = E ? [] : void 0, N2 = [], F = [], I, L, R, z;
    for (L = 0; L < y2; L++) {
      O[L] = D.length;
      var V = L + 1;
      for (R = c2[L], z = c2[L + 1]; R < z; R++) I = s2[R], D.push(I), N2[I] = V, j2 && (j2[I] = o2[R]);
      for (R = m2[L], z = m2[L + 1]; R < z; R++) I = p2[R], N2[I] !== V && D.push(I), F[I] = V, M2 && (M2[I] = d2[R]);
      if (E) for (R = O[L]; R < D.length; ) {
        I = D[R];
        var H2 = N2[I], U = F[I];
        if (H2 === V || U === V) {
          var W = H2 === V ? j2[I] : S2, G = U === V ? M2[I] : S2, K = w2(W, G);
          x2(K, S2) ? D.splice(R, 1) : (E.push(K), R++);
        }
      }
    }
    return O[y2] = D.length, t5.createSparseMatrix({ values: E, index: D, ptr: O, size: [v2, y2], datatype: u2 === t5._datatype && g2 === i2._datatype ? b2 : void 0 });
  };
}), name$270 = `matAlgo13xDD`, dependencies$270 = [`typed`], createMatAlgo13xDD = factory(name$270, dependencies$270, (t4) => {
  var { typed: n2 } = t4;
  return function(t5, i2, a2) {
    var o2 = t5._data, s2 = t5._size, c2 = t5._datatype, l2 = i2._data, u2 = i2._size, d2 = i2._datatype, p2 = [];
    if (s2.length !== u2.length) throw new DimensionError(s2.length, u2.length);
    for (var m2 = 0; m2 < s2.length; m2++) {
      if (s2[m2] !== u2[m2]) throw RangeError(`Dimension mismatch. Matrix A (` + s2 + `) must match Matrix B (` + u2 + `)`);
      p2[m2] = s2[m2];
    }
    var h2, g2 = a2;
    typeof c2 == `string` && c2 === d2 && (h2 = c2, g2 = n2.find(a2, [h2, h2]));
    var v2 = p2.length > 0 ? r2(g2, 0, p2, p2[0], o2, l2) : [];
    return t5.createDenseMatrix({ data: v2, size: p2, datatype: h2 });
  };
  function r2(t5, n3, i2, a2, o2, s2) {
    var c2 = [];
    if (n3 === i2.length - 1) for (var l2 = 0; l2 < a2; l2++) c2[l2] = t5(o2[l2], s2[l2]);
    else for (var u2 = 0; u2 < a2; u2++) c2[u2] = r2(t5, n3 + 1, i2, i2[n3 + 1], o2[u2], s2[u2]);
    return c2;
  }
});
function broadcast(t4, n2) {
  if (deepStrictEqual(t4.size(), n2.size())) return [t4, n2];
  var r2 = broadcastSizes(t4.size(), n2.size());
  return [t4, n2].map((t5) => _broadcastTo(t5, r2));
}
function _broadcastTo(t4, n2) {
  return deepStrictEqual(t4.size(), n2) ? t4 : t4.create(broadcastTo(t4.valueOf(), n2), t4.datatype());
}
var name$269 = `matrixAlgorithmSuite`, dependencies$269 = [`typed`, `matrix`], createMatrixAlgorithmSuite = factory(name$269, dependencies$269, (t4) => {
  var { typed: n2, matrix: r2 } = t4, i2 = createMatAlgo13xDD({ typed: n2 }), a2 = createMatAlgo14xDs({ typed: n2 });
  return function(t5) {
    var o2 = t5.elop, s2 = t5.SD || t5.DS, c2;
    o2 ? (c2 = { "DenseMatrix, DenseMatrix": (t6, n3) => i2(...broadcast(t6, n3), o2), "Array, Array": (t6, n3) => i2(...broadcast(r2(t6), r2(n3)), o2).valueOf(), "Array, DenseMatrix": (t6, n3) => i2(...broadcast(r2(t6), n3), o2), "DenseMatrix, Array": (t6, n3) => i2(...broadcast(t6, r2(n3)), o2) }, t5.SS && (c2[`SparseMatrix, SparseMatrix`] = (n3, r3) => t5.SS(...broadcast(n3, r3), o2, false)), t5.DS && (c2[`DenseMatrix, SparseMatrix`] = (n3, r3) => t5.DS(...broadcast(n3, r3), o2, false), c2[`Array, SparseMatrix`] = (n3, i3) => t5.DS(...broadcast(r2(n3), i3), o2, false)), s2 && (c2[`SparseMatrix, DenseMatrix`] = (t6, n3) => s2(...broadcast(n3, t6), o2, true), c2[`SparseMatrix, Array`] = (t6, n3) => s2(...broadcast(r2(n3), t6), o2, true))) : (c2 = { "DenseMatrix, DenseMatrix": n2.referToSelf((t6) => (n3, r3) => i2(...broadcast(n3, r3), t6)), "Array, Array": n2.referToSelf((t6) => (n3, a3) => i2(...broadcast(r2(n3), r2(a3)), t6).valueOf()), "Array, DenseMatrix": n2.referToSelf((t6) => (n3, a3) => i2(...broadcast(r2(n3), a3), t6)), "DenseMatrix, Array": n2.referToSelf((t6) => (n3, a3) => i2(...broadcast(n3, r2(a3)), t6)) }, t5.SS && (c2[`SparseMatrix, SparseMatrix`] = n2.referToSelf((n3) => (r3, i3) => t5.SS(...broadcast(r3, i3), n3, false))), t5.DS && (c2[`DenseMatrix, SparseMatrix`] = n2.referToSelf((n3) => (r3, i3) => t5.DS(...broadcast(r3, i3), n3, false)), c2[`Array, SparseMatrix`] = n2.referToSelf((n3) => (i3, a3) => t5.DS(...broadcast(r2(i3), a3), n3, false))), s2 && (c2[`SparseMatrix, DenseMatrix`] = n2.referToSelf((t6) => (n3, r3) => s2(...broadcast(r3, n3), t6, true)), c2[`SparseMatrix, Array`] = n2.referToSelf((t6) => (n3, i3) => s2(...broadcast(r2(i3), n3), t6, true))));
    var l2 = t5.scalar || `any`;
    (t5.Ds || t5.Ss) && (o2 ? (c2[`DenseMatrix,` + l2] = (t6, n3) => a2(t6, n3, o2, false), c2[l2 + `, DenseMatrix`] = (t6, n3) => a2(n3, t6, o2, true), c2[`Array,` + l2] = (t6, n3) => a2(r2(t6), n3, o2, false).valueOf(), c2[l2 + `, Array`] = (t6, n3) => a2(r2(n3), t6, o2, true).valueOf()) : (c2[`DenseMatrix,` + l2] = n2.referToSelf((t6) => (n3, r3) => a2(n3, r3, t6, false)), c2[l2 + `, DenseMatrix`] = n2.referToSelf((t6) => (n3, r3) => a2(r3, n3, t6, true)), c2[`Array,` + l2] = n2.referToSelf((t6) => (n3, i3) => a2(r2(n3), i3, t6, false).valueOf()), c2[l2 + `, Array`] = n2.referToSelf((t6) => (n3, i3) => a2(r2(i3), n3, t6, true).valueOf())));
    var u2 = t5.sS === void 0 ? t5.Ss : t5.sS;
    return o2 ? (t5.Ss && (c2[`SparseMatrix,` + l2] = (n3, r3) => t5.Ss(n3, r3, o2, false)), u2 && (c2[l2 + `, SparseMatrix`] = (t6, n3) => u2(n3, t6, o2, true))) : (t5.Ss && (c2[`SparseMatrix,` + l2] = n2.referToSelf((n3) => (r3, i3) => t5.Ss(r3, i3, n3, false))), u2 && (c2[l2 + `, SparseMatrix`] = n2.referToSelf((t6) => (n3, r3) => u2(r3, n3, t6, true)))), o2 && o2.signatures && extend$1(c2, o2.signatures), c2;
  };
}), name$268 = `mod`, dependencies$268 = [`typed`, `config`, `round`, `matrix`, `equalScalar`, `zeros`, `DenseMatrix`, `concat`], createMod = factory(name$268, dependencies$268, (t4) => {
  var { typed: n2, config: r2, round: i2, matrix: a2, equalScalar: o2, zeros: s2, DenseMatrix: c2, concat: l2 } = t4, u2 = createFloor({ typed: n2, config: r2, round: i2, matrix: a2, equalScalar: o2, zeros: s2, DenseMatrix: c2 }), d2 = createMatAlgo02xDS0({ typed: n2, equalScalar: o2 }), p2 = createMatAlgo03xDSf({ typed: n2 }), m2 = createMatAlgo05xSfSf({ typed: n2, equalScalar: o2 }), h2 = createMatAlgo11xS0s({ typed: n2, equalScalar: o2 }), g2 = createMatAlgo12xSfs({ typed: n2, DenseMatrix: c2 }), v2 = createMatrixAlgorithmSuite({ typed: n2, matrix: a2, concat: l2 });
  return n2(name$268, { "number, number": y2, "BigNumber, BigNumber": function(t5, n3) {
    return n3.isZero() ? t5 : t5.sub(n3.mul(u2(t5.div(n3))));
  }, "bigint, bigint": function(t5, n3) {
    if (n3 === 0n) return t5;
    if (t5 < 0) {
      var r3 = t5 % n3;
      return r3 === 0n ? r3 : r3 + n3;
    }
    return t5 % n3;
  }, "Fraction, Fraction": function(t5, n3) {
    return n3.equals(0) ? t5 : t5.sub(n3.mul(u2(t5.div(n3))));
  } }, v2({ SS: m2, DS: p2, SD: d2, Ss: h2, sS: g2 }));
  function y2(t5, n3) {
    return n3 === 0 ? t5 : t5 - n3 * u2(t5 / n3);
  }
}), name$267 = `matAlgo01xDSid`, dependencies$267 = [`typed`], createMatAlgo01xDSid = factory(name$267, dependencies$267, (t4) => {
  var { typed: n2 } = t4;
  return function(t5, r2, i2, a2) {
    var o2 = t5._data, s2 = t5._size, c2 = t5._datatype || t5.getDataType(), l2 = r2._values, u2 = r2._index, d2 = r2._ptr, p2 = r2._size, m2 = r2._datatype || r2._data === void 0 ? r2._datatype : r2.getDataType();
    if (s2.length !== p2.length) throw new DimensionError(s2.length, p2.length);
    if (s2[0] !== p2[0] || s2[1] !== p2[1]) throw RangeError(`Dimension mismatch. Matrix A (` + s2 + `) must match Matrix B (` + p2 + `)`);
    if (!l2) throw Error(`Cannot perform operation on Dense Matrix and Pattern Sparse Matrix`);
    var h2 = s2[0], g2 = s2[1], v2 = typeof c2 == `string` && c2 !== `mixed` && c2 === m2 ? c2 : void 0, y2 = v2 ? n2.find(i2, [v2, v2]) : i2, b2, x2, S2 = [];
    for (b2 = 0; b2 < h2; b2++) S2[b2] = [];
    var w2 = [], E = [];
    for (x2 = 0; x2 < g2; x2++) {
      for (var D = x2 + 1, O = d2[x2], j2 = d2[x2 + 1], M2 = O; M2 < j2; M2++) b2 = u2[M2], w2[b2] = a2 ? y2(l2[M2], o2[b2][x2]) : y2(o2[b2][x2], l2[M2]), E[b2] = D;
      for (b2 = 0; b2 < h2; b2++) E[b2] === D ? S2[b2][x2] = w2[b2] : S2[b2][x2] = o2[b2][x2];
    }
    return t5.createDenseMatrix({ data: S2, size: [h2, g2], datatype: c2 === t5._datatype && m2 === r2._datatype ? v2 : void 0 });
  };
}), name$266 = `matAlgo04xSidSid`, dependencies$266 = [`typed`, `equalScalar`], createMatAlgo04xSidSid = factory(name$266, dependencies$266, (t4) => {
  var { typed: n2, equalScalar: r2 } = t4;
  return function(t5, i2, a2) {
    var o2 = t5._values, s2 = t5._index, c2 = t5._ptr, l2 = t5._size, u2 = t5._datatype || t5._data === void 0 ? t5._datatype : t5.getDataType(), d2 = i2._values, p2 = i2._index, m2 = i2._ptr, h2 = i2._size, g2 = i2._datatype || i2._data === void 0 ? i2._datatype : i2.getDataType();
    if (l2.length !== h2.length) throw new DimensionError(l2.length, h2.length);
    if (l2[0] !== h2[0] || l2[1] !== h2[1]) throw RangeError(`Dimension mismatch. Matrix A (` + l2 + `) must match Matrix B (` + h2 + `)`);
    var v2 = l2[0], y2 = l2[1], b2, x2 = r2, S2 = 0, w2 = a2;
    typeof u2 == `string` && u2 === g2 && u2 !== `mixed` && (b2 = u2, x2 = n2.find(r2, [b2, b2]), S2 = n2.convert(0, b2), w2 = n2.find(a2, [b2, b2]));
    var E = o2 && d2 ? [] : void 0, D = [], O = [], j2 = o2 && d2 ? [] : void 0, M2 = o2 && d2 ? [] : void 0, N2 = [], F = [], I, L, R, z, V;
    for (L = 0; L < y2; L++) {
      O[L] = D.length;
      var H2 = L + 1;
      for (z = c2[L], V = c2[L + 1], R = z; R < V; R++) I = s2[R], D.push(I), N2[I] = H2, j2 && (j2[I] = o2[R]);
      for (z = m2[L], V = m2[L + 1], R = z; R < V; R++) if (I = p2[R], N2[I] === H2) {
        if (j2) {
          var U = w2(j2[I], d2[R]);
          x2(U, S2) ? N2[I] = null : j2[I] = U;
        }
      } else D.push(I), F[I] = H2, M2 && (M2[I] = d2[R]);
      if (j2 && M2) for (R = O[L]; R < D.length; ) I = D[R], N2[I] === H2 ? (E[R] = j2[I], R++) : F[I] === H2 ? (E[R] = M2[I], R++) : D.splice(R, 1);
    }
    return O[y2] = D.length, t5.createSparseMatrix({ values: E, index: D, ptr: O, size: [v2, y2], datatype: u2 === t5._datatype && g2 === i2._datatype ? b2 : void 0 });
  };
}), name$265 = `matAlgo10xSids`, dependencies$265 = [`typed`, `DenseMatrix`], createMatAlgo10xSids = factory(name$265, dependencies$265, (t4) => {
  var { typed: n2, DenseMatrix: r2 } = t4;
  return function(t5, i2, a2, o2) {
    var s2 = t5._values, c2 = t5._index, l2 = t5._ptr, u2 = t5._size, d2 = t5._datatype;
    if (!s2) throw Error(`Cannot perform operation on Pattern Sparse Matrix and Scalar value`);
    var p2 = u2[0], m2 = u2[1], h2, g2 = a2;
    typeof d2 == `string` && (h2 = d2, i2 = n2.convert(i2, h2), g2 = n2.find(a2, [h2, h2]));
    for (var v2 = [], y2 = [], b2 = [], x2 = 0; x2 < m2; x2++) {
      for (var S2 = x2 + 1, w2 = l2[x2], E = l2[x2 + 1], D = w2; D < E; D++) {
        var O = c2[D];
        y2[O] = s2[D], b2[O] = S2;
      }
      for (var j2 = 0; j2 < p2; j2++) x2 === 0 && (v2[j2] = []), b2[j2] === S2 ? v2[j2][x2] = o2 ? g2(i2, y2[j2]) : g2(y2[j2], i2) : v2[j2][x2] = i2;
    }
    return new r2({ data: v2, size: [p2, m2], datatype: h2 });
  };
});
function ArgumentsError(t4, n2, r2, i2) {
  if (!(this instanceof ArgumentsError)) throw SyntaxError(`Constructor must be called with the new operator`);
  this.fn = t4, this.count = n2, this.min = r2, this.max = i2, this.message = `Wrong number of arguments in function ` + t4 + ` (` + n2 + ` provided, ` + r2 + (i2 == null ? `` : `-` + i2) + ` expected)`, this.stack = Error().stack;
}
ArgumentsError.prototype = Error(), ArgumentsError.prototype.constructor = Error, ArgumentsError.prototype.name = `ArgumentsError`, ArgumentsError.prototype.isArgumentsError = true;
var name$264 = `gcd`, dependencies$264 = [`typed`, `config`, `round`, `matrix`, `equalScalar`, `zeros`, `BigNumber`, `DenseMatrix`, `concat`], gcdTypes = `number | BigNumber | Fraction | Matrix | Array`, gcdManyTypesSignature = `${gcdTypes}, ${gcdTypes}, ...${gcdTypes}`;
function is1d(t4) {
  return !t4.some((t5) => Array.isArray(t5));
}
var createGcd = factory(name$264, dependencies$264, (t4) => {
  var { typed: n2, matrix: r2, config: i2, round: a2, equalScalar: o2, zeros: s2, BigNumber: c2, DenseMatrix: l2, concat: u2 } = t4, d2 = createMod({ typed: n2, config: i2, round: a2, matrix: r2, equalScalar: o2, zeros: s2, DenseMatrix: l2, concat: u2 }), p2 = createMatAlgo01xDSid({ typed: n2 }), m2 = createMatAlgo04xSidSid({ typed: n2, equalScalar: o2 }), h2 = createMatAlgo10xSids({ typed: n2, DenseMatrix: l2 }), g2 = createMatrixAlgorithmSuite({ typed: n2, matrix: r2, concat: u2 });
  return n2(name$264, { "number, number": v2, "BigNumber, BigNumber": y2, "Fraction, Fraction": (t5, n3) => t5.gcd(n3) }, g2({ SS: m2, DS: p2, Ss: h2 }), { [gcdManyTypesSignature]: n2.referToSelf((t5) => (n3, r3, i3) => {
    for (var a3 = t5(n3, r3), o3 = 0; o3 < i3.length; o3++) a3 = t5(a3, i3[o3]);
    return a3;
  }), Array: n2.referToSelf((t5) => (n3) => {
    if (n3.length === 1 && Array.isArray(n3[0]) && is1d(n3[0])) return t5(...n3[0]);
    if (is1d(n3)) return t5(...n3);
    throw new ArgumentsError(`gcd() supports only 1d matrices!`);
  }), Matrix: n2.referToSelf((t5) => (n3) => t5(n3.toArray())) });
  function v2(t5, n3) {
    if (!isInteger(t5) || !isInteger(n3)) throw Error(`Parameters in function gcd must be integer numbers`);
    for (var r3; n3 !== 0; ) r3 = d2(t5, n3), t5 = n3, n3 = r3;
    return t5 < 0 ? -t5 : t5;
  }
  function y2(t5, n3) {
    if (!t5.isInt() || !n3.isInt()) throw Error(`Parameters in function gcd must be integer numbers`);
    for (var r3 = new c2(0); !n3.isZero(); ) {
      var i3 = d2(t5, n3);
      t5 = n3, n3 = i3;
    }
    return t5.lt(r3) ? t5.neg() : t5;
  }
}), name$263 = `matAlgo06xS0S0`, dependencies$263 = [`typed`, `equalScalar`], createMatAlgo06xS0S0 = factory(name$263, dependencies$263, (t4) => {
  var { typed: n2, equalScalar: r2 } = t4;
  return function(t5, i2, a2) {
    var o2 = t5._values, s2 = t5._size, c2 = t5._datatype || t5._data === void 0 ? t5._datatype : t5.getDataType(), l2 = i2._values, u2 = i2._size, d2 = i2._datatype || i2._data === void 0 ? i2._datatype : i2.getDataType();
    if (s2.length !== u2.length) throw new DimensionError(s2.length, u2.length);
    if (s2[0] !== u2[0] || s2[1] !== u2[1]) throw RangeError(`Dimension mismatch. Matrix A (` + s2 + `) must match Matrix B (` + u2 + `)`);
    var p2 = s2[0], m2 = s2[1], h2, g2 = r2, v2 = 0, y2 = a2;
    typeof c2 == `string` && c2 === d2 && c2 !== `mixed` && (h2 = c2, g2 = n2.find(r2, [h2, h2]), v2 = n2.convert(0, h2), y2 = n2.find(a2, [h2, h2]));
    for (var b2 = o2 && l2 ? [] : void 0, x2 = [], S2 = [], w2 = b2 ? [] : void 0, E = [], D = [], O = 0; O < m2; O++) {
      S2[O] = x2.length;
      var j2 = O + 1;
      if (scatter(t5, O, E, w2, D, j2, x2, y2), scatter(i2, O, E, w2, D, j2, x2, y2), w2) for (var M2 = S2[O]; M2 < x2.length; ) {
        var N2 = x2[M2];
        if (D[N2] === j2) {
          var F = w2[N2];
          g2(F, v2) ? x2.splice(M2, 1) : (b2.push(F), M2++);
        } else x2.splice(M2, 1);
      }
      else for (var I = S2[O]; I < x2.length; ) D[x2[I]] === j2 ? I++ : x2.splice(I, 1);
    }
    return S2[m2] = x2.length, t5.createSparseMatrix({ values: b2, index: x2, ptr: S2, size: [p2, m2], datatype: c2 === t5._datatype && d2 === i2._datatype ? h2 : void 0 });
  };
}), name$262 = `lcm`, dependencies$262 = [`typed`, `matrix`, `equalScalar`, `concat`], createLcm = factory(name$262, dependencies$262, (t4) => {
  var { typed: n2, matrix: r2, equalScalar: i2, concat: a2 } = t4, o2 = createMatAlgo02xDS0({ typed: n2, equalScalar: i2 }), s2 = createMatAlgo06xS0S0({ typed: n2, equalScalar: i2 }), c2 = createMatAlgo11xS0s({ typed: n2, equalScalar: i2 }), l2 = createMatrixAlgorithmSuite({ typed: n2, matrix: r2, concat: a2 }), u2 = `number | BigNumber | Fraction | Matrix | Array`, d2 = {};
  return d2[`${u2}, ${u2}, ...${u2}`] = n2.referToSelf((t5) => (n3, r3, i3) => {
    for (var a3 = t5(n3, r3), o3 = 0; o3 < i3.length; o3++) a3 = t5(a3, i3[o3]);
    return a3;
  }), n2(name$262, { "number, number": lcmNumber, "BigNumber, BigNumber": p2, "Fraction, Fraction": (t5, n3) => t5.lcm(n3) }, l2({ SS: s2, DS: o2, Ss: c2 }), d2);
  function p2(t5, n3) {
    if (!t5.isInt() || !n3.isInt()) throw Error(`Parameters in function lcm must be integer numbers`);
    if (t5.isZero()) return t5;
    if (n3.isZero()) return n3;
    for (var r3 = t5.times(n3); !n3.isZero(); ) {
      var i3 = n3;
      n3 = t5.mod(i3), t5 = i3;
    }
    return r3.div(t5).abs();
  }
});
function promoteLogarithm(t4, n2, r2, i2) {
  return function(a2) {
    if (a2 > 0 || r2.predictable) {
      if (a2 <= 0) return NaN;
      var o2 = a2.toString(16), s2 = o2.substring(0, 15);
      return t4 * (o2.length - s2.length) + n2(Number(`0x` + s2));
    }
    return i2(a2.toNumber());
  };
}
var name$261 = `log10`, dependencies$261 = [`typed`, `config`, `Complex`], log16 = log10Number(16), createLog10 = factory(name$261, dependencies$261, (t4) => {
  var { typed: n2, config: r2, Complex: i2 } = t4;
  function a2(t5) {
    return t5.log().div(Math.LN10);
  }
  function o2(t5) {
    return a2(new i2(t5, 0));
  }
  return n2(name$261, { number: function(t5) {
    return t5 >= 0 || r2.predictable ? log10Number(t5) : o2(t5);
  }, bigint: promoteLogarithm(log16, log10Number, r2, o2), Complex: a2, BigNumber: function(t5) {
    return !t5.isNegative() || r2.predictable ? t5.log() : o2(t5.toNumber());
  }, "Array | Matrix": n2.referToSelf((t5) => (n3) => deepMap(n3, t5)) });
}), name$260 = `log2`, dependencies$260 = [`typed`, `config`, `Complex`], createLog2 = factory(name$260, dependencies$260, (t4) => {
  var { typed: n2, config: r2, Complex: i2 } = t4;
  function a2(t5) {
    return o2(new i2(t5, 0));
  }
  return n2(name$260, { number: function(t5) {
    return t5 >= 0 || r2.predictable ? log2Number(t5) : a2(t5);
  }, bigint: promoteLogarithm(4, log2Number, r2, a2), Complex: o2, BigNumber: function(t5) {
    return !t5.isNegative() || r2.predictable ? t5.log(2) : a2(t5.toNumber());
  }, "Array | Matrix": n2.referToSelf((t5) => (n3) => deepMap(n3, t5)) });
  function o2(t5) {
    var n3 = Math.sqrt(t5.re * t5.re + t5.im * t5.im);
    return new i2(Math.log2 ? Math.log2(n3) : Math.log(n3) / Math.LN2, Math.atan2(t5.im, t5.re) / Math.LN2);
  }
}), name$259 = `multiplyScalar`, dependencies$259 = [`typed`], createMultiplyScalar = factory(name$259, dependencies$259, (t4) => {
  var { typed: n2 } = t4;
  return n2(`multiplyScalar`, { "number, number": multiplyNumber, "Complex, Complex": function(t5, n3) {
    return t5.mul(n3);
  }, "BigNumber, BigNumber": function(t5, n3) {
    return t5.times(n3);
  }, "bigint, bigint": function(t5, n3) {
    return t5 * n3;
  }, "Fraction, Fraction": function(t5, n3) {
    return t5.mul(n3);
  }, "number | Fraction | BigNumber | Complex, Unit": (t5, n3) => n3.multiply(t5), "Unit, number | Fraction | BigNumber | Complex | Unit": (t5, n3) => t5.multiply(n3) });
}), name$258 = `multiply`, dependencies$258 = [`typed`, `matrix`, `addScalar`, `multiplyScalar`, `equalScalar`, `dot`], createMultiply = factory(name$258, dependencies$258, (t4) => {
  var { typed: n2, matrix: r2, addScalar: i2, multiplyScalar: a2, equalScalar: o2, dot: s2 } = t4, c2 = createMatAlgo11xS0s({ typed: n2, equalScalar: o2 }), l2 = createMatAlgo14xDs({ typed: n2 });
  function u2(t5, n3) {
    switch (t5.length) {
      case 1:
        switch (n3.length) {
          case 1:
            if (t5[0] !== n3[0]) throw RangeError(`Dimension mismatch in multiplication. Vectors must have the same length`);
            break;
          case 2:
            if (t5[0] !== n3[0]) throw RangeError(`Dimension mismatch in multiplication. Vector length (` + t5[0] + `) must match Matrix rows (` + n3[0] + `)`);
            break;
          default:
            throw Error(`Can only multiply a 1 or 2 dimensional matrix (Matrix B has ` + n3.length + ` dimensions)`);
        }
        break;
      case 2:
        switch (n3.length) {
          case 1:
            if (t5[1] !== n3[0]) throw RangeError(`Dimension mismatch in multiplication. Matrix columns (` + t5[1] + `) must match Vector length (` + n3[0] + `)`);
            break;
          case 2:
            if (t5[1] !== n3[0]) throw RangeError(`Dimension mismatch in multiplication. Matrix A columns (` + t5[1] + `) must match Matrix B rows (` + n3[0] + `)`);
            break;
          default:
            throw Error(`Can only multiply a 1 or 2 dimensional matrix (Matrix B has ` + n3.length + ` dimensions)`);
        }
        break;
      default:
        throw Error(`Can only multiply a 1 or 2 dimensional matrix (Matrix A has ` + t5.length + ` dimensions)`);
    }
  }
  function d2(t5, n3, r3) {
    if (r3 === 0) throw Error(`Cannot multiply two empty vectors`);
    return s2(t5, n3);
  }
  function p2(t5, n3) {
    if (n3.storage() !== `dense`) throw Error(`Support for SparseMatrix not implemented`);
    return m2(t5, n3);
  }
  function m2(t5, r3) {
    var o3 = t5._data, s3 = t5._size, c3 = t5._datatype || t5.getDataType(), l3 = r3._data, u3 = r3._size, d3 = r3._datatype || r3.getDataType(), p3 = s3[0], m3 = u3[1], h3, g3 = i2, v3 = a2;
    c3 && d3 && c3 === d3 && typeof c3 == `string` && c3 !== `mixed` && (h3 = c3, g3 = n2.find(i2, [h3, h3]), v3 = n2.find(a2, [h3, h3]));
    for (var y3 = [], b3 = 0; b3 < m3; b3++) {
      for (var x3 = v3(o3[0], l3[0][b3]), S3 = 1; S3 < p3; S3++) x3 = g3(x3, v3(o3[S3], l3[S3][b3]));
      y3[b3] = x3;
    }
    return t5.createDenseMatrix({ data: y3, size: [m3], datatype: c3 === t5._datatype && d3 === r3._datatype ? h3 : void 0 });
  }
  var h2 = n2(`_multiplyMatrixVector`, { "DenseMatrix, any": v2, "SparseMatrix, any": x2 }), g2 = n2(`_multiplyMatrixMatrix`, { "DenseMatrix, DenseMatrix": y2, "DenseMatrix, SparseMatrix": b2, "SparseMatrix, DenseMatrix": S2, "SparseMatrix, SparseMatrix": w2 });
  function v2(t5, r3) {
    var o3 = t5._data, s3 = t5._size, c3 = t5._datatype || t5.getDataType(), l3 = r3._data, u3 = r3._datatype || r3.getDataType(), d3 = s3[0], p3 = s3[1], m3, h3 = i2, g3 = a2;
    c3 && u3 && c3 === u3 && typeof c3 == `string` && c3 !== `mixed` && (m3 = c3, h3 = n2.find(i2, [m3, m3]), g3 = n2.find(a2, [m3, m3]));
    for (var v3 = [], y3 = 0; y3 < d3; y3++) {
      for (var b3 = o3[y3], x3 = g3(b3[0], l3[0]), S3 = 1; S3 < p3; S3++) x3 = h3(x3, g3(b3[S3], l3[S3]));
      v3[y3] = x3;
    }
    return t5.createDenseMatrix({ data: v3, size: [d3], datatype: c3 === t5._datatype && u3 === r3._datatype ? m3 : void 0 });
  }
  function y2(t5, r3) {
    var o3 = t5._data, s3 = t5._size, c3 = t5._datatype || t5.getDataType(), l3 = r3._data, u3 = r3._size, d3 = r3._datatype || r3.getDataType(), p3 = s3[0], m3 = s3[1], h3 = u3[1], g3, v3 = i2, y3 = a2;
    c3 && d3 && c3 === d3 && typeof c3 == `string` && c3 !== `mixed` && c3 !== `mixed` && (g3 = c3, v3 = n2.find(i2, [g3, g3]), y3 = n2.find(a2, [g3, g3]));
    for (var b3 = [], x3 = 0; x3 < p3; x3++) {
      var S3 = o3[x3];
      b3[x3] = [];
      for (var w3 = 0; w3 < h3; w3++) {
        for (var E = y3(S3[0], l3[0][w3]), D = 1; D < m3; D++) E = v3(E, y3(S3[D], l3[D][w3]));
        b3[x3][w3] = E;
      }
    }
    return t5.createDenseMatrix({ data: b3, size: [p3, h3], datatype: c3 === t5._datatype && d3 === r3._datatype ? g3 : void 0 });
  }
  function b2(t5, r3) {
    var s3 = t5._data, c3 = t5._size, l3 = t5._datatype || t5.getDataType(), u3 = r3._values, d3 = r3._index, p3 = r3._ptr, m3 = r3._size, h3 = r3._datatype || r3._data === void 0 ? r3._datatype : r3.getDataType();
    if (!u3) throw Error(`Cannot multiply Dense Matrix times Pattern only Matrix`);
    var g3 = c3[0], v3 = m3[1], y3, b3 = i2, x3 = a2, S3 = o2, w3 = 0;
    l3 && h3 && l3 === h3 && typeof l3 == `string` && l3 !== `mixed` && (y3 = l3, b3 = n2.find(i2, [y3, y3]), x3 = n2.find(a2, [y3, y3]), S3 = n2.find(o2, [y3, y3]), w3 = n2.convert(0, y3));
    for (var E = [], D = [], O = [], j2 = r3.createSparseMatrix({ values: E, index: D, ptr: O, size: [g3, v3], datatype: l3 === t5._datatype && h3 === r3._datatype ? y3 : void 0 }), M2 = 0; M2 < v3; M2++) {
      O[M2] = D.length;
      var N2 = p3[M2], F = p3[M2 + 1];
      if (F > N2) for (var I = 0, L = 0; L < g3; L++) {
        for (var R = L + 1, z = void 0, V = N2; V < F; V++) {
          var H2 = d3[V];
          I === R ? z = b3(z, x3(s3[L][H2], u3[V])) : (z = x3(s3[L][H2], u3[V]), I = R);
        }
        I === R && !S3(z, w3) && (D.push(L), E.push(z));
      }
    }
    return O[v3] = D.length, j2;
  }
  function x2(t5, r3) {
    var s3 = t5._values, c3 = t5._index, l3 = t5._ptr, u3 = t5._datatype || t5._data === void 0 ? t5._datatype : t5.getDataType();
    if (!s3) throw Error(`Cannot multiply Pattern only Matrix times Dense Matrix`);
    var d3 = r3._data, p3 = r3._datatype || r3.getDataType(), m3 = t5._size[0], h3 = r3._size[0], g3 = [], v3 = [], y3 = [], b3, x3 = i2, S3 = a2, w3 = o2, E = 0;
    u3 && p3 && u3 === p3 && typeof u3 == `string` && u3 !== `mixed` && (b3 = u3, x3 = n2.find(i2, [b3, b3]), S3 = n2.find(a2, [b3, b3]), w3 = n2.find(o2, [b3, b3]), E = n2.convert(0, b3));
    var D = [], O = [];
    y3[0] = 0;
    for (var j2 = 0; j2 < h3; j2++) {
      var M2 = d3[j2];
      if (!w3(M2, E)) for (var N2 = l3[j2], F = l3[j2 + 1], I = N2; I < F; I++) {
        var L = c3[I];
        O[L] ? D[L] = x3(D[L], S3(M2, s3[I])) : (O[L] = true, v3.push(L), D[L] = S3(M2, s3[I]));
      }
    }
    for (var R = v3.length, z = 0; z < R; z++) g3[z] = D[v3[z]];
    return y3[1] = v3.length, t5.createSparseMatrix({ values: g3, index: v3, ptr: y3, size: [m3, 1], datatype: u3 === t5._datatype && p3 === r3._datatype ? b3 : void 0 });
  }
  function S2(t5, r3) {
    var s3 = t5._values, c3 = t5._index, l3 = t5._ptr, u3 = t5._datatype || t5._data === void 0 ? t5._datatype : t5.getDataType();
    if (!s3) throw Error(`Cannot multiply Pattern only Matrix times Dense Matrix`);
    var d3 = r3._data, p3 = r3._datatype || r3.getDataType(), m3 = t5._size[0], h3 = r3._size[0], g3 = r3._size[1], v3, y3 = i2, b3 = a2, x3 = o2, S3 = 0;
    u3 && p3 && u3 === p3 && typeof u3 == `string` && u3 !== `mixed` && (v3 = u3, y3 = n2.find(i2, [v3, v3]), b3 = n2.find(a2, [v3, v3]), x3 = n2.find(o2, [v3, v3]), S3 = n2.convert(0, v3));
    for (var w3 = [], E = [], D = [], O = t5.createSparseMatrix({ values: w3, index: E, ptr: D, size: [m3, g3], datatype: u3 === t5._datatype && p3 === r3._datatype ? v3 : void 0 }), j2 = [], M2 = [], N2 = 0; N2 < g3; N2++) {
      D[N2] = E.length;
      for (var F = N2 + 1, I = 0; I < h3; I++) {
        var L = d3[I][N2];
        if (!x3(L, S3)) for (var R = l3[I], z = l3[I + 1], V = R; V < z; V++) {
          var H2 = c3[V];
          M2[H2] === F ? j2[H2] = y3(j2[H2], b3(L, s3[V])) : (M2[H2] = F, E.push(H2), j2[H2] = b3(L, s3[V]));
        }
      }
      for (var U = D[N2], W = E.length, G = U; G < W; G++) w3[G] = j2[E[G]];
    }
    return D[g3] = E.length, O;
  }
  function w2(t5, r3) {
    var o3 = t5._values, s3 = t5._index, c3 = t5._ptr, l3 = t5._datatype || t5._data === void 0 ? t5._datatype : t5.getDataType(), u3 = r3._values, d3 = r3._index, p3 = r3._ptr, m3 = r3._datatype || r3._data === void 0 ? r3._datatype : r3.getDataType(), h3 = t5._size[0], g3 = r3._size[1], v3 = o3 && u3, y3, b3 = i2, x3 = a2;
    l3 && m3 && l3 === m3 && typeof l3 == `string` && l3 !== `mixed` && (y3 = l3, b3 = n2.find(i2, [y3, y3]), x3 = n2.find(a2, [y3, y3]));
    for (var S3 = v3 ? [] : void 0, w3 = [], E = [], D = t5.createSparseMatrix({ values: S3, index: w3, ptr: E, size: [h3, g3], datatype: l3 === t5._datatype && m3 === r3._datatype ? y3 : void 0 }), O = v3 ? [] : void 0, j2 = [], M2, N2, F, I, L, R, z, V, H2 = 0; H2 < g3; H2++) {
      E[H2] = w3.length;
      var U = H2 + 1;
      for (L = p3[H2], R = p3[H2 + 1], I = L; I < R; I++) if (V = d3[I], v3) for (N2 = c3[V], F = c3[V + 1], M2 = N2; M2 < F; M2++) z = s3[M2], j2[z] === U ? O[z] = b3(O[z], x3(u3[I], o3[M2])) : (j2[z] = U, w3.push(z), O[z] = x3(u3[I], o3[M2]));
      else for (N2 = c3[V], F = c3[V + 1], M2 = N2; M2 < F; M2++) z = s3[M2], j2[z] !== U && (j2[z] = U, w3.push(z));
      if (v3) for (var W = E[H2], G = w3.length, K = W; K < G; K++) S3[K] = O[w3[K]];
    }
    return E[g3] = w3.length, D;
  }
  return n2(name$258, a2, { "Array, Array": n2.referTo(`Matrix, Matrix`, (t5) => (n3, i3) => {
    u2(arraySize(n3), arraySize(i3));
    var a3 = t5(r2(n3), r2(i3));
    return isMatrix(a3) ? a3.valueOf() : a3;
  }), "Matrix, Matrix": function(t5, n3) {
    var r3 = t5.size(), i3 = n3.size();
    return u2(r3, i3), r3.length === 1 ? i3.length === 1 ? d2(t5, n3, r3[0]) : p2(t5, n3) : i3.length === 1 ? h2(t5, n3) : g2(t5, n3);
  }, "Matrix, Array": n2.referTo(`Matrix,Matrix`, (t5) => (n3, i3) => t5(n3, r2(i3))), "Array, Matrix": n2.referToSelf((t5) => (n3, i3) => t5(r2(n3, i3.storage()), i3)), "SparseMatrix, any": function(t5, n3) {
    return c2(t5, n3, a2, false);
  }, "DenseMatrix, any": function(t5, n3) {
    return l2(t5, n3, a2, false);
  }, "any, SparseMatrix": function(t5, n3) {
    return c2(n3, t5, a2, true);
  }, "any, DenseMatrix": function(t5, n3) {
    return l2(n3, t5, a2, true);
  }, "Array, any": function(t5, n3) {
    return l2(r2(t5), n3, a2, false).valueOf();
  }, "any, Array": function(t5, n3) {
    return l2(r2(n3), t5, a2, true).valueOf();
  }, "any, any": a2, "any, any, ...any": n2.referToSelf((t5) => (n3, r3, i3) => {
    for (var a3 = t5(n3, r3), o3 = 0; o3 < i3.length; o3++) a3 = t5(a3, i3[o3]);
    return a3;
  }) });
}), name$257 = `nthRoot`, dependencies$257 = [`typed`, `matrix`, `equalScalar`, `BigNumber`, `concat`], createNthRoot = factory(name$257, dependencies$257, (t4) => {
  var { typed: n2, matrix: r2, equalScalar: i2, BigNumber: a2, concat: o2 } = t4, s2 = createMatAlgo01xDSid({ typed: n2 }), c2 = createMatAlgo02xDS0({ typed: n2, equalScalar: i2 }), l2 = createMatAlgo06xS0S0({ typed: n2, equalScalar: i2 }), u2 = createMatAlgo11xS0s({ typed: n2, equalScalar: i2 }), d2 = createMatrixAlgorithmSuite({ typed: n2, matrix: r2, concat: o2 });
  function p2() {
    throw Error(`Complex number not supported in function nthRoot. Use nthRoots instead.`);
  }
  return n2(name$257, { number: nthRootNumber, "number, number": nthRootNumber, BigNumber: (t5) => m2(t5, new a2(2)), "BigNumber, BigNumber": m2, Complex: p2, "Complex, number": p2, Array: n2.referTo(`DenseMatrix,number`, (t5) => (n3) => t5(r2(n3), 2).valueOf()), DenseMatrix: n2.referTo(`DenseMatrix,number`, (t5) => (n3) => t5(n3, 2)), SparseMatrix: n2.referTo(`SparseMatrix,number`, (t5) => (n3) => t5(n3, 2)), "SparseMatrix, SparseMatrix": n2.referToSelf((t5) => (n3, r3) => {
    if (r3.density() === 1) return l2(n3, r3, t5);
    throw Error(`Root must be non-zero`);
  }), "DenseMatrix, SparseMatrix": n2.referToSelf((t5) => (n3, r3) => {
    if (r3.density() === 1) return s2(n3, r3, t5, false);
    throw Error(`Root must be non-zero`);
  }), "Array, SparseMatrix": n2.referTo(`DenseMatrix,SparseMatrix`, (t5) => (n3, i3) => t5(r2(n3), i3)), "number | BigNumber, SparseMatrix": n2.referToSelf((t5) => (n3, r3) => {
    if (r3.density() === 1) return u2(r3, n3, t5, true);
    throw Error(`Root must be non-zero`);
  }) }, d2({ scalar: `number | BigNumber`, SD: c2, Ss: u2, sS: false }));
  function m2(t5, n3) {
    var r3 = a2.precision, i3 = a2.clone({ precision: r3 + 2 }), o3 = new a2(0), s3 = new i3(1), c3 = n3.isNegative();
    if (c3 && (n3 = n3.neg()), n3.isZero()) throw Error(`Root must be non-zero`);
    if (t5.isNegative() && !n3.abs().mod(2).equals(1)) throw Error(`Root must be odd when a is negative.`);
    if (t5.isZero()) return c3 ? new i3(1 / 0) : 0;
    if (!t5.isFinite()) return c3 ? o3 : t5;
    var l3 = t5.abs().pow(s3.div(n3));
    return l3 = t5.isNeg() ? l3.neg() : l3, new a2((c3 ? s3.div(l3) : l3).toPrecision(r3));
  }
}), name$256 = `sign`, dependencies$256 = [`typed`, `BigNumber`, `Fraction`, `complex`], createSign = factory(name$256, dependencies$256, (t4) => {
  var { typed: n2, BigNumber: r2, complex: i2, Fraction: a2 } = t4;
  return n2(name$256, { number: signNumber, Complex: function(t5) {
    return t5.im === 0 ? i2(signNumber(t5.re)) : t5.sign();
  }, BigNumber: function(t5) {
    return new r2(t5.cmp(0));
  }, bigint: function(t5) {
    return t5 > 0n ? 1n : t5 < 0n ? -1n : 0n;
  }, Fraction: function(t5) {
    return t5.n === 0n ? new a2(0) : new a2(t5.s);
  }, "Array | Matrix": n2.referToSelf((t5) => (n3) => deepMap(n3, t5, true)), Unit: n2.referToSelf((t5) => (r3) => {
    if (!r3._isDerived() && r3.units[0].unit.offset !== 0) throw TypeError(`sign is ambiguous for units with offset`);
    return n2.find(t5, r3.valueType())(r3.value);
  }) });
}), name$255 = `sqrt`, dependencies$255 = [`config`, `typed`, `Complex`], createSqrt = factory(name$255, dependencies$255, (t4) => {
  var { config: n2, typed: r2, Complex: i2 } = t4;
  return r2(`sqrt`, { number: a2, Complex: function(t5) {
    return t5.sqrt();
  }, BigNumber: function(t5) {
    return !t5.isNegative() || n2.predictable ? t5.sqrt() : a2(t5.toNumber());
  }, Unit: function(t5) {
    return t5.pow(0.5);
  } });
  function a2(t5) {
    return isNaN(t5) ? NaN : t5 >= 0 || n2.predictable ? Math.sqrt(t5) : new i2(t5, 0).sqrt();
  }
}), name$254 = `square`, dependencies$254 = [`typed`], createSquare = factory(name$254, dependencies$254, (t4) => {
  var { typed: n2 } = t4;
  return n2(name$254, { number: squareNumber, Complex: function(t5) {
    return t5.mul(t5);
  }, BigNumber: function(t5) {
    return t5.times(t5);
  }, bigint: function(t5) {
    return t5 * t5;
  }, Fraction: function(t5) {
    return t5.mul(t5);
  }, Unit: function(t5) {
    return t5.pow(2);
  } });
}), name$253 = `subtract`, dependencies$253 = [`typed`, `matrix`, `equalScalar`, `subtractScalar`, `unaryMinus`, `DenseMatrix`, `concat`], createSubtract = factory(name$253, dependencies$253, (t4) => {
  var { typed: n2, matrix: r2, equalScalar: i2, subtractScalar: a2, unaryMinus: o2, DenseMatrix: s2, concat: c2 } = t4, l2 = createMatAlgo01xDSid({ typed: n2 }), u2 = createMatAlgo03xDSf({ typed: n2 }), d2 = createMatAlgo05xSfSf({ typed: n2, equalScalar: i2 }), p2 = createMatAlgo10xSids({ typed: n2, DenseMatrix: s2 }), m2 = createMatAlgo12xSfs({ typed: n2, DenseMatrix: s2 }), h2 = createMatrixAlgorithmSuite({ typed: n2, matrix: r2, concat: c2 });
  return n2(name$253, { "any, any": a2 }, h2({ elop: a2, SS: d2, DS: l2, SD: u2, Ss: m2, sS: p2 }));
}), name$252 = `xgcd`, dependencies$252 = [`typed`, `config`, `matrix`, `BigNumber`], createXgcd = factory(name$252, dependencies$252, (t4) => {
  var { typed: n2, config: r2, matrix: i2, BigNumber: a2 } = t4;
  return n2(name$252, { "number, number": function(t5, n3) {
    var a3 = xgcdNumber(t5, n3);
    return r2.matrix === `Array` ? a3 : i2(a3);
  }, "BigNumber, BigNumber": o2 });
  function o2(t5, n3) {
    var o3, s2, c2, l2 = new a2(0), u2 = new a2(1), d2 = l2, p2 = u2, m2 = u2, h2 = l2;
    if (!t5.isInt() || !n3.isInt()) throw Error(`Parameters in function xgcd must be integer numbers`);
    for (; !n3.isZero(); ) s2 = t5.div(n3).floor(), c2 = t5.mod(n3), o3 = d2, d2 = p2.minus(s2.times(d2)), p2 = o3, o3 = m2, m2 = h2.minus(s2.times(m2)), h2 = o3, t5 = n3, n3 = c2;
    var g2 = t5.lt(l2) ? [t5.neg(), p2.neg(), h2.neg()] : [t5, t5.isZero() ? 0 : p2, h2];
    return r2.matrix === `Array` ? g2 : i2(g2);
  }
}), name$251 = `invmod`, dependencies$251 = [`typed`, `config`, `BigNumber`, `xgcd`, `equal`, `smaller`, `mod`, `add`, `isInteger`], createInvmod = factory(name$251, dependencies$251, (t4) => {
  var { typed: n2, config: r2, BigNumber: i2, xgcd: a2, equal: o2, smaller: s2, mod: c2, add: l2, isInteger: u2 } = t4;
  return n2(name$251, { "number, number": d2, "BigNumber, BigNumber": d2 });
  function d2(t5, n3) {
    if (!u2(t5) || !u2(n3)) throw Error(`Parameters in function invmod must be integer numbers`);
    if (t5 = c2(t5, n3), o2(n3, 0)) throw Error(`Divisor must be non zero`);
    var r3 = a2(t5, n3);
    r3 = r3.valueOf();
    var [d3, p2] = r3;
    return o2(d3, i2(1)) ? (p2 = c2(p2, n3), s2(p2, i2(0)) && (p2 = l2(p2, n3)), p2) : NaN;
  }
}), name$250 = `matAlgo09xS0Sf`, dependencies$250 = [`typed`, `equalScalar`], createMatAlgo09xS0Sf = factory(name$250, dependencies$250, (t4) => {
  var { typed: n2, equalScalar: r2 } = t4;
  return function(t5, i2, a2) {
    var o2 = t5._values, s2 = t5._index, c2 = t5._ptr, l2 = t5._size, u2 = t5._datatype || t5._data === void 0 ? t5._datatype : t5.getDataType(), d2 = i2._values, p2 = i2._index, m2 = i2._ptr, h2 = i2._size, g2 = i2._datatype || i2._data === void 0 ? i2._datatype : i2.getDataType();
    if (l2.length !== h2.length) throw new DimensionError(l2.length, h2.length);
    if (l2[0] !== h2[0] || l2[1] !== h2[1]) throw RangeError(`Dimension mismatch. Matrix A (` + l2 + `) must match Matrix B (` + h2 + `)`);
    var v2 = l2[0], y2 = l2[1], b2, x2 = r2, S2 = 0, w2 = a2;
    typeof u2 == `string` && u2 === g2 && u2 !== `mixed` && (b2 = u2, x2 = n2.find(r2, [b2, b2]), S2 = n2.convert(0, b2), w2 = n2.find(a2, [b2, b2]));
    var E = o2 && d2 ? [] : void 0, D = [], O = [], j2 = E ? [] : void 0, M2 = [], N2, F, I, L, R;
    for (F = 0; F < y2; F++) {
      O[F] = D.length;
      var z = F + 1;
      if (j2) for (L = m2[F], R = m2[F + 1], I = L; I < R; I++) N2 = p2[I], M2[N2] = z, j2[N2] = d2[I];
      for (L = c2[F], R = c2[F + 1], I = L; I < R; I++) if (N2 = s2[I], j2) {
        var V = M2[N2] === z ? j2[N2] : S2, H2 = w2(o2[I], V);
        x2(H2, S2) || (D.push(N2), E.push(H2));
      } else D.push(N2);
    }
    return O[y2] = D.length, t5.createSparseMatrix({ values: E, index: D, ptr: O, size: [v2, y2], datatype: u2 === t5._datatype && g2 === i2._datatype ? b2 : void 0 });
  };
}), name$249 = `dotMultiply`, dependencies$249 = [`typed`, `matrix`, `equalScalar`, `multiplyScalar`, `concat`], createDotMultiply = factory(name$249, dependencies$249, (t4) => {
  var { typed: n2, matrix: r2, equalScalar: i2, multiplyScalar: a2, concat: o2 } = t4, s2 = createMatAlgo02xDS0({ typed: n2, equalScalar: i2 }), c2 = createMatAlgo09xS0Sf({ typed: n2, equalScalar: i2 }), l2 = createMatAlgo11xS0s({ typed: n2, equalScalar: i2 });
  return n2(name$249, createMatrixAlgorithmSuite({ typed: n2, matrix: r2, concat: o2 })({ elop: a2, SS: c2, DS: s2, Ss: l2 }));
});
function bitAndBigNumber(t4, n2) {
  if (t4.isFinite() && !t4.isInteger() || n2.isFinite() && !n2.isInteger()) throw Error(`Integers expected in function bitAnd`);
  var r2 = t4.constructor;
  if (t4.isNaN() || n2.isNaN()) return new r2(NaN);
  if (t4.isZero() || n2.eq(-1) || t4.eq(n2)) return t4;
  if (n2.isZero() || t4.eq(-1)) return n2;
  if (!t4.isFinite() || !n2.isFinite()) {
    if (!t4.isFinite() && !n2.isFinite()) return t4.isNegative() === n2.isNegative() ? t4 : new r2(0);
    if (!t4.isFinite()) return n2.isNegative() ? t4 : t4.isNegative() ? new r2(0) : n2;
    if (!n2.isFinite()) return t4.isNegative() ? n2 : n2.isNegative() ? new r2(0) : t4;
  }
  return bitwise(t4, n2, function(t5, n3) {
    return t5 & n3;
  });
}
function bitNotBigNumber(t4) {
  if (t4.isFinite() && !t4.isInteger()) throw Error(`Integer expected in function bitNot`);
  var n2 = t4.constructor, r2 = n2.precision;
  n2.config({ precision: 1e9 });
  var i2 = t4.plus(new n2(1));
  return i2.s = -i2.s || null, n2.config({ precision: r2 }), i2;
}
function bitOrBigNumber(t4, n2) {
  if (t4.isFinite() && !t4.isInteger() || n2.isFinite() && !n2.isInteger()) throw Error(`Integers expected in function bitOr`);
  var r2 = t4.constructor;
  if (t4.isNaN() || n2.isNaN()) return new r2(NaN);
  var i2 = new r2(-1);
  return t4.isZero() || n2.eq(i2) || t4.eq(n2) ? n2 : n2.isZero() || t4.eq(i2) ? t4 : !t4.isFinite() || !n2.isFinite() ? !t4.isFinite() && !t4.isNegative() && n2.isNegative() || t4.isNegative() && !n2.isNegative() && !n2.isFinite() ? i2 : t4.isNegative() && n2.isNegative() ? t4.isFinite() ? t4 : n2 : t4.isFinite() ? n2 : t4 : bitwise(t4, n2, function(t5, n3) {
    return t5 | n3;
  });
}
function bitwise(t4, n2, r2) {
  var i2 = t4.constructor, a2, o2, s2 = +(t4.s < 0), c2 = +(n2.s < 0);
  if (s2) {
    a2 = decCoefficientToBinaryString(bitNotBigNumber(t4));
    for (var l2 = 0; l2 < a2.length; ++l2) a2[l2] ^= 1;
  } else a2 = decCoefficientToBinaryString(t4);
  if (c2) {
    o2 = decCoefficientToBinaryString(bitNotBigNumber(n2));
    for (var u2 = 0; u2 < o2.length; ++u2) o2[u2] ^= 1;
  } else o2 = decCoefficientToBinaryString(n2);
  var d2, p2, m2;
  a2.length <= o2.length ? (d2 = a2, p2 = o2, m2 = s2) : (d2 = o2, p2 = a2, m2 = c2);
  var h2 = d2.length, g2 = p2.length, v2 = r2(s2, c2) ^ 1, y2 = new i2(v2 ^ 1), b2 = new i2(1), x2 = new i2(2), S2 = i2.precision;
  for (i2.config({ precision: 1e9 }); h2 > 0; ) r2(d2[--h2], p2[--g2]) === v2 && (y2 = y2.plus(b2)), b2 = b2.times(x2);
  for (; g2 > 0; ) r2(m2, p2[--g2]) === v2 && (y2 = y2.plus(b2)), b2 = b2.times(x2);
  return i2.config({ precision: S2 }), v2 === 0 && (y2.s = -y2.s), y2;
}
function decCoefficientToBinaryString(t4) {
  for (var n2 = t4.d, r2 = n2[0] + ``, i2 = 1; i2 < n2.length; ++i2) {
    for (var a2 = n2[i2] + ``, o2 = 7 - a2.length; o2--; ) a2 = `0` + a2;
    r2 += a2;
  }
  for (var s2 = r2.length; r2.charAt(s2) === `0`; ) s2--;
  var c2 = t4.e, l2 = r2.slice(0, s2 + 1 || 1), u2 = l2.length;
  if (c2 > 0) if (++c2 > u2) for (c2 -= u2; c2--; ) l2 += `0`;
  else c2 < u2 && (l2 = l2.slice(0, c2) + `.` + l2.slice(c2));
  for (var d2 = [0], p2 = 0; p2 < l2.length; ) {
    for (var m2 = d2.length; m2--; ) d2[m2] *= 10;
    d2[0] += parseInt(l2.charAt(p2++));
    for (var h2 = 0; h2 < d2.length; ++h2) d2[h2] > 1 && ((d2[h2 + 1] === null || d2[h2 + 1] === void 0) && (d2[h2 + 1] = 0), d2[h2 + 1] += d2[h2] >> 1, d2[h2] &= 1);
  }
  return d2.reverse();
}
function bitXor(t4, n2) {
  if (t4.isFinite() && !t4.isInteger() || n2.isFinite() && !n2.isInteger()) throw Error(`Integers expected in function bitXor`);
  var r2 = t4.constructor;
  if (t4.isNaN() || n2.isNaN()) return new r2(NaN);
  if (t4.isZero()) return n2;
  if (n2.isZero()) return t4;
  if (t4.eq(n2)) return new r2(0);
  var i2 = new r2(-1);
  return t4.eq(i2) ? bitNotBigNumber(n2) : n2.eq(i2) ? bitNotBigNumber(t4) : !t4.isFinite() || !n2.isFinite() ? !t4.isFinite() && !n2.isFinite() ? i2 : new r2(t4.isNegative() === n2.isNegative() ? 1 / 0 : -1 / 0) : bitwise(t4, n2, function(t5, n3) {
    return t5 ^ n3;
  });
}
function leftShiftBigNumber(t4, n2) {
  if (t4.isFinite() && !t4.isInteger() || n2.isFinite() && !n2.isInteger()) throw Error(`Integers expected in function leftShift`);
  var r2 = t4.constructor;
  return t4.isNaN() || n2.isNaN() || n2.isNegative() && !n2.isZero() ? new r2(NaN) : t4.isZero() || n2.isZero() ? t4 : !t4.isFinite() && !n2.isFinite() ? new r2(NaN) : n2.lt(55) ? t4.times(2 ** n2.toNumber() + ``) : t4.times(new r2(2).pow(n2));
}
function rightArithShiftBigNumber(t4, n2) {
  if (t4.isFinite() && !t4.isInteger() || n2.isFinite() && !n2.isInteger()) throw Error(`Integers expected in function rightArithShift`);
  var r2 = t4.constructor;
  return t4.isNaN() || n2.isNaN() || n2.isNegative() && !n2.isZero() ? new r2(NaN) : t4.isZero() || n2.isZero() ? t4 : n2.isFinite() ? n2.lt(55) ? t4.div(2 ** n2.toNumber() + ``).floor() : t4.div(new r2(2).pow(n2)).floor() : t4.isNegative() ? new r2(-1) : t4.isFinite() ? new r2(0) : new r2(NaN);
}
var name$248 = `bitAnd`, dependencies$248 = [`typed`, `matrix`, `equalScalar`, `concat`], createBitAnd = factory(name$248, dependencies$248, (t4) => {
  var { typed: n2, matrix: r2, equalScalar: i2, concat: a2 } = t4, o2 = createMatAlgo02xDS0({ typed: n2, equalScalar: i2 }), s2 = createMatAlgo06xS0S0({ typed: n2, equalScalar: i2 }), c2 = createMatAlgo11xS0s({ typed: n2, equalScalar: i2 }), l2 = createMatrixAlgorithmSuite({ typed: n2, matrix: r2, concat: a2 });
  return n2(name$248, { "number, number": bitAndNumber, "BigNumber, BigNumber": bitAndBigNumber, "bigint, bigint": (t5, n3) => t5 & n3 }, l2({ SS: s2, DS: o2, Ss: c2 }));
}), name$247 = `bitNot`, dependencies$247 = [`typed`], createBitNot = factory(name$247, dependencies$247, (t4) => {
  var { typed: n2 } = t4;
  return n2(name$247, { number: bitNotNumber, BigNumber: bitNotBigNumber, bigint: (t5) => ~t5, "Array | Matrix": n2.referToSelf((t5) => (n3) => deepMap(n3, t5)) });
}), name$246 = `bitOr`, dependencies$246 = [`typed`, `matrix`, `equalScalar`, `DenseMatrix`, `concat`], createBitOr = factory(name$246, dependencies$246, (t4) => {
  var { typed: n2, matrix: r2, equalScalar: i2, DenseMatrix: a2, concat: o2 } = t4, s2 = createMatAlgo01xDSid({ typed: n2 }), c2 = createMatAlgo04xSidSid({ typed: n2, equalScalar: i2 }), l2 = createMatAlgo10xSids({ typed: n2, DenseMatrix: a2 }), u2 = createMatrixAlgorithmSuite({ typed: n2, matrix: r2, concat: o2 });
  return n2(name$246, { "number, number": bitOrNumber, "BigNumber, BigNumber": bitOrBigNumber, "bigint, bigint": (t5, n3) => t5 | n3 }, u2({ SS: c2, DS: s2, Ss: l2 }));
}), name$245 = `matAlgo07xSSf`, dependencies$245 = [`typed`, `SparseMatrix`], createMatAlgo07xSSf = factory(name$245, dependencies$245, (t4) => {
  var { typed: n2, SparseMatrix: r2 } = t4;
  return function(t5, a2, o2) {
    var s2 = t5._size, c2 = t5._datatype || t5._data === void 0 ? t5._datatype : t5.getDataType(), l2 = a2._size, u2 = a2._datatype || a2._data === void 0 ? a2._datatype : a2.getDataType();
    if (s2.length !== l2.length) throw new DimensionError(s2.length, l2.length);
    if (s2[0] !== l2[0] || s2[1] !== l2[1]) throw RangeError(`Dimension mismatch. Matrix A (` + s2 + `) must match Matrix B (` + l2 + `)`);
    var d2 = s2[0], p2 = s2[1], m2, h2 = 0, g2 = o2;
    typeof c2 == `string` && c2 === u2 && c2 !== `mixed` && (m2 = c2, h2 = n2.convert(0, m2), g2 = n2.find(o2, [m2, m2]));
    for (var v2 = [], y2 = [], b2 = Array(p2 + 1).fill(0), x2 = [], S2 = [], w2 = [], E = [], D = 0; D < p2; D++) {
      var O = D + 1, j2 = 0;
      i2(t5, D, w2, x2, O), i2(a2, D, E, S2, O);
      for (var M2 = 0; M2 < d2; M2++) {
        var N2 = w2[M2] === O ? x2[M2] : h2, F = E[M2] === O ? S2[M2] : h2, I = g2(N2, F);
        I !== 0 && I !== false && (y2.push(M2), v2.push(I), j2++);
      }
      b2[D + 1] = b2[D] + j2;
    }
    return new r2({ values: v2, index: y2, ptr: b2, size: [d2, p2], datatype: c2 === t5._datatype && u2 === a2._datatype ? m2 : void 0 });
  };
  function i2(t5, n3, r3, i3, a2) {
    for (var o2 = t5._values, s2 = t5._index, c2 = t5._ptr, l2 = c2[n3], u2 = c2[n3 + 1]; l2 < u2; l2++) {
      var d2 = s2[l2];
      r3[d2] = a2, i3[d2] = o2[l2];
    }
  }
}), name$244 = `bitXor`, dependencies$244 = [`typed`, `matrix`, `DenseMatrix`, `concat`, `SparseMatrix`], createBitXor = factory(name$244, dependencies$244, (t4) => {
  var { typed: n2, matrix: r2, DenseMatrix: i2, concat: a2, SparseMatrix: o2 } = t4, s2 = createMatAlgo03xDSf({ typed: n2 }), c2 = createMatAlgo07xSSf({ typed: n2, SparseMatrix: o2 }), l2 = createMatAlgo12xSfs({ typed: n2, DenseMatrix: i2 }), u2 = createMatrixAlgorithmSuite({ typed: n2, matrix: r2, concat: a2 });
  return n2(name$244, { "number, number": bitXorNumber, "BigNumber, BigNumber": bitXor, "bigint, bigint": (t5, n3) => t5 ^ n3 }, u2({ SS: c2, DS: s2, Ss: l2 }));
}), name$243 = `arg`, dependencies$243 = [`typed`], createArg = factory(name$243, dependencies$243, (t4) => {
  var { typed: n2 } = t4;
  return n2(name$243, { number: function(t5) {
    return Math.atan2(0, t5);
  }, BigNumber: function(t5) {
    return t5.constructor.atan2(0, t5);
  }, Complex: function(t5) {
    return t5.arg();
  }, "Array | Matrix": n2.referToSelf((t5) => (n3) => deepMap(n3, t5)) });
}), name$242 = `conj`, dependencies$242 = [`typed`], createConj = factory(name$242, dependencies$242, (t4) => {
  var { typed: n2 } = t4;
  return n2(name$242, { "number | BigNumber | Fraction": (t5) => t5, Complex: (t5) => t5.conjugate(), Unit: n2.referToSelf((t5) => (n3) => new n3.constructor(t5(n3.toNumeric()), n3.formatUnits())), "Array | Matrix": n2.referToSelf((t5) => (n3) => deepMap(n3, t5)) });
}), name$241 = `im`, dependencies$241 = [`typed`], createIm = factory(name$241, dependencies$241, (t4) => {
  var { typed: n2 } = t4;
  return n2(name$241, { number: () => 0, "BigNumber | Fraction": (t5) => t5.mul(0), Complex: (t5) => t5.im, "Array | Matrix": n2.referToSelf((t5) => (n3) => deepMap(n3, t5)) });
}), name$240 = `re`, dependencies$240 = [`typed`], createRe = factory(name$240, dependencies$240, (t4) => {
  var { typed: n2 } = t4;
  return n2(name$240, { "number | BigNumber | Fraction": (t5) => t5, Complex: (t5) => t5.re, "Array | Matrix": n2.referToSelf((t5) => (n3) => deepMap(n3, t5)) });
}), name$239 = `not`, dependencies$239 = [`typed`], createNot = factory(name$239, dependencies$239, (t4) => {
  var { typed: n2 } = t4;
  return n2(name$239, { "null | undefined": () => true, number: notNumber, Complex: function(t5) {
    return t5.re === 0 && t5.im === 0;
  }, BigNumber: function(t5) {
    return t5.isZero() || t5.isNaN();
  }, bigint: (t5) => !t5, Unit: n2.referToSelf((t5) => (r2) => n2.find(t5, r2.valueType())(r2.value)), "Array | Matrix": n2.referToSelf((t5) => (n3) => deepMap(n3, t5)) });
}), name$238 = `nullish`, dependencies$238 = [`typed`, `matrix`, `size`, `flatten`, `deepEqual`], createNullish = factory(name$238, dependencies$238, (t4) => {
  var { typed: n2, matrix: r2, size: i2, flatten: a2, deepEqual: o2 } = t4, s2 = createMatAlgo03xDSf({ typed: n2 }), c2 = createMatAlgo14xDs({ typed: n2 }), l2 = createMatAlgo13xDD({ typed: n2 });
  return n2(name$238, { "number|bigint|Complex|BigNumber|Fraction|Unit|string|boolean|SparseMatrix, any": (t5, n3) => t5, "null, any": (t5, n3) => n3, "undefined, any": (t5, n3) => n3, "SparseMatrix, Array | Matrix": (t5, n3) => {
    var r3 = i2(t5), a3 = i2(n3);
    if (o2(r3, a3)) return t5;
    throw new DimensionError(r3, a3);
  }, "DenseMatrix, DenseMatrix": n2.referToSelf((t5) => (n3, r3) => l2(n3, r3, t5)), "DenseMatrix, SparseMatrix": n2.referToSelf((t5) => (n3, r3) => s2(n3, r3, t5, false)), "DenseMatrix, Array": n2.referToSelf((t5) => (n3, i3) => l2(n3, r2(i3), t5)), "DenseMatrix, any": n2.referToSelf((t5) => (n3, r3) => c2(n3, r3, t5, false)), "Array, Array": n2.referToSelf((t5) => (n3, i3) => l2(r2(n3), r2(i3), t5).valueOf()), "Array, DenseMatrix": n2.referToSelf((t5) => (n3, i3) => l2(r2(n3), i3, t5)), "Array, SparseMatrix": n2.referToSelf((t5) => (n3, i3) => s2(r2(n3), i3, t5, false)), "Array, any": n2.referToSelf((t5) => (n3, i3) => c2(r2(n3), i3, t5, false).valueOf()) });
}), name$237 = `or`, dependencies$237 = [`typed`, `matrix`, `equalScalar`, `DenseMatrix`, `concat`], createOr = factory(name$237, dependencies$237, (t4) => {
  var { typed: n2, matrix: r2, equalScalar: i2, DenseMatrix: a2, concat: o2 } = t4, s2 = createMatAlgo03xDSf({ typed: n2 }), c2 = createMatAlgo05xSfSf({ typed: n2, equalScalar: i2 }), l2 = createMatAlgo12xSfs({ typed: n2, DenseMatrix: a2 }), u2 = createMatrixAlgorithmSuite({ typed: n2, matrix: r2, concat: o2 });
  return n2(name$237, { "number, number": orNumber, "Complex, Complex": function(t5, n3) {
    return t5.re !== 0 || t5.im !== 0 || n3.re !== 0 || n3.im !== 0;
  }, "BigNumber, BigNumber": function(t5, n3) {
    return !t5.isZero() && !t5.isNaN() || !n3.isZero() && !n3.isNaN();
  }, "bigint, bigint": orNumber, "Unit, Unit": n2.referToSelf((t5) => (n3, r3) => t5(n3.value || 0, r3.value || 0)) }, u2({ SS: c2, DS: s2, Ss: l2 }));
}), name$236 = `xor`, dependencies$236 = [`typed`, `matrix`, `DenseMatrix`, `concat`, `SparseMatrix`], createXor = factory(name$236, dependencies$236, (t4) => {
  var { typed: n2, matrix: r2, DenseMatrix: i2, concat: a2, SparseMatrix: o2 } = t4, s2 = createMatAlgo03xDSf({ typed: n2 }), c2 = createMatAlgo07xSSf({ typed: n2, SparseMatrix: o2 }), l2 = createMatAlgo12xSfs({ typed: n2, DenseMatrix: i2 }), u2 = createMatrixAlgorithmSuite({ typed: n2, matrix: r2, concat: a2 });
  return n2(name$236, { "number, number": xorNumber, "Complex, Complex": function(t5, n3) {
    return (t5.re !== 0 || t5.im !== 0) != (n3.re !== 0 || n3.im !== 0);
  }, "bigint, bigint": xorNumber, "BigNumber, BigNumber": function(t5, n3) {
    return (!t5.isZero() && !t5.isNaN()) != (!n3.isZero() && !n3.isNaN());
  }, "Unit, Unit": n2.referToSelf((t5) => (n3, r3) => t5(n3.value || 0, r3.value || 0)) }, u2({ SS: c2, DS: s2, Ss: l2 }));
}), name$235 = `concat`, dependencies$235 = [`typed`, `matrix`, `isInteger`], createConcat = factory(name$235, dependencies$235, (t4) => {
  var { typed: n2, matrix: r2, isInteger: i2 } = t4;
  return n2(name$235, { "...Array | Matrix | number | BigNumber": function(t5) {
    var n3, a2 = t5.length, o2 = -1, s2, c2 = false, l2 = [];
    for (n3 = 0; n3 < a2; n3++) {
      var u2 = t5[n3];
      if (isMatrix(u2) && (c2 = true), isNumber(u2) || isBigNumber(u2)) {
        if (n3 !== a2 - 1) throw Error(`Dimension must be specified as last argument`);
        if (s2 = o2, o2 = u2.valueOf(), !i2(o2)) throw TypeError(`Integer number expected for dimension`);
        if (o2 < 0 || n3 > 0 && o2 > s2) throw new IndexError(o2, s2 + 1);
      } else {
        var d2 = clone$3(u2).valueOf(), p2 = arraySize(d2);
        if (l2[n3] = d2, s2 = o2, o2 = p2.length - 1, n3 > 0 && o2 !== s2) throw new DimensionError(s2 + 1, o2 + 1);
      }
    }
    if (l2.length === 0) throw SyntaxError(`At least one matrix expected`);
    for (var m2 = l2.shift(); l2.length; ) m2 = concat(m2, l2.shift(), o2);
    return c2 ? r2(m2) : m2;
  }, "...string": function(t5) {
    return t5.join(``);
  } });
}), name$234 = `column`, dependencies$234 = [`typed`, `Index`, `matrix`, `range`], createColumn = factory(name$234, dependencies$234, (t4) => {
  var { typed: n2, Index: r2, matrix: i2, range: a2 } = t4;
  return n2(name$234, { "Matrix, number": o2, "Array, number": function(t5, n3) {
    return o2(i2(clone$3(t5)), n3).valueOf();
  } });
  function o2(t5, n3) {
    if (t5.size().length !== 2) throw Error(`Only two dimensional matrix is supported`);
    validateIndex(n3, t5.size()[1]);
    var o3 = new r2(a2(0, t5.size()[0]), [n3]), s2 = t5.subset(o3);
    return isMatrix(s2) ? s2 : i2([[s2]]);
  }
}), name$233 = `count`, dependencies$233 = [`typed`, `size`, `prod`], createCount = factory(name$233, dependencies$233, (t4) => {
  var { typed: n2, size: r2, prod: i2 } = t4;
  return n2(name$233, { string: function(t5) {
    return t5.length;
  }, "Matrix | Array": function(t5) {
    return i2(r2(t5));
  } });
}), name$232 = `cross`, dependencies$232 = [`typed`, `matrix`, `subtract`, `multiply`], createCross = factory(name$232, dependencies$232, (t4) => {
  var { typed: n2, matrix: r2, subtract: i2, multiply: a2 } = t4;
  return n2(name$232, { "Matrix, Matrix": function(t5, n3) {
    return r2(o2(t5.toArray(), n3.toArray()));
  }, "Matrix, Array": function(t5, n3) {
    return r2(o2(t5.toArray(), n3));
  }, "Array, Matrix": function(t5, n3) {
    return r2(o2(t5, n3.toArray()));
  }, "Array, Array": o2 });
  function o2(t5, n3) {
    var r3 = Math.max(arraySize(t5).length, arraySize(n3).length);
    t5 = squeeze(t5), n3 = squeeze(n3);
    var o3 = arraySize(t5), s2 = arraySize(n3);
    if (o3.length !== 1 || s2.length !== 1 || o3[0] !== 3 || s2[0] !== 3) throw RangeError(`Vectors with length 3 expected (Size A = [` + o3.join(`, `) + `], B = [` + s2.join(`, `) + `])`);
    var c2 = [i2(a2(t5[1], n3[2]), a2(t5[2], n3[1])), i2(a2(t5[2], n3[0]), a2(t5[0], n3[2])), i2(a2(t5[0], n3[1]), a2(t5[1], n3[0]))];
    return r3 > 1 ? [c2] : c2;
  }
}), name$231 = `diag`, dependencies$231 = [`typed`, `matrix`, `DenseMatrix`, `SparseMatrix`], createDiag = factory(name$231, dependencies$231, (t4) => {
  var { typed: n2, matrix: r2, DenseMatrix: i2, SparseMatrix: a2 } = t4;
  return n2(name$231, { Array: function(t5) {
    return o2(t5, 0, arraySize(t5), null);
  }, "Array, number": function(t5, n3) {
    return o2(t5, n3, arraySize(t5), null);
  }, "Array, BigNumber": function(t5, n3) {
    return o2(t5, n3.toNumber(), arraySize(t5), null);
  }, "Array, string": function(t5, n3) {
    return o2(t5, 0, arraySize(t5), n3);
  }, "Array, number, string": function(t5, n3, r3) {
    return o2(t5, n3, arraySize(t5), r3);
  }, "Array, BigNumber, string": function(t5, n3, r3) {
    return o2(t5, n3.toNumber(), arraySize(t5), r3);
  }, Matrix: function(t5) {
    return o2(t5, 0, t5.size(), t5.storage());
  }, "Matrix, number": function(t5, n3) {
    return o2(t5, n3, t5.size(), t5.storage());
  }, "Matrix, BigNumber": function(t5, n3) {
    return o2(t5, n3.toNumber(), t5.size(), t5.storage());
  }, "Matrix, string": function(t5, n3) {
    return o2(t5, 0, t5.size(), n3);
  }, "Matrix, number, string": function(t5, n3, r3) {
    return o2(t5, n3, t5.size(), r3);
  }, "Matrix, BigNumber, string": function(t5, n3, r3) {
    return o2(t5, n3.toNumber(), t5.size(), r3);
  } });
  function o2(t5, n3, r3, i3) {
    if (!isInteger(n3)) throw TypeError(`Second parameter in function diag must be an integer`);
    var a3 = n3 > 0 ? n3 : 0, o3 = n3 < 0 ? -n3 : 0;
    switch (r3.length) {
      case 1:
        return s2(t5, n3, i3, r3[0], o3, a3);
      case 2:
        return c2(t5, n3, i3, r3, o3, a3);
    }
    throw RangeError(`Matrix for function diag must be 2 dimensional`);
  }
  function s2(t5, n3, r3, o3, s3, c3) {
    var l2 = [o3 + s3, o3 + c3];
    if (r3 && r3 !== `sparse` && r3 !== `dense`) throw TypeError(`Unknown matrix type ${r3}"`);
    var u2 = r3 === `sparse` ? a2.diagonal(l2, t5, n3) : i2.diagonal(l2, t5, n3);
    return r3 === null ? u2.valueOf() : u2;
  }
  function c2(t5, n3, i3, a3, o3, s3) {
    if (isMatrix(t5)) {
      var c3 = t5.diagonal(n3);
      return i3 === null ? c3.valueOf() : i3 === c3.storage() ? c3 : r2(c3, i3);
    }
    for (var l2 = Math.min(a3[0] - o3, a3[1] - s3), u2 = [], d2 = 0; d2 < l2; d2++) u2[d2] = t5[d2 + o3][d2 + s3];
    return i3 === null ? u2 : r2(u2);
  }
}), name$230 = `filter`, dependencies$230 = [`typed`], createFilter = factory(name$230, dependencies$230, (t4) => {
  var { typed: n2 } = t4;
  return n2(`filter`, { "Array, function": _filterCallback, "Matrix, function": function(t5, n3) {
    return t5.create(_filterCallback(t5.valueOf(), n3), t5.datatype());
  }, "Array, RegExp": filterRegExp, "Matrix, RegExp": function(t5, n3) {
    return t5.create(filterRegExp(t5.valueOf(), n3), t5.datatype());
  } });
});
function _filterCallback(t4, n2) {
  var r2 = optimizeCallback(n2, t4, `filter`);
  return r2.isUnary ? filter$1(t4, r2.fn) : filter$1(t4, function(t5, n3, i2) {
    return r2.fn(t5, [n3], i2);
  });
}
var name$229 = `flatten`, dependencies$229 = [`typed`], createFlatten = factory(name$229, dependencies$229, (t4) => {
  var { typed: n2 } = t4;
  return n2(name$229, { Array: function(t5) {
    return flatten$4(t5);
  }, DenseMatrix: function(t5) {
    return t5.create(flatten$4(t5.valueOf(), true), t5.datatype());
  }, SparseMatrix: function(t5) {
    throw TypeError(`SparseMatrix is not supported by function flatten because it does not support 1D vectors. Convert to a DenseMatrix or Array first. Example: flatten(x.toArray())`);
  } });
}), name$228 = `forEach`, dependencies$228 = [`typed`], createForEach = factory(name$228, dependencies$228, (t4) => {
  var { typed: n2 } = t4;
  return n2(name$228, { "Array, function": _forEach, "Matrix, function": function(t5, n3) {
    t5.forEach(n3);
  } });
});
function _forEach(t4, n2) {
  var r2 = optimizeCallback(n2, t4, name$228);
  deepForEach$1(t4, r2.fn, r2.isUnary);
}
var name$227 = `getMatrixDataType`, dependencies$227 = [`typed`], createGetMatrixDataType = factory(name$227, dependencies$227, (t4) => {
  var { typed: n2 } = t4;
  return n2(name$227, { Array: function(t5) {
    return getArrayDataType(t5, typeOf);
  }, Matrix: function(t5) {
    return t5.getDataType();
  } });
}), name$226 = `identity`, dependencies$226 = [`typed`, `config`, `matrix`, `BigNumber`, `DenseMatrix`, `SparseMatrix`], createIdentity = factory(name$226, dependencies$226, (t4) => {
  var { typed: n2, config: r2, matrix: i2, BigNumber: a2, DenseMatrix: o2, SparseMatrix: s2 } = t4;
  return n2(name$226, { "": function() {
    return r2.matrix === `Matrix` ? i2([]) : [];
  }, string: function(t5) {
    return i2(t5);
  }, "number | BigNumber": function(t5) {
    return l2(t5, t5, r2.matrix === `Matrix` ? `dense` : void 0);
  }, "number | BigNumber, string": function(t5, n3) {
    return l2(t5, t5, n3);
  }, "number | BigNumber, number | BigNumber": function(t5, n3) {
    return l2(t5, n3, r2.matrix === `Matrix` ? `dense` : void 0);
  }, "number | BigNumber, number | BigNumber, string": function(t5, n3, r3) {
    return l2(t5, n3, r3);
  }, Array: function(t5) {
    return c2(t5);
  }, "Array, string": function(t5, n3) {
    return c2(t5, n3);
  }, Matrix: function(t5) {
    return c2(t5.valueOf(), t5.storage());
  }, "Matrix, string": function(t5, n3) {
    return c2(t5.valueOf(), n3);
  } });
  function c2(t5, n3) {
    switch (t5.length) {
      case 0:
        return n3 ? i2(n3) : [];
      case 1:
        return l2(t5[0], t5[0], n3);
      case 2:
        return l2(t5[0], t5[1], n3);
      default:
        throw Error(`Vector containing two values expected`);
    }
  }
  function l2(t5, n3, r3) {
    var i3 = isBigNumber(t5) || isBigNumber(n3) ? a2 : null;
    if (isBigNumber(t5) && (t5 = t5.toNumber()), isBigNumber(n3) && (n3 = n3.toNumber()), !isInteger(t5) || t5 < 1 || !isInteger(n3) || n3 < 1) throw Error(`Parameters in function identity must be positive integers`);
    var c3 = i3 ? new a2(1) : 1, l3 = i3 ? new i3(0) : 0, u2 = [t5, n3];
    if (r3) {
      if (r3 === `sparse`) return s2.diagonal(u2, c3, 0, l3);
      if (r3 === `dense`) return o2.diagonal(u2, c3, 0, l3);
      throw TypeError(`Unknown matrix type "${r3}"`);
    }
    for (var d2 = resize([], u2, l3), p2 = t5 < n3 ? t5 : n3, m2 = 0; m2 < p2; m2++) d2[m2][m2] = c3;
    return d2;
  }
}), name$225 = `kron`, dependencies$225 = [`typed`, `matrix`, `multiplyScalar`], createKron = factory(name$225, dependencies$225, (t4) => {
  var { typed: n2, matrix: r2, multiplyScalar: i2 } = t4;
  return n2(name$225, { "Matrix, Matrix": function(t5, n3) {
    return r2(o2(t5.toArray(), n3.toArray()));
  }, "Matrix, Array": function(t5, n3) {
    return r2(o2(t5.toArray(), n3));
  }, "Array, Matrix": function(t5, n3) {
    return r2(o2(t5, n3.toArray()));
  }, "Array, Array": o2 });
  function a2(t5, n3) {
    return t5.flatMap((t6) => n3.map((n4) => i2(t6, n4)));
  }
  function o2(t5, n3) {
    var r3 = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : -1;
    if (r3 < 0) {
      var i3 = arraySize(t5).length, s2 = arraySize(n3).length;
      for (r3 = Math.max(i3, s2); i3++ < r3; ) t5 = [t5];
      for (; s2++ < r3; ) n3 = [n3];
    }
    return r3 === 1 ? a2(t5, n3) : t5.flatMap((t6) => n3.map((n4) => o2(t6, n4, r3 - 1)));
  }
}), name$224 = `map`, dependencies$224 = [`typed`], createMap = factory(name$224, dependencies$224, (t4) => {
  var { typed: n2 } = t4;
  return n2(name$224, { "Array, function": c2, "Matrix, function": function(t5, n3) {
    return t5.map(n3);
  }, "Array|Matrix, Array|Matrix, ...Array|Matrix|function": (t5, n3, i3) => r2([t5, n3, ...i3.slice(0, i3.length - 1)], i3[i3.length - 1]) });
  function r2(t5, r3) {
    if (typeof r3 != `function`) throw Error(`Last argument must be a callback function`);
    var l2 = t5[0].isMatrix, u2 = t5.map((t6) => t6.isMatrix ? t6.size() : arraySize(t6)), d2 = broadcastSizes(...u2), p2 = t5.length, m2 = l2 ? (t6, n3) => t6.get(n3) : get$1, h2 = t5.map((t6, n3) => {
      var r4 = u2[n3].map(() => 0);
      return t6.isMatrix ? t6.get(r4) : get$1(t6, r4);
    }), g2 = n2.isTypedFunction(r3) ? s2(r3, h2, d2.map(() => 0), t5) : o2(r3, p2);
    if (g2 < 2) return i2(t5, a2(g2, r3, null));
    var v2 = l2 ? t5.map((n3) => n3.isMatrix ? n3.create(broadcastTo(n3.toArray(), d2), n3.datatype()) : t5[0].create(broadcastTo(n3.valueOf(), d2))) : t5.map((t6) => t6.isMatrix ? broadcastTo(t6.toArray(), d2) : broadcastTo(t6, d2)), y2 = a2(g2, r3, v2), b2 = (t6, n3) => y2([t6, ...v2.slice(1).map((t7) => m2(t7, n3))], n3);
    return l2 ? v2[0].map(b2) : c2(v2[0], b2);
  }
  function i2(t5, n3) {
    var r3 = t5[0], i3 = t5.map((t6) => t6.isMatrix ? t6.valueOf() : t6), a3 = t5.map((t6) => t6.isMatrix ? t6.size() : arraySize(t6)), o3 = broadcastSizes(...a3), s3 = a3.map((t6) => o3.length - t6.length), c3 = o3.length - 1, l2 = n3.length > 1 ? [] : null, u2 = p2(i3, 0);
    if (r3.isMatrix) {
      var d2 = r3.create();
      return d2._data = u2, d2._size = o3, d2;
    } else return u2;
    function p2(t6) {
      var r4 = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : 0, i4 = o3[r4], a4 = Array(i4);
      if (r4 < c3) for (var u3 = function(n4) {
        l2 && (l2[r4] = n4), a4[n4] = p2(t6.map((t7, i5) => s3[i5] > r4 ? t7 : t7.length === 1 ? t7[0] : t7[n4]), r4 + 1);
      }, d3 = 0; d3 < i4; d3++) u3(d3);
      else for (var m2 = function(i5) {
        l2 && (l2[r4] = i5), a4[i5] = n3(t6.map((t7) => t7.length === 1 ? t7[0] : t7[i5]), l2 ? l2.slice() : void 0);
      }, h2 = 0; h2 < i4; h2++) m2(h2);
      return a4;
    }
  }
  function a2(t5, n3, r3) {
    switch (t5) {
      case 0:
        return (t6) => n3(...t6);
      case 1:
        return (t6, r4) => n3(...t6, r4);
      case 2:
        return (t6, i3) => n3(...t6, i3, ...r3);
    }
  }
  function o2(t5, n3) {
    var r3 = t5.toString();
    if (/arguments/.test(r3)) return 2;
    var i3 = r3.match(/\(.*?\)/);
    return /\.\.\./.test(i3) || t5.length > n3 + 1 ? 2 : +(t5.length === n3 + 1);
  }
  function s2(t5, r3, i3, a3) {
    return n2.resolve(t5, [...r3, i3, ...a3]) === null ? n2.resolve(t5, [...r3, i3]) === null ? (n2.resolve(t5, r3), 0) : 1 : 2;
  }
  function c2(t5, n3) {
    var r3 = optimizeCallback(n3, t5, name$224);
    return deepMap$1(t5, r3.fn, r3.isUnary);
  }
}), name$223 = `diff`, dependencies$223 = [`typed`, `matrix`, `subtract`, `number`], createDiff = factory(name$223, dependencies$223, (t4) => {
  var { typed: n2, matrix: r2, subtract: i2, number: a2 } = t4;
  return n2(name$223, { "Array | Matrix": function(t5) {
    return isMatrix(t5) ? r2(s2(t5.toArray())) : s2(t5);
  }, "Array | Matrix, number": function(t5, n3) {
    if (!isInteger(n3)) throw RangeError(`Dimension must be a whole number`);
    return isMatrix(t5) ? r2(o2(t5.toArray(), n3)) : o2(t5, n3);
  }, "Array, BigNumber": n2.referTo(`Array,number`, (t5) => (n3, r3) => t5(n3, a2(r3))), "Matrix, BigNumber": n2.referTo(`Matrix,number`, (t5) => (n3, r3) => t5(n3, a2(r3))) });
  function o2(t5, n3) {
    if (isMatrix(t5) && (t5 = t5.toArray()), !Array.isArray(t5)) throw RangeError(`Array/Matrix does not have that many dimensions`);
    if (n3 > 0) {
      var r3 = [];
      return t5.forEach((t6) => {
        r3.push(o2(t6, n3 - 1));
      }), r3;
    } else if (n3 === 0) return s2(t5);
    else throw RangeError(`Cannot have negative dimension`);
  }
  function s2(t5) {
    for (var n3 = [], r3 = t5.length, i3 = 1; i3 < r3; i3++) n3.push(c2(t5[i3 - 1], t5[i3]));
    return n3;
  }
  function c2(t5, n3) {
    isMatrix(t5) && (t5 = t5.toArray()), isMatrix(n3) && (n3 = n3.toArray());
    var r3 = Array.isArray(t5), a3 = Array.isArray(n3);
    if (r3 && a3) return l2(t5, n3);
    if (!r3 && !a3) return i2(n3, t5);
    throw TypeError(`Cannot calculate difference between 1 array and 1 non-array`);
  }
  function l2(t5, n3) {
    if (t5.length !== n3.length) throw RangeError(`Not all sub-arrays have the same length`);
    for (var r3 = [], i3 = t5.length, a3 = 0; a3 < i3; a3++) r3.push(c2(t5[a3], n3[a3]));
    return r3;
  }
}), name$222 = `ones`, dependencies$222 = [`typed`, `config`, `matrix`, `BigNumber`], createOnes = factory(name$222, dependencies$222, (t4) => {
  var { typed: n2, config: r2, matrix: i2, BigNumber: a2 } = t4;
  return n2(`ones`, { "": function() {
    return r2.matrix === `Array` ? o2([]) : o2([], `default`);
  }, "...number | BigNumber | string": function(t5) {
    return typeof t5[t5.length - 1] == `string` ? o2(t5, t5.pop()) : r2.matrix === `Array` ? o2(t5) : o2(t5, `default`);
  }, Array: o2, Matrix: function(t5) {
    var n3 = t5.storage();
    return o2(t5.valueOf(), n3);
  }, "Array | Matrix, string": function(t5, n3) {
    return o2(t5.valueOf(), n3);
  } });
  function o2(t5, n3) {
    var r3 = s2(t5) ? new a2(1) : 1;
    if (c2(t5), n3) {
      var o3 = i2(n3);
      return t5.length > 0 ? o3.resize(t5, r3) : o3;
    } else {
      var l2 = [];
      return t5.length > 0 ? resize(l2, t5, r3) : l2;
    }
  }
  function s2(t5) {
    var n3 = false;
    return t5.forEach(function(t6, r3, i3) {
      isBigNumber(t6) && (n3 = true, i3[r3] = t6.toNumber());
    }), n3;
  }
  function c2(t5) {
    t5.forEach(function(t6) {
      if (typeof t6 != `number` || !isInteger(t6) || t6 < 0) throw Error(`Parameters in function ones must be positive integers`);
    });
  }
});
function noBignumber() {
  throw Error(`No "bignumber" implementation available`);
}
function noFraction() {
  throw Error(`No "fraction" implementation available`);
}
function noMatrix() {
  throw Error(`No "matrix" implementation available`);
}
var name$221 = `range`, dependencies$221 = [`typed`, `config`, `?matrix`, `?bignumber`, `equal`, `smaller`, `smallerEq`, `larger`, `largerEq`, `add`, `isZero`, `isPositive`], createRange = factory(name$221, dependencies$221, (t4) => {
  var { typed: n2, config: r2, matrix: i2, bignumber: a2, smaller: o2, smallerEq: s2, larger: c2, largerEq: l2, add: u2, isZero: d2, isPositive: p2 } = t4;
  return n2(name$221, { string: h2, "string, boolean": h2, number: function(t5) {
    throw TypeError(`Too few arguments to function range(): ${t5}`);
  }, boolean: function(t5) {
    throw TypeError(`Unexpected type of argument 1 to function range(): ${t5}, number|bigint|BigNumber|Fraction`);
  }, "number, number": function(t5, n3) {
    return m2(g2(t5, n3, 1, false));
  }, "number, number, number": function(t5, n3, r3) {
    return m2(g2(t5, n3, r3, false));
  }, "number, number, boolean": function(t5, n3, r3) {
    return m2(g2(t5, n3, 1, r3));
  }, "number, number, number, boolean": function(t5, n3, r3, i3) {
    return m2(g2(t5, n3, r3, i3));
  }, "bigint, bigint|number": function(t5, n3) {
    return m2(g2(t5, n3, 1n, false));
  }, "number, bigint": function(t5, n3) {
    return m2(g2(BigInt(t5), n3, 1n, false));
  }, "bigint, bigint|number, bigint|number": function(t5, n3, r3) {
    return m2(g2(t5, n3, BigInt(r3), false));
  }, "number, bigint, bigint|number": function(t5, n3, r3) {
    return m2(g2(BigInt(t5), n3, BigInt(r3), false));
  }, "bigint, bigint|number, boolean": function(t5, n3, r3) {
    return m2(g2(t5, n3, 1n, r3));
  }, "number, bigint, boolean": function(t5, n3, r3) {
    return m2(g2(BigInt(t5), n3, 1n, r3));
  }, "bigint, bigint|number, bigint|number, boolean": function(t5, n3, r3, i3) {
    return m2(g2(t5, n3, BigInt(r3), i3));
  }, "number, bigint, bigint|number, boolean": function(t5, n3, r3, i3) {
    return m2(g2(BigInt(t5), n3, BigInt(r3), i3));
  }, "BigNumber, BigNumber": function(t5, n3) {
    var r3 = t5.constructor;
    return m2(g2(t5, n3, new r3(1), false));
  }, "BigNumber, BigNumber, BigNumber": function(t5, n3, r3) {
    return m2(g2(t5, n3, r3, false));
  }, "BigNumber, BigNumber, boolean": function(t5, n3, r3) {
    var i3 = t5.constructor;
    return m2(g2(t5, n3, new i3(1), r3));
  }, "BigNumber, BigNumber, BigNumber, boolean": function(t5, n3, r3, i3) {
    return m2(g2(t5, n3, r3, i3));
  }, "Fraction, Fraction": function(t5, n3) {
    return m2(g2(t5, n3, 1, false));
  }, "Fraction, Fraction, Fraction": function(t5, n3, r3) {
    return m2(g2(t5, n3, r3, false));
  }, "Fraction, Fraction, boolean": function(t5, n3, r3) {
    return m2(g2(t5, n3, 1, r3));
  }, "Fraction, Fraction, Fraction, boolean": function(t5, n3, r3, i3) {
    return m2(g2(t5, n3, r3, i3));
  }, "Unit, Unit, Unit": function(t5, n3, r3) {
    return m2(g2(t5, n3, r3, false));
  }, "Unit, Unit, Unit, boolean": function(t5, n3, r3, i3) {
    return m2(g2(t5, n3, r3, i3));
  } });
  function m2(t5) {
    return r2.matrix === `Matrix` ? i2 ? i2(t5) : noMatrix() : t5;
  }
  function h2(t5, n3) {
    var i3 = v2(t5);
    if (!i3) throw SyntaxError(`String "` + t5 + `" is no valid range`);
    return r2.number === `BigNumber` ? (a2 === void 0 && noBignumber(), m2(g2(a2(i3.start), a2(i3.end), a2(i3.step)))) : m2(g2(i3.start, i3.end, i3.step, n3));
  }
  function g2(t5, n3, r3, i3) {
    var a3 = [];
    if (d2(r3)) throw Error(`Step must be non-zero`);
    for (var m3 = p2(r3) ? i3 ? s2 : o2 : i3 ? l2 : c2, h3 = t5; m3(h3, n3); ) a3.push(h3), h3 = u2(h3, r3);
    return a3;
  }
  function v2(t5) {
    var n3 = t5.split(`:`).map(function(t6) {
      return Number(t6);
    });
    if (n3.some(function(t6) {
      return isNaN(t6);
    })) return null;
    switch (n3.length) {
      case 2:
        return { start: n3[0], end: n3[1], step: 1 };
      case 3:
        return { start: n3[0], end: n3[2], step: n3[1] };
      default:
        return null;
    }
  }
}), name$220 = `reshape`, dependencies$220 = [`typed`, `isInteger`, `matrix`], createReshape = factory(name$220, dependencies$220, (t4) => {
  var { typed: n2, isInteger: r2 } = t4;
  return n2(name$220, { "Matrix, Array": function(t5, n3) {
    return t5.reshape(n3, true);
  }, "Array, Array": function(t5, n3) {
    return n3.forEach(function(t6) {
      if (!r2(t6)) throw TypeError(`Invalid size for dimension: ` + t6);
    }), reshape(t5, n3);
  } });
}), name$219 = `resize`, dependencies$219 = [`config`, `matrix`], createResize = factory(name$219, dependencies$219, (t4) => {
  var { config: n2, matrix: r2 } = t4;
  return function(t5, a2, o2) {
    if (arguments.length !== 2 && arguments.length !== 3) throw new ArgumentsError(`resize`, arguments.length, 2, 3);
    if (isMatrix(a2) && (a2 = a2.valueOf()), isBigNumber(a2[0]) && (a2 = a2.map(function(t6) {
      return isBigNumber(t6) ? t6.toNumber() : t6;
    })), isMatrix(t5)) return t5.resize(a2, o2, true);
    if (typeof t5 == `string`) return i2(t5, a2, o2);
    var s2 = Array.isArray(t5) ? false : n2.matrix !== `Array`;
    if (a2.length === 0) {
      for (; Array.isArray(t5); ) t5 = t5[0];
      return clone$3(t5);
    } else {
      Array.isArray(t5) || (t5 = [t5]), t5 = clone$3(t5);
      var c2 = resize(t5, a2, o2);
      return s2 ? r2(c2) : c2;
    }
  };
  function i2(t5, n3, r3) {
    if (r3 !== void 0) {
      if (typeof r3 != `string` || r3.length !== 1) throw TypeError(`Single character expected as defaultValue`);
    } else r3 = ` `;
    if (n3.length !== 1) throw new DimensionError(n3.length, 1);
    var i3 = n3[0];
    if (typeof i3 != `number` || !isInteger(i3)) throw TypeError(`Invalid size, must contain positive integers (size: ` + format(n3) + `)`);
    if (t5.length > i3) return t5.substring(0, i3);
    if (t5.length < i3) {
      for (var a2 = t5, o2 = 0, s2 = i3 - t5.length; o2 < s2; o2++) a2 += r3;
      return a2;
    } else return t5;
  }
}), name$218 = `rotate`, dependencies$218 = [`typed`, `multiply`, `rotationMatrix`], createRotate = factory(name$218, dependencies$218, (t4) => {
  var { typed: n2, multiply: r2, rotationMatrix: i2 } = t4;
  return n2(name$218, { "Array , number | BigNumber | Complex | Unit": function(t5, n3) {
    return a2(t5, 2), r2(i2(n3), t5).toArray();
  }, "Matrix , number | BigNumber | Complex | Unit": function(t5, n3) {
    return a2(t5, 2), r2(i2(n3), t5);
  }, "Array, number | BigNumber | Complex | Unit, Array | Matrix": function(t5, n3, o2) {
    return a2(t5, 3), r2(i2(n3, o2), t5);
  }, "Matrix, number | BigNumber | Complex | Unit, Array | Matrix": function(t5, n3, o2) {
    return a2(t5, 3), r2(i2(n3, o2), t5);
  } });
  function a2(t5, n3) {
    var r3 = Array.isArray(t5) ? arraySize(t5) : t5.size();
    if (r3.length > 2 || r3.length === 2 && r3[1] !== 1 || r3[0] !== n3) throw RangeError(`Vector must be of dimensions 1x${n3}`);
  }
}), name$217 = `rotationMatrix`, dependencies$217 = [`typed`, `config`, `multiplyScalar`, `addScalar`, `unaryMinus`, `norm`, `matrix`, `BigNumber`, `DenseMatrix`, `SparseMatrix`, `cos`, `sin`], createRotationMatrix = factory(name$217, dependencies$217, (t4) => {
  var { typed: n2, config: r2, multiplyScalar: i2, addScalar: a2, unaryMinus: o2, norm: s2, BigNumber: c2, matrix: l2, DenseMatrix: u2, SparseMatrix: d2, cos: p2, sin: m2 } = t4;
  return n2(name$217, { "": function() {
    return r2.matrix === `Matrix` ? l2([]) : [];
  }, string: function(t5) {
    return l2(t5);
  }, "number | BigNumber | Complex | Unit": function(t5) {
    return h2(t5, r2.matrix === `Matrix` ? `dense` : void 0);
  }, "number | BigNumber | Complex | Unit, string": function(t5, n3) {
    return h2(t5, n3);
  }, "number | BigNumber | Complex | Unit, Array": function(t5, n3) {
    var r3 = l2(n3);
    return g2(r3), b2(t5, r3, void 0);
  }, "number | BigNumber | Complex | Unit, Matrix": function(t5, n3) {
    return g2(n3), b2(t5, n3, n3.storage() || (r2.matrix === `Matrix` ? `dense` : void 0));
  }, "number | BigNumber | Complex | Unit, Array, string": function(t5, n3, r3) {
    var i3 = l2(n3);
    return g2(i3), b2(t5, i3, r3);
  }, "number | BigNumber | Complex | Unit, Matrix, string": function(t5, n3, r3) {
    return g2(n3), b2(t5, n3, r3);
  } });
  function h2(t5, n3) {
    var r3 = isBigNumber(t5) ? new c2(-1) : -1, a3 = p2(t5), o3 = m2(t5);
    return y2([[a3, i2(r3, o3)], [o3, a3]], n3);
  }
  function g2(t5) {
    var n3 = t5.size();
    if (n3.length < 1 || n3[0] !== 3) throw RangeError(`Vector must be of dimensions 1x3`);
  }
  function v2(t5) {
    return t5.reduce((t6, n3) => i2(t6, n3));
  }
  function y2(t5, n3) {
    if (n3) {
      if (n3 === `sparse`) return new d2(t5);
      if (n3 === `dense`) return new u2(t5);
      throw TypeError(`Unknown matrix type "${n3}"`);
    }
    return t5;
  }
  function b2(t5, n3, r3) {
    var i3 = s2(n3);
    if (i3 === 0) throw RangeError(`Rotation around zero vector`);
    var l3 = isBigNumber(t5) ? c2 : null, u3 = l3 ? new l3(1) : 1, d3 = l3 ? new l3(-1) : -1, h3 = l3 ? new l3(n3.get([0]) / i3) : n3.get([0]) / i3, g3 = l3 ? new l3(n3.get([1]) / i3) : n3.get([1]) / i3, b3 = l3 ? new l3(n3.get([2]) / i3) : n3.get([2]) / i3, x2 = p2(t5), S2 = a2(u3, o2(x2)), w2 = m2(t5), E = a2(x2, v2([h3, h3, S2])), D = a2(v2([h3, g3, S2]), v2([d3, b3, w2])), O = a2(v2([h3, b3, S2]), v2([g3, w2])), j2 = a2(v2([h3, g3, S2]), v2([b3, w2])), M2 = a2(x2, v2([g3, g3, S2])), N2 = a2(v2([g3, b3, S2]), v2([d3, h3, w2])), F = a2(v2([h3, b3, S2]), v2([d3, g3, w2])), I = a2(v2([g3, b3, S2]), v2([h3, w2])), L = a2(x2, v2([b3, b3, S2]));
    return y2([[E, D, O], [j2, M2, N2], [F, I, L]], r3);
  }
}), name$216 = `row`, dependencies$216 = [`typed`, `Index`, `matrix`, `range`], createRow = factory(name$216, dependencies$216, (t4) => {
  var { typed: n2, Index: r2, matrix: i2, range: a2 } = t4;
  return n2(name$216, { "Matrix, number": o2, "Array, number": function(t5, n3) {
    return o2(i2(clone$3(t5)), n3).valueOf();
  } });
  function o2(t5, n3) {
    if (t5.size().length !== 2) throw Error(`Only two dimensional matrix is supported`);
    validateIndex(n3, t5.size()[0]);
    var o3 = a2(0, t5.size()[1]), s2 = new r2([n3], o3), c2 = t5.subset(s2);
    return isMatrix(c2) ? c2 : i2([[c2]]);
  }
}), name$215 = `size`, dependencies$215 = [`typed`], createSize = factory(name$215, dependencies$215, (t4) => {
  var { typed: n2 } = t4;
  return n2(name$215, { Matrix: (t5) => t5.size(), Array: arraySize, string: (t5) => [t5.length], "number | Complex | BigNumber | Unit | boolean | null": (t5) => [] });
}), name$214 = `squeeze`, dependencies$214 = [`typed`], createSqueeze = factory(name$214, dependencies$214, (t4) => {
  var { typed: n2 } = t4;
  return n2(name$214, { Array: function(t5) {
    return squeeze(clone$3(t5));
  }, Matrix: function(t5) {
    var n3 = squeeze(t5.toArray());
    return Array.isArray(n3) ? t5.create(n3, t5.datatype()) : n3;
  }, any: function(t5) {
    return clone$3(t5);
  } });
}), name$213 = `subset`, dependencies$213 = [`typed`, `matrix`, `zeros`, `add`], createSubset = factory(name$213, dependencies$213, (t4) => {
  var { typed: n2, matrix: r2, zeros: i2, add: a2 } = t4;
  return n2(name$213, { "Matrix, Index": function(t5, n3) {
    return isEmptyIndex(n3) ? r2() : (validateIndexSourceSize(t5, n3), t5.subset(n3));
  }, "Array, Index": n2.referTo(`Matrix, Index`, function(t5) {
    return function(n3, i3) {
      var a3 = t5(r2(n3), i3);
      return i3.isScalar() ? a3 : a3.valueOf();
    };
  }), "Object, Index": _getObjectProperty, "string, Index": _getSubstring, "Matrix, Index, any, any": function(t5, n3, r3, i3) {
    return isEmptyIndex(n3) ? t5 : (validateIndexSourceSize(t5, n3), t5.clone().subset(n3, o2(r3, n3), i3));
  }, "Array, Index, any, any": n2.referTo(`Matrix, Index, any, any`, function(t5) {
    return function(n3, i3, a3, o3) {
      var s2 = t5(r2(n3), i3, a3, o3);
      return s2.isMatrix ? s2.valueOf() : s2;
    };
  }), "Array, Index, any": n2.referTo(`Matrix, Index, any, any`, function(t5) {
    return function(n3, i3, a3) {
      return t5(r2(n3), i3, a3, void 0).valueOf();
    };
  }), "Matrix, Index, any": n2.referTo(`Matrix, Index, any, any`, function(t5) {
    return function(n3, r3, i3) {
      return t5(n3, r3, i3, void 0);
    };
  }), "string, Index, string": _setSubstring, "string, Index, string, string": _setSubstring, "Object, Index, any": _setObjectProperty });
  function o2(t5, n3) {
    if (typeof t5 == `string`) throw Error(`can't boradcast a string`);
    if (n3.isScalar()) return t5;
    var r3 = n3.size();
    if (r3.every((t6) => t6 > 0)) try {
      return a2(t5, i2(r3));
    } catch {
      return t5;
    }
    else return t5;
  }
});
function _getSubstring(t4, n2) {
  if (!isIndex(n2)) throw TypeError(`Index expected`);
  if (isEmptyIndex(n2)) return ``;
  if (validateIndexSourceSize(Array.from(t4), n2), n2.size().length !== 1) throw new DimensionError(n2.size().length, 1);
  var r2 = t4.length;
  validateIndex(n2.min()[0], r2), validateIndex(n2.max()[0], r2);
  var i2 = n2.dimension(0), a2 = ``;
  function o2(n3) {
    a2 += t4.charAt(n3);
  }
  return Number.isInteger(i2) ? o2(i2) : i2.forEach(o2), a2;
}
function _setSubstring(t4, n2, r2, i2) {
  if (!n2 || n2.isIndex !== true) throw TypeError(`Index expected`);
  if (isEmptyIndex(n2)) return t4;
  if (validateIndexSourceSize(Array.from(t4), n2), n2.size().length !== 1) throw new DimensionError(n2.size().length, 1);
  if (i2 !== void 0) {
    if (typeof i2 != `string` || i2.length !== 1) throw TypeError(`Single character expected as defaultValue`);
  } else i2 = ` `;
  var a2 = n2.dimension(0);
  if ((Number.isInteger(a2) ? 1 : a2.size()[0]) !== r2.length) throw new DimensionError(a2.size()[0], r2.length);
  var o2 = t4.length;
  validateIndex(n2.min()[0]), validateIndex(n2.max()[0]);
  for (var s2 = [], c2 = 0; c2 < o2; c2++) s2[c2] = t4.charAt(c2);
  function l2(t5, n3) {
    s2[t5] = r2.charAt(n3[0]);
  }
  if (Number.isInteger(a2) ? l2(a2, [0]) : a2.forEach(l2), s2.length > o2) for (var u2 = o2 - 1, d2 = s2.length; u2 < d2; u2++) s2[u2] || (s2[u2] = i2);
  return s2.join(``);
}
function _getObjectProperty(t4, n2) {
  if (!isEmptyIndex(n2)) {
    if (n2.size().length !== 1) throw new DimensionError(n2.size(), 1);
    var r2 = n2.dimension(0);
    if (typeof r2 != `string`) throw TypeError(`String expected as index to retrieve an object property`);
    return getSafeProperty(t4, r2);
  }
}
function _setObjectProperty(t4, n2, r2) {
  if (isEmptyIndex(n2)) return t4;
  if (n2.size().length !== 1) throw new DimensionError(n2.size(), 1);
  var i2 = n2.dimension(0);
  if (typeof i2 != `string`) throw TypeError(`String expected as index to retrieve an object property`);
  var a2 = clone$3(t4);
  return setSafeProperty(a2, i2, r2), a2;
}
var name$212 = `transpose`, dependencies$212 = [`typed`, `matrix`], createTranspose = factory(name$212, dependencies$212, (t4) => {
  var { typed: n2, matrix: r2 } = t4;
  return n2(name$212, { Array: (t5) => i2(r2(t5)).valueOf(), Matrix: i2, any: clone$3 });
  function i2(t5) {
    var n3 = t5.size(), r3;
    switch (n3.length) {
      case 1:
        r3 = t5.clone();
        break;
      case 2:
        var i3 = n3[0], s2 = n3[1];
        if (s2 === 0) throw RangeError(`Cannot transpose a 2D matrix with no columns (size: ` + format(n3) + `)`);
        switch (t5.storage()) {
          case `dense`:
            r3 = a2(t5, i3, s2);
            break;
          case `sparse`:
            r3 = o2(t5, i3, s2);
            break;
        }
        break;
      default:
        throw RangeError(`Matrix must be a vector or two dimensional (size: ` + format(n3) + `)`);
    }
    return r3;
  }
  function a2(t5, n3, r3) {
    for (var i3 = t5._data, a3 = [], o3, s2 = 0; s2 < r3; s2++) {
      o3 = a3[s2] = [];
      for (var c2 = 0; c2 < n3; c2++) o3[c2] = clone$3(i3[c2][s2]);
    }
    return t5.createDenseMatrix({ data: a3, size: [r3, n3], datatype: t5._datatype });
  }
  function o2(t5, n3, r3) {
    for (var i3 = t5._values, a3 = t5._index, o3 = t5._ptr, s2 = i3 ? [] : void 0, c2 = [], l2 = [], u2 = [], d2 = 0; d2 < n3; d2++) u2[d2] = 0;
    var p2, m2, h2;
    for (p2 = 0, m2 = a3.length; p2 < m2; p2++) u2[a3[p2]]++;
    for (var g2 = 0, v2 = 0; v2 < n3; v2++) l2.push(g2), g2 += u2[v2], u2[v2] = l2[v2];
    for (l2.push(g2), h2 = 0; h2 < r3; h2++) for (var y2 = o3[h2], b2 = o3[h2 + 1], x2 = y2; x2 < b2; x2++) {
      var S2 = u2[a3[x2]]++;
      c2[S2] = h2, i3 && (s2[S2] = clone$3(i3[x2]));
    }
    return t5.createSparseMatrix({ values: s2, index: c2, ptr: l2, size: [r3, n3], datatype: t5._datatype });
  }
}), name$211 = `ctranspose`, dependencies$211 = [`typed`, `transpose`, `conj`], createCtranspose = factory(name$211, dependencies$211, (t4) => {
  var { typed: n2, transpose: r2, conj: i2 } = t4;
  return n2(name$211, { any: function(t5) {
    return i2(r2(t5));
  } });
}), name$210 = `zeros`, dependencies$210 = [`typed`, `config`, `matrix`, `BigNumber`], createZeros = factory(name$210, dependencies$210, (t4) => {
  var { typed: n2, config: r2, matrix: i2, BigNumber: a2 } = t4;
  return n2(name$210, { "": function() {
    return r2.matrix === `Array` ? o2([]) : o2([], `default`);
  }, "...number | BigNumber | string": function(t5) {
    return typeof t5[t5.length - 1] == `string` ? o2(t5, t5.pop()) : r2.matrix === `Array` ? o2(t5) : o2(t5, `default`);
  }, Array: o2, Matrix: function(t5) {
    var n3 = t5.storage();
    return o2(t5.valueOf(), n3);
  }, "Array | Matrix, string": function(t5, n3) {
    return o2(t5.valueOf(), n3);
  } });
  function o2(t5, n3) {
    var r3 = s2(t5) ? new a2(0) : 0;
    if (c2(t5), n3) {
      var o3 = i2(n3);
      return t5.length > 0 ? o3.resize(t5, r3) : o3;
    } else {
      var l2 = [];
      return t5.length > 0 ? resize(l2, t5, r3) : l2;
    }
  }
  function s2(t5) {
    var n3 = false;
    return t5.forEach(function(t6, r3, i3) {
      isBigNumber(t6) && (n3 = true, i3[r3] = t6.toNumber());
    }), n3;
  }
  function c2(t5) {
    t5.forEach(function(t6) {
      if (typeof t6 != `number` || !isInteger(t6) || t6 < 0) throw Error(`Parameters in function zeros must be positive integers`);
    });
  }
}), name$209 = `fft`, dependencies$209 = [`typed`, `matrix`, `addScalar`, `multiplyScalar`, `divideScalar`, `exp`, `tau`, `i`, `dotDivide`, `conj`, `pow`, `ceil`, `log2`], createFft = factory(name$209, dependencies$209, (t4) => {
  var { typed: n2, matrix: r2, addScalar: i2, multiplyScalar: a2, divideScalar: o2, exp: s2, tau: c2, i: l2, dotDivide: u2, conj: d2, pow: p2, ceil: m2, log2: h2 } = t4;
  return n2(name$209, { Array: g2, Matrix: function(t5) {
    return t5.create(g2(t5.valueOf()), t5.datatype());
  } });
  function g2(t5) {
    var n3 = arraySize(t5);
    return n3.length === 1 ? b2(t5, n3[0]) : v2(t5.map((t6) => g2(t6, n3.slice(1))), 0);
  }
  function v2(t5, n3) {
    var r3 = arraySize(t5);
    if (n3 !== 0) return Array(r3[0]).fill(0).map((r4, i4) => v2(t5[i4], n3 - 1));
    if (r3.length === 1) return b2(t5);
    function i3(t6) {
      var n4 = arraySize(t6);
      return Array(n4[1]).fill(0).map((r4, i4) => Array(n4[0]).fill(0).map((n5, r5) => t6[r5][i4]));
    }
    return i3(v2(i3(t5), 1));
  }
  function y2(t5) {
    for (var n3 = t5.length, r3 = s2(o2(a2(-1, a2(l2, c2)), n3)), i3 = [], v3 = 1 - n3; v3 < n3; v3++) i3.push(p2(r3, o2(p2(v3, 2), 2)));
    for (var y3 = p2(2, m2(h2(n3 + n3 - 1))), x2 = [...Array(n3).fill(0).map((r4, o3) => a2(t5[o3], i3[n3 - 1 + o3])), ...Array(y3 - n3).fill(0)], S2 = [...Array(n3 + n3 - 1).fill(0).map((t6, n4) => o2(1, i3[n4])), ...Array(y3 - (n3 + n3 - 1)).fill(0)], w2 = b2(x2), E = b2(S2), D = u2(d2(g2(d2(Array(y3).fill(0).map((t6, n4) => a2(w2[n4], E[n4]))))), y3), O = [], j2 = n3 - 1; j2 < n3 + n3 - 1; j2++) O.push(a2(D[j2], i3[j2]));
    return O;
  }
  function b2(t5) {
    var n3 = t5.length;
    if (n3 === 1) return [t5[0]];
    if (n3 % 2 == 0) {
      for (var r3 = [...b2(t5.filter((t6, n4) => n4 % 2 == 0)), ...b2(t5.filter((t6, n4) => n4 % 2 == 1))], u3 = 0; u3 < n3 / 2; u3++) {
        var d3 = r3[u3], p3 = a2(r3[u3 + n3 / 2], s2(a2(a2(c2, l2), o2(-u3, n3))));
        r3[u3] = i2(d3, p3), r3[u3 + n3 / 2] = i2(d3, a2(-1, p3));
      }
      return r3;
    } else return y2(t5);
  }
}), name$208 = `ifft`, dependencies$208 = [`typed`, `fft`, `dotDivide`, `conj`], createIfft = factory(name$208, dependencies$208, (t4) => {
  var { typed: n2, fft: r2, dotDivide: i2, conj: a2 } = t4;
  return n2(name$208, { "Array | Matrix": function(t5) {
    var n3 = isMatrix(t5) ? t5.size() : arraySize(t5);
    return i2(a2(r2(a2(t5))), n3.reduce((t6, n4) => t6 * n4, 1));
  } });
});
function _typeof(t4) {
  "@babel/helpers - typeof";
  return _typeof = typeof Symbol == `function` && typeof Symbol.iterator == `symbol` ? function(t5) {
    return typeof t5;
  } : function(t5) {
    return t5 && typeof Symbol == `function` && t5.constructor === Symbol && t5 !== Symbol.prototype ? `symbol` : typeof t5;
  }, _typeof(t4);
}
function toPrimitive(t4, n2) {
  if (_typeof(t4) != `object` || !t4) return t4;
  var r2 = t4[Symbol.toPrimitive];
  if (r2 !== void 0) {
    var i2 = r2.call(t4, n2);
    if (_typeof(i2) != `object`) return i2;
    throw TypeError(`@@toPrimitive must return a primitive value.`);
  }
  return (n2 === `string` ? String : Number)(t4);
}
function toPropertyKey(t4) {
  var n2 = toPrimitive(t4, `string`);
  return _typeof(n2) == `symbol` ? n2 : n2 + ``;
}
function _defineProperty(t4, n2, r2) {
  return (n2 = toPropertyKey(n2)) in t4 ? Object.defineProperty(t4, n2, { value: r2, enumerable: true, configurable: true, writable: true }) : t4[n2] = r2, t4;
}
function ownKeys$3(t4, n2) {
  var r2 = Object.keys(t4);
  if (Object.getOwnPropertySymbols) {
    var i2 = Object.getOwnPropertySymbols(t4);
    n2 && (i2 = i2.filter(function(n3) {
      return Object.getOwnPropertyDescriptor(t4, n3).enumerable;
    })), r2.push.apply(r2, i2);
  }
  return r2;
}
function _objectSpread$3(t4) {
  for (var n2 = 1; n2 < arguments.length; n2++) {
    var r2 = arguments[n2] == null ? {} : arguments[n2];
    n2 % 2 ? ownKeys$3(Object(r2), true).forEach(function(n3) {
      _defineProperty(t4, n3, r2[n3]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(t4, Object.getOwnPropertyDescriptors(r2)) : ownKeys$3(Object(r2)).forEach(function(n3) {
      Object.defineProperty(t4, n3, Object.getOwnPropertyDescriptor(r2, n3));
    });
  }
  return t4;
}
var name$207 = `solveODE`, dependencies$207 = [`typed`, `add`, `subtract`, `multiply`, `divide`, `max`, `map`, `abs`, `isPositive`, `isNegative`, `larger`, `smaller`, `matrix`, `bignumber`, `unaryMinus`], createSolveODE = factory(name$207, dependencies$207, (t4) => {
  var { typed: n2, add: r2, subtract: i2, multiply: a2, divide: o2, max: s2, map: c2, abs: l2, isPositive: u2, isNegative: d2, larger: p2, smaller: m2, matrix: h2, bignumber: g2, unaryMinus: v2 } = t4;
  function y2(t5) {
    return function(n3, h3, y3, b3) {
      if (!(h3.length === 2 && (h3.every(D) || h3.every(isUnit)))) throw Error(`"tspan" must be an Array of two numeric values or two units [tStart, tEnd]`);
      var x3 = h3[0], S3 = h3[1], O2 = p2(S3, x3), j2 = b3.firstStep;
      if (j2 !== void 0 && !u2(j2)) throw Error(`"firstStep" must be positive`);
      var M2 = b3.maxStep;
      if (M2 !== void 0 && !u2(M2)) throw Error(`"maxStep" must be positive`);
      var N2 = b3.minStep;
      if (N2 && d2(N2)) throw Error(`"minStep" must be positive or zero`);
      var F = [x3, S3, j2, N2, M2].filter((t6) => t6 !== void 0);
      if (!(F.every(D) || F.every(isUnit))) throw Error(`Inconsistent type of "t" dependant variables`);
      for (var I = 1, L = b3.tol ? b3.tol : 1e-4, R = b3.minDelta ? b3.minDelta : 0.2, z = b3.maxDelta ? b3.maxDelta : 5, V = b3.maxIter ? b3.maxIter : 1e4, H2 = [x3, S3, ...y3, M2, N2].some(isBigNumber), [U, W, G, K] = H2 ? [g2(t5.a), g2(t5.c), g2(t5.b), g2(t5.bp)] : [t5.a, t5.c, t5.b, t5.bp], q = j2 ? O2 ? j2 : v2(j2) : o2(i2(S3, x3), I), J = [x3], ee = [y3], Y = i2(G, K), te = 0, ne = 0, X = w2(O2), ie = E(O2); X(J[te], S3); ) {
        var ae = [];
        q = ie(J[te], S3, q), ae.push(n3(J[te], ee[te]));
        for (var Z = 1; Z < W.length; ++Z) ae.push(n3(r2(J[te], a2(W[Z], q)), r2(ee[te], a2(q, U[Z], ae))));
        var oe = s2(l2(c2(a2(Y, ae), (t6) => isUnit(t6) ? t6.value : t6)));
        oe < L && L / oe > 1 / 4 && (J.push(r2(J[te], q)), ee.push(r2(ee[te], a2(q, G, ae))), te++);
        var $2 = 0.84 * (L / oe) ** (1 / 5);
        if (m2($2, R) ? $2 = R : p2($2, z) && ($2 = z), $2 = H2 ? g2($2) : $2, q = a2(q, $2), M2 && p2(l2(q), M2) ? q = O2 ? M2 : v2(M2) : N2 && m2(l2(q), N2) && (q = O2 ? N2 : v2(N2)), ne++, ne > V) throw Error(`Maximum number of iterations reached, try changing options`);
      }
      return { t: J, y: ee };
    };
  }
  function b2(t5, n3, r3, i3) {
    return y2({ a: [[], [1 / 2], [0, 3 / 4], [2 / 9, 1 / 3, 4 / 9]], c: [null, 1 / 2, 3 / 4, 1], b: [2 / 9, 1 / 3, 4 / 9, 0], bp: [7 / 24, 1 / 4, 1 / 3, 1 / 8] })(t5, n3, r3, i3);
  }
  function x2(t5, n3, r3, i3) {
    return y2({ a: [[], [1 / 5], [3 / 40, 9 / 40], [44 / 45, -56 / 15, 32 / 9], [19372 / 6561, -25360 / 2187, 64448 / 6561, -212 / 729], [9017 / 3168, -355 / 33, 46732 / 5247, 49 / 176, -5103 / 18656], [35 / 384, 0, 500 / 1113, 125 / 192, -2187 / 6784, 11 / 84]], c: [null, 1 / 5, 3 / 10, 4 / 5, 8 / 9, 1, 1], b: [35 / 384, 0, 500 / 1113, 125 / 192, -2187 / 6784, 11 / 84, 0], bp: [5179 / 57600, 0, 7571 / 16695, 393 / 640, -92097 / 339200, 187 / 2100, 1 / 40] })(t5, n3, r3, i3);
  }
  function S2(t5, n3, r3, i3) {
    var a3 = i3.method ? i3.method : `RK45`, o3 = { RK23: b2, RK45: x2 };
    if (a3.toUpperCase() in o3) {
      var s3 = _objectSpread$3({}, i3);
      return delete s3.method, o3[a3.toUpperCase()](t5, n3, r3, s3);
    } else {
      var c3 = Object.keys(o3).map((t6) => `"${t6}"`), l3 = `${c3.slice(0, -1).join(`, `)} and ${c3.slice(-1)}`;
      throw Error(`Unavailable method "${a3}". Available methods are ${l3}`);
    }
  }
  function w2(t5) {
    return t5 ? m2 : p2;
  }
  function E(t5) {
    var n3 = t5 ? p2 : m2;
    return function(t6, a3, o3) {
      return n3(r2(t6, o3), a3) ? i2(a3, t6) : o3;
    };
  }
  function D(t5) {
    return isBigNumber(t5) || isNumber(t5);
  }
  function O(t5, n3, r3, i3) {
    var a3 = S2(t5, n3.toArray(), r3.toArray(), i3);
    return { t: h2(a3.t), y: h2(a3.y) };
  }
  return n2(`solveODE`, { "function, Array, Array, Object": S2, "function, Matrix, Matrix, Object": O, "function, Array, Array": (t5, n3, r3) => S2(t5, n3, r3, {}), "function, Matrix, Matrix": (t5, n3, r3) => O(t5, n3, r3, {}), "function, Array, number | BigNumber | Unit": (t5, n3, r3) => {
    var i3 = S2(t5, n3, [r3], {});
    return { t: i3.t, y: i3.y.map((t6) => t6[0]) };
  }, "function, Matrix, number | BigNumber | Unit": (t5, n3, r3) => {
    var i3 = S2(t5, n3.toArray(), [r3], {});
    return { t: h2(i3.t), y: h2(i3.y.map((t6) => t6[0])) };
  }, "function, Array, number | BigNumber | Unit, Object": (t5, n3, r3, i3) => {
    var a3 = S2(t5, n3, [r3], i3);
    return { t: a3.t, y: a3.y.map((t6) => t6[0]) };
  }, "function, Matrix, number | BigNumber | Unit, Object": (t5, n3, r3, i3) => {
    var a3 = S2(t5, n3.toArray(), [r3], i3);
    return { t: h2(a3.t), y: h2(a3.y.map((t6) => t6[0])) };
  } });
}), name$206 = `erf`, dependencies$206 = [`typed`], createErf = factory(name$206, dependencies$206, (t4) => {
  var { typed: n2 } = t4;
  return n2(`name`, { number: function(t5) {
    var n3 = Math.abs(t5);
    return n3 >= MAX_NUM ? sign$2(t5) : n3 <= THRESH ? sign$2(t5) * r2(n3) : n3 <= 4 ? sign$2(t5) * (1 - i2(n3)) : sign$2(t5) * (1 - a2(n3));
  }, "Array | Matrix": n2.referToSelf((t5) => (n3) => deepMap(n3, t5)) });
  function r2(t5) {
    var n3 = t5 * t5, r3 = P[0][4] * n3, i3 = n3, a3;
    for (a3 = 0; a3 < 3; a3 += 1) r3 = (r3 + P[0][a3]) * n3, i3 = (i3 + Q[0][a3]) * n3;
    return t5 * (r3 + P[0][3]) / (i3 + Q[0][3]);
  }
  function i2(t5) {
    var n3 = P[1][8] * t5, r3 = t5, i3;
    for (i3 = 0; i3 < 7; i3 += 1) n3 = (n3 + P[1][i3]) * t5, r3 = (r3 + Q[1][i3]) * t5;
    var a3 = (n3 + P[1][7]) / (r3 + Q[1][7]), o2 = parseInt(t5 * 16) / 16, s2 = (t5 - o2) * (t5 + o2);
    return Math.exp(-o2 * o2) * Math.exp(-s2) * a3;
  }
  function a2(t5) {
    var n3 = 1 / (t5 * t5), r3 = P[2][5] * n3, i3 = n3, a3;
    for (a3 = 0; a3 < 4; a3 += 1) r3 = (r3 + P[2][a3]) * n3, i3 = (i3 + Q[2][a3]) * n3;
    var o2 = n3 * (r3 + P[2][4]) / (i3 + Q[2][4]);
    o2 = (SQRPI - o2) / t5, n3 = parseInt(t5 * 16) / 16;
    var s2 = (t5 - n3) * (t5 + n3);
    return Math.exp(-n3 * n3) * Math.exp(-s2) * o2;
  }
}), THRESH = 0.46875, SQRPI = 0.5641895835477563, P = [[3.1611237438705655, 113.86415415105016, 377.485237685302, 3209.3775891384694, 0.18577770618460315], [0.5641884969886701, 8.883149794388377, 66.11919063714163, 298.6351381974001, 881.952221241769, 1712.0476126340707, 2051.0783778260716, 1230.3393547979972, 21531153547440383e-24], [0.30532663496123236, 0.36034489994980445, 0.12578172611122926, 0.016083785148742275, 6587491615298378e-19, 0.016315387137302097]], Q = [[23.601290952344122, 244.02463793444417, 1282.6165260773723, 2844.236833439171], [15.744926110709835, 117.6939508913125, 537.1811018620099, 1621.3895745666903, 3290.7992357334597, 4362.619090143247, 3439.3676741437216, 1230.3393548037495], [2.568520192289822, 1.8729528499234604, 0.5279051029514285, 0.06051834131244132, 0.0023352049762686918]], MAX_NUM = 2 ** 53, name$205 = `zeta`, dependencies$205 = [`typed`, `config`, `multiply`, `pow`, `divide`, `factorial`, `equal`, `smallerEq`, `isBounded`, `isNegative`, `gamma`, `sin`, `subtract`, `add`, `?Complex`, `?BigNumber`, `pi`], createZeta = factory(name$205, dependencies$205, (t4) => {
  var { typed: n2, config: r2, multiply: i2, pow: a2, divide: o2, factorial: s2, equal: c2, smallerEq: l2, isBounded: u2, isNegative: d2, gamma: p2, sin: m2, subtract: h2, add: g2, Complex: v2, BigNumber: y2, pi: b2 } = t4;
  return n2(name$205, { number: (t5) => x2(t5, (t6) => t6, () => 20), BigNumber: (t5) => x2(t5, (t6) => new y2(t6), () => Math.abs(Math.log10(r2.relTol))), Complex: S2 });
  function x2(t5, n3, r3) {
    return c2(t5, 0) ? n3(-0.5) : c2(t5, 1) ? n3(NaN) : u2(t5) ? w2(t5, n3, r3, (t6) => t6) : d2(t5) ? n3(NaN) : n3(1);
  }
  function S2(t5) {
    return t5.re === 0 && t5.im === 0 ? new v2(-0.5) : t5.re === 1 ? new v2(NaN, NaN) : t5.re === 1 / 0 && t5.im === 0 ? new v2(1) : t5.im === 1 / 0 || t5.re === -1 / 0 ? new v2(NaN, NaN) : w2(t5, (t6) => t6, (t6) => Math.round(1.3 * 15 + 0.9 * Math.abs(t6.im)), (t6) => t6.re);
  }
  function w2(t5, n3, r3, s3) {
    var c3 = r3(t5);
    if (s3(t5) > -(c3 - 1) / 2) return D(t5, n3(c3), n3);
    var l3 = i2(a2(2, t5), a2(n3(b2), h2(t5, 1)));
    return l3 = i2(l3, m2(i2(o2(n3(b2), 2), t5))), l3 = i2(l3, p2(h2(1, t5))), i2(l3, w2(h2(1, t5), n3, r3, s3));
  }
  function E(t5, n3) {
    for (var r3 = t5, c3 = t5; l2(c3, n3); c3 = g2(c3, 1)) {
      var u3 = o2(i2(s2(g2(n3, h2(c3, 1))), a2(4, c3)), i2(s2(h2(n3, c3)), s2(i2(2, c3))));
      r3 = g2(r3, u3);
    }
    return i2(n3, r3);
  }
  function D(t5, n3, r3) {
    for (var s3 = o2(1, i2(E(r3(0), n3), h2(1, a2(2, h2(1, t5))))), c3 = r3(0), u3 = r3(1); l2(u3, n3); u3 = g2(u3, 1)) c3 = g2(c3, o2(i2((-1) ** (u3 - 1), E(u3, n3)), a2(u3, t5)));
    return i2(s3, c3);
  }
}), name$204 = `mode`, dependencies$204 = [`typed`, `isNaN`, `isNumeric`], createMode = factory(name$204, dependencies$204, (t4) => {
  var { typed: n2, isNaN: r2, isNumeric: i2 } = t4;
  return n2(name$204, { "Array | Matrix": a2, "...": function(t5) {
    return a2(t5);
  } });
  function a2(t5) {
    if (t5 = flatten$4(t5.valueOf()), t5.length === 0) throw Error(`Cannot calculate mode of an empty array`);
    for (var n3 = {}, a3 = [], o2 = 0, s2 = 0; s2 < t5.length; s2++) {
      var c2 = t5[s2];
      if (i2(c2) && r2(c2)) throw Error(`Cannot calculate mode of an array containing NaN values`);
      c2 in n3 || (n3[c2] = 0), n3[c2]++, n3[c2] === o2 ? a3.push(c2) : n3[c2] > o2 && (o2 = n3[c2], a3 = [c2]);
    }
    return a3;
  }
});
function improveErrorMessage(t4, n2, r2) {
  var i2;
  return String(t4).includes(`Unexpected type`) ? (i2 = arguments.length > 2 ? ` (type: ` + typeOf(r2) + `, value: ` + JSON.stringify(r2) + `)` : ` (type: ` + t4.data.actual + `)`, TypeError(`Cannot calculate ` + n2 + `, unexpected type of argument` + i2)) : String(t4).includes(`complex numbers`) ? (i2 = arguments.length > 2 ? ` (type: ` + typeOf(r2) + `, value: ` + JSON.stringify(r2) + `)` : ``, TypeError(`Cannot calculate ` + n2 + `, no ordering relation is defined for complex numbers` + i2)) : t4;
}
var name$203 = `prod`, dependencies$203 = [`typed`, `config`, `multiplyScalar`, `numeric`], createProd = factory(name$203, dependencies$203, (t4) => {
  var { typed: n2, config: r2, multiplyScalar: i2, numeric: a2 } = t4;
  return n2(name$203, { "Array | Matrix": o2, "Array | Matrix, number | BigNumber": function(t5, n3) {
    throw Error(`prod(A, dim) is not yet supported`);
  }, "...": function(t5) {
    return o2(t5);
  } });
  function o2(t5) {
    var n3;
    if (deepForEach(t5, function(t6) {
      try {
        n3 = n3 === void 0 ? t6 : i2(n3, t6);
      } catch (n4) {
        throw improveErrorMessage(n4, `prod`, t6);
      }
    }), typeof n3 == `string` && (n3 = a2(n3, safeNumberType(n3, r2))), n3 === void 0) throw Error(`Cannot calculate prod of an empty array`);
    return n3;
  }
}), name$202 = `format`, dependencies$202 = [`typed`], createFormat = factory(name$202, dependencies$202, (t4) => {
  var { typed: n2 } = t4;
  return n2(name$202, { any: format, "any, Object | function | number | BigNumber": format });
}), name$201 = `bin`, dependencies$201 = [`typed`, `format`], createBin = factory(name$201, dependencies$201, (t4) => {
  var { typed: n2, format: r2 } = t4;
  return n2(name$201, { "number | BigNumber": function(t5) {
    return r2(t5, { notation: `bin` });
  }, "number | BigNumber, number | BigNumber": function(t5, n3) {
    return r2(t5, { notation: `bin`, wordSize: n3 });
  } });
}), name$200 = `oct`, dependencies$200 = [`typed`, `format`], createOct = factory(name$200, dependencies$200, (t4) => {
  var { typed: n2, format: r2 } = t4;
  return n2(name$200, { "number | BigNumber": function(t5) {
    return r2(t5, { notation: `oct` });
  }, "number | BigNumber, number | BigNumber": function(t5, n3) {
    return r2(t5, { notation: `oct`, wordSize: n3 });
  } });
}), name$199 = `hex`, dependencies$199 = [`typed`, `format`], createHex = factory(name$199, dependencies$199, (t4) => {
  var { typed: n2, format: r2 } = t4;
  return n2(name$199, { "number | BigNumber": function(t5) {
    return r2(t5, { notation: `hex` });
  }, "number | BigNumber, number | BigNumber": function(t5, n3) {
    return r2(t5, { notation: `hex`, wordSize: n3 });
  } });
}), printTemplate = /\$([\w.]+)/g, name$198 = `print`, dependencies$198 = [`typed`], createPrint = factory(name$198, dependencies$198, (t4) => {
  var { typed: n2 } = t4;
  return n2(name$198, { "string, Object | Array": _print, "string, Object | Array, number | Object": _print });
});
function _print(t4, n2, r2) {
  return t4.replace(printTemplate, function(t5, i2) {
    var a2 = i2.split(`.`), o2 = n2[a2.shift()];
    for (o2 !== void 0 && o2.isMatrix && (o2 = o2.toArray()); a2.length && o2 !== void 0; ) {
      var s2 = a2.shift();
      o2 = s2 ? o2[s2] : o2 + `.`;
    }
    return o2 === void 0 ? t5 : isString(o2) ? o2 : format(o2, r2);
  });
}
var name$197 = `to`, dependencies$197 = [`typed`, `matrix`, `concat`], createTo = factory(name$197, dependencies$197, (t4) => {
  var { typed: n2, matrix: r2, concat: i2 } = t4;
  return n2(name$197, { "Unit, Unit | string": (t5, n3) => t5.to(n3) }, createMatrixAlgorithmSuite({ typed: n2, matrix: r2, concat: i2 })({ Ds: true }));
}), name$196 = `toBest`, dependencies$196 = [`typed`], createToBest = factory(name$196, dependencies$196, (t4) => {
  var { typed: n2 } = t4;
  return n2(name$196, { Unit: (t5) => t5.toBest(), "Unit, string": (t5, n3) => t5.toBest(n3.split(`,`)), "Unit, string, Object": (t5, n3, r2) => t5.toBest(n3.split(`,`), r2), "Unit, Array": (t5, n3) => t5.toBest(n3), "Unit, Array, Object": (t5, n3, r2) => t5.toBest(n3, r2) });
}), name$195 = `isPrime`, dependencies$195 = [`typed`], createIsPrime = factory(name$195, dependencies$195, (t4) => {
  var { typed: n2 } = t4;
  return n2(name$195, { number: function(t5) {
    if (t5 <= 3) return t5 > 1;
    if (t5 % 2 == 0 || t5 % 3 == 0) return false;
    for (var n3 = 5; n3 * n3 <= t5; n3 += 6) if (t5 % n3 === 0 || t5 % (n3 + 2) === 0) return false;
    return true;
  }, bigint: function(t5) {
    if (t5 <= 3n) return t5 > 1n;
    if (t5 % 2n == 0n || t5 % 3n == 0n) return false;
    for (var n3 = 5n; n3 * n3 <= t5; n3 += 6n) if (t5 % n3 === 0n || t5 % (n3 + 2n) === 0n) return false;
    return true;
  }, BigNumber: function(t5) {
    if (t5.lte(3)) return t5.gt(1);
    if (t5.mod(2).eq(0) || t5.mod(3).eq(0)) return false;
    if (t5.lt(2 ** 32)) {
      for (var n3 = t5.toNumber(), r2 = 5; r2 * r2 <= n3; r2 += 6) if (n3 % r2 === 0 || n3 % (r2 + 2) === 0) return false;
      return true;
    }
    function i2(t6, n4, r3) {
      for (var i3 = 1; !n4.eq(0); ) n4.mod(2).eq(0) ? (n4 = n4.div(2), t6 = t6.mul(t6).mod(r3)) : (n4 = n4.sub(1), i3 = t6.mul(i3).mod(r3));
      return i3;
    }
    t5 = new (t5.constructor.clone({ precision: t5.toFixed(0).length * 2 }))(t5);
    for (var a2 = 0, o2 = t5.sub(1); o2.mod(2).eq(0); ) o2 = o2.div(2), a2 += 1;
    var s2 = null;
    if (t5.lt(`3317044064679887385961981`)) s2 = [2, 3, 5, 7, 11, 13, 17, 19, 23, 29, 31, 37, 41].filter((n4) => n4 < t5);
    else {
      var c2 = Math.min(t5.toNumber() - 2, Math.floor(2 * (t5.toFixed(0).length * Math.log(10)) ** 2));
      s2 = [];
      for (var l2 = 2; l2 <= c2; l2 += 1) s2.push(c2);
    }
    for (var u2 = 0; u2 < s2.length; u2 += 1) {
      var d2 = s2[u2], p2 = i2(t5.sub(t5).add(d2), o2, t5);
      if (!p2.eq(1)) {
        for (var m2 = 0, h2 = p2; !h2.eq(t5.sub(1)); m2 += 1, h2 = h2.mul(h2).mod(t5)) if (m2 === a2 - 1) return false;
      }
    }
    return true;
  }, "Array | Matrix": n2.referToSelf((t5) => (n3) => deepMap(n3, t5)) });
}), name$194 = `numeric`, dependencies$194 = [`number`, `?bignumber`, `?fraction`], createNumeric = factory(name$194, dependencies$194, (t4) => {
  var { number: n2, bignumber: r2, fraction: i2 } = t4, a2 = { string: true, number: true, BigNumber: true, Fraction: true }, o2 = { number: (t5) => n2(t5), BigNumber: r2 ? (t5) => r2(t5) : noBignumber, bigint: (t5) => BigInt(t5), Fraction: i2 ? (t5) => i2(t5) : noFraction };
  return function(t5) {
    var n3 = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : `number`;
    if ((arguments.length > 2 ? arguments[2] : void 0) !== void 0) throw SyntaxError(`numeric() takes one or two arguments`);
    var r3 = typeOf(t5);
    if (!(r3 in a2)) throw TypeError(`Cannot convert ` + t5 + ` of type "` + r3 + `"; valid input types are ` + Object.keys(a2).join(`, `));
    if (!(n3 in o2)) throw TypeError(`Cannot convert ` + t5 + ` to type "` + n3 + `"; valid output types are ` + Object.keys(o2).join(`, `));
    return n3 === r3 ? t5 : o2[n3](t5);
  };
}), name$193 = `divideScalar`, dependencies$193 = [`typed`, `numeric`], createDivideScalar = factory(name$193, dependencies$193, (t4) => {
  var { typed: n2, numeric: r2 } = t4;
  return n2(name$193, { "number, number": function(t5, n3) {
    return t5 / n3;
  }, "Complex, Complex": function(t5, n3) {
    return t5.div(n3);
  }, "BigNumber, BigNumber": function(t5, n3) {
    return t5.div(n3);
  }, "bigint, bigint": function(t5, n3) {
    return t5 / n3;
  }, "Fraction, Fraction": function(t5, n3) {
    return t5.div(n3);
  }, "Unit, number | Complex | Fraction | BigNumber | Unit": (t5, n3) => t5.divide(n3), "number | Fraction | Complex | BigNumber, Unit": (t5, n3) => n3.divideInto(t5) });
}), name$192 = `pow`, dependencies$192 = [`typed`, `config`, `identity`, `multiply`, `matrix`, `inv`, `fraction`, `number`, `Complex`], createPow = factory(name$192, dependencies$192, (t4) => {
  var { typed: n2, config: r2, identity: i2, multiply: a2, matrix: o2, inv: s2, number: c2, fraction: l2, Complex: u2 } = t4;
  return n2(name$192, { "number, number": d2, "Complex, Complex": function(t5, n3) {
    return t5.pow(n3);
  }, "BigNumber, BigNumber": function(t5, n3) {
    return n3.isInteger() || t5 >= 0 || r2.predictable ? t5.pow(n3) : new u2(t5.toNumber(), 0).pow(n3.toNumber(), 0);
  }, "bigint, bigint": (t5, n3) => t5 ** n3, "Fraction, Fraction": function(t5, n3) {
    var i3 = t5.pow(n3);
    if (i3 != null) return i3;
    if (r2.predictable) throw Error(`Result of pow is non-rational and cannot be expressed as a fraction`);
    return d2(t5.valueOf(), n3.valueOf());
  }, "Array, number": p2, "Array, BigNumber": function(t5, n3) {
    return p2(t5, n3.toNumber());
  }, "Matrix, number": m2, "Matrix, BigNumber": function(t5, n3) {
    return m2(t5, n3.toNumber());
  }, "Unit, number | BigNumber": function(t5, n3) {
    return t5.pow(n3);
  } });
  function d2(t5, n3) {
    if (r2.predictable && !isInteger(n3) && t5 < 0) try {
      var i3 = l2(n3), a3 = c2(i3);
      if ((n3 === a3 || Math.abs((n3 - a3) / n3) < 1e-14) && i3.d % 2n == 1n) return (i3.n % 2n == 0n ? 1 : -1) * (-t5) ** +n3;
    } catch {
    }
    return r2.predictable && (t5 < -1 && n3 === 1 / 0 || t5 > -1 && t5 < 0 && n3 === -1 / 0) ? NaN : isInteger(n3) || t5 >= 0 || r2.predictable ? powNumber(t5, n3) : t5 * t5 < 1 && n3 === 1 / 0 || t5 * t5 > 1 && n3 === -1 / 0 ? 0 : new u2(t5, 0).pow(n3, 0);
  }
  function p2(t5, n3) {
    if (!isInteger(n3)) throw TypeError(`For A^b, b must be an integer (value is ` + n3 + `)`);
    var r3 = arraySize(t5);
    if (r3.length !== 2) throw Error(`For A^b, A must be 2 dimensional (A has ` + r3.length + ` dimensions)`);
    if (r3[0] !== r3[1]) throw Error(`For A^b, A must be square (size is ` + r3[0] + `x` + r3[1] + `)`);
    if (n3 < 0) try {
      return p2(s2(t5), -n3);
    } catch (t6) {
      throw t6.message === `Cannot calculate inverse, determinant is zero` ? TypeError(`For A^b, when A is not invertible, b must be a positive integer (value is ` + n3 + `)`) : t6;
    }
    for (var o3 = i2(r3[0]).valueOf(), c3 = t5; n3 >= 1; ) (n3 & 1) == 1 && (o3 = a2(c3, o3)), n3 >>= 1, c3 = a2(c3, c3);
    return o3;
  }
  function m2(t5, n3) {
    return o2(p2(t5.valueOf(), n3));
  }
}), NO_INT = `Number of decimals in function round must be an integer`, name$191 = `round`, dependencies$191 = [`typed`, `config`, `matrix`, `equalScalar`, `zeros`, `BigNumber`, `DenseMatrix`], createRound = factory(name$191, dependencies$191, (t4) => {
  var { typed: n2, config: r2, matrix: i2, equalScalar: a2, zeros: o2, BigNumber: s2, DenseMatrix: c2 } = t4, l2 = createMatAlgo11xS0s({ typed: n2, equalScalar: a2 }), u2 = createMatAlgo12xSfs({ typed: n2, DenseMatrix: c2 }), d2 = createMatAlgo14xDs({ typed: n2 });
  function p2(t5) {
    return Math.abs(splitNumber(t5).exponent);
  }
  return n2(name$191, { number: function(t5) {
    var n3 = roundNumber(t5, p2(r2.relTol));
    return roundNumber(nearlyEqual$1(t5, n3, r2.relTol, r2.absTol) ? n3 : t5);
  }, "number, number": function(t5, n3) {
    var i3 = p2(r2.relTol);
    if (n3 >= i3) return roundNumber(t5, n3);
    var a3 = roundNumber(t5, i3);
    return roundNumber(nearlyEqual$1(t5, a3, r2.relTol, r2.absTol) ? a3 : t5, n3);
  }, "number, BigNumber": function(t5, n3) {
    if (!n3.isInteger()) throw TypeError(NO_INT);
    return new s2(t5).toDecimalPlaces(n3.toNumber());
  }, Complex: function(t5) {
    return t5.round();
  }, "Complex, number": function(t5, n3) {
    if (n3 % 1) throw TypeError(NO_INT);
    return t5.round(n3);
  }, "Complex, BigNumber": function(t5, n3) {
    if (!n3.isInteger()) throw TypeError(NO_INT);
    var r3 = n3.toNumber();
    return t5.round(r3);
  }, BigNumber: function(t5) {
    var n3 = new s2(t5).toDecimalPlaces(p2(r2.relTol));
    return (nearlyEqual(t5, n3, r2.relTol, r2.absTol) ? n3 : t5).toDecimalPlaces(0);
  }, "BigNumber, BigNumber": function(t5, n3) {
    if (!n3.isInteger()) throw TypeError(NO_INT);
    var i3 = p2(r2.relTol);
    if (n3 >= i3) return t5.toDecimalPlaces(n3.toNumber());
    var a3 = t5.toDecimalPlaces(i3);
    return (nearlyEqual(t5, a3, r2.relTol, r2.absTol) ? a3 : t5).toDecimalPlaces(n3.toNumber());
  }, bigint: (t5) => t5, "bigint, number": (t5, n3) => t5, "bigint, BigNumber": (t5, n3) => t5, Fraction: function(t5) {
    return t5.round();
  }, "Fraction, number": function(t5, n3) {
    if (n3 % 1) throw TypeError(NO_INT);
    return t5.round(n3);
  }, "Fraction, BigNumber": function(t5, n3) {
    if (!n3.isInteger()) throw TypeError(NO_INT);
    return t5.round(n3.toNumber());
  }, "Unit, number, Unit": n2.referToSelf((t5) => function(n3, r3, i3) {
    var a3 = n3.toNumeric(i3);
    return i3.multiply(t5(a3, r3));
  }), "Unit, BigNumber, Unit": n2.referToSelf((t5) => (n3, r3, i3) => t5(n3, r3.toNumber(), i3)), "Array | Matrix, number | BigNumber, Unit": n2.referToSelf((t5) => (n3, r3, i3) => deepMap(n3, (n4) => t5(n4, r3, i3), true)), "Array | Matrix | Unit, Unit": n2.referToSelf((t5) => (n3, r3) => t5(n3, 0, r3)), "Array | Matrix": n2.referToSelf((t5) => (n3) => deepMap(n3, t5, true)), "SparseMatrix, number | BigNumber": n2.referToSelf((t5) => (n3, r3) => l2(n3, r3, t5, false)), "DenseMatrix, number | BigNumber": n2.referToSelf((t5) => (n3, r3) => d2(n3, r3, t5, false)), "Array, number | BigNumber": n2.referToSelf((t5) => (n3, r3) => d2(i2(n3), r3, t5, false).valueOf()), "number | Complex | BigNumber | Fraction, SparseMatrix": n2.referToSelf((t5) => (n3, r3) => a2(n3, 0) ? o2(r3.size(), r3.storage()) : u2(r3, n3, t5, true)), "number | Complex | BigNumber | Fraction, DenseMatrix": n2.referToSelf((t5) => (n3, r3) => a2(n3, 0) ? o2(r3.size(), r3.storage()) : d2(r3, n3, t5, true)), "number | Complex | BigNumber | Fraction, Array": n2.referToSelf((t5) => (n3, r3) => d2(i2(r3), n3, t5, true).valueOf()) });
}), name$190 = `log`, dependencies$190 = [`config`, `typed`, `typeOf`, `divideScalar`, `Complex`], nlg16 = Math.log(16), createLog = factory(name$190, dependencies$190, (t4) => {
  var { typed: n2, typeOf: r2, config: i2, divideScalar: a2, Complex: o2 } = t4;
  function s2(t5) {
    return t5.log();
  }
  function c2(t5) {
    return s2(new o2(t5, 0));
  }
  return n2(name$190, { number: function(t5) {
    return t5 >= 0 || i2.predictable ? logNumber(t5) : c2(t5);
  }, bigint: promoteLogarithm(nlg16, logNumber, i2, c2), Complex: s2, BigNumber: function(t5) {
    return !t5.isNegative() || i2.predictable ? t5.ln() : c2(t5.toNumber());
  }, "any, any": n2.referToSelf((t5) => (n3, i3) => {
    if (r2(n3) === `Fraction` && r2(i3) === `Fraction`) {
      var o3 = n3.log(i3);
      if (o3 !== null) return o3;
    }
    return a2(t5(n3), t5(i3));
  }) });
}), name$189 = `log1p`, dependencies$189 = [`typed`, `config`, `divideScalar`, `log`, `Complex`], createLog1p = factory(name$189, dependencies$189, (t4) => {
  var { typed: n2, config: r2, divideScalar: i2, log: a2, Complex: o2 } = t4;
  return n2(name$189, { number: function(t5) {
    return t5 >= -1 || r2.predictable ? log1p(t5) : s2(new o2(t5, 0));
  }, Complex: s2, BigNumber: function(t5) {
    var n3 = t5.plus(1);
    return !n3.isNegative() || r2.predictable ? n3.ln() : s2(new o2(t5.toNumber(), 0));
  }, "Array | Matrix": n2.referToSelf((t5) => (n3) => deepMap(n3, t5)), "any, any": n2.referToSelf((t5) => (n3, r3) => i2(t5(n3), a2(r3))) });
  function s2(t5) {
    var n3 = t5.re + 1;
    return new o2(Math.log(Math.sqrt(n3 * n3 + t5.im * t5.im)), Math.atan2(t5.im, n3));
  }
}), name$188 = `nthRoots`, dependencies$188 = [`config`, `typed`, `divideScalar`, `Complex`], createNthRoots = factory(name$188, dependencies$188, (t4) => {
  var { typed: n2, config: r2, divideScalar: i2, Complex: a2 } = t4, o2 = [function(t5) {
    return new a2(t5, 0);
  }, function(t5) {
    return new a2(0, t5);
  }, function(t5) {
    return new a2(-t5, 0);
  }, function(t5) {
    return new a2(0, -t5);
  }];
  function s2(t5, n3) {
    if (n3 < 0) throw Error(`Root must be greater than zero`);
    if (n3 === 0) throw Error(`Root must be non-zero`);
    if (n3 % 1 != 0) throw Error(`Root must be an integer`);
    if (t5 === 0 || t5.abs() === 0) return [new a2(0, 0)];
    var r3 = typeof t5 == `number`, i3;
    (r3 || t5.re === 0 || t5.im === 0) && (i3 = r3 ? 2 * (t5 < 0) : t5.im === 0 ? 2 * (t5.re < 0) : 2 * (t5.im < 0) + 1);
    for (var s3 = t5.arg(), c2 = t5.abs(), l2 = [], u2 = c2 ** (1 / n3), d2 = 0; d2 < n3; d2++) {
      var p2 = (i3 + 4 * d2) / n3;
      if (p2 === Math.round(p2)) {
        l2.push(o2[p2 % 4](u2));
        continue;
      }
      l2.push(new a2({ r: u2, phi: (s3 + 2 * Math.PI * d2) / n3 }));
    }
    return l2;
  }
  return n2(name$188, { Complex: function(t5) {
    return s2(t5, 2);
  }, "Complex, number": s2 });
}), name$187 = `dotPow`, dependencies$187 = [`typed`, `equalScalar`, `matrix`, `pow`, `DenseMatrix`, `concat`, `SparseMatrix`], createDotPow = factory(name$187, dependencies$187, (t4) => {
  var { typed: n2, equalScalar: r2, matrix: i2, pow: a2, DenseMatrix: o2, concat: s2, SparseMatrix: c2 } = t4, l2 = createMatAlgo03xDSf({ typed: n2 }), u2 = createMatAlgo07xSSf({ typed: n2, SparseMatrix: c2 }), d2 = createMatAlgo11xS0s({ typed: n2, equalScalar: r2 }), p2 = createMatAlgo12xSfs({ typed: n2, DenseMatrix: o2 }), m2 = createMatrixAlgorithmSuite({ typed: n2, matrix: i2, concat: s2 }), h2 = {};
  for (var g2 in a2.signatures) Object.prototype.hasOwnProperty.call(a2.signatures, g2) && !g2.includes(`Matrix`) && !g2.includes(`Array`) && (h2[g2] = a2.signatures[g2]);
  return n2(name$187, m2({ elop: n2(h2), SS: u2, DS: l2, Ss: d2, sS: p2 }));
}), name$186 = `dotDivide`, dependencies$186 = [`typed`, `matrix`, `equalScalar`, `divideScalar`, `DenseMatrix`, `concat`, `SparseMatrix`], createDotDivide = factory(name$186, dependencies$186, (t4) => {
  var { typed: n2, matrix: r2, equalScalar: i2, divideScalar: a2, DenseMatrix: o2, concat: s2, SparseMatrix: c2 } = t4, l2 = createMatAlgo02xDS0({ typed: n2, equalScalar: i2 }), u2 = createMatAlgo03xDSf({ typed: n2 }), d2 = createMatAlgo07xSSf({ typed: n2, SparseMatrix: c2 }), p2 = createMatAlgo11xS0s({ typed: n2, equalScalar: i2 }), m2 = createMatAlgo12xSfs({ typed: n2, DenseMatrix: o2 });
  return n2(name$186, createMatrixAlgorithmSuite({ typed: n2, matrix: r2, concat: s2 })({ elop: a2, SS: d2, DS: u2, SD: l2, Ss: p2, sS: m2 }));
});
function createSolveValidation(t4) {
  var { DenseMatrix: n2 } = t4;
  return function(t5, r2, i2) {
    var a2 = t5.size();
    if (a2.length !== 2) throw RangeError(`Matrix must be two dimensional (size: ` + format(a2) + `)`);
    var o2 = a2[0];
    if (o2 !== a2[1]) throw RangeError(`Matrix must be square (size: ` + format(a2) + `)`);
    var s2 = [];
    if (isMatrix(r2)) {
      var c2 = r2.size(), l2 = r2._data;
      if (c2.length === 1) {
        if (c2[0] !== o2) throw RangeError(`Dimension mismatch. Matrix columns must match vector length.`);
        for (var u2 = 0; u2 < o2; u2++) s2[u2] = [l2[u2]];
        return new n2({ data: s2, size: [o2, 1], datatype: r2._datatype });
      }
      if (c2.length === 2) {
        if (c2[0] !== o2 || c2[1] !== 1) throw RangeError(`Dimension mismatch. Matrix columns must match vector length.`);
        if (isDenseMatrix(r2)) {
          if (i2) {
            s2 = [];
            for (var d2 = 0; d2 < o2; d2++) s2[d2] = [l2[d2][0]];
            return new n2({ data: s2, size: [o2, 1], datatype: r2._datatype });
          }
          return r2;
        }
        if (isSparseMatrix(r2)) {
          for (var p2 = 0; p2 < o2; p2++) s2[p2] = [0];
          for (var m2 = r2._values, h2 = r2._index, g2 = r2._ptr, v2 = g2[1], y2 = g2[0]; y2 < v2; y2++) {
            var b2 = h2[y2];
            s2[b2][0] = m2[y2];
          }
          return new n2({ data: s2, size: [o2, 1], datatype: r2._datatype });
        }
      }
      throw RangeError(`Dimension mismatch. The right side has to be either 1- or 2-dimensional vector.`);
    }
    if (isArray(r2)) {
      var x2 = arraySize(r2);
      if (x2.length === 1) {
        if (x2[0] !== o2) throw RangeError(`Dimension mismatch. Matrix columns must match vector length.`);
        for (var S2 = 0; S2 < o2; S2++) s2[S2] = [r2[S2]];
        return new n2({ data: s2, size: [o2, 1] });
      }
      if (x2.length === 2) {
        if (x2[0] !== o2 || x2[1] !== 1) throw RangeError(`Dimension mismatch. Matrix columns must match vector length.`);
        for (var w2 = 0; w2 < o2; w2++) s2[w2] = [r2[w2][0]];
        return new n2({ data: s2, size: [o2, 1] });
      }
      throw RangeError(`Dimension mismatch. The right side has to be either 1- or 2-dimensional vector.`);
    }
  };
}
var name$185 = `lsolve`, dependencies$185 = [`typed`, `matrix`, `divideScalar`, `multiplyScalar`, `subtractScalar`, `equalScalar`, `DenseMatrix`], createLsolve = factory(name$185, dependencies$185, (t4) => {
  var { typed: n2, matrix: r2, divideScalar: i2, multiplyScalar: a2, subtractScalar: o2, equalScalar: s2, DenseMatrix: c2 } = t4, l2 = createSolveValidation({ DenseMatrix: c2 });
  return n2(name$185, { "SparseMatrix, Array | Matrix": function(t5, n3) {
    return d2(t5, n3);
  }, "DenseMatrix, Array | Matrix": function(t5, n3) {
    return u2(t5, n3);
  }, "Array, Array | Matrix": function(t5, n3) {
    return u2(r2(t5), n3).valueOf();
  } });
  function u2(t5, n3) {
    n3 = l2(t5, n3, true);
    for (var r3 = n3._data, u3 = t5._size[0], d3 = t5._size[1], p2 = [], m2 = t5._data, h2 = 0; h2 < d3; h2++) {
      var g2 = r3[h2][0] || 0, v2 = void 0;
      if (s2(g2, 0)) v2 = 0;
      else {
        var y2 = m2[h2][h2];
        if (s2(y2, 0)) throw Error(`Linear system cannot be solved since matrix is singular`);
        v2 = i2(g2, y2);
        for (var b2 = h2 + 1; b2 < u3; b2++) r3[b2] = [o2(r3[b2][0] || 0, a2(v2, m2[b2][h2]))];
      }
      p2[h2] = [v2];
    }
    return new c2({ data: p2, size: [u3, 1] });
  }
  function d2(t5, n3) {
    n3 = l2(t5, n3, true);
    for (var r3 = n3._data, u3 = t5._size[0], d3 = t5._size[1], p2 = t5._values, m2 = t5._index, h2 = t5._ptr, g2 = [], v2 = 0; v2 < d3; v2++) {
      var y2 = r3[v2][0] || 0;
      if (s2(y2, 0)) g2[v2] = [0];
      else {
        for (var b2 = 0, x2 = [], S2 = [], w2 = h2[v2], E = h2[v2 + 1], D = w2; D < E; D++) {
          var O = m2[D];
          O === v2 ? b2 = p2[D] : O > v2 && (x2.push(p2[D]), S2.push(O));
        }
        if (s2(b2, 0)) throw Error(`Linear system cannot be solved since matrix is singular`);
        for (var j2 = i2(y2, b2), M2 = 0, N2 = S2.length; M2 < N2; M2++) {
          var F = S2[M2];
          r3[F] = [o2(r3[F][0] || 0, a2(j2, x2[M2]))];
        }
        g2[v2] = [j2];
      }
    }
    return new c2({ data: g2, size: [u3, 1] });
  }
}), name$184 = `usolve`, dependencies$184 = [`typed`, `matrix`, `divideScalar`, `multiplyScalar`, `subtractScalar`, `equalScalar`, `DenseMatrix`], createUsolve = factory(name$184, dependencies$184, (t4) => {
  var { typed: n2, matrix: r2, divideScalar: i2, multiplyScalar: a2, subtractScalar: o2, equalScalar: s2, DenseMatrix: c2 } = t4, l2 = createSolveValidation({ DenseMatrix: c2 });
  return n2(name$184, { "SparseMatrix, Array | Matrix": function(t5, n3) {
    return d2(t5, n3);
  }, "DenseMatrix, Array | Matrix": function(t5, n3) {
    return u2(t5, n3);
  }, "Array, Array | Matrix": function(t5, n3) {
    return u2(r2(t5), n3).valueOf();
  } });
  function u2(t5, n3) {
    n3 = l2(t5, n3, true);
    for (var r3 = n3._data, u3 = t5._size[0], d3 = t5._size[1], p2 = [], m2 = t5._data, h2 = d3 - 1; h2 >= 0; h2--) {
      var g2 = r3[h2][0] || 0, v2 = void 0;
      if (s2(g2, 0)) v2 = 0;
      else {
        var y2 = m2[h2][h2];
        if (s2(y2, 0)) throw Error(`Linear system cannot be solved since matrix is singular`);
        v2 = i2(g2, y2);
        for (var b2 = h2 - 1; b2 >= 0; b2--) r3[b2] = [o2(r3[b2][0] || 0, a2(v2, m2[b2][h2]))];
      }
      p2[h2] = [v2];
    }
    return new c2({ data: p2, size: [u3, 1] });
  }
  function d2(t5, n3) {
    n3 = l2(t5, n3, true);
    for (var r3 = n3._data, u3 = t5._size[0], d3 = t5._size[1], p2 = t5._values, m2 = t5._index, h2 = t5._ptr, g2 = [], v2 = d3 - 1; v2 >= 0; v2--) {
      var y2 = r3[v2][0] || 0;
      if (s2(y2, 0)) g2[v2] = [0];
      else {
        for (var b2 = 0, x2 = [], S2 = [], w2 = h2[v2], E = h2[v2 + 1] - 1; E >= w2; E--) {
          var D = m2[E];
          D === v2 ? b2 = p2[E] : D < v2 && (x2.push(p2[E]), S2.push(D));
        }
        if (s2(b2, 0)) throw Error(`Linear system cannot be solved since matrix is singular`);
        for (var O = i2(y2, b2), j2 = 0, M2 = S2.length; j2 < M2; j2++) {
          var N2 = S2[j2];
          r3[N2] = [o2(r3[N2][0], a2(O, x2[j2]))];
        }
        g2[v2] = [O];
      }
    }
    return new c2({ data: g2, size: [u3, 1] });
  }
}), name$183 = `lsolveAll`, dependencies$183 = [`typed`, `matrix`, `divideScalar`, `multiplyScalar`, `subtractScalar`, `equalScalar`, `DenseMatrix`], createLsolveAll = factory(name$183, dependencies$183, (t4) => {
  var { typed: n2, matrix: r2, divideScalar: i2, multiplyScalar: a2, subtractScalar: o2, equalScalar: s2, DenseMatrix: c2 } = t4, l2 = createSolveValidation({ DenseMatrix: c2 });
  return n2(name$183, { "SparseMatrix, Array | Matrix": function(t5, n3) {
    return d2(t5, n3);
  }, "DenseMatrix, Array | Matrix": function(t5, n3) {
    return u2(t5, n3);
  }, "Array, Array | Matrix": function(t5, n3) {
    return u2(r2(t5), n3).map((t6) => t6.valueOf());
  } });
  function u2(t5, n3) {
    for (var r3 = [l2(t5, n3, true)._data.map((t6) => t6[0])], u3 = t5._data, d3 = t5._size[0], p2 = t5._size[1], m2 = 0; m2 < p2; m2++) for (var h2 = r3.length, g2 = 0; g2 < h2; g2++) {
      var v2 = r3[g2];
      if (!s2(u3[m2][m2], 0)) {
        v2[m2] = i2(v2[m2], u3[m2][m2]);
        for (var y2 = m2 + 1; y2 < p2; y2++) v2[y2] = o2(v2[y2], a2(v2[m2], u3[y2][m2]));
      } else if (!s2(v2[m2], 0)) {
        if (g2 === 0) return [];
        r3.splice(g2, 1), --g2, --h2;
      } else if (g2 === 0) {
        var b2 = [...v2];
        b2[m2] = 1;
        for (var x2 = m2 + 1; x2 < p2; x2++) b2[x2] = o2(b2[x2], u3[x2][m2]);
        r3.push(b2);
      }
    }
    return r3.map((t6) => new c2({ data: t6.map((t7) => [t7]), size: [d3, 1] }));
  }
  function d2(t5, n3) {
    for (var r3 = [l2(t5, n3, true)._data.map((t6) => t6[0])], u3 = t5._size[0], d3 = t5._size[1], p2 = t5._values, m2 = t5._index, h2 = t5._ptr, g2 = 0; g2 < d3; g2++) for (var v2 = r3.length, y2 = 0; y2 < v2; y2++) {
      for (var b2 = r3[y2], x2 = [], S2 = [], w2 = h2[g2], E = h2[g2 + 1], D = 0, O = w2; O < E; O++) {
        var j2 = m2[O];
        j2 === g2 ? D = p2[O] : j2 > g2 && (x2.push(p2[O]), S2.push(j2));
      }
      if (!s2(D, 0)) {
        b2[g2] = i2(b2[g2], D);
        for (var M2 = 0, N2 = S2.length; M2 < N2; M2++) {
          var F = S2[M2];
          b2[F] = o2(b2[F], a2(b2[g2], x2[M2]));
        }
      } else if (!s2(b2[g2], 0)) {
        if (y2 === 0) return [];
        r3.splice(y2, 1), --y2, --v2;
      } else if (y2 === 0) {
        var I = [...b2];
        I[g2] = 1;
        for (var L = 0, R = S2.length; L < R; L++) {
          var z = S2[L];
          I[z] = o2(I[z], x2[L]);
        }
        r3.push(I);
      }
    }
    return r3.map((t6) => new c2({ data: t6.map((t7) => [t7]), size: [u3, 1] }));
  }
}), name$182 = `usolveAll`, dependencies$182 = [`typed`, `matrix`, `divideScalar`, `multiplyScalar`, `subtractScalar`, `equalScalar`, `DenseMatrix`], createUsolveAll = factory(name$182, dependencies$182, (t4) => {
  var { typed: n2, matrix: r2, divideScalar: i2, multiplyScalar: a2, subtractScalar: o2, equalScalar: s2, DenseMatrix: c2 } = t4, l2 = createSolveValidation({ DenseMatrix: c2 });
  return n2(name$182, { "SparseMatrix, Array | Matrix": function(t5, n3) {
    return d2(t5, n3);
  }, "DenseMatrix, Array | Matrix": function(t5, n3) {
    return u2(t5, n3);
  }, "Array, Array | Matrix": function(t5, n3) {
    return u2(r2(t5), n3).map((t6) => t6.valueOf());
  } });
  function u2(t5, n3) {
    for (var r3 = [l2(t5, n3, true)._data.map((t6) => t6[0])], u3 = t5._data, d3 = t5._size[0], p2 = t5._size[1] - 1; p2 >= 0; p2--) for (var m2 = r3.length, h2 = 0; h2 < m2; h2++) {
      var g2 = r3[h2];
      if (!s2(u3[p2][p2], 0)) {
        g2[p2] = i2(g2[p2], u3[p2][p2]);
        for (var v2 = p2 - 1; v2 >= 0; v2--) g2[v2] = o2(g2[v2], a2(g2[p2], u3[v2][p2]));
      } else if (!s2(g2[p2], 0)) {
        if (h2 === 0) return [];
        r3.splice(h2, 1), --h2, --m2;
      } else if (h2 === 0) {
        var y2 = [...g2];
        y2[p2] = 1;
        for (var b2 = p2 - 1; b2 >= 0; b2--) y2[b2] = o2(y2[b2], u3[b2][p2]);
        r3.push(y2);
      }
    }
    return r3.map((t6) => new c2({ data: t6.map((t7) => [t7]), size: [d3, 1] }));
  }
  function d2(t5, n3) {
    for (var r3 = [l2(t5, n3, true)._data.map((t6) => t6[0])], u3 = t5._size[0], d3 = t5._size[1], p2 = t5._values, m2 = t5._index, h2 = t5._ptr, g2 = d3 - 1; g2 >= 0; g2--) for (var v2 = r3.length, y2 = 0; y2 < v2; y2++) {
      for (var b2 = r3[y2], x2 = [], S2 = [], w2 = h2[g2], E = h2[g2 + 1], D = 0, O = E - 1; O >= w2; O--) {
        var j2 = m2[O];
        j2 === g2 ? D = p2[O] : j2 < g2 && (x2.push(p2[O]), S2.push(j2));
      }
      if (!s2(D, 0)) {
        b2[g2] = i2(b2[g2], D);
        for (var M2 = 0, N2 = S2.length; M2 < N2; M2++) {
          var F = S2[M2];
          b2[F] = o2(b2[F], a2(b2[g2], x2[M2]));
        }
      } else if (!s2(b2[g2], 0)) {
        if (y2 === 0) return [];
        r3.splice(y2, 1), --y2, --v2;
      } else if (y2 === 0) {
        var I = [...b2];
        I[g2] = 1;
        for (var L = 0, R = S2.length; L < R; L++) {
          var z = S2[L];
          I[z] = o2(I[z], x2[L]);
        }
        r3.push(I);
      }
    }
    return r3.map((t6) => new c2({ data: t6.map((t7) => [t7]), size: [u3, 1] }));
  }
}), name$181 = `matAlgo08xS0Sid`, dependencies$181 = [`typed`, `equalScalar`], createMatAlgo08xS0Sid = factory(name$181, dependencies$181, (t4) => {
  var { typed: n2, equalScalar: r2 } = t4;
  return function(t5, i2, a2) {
    var o2 = t5._values, s2 = t5._index, c2 = t5._ptr, l2 = t5._size, u2 = t5._datatype || t5._data === void 0 ? t5._datatype : t5.getDataType(), d2 = i2._values, p2 = i2._index, m2 = i2._ptr, h2 = i2._size, g2 = i2._datatype || i2._data === void 0 ? i2._datatype : i2.getDataType();
    if (l2.length !== h2.length) throw new DimensionError(l2.length, h2.length);
    if (l2[0] !== h2[0] || l2[1] !== h2[1]) throw RangeError(`Dimension mismatch. Matrix A (` + l2 + `) must match Matrix B (` + h2 + `)`);
    if (!o2 || !d2) throw Error(`Cannot perform operation on Pattern Sparse Matrices`);
    var v2 = l2[0], y2 = l2[1], b2, x2 = r2, S2 = 0, w2 = a2;
    typeof u2 == `string` && u2 === g2 && u2 !== `mixed` && (b2 = u2, x2 = n2.find(r2, [b2, b2]), S2 = n2.convert(0, b2), w2 = n2.find(a2, [b2, b2]));
    for (var E = [], D = [], O = [], j2 = [], M2 = [], N2, F, I, L, R = 0; R < y2; R++) {
      O[R] = D.length;
      var z = R + 1;
      for (F = c2[R], I = c2[R + 1], N2 = F; N2 < I; N2++) L = s2[N2], M2[L] = z, j2[L] = o2[N2], D.push(L);
      for (F = m2[R], I = m2[R + 1], N2 = F; N2 < I; N2++) L = p2[N2], M2[L] === z && (j2[L] = w2(j2[L], d2[N2]));
      for (N2 = O[R]; N2 < D.length; ) {
        L = D[N2];
        var V = j2[L];
        x2(V, S2) ? D.splice(N2, 1) : (E.push(V), N2++);
      }
    }
    return O[y2] = D.length, t5.createSparseMatrix({ values: E, index: D, ptr: O, size: [v2, y2], datatype: u2 === t5._datatype && g2 === i2._datatype ? b2 : void 0 });
  };
}), createUseMatrixForArrayScalar = factory(`useMatrixForArrayScalar`, [`typed`, `matrix`], (t4) => {
  var { typed: n2, matrix: r2 } = t4;
  return { "Array, number": n2.referTo(`DenseMatrix, number`, (t5) => (n3, i2) => t5(r2(n3), i2).valueOf()), "Array, BigNumber": n2.referTo(`DenseMatrix, BigNumber`, (t5) => (n3, i2) => t5(r2(n3), i2).valueOf()), "number, Array": n2.referTo(`number, DenseMatrix`, (t5) => (n3, i2) => t5(n3, r2(i2)).valueOf()), "BigNumber, Array": n2.referTo(`BigNumber, DenseMatrix`, (t5) => (n3, i2) => t5(n3, r2(i2)).valueOf()) };
}), name$180 = `leftShift`, dependencies$180 = [`typed`, `matrix`, `equalScalar`, `zeros`, `DenseMatrix`, `concat`], createLeftShift = factory(name$180, dependencies$180, (t4) => {
  var { typed: n2, matrix: r2, equalScalar: i2, zeros: a2, DenseMatrix: o2, concat: s2 } = t4, c2 = createMatAlgo01xDSid({ typed: n2 }), l2 = createMatAlgo02xDS0({ typed: n2, equalScalar: i2 }), u2 = createMatAlgo08xS0Sid({ typed: n2, equalScalar: i2 }), d2 = createMatAlgo10xSids({ typed: n2, DenseMatrix: o2 }), p2 = createMatAlgo11xS0s({ typed: n2, equalScalar: i2 }), m2 = createMatAlgo14xDs({ typed: n2 }), h2 = createMatrixAlgorithmSuite({ typed: n2, matrix: r2, concat: s2 }), g2 = createUseMatrixForArrayScalar({ typed: n2, matrix: r2 });
  return n2(name$180, { "number, number": leftShiftNumber, "BigNumber, BigNumber": leftShiftBigNumber, "bigint, bigint": (t5, n3) => t5 << n3, "SparseMatrix, number | BigNumber": n2.referToSelf((t5) => (n3, r3) => i2(r3, 0) ? n3.clone() : p2(n3, r3, t5, false)), "DenseMatrix, number | BigNumber": n2.referToSelf((t5) => (n3, r3) => i2(r3, 0) ? n3.clone() : m2(n3, r3, t5, false)), "number | BigNumber, SparseMatrix": n2.referToSelf((t5) => (n3, r3) => i2(n3, 0) ? a2(r3.size(), r3.storage()) : d2(r3, n3, t5, true)), "number | BigNumber, DenseMatrix": n2.referToSelf((t5) => (n3, r3) => i2(n3, 0) ? a2(r3.size(), r3.storage()) : m2(r3, n3, t5, true)) }, g2, h2({ SS: u2, DS: c2, SD: l2 }));
}), name$179 = `rightArithShift`, dependencies$179 = [`typed`, `matrix`, `equalScalar`, `zeros`, `DenseMatrix`, `concat`], createRightArithShift = factory(name$179, dependencies$179, (t4) => {
  var { typed: n2, matrix: r2, equalScalar: i2, zeros: a2, DenseMatrix: o2, concat: s2 } = t4, c2 = createMatAlgo01xDSid({ typed: n2 }), l2 = createMatAlgo02xDS0({ typed: n2, equalScalar: i2 }), u2 = createMatAlgo08xS0Sid({ typed: n2, equalScalar: i2 }), d2 = createMatAlgo10xSids({ typed: n2, DenseMatrix: o2 }), p2 = createMatAlgo11xS0s({ typed: n2, equalScalar: i2 }), m2 = createMatAlgo14xDs({ typed: n2 }), h2 = createMatrixAlgorithmSuite({ typed: n2, matrix: r2, concat: s2 }), g2 = createUseMatrixForArrayScalar({ typed: n2, matrix: r2 });
  return n2(name$179, { "number, number": rightArithShiftNumber, "BigNumber, BigNumber": rightArithShiftBigNumber, "bigint, bigint": (t5, n3) => t5 >> n3, "SparseMatrix, number | BigNumber": n2.referToSelf((t5) => (n3, r3) => i2(r3, 0) ? n3.clone() : p2(n3, r3, t5, false)), "DenseMatrix, number | BigNumber": n2.referToSelf((t5) => (n3, r3) => i2(r3, 0) ? n3.clone() : m2(n3, r3, t5, false)), "number | BigNumber, SparseMatrix": n2.referToSelf((t5) => (n3, r3) => i2(n3, 0) ? a2(r3.size(), r3.storage()) : d2(r3, n3, t5, true)), "number | BigNumber, DenseMatrix": n2.referToSelf((t5) => (n3, r3) => i2(n3, 0) ? a2(r3.size(), r3.storage()) : m2(r3, n3, t5, true)) }, g2, h2({ SS: u2, DS: c2, SD: l2 }));
}), name$178 = `rightLogShift`, dependencies$178 = [`typed`, `matrix`, `equalScalar`, `zeros`, `DenseMatrix`, `concat`], createRightLogShift = factory(name$178, dependencies$178, (t4) => {
  var { typed: n2, matrix: r2, equalScalar: i2, zeros: a2, DenseMatrix: o2, concat: s2 } = t4, c2 = createMatAlgo01xDSid({ typed: n2 }), l2 = createMatAlgo02xDS0({ typed: n2, equalScalar: i2 }), u2 = createMatAlgo08xS0Sid({ typed: n2, equalScalar: i2 }), d2 = createMatAlgo10xSids({ typed: n2, DenseMatrix: o2 }), p2 = createMatAlgo11xS0s({ typed: n2, equalScalar: i2 }), m2 = createMatAlgo14xDs({ typed: n2 }), h2 = createMatrixAlgorithmSuite({ typed: n2, matrix: r2, concat: s2 }), g2 = createUseMatrixForArrayScalar({ typed: n2, matrix: r2 });
  return n2(name$178, { "number, number": rightLogShiftNumber, "SparseMatrix, number | BigNumber": n2.referToSelf((t5) => (n3, r3) => i2(r3, 0) ? n3.clone() : p2(n3, r3, t5, false)), "DenseMatrix, number | BigNumber": n2.referToSelf((t5) => (n3, r3) => i2(r3, 0) ? n3.clone() : m2(n3, r3, t5, false)), "number | BigNumber, SparseMatrix": n2.referToSelf((t5) => (n3, r3) => i2(n3, 0) ? a2(r3.size(), r3.storage()) : d2(r3, n3, t5, true)), "number | BigNumber, DenseMatrix": n2.referToSelf((t5) => (n3, r3) => i2(n3, 0) ? a2(r3.size(), r3.storage()) : m2(r3, n3, t5, true)) }, g2, h2({ SS: u2, DS: c2, SD: l2 }));
}), name$177 = `and`, dependencies$177 = [`typed`, `matrix`, `equalScalar`, `zeros`, `not`, `concat`], createAnd = factory(name$177, dependencies$177, (t4) => {
  var { typed: n2, matrix: r2, equalScalar: i2, zeros: a2, not: o2, concat: s2 } = t4, c2 = createMatAlgo02xDS0({ typed: n2, equalScalar: i2 }), l2 = createMatAlgo06xS0S0({ typed: n2, equalScalar: i2 }), u2 = createMatAlgo11xS0s({ typed: n2, equalScalar: i2 }), d2 = createMatAlgo14xDs({ typed: n2 }), p2 = createMatrixAlgorithmSuite({ typed: n2, matrix: r2, concat: s2 });
  return n2(name$177, { "number, number": andNumber, "Complex, Complex": function(t5, n3) {
    return (t5.re !== 0 || t5.im !== 0) && (n3.re !== 0 || n3.im !== 0);
  }, "BigNumber, BigNumber": function(t5, n3) {
    return !t5.isZero() && !n3.isZero() && !t5.isNaN() && !n3.isNaN();
  }, "bigint, bigint": andNumber, "Unit, Unit": n2.referToSelf((t5) => (n3, r3) => t5(n3.value || 0, r3.value || 0)), "SparseMatrix, any": n2.referToSelf((t5) => (n3, r3) => o2(r3) ? a2(n3.size(), n3.storage()) : u2(n3, r3, t5, false)), "DenseMatrix, any": n2.referToSelf((t5) => (n3, r3) => o2(r3) ? a2(n3.size(), n3.storage()) : d2(n3, r3, t5, false)), "any, SparseMatrix": n2.referToSelf((t5) => (n3, r3) => o2(n3) ? a2(n3.size(), n3.storage()) : u2(r3, n3, t5, true)), "any, DenseMatrix": n2.referToSelf((t5) => (n3, r3) => o2(n3) ? a2(n3.size(), n3.storage()) : d2(r3, n3, t5, true)), "Array, any": n2.referToSelf((t5) => (n3, i3) => t5(r2(n3), i3).valueOf()), "any, Array": n2.referToSelf((t5) => (n3, i3) => t5(n3, r2(i3)).valueOf()) }, p2({ SS: l2, DS: c2 }));
}), name$176 = `compare`, dependencies$176 = [`typed`, `config`, `matrix`, `equalScalar`, `BigNumber`, `Fraction`, `DenseMatrix`, `concat`], createCompare = factory(name$176, dependencies$176, (t4) => {
  var { typed: n2, config: r2, equalScalar: i2, matrix: a2, BigNumber: o2, Fraction: s2, DenseMatrix: c2, concat: l2 } = t4, u2 = createMatAlgo03xDSf({ typed: n2 }), d2 = createMatAlgo05xSfSf({ typed: n2, equalScalar: i2 }), p2 = createMatAlgo12xSfs({ typed: n2, DenseMatrix: c2 }), m2 = createMatrixAlgorithmSuite({ typed: n2, matrix: a2, concat: l2 }), h2 = createCompareUnits({ typed: n2 });
  return n2(name$176, createCompareNumber({ typed: n2, config: r2 }), { "boolean, boolean": function(t5, n3) {
    return t5 === n3 ? 0 : t5 > n3 ? 1 : -1;
  }, "BigNumber, BigNumber": function(t5, n3) {
    return nearlyEqual(t5, n3, r2.relTol, r2.absTol) ? new o2(0) : new o2(t5.cmp(n3));
  }, "bigint, bigint": function(t5, n3) {
    return t5 === n3 ? 0n : t5 > n3 ? 1n : -1n;
  }, "Fraction, Fraction": function(t5, n3) {
    return new s2(t5.compare(n3));
  }, "Complex, Complex": function() {
    throw TypeError(`No ordering relation is defined for complex numbers`);
  } }, h2, m2({ SS: d2, DS: u2, Ss: p2 }));
}), createCompareNumber = factory(name$176, [`typed`, `config`], (t4) => {
  var { typed: n2, config: r2 } = t4;
  return n2(name$176, { "number, number": function(t5, n3) {
    return nearlyEqual$1(t5, n3, r2.relTol, r2.absTol) ? 0 : t5 > n3 ? 1 : -1;
  } });
}), require_naturalSort = __commonJSMin(((t4, n2) => {
  n2.exports = function t5(n3, r2) {
    var i2 = /(^([+\-]?(?:0|[1-9]\d*)(?:\.\d*)?(?:[eE][+\-]?\d+)?)?$|^0x[0-9a-f]+$|\d+)/gi, a2 = /(^[ ]*|[ ]*$)/g, o2 = /(^([\w ]+,?[\w ]+)?[\w ]+,?[\w ]+\d+:\d+(:\d+)?[\w ]?|^\d{1,4}[\/\-]\d{1,4}[\/\-]\d{1,4}|^\w+, \w+ \d+, \d{4})/, s2 = /^0x[0-9a-f]+$/i, c2 = /^0/, l2 = function(n4) {
      return t5.insensitive && (`` + n4).toLowerCase() || `` + n4;
    }, u2 = l2(n3).replace(a2, ``) || ``, d2 = l2(r2).replace(a2, ``) || ``, p2 = u2.replace(i2, `\0$1\0`).replace(/\0$/, ``).replace(/^\0/, ``).split(`\0`), m2 = d2.replace(i2, `\0$1\0`).replace(/\0$/, ``).replace(/^\0/, ``).split(`\0`), h2 = parseInt(u2.match(s2), 16) || p2.length !== 1 && u2.match(o2) && Date.parse(u2), g2 = parseInt(d2.match(s2), 16) || h2 && d2.match(o2) && Date.parse(d2) || null, v2, y2;
    if (g2) {
      if (h2 < g2) return -1;
      if (h2 > g2) return 1;
    }
    for (var b2 = 0, x2 = Math.max(p2.length, m2.length); b2 < x2; b2++) {
      if (v2 = !(p2[b2] || ``).match(c2) && parseFloat(p2[b2]) || p2[b2] || 0, y2 = !(m2[b2] || ``).match(c2) && parseFloat(m2[b2]) || m2[b2] || 0, isNaN(v2) !== isNaN(y2)) return isNaN(v2) ? 1 : -1;
      if (typeof v2 != typeof y2 && (v2 += ``, y2 += ``), v2 < y2) return -1;
      if (v2 > y2) return 1;
    }
    return 0;
  };
})), import_naturalSort = __toESM(require_naturalSort()), name$175 = `compareNatural`, dependencies$175 = [`typed`, `compare`], createCompareNatural = factory(name$175, dependencies$175, (t4) => {
  var { typed: n2, compare: r2 } = t4, i2 = r2.signatures[`boolean,boolean`];
  return n2(name$175, { "any, any": a2 });
  function a2(t5, n3) {
    var l2 = typeOf(t5), u2 = typeOf(n3), d2;
    if ((l2 === `number` || l2 === `BigNumber` || l2 === `Fraction` || l2 === `bigint`) && (u2 === `number` || u2 === `BigNumber` || u2 === `Fraction` || u2 === `bigint`)) return d2 = r2(t5, n3), d2.toString() === `0` ? (0, import_naturalSort.default)(l2, u2) : d2 > 0 ? 1 : -1;
    var p2 = [`Array`, `DenseMatrix`, `SparseMatrix`];
    if (p2.includes(l2) || p2.includes(u2)) return d2 = o2(a2, t5, n3), d2 === 0 ? (0, import_naturalSort.default)(l2, u2) : d2;
    if (l2 !== u2) return (0, import_naturalSort.default)(l2, u2);
    if (l2 === `Complex`) return compareComplexNumbers(t5, n3);
    if (l2 === `Unit`) return t5.equalBase(n3) ? a2(t5.value, n3.value) : s2(a2, t5.formatUnits(), n3.formatUnits());
    if (l2 === `boolean`) return i2(t5, n3);
    if (l2 === `string`) return (0, import_naturalSort.default)(t5, n3);
    if (l2 === `Object`) return c2(a2, t5, n3);
    if (l2 === `null` || l2 === `undefined`) return 0;
    throw TypeError(`Unsupported type of value "` + l2 + `"`);
  }
  function o2(t5, n3, r3) {
    return isSparseMatrix(n3) && isSparseMatrix(r3) ? s2(t5, n3.toJSON().values, r3.toJSON().values) : isSparseMatrix(n3) ? o2(t5, n3.toArray(), r3) : isSparseMatrix(r3) ? o2(t5, n3, r3.toArray()) : isDenseMatrix(n3) ? o2(t5, n3.toJSON().data, r3) : isDenseMatrix(r3) ? o2(t5, n3, r3.toJSON().data) : Array.isArray(n3) ? Array.isArray(r3) ? s2(t5, n3, r3) : o2(t5, n3, [r3]) : o2(t5, [n3], r3);
  }
  function s2(t5, n3, r3) {
    for (var i3 = 0, a3 = Math.min(n3.length, r3.length); i3 < a3; i3++) {
      var o3 = t5(n3[i3], r3[i3]);
      if (o3 !== 0) return o3;
    }
    return n3.length > r3.length ? 1 : n3.length < r3.length ? -1 : 0;
  }
  function c2(t5, n3, r3) {
    var i3 = Object.keys(n3), a3 = Object.keys(r3);
    i3.sort(import_naturalSort.default), a3.sort(import_naturalSort.default);
    var o3 = s2(t5, i3, a3);
    if (o3 !== 0) return o3;
    for (var c3 = 0; c3 < i3.length; c3++) {
      var l2 = t5(n3[i3[c3]], r3[a3[c3]]);
      if (l2 !== 0) return l2;
    }
    return 0;
  }
});
function compareComplexNumbers(t4, n2) {
  return t4.re > n2.re ? 1 : t4.re < n2.re ? -1 : t4.im > n2.im ? 1 : t4.im < n2.im ? -1 : 0;
}
var name$174 = `compareText`, dependencies$174 = [`typed`, `matrix`, `concat`];
compareText.signature = `any, any`;
var createCompareText = factory(name$174, dependencies$174, (t4) => {
  var { typed: n2, matrix: r2, concat: i2 } = t4;
  return n2(name$174, compareText, createMatrixAlgorithmSuite({ typed: n2, matrix: r2, concat: i2 })({ elop: compareText, Ds: true }));
}), name$173 = `equal`, dependencies$173 = [`typed`, `matrix`, `equalScalar`, `DenseMatrix`, `SparseMatrix`], createEqual = factory(name$173, dependencies$173, (t4) => {
  var { typed: n2, matrix: r2, equalScalar: i2, DenseMatrix: a2, concat: o2, SparseMatrix: s2 } = t4, c2 = createMatAlgo03xDSf({ typed: n2 }), l2 = createMatAlgo07xSSf({ typed: n2, SparseMatrix: s2 }), u2 = createMatAlgo12xSfs({ typed: n2, DenseMatrix: a2 }), d2 = createMatrixAlgorithmSuite({ typed: n2, matrix: r2 });
  return n2(name$173, createEqualNumber({ typed: n2, equalScalar: i2 }), d2({ elop: i2, SS: l2, DS: c2, Ss: u2 }));
}), createEqualNumber = factory(name$173, [`typed`, `equalScalar`], (t4) => {
  var { typed: n2, equalScalar: r2 } = t4;
  return n2(name$173, { "any, any": function(t5, n3) {
    return t5 === null ? n3 === null : n3 === null ? t5 === null : t5 === void 0 ? n3 === void 0 : n3 === void 0 ? t5 === void 0 : r2(t5, n3);
  } });
}), name$172 = `equalText`, dependencies$172 = [`typed`, `compareText`, `isZero`], createEqualText = factory(name$172, dependencies$172, (t4) => {
  var { typed: n2, compareText: r2, isZero: i2 } = t4;
  return n2(name$172, { "any, any": function(t5, n3) {
    return i2(r2(t5, n3));
  } });
}), name$171 = `smaller`, dependencies$171 = [`typed`, `config`, `bignumber`, `matrix`, `DenseMatrix`, `concat`, `SparseMatrix`], createSmaller = factory(name$171, dependencies$171, (t4) => {
  var { typed: n2, config: r2, bignumber: i2, matrix: a2, DenseMatrix: o2, concat: s2, SparseMatrix: c2 } = t4, l2 = createMatAlgo03xDSf({ typed: n2 }), u2 = createMatAlgo07xSSf({ typed: n2, SparseMatrix: c2 }), d2 = createMatAlgo12xSfs({ typed: n2, DenseMatrix: o2 }), p2 = createMatrixAlgorithmSuite({ typed: n2, matrix: a2, concat: s2 }), m2 = createCompareUnits({ typed: n2 });
  function h2(t5, n3) {
    return t5.lt(n3) && !nearlyEqual(t5, n3, r2.relTol, r2.absTol);
  }
  return n2(name$171, createSmallerNumber({ typed: n2, config: r2 }), { "boolean, boolean": (t5, n3) => t5 < n3, "BigNumber, BigNumber": h2, "bigint, bigint": (t5, n3) => t5 < n3, "Fraction, Fraction": (t5, n3) => t5.compare(n3) === -1, "Fraction, BigNumber": function(t5, n3) {
    return h2(i2(t5), n3);
  }, "BigNumber, Fraction": function(t5, n3) {
    return h2(t5, i2(n3));
  }, "Complex, Complex": function(t5, n3) {
    throw TypeError(`No ordering relation is defined for complex numbers`);
  } }, m2, p2({ SS: u2, DS: l2, Ss: d2 }));
}), createSmallerNumber = factory(name$171, [`typed`, `config`], (t4) => {
  var { typed: n2, config: r2 } = t4;
  return n2(name$171, { "number, number": function(t5, n3) {
    return t5 < n3 && !nearlyEqual$1(t5, n3, r2.relTol, r2.absTol);
  } });
}), name$170 = `smallerEq`, dependencies$170 = [`typed`, `config`, `matrix`, `DenseMatrix`, `concat`, `SparseMatrix`], createSmallerEq = factory(name$170, dependencies$170, (t4) => {
  var { typed: n2, config: r2, matrix: i2, DenseMatrix: a2, concat: o2, SparseMatrix: s2 } = t4, c2 = createMatAlgo03xDSf({ typed: n2 }), l2 = createMatAlgo07xSSf({ typed: n2, SparseMatrix: s2 }), u2 = createMatAlgo12xSfs({ typed: n2, DenseMatrix: a2 }), d2 = createMatrixAlgorithmSuite({ typed: n2, matrix: i2, concat: o2 }), p2 = createCompareUnits({ typed: n2 });
  return n2(name$170, createSmallerEqNumber({ typed: n2, config: r2 }), { "boolean, boolean": (t5, n3) => t5 <= n3, "BigNumber, BigNumber": function(t5, n3) {
    return t5.lte(n3) || nearlyEqual(t5, n3, r2.relTol, r2.absTol);
  }, "bigint, bigint": (t5, n3) => t5 <= n3, "Fraction, Fraction": (t5, n3) => t5.compare(n3) !== 1, "Complex, Complex": function() {
    throw TypeError(`No ordering relation is defined for complex numbers`);
  } }, p2, d2({ SS: l2, DS: c2, Ss: u2 }));
}), createSmallerEqNumber = factory(name$170, [`typed`, `config`], (t4) => {
  var { typed: n2, config: r2 } = t4;
  return n2(name$170, { "number, number": function(t5, n3) {
    return t5 <= n3 || nearlyEqual$1(t5, n3, r2.relTol, r2.absTol);
  } });
}), name$169 = `larger`, dependencies$169 = [`typed`, `config`, `bignumber`, `matrix`, `DenseMatrix`, `concat`, `SparseMatrix`], createLarger = factory(name$169, dependencies$169, (t4) => {
  var { typed: n2, config: r2, bignumber: i2, matrix: a2, DenseMatrix: o2, concat: s2, SparseMatrix: c2 } = t4, l2 = createMatAlgo03xDSf({ typed: n2 }), u2 = createMatAlgo07xSSf({ typed: n2, SparseMatrix: c2 }), d2 = createMatAlgo12xSfs({ typed: n2, DenseMatrix: o2 }), p2 = createMatrixAlgorithmSuite({ typed: n2, matrix: a2, concat: s2 }), m2 = createCompareUnits({ typed: n2 });
  function h2(t5, n3) {
    return t5.gt(n3) && !nearlyEqual(t5, n3, r2.relTol, r2.absTol);
  }
  return n2(name$169, createLargerNumber({ typed: n2, config: r2 }), { "boolean, boolean": (t5, n3) => t5 > n3, "BigNumber, BigNumber": h2, "bigint, bigint": (t5, n3) => t5 > n3, "Fraction, Fraction": (t5, n3) => t5.compare(n3) === 1, "Fraction, BigNumber": function(t5, n3) {
    return h2(i2(t5), n3);
  }, "BigNumber, Fraction": function(t5, n3) {
    return h2(t5, i2(n3));
  }, "Complex, Complex": function() {
    throw TypeError(`No ordering relation is defined for complex numbers`);
  } }, m2, p2({ SS: u2, DS: l2, Ss: d2 }));
}), createLargerNumber = factory(name$169, [`typed`, `config`], (t4) => {
  var { typed: n2, config: r2 } = t4;
  return n2(name$169, { "number, number": function(t5, n3) {
    return t5 > n3 && !nearlyEqual$1(t5, n3, r2.relTol, r2.absTol);
  } });
}), name$168 = `largerEq`, dependencies$168 = [`typed`, `config`, `matrix`, `DenseMatrix`, `concat`, `SparseMatrix`], createLargerEq = factory(name$168, dependencies$168, (t4) => {
  var { typed: n2, config: r2, matrix: i2, DenseMatrix: a2, concat: o2, SparseMatrix: s2 } = t4, c2 = createMatAlgo03xDSf({ typed: n2 }), l2 = createMatAlgo07xSSf({ typed: n2, SparseMatrix: s2 }), u2 = createMatAlgo12xSfs({ typed: n2, DenseMatrix: a2 }), d2 = createMatrixAlgorithmSuite({ typed: n2, matrix: i2, concat: o2 }), p2 = createCompareUnits({ typed: n2 });
  return n2(name$168, createLargerEqNumber({ typed: n2, config: r2 }), { "boolean, boolean": (t5, n3) => t5 >= n3, "BigNumber, BigNumber": function(t5, n3) {
    return t5.gte(n3) || nearlyEqual(t5, n3, r2.relTol, r2.absTol);
  }, "bigint, bigint": function(t5, n3) {
    return t5 >= n3;
  }, "Fraction, Fraction": (t5, n3) => t5.compare(n3) !== -1, "Complex, Complex": function() {
    throw TypeError(`No ordering relation is defined for complex numbers`);
  } }, p2, d2({ SS: l2, DS: c2, Ss: u2 }));
}), createLargerEqNumber = factory(name$168, [`typed`, `config`], (t4) => {
  var { typed: n2, config: r2 } = t4;
  return n2(name$168, { "number, number": function(t5, n3) {
    return t5 >= n3 || nearlyEqual$1(t5, n3, r2.relTol, r2.absTol);
  } });
}), name$167 = `deepEqual`, dependencies$167 = [`typed`, `equal`], createDeepEqual = factory(name$167, dependencies$167, (t4) => {
  var { typed: n2, equal: r2 } = t4;
  return n2(name$167, { "any, any": function(t5, n3) {
    return i2(t5.valueOf(), n3.valueOf());
  } });
  function i2(t5, n3) {
    if (Array.isArray(t5)) if (Array.isArray(n3)) {
      var a2 = t5.length;
      if (a2 !== n3.length) return false;
      for (var o2 = 0; o2 < a2; o2++) if (!i2(t5[o2], n3[o2])) return false;
      return true;
    } else return false;
    else if (Array.isArray(n3)) return false;
    else return r2(t5, n3);
  }
}), name$166 = `unequal`, dependencies$166 = [`typed`, `config`, `equalScalar`, `matrix`, `DenseMatrix`, `concat`, `SparseMatrix`], createUnequal = factory(name$166, dependencies$166, (t4) => {
  var { typed: n2, config: r2, equalScalar: i2, matrix: a2, DenseMatrix: o2, concat: s2, SparseMatrix: c2 } = t4, l2 = createMatAlgo03xDSf({ typed: n2 }), u2 = createMatAlgo07xSSf({ typed: n2, SparseMatrix: c2 }), d2 = createMatAlgo12xSfs({ typed: n2, DenseMatrix: o2 }), p2 = createMatrixAlgorithmSuite({ typed: n2, matrix: a2, concat: s2 });
  return n2(name$166, createUnequalNumber({ typed: n2, equalScalar: i2 }), p2({ elop: m2, SS: u2, DS: l2, Ss: d2 }));
  function m2(t5, n3) {
    return !i2(t5, n3);
  }
}), createUnequalNumber = factory(name$166, [`typed`, `equalScalar`], (t4) => {
  var { typed: n2, equalScalar: r2 } = t4;
  return n2(name$166, { "any, any": function(t5, n3) {
    return t5 === null ? n3 !== null : n3 === null ? t5 !== null : t5 === void 0 ? n3 !== void 0 : n3 === void 0 ? t5 !== void 0 : !r2(t5, n3);
  } });
}), name$165 = `partitionSelect`, dependencies$165 = [`typed`, `isNumeric`, `isNaN`, `compare`], createPartitionSelect = factory(name$165, dependencies$165, (t4) => {
  var { typed: n2, isNumeric: r2, isNaN: i2, compare: a2 } = t4, o2 = a2, s2 = (t5, n3) => -a2(t5, n3);
  return n2(name$165, { "Array | Matrix, number": function(t5, n3) {
    return c2(t5, n3, o2);
  }, "Array | Matrix, number, string": function(t5, n3, r3) {
    if (r3 === `asc`) return c2(t5, n3, o2);
    if (r3 === `desc`) return c2(t5, n3, s2);
    throw Error(`Compare string must be "asc" or "desc"`);
  }, "Array | Matrix, number, function": c2 });
  function c2(t5, n3, r3) {
    if (!isInteger(n3) || n3 < 0) throw Error(`k must be a non-negative integer`);
    if (isMatrix(t5)) {
      if (t5.size().length > 1) throw Error(`Only one dimensional matrices supported`);
      return l2(t5.valueOf(), n3, r3);
    }
    if (Array.isArray(t5)) return l2(t5, n3, r3);
  }
  function l2(t5, n3, a3) {
    if (n3 >= t5.length) throw Error(`k out of bounds`);
    for (var o3 = 0; o3 < t5.length; o3++) if (r2(t5[o3]) && i2(t5[o3])) return t5[o3];
    for (var s3 = 0, c3 = t5.length - 1; s3 < c3; ) {
      for (var l3 = s3, u2 = c3, d2 = t5[Math.floor(Math.random() * (c3 - s3 + 1)) + s3]; l3 < u2; ) if (a3(t5[l3], d2) >= 0) {
        var p2 = t5[u2];
        t5[u2] = t5[l3], t5[l3] = p2, --u2;
      } else ++l3;
      a3(t5[l3], d2) > 0 && --l3, n3 <= l3 ? c3 = l3 : s3 = l3 + 1;
    }
    return t5[n3];
  }
}), name$164 = `sort`, dependencies$164 = [`typed`, `matrix`, `compare`, `compareNatural`], createSort = factory(name$164, dependencies$164, (t4) => {
  var { typed: n2, matrix: r2, compare: i2, compareNatural: a2 } = t4, o2 = i2, s2 = (t5, n3) => -i2(t5, n3);
  return n2(name$164, { Array: function(t5) {
    return l2(t5), t5.sort(o2);
  }, Matrix: function(t5) {
    return u2(t5), r2(t5.toArray().sort(o2), t5.storage());
  }, "Array, function": function(t5, n3) {
    return l2(t5), t5.sort(n3);
  }, "Matrix, function": function(t5, n3) {
    return u2(t5), r2(t5.toArray().sort(n3), t5.storage());
  }, "Array, string": function(t5, n3) {
    return l2(t5), t5.sort(c2(n3));
  }, "Matrix, string": function(t5, n3) {
    return u2(t5), r2(t5.toArray().sort(c2(n3)), t5.storage());
  } });
  function c2(t5) {
    if (t5 === `asc`) return o2;
    if (t5 === `desc`) return s2;
    if (t5 === `natural`) return a2;
    throw Error(`String "asc", "desc", or "natural" expected`);
  }
  function l2(t5) {
    if (arraySize(t5).length !== 1) throw Error(`One dimensional array expected`);
  }
  function u2(t5) {
    if (t5.size().length !== 1) throw Error(`One dimensional matrix expected`);
  }
}), name$163 = `max`, dependencies$163 = [`typed`, `config`, `numeric`, `larger`, `isNaN`], createMax = factory(name$163, dependencies$163, (t4) => {
  var { typed: n2, config: r2, numeric: i2, larger: a2, isNaN: o2 } = t4;
  return n2(name$163, { "Array | Matrix": c2, "Array | Matrix, number | BigNumber": function(t5, n3) {
    return reduce$1(t5, n3.valueOf(), s2);
  }, "...": function(t5) {
    if (containsCollections(t5)) throw TypeError(`Scalar values expected in function max`);
    return c2(t5);
  } });
  function s2(t5, n3) {
    try {
      return a2(t5, n3) ? t5 : n3;
    } catch (t6) {
      throw improveErrorMessage(t6, `max`, n3);
    }
  }
  function c2(t5) {
    var n3;
    if (deepForEach(t5, function(t6) {
      try {
        (o2(t6) || n3 === void 0 || a2(t6, n3)) && (n3 = t6);
      } catch (n4) {
        throw improveErrorMessage(n4, `max`, t6);
      }
    }), n3 === void 0) throw Error(`Cannot calculate max of an empty array`);
    return typeof n3 == `string` && (n3 = i2(n3, safeNumberType(n3, r2))), n3;
  }
}), name$162 = `min`, dependencies$162 = [`typed`, `config`, `numeric`, `smaller`, `isNaN`], createMin = factory(name$162, dependencies$162, (t4) => {
  var { typed: n2, config: r2, numeric: i2, smaller: a2, isNaN: o2 } = t4;
  return n2(name$162, { "Array | Matrix": c2, "Array | Matrix, number | BigNumber": function(t5, n3) {
    return reduce$1(t5, n3.valueOf(), s2);
  }, "...": function(t5) {
    if (containsCollections(t5)) throw TypeError(`Scalar values expected in function min`);
    return c2(t5);
  } });
  function s2(t5, n3) {
    try {
      return a2(t5, n3) ? t5 : n3;
    } catch (t6) {
      throw improveErrorMessage(t6, `min`, n3);
    }
  }
  function c2(t5) {
    var n3;
    if (deepForEach(t5, function(t6) {
      try {
        (o2(t6) || n3 === void 0 || a2(t6, n3)) && (n3 = t6);
      } catch (n4) {
        throw improveErrorMessage(n4, `min`, t6);
      }
    }), n3 === void 0) throw Error(`Cannot calculate min of an empty array`);
    return typeof n3 == `string` && (n3 = i2(n3, safeNumberType(n3, r2))), n3;
  }
}), name$161 = `ImmutableDenseMatrix`, dependencies$161 = [`smaller`, `DenseMatrix`], createImmutableDenseMatrixClass = factory(name$161, dependencies$161, (t4) => {
  var { smaller: n2, DenseMatrix: r2 } = t4;
  function i2(t5, n3) {
    if (!(this instanceof i2)) throw SyntaxError(`Constructor must be called with the new operator`);
    if (n3 && !isString(n3)) throw Error(`Invalid datatype: ` + n3);
    if (isMatrix(t5) || isArray(t5)) {
      var a2 = new r2(t5, n3);
      this._data = a2._data, this._size = a2._size, this._datatype = a2._datatype, this._min = null, this._max = null;
    } else if (t5 && isArray(t5.data) && isArray(t5.size)) this._data = t5.data, this._size = t5.size, this._datatype = t5.datatype, this._min = t5.min === void 0 ? null : t5.min, this._max = t5.max === void 0 ? null : t5.max;
    else if (t5) throw TypeError(`Unsupported type of data (` + typeOf(t5) + `)`);
    else this._data = [], this._size = [0], this._datatype = n3, this._min = null, this._max = null;
  }
  return i2.prototype = new r2(), i2.prototype.type = `ImmutableDenseMatrix`, i2.prototype.isImmutableDenseMatrix = true, i2.prototype.subset = function(t5) {
    switch (arguments.length) {
      case 1:
        var n3 = r2.prototype.subset.call(this, t5);
        return isMatrix(n3) ? new i2({ data: n3._data, size: n3._size, datatype: n3._datatype }) : n3;
      case 2:
      case 3:
        throw Error(`Cannot invoke set subset on an Immutable Matrix instance`);
      default:
        throw SyntaxError(`Wrong number of arguments`);
    }
  }, i2.prototype.set = function() {
    throw Error(`Cannot invoke set on an Immutable Matrix instance`);
  }, i2.prototype.resize = function() {
    throw Error(`Cannot invoke resize on an Immutable Matrix instance`);
  }, i2.prototype.reshape = function() {
    throw Error(`Cannot invoke reshape on an Immutable Matrix instance`);
  }, i2.prototype.clone = function() {
    return new i2({ data: clone$3(this._data), size: clone$3(this._size), datatype: this._datatype });
  }, i2.prototype.toJSON = function() {
    return { mathjs: `ImmutableDenseMatrix`, data: this._data, size: this._size, datatype: this._datatype };
  }, i2.fromJSON = function(t5) {
    return new i2(t5);
  }, i2.prototype.swapRows = function() {
    throw Error(`Cannot invoke swapRows on an Immutable Matrix instance`);
  }, i2.prototype.min = function() {
    if (this._min === null) {
      var t5 = null;
      this.forEach(function(r3) {
        (t5 === null || n2(r3, t5)) && (t5 = r3);
      }), this._min = t5 === null ? void 0 : t5;
    }
    return this._min;
  }, i2.prototype.max = function() {
    if (this._max === null) {
      var t5 = null;
      this.forEach(function(r3) {
        (t5 === null || n2(t5, r3)) && (t5 = r3);
      }), this._max = t5 === null ? void 0 : t5;
    }
    return this._max;
  }, i2;
}, { isClass: true }), name$160 = `Index`, dependencies$160 = [`ImmutableDenseMatrix`, `getMatrixDataType`], createIndexClass = factory(name$160, dependencies$160, (t4) => {
  var { ImmutableDenseMatrix: n2, getMatrixDataType: r2 } = t4;
  function i2() {
    if (!(this instanceof i2)) throw SyntaxError(`Constructor must be called with the new operator`);
    this._dimensions = [], this._sourceSize = [], this._isScalar = true;
    for (var t5 = 0, n3 = arguments.length; t5 < n3; t5++) {
      var o2 = t5 < 0 || arguments.length <= t5 ? void 0 : arguments[t5], s2 = isArray(o2), c2 = isMatrix(o2), l2 = typeof o2, u2 = null;
      if (isRange(o2)) this._dimensions.push(o2), this._isScalar = false;
      else if (s2 || c2) {
        var d2 = void 0;
        this._isScalar = false, r2(o2) === `boolean` ? (s2 && (d2 = a2(_booleansArrayToNumbersForIndex(o2).valueOf())), c2 && (d2 = a2(_booleansArrayToNumbersForIndex(o2._data).valueOf())), u2 = o2.valueOf().length) : d2 = a2(o2.valueOf()), this._dimensions.push(d2);
      } else if (l2 === `number`) this._dimensions.push(o2);
      else if (l2 === `bigint`) this._dimensions.push(Number(o2));
      else if (l2 === `string`) this._dimensions.push(o2);
      else throw TypeError(`Dimension must be an Array, Matrix, number, bigint, string, or Range`);
      this._sourceSize.push(u2);
    }
  }
  i2.prototype.type = `Index`, i2.prototype.isIndex = true;
  function a2(t5) {
    for (var r3 = 0, i3 = t5.length; r3 < i3; r3++) if (!isNumber(t5[r3]) || !isInteger(t5[r3])) throw TypeError(`Index parameters must be positive integer numbers`);
    var a3 = new n2();
    return a3._data = t5, a3._size = [t5.length], a3;
  }
  return i2.prototype.clone = function() {
    var t5 = new i2();
    return t5._dimensions = clone$3(this._dimensions), t5._isScalar = this._isScalar, t5._sourceSize = this._sourceSize, t5;
  }, i2.create = function(t5) {
    var n3 = new i2();
    return i2.apply(n3, t5), n3;
  }, i2.prototype.size = function() {
    for (var t5 = [], n3 = 0, r3 = this._dimensions.length; n3 < r3; n3++) {
      var i3 = this._dimensions[n3];
      t5[n3] = isString(i3) || isNumber(i3) ? 1 : i3.size()[0];
    }
    return t5;
  }, i2.prototype.max = function() {
    for (var t5 = [], n3 = 0, r3 = this._dimensions.length; n3 < r3; n3++) {
      var i3 = this._dimensions[n3];
      t5[n3] = isString(i3) || isNumber(i3) ? i3 : i3.max();
    }
    return t5;
  }, i2.prototype.min = function() {
    for (var t5 = [], n3 = 0, r3 = this._dimensions.length; n3 < r3; n3++) {
      var i3 = this._dimensions[n3];
      t5[n3] = isString(i3) || isNumber(i3) ? i3 : i3.min();
    }
    return t5;
  }, i2.prototype.forEach = function(t5) {
    for (var n3 = 0, r3 = this._dimensions.length; n3 < r3; n3++) t5(this._dimensions[n3], n3, this);
  }, i2.prototype.dimension = function(t5) {
    return isNumber(t5) ? this._dimensions[t5] ?? null : null;
  }, i2.prototype.isObjectProperty = function() {
    return this._dimensions.length === 1 && isString(this._dimensions[0]);
  }, i2.prototype.getObjectProperty = function() {
    return this.isObjectProperty() ? this._dimensions[0] : null;
  }, i2.prototype.isScalar = function() {
    return this._isScalar;
  }, i2.prototype.toArray = function() {
    for (var t5 = [], n3 = 0, r3 = this._dimensions.length; n3 < r3; n3++) {
      var i3 = this._dimensions[n3];
      t5.push(isString(i3) || isNumber(i3) ? i3 : i3.toArray());
    }
    return t5;
  }, i2.prototype.valueOf = i2.prototype.toArray, i2.prototype.toString = function() {
    for (var t5 = [], n3 = 0, r3 = this._dimensions.length; n3 < r3; n3++) {
      var i3 = this._dimensions[n3];
      isString(i3) ? t5.push(JSON.stringify(i3)) : t5.push(i3.toString());
    }
    return `[` + t5.join(`, `) + `]`;
  }, i2.prototype.toJSON = function() {
    return { mathjs: `Index`, dimensions: this._dimensions };
  }, i2.fromJSON = function(t5) {
    return i2.create(t5.dimensions);
  }, i2;
}, { isClass: true });
function _booleansArrayToNumbersForIndex(t4) {
  var n2 = [];
  return t4.forEach((t5, r2) => {
    t5 && n2.push(r2);
  }), n2;
}
var name$159 = `FibonacciHeap`, dependencies$159 = [`smaller`, `larger`], createFibonacciHeapClass = factory(name$159, dependencies$159, (t4) => {
  var { smaller: n2, larger: r2 } = t4, i2 = 1 / Math.log((1 + Math.sqrt(5)) / 2);
  function a2() {
    if (!(this instanceof a2)) throw SyntaxError(`Constructor must be called with the new operator`);
    this._minimum = null, this._size = 0;
  }
  a2.prototype.type = `FibonacciHeap`, a2.prototype.isFibonacciHeap = true, a2.prototype.insert = function(t5, r3) {
    var i3 = { key: t5, value: r3, degree: 0 };
    if (this._minimum) {
      var a3 = this._minimum;
      i3.left = a3, i3.right = a3.right, a3.right = i3, i3.right.left = i3, n2(t5, a3.key) && (this._minimum = i3);
    } else i3.left = i3, i3.right = i3, this._minimum = i3;
    return this._size++, i3;
  }, a2.prototype.size = function() {
    return this._size;
  }, a2.prototype.clear = function() {
    this._minimum = null, this._size = 0;
  }, a2.prototype.isEmpty = function() {
    return this._size === 0;
  }, a2.prototype.extractMinimum = function() {
    var t5 = this._minimum;
    if (t5 === null) return t5;
    for (var n3 = this._minimum, r3 = t5.degree, i3 = t5.child; r3 > 0; ) {
      var a3 = i3.right;
      i3.left.right = i3.right, i3.right.left = i3.left, i3.left = n3, i3.right = n3.right, n3.right = i3, i3.right.left = i3, i3.parent = null, i3 = a3, r3--;
    }
    return t5.left.right = t5.right, t5.right.left = t5.left, t5 === t5.right ? n3 = null : (n3 = t5.right, n3 = u2(n3, this._size)), this._size--, this._minimum = n3, t5;
  }, a2.prototype.remove = function(t5) {
    this._minimum = o2(this._minimum, t5, -1), this.extractMinimum();
  };
  function o2(t5, r3, i3) {
    r3.key = i3;
    var a3 = r3.parent;
    return a3 && n2(r3.key, a3.key) && (s2(t5, r3, a3), c2(t5, a3)), n2(r3.key, t5.key) && (t5 = r3), t5;
  }
  function s2(t5, n3, r3) {
    n3.left.right = n3.right, n3.right.left = n3.left, r3.degree--, r3.child === n3 && (r3.child = n3.right), r3.degree === 0 && (r3.child = null), n3.left = t5, n3.right = t5.right, t5.right = n3, n3.right.left = n3, n3.parent = null, n3.mark = false;
  }
  function c2(t5, n3) {
    var r3 = n3.parent;
    r3 && (n3.mark ? (s2(t5, n3, r3), c2(r3)) : n3.mark = true);
  }
  var l2 = function(t5, n3) {
    t5.left.right = t5.right, t5.right.left = t5.left, t5.parent = n3, n3.child ? (t5.left = n3.child, t5.right = n3.child.right, n3.child.right = t5, t5.right.left = t5) : (n3.child = t5, t5.right = t5, t5.left = t5), n3.degree++, t5.mark = false;
  };
  function u2(t5, a3) {
    var o3 = Math.floor(Math.log(a3) * i2) + 1, s3 = Array(o3), c3 = 0, u3 = t5;
    if (u3) for (c3++, u3 = u3.right; u3 !== t5; ) c3++, u3 = u3.right;
    for (var d2; c3 > 0; ) {
      for (var p2 = u3.degree, m2 = u3.right; d2 = s3[p2], d2; ) {
        if (r2(u3.key, d2.key)) {
          var h2 = d2;
          d2 = u3, u3 = h2;
        }
        l2(d2, u3), s3[p2] = null, p2++;
      }
      s3[p2] = u3, u3 = m2, c3--;
    }
    t5 = null;
    for (var g2 = 0; g2 < o3; g2++) d2 = s3[g2], d2 && (t5 ? (d2.left.right = d2.right, d2.right.left = d2.left, d2.left = t5, d2.right = t5.right, t5.right = d2, d2.right.left = d2, n2(d2.key, t5.key) && (t5 = d2)) : t5 = d2);
    return t5;
  }
  return a2;
}, { isClass: true }), name$158 = `Spa`, dependencies$158 = [`addScalar`, `equalScalar`, `FibonacciHeap`], createSpaClass = factory(name$158, dependencies$158, (t4) => {
  var { addScalar: n2, equalScalar: r2, FibonacciHeap: i2 } = t4;
  function a2() {
    if (!(this instanceof a2)) throw SyntaxError(`Constructor must be called with the new operator`);
    this._values = [], this._heap = new i2();
  }
  return a2.prototype.type = `Spa`, a2.prototype.isSpa = true, a2.prototype.set = function(t5, n3) {
    if (this._values[t5]) this._values[t5].value = n3;
    else {
      var r3 = this._heap.insert(t5, n3);
      this._values[t5] = r3;
    }
  }, a2.prototype.get = function(t5) {
    var n3 = this._values[t5];
    return n3 ? n3.value : 0;
  }, a2.prototype.accumulate = function(t5, r3) {
    var i3 = this._values[t5];
    i3 ? i3.value = n2(i3.value, r3) : (i3 = this._heap.insert(t5, r3), this._values[t5] = i3);
  }, a2.prototype.forEach = function(t5, n3, i3) {
    var a3 = this._heap, o2 = this._values, s2 = [], c2 = a3.extractMinimum();
    for (c2 && s2.push(c2); c2 && c2.key <= n3; ) c2.key >= t5 && (r2(c2.value, 0) || i3(c2.key, c2.value, this)), c2 = a3.extractMinimum(), c2 && s2.push(c2);
    for (var l2 = 0; l2 < s2.length; l2++) {
      var u2 = s2[l2];
      c2 = a3.insert(u2.key, u2.value), o2[c2.key] = c2;
    }
  }, a2.prototype.swap = function(t5, n3) {
    var r3 = this._values[t5], i3 = this._values[n3];
    if (!r3 && i3) r3 = this._heap.insert(t5, i3.value), this._heap.remove(i3), this._values[t5] = r3, this._values[n3] = void 0;
    else if (r3 && !i3) i3 = this._heap.insert(n3, r3.value), this._heap.remove(r3), this._values[n3] = i3, this._values[t5] = void 0;
    else if (r3 && i3) {
      var a3 = r3.value;
      r3.value = i3.value, i3.value = a3;
    }
  }, a2;
}, { isClass: true });
function lruQueue(t4) {
  var n2 = 0, r2 = 1, i2 = /* @__PURE__ */ Object.create(null), a2 = /* @__PURE__ */ Object.create(null), o2 = 0, s2 = function(t5) {
    var s3 = a2[t5];
    if (s3 && (delete i2[s3], delete a2[t5], --n2, r2 === s3)) {
      if (!n2) {
        o2 = 0, r2 = 1;
        return;
      }
      for (; !Object.prototype.hasOwnProperty.call(i2, ++r2); ) ;
    }
  };
  return t4 = Math.abs(t4), { hit: function(c2) {
    var l2 = a2[c2], u2 = ++o2;
    if (i2[u2] = c2, a2[c2] = u2, !l2) return ++n2, n2 <= t4 ? void 0 : (c2 = i2[r2], s2(c2), c2);
    if (delete i2[l2], r2 === l2) for (; !Object.prototype.hasOwnProperty.call(i2, ++r2); ) ;
  }, delete: s2, clear: function() {
    n2 = o2 = 0, r2 = 1, i2 = /* @__PURE__ */ Object.create(null), a2 = /* @__PURE__ */ Object.create(null);
  } };
}
function memoize$1(t4) {
  var { hasher: n2, limit: r2 } = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
  return r2 ??= 1 / 0, n2 ??= JSON.stringify, function i2() {
    typeof i2.cache != `object` && (i2.cache = { values: /* @__PURE__ */ new Map(), lru: lruQueue(r2 || 1 / 0) });
    var a2 = [...arguments], o2 = n2(a2);
    if (i2.cache.values.has(o2)) return i2.cache.lru.hit(o2), i2.cache.values.get(o2);
    var s2 = t4.apply(t4, a2);
    return i2.cache.values.set(o2, s2), i2.cache.values.delete(i2.cache.lru.hit(o2)), s2;
  };
}
var createBigNumberE = memoize$1(function(t4) {
  return new t4(1).exp();
}, { hasher }), createBigNumberPhi = memoize$1(function(t4) {
  return new t4(1).plus(new t4(5).sqrt()).div(2);
}, { hasher }), createBigNumberPi = memoize$1(function(t4) {
  return t4.acos(-1);
}, { hasher }), createBigNumberTau = memoize$1(function(t4) {
  return createBigNumberPi(t4).times(2);
}, { hasher });
function hasher(t4) {
  return t4[0].precision;
}
function ownKeys$2(t4, n2) {
  var r2 = Object.keys(t4);
  if (Object.getOwnPropertySymbols) {
    var i2 = Object.getOwnPropertySymbols(t4);
    n2 && (i2 = i2.filter(function(n3) {
      return Object.getOwnPropertyDescriptor(t4, n3).enumerable;
    })), r2.push.apply(r2, i2);
  }
  return r2;
}
function _objectSpread$2(t4) {
  for (var n2 = 1; n2 < arguments.length; n2++) {
    var r2 = arguments[n2] == null ? {} : arguments[n2];
    n2 % 2 ? ownKeys$2(Object(r2), true).forEach(function(n3) {
      _defineProperty(t4, n3, r2[n3]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(t4, Object.getOwnPropertyDescriptors(r2)) : ownKeys$2(Object(r2)).forEach(function(n3) {
      Object.defineProperty(t4, n3, Object.getOwnPropertyDescriptor(r2, n3));
    });
  }
  return t4;
}
var name$157 = `Unit`, dependencies$157 = [`?on`, `config`, `addScalar`, `subtractScalar`, `multiplyScalar`, `divideScalar`, `pow`, `abs`, `fix`, `round`, `equal`, `isNumeric`, `format`, `number`, `Complex`, `BigNumber`, `Fraction`], createUnitClass = factory(name$157, dependencies$157, (t4) => {
  var { on: n2, config: r2, addScalar: i2, subtractScalar: a2, multiplyScalar: o2, divideScalar: s2, pow: c2, abs: l2, fix: u2, round: d2, equal: p2, isNumeric: m2, format: h2, number: g2, Complex: v2, BigNumber: y2, Fraction: b2 } = t4, x2 = g2, S2 = false, w2 = true;
  function E(t5, n3) {
    if (!(this instanceof E)) throw Error(`Constructor must be called with the new operator`);
    if (!(t5 == null || m2(t5) || isComplex(t5))) throw TypeError(`First parameter in Unit constructor must be number, BigNumber, Fraction, Complex, or undefined`);
    if (this.fixPrefix = S2, this.skipAutomaticSimplification = w2, n3 === void 0) this.units = [], this.dimensions = ee.map((t6) => 0);
    else if (typeof n3 == `string`) {
      var r3 = E.parse(n3);
      this.units = r3.units, this.dimensions = r3.dimensions;
    } else if (isUnit(n3) && n3.value === null) this.fixPrefix = n3.fixPrefix, this.skipAutomaticSimplification = n3.skipAutomaticSimplification, this.dimensions = n3.dimensions.slice(0), this.units = n3.units.map((t6) => _extends({}, t6));
    else throw TypeError(`Second parameter in Unit constructor must be a string or valueless Unit`);
    this.value = this._normalize(t5);
  }
  Object.defineProperty(E, "name", { value: `Unit` }), E.prototype.constructor = E, E.prototype.type = `Unit`, E.prototype.isUnit = true;
  var D, O, j2;
  function M2() {
    for (; j2 === ` ` || j2 === `	`; ) I();
  }
  function N2(t5) {
    return t5 >= `0` && t5 <= `9` || t5 === `.`;
  }
  function F(t5) {
    return t5 >= `0` && t5 <= `9`;
  }
  function I() {
    O++, j2 = D.charAt(O);
  }
  function L(t5) {
    O = t5, j2 = D.charAt(O);
  }
  function R() {
    var t5 = ``, n3 = O;
    if (j2 === `+` ? I() : j2 === `-` && (t5 += j2, I()), !N2(j2)) return L(n3), null;
    if (j2 === `.`) {
      if (t5 += j2, I(), !F(j2)) return L(n3), null;
    } else {
      for (; F(j2); ) t5 += j2, I();
      j2 === `.` && (t5 += j2, I());
    }
    for (; F(j2); ) t5 += j2, I();
    if (j2 === `E` || j2 === `e`) {
      var r3 = ``, i3 = O;
      if (r3 += j2, I(), (j2 === `+` || j2 === `-`) && (r3 += j2, I()), !F(j2)) return L(i3), t5;
      for (t5 += r3; F(j2); ) t5 += j2, I();
    }
    return t5;
  }
  function z() {
    for (var t5 = ``; F(j2) || E.isValidAlpha(j2); ) t5 += j2, I();
    var n3 = t5.charAt(0);
    return E.isValidAlpha(n3) ? t5 : null;
  }
  function V(t5) {
    return j2 === t5 ? (I(), t5) : null;
  }
  E.parse = function(t5, n3) {
    if (n3 ||= {}, D = t5, O = -1, j2 = ``, typeof D != `string`) throw TypeError(`Invalid argument in Unit.parse, string expected`);
    var i3 = new E();
    i3.units = [];
    var a3 = 1, o3 = false;
    I(), M2();
    var s3 = R(), c3 = null;
    if (s3) {
      if (r2.number === `BigNumber`) c3 = new y2(s3);
      else if (r2.number === `Fraction`) try {
        c3 = new b2(s3);
      } catch {
        c3 = parseFloat(s3);
      }
      else c3 = parseFloat(s3);
      M2(), V(`*`) ? (a3 = 1, o3 = true) : V(`/`) && (a3 = -1, o3 = true);
    }
    for (var l3 = [], u3 = 1; ; ) {
      for (M2(); j2 === `(`; ) l3.push(a3), u3 *= a3, a3 = 1, I(), M2();
      var d3 = void 0;
      if (j2) {
        var p3 = j2;
        if (d3 = z(), d3 === null) throw SyntaxError(`Unexpected "` + p3 + `" in "` + D + `" at index ` + O.toString());
      } else break;
      var m3 = H2(d3);
      if (m3 === null) throw SyntaxError(`Unit "` + d3 + `" not found.`);
      var h3 = a3 * u3;
      if (M2(), V(`^`)) {
        M2();
        var g3 = R();
        if (g3 === null) throw SyntaxError(`In "` + t5 + `", "^" must be followed by a floating-point number`);
        h3 *= g3;
      }
      i3.units.push({ unit: m3.unit, prefix: m3.prefix, power: h3 });
      for (var v3 = 0; v3 < ee.length; v3++) i3.dimensions[v3] += (m3.unit.dimensions[v3] || 0) * h3;
      for (M2(); j2 === `)`; ) {
        if (l3.length === 0) throw SyntaxError(`Unmatched ")" in "` + D + `" at index ` + O.toString());
        u3 /= l3.pop(), I(), M2();
      }
      if (o3 = false, V(`*`) ? (a3 = 1, o3 = true) : V(`/`) ? (a3 = -1, o3 = true) : a3 = 1, m3.unit.base) {
        var x3 = m3.unit.base.key;
        Z.auto[x3] = { unit: m3.unit, prefix: m3.prefix };
      }
    }
    if (M2(), j2) throw SyntaxError(`Could not parse: "` + t5 + `"`);
    if (o3) throw SyntaxError(`Trailing characters: "` + t5 + `"`);
    if (l3.length !== 0) throw SyntaxError(`Unmatched "(" in "` + D + `"`);
    if (i3.units.length === 0 && !n3.allowNoUnits) throw SyntaxError(`"` + t5 + `" contains no units`);
    return i3.value = c3 === void 0 ? null : i3._normalize(c3), i3;
  }, E.prototype.clone = function() {
    var t5 = new E();
    t5.fixPrefix = this.fixPrefix, t5.skipAutomaticSimplification = this.skipAutomaticSimplification, t5.value = clone$3(this.value), t5.dimensions = this.dimensions.slice(0), t5.units = [];
    for (var n3 = 0; n3 < this.units.length; n3++) for (var r3 in t5.units[n3] = {}, this.units[n3]) hasOwnProperty$1(this.units[n3], r3) && (t5.units[n3][r3] = this.units[n3][r3]);
    return t5;
  }, E.prototype.valueType = function() {
    return typeOf(this.value);
  }, E.prototype._isDerived = function() {
    return this.units.length === 0 ? false : this.units.length > 1 || Math.abs(this.units[0].power - 1) > 1e-15;
  }, E.prototype._normalize = function(t5) {
    if (t5 == null || this.units.length === 0) return t5;
    for (var n3 = t5, r3 = E._getNumberConverter(typeOf(t5)), i3 = 0; i3 < this.units.length; i3++) {
      var a3 = r3(this.units[i3].unit.value), s3 = r3(this.units[i3].prefix.value), l3 = r3(this.units[i3].power);
      n3 = o2(n3, c2(o2(a3, s3), l3));
    }
    return n3;
  }, E.prototype._denormalize = function(t5, n3) {
    if (t5 == null || this.units.length === 0) return t5;
    for (var r3 = t5, i3 = E._getNumberConverter(typeOf(t5)), a3 = 0; a3 < this.units.length; a3++) {
      var l3 = i3(this.units[a3].unit.value), u3 = i3(this.units[a3].prefix.value), d3 = i3(this.units[a3].power);
      r3 = s2(r3, c2(o2(l3, u3), d3));
    }
    return r3;
  };
  var H2 = memoize$1((t5) => {
    if (hasOwnProperty$1(X, t5)) {
      var n3 = X[t5];
      return { unit: n3, prefix: n3.prefixes[``] };
    }
    for (var r3 in X) if (hasOwnProperty$1(X, r3) && endsWith(t5, r3)) {
      var i3 = X[r3], a3 = t5.length - r3.length, o3 = t5.substring(0, a3), s3 = hasOwnProperty$1(i3.prefixes, o3) ? i3.prefixes[o3] : void 0;
      if (s3 !== void 0) return { unit: i3, prefix: s3 };
    }
    return null;
  }, { hasher: (t5) => t5[0], limit: 100 });
  E.isValuelessUnit = function(t5) {
    return H2(t5) !== null;
  }, E.prototype.hasBase = function(t5) {
    if (typeof t5 == `string` && (t5 = Y[t5]), !t5) return false;
    for (var n3 = 0; n3 < ee.length; n3++) if (Math.abs((this.dimensions[n3] || 0) - (t5.dimensions[n3] || 0)) > 1e-12) return false;
    return true;
  }, E.prototype.equalBase = function(t5) {
    for (var n3 = 0; n3 < ee.length; n3++) if (Math.abs((this.dimensions[n3] || 0) - (t5.dimensions[n3] || 0)) > 1e-12) return false;
    return true;
  }, E.prototype.equals = function(t5) {
    return this.equalBase(t5) && p2(this.value, t5.value);
  }, E.prototype.multiply = function(t5) {
    for (var n3 = this.clone(), r3 = isUnit(t5) ? t5 : new E(t5), i3 = 0; i3 < ee.length; i3++) n3.dimensions[i3] = (this.dimensions[i3] || 0) + (r3.dimensions[i3] || 0);
    for (var a3 = 0; a3 < r3.units.length; a3++) {
      var s3 = _objectSpread$2({}, r3.units[a3]);
      n3.units.push(s3);
    }
    return this.value !== null || r3.value !== null ? n3.value = o2(this.value === null ? this._normalize(W(r3.value)) : this.value, r3.value === null ? r3._normalize(W(this.value)) : r3.value) : n3.value = null, isUnit(t5) && (n3.skipAutomaticSimplification = false), U(n3);
  }, E.prototype.divideInto = function(t5) {
    return new E(t5).divide(this);
  }, E.prototype.divide = function(t5) {
    for (var n3 = this.clone(), r3 = isUnit(t5) ? t5 : new E(t5), i3 = 0; i3 < ee.length; i3++) n3.dimensions[i3] = (this.dimensions[i3] || 0) - (r3.dimensions[i3] || 0);
    for (var a3 = 0; a3 < r3.units.length; a3++) {
      var o3 = _objectSpread$2(_objectSpread$2({}, r3.units[a3]), {}, { power: -r3.units[a3].power });
      n3.units.push(o3);
    }
    return this.value !== null || r3.value !== null ? n3.value = s2(this.value === null ? this._normalize(W(r3.value)) : this.value, r3.value === null ? r3._normalize(W(this.value)) : r3.value) : n3.value = null, isUnit(t5) && (n3.skipAutomaticSimplification = false), U(n3);
  }, E.prototype.pow = function(t5) {
    for (var n3 = this.clone(), r3 = 0; r3 < ee.length; r3++) n3.dimensions[r3] = (this.dimensions[r3] || 0) * t5;
    for (var i3 = 0; i3 < n3.units.length; i3++) n3.units[i3].power *= t5;
    return n3.value === null ? n3.value = null : n3.value = c2(n3.value, t5), n3.skipAutomaticSimplification = false, U(n3);
  };
  function U(t5) {
    return t5.equalBase(Y.NONE) && t5.value !== null && !r2.predictable ? t5.value : t5;
  }
  function W(t5) {
    return E._getNumberConverter(typeOf(t5))(1);
  }
  E.prototype.abs = function() {
    var t5 = this.clone();
    if (t5.value !== null) if (t5._isDerived() || t5.units.length === 0 || t5.units[0].unit.offset === 0) t5.value = l2(t5.value);
    else {
      var n3 = t5._numberConverter(), r3 = o2(n3(t5.units[0].unit.value), n3(t5.units[0].unit.offset));
      t5.value = a2(l2(i2(t5.value, r3)), r3);
    }
    for (var s3 in t5.units) (t5.units[s3].unit.name === `VA` || t5.units[s3].unit.name === `VAR`) && (t5.units[s3].unit = X.W);
    return t5;
  }, E.prototype.to = function(t5) {
    var n3 = this.value === null ? this._normalize(1) : this.value, r3;
    if (typeof t5 == `string`) r3 = E.parse(t5);
    else if (isUnit(t5)) r3 = t5.clone();
    else throw Error(`String or Unit expected as parameter`);
    if (!this.equalBase(r3)) throw Error(`Units do not match ('${r3.toString()}' != '${this.toString()}')`);
    if (r3.value !== null) throw Error(`Cannot convert to a unit with a value`);
    if (this.value === null || this._isDerived() || this.units.length === 0 || r3.units.length === 0 || this.units[0].unit.offset === r3.units[0].unit.offset) r3.value = clone$3(n3);
    else {
      var s3 = E._getNumberConverter(typeOf(n3)), c3 = this.units[0].unit.value, l3 = this.units[0].unit.offset, u3 = o2(c3, l3), d3 = r3.units[0].unit.value, p3 = r3.units[0].unit.offset, m3 = o2(d3, p3);
      r3.value = i2(n3, s3(a2(u3, m3)));
    }
    return r3.fixPrefix = true, r3.skipAutomaticSimplification = true, r3;
  }, E.prototype.toNumber = function(t5) {
    return x2(this.toNumeric(t5));
  }, E.prototype.toNumeric = function(t5) {
    var n3 = t5 ? this.to(t5) : this.clone();
    return n3._isDerived() || n3.units.length === 0 ? n3._denormalize(n3.value) : n3._denormalize(n3.value, n3.units[0].prefix.value);
  }, E.prototype.toString = function() {
    return this.format();
  }, E.prototype.toJSON = function() {
    return { mathjs: `Unit`, value: this._denormalize(this.value), unit: this.units.length > 0 ? this.formatUnits() : null, fixPrefix: this.fixPrefix, skipSimp: this.skipAutomaticSimplification };
  }, E.fromJSON = function(t5) {
    var a3 = new E(t5.value, t5.unit ?? void 0);
    return a3.fixPrefix = t5.fixPrefix ?? S2, a3.skipAutomaticSimplification = t5.skipSimp ?? w2, a3;
  }, E.prototype.valueOf = E.prototype.toString, E.prototype.simplify = function() {
    var t5 = this.clone(), n3 = [], r3;
    for (var i3 in oe) if (hasOwnProperty$1(oe, i3) && t5.hasBase(Y[i3])) {
      r3 = i3;
      break;
    }
    if (r3 === `NONE`) t5.units = [];
    else {
      var a3;
      if (r3 && hasOwnProperty$1(oe, r3) && (a3 = oe[r3]), a3) t5.units = [{ unit: a3.unit, prefix: a3.prefix, power: 1 }];
      else {
        for (var o3 = false, s3 = 0; s3 < ee.length; s3++) {
          var c3 = ee[s3];
          Math.abs(t5.dimensions[s3] || 0) > 1e-12 && (hasOwnProperty$1(oe, c3) ? n3.push({ unit: oe[c3].unit, prefix: oe[c3].prefix, power: t5.dimensions[s3] || 0 }) : o3 = true);
        }
        n3.length < t5.units.length && !o3 && (t5.units = n3);
      }
    }
    return t5;
  }, E.prototype.toSI = function() {
    for (var t5 = this.clone(), n3 = [], r3 = 0; r3 < ee.length; r3++) {
      var i3 = ee[r3];
      if (Math.abs(t5.dimensions[r3] || 0) > 1e-12) if (hasOwnProperty$1(Z.si, i3)) n3.push({ unit: Z.si[i3].unit, prefix: Z.si[i3].prefix, power: t5.dimensions[r3] || 0 });
      else throw Error(`Cannot express custom unit ` + i3 + ` in SI units`);
    }
    return t5.units = n3, t5.fixPrefix = true, t5.skipAutomaticSimplification = true, this.value === null ? t5 : (t5.value = null, this.to(t5));
  }, E.prototype.formatUnits = function() {
    for (var t5 = ``, n3 = ``, r3 = 0, i3 = 0, a3 = 0; a3 < this.units.length; a3++) this.units[a3].power > 0 ? (r3++, t5 += ` ` + this.units[a3].prefix.name + this.units[a3].unit.name, Math.abs(this.units[a3].power - 1) > 1e-15 && (t5 += `^` + this.units[a3].power)) : this.units[a3].power < 0 && i3++;
    if (i3 > 0) for (var o3 = 0; o3 < this.units.length; o3++) this.units[o3].power < 0 && (r3 > 0 ? (n3 += ` ` + this.units[o3].prefix.name + this.units[o3].unit.name, Math.abs(this.units[o3].power + 1) > 1e-15 && (n3 += `^` + -this.units[o3].power)) : (n3 += ` ` + this.units[o3].prefix.name + this.units[o3].unit.name, n3 += `^` + this.units[o3].power));
    t5 = t5.substr(1), n3 = n3.substr(1), r3 > 1 && i3 > 0 && (t5 = `(` + t5 + `)`), i3 > 1 && r3 > 0 && (n3 = `(` + n3 + `)`);
    var s3 = t5;
    return r3 > 0 && i3 > 0 && (s3 += ` / `), s3 += n3, s3;
  }, E.prototype.toBest = function() {
    var t5 = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : [], n3 = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
    if (t5 && !Array.isArray(t5)) throw Error(`Invalid unit type. Expected string or Unit.`);
    var r3 = this.units[0].unit.prefixes;
    if (t5 && t5.length > 0) {
      var i3 = t5.map((t6) => {
        var n4 = null;
        if (typeof t6 == `string`) {
          if (n4 = E.parse(t6), !n4) throw Error(`Invalid unit type. Expected compatible string or Unit.`);
        } else if (!isUnit(t6)) throw Error(`Invalid unit type. Expected compatible string or Unit.`);
        n4 === null && (n4 = t6.clone());
        try {
          return this.to(n4.formatUnits()), n4;
        } catch {
          throw Error(`Invalid unit type. Expected compatible string or Unit.`);
        }
      }).map((t6) => t6.units[0].prefix);
      this.units[0].unit.prefixes = i3.reduce((t6, n4) => (t6[n4.name] = n4, t6), {}), this.units[0].prefix = i3[0];
    }
    var a3 = G(this, n3).simp;
    return this.units[0].unit.prefixes = r3, a3.fixPrefix = true, a3;
  }, E.prototype.format = function(t5) {
    var { simp: n3, valueStr: r3, unitStr: i3 } = G(this, t5), a3 = r3;
    return n3.value && isComplex(n3.value) && (a3 = `(` + a3 + `)`), i3.length > 0 && a3.length > 0 && (a3 += ` `), a3 += i3, a3;
  };
  function G(t5) {
    var n3 = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {}, r3 = t5.skipAutomaticSimplification || t5.value === null ? t5.clone() : t5.simplify();
    K(r3), q(r3, n3.offset);
    var i3 = r3._denormalize(r3.value);
    return { simp: r3, valueStr: r3.value === null ? `` : h2(i3, n3 || {}), unitStr: r3.formatUnits() };
  }
  function K(t5) {
    var n3 = false;
    for (var r3 in t5.value !== void 0 && t5.value !== null && isComplex(t5.value) && (n3 = Math.abs(t5.value.re) < 1e-14), t5.units) hasOwnProperty$1(t5.units, r3) && t5.units[r3].unit && (t5.units[r3].unit.name === `VA` && n3 ? t5.units[r3].unit = X.VAR : t5.units[r3].unit.name === `VAR` && !n3 && (t5.units[r3].unit = X.VA));
  }
  function q(t5, n3) {
    t5.units.length === 1 && !t5.fixPrefix && Math.abs(t5.units[0].power - Math.round(t5.units[0].power)) < 1e-14 && (t5.units[0].prefix = t5._bestPrefix(n3));
  }
  E.prototype._bestPrefix = function() {
    var t5 = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : 1.2;
    if (this.units.length !== 1 || Math.abs(this.units[0].power - Math.round(this.units[0].power)) >= 1e-14) throw Error(`Can only compute the best prefix for single units with integer powers, like kg, s^2, N^-1, and so forth!`);
    var n3 = this.value === null ? 0 : l2(this.value), r3 = l2(this.units[0].unit.value), i3 = this.units[0].prefix;
    if (n3 === 0) return i3;
    var a3 = this.units[0].power, o3 = Math.log(n3 / (i3.value * r3) ** +a3) / Math.LN10 - t5;
    if (o3 > -2.200001 && o3 < 1.800001) return i3;
    o3 = Math.abs(o3);
    var s3 = this.units[0].unit.prefixes;
    for (var c3 in s3) if (hasOwnProperty$1(s3, c3)) {
      var u3 = s3[c3];
      if (u3.scientific) {
        var d3 = Math.abs(Math.log(n3 / (u3.value * r3) ** +a3) / Math.LN10 - t5);
        (d3 < o3 || d3 === o3 && u3.name.length < i3.name.length) && (i3 = u3, o3 = d3);
      }
    }
    return i3;
  }, E.prototype.splitUnit = function(t5) {
    for (var n3 = this.clone(), r3 = [], o3 = 0; o3 < t5.length && (n3 = n3.to(t5[o3]), o3 !== t5.length - 1); o3++) {
      var s3 = n3.toNumeric(), c3 = d2(s3), l3 = void 0;
      l3 = p2(c3, s3) ? c3 : u2(n3.toNumeric());
      var m3 = new E(l3, t5[o3].toString());
      r3.push(m3), n3 = a2(n3, m3);
    }
    for (var h3 = 0, g3 = 0; g3 < r3.length; g3++) h3 = i2(h3, r3[g3].value);
    return p2(h3, this.value) && (n3.value = 0), r3.push(n3), r3;
  };
  var J = { NONE: { "": { name: ``, value: 1, scientific: true } }, SHORT: { "": { name: ``, value: 1, scientific: true }, da: { name: `da`, value: 10, scientific: false }, h: { name: `h`, value: 100, scientific: false }, k: { name: `k`, value: 1e3, scientific: true }, M: { name: `M`, value: 1e6, scientific: true }, G: { name: `G`, value: 1e9, scientific: true }, T: { name: `T`, value: 1e12, scientific: true }, P: { name: `P`, value: 1e15, scientific: true }, E: { name: `E`, value: 1e18, scientific: true }, Z: { name: `Z`, value: 1e21, scientific: true }, Y: { name: `Y`, value: 1e24, scientific: true }, R: { name: `R`, value: 1e27, scientific: true }, Q: { name: `Q`, value: 1e30, scientific: true }, d: { name: `d`, value: 0.1, scientific: false }, c: { name: `c`, value: 0.01, scientific: false }, m: { name: `m`, value: 1e-3, scientific: true }, u: { name: `u`, value: 1e-6, scientific: true }, n: { name: `n`, value: 1e-9, scientific: true }, p: { name: `p`, value: 1e-12, scientific: true }, f: { name: `f`, value: 1e-15, scientific: true }, a: { name: `a`, value: 1e-18, scientific: true }, z: { name: `z`, value: 1e-21, scientific: true }, y: { name: `y`, value: 1e-24, scientific: true }, r: { name: `r`, value: 1e-27, scientific: true }, q: { name: `q`, value: 1e-30, scientific: true } }, LONG: { "": { name: ``, value: 1, scientific: true }, deca: { name: `deca`, value: 10, scientific: false }, hecto: { name: `hecto`, value: 100, scientific: false }, kilo: { name: `kilo`, value: 1e3, scientific: true }, mega: { name: `mega`, value: 1e6, scientific: true }, giga: { name: `giga`, value: 1e9, scientific: true }, tera: { name: `tera`, value: 1e12, scientific: true }, peta: { name: `peta`, value: 1e15, scientific: true }, exa: { name: `exa`, value: 1e18, scientific: true }, zetta: { name: `zetta`, value: 1e21, scientific: true }, yotta: { name: `yotta`, value: 1e24, scientific: true }, ronna: { name: `ronna`, value: 1e27, scientific: true }, quetta: { name: `quetta`, value: 1e30, scientific: true }, deci: { name: `deci`, value: 0.1, scientific: false }, centi: { name: `centi`, value: 0.01, scientific: false }, milli: { name: `milli`, value: 1e-3, scientific: true }, micro: { name: `micro`, value: 1e-6, scientific: true }, nano: { name: `nano`, value: 1e-9, scientific: true }, pico: { name: `pico`, value: 1e-12, scientific: true }, femto: { name: `femto`, value: 1e-15, scientific: true }, atto: { name: `atto`, value: 1e-18, scientific: true }, zepto: { name: `zepto`, value: 1e-21, scientific: true }, yocto: { name: `yocto`, value: 1e-24, scientific: true }, ronto: { name: `ronto`, value: 1e-27, scientific: true }, quecto: { name: `quecto`, value: 1e-30, scientific: true } }, SQUARED: { "": { name: ``, value: 1, scientific: true }, da: { name: `da`, value: 100, scientific: false }, h: { name: `h`, value: 1e4, scientific: false }, k: { name: `k`, value: 1e6, scientific: true }, M: { name: `M`, value: 1e12, scientific: true }, G: { name: `G`, value: 1e18, scientific: true }, T: { name: `T`, value: 1e24, scientific: true }, P: { name: `P`, value: 1e30, scientific: true }, E: { name: `E`, value: 1e36, scientific: true }, Z: { name: `Z`, value: 1e42, scientific: true }, Y: { name: `Y`, value: 1e48, scientific: true }, R: { name: `R`, value: 1e54, scientific: true }, Q: { name: `Q`, value: 1e60, scientific: true }, d: { name: `d`, value: 0.01, scientific: false }, c: { name: `c`, value: 1e-4, scientific: false }, m: { name: `m`, value: 1e-6, scientific: true }, u: { name: `u`, value: 1e-12, scientific: true }, n: { name: `n`, value: 1e-18, scientific: true }, p: { name: `p`, value: 1e-24, scientific: true }, f: { name: `f`, value: 1e-30, scientific: true }, a: { name: `a`, value: 1e-36, scientific: true }, z: { name: `z`, value: 1e-42, scientific: true }, y: { name: `y`, value: 1e-48, scientific: true }, r: { name: `r`, value: 1e-54, scientific: true }, q: { name: `q`, value: 1e-60, scientific: true } }, CUBIC: { "": { name: ``, value: 1, scientific: true }, da: { name: `da`, value: 1e3, scientific: false }, h: { name: `h`, value: 1e6, scientific: false }, k: { name: `k`, value: 1e9, scientific: true }, M: { name: `M`, value: 1e18, scientific: true }, G: { name: `G`, value: 1e27, scientific: true }, T: { name: `T`, value: 1e36, scientific: true }, P: { name: `P`, value: 1e45, scientific: true }, E: { name: `E`, value: 1e54, scientific: true }, Z: { name: `Z`, value: 1e63, scientific: true }, Y: { name: `Y`, value: 1e72, scientific: true }, R: { name: `R`, value: 1e81, scientific: true }, Q: { name: `Q`, value: 1e90, scientific: true }, d: { name: `d`, value: 1e-3, scientific: false }, c: { name: `c`, value: 1e-6, scientific: false }, m: { name: `m`, value: 1e-9, scientific: true }, u: { name: `u`, value: 1e-18, scientific: true }, n: { name: `n`, value: 1e-27, scientific: true }, p: { name: `p`, value: 1e-36, scientific: true }, f: { name: `f`, value: 1e-45, scientific: true }, a: { name: `a`, value: 1e-54, scientific: true }, z: { name: `z`, value: 1e-63, scientific: true }, y: { name: `y`, value: 1e-72, scientific: true }, r: { name: `r`, value: 1e-81, scientific: true }, q: { name: `q`, value: 1e-90, scientific: true } }, BINARY_SHORT_SI: { "": { name: ``, value: 1, scientific: true }, k: { name: `k`, value: 1e3, scientific: true }, M: { name: `M`, value: 1e6, scientific: true }, G: { name: `G`, value: 1e9, scientific: true }, T: { name: `T`, value: 1e12, scientific: true }, P: { name: `P`, value: 1e15, scientific: true }, E: { name: `E`, value: 1e18, scientific: true }, Z: { name: `Z`, value: 1e21, scientific: true }, Y: { name: `Y`, value: 1e24, scientific: true } }, BINARY_SHORT_IEC: { "": { name: ``, value: 1, scientific: true }, Ki: { name: `Ki`, value: 1024, scientific: true }, Mi: { name: `Mi`, value: 1024 ** 2, scientific: true }, Gi: { name: `Gi`, value: 1024 ** 3, scientific: true }, Ti: { name: `Ti`, value: 1024 ** 4, scientific: true }, Pi: { name: `Pi`, value: 1024 ** 5, scientific: true }, Ei: { name: `Ei`, value: 1024 ** 6, scientific: true }, Zi: { name: `Zi`, value: 1024 ** 7, scientific: true }, Yi: { name: `Yi`, value: 1024 ** 8, scientific: true } }, BINARY_LONG_SI: { "": { name: ``, value: 1, scientific: true }, kilo: { name: `kilo`, value: 1e3, scientific: true }, mega: { name: `mega`, value: 1e6, scientific: true }, giga: { name: `giga`, value: 1e9, scientific: true }, tera: { name: `tera`, value: 1e12, scientific: true }, peta: { name: `peta`, value: 1e15, scientific: true }, exa: { name: `exa`, value: 1e18, scientific: true }, zetta: { name: `zetta`, value: 1e21, scientific: true }, yotta: { name: `yotta`, value: 1e24, scientific: true } }, BINARY_LONG_IEC: { "": { name: ``, value: 1, scientific: true }, kibi: { name: `kibi`, value: 1024, scientific: true }, mebi: { name: `mebi`, value: 1024 ** 2, scientific: true }, gibi: { name: `gibi`, value: 1024 ** 3, scientific: true }, tebi: { name: `tebi`, value: 1024 ** 4, scientific: true }, pebi: { name: `pebi`, value: 1024 ** 5, scientific: true }, exi: { name: `exi`, value: 1024 ** 6, scientific: true }, zebi: { name: `zebi`, value: 1024 ** 7, scientific: true }, yobi: { name: `yobi`, value: 1024 ** 8, scientific: true } }, BTU: { "": { name: ``, value: 1, scientific: true }, MM: { name: `MM`, value: 1e6, scientific: true } } };
  J.SHORTLONG = _extends({}, J.SHORT, J.LONG), J.BINARY_SHORT = _extends({}, J.BINARY_SHORT_SI, J.BINARY_SHORT_IEC), J.BINARY_LONG = _extends({}, J.BINARY_LONG_SI, J.BINARY_LONG_IEC);
  var ee = [`MASS`, `LENGTH`, `TIME`, `CURRENT`, `TEMPERATURE`, `LUMINOUS_INTENSITY`, `AMOUNT_OF_SUBSTANCE`, `ANGLE`, `BIT`], Y = { NONE: { dimensions: [0, 0, 0, 0, 0, 0, 0, 0, 0] }, MASS: { dimensions: [1, 0, 0, 0, 0, 0, 0, 0, 0] }, LENGTH: { dimensions: [0, 1, 0, 0, 0, 0, 0, 0, 0] }, TIME: { dimensions: [0, 0, 1, 0, 0, 0, 0, 0, 0] }, CURRENT: { dimensions: [0, 0, 0, 1, 0, 0, 0, 0, 0] }, TEMPERATURE: { dimensions: [0, 0, 0, 0, 1, 0, 0, 0, 0] }, LUMINOUS_INTENSITY: { dimensions: [0, 0, 0, 0, 0, 1, 0, 0, 0] }, AMOUNT_OF_SUBSTANCE: { dimensions: [0, 0, 0, 0, 0, 0, 1, 0, 0] }, FORCE: { dimensions: [1, 1, -2, 0, 0, 0, 0, 0, 0] }, SURFACE: { dimensions: [0, 2, 0, 0, 0, 0, 0, 0, 0] }, VOLUME: { dimensions: [0, 3, 0, 0, 0, 0, 0, 0, 0] }, ENERGY: { dimensions: [1, 2, -2, 0, 0, 0, 0, 0, 0] }, POWER: { dimensions: [1, 2, -3, 0, 0, 0, 0, 0, 0] }, PRESSURE: { dimensions: [1, -1, -2, 0, 0, 0, 0, 0, 0] }, ELECTRIC_CHARGE: { dimensions: [0, 0, 1, 1, 0, 0, 0, 0, 0] }, ELECTRIC_CAPACITANCE: { dimensions: [-1, -2, 4, 2, 0, 0, 0, 0, 0] }, ELECTRIC_POTENTIAL: { dimensions: [1, 2, -3, -1, 0, 0, 0, 0, 0] }, ELECTRIC_RESISTANCE: { dimensions: [1, 2, -3, -2, 0, 0, 0, 0, 0] }, ELECTRIC_INDUCTANCE: { dimensions: [1, 2, -2, -2, 0, 0, 0, 0, 0] }, ELECTRIC_CONDUCTANCE: { dimensions: [-1, -2, 3, 2, 0, 0, 0, 0, 0] }, MAGNETIC_FLUX: { dimensions: [1, 2, -2, -1, 0, 0, 0, 0, 0] }, MAGNETIC_FLUX_DENSITY: { dimensions: [1, 0, -2, -1, 0, 0, 0, 0, 0] }, FREQUENCY: { dimensions: [0, 0, -1, 0, 0, 0, 0, 0, 0] }, ANGLE: { dimensions: [0, 0, 0, 0, 0, 0, 0, 1, 0] }, BIT: { dimensions: [0, 0, 0, 0, 0, 0, 0, 0, 1] } };
  for (var te in Y) hasOwnProperty$1(Y, te) && (Y[te].key = te);
  var ne = { name: ``, base: {}, value: 1, offset: 0, dimensions: ee.map((t5) => 0) }, X = { meter: { name: `meter`, base: Y.LENGTH, prefixes: J.LONG, value: 1, offset: 0 }, inch: { name: `inch`, base: Y.LENGTH, prefixes: J.NONE, value: 0.0254, offset: 0 }, foot: { name: `foot`, base: Y.LENGTH, prefixes: J.NONE, value: 0.3048, offset: 0 }, yard: { name: `yard`, base: Y.LENGTH, prefixes: J.NONE, value: 0.9144, offset: 0 }, mile: { name: `mile`, base: Y.LENGTH, prefixes: J.NONE, value: 1609.344, offset: 0 }, link: { name: `link`, base: Y.LENGTH, prefixes: J.NONE, value: 0.201168, offset: 0 }, rod: { name: `rod`, base: Y.LENGTH, prefixes: J.NONE, value: 5.0292, offset: 0 }, chain: { name: `chain`, base: Y.LENGTH, prefixes: J.NONE, value: 20.1168, offset: 0 }, angstrom: { name: `angstrom`, base: Y.LENGTH, prefixes: J.NONE, value: 1e-10, offset: 0 }, m: { name: `m`, base: Y.LENGTH, prefixes: J.SHORT, value: 1, offset: 0 }, in: { name: `in`, base: Y.LENGTH, prefixes: J.NONE, value: 0.0254, offset: 0 }, ft: { name: `ft`, base: Y.LENGTH, prefixes: J.NONE, value: 0.3048, offset: 0 }, yd: { name: `yd`, base: Y.LENGTH, prefixes: J.NONE, value: 0.9144, offset: 0 }, mi: { name: `mi`, base: Y.LENGTH, prefixes: J.NONE, value: 1609.344, offset: 0 }, li: { name: `li`, base: Y.LENGTH, prefixes: J.NONE, value: 0.201168, offset: 0 }, rd: { name: `rd`, base: Y.LENGTH, prefixes: J.NONE, value: 5.02921, offset: 0 }, ch: { name: `ch`, base: Y.LENGTH, prefixes: J.NONE, value: 20.1168, offset: 0 }, mil: { name: `mil`, base: Y.LENGTH, prefixes: J.NONE, value: 254e-7, offset: 0 }, m2: { name: `m2`, base: Y.SURFACE, prefixes: J.SQUARED, value: 1, offset: 0 }, sqin: { name: `sqin`, base: Y.SURFACE, prefixes: J.NONE, value: 64516e-8, offset: 0 }, sqft: { name: `sqft`, base: Y.SURFACE, prefixes: J.NONE, value: 0.09290304, offset: 0 }, sqyd: { name: `sqyd`, base: Y.SURFACE, prefixes: J.NONE, value: 0.83612736, offset: 0 }, sqmi: { name: `sqmi`, base: Y.SURFACE, prefixes: J.NONE, value: 2589988110336e-6, offset: 0 }, sqrd: { name: `sqrd`, base: Y.SURFACE, prefixes: J.NONE, value: 25.29295, offset: 0 }, sqch: { name: `sqch`, base: Y.SURFACE, prefixes: J.NONE, value: 404.6873, offset: 0 }, sqmil: { name: `sqmil`, base: Y.SURFACE, prefixes: J.NONE, value: 64516e-14, offset: 0 }, acre: { name: `acre`, base: Y.SURFACE, prefixes: J.NONE, value: 4046.86, offset: 0 }, hectare: { name: `hectare`, base: Y.SURFACE, prefixes: J.NONE, value: 1e4, offset: 0 }, m3: { name: `m3`, base: Y.VOLUME, prefixes: J.CUBIC, value: 1, offset: 0 }, L: { name: `L`, base: Y.VOLUME, prefixes: J.SHORT, value: 1e-3, offset: 0 }, l: { name: `l`, base: Y.VOLUME, prefixes: J.SHORT, value: 1e-3, offset: 0 }, litre: { name: `litre`, base: Y.VOLUME, prefixes: J.LONG, value: 1e-3, offset: 0 }, cuin: { name: `cuin`, base: Y.VOLUME, prefixes: J.NONE, value: 16387064e-12, offset: 0 }, cuft: { name: `cuft`, base: Y.VOLUME, prefixes: J.NONE, value: 0.028316846592, offset: 0 }, cuyd: { name: `cuyd`, base: Y.VOLUME, prefixes: J.NONE, value: 0.764554857984, offset: 0 }, teaspoon: { name: `teaspoon`, base: Y.VOLUME, prefixes: J.NONE, value: 5e-6, offset: 0 }, tablespoon: { name: `tablespoon`, base: Y.VOLUME, prefixes: J.NONE, value: 15e-6, offset: 0 }, drop: { name: `drop`, base: Y.VOLUME, prefixes: J.NONE, value: 5e-8, offset: 0 }, gtt: { name: `gtt`, base: Y.VOLUME, prefixes: J.NONE, value: 5e-8, offset: 0 }, minim: { name: `minim`, base: Y.VOLUME, prefixes: J.NONE, value: 61611519921875e-21, offset: 0 }, fluiddram: { name: `fluiddram`, base: Y.VOLUME, prefixes: J.NONE, value: 36966911953125e-19, offset: 0 }, fluidounce: { name: `fluidounce`, base: Y.VOLUME, prefixes: J.NONE, value: 295735295625e-16, offset: 0 }, gill: { name: `gill`, base: Y.VOLUME, prefixes: J.NONE, value: 11829411825e-14, offset: 0 }, cc: { name: `cc`, base: Y.VOLUME, prefixes: J.NONE, value: 1e-6, offset: 0 }, cup: { name: `cup`, base: Y.VOLUME, prefixes: J.NONE, value: 2365882365e-13, offset: 0 }, pint: { name: `pint`, base: Y.VOLUME, prefixes: J.NONE, value: 473176473e-12, offset: 0 }, quart: { name: `quart`, base: Y.VOLUME, prefixes: J.NONE, value: 946352946e-12, offset: 0 }, gallon: { name: `gallon`, base: Y.VOLUME, prefixes: J.NONE, value: 0.003785411784, offset: 0 }, beerbarrel: { name: `beerbarrel`, base: Y.VOLUME, prefixes: J.NONE, value: 0.117347765304, offset: 0 }, oilbarrel: { name: `oilbarrel`, base: Y.VOLUME, prefixes: J.NONE, value: 0.158987294928, offset: 0 }, hogshead: { name: `hogshead`, base: Y.VOLUME, prefixes: J.NONE, value: 0.238480942392, offset: 0 }, g: { name: `g`, base: Y.MASS, prefixes: J.SHORT, value: 1e-3, offset: 0 }, gram: { name: `gram`, base: Y.MASS, prefixes: J.LONG, value: 1e-3, offset: 0 }, ton: { name: `ton`, base: Y.MASS, prefixes: J.SHORT, value: 907.18474, offset: 0 }, t: { name: `t`, base: Y.MASS, prefixes: J.SHORT, value: 1e3, offset: 0 }, tonne: { name: `tonne`, base: Y.MASS, prefixes: J.LONG, value: 1e3, offset: 0 }, grain: { name: `grain`, base: Y.MASS, prefixes: J.NONE, value: 6479891e-11, offset: 0 }, dram: { name: `dram`, base: Y.MASS, prefixes: J.NONE, value: 0.0017718451953125, offset: 0 }, ounce: { name: `ounce`, base: Y.MASS, prefixes: J.NONE, value: 0.028349523125, offset: 0 }, poundmass: { name: `poundmass`, base: Y.MASS, prefixes: J.NONE, value: 0.45359237, offset: 0 }, hundredweight: { name: `hundredweight`, base: Y.MASS, prefixes: J.NONE, value: 45.359237, offset: 0 }, stick: { name: `stick`, base: Y.MASS, prefixes: J.NONE, value: 0.115, offset: 0 }, stone: { name: `stone`, base: Y.MASS, prefixes: J.NONE, value: 6.35029318, offset: 0 }, gr: { name: `gr`, base: Y.MASS, prefixes: J.NONE, value: 6479891e-11, offset: 0 }, dr: { name: `dr`, base: Y.MASS, prefixes: J.NONE, value: 0.0017718451953125, offset: 0 }, oz: { name: `oz`, base: Y.MASS, prefixes: J.NONE, value: 0.028349523125, offset: 0 }, lbm: { name: `lbm`, base: Y.MASS, prefixes: J.NONE, value: 0.45359237, offset: 0 }, cwt: { name: `cwt`, base: Y.MASS, prefixes: J.NONE, value: 45.359237, offset: 0 }, s: { name: `s`, base: Y.TIME, prefixes: J.SHORT, value: 1, offset: 0 }, min: { name: `min`, base: Y.TIME, prefixes: J.NONE, value: 60, offset: 0 }, h: { name: `h`, base: Y.TIME, prefixes: J.NONE, value: 3600, offset: 0 }, second: { name: `second`, base: Y.TIME, prefixes: J.LONG, value: 1, offset: 0 }, sec: { name: `sec`, base: Y.TIME, prefixes: J.LONG, value: 1, offset: 0 }, minute: { name: `minute`, base: Y.TIME, prefixes: J.NONE, value: 60, offset: 0 }, hour: { name: `hour`, base: Y.TIME, prefixes: J.NONE, value: 3600, offset: 0 }, day: { name: `day`, base: Y.TIME, prefixes: J.NONE, value: 86400, offset: 0 }, week: { name: `week`, base: Y.TIME, prefixes: J.NONE, value: 7 * 86400, offset: 0 }, month: { name: `month`, base: Y.TIME, prefixes: J.NONE, value: 2629800, offset: 0 }, year: { name: `year`, base: Y.TIME, prefixes: J.NONE, value: 31557600, offset: 0 }, decade: { name: `decade`, base: Y.TIME, prefixes: J.NONE, value: 315576e3, offset: 0 }, century: { name: `century`, base: Y.TIME, prefixes: J.NONE, value: 315576e4, offset: 0 }, millennium: { name: `millennium`, base: Y.TIME, prefixes: J.NONE, value: 315576e5, offset: 0 }, hertz: { name: `Hertz`, base: Y.FREQUENCY, prefixes: J.LONG, value: 1, offset: 0, reciprocal: true }, Hz: { name: `Hz`, base: Y.FREQUENCY, prefixes: J.SHORT, value: 1, offset: 0, reciprocal: true }, rad: { name: `rad`, base: Y.ANGLE, prefixes: J.SHORT, value: 1, offset: 0 }, radian: { name: `radian`, base: Y.ANGLE, prefixes: J.LONG, value: 1, offset: 0 }, deg: { name: `deg`, base: Y.ANGLE, prefixes: J.SHORT, value: null, offset: 0 }, degree: { name: `degree`, base: Y.ANGLE, prefixes: J.LONG, value: null, offset: 0 }, grad: { name: `grad`, base: Y.ANGLE, prefixes: J.SHORT, value: null, offset: 0 }, gradian: { name: `gradian`, base: Y.ANGLE, prefixes: J.LONG, value: null, offset: 0 }, cycle: { name: `cycle`, base: Y.ANGLE, prefixes: J.NONE, value: null, offset: 0 }, arcsec: { name: `arcsec`, base: Y.ANGLE, prefixes: J.NONE, value: null, offset: 0 }, arcmin: { name: `arcmin`, base: Y.ANGLE, prefixes: J.NONE, value: null, offset: 0 }, A: { name: `A`, base: Y.CURRENT, prefixes: J.SHORT, value: 1, offset: 0 }, ampere: { name: `ampere`, base: Y.CURRENT, prefixes: J.LONG, value: 1, offset: 0 }, K: { name: `K`, base: Y.TEMPERATURE, prefixes: J.SHORT, value: 1, offset: 0 }, degC: { name: `degC`, base: Y.TEMPERATURE, prefixes: J.SHORT, value: 1, offset: 273.15 }, degF: { name: `degF`, base: Y.TEMPERATURE, prefixes: J.SHORT, value: new b2(5, 9), offset: 459.67 }, degR: { name: `degR`, base: Y.TEMPERATURE, prefixes: J.SHORT, value: new b2(5, 9), offset: 0 }, kelvin: { name: `kelvin`, base: Y.TEMPERATURE, prefixes: J.LONG, value: 1, offset: 0 }, celsius: { name: `celsius`, base: Y.TEMPERATURE, prefixes: J.LONG, value: 1, offset: 273.15 }, fahrenheit: { name: `fahrenheit`, base: Y.TEMPERATURE, prefixes: J.LONG, value: new b2(5, 9), offset: 459.67 }, rankine: { name: `rankine`, base: Y.TEMPERATURE, prefixes: J.LONG, value: new b2(5, 9), offset: 0 }, mol: { name: `mol`, base: Y.AMOUNT_OF_SUBSTANCE, prefixes: J.SHORT, value: 1, offset: 0 }, mole: { name: `mole`, base: Y.AMOUNT_OF_SUBSTANCE, prefixes: J.LONG, value: 1, offset: 0 }, cd: { name: `cd`, base: Y.LUMINOUS_INTENSITY, prefixes: J.SHORT, value: 1, offset: 0 }, candela: { name: `candela`, base: Y.LUMINOUS_INTENSITY, prefixes: J.LONG, value: 1, offset: 0 }, N: { name: `N`, base: Y.FORCE, prefixes: J.SHORT, value: 1, offset: 0 }, newton: { name: `newton`, base: Y.FORCE, prefixes: J.LONG, value: 1, offset: 0 }, dyn: { name: `dyn`, base: Y.FORCE, prefixes: J.SHORT, value: 1e-5, offset: 0 }, dyne: { name: `dyne`, base: Y.FORCE, prefixes: J.LONG, value: 1e-5, offset: 0 }, lbf: { name: `lbf`, base: Y.FORCE, prefixes: J.NONE, value: 4.4482216152605, offset: 0 }, poundforce: { name: `poundforce`, base: Y.FORCE, prefixes: J.NONE, value: 4.4482216152605, offset: 0 }, kip: { name: `kip`, base: Y.FORCE, prefixes: J.LONG, value: 4448.2216, offset: 0 }, kilogramforce: { name: `kilogramforce`, base: Y.FORCE, prefixes: J.NONE, value: 9.80665, offset: 0 }, J: { name: `J`, base: Y.ENERGY, prefixes: J.SHORT, value: 1, offset: 0 }, joule: { name: `joule`, base: Y.ENERGY, prefixes: J.LONG, value: 1, offset: 0 }, erg: { name: `erg`, base: Y.ENERGY, prefixes: J.SHORTLONG, value: 1e-7, offset: 0 }, Wh: { name: `Wh`, base: Y.ENERGY, prefixes: J.SHORT, value: 3600, offset: 0 }, BTU: { name: `BTU`, base: Y.ENERGY, prefixes: J.BTU, value: 1055.05585262, offset: 0 }, eV: { name: `eV`, base: Y.ENERGY, prefixes: J.SHORT, value: 1602176565e-28, offset: 0 }, electronvolt: { name: `electronvolt`, base: Y.ENERGY, prefixes: J.LONG, value: 1602176565e-28, offset: 0 }, W: { name: `W`, base: Y.POWER, prefixes: J.SHORT, value: 1, offset: 0 }, watt: { name: `watt`, base: Y.POWER, prefixes: J.LONG, value: 1, offset: 0 }, hp: { name: `hp`, base: Y.POWER, prefixes: J.NONE, value: 745.6998715386, offset: 0 }, VAR: { name: `VAR`, base: Y.POWER, prefixes: J.SHORT, value: v2.I, offset: 0 }, VA: { name: `VA`, base: Y.POWER, prefixes: J.SHORT, value: 1, offset: 0 }, Pa: { name: `Pa`, base: Y.PRESSURE, prefixes: J.SHORT, value: 1, offset: 0 }, psi: { name: `psi`, base: Y.PRESSURE, prefixes: J.NONE, value: 6894.75729276459, offset: 0 }, atm: { name: `atm`, base: Y.PRESSURE, prefixes: J.NONE, value: 101325, offset: 0 }, bar: { name: `bar`, base: Y.PRESSURE, prefixes: J.SHORTLONG, value: 1e5, offset: 0 }, torr: { name: `torr`, base: Y.PRESSURE, prefixes: J.NONE, value: 133.322, offset: 0 }, mmHg: { name: `mmHg`, base: Y.PRESSURE, prefixes: J.NONE, value: 133.322, offset: 0 }, mmH2O: { name: `mmH2O`, base: Y.PRESSURE, prefixes: J.NONE, value: 9.80665, offset: 0 }, cmH2O: { name: `cmH2O`, base: Y.PRESSURE, prefixes: J.NONE, value: 98.0665, offset: 0 }, coulomb: { name: `coulomb`, base: Y.ELECTRIC_CHARGE, prefixes: J.LONG, value: 1, offset: 0 }, C: { name: `C`, base: Y.ELECTRIC_CHARGE, prefixes: J.SHORT, value: 1, offset: 0 }, Ah: { name: `Ah`, base: Y.ELECTRIC_CHARGE, prefixes: J.SHORT, value: 3600, offset: 0 }, farad: { name: `farad`, base: Y.ELECTRIC_CAPACITANCE, prefixes: J.LONG, value: 1, offset: 0 }, F: { name: `F`, base: Y.ELECTRIC_CAPACITANCE, prefixes: J.SHORT, value: 1, offset: 0 }, volt: { name: `volt`, base: Y.ELECTRIC_POTENTIAL, prefixes: J.LONG, value: 1, offset: 0 }, V: { name: `V`, base: Y.ELECTRIC_POTENTIAL, prefixes: J.SHORT, value: 1, offset: 0 }, ohm: { name: `ohm`, base: Y.ELECTRIC_RESISTANCE, prefixes: J.SHORTLONG, value: 1, offset: 0 }, henry: { name: `henry`, base: Y.ELECTRIC_INDUCTANCE, prefixes: J.LONG, value: 1, offset: 0 }, H: { name: `H`, base: Y.ELECTRIC_INDUCTANCE, prefixes: J.SHORT, value: 1, offset: 0 }, siemens: { name: `siemens`, base: Y.ELECTRIC_CONDUCTANCE, prefixes: J.LONG, value: 1, offset: 0 }, S: { name: `S`, base: Y.ELECTRIC_CONDUCTANCE, prefixes: J.SHORT, value: 1, offset: 0 }, weber: { name: `weber`, base: Y.MAGNETIC_FLUX, prefixes: J.LONG, value: 1, offset: 0 }, Wb: { name: `Wb`, base: Y.MAGNETIC_FLUX, prefixes: J.SHORT, value: 1, offset: 0 }, tesla: { name: `tesla`, base: Y.MAGNETIC_FLUX_DENSITY, prefixes: J.LONG, value: 1, offset: 0 }, T: { name: `T`, base: Y.MAGNETIC_FLUX_DENSITY, prefixes: J.SHORT, value: 1, offset: 0 }, b: { name: `b`, base: Y.BIT, prefixes: J.BINARY_SHORT, value: 1, offset: 0 }, bits: { name: `bits`, base: Y.BIT, prefixes: J.BINARY_LONG, value: 1, offset: 0 }, B: { name: `B`, base: Y.BIT, prefixes: J.BINARY_SHORT, value: 8, offset: 0 }, bytes: { name: `bytes`, base: Y.BIT, prefixes: J.BINARY_LONG, value: 8, offset: 0 } }, ie = { meters: `meter`, inches: `inch`, feet: `foot`, yards: `yard`, miles: `mile`, links: `link`, rods: `rod`, chains: `chain`, angstroms: `angstrom`, lt: `l`, litres: `litre`, liter: `litre`, liters: `litre`, teaspoons: `teaspoon`, tablespoons: `tablespoon`, minims: `minim`, fldr: `fluiddram`, fluiddrams: `fluiddram`, floz: `fluidounce`, fluidounces: `fluidounce`, gi: `gill`, gills: `gill`, cp: `cup`, cups: `cup`, pt: `pint`, pints: `pint`, qt: `quart`, quarts: `quart`, gal: `gallon`, gallons: `gallon`, bbl: `beerbarrel`, beerbarrels: `beerbarrel`, obl: `oilbarrel`, oilbarrels: `oilbarrel`, hogsheads: `hogshead`, gtts: `gtt`, grams: `gram`, tons: `ton`, tonnes: `tonne`, grains: `grain`, drams: `dram`, ounces: `ounce`, poundmasses: `poundmass`, hundredweights: `hundredweight`, sticks: `stick`, lb: `lbm`, lbs: `lbm`, kips: `kip`, kgf: `kilogramforce`, acres: `acre`, hectares: `hectare`, sqfeet: `sqft`, sqyard: `sqyd`, sqmile: `sqmi`, sqmiles: `sqmi`, mmhg: `mmHg`, mmh2o: `mmH2O`, cmh2o: `cmH2O`, seconds: `second`, secs: `second`, minutes: `minute`, mins: `minute`, hours: `hour`, hr: `hour`, hrs: `hour`, days: `day`, weeks: `week`, months: `month`, years: `year`, decades: `decade`, centuries: `century`, millennia: `millennium`, hertz: `hertz`, radians: `radian`, degrees: `degree`, gradians: `gradian`, cycles: `cycle`, arcsecond: `arcsec`, arcseconds: `arcsec`, arcminute: `arcmin`, arcminutes: `arcmin`, BTUs: `BTU`, watts: `watt`, joules: `joule`, amperes: `ampere`, amps: `ampere`, amp: `ampere`, coulombs: `coulomb`, volts: `volt`, ohms: `ohm`, farads: `farad`, webers: `weber`, teslas: `tesla`, electronvolts: `electronvolt`, moles: `mole`, bit: `bits`, byte: `bytes` };
  function ae(t5) {
    if (t5.number === `BigNumber`) {
      var n3 = createBigNumberPi(y2);
      X.rad.value = new y2(1), X.deg.value = n3.div(180), X.grad.value = n3.div(200), X.cycle.value = n3.times(2), X.arcsec.value = n3.div(648e3), X.arcmin.value = n3.div(10800);
    } else X.rad.value = 1, X.deg.value = Math.PI / 180, X.grad.value = Math.PI / 200, X.cycle.value = Math.PI * 2, X.arcsec.value = Math.PI / 648e3, X.arcmin.value = Math.PI / 10800;
    X.radian.value = X.rad.value, X.degree.value = X.deg.value, X.gradian.value = X.grad.value;
  }
  ae(r2), n2 && n2(`config`, function(t5, n3) {
    t5.number !== n3.number && ae(t5);
  });
  var Z = { si: { NONE: { unit: ne, prefix: J.NONE[``] }, LENGTH: { unit: X.m, prefix: J.SHORT[``] }, MASS: { unit: X.g, prefix: J.SHORT.k }, TIME: { unit: X.s, prefix: J.SHORT[``] }, CURRENT: { unit: X.A, prefix: J.SHORT[``] }, TEMPERATURE: { unit: X.K, prefix: J.SHORT[``] }, LUMINOUS_INTENSITY: { unit: X.cd, prefix: J.SHORT[``] }, AMOUNT_OF_SUBSTANCE: { unit: X.mol, prefix: J.SHORT[``] }, ANGLE: { unit: X.rad, prefix: J.SHORT[``] }, BIT: { unit: X.bits, prefix: J.SHORT[``] }, FORCE: { unit: X.N, prefix: J.SHORT[``] }, ENERGY: { unit: X.J, prefix: J.SHORT[``] }, POWER: { unit: X.W, prefix: J.SHORT[``] }, PRESSURE: { unit: X.Pa, prefix: J.SHORT[``] }, ELECTRIC_CHARGE: { unit: X.C, prefix: J.SHORT[``] }, ELECTRIC_CAPACITANCE: { unit: X.F, prefix: J.SHORT[``] }, ELECTRIC_POTENTIAL: { unit: X.V, prefix: J.SHORT[``] }, ELECTRIC_RESISTANCE: { unit: X.ohm, prefix: J.SHORT[``] }, ELECTRIC_INDUCTANCE: { unit: X.H, prefix: J.SHORT[``] }, ELECTRIC_CONDUCTANCE: { unit: X.S, prefix: J.SHORT[``] }, MAGNETIC_FLUX: { unit: X.Wb, prefix: J.SHORT[``] }, MAGNETIC_FLUX_DENSITY: { unit: X.T, prefix: J.SHORT[``] }, FREQUENCY: { unit: X.Hz, prefix: J.SHORT[``] } } };
  Z.cgs = JSON.parse(JSON.stringify(Z.si)), Z.cgs.LENGTH = { unit: X.m, prefix: J.SHORT.c }, Z.cgs.MASS = { unit: X.g, prefix: J.SHORT[``] }, Z.cgs.FORCE = { unit: X.dyn, prefix: J.SHORT[``] }, Z.cgs.ENERGY = { unit: X.erg, prefix: J.NONE[``] }, Z.us = JSON.parse(JSON.stringify(Z.si)), Z.us.LENGTH = { unit: X.ft, prefix: J.NONE[``] }, Z.us.MASS = { unit: X.lbm, prefix: J.NONE[``] }, Z.us.TEMPERATURE = { unit: X.degF, prefix: J.NONE[``] }, Z.us.FORCE = { unit: X.lbf, prefix: J.NONE[``] }, Z.us.ENERGY = { unit: X.BTU, prefix: J.BTU[``] }, Z.us.POWER = { unit: X.hp, prefix: J.NONE[``] }, Z.us.PRESSURE = { unit: X.psi, prefix: J.NONE[``] }, Z.auto = JSON.parse(JSON.stringify(Z.si));
  var oe = Z.auto;
  for (var $2 in E.setUnitSystem = function(t5) {
    if (hasOwnProperty$1(Z, t5)) oe = Z[t5];
    else throw Error(`Unit system ` + t5 + ` does not exist. Choices are: ` + Object.keys(Z).join(`, `));
  }, E.getUnitSystem = function() {
    for (var t5 in Z) if (hasOwnProperty$1(Z, t5) && Z[t5] === oe) return t5;
  }, E.typeConverters = { BigNumber: function(t5) {
    return t5 != null && t5.isFraction ? new y2(String(t5.n)).div(String(t5.d)).times(String(t5.s)) : new y2(t5 + ``);
  }, Fraction: function(t5) {
    return new b2(t5);
  }, Complex: function(t5) {
    return t5;
  }, number: function(t5) {
    return t5 != null && t5.isFraction ? g2(t5) : t5;
  } }, E.prototype._numberConverter = function() {
    var t5 = E.typeConverters[this.valueType()];
    if (t5) return t5;
    throw TypeError(`Unsupported Unit value type "` + this.valueType() + `"`);
  }, E._getNumberConverter = function(t5) {
    if (!E.typeConverters[t5]) throw TypeError(`Unsupported type "` + t5 + `"`);
    return E.typeConverters[t5];
  }, X) if (hasOwnProperty$1(X, $2)) {
    var se = X[$2];
    se.dimensions = se.base.dimensions;
  }
  for (var ce in ie) if (hasOwnProperty$1(ie, ce)) {
    var le = X[ie[ce]], ue = {};
    for (var de in le) hasOwnProperty$1(le, de) && (ue[de] = le[de]);
    ue.name = ce, X[ce] = ue;
  }
  E.isValidAlpha = function(t5) {
    return /^[a-zA-Z]$/.test(t5);
  };
  function fe(t5) {
    for (var n3 = 0; n3 < t5.length; n3++) {
      if (j2 = t5.charAt(n3), n3 === 0 && !E.isValidAlpha(j2)) throw Error(`Invalid unit name (must begin with alpha character): "` + t5 + `"`);
      if (n3 > 0 && !(E.isValidAlpha(j2) || F(j2))) throw Error(`Invalid unit name (only alphanumeric characters are allowed): "` + t5 + `"`);
    }
  }
  return E.createUnit = function(t5, n3) {
    if (typeof t5 != `object`) throw TypeError(`createUnit expects first parameter to be of type 'Object'`);
    if (n3 && n3.override) {
      for (var r3 in t5) if (hasOwnProperty$1(t5, r3) && E.deleteUnit(r3), t5[r3].aliases) for (var i3 = 0; i3 < t5[r3].aliases.length; i3++) E.deleteUnit(t5[r3].aliases[i3]);
    }
    var a3;
    for (var o3 in t5) hasOwnProperty$1(t5, o3) && (a3 = E.createUnitSingle(o3, t5[o3]));
    return a3;
  }, E.createUnitSingle = function(t5, n3) {
    if (n3 ??= {}, typeof t5 != `string`) throw TypeError(`createUnitSingle expects first parameter to be of type 'string'`);
    if (hasOwnProperty$1(X, t5)) throw Error(`Cannot create unit "` + t5 + `": a unit with that name already exists`);
    fe(t5);
    var r3 = null, i3 = [], a3 = 0, o3, s3, c3;
    if (n3 && n3.type === `Unit`) r3 = n3.clone();
    else if (typeof n3 == `string`) n3 !== `` && (o3 = n3);
    else if (typeof n3 == `object`) o3 = n3.definition, s3 = n3.prefixes, a3 = n3.offset, c3 = n3.baseName, n3.aliases && (i3 = n3.aliases.valueOf());
    else throw TypeError(`Cannot create unit "` + t5 + `" from "` + n3.toString() + `": expecting "string" or "Unit" or "Object"`);
    if (i3) {
      for (var l3 = 0; l3 < i3.length; l3++) if (hasOwnProperty$1(X, i3[l3])) throw Error(`Cannot create alias "` + i3[l3] + `": a unit with that name already exists`);
    }
    if (o3 && typeof o3 == `string` && !r3) try {
      r3 = E.parse(o3, { allowNoUnits: true });
    } catch (n4) {
      throw n4.message = `Could not create unit "` + t5 + `" from "` + o3 + `": ` + n4.message, n4;
    }
    else o3 && o3.type === `Unit` && (r3 = o3.clone());
    i3 ||= [], a3 ||= 0, s3 = s3 && s3.toUpperCase && J[s3.toUpperCase()] || J.NONE;
    var u3 = {};
    if (r3) {
      u3 = { name: t5, value: r3.value, dimensions: r3.dimensions.slice(0), prefixes: s3, offset: a3 };
      var d3 = false;
      for (var p3 in Y) if (hasOwnProperty$1(Y, p3)) {
        for (var m3 = true, h3 = 0; h3 < ee.length; h3++) if (Math.abs((u3.dimensions[h3] || 0) - (Y[p3].dimensions[h3] || 0)) > 1e-12) {
          m3 = false;
          break;
        }
        if (m3) {
          d3 = true, u3.base = Y[p3];
          break;
        }
      }
      if (!d3) {
        c3 ||= t5 + `_STUFF`;
        var g3 = { dimensions: r3.dimensions.slice(0) };
        g3.key = c3, Y[c3] = g3, oe[c3] = { unit: u3, prefix: J.NONE[``] }, u3.base = Y[c3];
      }
    } else {
      if (c3 ||= t5 + `_STUFF`, ee.indexOf(c3) >= 0) throw Error(`Cannot create new base unit "` + t5 + `": a base unit with that name already exists (and cannot be overridden)`);
      for (var v3 in ee.push(c3), Y) hasOwnProperty$1(Y, v3) && (Y[v3].dimensions[ee.length - 1] = 0);
      for (var y3 = { dimensions: [] }, b3 = 0; b3 < ee.length; b3++) y3.dimensions[b3] = 0;
      y3.dimensions[ee.length - 1] = 1, y3.key = c3, Y[c3] = y3, u3 = { name: t5, value: 1, dimensions: Y[c3].dimensions.slice(0), prefixes: s3, offset: a3, base: Y[c3] }, oe[c3] = { unit: u3, prefix: J.NONE[``] };
    }
    E.UNITS[t5] = u3;
    for (var x3 = 0; x3 < i3.length; x3++) {
      var S3 = i3[x3], w3 = {};
      for (var D2 in u3) hasOwnProperty$1(u3, D2) && (w3[D2] = u3[D2]);
      w3.name = S3, E.UNITS[S3] = w3;
    }
    return delete H2.cache, new E(null, t5);
  }, E.deleteUnit = function(t5) {
    delete E.UNITS[t5], delete H2.cache;
  }, E.PREFIXES = J, E.BASE_DIMENSIONS = ee, E.BASE_UNITS = Y, E.UNIT_SYSTEMS = Z, E.UNITS = X, E;
}, { isClass: true }), name$156 = `unit`, dependencies$156 = [`typed`, `Unit`], createUnitFunction = factory(name$156, dependencies$156, (t4) => {
  var { typed: n2, Unit: r2 } = t4;
  return n2(name$156, { Unit: function(t5) {
    return t5.clone();
  }, string: function(t5) {
    return r2.isValuelessUnit(t5) ? new r2(null, t5) : r2.parse(t5, { allowNoUnits: true });
  }, "number | BigNumber | Fraction | Complex, string | Unit": function(t5, n3) {
    return new r2(t5, n3);
  }, "number | BigNumber | Fraction": function(t5) {
    return new r2(t5);
  }, "Array | Matrix": n2.referToSelf((t5) => (n3) => deepMap(n3, t5)) });
}), name$155 = `sparse`, dependencies$155 = [`typed`, `SparseMatrix`], createSparse = factory(name$155, dependencies$155, (t4) => {
  var { typed: n2, SparseMatrix: r2 } = t4;
  return n2(name$155, { "": function() {
    return new r2([]);
  }, string: function(t5) {
    return new r2([], t5);
  }, "Array | Matrix": function(t5) {
    return new r2(t5);
  }, "Array | Matrix, string": function(t5, n3) {
    return new r2(t5, n3);
  } });
}), name$154 = `createUnit`, dependencies$154 = [`typed`, `Unit`], createCreateUnit = factory(name$154, dependencies$154, (t4) => {
  var { typed: n2, Unit: r2 } = t4;
  return n2(name$154, { "Object, Object": function(t5, n3) {
    return r2.createUnit(t5, n3);
  }, Object: function(t5) {
    return r2.createUnit(t5, {});
  }, "string, Unit | string | Object, Object": function(t5, n3, i2) {
    var a2 = {};
    return a2[t5] = n3, r2.createUnit(a2, i2);
  }, "string, Unit | string | Object": function(t5, n3) {
    var i2 = {};
    return i2[t5] = n3, r2.createUnit(i2, {});
  }, string: function(t5) {
    var n3 = {};
    return n3[t5] = {}, r2.createUnit(n3, {});
  } });
}), name$153 = `acos`, dependencies$153 = [`typed`, `config`, `Complex`], createAcos = factory(name$153, dependencies$153, (t4) => {
  var { typed: n2, config: r2, Complex: i2 } = t4;
  return n2(name$153, { number: function(t5) {
    return t5 >= -1 && t5 <= 1 || r2.predictable ? Math.acos(t5) : new i2(t5, 0).acos();
  }, Complex: function(t5) {
    return t5.acos();
  }, BigNumber: function(t5) {
    return t5.acos();
  } });
}), name$152 = `acosh`, dependencies$152 = [`typed`, `config`, `Complex`], createAcosh = factory(name$152, dependencies$152, (t4) => {
  var { typed: n2, config: r2, Complex: i2 } = t4;
  return n2(name$152, { number: function(t5) {
    return t5 >= 1 || r2.predictable ? acoshNumber(t5) : t5 <= -1 ? new i2(Math.log(Math.sqrt(t5 * t5 - 1) - t5), Math.PI) : new i2(t5, 0).acosh();
  }, Complex: function(t5) {
    return t5.acosh();
  }, BigNumber: function(t5) {
    return t5.acosh();
  } });
}), name$151 = `acot`, dependencies$151 = [`typed`, `BigNumber`], createAcot = factory(name$151, dependencies$151, (t4) => {
  var { typed: n2, BigNumber: r2 } = t4;
  return n2(name$151, { number: acotNumber, Complex: function(t5) {
    return t5.acot();
  }, BigNumber: function(t5) {
    return new r2(1).div(t5).atan();
  } });
}), name$150 = `acoth`, dependencies$150 = [`typed`, `config`, `Complex`, `BigNumber`], createAcoth = factory(name$150, dependencies$150, (t4) => {
  var { typed: n2, config: r2, Complex: i2, BigNumber: a2 } = t4;
  return n2(name$150, { number: function(t5) {
    return t5 >= 1 || t5 <= -1 || r2.predictable ? acothNumber(t5) : new i2(t5, 0).acoth();
  }, Complex: function(t5) {
    return t5.acoth();
  }, BigNumber: function(t5) {
    return new a2(1).div(t5).atanh();
  } });
}), name$149 = `acsc`, dependencies$149 = [`typed`, `config`, `Complex`, `BigNumber`], createAcsc = factory(name$149, dependencies$149, (t4) => {
  var { typed: n2, config: r2, Complex: i2, BigNumber: a2 } = t4;
  return n2(name$149, { number: function(t5) {
    return t5 <= -1 || t5 >= 1 || r2.predictable ? acscNumber(t5) : new i2(t5, 0).acsc();
  }, Complex: function(t5) {
    return t5.acsc();
  }, BigNumber: function(t5) {
    return new a2(1).div(t5).asin();
  } });
}), name$148 = `acsch`, dependencies$148 = [`typed`, `BigNumber`], createAcsch = factory(name$148, dependencies$148, (t4) => {
  var { typed: n2, BigNumber: r2 } = t4;
  return n2(name$148, { number: acschNumber, Complex: function(t5) {
    return t5.acsch();
  }, BigNumber: function(t5) {
    return new r2(1).div(t5).asinh();
  } });
}), name$147 = `asec`, dependencies$147 = [`typed`, `config`, `Complex`, `BigNumber`], createAsec = factory(name$147, dependencies$147, (t4) => {
  var { typed: n2, config: r2, Complex: i2, BigNumber: a2 } = t4;
  return n2(name$147, { number: function(t5) {
    return t5 <= -1 || t5 >= 1 || r2.predictable ? asecNumber(t5) : new i2(t5, 0).asec();
  }, Complex: function(t5) {
    return t5.asec();
  }, BigNumber: function(t5) {
    return new a2(1).div(t5).acos();
  } });
}), name$146 = `asech`, dependencies$146 = [`typed`, `config`, `Complex`, `BigNumber`], createAsech = factory(name$146, dependencies$146, (t4) => {
  var { typed: n2, config: r2, Complex: i2, BigNumber: a2 } = t4;
  return n2(name$146, { number: function(t5) {
    if (t5 <= 1 && t5 >= -1 || r2.predictable) {
      var n3 = 1 / t5;
      if (n3 > 0 || r2.predictable) return asechNumber(t5);
      var a3 = Math.sqrt(n3 * n3 - 1);
      return new i2(Math.log(a3 - n3), Math.PI);
    }
    return new i2(t5, 0).asech();
  }, Complex: function(t5) {
    return t5.asech();
  }, BigNumber: function(t5) {
    return new a2(1).div(t5).acosh();
  } });
}), name$145 = `asin`, dependencies$145 = [`typed`, `config`, `Complex`], createAsin = factory(name$145, dependencies$145, (t4) => {
  var { typed: n2, config: r2, Complex: i2 } = t4;
  return n2(name$145, { number: function(t5) {
    return t5 >= -1 && t5 <= 1 || r2.predictable ? Math.asin(t5) : new i2(t5, 0).asin();
  }, Complex: function(t5) {
    return t5.asin();
  }, BigNumber: function(t5) {
    return t5.asin();
  } });
}), name$144 = `asinh`, dependencies$144 = [`typed`], createAsinh = factory(name$144, dependencies$144, (t4) => {
  var { typed: n2 } = t4;
  return n2(`asinh`, { number: asinhNumber, Complex: function(t5) {
    return t5.asinh();
  }, BigNumber: function(t5) {
    return t5.asinh();
  } });
}), name$143 = `atan`, dependencies$143 = [`typed`], createAtan = factory(name$143, dependencies$143, (t4) => {
  var { typed: n2 } = t4;
  return n2(`atan`, { number: function(t5) {
    return Math.atan(t5);
  }, Complex: function(t5) {
    return t5.atan();
  }, BigNumber: function(t5) {
    return t5.atan();
  } });
}), name$142 = `atan2`, dependencies$142 = [`typed`, `matrix`, `equalScalar`, `BigNumber`, `DenseMatrix`, `concat`], createAtan2 = factory(name$142, dependencies$142, (t4) => {
  var { typed: n2, matrix: r2, equalScalar: i2, BigNumber: a2, DenseMatrix: o2, concat: s2 } = t4, c2 = createMatAlgo02xDS0({ typed: n2, equalScalar: i2 }), l2 = createMatAlgo03xDSf({ typed: n2 }), u2 = createMatAlgo09xS0Sf({ typed: n2, equalScalar: i2 }), d2 = createMatAlgo11xS0s({ typed: n2, equalScalar: i2 }), p2 = createMatAlgo12xSfs({ typed: n2, DenseMatrix: o2 }), m2 = createMatrixAlgorithmSuite({ typed: n2, matrix: r2, concat: s2 });
  return n2(name$142, { "number, number": Math.atan2, "BigNumber, BigNumber": (t5, n3) => a2.atan2(t5, n3) }, m2({ scalar: `number | BigNumber`, SS: u2, DS: l2, SD: c2, Ss: d2, sS: p2 }));
}), name$141 = `atanh`, dependencies$141 = [`typed`, `config`, `Complex`], createAtanh = factory(name$141, dependencies$141, (t4) => {
  var { typed: n2, config: r2, Complex: i2 } = t4;
  return n2(name$141, { number: function(t5) {
    return t5 <= 1 && t5 >= -1 || r2.predictable ? atanhNumber(t5) : new i2(t5, 0).atanh();
  }, Complex: function(t5) {
    return t5.atanh();
  }, BigNumber: function(t5) {
    return t5.atanh();
  } });
}), createTrigUnit = factory(`trigUnit`, [`typed`], (t4) => {
  var { typed: n2 } = t4;
  return { Unit: n2.referToSelf((t5) => (r2) => {
    if (!r2.hasBase(r2.constructor.BASE_UNITS.ANGLE)) throw TypeError(`Unit in function cot is no angle`);
    return n2.find(t5, r2.valueType())(r2.value);
  }) };
}), name$140 = `cos`, dependencies$140 = [`typed`], createCos = factory(name$140, dependencies$140, (t4) => {
  var { typed: n2 } = t4, r2 = createTrigUnit({ typed: n2 });
  return n2(name$140, { number: Math.cos, "Complex | BigNumber": (t5) => t5.cos() }, r2);
}), name$139 = `cosh`, dependencies$139 = [`typed`], createCosh = factory(name$139, dependencies$139, (t4) => {
  var { typed: n2 } = t4;
  return n2(name$139, { number: cosh$3, "Complex | BigNumber": (t5) => t5.cosh() });
}), name$138 = `cot`, dependencies$138 = [`typed`, `BigNumber`], createCot = factory(name$138, dependencies$138, (t4) => {
  var { typed: n2, BigNumber: r2 } = t4, i2 = createTrigUnit({ typed: n2 });
  return n2(name$138, { number: cotNumber, Complex: (t5) => t5.cot(), BigNumber: (t5) => new r2(1).div(t5.tan()) }, i2);
}), name$137 = `coth`, dependencies$137 = [`typed`, `BigNumber`], createCoth = factory(name$137, dependencies$137, (t4) => {
  var { typed: n2, BigNumber: r2 } = t4;
  return n2(name$137, { number: cothNumber, Complex: (t5) => t5.coth(), BigNumber: (t5) => new r2(1).div(t5.tanh()) });
}), name$136 = `csc`, dependencies$136 = [`typed`, `BigNumber`], createCsc = factory(name$136, dependencies$136, (t4) => {
  var { typed: n2, BigNumber: r2 } = t4, i2 = createTrigUnit({ typed: n2 });
  return n2(name$136, { number: cscNumber, Complex: (t5) => t5.csc(), BigNumber: (t5) => new r2(1).div(t5.sin()) }, i2);
}), name$135 = `csch`, dependencies$135 = [`typed`, `BigNumber`], createCsch = factory(name$135, dependencies$135, (t4) => {
  var { typed: n2, BigNumber: r2 } = t4;
  return n2(name$135, { number: cschNumber, Complex: (t5) => t5.csch(), BigNumber: (t5) => new r2(1).div(t5.sinh()) });
}), name$134 = `sec`, dependencies$134 = [`typed`, `BigNumber`], createSec = factory(name$134, dependencies$134, (t4) => {
  var { typed: n2, BigNumber: r2 } = t4, i2 = createTrigUnit({ typed: n2 });
  return n2(name$134, { number: secNumber, Complex: (t5) => t5.sec(), BigNumber: (t5) => new r2(1).div(t5.cos()) }, i2);
}), name$133 = `sech`, dependencies$133 = [`typed`, `BigNumber`], createSech = factory(name$133, dependencies$133, (t4) => {
  var { typed: n2, BigNumber: r2 } = t4;
  return n2(name$133, { number: sechNumber, Complex: (t5) => t5.sech(), BigNumber: (t5) => new r2(1).div(t5.cosh()) });
}), name$132 = `sin`, dependencies$132 = [`typed`], createSin = factory(name$132, dependencies$132, (t4) => {
  var { typed: n2 } = t4, r2 = createTrigUnit({ typed: n2 });
  return n2(name$132, { number: Math.sin, "Complex | BigNumber": (t5) => t5.sin() }, r2);
}), name$131 = `sinh`, dependencies$131 = [`typed`], createSinh = factory(name$131, dependencies$131, (t4) => {
  var { typed: n2 } = t4;
  return n2(name$131, { number: sinhNumber, "Complex | BigNumber": (t5) => t5.sinh() });
}), name$130 = `tan`, dependencies$130 = [`typed`], createTan = factory(name$130, dependencies$130, (t4) => {
  var { typed: n2 } = t4, r2 = createTrigUnit({ typed: n2 });
  return n2(name$130, { number: Math.tan, "Complex | BigNumber": (t5) => t5.tan() }, r2);
}), name$129 = `tanh`, dependencies$129 = [`typed`], createTanh = factory(name$129, dependencies$129, (t4) => {
  var { typed: n2 } = t4;
  return n2(`tanh`, { number: tanh$2, "Complex | BigNumber": (t5) => t5.tanh() });
}), name$128 = `setCartesian`, dependencies$128 = [`typed`, `size`, `subset`, `compareNatural`, `Index`, `DenseMatrix`], createSetCartesian = factory(name$128, dependencies$128, (t4) => {
  var { typed: n2, size: r2, subset: i2, compareNatural: a2, Index: o2, DenseMatrix: s2 } = t4;
  return n2(name$128, { "Array | Matrix, Array | Matrix": function(t5, n3) {
    var c2 = [];
    if (i2(r2(t5), new o2(0)) !== 0 && i2(r2(n3), new o2(0)) !== 0) {
      var l2 = flatten$4(Array.isArray(t5) ? t5 : t5.toArray()).sort(a2), u2 = flatten$4(Array.isArray(n3) ? n3 : n3.toArray()).sort(a2);
      c2 = [];
      for (var d2 = 0; d2 < l2.length; d2++) for (var p2 = 0; p2 < u2.length; p2++) c2.push([l2[d2], u2[p2]]);
    }
    return Array.isArray(t5) && Array.isArray(n3) ? c2 : new s2(c2);
  } });
}), name$127 = `setDifference`, dependencies$127 = [`typed`, `size`, `subset`, `compareNatural`, `Index`, `DenseMatrix`], createSetDifference = factory(name$127, dependencies$127, (t4) => {
  var { typed: n2, size: r2, subset: i2, compareNatural: a2, Index: o2, DenseMatrix: s2 } = t4;
  return n2(name$127, { "Array | Matrix, Array | Matrix": function(t5, n3) {
    var c2;
    if (i2(r2(t5), new o2(0)) === 0) c2 = [];
    else if (i2(r2(n3), new o2(0)) === 0) return flatten$4(t5.toArray());
    else {
      var l2 = identify(flatten$4(Array.isArray(t5) ? t5 : t5.toArray()).sort(a2)), u2 = identify(flatten$4(Array.isArray(n3) ? n3 : n3.toArray()).sort(a2));
      c2 = [];
      for (var d2, p2 = 0; p2 < l2.length; p2++) {
        d2 = false;
        for (var m2 = 0; m2 < u2.length; m2++) if (a2(l2[p2].value, u2[m2].value) === 0 && l2[p2].identifier === u2[m2].identifier) {
          d2 = true;
          break;
        }
        d2 || c2.push(l2[p2]);
      }
    }
    return Array.isArray(t5) && Array.isArray(n3) ? generalize(c2) : new s2(generalize(c2));
  } });
}), name$126 = `setDistinct`, dependencies$126 = [`typed`, `size`, `subset`, `compareNatural`, `Index`, `DenseMatrix`], createSetDistinct = factory(name$126, dependencies$126, (t4) => {
  var { typed: n2, size: r2, subset: i2, compareNatural: a2, Index: o2, DenseMatrix: s2 } = t4;
  return n2(name$126, { "Array | Matrix": function(t5) {
    var n3;
    if (i2(r2(t5), new o2(0)) === 0) n3 = [];
    else {
      var c2 = flatten$4(Array.isArray(t5) ? t5 : t5.toArray()).sort(a2);
      n3 = [], n3.push(c2[0]);
      for (var l2 = 1; l2 < c2.length; l2++) a2(c2[l2], c2[l2 - 1]) !== 0 && n3.push(c2[l2]);
    }
    return Array.isArray(t5) ? n3 : new s2(n3);
  } });
}), name$125 = `setIntersect`, dependencies$125 = [`typed`, `size`, `subset`, `compareNatural`, `Index`, `DenseMatrix`], createSetIntersect = factory(name$125, dependencies$125, (t4) => {
  var { typed: n2, size: r2, subset: i2, compareNatural: a2, Index: o2, DenseMatrix: s2 } = t4;
  return n2(name$125, { "Array | Matrix, Array | Matrix": function(t5, n3) {
    var c2;
    if (i2(r2(t5), new o2(0)) === 0 || i2(r2(n3), new o2(0)) === 0) c2 = [];
    else {
      var l2 = identify(flatten$4(Array.isArray(t5) ? t5 : t5.toArray()).sort(a2)), u2 = identify(flatten$4(Array.isArray(n3) ? n3 : n3.toArray()).sort(a2));
      c2 = [];
      for (var d2 = 0; d2 < l2.length; d2++) for (var p2 = 0; p2 < u2.length; p2++) if (a2(l2[d2].value, u2[p2].value) === 0 && l2[d2].identifier === u2[p2].identifier) {
        c2.push(l2[d2]);
        break;
      }
    }
    return Array.isArray(t5) && Array.isArray(n3) ? generalize(c2) : new s2(generalize(c2));
  } });
}), name$124 = `setIsSubset`, dependencies$124 = [`typed`, `size`, `subset`, `compareNatural`, `Index`], createSetIsSubset = factory(name$124, dependencies$124, (t4) => {
  var { typed: n2, size: r2, subset: i2, compareNatural: a2, Index: o2 } = t4;
  return n2(name$124, { "Array | Matrix, Array | Matrix": function(t5, n3) {
    if (i2(r2(t5), new o2(0)) === 0) return true;
    if (i2(r2(n3), new o2(0)) === 0) return false;
    for (var s2 = identify(flatten$4(Array.isArray(t5) ? t5 : t5.toArray()).sort(a2)), c2 = identify(flatten$4(Array.isArray(n3) ? n3 : n3.toArray()).sort(a2)), l2, u2 = 0; u2 < s2.length; u2++) {
      l2 = false;
      for (var d2 = 0; d2 < c2.length; d2++) if (a2(s2[u2].value, c2[d2].value) === 0 && s2[u2].identifier === c2[d2].identifier) {
        l2 = true;
        break;
      }
      if (l2 === false) return false;
    }
    return true;
  } });
}), name$123 = `setMultiplicity`, dependencies$123 = [`typed`, `size`, `subset`, `compareNatural`, `Index`], createSetMultiplicity = factory(name$123, dependencies$123, (t4) => {
  var { typed: n2, size: r2, subset: i2, compareNatural: a2, Index: o2 } = t4;
  return n2(name$123, { "number | BigNumber | Fraction | Complex, Array | Matrix": function(t5, n3) {
    if (i2(r2(n3), new o2(0)) === 0) return 0;
    for (var s2 = flatten$4(Array.isArray(n3) ? n3 : n3.toArray()), c2 = 0, l2 = 0; l2 < s2.length; l2++) a2(s2[l2], t5) === 0 && c2++;
    return c2;
  } });
}), name$122 = `setPowerset`, dependencies$122 = [`typed`, `size`, `subset`, `compareNatural`, `Index`], createSetPowerset = factory(name$122, dependencies$122, (t4) => {
  var { typed: n2, size: r2, subset: i2, compareNatural: a2, Index: o2 } = t4;
  return n2(name$122, { "Array | Matrix": function(t5) {
    if (i2(r2(t5), new o2(0)) === 0) return [];
    for (var n3 = flatten$4(Array.isArray(t5) ? t5 : t5.toArray()).sort(a2), l2 = [], u2 = 0; u2.toString(2).length <= n3.length; ) l2.push(s2(n3, u2.toString(2).split(``).reverse())), u2++;
    return c2(l2);
  } });
  function s2(t5, n3) {
    for (var r3 = [], i3 = 0; i3 < n3.length; i3++) n3[i3] === `1` && r3.push(t5[i3]);
    return r3;
  }
  function c2(t5) {
    for (var n3 = [], r3 = t5.length - 1; r3 > 0; r3--) for (var i3 = 0; i3 < r3; i3++) t5[i3].length > t5[i3 + 1].length && (n3 = t5[i3], t5[i3] = t5[i3 + 1], t5[i3 + 1] = n3);
    return t5;
  }
}), name$121 = `setSize`, dependencies$121 = [`typed`, `compareNatural`], createSetSize = factory(name$121, dependencies$121, (t4) => {
  var { typed: n2, compareNatural: r2 } = t4;
  return n2(name$121, { "Array | Matrix": function(t5) {
    return Array.isArray(t5) ? flatten$4(t5).length : flatten$4(t5.toArray()).length;
  }, "Array | Matrix, boolean": function(t5, n3) {
    if (n3 === false || t5.length === 0) return Array.isArray(t5) ? flatten$4(t5).length : flatten$4(t5.toArray()).length;
    for (var i2 = flatten$4(Array.isArray(t5) ? t5 : t5.toArray()).sort(r2), a2 = 1, o2 = 1; o2 < i2.length; o2++) r2(i2[o2], i2[o2 - 1]) !== 0 && a2++;
    return a2;
  } });
}), name$120 = `setSymDifference`, dependencies$120 = [`typed`, `size`, `concat`, `subset`, `setDifference`, `Index`], createSetSymDifference = factory(name$120, dependencies$120, (t4) => {
  var { typed: n2, size: r2, concat: i2, subset: a2, setDifference: o2, Index: s2 } = t4;
  return n2(name$120, { "Array | Matrix, Array | Matrix": function(t5, n3) {
    if (a2(r2(t5), new s2(0)) === 0) return flatten$4(n3);
    if (a2(r2(n3), new s2(0)) === 0) return flatten$4(t5);
    var c2 = flatten$4(t5), l2 = flatten$4(n3);
    return i2(o2(c2, l2), o2(l2, c2));
  } });
}), name$119 = `setUnion`, dependencies$119 = [`typed`, `size`, `concat`, `subset`, `setIntersect`, `setSymDifference`, `Index`], createSetUnion = factory(name$119, dependencies$119, (t4) => {
  var { typed: n2, size: r2, concat: i2, subset: a2, setIntersect: o2, setSymDifference: s2, Index: c2 } = t4;
  return n2(name$119, { "Array | Matrix, Array | Matrix": function(t5, n3) {
    if (a2(r2(t5), new c2(0)) === 0) return flatten$4(n3);
    if (a2(r2(n3), new c2(0)) === 0) return flatten$4(t5);
    var l2 = flatten$4(t5), u2 = flatten$4(n3);
    return i2(s2(l2, u2), o2(l2, u2));
  } });
}), name$118 = `add`, dependencies$118 = [`typed`, `matrix`, `addScalar`, `equalScalar`, `DenseMatrix`, `SparseMatrix`, `concat`], createAdd = factory(name$118, dependencies$118, (t4) => {
  var { typed: n2, matrix: r2, addScalar: i2, equalScalar: a2, DenseMatrix: o2, SparseMatrix: s2, concat: c2 } = t4, l2 = createMatAlgo01xDSid({ typed: n2 }), u2 = createMatAlgo04xSidSid({ typed: n2, equalScalar: a2 }), d2 = createMatAlgo10xSids({ typed: n2, DenseMatrix: o2 }), p2 = createMatrixAlgorithmSuite({ typed: n2, matrix: r2, concat: c2 });
  return n2(name$118, { "any, any": i2, "any, any, ...any": n2.referToSelf((t5) => (n3, r3, i3) => {
    for (var a3 = t5(n3, r3), o3 = 0; o3 < i3.length; o3++) a3 = t5(a3, i3[o3]);
    return a3;
  }) }, p2({ elop: i2, DS: l2, SS: u2, Ss: d2 }));
}), name$117 = `hypot`, dependencies$117 = [`typed`, `abs`, `addScalar`, `divideScalar`, `multiplyScalar`, `sqrt`, `smaller`, `isPositive`], createHypot = factory(name$117, dependencies$117, (t4) => {
  var { typed: n2, abs: r2, addScalar: i2, divideScalar: a2, multiplyScalar: o2, sqrt: s2, smaller: c2, isPositive: l2 } = t4;
  return n2(name$117, { "... number | BigNumber": u2, Array: u2, Matrix: (t5) => u2(flatten$4(t5.toArray(), true)) });
  function u2(t5) {
    for (var n3 = 0, u3 = 0, d2 = 0; d2 < t5.length; d2++) {
      if (isComplex(t5[d2])) throw TypeError(`Unexpected type of argument to hypot`);
      var p2 = r2(t5[d2]);
      c2(u3, p2) ? (n3 = o2(n3, o2(a2(u3, p2), a2(u3, p2))), n3 = i2(n3, 1), u3 = p2) : n3 = i2(n3, l2(p2) ? o2(a2(p2, u3), a2(p2, u3)) : p2);
    }
    return o2(u3, s2(n3));
  }
}), name$116 = `norm`, dependencies$116 = [`typed`, `abs`, `add`, `pow`, `conj`, `sqrt`, `multiply`, `equalScalar`, `larger`, `smaller`, `matrix`, `ctranspose`, `eigs`], createNorm = factory(name$116, dependencies$116, (t4) => {
  var { typed: n2, abs: r2, add: i2, pow: a2, conj: o2, sqrt: s2, multiply: c2, equalScalar: l2, larger: u2, smaller: d2, matrix: p2, ctranspose: m2, eigs: h2 } = t4;
  return n2(name$116, { number: Math.abs, Complex: function(t5) {
    return t5.abs();
  }, BigNumber: function(t5) {
    return t5.abs();
  }, boolean: function(t5) {
    return Math.abs(t5);
  }, Array: function(t5) {
    return D(p2(t5), 2);
  }, Matrix: function(t5) {
    return D(t5, 2);
  }, "Array, number | BigNumber | string": function(t5, n3) {
    return D(p2(t5), n3);
  }, "Matrix, number | BigNumber | string": function(t5, n3) {
    return D(t5, n3);
  } });
  function g2(t5) {
    var n3 = 0;
    return t5.forEach(function(t6) {
      var i3 = r2(t6);
      u2(i3, n3) && (n3 = i3);
    }, true), n3;
  }
  function v2(t5) {
    var n3;
    return t5.forEach(function(t6) {
      var i3 = r2(t6);
      (!n3 || d2(i3, n3)) && (n3 = i3);
    }, true), n3 || 0;
  }
  function y2(t5, n3) {
    if (n3 === 1 / 0 || n3 === `inf`) return g2(t5);
    if (n3 === -1 / 0 || n3 === `-inf`) return v2(t5);
    if (n3 === `fro`) return D(t5, 2);
    if (typeof n3 == `number` && !isNaN(n3)) {
      if (!l2(n3, 0)) {
        var o3 = 0;
        return t5.forEach(function(t6) {
          o3 = i2(a2(r2(t6), n3), o3);
        }, true), a2(o3, 1 / n3);
      }
      return 1 / 0;
    }
    throw Error(`Unsupported parameter value`);
  }
  function b2(t5) {
    var n3 = 0;
    return t5.forEach(function(t6, r3) {
      n3 = i2(n3, c2(t6, o2(t6)));
    }), r2(s2(n3));
  }
  function x2(t5) {
    var n3 = [], a3 = 0;
    return t5.forEach(function(t6, o3) {
      var s3 = o3[1], c3 = i2(n3[s3] || 0, r2(t6));
      u2(c3, a3) && (a3 = c3), n3[s3] = c3;
    }, true), a3;
  }
  function S2(t5) {
    var n3 = t5.size();
    if (n3[0] !== n3[1]) throw RangeError(`Invalid matrix dimensions`);
    var i3 = h2(c2(m2(t5), t5)).values.toArray(), a3 = i3[i3.length - 1];
    return r2(s2(a3));
  }
  function w2(t5) {
    var n3 = [], a3 = 0;
    return t5.forEach(function(t6, o3) {
      var s3 = o3[0], c3 = i2(n3[s3] || 0, r2(t6));
      u2(c3, a3) && (a3 = c3), n3[s3] = c3;
    }, true), a3;
  }
  function E(t5, n3) {
    if (n3 === 1) return x2(t5);
    if (n3 === 1 / 0 || n3 === `inf`) return w2(t5);
    if (n3 === `fro`) return b2(t5);
    if (n3 === 2) return S2(t5);
    throw Error(`Unsupported parameter value ` + n3);
  }
  function D(t5, n3) {
    var r3 = t5.size();
    if (r3.length === 1) return y2(t5, n3);
    if (r3.length === 2) {
      if (r3[0] && r3[1]) return E(t5, n3);
      throw RangeError(`Invalid matrix dimensions`);
    }
  }
}), name$115 = `dot`, dependencies$115 = [`typed`, `addScalar`, `multiplyScalar`, `conj`, `size`], createDot = factory(name$115, dependencies$115, (t4) => {
  var { typed: n2, addScalar: r2, multiplyScalar: i2, conj: a2, size: o2 } = t4;
  return n2(name$115, { "Array | DenseMatrix, Array | DenseMatrix": c2, "SparseMatrix, SparseMatrix": l2 });
  function s2(t5, n3) {
    var r3 = o2(t5), i3 = o2(n3), a3, s3;
    if (r3.length === 1) a3 = r3[0];
    else if (r3.length === 2 && r3[1] === 1) a3 = r3[0];
    else throw RangeError(`Expected a column vector, instead got a matrix of size (` + r3.join(`, `) + `)`);
    if (i3.length === 1) s3 = i3[0];
    else if (i3.length === 2 && i3[1] === 1) s3 = i3[0];
    else throw RangeError(`Expected a column vector, instead got a matrix of size (` + i3.join(`, `) + `)`);
    if (a3 !== s3) throw RangeError(`Vectors must have equal length (` + a3 + ` != ` + s3 + `)`);
    if (a3 === 0) throw RangeError(`Cannot calculate the dot product of empty vectors`);
    return a3;
  }
  function c2(t5, c3) {
    var l3 = s2(t5, c3), u2 = isMatrix(t5) ? t5._data : t5, d2 = isMatrix(t5) ? t5._datatype || t5.getDataType() : void 0, p2 = isMatrix(c3) ? c3._data : c3, m2 = isMatrix(c3) ? c3._datatype || c3.getDataType() : void 0, h2 = o2(t5).length === 2, g2 = o2(c3).length === 2, v2 = r2, y2 = i2;
    if (d2 && m2 && d2 === m2 && typeof d2 == `string` && d2 !== `mixed`) {
      var b2 = d2;
      v2 = n2.find(r2, [b2, b2]), y2 = n2.find(i2, [b2, b2]);
    }
    if (!h2 && !g2) {
      for (var x2 = y2(a2(u2[0]), p2[0]), S2 = 1; S2 < l3; S2++) x2 = v2(x2, y2(a2(u2[S2]), p2[S2]));
      return x2;
    }
    if (!h2 && g2) {
      for (var w2 = y2(a2(u2[0]), p2[0][0]), E = 1; E < l3; E++) w2 = v2(w2, y2(a2(u2[E]), p2[E][0]));
      return w2;
    }
    if (h2 && !g2) {
      for (var D = y2(a2(u2[0][0]), p2[0]), O = 1; O < l3; O++) D = v2(D, y2(a2(u2[O][0]), p2[O]));
      return D;
    }
    if (h2 && g2) {
      for (var j2 = y2(a2(u2[0][0]), p2[0][0]), M2 = 1; M2 < l3; M2++) j2 = v2(j2, y2(a2(u2[M2][0]), p2[M2][0]));
      return j2;
    }
  }
  function l2(t5, n3) {
    s2(t5, n3);
    for (var a3 = t5._index, o3 = t5._values, c3 = n3._index, l3 = n3._values, u2 = 0, d2 = r2, p2 = i2, m2 = 0, h2 = 0; m2 < a3.length && h2 < c3.length; ) {
      var g2 = a3[m2], v2 = c3[h2];
      if (g2 < v2) {
        m2++;
        continue;
      }
      if (g2 > v2) {
        h2++;
        continue;
      }
      g2 === v2 && (u2 = d2(u2, p2(o3[m2], l3[h2])), m2++, h2++);
    }
    return u2;
  }
}), name$114 = `trace`, dependencies$114 = [`typed`, `matrix`, `add`], createTrace = factory(name$114, dependencies$114, (t4) => {
  var { typed: n2, matrix: r2, add: i2 } = t4;
  return n2(`trace`, { Array: function(t5) {
    return a2(r2(t5));
  }, SparseMatrix: o2, DenseMatrix: a2, any: clone$3 });
  function a2(t5) {
    var n3 = t5._size, r3 = t5._data;
    switch (n3.length) {
      case 1:
        if (n3[0] === 1) return clone$3(r3[0]);
        throw RangeError(`Matrix must be square (size: ` + format(n3) + `)`);
      case 2:
        var a3 = n3[0];
        if (a3 === n3[1]) {
          for (var o3 = 0, s2 = 0; s2 < a3; s2++) o3 = i2(o3, r3[s2][s2]);
          return o3;
        } else throw RangeError(`Matrix must be square (size: ` + format(n3) + `)`);
      default:
        throw RangeError(`Matrix must be two dimensional (size: ` + format(n3) + `)`);
    }
  }
  function o2(t5) {
    var n3 = t5._values, r3 = t5._index, a3 = t5._ptr, o3 = t5._size, s2 = o3[0], c2 = o3[1];
    if (s2 === c2) {
      var l2 = 0;
      if (n3.length > 0) for (var u2 = 0; u2 < c2; u2++) for (var d2 = a3[u2], p2 = a3[u2 + 1], m2 = d2; m2 < p2; m2++) {
        var h2 = r3[m2];
        if (h2 === u2) {
          l2 = i2(l2, n3[m2]);
          break;
        }
        if (h2 > u2) break;
      }
      return l2;
    }
    throw RangeError(`Matrix must be square (size: ` + format(o3) + `)`);
  }
}), name$113 = `index`, dependencies$113 = [`typed`, `Index`], createIndex = factory(name$113, dependencies$113, (t4) => {
  var { typed: n2, Index: r2 } = t4;
  return n2(name$113, { "...number | string | BigNumber | Range | Array | Matrix": function(t5) {
    var n3 = t5.map(function(t6) {
      return isBigNumber(t6) ? t6.toNumber() : isArray(t6) || isMatrix(t6) ? t6.map(function(t7) {
        return isBigNumber(t7) ? t7.toNumber() : t7;
      }) : t6;
    }), i2 = new r2();
    return r2.apply(i2, n3), i2;
  } });
}), keywords = /* @__PURE__ */ new Set([`end`]), name$112 = `Node`, dependencies$112 = [`mathWithTransform`], createNode = factory(name$112, dependencies$112, (t4) => {
  var { mathWithTransform: n2 } = t4;
  function r2(t5) {
    for (var n3 of [...keywords]) if (t5.has(n3)) throw Error(`Scope contains an illegal symbol, "` + n3 + `" is a reserved keyword`);
  }
  class i2 {
    get type() {
      return `Node`;
    }
    get isNode() {
      return true;
    }
    evaluate(t5) {
      return this.compile().evaluate(t5);
    }
    compile() {
      var t5 = this._compile(n2, {}), i3 = {}, a2 = null;
      function o2(n3) {
        var o3 = createMap$1(n3);
        return r2(o3), t5(o3, i3, a2);
      }
      return { evaluate: o2 };
    }
    _compile(t5, n3) {
      throw Error(`Method _compile must be implemented by type ` + this.type);
    }
    forEach(t5) {
      throw Error(`Cannot run forEach on a Node interface`);
    }
    map(t5) {
      throw Error(`Cannot run map on a Node interface`);
    }
    _ifNode(t5) {
      if (!isNode(t5)) throw TypeError(`Callback function must return a Node`);
      return t5;
    }
    traverse(t5) {
      t5(this, null, null);
      function n3(t6, r3) {
        t6.forEach(function(t7, i3, a2) {
          r3(t7, i3, a2), n3(t7, r3);
        });
      }
      n3(this, t5);
    }
    transform(t5) {
      function n3(r3, i3, a2) {
        var o2 = t5(r3, i3, a2);
        return o2 === r3 ? r3.map(n3) : o2;
      }
      return n3(this, null, null);
    }
    filter(t5) {
      var n3 = [];
      return this.traverse(function(r3, i3, a2) {
        t5(r3, i3, a2) && n3.push(r3);
      }), n3;
    }
    clone() {
      throw Error(`Cannot clone a Node interface`);
    }
    cloneDeep() {
      return this.map(function(t5) {
        return t5.cloneDeep();
      });
    }
    equals(t5) {
      return t5 ? this.type === t5.type && deepStrictEqual(this, t5) : false;
    }
    toString(t5) {
      var n3 = this._getCustomString(t5);
      return n3 === void 0 ? this._toString(t5) : n3;
    }
    _toString() {
      throw Error(`_toString not implemented for ` + this.type);
    }
    toJSON() {
      throw Error(`Cannot serialize object: toJSON not implemented by ` + this.type);
    }
    toHTML(t5) {
      var n3 = this._getCustomString(t5);
      return n3 === void 0 ? this._toHTML(t5) : n3;
    }
    _toHTML() {
      throw Error(`_toHTML not implemented for ` + this.type);
    }
    toTex(t5) {
      var n3 = this._getCustomString(t5);
      return n3 === void 0 ? this._toTex(t5) : n3;
    }
    _toTex(t5) {
      throw Error(`_toTex not implemented for ` + this.type);
    }
    _getCustomString(t5) {
      if (t5 && typeof t5 == `object`) switch (typeof t5.handler) {
        case `object`:
        case `undefined`:
          return;
        case `function`:
          return t5.handler(this, t5);
        default:
          throw TypeError(`Object or function expected as callback`);
      }
    }
    getIdentifier() {
      return this.type;
    }
    getContent() {
      return this;
    }
  }
  return i2;
}, { isClass: true, isNode: true });
function errorTransform(t4) {
  return t4 && t4.isIndexError ? new IndexError(t4.index + 1, t4.min + 1, t4.max === void 0 ? void 0 : t4.max + 1) : t4;
}
function accessFactory(t4) {
  var { subset: n2 } = t4;
  return function(t5, r2) {
    try {
      if (Array.isArray(t5)) return n2(t5, r2);
      if (t5 && typeof t5.subset == `function`) return t5.subset(r2);
      if (typeof t5 == `string`) return n2(t5, r2);
      if (typeof t5 == `object`) {
        if (!r2.isObjectProperty()) throw TypeError(`Cannot apply a numeric index as object property`);
        return getSafeProperty(t5, r2.getObjectProperty());
      } else throw TypeError(`Cannot apply index: unsupported type of object`);
    } catch (t6) {
      throw errorTransform(t6);
    }
  };
}
var name$111 = `AccessorNode`, dependencies$111 = [`subset`, `Node`], createAccessorNode = factory(name$111, dependencies$111, (t4) => {
  var { subset: n2, Node: r2 } = t4, i2 = accessFactory({ subset: n2 });
  function a2(t5) {
    return !(isAccessorNode(t5) || isArrayNode(t5) || isConstantNode(t5) || isFunctionNode(t5) || isObjectNode(t5) || isParenthesisNode(t5) || isSymbolNode(t5));
  }
  class o2 extends r2 {
    constructor(t5, n3) {
      var r3 = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : false;
      if (super(), !isNode(t5)) throw TypeError(`Node expected for parameter "object"`);
      if (!isIndexNode(n3)) throw TypeError(`IndexNode expected for parameter "index"`);
      this.object = t5, this.index = n3, this.optionalChaining = r3;
    }
    get name() {
      return this.index ? this.index.isObjectProperty() ? this.index.getObjectProperty() : `` : this.object.name || ``;
    }
    get type() {
      return name$111;
    }
    get isAccessorNode() {
      return true;
    }
    _compile(t5, n3) {
      var r3 = this.object._compile(t5, n3), a3 = this.index._compile(t5, n3), o3 = this.optionalChaining, s2 = isAccessorNode(this.object) && this.object.optionalChaining;
      if (this.index.isObjectProperty()) {
        var c2 = this.index.getObjectProperty();
        return function(t6, n4, i3) {
          var a4 = i3 || {}, l2 = r3(t6, n4, a4);
          if (o3 && l2 == null) {
            a4.optionalShortCircuit = true;
            return;
          }
          if (!(s2 && a4 != null && a4.optionalShortCircuit)) return getSafeProperty(l2, c2);
        };
      } else return function(t6, n4, c3) {
        var l2 = c3 || {}, u2 = r3(t6, n4, l2);
        if (o3 && u2 == null) {
          l2.optionalShortCircuit = true;
          return;
        }
        if (!(s2 && l2 != null && l2.optionalShortCircuit)) return i2(u2, a3(t6, n4, u2));
      };
    }
    forEach(t5) {
      t5(this.object, `object`, this), t5(this.index, `index`, this);
    }
    map(t5) {
      return new o2(this._ifNode(t5(this.object, `object`, this)), this._ifNode(t5(this.index, `index`, this)), this.optionalChaining);
    }
    clone() {
      return new o2(this.object, this.index, this.optionalChaining);
    }
    _toString(t5) {
      var n3 = this.object.toString(t5);
      a2(this.object) && (n3 = `(` + n3 + `)`);
      var r3 = this.optionalChaining ? this.index.dotNotation ? `?` : `?.` : ``;
      return n3 + r3 + this.index.toString(t5);
    }
    _toHTML(t5) {
      var n3 = this.object.toHTML(t5);
      return a2(this.object) && (n3 = `<span class="math-parenthesis math-round-parenthesis">(</span>` + n3 + `<span class="math-parenthesis math-round-parenthesis">)</span>`), n3 + this.index.toHTML(t5);
    }
    _toTex(t5) {
      var n3 = this.object.toTex(t5);
      return a2(this.object) && (n3 = `\\left(' + object + '\\right)`), n3 + this.index.toTex(t5);
    }
    toJSON() {
      return { mathjs: name$111, object: this.object, index: this.index, optionalChaining: this.optionalChaining };
    }
    static fromJSON(t5) {
      return new o2(t5.object, t5.index, t5.optionalChaining);
    }
  }
  return _defineProperty(o2, `name`, name$111), o2;
}, { isClass: true, isNode: true }), name$110 = `ArrayNode`, dependencies$110 = [`Node`], createArrayNode = factory(name$110, dependencies$110, (t4) => {
  var { Node: n2 } = t4;
  class r2 extends n2 {
    constructor(t5) {
      if (super(), this.items = t5 || [], !Array.isArray(this.items) || !this.items.every(isNode)) throw TypeError(`Array containing Nodes expected`);
    }
    get type() {
      return name$110;
    }
    get isArrayNode() {
      return true;
    }
    _compile(t5, n3) {
      var r3 = map$1(this.items, function(r4) {
        return r4._compile(t5, n3);
      });
      if (t5.config.matrix !== `Array`) {
        var i2 = t5.matrix;
        return function(t6, n4, a2) {
          return i2(map$1(r3, function(r4) {
            return r4(t6, n4, a2);
          }));
        };
      } else return function(t6, n4, i3) {
        return map$1(r3, function(r4) {
          return r4(t6, n4, i3);
        });
      };
    }
    forEach(t5) {
      for (var n3 = 0; n3 < this.items.length; n3++) {
        var r3 = this.items[n3];
        t5(r3, `items[` + n3 + `]`, this);
      }
    }
    map(t5) {
      for (var n3 = [], i2 = 0; i2 < this.items.length; i2++) n3[i2] = this._ifNode(t5(this.items[i2], `items[` + i2 + `]`, this));
      return new r2(n3);
    }
    clone() {
      return new r2(this.items.slice(0));
    }
    _toString(t5) {
      return `[` + this.items.map(function(n3) {
        return n3.toString(t5);
      }).join(`, `) + `]`;
    }
    toJSON() {
      return { mathjs: name$110, items: this.items };
    }
    static fromJSON(t5) {
      return new r2(t5.items);
    }
    _toHTML(t5) {
      return `<span class="math-parenthesis math-square-parenthesis">[</span>` + this.items.map(function(n3) {
        return n3.toHTML(t5);
      }).join(`<span class="math-separator">,</span>`) + `<span class="math-parenthesis math-square-parenthesis">]</span>`;
    }
    _toTex(t5) {
      function n3(r3, i2) {
        var a2 = r3.some(isArrayNode) && !r3.every(isArrayNode), o2 = i2 || a2, s2 = o2 ? `&` : `\\\\`, c2 = r3.map(function(r4) {
          return r4.items ? n3(r4.items, !i2) : r4.toTex(t5);
        }).join(s2);
        return a2 || !o2 || o2 && !i2 ? `\\begin{bmatrix}` + c2 + `\\end{bmatrix}` : c2;
      }
      return n3(this.items, false);
    }
  }
  return _defineProperty(r2, `name`, name$110), r2;
}, { isClass: true, isNode: true });
function assignFactory(t4) {
  var { subset: n2, matrix: r2 } = t4;
  return function(t5, i2, a2) {
    try {
      if (Array.isArray(t5)) return r2(t5).subset(i2, a2).valueOf().forEach((n3, r3) => {
        t5[r3] = n3;
      }), t5;
      if (t5 && typeof t5.subset == `function`) return t5.subset(i2, a2);
      if (typeof t5 == `string`) return n2(t5, i2, a2);
      if (typeof t5 == `object`) {
        if (!i2.isObjectProperty()) throw TypeError(`Cannot apply a numeric index as object property`);
        return setSafeProperty(t5, i2.getObjectProperty(), a2), t5;
      } else throw TypeError(`Cannot apply index: unsupported type of object`);
    } catch (t6) {
      throw errorTransform(t6);
    }
  };
}
var properties = [{ AssignmentNode: {}, FunctionAssignmentNode: {} }, { ConditionalNode: { latexLeftParens: false, latexRightParens: false, latexParens: false } }, { "OperatorNode:or": { op: `or`, associativity: `left`, associativeWith: [] } }, { "OperatorNode:xor": { op: `xor`, associativity: `left`, associativeWith: [] } }, { "OperatorNode:and": { op: `and`, associativity: `left`, associativeWith: [] } }, { "OperatorNode:bitOr": { op: `|`, associativity: `left`, associativeWith: [] } }, { "OperatorNode:bitXor": { op: `^|`, associativity: `left`, associativeWith: [] } }, { "OperatorNode:bitAnd": { op: `&`, associativity: `left`, associativeWith: [] } }, { "OperatorNode:equal": { op: `==`, associativity: `left`, associativeWith: [] }, "OperatorNode:unequal": { op: `!=`, associativity: `left`, associativeWith: [] }, "OperatorNode:smaller": { op: `<`, associativity: `left`, associativeWith: [] }, "OperatorNode:larger": { op: `>`, associativity: `left`, associativeWith: [] }, "OperatorNode:smallerEq": { op: `<=`, associativity: `left`, associativeWith: [] }, "OperatorNode:largerEq": { op: `>=`, associativity: `left`, associativeWith: [] }, RelationalNode: { associativity: `left`, associativeWith: [] } }, { "OperatorNode:leftShift": { op: `<<`, associativity: `left`, associativeWith: [] }, "OperatorNode:rightArithShift": { op: `>>`, associativity: `left`, associativeWith: [] }, "OperatorNode:rightLogShift": { op: `>>>`, associativity: `left`, associativeWith: [] } }, { "OperatorNode:to": { op: `to`, associativity: `left`, associativeWith: [] } }, { RangeNode: {} }, { "OperatorNode:add": { op: `+`, associativity: `left`, associativeWith: [`OperatorNode:add`, `OperatorNode:subtract`] }, "OperatorNode:subtract": { op: `-`, associativity: `left`, associativeWith: [] } }, { "OperatorNode:multiply": { op: `*`, associativity: `left`, associativeWith: [`OperatorNode:multiply`, `OperatorNode:divide`, `Operator:dotMultiply`, `Operator:dotDivide`] }, "OperatorNode:divide": { op: `/`, associativity: `left`, associativeWith: [], latexLeftParens: false, latexRightParens: false, latexParens: false }, "OperatorNode:dotMultiply": { op: `.*`, associativity: `left`, associativeWith: [`OperatorNode:multiply`, `OperatorNode:divide`, `OperatorNode:dotMultiply`, `OperatorNode:doDivide`] }, "OperatorNode:dotDivide": { op: `./`, associativity: `left`, associativeWith: [] }, "OperatorNode:mod": { op: `mod`, associativity: `left`, associativeWith: [] } }, { "OperatorNode:multiply": { associativity: `left`, associativeWith: [`OperatorNode:multiply`, `OperatorNode:divide`, `Operator:dotMultiply`, `Operator:dotDivide`] } }, { "OperatorNode:unaryPlus": { op: `+`, associativity: `right` }, "OperatorNode:unaryMinus": { op: `-`, associativity: `right` }, "OperatorNode:bitNot": { op: `~`, associativity: `right` }, "OperatorNode:not": { op: `not`, associativity: `right` } }, { "OperatorNode:pow": { op: `^`, associativity: `right`, associativeWith: [], latexRightParens: false }, "OperatorNode:dotPow": { op: `.^`, associativity: `right`, associativeWith: [] } }, { "OperatorNode:nullish": { op: `??`, associativity: `left`, associativeWith: [] } }, { "OperatorNode:factorial": { op: `!`, associativity: `left` } }, { "OperatorNode:ctranspose": { op: `'`, associativity: `left` } }];
function unwrapParen(t4, n2) {
  if (!n2 || n2 !== `auto`) return t4;
  for (var r2 = t4; isParenthesisNode(r2); ) r2 = r2.content;
  return r2;
}
function getPrecedence(t4, n2, r2, i2) {
  var a2 = t4;
  n2 !== `keep` && (a2 = t4.getContent());
  for (var o2 = a2.getIdentifier(), s2 = null, c2 = 0; c2 < properties.length; c2++) if (o2 in properties[c2]) {
    s2 = c2;
    break;
  }
  if (o2 === `OperatorNode:multiply` && a2.implicit && r2 !== `show`) {
    var l2 = unwrapParen(a2.args[0], n2);
    !(isConstantNode(l2) && i2 && i2.getIdentifier() === `OperatorNode:divide` && rule2Node(unwrapParen(i2.args[0], n2))) && !(l2.getIdentifier() === `OperatorNode:divide` && rule2Node(unwrapParen(l2.args[0], n2)) && isConstantNode(unwrapParen(l2.args[1]))) && (s2 += 1);
  }
  return s2;
}
function getAssociativity(t4, n2) {
  var r2 = t4;
  n2 !== `keep` && (r2 = t4.getContent());
  var i2 = r2.getIdentifier(), a2 = getPrecedence(r2, n2);
  if (a2 === null) return null;
  var o2 = properties[a2][i2];
  if (hasOwnProperty$1(o2, `associativity`)) {
    if (o2.associativity === `left`) return `left`;
    if (o2.associativity === `right`) return `right`;
    throw Error(`'` + i2 + `' has the invalid associativity '` + o2.associativity + `'.`);
  }
  return null;
}
function isAssociativeWith(t4, n2, r2) {
  var i2 = r2 === `keep` ? t4 : t4.getContent(), a2 = r2 === `keep` ? n2 : t4.getContent(), o2 = i2.getIdentifier(), s2 = a2.getIdentifier(), c2 = getPrecedence(i2, r2);
  if (c2 === null) return null;
  var l2 = properties[c2][o2];
  if (hasOwnProperty$1(l2, `associativeWith`) && l2.associativeWith instanceof Array) {
    for (var u2 = 0; u2 < l2.associativeWith.length; u2++) if (l2.associativeWith[u2] === s2) return true;
    return false;
  }
  return null;
}
function getOperator(t4) {
  var n2 = `OperatorNode:` + t4;
  for (var r2 of properties) if (n2 in r2) return r2[n2].op;
  return null;
}
var name$109 = `AssignmentNode`, dependencies$109 = [`subset`, `?matrix`, `Node`], createAssignmentNode = factory(name$109, dependencies$109, (t4) => {
  var { subset: n2, matrix: r2, Node: i2 } = t4, a2 = accessFactory({ subset: n2 }), o2 = assignFactory({ subset: n2, matrix: r2 });
  function s2(t5, n3, r3) {
    n3 ||= `keep`;
    var i3 = getPrecedence(t5, n3, r3), a3 = getPrecedence(t5.value, n3, r3);
    return n3 === `all` || a3 !== null && a3 <= i3;
  }
  class c2 extends i2 {
    constructor(t5, n3, r3) {
      if (super(), this.object = t5, this.index = r3 ? n3 : null, this.value = r3 || n3, !isSymbolNode(t5) && !isAccessorNode(t5)) throw TypeError(`SymbolNode or AccessorNode expected as "object"`);
      if (isSymbolNode(t5) && t5.name === `end`) throw Error(`Cannot assign to symbol "end"`);
      if (this.index && !isIndexNode(this.index)) throw TypeError(`IndexNode expected as "index"`);
      if (!isNode(this.value)) throw TypeError(`Node expected as "value"`);
    }
    get name() {
      return this.index ? this.index.isObjectProperty() ? this.index.getObjectProperty() : `` : this.object.name || ``;
    }
    get type() {
      return name$109;
    }
    get isAssignmentNode() {
      return true;
    }
    _compile(t5, n3) {
      var r3 = this.object._compile(t5, n3), i3 = this.index ? this.index._compile(t5, n3) : null, s3 = this.value._compile(t5, n3), c3 = this.object.name;
      if (!this.index) {
        if (!isSymbolNode(this.object)) throw TypeError(`SymbolNode expected as object`);
        return function(t6, n4, r4) {
          var i4 = s3(t6, n4, r4);
          return t6.set(c3, i4), i4;
        };
      } else if (this.index.isObjectProperty()) {
        var l2 = this.index.getObjectProperty();
        return function(t6, n4, i4) {
          var a3 = r3(t6, n4, i4), o3 = s3(t6, n4, i4);
          return setSafeProperty(a3, l2, o3), o3;
        };
      } else if (isSymbolNode(this.object)) return function(t6, n4, a3) {
        var l3 = r3(t6, n4, a3), u3 = s3(t6, n4, a3), d3 = i3(t6, n4, l3);
        return t6.set(c3, o2(l3, d3, u3)), u3;
      };
      else {
        var u2 = this.object.object._compile(t5, n3);
        if (this.object.index.isObjectProperty()) {
          var d2 = this.object.index.getObjectProperty();
          return function(t6, n4, r4) {
            var a3 = u2(t6, n4, r4), c4 = getSafeProperty(a3, d2), l3 = i3(t6, n4, c4), p3 = s3(t6, n4, r4);
            return setSafeProperty(a3, d2, o2(c4, l3, p3)), p3;
          };
        } else {
          var p2 = this.object.index._compile(t5, n3);
          return function(t6, n4, r4) {
            var c4 = u2(t6, n4, r4), l3 = p2(t6, n4, c4), d3 = a2(c4, l3), m2 = i3(t6, n4, d3), h2 = s3(t6, n4, r4);
            return o2(c4, l3, o2(d3, m2, h2)), h2;
          };
        }
      }
    }
    forEach(t5) {
      t5(this.object, `object`, this), this.index && t5(this.index, `index`, this), t5(this.value, `value`, this);
    }
    map(t5) {
      return new c2(this._ifNode(t5(this.object, `object`, this)), this.index ? this._ifNode(t5(this.index, `index`, this)) : null, this._ifNode(t5(this.value, `value`, this)));
    }
    clone() {
      return new c2(this.object, this.index, this.value);
    }
    _toString(t5) {
      var n3 = this.object.toString(t5), r3 = this.index ? this.index.toString(t5) : ``, i3 = this.value.toString(t5);
      return s2(this, t5 && t5.parenthesis, t5 && t5.implicit) && (i3 = `(` + i3 + `)`), n3 + r3 + ` = ` + i3;
    }
    toJSON() {
      return { mathjs: name$109, object: this.object, index: this.index, value: this.value };
    }
    static fromJSON(t5) {
      return new c2(t5.object, t5.index, t5.value);
    }
    _toHTML(t5) {
      var n3 = this.object.toHTML(t5), r3 = this.index ? this.index.toHTML(t5) : ``, i3 = this.value.toHTML(t5);
      return s2(this, t5 && t5.parenthesis, t5 && t5.implicit) && (i3 = `<span class="math-paranthesis math-round-parenthesis">(</span>` + i3 + `<span class="math-paranthesis math-round-parenthesis">)</span>`), n3 + r3 + `<span class="math-operator math-assignment-operator math-variable-assignment-operator math-binary-operator">=</span>` + i3;
    }
    _toTex(t5) {
      var n3 = this.object.toTex(t5), r3 = this.index ? this.index.toTex(t5) : ``, i3 = this.value.toTex(t5);
      return s2(this, t5 && t5.parenthesis, t5 && t5.implicit) && (i3 = `\\left(${i3}\\right)`), n3 + r3 + `=` + i3;
    }
  }
  return _defineProperty(c2, `name`, name$109), c2;
}, { isClass: true, isNode: true }), name$108 = `BlockNode`, dependencies$108 = [`ResultSet`, `Node`], createBlockNode = factory(name$108, dependencies$108, (t4) => {
  var { ResultSet: n2, Node: r2 } = t4;
  class i2 extends r2 {
    constructor(t5) {
      if (super(), !Array.isArray(t5)) throw Error(`Array expected`);
      this.blocks = t5.map(function(t6) {
        var n3 = t6 && t6.node, r3 = t6 && t6.visible !== void 0 ? t6.visible : true;
        if (!isNode(n3)) throw TypeError(`Property "node" must be a Node`);
        if (typeof r3 != `boolean`) throw TypeError(`Property "visible" must be a boolean`);
        return { node: n3, visible: r3 };
      });
    }
    get type() {
      return name$108;
    }
    get isBlockNode() {
      return true;
    }
    _compile(t5, r3) {
      var i3 = map$1(this.blocks, function(n3) {
        return { evaluate: n3.node._compile(t5, r3), visible: n3.visible };
      });
      return function(t6, r4, a2) {
        var o2 = [];
        return forEach(i3, function(n3) {
          var i4 = n3.evaluate(t6, r4, a2);
          n3.visible && o2.push(i4);
        }), new n2(o2);
      };
    }
    forEach(t5) {
      for (var n3 = 0; n3 < this.blocks.length; n3++) t5(this.blocks[n3].node, `blocks[` + n3 + `].node`, this);
    }
    map(t5) {
      for (var n3 = [], r3 = 0; r3 < this.blocks.length; r3++) {
        var a2 = this.blocks[r3];
        n3[r3] = { node: this._ifNode(t5(a2.node, `blocks[` + r3 + `].node`, this)), visible: a2.visible };
      }
      return new i2(n3);
    }
    clone() {
      return new i2(this.blocks.map(function(t5) {
        return { node: t5.node, visible: t5.visible };
      }));
    }
    _toString(t5) {
      return this.blocks.map(function(n3) {
        return n3.node.toString(t5) + (n3.visible ? `` : `;`);
      }).join(`
`);
    }
    toJSON() {
      return { mathjs: name$108, blocks: this.blocks };
    }
    static fromJSON(t5) {
      return new i2(t5.blocks);
    }
    _toHTML(t5) {
      return this.blocks.map(function(n3) {
        return n3.node.toHTML(t5) + (n3.visible ? `` : `<span class="math-separator">;</span>`);
      }).join(`<span class="math-separator"><br /></span>`);
    }
    _toTex(t5) {
      return this.blocks.map(function(n3) {
        return n3.node.toTex(t5) + (n3.visible ? `` : `;`);
      }).join(`\\;\\;
`);
    }
  }
  return _defineProperty(i2, `name`, name$108), i2;
}, { isClass: true, isNode: true }), name$107 = `ConditionalNode`, dependencies$107 = [`Node`], createConditionalNode = factory(name$107, dependencies$107, (t4) => {
  var { Node: n2 } = t4;
  function r2(t5) {
    if (typeof t5 == `number` || typeof t5 == `boolean` || typeof t5 == `string`) return !!t5;
    if (t5) {
      if (isBigNumber(t5)) return !t5.isZero();
      if (isComplex(t5)) return !!(t5.re || t5.im);
      if (isUnit(t5)) return !!t5.value;
    }
    if (t5 == null) return false;
    throw TypeError(`Unsupported type of condition "` + typeOf(t5) + `"`);
  }
  class i2 extends n2 {
    constructor(t5, n3, r3) {
      if (super(), !isNode(t5)) throw TypeError(`Parameter condition must be a Node`);
      if (!isNode(n3)) throw TypeError(`Parameter trueExpr must be a Node`);
      if (!isNode(r3)) throw TypeError(`Parameter falseExpr must be a Node`);
      this.condition = t5, this.trueExpr = n3, this.falseExpr = r3;
    }
    get type() {
      return name$107;
    }
    get isConditionalNode() {
      return true;
    }
    _compile(t5, n3) {
      var i3 = this.condition._compile(t5, n3), a2 = this.trueExpr._compile(t5, n3), o2 = this.falseExpr._compile(t5, n3);
      return function(t6, n4, s2) {
        return r2(i3(t6, n4, s2)) ? a2(t6, n4, s2) : o2(t6, n4, s2);
      };
    }
    forEach(t5) {
      t5(this.condition, `condition`, this), t5(this.trueExpr, `trueExpr`, this), t5(this.falseExpr, `falseExpr`, this);
    }
    map(t5) {
      return new i2(this._ifNode(t5(this.condition, `condition`, this)), this._ifNode(t5(this.trueExpr, `trueExpr`, this)), this._ifNode(t5(this.falseExpr, `falseExpr`, this)));
    }
    clone() {
      return new i2(this.condition, this.trueExpr, this.falseExpr);
    }
    _toString(t5) {
      var n3 = t5 && t5.parenthesis ? t5.parenthesis : `keep`, r3 = getPrecedence(this, n3, t5 && t5.implicit), i3 = this.condition.toString(t5), a2 = getPrecedence(this.condition, n3, t5 && t5.implicit);
      (n3 === `all` || this.condition.type === `OperatorNode` || a2 !== null && a2 <= r3) && (i3 = `(` + i3 + `)`);
      var o2 = this.trueExpr.toString(t5), s2 = getPrecedence(this.trueExpr, n3, t5 && t5.implicit);
      (n3 === `all` || this.trueExpr.type === `OperatorNode` || s2 !== null && s2 <= r3) && (o2 = `(` + o2 + `)`);
      var c2 = this.falseExpr.toString(t5), l2 = getPrecedence(this.falseExpr, n3, t5 && t5.implicit);
      return (n3 === `all` || this.falseExpr.type === `OperatorNode` || l2 !== null && l2 <= r3) && (c2 = `(` + c2 + `)`), i3 + ` ? ` + o2 + ` : ` + c2;
    }
    toJSON() {
      return { mathjs: name$107, condition: this.condition, trueExpr: this.trueExpr, falseExpr: this.falseExpr };
    }
    static fromJSON(t5) {
      return new i2(t5.condition, t5.trueExpr, t5.falseExpr);
    }
    _toHTML(t5) {
      var n3 = t5 && t5.parenthesis ? t5.parenthesis : `keep`, r3 = getPrecedence(this, n3, t5 && t5.implicit), i3 = this.condition.toHTML(t5), a2 = getPrecedence(this.condition, n3, t5 && t5.implicit);
      (n3 === `all` || this.condition.type === `OperatorNode` || a2 !== null && a2 <= r3) && (i3 = `<span class="math-parenthesis math-round-parenthesis">(</span>` + i3 + `<span class="math-parenthesis math-round-parenthesis">)</span>`);
      var o2 = this.trueExpr.toHTML(t5), s2 = getPrecedence(this.trueExpr, n3, t5 && t5.implicit);
      (n3 === `all` || this.trueExpr.type === `OperatorNode` || s2 !== null && s2 <= r3) && (o2 = `<span class="math-parenthesis math-round-parenthesis">(</span>` + o2 + `<span class="math-parenthesis math-round-parenthesis">)</span>`);
      var c2 = this.falseExpr.toHTML(t5), l2 = getPrecedence(this.falseExpr, n3, t5 && t5.implicit);
      return (n3 === `all` || this.falseExpr.type === `OperatorNode` || l2 !== null && l2 <= r3) && (c2 = `<span class="math-parenthesis math-round-parenthesis">(</span>` + c2 + `<span class="math-parenthesis math-round-parenthesis">)</span>`), i3 + `<span class="math-operator math-conditional-operator">?</span>` + o2 + `<span class="math-operator math-conditional-operator">:</span>` + c2;
    }
    _toTex(t5) {
      return `\\begin{cases} {` + this.trueExpr.toTex(t5) + `}, &\\quad{\\text{if }\\;` + this.condition.toTex(t5) + `}\\\\{` + this.falseExpr.toTex(t5) + `}, &\\quad{\\text{otherwise}}\\end{cases}`;
    }
  }
  return _defineProperty(i2, `name`, name$107), i2;
}, { isClass: true, isNode: true }), require_dist = __commonJSMin(((t4, n2) => {
  var r2 = Object.assign || function(t5) {
    for (var n3 = 1; n3 < arguments.length; n3++) {
      var r3 = arguments[n3];
      for (var i3 in r3) Object.prototype.hasOwnProperty.call(r3, i3) && (t5[i3] = r3[i3]);
    }
    return t5;
  }, i2 = { "{": `\\{`, "}": `\\}`, "\\": `\\textbackslash{}`, "#": `\\#`, $: `\\$`, "%": `\\%`, "&": `\\&`, "^": `\\textasciicircum{}`, _: `\\_`, "~": `\\textasciitilde{}` }, a2 = { "–": `\\--`, "—": `\\---`, " ": `~`, "	": `\\qquad{}`, "\r\n": `\\newline{}`, "\n": `\\newline{}` }, o2 = function(t5, n3) {
    return r2({}, t5, n3);
  };
  n2.exports = function(t5) {
    for (var n3 = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {}, s2 = n3.preserveFormatting, c2 = s2 === void 0 ? false : s2, l2 = n3.escapeMapFn, u2 = l2 === void 0 ? o2 : l2, d2 = String(t5), p2 = ``, m2 = u2(r2({}, i2), c2 ? r2({}, a2) : {}), h2 = Object.keys(m2), g2 = function() {
      var t6 = false;
      h2.forEach(function(n4, r3) {
        t6 || d2.length >= n4.length && d2.slice(0, n4.length) === n4 && (p2 += m2[h2[r3]], d2 = d2.slice(n4.length, d2.length), t6 = true);
      }), t6 || (p2 += d2.slice(0, 1), d2 = d2.slice(1, d2.length));
    }; d2; ) g2();
    return p2;
  };
})), import_dist = __toESM(require_dist()), latexSymbols = { Alpha: `A`, alpha: `\\alpha`, Beta: `B`, beta: `\\beta`, Gamma: `\\Gamma`, gamma: `\\gamma`, Delta: `\\Delta`, delta: `\\delta`, Epsilon: `E`, epsilon: `\\epsilon`, varepsilon: `\\varepsilon`, Zeta: `Z`, zeta: `\\zeta`, Eta: `H`, eta: `\\eta`, Theta: `\\Theta`, theta: `\\theta`, vartheta: `\\vartheta`, Iota: `I`, iota: `\\iota`, Kappa: `K`, kappa: `\\kappa`, varkappa: `\\varkappa`, Lambda: `\\Lambda`, lambda: `\\lambda`, Mu: `M`, mu: `\\mu`, Nu: `N`, nu: `\\nu`, Xi: `\\Xi`, xi: `\\xi`, Omicron: `O`, omicron: `o`, Pi: `\\Pi`, pi: `\\pi`, varpi: `\\varpi`, Rho: `P`, rho: `\\rho`, varrho: `\\varrho`, Sigma: `\\Sigma`, sigma: `\\sigma`, varsigma: `\\varsigma`, Tau: `T`, tau: `\\tau`, Upsilon: `\\Upsilon`, upsilon: `\\upsilon`, Phi: `\\Phi`, phi: `\\phi`, varphi: `\\varphi`, Chi: `X`, chi: `\\chi`, Psi: `\\Psi`, psi: `\\psi`, Omega: `\\Omega`, omega: `\\omega`, true: `\\mathrm{True}`, false: `\\mathrm{False}`, i: `i`, inf: `\\infty`, Inf: `\\infty`, infinity: `\\infty`, Infinity: `\\infty`, oo: `\\infty`, lim: `\\lim`, undefined: `\\mathbf{?}` }, latexOperators = { transpose: `^\\top`, ctranspose: `^H`, factorial: `!`, pow: `^`, dotPow: `.^\\wedge`, unaryPlus: `+`, unaryMinus: `-`, bitNot: `\\~`, not: `\\neg`, multiply: `\\cdot`, divide: `\\frac`, dotMultiply: `.\\cdot`, dotDivide: `.:`, mod: `\\mod`, add: `+`, subtract: `-`, to: `\\rightarrow`, leftShift: `<<`, rightArithShift: `>>`, rightLogShift: `>>>`, equal: `=`, unequal: `\\neq`, smaller: `<`, larger: `>`, smallerEq: `\\leq`, largerEq: `\\geq`, bitAnd: `\\&`, bitXor: `\\underline{|}`, bitOr: `|`, and: `\\wedge`, xor: `\\veebar`, or: `\\vee` }, latexFunctions = { abs: { 1: "\\left|${args[0]}\\right|" }, add: { 2: `\\left(\${args[0]}${latexOperators.add}\${args[1]}\\right)` }, cbrt: { 1: "\\sqrt[3]{${args[0]}}" }, ceil: { 1: "\\left\\lceil${args[0]}\\right\\rceil" }, cube: { 1: "\\left(${args[0]}\\right)^3" }, divide: { 2: "\\frac{${args[0]}}{${args[1]}}" }, dotDivide: { 2: `\\left(\${args[0]}${latexOperators.dotDivide}\${args[1]}\\right)` }, dotMultiply: { 2: `\\left(\${args[0]}${latexOperators.dotMultiply}\${args[1]}\\right)` }, dotPow: { 2: `\\left(\${args[0]}${latexOperators.dotPow}\${args[1]}\\right)` }, exp: { 1: "\\exp\\left(${args[0]}\\right)" }, expm1: `\\left(e${latexOperators.pow}{\${args[0]}}-1\\right)`, fix: { 1: "\\mathrm{${name}}\\left(${args[0]}\\right)" }, floor: { 1: "\\left\\lfloor${args[0]}\\right\\rfloor" }, fraction: { 2: "\\frac{${args[0]}}{${args[1]}}" }, gcd: "\\gcd\\left(${args}\\right)", hypot: "\\hypot\\left(${args}\\right)", log: { 1: "\\ln\\left(${args[0]}\\right)", 2: "\\log_{${args[1]}}\\left(${args[0]}\\right)" }, log10: { 1: "\\log_{10}\\left(${args[0]}\\right)" }, log1p: { 1: "\\ln\\left(${args[0]}+1\\right)", 2: "\\log_{${args[1]}}\\left(${args[0]}+1\\right)" }, log2: "\\log_{2}\\left(${args[0]}\\right)", mod: { 2: `\\left(\${args[0]}${latexOperators.mod}\${args[1]}\\right)` }, multiply: { 2: `\\left(\${args[0]}${latexOperators.multiply}\${args[1]}\\right)` }, norm: { 1: "\\left\\|${args[0]}\\right\\|", 2: void 0 }, nthRoot: { 2: "\\sqrt[${args[1]}]{${args[0]}}" }, nthRoots: { 2: "\\{y : y^${args[1]} = {${args[0]}}\\}" }, pow: { 2: `\\left(\${args[0]}\\right)${latexOperators.pow}{\${args[1]}}` }, round: { 1: "\\left\\lfloor${args[0]}\\right\\rceil", 2: void 0 }, sign: { 1: "\\mathrm{${name}}\\left(${args[0]}\\right)" }, sqrt: { 1: "\\sqrt{${args[0]}}" }, square: { 1: "\\left(${args[0]}\\right)^2" }, subtract: { 2: `\\left(\${args[0]}${latexOperators.subtract}\${args[1]}\\right)` }, unaryMinus: { 1: `${latexOperators.unaryMinus}\\left(\${args[0]}\\right)` }, unaryPlus: { 1: `${latexOperators.unaryPlus}\\left(\${args[0]}\\right)` }, bitAnd: { 2: `\\left(\${args[0]}${latexOperators.bitAnd}\${args[1]}\\right)` }, bitNot: { 1: latexOperators.bitNot + "\\left(${args[0]}\\right)" }, bitOr: { 2: `\\left(\${args[0]}${latexOperators.bitOr}\${args[1]}\\right)` }, bitXor: { 2: `\\left(\${args[0]}${latexOperators.bitXor}\${args[1]}\\right)` }, leftShift: { 2: `\\left(\${args[0]}${latexOperators.leftShift}\${args[1]}\\right)` }, rightArithShift: { 2: `\\left(\${args[0]}${latexOperators.rightArithShift}\${args[1]}\\right)` }, rightLogShift: { 2: `\\left(\${args[0]}${latexOperators.rightLogShift}\${args[1]}\\right)` }, bellNumbers: { 1: "\\mathrm{B}_{${args[0]}}" }, catalan: { 1: "\\mathrm{C}_{${args[0]}}" }, stirlingS2: { 2: "\\mathrm{S}\\left(${args}\\right)" }, arg: { 1: "\\arg\\left(${args[0]}\\right)" }, conj: { 1: "\\left(${args[0]}\\right)^*" }, im: { 1: "\\Im\\left\\lbrace${args[0]}\\right\\rbrace" }, re: { 1: "\\Re\\left\\lbrace${args[0]}\\right\\rbrace" }, and: { 2: `\\left(\${args[0]}${latexOperators.and}\${args[1]}\\right)` }, not: { 1: latexOperators.not + "\\left(${args[0]}\\right)" }, or: { 2: `\\left(\${args[0]}${latexOperators.or}\${args[1]}\\right)` }, xor: { 2: `\\left(\${args[0]}${latexOperators.xor}\${args[1]}\\right)` }, cross: { 2: "\\left(${args[0]}\\right)\\times\\left(${args[1]}\\right)" }, ctranspose: { 1: `\\left(\${args[0]}\\right)${latexOperators.ctranspose}` }, det: { 1: "\\det\\left(${args[0]}\\right)" }, dot: { 2: "\\left(${args[0]}\\cdot${args[1]}\\right)" }, expm: { 1: "\\exp\\left(${args[0]}\\right)" }, inv: { 1: "\\left(${args[0]}\\right)^{-1}" }, pinv: { 1: "\\left(${args[0]}\\right)^{+}" }, sqrtm: { 1: `{\${args[0]}}${latexOperators.pow}{\\frac{1}{2}}` }, trace: { 1: "\\mathrm{tr}\\left(${args[0]}\\right)" }, transpose: { 1: `\\left(\${args[0]}\\right)${latexOperators.transpose}` }, combinations: { 2: "\\binom{${args[0]}}{${args[1]}}" }, combinationsWithRep: { 2: "\\left(\\!\\!{\\binom{${args[0]}}{${args[1]}}}\\!\\!\\right)" }, factorial: { 1: `\\left(\${args[0]}\\right)${latexOperators.factorial}` }, gamma: { 1: "\\Gamma\\left(${args[0]}\\right)" }, lgamma: { 1: "\\ln\\Gamma\\left(${args[0]}\\right)" }, equal: { 2: `\\left(\${args[0]}${latexOperators.equal}\${args[1]}\\right)` }, larger: { 2: `\\left(\${args[0]}${latexOperators.larger}\${args[1]}\\right)` }, largerEq: { 2: `\\left(\${args[0]}${latexOperators.largerEq}\${args[1]}\\right)` }, smaller: { 2: `\\left(\${args[0]}${latexOperators.smaller}\${args[1]}\\right)` }, smallerEq: { 2: `\\left(\${args[0]}${latexOperators.smallerEq}\${args[1]}\\right)` }, unequal: { 2: `\\left(\${args[0]}${latexOperators.unequal}\${args[1]}\\right)` }, erf: { 1: "erf\\left(${args[0]}\\right)" }, max: "\\max\\left(${args}\\right)", min: "\\min\\left(${args}\\right)", variance: "\\mathrm{Var}\\left(${args}\\right)", acos: { 1: "\\cos^{-1}\\left(${args[0]}\\right)" }, acosh: { 1: "\\cosh^{-1}\\left(${args[0]}\\right)" }, acot: { 1: "\\cot^{-1}\\left(${args[0]}\\right)" }, acoth: { 1: "\\coth^{-1}\\left(${args[0]}\\right)" }, acsc: { 1: "\\csc^{-1}\\left(${args[0]}\\right)" }, acsch: { 1: "\\mathrm{csch}^{-1}\\left(${args[0]}\\right)" }, asec: { 1: "\\sec^{-1}\\left(${args[0]}\\right)" }, asech: { 1: "\\mathrm{sech}^{-1}\\left(${args[0]}\\right)" }, asin: { 1: "\\sin^{-1}\\left(${args[0]}\\right)" }, asinh: { 1: "\\sinh^{-1}\\left(${args[0]}\\right)" }, atan: { 1: "\\tan^{-1}\\left(${args[0]}\\right)" }, atan2: { 2: "\\mathrm{atan2}\\left(${args}\\right)" }, atanh: { 1: "\\tanh^{-1}\\left(${args[0]}\\right)" }, cos: { 1: "\\cos\\left(${args[0]}\\right)" }, cosh: { 1: "\\cosh\\left(${args[0]}\\right)" }, cot: { 1: "\\cot\\left(${args[0]}\\right)" }, coth: { 1: "\\coth\\left(${args[0]}\\right)" }, csc: { 1: "\\csc\\left(${args[0]}\\right)" }, csch: { 1: "\\mathrm{csch}\\left(${args[0]}\\right)" }, sec: { 1: "\\sec\\left(${args[0]}\\right)" }, sech: { 1: "\\mathrm{sech}\\left(${args[0]}\\right)" }, sin: { 1: "\\sin\\left(${args[0]}\\right)" }, sinh: { 1: "\\sinh\\left(${args[0]}\\right)" }, tan: { 1: "\\tan\\left(${args[0]}\\right)" }, tanh: { 1: "\\tanh\\left(${args[0]}\\right)" }, to: { 2: `\\left(\${args[0]}${latexOperators.to}\${args[1]}\\right)` }, numeric: function(t4, n2) {
  return t4.args[0].toTex();
}, number: { 0: `0`, 1: "\\left(${args[0]}\\right)", 2: "\\left(\\left(${args[0]}\\right)${args[1]}\\right)" }, string: { 0: `\\mathtt{""}`, 1: "\\mathrm{string}\\left(${args[0]}\\right)" }, bignumber: { 0: `0`, 1: "\\left(${args[0]}\\right)" }, bigint: { 0: `0`, 1: "\\left(${args[0]}\\right)" }, complex: { 0: `0`, 1: "\\left(${args[0]}\\right)", 2: `\\left(\\left(\${args[0]}\\right)+${latexSymbols.i}\\cdot\\left(\${args[1]}\\right)\\right)` }, matrix: { 0: `\\begin{bmatrix}\\end{bmatrix}`, 1: "\\left(${args[0]}\\right)", 2: "\\left(${args[0]}\\right)" }, sparse: { 0: `\\begin{bsparse}\\end{bsparse}`, 1: "\\left(${args[0]}\\right)" }, unit: { 1: "\\left(${args[0]}\\right)", 2: "\\left(\\left(${args[0]}\\right)${args[1]}\\right)" } }, defaultTemplate = "\\mathrm{${name}}\\left(${args}\\right)", latexUnits = { deg: `^\\circ` };
function escapeLatex(t4) {
  return (0, import_dist.default)(t4, { preserveFormatting: true });
}
function toSymbol(t4, n2) {
  return n2 = n2 === void 0 ? false : n2, n2 ? hasOwnProperty$1(latexUnits, t4) ? latexUnits[t4] : `\\mathrm{` + escapeLatex(t4) + `}` : hasOwnProperty$1(latexSymbols, t4) ? latexSymbols[t4] : escapeLatex(t4);
}
var name$106 = `ConstantNode`, dependencies$106 = [`Node`, `isBounded`], createConstantNode = factory(name$106, dependencies$106, (t4) => {
  var { Node: n2, isBounded: r2 } = t4;
  class i2 extends n2 {
    constructor(t5) {
      super(), this.value = t5;
    }
    get type() {
      return name$106;
    }
    get isConstantNode() {
      return true;
    }
    _compile(t5, n3) {
      var r3 = this.value;
      return function() {
        return r3;
      };
    }
    forEach(t5) {
    }
    map(t5) {
      return this.clone();
    }
    clone() {
      return new i2(this.value);
    }
    _toString(t5) {
      return format(this.value, t5);
    }
    _toHTML(t5) {
      var n3 = this._toString(t5);
      switch (typeOf(this.value)) {
        case `number`:
        case `bigint`:
        case `BigNumber`:
        case `Fraction`:
          return `<span class="math-number">` + n3 + `</span>`;
        case `string`:
          return `<span class="math-string">` + n3 + `</span>`;
        case `boolean`:
          return `<span class="math-boolean">` + n3 + `</span>`;
        case `null`:
          return `<span class="math-null-symbol">` + n3 + `</span>`;
        case `undefined`:
          return `<span class="math-undefined">` + n3 + `</span>`;
        default:
          return `<span class="math-symbol">` + n3 + `</span>`;
      }
    }
    toJSON() {
      return { mathjs: name$106, value: this.value };
    }
    static fromJSON(t5) {
      return new i2(t5.value);
    }
    _toTex(t5) {
      var n3 = this._toString(t5);
      switch (typeOf(this.value)) {
        case `string`:
          return `\\mathtt{` + escapeLatex(n3) + `}`;
        case `number`:
        case `BigNumber`:
          if (!r2(this.value)) return this.value.valueOf() < 0 ? `-\\infty` : `\\infty`;
          var i3 = n3.toLowerCase().indexOf(`e`);
          return i3 === -1 ? n3 : n3.substring(0, i3) + `\\cdot10^{` + n3.substring(i3 + 1) + `}`;
        case `bigint`:
          return n3.toString();
        case `Fraction`:
          return this.value.toLatex();
        default:
          return n3;
      }
    }
  }
  return _defineProperty(i2, `name`, name$106), i2;
}, { isClass: true, isNode: true }), name$105 = `FunctionAssignmentNode`, dependencies$105 = [`typed`, `Node`], createFunctionAssignmentNode = factory(name$105, dependencies$105, (t4) => {
  var { typed: n2, Node: r2 } = t4;
  function i2(t5, n3, r3) {
    var i3 = getPrecedence(t5, n3, r3), a3 = getPrecedence(t5.expr, n3, r3);
    return n3 === `all` || a3 !== null && a3 <= i3;
  }
  class a2 extends r2 {
    constructor(t5, n3, r3) {
      if (super(), typeof t5 != `string`) throw TypeError(`String expected for parameter "name"`);
      if (!Array.isArray(n3)) throw TypeError(`Array containing strings or objects expected for parameter "params"`);
      if (!isNode(r3)) throw TypeError(`Node expected for parameter "expr"`);
      if (keywords.has(t5)) throw Error(`Illegal function name, "` + t5 + `" is a reserved keyword`);
      var i3 = /* @__PURE__ */ new Set();
      for (var a3 of n3) {
        var o2 = typeof a3 == `string` ? a3 : a3.name;
        if (i3.has(o2)) throw Error(`Duplicate parameter name "${o2}"`);
        i3.add(o2);
      }
      this.name = t5, this.params = n3.map(function(t6) {
        return t6 && t6.name || t6;
      }), this.types = n3.map(function(t6) {
        return t6 && t6.type || `any`;
      }), this.expr = r3;
    }
    get type() {
      return name$105;
    }
    get isFunctionAssignmentNode() {
      return true;
    }
    _compile(t5, r3) {
      var i3 = Object.create(r3);
      forEach(this.params, function(t6) {
        i3[t6] = true;
      });
      var a3 = this.expr, o2 = a3._compile(t5, i3), s2 = this.name, c2 = this.params, l2 = join(this.types, `,`), u2 = s2 + `(` + join(this.params, `, `) + `)`;
      return function(t6, r4, i4) {
        var d2 = {};
        d2[l2] = function() {
          for (var n3 = Object.create(r4), a4 = 0; a4 < c2.length; a4++) n3[c2[a4]] = arguments[a4];
          return o2(t6, n3, i4);
        };
        var p2 = n2(s2, d2);
        return p2.syntax = u2, p2.expr = a3.toString(), t6.set(s2, p2), p2;
      };
    }
    forEach(t5) {
      t5(this.expr, `expr`, this);
    }
    map(t5) {
      var n3 = this._ifNode(t5(this.expr, `expr`, this));
      return new a2(this.name, this.params.slice(0), n3);
    }
    clone() {
      return new a2(this.name, this.params.slice(0), this.expr);
    }
    _toString(t5) {
      var n3 = t5 && t5.parenthesis ? t5.parenthesis : `keep`, r3 = this.expr.toString(t5);
      return i2(this, n3, t5 && t5.implicit) && (r3 = `(` + r3 + `)`), this.name + `(` + this.params.join(`, `) + `) = ` + r3;
    }
    toJSON() {
      var t5 = this.types;
      return { mathjs: name$105, name: this.name, params: this.params.map(function(n3, r3) {
        return { name: n3, type: t5[r3] };
      }), expr: this.expr };
    }
    static fromJSON(t5) {
      return new a2(t5.name, t5.params, t5.expr);
    }
    _toHTML(t5) {
      for (var n3 = t5 && t5.parenthesis ? t5.parenthesis : `keep`, r3 = [], a3 = 0; a3 < this.params.length; a3++) r3.push(`<span class="math-symbol math-parameter">` + escape(this.params[a3]) + `</span>`);
      var o2 = this.expr.toHTML(t5);
      return i2(this, n3, t5 && t5.implicit) && (o2 = `<span class="math-parenthesis math-round-parenthesis">(</span>` + o2 + `<span class="math-parenthesis math-round-parenthesis">)</span>`), `<span class="math-function">` + escape(this.name) + `</span><span class="math-parenthesis math-round-parenthesis">(</span>` + r3.join(`<span class="math-separator">,</span>`) + `<span class="math-parenthesis math-round-parenthesis">)</span><span class="math-operator math-assignment-operator math-variable-assignment-operator math-binary-operator">=</span>` + o2;
    }
    _toTex(t5) {
      var n3 = t5 && t5.parenthesis ? t5.parenthesis : `keep`, r3 = this.expr.toTex(t5);
      return i2(this, n3, t5 && t5.implicit) && (r3 = `\\left(${r3}\\right)`), `\\mathrm{` + this.name + `}\\left(` + this.params.map(toSymbol).join(`,`) + `\\right)=` + r3;
    }
  }
  return _defineProperty(a2, `name`, name$105), a2;
}, { isClass: true, isNode: true }), name$104 = `IndexNode`, dependencies$104 = [`Node`, `size`], createIndexNode = factory(name$104, dependencies$104, (t4) => {
  var { Node: n2, size: r2 } = t4;
  class i2 extends n2 {
    constructor(t5, n3) {
      if (super(), this.dimensions = t5, this.dotNotation = n3 || false, !Array.isArray(t5) || !t5.every(isNode)) throw TypeError(`Array containing Nodes expected for parameter "dimensions"`);
      if (this.dotNotation && !this.isObjectProperty()) throw Error(`dotNotation only applicable for object properties`);
    }
    get type() {
      return name$104;
    }
    get isIndexNode() {
      return true;
    }
    _compile(t5, n3) {
      var i3 = map$1(this.dimensions, function(i4, a3) {
        if (i4.filter((t6) => t6.isSymbolNode && t6.name === `end`).length > 0) {
          var o2 = Object.create(n3);
          o2.end = true;
          var s2 = i4._compile(t5, o2);
          return function(t6, n4, i5) {
            if (!isMatrix(i5) && !isArray(i5) && !isString(i5)) throw TypeError(`Cannot resolve "end": context must be a Matrix, Array, or string but is ` + typeOf(i5));
            var o3 = r2(i5), c2 = Object.create(n4);
            return c2.end = o3[a3], s2(t6, c2, i5);
          };
        } else return i4._compile(t5, n3);
      }), a2 = getSafeProperty(t5, `index`);
      return function(t6, n4, r3) {
        return a2(...map$1(i3, function(i4) {
          return i4(t6, n4, r3);
        }));
      };
    }
    forEach(t5) {
      for (var n3 = 0; n3 < this.dimensions.length; n3++) t5(this.dimensions[n3], `dimensions[` + n3 + `]`, this);
    }
    map(t5) {
      for (var n3 = [], r3 = 0; r3 < this.dimensions.length; r3++) n3[r3] = this._ifNode(t5(this.dimensions[r3], `dimensions[` + r3 + `]`, this));
      return new i2(n3, this.dotNotation);
    }
    clone() {
      return new i2(this.dimensions.slice(0), this.dotNotation);
    }
    isObjectProperty() {
      return this.dimensions.length === 1 && isConstantNode(this.dimensions[0]) && typeof this.dimensions[0].value == `string`;
    }
    getObjectProperty() {
      return this.isObjectProperty() ? this.dimensions[0].value : null;
    }
    _toString(t5) {
      return this.dotNotation ? `.` + this.getObjectProperty() : `[` + this.dimensions.join(`, `) + `]`;
    }
    toJSON() {
      return { mathjs: name$104, dimensions: this.dimensions, dotNotation: this.dotNotation };
    }
    static fromJSON(t5) {
      return new i2(t5.dimensions, t5.dotNotation);
    }
    _toHTML(t5) {
      for (var n3 = [], r3 = 0; r3 < this.dimensions.length; r3++) n3[r3] = this.dimensions[r3].toHTML();
      return this.dotNotation ? `<span class="math-operator math-accessor-operator">.</span><span class="math-symbol math-property">` + escape(this.getObjectProperty()) + `</span>` : `<span class="math-parenthesis math-square-parenthesis">[</span>` + n3.join(`<span class="math-separator">,</span>`) + `<span class="math-parenthesis math-square-parenthesis">]</span>`;
    }
    _toTex(t5) {
      var n3 = this.dimensions.map(function(n4) {
        return n4.toTex(t5);
      });
      return this.dotNotation ? `.` + this.getObjectProperty() : `_{` + n3.join(`,`) + `}`;
    }
  }
  return _defineProperty(i2, `name`, name$104), i2;
}, { isClass: true, isNode: true }), name$103 = `ObjectNode`, dependencies$103 = [`Node`], createObjectNode = factory(name$103, dependencies$103, (t4) => {
  var { Node: n2 } = t4;
  class r2 extends n2 {
    constructor(t5) {
      if (super(), this.properties = t5 || {}, t5 && (typeof t5 != `object` || !Object.keys(t5).every(function(n3) {
        return isNode(t5[n3]);
      }))) throw TypeError(`Object containing Nodes expected`);
    }
    get type() {
      return name$103;
    }
    get isObjectNode() {
      return true;
    }
    _compile(t5, n3) {
      var r3 = {};
      for (var i2 in this.properties) if (hasOwnProperty$1(this.properties, i2)) {
        var a2 = stringify(i2), o2 = JSON.parse(a2);
        r3[o2] = getSafeProperty(this.properties, i2)._compile(t5, n3);
      }
      return function(t6, n4, i3) {
        var a3 = {};
        for (var o3 in r3) hasOwnProperty$1(r3, o3) && (a3[o3] = r3[o3](t6, n4, i3));
        return a3;
      };
    }
    forEach(t5) {
      for (var n3 in this.properties) hasOwnProperty$1(this.properties, n3) && t5(this.properties[n3], `properties[` + stringify(n3) + `]`, this);
    }
    map(t5) {
      var n3 = {};
      for (var i2 in this.properties) hasOwnProperty$1(this.properties, i2) && (n3[i2] = this._ifNode(t5(this.properties[i2], `properties[` + stringify(i2) + `]`, this)));
      return new r2(n3);
    }
    clone() {
      var t5 = {};
      for (var n3 in this.properties) hasOwnProperty$1(this.properties, n3) && (t5[n3] = this.properties[n3]);
      return new r2(t5);
    }
    _toString(t5) {
      var n3 = [];
      for (var r3 in this.properties) hasOwnProperty$1(this.properties, r3) && n3.push(stringify(r3) + `: ` + this.properties[r3].toString(t5));
      return `{` + n3.join(`, `) + `}`;
    }
    toJSON() {
      return { mathjs: name$103, properties: this.properties };
    }
    static fromJSON(t5) {
      return new r2(t5.properties);
    }
    _toHTML(t5) {
      var n3 = [];
      for (var r3 in this.properties) hasOwnProperty$1(this.properties, r3) && n3.push(`<span class="math-symbol math-property">` + escape(r3) + `</span><span class="math-operator math-assignment-operator math-property-assignment-operator math-binary-operator">:</span>` + this.properties[r3].toHTML(t5));
      return `<span class="math-parenthesis math-curly-parenthesis">{</span>` + n3.join(`<span class="math-separator">,</span>`) + `<span class="math-parenthesis math-curly-parenthesis">}</span>`;
    }
    _toTex(t5) {
      var n3 = [];
      for (var r3 in this.properties) hasOwnProperty$1(this.properties, r3) && n3.push(`\\mathbf{` + r3 + `:} & ` + this.properties[r3].toTex(t5) + `\\\\`);
      return `\\left\\{\\begin{array}{ll}` + n3.join(`
`) + `\\end{array}\\right\\}`;
    }
  }
  return _defineProperty(r2, `name`, name$103), r2;
}, { isClass: true, isNode: true });
function createSubScope(t4, n2) {
  return new PartitionedMap(t4, new ObjectWrappingMap(n2), new Set(Object.keys(n2)));
}
var name$102 = `OperatorNode`, dependencies$102 = [`Node`], createOperatorNode = factory(name$102, dependencies$102, (t4) => {
  var { Node: n2 } = t4;
  function r2(t5, n3) {
    var i3 = t5;
    if (n3 === `auto`) for (; isParenthesisNode(i3); ) i3 = i3.content;
    return isConstantNode(i3) ? true : isOperatorNode(i3) ? r2(i3.args[0], n3) : false;
  }
  function i2(t5, n3, i3, a3, o2) {
    var s2 = getPrecedence(t5, n3, i3), c2 = getAssociativity(t5, n3);
    if (n3 === `all` || a3.length > 2 && t5.getIdentifier() !== `OperatorNode:add` && t5.getIdentifier() !== `OperatorNode:multiply`) return a3.map(function(t6) {
      switch (t6.getContent().type) {
        case `ArrayNode`:
        case `ConstantNode`:
        case `SymbolNode`:
        case `ParenthesisNode`:
          return false;
        default:
          return true;
      }
    });
    var l2;
    switch (a3.length) {
      case 0:
        l2 = [];
        break;
      case 1:
        var u2 = getPrecedence(a3[0], n3, i3, t5);
        if (o2 && u2 !== null) {
          var d2, p2;
          if (n3 === `keep` ? (d2 = a3[0].getIdentifier(), p2 = t5.getIdentifier()) : (d2 = a3[0].getContent().getIdentifier(), p2 = t5.getContent().getIdentifier()), properties[s2][p2].latexLeftParens === false) {
            l2 = [false];
            break;
          }
          if (properties[u2][d2].latexParens === false) {
            l2 = [false];
            break;
          }
        }
        if (u2 === null) {
          l2 = [false];
          break;
        }
        if (u2 <= s2) {
          l2 = [true];
          break;
        }
        l2 = [false];
        break;
      case 2:
        var m2, h2 = getPrecedence(a3[0], n3, i3, t5), g2 = isAssociativeWith(t5, a3[0], n3);
        m2 = h2 === null ? false : h2 === s2 && c2 === `right` && !g2 ? true : h2 < s2;
        var v2, y2 = getPrecedence(a3[1], n3, i3, t5), b2 = isAssociativeWith(t5, a3[1], n3);
        if (v2 = y2 === null ? false : y2 === s2 && c2 === `left` && !b2 ? true : y2 < s2, o2) {
          var x2, S2, w2;
          n3 === `keep` ? (x2 = t5.getIdentifier(), S2 = t5.args[0].getIdentifier(), w2 = t5.args[1].getIdentifier()) : (x2 = t5.getContent().getIdentifier(), S2 = t5.args[0].getContent().getIdentifier(), w2 = t5.args[1].getContent().getIdentifier()), h2 !== null && (properties[s2][x2].latexLeftParens === false && (m2 = false), properties[h2][S2].latexParens === false && (m2 = false)), y2 !== null && (properties[s2][x2].latexRightParens === false && (v2 = false), properties[y2][w2].latexParens === false && (v2 = false));
        }
        l2 = [m2, v2];
        break;
      default:
        (t5.getIdentifier() === `OperatorNode:add` || t5.getIdentifier() === `OperatorNode:multiply`) && (l2 = a3.map(function(r3) {
          var a4 = getPrecedence(r3, n3, i3, t5), o3 = isAssociativeWith(t5, r3, n3), l3 = getAssociativity(r3, n3);
          return a4 === null ? false : s2 === a4 && c2 === l3 && !o3 ? true : a4 < s2;
        }));
        break;
    }
    if (a3.length >= 2 && t5.getIdentifier() === `OperatorNode:multiply` && t5.implicit && n3 !== `all` && i3 === `hide`) for (var E = 1; E < l2.length; ++E) r2(a3[E], n3) && !l2[E - 1] && (n3 !== `keep` || !isParenthesisNode(a3[E - 1])) && (l2[E] = true);
    return l2;
  }
  class a2 extends n2 {
    constructor(t5, n3, r3, i3, a3) {
      if (super(), typeof t5 != `string`) throw TypeError(`string expected for parameter "op"`);
      if (typeof n3 != `string`) throw TypeError(`string expected for parameter "fn"`);
      if (!Array.isArray(r3) || !r3.every(isNode)) throw TypeError(`Array containing Nodes expected for parameter "args"`);
      this.implicit = i3 === true, this.isPercentage = a3 === true, this.op = t5, this.fn = n3, this.args = r3 || [];
    }
    get type() {
      return name$102;
    }
    get isOperatorNode() {
      return true;
    }
    _compile(t5, n3) {
      if (typeof this.fn != `string` || !isSafeMethod(t5, this.fn)) throw t5[this.fn] ? Error(`No access to function "` + this.fn + `"`) : Error(`Function ` + this.fn + ` missing in provided namespace "math"`);
      var r3 = getSafeProperty(t5, this.fn), i3 = map$1(this.args, function(r4) {
        return r4._compile(t5, n3);
      });
      if (typeof r3 == `function` && r3.rawArgs === true) {
        var a3 = this.args;
        return function(n4, i4, o3) {
          return r3(a3, t5, createSubScope(n4, i4));
        };
      } else if (i3.length === 1) {
        var o2 = i3[0];
        return function(t6, n4, i4) {
          return r3(o2(t6, n4, i4));
        };
      } else if (i3.length === 2) {
        var s2 = i3[0], c2 = i3[1];
        return function(t6, n4, i4) {
          return r3(s2(t6, n4, i4), c2(t6, n4, i4));
        };
      } else return function(t6, n4, a4) {
        return r3.apply(null, map$1(i3, function(r4) {
          return r4(t6, n4, a4);
        }));
      };
    }
    forEach(t5) {
      for (var n3 = 0; n3 < this.args.length; n3++) t5(this.args[n3], `args[` + n3 + `]`, this);
    }
    map(t5) {
      for (var n3 = [], r3 = 0; r3 < this.args.length; r3++) n3[r3] = this._ifNode(t5(this.args[r3], `args[` + r3 + `]`, this));
      return new a2(this.op, this.fn, n3, this.implicit, this.isPercentage);
    }
    clone() {
      return new a2(this.op, this.fn, this.args.slice(0), this.implicit, this.isPercentage);
    }
    isUnary() {
      return this.args.length === 1;
    }
    isBinary() {
      return this.args.length === 2;
    }
    _toString(t5) {
      var n3 = t5 && t5.parenthesis ? t5.parenthesis : `keep`, r3 = t5 && t5.implicit ? t5.implicit : `hide`, a3 = this.args, o2 = i2(this, n3, r3, a3, false);
      if (a3.length === 1) {
        var s2 = getAssociativity(this, n3), c2 = a3[0].toString(t5);
        o2[0] && (c2 = `(` + c2 + `)`);
        var l2 = /[a-zA-Z]+/.test(this.op);
        return s2 === `right` ? this.op + (l2 ? ` ` : ``) + c2 : s2 === `left` ? c2 + (l2 ? ` ` : ``) + this.op : c2 + this.op;
      } else if (a3.length === 2) {
        var u2 = a3[0].toString(t5), d2 = a3[1].toString(t5);
        return o2[0] && (u2 = `(` + u2 + `)`), o2[1] && (d2 = `(` + d2 + `)`), this.implicit && this.getIdentifier() === `OperatorNode:multiply` && r3 === `hide` ? u2 + ` ` + d2 : u2 + ` ` + this.op + ` ` + d2;
      } else if (a3.length > 2 && (this.getIdentifier() === `OperatorNode:add` || this.getIdentifier() === `OperatorNode:multiply`)) {
        var p2 = a3.map(function(n4, r4) {
          return n4 = n4.toString(t5), o2[r4] && (n4 = `(` + n4 + `)`), n4;
        });
        return this.implicit && this.getIdentifier() === `OperatorNode:multiply` && r3 === `hide` ? p2.join(` `) : p2.join(` ` + this.op + ` `);
      } else return this.fn + `(` + this.args.join(`, `) + `)`;
    }
    toJSON() {
      return { mathjs: name$102, op: this.op, fn: this.fn, args: this.args, implicit: this.implicit, isPercentage: this.isPercentage };
    }
    static fromJSON(t5) {
      return new a2(t5.op, t5.fn, t5.args, t5.implicit, t5.isPercentage);
    }
    _toHTML(t5) {
      var n3 = t5 && t5.parenthesis ? t5.parenthesis : `keep`, r3 = t5 && t5.implicit ? t5.implicit : `hide`, a3 = this.args, o2 = i2(this, n3, r3, a3, false);
      if (a3.length === 1) {
        var s2 = getAssociativity(this, n3), c2 = a3[0].toHTML(t5);
        return o2[0] && (c2 = `<span class="math-parenthesis math-round-parenthesis">(</span>` + c2 + `<span class="math-parenthesis math-round-parenthesis">)</span>`), s2 === `right` ? `<span class="math-operator math-unary-operator math-lefthand-unary-operator">` + escape(this.op) + `</span>` + c2 : c2 + `<span class="math-operator math-unary-operator math-righthand-unary-operator">` + escape(this.op) + `</span>`;
      } else if (a3.length === 2) {
        var l2 = a3[0].toHTML(t5), u2 = a3[1].toHTML(t5);
        return o2[0] && (l2 = `<span class="math-parenthesis math-round-parenthesis">(</span>` + l2 + `<span class="math-parenthesis math-round-parenthesis">)</span>`), o2[1] && (u2 = `<span class="math-parenthesis math-round-parenthesis">(</span>` + u2 + `<span class="math-parenthesis math-round-parenthesis">)</span>`), this.implicit && this.getIdentifier() === `OperatorNode:multiply` && r3 === `hide` ? l2 + `<span class="math-operator math-binary-operator math-implicit-binary-operator"></span>` + u2 : l2 + `<span class="math-operator math-binary-operator math-explicit-binary-operator">` + escape(this.op) + `</span>` + u2;
      } else {
        var d2 = a3.map(function(n4, r4) {
          return n4 = n4.toHTML(t5), o2[r4] && (n4 = `<span class="math-parenthesis math-round-parenthesis">(</span>` + n4 + `<span class="math-parenthesis math-round-parenthesis">)</span>`), n4;
        });
        return a3.length > 2 && (this.getIdentifier() === `OperatorNode:add` || this.getIdentifier() === `OperatorNode:multiply`) ? this.implicit && this.getIdentifier() === `OperatorNode:multiply` && r3 === `hide` ? d2.join(`<span class="math-operator math-binary-operator math-implicit-binary-operator"></span>`) : d2.join(`<span class="math-operator math-binary-operator math-explicit-binary-operator">` + escape(this.op) + `</span>`) : `<span class="math-function">` + escape(this.fn) + `</span><span class="math-paranthesis math-round-parenthesis">(</span>` + d2.join(`<span class="math-separator">,</span>`) + `<span class="math-paranthesis math-round-parenthesis">)</span>`;
      }
    }
    _toTex(t5) {
      var n3 = t5 && t5.parenthesis ? t5.parenthesis : `keep`, r3 = t5 && t5.implicit ? t5.implicit : `hide`, a3 = this.args, o2 = i2(this, n3, r3, a3, true), s2 = latexOperators[this.fn];
      if (s2 = s2 === void 0 ? this.op : s2, a3.length === 1) {
        var c2 = getAssociativity(this, n3), l2 = a3[0].toTex(t5);
        return o2[0] && (l2 = `\\left(${l2}\\right)`), c2 === `right` ? s2 + l2 : l2 + s2;
      } else if (a3.length === 2) {
        var u2 = a3[0], d2 = u2.toTex(t5);
        o2[0] && (d2 = `\\left(${d2}\\right)`);
        var p2 = a3[1].toTex(t5);
        o2[1] && (p2 = `\\left(${p2}\\right)`);
        var m2 = n3 === `keep` ? u2.getIdentifier() : u2.getContent().getIdentifier();
        switch (this.getIdentifier()) {
          case `OperatorNode:divide`:
            return s2 + `{` + d2 + `}{` + p2 + `}`;
          case `OperatorNode:pow`:
            switch (d2 = `{` + d2 + `}`, p2 = `{` + p2 + `}`, m2) {
              case `ConditionalNode`:
              case `OperatorNode:divide`:
                d2 = `\\left(${d2}\\right)`;
            }
            break;
          case `OperatorNode:multiply`:
            if (this.implicit && r3 === `hide`) return d2 + `~` + p2;
        }
        return d2 + s2 + p2;
      } else if (a3.length > 2 && (this.getIdentifier() === `OperatorNode:add` || this.getIdentifier() === `OperatorNode:multiply`)) {
        var h2 = a3.map(function(n4, r4) {
          return n4 = n4.toTex(t5), o2[r4] && (n4 = `\\left(${n4}\\right)`), n4;
        });
        return this.getIdentifier() === `OperatorNode:multiply` && this.implicit && r3 === `hide` ? h2.join(`~`) : h2.join(s2);
      } else return `\\mathrm{` + this.fn + `}\\left(` + a3.map(function(n4) {
        return n4.toTex(t5);
      }).join(`,`) + `\\right)`;
    }
    getIdentifier() {
      return this.type + `:` + this.fn;
    }
  }
  return _defineProperty(a2, `name`, name$102), a2;
}, { isClass: true, isNode: true }), name$101 = `ParenthesisNode`, dependencies$101 = [`Node`], createParenthesisNode = factory(name$101, dependencies$101, (t4) => {
  var { Node: n2 } = t4;
  class r2 extends n2 {
    constructor(t5) {
      if (super(), !isNode(t5)) throw TypeError(`Node expected for parameter "content"`);
      this.content = t5;
    }
    get type() {
      return name$101;
    }
    get isParenthesisNode() {
      return true;
    }
    _compile(t5, n3) {
      return this.content._compile(t5, n3);
    }
    getContent() {
      return this.content.getContent();
    }
    forEach(t5) {
      t5(this.content, `content`, this);
    }
    map(t5) {
      return new r2(t5(this.content, `content`, this));
    }
    clone() {
      return new r2(this.content);
    }
    _toString(t5) {
      return !t5 || t5 && !t5.parenthesis || t5 && t5.parenthesis === `keep` ? `(` + this.content.toString(t5) + `)` : this.content.toString(t5);
    }
    toJSON() {
      return { mathjs: name$101, content: this.content };
    }
    static fromJSON(t5) {
      return new r2(t5.content);
    }
    _toHTML(t5) {
      return !t5 || t5 && !t5.parenthesis || t5 && t5.parenthesis === `keep` ? `<span class="math-parenthesis math-round-parenthesis">(</span>` + this.content.toHTML(t5) + `<span class="math-parenthesis math-round-parenthesis">)</span>` : this.content.toHTML(t5);
    }
    _toTex(t5) {
      return !t5 || t5 && !t5.parenthesis || t5 && t5.parenthesis === `keep` ? `\\left(${this.content.toTex(t5)}\\right)` : this.content.toTex(t5);
    }
  }
  return _defineProperty(r2, `name`, name$101), r2;
}, { isClass: true, isNode: true }), name$100 = `RangeNode`, dependencies$100 = [`Node`], createRangeNode = factory(name$100, dependencies$100, (t4) => {
  var { Node: n2 } = t4;
  function r2(t5, n3, r3) {
    var i3 = getPrecedence(t5, n3, r3), a2 = {}, o2 = getPrecedence(t5.start, n3, r3);
    if (a2.start = o2 !== null && o2 <= i3 || n3 === `all`, t5.step) {
      var s2 = getPrecedence(t5.step, n3, r3);
      a2.step = s2 !== null && s2 <= i3 || n3 === `all`;
    }
    var c2 = getPrecedence(t5.end, n3, r3);
    return a2.end = c2 !== null && c2 <= i3 || n3 === `all`, a2;
  }
  class i2 extends n2 {
    constructor(t5, n3, r3) {
      if (super(), !isNode(t5) || !isNode(n3) || r3 && !isNode(r3)) throw TypeError(`Node expected`);
      if (arguments.length > 3) throw Error(`Too many arguments`);
      this.start = t5, this.end = n3, this.step = r3 || null;
    }
    get type() {
      return name$100;
    }
    get isRangeNode() {
      return true;
    }
    needsEnd() {
      return this.filter(function(t5) {
        return isSymbolNode(t5) && t5.name === `end`;
      }).length > 0;
    }
    _compile(t5, n3) {
      var r3 = t5.range, i3 = this.start._compile(t5, n3), a2 = this.end._compile(t5, n3);
      if (this.step) {
        var o2 = this.step._compile(t5, n3);
        return function(t6, n4, s2) {
          return r3(i3(t6, n4, s2), a2(t6, n4, s2), o2(t6, n4, s2));
        };
      } else return function(t6, n4, o3) {
        return r3(i3(t6, n4, o3), a2(t6, n4, o3));
      };
    }
    forEach(t5) {
      t5(this.start, `start`, this), t5(this.end, `end`, this), this.step && t5(this.step, `step`, this);
    }
    map(t5) {
      return new i2(this._ifNode(t5(this.start, `start`, this)), this._ifNode(t5(this.end, `end`, this)), this.step && this._ifNode(t5(this.step, `step`, this)));
    }
    clone() {
      return new i2(this.start, this.end, this.step && this.step);
    }
    _toString(t5) {
      var n3 = t5 && t5.parenthesis ? t5.parenthesis : `keep`, i3 = r2(this, n3, t5 && t5.implicit), a2, o2 = this.start.toString(t5);
      if (i3.start && (o2 = `(` + o2 + `)`), a2 = o2, this.step) {
        var s2 = this.step.toString(t5);
        i3.step && (s2 = `(` + s2 + `)`), a2 += `:` + s2;
      }
      var c2 = this.end.toString(t5);
      return i3.end && (c2 = `(` + c2 + `)`), a2 += `:` + c2, a2;
    }
    toJSON() {
      return { mathjs: name$100, start: this.start, end: this.end, step: this.step };
    }
    static fromJSON(t5) {
      return new i2(t5.start, t5.end, t5.step);
    }
    _toHTML(t5) {
      var n3 = t5 && t5.parenthesis ? t5.parenthesis : `keep`, i3 = r2(this, n3, t5 && t5.implicit), a2, o2 = this.start.toHTML(t5);
      if (i3.start && (o2 = `<span class="math-parenthesis math-round-parenthesis">(</span>` + o2 + `<span class="math-parenthesis math-round-parenthesis">)</span>`), a2 = o2, this.step) {
        var s2 = this.step.toHTML(t5);
        i3.step && (s2 = `<span class="math-parenthesis math-round-parenthesis">(</span>` + s2 + `<span class="math-parenthesis math-round-parenthesis">)</span>`), a2 += `<span class="math-operator math-range-operator">:</span>` + s2;
      }
      var c2 = this.end.toHTML(t5);
      return i3.end && (c2 = `<span class="math-parenthesis math-round-parenthesis">(</span>` + c2 + `<span class="math-parenthesis math-round-parenthesis">)</span>`), a2 += `<span class="math-operator math-range-operator">:</span>` + c2, a2;
    }
    _toTex(t5) {
      var n3 = t5 && t5.parenthesis ? t5.parenthesis : `keep`, i3 = r2(this, n3, t5 && t5.implicit), a2 = this.start.toTex(t5);
      if (i3.start && (a2 = `\\left(${a2}\\right)`), this.step) {
        var o2 = this.step.toTex(t5);
        i3.step && (o2 = `\\left(${o2}\\right)`), a2 += `:` + o2;
      }
      var s2 = this.end.toTex(t5);
      return i3.end && (s2 = `\\left(${s2}\\right)`), a2 += `:` + s2, a2;
    }
  }
  return _defineProperty(i2, `name`, name$100), i2;
}, { isClass: true, isNode: true }), name$99 = `RelationalNode`, dependencies$99 = [`Node`], createRelationalNode = factory(name$99, dependencies$99, (t4) => {
  var { Node: n2 } = t4, r2 = { equal: `==`, unequal: `!=`, smaller: `<`, larger: `>`, smallerEq: `<=`, largerEq: `>=` };
  class i2 extends n2 {
    constructor(t5, n3) {
      if (super(), !Array.isArray(t5)) throw TypeError(`Parameter conditionals must be an array`);
      if (!Array.isArray(n3)) throw TypeError(`Parameter params must be an array`);
      if (t5.length !== n3.length - 1) throw TypeError(`Parameter params must contain exactly one more element than parameter conditionals`);
      this.conditionals = t5, this.params = n3;
    }
    get type() {
      return name$99;
    }
    get isRelationalNode() {
      return true;
    }
    _compile(t5, n3) {
      var r3 = this, i3 = this.params.map((r4) => r4._compile(t5, n3));
      return function(n4, a2, o2) {
        for (var s2, c2 = i3[0](n4, a2, o2), l2 = 0; l2 < r3.conditionals.length; l2++) if (s2 = c2, c2 = i3[l2 + 1](n4, a2, o2), !getSafeProperty(t5, r3.conditionals[l2])(s2, c2)) return false;
        return true;
      };
    }
    forEach(t5) {
      this.params.forEach((n3, r3) => t5(n3, `params[` + r3 + `]`, this), this);
    }
    map(t5) {
      return new i2(this.conditionals.slice(), this.params.map((n3, r3) => this._ifNode(t5(n3, `params[` + r3 + `]`, this)), this));
    }
    clone() {
      return new i2(this.conditionals, this.params);
    }
    _toString(t5) {
      for (var n3 = t5 && t5.parenthesis ? t5.parenthesis : `keep`, i3 = getPrecedence(this, n3, t5 && t5.implicit), a2 = this.params.map(function(r3, a3) {
        var o3 = getPrecedence(r3, n3, t5 && t5.implicit);
        return n3 === `all` || o3 !== null && o3 <= i3 ? `(` + r3.toString(t5) + `)` : r3.toString(t5);
      }), o2 = a2[0], s2 = 0; s2 < this.conditionals.length; s2++) o2 += ` ` + r2[this.conditionals[s2]], o2 += ` ` + a2[s2 + 1];
      return o2;
    }
    toJSON() {
      return { mathjs: name$99, conditionals: this.conditionals, params: this.params };
    }
    static fromJSON(t5) {
      return new i2(t5.conditionals, t5.params);
    }
    _toHTML(t5) {
      for (var n3 = t5 && t5.parenthesis ? t5.parenthesis : `keep`, i3 = getPrecedence(this, n3, t5 && t5.implicit), a2 = this.params.map(function(r3, a3) {
        var o3 = getPrecedence(r3, n3, t5 && t5.implicit);
        return n3 === `all` || o3 !== null && o3 <= i3 ? `<span class="math-parenthesis math-round-parenthesis">(</span>` + r3.toHTML(t5) + `<span class="math-parenthesis math-round-parenthesis">)</span>` : r3.toHTML(t5);
      }), o2 = a2[0], s2 = 0; s2 < this.conditionals.length; s2++) o2 += `<span class="math-operator math-binary-operator math-explicit-binary-operator">` + escape(r2[this.conditionals[s2]]) + `</span>` + a2[s2 + 1];
      return o2;
    }
    _toTex(t5) {
      for (var n3 = t5 && t5.parenthesis ? t5.parenthesis : `keep`, r3 = getPrecedence(this, n3, t5 && t5.implicit), i3 = this.params.map(function(i4, a3) {
        var o3 = getPrecedence(i4, n3, t5 && t5.implicit);
        return n3 === `all` || o3 !== null && o3 <= r3 ? `\\left(` + i4.toTex(t5) + `\right)` : i4.toTex(t5);
      }), a2 = i3[0], o2 = 0; o2 < this.conditionals.length; o2++) a2 += latexOperators[this.conditionals[o2]] + i3[o2 + 1];
      return a2;
    }
  }
  return _defineProperty(i2, `name`, name$99), i2;
}, { isClass: true, isNode: true }), name$98 = `SymbolNode`, dependencies$98 = [`math`, `?Unit`, `Node`], createSymbolNode = factory(name$98, dependencies$98, (t4) => {
  var { math: n2, Unit: r2, Node: i2 } = t4;
  function a2(t5) {
    return r2 ? r2.isValuelessUnit(t5) : false;
  }
  class o2 extends i2 {
    constructor(t5) {
      if (super(), typeof t5 != `string`) throw TypeError(`String expected for parameter "name"`);
      this.name = t5;
    }
    get type() {
      return `SymbolNode`;
    }
    get isSymbolNode() {
      return true;
    }
    _compile(t5, n3) {
      var i3 = this.name;
      if (n3[i3] === true) return function(t6, n4, r3) {
        return getSafeProperty(n4, i3);
      };
      if (i3 in t5) return function(n4, r3, a3) {
        return n4.has(i3) ? n4.get(i3) : getSafeProperty(t5, i3);
      };
      var s2 = a2(i3);
      return function(t6, n4, a3) {
        return t6.has(i3) ? t6.get(i3) : s2 ? new r2(null, i3) : o2.onUndefinedSymbol(i3);
      };
    }
    forEach(t5) {
    }
    map(t5) {
      return this.clone();
    }
    static onUndefinedSymbol(t5) {
      throw Error(`Undefined symbol ` + t5);
    }
    clone() {
      return new o2(this.name);
    }
    _toString(t5) {
      return this.name;
    }
    _toHTML(t5) {
      var n3 = escape(this.name);
      return n3 === `true` || n3 === `false` ? `<span class="math-symbol math-boolean">` + n3 + `</span>` : n3 === `i` ? `<span class="math-symbol math-imaginary-symbol">` + n3 + `</span>` : n3 === `Infinity` ? `<span class="math-symbol math-infinity-symbol">` + n3 + `</span>` : n3 === `NaN` ? `<span class="math-symbol math-nan-symbol">` + n3 + `</span>` : n3 === `null` ? `<span class="math-symbol math-null-symbol">` + n3 + `</span>` : n3 === `undefined` ? `<span class="math-symbol math-undefined-symbol">` + n3 + `</span>` : `<span class="math-symbol">` + n3 + `</span>`;
    }
    toJSON() {
      return { mathjs: `SymbolNode`, name: this.name };
    }
    static fromJSON(t5) {
      return new o2(t5.name);
    }
    _toTex(t5) {
      var r3 = false;
      n2[this.name] === void 0 && a2(this.name) && (r3 = true);
      var i3 = toSymbol(this.name, r3);
      return i3[0] === `\\` ? i3 : ` ` + i3;
    }
  }
  return o2;
}, { isClass: true, isNode: true }), name$97 = `FunctionNode`, dependencies$97 = [`math`, `Node`, `SymbolNode`], createFunctionNode = factory(name$97, dependencies$97, (t4) => {
  var n2, { math: r2, Node: i2, SymbolNode: a2 } = t4, o2 = (t5) => format(t5, { truncate: 78 });
  function s2(t5, n3, r3) {
    for (var i3 = ``, a3 = /\$(?:\{([a-z_][a-z_0-9]*)(?:\[([0-9]+)\])?\}|\$)/gi, o3 = 0, s3; (s3 = a3.exec(t5)) !== null; ) if (i3 += t5.substring(o3, s3.index), o3 = s3.index, s3[0] === `$$`) i3 += `$`, o3++;
    else {
      o3 += s3[0].length;
      var c3 = n3[s3[1]];
      if (!c3) throw ReferenceError(`Template: Property ` + s3[1] + ` does not exist.`);
      if (s3[2] === void 0) switch (typeof c3) {
        case `string`:
          i3 += c3;
          break;
        case `object`:
          if (isNode(c3)) i3 += c3.toTex(r3);
          else if (Array.isArray(c3)) i3 += c3.map(function(t6, n4) {
            if (isNode(t6)) return t6.toTex(r3);
            throw TypeError(`Template: ` + s3[1] + `[` + n4 + `] is not a Node.`);
          }).join(`,`);
          else throw TypeError(`Template: ` + s3[1] + ` has to be a Node, String or array of Nodes`);
          break;
        default:
          throw TypeError(`Template: ` + s3[1] + ` has to be a Node, String or array of Nodes`);
      }
      else if (isNode(c3[s3[2]] && c3[s3[2]])) i3 += c3[s3[2]].toTex(r3);
      else throw TypeError(`Template: ` + s3[1] + `[` + s3[2] + `] is not a Node.`);
    }
    return i3 += t5.slice(o3), i3;
  }
  class c2 extends i2 {
    constructor(t5, n3, r3) {
      if (super(), typeof t5 == `string` && (t5 = new a2(t5)), !isNode(t5)) throw TypeError(`Node expected as parameter "fn"`);
      if (!Array.isArray(n3) || !n3.every(isNode)) throw TypeError(`Array containing Nodes expected for parameter "args"`);
      var i3 = typeof r3;
      if (!(i3 === `undefined` || i3 === `boolean`)) throw TypeError(`optional flag, if specified, must be boolean`);
      this.fn = t5, this.args = n3 || [], this.optional = !!r3;
    }
    get name() {
      return this.fn.name || ``;
    }
    get type() {
      return name$97;
    }
    get isFunctionNode() {
      return true;
    }
    _compile(t5, n3) {
      var r3 = this.args.map((r4) => r4._compile(t5, n3)), i3 = this.optional || isAccessorNode(this.fn) && this.fn.optionalChaining;
      if (isSymbolNode(this.fn)) {
        var a3 = this.fn.name;
        if (n3[a3]) {
          var s3 = this.args;
          return function(n4, c3, l3) {
            var u3 = getSafeProperty(c3, a3);
            if (!(i3 && u3 === void 0)) {
              if (typeof u3 != `function`) throw TypeError(`Argument '${a3}' was not a function; received: ${o2(u3)}`);
              if (u3.rawArgs) return u3(s3, t5, createSubScope(n4, c3));
              var d3 = r3.map((t6) => t6(n4, c3, l3));
              return u3.apply(u3, d3);
            }
          };
        } else {
          var l2 = a3 in t5 ? getSafeProperty(t5, a3) : void 0, u2 = typeof l2 == `function` && l2.rawArgs === true, d2 = (n4) => {
            var r4;
            if (n4.has(a3)) r4 = n4.get(a3);
            else if (a3 in t5) r4 = getSafeProperty(t5, a3);
            else if (i3) r4 = void 0;
            else return c2.onUndefinedFunction(a3);
            if (typeof r4 == `function` || i3 && r4 === void 0) return r4;
            throw TypeError(`'${a3}' is not a function; its value is:
  ${o2(r4)}`);
          };
          if (u2) {
            var p2 = this.args;
            return function(n4, i4, a4) {
              var o3 = d2(n4);
              return o3.rawArgs === true ? o3(p2, t5, createSubScope(n4, i4)) : o3(...r3.map((t6) => t6(n4, i4, a4)));
            };
          } else switch (r3.length) {
            case 0:
              return function(t6, n4, r4) {
                var a4 = d2(t6);
                if (!(i3 && a4 === void 0)) return a4();
              };
            case 1:
              return function(t6, n4, a4) {
                var o3 = d2(t6);
                if (!(i3 && o3 === void 0)) {
                  var s4 = r3[0];
                  return o3(s4(t6, n4, a4));
                }
              };
            case 2:
              return function(t6, n4, a4) {
                var o3 = d2(t6);
                if (!(i3 && o3 === void 0)) {
                  var s4 = r3[0], c3 = r3[1];
                  return o3(s4(t6, n4, a4), c3(t6, n4, a4));
                }
              };
            default:
              return function(t6, n4, a4) {
                var o3 = d2(t6);
                if (!(i3 && o3 === void 0)) return o3(...r3.map((r4) => r4(t6, n4, a4)));
              };
          }
        }
      } else if (isAccessorNode(this.fn) && isIndexNode(this.fn.index) && this.fn.index.isObjectProperty()) {
        var m2 = this.fn.object._compile(t5, n3), h2 = this.fn.index.getObjectProperty(), g2 = this.args;
        return function(n4, a4, o3) {
          var s4 = m2(n4, a4, o3);
          if (!(i3 && (s4 == null || s4[h2] === void 0))) {
            var c3 = getSafeMethod(s4, h2);
            if (c3 != null && c3.rawArgs) return c3(g2, t5, createSubScope(n4, a4));
            var l3 = r3.map((t6) => t6(n4, a4, o3));
            return c3.apply(s4, l3);
          }
        };
      } else {
        var v2 = this.fn.toString(), y2 = this.fn._compile(t5, n3), b2 = this.args;
        return function(n4, a4, s4) {
          var c3 = y2(n4, a4, s4);
          if (!(i3 && c3 === void 0)) {
            if (typeof c3 != `function`) throw TypeError(`Expression '${v2}' did not evaluate to a function; value is:
  ${o2(c3)}`);
            if (c3.rawArgs) return c3(b2, t5, createSubScope(n4, a4));
            var l3 = r3.map((t6) => t6(n4, a4, s4));
            return c3.apply(c3, l3);
          }
        };
      }
    }
    forEach(t5) {
      t5(this.fn, `fn`, this);
      for (var n3 = 0; n3 < this.args.length; n3++) t5(this.args[n3], `args[` + n3 + `]`, this);
    }
    map(t5) {
      for (var n3 = this._ifNode(t5(this.fn, `fn`, this)), r3 = [], i3 = 0; i3 < this.args.length; i3++) r3[i3] = this._ifNode(t5(this.args[i3], `args[` + i3 + `]`, this));
      return new c2(n3, r3);
    }
    clone() {
      return new c2(this.fn, this.args.slice(0));
    }
    toString(t5) {
      var n3, r3 = this.fn.toString(t5);
      return t5 && typeof t5.handler == `object` && hasOwnProperty$1(t5.handler, r3) && (n3 = t5.handler[r3](this, t5)), n3 === void 0 ? super.toString(t5) : n3;
    }
    _toString(t5) {
      var n3 = this.args.map(function(n4) {
        return n4.toString(t5);
      });
      return (isFunctionAssignmentNode(this.fn) ? `(` + this.fn.toString(t5) + `)` : this.fn.toString(t5)) + `(` + n3.join(`, `) + `)`;
    }
    toJSON() {
      return { mathjs: name$97, fn: this.fn, args: this.args };
    }
    _toHTML(t5) {
      var n3 = this.args.map(function(n4) {
        return n4.toHTML(t5);
      });
      return `<span class="math-function">` + escape(this.fn) + `</span><span class="math-paranthesis math-round-parenthesis">(</span>` + n3.join(`<span class="math-separator">,</span>`) + `<span class="math-paranthesis math-round-parenthesis">)</span>`;
    }
    toTex(t5) {
      var n3;
      return t5 && typeof t5.handler == `object` && hasOwnProperty$1(t5.handler, this.name) && (n3 = t5.handler[this.name](this, t5)), n3 === void 0 ? super.toTex(t5) : n3;
    }
    _toTex(t5) {
      var n3 = this.args.map(function(n4) {
        return n4.toTex(t5);
      }), i3;
      latexFunctions[this.name] && (i3 = latexFunctions[this.name]), r2[this.name] && (typeof r2[this.name].toTex == `function` || typeof r2[this.name].toTex == `object` || typeof r2[this.name].toTex == `string`) && (i3 = r2[this.name].toTex);
      var a3;
      switch (typeof i3) {
        case `function`:
          a3 = i3(this, t5);
          break;
        case `string`:
          a3 = s2(i3, this, t5);
          break;
        case `object`:
          switch (typeof i3[n3.length]) {
            case `function`:
              a3 = i3[n3.length](this, t5);
              break;
            case `string`:
              a3 = s2(i3[n3.length], this, t5);
              break;
          }
      }
      return a3 === void 0 ? s2(defaultTemplate, this, t5) : a3;
    }
    getIdentifier() {
      return this.type + `:` + this.name;
    }
  }
  return n2 = c2, _defineProperty(c2, `name`, name$97), _defineProperty(c2, `onUndefinedFunction`, function(t5) {
    throw Error(`Undefined function ` + t5);
  }), _defineProperty(c2, `fromJSON`, function(t5) {
    return new n2(t5.fn, t5.args);
  }), c2;
}, { isClass: true, isNode: true }), name$96 = `parse`, dependencies$96 = [`typed`, `numeric`, `config`, `AccessorNode`, `ArrayNode`, `AssignmentNode`, `BlockNode`, `ConditionalNode`, `ConstantNode`, `FunctionAssignmentNode`, `FunctionNode`, `IndexNode`, `ObjectNode`, `OperatorNode`, `ParenthesisNode`, `RangeNode`, `RelationalNode`, `SymbolNode`], createParse = factory(name$96, dependencies$96, (t4) => {
  var { typed: n2, numeric: r2, config: i2, AccessorNode: a2, ArrayNode: o2, AssignmentNode: s2, BlockNode: c2, ConditionalNode: l2, ConstantNode: u2, FunctionAssignmentNode: d2, FunctionNode: p2, IndexNode: m2, ObjectNode: h2, OperatorNode: g2, ParenthesisNode: v2, RangeNode: y2, RelationalNode: b2, SymbolNode: x2 } = t4, S2 = n2(name$96, { string: function(t5) {
    return K(t5, {});
  }, "Array | Matrix": function(t5) {
    return w2(t5, {});
  }, "string, Object": function(t5, n3) {
    return K(t5, n3.nodes === void 0 ? {} : n3.nodes);
  }, "Array | Matrix, Object": w2 });
  function w2(t5) {
    var n3 = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {}, r3 = n3.nodes === void 0 ? {} : n3.nodes;
    return deepMap(t5, function(t6) {
      if (typeof t6 != `string`) throw TypeError(`String expected`);
      return K(t6, r3);
    });
  }
  var E = { NULL: 0, DELIMITER: 1, NUMBER: 2, SYMBOL: 3, UNKNOWN: 4 }, D = { ",": true, "(": true, ")": true, "[": true, "]": true, "{": true, "}": true, '"': true, "'": true, ";": true, "+": true, "-": true, "*": true, ".*": true, "/": true, "./": true, "%": true, "^": true, ".^": true, "~": true, "!": true, "&": true, "|": true, "^|": true, "=": true, ":": true, "?": true, "?.": true, "??": true, "==": true, "!=": true, "<": true, ">": true, "<=": true, ">=": true, "<<": true, ">>": true, ">>>": true }, O = { mod: true, to: true, in: true, and: true, xor: true, or: true, not: true }, j2 = { true: true, false: false, null: null, undefined: void 0 }, M2 = [`NaN`, `Infinity`], N2 = { '"': `"`, "'": `'`, "\\": `\\`, "/": `/`, b: `\b`, f: `\f`, n: `
`, r: `\r`, t: `	` };
  function F() {
    return { extraNodes: {}, expression: ``, comment: ``, index: 0, token: ``, tokenType: E.NULL, nestingLevel: 0, conditionalLevel: null };
  }
  function I(t5, n3) {
    return t5.expression.substr(t5.index, n3);
  }
  function L(t5) {
    return I(t5, 1);
  }
  function R(t5) {
    t5.index++;
  }
  function z(t5) {
    return t5.expression.charAt(t5.index - 1);
  }
  function V(t5) {
    return t5.expression.charAt(t5.index + 1);
  }
  function H2(t5) {
    for (t5.tokenType = E.NULL, t5.token = ``, t5.comment = ``; ; ) {
      if (L(t5) === `#`) for (; L(t5) !== `
` && L(t5) !== ``; ) t5.comment += L(t5), R(t5);
      if (S2.isWhitespace(L(t5), t5.nestingLevel)) R(t5);
      else break;
    }
    if (L(t5) === ``) {
      t5.tokenType = E.DELIMITER;
      return;
    }
    if (L(t5) === `
` && !t5.nestingLevel) {
      t5.tokenType = E.DELIMITER, t5.token = L(t5), R(t5);
      return;
    }
    var n3 = L(t5), r3 = I(t5, 2), i3 = I(t5, 3);
    if (i3.length === 3 && D[i3]) {
      t5.tokenType = E.DELIMITER, t5.token = i3, R(t5), R(t5), R(t5);
      return;
    }
    if (r3.length === 2 && D[r3] && (r3 !== `?.` || !S2.isDigit(t5.expression.charAt(t5.index + 2)))) {
      t5.tokenType = E.DELIMITER, t5.token = r3, R(t5), R(t5);
      return;
    }
    if (D[n3]) {
      t5.tokenType = E.DELIMITER, t5.token = n3, R(t5);
      return;
    }
    if (S2.isDigitDot(n3)) {
      t5.tokenType = E.NUMBER;
      var a3 = I(t5, 2);
      if (a3 === `0b` || a3 === `0o` || a3 === `0x`) {
        for (t5.token += L(t5), R(t5), t5.token += L(t5), R(t5); S2.isAlpha(L(t5), z(t5), V(t5)) || S2.isDigit(L(t5)); ) t5.token += L(t5), R(t5);
        if (L(t5) === `.`) for (t5.token += `.`, R(t5); S2.isAlpha(L(t5), z(t5), V(t5)) || S2.isDigit(L(t5)); ) t5.token += L(t5), R(t5);
        else if (L(t5) === `i`) for (t5.token += `i`, R(t5); S2.isDigit(L(t5)); ) t5.token += L(t5), R(t5);
        return;
      }
      if (L(t5) === `.`) {
        if (t5.token += L(t5), R(t5), !S2.isDigit(L(t5))) {
          t5.tokenType = E.DELIMITER;
          return;
        }
      } else {
        for (; S2.isDigit(L(t5)); ) t5.token += L(t5), R(t5);
        S2.isDecimalMark(L(t5), V(t5)) && (t5.token += L(t5), R(t5));
      }
      for (; S2.isDigit(L(t5)); ) t5.token += L(t5), R(t5);
      if (L(t5) === `E` || L(t5) === `e`) {
        if (S2.isDigit(V(t5)) || V(t5) === `-` || V(t5) === `+`) {
          if (t5.token += L(t5), R(t5), (L(t5) === `+` || L(t5) === `-`) && (t5.token += L(t5), R(t5)), !S2.isDigit(L(t5))) throw ke(t5, `Digit expected, got "` + L(t5) + `"`);
          for (; S2.isDigit(L(t5)); ) t5.token += L(t5), R(t5);
          if (S2.isDecimalMark(L(t5), V(t5))) throw ke(t5, `Digit expected, got "` + L(t5) + `"`);
        } else if (S2.isDecimalMark(V(t5), t5.expression.charAt(t5.index + 2))) throw R(t5), ke(t5, `Digit expected, got "` + L(t5) + `"`);
      }
      return;
    }
    if (S2.isAlpha(L(t5), z(t5), V(t5))) {
      for (; S2.isAlpha(L(t5), z(t5), V(t5)) || S2.isDigit(L(t5)); ) t5.token += L(t5), R(t5);
      hasOwnProperty$1(O, t5.token) ? t5.tokenType = E.DELIMITER : t5.tokenType = E.SYMBOL;
      return;
    }
    for (t5.tokenType = E.UNKNOWN; L(t5) !== ``; ) t5.token += L(t5), R(t5);
    throw ke(t5, `Syntax error in part "` + t5.token + `"`);
  }
  function U(t5) {
    do
      H2(t5);
    while (t5.token === `
`);
  }
  function W(t5) {
    t5.nestingLevel++;
  }
  function G(t5) {
    t5.nestingLevel--;
  }
  S2.isAlpha = function(t5, n3, r3) {
    return S2.isValidLatinOrGreek(t5) || S2.isValidMathSymbol(t5, r3) || S2.isValidMathSymbol(n3, t5);
  }, S2.isValidLatinOrGreek = function(t5) {
    return /^[a-zA-Z_$\u00C0-\u02AF\u0370-\u03FF\u2100-\u214F]$/.test(t5);
  }, S2.isValidMathSymbol = function(t5, n3) {
    return /^[\uD835]$/.test(t5) && /^[\uDC00-\uDFFF]$/.test(n3) && /^[^\uDC55\uDC9D\uDCA0\uDCA1\uDCA3\uDCA4\uDCA7\uDCA8\uDCAD\uDCBA\uDCBC\uDCC4\uDD06\uDD0B\uDD0C\uDD15\uDD1D\uDD3A\uDD3F\uDD45\uDD47-\uDD49\uDD51\uDEA6\uDEA7\uDFCC\uDFCD]$/.test(n3);
  }, S2.isWhitespace = function(t5, n3) {
    return t5 === ` ` || t5 === `	` || t5 === ` ` || t5 === `
` && n3 > 0;
  }, S2.isDecimalMark = function(t5, n3) {
    return t5 === `.` && n3 !== `/` && n3 !== `*` && n3 !== `^`;
  }, S2.isDigitDot = function(t5) {
    return t5 >= `0` && t5 <= `9` || t5 === `.`;
  }, S2.isDigit = function(t5) {
    return t5 >= `0` && t5 <= `9`;
  };
  function K(t5, n3) {
    var r3 = F();
    _extends(r3, { expression: t5, extraNodes: n3 }), H2(r3);
    var i3 = q(r3);
    if (r3.token !== ``) throw r3.tokenType === E.DELIMITER ? Ae(r3, `Unexpected operator ` + r3.token) : ke(r3, `Unexpected part "` + r3.token + `"`);
    return i3;
  }
  function q(t5) {
    var n3, r3 = [], i3;
    for (t5.token !== `` && t5.token !== `
` && t5.token !== `;` && (n3 = J(t5), t5.comment && (n3.comment = t5.comment)); t5.token === `
` || t5.token === `;`; ) r3.length === 0 && n3 && (i3 = t5.token !== `;`, r3.push({ node: n3, visible: i3 })), H2(t5), t5.token !== `
` && t5.token !== `;` && t5.token !== `` && (n3 = J(t5), t5.comment && (n3.comment = t5.comment), i3 = t5.token !== `;`, r3.push({ node: n3, visible: i3 }));
    return r3.length > 0 ? new c2(r3) : (n3 || (n3 = new u2(void 0), t5.comment && (n3.comment = t5.comment)), n3);
  }
  function J(t5) {
    var n3, r3, i3, a3, o3 = ee(t5);
    if (t5.token === `=`) {
      if (isSymbolNode(o3)) return n3 = o3.name, U(t5), i3 = J(t5), new s2(new x2(n3), i3);
      if (isAccessorNode(o3)) {
        if (o3.optionalChaining) throw ke(t5, `Cannot assign to optional chain`);
        return U(t5), i3 = J(t5), new s2(o3.object, o3.index, i3);
      } else if (isFunctionNode(o3) && isSymbolNode(o3.fn) && (a3 = true, r3 = [], n3 = o3.name, o3.args.forEach(function(t6, n4) {
        isSymbolNode(t6) ? r3[n4] = t6.name : a3 = false;
      }), a3)) return U(t5), i3 = J(t5), new d2(n3, r3, i3);
      throw ke(t5, `Invalid left hand side of assignment operator =`);
    }
    return o3;
  }
  function ee(t5) {
    for (var n3 = Y(t5); t5.token === `?`; ) {
      var r3 = t5.conditionalLevel;
      t5.conditionalLevel = t5.nestingLevel, U(t5);
      var i3 = n3, a3 = J(t5);
      if (t5.token !== `:`) throw ke(t5, `False part of conditional expression expected`);
      t5.conditionalLevel = null, U(t5), n3 = new l2(i3, a3, J(t5)), t5.conditionalLevel = r3;
    }
    return n3;
  }
  function Y(t5) {
    for (var n3 = te(t5); t5.token === `or`; ) U(t5), n3 = new g2(`or`, `or`, [n3, te(t5)]);
    return n3;
  }
  function te(t5) {
    for (var n3 = ne(t5); t5.token === `xor`; ) U(t5), n3 = new g2(`xor`, `xor`, [n3, ne(t5)]);
    return n3;
  }
  function ne(t5) {
    for (var n3 = X(t5); t5.token === `and`; ) U(t5), n3 = new g2(`and`, `and`, [n3, X(t5)]);
    return n3;
  }
  function X(t5) {
    for (var n3 = ie(t5); t5.token === `|`; ) U(t5), n3 = new g2(`|`, `bitOr`, [n3, ie(t5)]);
    return n3;
  }
  function ie(t5) {
    for (var n3 = ae(t5); t5.token === `^|`; ) U(t5), n3 = new g2(`^|`, `bitXor`, [n3, ae(t5)]);
    return n3;
  }
  function ae(t5) {
    for (var n3 = Z(t5); t5.token === `&`; ) U(t5), n3 = new g2(`&`, `bitAnd`, [n3, Z(t5)]);
    return n3;
  }
  function Z(t5) {
    for (var n3 = [oe(t5)], r3 = [], i3 = { "==": `equal`, "!=": `unequal`, "<": `smaller`, ">": `larger`, "<=": `smallerEq`, ">=": `largerEq` }; hasOwnProperty$1(i3, t5.token); ) {
      var a3 = { name: t5.token, fn: i3[t5.token] };
      r3.push(a3), U(t5), n3.push(oe(t5));
    }
    return n3.length === 1 ? n3[0] : n3.length === 2 ? new g2(r3[0].name, r3[0].fn, n3) : new b2(r3.map((t6) => t6.fn), n3);
  }
  function oe(t5) {
    for (var n3 = $2(t5), r3, i3, a3, o3 = { "<<": `leftShift`, ">>": `rightArithShift`, ">>>": `rightLogShift` }; hasOwnProperty$1(o3, t5.token); ) r3 = t5.token, i3 = o3[r3], U(t5), a3 = [n3, $2(t5)], n3 = new g2(r3, i3, a3);
    return n3;
  }
  function $2(t5) {
    for (var n3 = se(t5), r3, i3, a3, o3 = { to: `to`, in: `to` }; hasOwnProperty$1(o3, t5.token); ) r3 = t5.token, i3 = o3[r3], U(t5), r3 === `in` && `])},;`.includes(t5.token) ? n3 = new g2(`*`, `multiply`, [n3, new x2(`in`)], true) : (a3 = [n3, se(t5)], n3 = new g2(r3, i3, a3));
    return n3;
  }
  function se(t5) {
    var n3, r3 = [];
    if (t5.token === `:`) {
      if (t5.conditionalLevel === t5.nestingLevel) throw ke(t5, `The true-expression of a conditional operator may not be empty`);
      n3 = new u2(1);
    } else n3 = ce(t5);
    if (t5.token === `:` && t5.conditionalLevel !== t5.nestingLevel) {
      for (r3.push(n3); t5.token === `:` && r3.length < 3; ) U(t5), t5.token === `)` || t5.token === `]` || t5.token === `,` || t5.token === `` ? r3.push(new x2(`end`)) : r3.push(ce(t5));
      n3 = r3.length === 3 ? new y2(r3[0], r3[2], r3[1]) : new y2(r3[0], r3[1]);
    }
    return n3;
  }
  function ce(t5) {
    for (var n3 = le(t5), r3, i3, a3, o3 = { "+": `add`, "-": `subtract` }; hasOwnProperty$1(o3, t5.token); ) {
      r3 = t5.token, i3 = o3[r3], U(t5);
      var s3 = le(t5);
      a3 = s3.isPercentage ? [n3, new g2(`*`, `multiply`, [n3, s3])] : [n3, s3], n3 = new g2(r3, i3, a3);
    }
    return n3;
  }
  function le(t5) {
    for (var n3 = ue(t5), r3 = n3, i3, a3, o3 = { "*": `multiply`, ".*": `dotMultiply`, "/": `divide`, "./": `dotDivide`, "%": `mod`, mod: `mod` }; hasOwnProperty$1(o3, t5.token); ) i3 = t5.token, a3 = o3[i3], U(t5), r3 = ue(t5), n3 = new g2(i3, a3, [n3, r3]);
    return n3;
  }
  function ue(t5) {
    for (var n3 = de(t5), r3 = n3; t5.tokenType === E.SYMBOL || t5.token === `in` && isConstantNode(n3) || t5.token === `in` && isOperatorNode(n3) && n3.fn === `unaryMinus` && isConstantNode(n3.args[0]) || t5.tokenType === E.NUMBER && !isConstantNode(r3) && (!isOperatorNode(r3) || r3.op === `!`) || t5.token === `(`; ) r3 = de(t5), n3 = new g2(`*`, `multiply`, [n3, r3], true);
    return n3;
  }
  function de(t5) {
    for (var n3 = fe(t5), r3 = n3, i3 = []; t5.token === `/` && rule2Node(r3); ) if (i3.push(_extends({}, t5)), U(t5), t5.tokenType === E.NUMBER) if (i3.push(_extends({}, t5)), U(t5), t5.tokenType === E.SYMBOL || t5.token === `(` || t5.token === `in`) _extends(t5, i3.pop()), i3.pop(), r3 = fe(t5), n3 = new g2(`/`, `divide`, [n3, r3]);
    else {
      i3.pop(), _extends(t5, i3.pop());
      break;
    }
    else {
      _extends(t5, i3.pop());
      break;
    }
    return n3;
  }
  function fe(t5) {
    var n3 = pe(t5);
    if (t5.token === `%`) {
      var r3 = _extends({}, t5);
      U(t5);
      try {
        pe(t5), _extends(t5, r3);
      } catch {
        n3 = new g2(`/`, `divide`, [n3, new u2(100)], false, true);
      }
    }
    return n3;
  }
  function pe(t5) {
    var n3, r3, i3, a3 = { "-": `unaryMinus`, "+": `unaryPlus`, "~": `bitNot`, not: `not` };
    return hasOwnProperty$1(a3, t5.token) ? (i3 = a3[t5.token], n3 = t5.token, U(t5), r3 = [pe(t5)], new g2(n3, i3, r3)) : me(t5);
  }
  function me(t5) {
    var n3 = he(t5), r3, i3, a3;
    return (t5.token === `^` || t5.token === `.^`) && (r3 = t5.token, i3 = r3 === `^` ? `pow` : `dotPow`, U(t5), a3 = [n3, pe(t5)], n3 = new g2(r3, i3, a3)), n3;
  }
  function he(t5) {
    for (var n3 = ge(t5); t5.token === `??`; ) U(t5), n3 = new g2(`??`, `nullish`, [n3, ge(t5)]);
    return n3;
  }
  function ge(t5) {
    for (var n3 = _e(t5), r3, i3, a3, o3 = { "!": `factorial`, "'": `ctranspose` }; hasOwnProperty$1(o3, t5.token); ) r3 = t5.token, i3 = o3[r3], H2(t5), a3 = [n3], n3 = new g2(r3, i3, a3), n3 = ye(t5, n3);
    return n3;
  }
  function _e(t5) {
    var n3 = [];
    if (t5.tokenType === E.SYMBOL && hasOwnProperty$1(t5.extraNodes, t5.token)) {
      var r3 = t5.extraNodes[t5.token];
      if (H2(t5), t5.token === `(`) {
        if (n3 = [], W(t5), H2(t5), t5.token !== `)`) for (n3.push(J(t5)); t5.token === `,`; ) H2(t5), n3.push(J(t5));
        if (t5.token !== `)`) throw ke(t5, `Parenthesis ) expected`);
        G(t5), H2(t5);
      }
      return new r3(n3);
    }
    return ve(t5);
  }
  function ve(t5) {
    var n3, i3;
    return t5.tokenType === E.SYMBOL || t5.tokenType === E.DELIMITER && t5.token in O ? (i3 = t5.token, H2(t5), n3 = hasOwnProperty$1(j2, i3) ? new u2(j2[i3]) : M2.includes(i3) ? new u2(r2(i3, `number`)) : new x2(i3), n3 = ye(t5, n3), n3) : be(t5);
  }
  function ye(t5, n3, r3) {
    for (var i3; ; ) {
      var o3 = false;
      t5.token === `?.` && (o3 = true, H2(t5));
      var s3 = (t5.token === `(` || t5.token === `[` || t5.token === `.`) && true;
      if (!(o3 || s3)) break;
      if (i3 = [], t5.token === `(`) if (o3 || isSymbolNode(n3) || isAccessorNode(n3)) {
        if (W(t5), H2(t5), t5.token !== `)`) for (i3.push(J(t5)); t5.token === `,`; ) H2(t5), i3.push(J(t5));
        if (t5.token !== `)`) throw ke(t5, `Parenthesis ) expected`);
        G(t5), H2(t5), n3 = new p2(n3, i3, o3);
      } else return n3;
      else if (t5.token === `[`) {
        if (W(t5), H2(t5), t5.token !== `]`) for (i3.push(J(t5)); t5.token === `,`; ) H2(t5), i3.push(J(t5));
        if (t5.token !== `]`) throw ke(t5, `Parenthesis ] expected`);
        G(t5), H2(t5), n3 = new a2(n3, new m2(i3), o3);
      } else {
        if (o3 || H2(t5), !(t5.tokenType === E.SYMBOL || t5.tokenType === E.DELIMITER && t5.token in O)) {
          var c3 = `Property name expected after `;
          throw c3 += o3 ? `optional chain` : `dot`, ke(t5, c3);
        }
        i3.push(new u2(t5.token)), H2(t5), n3 = new a2(n3, new m2(i3, true), o3);
      }
    }
    return n3;
  }
  function be(t5) {
    var n3, r3;
    return t5.token === `"` || t5.token === `'` ? (r3 = xe(t5, t5.token), n3 = new u2(r3), n3 = ye(t5, n3), n3) : Se(t5);
  }
  function xe(t5, n3) {
    for (var r3 = ``; L(t5) !== `` && L(t5) !== n3; ) if (L(t5) === `\\`) {
      R(t5);
      var i3 = L(t5), a3 = N2[i3];
      if (a3 !== void 0) r3 += a3, t5.index += 1;
      else if (i3 === `u`) {
        var o3 = t5.expression.slice(t5.index + 1, t5.index + 5);
        if (/^[0-9A-Fa-f]{4}$/.test(o3)) r3 += String.fromCharCode(parseInt(o3, 16)), t5.index += 5;
        else throw ke(t5, `Invalid unicode character \\u${o3}`);
      } else throw ke(t5, `Bad escape character \\${i3}`);
    } else r3 += L(t5), R(t5);
    if (H2(t5), t5.token !== n3) throw ke(t5, `End of string ${n3} expected`);
    return H2(t5), r3;
  }
  function Se(t5) {
    var n3, r3, i3, a3;
    if (t5.token === `[`) {
      if (W(t5), H2(t5), t5.token !== `]`) {
        var s3 = Ce(t5);
        if (t5.token === `;`) {
          for (i3 = 1, r3 = [s3]; t5.token === `;`; ) H2(t5), t5.token !== `]` && (r3[i3] = Ce(t5), i3++);
          if (t5.token !== `]`) throw ke(t5, `End of matrix ] expected`);
          G(t5), H2(t5), a3 = r3[0].items.length;
          for (var c3 = 1; c3 < i3; c3++) if (r3[c3].items.length !== a3) throw Ae(t5, `Column dimensions mismatch (` + r3[c3].items.length + ` !== ` + a3 + `)`);
          n3 = new o2(r3);
        } else {
          if (t5.token !== `]`) throw ke(t5, `End of matrix ] expected`);
          G(t5), H2(t5), n3 = s3;
        }
      } else G(t5), H2(t5), n3 = new o2([]);
      return ye(t5, n3);
    }
    return we(t5);
  }
  function Ce(t5) {
    for (var n3 = [J(t5)], r3 = 1; t5.token === `,`; ) H2(t5), t5.token !== `]` && t5.token !== `;` && (n3[r3] = J(t5), r3++);
    return new o2(n3);
  }
  function we(t5) {
    if (t5.token === `{`) {
      W(t5);
      var n3, r3 = {};
      do
        if (H2(t5), t5.token !== `}`) {
          if (t5.token === `"` || t5.token === `'`) n3 = xe(t5, t5.token);
          else if (t5.tokenType === E.SYMBOL || t5.tokenType === E.DELIMITER && t5.token in O) n3 = t5.token, H2(t5);
          else throw ke(t5, `Symbol or string expected as object key`);
          if (t5.token !== `:`) throw ke(t5, `Colon : expected after object key`);
          H2(t5), r3[n3] = J(t5);
        }
      while (t5.token === `,`);
      if (t5.token !== `}`) throw ke(t5, `Comma , or bracket } expected after object value`);
      G(t5), H2(t5);
      var i3 = new h2(r3);
      return i3 = ye(t5, i3), i3;
    }
    return Te(t5);
  }
  function Te(t5) {
    var n3;
    if (t5.tokenType === E.NUMBER) {
      n3 = t5.token, H2(t5);
      var a3 = safeNumberType(n3, i2);
      return new u2(r2(n3, a3));
    }
    return Ee(t5);
  }
  function Ee(t5) {
    var n3;
    if (t5.token === `(`) {
      if (W(t5), H2(t5), n3 = J(t5), t5.token !== `)`) throw ke(t5, `Parenthesis ) expected`);
      return G(t5), H2(t5), n3 = new v2(n3), n3 = ye(t5, n3), n3;
    }
    return De(t5);
  }
  function De(t5) {
    throw t5.token === `` ? ke(t5, `Unexpected end of expression`) : ke(t5, `Value expected`);
  }
  function Oe(t5) {
    return t5.index - t5.token.length + 1;
  }
  function ke(t5, n3) {
    var r3 = Oe(t5), i3 = SyntaxError(n3 + ` (char ` + r3 + `)`);
    return i3.char = r3, i3;
  }
  function Ae(t5, n3) {
    var r3 = Oe(t5), i3 = SyntaxError(n3 + ` (char ` + r3 + `)`);
    return i3.char = r3, i3;
  }
  return n2.addConversion({ from: `string`, to: `Node`, convert: S2 }), S2;
}), name$95 = `compile`, dependencies$95 = [`typed`, `parse`], createCompile = factory(name$95, dependencies$95, (t4) => {
  var { typed: n2, parse: r2 } = t4;
  return n2(name$95, { string: function(t5) {
    return r2(t5).compile();
  }, "Array | Matrix": function(t5) {
    return deepMap(t5, function(t6) {
      return r2(t6).compile();
    });
  } });
}), name$94 = `evaluate`, dependencies$94 = [`typed`, `parse`], createEvaluate = factory(name$94, dependencies$94, (t4) => {
  var { typed: n2, parse: r2 } = t4;
  return n2(name$94, { string: function(t5) {
    var n3 = createEmptyMap();
    return r2(t5).compile().evaluate(n3);
  }, "string, Map | Object": function(t5, n3) {
    return r2(t5).compile().evaluate(n3);
  }, "Array | Matrix": function(t5) {
    var n3 = createEmptyMap();
    return deepMap(t5, function(t6) {
      return r2(t6).compile().evaluate(n3);
    });
  }, "Array | Matrix, Map | Object": function(t5, n3) {
    return deepMap(t5, function(t6) {
      return r2(t6).compile().evaluate(n3);
    });
  } });
}), name$93 = `Parser`, dependencies$93 = [`evaluate`, `parse`], createParserClass = factory(name$93, dependencies$93, (t4) => {
  var { evaluate: n2, parse: r2 } = t4;
  function i2() {
    if (!(this instanceof i2)) throw SyntaxError(`Constructor must be called with the new operator`);
    Object.defineProperty(this, "scope", { value: createEmptyMap(), writable: false });
  }
  i2.prototype.type = `Parser`, i2.prototype.isParser = true, i2.prototype.evaluate = function(t5) {
    return n2(t5, this.scope);
  }, i2.prototype.get = function(t5) {
    if (this.scope.has(t5)) return this.scope.get(t5);
  }, i2.prototype.getAll = function() {
    return toObject(this.scope);
  }, i2.prototype.getAllAsMap = function() {
    return this.scope;
  };
  function a2(t5) {
    if (t5.length === 0) return false;
    for (var n3 = 0; n3 < t5.length; n3++) {
      var i3 = t5.charAt(n3 - 1), a3 = t5.charAt(n3), o2 = t5.charAt(n3 + 1);
      if (!(r2.isAlpha(a3, i3, o2) || n3 > 0 && r2.isDigit(a3))) return false;
    }
    return true;
  }
  return i2.prototype.set = function(t5, n3) {
    if (!a2(t5)) throw Error(`Invalid variable name: '${t5}'. Variable names must follow the specified rules.`);
    return this.scope.set(t5, n3), n3;
  }, i2.prototype.remove = function(t5) {
    this.scope.delete(t5);
  }, i2.prototype.clear = function() {
    this.scope.clear();
  }, i2.prototype.toJSON = function() {
    var t5 = { mathjs: `Parser`, variables: {}, functions: {} };
    for (var [n3, r3] of this.scope) if (isFunction$1(r3)) {
      if (!isExpressionFunction(r3)) throw Error(`Cannot serialize external function ${n3}`);
      t5.functions[n3] = `${r3.syntax} = ${r3.expr}`;
    } else t5.variables[n3] = r3;
    return t5;
  }, i2.fromJSON = function(t5) {
    var n3 = new i2();
    return Object.entries(t5.variables).forEach((t6) => {
      var [r3, i3] = t6;
      return n3.set(r3, i3);
    }), Object.entries(t5.functions).forEach((t6) => {
      var [r3, i3] = t6;
      return n3.evaluate(i3);
    }), n3;
  }, i2;
}, { isClass: true });
function isExpressionFunction(t4) {
  return typeof t4 == `function` && typeof t4.syntax == `string` && typeof t4.expr == `string`;
}
var name$92 = `parser`, dependencies$92 = [`typed`, `Parser`], createParser = factory(name$92, dependencies$92, (t4) => {
  var { typed: n2, Parser: r2 } = t4;
  return n2(name$92, { "": function() {
    return new r2();
  } });
}), name$91 = `lup`, dependencies$91 = [`typed`, `matrix`, `abs`, `addScalar`, `divideScalar`, `multiplyScalar`, `subtractScalar`, `larger`, `equalScalar`, `unaryMinus`, `DenseMatrix`, `SparseMatrix`, `Spa`], createLup = factory(name$91, dependencies$91, (t4) => {
  var { typed: n2, matrix: r2, abs: i2, addScalar: a2, divideScalar: o2, multiplyScalar: s2, subtractScalar: c2, larger: l2, equalScalar: u2, unaryMinus: d2, DenseMatrix: p2, SparseMatrix: m2, Spa: h2 } = t4;
  return n2(name$91, { DenseMatrix: function(t5) {
    return g2(t5);
  }, SparseMatrix: function(t5) {
    return v2(t5);
  }, Array: function(t5) {
    var n3 = g2(r2(t5));
    return { L: n3.L.valueOf(), U: n3.U.valueOf(), p: n3.p };
  } });
  function g2(t5) {
    var n3 = t5._size[0], r3 = t5._size[1], d3 = Math.min(n3, r3), m3 = clone$3(t5._data), h3 = [], g3 = [n3, d3], v3 = [], y2 = [d3, r3], b2, x2, S2, w2 = [];
    for (b2 = 0; b2 < n3; b2++) w2[b2] = b2;
    for (x2 = 0; x2 < r3; x2++) {
      if (x2 > 0) for (b2 = 0; b2 < n3; b2++) {
        var E = Math.min(b2, x2), D = 0;
        for (S2 = 0; S2 < E; S2++) D = a2(D, s2(m3[b2][S2], m3[S2][x2]));
        m3[b2][x2] = c2(m3[b2][x2], D);
      }
      var O = x2, j2 = 0, M2 = 0;
      for (b2 = x2; b2 < n3; b2++) {
        var N2 = m3[b2][x2], F = i2(N2);
        l2(F, j2) && (O = b2, j2 = F, M2 = N2);
      }
      if (x2 !== O && (w2[x2] = [w2[O], w2[O] = w2[x2]][0], p2._swapRows(x2, O, m3)), x2 < n3) for (b2 = x2 + 1; b2 < n3; b2++) {
        var I = m3[b2][x2];
        u2(I, 0) || (m3[b2][x2] = o2(m3[b2][x2], M2));
      }
    }
    for (x2 = 0; x2 < r3; x2++) for (b2 = 0; b2 < n3; b2++) {
      if (x2 === 0 && (b2 < r3 && (v3[b2] = []), h3[b2] = []), b2 < x2) {
        b2 < r3 && (v3[b2][x2] = m3[b2][x2]), x2 < n3 && (h3[b2][x2] = 0);
        continue;
      }
      if (b2 === x2) {
        b2 < r3 && (v3[b2][x2] = m3[b2][x2]), x2 < n3 && (h3[b2][x2] = 1);
        continue;
      }
      b2 < r3 && (v3[b2][x2] = 0), x2 < n3 && (h3[b2][x2] = m3[b2][x2]);
    }
    var L = new p2({ data: h3, size: g3 }), R = new p2({ data: v3, size: y2 }), z = [];
    for (b2 = 0, d3 = w2.length; b2 < d3; b2++) z[w2[b2]] = b2;
    return { L, U: R, p: z, toString: function() {
      return `L: ` + this.L.toString() + `
U: ` + this.U.toString() + `
P: ` + this.p;
    } };
  }
  function v2(t5) {
    var n3 = t5._size[0], r3 = t5._size[1], a3 = Math.min(n3, r3), c3 = t5._values, p3 = t5._index, g3 = t5._ptr, v3 = [], y2 = [], b2 = [], x2 = [n3, a3], S2 = [], w2 = [], E = [], D = [a3, r3], O, j2, M2, N2 = [], F = [];
    for (O = 0; O < n3; O++) N2[O] = O, F[O] = O;
    var I = function(t6, n4) {
      var r4 = F[t6], i3 = F[n4];
      N2[r4] = n4, N2[i3] = t6, F[t6] = i3, F[n4] = r4;
    }, L = function() {
      var t6 = new h2();
      j2 < n3 && (b2.push(v3.length), v3.push(1), y2.push(j2)), E.push(S2.length);
      var r4 = g3[j2], a4 = g3[j2 + 1];
      for (M2 = r4; M2 < a4; M2++) O = p3[M2], t6.set(N2[O], c3[M2]);
      j2 > 0 && t6.forEach(0, j2 - 1, function(n4, r5) {
        m2._forEachRow(n4, v3, y2, b2, function(i3, a5) {
          i3 > n4 && t6.accumulate(i3, d2(s2(a5, r5)));
        });
      });
      var F2 = j2, L2 = t6.get(j2), R = i2(L2);
      t6.forEach(j2 + 1, n3 - 1, function(t7, n4) {
        var r5 = i2(n4);
        l2(r5, R) && (F2 = t7, R = r5, L2 = n4);
      }), j2 !== F2 && (m2._swapRows(j2, F2, x2[1], v3, y2, b2), m2._swapRows(j2, F2, D[1], S2, w2, E), t6.swap(j2, F2), I(j2, F2)), t6.forEach(0, n3 - 1, function(t7, n4) {
        t7 <= j2 ? (S2.push(n4), w2.push(t7)) : (n4 = o2(n4, L2), u2(n4, 0) || (v3.push(n4), y2.push(t7)));
      });
    };
    for (j2 = 0; j2 < r3; j2++) L();
    return E.push(S2.length), b2.push(v3.length), { L: new m2({ values: v3, index: y2, ptr: b2, size: x2 }), U: new m2({ values: S2, index: w2, ptr: E, size: D }), p: N2, toString: function() {
      return `L: ` + this.L.toString() + `
U: ` + this.U.toString() + `
P: ` + this.p;
    } };
  }
}), name$90 = `qr`, dependencies$90 = [`typed`, `matrix`, `zeros`, `identity`, `isZero`, `equal`, `sign`, `sqrt`, `conj`, `unaryMinus`, `addScalar`, `divideScalar`, `multiplyScalar`, `subtractScalar`, `complex`], createQr = factory(name$90, dependencies$90, (t4) => {
  var { typed: n2, matrix: r2, zeros: i2, identity: a2, isZero: o2, equal: s2, sign: c2, sqrt: l2, conj: u2, unaryMinus: d2, addScalar: p2, divideScalar: m2, multiplyScalar: h2, subtractScalar: g2, complex: v2 } = t4;
  return _extends(n2(name$90, { DenseMatrix: function(t5) {
    return b2(t5);
  }, SparseMatrix: function(t5) {
    return x2();
  }, Array: function(t5) {
    var n3 = b2(r2(t5));
    return { Q: n3.Q.valueOf(), R: n3.R.valueOf() };
  } }), { _denseQRimpl: y2 });
  function y2(t5) {
    var n3 = t5._size[0], r3 = t5._size[1], v3 = a2([n3], `dense`), y3 = v3._data, b3 = t5.clone(), x3 = b3._data, S2, w2, E, D = i2([n3], ``);
    for (E = 0; E < Math.min(r3, n3); ++E) {
      var O = x3[E][E], j2 = d2(s2(O, 0) ? 1 : c2(O)), M2 = u2(j2), N2 = 0;
      for (S2 = E; S2 < n3; S2++) N2 = p2(N2, h2(x3[S2][E], u2(x3[S2][E])));
      var F = h2(j2, l2(N2));
      if (!o2(F)) {
        var I = g2(O, F);
        for (D[E] = 1, S2 = E + 1; S2 < n3; S2++) D[S2] = m2(x3[S2][E], I);
        var L = d2(u2(m2(I, F))), R = void 0;
        for (w2 = E; w2 < r3; w2++) {
          for (R = 0, S2 = E; S2 < n3; S2++) R = p2(R, h2(u2(D[S2]), x3[S2][w2]));
          for (R = h2(R, L), S2 = E; S2 < n3; S2++) x3[S2][w2] = h2(g2(x3[S2][w2], h2(D[S2], R)), M2);
        }
        for (S2 = 0; S2 < n3; S2++) {
          for (R = 0, w2 = E; w2 < n3; w2++) R = p2(R, h2(y3[S2][w2], D[w2]));
          for (R = h2(R, L), w2 = E; w2 < n3; ++w2) y3[S2][w2] = m2(g2(y3[S2][w2], h2(R, u2(D[w2]))), M2);
        }
      }
    }
    return { Q: v3, R: b3, toString: function() {
      return `Q: ` + this.Q.toString() + `
R: ` + this.R.toString();
    } };
  }
  function b2(t5) {
    var n3 = y2(t5), r3 = n3.R._data;
    if (t5._data.length > 0) for (var i3 = r3[0][0].type === `Complex` ? v2(0) : 0, a3 = 0; a3 < r3.length; ++a3) for (var o3 = 0; o3 < a3 && o3 < (r3[0] || []).length; ++o3) r3[a3][o3] = i3;
    return n3;
  }
  function x2(t5) {
    throw Error(`qr not implemented for sparse matrices yet`);
  }
});
function csPermute(t4, n2, r2, i2) {
  for (var a2 = t4._values, o2 = t4._index, s2 = t4._ptr, c2 = t4._size, l2 = t4._datatype, u2 = c2[0], d2 = c2[1], p2 = null, m2 = [], h2 = [], g2 = 0, v2 = 0; v2 < d2; v2++) {
    h2[v2] = g2;
    for (var y2 = r2 ? r2[v2] : v2, b2 = s2[y2], x2 = s2[y2 + 1], S2 = b2; S2 < x2; S2++) m2[g2] = o2[S2], p2 && (p2[g2] = a2[S2]), g2++;
  }
  return h2[d2] = g2, t4.createSparseMatrix({ values: p2, index: m2, ptr: h2, size: [u2, d2], datatype: l2 });
}
function csTdfs(t4, n2, r2, i2, a2, o2, s2) {
  var c2 = 0;
  for (r2[s2] = t4; c2 >= 0; ) {
    var l2 = r2[s2 + c2], u2 = r2[i2 + l2];
    u2 === -1 ? (c2--, o2[n2++] = l2) : (r2[i2 + l2] = r2[a2 + u2], ++c2, r2[s2 + c2] = u2);
  }
  return n2;
}
function csPost(t4, n2) {
  if (!t4) return null;
  var r2 = 0, i2, a2 = [], o2 = [], s2 = 0, c2 = n2, l2 = 2 * n2;
  for (i2 = 0; i2 < n2; i2++) o2[s2 + i2] = -1;
  for (i2 = n2 - 1; i2 >= 0; i2--) t4[i2] !== -1 && (o2[c2 + i2] = o2[s2 + t4[i2]], o2[s2 + t4[i2]] = i2);
  for (i2 = 0; i2 < n2; i2++) t4[i2] === -1 && (r2 = csTdfs(i2, r2, o2, s2, c2, a2, l2));
  return a2;
}
function csEtree(t4, n2) {
  if (!t4) return null;
  var r2 = t4._index, i2 = t4._ptr, a2 = t4._size, o2 = a2[0], s2 = a2[1], c2 = [], l2 = [], u2 = 0, d2 = s2, p2, m2;
  for (p2 = 0; p2 < o2; p2++) l2[d2 + p2] = -1;
  for (var h2 = 0; h2 < s2; h2++) {
    c2[h2] = -1, l2[u2 + h2] = -1;
    for (var g2 = i2[h2], v2 = i2[h2 + 1], y2 = g2; y2 < v2; y2++) {
      var b2 = r2[y2];
      for (p2 = l2[d2 + b2]; p2 !== -1 && p2 < h2; p2 = m2) m2 = l2[u2 + p2], l2[u2 + p2] = h2, m2 === -1 && (c2[p2] = h2);
      l2[d2 + b2] = h2;
    }
  }
  return c2;
}
function csFkeep(t4, n2, r2) {
  for (var i2 = t4._values, a2 = t4._index, o2 = t4._ptr, s2 = t4._size[1], c2 = 0, l2 = 0; l2 < s2; l2++) {
    var u2 = o2[l2];
    for (o2[l2] = c2; u2 < o2[l2 + 1]; u2++) n2(a2[u2], l2, i2 ? i2[u2] : 1, r2) && (a2[c2] = a2[u2], i2 && (i2[c2] = i2[u2]), c2++);
  }
  return o2[s2] = c2, a2.splice(c2, a2.length - c2), i2 && i2.splice(c2, i2.length - c2), c2;
}
function csFlip(t4) {
  return -t4 - 2;
}
var name$89 = `csAmd`, dependencies$89 = [`add`, `multiply`, `transpose`], createCsAmd = factory(name$89, dependencies$89, (t4) => {
  var { add: n2, multiply: r2, transpose: i2 } = t4;
  return function(t5, n3) {
    if (!n3 || t5 <= 0 || t5 > 3) return null;
    var r3 = n3._size, i3 = r3[0], u2 = r3[1], d2 = 0, p2 = Math.max(16, 10 * Math.sqrt(u2));
    p2 = Math.min(u2 - 2, p2);
    var m2 = a2(t5, n3, i3, u2, p2);
    csFkeep(m2, l2, null);
    for (var h2 = m2._index, g2 = m2._ptr, v2 = g2[u2], y2 = [], b2 = [], x2 = 0, S2 = u2 + 1, w2 = 2 * (u2 + 1), E = 3 * (u2 + 1), D = 4 * (u2 + 1), O = 5 * (u2 + 1), j2 = 6 * (u2 + 1), M2 = 7 * (u2 + 1), N2 = y2, F = o2(u2, g2, b2, x2, E, N2, w2, M2, S2, j2, D, O), I = s2(u2, g2, b2, O, D, j2, p2, S2, E, N2, w2), L = 0, R, z, V, H2, U, W, G, K, q, J, ee, Y, te, ne, X, ie; I < u2; ) {
      for (V = -1; L < u2 && (V = b2[E + L]) === -1; L++) ;
      b2[w2 + V] !== -1 && (N2[b2[w2 + V]] = -1), b2[E + L] = b2[w2 + V];
      var ae = b2[D + V], Z = b2[S2 + V];
      I += Z;
      var oe = 0;
      b2[S2 + V] = -Z;
      var $2 = g2[V], se = ae === 0 ? $2 : v2, ce = se;
      for (H2 = 1; H2 <= ae + 1; H2++) {
        for (H2 > ae ? (W = V, G = $2, K = b2[x2 + V] - ae) : (W = h2[$2++], G = g2[W], K = b2[x2 + W]), U = 1; U <= K; U++) R = h2[G++], !((q = b2[S2 + R]) <= 0) && (oe += q, b2[S2 + R] = -q, h2[ce++] = R, b2[w2 + R] !== -1 && (N2[b2[w2 + R]] = N2[R]), N2[R] === -1 ? b2[E + b2[O + R]] = b2[w2 + R] : b2[w2 + N2[R]] = b2[w2 + R]);
        W !== V && (g2[W] = csFlip(V), b2[j2 + W] = 0);
      }
      for (ae !== 0 && (v2 = ce), b2[O + V] = oe, g2[V] = se, b2[x2 + V] = ce - se, b2[D + V] = -2, F = c2(F, d2, b2, j2, u2), J = se; J < ce; J++) if (R = h2[J], !((ee = b2[D + R]) <= 0)) {
        q = -b2[S2 + R];
        var le = F - q;
        for ($2 = g2[R], Y = g2[R] + ee - 1; $2 <= Y; $2++) W = h2[$2], b2[j2 + W] >= F ? b2[j2 + W] -= q : b2[j2 + W] !== 0 && (b2[j2 + W] = b2[O + W] + le);
      }
      for (J = se; J < ce; J++) {
        for (R = h2[J], Y = g2[R], te = Y + b2[D + R] - 1, ne = Y, X = 0, ie = 0, $2 = Y; $2 <= te; $2++) if (W = h2[$2], b2[j2 + W] !== 0) {
          var ue = b2[j2 + W] - F;
          ue > 0 ? (ie += ue, h2[ne++] = W, X += W) : (g2[W] = csFlip(V), b2[j2 + W] = 0);
        }
        b2[D + R] = ne - Y + 1;
        var de = ne, fe = Y + b2[x2 + R];
        for ($2 = te + 1; $2 < fe; $2++) {
          z = h2[$2];
          var pe = b2[S2 + z];
          pe <= 0 || (ie += pe, h2[ne++] = z, X += z);
        }
        ie === 0 ? (g2[R] = csFlip(V), q = -b2[S2 + R], oe -= q, Z += q, I += q, b2[S2 + R] = 0, b2[D + R] = -1) : (b2[O + R] = Math.min(b2[O + R], ie), h2[ne] = h2[de], h2[de] = h2[Y], h2[Y] = V, b2[x2 + R] = ne - Y + 1, X = (X < 0 ? -X : X) % u2, b2[w2 + R] = b2[M2 + X], b2[M2 + X] = R, N2[R] = X);
      }
      for (b2[O + V] = oe, d2 = Math.max(d2, oe), F = c2(F + d2, d2, b2, j2, u2), J = se; J < ce; J++) if (R = h2[J], !(b2[S2 + R] >= 0)) for (X = N2[R], R = b2[M2 + X], b2[M2 + X] = -1; R !== -1 && b2[w2 + R] !== -1; R = b2[w2 + R], F++) {
        for (K = b2[x2 + R], ee = b2[D + R], $2 = g2[R] + 1; $2 <= g2[R] + K - 1; $2++) b2[j2 + h2[$2]] = F;
        var me = R;
        for (z = b2[w2 + R]; z !== -1; ) {
          var he = b2[x2 + z] === K && b2[D + z] === ee;
          for ($2 = g2[z] + 1; he && $2 <= g2[z] + K - 1; $2++) b2[j2 + h2[$2]] !== F && (he = 0);
          he ? (g2[z] = csFlip(R), b2[S2 + R] += b2[S2 + z], b2[S2 + z] = 0, b2[D + z] = -1, z = b2[w2 + z], b2[w2 + me] = z) : (me = z, z = b2[w2 + z]);
        }
      }
      for ($2 = se, J = se; J < ce; J++) R = h2[J], !((q = -b2[S2 + R]) <= 0) && (b2[S2 + R] = q, ie = b2[O + R] + oe - q, ie = Math.min(ie, u2 - I - q), b2[E + ie] !== -1 && (N2[b2[E + ie]] = R), b2[w2 + R] = b2[E + ie], N2[R] = -1, b2[E + ie] = R, L = Math.min(L, ie), b2[O + R] = ie, h2[$2++] = R);
      b2[S2 + V] = Z, (b2[x2 + V] = $2 - se) === 0 && (g2[V] = -1, b2[j2 + V] = 0), ae !== 0 && (v2 = $2);
    }
    for (R = 0; R < u2; R++) g2[R] = csFlip(g2[R]);
    for (z = 0; z <= u2; z++) b2[E + z] = -1;
    for (z = u2; z >= 0; z--) b2[S2 + z] > 0 || (b2[w2 + z] = b2[E + g2[z]], b2[E + g2[z]] = z);
    for (W = u2; W >= 0; W--) b2[S2 + W] <= 0 || g2[W] !== -1 && (b2[w2 + W] = b2[E + g2[W]], b2[E + g2[W]] = W);
    for (V = 0, R = 0; R <= u2; R++) g2[R] === -1 && (V = csTdfs(R, V, b2, E, w2, y2, j2));
    return y2.splice(y2.length - 1, 1), y2;
  };
  function a2(t5, a3, o3, s3, c3) {
    var l3 = i2(a3);
    if (t5 === 1 && s3 === o3) return n2(a3, l3);
    if (t5 === 2) {
      for (var u2 = l3._index, d2 = l3._ptr, p2 = 0, m2 = 0; m2 < o3; m2++) {
        var h2 = d2[m2];
        if (d2[m2] = p2, !(d2[m2 + 1] - h2 > c3)) for (var g2 = d2[m2 + 1]; h2 < g2; h2++) u2[p2++] = u2[h2];
      }
      return d2[o3] = p2, a3 = i2(l3), r2(l3, a3);
    }
    return r2(l3, a3);
  }
  function o2(t5, n3, r3, i3, a3, o3, s3, l3, u2, d2, p2, m2) {
    for (var h2 = 0; h2 < t5; h2++) r3[i3 + h2] = n3[h2 + 1] - n3[h2];
    r3[i3 + t5] = 0;
    for (var g2 = 0; g2 <= t5; g2++) r3[a3 + g2] = -1, o3[g2] = -1, r3[s3 + g2] = -1, r3[l3 + g2] = -1, r3[u2 + g2] = 1, r3[d2 + g2] = 1, r3[p2 + g2] = 0, r3[m2 + g2] = r3[i3 + g2];
    var v2 = c2(0, 0, r3, d2, t5);
    return r3[p2 + t5] = -2, n3[t5] = -1, r3[d2 + t5] = 0, v2;
  }
  function s2(t5, n3, r3, i3, a3, o3, s3, c3, l3, u2, d2) {
    for (var p2 = 0, m2 = 0; m2 < t5; m2++) {
      var h2 = r3[i3 + m2];
      if (h2 === 0) r3[a3 + m2] = -2, p2++, n3[m2] = -1, r3[o3 + m2] = 0;
      else if (h2 > s3) r3[c3 + m2] = 0, r3[a3 + m2] = -1, p2++, n3[m2] = csFlip(t5), r3[c3 + t5]++;
      else {
        var g2 = r3[l3 + h2];
        g2 !== -1 && (u2[g2] = m2), r3[d2 + m2] = r3[l3 + h2], r3[l3 + h2] = m2;
      }
    }
    return p2;
  }
  function c2(t5, n3, r3, i3, a3) {
    if (t5 < 2 || t5 + n3 < 0) {
      for (var o3 = 0; o3 < a3; o3++) r3[i3 + o3] !== 0 && (r3[i3 + o3] = 1);
      t5 = 2;
    }
    return t5;
  }
  function l2(t5, n3) {
    return t5 !== n3;
  }
});
function csLeaf(t4, n2, r2, i2, a2, o2, s2) {
  var c2, l2, u2 = 0, d2;
  if (t4 <= n2 || r2[i2 + n2] <= r2[a2 + t4]) return -1;
  r2[a2 + t4] = r2[i2 + n2];
  var p2 = r2[o2 + t4];
  if (r2[o2 + t4] = n2, p2 === -1) u2 = 1, d2 = t4;
  else {
    for (u2 = 2, d2 = p2; d2 !== r2[s2 + d2]; d2 = r2[s2 + d2]) ;
    for (c2 = p2; c2 !== d2; c2 = l2) l2 = r2[s2 + c2], r2[s2 + c2] = d2;
  }
  return { jleaf: u2, q: d2 };
}
var name$88 = `csCounts`, dependencies$88 = [`transpose`], createCsCounts = factory(name$88, dependencies$88, (t4) => {
  var { transpose: n2 } = t4;
  return function(t5, r2, i2, a2) {
    if (!t5 || !r2 || !i2) return null;
    var o2 = t5._size, s2 = o2[0], c2 = o2[1], l2, u2, d2, p2, m2, h2, g2, v2 = 4 * c2 + (a2 ? c2 + s2 + 1 : 0), y2 = [], b2 = 0, x2 = c2, S2 = 2 * c2, w2 = 3 * c2, E = 4 * c2, D = 5 * c2 + 1;
    for (d2 = 0; d2 < v2; d2++) y2[d2] = -1;
    var O = [], j2 = n2(t5), M2 = j2._index, N2 = j2._ptr;
    for (d2 = 0; d2 < c2; d2++) for (u2 = i2[d2], O[u2] = +(y2[w2 + u2] === -1); u2 !== -1 && y2[w2 + u2] === -1; u2 = r2[u2]) y2[w2 + u2] = d2;
    if (a2) {
      for (d2 = 0; d2 < c2; d2++) y2[i2[d2]] = d2;
      for (l2 = 0; l2 < s2; l2++) {
        for (d2 = c2, h2 = N2[l2], g2 = N2[l2 + 1], m2 = h2; m2 < g2; m2++) d2 = Math.min(d2, y2[M2[m2]]);
        y2[D + l2] = y2[E + d2], y2[E + d2] = l2;
      }
    }
    for (l2 = 0; l2 < c2; l2++) y2[b2 + l2] = l2;
    for (d2 = 0; d2 < c2; d2++) {
      for (u2 = i2[d2], r2[u2] !== -1 && O[r2[u2]]--, p2 = a2 ? y2[E + d2] : u2; p2 !== -1; p2 = a2 ? y2[D + p2] : -1) for (m2 = N2[p2]; m2 < N2[p2 + 1]; m2++) {
        l2 = M2[m2];
        var F = csLeaf(l2, u2, y2, w2, x2, S2, b2);
        F.jleaf >= 1 && O[u2]++, F.jleaf === 2 && O[F.q]--;
      }
      r2[u2] !== -1 && (y2[b2 + u2] = r2[u2]);
    }
    for (u2 = 0; u2 < c2; u2++) r2[u2] !== -1 && (O[r2[u2]] += O[u2]);
    return O;
  };
}), name$87 = `csSqr`, dependencies$87 = [`add`, `multiply`, `transpose`], createCsSqr = factory(name$87, dependencies$87, (t4) => {
  var { add: n2, multiply: r2, transpose: i2 } = t4, a2 = createCsAmd({ add: n2, multiply: r2, transpose: i2 }), o2 = createCsCounts({ transpose: i2 });
  return function(t5, n3, r3) {
    var i3 = n3._ptr, c2 = n3._size[1], l2, u2 = {};
    if (u2.q = a2(t5, n3), t5 && !u2.q) return null;
    if (r3) {
      var d2 = t5 ? csPermute(n3, null, u2.q) : n3;
      u2.parent = csEtree(d2);
      var p2 = csPost(u2.parent, c2);
      if (u2.cp = o2(d2, u2.parent, p2, 1), d2 && u2.parent && u2.cp && s2(d2, u2)) for (u2.unz = 0, l2 = 0; l2 < c2; l2++) u2.unz += u2.cp[l2];
    } else u2.unz = 4 * i3[c2] + c2, u2.lnz = u2.unz;
    return u2;
  };
  function s2(t5, n3) {
    var r3 = t5._ptr, i3 = t5._index, a3 = t5._size, o3 = a3[0], s3 = a3[1];
    n3.pinv = [], n3.leftmost = [];
    var c2 = n3.parent, l2 = n3.pinv, u2 = n3.leftmost, d2 = [], p2 = 0, m2 = o3, h2 = o3 + s3, g2 = o3 + 2 * s3, v2, y2, b2, x2, S2;
    for (y2 = 0; y2 < s3; y2++) d2[m2 + y2] = -1, d2[h2 + y2] = -1, d2[g2 + y2] = 0;
    for (v2 = 0; v2 < o3; v2++) u2[v2] = -1;
    for (y2 = s3 - 1; y2 >= 0; y2--) for (x2 = r3[y2], S2 = r3[y2 + 1], b2 = x2; b2 < S2; b2++) u2[i3[b2]] = y2;
    for (v2 = o3 - 1; v2 >= 0; v2--) l2[v2] = -1, y2 = u2[v2], y2 !== -1 && (d2[g2 + y2]++ === 0 && (d2[h2 + y2] = v2), d2[p2 + v2] = d2[m2 + y2], d2[m2 + y2] = v2);
    for (n3.lnz = 0, n3.m2 = o3, y2 = 0; y2 < s3; y2++) if (v2 = d2[m2 + y2], n3.lnz++, v2 < 0 && (v2 = n3.m2++), l2[v2] = y2, !(--g2[y2] <= 0)) {
      n3.lnz += d2[g2 + y2];
      var w2 = c2[y2];
      w2 !== -1 && (d2[g2 + w2] === 0 && (d2[h2 + w2] = d2[h2 + y2]), d2[p2 + d2[h2 + y2]] = d2[m2 + w2], d2[m2 + w2] = d2[p2 + v2], d2[g2 + w2] += d2[g2 + y2]);
    }
    for (v2 = 0; v2 < o3; v2++) l2[v2] < 0 && (l2[v2] = y2++);
    return true;
  }
});
function csMarked(t4, n2) {
  return t4[n2] < 0;
}
function csMark(t4, n2) {
  t4[n2] = csFlip(t4[n2]);
}
function csUnflip(t4) {
  return t4 < 0 ? csFlip(t4) : t4;
}
function csDfs(t4, n2, r2, i2, a2) {
  var o2 = n2._index, s2 = n2._ptr, c2 = n2._size[1], l2, u2, d2, p2 = 0;
  for (i2[0] = t4; p2 >= 0; ) {
    t4 = i2[p2];
    var m2 = a2 ? a2[t4] : t4;
    csMarked(s2, t4) || (csMark(s2, t4), i2[c2 + p2] = m2 < 0 ? 0 : csUnflip(s2[m2]));
    var h2 = 1;
    for (u2 = i2[c2 + p2], d2 = m2 < 0 ? 0 : csUnflip(s2[m2 + 1]); u2 < d2; u2++) if (l2 = o2[u2], !csMarked(s2, l2)) {
      i2[c2 + p2] = u2, i2[++p2] = l2, h2 = 0;
      break;
    }
    h2 && (p2--, i2[--r2] = t4);
  }
  return r2;
}
function csReach(t4, n2, r2, i2, a2) {
  var o2 = t4._ptr, s2 = t4._size, c2 = n2._index, l2 = n2._ptr, u2 = s2[1], d2, p2, m2, h2 = u2;
  for (p2 = l2[r2], m2 = l2[r2 + 1], d2 = p2; d2 < m2; d2++) {
    var g2 = c2[d2];
    csMarked(o2, g2) || (h2 = csDfs(g2, t4, h2, i2, a2));
  }
  for (d2 = h2; d2 < u2; d2++) csMark(o2, i2[d2]);
  return h2;
}
var name$86 = `csSpsolve`, dependencies$86 = [`divideScalar`, `multiply`, `subtract`], createCsSpsolve = factory(name$86, dependencies$86, (t4) => {
  var { divideScalar: n2, multiply: r2, subtract: i2 } = t4;
  return function(t5, a2, o2, s2, c2, l2, u2) {
    var d2 = t5._values, p2 = t5._index, m2 = t5._ptr, h2 = t5._size[1], g2 = a2._values, v2 = a2._index, y2 = a2._ptr, b2, x2, S2, w2, E = csReach(t5, a2, o2, s2, l2);
    for (b2 = E; b2 < h2; b2++) c2[s2[b2]] = 0;
    for (x2 = y2[o2], S2 = y2[o2 + 1], b2 = x2; b2 < S2; b2++) c2[v2[b2]] = g2[b2];
    for (var D = E; D < h2; D++) {
      var O = s2[D], j2 = l2 ? l2[O] : O;
      if (!(j2 < 0)) for (x2 = m2[j2], S2 = m2[j2 + 1], c2[O] = n2(c2[O], d2[u2 ? x2 : S2 - 1]), b2 = u2 ? x2 + 1 : x2, w2 = u2 ? S2 : S2 - 1; b2 < w2; b2++) {
        var M2 = p2[b2];
        c2[M2] = i2(c2[M2], r2(d2[b2], c2[O]));
      }
    }
    return E;
  };
}), name$85 = `csLu`, dependencies$85 = [`abs`, `divideScalar`, `multiply`, `subtract`, `larger`, `largerEq`, `SparseMatrix`], createCsLu = factory(name$85, dependencies$85, (t4) => {
  var { abs: n2, divideScalar: r2, multiply: i2, subtract: a2, larger: o2, largerEq: s2, SparseMatrix: c2 } = t4, l2 = createCsSpsolve({ divideScalar: r2, multiply: i2, subtract: a2 });
  return function(t5, a3, u2) {
    if (!t5) return null;
    var d2 = t5._size[1], p2, m2 = 100, h2 = 100;
    a3 && (p2 = a3.q, m2 = a3.lnz || m2, h2 = a3.unz || h2);
    var g2 = [], v2 = [], y2 = [], b2 = new c2({ values: g2, index: v2, ptr: y2, size: [d2, d2] }), x2 = [], S2 = [], w2 = [], E = new c2({ values: x2, index: S2, ptr: w2, size: [d2, d2] }), D = [], O, j2, M2 = [], N2 = [];
    for (O = 0; O < d2; O++) M2[O] = 0, D[O] = -1, y2[O + 1] = 0;
    m2 = 0, h2 = 0;
    for (var F = 0; F < d2; F++) {
      y2[F] = m2, w2[F] = h2;
      var I = p2 ? p2[F] : F, L = l2(b2, t5, I, N2, M2, D, 1), R = -1, z = -1;
      for (j2 = L; j2 < d2; j2++) if (O = N2[j2], D[O] < 0) {
        var V = n2(M2[O]);
        o2(V, z) && (z = V, R = O);
      } else S2[h2] = D[O], x2[h2++] = M2[O];
      if (R === -1 || z <= 0) return null;
      D[I] < 0 && s2(n2(M2[I]), i2(z, u2)) && (R = I);
      var H2 = M2[R];
      for (S2[h2] = F, x2[h2++] = H2, D[R] = F, v2[m2] = R, g2[m2++] = 1, j2 = L; j2 < d2; j2++) O = N2[j2], D[O] < 0 && (v2[m2] = O, g2[m2++] = r2(M2[O], H2)), M2[O] = 0;
    }
    for (y2[d2] = m2, w2[d2] = h2, j2 = 0; j2 < m2; j2++) v2[j2] = D[v2[j2]];
    return g2.splice(m2, g2.length - m2), v2.splice(m2, v2.length - m2), x2.splice(h2, x2.length - h2), S2.splice(h2, S2.length - h2), { L: b2, U: E, pinv: D };
  };
}), name$84 = `slu`, dependencies$84 = [`typed`, `abs`, `add`, `multiply`, `transpose`, `divideScalar`, `subtract`, `larger`, `largerEq`, `SparseMatrix`], createSlu = factory(name$84, dependencies$84, (t4) => {
  var { typed: n2, abs: r2, add: i2, multiply: a2, transpose: o2, divideScalar: s2, subtract: c2, larger: l2, largerEq: u2, SparseMatrix: d2 } = t4, p2 = createCsSqr({ add: i2, multiply: a2, transpose: o2 }), m2 = createCsLu({ abs: r2, divideScalar: s2, multiply: a2, subtract: c2, larger: l2, largerEq: u2, SparseMatrix: d2 });
  return n2(name$84, { "SparseMatrix, number, number": function(t5, n3, r3) {
    if (!isInteger(n3) || n3 < 0 || n3 > 3) throw Error(`Symbolic Ordering and Analysis order must be an integer number in the interval [0, 3]`);
    if (r3 < 0 || r3 > 1) throw Error(`Partial pivoting threshold must be a number from 0 to 1`);
    var i3 = p2(n3, t5, false), a3 = m2(t5, i3, r3);
    return { L: a3.L, U: a3.U, p: a3.pinv, q: i3.q, toString: function() {
      return `L: ` + this.L.toString() + `
U: ` + this.U.toString() + `
p: ` + this.p.toString() + (this.q ? `
q: ` + this.q.toString() : ``) + `
`;
    } };
  } });
});
function csIpvec(t4, n2) {
  var r2, i2 = n2.length, a2 = [];
  if (t4) for (r2 = 0; r2 < i2; r2++) a2[t4[r2]] = n2[r2];
  else for (r2 = 0; r2 < i2; r2++) a2[r2] = n2[r2];
  return a2;
}
var name$83 = `lusolve`, dependencies$83 = [`typed`, `matrix`, `lup`, `slu`, `usolve`, `lsolve`, `DenseMatrix`], createLusolve = factory(name$83, dependencies$83, (t4) => {
  var { typed: n2, matrix: r2, lup: i2, slu: a2, usolve: o2, lsolve: s2, DenseMatrix: c2 } = t4, l2 = createSolveValidation({ DenseMatrix: c2 });
  return n2(name$83, { "Array, Array | Matrix": function(t5, n3) {
    t5 = r2(t5);
    var a3 = i2(t5);
    return d2(a3.L, a3.U, a3.p, null, n3).valueOf();
  }, "DenseMatrix, Array | Matrix": function(t5, n3) {
    var r3 = i2(t5);
    return d2(r3.L, r3.U, r3.p, null, n3);
  }, "SparseMatrix, Array | Matrix": function(t5, n3) {
    var r3 = i2(t5);
    return d2(r3.L, r3.U, r3.p, null, n3);
  }, "SparseMatrix, Array | Matrix, number, number": function(t5, n3, r3, i3) {
    var o3 = a2(t5, r3, i3);
    return d2(o3.L, o3.U, o3.p, o3.q, n3);
  }, "Object, Array | Matrix": function(t5, n3) {
    return d2(t5.L, t5.U, t5.p, t5.q, n3);
  } });
  function u2(t5) {
    if (isMatrix(t5)) return t5;
    if (isArray(t5)) return r2(t5);
    throw TypeError(`Invalid Matrix LU decomposition`);
  }
  function d2(t5, n3, r3, i3, a3) {
    t5 = u2(t5), n3 = u2(n3), r3 && (a3 = l2(t5, a3, true), a3._data = csIpvec(r3, a3._data));
    var c3 = s2(t5, a3), d3 = o2(n3, c3);
    return i3 && (d3._data = csIpvec(i3, d3._data)), d3;
  }
}), name$82 = `polynomialRoot`, dependencies$82 = [`typed`, `isZero`, `equalScalar`, `add`, `subtract`, `multiply`, `divide`, `sqrt`, `unaryMinus`, `cbrt`, `typeOf`, `im`, `re`], createPolynomialRoot = factory(name$82, dependencies$82, (t4) => {
  var { typed: n2, isZero: r2, equalScalar: i2, add: a2, subtract: o2, multiply: s2, divide: c2, sqrt: l2, unaryMinus: u2, cbrt: d2, typeOf: p2, im: m2, re: h2 } = t4;
  return n2(name$82, { "number|Complex, ...number|Complex": (t5, n3) => {
    for (var g2 = [t5, ...n3]; g2.length > 0 && r2(g2[g2.length - 1]); ) g2.pop();
    if (g2.length < 2) throw RangeError(`Polynomial [${t5}, ${n3}] must have a non-zero non-constant coefficient`);
    switch (g2.length) {
      case 2:
        return [u2(c2(g2[0], g2[1]))];
      case 3:
        var [v2, y2, b2] = g2, x2 = s2(2, b2), S2 = s2(y2, y2), w2 = s2(4, b2, v2);
        if (i2(S2, w2)) return [c2(u2(y2), x2)];
        var E = l2(o2(S2, w2));
        return [c2(o2(E, y2), x2), c2(o2(u2(E), y2), x2)];
      case 4:
        var [D, O, j2, M2] = g2, N2 = u2(s2(3, M2)), F = s2(j2, j2), I = s2(3, M2, O), L = a2(s2(2, j2, j2, j2), s2(27, M2, M2, D)), R = s2(9, M2, j2, O);
        if (i2(F, I) && i2(L, R)) return [c2(j2, N2)];
        var z = o2(F, I), V = o2(L, R);
        return i2(a2(s2(18, M2, j2, O, D), s2(j2, j2, O, O)), a2(s2(4, j2, j2, j2, D), s2(4, M2, O, O, O), s2(27, M2, M2, D, D))) ? [c2(o2(s2(4, M2, j2, O), a2(s2(9, M2, M2, D), s2(j2, j2, j2))), s2(M2, z)), c2(o2(s2(9, M2, D), s2(j2, O)), s2(2, z))] : d2(i2(F, I) ? V : c2(a2(V, l2(o2(s2(V, V), s2(4, z, z, z)))), 2), true).toArray().map((t6) => c2(a2(j2, t6, c2(z, t6)), N2)).map((t6) => p2(t6) === `Complex` && i2(h2(t6), h2(t6) + m2(t6)) ? h2(t6) : t6);
      default:
        throw RangeError(`only implemented for cubic or lower-order polynomials, not ${g2}`);
    }
  } });
}), name$81 = `Help`, dependencies$81 = [`evaluate`], createHelpClass = factory(name$81, dependencies$81, (t4) => {
  var { evaluate: n2 } = t4;
  function r2(t5) {
    if (!(this instanceof r2)) throw SyntaxError(`Constructor must be called with the new operator`);
    if (!t5) throw Error(`Argument "doc" missing`);
    this.doc = t5;
  }
  return r2.prototype.type = `Help`, r2.prototype.isHelp = true, r2.prototype.toString = function() {
    var t5 = this.doc || {}, r3 = `
`;
    if (t5.name && (r3 += `Name: ` + t5.name + `

`), t5.category && (r3 += `Category: ` + t5.category + `

`), t5.description && (r3 += `Description:
    ` + t5.description + `

`), t5.syntax && (r3 += `Syntax:
    ` + t5.syntax.join(`
    `) + `

`), t5.examples) {
      r3 += `Examples:
`;
      for (var i2 = false, a2 = n2(`config()`), o2 = { config: (t6) => (i2 = true, n2(`config(newConfig)`, { newConfig: t6 })) }, s2 = 0; s2 < t5.examples.length; s2++) {
        var c2 = t5.examples[s2];
        r3 += `    ` + c2 + `
`;
        var l2 = void 0;
        try {
          l2 = n2(c2, o2);
        } catch (t6) {
          l2 = t6;
        }
        l2 !== void 0 && !isHelp(l2) && (r3 += `        ` + format(l2, { precision: 14 }) + `
`);
      }
      r3 += `
`, i2 && n2(`config(originalConfig)`, { originalConfig: a2 });
    }
    return t5.mayThrow && t5.mayThrow.length && (r3 += `Throws: ` + t5.mayThrow.join(`, `) + `

`), t5.seealso && t5.seealso.length && (r3 += `See also: ` + t5.seealso.join(`, `) + `
`), r3;
  }, r2.prototype.toJSON = function() {
    var t5 = clone$3(this.doc);
    return t5.mathjs = `Help`, t5;
  }, r2.fromJSON = function(t5) {
    var n3 = {};
    return Object.keys(t5).filter((t6) => t6 !== `mathjs`).forEach((r3) => {
      n3[r3] = t5[r3];
    }), new r2(n3);
  }, r2.prototype.valueOf = r2.prototype.toString, r2;
}, { isClass: true }), name$80 = `Chain`, dependencies$80 = [`?on`, `math`, `typed`], createChainClass = factory(name$80, dependencies$80, (t4) => {
  var { on: n2, math: r2, typed: i2 } = t4;
  function a2(t5) {
    if (!(this instanceof a2)) throw SyntaxError(`Constructor must be called with the new operator`);
    isChain(t5) ? this.value = t5.value : this.value = t5;
  }
  a2.prototype.type = `Chain`, a2.prototype.isChain = true, a2.prototype.done = function() {
    return this.value;
  }, a2.prototype.valueOf = function() {
    return this.value;
  }, a2.prototype.toString = function() {
    return format(this.value);
  }, a2.prototype.toJSON = function() {
    return { mathjs: `Chain`, value: this.value };
  }, a2.fromJSON = function(t5) {
    return new a2(t5.value);
  };
  function o2(t5, n3) {
    typeof n3 == `function` && (a2.prototype[t5] = c2(n3));
  }
  function s2(t5, n3) {
    lazy(a2.prototype, t5, function() {
      var t6 = n3();
      if (typeof t6 == `function`) return c2(t6);
    });
  }
  function c2(t5) {
    return function() {
      if (arguments.length === 0) return new a2(t5(this.value));
      for (var n3 = [this.value], r3 = 0; r3 < arguments.length; r3++) n3[r3 + 1] = arguments[r3];
      if (i2.isTypedFunction(t5)) {
        var o3 = i2.resolve(t5, n3);
        if (o3.params.length === 1) throw Error(`chain function ` + t5.name + ` cannot match rest parameter between chain value and additional arguments.`);
        return new a2(o3.implementation.apply(t5, n3));
      }
      return new a2(t5.apply(t5, n3));
    };
  }
  a2.createProxy = function(t5, n3) {
    if (typeof t5 == `string`) o2(t5, n3);
    else {
      var r3 = function(n4) {
        hasOwnProperty$1(t5, n4) && l2[n4] === void 0 && s2(n4, () => t5[n4]);
      };
      for (var i3 in t5) r3(i3);
    }
  };
  var l2 = { expression: true, docs: true, type: true, classes: true, json: true, error: true, isChain: true };
  return a2.createProxy(r2), n2 && n2(`import`, function(t5, n3, r3) {
    r3 || s2(t5, n3);
  }), a2;
}, { isClass: true }), eDocs = { name: `e`, category: `Constants`, syntax: [`e`], description: `Euler's number, the base of the natural logarithm. Approximately equal to 2.71828`, examples: [`e`, `e ^ 2`, `exp(2)`, `log(e)`], seealso: [`exp`] }, falseDocs = { name: `false`, category: `Constants`, syntax: [`false`], description: `Boolean value false`, examples: [`false`], seealso: [`true`] }, iDocs = { name: `i`, category: `Constants`, syntax: [`i`], description: `Imaginary unit, defined as i*i=-1. A complex number is described as a + b*i, where a is the real part, and b is the imaginary part.`, examples: [`i`, `i * i`, `sqrt(-1)`], seealso: [] }, InfinityDocs = { name: `Infinity`, category: `Constants`, syntax: [`Infinity`], description: `Infinity, a number which is larger than the maximum number that can be handled by a floating point number.`, examples: [`Infinity`, `1 / 0`], seealso: [] }, LN10Docs = { name: `LN10`, category: `Constants`, syntax: [`LN10`], description: `Returns the natural logarithm of 10, approximately equal to 2.302`, examples: [`LN10`, `log(10)`], seealso: [] }, LN2Docs = { name: `LN2`, category: `Constants`, syntax: [`LN2`], description: `Returns the natural logarithm of 2, approximately equal to 0.693`, examples: [`LN2`, `log(2)`], seealso: [] }, LOG10EDocs = { name: `LOG10E`, category: `Constants`, syntax: [`LOG10E`], description: `Returns the base-10 logarithm of E, approximately equal to 0.434`, examples: [`LOG10E`, `log(e, 10)`], seealso: [] }, LOG2EDocs = { name: `LOG2E`, category: `Constants`, syntax: [`LOG2E`], description: `Returns the base-2 logarithm of E, approximately equal to 1.442`, examples: [`LOG2E`, `log(e, 2)`], seealso: [] }, NaNDocs = { name: `NaN`, category: `Constants`, syntax: [`NaN`], description: `Not a number`, examples: [`NaN`, `0 / 0`], seealso: [] }, nullDocs = { name: `null`, category: `Constants`, syntax: [`null`], description: `Value null`, examples: [`null`], seealso: [`true`, `false`] }, phiDocs = { name: `phi`, category: `Constants`, syntax: [`phi`], description: "Phi is the golden ratio. Two quantities are in the golden ratio if their ratio is the same as the ratio of their sum to the larger of the two quantities. Phi is defined as `(1 + sqrt(5)) / 2` and is approximately 1.618034...", examples: [`phi`], seealso: [] }, piDocs = { name: `pi`, category: `Constants`, syntax: [`pi`], description: `The number pi is a mathematical constant that is the ratio of a circle's circumference to its diameter, and is approximately equal to 3.14159`, examples: [`pi`, `sin(pi/2)`], seealso: [`tau`] }, SQRT12Docs = { name: `SQRT1_2`, category: `Constants`, syntax: [`SQRT1_2`], description: `Returns the square root of 1/2, approximately equal to 0.707`, examples: [`SQRT1_2`, `sqrt(1/2)`], seealso: [] }, SQRT2Docs = { name: `SQRT2`, category: `Constants`, syntax: [`SQRT2`], description: `Returns the square root of 2, approximately equal to 1.414`, examples: [`SQRT2`, `sqrt(2)`], seealso: [] }, tauDocs = { name: `tau`, category: `Constants`, syntax: [`tau`], description: `Tau is the ratio constant of a circle's circumference to radius, equal to 2 * pi, approximately 6.2832.`, examples: [`tau`, `2 * pi`], seealso: [`pi`] }, trueDocs = { name: `true`, category: `Constants`, syntax: [`true`], description: `Boolean value true`, examples: [`true`], seealso: [`false`] }, versionDocs = { name: `version`, category: `Constants`, syntax: [`version`], description: `A string with the version number of math.js`, examples: [`version`], seealso: [] }, bignumberDocs = { name: `bignumber`, category: `Construction`, syntax: [`bignumber(x)`], description: `Create a big number from a number or string.`, examples: [`0.1 + 0.2`, `bignumber(0.1) + bignumber(0.2)`, `bignumber("7.2")`, `bignumber("7.2e500")`, `bignumber([0.1, 0.2, 0.3])`], seealso: [`boolean`, `bigint`, `complex`, `fraction`, `index`, `matrix`, `string`, `unit`] }, bigintDocs = { name: `bigint`, category: `Construction`, syntax: [`bigint(x)`], description: `Create a bigint, an integer with an arbitrary number of digits, from a number or string.`, examples: [`123123123123123123 # a large number will lose digits`, `bigint("123123123123123123")`, `bignumber(["1", "3", "5"])`], seealso: [`boolean`, `bignumber`, `number`, `complex`, `fraction`, `index`, `matrix`, `string`, `unit`] }, booleanDocs = { name: `boolean`, category: `Construction`, syntax: [`x`, `boolean(x)`], description: `Convert a string or number into a boolean.`, examples: [`boolean(0)`, `boolean(1)`, `boolean(3)`, `boolean("true")`, `boolean("false")`, `boolean([1, 0, 1, 1])`], seealso: [`bignumber`, `complex`, `index`, `matrix`, `number`, `string`, `unit`] }, complexDocs = { name: `complex`, category: `Construction`, syntax: [`complex()`, `complex(re, im)`, `complex(string)`], description: `Create a complex number.`, examples: [`complex()`, `complex(2, 3)`, `complex("7 - 2i")`], seealso: [`bignumber`, `boolean`, `index`, `matrix`, `number`, `string`, `unit`] }, createUnitDocs = { name: `createUnit`, category: `Construction`, syntax: [`createUnit(definitions)`, `createUnit(name, definition)`], description: `Create a user-defined unit and register it with the Unit type.`, examples: [`createUnit("foo")`, `createUnit("knot", {definition: "0.514444444 m/s", aliases: ["knots", "kt", "kts"]})`, `createUnit("mph", "1 mile/hour")`], seealso: [`unit`, `splitUnit`] }, fractionDocs = { name: `fraction`, category: `Construction`, syntax: [`fraction(num)`, `fraction(matrix)`, `fraction(num,den)`, `fraction({n: num, d: den})`], description: `Create a fraction from a number or from integer numerator and denominator.`, examples: [`fraction(0.125)`, `fraction(1, 3) + fraction(2, 5)`, `fraction({n: 333, d: 53})`, `fraction([sqrt(9), sqrt(10), sqrt(11)])`], seealso: [`bignumber`, `boolean`, `complex`, `index`, `matrix`, `string`, `unit`] }, indexDocs = { name: `index`, category: `Construction`, syntax: [`[start]`, `[start:end]`, `[start:step:end]`, `[start1, start 2, ...]`, `[start1:end1, start2:end2, ...]`, `[start1:step1:end1, start2:step2:end2, ...]`], description: `Create an index to get or replace a subset of a matrix`, examples: [`A = [1, 2, 3; 4, 5, 6]`, `A[1, :]`, `A[1, 2] = 50`, `A[1:2, 1:2] = 1`, `B = [1, 2, 3]`, `B[B>1 and B<3]`], seealso: [`bignumber`, `boolean`, `complex`, `matrix`, `number`, `range`, `string`, `unit`] }, matrixDocs = { name: `matrix`, category: `Construction`, syntax: [`[]`, `[a1, b1, ...; a2, b2, ...]`, `matrix()`, `matrix("dense")`, `matrix([...])`], description: `Create a matrix.`, examples: [`[]`, `[1, 2, 3]`, `[1, 2, 3; 4, 5, 6]`, `matrix()`, `matrix([3, 4])`, `matrix([3, 4; 5, 6], "sparse")`, `matrix([3, 4; 5, 6], "sparse", "number")`], seealso: [`bignumber`, `boolean`, `complex`, `index`, `number`, `string`, `unit`, `sparse`] }, numberDocs = { name: `number`, category: `Construction`, syntax: [`x`, `number(x)`, `number(unit, valuelessUnit)`], description: `Create a number or convert a string or boolean into a number.`, examples: [`2`, `2e3`, `4.05`, `number(2)`, `number("7.2")`, `number(true)`, `number([true, false, true, true])`, `number(unit("52cm"), "m")`], seealso: [`bignumber`, `bigint`, `boolean`, `complex`, `fraction`, `index`, `matrix`, `string`, `unit`] }, sparseDocs = { name: `sparse`, category: `Construction`, syntax: [`sparse()`, `sparse([a1, b1, ...; a1, b2, ...])`, `sparse([a1, b1, ...; a1, b2, ...], "number")`], description: `Create a sparse matrix.`, examples: [`sparse()`, `sparse([3, 4; 5, 6])`, `sparse([3, 0; 5, 0], "number")`], seealso: [`bignumber`, `boolean`, `complex`, `index`, `number`, `string`, `unit`, `matrix`] }, splitUnitDocs = { name: `splitUnit`, category: `Construction`, syntax: [`splitUnit(unit: Unit, parts: Unit[])`], description: `Split a unit in an array of units whose sum is equal to the original unit.`, examples: [`splitUnit(1 m, ["feet", "inch"])`], seealso: [`unit`, `createUnit`] }, stringDocs = { name: `string`, category: `Construction`, syntax: [`"text"`, `string(x)`], description: `Create a string or convert a value to a string`, examples: [`"Hello World!"`, `string(4.2)`, `string(3 + 2i)`], seealso: [`bignumber`, `boolean`, `complex`, `index`, `matrix`, `number`, `unit`] }, unitDocs = { name: `unit`, category: `Construction`, syntax: [`value unit`, `unit(value, unit)`, `unit(string)`], description: `Create a unit.`, examples: [`5.5 mm`, `3 inch`, `unit(7.1, "kilogram")`, `unit("23 deg")`], seealso: [`bignumber`, `boolean`, `complex`, `index`, `matrix`, `number`, `string`] }, configDocs = { name: `config`, category: `Core`, syntax: [`config()`, `config(options)`], description: `Get configuration or change configuration.`, examples: [`config()`, `1/3 + 1/4`, `config({number: "Fraction"})`, `1/3 + 1/4`], seealso: [] }, importDocs = { name: `import`, category: `Core`, syntax: [`import(functions)`, `import(functions, options)`], description: `Import functions or constants from an object.`, examples: [`import({myFn: f(x)=x^2, myConstant: 32 })`, `myFn(2)`, `myConstant`], seealso: [] }, typedDocs = { name: `typed`, category: `Core`, syntax: [`typed(signatures)`, `typed(name, signatures)`], description: `Create a typed function.`, examples: [`double = typed({ "number": f(x)=x+x, "string": f(x)=concat(x,x) })`, `double(2)`, `double("hello")`], seealso: [] }, derivativeDocs = { name: `derivative`, category: `Algebra`, syntax: [`derivative(expr, variable)`, `derivative(expr, variable, {simplify: boolean})`], description: `Takes the derivative of an expression expressed in parser Nodes. The derivative will be taken over the supplied variable in the second parameter. If there are multiple variables in the expression, it will return a partial derivative.`, examples: [`derivative("2x^3", "x")`, `derivative("2x^3", "x", {simplify: false})`, `derivative("2x^2 + 3x + 4", "x")`, `derivative("sin(2x)", "x")`, `f = parse("x^2 + x")`, `x = parse("x")`, `df = derivative(f, x)`, `df.evaluate({x: 3})`], seealso: [`simplify`, `parse`, `evaluate`] }, leafCountDocs = { name: `leafCount`, category: `Algebra`, syntax: [`leafCount(expr)`], description: `Computes the number of leaves in the parse tree of the given expression`, examples: [`leafCount("e^(i*pi)-1")`, `leafCount(parse("{a: 22/7, b: 10^(1/2)}"))`], seealso: [`simplify`] }, lsolveDocs = { name: `lsolve`, category: `Algebra`, syntax: [`x=lsolve(L, b)`], description: `Finds one solution of the linear system L * x = b where L is an [n x n] lower triangular matrix and b is a [n] column vector.`, examples: [`a = [-2, 3; 2, 1]`, `b = [11, 9]`, `x = lsolve(a, b)`], seealso: [`lsolveAll`, `lup`, `lusolve`, `usolve`, `matrix`, `sparse`] }, lsolveAllDocs = { name: `lsolveAll`, category: `Algebra`, syntax: [`x=lsolveAll(L, b)`], description: `Finds all solutions of the linear system L * x = b where L is an [n x n] lower triangular matrix and b is a [n] column vector.`, examples: [`a = [-2, 3; 2, 1]`, `b = [11, 9]`, `x = lsolve(a, b)`], seealso: [`lsolve`, `lup`, `lusolve`, `usolve`, `matrix`, `sparse`] }, lupDocs = { name: `lup`, category: `Algebra`, syntax: [`lup(m)`], description: `Calculate the Matrix LU decomposition with partial pivoting. Matrix A is decomposed in three matrices (L, U, P) where P * A = L * U`, examples: [`lup([[2, 1], [1, 4]])`, `lup(matrix([[2, 1], [1, 4]]))`, `lup(sparse([[2, 1], [1, 4]]))`], seealso: [`lusolve`, `lsolve`, `usolve`, `matrix`, `sparse`, `slu`, `qr`] }, lusolveDocs = { name: `lusolve`, category: `Algebra`, syntax: [`x=lusolve(A, b)`, `x=lusolve(lu, b)`], description: `Solves the linear system A * x = b where A is an [n x n] matrix and b is a [n] column vector.`, examples: [`a = [-2, 3; 2, 1]`, `b = [11, 9]`, `x = lusolve(a, b)`], seealso: [`lup`, `slu`, `lsolve`, `usolve`, `matrix`, `sparse`] }, polynomialRootDocs = { name: `polynomialRoot`, category: `Algebra`, syntax: [`x=polynomialRoot(-6, 3)`, `x=polynomialRoot(4, -4, 1)`, `x=polynomialRoot(-8, 12, -6, 1)`], description: `Finds the roots of a univariate polynomial given by its coefficients starting from constant, linear, and so on, increasing in degree.`, examples: [`a = polynomialRoot(-6, 11, -6, 1)`], seealso: [`cbrt`, `sqrt`] }, qrDocs = { name: `qr`, category: `Algebra`, syntax: [`qr(A)`], description: "Calculates the Matrix QR decomposition. Matrix `A` is decomposed in two matrices (`Q`, `R`) where `Q` is an orthogonal matrix and `R` is an upper triangular matrix.", examples: [`qr([[1, -1,  4], [1,  4, -2], [1,  4,  2], [1,  -1, 0]])`], seealso: [`lup`, `slu`, `matrix`] }, rationalizeDocs = { name: `rationalize`, category: `Algebra`, syntax: [`rationalize(expr)`, `rationalize(expr, scope)`, `rationalize(expr, scope, detailed)`], description: `Transform a rationalizable expression in a rational fraction. If rational fraction is one variable polynomial then converts the numerator and denominator in canonical form, with decreasing exponents, returning the coefficients of numerator.`, examples: [`rationalize("2x/y - y/(x+1)")`, `rationalize("2x/y - y/(x+1)", true)`], seealso: [`simplify`] }, resolveDocs = { name: `resolve`, category: `Algebra`, syntax: [`resolve(node, scope)`], description: `Recursively substitute variables in an expression tree.`, examples: [`resolve(parse("1 + x"), { x: 7 })`, `resolve(parse("size(text)"), { text: "Hello World" })`, `resolve(parse("x + y"), { x: parse("3z") })`, `resolve(parse("3x"), { x: parse("y+z"), z: parse("w^y") })`], seealso: [`simplify`, `evaluate`], mayThrow: [`ReferenceError`] }, simplifyDocs = { name: `simplify`, category: `Algebra`, syntax: [`simplify(expr)`, `simplify(expr, rules)`], description: `Simplify an expression tree.`, examples: [`simplify("3 + 2 / 4")`, `simplify("2x + x")`, `f = parse("x * (x + 2 + x)")`, `simplified = simplify(f)`, `simplified.evaluate({x: 2})`], seealso: [`simplifyCore`, `derivative`, `evaluate`, `parse`, `rationalize`, `resolve`] }, simplifyConstantDocs = { name: `simplifyConstant`, category: `Algebra`, syntax: [`simplifyConstant(expr)`, `simplifyConstant(expr, options)`], description: `Replace constant subexpressions of node with their values.`, examples: [`simplifyConstant("(3-3)*x")`, `simplifyConstant(parse("z-cos(tau/8)"))`], seealso: [`simplify`, `simplifyCore`, `evaluate`] }, simplifyCoreDocs = { name: `simplifyCore`, category: `Algebra`, syntax: [`simplifyCore(node)`], description: `Perform simple one-pass simplifications on an expression tree.`, examples: [`simplifyCore(parse("0*x"))`, `simplifyCore(parse("(x+0)*2"))`], seealso: [`simplify`, `simplifyConstant`, `evaluate`] }, sluDocs = { name: `slu`, category: `Algebra`, syntax: [`slu(A, order, threshold)`], description: `Calculate the Matrix LU decomposition with full pivoting. Matrix A is decomposed in two matrices (L, U) and two permutation vectors (pinv, q) where P * A * Q = L * U`, examples: [`slu(sparse([4.5, 0, 3.2, 0; 3.1, 2.9, 0, 0.9; 0, 1.7, 3, 0; 3.5, 0.4, 0, 1]), 1, 0.001)`], seealso: [`lusolve`, `lsolve`, `usolve`, `matrix`, `sparse`, `lup`, `qr`] }, symbolicEqualDocs = { name: `symbolicEqual`, category: `Algebra`, syntax: [`symbolicEqual(expr1, expr2)`, `symbolicEqual(expr1, expr2, options)`], description: `Returns true if the difference of the expressions simplifies to 0`, examples: [`symbolicEqual("x*y","y*x")`, `symbolicEqual("abs(x^2)", "x^2")`, `symbolicEqual("abs(x)", "x", {context: {abs: {trivial: true}}})`], seealso: [`simplify`, `evaluate`] }, usolveDocs = { name: `usolve`, category: `Algebra`, syntax: [`x=usolve(U, b)`], description: `Finds one solution of the linear system U * x = b where U is an [n x n] upper triangular matrix and b is a [n] column vector.`, examples: [`x=usolve(sparse([1, 1, 1, 1; 0, 1, 1, 1; 0, 0, 1, 1; 0, 0, 0, 1]), [1; 2; 3; 4])`], seealso: [`usolveAll`, `lup`, `lusolve`, `lsolve`, `matrix`, `sparse`] }, usolveAllDocs = { name: `usolveAll`, category: `Algebra`, syntax: [`x=usolve(U, b)`], description: `Finds all solutions of the linear system U * x = b where U is an [n x n] upper triangular matrix and b is a [n] column vector.`, examples: [`x=usolve(sparse([1, 1, 1, 1; 0, 1, 1, 1; 0, 0, 1, 1; 0, 0, 0, 1]), [1; 2; 3; 4])`], seealso: [`usolve`, `lup`, `lusolve`, `lsolve`, `matrix`, `sparse`] }, absDocs = { name: `abs`, category: `Arithmetic`, syntax: [`abs(x)`], description: `Compute the absolute value.`, examples: [`abs(3.5)`, `abs(-4.2)`], seealso: [`sign`] }, addDocs = { name: `add`, category: `Operators`, syntax: [`x + y`, `add(x, y)`], description: `Add two values.`, examples: [`a = 2.1 + 3.6`, `a - 3.6`, `3 + 2i`, `3 cm + 2 inch`, `"2.3" + "4"`], seealso: [`subtract`] }, cbrtDocs = { name: `cbrt`, category: `Arithmetic`, syntax: [`cbrt(x)`, `cbrt(x, allRoots)`], description: "Compute the cubic root value. If x = y * y * y, then y is the cubic root of x. When `x` is a number or complex number, an optional second argument `allRoots` can be provided to return all three cubic roots. If not provided, the principal root is returned", examples: [`cbrt(64)`, `cube(4)`, `cbrt(-8)`, `cbrt(2 + 3i)`, `cbrt(8i)`, `cbrt(8i, true)`, `cbrt(27 m^3)`], seealso: [`square`, `sqrt`, `cube`, `multiply`] }, ceilDocs = { name: `ceil`, category: `Arithmetic`, syntax: [`ceil(x)`, `ceil(x, n)`, `ceil(unit, valuelessUnit)`, `ceil(unit, n, valuelessUnit)`], description: `Round a value towards plus infinity. If x is complex, both real and imaginary part are rounded towards plus infinity.`, examples: [`ceil(3.2)`, `ceil(3.8)`, `ceil(-4.2)`, `ceil(3.241cm, cm)`, `ceil(3.241cm, 2, cm)`], seealso: [`floor`, `fix`, `round`] }, cubeDocs = { name: `cube`, category: `Arithmetic`, syntax: [`cube(x)`], description: `Compute the cube of a value. The cube of x is x * x * x.`, examples: [`cube(2)`, `2^3`, `2 * 2 * 2`], seealso: [`multiply`, `square`, `pow`] }, divideDocs = { name: `divide`, category: `Operators`, syntax: [`x / y`, `divide(x, y)`], description: `Divide two values.`, examples: [`a = 2 / 3`, `a * 3`, `4.5 / 2`, `3 + 4 / 2`, `(3 + 4) / 2`, `18 km / 4.5`], seealso: [`multiply`] }, dotDivideDocs = { name: `dotDivide`, category: `Operators`, syntax: [`x ./ y`, `dotDivide(x, y)`], description: `Divide two values element wise.`, examples: [`a = [1, 2, 3; 4, 5, 6]`, `b = [2, 1, 1; 3, 2, 5]`, `a ./ b`], seealso: [`multiply`, `dotMultiply`, `divide`] }, dotMultiplyDocs = { name: `dotMultiply`, category: `Operators`, syntax: [`x .* y`, `dotMultiply(x, y)`], description: `Multiply two values element wise.`, examples: [`a = [1, 2, 3; 4, 5, 6]`, `b = [2, 1, 1; 3, 2, 5]`, `a .* b`], seealso: [`multiply`, `divide`, `dotDivide`] }, dotPowDocs = { name: `dotPow`, category: `Operators`, syntax: [`x .^ y`, `dotPow(x, y)`], description: `Calculates the power of x to y element wise.`, examples: [`a = [1, 2, 3; 4, 5, 6]`, `a .^ 2`], seealso: [`pow`] }, expDocs = { name: `exp`, category: `Arithmetic`, syntax: [`exp(x)`], description: `Calculate the exponent of a value.`, examples: [`exp(1.3)`, `e ^ 1.3`, `log(exp(1.3))`, `x = 2.4`, `(exp(i*x) == cos(x) + i*sin(x))   # Euler's formula`], seealso: [`expm`, `expm1`, `pow`, `log`] }, expmDocs = { name: `expm`, category: `Arithmetic`, syntax: [`exp(x)`], description: `Compute the matrix exponential, expm(A) = e^A. The matrix must be square. Not to be confused with exp(a), which performs element-wise exponentiation.`, examples: [`expm([[0,2],[0,0]])`], seealso: [`exp`] }, expm1Docs = { name: `expm1`, category: `Arithmetic`, syntax: [`expm1(x)`], description: `Calculate the value of subtracting 1 from the exponential value.`, examples: [`expm1(2)`, `pow(e, 2) - 1`, `log(expm1(2) + 1)`], seealso: [`exp`, `pow`, `log`] }, fixDocs = { name: `fix`, category: `Arithmetic`, syntax: [`fix(x)`, `fix(x, n)`, `fix(unit, valuelessUnit)`, `fix(unit, n, valuelessUnit)`], description: `Round a value towards zero. If x is complex, both real and imaginary part are rounded towards zero.`, examples: [`fix(3.2)`, `fix(3.8)`, `fix(-4.2)`, `fix(-4.8)`, `fix(3.241cm, cm)`, `fix(3.241cm, 2, cm)`], seealso: [`ceil`, `floor`, `round`] }, floorDocs = { name: `floor`, category: `Arithmetic`, syntax: [`floor(x)`, `floor(x, n)`, `floor(unit, valuelessUnit)`, `floor(unit, n, valuelessUnit)`], description: `Round a value towards minus infinity.If x is complex, both real and imaginary part are rounded towards minus infinity.`, examples: [`floor(3.2)`, `floor(3.8)`, `floor(-4.2)`, `floor(3.241cm, cm)`, `floor(3.241cm, 2, cm)`], seealso: [`ceil`, `fix`, `round`] }, gcdDocs = { name: `gcd`, category: `Arithmetic`, syntax: [`gcd(a, b)`, `gcd(a, b, c, ...)`], description: `Compute the greatest common divisor.`, examples: [`gcd(8, 12)`, `gcd(-4, 6)`, `gcd(25, 15, -10)`], seealso: [`lcm`, `xgcd`] }, hypotDocs = { name: `hypot`, category: `Arithmetic`, syntax: [`hypot(a, b, c, ...)`, `hypot([a, b, c, ...])`], description: `Calculate the hypotenuse of a list with values.`, examples: [`hypot(3, 4)`, `sqrt(3^2 + 4^2)`, `hypot(-2)`, `hypot([3, 4, 5])`], seealso: [`abs`, `norm`] }, invmodDocs = { name: `invmod`, category: `Arithmetic`, syntax: [`invmod(a, b)`], description: `Calculate the (modular) multiplicative inverse of a modulo b. Solution to the equation ax ≣ 1 (mod b)`, examples: [`invmod(8, 12)`, `invmod(7, 13)`, `invmod(15151, 15122)`], seealso: [`gcd`, `xgcd`] }, lcmDocs = { name: `lcm`, category: `Arithmetic`, syntax: [`lcm(x, y)`], description: `Compute the least common multiple.`, examples: [`lcm(4, 6)`, `lcm(6, 21)`, `lcm(6, 21, 5)`], seealso: [`gcd`] }, logDocs = { name: `log`, category: `Arithmetic`, syntax: [`log(x)`, `log(x, base)`], description: `Compute the logarithm of a value. If no base is provided, the natural logarithm of x is calculated. If base if provided, the logarithm is calculated for the specified base. log(x, base) is defined as log(x) / log(base).`, examples: [`log(3.5)`, `a = log(2.4)`, `exp(a)`, `10 ^ 4`, `log(10000, 10)`, `log(10000) / log(10)`, `b = log(1024, 2)`, `2 ^ b`], seealso: [`exp`, `log1p`, `log2`, `log10`] }, log10Docs = { name: `log10`, category: `Arithmetic`, syntax: [`log10(x)`], description: `Compute the 10-base logarithm of a value.`, examples: [`log10(0.00001)`, `log10(10000)`, `10 ^ 4`, `log(10000) / log(10)`, `log(10000, 10)`], seealso: [`exp`, `log`] }, log1pDocs = { name: `log1p`, category: `Arithmetic`, syntax: [`log1p(x)`, `log1p(x, base)`], description: "Calculate the logarithm of a `value+1`", examples: [`log1p(2.5)`, `exp(log1p(1.4))`, `pow(10, 4)`, `log1p(9999, 10)`, `log1p(9999) / log(10)`], seealso: [`exp`, `log`, `log2`, `log10`] }, log2Docs = { name: `log2`, category: `Arithmetic`, syntax: [`log2(x)`], description: "Calculate the 2-base of a value. This is the same as calculating `log(x, 2)`.", examples: [`log2(0.03125)`, `log2(16)`, `log2(16) / log2(2)`, `pow(2, 4)`], seealso: [`exp`, `log1p`, `log`, `log10`] }, modDocs = { name: `mod`, category: `Operators`, syntax: [`x % y`, `x mod y`, `mod(x, y)`], description: `Calculates the modulus, the remainder of an integer division.`, examples: [`7 % 3`, `11 % 2`, `10 mod 4`, `isOdd(x) = x % 2`, `isOdd(2)`, `isOdd(3)`], seealso: [`divide`] }, multiplyDocs = { name: `multiply`, category: `Operators`, syntax: [`x * y`, `multiply(x, y)`], description: `multiply two values.`, examples: [`a = 2.1 * 3.4`, `a / 3.4`, `2 * 3 + 4`, `2 * (3 + 4)`, `3 * 2.1 km`], seealso: [`divide`] }, normDocs = { name: `norm`, category: `Arithmetic`, syntax: [`norm(x)`, `norm(x, p)`], description: `Calculate the norm of a number, vector or matrix.`, examples: [`abs(-3.5)`, `norm(-3.5)`, `norm(3 - 4i)`, `norm([1, 2, -3], Infinity)`, `norm([1, 2, -3], -Infinity)`, `norm([3, 4], 2)`, `norm([[1, 2], [3, 4]], 1)`, `norm([[1, 2], [3, 4]], "inf")`, `norm([[1, 2], [3, 4]], "fro")`] }, nthRootDocs = { name: `nthRoot`, category: `Arithmetic`, syntax: [`nthRoot(a)`, `nthRoot(a, root)`], description: `Calculate the nth root of a value. The principal nth root of a positive real number A, is the positive real solution of the equation "x^root = A".`, examples: [`4 ^ 3`, `nthRoot(64, 3)`, `nthRoot(9, 2)`, `sqrt(9)`], seealso: [`nthRoots`, `pow`, `sqrt`] }, nthRootsDocs = { name: `nthRoots`, category: `Arithmetic`, syntax: [`nthRoots(A)`, `nthRoots(A, root)`], description: `Calculate the nth roots of a value. An nth root of a positive real number A, is a positive real solution of the equation "x^root = A". This function returns an array of complex values.`, examples: [`nthRoots(1)`, `nthRoots(1, 3)`], seealso: [`sqrt`, `pow`, `nthRoot`] }, powDocs = { name: `pow`, category: `Operators`, syntax: [`x ^ y`, `pow(x, y)`], description: `Calculates the power of x to y, x^y.`, examples: [`2^3`, `2*2*2`, `1 + e ^ (pi * i)`, `pow([[1, 2], [4, 3]], 2)`, `pow([[1, 2], [4, 3]], -1)`], seealso: [`multiply`, `nthRoot`, `nthRoots`, `sqrt`] }, roundDocs = { name: `round`, category: `Arithmetic`, syntax: [`round(x)`, `round(x, n)`, `round(unit, valuelessUnit)`, `round(unit, n, valuelessUnit)`], description: `round a value towards the nearest integer.If x is complex, both real and imaginary part are rounded towards the nearest integer. When n is specified, the value is rounded to n decimals.`, examples: [`round(3.2)`, `round(3.8)`, `round(-4.2)`, `round(-4.8)`, `round(pi, 3)`, `round(123.45678, 2)`, `round(3.241cm, 2, cm)`, `round([3.2, 3.8, -4.7])`], seealso: [`ceil`, `floor`, `fix`] }, signDocs = { name: `sign`, category: `Arithmetic`, syntax: [`sign(x)`], description: `Compute the sign of a value. The sign of a value x is 1 when x>0, -1 when x<0, and 0 when x=0.`, examples: [`sign(3.5)`, `sign(-4.2)`, `sign(0)`], seealso: [`abs`] }, sqrtDocs = { name: `sqrt`, category: `Arithmetic`, syntax: [`sqrt(x)`], description: `Compute the square root value. If x = y * y, then y is the square root of x.`, examples: [`sqrt(25)`, `5 * 5`, `sqrt(-1)`], seealso: [`square`, `sqrtm`, `multiply`, `nthRoot`, `nthRoots`, `pow`] }, sqrtmDocs = { name: `sqrtm`, category: `Arithmetic`, syntax: [`sqrtm(x)`], description: "Calculate the principal square root of a square matrix. The principal square root matrix `X` of another matrix `A` is such that `X * X = A`.", examples: [`sqrtm([[33, 24], [48, 57]])`], seealso: [`sqrt`, `abs`, `square`, `multiply`] }, sylvesterDocs = { name: `sylvester`, category: `Algebra`, syntax: [`sylvester(A,B,C)`], description: `Solves the real-valued Sylvester equation AX+XB=C for X`, examples: [`sylvester([[-1, -2], [1, 1]], [[-2, 1], [-1, 2]], [[-3, 2], [3, 0]])`, `A = [[-1, -2], [1, 1]]; B = [[2, -1], [1, -2]]; C = [[-3, 2], [3, 0]]`, `sylvester(A, B, C)`], seealso: [`schur`, `lyap`] }, schurDocs = { name: `schur`, category: `Algebra`, syntax: [`schur(A)`], description: `Performs a real Schur decomposition of the real matrix A = UTU'`, examples: [`schur([[1, 0], [-4, 3]])`, `A = [[1, 0], [-4, 3]]`, `schur(A)`], seealso: [`lyap`, `sylvester`] }, lyapDocs = { name: `lyap`, category: `Algebra`, syntax: [`lyap(A,Q)`], description: `Solves the Continuous-time Lyapunov equation AP+PA'+Q=0 for P`, examples: [`lyap([[-2, 0], [1, -4]], [[3, 1], [1, 3]])`, `A = [[-2, 0], [1, -4]]`, `Q = [[3, 1], [1, 3]]`, `lyap(A,Q)`], seealso: [`schur`, `sylvester`] }, squareDocs = { name: `square`, category: `Arithmetic`, syntax: [`square(x)`], description: `Compute the square of a value. The square of x is x * x.`, examples: [`square(3)`, `sqrt(9)`, `3^2`, `3 * 3`], seealso: [`multiply`, `pow`, `sqrt`, `cube`] }, subtractDocs = { name: `subtract`, category: `Operators`, syntax: [`x - y`, `subtract(x, y)`], description: `subtract two values.`, examples: [`a = 5.3 - 2`, `a + 2`, `2/3 - 1/6`, `2 * 3 - 3`, `2.1 km - 500m`], seealso: [`add`] }, unaryMinusDocs = { name: `unaryMinus`, category: `Operators`, syntax: [`-x`, `unaryMinus(x)`], description: `Inverse the sign of a value. Converts booleans and strings to numbers.`, examples: [`-4.5`, `-(-5.6)`, `-"22"`], seealso: [`add`, `subtract`, `unaryPlus`] }, unaryPlusDocs = { name: `unaryPlus`, category: `Operators`, syntax: [`+x`, `unaryPlus(x)`], description: `Converts booleans and strings to numbers.`, examples: [`+true`, `+"2"`], seealso: [`add`, `subtract`, `unaryMinus`] }, xgcdDocs = { name: `xgcd`, category: `Arithmetic`, syntax: [`xgcd(a, b)`], description: `Calculate the extended greatest common divisor for two values. The result is an array [d, x, y] with 3 entries, where d is the greatest common divisor, and d = x * a + y * b.`, examples: [`xgcd(8, 12)`, `gcd(8, 12)`, `xgcd(36163, 21199)`], seealso: [`gcd`, `lcm`] }, numDocs = { name: `num`, category: `Fraction`, syntax: [`num(x)`], description: `Get the numerator of a fraction.`, examples: [`num(fraction(2, 3))`, `num(fraction(5, 8))`], seealso: [`den`, `fraction`] }, denDocs = { name: `den`, category: `Fraction`, syntax: [`den(x)`], description: `Get the denominator of a fraction.`, examples: [`den(fraction(2, 3))`, `den(fraction(5, 8))`], seealso: [`num`, `fraction`] }, bitAndDocs = { name: `bitAnd`, category: `Bitwise`, syntax: [`x & y`, `bitAnd(x, y)`], description: `Bitwise AND operation. Performs the logical AND operation on each pair of the corresponding bits of the two given values by multiplying them. If both bits in the compared position are 1, the bit in the resulting binary representation is 1, otherwise, the result is 0`, examples: [`5 & 3`, `bitAnd(53, 131)`, `[1, 12, 31] & 42`], seealso: [`bitNot`, `bitOr`, `bitXor`, `leftShift`, `rightArithShift`, `rightLogShift`] }, bitNotDocs = { name: `bitNot`, category: `Bitwise`, syntax: [`~x`, `bitNot(x)`], description: `Bitwise NOT operation. Performs a logical negation on each bit of the given value. Bits that are 0 become 1, and those that are 1 become 0.`, examples: [`~1`, `~2`, `bitNot([2, -3, 4])`], seealso: [`bitAnd`, `bitOr`, `bitXor`, `leftShift`, `rightArithShift`, `rightLogShift`] }, bitOrDocs = { name: `bitOr`, category: `Bitwise`, syntax: [`x | y`, `bitOr(x, y)`], description: `Bitwise OR operation. Performs the logical inclusive OR operation on each pair of corresponding bits of the two given values. The result in each position is 1 if the first bit is 1 or the second bit is 1 or both bits are 1, otherwise, the result is 0.`, examples: [`5 | 3`, `bitOr([1, 2, 3], 4)`], seealso: [`bitAnd`, `bitNot`, `bitXor`, `leftShift`, `rightArithShift`, `rightLogShift`] }, bitXorDocs = { name: `bitXor`, category: `Bitwise`, syntax: [`bitXor(x, y)`], description: `Bitwise XOR operation, exclusive OR. Performs the logical exclusive OR operation on each pair of corresponding bits of the two given values. The result in each position is 1 if only the first bit is 1 or only the second bit is 1, but will be 0 if both are 0 or both are 1.`, examples: [`bitOr(1, 2)`, `bitXor([2, 3, 4], 4)`], seealso: [`bitAnd`, `bitNot`, `bitOr`, `leftShift`, `rightArithShift`, `rightLogShift`] }, leftShiftDocs = { name: `leftShift`, category: `Bitwise`, syntax: [`x << y`, `leftShift(x, y)`], description: `Bitwise left logical shift of a value x by y number of bits.`, examples: [`4 << 1`, `8 >> 1`], seealso: [`bitAnd`, `bitNot`, `bitOr`, `bitXor`, `rightArithShift`, `rightLogShift`] }, rightArithShiftDocs = { name: `rightArithShift`, category: `Bitwise`, syntax: [`x >> y`, `rightArithShift(x, y)`], description: `Bitwise right arithmetic shift of a value x by y number of bits.`, examples: [`8 >> 1`, `4 << 1`, `-12 >> 2`], seealso: [`bitAnd`, `bitNot`, `bitOr`, `bitXor`, `leftShift`, `rightLogShift`] }, rightLogShiftDocs = { name: `rightLogShift`, category: `Bitwise`, syntax: [`x >>> y`, `rightLogShift(x, y)`], description: `Bitwise right logical shift of a value x by y number of bits.`, examples: [`8 >>> 1`, `4 << 1`, `-12 >>> 2`], seealso: [`bitAnd`, `bitNot`, `bitOr`, `bitXor`, `leftShift`, `rightArithShift`] }, bellNumbersDocs = { name: `bellNumbers`, category: `Combinatorics`, syntax: [`bellNumbers(n)`], description: "The Bell Numbers count the number of partitions of a set. A partition is a pairwise disjoint subset of S whose union is S. `bellNumbers` only takes integer arguments. The following condition must be enforced: n >= 0.", examples: [`bellNumbers(3)`, `bellNumbers(8)`], seealso: [`stirlingS2`] }, catalanDocs = { name: `catalan`, category: `Combinatorics`, syntax: [`catalan(n)`], description: `The Catalan Numbers enumerate combinatorial structures of many different types. catalan only takes integer arguments. The following condition must be enforced: n >= 0.`, examples: [`catalan(3)`, `catalan(8)`], seealso: [`bellNumbers`] }, compositionDocs = { name: `composition`, category: `Combinatorics`, syntax: [`composition(n, k)`], description: `The composition counts of n into k parts. composition only takes integer arguments. The following condition must be enforced: k <= n.`, examples: [`composition(5, 3)`], seealso: [`combinations`] }, stirlingS2Docs = { name: `stirlingS2`, category: `Combinatorics`, syntax: [`stirlingS2(n, k)`], description: "The Stirling numbers of the second kind, counts the number of ways to partition a set of n labelled objects into k nonempty unlabelled subsets. `stirlingS2` only takes integer arguments. The following condition must be enforced: k <= n. If n = k or k = 1, then s(n,k) = 1.", examples: [`stirlingS2(5, 3)`], seealso: [`bellNumbers`, `bernoulli`] }, argDocs = { name: `arg`, category: `Complex`, syntax: [`arg(x)`], description: `Compute the argument of a complex value. If x = a+bi, the argument is computed as atan2(b, a).`, examples: [`arg(2 + 2i)`, `atan2(3, 2)`, `arg(2 + 3i)`], seealso: [`re`, `im`, `conj`, `abs`] }, conjDocs = { name: `conj`, category: `Complex`, syntax: [`conj(x)`], description: `Compute the complex conjugate of a complex value. If x = a+bi, the complex conjugate is a-bi.`, examples: [`conj(2 + 3i)`, `conj(2 - 3i)`, `conj(-5.2i)`], seealso: [`re`, `im`, `abs`, `arg`] }, imDocs = { name: `im`, category: `Complex`, syntax: [`im(x)`], description: `Get the imaginary part of a complex number.`, examples: [`im(2 + 3i)`, `re(2 + 3i)`, `im(-5.2i)`, `im(2.4)`], seealso: [`re`, `conj`, `abs`, `arg`] }, reDocs = { name: `re`, category: `Complex`, syntax: [`re(x)`], description: `Get the real part of a complex number.`, examples: [`re(2 + 3i)`, `im(2 + 3i)`, `re(-5.2i)`, `re(2.4)`], seealso: [`im`, `conj`, `abs`, `arg`] }, evaluateDocs = { name: `evaluate`, category: `Expression`, syntax: [`evaluate(expression)`, `evaluate(expression, scope)`, `evaluate([expr1, expr2, expr3, ...])`, `evaluate([expr1, expr2, expr3, ...], scope)`], description: `Evaluate an expression or an array with expressions.`, examples: [`evaluate("2 + 3")`, `evaluate("sqrt(16)")`, `evaluate("2 inch to cm")`, `evaluate("sin(x * pi)", { "x": 1/2 })`, `evaluate(["width=2", "height=4","width*height"])`], seealso: [`parser`, `parse`, `compile`] }, parserDocs = { name: `parser`, category: `Expression`, syntax: [`parser()`], description: `Create a parser object that keeps a context of variables and their values, allowing the evaluation of expressions in that context.`, examples: [`myParser = parser()`, `myParser.evaluate("sqrt(3^2 + 4^2)")`, `myParser.set("x", 3)`, `myParser.evaluate("y = x + 3")`, `myParser.evaluate(["y = x + 3", "y = y + 1"])`, `myParser.get("y")`], seealso: [`evaluate`, `parse`, `compile`] }, parseDocs = { name: `parse`, category: `Expression`, syntax: [`parse(expr)`, `parse(expr, options)`, `parse([expr1, expr2, expr3, ...])`, `parse([expr1, expr2, expr3, ...], options)`], description: `Parse an expression. Returns a node tree, which can be evaluated by invoking node.evaluate() or transformed into a functional object via node.compile().`, examples: [`node1 = parse("sqrt(3^2 + 4^2)")`, `node1.evaluate()`, `code1 = node1.compile()`, `code1.evaluate()`, `scope = {a: 3, b: 4}`, `node2 = parse("a * b")`, `node2.evaluate(scope)`, `code2 = node2.compile()`, `code2.evaluate(scope)`], seealso: [`parser`, `evaluate`, `compile`] }, compileDocs = { name: `compile`, category: `Expression`, syntax: [`compile(expr) `, `compile([expr1, expr2, expr3, ...])`], description: `Parse and compile an expression. Returns a an object with a function evaluate([scope]) to evaluate the compiled expression.`, examples: [`code1 = compile("sqrt(3^2 + 4^2)")`, `code1.evaluate() `, `code2 = compile("a * b")`, `code2.evaluate({a: 3, b: 4})`], seealso: [`parser`, `parse`, `evaluate`] }, helpDocs = { name: `help`, category: `Expression`, syntax: [`help(object)`, `help(string)`], description: `Display documentation on a function or data type.`, examples: [`help(sqrt)`, `help("complex")`], seealso: [] }, distanceDocs = { name: `distance`, category: `Geometry`, syntax: [`distance([x1, y1], [x2, y2])`, `distance([[x1, y1], [x2, y2]])`], description: `Calculates the Euclidean distance between two points.`, examples: [`distance([0,0], [4,4])`, `distance([[0,0], [4,4]])`], seealso: [] }, intersectDocs = { name: `intersect`, category: `Geometry`, syntax: [`intersect(expr1, expr2, expr3, expr4)`, `intersect(expr1, expr2, expr3)`], description: `Computes the intersection point of lines and/or planes.`, examples: [`intersect([0, 0], [10, 10], [10, 0], [0, 10])`, `intersect([1, 0, 1],  [4, -2, 2], [1, 1, 1, 6])`], seealso: [] }, andDocs = { name: `and`, category: `Logical`, syntax: [`x and y`, `and(x, y)`], description: `Logical and. Test whether two values are both defined with a nonzero/nonempty value.`, examples: [`true and false`, `true and true`, `2 and 4`], seealso: [`not`, `or`, `xor`] }, notDocs = { name: `not`, category: `Logical`, syntax: [`not x`, `not(x)`], description: `Logical not. Flips the boolean value of given argument.`, examples: [`not true`, `not false`, `not 2`, `not 0`], seealso: [`and`, `or`, `xor`] }, nullishDocs = { name: `nullish`, category: `Logical`, syntax: [`x ?? y`, `nullish(x, y)`], description: `Nullish coalescing operator. Returns the right-hand operand when the left-hand operand is null or undefined, and otherwise returns the left-hand operand.`, examples: [`null ?? 42`, `undefined ?? 42`, `0 ?? 42`, `false ?? 42`, `null ?? undefined ?? 42`], seealso: [`and`, `or`, `not`] }, orDocs = { name: `or`, category: `Logical`, syntax: [`x or y`, `or(x, y)`], description: `Logical or. Test if at least one value is defined with a nonzero/nonempty value.`, examples: [`true or false`, `false or false`, `0 or 4`], seealso: [`not`, `and`, `xor`] }, xorDocs = { name: `xor`, category: `Logical`, syntax: [`x xor y`, `xor(x, y)`], description: `Logical exclusive or, xor. Test whether one and only one value is defined with a nonzero/nonempty value.`, examples: [`true xor false`, `false xor false`, `true xor true`, `0 xor 4`], seealso: [`not`, `and`, `or`] }, mapSlicesDocs = { name: `mapSlices`, category: `Matrix`, syntax: [`mapSlices(A, dim, callback)`], description: `Generate a matrix one dimension less than A by applying callback to each slice of A along dimension dim.`, examples: [`A = [[1, 2], [3, 4]]`, `mapSlices(A, 1, sum)`, `mapSlices(A, 2, prod)`], seealso: [`map`, `forEach`] }, columnDocs = { name: `column`, category: `Matrix`, syntax: [`column(x, index)`], description: `Return a column from a matrix or array.`, examples: [`A = [[1, 2], [3, 4]]`, `column(A, 1)`, `column(A, 2)`], seealso: [`row`, `matrixFromColumns`] }, concatDocs = { name: `concat`, category: `Matrix`, syntax: [`concat(A, B, C, ...)`, `concat(A, B, C, ..., dim)`], description: `Concatenate matrices. By default, the matrices are concatenated by the last dimension. The dimension on which to concatenate can be provided as last argument.`, examples: [`A = [1, 2; 5, 6]`, `B = [3, 4; 7, 8]`, `concat(A, B)`, `concat(A, B, 1)`, `concat(A, B, 2)`], seealso: [`det`, `diag`, `identity`, `inv`, `ones`, `range`, `size`, `squeeze`, `subset`, `trace`, `transpose`, `zeros`] }, countDocs = { name: `count`, category: `Matrix`, syntax: [`count(x)`], description: `Count the number of elements of a matrix, array or string.`, examples: [`a = [1, 2; 3, 4; 5, 6]`, `count(a)`, `size(a)`, `count("hello world")`], seealso: [`size`] }, crossDocs = { name: `cross`, category: `Matrix`, syntax: [`cross(A, B)`], description: `Calculate the cross product for two vectors in three dimensional space.`, examples: [`cross([1, 1, 0],  [0, 1, 1])`, `cross([3, -3, 1], [4, 9, 2])`, `cross([2, 3, 4],  [5, 6, 7])`], seealso: [`multiply`, `dot`] }, ctransposeDocs = { name: `ctranspose`, category: `Matrix`, syntax: [`x'`, `ctranspose(x)`], description: `Complex Conjugate and Transpose a matrix`, examples: [`a = [1, 2, 3; 4, 5, 6]`, `a'`, `ctranspose(a)`], seealso: [`concat`, `det`, `diag`, `identity`, `inv`, `ones`, `range`, `size`, `squeeze`, `subset`, `trace`, `zeros`] }, detDocs = { name: `det`, category: `Matrix`, syntax: [`det(x)`], description: `Calculate the determinant of a matrix`, examples: [`det([1, 2; 3, 4])`, `det([-2, 2, 3; -1, 1, 3; 2, 0, -1])`], seealso: [`concat`, `diag`, `identity`, `inv`, `ones`, `range`, `size`, `squeeze`, `subset`, `trace`, `transpose`, `zeros`] }, diagDocs = { name: `diag`, category: `Matrix`, syntax: [`diag(x)`, `diag(x, k)`], description: `Create a diagonal matrix or retrieve the diagonal of a matrix. When x is a vector, a matrix with the vector values on the diagonal will be returned. When x is a matrix, a vector with the diagonal values of the matrix is returned. When k is provided, the k-th diagonal will be filled in or retrieved, if k is positive, the values are placed on the super diagonal. When k is negative, the values are placed on the sub diagonal.`, examples: [`diag(1:3)`, `diag(1:3, 1)`, `a = [1, 2, 3; 4, 5, 6; 7, 8, 9]`, `diag(a)`], seealso: [`concat`, `det`, `identity`, `inv`, `ones`, `range`, `size`, `squeeze`, `subset`, `trace`, `transpose`, `zeros`] }, diffDocs = { name: `diff`, category: `Matrix`, syntax: [`diff(arr)`, `diff(arr, dim)`], description: [`Create a new matrix or array with the difference of the passed matrix or array.`, `Dim parameter is optional and used to indicate the dimension of the array/matrix to apply the difference`, `If no dimension parameter is passed it is assumed as dimension 0`, `Dimension is zero-based in javascript and one-based in the parser`, `Arrays must be 'rectangular' meaning arrays like [1, 2]`, `If something is passed as a matrix it will be returned as a matrix but other than that all matrices are converted to arrays`], examples: [`A = [1, 2, 4, 7, 0]`, `diff(A)`, `diff(A, 1)`, `B = [[1, 2], [3, 4]]`, `diff(B)`, `diff(B, 1)`, `diff(B, 2)`, `diff(B, bignumber(2))`, `diff([[1, 2], matrix([3, 4])], 2)`], seealso: [`subtract`, `partitionSelect`] }, dotDocs = { name: `dot`, category: `Matrix`, syntax: [`dot(A, B)`, `A * B`], description: `Calculate the dot product of two vectors. The dot product of A = [a1, a2, a3, ..., an] and B = [b1, b2, b3, ..., bn] is defined as dot(A, B) = a1 * b1 + a2 * b2 + a3 * b3 + ... + an * bn`, examples: [`dot([2, 4, 1], [2, 2, 3])`, `[2, 4, 1] * [2, 2, 3]`], seealso: [`multiply`, `cross`] }, eigsDocs = { name: `eigs`, category: `Matrix`, syntax: [`eigs(x)`], description: `Calculate the eigenvalues and optionally eigenvectors of a square matrix`, examples: [`eigs([[5, 2.3], [2.3, 1]])`, `eigs([[1, 2, 3], [4, 5, 6], [7, 8, 9]], { precision: 1e-6, eigenvectors: false })`], seealso: [`inv`] }, filterDocs = { name: `filter`, category: `Matrix`, syntax: [`filter(x, test)`], description: `Filter items in a matrix.`, examples: [`isPositive(x) = x > 0`, `filter([6, -2, -1, 4, 3], isPositive)`, `filter([6, -2, 0, 1, 0], x != 0)`], seealso: [`sort`, `map`, `forEach`] }, flattenDocs = { name: `flatten`, category: `Matrix`, syntax: [`flatten(x)`], description: `Flatten a multi dimensional matrix into a single dimensional matrix.`, examples: [`a = [1, 2, 3; 4, 5, 6]`, `size(a)`, `b = flatten(a)`, `size(b)`], seealso: [`concat`, `resize`, `size`, `squeeze`] }, forEachDocs = { name: `forEach`, category: `Matrix`, syntax: [`forEach(x, callback)`], description: `Iterates over all elements of a matrix/array, and executes the given callback function.`, examples: [`numberOfPets = {}`, `addPet(n) = numberOfPets[n] = (numberOfPets[n] ? numberOfPets[n]:0 ) + 1;`, `forEach(["Dog","Cat","Cat"], addPet)`, `numberOfPets`], seealso: [`map`, `sort`, `filter`] }, getMatrixDataTypeDocs = { name: `getMatrixDataType`, category: `Matrix`, syntax: [`getMatrixDataType(x)`], description: `Find the data type of all elements in a matrix or array, for example "number" if all items are a number and "Complex" if all values are complex numbers. If a matrix contains more than one data type, it will return "mixed".`, examples: [`getMatrixDataType([1, 2, 3])`, `getMatrixDataType([[5 cm], [2 inch]])`, `getMatrixDataType([1, "text"])`, `getMatrixDataType([1, bignumber(4)])`], seealso: [`matrix`, `sparse`, `typeOf`] }, identityDocs = { name: `identity`, category: `Matrix`, syntax: [`identity(n)`, `identity(m, n)`, `identity([m, n])`], description: `Returns the identity matrix with size m-by-n. The matrix has ones on the diagonal and zeros elsewhere.`, examples: [`identity(3)`, `identity(3, 5)`, `a = [1, 2, 3; 4, 5, 6]`, `identity(size(a))`], seealso: [`concat`, `det`, `diag`, `inv`, `ones`, `range`, `size`, `squeeze`, `subset`, `trace`, `transpose`, `zeros`] }, invDocs = { name: `inv`, category: `Matrix`, syntax: [`inv(x)`], description: `Calculate the inverse of a matrix`, examples: [`inv([1, 2; 3, 4])`, `inv(4)`, `1 / 4`], seealso: [`concat`, `det`, `diag`, `identity`, `ones`, `range`, `size`, `squeeze`, `subset`, `trace`, `transpose`, `zeros`] }, pinvDocs = { name: `pinv`, category: `Matrix`, syntax: [`pinv(x)`], description: `Calculate the Moore–Penrose inverse of a matrix`, examples: [`pinv([1, 2; 3, 4])`, `pinv([[1, 0], [0, 1], [0, 1]])`, `pinv(4)`], seealso: [`inv`] }, kronDocs = { name: `kron`, category: `Matrix`, syntax: [`kron(x, y)`], description: `Calculates the Kronecker product of 2 matrices or vectors.`, examples: [`kron([[1, 0], [0, 1]], [[1, 2], [3, 4]])`, `kron([1,1], [2,3,4])`], seealso: [`multiply`, `dot`, `cross`] }, mapDocs = { name: `map`, category: `Matrix`, syntax: [`map(x, callback)`, `map(x, y, ..., callback)`], description: `Create a new matrix or array with the results of the callback function executed on each entry of the matrix/array or the matrices/arrays.`, examples: [`map([1, 2, 3], square)`, `map([1, 2], [3, 4], f(a,b) = a + b)`], seealso: [`filter`, `forEach`] }, matrixFromColumnsDocs = { name: `matrixFromColumns`, category: `Matrix`, syntax: [`matrixFromColumns(...arr)`, `matrixFromColumns(row1, row2)`, `matrixFromColumns(row1, row2, row3)`], description: `Create a dense matrix from vectors as individual columns.`, examples: [`matrixFromColumns([1, 2, 3], [[4],[5],[6]])`], seealso: [`matrix`, `matrixFromRows`, `matrixFromFunction`, `zeros`] }, matrixFromFunctionDocs = { name: `matrixFromFunction`, category: `Matrix`, syntax: [`matrixFromFunction(size, fn)`, `matrixFromFunction(size, fn, format)`, `matrixFromFunction(size, fn, format, datatype)`, `matrixFromFunction(size, format, fn)`, `matrixFromFunction(size, format, datatype, fn)`], description: `Create a matrix by evaluating a generating function at each index.`, examples: [`f(I) = I[1] - I[2]`, `matrixFromFunction([3,3], f)`, `g(I) = I[1] - I[2] == 1 ? 4 : 0`, `matrixFromFunction([100, 100], "sparse", g)`, `matrixFromFunction([5], random)`], seealso: [`matrix`, `matrixFromRows`, `matrixFromColumns`, `zeros`] }, matrixFromRowsDocs = { name: `matrixFromRows`, category: `Matrix`, syntax: [`matrixFromRows(...arr)`, `matrixFromRows(row1, row2)`, `matrixFromRows(row1, row2, row3)`], description: `Create a dense matrix from vectors as individual rows.`, examples: [`matrixFromRows([1, 2, 3], [[4],[5],[6]])`], seealso: [`matrix`, `matrixFromColumns`, `matrixFromFunction`, `zeros`] }, onesDocs = { name: `ones`, category: `Matrix`, syntax: [`ones(m)`, `ones(m, n)`, `ones(m, n, p, ...)`, `ones([m])`, `ones([m, n])`, `ones([m, n, p, ...])`], description: `Create a matrix containing ones.`, examples: [`ones(3)`, `ones(3, 5)`, `ones([2,3]) * 4.5`, `a = [1, 2, 3; 4, 5, 6]`, `ones(size(a))`], seealso: [`concat`, `det`, `diag`, `identity`, `inv`, `range`, `size`, `squeeze`, `subset`, `trace`, `transpose`, `zeros`] }, partitionSelectDocs = { name: `partitionSelect`, category: `Matrix`, syntax: [`partitionSelect(x, k)`, `partitionSelect(x, k, compare)`], description: `Partition-based selection of an array or 1D matrix. Will find the kth smallest value, and mutates the input array. Uses Quickselect.`, examples: [`partitionSelect([5, 10, 1], 2)`, `partitionSelect(["C", "B", "A", "D"], 1, compareText)`, `arr = [5, 2, 1]`, `partitionSelect(arr, 0) # returns 1, arr is now: [1, 2, 5]`, `arr`, `partitionSelect(arr, 1, 'desc') # returns 2, arr is now: [5, 2, 1]`, `arr`], seealso: [`sort`] }, rangeDocs = { name: `range`, category: `Type`, syntax: [`start:end`, `start:step:end`, `range(start, end)`, `range(start, end, step)`, `range(string)`], description: `Create a range. Lower bound of the range is included, upper bound is excluded.`, examples: [`1:5`, `3:-1:-3`, `range(3, 7)`, `range(0, 12, 2)`, `range("4:10")`, `range(1m, 1m, 3m)`, `a = [1, 2, 3, 4; 5, 6, 7, 8]`, `a[1:2, 1:2]`], seealso: [`concat`, `det`, `diag`, `identity`, `inv`, `ones`, `size`, `squeeze`, `subset`, `trace`, `transpose`, `zeros`] }, reshapeDocs = { name: `reshape`, category: `Matrix`, syntax: [`reshape(x, sizes)`], description: `Reshape a multi dimensional array to fit the specified dimensions.`, examples: [`reshape([1, 2, 3, 4, 5, 6], [2, 3])`, `reshape([[1, 2], [3, 4]], [1, 4])`, `reshape([[1, 2], [3, 4]], [4])`, `reshape([1, 2, 3, 4], [-1, 2])`], seealso: [`size`, `squeeze`, `resize`] }, resizeDocs = { name: `resize`, category: `Matrix`, syntax: [`resize(x, size)`, `resize(x, size, defaultValue)`], description: `Resize a matrix.`, examples: [`resize([1,2,3,4,5], [3])`, `resize([1,2,3], [5])`, `resize([1,2,3], [5], -1)`, `resize(2, [2, 3])`, `resize("hello", [8], "!")`], seealso: [`size`, `subset`, `squeeze`, `reshape`] }, rotateDocs = { name: `rotate`, category: `Matrix`, syntax: [`rotate(w, theta)`, `rotate(w, theta, v)`], description: `Returns a 2-D rotation matrix (2x2) for a given angle (in radians). Returns a 2-D rotation matrix (3x3) of a given angle (in radians) around given axis.`, examples: [`rotate([1, 0], pi / 2)`, `rotate(matrix([1, 0]), unit("35deg"))`, `rotate([1, 0, 0], unit("90deg"), [0, 0, 1])`, `rotate(matrix([1, 0, 0]), unit("90deg"), matrix([0, 0, 1]))`], seealso: [`matrix`, `rotationMatrix`] }, rotationMatrixDocs = { name: `rotationMatrix`, category: `Matrix`, syntax: [`rotationMatrix(theta)`, `rotationMatrix(theta, v)`, `rotationMatrix(theta, v, format)`], description: `Returns a 2-D rotation matrix (2x2) for a given angle (in radians). Returns a 2-D rotation matrix (3x3) of a given angle (in radians) around given axis.`, examples: [`rotationMatrix(pi / 2)`, `rotationMatrix(unit("45deg"), [0, 0, 1])`, `rotationMatrix(1, matrix([0, 0, 1]), "sparse")`], seealso: [`cos`, `sin`] }, rowDocs = { name: `row`, category: `Matrix`, syntax: [`row(x, index)`], description: `Return a row from a matrix or array.`, examples: [`A = [[1, 2], [3, 4]]`, `row(A, 1)`, `row(A, 2)`], seealso: [`column`, `matrixFromRows`] }, sizeDocs = { name: `size`, category: `Matrix`, syntax: [`size(x)`], description: `Calculate the size of a matrix.`, examples: [`size(2.3)`, `size("hello world")`, `a = [1, 2; 3, 4; 5, 6]`, `size(a)`, `size(1:6)`], seealso: [`concat`, `count`, `det`, `diag`, `identity`, `inv`, `ones`, `range`, `squeeze`, `subset`, `trace`, `transpose`, `zeros`] }, sortDocs = { name: `sort`, category: `Matrix`, syntax: [`sort(x)`, `sort(x, compare)`], description: `Sort the items in a matrix. Compare can be a string "asc", "desc", "natural", or a custom sort function.`, examples: [`sort([5, 10, 1])`, `sort(["C", "B", "A", "D"], "natural")`, `sortByLength(a, b) = size(a)[1] - size(b)[1]`, `sort(["Langdon", "Tom", "Sara"], sortByLength)`, `sort(["10", "1", "2"], "natural")`], seealso: [`map`, `filter`, `forEach`] }, squeezeDocs = { name: `squeeze`, category: `Matrix`, syntax: [`squeeze(x)`], description: `Remove inner and outer singleton dimensions from a matrix.`, examples: [`a = zeros(3,2,1)`, `size(squeeze(a))`, `b = zeros(1,1,3)`, `size(squeeze(b))`], seealso: [`concat`, `det`, `diag`, `identity`, `inv`, `ones`, `range`, `size`, `subset`, `trace`, `transpose`, `zeros`] }, subsetDocs = { name: `subset`, category: `Matrix`, syntax: [`value(index)`, `value(index) = replacement`, `subset(value, [index])`, `subset(value, [index], replacement)`], description: "Get or set a subset of the entries of a matrix or characters of a string. Indexes are one-based. There should be one index specification for each dimension of the target. Each specification can be a single index, a list of indices, or a range in colon notation `l:u`. In a range, both the lower bound l and upper bound u are included; and if a bound is omitted it defaults to the most extreme valid value. The cartesian product of the indices specified in each dimension determines the target of the operation.", examples: [`d = [1, 2; 3, 4]`, `e = []`, `e[1, 1:2] = [5, 6]`, `e[2, :] = [7, 8]`, `f = d * e`, `f[2, 1]`, `f[:, 1]`, `f[[1,2], [1,3]] = [9, 10; 11, 12]`, `f`], seealso: [`concat`, `det`, `diag`, `identity`, `inv`, `ones`, `range`, `size`, `squeeze`, `trace`, `transpose`, `zeros`] }, traceDocs = { name: `trace`, category: `Matrix`, syntax: [`trace(A)`], description: `Calculate the trace of a matrix: the sum of the elements on the main diagonal of a square matrix.`, examples: [`A = [1, 2, 3; -1, 2, 3; 2, 0, 3]`, `trace(A)`], seealso: [`concat`, `det`, `diag`, `identity`, `inv`, `ones`, `range`, `size`, `squeeze`, `subset`, `transpose`, `zeros`] }, transposeDocs = { name: `transpose`, category: `Matrix`, syntax: [`x'`, `transpose(x)`], description: `Transpose a matrix`, examples: [`a = [1, 2, 3; 4, 5, 6]`, `a'`, `transpose(a)`], seealso: [`concat`, `det`, `diag`, `identity`, `inv`, `ones`, `range`, `size`, `squeeze`, `subset`, `trace`, `zeros`] }, zerosDocs = { name: `zeros`, category: `Matrix`, syntax: [`zeros(m)`, `zeros(m, n)`, `zeros(m, n, p, ...)`, `zeros([m])`, `zeros([m, n])`, `zeros([m, n, p, ...])`], description: `Create a matrix containing zeros.`, examples: [`zeros(3)`, `zeros(3, 5)`, `a = [1, 2, 3; 4, 5, 6]`, `zeros(size(a))`], seealso: [`concat`, `det`, `diag`, `identity`, `inv`, `ones`, `range`, `size`, `squeeze`, `subset`, `trace`, `transpose`] }, fftDocs = { name: `fft`, category: `Matrix`, syntax: [`fft(x)`], description: `Calculate N-dimensional Fourier transform`, examples: [`fft([[1, 0], [1, 0]])`], seealso: [`ifft`] }, ifftDocs = { name: `ifft`, category: `Matrix`, syntax: [`ifft(x)`], description: `Calculate N-dimensional inverse Fourier transform`, examples: [`ifft([[2, 2], [0, 0]])`], seealso: [`fft`] }, bernoulliDocs = { name: `bernoulli`, category: `Probability`, syntax: [`bernoulli(n)`], description: `The nth Bernoulli number`, examples: [`bernoulli(4)`, `bernoulli(fraction(12))`], seealso: [`combinations`, `gamma`, `stirlingS2`] }, combinationsDocs = { name: `combinations`, category: `Probability`, syntax: [`combinations(n, k)`], description: `Compute the number of combinations of n items taken k at a time`, examples: [`combinations(7, 5)`], seealso: [`combinationsWithRep`, `permutations`, `factorial`] }, combinationsWithRepDocs = { name: `combinationsWithRep`, category: `Probability`, syntax: [`combinationsWithRep(n, k)`], description: `Compute the number of combinations of n items taken k at a time with replacements.`, examples: [`combinationsWithRep(7, 5)`], seealso: [`combinations`, `permutations`, `factorial`] }, factorialDocs = { name: `factorial`, category: `Probability`, syntax: [`n!`, `factorial(n)`], description: `Compute the factorial of a value`, examples: [`5!`, `5 * 4 * 3 * 2 * 1`, `3!`], seealso: [`combinations`, `combinationsWithRep`, `permutations`, `gamma`] }, gammaDocs = { name: `gamma`, category: `Probability`, syntax: [`gamma(n)`], description: `Compute the gamma function. For small values, the Lanczos approximation is used, and for large values the extended Stirling approximation.`, examples: [`gamma(4)`, `3!`, `gamma(1/2)`, `sqrt(pi)`], seealso: [`factorial`] }, lgammaDocs = { name: `lgamma`, category: `Probability`, syntax: [`lgamma(n)`], description: `Logarithm of the gamma function for real, positive numbers and complex numbers, using Lanczos approximation for numbers and Stirling series for complex numbers.`, examples: [`lgamma(4)`, `lgamma(1/2)`, `lgamma(i)`, `lgamma(complex(1.1, 2))`], seealso: [`gamma`] }, kldivergenceDocs = { name: `kldivergence`, category: `Probability`, syntax: [`kldivergence(x, y)`], description: `Calculate the Kullback-Leibler (KL) divergence  between two distributions.`, examples: [`kldivergence([0.7,0.5,0.4], [0.2,0.9,0.5])`], seealso: [] }, multinomialDocs = { name: `multinomial`, category: `Probability`, syntax: [`multinomial(A)`], description: "Multinomial Coefficients compute the number of ways of picking a1, a2, ..., ai unordered outcomes from `n` possibilities. multinomial takes one array of integers as an argument. The following condition must be enforced: every ai > 0.", examples: [`multinomial([1, 2, 1])`], seealso: [`combinations`, `factorial`] }, permutationsDocs = { name: `permutations`, category: `Probability`, syntax: [`permutations(n)`, `permutations(n, k)`], description: `Compute the number of permutations of n items taken k at a time`, examples: [`permutations(5)`, `permutations(5, 3)`], seealso: [`combinations`, `combinationsWithRep`, `factorial`] }, pickRandomDocs = { name: `pickRandom`, category: `Probability`, syntax: [`pickRandom(array)`, `pickRandom(array, number)`, `pickRandom(array, weights)`, `pickRandom(array, number, weights)`, `pickRandom(array, weights, number)`], description: `Pick a random entry from a given array.`, examples: [`pickRandom(0:10)`, `pickRandom([1, 3, 1, 6])`, `pickRandom([1, 3, 1, 6], 2)`, `pickRandom([1, 3, 1, 6], [2, 3, 2, 1])`, `pickRandom([1, 3, 1, 6], 2, [2, 3, 2, 1])`, `pickRandom([1, 3, 1, 6], [2, 3, 2, 1], 2)`], seealso: [`random`, `randomInt`] }, randomDocs = { name: `random`, category: `Probability`, syntax: [`random()`, `random(max)`, `random(min, max)`, `random(size)`, `random(size, max)`, `random(size, min, max)`], description: `Return a random number.`, examples: [`random()`, `random(10, 20)`, `random([2, 3])`], seealso: [`pickRandom`, `randomInt`] }, randomIntDocs = { name: `randomInt`, category: `Probability`, syntax: [`randomInt(max)`, `randomInt(min, max)`, `randomInt(size)`, `randomInt(size, max)`, `randomInt(size, min, max)`], description: `Return a random integer number`, examples: [`randomInt(10, 20)`, `randomInt([2, 3], 10)`], seealso: [`pickRandom`, `random`] }, compareDocs = { name: `compare`, category: `Relational`, syntax: [`compare(x, y)`], description: `Compare two values. Returns 1 when x > y, -1 when x < y, and 0 when x == y.`, examples: [`compare(2, 3)`, `compare(3, 2)`, `compare(2, 2)`, `compare(5cm, 40mm)`, `compare(2, [1, 2, 3])`], seealso: [`equal`, `unequal`, `smaller`, `smallerEq`, `largerEq`, `compareNatural`, `compareText`] }, compareNaturalDocs = { name: `compareNatural`, category: `Relational`, syntax: [`compareNatural(x, y)`], description: `Compare two values of any type in a deterministic, natural way. Returns 1 when x > y, -1 when x < y, and 0 when x == y.`, examples: [`compareNatural(2, 3)`, `compareNatural(3, 2)`, `compareNatural(2, 2)`, `compareNatural(5cm, 40mm)`, `compareNatural("2", "10")`, `compareNatural(2 + 3i, 2 + 4i)`, `compareNatural([1, 2, 4], [1, 2, 3])`, `compareNatural([1, 5], [1, 2, 3])`, `compareNatural([1, 2], [1, 2])`, `compareNatural({a: 2}, {a: 4})`], seealso: [`equal`, `unequal`, `smaller`, `smallerEq`, `largerEq`, `compare`, `compareText`] }, compareTextDocs = { name: `compareText`, category: `Relational`, syntax: [`compareText(x, y)`], description: `Compare two strings lexically. Comparison is case sensitive. Returns 1 when x > y, -1 when x < y, and 0 when x == y.`, examples: [`compareText("B", "A")`, `compareText("A", "B")`, `compareText("A", "A")`, `compareText("2", "10")`, `compare("2", "10")`, `compare(2, 10)`, `compareNatural("2", "10")`, `compareText("B", ["A", "B", "C"])`], seealso: [`compare`, `compareNatural`] }, deepEqualDocs = { name: `deepEqual`, category: `Relational`, syntax: [`deepEqual(x, y)`], description: `Check equality of two matrices element wise. Returns true if the size of both matrices is equal and when and each of the elements are equal.`, examples: [`deepEqual([1,3,4], [1,3,4])`, `deepEqual([1,3,4], [1,3])`], seealso: [`equal`, `unequal`, `smaller`, `larger`, `smallerEq`, `largerEq`, `compare`] }, equalDocs = { name: `equal`, category: `Relational`, syntax: [`x == y`, `equal(x, y)`], description: `Check equality of two values. Returns true if the values are equal, and false if not.`, examples: [`2+2 == 3`, `2+2 == 4`, `a = 3.2`, `b = 6-2.8`, `a == b`, `50cm == 0.5m`], seealso: [`unequal`, `smaller`, `larger`, `smallerEq`, `largerEq`, `compare`, `deepEqual`, `equalText`] }, equalTextDocs = { name: `equalText`, category: `Relational`, syntax: [`equalText(x, y)`], description: `Check equality of two strings. Comparison is case sensitive. Returns true if the values are equal, and false if not.`, examples: [`equalText("Hello", "Hello")`, `equalText("a", "A")`, `equal("2e3", "2000")`, `equalText("2e3", "2000")`, `equalText("B", ["A", "B", "C"])`], seealso: [`compare`, `compareNatural`, `compareText`, `equal`] }, largerDocs = { name: `larger`, category: `Relational`, syntax: [`x > y`, `larger(x, y)`], description: `Check if value x is larger than y. Returns true if x is larger than y, and false if not. Comparing a value with NaN returns false.`, examples: [`2 > 3`, `5 > 2*2`, `a = 3.3`, `b = 6-2.8`, `(a > b)`, `(b < a)`, `5 cm > 2 inch`], seealso: [`equal`, `unequal`, `smaller`, `smallerEq`, `largerEq`, `compare`] }, largerEqDocs = { name: `largerEq`, category: `Relational`, syntax: [`x >= y`, `largerEq(x, y)`], description: `Check if value x is larger or equal to y. Returns true if x is larger or equal to y, and false if not.`, examples: [`2 >= 1+1`, `2 > 1+1`, `a = 3.2`, `b = 6-2.8`, `(a >= b)`], seealso: [`equal`, `unequal`, `smallerEq`, `smaller`, `compare`] }, smallerDocs = { name: `smaller`, category: `Relational`, syntax: [`x < y`, `smaller(x, y)`], description: `Check if value x is smaller than value y. Returns true if x is smaller than y, and false if not. Comparing a value with NaN returns false.`, examples: [`2 < 3`, `5 < 2*2`, `a = 3.3`, `b = 6-2.8`, `(a < b)`, `5 cm < 2 inch`], seealso: [`equal`, `unequal`, `larger`, `smallerEq`, `largerEq`, `compare`] }, smallerEqDocs = { name: `smallerEq`, category: `Relational`, syntax: [`x <= y`, `smallerEq(x, y)`], description: `Check if value x is smaller or equal to value y. Returns true if x is smaller than y, and false if not.`, examples: [`2 <= 1+1`, `2 < 1+1`, `a = 3.2`, `b = 6-2.8`, `(a <= b)`], seealso: [`equal`, `unequal`, `larger`, `smaller`, `largerEq`, `compare`] }, unequalDocs = { name: `unequal`, category: `Relational`, syntax: [`x != y`, `unequal(x, y)`], description: `Check unequality of two values. Returns true if the values are unequal, and false if they are equal.`, examples: [`2+2 != 3`, `2+2 != 4`, `a = 3.2`, `b = 6-2.8`, `a != b`, `50cm != 0.5m`, `5 cm != 2 inch`], seealso: [`equal`, `smaller`, `larger`, `smallerEq`, `largerEq`, `compare`, `deepEqual`] }, setCartesianDocs = { name: `setCartesian`, category: `Set`, syntax: [`setCartesian(set1, set2)`], description: `Create the cartesian product of two (multi)sets. Multi-dimension arrays will be converted to single-dimension arrays and the values will be sorted in ascending order before the operation.`, examples: [`setCartesian([1, 2], [3, 4])`], seealso: [`setUnion`, `setIntersect`, `setDifference`, `setPowerset`] }, setDifferenceDocs = { name: `setDifference`, category: `Set`, syntax: [`setDifference(set1, set2)`], description: `Create the difference of two (multi)sets: every element of set1, that is not the element of set2. Multi-dimension arrays will be converted to single-dimension arrays before the operation.`, examples: [`setDifference([1, 2, 3, 4], [3, 4, 5, 6])`, `setDifference([[1, 2], [3, 4]], [[3, 4], [5, 6]])`], seealso: [`setUnion`, `setIntersect`, `setSymDifference`] }, setDistinctDocs = { name: `setDistinct`, category: `Set`, syntax: [`setDistinct(set)`], description: `Collect the distinct elements of a multiset. A multi-dimension array will be converted to a single-dimension array before the operation.`, examples: [`setDistinct([1, 1, 1, 2, 2, 3])`], seealso: [`setMultiplicity`] }, setIntersectDocs = { name: `setIntersect`, category: `Set`, syntax: [`setIntersect(set1, set2)`], description: `Create the intersection of two (multi)sets. Multi-dimension arrays will be converted to single-dimension arrays before the operation.`, examples: [`setIntersect([1, 2, 3, 4], [3, 4, 5, 6])`, `setIntersect([[1, 2], [3, 4]], [[3, 4], [5, 6]])`], seealso: [`setUnion`, `setDifference`] }, setIsSubsetDocs = { name: `setIsSubset`, category: `Set`, syntax: [`setIsSubset(set1, set2)`], description: `Check whether a (multi)set is a subset of another (multi)set: every element of set1 is the element of set2. Multi-dimension arrays will be converted to single-dimension arrays before the operation.`, examples: [`setIsSubset([1, 2], [3, 4, 5, 6])`, `setIsSubset([3, 4], [3, 4, 5, 6])`], seealso: [`setUnion`, `setIntersect`, `setDifference`] }, setMultiplicityDocs = { name: `setMultiplicity`, category: `Set`, syntax: [`setMultiplicity(element, set)`], description: `Count the multiplicity of an element in a multiset. A multi-dimension array will be converted to a single-dimension array before the operation.`, examples: [`setMultiplicity(1, [1, 2, 2, 4])`, `setMultiplicity(2, [1, 2, 2, 4])`], seealso: [`setDistinct`, `setSize`] }, setPowersetDocs = { name: `setPowerset`, category: `Set`, syntax: [`setPowerset(set)`], description: `Create the powerset of a (multi)set: the powerset contains very possible subsets of a (multi)set. A multi-dimension array will be converted to a single-dimension array before the operation.`, examples: [`setPowerset([1, 2, 3])`], seealso: [`setCartesian`] }, setSizeDocs = { name: `setSize`, category: `Set`, syntax: [`setSize(set)`, `setSize(set, unique)`], description: `Count the number of elements of a (multi)set. When the second parameter "unique" is true, count only the unique values. A multi-dimension array will be converted to a single-dimension array before the operation.`, examples: [`setSize([1, 2, 2, 4])`, `setSize([1, 2, 2, 4], true)`], seealso: [`setUnion`, `setIntersect`, `setDifference`] }, setSymDifferenceDocs = { name: `setSymDifference`, category: `Set`, syntax: [`setSymDifference(set1, set2)`], description: `Create the symmetric difference of two (multi)sets. Multi-dimension arrays will be converted to single-dimension arrays before the operation.`, examples: [`setSymDifference([1, 2, 3, 4], [3, 4, 5, 6])`, `setSymDifference([[1, 2], [3, 4]], [[3, 4], [5, 6]])`], seealso: [`setUnion`, `setIntersect`, `setDifference`] }, setUnionDocs = { name: `setUnion`, category: `Set`, syntax: [`setUnion(set1, set2)`], description: `Create the union of two (multi)sets. Multi-dimension arrays will be converted to single-dimension arrays before the operation.`, examples: [`setUnion([1, 2, 3, 4], [3, 4, 5, 6])`, `setUnion([[1, 2], [3, 4]], [[3, 4], [5, 6]])`], seealso: [`setIntersect`, `setDifference`] }, zpk2tfDocs = { name: `zpk2tf`, category: `Signal`, syntax: [`zpk2tf(z, p, k)`], description: `Compute the transfer function of a zero-pole-gain model.`, examples: [`zpk2tf([1, 2], [-1, -2], 1)`, `zpk2tf([1, 2], [-1, -2])`, `zpk2tf([1 - 3i, 2 + 2i], [-1, -2])`], seealso: [] }, freqzDocs = { name: `freqz`, category: `Signal`, syntax: [`freqz(b, a)`, `freqz(b, a, w)`], description: `Calculates the frequency response of a filter given its numerator and denominator coefficients.`, examples: [`freqz([1, 2], [1, 2, 3])`, `freqz([1, 2], [1, 2, 3], [0, 1])`, `freqz([1, 2], [1, 2, 3], 512)`], seealso: [] }, erfDocs = { name: `erf`, category: `Special`, syntax: [`erf(x)`], description: `Compute the erf function of a value using a rational Chebyshev approximations for different intervals of x`, examples: [`erf(0.2)`, `erf(-0.5)`, `erf(4)`], seealso: [] }, zetaDocs = { name: `zeta`, category: `Special`, syntax: [`zeta(s)`], description: `Compute the Riemann Zeta Function using an infinite series and Riemann's Functional Equation for the entire complex plane`, examples: [`zeta(0.2)`, `zeta(-0.5)`, `zeta(4)`], seealso: [] }, madDocs = { name: `mad`, category: `Statistics`, syntax: [`mad(a, b, c, ...)`, `mad(A)`], description: `Compute the median absolute deviation of a matrix or a list with values. The median absolute deviation is defined as the median of the absolute deviations from the median.`, examples: [`mad(10, 20, 30)`, `mad([1, 2, 3])`], seealso: [`mean`, `median`, `std`, `abs`] }, maxDocs = { name: `max`, category: `Statistics`, syntax: [`max(a, b, c, ...)`, `max(A)`, `max(A, dimension)`], description: `Compute the maximum value of a list of values. If any NaN values are found, the function yields the last NaN in the input.`, examples: [`max(2, 3, 4, 1)`, `max([2, 3, 4, 1])`, `max([2, 5; 4, 3])`, `max([2, 5; 4, 3], 1)`, `max([2, 5; 4, 3], 2)`, `max(2.7, 7.1, -4.5, 2.0, 4.1)`, `min(2.7, 7.1, -4.5, 2.0, 4.1)`], seealso: [`mean`, `median`, `min`, `prod`, `std`, `sum`, `variance`] }, meanDocs = { name: `mean`, category: `Statistics`, syntax: [`mean(a, b, c, ...)`, `mean(A)`, `mean(A, dimension)`], description: `Compute the arithmetic mean of a list of values.`, examples: [`mean(2, 3, 4, 1)`, `mean([2, 3, 4, 1])`, `mean([2, 5; 4, 3])`, `mean([2, 5; 4, 3], 1)`, `mean([2, 5; 4, 3], 2)`, `mean([1.0, 2.7, 3.2, 4.0])`], seealso: [`max`, `median`, `min`, `prod`, `std`, `sum`, `variance`] }, medianDocs = { name: `median`, category: `Statistics`, syntax: [`median(a, b, c, ...)`, `median(A)`], description: `Compute the median of all values. The values are sorted and the middle value is returned. In case of an even number of values, the average of the two middle values is returned.`, examples: [`median(5, 2, 7)`, `median([3, -1, 5, 7])`], seealso: [`max`, `mean`, `min`, `prod`, `std`, `sum`, `variance`, `quantileSeq`] }, minDocs = { name: `min`, category: `Statistics`, syntax: [`min(a, b, c, ...)`, `min(A)`, `min(A, dimension)`], description: `Compute the minimum value of a list of values. If any NaN values are found, the function yields the last NaN in the input.`, examples: [`min(2, 3, 4, 1)`, `min([2, 3, 4, 1])`, `min([2, 5; 4, 3])`, `min([2, 5; 4, 3], 1)`, `min([2, 5; 4, 3], 2)`, `min(2.7, 7.1, -4.5, 2.0, 4.1)`, `max(2.7, 7.1, -4.5, 2.0, 4.1)`], seealso: [`max`, `mean`, `median`, `prod`, `std`, `sum`, `variance`] }, modeDocs = { name: `mode`, category: `Statistics`, syntax: [`mode(a, b, c, ...)`, `mode(A)`, `mode(A, a, b, B, c, ...)`], description: `Computes the mode of all values as an array. In case mode being more than one, multiple values are returned in an array.`, examples: [`mode(2, 1, 4, 3, 1)`, `mode([1, 2.7, 3.2, 4, 2.7])`, `mode(1, 4, 6, 1, 6)`], seealso: [`max`, `mean`, `min`, `median`, `prod`, `std`, `sum`, `variance`] }, prodDocs = { name: `prod`, category: `Statistics`, syntax: [`prod(a, b, c, ...)`, `prod(A)`], description: `Compute the product of all values.`, examples: [`prod(2, 3, 4)`, `prod([2, 3, 4])`, `prod([2, 5; 4, 3])`], seealso: [`max`, `mean`, `min`, `median`, `min`, `std`, `sum`, `variance`] }, quantileSeqDocs = { name: `quantileSeq`, category: `Statistics`, syntax: [`quantileSeq(A, prob[, sorted])`, `quantileSeq(A, [prob1, prob2, ...][, sorted])`, `quantileSeq(A, N[, sorted])`], description: `Compute the prob order quantile of a matrix or a list with values. The sequence is sorted and the middle value is returned. Supported types of sequence values are: Number, BigNumber, Unit Supported types of probability are: Number, BigNumber. 

In case of a (multi dimensional) array or matrix, the prob order quantile of all elements will be calculated.`, examples: [`quantileSeq([3, -1, 5, 7], 0.5)`, `quantileSeq([3, -1, 5, 7], [1/3, 2/3])`, `quantileSeq([3, -1, 5, 7], 2)`, `quantileSeq([-1, 3, 5, 7], 0.5, true)`], seealso: [`mean`, `median`, `min`, `max`, `prod`, `std`, `sum`, `variance`] }, stdDocs = { name: `std`, category: `Statistics`, syntax: [`std(a, b, c, ...)`, `std(A)`, `std(A, dimension)`, `std(A, normalization)`, `std(A, dimension, normalization)`], description: `Compute the standard deviation of all values, defined as std(A) = sqrt(variance(A)). Optional parameter normalization can be "unbiased" (default), "uncorrected", or "biased".`, examples: [`std(2, 4, 6)`, `std([2, 4, 6, 8])`, `std([2, 4, 6, 8], "uncorrected")`, `std([2, 4, 6, 8], "biased")`, `std([1, 2, 3; 4, 5, 6])`], seealso: [`max`, `mean`, `min`, `median`, `prod`, `sum`, `variance`] }, cumSumDocs = { name: `cumsum`, category: `Statistics`, syntax: [`cumsum(a, b, c, ...)`, `cumsum(A)`], description: `Compute the cumulative sum of all values.`, examples: [`cumsum(2, 3, 4, 1)`, `cumsum([2, 3, 4, 1])`, `cumsum([1, 2; 3, 4])`, `cumsum([1, 2; 3, 4], 1)`, `cumsum([1, 2; 3, 4], 2)`], seealso: [`max`, `mean`, `median`, `min`, `prod`, `std`, `sum`, `variance`] }, sumDocs = { name: `sum`, category: `Statistics`, syntax: [`sum(a, b, c, ...)`, `sum(A)`, `sum(A, dimension)`], description: `Compute the sum of all values.`, examples: [`sum(2, 3, 4, 1)`, `sum([2, 3, 4, 1])`, `sum([2, 5; 4, 3])`], seealso: [`max`, `mean`, `median`, `min`, `prod`, `std`, `variance`] }, varianceDocs = { name: `variance`, category: `Statistics`, syntax: [`variance(a, b, c, ...)`, `variance(A)`, `variance(A, dimension)`, `variance(A, normalization)`, `variance(A, dimension, normalization)`], description: `Compute the variance of all values. Optional parameter normalization can be "unbiased" (default), "uncorrected", or "biased".`, examples: [`variance(2, 4, 6)`, `variance([2, 4, 6, 8])`, `variance([2, 4, 6, 8], "uncorrected")`, `variance([2, 4, 6, 8], "biased")`, `variance([1, 2, 3; 4, 5, 6])`], seealso: [`max`, `mean`, `min`, `median`, `min`, `prod`, `std`, `sum`] }, corrDocs = { name: `corr`, category: `Statistics`, syntax: [`corr(A,B)`], description: `Compute the correlation coefficient of a two list with values, For matrices, the matrix correlation coefficient is calculated.`, examples: [`corr([2, 4, 6, 8],[1, 2, 3, 6])`, `corr(matrix([[1, 2.2, 3, 4.8, 5], [1, 2, 3, 4, 5]]), matrix([[4, 5.3, 6.6, 7, 8], [1, 2, 3, 4, 5]]))`], seealso: [`max`, `mean`, `min`, `median`, `min`, `prod`, `std`, `sum`] }, acosDocs = { name: `acos`, category: `Trigonometry`, syntax: [`acos(x)`], description: `Compute the inverse cosine of a value in radians.`, examples: [`acos(0.5)`, `acos(cos(2.3))`], seealso: [`cos`, `atan`, `asin`] }, acoshDocs = { name: `acosh`, category: `Trigonometry`, syntax: [`acosh(x)`], description: "Calculate the hyperbolic arccos of a value, defined as `acosh(x) = ln(sqrt(x^2 - 1) + x)`.", examples: [`acosh(1.5)`], seealso: [`cosh`, `asinh`, `atanh`] }, acotDocs = { name: `acot`, category: `Trigonometry`, syntax: [`acot(x)`], description: `Calculate the inverse cotangent of a value.`, examples: [`acot(0.5)`, `acot(cot(0.5))`, `acot(2)`], seealso: [`cot`, `atan`] }, acothDocs = { name: `acoth`, category: `Trigonometry`, syntax: [`acoth(x)`], description: "Calculate the inverse hyperbolic tangent of a value, defined as `acoth(x) = (ln((x+1)/x) + ln(x/(x-1))) / 2`.", examples: [`acoth(2)`, `acoth(0.5)`], seealso: [`acsch`, `asech`] }, acscDocs = { name: `acsc`, category: `Trigonometry`, syntax: [`acsc(x)`], description: `Calculate the inverse cotangent of a value.`, examples: [`acsc(2)`, `acsc(csc(0.5))`, `acsc(0.5)`], seealso: [`csc`, `asin`, `asec`] }, acschDocs = { name: `acsch`, category: `Trigonometry`, syntax: [`acsch(x)`], description: "Calculate the inverse hyperbolic cosecant of a value, defined as `acsch(x) = ln(1/x + sqrt(1/x^2 + 1))`.", examples: [`acsch(0.5)`], seealso: [`asech`, `acoth`] }, asecDocs = { name: `asec`, category: `Trigonometry`, syntax: [`asec(x)`], description: `Calculate the inverse secant of a value.`, examples: [`asec(0.5)`, `asec(sec(0.5))`, `asec(2)`], seealso: [`acos`, `acot`, `acsc`] }, asechDocs = { name: `asech`, category: `Trigonometry`, syntax: [`asech(x)`], description: `Calculate the inverse secant of a value.`, examples: [`asech(0.5)`], seealso: [`acsch`, `acoth`] }, asinDocs = { name: `asin`, category: `Trigonometry`, syntax: [`asin(x)`], description: `Compute the inverse sine of a value in radians.`, examples: [`asin(0.5)`, `asin(sin(0.5))`], seealso: [`sin`, `acos`, `atan`] }, asinhDocs = { name: `asinh`, category: `Trigonometry`, syntax: [`asinh(x)`], description: "Calculate the hyperbolic arcsine of a value, defined as `asinh(x) = ln(x + sqrt(x^2 + 1))`.", examples: [`asinh(0.5)`], seealso: [`acosh`, `atanh`] }, atanDocs = { name: `atan`, category: `Trigonometry`, syntax: [`atan(x)`], description: `Compute the inverse tangent of a value in radians.`, examples: [`atan(0.5)`, `atan(tan(0.5))`], seealso: [`tan`, `acos`, `asin`] }, atan2Docs = { name: `atan2`, category: `Trigonometry`, syntax: [`atan2(y, x)`], description: `Computes the principal value of the arc tangent of y/x in radians.`, examples: [`atan2(2, 2) / pi`, `angle = 60 deg in rad`, `x = cos(angle)`, `y = sin(angle)`, `atan2(y, x)`], seealso: [`sin`, `cos`, `tan`] }, atanhDocs = { name: `atanh`, category: `Trigonometry`, syntax: [`atanh(x)`], description: "Calculate the hyperbolic arctangent of a value, defined as `atanh(x) = ln((1 + x)/(1 - x)) / 2`.", examples: [`atanh(0.5)`], seealso: [`acosh`, `asinh`] }, cosDocs = { name: `cos`, category: `Trigonometry`, syntax: [`cos(x)`], description: `Compute the cosine of x in radians.`, examples: [`cos(2)`, `cos(pi / 4) ^ 2`, `cos(180 deg)`, `cos(60 deg)`, `sin(0.2)^2 + cos(0.2)^2`], seealso: [`acos`, `sin`, `tan`] }, coshDocs = { name: `cosh`, category: `Trigonometry`, syntax: [`cosh(x)`], description: `Compute the hyperbolic cosine of x in radians.`, examples: [`cosh(0.5)`], seealso: [`sinh`, `tanh`, `coth`] }, cotDocs = { name: `cot`, category: `Trigonometry`, syntax: [`cot(x)`], description: `Compute the cotangent of x in radians. Defined as 1/tan(x)`, examples: [`cot(2)`, `1 / tan(2)`], seealso: [`sec`, `csc`, `tan`] }, cothDocs = { name: `coth`, category: `Trigonometry`, syntax: [`coth(x)`], description: `Compute the hyperbolic cotangent of x in radians.`, examples: [`coth(2)`, `1 / tanh(2)`], seealso: [`sech`, `csch`, `tanh`] }, cscDocs = { name: `csc`, category: `Trigonometry`, syntax: [`csc(x)`], description: `Compute the cosecant of x in radians. Defined as 1/sin(x)`, examples: [`csc(2)`, `1 / sin(2)`], seealso: [`sec`, `cot`, `sin`] }, cschDocs = { name: `csch`, category: `Trigonometry`, syntax: [`csch(x)`], description: `Compute the hyperbolic cosecant of x in radians. Defined as 1/sinh(x)`, examples: [`csch(2)`, `1 / sinh(2)`], seealso: [`sech`, `coth`, `sinh`] }, secDocs = { name: `sec`, category: `Trigonometry`, syntax: [`sec(x)`], description: `Compute the secant of x in radians. Defined as 1/cos(x)`, examples: [`sec(2)`, `1 / cos(2)`], seealso: [`cot`, `csc`, `cos`] }, sechDocs = { name: `sech`, category: `Trigonometry`, syntax: [`sech(x)`], description: `Compute the hyperbolic secant of x in radians. Defined as 1/cosh(x)`, examples: [`sech(2)`, `1 / cosh(2)`], seealso: [`coth`, `csch`, `cosh`] }, sinDocs = { name: `sin`, category: `Trigonometry`, syntax: [`sin(x)`], description: `Compute the sine of x in radians.`, examples: [`sin(2)`, `sin(pi / 4) ^ 2`, `sin(90 deg)`, `sin(30 deg)`, `sin(0.2)^2 + cos(0.2)^2`], seealso: [`asin`, `cos`, `tan`] }, sinhDocs = { name: `sinh`, category: `Trigonometry`, syntax: [`sinh(x)`], description: `Compute the hyperbolic sine of x in radians.`, examples: [`sinh(0.5)`], seealso: [`cosh`, `tanh`] }, tanDocs = { name: `tan`, category: `Trigonometry`, syntax: [`tan(x)`], description: `Compute the tangent of x in radians.`, examples: [`tan(0.5)`, `sin(0.5) / cos(0.5)`, `tan(pi / 4)`, `tan(45 deg)`], seealso: [`atan`, `sin`, `cos`] }, tanhDocs = { name: `tanh`, category: `Trigonometry`, syntax: [`tanh(x)`], description: `Compute the hyperbolic tangent of x in radians.`, examples: [`tanh(0.5)`, `sinh(0.5) / cosh(0.5)`], seealso: [`sinh`, `cosh`] }, toDocs = { name: `to`, category: `Units`, syntax: [`x to unit`, `to(x, unit)`], description: `Change the unit of a value.`, examples: [`5 inch to cm`, `3.2kg to g`, `16 bytes in bits`], seealso: [] }, toBestDocs = { name: `toBest`, category: `Units`, syntax: [`toBest(x)`, `toBest(x, unitList)`, `toBest(x, unitList, options)`], description: `Converts to the most appropriate display unit.`, examples: [`toBest(unit(5000, "m"))`, `toBest(unit(3500000, "W"))`, `toBest(unit(0.000000123, "A"))`, `toBest(unit(10, "m"), "cm")`, `toBest(unit(10, "m"), "mm,km", {offset: 1.5})`], seealso: [] }, binDocs = { name: `bin`, category: `Utils`, syntax: [`bin(value)`], description: `Format a number as binary`, examples: [`bin(2)`], seealso: [`oct`, `hex`] }, cloneDocs = { name: `clone`, category: `Utils`, syntax: [`clone(x)`], description: `Clone a variable. Creates a copy of primitive variables, and a deep copy of matrices`, examples: [`clone(3.5)`, `clone(2 - 4i)`, `clone(45 deg)`, `clone([1, 2; 3, 4])`, `clone("hello world")`], seealso: [] }, formatDocs = { name: `format`, category: `Utils`, syntax: [`format(value)`, `format(value, precision)`], description: `Format a value of any type as string.`, examples: [`format(2.3)`, `format(3 - 4i)`, `format([])`, `format(pi, 3)`], seealso: [`print`] }, hasNumericValueDocs = { name: `hasNumericValue`, category: `Utils`, syntax: [`hasNumericValue(x)`], description: `Test whether a value is an numeric value. In case of a string, true is returned if the string contains a numeric value.`, examples: [`hasNumericValue(2)`, `hasNumericValue("2")`, `isNumeric("2")`, `hasNumericValue(0)`, `hasNumericValue(bignumber(500))`, `hasNumericValue(fraction(0.125))`, `hasNumericValue(2 + 3i)`, `hasNumericValue([2.3, "foo", false])`], seealso: [`isInteger`, `isZero`, `isNegative`, `isPositive`, `isNaN`, `isNumeric`] }, hexDocs = { name: `hex`, category: `Utils`, syntax: [`hex(value)`], description: `Format a number as hexadecimal`, examples: [`hex(240)`], seealso: [`bin`, `oct`] }, isIntegerDocs = { name: `isInteger`, category: `Utils`, syntax: [`isInteger(x)`], description: `Test whether a value is an integer number.`, examples: [`isInteger(2)`, `isInteger(3.5)`, `isInteger([3, 0.5, -2])`], seealso: [`isNegative`, `isNumeric`, `isPositive`, `isZero`] }, isNaNDocs = { name: `isNaN`, category: `Utils`, syntax: [`isNaN(x)`], description: `Test whether a value is NaN (not a number)`, examples: [`isNaN(2)`, `isNaN(0 / 0)`, `isNaN(NaN)`, `isNaN(Infinity)`], seealso: [`isNegative`, `isNumeric`, `isPositive`, `isZero`, `isFinite`, `isBounded`] }, isBoundedDocs = { name: `isBounded`, category: `Utils`, syntax: [`isBounded(x)`], description: `Test whether a value or its entries are bounded.`, examples: [`isBounded(Infinity)`, `isBounded(bigint(3))`, `isBounded([3, -Infinity, -3])`], seealso: [`isFinite`, `isNumeric`, `isNaN`, `isNegative`, `isPositive`] }, isFiniteDocs = { name: `isFinite`, category: `Utils`, syntax: [`isFinite(x)`], description: `Test whether a value is finite, elementwise on collections.`, examples: [`isFinite(Infinity)`, `isFinite(bigint(3))`, `isFinite([3, -Infinity, -3])`], seealso: [`isBounded`, `isNumeric`, `isNaN`, `isNegative`, `isPositive`] }, isNegativeDocs = { name: `isNegative`, category: `Utils`, syntax: [`isNegative(x)`], description: `Test whether a value is negative: smaller than zero.`, examples: [`isNegative(2)`, `isNegative(0)`, `isNegative(-4)`, `isNegative([3, 0.5, -2])`], seealso: [`isInteger`, `isNumeric`, `isPositive`, `isZero`] }, isNumericDocs = { name: `isNumeric`, category: `Utils`, syntax: [`isNumeric(x)`], description: `Test whether a value is a numeric value. Returns true when the input is a number, BigNumber, Fraction, or boolean.`, examples: [`isNumeric(2)`, `isNumeric("2")`, `hasNumericValue("2")`, `isNumeric(0)`, `isNumeric(bignumber(500))`, `isNumeric(fraction(0.125))`, `isNumeric(2 + 3i)`, `isNumeric([2.3, "foo", false])`], seealso: [`isInteger`, `isZero`, `isNegative`, `isPositive`, `isNaN`, `hasNumericValue`, `isFinite`, `isBounded`] }, isPositiveDocs = { name: `isPositive`, category: `Utils`, syntax: [`isPositive(x)`], description: `Test whether a value is positive: larger than zero.`, examples: [`isPositive(2)`, `isPositive(0)`, `isPositive(-4)`, `isPositive([3, 0.5, -2])`], seealso: [`isInteger`, `isNumeric`, `isNegative`, `isZero`] }, isPrimeDocs = { name: `isPrime`, category: `Utils`, syntax: [`isPrime(x)`], description: `Test whether a value is prime: has no divisors other than itself and one.`, examples: [`isPrime(3)`, `isPrime(-2)`, `isPrime([2, 17, 100])`], seealso: [`isInteger`, `isNumeric`, `isNegative`, `isZero`] }, isZeroDocs = { name: `isZero`, category: `Utils`, syntax: [`isZero(x)`], description: `Test whether a value is zero.`, examples: [`isZero(2)`, `isZero(0)`, `isZero(-4)`, `isZero([3, 0, -2, 0])`], seealso: [`isInteger`, `isNumeric`, `isNegative`, `isPositive`] }, numericDocs = { name: `numeric`, category: `Utils`, syntax: [`numeric(x)`], description: `Convert a numeric input to a specific numeric type: number, BigNumber, bigint, or Fraction.`, examples: [`numeric("4")`, `numeric("4", "number")`, `numeric("4", "bigint")`, `numeric("4", "BigNumber")`, `numeric("4", "Fraction")`, `numeric(4, "Fraction")`, `numeric(fraction(2, 5), "number")`], seealso: [`number`, `bigint`, `fraction`, `bignumber`, `string`, `format`] }, octDocs = { name: `oct`, category: `Utils`, syntax: [`oct(value)`], description: `Format a number as octal`, examples: [`oct(56)`], seealso: [`bin`, `hex`] }, printDocs = { name: `print`, category: `Utils`, syntax: [`print(template, values)`, `print(template, values, precision)`], description: `Interpolate values into a string template.`, examples: [`print("Lucy is $age years old", {age: 5})`, `print("The value of pi is $pi", {pi: pi}, 3)`, `print("Hello, $user.name!", {user: {name: "John"}})`, `print("Values: $1, $2, $3", [6, 9, 4])`], seealso: [`format`] }, typeOfDocs = { name: `typeOf`, category: `Utils`, syntax: [`typeOf(x)`], description: `Get the type of a variable.`, examples: [`typeOf(3.5)`, `typeOf(2 - 4i)`, `typeOf(45 deg)`, `typeOf("hello world")`], seealso: [`getMatrixDataType`] }, solveODEDocs = { name: `solveODE`, category: `Numeric`, syntax: [`solveODE(func, tspan, y0)`, `solveODE(func, tspan, y0, options)`], description: `Numerical Integration of Ordinary Differential Equations.`, examples: [`f(t,y) = y`, `tspan = [0, 4]`, `solveODE(f, tspan, 1)`, `solveODE(f, tspan, [1, 2])`, `solveODE(f, tspan, 1, { method:"RK23", maxStep:0.1 })`], seealso: [`derivative`, `simplifyCore`] }, embeddedDocs = { bignumber: bignumberDocs, bigint: bigintDocs, boolean: booleanDocs, complex: complexDocs, createUnit: createUnitDocs, fraction: fractionDocs, index: indexDocs, matrix: matrixDocs, number: numberDocs, sparse: sparseDocs, splitUnit: splitUnitDocs, string: stringDocs, unit: unitDocs, e: eDocs, E: eDocs, false: falseDocs, i: iDocs, Infinity: InfinityDocs, LN2: LN2Docs, LN10: LN10Docs, LOG2E: LOG2EDocs, LOG10E: LOG10EDocs, NaN: NaNDocs, null: nullDocs, pi: piDocs, PI: piDocs, phi: phiDocs, SQRT1_2: SQRT12Docs, SQRT2: SQRT2Docs, tau: tauDocs, true: trueDocs, version: versionDocs, speedOfLight: { description: `Speed of light in vacuum`, examples: [`speedOfLight`] }, gravitationConstant: { description: `Newtonian constant of gravitation`, examples: [`gravitationConstant`] }, planckConstant: { description: `Planck constant`, examples: [`planckConstant`] }, reducedPlanckConstant: { description: `Reduced Planck constant`, examples: [`reducedPlanckConstant`] }, magneticConstant: { description: `Magnetic constant (vacuum permeability)`, examples: [`magneticConstant`] }, electricConstant: { description: `Electric constant (vacuum permeability)`, examples: [`electricConstant`] }, vacuumImpedance: { description: `Characteristic impedance of vacuum`, examples: [`vacuumImpedance`] }, coulomb: { description: `Coulomb's constant. Deprecated in favor of coulombConstant`, examples: [`coulombConstant`] }, coulombConstant: { description: `Coulomb's constant`, examples: [`coulombConstant`] }, elementaryCharge: { description: `Elementary charge`, examples: [`elementaryCharge`] }, bohrMagneton: { description: `Bohr magneton`, examples: [`bohrMagneton`] }, conductanceQuantum: { description: `Conductance quantum`, examples: [`conductanceQuantum`] }, inverseConductanceQuantum: { description: `Inverse conductance quantum`, examples: [`inverseConductanceQuantum`] }, magneticFluxQuantum: { description: `Magnetic flux quantum`, examples: [`magneticFluxQuantum`] }, nuclearMagneton: { description: `Nuclear magneton`, examples: [`nuclearMagneton`] }, klitzing: { description: `Von Klitzing constant`, examples: [`klitzing`] }, bohrRadius: { description: `Bohr radius`, examples: [`bohrRadius`] }, classicalElectronRadius: { description: `Classical electron radius`, examples: [`classicalElectronRadius`] }, electronMass: { description: `Electron mass`, examples: [`electronMass`] }, fermiCoupling: { description: `Fermi coupling constant`, examples: [`fermiCoupling`] }, fineStructure: { description: `Fine-structure constant`, examples: [`fineStructure`] }, hartreeEnergy: { description: `Hartree energy`, examples: [`hartreeEnergy`] }, protonMass: { description: `Proton mass`, examples: [`protonMass`] }, deuteronMass: { description: `Deuteron Mass`, examples: [`deuteronMass`] }, neutronMass: { description: `Neutron mass`, examples: [`neutronMass`] }, quantumOfCirculation: { description: `Quantum of circulation`, examples: [`quantumOfCirculation`] }, rydberg: { description: `Rydberg constant`, examples: [`rydberg`] }, thomsonCrossSection: { description: `Thomson cross section`, examples: [`thomsonCrossSection`] }, weakMixingAngle: { description: `Weak mixing angle`, examples: [`weakMixingAngle`] }, efimovFactor: { description: `Efimov factor`, examples: [`efimovFactor`] }, atomicMass: { description: `Atomic mass constant`, examples: [`atomicMass`] }, avogadro: { description: `Avogadro's number`, examples: [`avogadro`] }, boltzmann: { description: `Boltzmann constant`, examples: [`boltzmann`] }, faraday: { description: `Faraday constant`, examples: [`faraday`] }, firstRadiation: { description: `First radiation constant`, examples: [`firstRadiation`] }, loschmidt: { description: `Loschmidt constant at T=273.15 K and p=101.325 kPa`, examples: [`loschmidt`] }, gasConstant: { description: `Gas constant`, examples: [`gasConstant`] }, molarPlanckConstant: { description: `Molar Planck constant`, examples: [`molarPlanckConstant`] }, molarVolume: { description: `Molar volume of an ideal gas at T=273.15 K and p=101.325 kPa`, examples: [`molarVolume`] }, sackurTetrode: { description: `Sackur-Tetrode constant at T=1 K and p=101.325 kPa`, examples: [`sackurTetrode`] }, secondRadiation: { description: `Second radiation constant`, examples: [`secondRadiation`] }, stefanBoltzmann: { description: `Stefan-Boltzmann constant`, examples: [`stefanBoltzmann`] }, wienDisplacement: { description: `Wien displacement law constant`, examples: [`wienDisplacement`] }, molarMass: { description: `Molar mass constant`, examples: [`molarMass`] }, molarMassC12: { description: `Molar mass constant of carbon-12`, examples: [`molarMassC12`] }, gravity: { description: `Standard acceleration of gravity (standard acceleration of free-fall on Earth)`, examples: [`gravity`] }, planckLength: { description: `Planck length`, examples: [`planckLength`] }, planckMass: { description: `Planck mass`, examples: [`planckMass`] }, planckTime: { description: `Planck time`, examples: [`planckTime`] }, planckCharge: { description: `Planck charge`, examples: [`planckCharge`] }, planckTemperature: { description: `Planck temperature`, examples: [`planckTemperature`] }, derivative: derivativeDocs, lsolve: lsolveDocs, lsolveAll: lsolveAllDocs, lup: lupDocs, lusolve: lusolveDocs, leafCount: leafCountDocs, polynomialRoot: polynomialRootDocs, resolve: resolveDocs, simplify: simplifyDocs, simplifyConstant: simplifyConstantDocs, simplifyCore: simplifyCoreDocs, symbolicEqual: symbolicEqualDocs, rationalize: rationalizeDocs, slu: sluDocs, usolve: usolveDocs, usolveAll: usolveAllDocs, qr: qrDocs, abs: absDocs, add: addDocs, cbrt: cbrtDocs, ceil: ceilDocs, cube: cubeDocs, divide: divideDocs, dotDivide: dotDivideDocs, dotMultiply: dotMultiplyDocs, dotPow: dotPowDocs, exp: expDocs, expm: expmDocs, expm1: expm1Docs, fix: fixDocs, floor: floorDocs, gcd: gcdDocs, hypot: hypotDocs, lcm: lcmDocs, log: logDocs, log2: log2Docs, log1p: log1pDocs, log10: log10Docs, mod: modDocs, multiply: multiplyDocs, norm: normDocs, nthRoot: nthRootDocs, nthRoots: nthRootsDocs, pow: powDocs, round: roundDocs, sign: signDocs, sqrt: sqrtDocs, sqrtm: sqrtmDocs, square: squareDocs, subtract: subtractDocs, unaryMinus: unaryMinusDocs, unaryPlus: unaryPlusDocs, xgcd: xgcdDocs, invmod: invmodDocs, num: numDocs, den: denDocs, bitAnd: bitAndDocs, bitNot: bitNotDocs, bitOr: bitOrDocs, bitXor: bitXorDocs, leftShift: leftShiftDocs, rightArithShift: rightArithShiftDocs, rightLogShift: rightLogShiftDocs, bellNumbers: bellNumbersDocs, catalan: catalanDocs, composition: compositionDocs, stirlingS2: stirlingS2Docs, config: configDocs, import: importDocs, typed: typedDocs, arg: argDocs, conj: conjDocs, re: reDocs, im: imDocs, evaluate: evaluateDocs, help: helpDocs, parse: parseDocs, parser: parserDocs, compile: compileDocs, distance: distanceDocs, intersect: intersectDocs, and: andDocs, not: notDocs, nullish: nullishDocs, or: orDocs, xor: xorDocs, mapSlices: mapSlicesDocs, concat: concatDocs, count: countDocs, cross: crossDocs, column: columnDocs, ctranspose: ctransposeDocs, det: detDocs, diag: diagDocs, diff: diffDocs, dot: dotDocs, getMatrixDataType: getMatrixDataTypeDocs, identity: identityDocs, filter: filterDocs, flatten: flattenDocs, forEach: forEachDocs, inv: invDocs, pinv: pinvDocs, eigs: eigsDocs, kron: kronDocs, matrixFromFunction: matrixFromFunctionDocs, matrixFromRows: matrixFromRowsDocs, matrixFromColumns: matrixFromColumnsDocs, map: mapDocs, ones: onesDocs, partitionSelect: partitionSelectDocs, range: rangeDocs, resize: resizeDocs, reshape: reshapeDocs, rotate: rotateDocs, rotationMatrix: rotationMatrixDocs, row: rowDocs, size: sizeDocs, sort: sortDocs, squeeze: squeezeDocs, subset: subsetDocs, trace: traceDocs, transpose: transposeDocs, zeros: zerosDocs, fft: fftDocs, ifft: ifftDocs, sylvester: sylvesterDocs, schur: schurDocs, lyap: lyapDocs, solveODE: solveODEDocs, bernoulli: bernoulliDocs, combinations: combinationsDocs, combinationsWithRep: combinationsWithRepDocs, factorial: factorialDocs, gamma: gammaDocs, kldivergence: kldivergenceDocs, lgamma: lgammaDocs, multinomial: multinomialDocs, permutations: permutationsDocs, pickRandom: pickRandomDocs, random: randomDocs, randomInt: randomIntDocs, compare: compareDocs, compareNatural: compareNaturalDocs, compareText: compareTextDocs, deepEqual: deepEqualDocs, equal: equalDocs, equalText: equalTextDocs, larger: largerDocs, largerEq: largerEqDocs, smaller: smallerDocs, smallerEq: smallerEqDocs, unequal: unequalDocs, setCartesian: setCartesianDocs, setDifference: setDifferenceDocs, setDistinct: setDistinctDocs, setIntersect: setIntersectDocs, setIsSubset: setIsSubsetDocs, setMultiplicity: setMultiplicityDocs, setPowerset: setPowersetDocs, setSize: setSizeDocs, setSymDifference: setSymDifferenceDocs, setUnion: setUnionDocs, zpk2tf: zpk2tfDocs, freqz: freqzDocs, erf: erfDocs, zeta: zetaDocs, cumsum: cumSumDocs, mad: madDocs, max: maxDocs, mean: meanDocs, median: medianDocs, min: minDocs, mode: modeDocs, prod: prodDocs, quantileSeq: quantileSeqDocs, std: stdDocs, sum: sumDocs, variance: varianceDocs, corr: corrDocs, acos: acosDocs, acosh: acoshDocs, acot: acotDocs, acoth: acothDocs, acsc: acscDocs, acsch: acschDocs, asec: asecDocs, asech: asechDocs, asin: asinDocs, asinh: asinhDocs, atan: atanDocs, atanh: atanhDocs, atan2: atan2Docs, cos: cosDocs, cosh: coshDocs, cot: cotDocs, coth: cothDocs, csc: cscDocs, csch: cschDocs, sec: secDocs, sech: sechDocs, sin: sinDocs, sinh: sinhDocs, tan: tanDocs, tanh: tanhDocs, to: toDocs, toBest: toBestDocs, clone: cloneDocs, format: formatDocs, bin: binDocs, oct: octDocs, hex: hexDocs, isNaN: isNaNDocs, isBounded: isBoundedDocs, isFinite: isFiniteDocs, isInteger: isIntegerDocs, isNegative: isNegativeDocs, isNumeric: isNumericDocs, hasNumericValue: hasNumericValueDocs, isPositive: isPositiveDocs, isPrime: isPrimeDocs, isZero: isZeroDocs, print: printDocs, typeOf: typeOfDocs, numeric: numericDocs }, name$79 = `help`, dependencies$79 = [`typed`, `mathWithTransform`, `Help`], createHelp = factory(name$79, dependencies$79, (t4) => {
  var { typed: n2, mathWithTransform: r2, Help: i2 } = t4;
  return n2(name$79, { any: function(t5) {
    var n3, a2 = t5;
    if (typeof t5 != `string`) {
      for (n3 in r2) if (hasOwnProperty$1(r2, n3) && t5 === r2[n3]) {
        a2 = n3;
        break;
      }
    }
    var o2 = getSafeProperty(embeddedDocs, a2);
    if (!o2) {
      var s2 = typeof a2 == `function` ? a2.name : a2;
      throw Error(`No documentation found on "` + s2 + `"`);
    }
    return new i2(o2);
  } });
}), name$78 = `chain`, dependencies$78 = [`typed`, `Chain`], createChain = factory(name$78, dependencies$78, (t4) => {
  var { typed: n2, Chain: r2 } = t4;
  return n2(name$78, { "": function() {
    return new r2();
  }, any: function(t5) {
    return new r2(t5);
  } });
}), name$77 = `det`, dependencies$77 = [`typed`, `matrix`, `subtractScalar`, `multiply`, `divideScalar`, `isZero`, `unaryMinus`], createDet = factory(name$77, dependencies$77, (t4) => {
  var { typed: n2, matrix: r2, subtractScalar: i2, multiply: a2, divideScalar: o2, isZero: s2, unaryMinus: c2 } = t4;
  return n2(name$77, { any: function(t5) {
    return clone$3(t5);
  }, "Array | Matrix": function(t5) {
    var n3;
    switch (isMatrix(t5) ? n3 = t5.size() : Array.isArray(t5) ? (t5 = r2(t5), n3 = t5.size()) : n3 = [], n3.length) {
      case 0:
        return clone$3(t5);
      case 1:
        if (n3[0] === 1) return clone$3(t5.valueOf()[0]);
        if (n3[0] === 0) return 1;
        throw RangeError(`Matrix must be square (size: ` + format(n3) + `)`);
      case 2:
        var i3 = n3[0], a3 = n3[1];
        if (i3 === a3) return l2(t5.clone().valueOf(), i3);
        if (a3 === 0) return 1;
        throw RangeError(`Matrix must be square (size: ` + format(n3) + `)`);
      default:
        throw RangeError(`Matrix must be two dimensional (size: ` + format(n3) + `)`);
    }
  } });
  function l2(t5, n3, r3) {
    if (n3 === 1) return clone$3(t5[0][0]);
    if (n3 === 2) return i2(a2(t5[0][0], t5[1][1]), a2(t5[1][0], t5[0][1]));
    for (var l3 = false, u2 = Array(n3).fill(0).map((t6, n4) => n4), d2 = 0; d2 < n3; d2++) {
      var p2 = u2[d2];
      if (s2(t5[p2][d2])) {
        var m2 = void 0;
        for (m2 = d2 + 1; m2 < n3; m2++) if (!s2(t5[u2[m2]][d2])) {
          p2 = u2[m2], u2[m2] = u2[d2], u2[d2] = p2, l3 = !l3;
          break;
        }
        if (m2 === n3) return t5[p2][d2];
      }
      for (var h2 = t5[p2][d2], g2 = d2 === 0 ? 1 : t5[u2[d2 - 1]][d2 - 1], v2 = d2 + 1; v2 < n3; v2++) for (var y2 = u2[v2], b2 = d2 + 1; b2 < n3; b2++) t5[y2][b2] = o2(i2(a2(t5[y2][b2], h2), a2(t5[y2][d2], t5[p2][b2])), g2);
    }
    var x2 = t5[u2[n3 - 1]][n3 - 1];
    return l3 ? c2(x2) : x2;
  }
}), name$76 = `inv`, dependencies$76 = [`typed`, `matrix`, `divideScalar`, `addScalar`, `multiply`, `unaryMinus`, `det`, `identity`, `abs`], createInv = factory(name$76, dependencies$76, (t4) => {
  var { typed: n2, matrix: r2, divideScalar: i2, addScalar: a2, multiply: o2, unaryMinus: s2, det: c2, identity: l2, abs: u2 } = t4;
  return n2(name$76, { "Array | Matrix": function(t5) {
    var n3 = isMatrix(t5) ? t5.size() : arraySize(t5);
    switch (n3.length) {
      case 1:
        if (n3[0] === 1) return isMatrix(t5) ? r2([i2(1, t5.valueOf()[0])]) : [i2(1, t5[0])];
        throw RangeError(`Matrix must be square (size: ` + format(n3) + `)`);
      case 2:
        var a3 = n3[0], o3 = n3[1];
        if (a3 === o3) return isMatrix(t5) ? r2(d2(t5.valueOf(), a3, o3), t5.storage()) : d2(t5, a3, o3);
        throw RangeError(`Matrix must be square (size: ` + format(n3) + `)`);
      default:
        throw RangeError(`Matrix must be two dimensional (size: ` + format(n3) + `)`);
    }
  }, any: function(t5) {
    return i2(1, t5);
  } });
  function d2(t5, n3, r3) {
    var d3, p2, m2, h2, g2;
    if (n3 === 1) {
      if (h2 = t5[0][0], h2 === 0) throw Error(`Cannot calculate inverse, determinant is zero`);
      return [[i2(1, h2)]];
    } else if (n3 === 2) {
      var v2 = c2(t5);
      if (v2 === 0) throw Error(`Cannot calculate inverse, determinant is zero`);
      return [[i2(t5[1][1], v2), i2(s2(t5[0][1]), v2)], [i2(s2(t5[1][0]), v2), i2(t5[0][0], v2)]];
    } else {
      var y2 = t5.concat();
      for (d3 = 0; d3 < n3; d3++) y2[d3] = y2[d3].concat();
      for (var b2 = l2(n3).valueOf(), x2 = 0; x2 < r3; x2++) {
        var S2 = u2(y2[x2][x2]), w2 = x2;
        for (d3 = x2 + 1; d3 < n3; ) u2(y2[d3][x2]) > S2 && (S2 = u2(y2[d3][x2]), w2 = d3), d3++;
        if (S2 === 0) throw Error(`Cannot calculate inverse, determinant is zero`);
        d3 = w2, d3 !== x2 && (g2 = y2[x2], y2[x2] = y2[d3], y2[d3] = g2, g2 = b2[x2], b2[x2] = b2[d3], b2[d3] = g2);
        var E = y2[x2], D = b2[x2];
        for (d3 = 0; d3 < n3; d3++) {
          var O = y2[d3], j2 = b2[d3];
          if (d3 !== x2) {
            if (O[x2] !== 0) {
              for (m2 = i2(s2(O[x2]), E[x2]), p2 = x2; p2 < r3; p2++) O[p2] = a2(O[p2], o2(m2, E[p2]));
              for (p2 = 0; p2 < r3; p2++) j2[p2] = a2(j2[p2], o2(m2, D[p2]));
            }
          } else {
            for (m2 = E[x2], p2 = x2; p2 < r3; p2++) O[p2] = i2(O[p2], m2);
            for (p2 = 0; p2 < r3; p2++) j2[p2] = i2(j2[p2], m2);
          }
        }
      }
      return b2;
    }
  }
}), name$75 = `pinv`, dependencies$75 = [`typed`, `matrix`, `inv`, `deepEqual`, `equal`, `dotDivide`, `dot`, `ctranspose`, `divideScalar`, `multiply`, `add`, `Complex`], createPinv = factory(name$75, dependencies$75, (t4) => {
  var { typed: n2, matrix: r2, inv: i2, deepEqual: a2, equal: o2, dotDivide: s2, dot: c2, ctranspose: l2, divideScalar: u2, multiply: d2, add: p2, Complex: m2 } = t4;
  return n2(name$75, { "Array | Matrix": function(t5) {
    var n3 = isMatrix(t5) ? t5.size() : arraySize(t5);
    switch (n3.length) {
      case 1:
        return b2(t5) ? l2(t5) : n3[0] === 1 ? i2(t5) : s2(l2(t5), c2(t5, t5));
      case 2:
        if (b2(t5)) return l2(t5);
        var a3 = n3[0], o3 = n3[1];
        if (a3 === o3) try {
          return i2(t5);
        } catch (t6) {
          if (!(t6 instanceof Error && t6.message.match(/Cannot calculate inverse, determinant is zero/))) throw t6;
        }
        return isMatrix(t5) ? r2(h2(t5.valueOf(), a3, o3), t5.storage()) : h2(t5, a3, o3);
      default:
        throw RangeError(`Matrix must be two dimensional (size: ` + format(n3) + `)`);
    }
  }, any: function(t5) {
    return o2(t5, 0) ? clone$3(t5) : u2(1, t5);
  } });
  function h2(t5, n3, r3) {
    var { C: a3, F: o3 } = v2(t5, n3, r3), s3 = d2(i2(d2(l2(a3), a3)), l2(a3));
    return d2(d2(l2(o3), i2(d2(o3, l2(o3)))), s3);
  }
  function g2(t5, n3, r3) {
    for (var i3 = clone$3(t5), a3 = 0, o3 = 0; o3 < n3; o3++) {
      if (r3 <= a3) return i3;
      for (var c3 = o3; y2(i3[c3][a3]); ) if (c3++, n3 === c3 && (c3 = o3, a3++, r3 === a3)) return i3;
      [i3[c3], i3[o3]] = [i3[o3], i3[c3]];
      for (var l3 = i3[o3][a3], u3 = 0; u3 < r3; u3++) i3[o3][u3] = s2(i3[o3][u3], l3);
      for (var m3 = 0; m3 < n3; m3++) if (m3 !== o3) {
        l3 = i3[m3][a3];
        for (var h3 = 0; h3 < r3; h3++) i3[m3][h3] = p2(i3[m3][h3], d2(-1, d2(l3, i3[o3][h3])));
      }
      a3++;
    }
    return i3;
  }
  function v2(t5, n3, r3) {
    var i3 = g2(t5, n3, r3);
    return { C: t5.map((t6, r4) => t6.filter((t7, r5) => r5 < n3 && !y2(c2(i3[r5], i3[r5])))), F: i3.filter((t6, n4) => !y2(c2(i3[n4], i3[n4]))) };
  }
  function y2(t5) {
    return o2(p2(t5, m2(1, 1)), p2(0, m2(1, 1)));
  }
  function b2(t5) {
    return a2(p2(t5, m2(1, 1)), p2(d2(t5, 0), m2(1, 1)));
  }
});
function createComplexEigs(t4) {
  var { addScalar: n2, subtract: r2, flatten: i2, multiply: a2, multiplyScalar: o2, divideScalar: s2, sqrt: c2, abs: l2, bignumber: u2, diag: d2, size: p2, reshape: m2, inv: h2, qr: g2, usolve: v2, usolveAll: y2, equal: b2, complex: x2, larger: S2, smaller: w2, matrixFromColumns: E, dot: D } = t4;
  function O(t5, n3, r3, i3) {
    var a3 = arguments.length > 4 && arguments[4] !== void 0 ? arguments[4] : true, o3 = j2(t5, n3, r3, i3, a3);
    M2(t5, n3, r3, i3, a3, o3);
    var { values: s3, C: c3 } = N2(t5, n3, r3, i3, a3);
    return a3 ? { values: s3, eigenvectors: F(t5, n3, c3, o3, s3, r3, i3) } : { values: s3 };
  }
  function j2(t5, r3, i3, a3, c3) {
    var p3 = a3 === `BigNumber`, m3 = a3 === `Complex`, h3 = p3 ? u2(0) : 0, g3 = p3 ? u2(1) : m3 ? x2(1) : 1, v3 = p3 ? u2(1) : 1, y3 = p3 ? u2(10) : 2, E2 = o2(y3, y3), D2;
    c3 && (D2 = Array(r3).fill(g3));
    for (var O2 = false; !O2; ) {
      O2 = true;
      for (var j3 = 0; j3 < r3; j3++) {
        for (var M3 = h3, N3 = h3, F2 = 0; F2 < r3; F2++) j3 !== F2 && (M3 = n2(M3, l2(t5[F2][j3])), N3 = n2(N3, l2(t5[j3][F2])));
        if (!b2(M3, 0) && !b2(N3, 0)) {
          for (var I2 = v3, L2 = M3, R2 = s2(N3, y3), z2 = o2(N3, y3); w2(L2, R2); ) L2 = o2(L2, E2), I2 = o2(I2, y3);
          for (; S2(L2, z2); ) L2 = s2(L2, E2), I2 = s2(I2, y3);
          if (w2(s2(n2(L2, N3), I2), o2(n2(M3, N3), 0.95))) {
            O2 = false;
            for (var V2 = s2(1, I2), H3 = 0; H3 < r3; H3++) j3 !== H3 && (t5[j3][H3] = o2(t5[j3][H3], V2), t5[H3][j3] = o2(t5[H3][j3], I2));
            c3 && (D2[j3] = o2(D2[j3], V2));
          }
        }
      }
    }
    return c3 ? d2(D2) : null;
  }
  function M2(t5, i3, a3, c3, d3, p3) {
    var m3 = c3 === `BigNumber`, h3 = m3 ? u2(0) : c3 === `Complex` ? x2(0) : 0;
    m3 && (a3 = u2(a3));
    for (var g3 = 0; g3 < i3 - 2; g3++) {
      for (var v3 = 0, y3 = h3, b3 = g3 + 1; b3 < i3; b3++) {
        var S3 = t5[b3][g3];
        w2(l2(y3), l2(S3)) && (y3 = S3, v3 = b3);
      }
      if (!w2(l2(y3), a3)) {
        if (v3 !== g3 + 1) {
          var E2 = t5[v3];
          t5[v3] = t5[g3 + 1], t5[g3 + 1] = E2;
          for (var D2 = 0; D2 < i3; D2++) {
            var O2 = t5[D2][v3];
            t5[D2][v3] = t5[D2][g3 + 1], t5[D2][g3 + 1] = O2;
          }
          if (d3) {
            var j3 = p3[v3];
            p3[v3] = p3[g3 + 1], p3[g3 + 1] = j3;
          }
        }
        for (var M3 = g3 + 2; M3 < i3; M3++) {
          var N3 = s2(t5[M3][g3], y3);
          if (N3 !== 0) {
            for (var F2 = 0; F2 < i3; F2++) t5[M3][F2] = r2(t5[M3][F2], o2(N3, t5[g3 + 1][F2]));
            for (var I2 = 0; I2 < i3; I2++) t5[I2][g3 + 1] = n2(t5[I2][g3 + 1], o2(N3, t5[I2][M3]));
            if (d3) for (var L2 = 0; L2 < i3; L2++) p3[M3][L2] = r2(p3[M3][L2], o2(N3, p3[g3 + 1][L2]));
          }
        }
      }
    }
    return p3;
  }
  function N2(t5, i3, o3, s3, c3) {
    var p3 = s3 === `BigNumber`, m3 = p3 ? u2(1) : s3 === `Complex` ? x2(1) : 1;
    p3 && (o3 = u2(o3));
    for (var h3 = clone$3(t5), v3 = [], y3 = i3, b3 = [], S3 = c3 ? d2(Array(i3).fill(m3)) : void 0, E2 = c3 ? d2(Array(y3).fill(m3)) : void 0, D2 = 0; D2 <= 100; ) {
      D2 += 1;
      for (var O2 = h3[y3 - 1][y3 - 1], j3 = 0; j3 < y3; j3++) h3[j3][j3] = r2(h3[j3][j3], O2);
      var { Q: M3, R: N3 } = g2(h3);
      h3 = a2(N3, M3);
      for (var F2 = 0; F2 < y3; F2++) h3[F2][F2] = n2(h3[F2][F2], O2);
      if (c3 && (E2 = a2(E2, M3)), y3 === 1 || w2(l2(h3[y3 - 1][y3 - 2]), o3)) {
        D2 = 0, v3.push(h3[y3 - 1][y3 - 1]), c3 && (b3.unshift([[1]]), R(E2, i3), S3 = a2(S3, E2), y3 > 1 && (E2 = d2(Array(y3 - 1).fill(m3)))), --y3, h3.pop();
        for (var V2 = 0; V2 < y3; V2++) h3[V2].pop();
      } else if (y3 === 2 || w2(l2(h3[y3 - 2][y3 - 3]), o3)) {
        D2 = 0;
        var H3 = I(h3[y3 - 2][y3 - 2], h3[y3 - 2][y3 - 1], h3[y3 - 1][y3 - 2], h3[y3 - 1][y3 - 1]);
        v3.push(...H3), c3 && (b3.unshift(L(h3[y3 - 2][y3 - 2], h3[y3 - 2][y3 - 1], h3[y3 - 1][y3 - 2], h3[y3 - 1][y3 - 1], H3[0], H3[1], o3, s3)), R(E2, i3), S3 = a2(S3, E2), y3 > 2 && (E2 = d2(Array(y3 - 2).fill(m3)))), y3 -= 2, h3.pop(), h3.pop();
        for (var U2 = 0; U2 < y3; U2++) h3[U2].pop(), h3[U2].pop();
      }
      if (y3 === 0) break;
    }
    if (v3.sort((t6, n3) => +r2(l2(t6), l2(n3))), D2 > 100) {
      var W2 = Error(`The eigenvalues failed to converge. Only found these eigenvalues: ` + v3.join(`, `));
      throw W2.values = v3, W2.vectors = [], W2;
    }
    return { values: v3, C: c3 ? a2(S3, z(b3, i3)) : void 0 };
  }
  function F(t5, n3, o3, s3, c3, l3, p3) {
    var m3 = a2(h2(o3), t5, o3), g3 = p3 === `BigNumber`, v3 = p3 === `Complex`, S3 = g3 ? u2(0) : v3 ? x2(0) : 0, w3 = g3 ? u2(1) : v3 ? x2(1) : 1, E2 = [], D2 = [];
    for (var O2 of c3) {
      var j3 = V(E2, O2, b2);
      j3 === -1 ? (E2.push(O2), D2.push(1)) : D2[j3] += 1;
    }
    for (var M3 = [], N3 = E2.length, F2 = Array(n3).fill(S3), I2 = d2(Array(n3).fill(w3)), L2 = function() {
      var t6 = E2[R2], c4 = r2(m3, a2(t6, I2)), u3 = y2(c4, F2);
      for (u3.shift(); u3.length < D2[R2]; ) {
        var d3 = H2(c4, n3, u3, l3, p3);
        if (d3 === null) break;
        u3.push(d3);
      }
      var g4 = a2(h2(s3), o3);
      u3 = u3.map((t7) => a2(g4, t7)), M3.push(...u3.map((n4) => ({ value: t6, vector: i2(n4) })));
    }, R2 = 0; R2 < N3; R2++) L2();
    return M3;
  }
  function I(t5, i3, a3, s3) {
    var l3 = n2(t5, s3), u3 = r2(o2(t5, s3), o2(i3, a3)), d3 = o2(l3, 0.5), p3 = o2(c2(r2(o2(l3, l3), o2(4, u3))), 0.5);
    return [n2(d3, p3), r2(d3, p3)];
  }
  function L(t5, n3, i3, a3, o3, s3, c3, d3) {
    var p3 = d3 === `BigNumber`, m3 = d3 === `Complex`, h3 = p3 ? u2(0) : m3 ? x2(0) : 0, g3 = p3 ? u2(1) : m3 ? x2(1) : 1;
    if (w2(l2(i3), c3)) return [[g3, h3], [h3, g3]];
    if (S2(l2(r2(o3, s3)), c3)) return [[r2(o3, a3), r2(s3, a3)], [i3, i3]];
    var v3 = r2(t5, o3), y3 = r2(a3, o3);
    return w2(l2(n3), c3) && w2(l2(y3), c3) ? [[v3, g3], [i3, h3]] : [[n3, h3], [y3, g3]];
  }
  function R(t5, n3) {
    for (var r3 = 0; r3 < t5.length; r3++) t5[r3].push(...Array(n3 - t5[r3].length).fill(0));
    for (var i3 = t5.length; i3 < n3; i3++) t5.push(Array(n3).fill(0)), t5[i3][i3] = 1;
    return t5;
  }
  function z(t5, n3) {
    for (var r3 = [], i3 = 0; i3 < n3; i3++) r3[i3] = Array(n3).fill(0);
    var a3 = 0;
    for (var o3 of t5) {
      for (var s3 = o3.length, c3 = 0; c3 < s3; c3++) for (var l3 = 0; l3 < s3; l3++) r3[a3 + c3][a3 + l3] = o3[c3][l3];
      a3 += s3;
    }
    return r3;
  }
  function V(t5, n3, r3) {
    for (var i3 = 0; i3 < t5.length; i3++) if (r3(t5[i3], n3)) return i3;
    return -1;
  }
  function H2(t5, n3, r3, i3, a3) {
    for (var o3 = a3 === `BigNumber` ? u2(1e3) : 1e3, s3, c3 = 0; c3 < 5; ++c3) {
      s3 = U(n3, r3, a3);
      try {
        s3 = v2(t5, s3);
      } catch {
        continue;
      }
      if (S2(G(s3), o3)) break;
    }
    if (c3 >= 5) return null;
    for (c3 = 0; ; ) {
      var l3 = v2(t5, s3);
      if (w2(G(W(s3, [l3])), i3)) break;
      if (++c3 >= 10) return null;
      s3 = K(l3);
    }
    return s3;
  }
  function U(t5, n3, r3) {
    var i3 = r3 === `BigNumber`, a3 = r3 === `Complex`, o3 = Array(t5).fill(0).map((t6) => 2 * Math.random() - 1);
    return i3 && (o3 = o3.map((t6) => u2(t6))), a3 && (o3 = o3.map((t6) => x2(t6))), o3 = W(o3, n3), K(o3, r3);
  }
  function W(t5, n3) {
    var i3 = p2(t5);
    for (var o3 of n3) o3 = m2(o3, i3), t5 = r2(t5, a2(s2(D(o3, t5), D(o3, o3)), o3));
    return t5;
  }
  function G(t5) {
    return l2(c2(D(t5, t5)));
  }
  function K(t5, n3) {
    return a2(s2(n3 === `BigNumber` ? u2(1) : n3 === `Complex` ? x2(1) : 1, G(t5)), t5);
  }
  return O;
}
function createRealSymmetric(t4) {
  var { config: n2, addScalar: r2, subtract: i2, abs: a2, atan: o2, cos: s2, sin: c2, multiplyScalar: l2, inv: u2, bignumber: d2, multiply: p2, add: m2 } = t4;
  function h2(t5, r3) {
    var i3 = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : n2.relTol, a3 = arguments.length > 3 ? arguments[3] : void 0, o3 = arguments.length > 4 ? arguments[4] : void 0;
    if (a3 === `number`) return g2(t5, i3, o3);
    if (a3 === `BigNumber`) return v2(t5, i3, o3);
    throw TypeError(`Unsupported data type: ` + a3);
  }
  function g2(t5, n3, r3) {
    var i3 = t5.length, a3 = Math.abs(n3 / i3), o3, s3;
    if (r3) {
      s3 = Array(i3);
      for (var c3 = 0; c3 < i3; c3++) s3[c3] = Array(i3).fill(0), s3[c3][c3] = 1;
    }
    for (var l3 = D(t5); Math.abs(l3[1]) >= Math.abs(a3); ) {
      var u3 = l3[0][0], d3 = l3[0][1];
      o3 = y2(t5[u3][u3], t5[d3][d3], t5[u3][d3]), t5 = E(t5, o3, u3, d3), r3 && (s3 = x2(s3, o3, u3, d3)), l3 = D(t5);
    }
    for (var p3 = Array(i3).fill(0), m3 = 0; m3 < i3; m3++) p3[m3] = t5[m3][m3];
    return j2(clone$3(p3), s3, r3);
  }
  function v2(t5, n3, r3) {
    var i3 = t5.length, o3 = a2(n3 / i3), s3, c3;
    if (r3) {
      c3 = Array(i3);
      for (var l3 = 0; l3 < i3; l3++) c3[l3] = Array(i3).fill(0), c3[l3][l3] = 1;
    }
    for (var u3 = O(t5); a2(u3[1]) >= a2(o3); ) {
      var d3 = u3[0][0], p3 = u3[0][1];
      s3 = b2(t5[d3][d3], t5[p3][p3], t5[d3][p3]), t5 = w2(t5, s3, d3, p3), r3 && (c3 = S2(c3, s3, d3, p3)), u3 = O(t5);
    }
    for (var m3 = Array(i3).fill(0), h3 = 0; h3 < i3; h3++) m3[h3] = t5[h3][h3];
    return j2(clone$3(m3), c3, r3);
  }
  function y2(t5, r3, i3) {
    var a3 = r3 - t5;
    return Math.abs(a3) <= n2.relTol ? Math.PI / 4 : 0.5 * Math.atan(2 * i3 / (r3 - t5));
  }
  function b2(t5, r3, s3) {
    var c3 = i2(r3, t5);
    return a2(c3) <= n2.relTol ? d2(-1).acos().div(4) : l2(0.5, o2(p2(2, s3, u2(c3))));
  }
  function x2(t5, n3, r3, i3) {
    for (var a3 = t5.length, o3 = Math.cos(n3), s3 = Math.sin(n3), c3 = Array(a3).fill(0), l3 = Array(a3).fill(0), u3 = 0; u3 < a3; u3++) c3[u3] = o3 * t5[u3][r3] - s3 * t5[u3][i3], l3[u3] = s3 * t5[u3][r3] + o3 * t5[u3][i3];
    for (var d3 = 0; d3 < a3; d3++) t5[d3][r3] = c3[d3], t5[d3][i3] = l3[d3];
    return t5;
  }
  function S2(t5, n3, a3, o3) {
    for (var u3 = t5.length, p3 = s2(n3), m3 = c2(n3), h3 = Array(u3).fill(d2(0)), g3 = Array(u3).fill(d2(0)), v3 = 0; v3 < u3; v3++) h3[v3] = i2(l2(p3, t5[v3][a3]), l2(m3, t5[v3][o3])), g3[v3] = r2(l2(m3, t5[v3][a3]), l2(p3, t5[v3][o3]));
    for (var y3 = 0; y3 < u3; y3++) t5[y3][a3] = h3[y3], t5[y3][o3] = g3[y3];
    return t5;
  }
  function w2(t5, n3, a3, o3) {
    for (var u3 = t5.length, h3 = d2(s2(n3)), g3 = d2(c2(n3)), v3 = l2(h3, h3), y3 = l2(g3, g3), b3 = Array(u3).fill(d2(0)), x3 = Array(u3).fill(d2(0)), S3 = p2(d2(2), h3, g3, t5[a3][o3]), w3 = r2(i2(l2(v3, t5[a3][a3]), S3), l2(y3, t5[o3][o3])), E2 = m2(l2(y3, t5[a3][a3]), S3, l2(v3, t5[o3][o3])), D2 = 0; D2 < u3; D2++) b3[D2] = i2(l2(h3, t5[a3][D2]), l2(g3, t5[o3][D2])), x3[D2] = r2(l2(g3, t5[a3][D2]), l2(h3, t5[o3][D2]));
    t5[a3][a3] = w3, t5[o3][o3] = E2, t5[a3][o3] = d2(0), t5[o3][a3] = d2(0);
    for (var O2 = 0; O2 < u3; O2++) O2 !== a3 && O2 !== o3 && (t5[a3][O2] = b3[O2], t5[O2][a3] = b3[O2], t5[o3][O2] = x3[O2], t5[O2][o3] = x3[O2]);
    return t5;
  }
  function E(t5, n3, r3, i3) {
    for (var a3 = t5.length, o3 = Math.cos(n3), s3 = Math.sin(n3), c3 = o3 * o3, l3 = s3 * s3, u3 = Array(a3).fill(0), d3 = Array(a3).fill(0), p3 = c3 * t5[r3][r3] - 2 * o3 * s3 * t5[r3][i3] + l3 * t5[i3][i3], m3 = l3 * t5[r3][r3] + 2 * o3 * s3 * t5[r3][i3] + c3 * t5[i3][i3], h3 = 0; h3 < a3; h3++) u3[h3] = o3 * t5[r3][h3] - s3 * t5[i3][h3], d3[h3] = s3 * t5[r3][h3] + o3 * t5[i3][h3];
    t5[r3][r3] = p3, t5[i3][i3] = m3, t5[r3][i3] = 0, t5[i3][r3] = 0;
    for (var g3 = 0; g3 < a3; g3++) g3 !== r3 && g3 !== i3 && (t5[r3][g3] = u3[g3], t5[g3][r3] = u3[g3], t5[i3][g3] = d3[g3], t5[g3][i3] = d3[g3]);
    return t5;
  }
  function D(t5) {
    for (var n3 = t5.length, r3 = 0, i3 = [0, 1], a3 = 0; a3 < n3; a3++) for (var o3 = a3 + 1; o3 < n3; o3++) Math.abs(r3) < Math.abs(t5[a3][o3]) && (r3 = Math.abs(t5[a3][o3]), i3 = [a3, o3]);
    return [i3, r3];
  }
  function O(t5) {
    for (var n3 = t5.length, r3 = 0, i3 = [0, 1], o3 = 0; o3 < n3; o3++) for (var s3 = o3 + 1; s3 < n3; s3++) a2(r3) < a2(t5[o3][s3]) && (r3 = a2(t5[o3][s3]), i3 = [o3, s3]);
    return [i3, r3];
  }
  function j2(t5, n3, r3) {
    var i3 = t5.length, o3 = Array(i3), s3;
    if (r3) {
      s3 = Array(i3);
      for (var c3 = 0; c3 < i3; c3++) s3[c3] = Array(i3);
    }
    for (var l3 = 0; l3 < i3; l3++) {
      for (var u3 = 0, d3 = t5[0], p3 = 0; p3 < t5.length; p3++) a2(t5[p3]) < a2(d3) && (u3 = p3, d3 = t5[u3]);
      if (o3[l3] = t5.splice(u3, 1)[0], r3) for (var m3 = 0; m3 < i3; m3++) s3[l3][m3] = n3[m3][u3], n3[m3].splice(u3, 1);
    }
    return r3 ? { values: o3, eigenvectors: s3.map((t6, n4) => ({ value: o3[n4], vector: t6 })) } : { values: o3 };
  }
  return h2;
}
var name$74 = `eigs`, dependencies$74 = `config.typed.matrix.addScalar.equal.subtract.abs.atan.cos.sin.multiplyScalar.divideScalar.inv.bignumber.multiply.add.larger.column.flatten.number.complex.sqrt.diag.size.reshape.qr.usolve.usolveAll.im.re.smaller.matrixFromColumns.dot`.split(`.`), createEigs = factory(name$74, dependencies$74, (t4) => {
  var { config: n2, typed: r2, matrix: i2, addScalar: a2, subtract: o2, equal: s2, abs: c2, atan: l2, cos: u2, sin: d2, multiplyScalar: p2, divideScalar: m2, inv: h2, bignumber: g2, multiply: v2, add: y2, larger: b2, column: x2, flatten: S2, number: w2, complex: E, sqrt: D, diag: O, size: j2, reshape: M2, qr: N2, usolve: F, usolveAll: I, im: L, re: R, smaller: z, matrixFromColumns: V, dot: H2 } = t4, U = createRealSymmetric({ config: n2, addScalar: a2, subtract: o2, abs: c2, atan: l2, cos: u2, sin: d2, multiplyScalar: p2, inv: h2, bignumber: g2, multiply: v2, add: y2 }), W = createComplexEigs({ addScalar: a2, subtract: o2, multiply: v2, multiplyScalar: p2, flatten: S2, divideScalar: m2, sqrt: D, abs: c2, bignumber: g2, diag: O, size: j2, reshape: M2, qr: N2, inv: h2, usolve: F, usolveAll: I, equal: s2, complex: E, larger: b2, smaller: z, matrixFromColumns: V, dot: H2 });
  return r2(`eigs`, { Array: function(t5) {
    return G(i2(t5));
  }, "Array, number|BigNumber": function(t5, n3) {
    return G(i2(t5), { precision: n3 });
  }, "Array, Object"(t5, n3) {
    return G(i2(t5), n3);
  }, Matrix: function(t5) {
    return G(t5, { matricize: true });
  }, "Matrix, number|BigNumber": function(t5, n3) {
    return G(t5, { precision: n3, matricize: true });
  }, "Matrix, Object": function(t5, n3) {
    var r3 = { matricize: true };
    return _extends(r3, n3), G(t5, r3);
  } });
  function G(t5) {
    var a3 = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {}, o3 = `eigenvectors` in a3 ? a3.eigenvectors : true, s3 = K(t5, a3.precision ?? n2.relTol, o3);
    return a3.matricize && (s3.values = i2(s3.values), o3 && (s3.eigenvectors = s3.eigenvectors.map((t6) => {
      var { value: n3, vector: r3 } = t6;
      return { value: n3, vector: i2(r3) };
    }))), o3 && Object.defineProperty(s3, "vectors", { enumerable: false, get: () => {
      throw Error(`eigs(M).vectors replaced with eigs(M).eigenvectors`);
    } }), s3;
  }
  function K(t5, n3, r3) {
    var i3 = t5.toArray(), a3 = t5.size();
    if (a3.length !== 2 || a3[0] !== a3[1]) throw RangeError(`Matrix must be square (size: ${format(a3)})`);
    var o3 = a3[0];
    return J(i3, o3, n3) && (ee(i3, o3), q(i3, o3, n3)) ? U(i3, o3, n3, Y(t5, i3, o3), r3) : W(i3, o3, n3, Y(t5, i3, o3), r3);
  }
  function q(t5, n3, r3) {
    for (var i3 = 0; i3 < n3; i3++) for (var a3 = i3; a3 < n3; a3++) if (b2(g2(c2(o2(t5[i3][a3], t5[a3][i3]))), r3)) return false;
    return true;
  }
  function J(t5, n3, r3) {
    for (var i3 = 0; i3 < n3; i3++) for (var a3 = 0; a3 < n3; a3++) if (b2(g2(c2(L(t5[i3][a3]))), r3)) return false;
    return true;
  }
  function ee(t5, n3) {
    for (var r3 = 0; r3 < n3; r3++) for (var i3 = 0; i3 < n3; i3++) t5[r3][i3] = R(t5[r3][i3]);
  }
  function Y(t5, n3, r3) {
    var i3 = t5.datatype();
    if (i3 === `number` || i3 === `BigNumber` || i3 === `Complex`) return i3;
    for (var a3 = false, o3 = false, s3 = false, c3 = 0; c3 < r3; c3++) for (var l3 = 0; l3 < r3; l3++) {
      var u3 = n3[c3][l3];
      if (isNumber(u3) || isFraction(u3)) a3 = true;
      else if (isBigNumber(u3)) o3 = true;
      else if (isComplex(u3)) s3 = true;
      else throw TypeError(`Unsupported type in Matrix: ` + typeOf(u3));
    }
    if (o3 && s3 && console.warn(`Complex BigNumbers not supported, this operation will lose precission.`), s3) {
      for (var d3 = 0; d3 < r3; d3++) for (var p3 = 0; p3 < r3; p3++) n3[d3][p3] = E(n3[d3][p3]);
      return `Complex`;
    }
    if (o3) {
      for (var m3 = 0; m3 < r3; m3++) for (var h3 = 0; h3 < r3; h3++) n3[m3][h3] = g2(n3[m3][h3]);
      return `BigNumber`;
    }
    if (a3) {
      for (var v3 = 0; v3 < r3; v3++) for (var y3 = 0; y3 < r3; y3++) n3[v3][y3] = w2(n3[v3][y3]);
      return `number`;
    } else throw TypeError(`Matrix contains unsupported types only.`);
  }
}), name$73 = `expm`, dependencies$73 = [`typed`, `abs`, `add`, `identity`, `inv`, `multiply`], createExpm = factory(name$73, dependencies$73, (t4) => {
  var { typed: n2, abs: r2, add: i2, identity: a2, inv: o2, multiply: s2 } = t4;
  return n2(name$73, { Matrix: function(t5) {
    var n3 = t5.size();
    if (n3.length !== 2 || n3[0] !== n3[1]) throw RangeError(`Matrix must be square (size: ` + format(n3) + `)`);
    for (var r3 = n3[0], u3 = l2(c2(t5), 1e-15), d2 = u3.q, p2 = u3.j, m2 = s2(t5, 2 ** -p2), h2 = a2(r3), g2 = a2(r3), v2 = 1, y2 = m2, b2 = -1, x2 = 1; x2 <= d2; x2++) x2 > 1 && (y2 = s2(y2, m2), b2 = -b2), v2 = v2 * (d2 - x2 + 1) / ((2 * d2 - x2 + 1) * x2), h2 = i2(h2, s2(v2, y2)), g2 = i2(g2, s2(v2 * b2, y2));
    for (var S2 = s2(o2(g2), h2), w2 = 0; w2 < p2; w2++) S2 = s2(S2, S2);
    return isSparseMatrix(t5) ? t5.createSparseMatrix(S2) : S2;
  } });
  function c2(t5) {
    for (var n3 = t5.size()[0], i3 = 0, a3 = 0; a3 < n3; a3++) {
      for (var o3 = 0, s3 = 0; s3 < n3; s3++) o3 += r2(t5.get([a3, s3]));
      i3 = Math.max(o3, i3);
    }
    return i3;
  }
  function l2(t5, n3) {
    for (var r3 = 30, i3 = 0; i3 < r3; i3++) for (var a3 = 0; a3 <= i3; a3++) {
      var o3 = i3 - a3;
      if (u2(t5, a3, o3) < n3) return { q: a3, j: o3 };
    }
    throw Error(`Could not find acceptable parameters to compute the matrix exponential (try increasing maxSearchSize in expm.js)`);
  }
  function u2(t5, n3, r3) {
    for (var i3 = 1, a3 = 2; a3 <= n3; a3++) i3 *= a3;
    for (var o3 = i3, s3 = n3 + 1; s3 <= 2 * n3; s3++) o3 *= s3;
    var c3 = o3 * (2 * n3 + 1);
    return 8 * (t5 / 2 ** r3) ** (2 * n3) * i3 * i3 / (o3 * c3);
  }
}), name$72 = `sqrtm`, dependencies$72 = [`typed`, `abs`, `add`, `multiply`, `map`, `sqrt`, `subtract`, `inv`, `size`, `max`, `identity`], createSqrtm = factory(name$72, dependencies$72, (t4) => {
  var { typed: n2, abs: r2, add: i2, multiply: a2, map: o2, sqrt: s2, subtract: c2, inv: l2, size: u2, max: d2, identity: p2 } = t4, m2 = 1e3, h2 = 1e-6;
  function g2(t5) {
    var n3, o3 = 0, s3 = t5, g3 = p2(u2(t5));
    do {
      var v2 = s3;
      if (s3 = a2(0.5, i2(v2, l2(g3))), g3 = a2(0.5, i2(g3, l2(v2))), n3 = d2(r2(c2(s3, v2))), n3 > h2 && ++o3 > m2) throw Error(`computing square root of matrix: iterative method could not converge`);
    } while (n3 > h2);
    return s3;
  }
  return n2(name$72, { "Array | Matrix": function(t5) {
    var n3 = isMatrix(t5) ? t5.size() : arraySize(t5);
    switch (n3.length) {
      case 1:
        if (n3[0] === 1) return o2(t5, s2);
        throw RangeError(`Matrix must be square (size: ` + format(n3) + `)`);
      case 2:
        if (n3[0] === n3[1]) return g2(t5);
        throw RangeError(`Matrix must be square (size: ` + format(n3) + `)`);
      default:
        throw RangeError(`Matrix must be at most two dimensional (size: ` + format(n3) + `)`);
    }
  } });
}), name$71 = `sylvester`, dependencies$71 = [`typed`, `schur`, `matrixFromColumns`, `matrix`, `multiply`, `range`, `concat`, `transpose`, `index`, `subset`, `add`, `subtract`, `identity`, `lusolve`, `abs`], createSylvester = factory(name$71, dependencies$71, (t4) => {
  var { typed: n2, schur: r2, matrixFromColumns: i2, matrix: a2, multiply: o2, range: s2, concat: c2, transpose: l2, index: u2, subset: d2, add: p2, subtract: m2, identity: h2, lusolve: g2, abs: v2, config: y2 } = t4;
  return n2(name$71, { "Matrix, Matrix, Matrix": b2, "Array, Matrix, Matrix": function(t5, n3, r3) {
    return b2(a2(t5), n3, r3);
  }, "Array, Array, Matrix": function(t5, n3, r3) {
    return b2(a2(t5), a2(n3), r3);
  }, "Array, Matrix, Array": function(t5, n3, r3) {
    return b2(a2(t5), n3, a2(r3));
  }, "Matrix, Array, Matrix": function(t5, n3, r3) {
    return b2(t5, a2(n3), r3);
  }, "Matrix, Array, Array": function(t5, n3, r3) {
    return b2(t5, a2(n3), a2(r3));
  }, "Matrix, Matrix, Array": function(t5, n3, r3) {
    return b2(t5, n3, a2(r3));
  }, "Array, Array, Array": function(t5, n3, r3) {
    return b2(a2(t5), a2(n3), a2(r3)).toArray();
  } });
  function b2(t5, n3, y3) {
    for (var b3 = n3.size()[0], x2 = t5.size()[0], S2 = r2(t5), w2 = S2.T, E = S2.U, D = r2(o2(-1, n3)), O = D.T, j2 = D.U, M2 = o2(o2(l2(E), y3), j2), N2 = s2(0, x2), F = [], I = (t6, n4) => c2(t6, n4, 1), L = (t6, n4) => c2(t6, n4, 0), R = 0; R < b3; R++) if (R < b3 - 1 && v2(d2(O, u2(R + 1, R))) > 1e-5) {
      for (var z = L(d2(M2, u2(N2, [R])), d2(M2, u2(N2, [R + 1]))), V = 0; V < R; V++) z = p2(z, L(o2(F[V], d2(O, u2(V, R))), o2(F[V], d2(O, u2(V, R + 1)))));
      var H2 = o2(h2(x2), o2(-1, d2(O, u2(R, R)))), U = o2(h2(x2), o2(-1, d2(O, u2(R + 1, R)))), W = o2(h2(x2), o2(-1, d2(O, u2(R, R + 1)))), G = o2(h2(x2), o2(-1, d2(O, u2(R + 1, R + 1)))), K = g2(L(I(p2(w2, H2), U), I(W, p2(w2, G))), z);
      F[R] = K.subset(u2(s2(0, x2), [0])), F[R + 1] = K.subset(u2(s2(x2, 2 * x2), [0])), R++;
    } else {
      for (var q = d2(M2, u2(N2, [R])), J = 0; J < R; J++) q = p2(q, o2(F[J], d2(O, u2(J, R))));
      F[R] = g2(m2(w2, o2(d2(O, u2(R, R)), h2(x2))), q);
    }
    return o2(E, o2(a2(i2(...F)), l2(j2)));
  }
}), name$70 = `schur`, dependencies$70 = [`typed`, `matrix`, `identity`, `multiply`, `qr`, `norm`, `subtract`], createSchur = factory(name$70, dependencies$70, (t4) => {
  var { typed: n2, matrix: r2, identity: i2, multiply: a2, qr: o2, norm: s2, subtract: c2 } = t4;
  return n2(name$70, { Array: function(t5) {
    var n3 = l2(r2(t5));
    return { U: n3.U.valueOf(), T: n3.T.valueOf() };
  }, Matrix: function(t5) {
    return l2(t5);
  } });
  function l2(t5) {
    var n3 = t5.size()[0], r3 = t5, l3 = i2(n3), u2 = 0, d2;
    do {
      d2 = r3;
      var p2 = o2(r3), m2 = p2.Q, h2 = p2.R;
      if (r3 = a2(h2, m2), l3 = a2(l3, m2), u2++ > 100) break;
    } while (s2(c2(r3, d2)) > 1e-4);
    return { U: l3, T: r3 };
  }
}), name$69 = `lyap`, dependencies$69 = [`typed`, `matrix`, `sylvester`, `multiply`, `transpose`], createLyap = factory(name$69, dependencies$69, (t4) => {
  var { typed: n2, matrix: r2, sylvester: i2, multiply: a2, transpose: o2 } = t4;
  return n2(name$69, { "Matrix, Matrix": function(t5, n3) {
    return i2(t5, o2(t5), a2(-1, n3));
  }, "Array, Matrix": function(t5, n3) {
    return i2(r2(t5), o2(r2(t5)), a2(-1, n3));
  }, "Matrix, Array": function(t5, n3) {
    return i2(t5, o2(r2(t5)), r2(a2(-1, n3)));
  }, "Array, Array": function(t5, n3) {
    return i2(r2(t5), o2(r2(t5)), r2(a2(-1, n3))).toArray();
  } });
}), name$68 = `divide`, dependencies$68 = [`typed`, `matrix`, `multiply`, `equalScalar`, `divideScalar`, `inv`], createDivide = factory(name$68, dependencies$68, (t4) => {
  var { typed: n2, matrix: r2, multiply: i2, equalScalar: a2, divideScalar: o2, inv: s2 } = t4, c2 = createMatAlgo11xS0s({ typed: n2, equalScalar: a2 }), l2 = createMatAlgo14xDs({ typed: n2 });
  return n2(`divide`, extend$1({ "Array | Matrix, Array | Matrix": function(t5, n3) {
    return i2(t5, s2(n3));
  }, "DenseMatrix, any": function(t5, n3) {
    return l2(t5, n3, o2, false);
  }, "SparseMatrix, any": function(t5, n3) {
    return c2(t5, n3, o2, false);
  }, "Array, any": function(t5, n3) {
    return l2(r2(t5), n3, o2, false).valueOf();
  }, "any, Array | Matrix": function(t5, n3) {
    return i2(t5, s2(n3));
  } }, o2.signatures));
}), name$67 = `distance`, dependencies$67 = [`typed`, `addScalar`, `subtractScalar`, `divideScalar`, `multiplyScalar`, `deepEqual`, `sqrt`, `abs`], createDistance = factory(name$67, dependencies$67, (t4) => {
  var { typed: n2, addScalar: r2, subtractScalar: i2, multiplyScalar: a2, divideScalar: o2, deepEqual: s2, sqrt: c2, abs: l2 } = t4;
  return n2(name$67, { "Array, Array, Array": function(t5, n3, r3) {
    if (t5.length === 2 && n3.length === 2 && r3.length === 2) {
      if (!d2(t5)) throw TypeError(`Array with 2 numbers or BigNumbers expected for first argument`);
      if (!d2(n3)) throw TypeError(`Array with 2 numbers or BigNumbers expected for second argument`);
      if (!d2(r3)) throw TypeError(`Array with 2 numbers or BigNumbers expected for third argument`);
      if (s2(n3, r3)) throw TypeError(`LinePoint1 should not be same with LinePoint2`);
      var o3 = i2(r3[1], n3[1]), c3 = i2(n3[0], r3[0]), l3 = i2(a2(r3[0], n3[1]), a2(n3[0], r3[1]));
      return y2(t5[0], t5[1], o3, c3, l3);
    } else throw TypeError(`Invalid Arguments: Try again`);
  }, "Object, Object, Object": function(t5, n3, r3) {
    if (Object.keys(t5).length === 2 && Object.keys(n3).length === 2 && Object.keys(r3).length === 2) {
      if (!d2(t5)) throw TypeError(`Values of pointX and pointY should be numbers or BigNumbers`);
      if (!d2(n3)) throw TypeError(`Values of lineOnePtX and lineOnePtY should be numbers or BigNumbers`);
      if (!d2(r3)) throw TypeError(`Values of lineTwoPtX and lineTwoPtY should be numbers or BigNumbers`);
      if (s2(g2(n3), g2(r3))) throw TypeError(`LinePoint1 should not be same with LinePoint2`);
      if (`pointX` in t5 && `pointY` in t5 && `lineOnePtX` in n3 && `lineOnePtY` in n3 && `lineTwoPtX` in r3 && `lineTwoPtY` in r3) {
        var o3 = i2(r3.lineTwoPtY, n3.lineOnePtY), c3 = i2(n3.lineOnePtX, r3.lineTwoPtX), l3 = i2(a2(r3.lineTwoPtX, n3.lineOnePtY), a2(n3.lineOnePtX, r3.lineTwoPtY));
        return y2(t5.pointX, t5.pointY, o3, c3, l3);
      } else throw TypeError(`Key names do not match`);
    } else throw TypeError(`Invalid Arguments: Try again`);
  }, "Array, Array": function(t5, n3) {
    if (t5.length === 2 && n3.length === 3) {
      if (!d2(t5)) throw TypeError(`Array with 2 numbers or BigNumbers expected for first argument`);
      if (!p2(n3)) throw TypeError(`Array with 3 numbers or BigNumbers expected for second argument`);
      return y2(t5[0], t5[1], n3[0], n3[1], n3[2]);
    } else if (t5.length === 3 && n3.length === 6) {
      if (!p2(t5)) throw TypeError(`Array with 3 numbers or BigNumbers expected for first argument`);
      if (!h2(n3)) throw TypeError(`Array with 6 numbers or BigNumbers expected for second argument`);
      return b2(t5[0], t5[1], t5[2], n3[0], n3[1], n3[2], n3[3], n3[4], n3[5]);
    } else if (t5.length === n3.length && t5.length > 0) {
      if (!m2(t5) || !m2(n3)) throw TypeError(`All values of an array should be numbers or BigNumbers`);
      return x2(t5, n3);
    } else throw TypeError(`Invalid Arguments: Try again`);
  }, "Object, Object": function(t5, n3) {
    if (Object.keys(t5).length === 2 && Object.keys(n3).length === 3) {
      if (!d2(t5)) throw TypeError(`Values of pointX and pointY should be numbers or BigNumbers`);
      if (!p2(n3)) throw TypeError(`Values of xCoeffLine, yCoeffLine and constant should be numbers or BigNumbers`);
      if (`pointX` in t5 && `pointY` in t5 && `xCoeffLine` in n3 && `yCoeffLine` in n3 && `constant` in n3) return y2(t5.pointX, t5.pointY, n3.xCoeffLine, n3.yCoeffLine, n3.constant);
      throw TypeError(`Key names do not match`);
    } else if (Object.keys(t5).length === 3 && Object.keys(n3).length === 6) {
      if (!p2(t5)) throw TypeError(`Values of pointX, pointY and pointZ should be numbers or BigNumbers`);
      if (!h2(n3)) throw TypeError(`Values of x0, y0, z0, a, b and c should be numbers or BigNumbers`);
      if (`pointX` in t5 && `pointY` in t5 && `x0` in n3 && `y0` in n3 && `z0` in n3 && `a` in n3 && `b` in n3 && `c` in n3) return b2(t5.pointX, t5.pointY, t5.pointZ, n3.x0, n3.y0, n3.z0, n3.a, n3.b, n3.c);
      throw TypeError(`Key names do not match`);
    } else if (Object.keys(t5).length === 2 && Object.keys(n3).length === 2) {
      if (!d2(t5)) throw TypeError(`Values of pointOneX and pointOneY should be numbers or BigNumbers`);
      if (!d2(n3)) throw TypeError(`Values of pointTwoX and pointTwoY should be numbers or BigNumbers`);
      if (`pointOneX` in t5 && `pointOneY` in t5 && `pointTwoX` in n3 && `pointTwoY` in n3) return x2([t5.pointOneX, t5.pointOneY], [n3.pointTwoX, n3.pointTwoY]);
      throw TypeError(`Key names do not match`);
    } else if (Object.keys(t5).length === 3 && Object.keys(n3).length === 3) {
      if (!p2(t5)) throw TypeError(`Values of pointOneX, pointOneY and pointOneZ should be numbers or BigNumbers`);
      if (!p2(n3)) throw TypeError(`Values of pointTwoX, pointTwoY and pointTwoZ should be numbers or BigNumbers`);
      if (`pointOneX` in t5 && `pointOneY` in t5 && `pointOneZ` in t5 && `pointTwoX` in n3 && `pointTwoY` in n3 && `pointTwoZ` in n3) return x2([t5.pointOneX, t5.pointOneY, t5.pointOneZ], [n3.pointTwoX, n3.pointTwoY, n3.pointTwoZ]);
      throw TypeError(`Key names do not match`);
    } else throw TypeError(`Invalid Arguments: Try again`);
  }, Array: function(t5) {
    if (!v2(t5)) throw TypeError(`Incorrect array format entered for pairwise distance calculation`);
    return S2(t5);
  } });
  function u2(t5) {
    return typeof t5 == `number` || isBigNumber(t5);
  }
  function d2(t5) {
    return t5.constructor !== Array && (t5 = g2(t5)), u2(t5[0]) && u2(t5[1]);
  }
  function p2(t5) {
    return t5.constructor !== Array && (t5 = g2(t5)), u2(t5[0]) && u2(t5[1]) && u2(t5[2]);
  }
  function m2(t5) {
    return Array.isArray(t5) || (t5 = g2(t5)), t5.every(u2);
  }
  function h2(t5) {
    return t5.constructor !== Array && (t5 = g2(t5)), u2(t5[0]) && u2(t5[1]) && u2(t5[2]) && u2(t5[3]) && u2(t5[4]) && u2(t5[5]);
  }
  function g2(t5) {
    for (var n3 = Object.keys(t5), r3 = [], i3 = 0; i3 < n3.length; i3++) r3.push(t5[n3[i3]]);
    return r3;
  }
  function v2(t5) {
    if (t5[0].length === 2 && u2(t5[0][0]) && u2(t5[0][1])) {
      if (t5.some((t6) => t6.length !== 2 || !u2(t6[0]) || !u2(t6[1]))) return false;
    } else if (t5[0].length === 3 && u2(t5[0][0]) && u2(t5[0][1]) && u2(t5[0][2])) {
      if (t5.some((t6) => t6.length !== 3 || !u2(t6[0]) || !u2(t6[1]) || !u2(t6[2]))) return false;
    } else return false;
    return true;
  }
  function y2(t5, n3, i3, s3, u3) {
    return o2(l2(r2(r2(a2(i3, t5), a2(s3, n3)), u3)), c2(r2(a2(i3, i3), a2(s3, s3))));
  }
  function b2(t5, n3, s3, l3, u3, d3, p3, m3, h3) {
    var g3 = [i2(a2(i2(u3, n3), h3), a2(i2(d3, s3), m3)), i2(a2(i2(d3, s3), p3), a2(i2(l3, t5), h3)), i2(a2(i2(l3, t5), m3), a2(i2(u3, n3), p3))];
    g3 = c2(r2(r2(a2(g3[0], g3[0]), a2(g3[1], g3[1])), a2(g3[2], g3[2])));
    var v3 = c2(r2(r2(a2(p3, p3), a2(m3, m3)), a2(h3, h3)));
    return o2(g3, v3);
  }
  function x2(t5, n3) {
    for (var o3 = t5.length, s3 = 0, l3 = 0, u3 = 0; u3 < o3; u3++) l3 = i2(t5[u3], n3[u3]), s3 = r2(a2(l3, l3), s3);
    return c2(s3);
  }
  function S2(t5) {
    for (var n3 = [], r3 = [], i3 = [], a3 = 0; a3 < t5.length - 1; a3++) for (var o3 = a3 + 1; o3 < t5.length; o3++) t5[0].length === 2 ? (r3 = [t5[a3][0], t5[a3][1]], i3 = [t5[o3][0], t5[o3][1]]) : t5[0].length === 3 && (r3 = [t5[a3][0], t5[a3][1], t5[a3][2]], i3 = [t5[o3][0], t5[o3][1], t5[o3][2]]), n3.push(x2(r3, i3));
    return n3;
  }
}), name$66 = `intersect`, dependencies$66 = [`typed`, `config`, `abs`, `add`, `addScalar`, `matrix`, `multiply`, `multiplyScalar`, `divideScalar`, `subtract`, `smaller`, `equalScalar`, `flatten`, `isZero`, `isNumeric`], createIntersect = factory(name$66, dependencies$66, (t4) => {
  var { typed: n2, config: r2, abs: i2, add: a2, addScalar: o2, matrix: s2, multiply: c2, multiplyScalar: l2, divideScalar: u2, subtract: d2, smaller: p2, equalScalar: m2, flatten: h2, isZero: g2, isNumeric: v2 } = t4;
  return n2(`intersect`, { "Array, Array, Array": y2, "Array, Array, Array, Array": b2, "Matrix, Matrix, Matrix": function(t5, n3, r3) {
    var i3 = y2(t5.valueOf(), n3.valueOf(), r3.valueOf());
    return i3 === null ? null : s2(i3);
  }, "Matrix, Matrix, Matrix, Matrix": function(t5, n3, r3, i3) {
    var a3 = b2(t5.valueOf(), n3.valueOf(), r3.valueOf(), i3.valueOf());
    return a3 === null ? null : s2(a3);
  } });
  function y2(t5, n3, r3) {
    if (t5 = x2(t5), n3 = x2(n3), r3 = x2(r3), !w2(t5)) throw TypeError(`Array with 3 numbers or BigNumbers expected for first argument`);
    if (!w2(n3)) throw TypeError(`Array with 3 numbers or BigNumbers expected for second argument`);
    if (!E(r3)) throw TypeError(`Array with 4 numbers expected as third argument`);
    return M2(t5[0], t5[1], t5[2], n3[0], n3[1], n3[2], r3[0], r3[1], r3[2], r3[3]);
  }
  function b2(t5, n3, r3, i3) {
    if (t5 = x2(t5), n3 = x2(n3), r3 = x2(r3), i3 = x2(i3), t5.length === 2) {
      if (!S2(t5)) throw TypeError(`Array with 2 numbers or BigNumbers expected for first argument`);
      if (!S2(n3)) throw TypeError(`Array with 2 numbers or BigNumbers expected for second argument`);
      if (!S2(r3)) throw TypeError(`Array with 2 numbers or BigNumbers expected for third argument`);
      if (!S2(i3)) throw TypeError(`Array with 2 numbers or BigNumbers expected for fourth argument`);
      return D(t5, n3, r3, i3);
    } else if (t5.length === 3) {
      if (!w2(t5)) throw TypeError(`Array with 3 numbers or BigNumbers expected for first argument`);
      if (!w2(n3)) throw TypeError(`Array with 3 numbers or BigNumbers expected for second argument`);
      if (!w2(r3)) throw TypeError(`Array with 3 numbers or BigNumbers expected for third argument`);
      if (!w2(i3)) throw TypeError(`Array with 3 numbers or BigNumbers expected for fourth argument`);
      return j2(t5[0], t5[1], t5[2], n3[0], n3[1], n3[2], r3[0], r3[1], r3[2], i3[0], i3[1], i3[2]);
    } else throw TypeError(`Arrays with two or thee dimensional points expected`);
  }
  function x2(t5) {
    return t5.length === 1 ? t5[0] : t5.length > 1 && Array.isArray(t5[0]) && t5.every((t6) => Array.isArray(t6) && t6.length === 1) ? h2(t5) : t5;
  }
  function S2(t5) {
    return t5.length === 2 && v2(t5[0]) && v2(t5[1]);
  }
  function w2(t5) {
    return t5.length === 3 && v2(t5[0]) && v2(t5[1]) && v2(t5[2]);
  }
  function E(t5) {
    return t5.length === 4 && v2(t5[0]) && v2(t5[1]) && v2(t5[2]) && v2(t5[3]);
  }
  function D(t5, n3, s3, m3) {
    var h3 = t5, v3 = s3, y3 = d2(h3, n3), b3 = d2(v3, m3), x3 = d2(l2(y3[0], b3[1]), l2(b3[0], y3[1]));
    if (g2(x3) || p2(i2(x3), r2.relTol)) return null;
    var S3 = l2(b3[0], h3[1]), w3 = l2(b3[1], h3[0]), E2 = l2(b3[0], v3[1]), D2 = l2(b3[1], v3[0]);
    return a2(c2(y3, u2(o2(d2(d2(S3, w3), E2), D2), x3)), h3);
  }
  function O(t5, n3, r3, i3, a3, s3, c3, u3, p3, m3, h3, g3) {
    var v3 = l2(d2(t5, n3), d2(r3, i3)), y3 = l2(d2(a3, s3), d2(c3, u3)), b3 = l2(d2(p3, m3), d2(h3, g3));
    return o2(o2(v3, y3), b3);
  }
  function j2(t5, n3, r3, i3, a3, s3, c3, p3, h3, v3, y3, b3) {
    var x3 = O(t5, c3, v3, c3, n3, p3, y3, p3, r3, h3, b3, h3), S3 = O(v3, c3, i3, t5, y3, p3, a3, n3, b3, h3, s3, r3), w3 = O(t5, c3, i3, t5, n3, p3, a3, n3, r3, h3, s3, r3), E2 = O(v3, c3, v3, c3, y3, p3, y3, p3, b3, h3, b3, h3), D2 = O(i3, t5, i3, t5, a3, n3, a3, n3, s3, r3, s3, r3), j3 = d2(l2(x3, S3), l2(w3, E2)), M3 = d2(l2(D2, E2), l2(S3, S3));
    if (g2(M3)) return null;
    var N2 = u2(j3, M3), F = u2(o2(x3, l2(N2, S3)), E2), I = o2(t5, l2(N2, d2(i3, t5))), L = o2(n3, l2(N2, d2(a3, n3))), R = o2(r3, l2(N2, d2(s3, r3))), z = o2(c3, l2(F, d2(v3, c3))), V = o2(p3, l2(F, d2(y3, p3))), H2 = o2(h3, l2(F, d2(b3, h3)));
    return m2(I, z) && m2(L, V) && m2(R, H2) ? [I, L, R] : null;
  }
  function M2(t5, n3, r3, i3, a3, s3, c3, p3, m3, h3) {
    var g3 = l2(t5, c3), v3 = l2(i3, c3), y3 = l2(n3, p3), b3 = l2(a3, p3), x3 = l2(r3, m3), S3 = l2(s3, m3), w3 = u2(d2(d2(d2(h3, g3), y3), x3), d2(d2(d2(o2(o2(v3, b3), S3), g3), y3), x3));
    return [o2(t5, l2(w3, d2(i3, t5))), o2(n3, l2(w3, d2(a3, n3))), o2(r3, l2(w3, d2(s3, r3)))];
  }
}), name$65 = `sum`, dependencies$65 = [`typed`, `config`, `add`, `numeric`], createSum = factory(name$65, dependencies$65, (t4) => {
  var { typed: n2, config: r2, add: i2, numeric: a2 } = t4;
  return n2(name$65, { "Array | Matrix": o2, "Array | Matrix, number | BigNumber": s2, "...": function(t5) {
    if (containsCollections(t5)) throw TypeError(`Scalar values expected in function sum`);
    return o2(t5);
  } });
  function o2(t5) {
    var n3;
    return deepForEach(t5, function(t6) {
      try {
        n3 = n3 === void 0 ? t6 : i2(n3, t6);
      } catch (n4) {
        throw improveErrorMessage(n4, `sum`, t6);
      }
    }), n3 === void 0 && (n3 = a2(0, r2.number)), typeof n3 == `string` && (n3 = a2(n3, safeNumberType(n3, r2))), n3;
  }
  function s2(t5, n3) {
    try {
      return reduce$1(t5, n3, i2);
    } catch (t6) {
      throw improveErrorMessage(t6, `sum`);
    }
  }
}), name$64 = `cumsum`, dependencies$64 = [`typed`, `add`, `unaryPlus`], createCumSum = factory(name$64, dependencies$64, (t4) => {
  var { typed: n2, add: r2, unaryPlus: i2 } = t4;
  return n2(name$64, { Array: a2, Matrix: function(t5) {
    return t5.create(a2(t5.valueOf(), t5.datatype()));
  }, "Array, number | BigNumber": s2, "Matrix, number | BigNumber": function(t5, n3) {
    return t5.create(s2(t5.valueOf(), n3), t5.datatype());
  }, "...": function(t5) {
    if (containsCollections(t5)) throw TypeError(`All values expected to be scalar in function cumsum`);
    return a2(t5);
  } });
  function a2(t5) {
    try {
      return o2(t5);
    } catch (t6) {
      throw improveErrorMessage(t6, name$64);
    }
  }
  function o2(t5) {
    if (t5.length === 0) return [];
    for (var n3 = [i2(t5[0])], a3 = 1; a3 < t5.length; ++a3) n3.push(r2(n3[a3 - 1], t5[a3]));
    return n3;
  }
  function s2(t5, n3) {
    var r3 = arraySize(t5);
    if (n3 < 0 || n3 >= r3.length) throw new IndexError(n3, r3.length);
    try {
      return c2(t5, n3);
    } catch (t6) {
      throw improveErrorMessage(t6, name$64);
    }
  }
  function c2(t5, n3) {
    var r3, i3, a3;
    if (n3 <= 0) {
      var s3 = t5[0][0];
      if (Array.isArray(s3)) {
        for (a3 = _switch$1(t5), i3 = [], r3 = 0; r3 < a3.length; r3++) i3[r3] = c2(a3[r3], n3 - 1);
        return i3;
      } else return o2(t5);
    } else {
      for (i3 = [], r3 = 0; r3 < t5.length; r3++) i3[r3] = c2(t5[r3], n3 - 1);
      return i3;
    }
  }
}), name$63 = `mean`, dependencies$63 = [`typed`, `add`, `divide`], createMean = factory(name$63, dependencies$63, (t4) => {
  var { typed: n2, add: r2, divide: i2 } = t4;
  return n2(name$63, { "Array | Matrix": o2, "Array | Matrix, number | BigNumber": a2, "...": function(t5) {
    if (containsCollections(t5)) throw TypeError(`Scalar values expected in function mean`);
    return o2(t5);
  } });
  function a2(t5, n3) {
    try {
      return i2(reduce$1(t5, n3, r2), (Array.isArray(t5) ? arraySize(t5) : t5.size())[n3]);
    } catch (t6) {
      throw improveErrorMessage(t6, `mean`);
    }
  }
  function o2(t5) {
    var n3, a3 = 0;
    if (deepForEach(t5, function(t6) {
      try {
        n3 = n3 === void 0 ? t6 : r2(n3, t6), a3++;
      } catch (n4) {
        throw improveErrorMessage(n4, `mean`, t6);
      }
    }), a3 === 0) throw Error(`Cannot calculate the mean of an empty array`);
    return i2(n3, a3);
  }
}), name$62 = `median`, dependencies$62 = [`typed`, `add`, `divide`, `compare`, `partitionSelect`], createMedian = factory(name$62, dependencies$62, (t4) => {
  var { typed: n2, add: r2, divide: i2, compare: a2, partitionSelect: o2 } = t4;
  function s2(t5) {
    try {
      t5 = flatten$4(t5.valueOf());
      var n3 = t5.length;
      if (n3 === 0) throw Error(`Cannot calculate median of an empty array`);
      if (n3 % 2 == 0) {
        for (var r3 = n3 / 2 - 1, i3 = o2(t5, r3 + 1), s3 = t5[r3], u2 = 0; u2 < r3; ++u2) a2(t5[u2], s3) > 0 && (s3 = t5[u2]);
        return l2(s3, i3);
      } else return c2(o2(t5, (n3 - 1) / 2));
    } catch (t6) {
      throw improveErrorMessage(t6, `median`);
    }
  }
  var c2 = n2({ "number | BigNumber | Complex | Unit": function(t5) {
    return t5;
  } }), l2 = n2({ "number | BigNumber | Complex | Unit, number | BigNumber | Complex | Unit": function(t5, n3) {
    return i2(r2(t5, n3), 2);
  } });
  return n2(name$62, { "Array | Matrix": s2, "Array | Matrix, number | BigNumber": function(t5, n3) {
    throw Error(`median(A, dim) is not yet supported`);
  }, "...": function(t5) {
    if (containsCollections(t5)) throw TypeError(`Scalar values expected in function median`);
    return s2(t5);
  } });
}), name$61 = `mad`, dependencies$61 = [`typed`, `abs`, `map`, `median`, `subtract`], createMad = factory(name$61, dependencies$61, (t4) => {
  var { typed: n2, abs: r2, map: i2, median: a2, subtract: o2 } = t4;
  return n2(name$61, { "Array | Matrix": s2, "...": function(t5) {
    return s2(t5);
  } });
  function s2(t5) {
    if (t5 = flatten$4(t5.valueOf()), t5.length === 0) throw Error(`Cannot calculate median absolute deviation (mad) of an empty array`);
    try {
      var n3 = a2(t5);
      return a2(i2(t5, function(t6) {
        return r2(o2(t6, n3));
      }));
    } catch (t6) {
      throw t6 instanceof TypeError && t6.message.includes(`median`) ? TypeError(t6.message.replace(`median`, `mad`)) : improveErrorMessage(t6, `mad`);
    }
  }
}), DEFAULT_NORMALIZATION = `unbiased`, name$60 = `variance`, dependencies$60 = [`typed`, `add`, `subtract`, `multiply`, `divide`, `mapSlices`, `isNaN`], createVariance = factory(name$60, dependencies$60, (t4) => {
  var { typed: n2, add: r2, subtract: i2, multiply: a2, divide: o2, mapSlices: s2, isNaN: c2 } = t4;
  return n2(name$60, { "Array | Matrix": function(t5) {
    return l2(t5, DEFAULT_NORMALIZATION);
  }, "Array | Matrix, string": l2, "Array | Matrix, number | BigNumber": function(t5, n3) {
    return u2(t5, n3, DEFAULT_NORMALIZATION);
  }, "Array | Matrix, number | BigNumber, string": u2, "...": function(t5) {
    return l2(t5, DEFAULT_NORMALIZATION);
  } });
  function l2(t5, n3) {
    var s3, l3 = 0;
    if (t5.length === 0) throw SyntaxError(`Function variance requires one or more parameters (0 provided)`);
    if (deepForEach(t5, function(t6) {
      try {
        s3 = s3 === void 0 ? t6 : r2(s3, t6), l3++;
      } catch (n4) {
        throw improveErrorMessage(n4, `variance`, t6);
      }
    }), l3 === 0) throw Error(`Cannot calculate variance of an empty array`);
    var u3 = o2(s3, l3);
    if (s3 = void 0, deepForEach(t5, function(t6) {
      var n4 = i2(t6, u3);
      s3 = s3 === void 0 ? a2(n4, n4) : r2(s3, a2(n4, n4));
    }), c2(s3)) return s3;
    switch (n3) {
      case `uncorrected`:
        return o2(s3, l3);
      case `biased`:
        return o2(s3, l3 + 1);
      case `unbiased`:
        var d2 = isBigNumber(s3) ? s3.mul(0) : 0;
        return l3 === 1 ? d2 : o2(s3, l3 - 1);
      default:
        throw Error(`Unknown normalization "` + n3 + `". Choose "unbiased" (default), "uncorrected", or "biased".`);
    }
  }
  function u2(t5, n3, r3) {
    try {
      if (t5.length === 0) throw SyntaxError(`Function variance requires one or more parameters (0 provided)`);
      return s2(t5, n3, (t6) => l2(t6, r3));
    } catch (t6) {
      throw improveErrorMessage(t6, `variance`);
    }
  }
}), name$59 = `quantileSeq`, dependencies$59 = [`typed`, `?bignumber`, `add`, `subtract`, `divide`, `multiply`, `partitionSelect`, `compare`, `isInteger`, `smaller`, `smallerEq`, `larger`, `mapSlices`], createQuantileSeq = factory(name$59, dependencies$59, (t4) => {
  var { typed: n2, bignumber: r2, add: i2, subtract: a2, divide: o2, multiply: s2, partitionSelect: c2, compare: l2, isInteger: u2, smaller: d2, smallerEq: p2, larger: m2, mapSlices: h2 } = t4;
  return n2(name$59, { "Array | Matrix, number | BigNumber": (t5, n3) => v2(t5, n3, false), "Array | Matrix, number | BigNumber, number": (t5, n3, r3) => g2(t5, n3, false, r3, v2), "Array | Matrix, number | BigNumber, boolean": v2, "Array | Matrix, number | BigNumber, boolean, number": (t5, n3, r3, i3) => g2(t5, n3, r3, i3, v2), "Array | Matrix, Array | Matrix": (t5, n3) => y2(t5, n3, false), "Array | Matrix, Array | Matrix, number": (t5, n3, r3) => g2(t5, n3, false, r3, y2), "Array | Matrix, Array | Matrix, boolean": y2, "Array | Matrix, Array | Matrix, boolean, number": (t5, n3, r3, i3) => g2(t5, n3, r3, i3, y2) });
  function g2(t5, n3, r3, i3, a3) {
    return h2(t5, i3, (t6) => a3(t6, n3, r3));
  }
  function v2(t5, n3, a3) {
    var s3, c3 = t5.valueOf();
    if (d2(n3, 0)) throw Error(`N/prob must be non-negative`);
    if (p2(n3, 1)) return isNumber(n3) ? b2(c3, n3, a3) : r2(b2(c3, n3, a3));
    if (m2(n3, 1)) {
      if (!u2(n3)) throw Error(`N must be a positive integer`);
      if (m2(n3, 4294967295)) throw Error(`N must be less than or equal to 2^32-1, as that is the maximum length of an Array`);
      var l3 = i2(n3, 1);
      s3 = [];
      for (var h3 = 0; d2(h3, n3); h3++) {
        var g3 = o2(h3 + 1, l3);
        s3.push(b2(c3, g3, a3));
      }
      return isNumber(n3) ? s3 : r2(s3);
    }
  }
  function y2(t5, n3, r3) {
    for (var i3 = t5.valueOf(), a3 = n3.valueOf(), o3 = [], s3 = 0; s3 < a3.length; ++s3) o3.push(b2(i3, a3[s3], r3));
    return o3;
  }
  function b2(t5, n3, r3) {
    var o3 = flatten$4(t5), d3 = o3.length;
    if (d3 === 0) throw Error(`Cannot calculate quantile of an empty sequence`);
    var p3 = isNumber(n3) ? n3 * (d3 - 1) : n3.times(d3 - 1), m3 = isNumber(n3) ? Math.floor(p3) : p3.floor().toNumber(), h3 = isNumber(n3) ? p3 % 1 : p3.minus(m3);
    if (u2(p3)) return r3 ? o3[p3] : c2(o3, isNumber(n3) ? p3 : p3.valueOf());
    var g3, v3;
    if (r3) g3 = o3[m3], v3 = o3[m3 + 1];
    else {
      v3 = c2(o3, m3 + 1), g3 = o3[m3];
      for (var y3 = 0; y3 < m3; ++y3) l2(o3[y3], g3) > 0 && (g3 = o3[y3]);
    }
    return i2(s2(g3, a2(1, h3)), s2(v3, h3));
  }
}), name$58 = `std`, dependencies$58 = [`typed`, `map`, `sqrt`, `variance`], createStd = factory(name$58, dependencies$58, (t4) => {
  var { typed: n2, map: r2, sqrt: i2, variance: a2 } = t4;
  return n2(name$58, { "Array | Matrix": o2, "Array | Matrix, string": o2, "Array | Matrix, number | BigNumber": o2, "Array | Matrix, number | BigNumber, string": o2, "...": function(t5) {
    return o2(t5);
  } });
  function o2(t5, n3) {
    if (t5.length === 0) throw SyntaxError(`Function std requires one or more parameters (0 provided)`);
    try {
      var o3 = a2.apply(null, arguments);
      return isCollection(o3) ? r2(o3, i2) : i2(o3);
    } catch (t6) {
      throw t6 instanceof TypeError && t6.message.includes(` variance`) ? TypeError(t6.message.replace(` variance`, ` std`)) : t6;
    }
  }
}), name$57 = `corr`, dependencies$57 = [`typed`, `matrix`, `mean`, `sqrt`, `sum`, `add`, `subtract`, `multiply`, `pow`, `divide`], createCorr = factory(name$57, dependencies$57, (t4) => {
  var { typed: n2, matrix: r2, sqrt: i2, sum: a2, add: o2, subtract: s2, multiply: c2, pow: l2, divide: u2 } = t4;
  return n2(name$57, { "Array, Array": function(t5, n3) {
    return d2(t5, n3);
  }, "Matrix, Matrix": function(t5, n3) {
    var i3 = d2(t5.toArray(), n3.toArray());
    return Array.isArray(i3) ? r2(i3) : i3;
  } });
  function d2(t5, n3) {
    var r3 = [];
    if (Array.isArray(t5[0]) && Array.isArray(n3[0])) {
      if (t5.length !== n3.length) throw SyntaxError(`Dimension mismatch. Array A and B must have the same length.`);
      for (var i3 = 0; i3 < t5.length; i3++) {
        if (t5[i3].length !== n3[i3].length) throw SyntaxError(`Dimension mismatch. Array A and B must have the same number of elements.`);
        r3.push(p2(t5[i3], n3[i3]));
      }
      return r3;
    } else {
      if (t5.length !== n3.length) throw SyntaxError(`Dimension mismatch. Array A and B must have the same number of elements.`);
      return p2(t5, n3);
    }
  }
  function p2(t5, n3) {
    var r3 = t5.length, d3 = a2(t5), p3 = a2(n3), m2 = t5.reduce((t6, r4, i3) => o2(t6, c2(r4, n3[i3])), 0), h2 = a2(t5.map((t6) => l2(t6, 2))), g2 = a2(n3.map((t6) => l2(t6, 2)));
    return u2(s2(c2(r3, m2), c2(d3, p3)), i2(c2(s2(c2(r3, h2), l2(d3, 2)), s2(c2(r3, g2), l2(p3, 2)))));
  }
}), name$56 = `bernoulli`, dependencies$56 = [`typed`, `config`, `isInteger`, `number`, `?BigNumber`, `?Fraction`], createBernoulli = factory(name$56, dependencies$56, (t4) => {
  var { typed: n2, config: r2, number: i2, BigNumber: a2, Fraction: o2 } = t4, s2 = [void 0], c2 = [void 0], l2 = [void 0], u2 = 50;
  return n2(name$56, { number: (t5) => _bernoulli(t5, (t6) => t6, s2, (t6, n3) => t6 + n3, (t6, n3) => t6 * n3, (t6, n3) => t6 / n3), "bigint | Fraction": (t5) => _bernoulli(i2(t5), (t6) => new o2(t6), c2, (t6, n3) => t6.add(n3), (t6, n3) => t6.mul(n3), (t6, n3) => t6.div(n3)), BigNumber: (t5) => (r2.precision !== u2 && (l2 = [void 0], u2 = r2.precision), _bernoulli(i2(t5), (t6) => new a2(t6), l2, (t6, n3) => t6.add(n3), (t6, n3) => t6.mul(n3), (t6, n3) => t6.div(n3))) });
});
function _bernoulli(t4, n2, r2, i2, a2, o2) {
  if (t4 < 0 || !isInteger(t4)) throw RangeError(`Bernoulli index must be nonnegative integer`);
  if (t4 === 0) return n2(1);
  if (t4 === 1) return o2(n2(-1), n2(2));
  if (t4 % 2 == 1) return n2(0);
  var s2 = n2(1);
  r2.length === 1 && r2.push([o2(s2, n2(-3)), o2(s2, n2(-2)), o2(s2, n2(6))]);
  for (var c2 = t4 / 2, l2 = n2(0), u2 = n2(2); r2.length <= c2; ) {
    for (var d2 = r2.length, p2 = Math.floor((d2 + 1) / 2), m2 = l2, h2 = 1; h2 < p2; ++h2) m2 = i2(m2, a2(r2[h2][0], r2[d2 - h2][0]));
    m2 = a2(m2, u2), d2 % 2 == 0 && (m2 = i2(m2, a2(r2[p2][0], r2[p2][0]))), m2 = o2(m2, n2(-(2 * d2 + 1)));
    var g2 = o2(a2(r2[d2 - 1][1], n2(-d2 * (2 * d2 - 1))), u2);
    r2.push([m2, g2, a2(g2, m2)]);
  }
  return r2[c2][2];
}
var name$55 = `combinations`, dependencies$55 = [`typed`], createCombinations = factory(name$55, dependencies$55, (t4) => {
  var { typed: n2 } = t4;
  return n2(name$55, { "number, number": combinationsNumber, "BigNumber, BigNumber": function(t5, n3) {
    var r2 = t5.constructor, i2, a2, o2 = t5.minus(n3), s2 = new r2(1);
    if (!isPositiveInteger$2(t5) || !isPositiveInteger$2(n3)) throw TypeError(`Positive integer value expected in function combinations`);
    if (n3.gt(t5)) throw TypeError(`k must be less than n in function combinations`);
    if (i2 = s2, n3.lt(o2)) for (a2 = s2; a2.lte(o2); a2 = a2.plus(s2)) i2 = i2.times(n3.plus(a2)).dividedBy(a2);
    else for (a2 = s2; a2.lte(n3); a2 = a2.plus(s2)) i2 = i2.times(o2.plus(a2)).dividedBy(a2);
    return i2;
  } });
});
function isPositiveInteger$2(t4) {
  return t4.isInteger() && t4.gte(0);
}
var name$54 = `combinationsWithRep`, dependencies$54 = [`typed`], createCombinationsWithRep = factory(name$54, dependencies$54, (t4) => {
  var { typed: n2 } = t4;
  return n2(name$54, { "number, number": function(t5, n3) {
    if (!isInteger(t5) || t5 < 0 || !isInteger(n3) || n3 < 0) throw TypeError(`Positive integer value expected in function combinationsWithRep`);
    if (t5 < 1) throw TypeError(`k must be less than or equal to n + k - 1`);
    return n3 < t5 - 1 ? product(t5, t5 + n3 - 1) / product(1, n3) : product(n3 + 1, t5 + n3 - 1) / product(1, t5 - 1);
  }, "BigNumber, BigNumber": function(t5, n3) {
    var r2 = t5.constructor, i2, a2, o2 = new r2(1), s2 = t5.minus(o2);
    if (!isPositiveInteger$1(t5) || !isPositiveInteger$1(n3)) throw TypeError(`Positive integer value expected in function combinationsWithRep`);
    if (t5.lt(o2)) throw TypeError(`k must be less than or equal to n + k - 1 in function combinationsWithRep`);
    if (i2 = o2, n3.lt(s2)) for (a2 = o2; a2.lte(s2); a2 = a2.plus(o2)) i2 = i2.times(n3.plus(a2)).dividedBy(a2);
    else for (a2 = o2; a2.lte(n3); a2 = a2.plus(o2)) i2 = i2.times(s2.plus(a2)).dividedBy(a2);
    return i2;
  } });
});
function isPositiveInteger$1(t4) {
  return t4.isInteger() && t4.gte(0);
}
var name$53 = `gamma`, dependencies$53 = [`typed`, `config`, `multiplyScalar`, `pow`, `BigNumber`, `Complex`], createGamma = factory(name$53, dependencies$53, (t4) => {
  var { typed: n2, config: r2, multiplyScalar: i2, pow: a2, BigNumber: o2, Complex: s2 } = t4;
  function c2(t5) {
    if (t5.im === 0) return gammaNumber(t5.re);
    if (t5.re < 0.5) {
      var n3 = new s2(1 - t5.re, -t5.im), r3 = new s2(Math.PI * t5.re, Math.PI * t5.im);
      return new s2(Math.PI).div(r3.sin()).div(c2(n3));
    }
    t5 = new s2(t5.re - 1, t5.im);
    for (var i3 = new s2(gammaP[0], 0), a3 = 1; a3 < gammaP.length; ++a3) {
      var o3 = new s2(gammaP[a3], 0);
      i3 = i3.add(o3.div(t5.add(a3)));
    }
    var l3 = new s2(t5.re + gammaG + 0.5, t5.im), u2 = Math.sqrt(2 * Math.PI), d2 = l3.pow(t5.add(0.5)), p2 = l3.neg().exp();
    return i3.mul(u2).mul(d2).mul(p2);
  }
  return n2(name$53, { number: gammaNumber, Complex: c2, BigNumber: function(t5) {
    if (t5.isInteger()) return t5.isNegative() || t5.isZero() ? new o2(1 / 0) : l2(t5.minus(1));
    if (!t5.isFinite()) return new o2(t5.isNegative() ? NaN : 1 / 0);
    throw Error(`Integer BigNumber expected`);
  } });
  function l2(t5) {
    if (t5 < 8) return new o2([1, 1, 2, 6, 24, 120, 720, 5040][t5]);
    var n3 = r2.precision + (Math.log(t5.toNumber()) | 0), i3 = o2.clone({ precision: n3 });
    if (t5 % 2 == 1) return t5.times(l2(new o2(t5 - 1)));
    for (var a3 = t5, s3 = new i3(t5), c3 = t5.toNumber(); a3 > 2; ) a3 -= 2, c3 += a3, s3 = s3.times(c3);
    return new o2(s3.toPrecision(o2.precision));
  }
}), name$52 = `lgamma`, dependencies$52 = [`Complex`, `typed`], createLgamma = factory(name$52, dependencies$52, (t4) => {
  var { Complex: n2, typed: r2 } = t4, i2 = 7, a2 = 7, o2 = [-0.029550653594771242, 0.00641025641025641, -0.0019175269175269176, 8417508417508417e-19, -5952380952380953e-19, 7936507936507937e-19, -0.002777777777777778, 0.08333333333333333];
  return r2(name$52, { number: lgammaNumber, Complex: s2, BigNumber: function() {
    throw Error(`mathjs doesn't yet provide an implementation of the algorithm lgamma for BigNumber`);
  } });
  function s2(t5) {
    var r3 = 6.283185307179586, o3 = 1.1447298858494002, u2 = 0.1;
    if (t5.isNaN()) return new n2(NaN, NaN);
    if (t5.im === 0) return new n2(lgammaNumber(t5.re), 0);
    if (t5.re >= i2 || Math.abs(t5.im) >= a2) return c2(t5);
    if (t5.re <= u2) {
      var d2 = copysign(r3, t5.im) * Math.floor(0.5 * t5.re + 0.25), p2 = t5.mul(Math.PI).sin().log(), m2 = s2(new n2(1 - t5.re, -t5.im));
      return new n2(o3, d2).sub(p2).sub(m2);
    } else if (t5.im >= 0) return l2(t5);
    else return l2(t5.conjugate()).conjugate();
  }
  function c2(t5) {
    for (var r3 = t5.sub(0.5).mul(t5.log()).sub(t5).add(lnSqrt2PI), i3 = new n2(1, 0).div(t5), a3 = i3.div(t5), s3 = o2[0], c3 = o2[1], l3 = 2 * a3.re, u2 = a3.re * a3.re + a3.im * a3.im, d2 = 2; d2 < 8; d2++) {
      var p2 = c3;
      c3 = -u2 * s3 + o2[d2], s3 = l3 * s3 + p2;
    }
    var m2 = i3.mul(a3.mul(s3).add(c3));
    return r3.add(m2);
  }
  function l2(t5) {
    var r3 = 0, a3 = 0, o3 = t5;
    for (t5 = t5.add(1); t5.re <= i2; ) {
      o3 = o3.mul(t5);
      var s3 = +(o3.im < 0);
      s3 !== 0 && a3 === 0 && r3++, a3 = s3, t5 = t5.add(1);
    }
    return c2(t5).sub(o3.log()).sub(new n2(0, r3 * 2 * Math.PI * 1));
  }
}), name$51 = `factorial`, dependencies$51 = [`typed`, `gamma`], createFactorial = factory(name$51, dependencies$51, (t4) => {
  var { typed: n2, gamma: r2 } = t4;
  return n2(name$51, { number: function(t5) {
    if (t5 < 0) throw Error(`Value must be non-negative`);
    return r2(t5 + 1);
  }, BigNumber: function(t5) {
    if (t5.isNegative()) throw Error(`Value must be non-negative`);
    return r2(t5.plus(1));
  }, "Array | Matrix": n2.referToSelf((t5) => (n3) => deepMap(n3, t5)) });
}), name$50 = `kldivergence`, dependencies$50 = [`typed`, `matrix`, `divide`, `sum`, `multiply`, `map`, `dotDivide`, `log`, `isNumeric`], createKldivergence = factory(name$50, dependencies$50, (t4) => {
  var { typed: n2, matrix: r2, divide: i2, sum: a2, multiply: o2, map: s2, dotDivide: c2, log: l2, isNumeric: u2 } = t4;
  return n2(name$50, { "Array, Array": function(t5, n3) {
    return d2(r2(t5), r2(n3));
  }, "Matrix, Array": function(t5, n3) {
    return d2(t5, r2(n3));
  }, "Array, Matrix": function(t5, n3) {
    return d2(r2(t5), n3);
  }, "Matrix, Matrix": function(t5, n3) {
    return d2(t5, n3);
  } });
  function d2(t5, n3) {
    var r3 = n3.size().length, d3 = t5.size().length;
    if (r3 > 1) throw Error(`first object must be one dimensional`);
    if (d3 > 1) throw Error(`second object must be one dimensional`);
    if (r3 !== d3) throw Error(`Length of two vectors must be equal`);
    if (a2(t5) === 0) throw Error(`Sum of elements in first object must be non zero`);
    if (a2(n3) === 0) throw Error(`Sum of elements in second object must be non zero`);
    var p2 = i2(t5, a2(t5)), m2 = a2(o2(p2, s2(c2(p2, i2(n3, a2(n3))), (t6) => l2(t6))));
    return u2(m2) ? m2 : NaN;
  }
}), name$49 = `multinomial`, dependencies$49 = [`typed`, `add`, `divide`, `multiply`, `factorial`, `isInteger`, `isPositive`], createMultinomial = factory(name$49, dependencies$49, (t4) => {
  var { typed: n2, add: r2, divide: i2, multiply: a2, factorial: o2, isInteger: s2, isPositive: c2 } = t4;
  return n2(name$49, { "Array | Matrix": function(t5) {
    var n3 = 0, l2 = 1;
    return deepForEach(t5, function(t6) {
      if (!s2(t6) || !c2(t6)) throw TypeError(`Positive integer value expected in function multinomial`);
      n3 = r2(n3, t6), l2 = a2(l2, o2(t6));
    }), i2(o2(n3), l2);
  } });
}), name$48 = `permutations`, dependencies$48 = [`typed`, `factorial`], createPermutations = factory(name$48, dependencies$48, (t4) => {
  var { typed: n2, factorial: r2 } = t4;
  return n2(name$48, { "number | BigNumber": r2, "number, number": function(t5, n3) {
    if (!isInteger(t5) || t5 < 0 || !isInteger(n3) || n3 < 0) throw TypeError(`Positive integer value expected in function permutations`);
    if (n3 > t5) throw TypeError(`second argument k must be less than or equal to first argument n`);
    return product(t5 - n3 + 1, t5);
  }, "BigNumber, BigNumber": function(t5, n3) {
    var r3, i2;
    if (!isPositiveInteger(t5) || !isPositiveInteger(n3)) throw TypeError(`Positive integer value expected in function permutations`);
    if (n3.gt(t5)) throw TypeError(`second argument k must be less than or equal to first argument n`);
    for (r3 = t5.mul(0).add(1), i2 = t5.minus(n3).plus(1); i2.lte(t5); i2 = i2.plus(1)) r3 = r3.times(i2);
    return r3;
  } });
});
function isPositiveInteger(t4) {
  return t4.isInteger() && t4.gte(0);
}
var require_alea = __commonJSMin(((t4, n2) => {
  (function(t5, n3, r2) {
    function i2(t6) {
      var n4 = this, r3 = s2();
      n4.next = function() {
        var t7 = 2091639 * n4.s0 + n4.c * 23283064365386963e-26;
        return n4.s0 = n4.s1, n4.s1 = n4.s2, n4.s2 = t7 - (n4.c = t7 | 0);
      }, n4.c = 1, n4.s0 = r3(` `), n4.s1 = r3(` `), n4.s2 = r3(` `), n4.s0 -= r3(t6), n4.s0 < 0 && (n4.s0 += 1), n4.s1 -= r3(t6), n4.s1 < 0 && (n4.s1 += 1), n4.s2 -= r3(t6), n4.s2 < 0 && (n4.s2 += 1), r3 = null;
    }
    function a2(t6, n4) {
      return n4.c = t6.c, n4.s0 = t6.s0, n4.s1 = t6.s1, n4.s2 = t6.s2, n4;
    }
    function o2(t6, n4) {
      var r3 = new i2(t6), o3 = n4 && n4.state, s3 = r3.next;
      return s3.int32 = function() {
        return r3.next() * 4294967296 | 0;
      }, s3.double = function() {
        return s3() + (s3() * 2097152 | 0) * 11102230246251565e-32;
      }, s3.quick = s3, o3 && (typeof o3 == `object` && a2(o3, r3), s3.state = function() {
        return a2(r3, {});
      }), s3;
    }
    function s2() {
      var t6 = 4022871197;
      return function(n4) {
        n4 = String(n4);
        for (var r3 = 0; r3 < n4.length; r3++) {
          t6 += n4.charCodeAt(r3);
          var i3 = 0.02519603282416938 * t6;
          t6 = i3 >>> 0, i3 -= t6, i3 *= t6, t6 = i3 >>> 0, i3 -= t6, t6 += i3 * 4294967296;
        }
        return (t6 >>> 0) * 23283064365386963e-26;
      };
    }
    n3 && n3.exports ? n3.exports = o2 : r2 && r2.amd ? r2(function() {
      return o2;
    }) : this.alea = o2;
  })(t4, typeof n2 == `object` && n2, typeof define == `function` && define);
})), require_xor128 = __commonJSMin(((t4, n2) => {
  (function(t5, n3, r2) {
    function i2(t6) {
      var n4 = this, r3 = ``;
      n4.x = 0, n4.y = 0, n4.z = 0, n4.w = 0, n4.next = function() {
        var t7 = n4.x ^ n4.x << 11;
        return n4.x = n4.y, n4.y = n4.z, n4.z = n4.w, n4.w ^= n4.w >>> 19 ^ t7 ^ t7 >>> 8;
      }, t6 === (t6 | 0) ? n4.x = t6 : r3 += t6;
      for (var i3 = 0; i3 < r3.length + 64; i3++) n4.x ^= r3.charCodeAt(i3) | 0, n4.next();
    }
    function a2(t6, n4) {
      return n4.x = t6.x, n4.y = t6.y, n4.z = t6.z, n4.w = t6.w, n4;
    }
    function o2(t6, n4) {
      var r3 = new i2(t6), o3 = n4 && n4.state, s2 = function() {
        return (r3.next() >>> 0) / 4294967296;
      };
      return s2.double = function() {
        do
          var t7 = ((r3.next() >>> 11) + (r3.next() >>> 0) / 4294967296) / (1 << 21);
        while (t7 === 0);
        return t7;
      }, s2.int32 = r3.next, s2.quick = s2, o3 && (typeof o3 == `object` && a2(o3, r3), s2.state = function() {
        return a2(r3, {});
      }), s2;
    }
    n3 && n3.exports ? n3.exports = o2 : r2 && r2.amd ? r2(function() {
      return o2;
    }) : this.xor128 = o2;
  })(t4, typeof n2 == `object` && n2, typeof define == `function` && define);
})), require_xorwow = __commonJSMin(((t4, n2) => {
  (function(t5, n3, r2) {
    function i2(t6) {
      var n4 = this, r3 = ``;
      n4.next = function() {
        var t7 = n4.x ^ n4.x >>> 2;
        return n4.x = n4.y, n4.y = n4.z, n4.z = n4.w, n4.w = n4.v, (n4.d = n4.d + 362437 | 0) + (n4.v = n4.v ^ n4.v << 4 ^ (t7 ^ t7 << 1)) | 0;
      }, n4.x = 0, n4.y = 0, n4.z = 0, n4.w = 0, n4.v = 0, t6 === (t6 | 0) ? n4.x = t6 : r3 += t6;
      for (var i3 = 0; i3 < r3.length + 64; i3++) n4.x ^= r3.charCodeAt(i3) | 0, i3 == r3.length && (n4.d = n4.x << 10 ^ n4.x >>> 4), n4.next();
    }
    function a2(t6, n4) {
      return n4.x = t6.x, n4.y = t6.y, n4.z = t6.z, n4.w = t6.w, n4.v = t6.v, n4.d = t6.d, n4;
    }
    function o2(t6, n4) {
      var r3 = new i2(t6), o3 = n4 && n4.state, s2 = function() {
        return (r3.next() >>> 0) / 4294967296;
      };
      return s2.double = function() {
        do
          var t7 = ((r3.next() >>> 11) + (r3.next() >>> 0) / 4294967296) / (1 << 21);
        while (t7 === 0);
        return t7;
      }, s2.int32 = r3.next, s2.quick = s2, o3 && (typeof o3 == `object` && a2(o3, r3), s2.state = function() {
        return a2(r3, {});
      }), s2;
    }
    n3 && n3.exports ? n3.exports = o2 : r2 && r2.amd ? r2(function() {
      return o2;
    }) : this.xorwow = o2;
  })(t4, typeof n2 == `object` && n2, typeof define == `function` && define);
})), require_xorshift7 = __commonJSMin(((t4, n2) => {
  (function(t5, n3, r2) {
    function i2(t6) {
      var n4 = this;
      n4.next = function() {
        var t7 = n4.x, r4 = n4.i, i3 = t7[r4], a3;
        return i3 ^= i3 >>> 7, a3 = i3 ^ i3 << 24, i3 = t7[r4 + 1 & 7], a3 ^= i3 ^ i3 >>> 10, i3 = t7[r4 + 3 & 7], a3 ^= i3 ^ i3 >>> 3, i3 = t7[r4 + 4 & 7], a3 ^= i3 ^ i3 << 7, i3 = t7[r4 + 7 & 7], i3 ^= i3 << 13, a3 ^= i3 ^ i3 << 9, t7[r4] = a3, n4.i = r4 + 1 & 7, a3;
      };
      function r3(t7, n5) {
        var r4, i3 = [];
        if (n5 === (n5 | 0)) i3[0] = n5;
        else for (n5 = `` + n5, r4 = 0; r4 < n5.length; ++r4) i3[r4 & 7] = i3[r4 & 7] << 15 ^ n5.charCodeAt(r4) + i3[r4 + 1 & 7] << 13;
        for (; i3.length < 8; ) i3.push(0);
        for (r4 = 0; r4 < 8 && i3[r4] === 0; ++r4) ;
        for (r4 == 8 ? i3[7] = -1 : i3[r4], t7.x = i3, t7.i = 0, r4 = 256; r4 > 0; --r4) t7.next();
      }
      r3(n4, t6);
    }
    function a2(t6, n4) {
      return n4.x = t6.x.slice(), n4.i = t6.i, n4;
    }
    function o2(t6, n4) {
      t6 ??= +/* @__PURE__ */ new Date();
      var r3 = new i2(t6), o3 = n4 && n4.state, s2 = function() {
        return (r3.next() >>> 0) / 4294967296;
      };
      return s2.double = function() {
        do
          var t7 = ((r3.next() >>> 11) + (r3.next() >>> 0) / 4294967296) / (1 << 21);
        while (t7 === 0);
        return t7;
      }, s2.int32 = r3.next, s2.quick = s2, o3 && (o3.x && a2(o3, r3), s2.state = function() {
        return a2(r3, {});
      }), s2;
    }
    n3 && n3.exports ? n3.exports = o2 : r2 && r2.amd ? r2(function() {
      return o2;
    }) : this.xorshift7 = o2;
  })(t4, typeof n2 == `object` && n2, typeof define == `function` && define);
})), require_xor4096 = __commonJSMin(((t4, n2) => {
  (function(t5, n3, r2) {
    function i2(t6) {
      var n4 = this;
      n4.next = function() {
        var t7 = n4.w, r4 = n4.X, i3 = n4.i, a3, o3;
        return n4.w = t7 = t7 + 1640531527 | 0, o3 = r4[i3 + 34 & 127], a3 = r4[i3 = i3 + 1 & 127], o3 ^= o3 << 13, a3 ^= a3 << 17, o3 ^= o3 >>> 15, a3 ^= a3 >>> 12, o3 = r4[i3] = o3 ^ a3, n4.i = i3, o3 + (t7 ^ t7 >>> 16) | 0;
      };
      function r3(t7, n5) {
        var r4, i3, a3, o3, s2, c2 = [], l2 = 128;
        for (n5 === (n5 | 0) ? (i3 = n5, n5 = null) : (n5 += `\0`, i3 = 0, l2 = Math.max(l2, n5.length)), a3 = 0, o3 = -32; o3 < l2; ++o3) n5 && (i3 ^= n5.charCodeAt((o3 + 32) % n5.length)), o3 === 0 && (s2 = i3), i3 ^= i3 << 10, i3 ^= i3 >>> 15, i3 ^= i3 << 4, i3 ^= i3 >>> 13, o3 >= 0 && (s2 = s2 + 1640531527 | 0, r4 = c2[o3 & 127] ^= i3 + s2, a3 = r4 == 0 ? a3 + 1 : 0);
        for (a3 >= 128 && (c2[(n5 && n5.length || 0) & 127] = -1), a3 = 127, o3 = 512; o3 > 0; --o3) i3 = c2[a3 + 34 & 127], r4 = c2[a3 = a3 + 1 & 127], i3 ^= i3 << 13, r4 ^= r4 << 17, i3 ^= i3 >>> 15, r4 ^= r4 >>> 12, c2[a3] = i3 ^ r4;
        t7.w = s2, t7.X = c2, t7.i = a3;
      }
      r3(n4, t6);
    }
    function a2(t6, n4) {
      return n4.i = t6.i, n4.w = t6.w, n4.X = t6.X.slice(), n4;
    }
    function o2(t6, n4) {
      t6 ??= +/* @__PURE__ */ new Date();
      var r3 = new i2(t6), o3 = n4 && n4.state, s2 = function() {
        return (r3.next() >>> 0) / 4294967296;
      };
      return s2.double = function() {
        do
          var t7 = ((r3.next() >>> 11) + (r3.next() >>> 0) / 4294967296) / (1 << 21);
        while (t7 === 0);
        return t7;
      }, s2.int32 = r3.next, s2.quick = s2, o3 && (o3.X && a2(o3, r3), s2.state = function() {
        return a2(r3, {});
      }), s2;
    }
    n3 && n3.exports ? n3.exports = o2 : r2 && r2.amd ? r2(function() {
      return o2;
    }) : this.xor4096 = o2;
  })(t4, typeof n2 == `object` && n2, typeof define == `function` && define);
})), require_tychei = __commonJSMin(((t4, n2) => {
  (function(t5, n3, r2) {
    function i2(t6) {
      var n4 = this, r3 = ``;
      n4.next = function() {
        var t7 = n4.b, r4 = n4.c, i4 = n4.d, a3 = n4.a;
        return t7 = t7 << 25 ^ t7 >>> 7 ^ r4, r4 = r4 - i4 | 0, i4 = i4 << 24 ^ i4 >>> 8 ^ a3, a3 = a3 - t7 | 0, n4.b = t7 = t7 << 20 ^ t7 >>> 12 ^ r4, n4.c = r4 = r4 - i4 | 0, n4.d = i4 << 16 ^ r4 >>> 16 ^ a3, n4.a = a3 - t7 | 0;
      }, n4.a = 0, n4.b = 0, n4.c = -1640531527, n4.d = 1367130551, t6 === Math.floor(t6) ? (n4.a = t6 / 4294967296 | 0, n4.b = t6 | 0) : r3 += t6;
      for (var i3 = 0; i3 < r3.length + 20; i3++) n4.b ^= r3.charCodeAt(i3) | 0, n4.next();
    }
    function a2(t6, n4) {
      return n4.a = t6.a, n4.b = t6.b, n4.c = t6.c, n4.d = t6.d, n4;
    }
    function o2(t6, n4) {
      var r3 = new i2(t6), o3 = n4 && n4.state, s2 = function() {
        return (r3.next() >>> 0) / 4294967296;
      };
      return s2.double = function() {
        do
          var t7 = ((r3.next() >>> 11) + (r3.next() >>> 0) / 4294967296) / (1 << 21);
        while (t7 === 0);
        return t7;
      }, s2.int32 = r3.next, s2.quick = s2, o3 && (typeof o3 == `object` && a2(o3, r3), s2.state = function() {
        return a2(r3, {});
      }), s2;
    }
    n3 && n3.exports ? n3.exports = o2 : r2 && r2.amd ? r2(function() {
      return o2;
    }) : this.tychei = o2;
  })(t4, typeof n2 == `object` && n2, typeof define == `function` && define);
})), require___vite_browser_external = __commonJSMin(((t4, n2) => {
  n2.exports = {};
})), require_seedrandom$1 = __commonJSMin(((t4, n2) => {
  (function(t5, r2, i2) {
    var a2 = 256, o2 = 6, s2 = 52, c2 = `random`, l2 = i2.pow(a2, o2), u2 = i2.pow(2, s2), d2 = u2 * 2, p2 = a2 - 1, m2;
    function h2(t6, n3, s3) {
      var p3 = [];
      n3 = n3 == 1 ? { entropy: true } : n3 || {};
      var m3 = b2(y2(n3.entropy ? [t6, S2(r2)] : t6 ?? x2(), 3), p3), h3 = new g2(p3), w2 = function() {
        for (var t7 = h3.g(o2), n4 = l2, r3 = 0; t7 < u2; ) t7 = (t7 + r3) * a2, n4 *= a2, r3 = h3.g(1);
        for (; t7 >= d2; ) t7 /= 2, n4 /= 2, r3 >>>= 1;
        return (t7 + r3) / n4;
      };
      return w2.int32 = function() {
        return h3.g(4) | 0;
      }, w2.quick = function() {
        return h3.g(4) / 4294967296;
      }, w2.double = w2, b2(S2(h3.S), r2), (n3.pass || s3 || function(t7, n4, r3, a3) {
        return a3 && (a3.S && v2(a3, h3), t7.state = function() {
          return v2(h3, {});
        }), r3 ? (i2[c2] = t7, n4) : t7;
      })(w2, m3, `global` in n3 ? n3.global : this == i2, n3.state);
    }
    function g2(t6) {
      var n3, r3 = t6.length, i3 = this, o3 = 0, s3 = i3.i = i3.j = 0, c3 = i3.S = [];
      for (r3 || (t6 = [r3++]); o3 < a2; ) c3[o3] = o3++;
      for (o3 = 0; o3 < a2; o3++) c3[o3] = c3[s3 = p2 & s3 + t6[o3 % r3] + (n3 = c3[o3])], c3[s3] = n3;
      (i3.g = function(t7) {
        for (var n4, r4 = 0, o4 = i3.i, s4 = i3.j, c4 = i3.S; t7--; ) n4 = c4[o4 = p2 & o4 + 1], r4 = r4 * a2 + c4[p2 & (c4[o4] = c4[s4 = p2 & s4 + n4]) + (c4[s4] = n4)];
        return i3.i = o4, i3.j = s4, r4;
      })(a2);
    }
    function v2(t6, n3) {
      return n3.i = t6.i, n3.j = t6.j, n3.S = t6.S.slice(), n3;
    }
    function y2(t6, n3) {
      var r3 = [], i3 = typeof t6, a3;
      if (n3 && i3 == `object`) for (a3 in t6) try {
        r3.push(y2(t6[a3], n3 - 1));
      } catch {
      }
      return r3.length ? r3 : i3 == `string` ? t6 : t6 + `\0`;
    }
    function b2(t6, n3) {
      for (var r3 = t6 + ``, i3, a3 = 0; a3 < r3.length; ) n3[p2 & a3] = p2 & (i3 ^= n3[p2 & a3] * 19) + r3.charCodeAt(a3++);
      return S2(n3);
    }
    function x2() {
      try {
        var n3;
        return m2 && (n3 = m2.randomBytes) ? n3 = n3(a2) : (n3 = new Uint8Array(a2), (t5.crypto || t5.msCrypto).getRandomValues(n3)), S2(n3);
      } catch {
        var i3 = t5.navigator, o3 = i3 && i3.plugins;
        return [+/* @__PURE__ */ new Date(), t5, o3, t5.screen, S2(r2)];
      }
    }
    function S2(t6) {
      return String.fromCharCode.apply(0, t6);
    }
    if (b2(i2.random(), r2), typeof n2 == `object` && n2.exports) {
      n2.exports = h2;
      try {
        m2 = require___vite_browser_external();
      } catch {
      }
    } else typeof define == `function` && define.amd ? define(function() {
      return h2;
    }) : i2[`seed` + c2] = h2;
  })(typeof self < `u` ? self : t4, [], Math);
})), require_seedrandom = __commonJSMin(((t4, n2) => {
  var r2 = require_alea(), i2 = require_xor128(), a2 = require_xorwow(), o2 = require_xorshift7(), s2 = require_xor4096(), c2 = require_tychei(), l2 = require_seedrandom$1();
  l2.alea = r2, l2.xor128 = i2, l2.xorwow = a2, l2.xorshift7 = o2, l2.xor4096 = s2, l2.tychei = c2, n2.exports = l2;
})), import_seedrandom = __toESM(require_seedrandom()), singletonRandom = (0, import_seedrandom.default)(Date.now());
function createRng(t4) {
  var n2;
  function r2(t5) {
    n2 = t5 === null ? singletonRandom : (0, import_seedrandom.default)(String(t5));
  }
  r2(t4);
  function i2() {
    return n2();
  }
  return i2;
}
var name$47 = `pickRandom`, dependencies$47 = [`typed`, `config`, `?on`], createPickRandom = factory(name$47, dependencies$47, (t4) => {
  var { typed: n2, config: r2, on: i2 } = t4, a2 = createRng(r2.randomSeed);
  return i2 && i2(`config`, function(t5, n3) {
    t5.randomSeed !== n3.randomSeed && (a2 = createRng(t5.randomSeed));
  }), n2(name$47, { "Array | Matrix": function(t5) {
    return o2(t5, {});
  }, "Array | Matrix, Object": function(t5, n3) {
    return o2(t5, n3);
  }, "Array | Matrix, number": function(t5, n3) {
    return o2(t5, { number: n3 });
  }, "Array | Matrix, Array | Matrix": function(t5, n3) {
    return o2(t5, { weights: n3 });
  }, "Array | Matrix, Array | Matrix, number": function(t5, n3, r3) {
    return o2(t5, { number: r3, weights: n3 });
  }, "Array | Matrix, number, Array | Matrix": function(t5, n3, r3) {
    return o2(t5, { number: n3, weights: r3 });
  } });
  function o2(t5, n3) {
    var { number: r3, weights: i3, elementWise: o3 = true } = n3, s2 = r3 === void 0;
    s2 && (r3 = 1);
    var c2 = isMatrix(t5) ? t5.create : isMatrix(i3) ? i3.create : null;
    t5 = t5.valueOf(), i3 &&= i3.valueOf(), o3 === true && (t5 = flatten$4(t5), i3 = flatten$4(i3));
    var l2 = 0;
    if (i3 !== void 0) {
      if (i3.length !== t5.length) throw Error(`Weights must have the same length as possibles`);
      for (var u2 = 0, d2 = i3.length; u2 < d2; u2++) {
        if (!isNumber(i3[u2]) || i3[u2] < 0) throw Error(`Weights must be an array of positive numbers`);
        l2 += i3[u2];
      }
    }
    for (var p2 = t5.length, m2 = [], h2; m2.length < r3; ) {
      if (i3 === void 0) h2 = t5[Math.floor(a2() * p2)];
      else for (var g2 = a2() * l2, v2 = 0, y2 = t5.length; v2 < y2; v2++) if (g2 -= i3[v2], g2 < 0) {
        h2 = t5[v2];
        break;
      }
      m2.push(h2);
    }
    return s2 ? m2[0] : c2 ? c2(m2) : m2;
  }
});
function randomMatrix(t4, n2) {
  var r2 = [];
  if (t4 = t4.slice(0), t4.length > 1) for (var i2 = 0, a2 = t4.shift(); i2 < a2; i2++) r2.push(randomMatrix(t4, n2));
  else for (var o2 = 0, s2 = t4.shift(); o2 < s2; o2++) r2.push(n2());
  return r2;
}
var name$46 = `random`, dependencies$46 = [`typed`, `config`, `?on`], createRandom = factory(name$46, dependencies$46, (t4) => {
  var { typed: n2, config: r2, on: i2 } = t4, a2 = createRng(r2.randomSeed);
  return i2 && i2(`config`, function(t5, n3) {
    t5.randomSeed !== n3.randomSeed && (a2 = createRng(t5.randomSeed));
  }), n2(name$46, { "": () => s2(0, 1), number: (t5) => s2(0, t5), "number, number": (t5, n3) => s2(t5, n3), "Array | Matrix": (t5) => o2(t5, 0, 1), "Array | Matrix, number": (t5, n3) => o2(t5, 0, n3), "Array | Matrix, number, number": (t5, n3, r3) => o2(t5, n3, r3) });
  function o2(t5, n3, r3) {
    var i3 = randomMatrix(t5.valueOf(), () => s2(n3, r3));
    return isMatrix(t5) ? t5.create(i3, `number`) : i3;
  }
  function s2(t5, n3) {
    return t5 + a2() * (n3 - t5);
  }
}), name$45 = `randomInt`, dependencies$45 = [`typed`, `config`, `log2`, `?on`], createRandomInt = factory(name$45, dependencies$45, (t4) => {
  var { typed: n2, config: r2, log2: i2, on: a2 } = t4, o2 = createRng(r2.randomSeed);
  return a2 && a2(`config`, function(t5, n3) {
    t5.randomSeed !== n3.randomSeed && (o2 = createRng(t5.randomSeed));
  }), n2(name$45, { "": () => c2(0, 2), number: (t5) => c2(0, t5), "number, number": (t5, n3) => c2(t5, n3), bigint: (t5) => l2(0n, t5), "bigint, bigint": l2, "Array | Matrix": (t5) => s2(t5, 0, 1), "Array | Matrix, number": (t5, n3) => s2(t5, 0, n3), "Array | Matrix, number, number": (t5, n3, r3) => s2(t5, n3, r3) });
  function s2(t5, n3, r3) {
    var i3 = randomMatrix(t5.valueOf(), () => c2(n3, r3));
    return isMatrix(t5) ? t5.create(i3, `number`) : i3;
  }
  function c2(t5, n3) {
    return Math.floor(t5 + o2() * (n3 - t5));
  }
  function l2(t5, n3) {
    var r3 = 2n ** 30n, a3 = n3 - t5;
    if (a3 <= r3) return t5 + BigInt(c2(0, Number(a3)));
    for (var s3 = i2(a3), l3 = a3; l3 >= a3; ) {
      l3 = 0n;
      for (var u2 = 0; u2 < s3; ++u2) l3 = 2n * l3 + (o2() < 0.5 ? 0n : 1n);
    }
    return t5 + l3;
  }
}), name$44 = `stirlingS2`, dependencies$44 = [`typed`, `addScalar`, `subtractScalar`, `multiplyScalar`, `divideScalar`, `pow`, `factorial`, `combinations`, `isNegative`, `isInteger`, `number`, `?bignumber`, `larger`], createStirlingS2 = factory(name$44, dependencies$44, (t4) => {
  var { typed: n2, addScalar: r2, subtractScalar: i2, multiplyScalar: a2, divideScalar: o2, pow: s2, factorial: c2, combinations: l2, isNegative: u2, isInteger: d2, number: p2, bignumber: m2, larger: h2 } = t4, g2 = [], v2 = [];
  return n2(name$44, { "number | BigNumber, number | BigNumber": function(t5, n3) {
    if (!d2(t5) || u2(t5) || !d2(n3) || u2(n3)) throw TypeError(`Non-negative integer value expected in function stirlingS2`);
    if (h2(n3, t5)) throw TypeError(`k must be less than or equal to n in function stirlingS2`);
    var i3 = !(isNumber(t5) && isNumber(n3)), o3 = i3 ? v2 : g2, s3 = i3 ? m2 : p2, c3 = p2(t5), l3 = p2(n3);
    if (o3[c3] && o3[c3].length > l3) return o3[c3][l3];
    for (var y2 = 0; y2 <= c3; ++y2) if (o3[y2] || (o3[y2] = [s3(+(y2 === 0))]), y2 !== 0) for (var b2 = o3[y2], x2 = o3[y2 - 1], S2 = b2.length; S2 <= y2 && S2 <= l3; ++S2) S2 === y2 ? b2[S2] = 1 : b2[S2] = r2(a2(s3(S2), x2[S2]), x2[S2 - 1]);
    return o3[c3][l3];
  } });
}), name$43 = `bellNumbers`, dependencies$43 = [`typed`, `addScalar`, `isNegative`, `isInteger`, `stirlingS2`], createBellNumbers = factory(name$43, dependencies$43, (t4) => {
  var { typed: n2, addScalar: r2, isNegative: i2, isInteger: a2, stirlingS2: o2 } = t4;
  return n2(name$43, { "number | BigNumber": function(t5) {
    if (!a2(t5) || i2(t5)) throw TypeError(`Non-negative integer value expected in function bellNumbers`);
    for (var n3 = 0, s2 = 0; s2 <= t5; s2++) n3 = r2(n3, o2(t5, s2));
    return n3;
  } });
}), name$42 = `catalan`, dependencies$42 = [`typed`, `addScalar`, `divideScalar`, `multiplyScalar`, `combinations`, `isNegative`, `isInteger`], createCatalan = factory(name$42, dependencies$42, (t4) => {
  var { typed: n2, addScalar: r2, divideScalar: i2, multiplyScalar: a2, combinations: o2, isNegative: s2, isInteger: c2 } = t4;
  return n2(name$42, { "number | BigNumber": function(t5) {
    if (!c2(t5) || s2(t5)) throw TypeError(`Non-negative integer value expected in function catalan`);
    return i2(o2(a2(t5, 2), t5), r2(t5, 1));
  } });
}), name$41 = `composition`, dependencies$41 = [`typed`, `addScalar`, `combinations`, `isNegative`, `isPositive`, `isInteger`, `larger`], createComposition = factory(name$41, dependencies$41, (t4) => {
  var { typed: n2, addScalar: r2, combinations: i2, isPositive: a2, isNegative: o2, isInteger: s2, larger: c2 } = t4;
  return n2(name$41, { "number | BigNumber, number | BigNumber": function(t5, n3) {
    if (!s2(t5) || !a2(t5) || !s2(n3) || !a2(n3)) throw TypeError(`Positive integer value expected in function composition`);
    if (c2(n3, t5)) throw TypeError(`k must be less than or equal to n in function composition`);
    return i2(r2(t5, -1), r2(n3, -1));
  } });
}), name$40 = `leafCount`, dependencies$40 = [`parse`, `typed`], createLeafCount = factory(name$40, dependencies$40, (t4) => {
  var { parse: n2, typed: r2 } = t4;
  function i2(t5) {
    var n3 = 0;
    return t5.forEach((t6) => {
      n3 += i2(t6);
    }), n3 || 1;
  }
  return r2(name$40, { Node: function(t5) {
    return i2(t5);
  } });
});
function isNumericNode(t4) {
  return isConstantNode(t4) || isOperatorNode(t4) && t4.isUnary() && isConstantNode(t4.args[0]);
}
function isConstantExpression(t4) {
  return !!(isConstantNode(t4) || (isFunctionNode(t4) || isOperatorNode(t4)) && t4.args.every(isConstantExpression) || isParenthesisNode(t4) && isConstantExpression(t4.content));
}
function ownKeys$1(t4, n2) {
  var r2 = Object.keys(t4);
  if (Object.getOwnPropertySymbols) {
    var i2 = Object.getOwnPropertySymbols(t4);
    n2 && (i2 = i2.filter(function(n3) {
      return Object.getOwnPropertyDescriptor(t4, n3).enumerable;
    })), r2.push.apply(r2, i2);
  }
  return r2;
}
function _objectSpread$1(t4) {
  for (var n2 = 1; n2 < arguments.length; n2++) {
    var r2 = arguments[n2] == null ? {} : arguments[n2];
    n2 % 2 ? ownKeys$1(Object(r2), true).forEach(function(n3) {
      _defineProperty(t4, n3, r2[n3]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(t4, Object.getOwnPropertyDescriptors(r2)) : ownKeys$1(Object(r2)).forEach(function(n3) {
      Object.defineProperty(t4, n3, Object.getOwnPropertyDescriptor(r2, n3));
    });
  }
  return t4;
}
var name$39 = `simplifyUtil`, dependencies$39 = [`FunctionNode`, `OperatorNode`, `SymbolNode`], createUtil = factory(name$39, dependencies$39, (t4) => {
  var { FunctionNode: n2, OperatorNode: r2, SymbolNode: i2 } = t4, a2 = true, o2 = false, s2 = `defaultF`, c2 = { add: { trivial: a2, total: a2, commutative: a2, associative: a2 }, unaryPlus: { trivial: a2, total: a2, commutative: a2, associative: a2 }, subtract: { trivial: o2, total: a2, commutative: o2, associative: o2 }, multiply: { trivial: a2, total: a2, commutative: a2, associative: a2 }, divide: { trivial: o2, total: a2, commutative: o2, associative: o2 }, paren: { trivial: a2, total: a2, commutative: a2, associative: o2 }, defaultF: { trivial: o2, total: a2, commutative: o2, associative: o2 } }, l2 = { divide: { total: o2 }, log: { total: o2 } }, u2 = { subtract: { total: o2 }, abs: { trivial: a2 }, log: { total: a2 } };
  function d2(t5, n3) {
    var r3 = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : c2, i3 = s2;
    if (typeof t5 == `string` ? i3 = t5 : isOperatorNode(t5) ? i3 = t5.fn.toString() : isFunctionNode(t5) ? i3 = t5.name : isParenthesisNode(t5) && (i3 = `paren`), hasOwnProperty$1(r3, i3)) {
      var a3 = r3[i3];
      if (hasOwnProperty$1(a3, n3)) return a3[n3];
      if (hasOwnProperty$1(c2, i3)) return c2[i3][n3];
    }
    if (hasOwnProperty$1(r3, s2)) {
      var o3 = r3[s2];
      return hasOwnProperty$1(o3, n3) ? o3[n3] : c2[s2][n3];
    }
    if (hasOwnProperty$1(c2, i3)) {
      var l3 = c2[i3];
      if (hasOwnProperty$1(l3, n3)) return l3[n3];
    }
    return c2[s2][n3];
  }
  function p2(t5) {
    return d2(t5, `commutative`, arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : c2);
  }
  function m2(t5) {
    return d2(t5, `associative`, arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : c2);
  }
  function h2(t5, n3) {
    var r3 = _objectSpread$1({}, t5);
    for (var i3 in n3) hasOwnProperty$1(t5, i3) ? r3[i3] = _objectSpread$1(_objectSpread$1({}, n3[i3]), t5[i3]) : r3[i3] = n3[i3];
    return r3;
  }
  function g2(t5, n3) {
    if (!t5.args || t5.args.length === 0) return t5;
    t5.args = v2(t5, n3);
    for (var r3 = 0; r3 < t5.args.length; r3++) g2(t5.args[r3], n3);
  }
  function v2(t5, n3) {
    var r3, i3 = [], a3 = function(t6) {
      for (var n4 = 0; n4 < t6.args.length; n4++) {
        var o3 = t6.args[n4];
        isOperatorNode(o3) && r3 === o3.op ? a3(o3) : i3.push(o3);
      }
    };
    return m2(t5, n3) ? (r3 = t5.op, a3(t5), i3) : t5.args;
  }
  function y2(t5, n3) {
    if (!(!t5.args || t5.args.length === 0)) {
      for (var r3 = x2(t5), i3 = t5.args.length, a3 = 0; a3 < i3; a3++) y2(t5.args[a3], n3);
      if (i3 > 2 && m2(t5, n3)) {
        for (var o3 = t5.args.pop(); t5.args.length > 0; ) o3 = r3([t5.args.pop(), o3]);
        t5.args = o3.args;
      }
    }
  }
  function b2(t5, n3) {
    if (!(!t5.args || t5.args.length === 0)) {
      for (var r3 = x2(t5), i3 = t5.args.length, a3 = 0; a3 < i3; a3++) b2(t5.args[a3], n3);
      if (i3 > 2 && m2(t5, n3)) {
        for (var o3 = t5.args.shift(); t5.args.length > 0; ) o3 = r3([o3, t5.args.shift()]);
        t5.args = o3.args;
      }
    }
  }
  function x2(t5) {
    return isOperatorNode(t5) ? function(n3) {
      try {
        return new r2(t5.op, t5.fn, n3, t5.implicit);
      } catch (t6) {
        return console.error(t6), [];
      }
    } : function(r3) {
      return new n2(new i2(t5.name), r3);
    };
  }
  return { createMakeNodeFunction: x2, hasProperty: d2, isCommutative: p2, isAssociative: m2, mergeContext: h2, flatten: g2, allChildren: v2, unflattenr: y2, unflattenl: b2, defaultContext: c2, realContext: l2, positiveContext: u2 };
}), name$38 = `simplify`, dependencies$38 = [`typed`, `parse`, `equal`, `resolve`, `simplifyConstant`, `simplifyCore`, `AccessorNode`, `ArrayNode`, `ConstantNode`, `FunctionNode`, `IndexNode`, `ObjectNode`, `OperatorNode`, `ParenthesisNode`, `SymbolNode`, `replacer`], createSimplify = factory(name$38, dependencies$38, (t4) => {
  var { typed: n2, parse: r2, equal: i2, resolve: a2, simplifyConstant: o2, simplifyCore: s2, AccessorNode: c2, ArrayNode: l2, ConstantNode: u2, FunctionNode: d2, IndexNode: p2, ObjectNode: m2, OperatorNode: h2, ParenthesisNode: g2, SymbolNode: v2, replacer: y2 } = t4, { hasProperty: b2, isCommutative: x2, isAssociative: S2, mergeContext: w2, flatten: E, unflattenr: D, unflattenl: O, createMakeNodeFunction: j2, defaultContext: M2, realContext: N2, positiveContext: F } = createUtil({ FunctionNode: d2, OperatorNode: h2, SymbolNode: v2 });
  n2.addConversion({ from: `Object`, to: `Map`, convert: createMap$1 });
  var I = n2(`simplify`, { Node: W, "Node, Map": (t5, n3) => W(t5, false, n3), "Node, Map, Object": (t5, n3, r3) => W(t5, false, n3, r3), "Node, Array": W, "Node, Array, Map": W, "Node, Array, Map, Object": W });
  n2.removeConversion({ from: `Object`, to: `Map`, convert: createMap$1 }), I.defaultContext = M2, I.realContext = N2, I.positiveContext = F;
  function L(t5) {
    return t5.transform(function(t6) {
      return isParenthesisNode(t6) ? L(t6.content) : t6;
    });
  }
  var R = { true: true, false: true, e: true, i: true, Infinity: true, LN2: true, LN10: true, LOG2E: true, LOG10E: true, NaN: true, phi: true, pi: true, SQRT1_2: true, SQRT2: true, tau: true };
  I.rules = [s2, { l: `log(e)`, r: `1` }, { s: `n-n1 -> n+-n1`, assuming: { subtract: { total: true } } }, { s: `n-n -> 0`, assuming: { subtract: { total: false } } }, { s: `-(cl*v) -> v * (-cl)`, assuming: { multiply: { commutative: true }, subtract: { total: true } } }, { s: `-(cl*v) -> (-cl) * v`, assuming: { multiply: { commutative: false }, subtract: { total: true } } }, { s: `-(v*cl) -> v * (-cl)`, assuming: { multiply: { commutative: false }, subtract: { total: true } } }, { l: `-(n1/n2)`, r: `-n1/n2` }, { l: `-v`, r: `v * (-1)` }, { l: `(n1 + n2)*(-1)`, r: `n1*(-1) + n2*(-1)`, repeat: true }, { l: `n/n1^n2`, r: `n*n1^-n2` }, { l: `n/n1`, r: `n*n1^-1` }, { s: `(n1*n2)^n3 -> n1^n3 * n2^n3`, assuming: { multiply: { commutative: true } } }, { s: `(n1*n2)^(-1) -> n2^(-1) * n1^(-1)`, assuming: { multiply: { commutative: false } } }, { s: `(n ^ n1) ^ n2 -> n ^ (n1 * n2)`, assuming: { divide: { total: true } } }, { l: ` vd   * ( vd   * n1 + n2)`, r: `vd^2       * n1 +  vd   * n2` }, { s: ` vd   * (vd^n4 * n1 + n2)   ->  vd^(1+n4)  * n1 +  vd   * n2`, assuming: { divide: { total: true } } }, { s: `vd^n3 * ( vd   * n1 + n2)   ->  vd^(n3+1)  * n1 + vd^n3 * n2`, assuming: { divide: { total: true } } }, { s: `vd^n3 * (vd^n4 * n1 + n2)   ->  vd^(n3+n4) * n1 + vd^n3 * n2`, assuming: { divide: { total: true } } }, { l: `n*n`, r: `n^2` }, { s: `n * n^n1 -> n^(n1+1)`, assuming: { divide: { total: true } } }, { s: `n^n1 * n^n2 -> n^(n1+n2)`, assuming: { divide: { total: true } } }, o2, { s: `n+n -> 2*n`, assuming: { add: { total: true } } }, { l: `n+-n`, r: `0` }, { l: `vd*n + vd`, r: `vd*(n+1)` }, { l: `n3*n1 + n3*n2`, r: `n3*(n1+n2)` }, { l: `n3^(-n4)*n1 +   n3  * n2`, r: `n3^(-n4)*(n1 + n3^(n4+1) *n2)` }, { l: `n3^(-n4)*n1 + n3^n5 * n2`, r: `n3^(-n4)*(n1 + n3^(n4+n5)*n2)` }, { s: `n*vd + vd -> (n+1)*vd`, assuming: { multiply: { commutative: false } } }, { s: `vd + n*vd -> (1+n)*vd`, assuming: { multiply: { commutative: false } } }, { s: `n1*n3 + n2*n3 -> (n1+n2)*n3`, assuming: { multiply: { commutative: false } } }, { s: `n^n1 * n -> n^(n1+1)`, assuming: { divide: { total: true }, multiply: { commutative: false } } }, { s: `n1*n3^(-n4) + n2 * n3    -> (n1 + n2*n3^(n4 +  1))*n3^(-n4)`, assuming: { multiply: { commutative: false } } }, { s: `n1*n3^(-n4) + n2 * n3^n5 -> (n1 + n2*n3^(n4 + n5))*n3^(-n4)`, assuming: { multiply: { commutative: false } } }, { l: `n*cd + cd`, r: `(n+1)*cd` }, { s: `cd*n + cd -> cd*(n+1)`, assuming: { multiply: { commutative: false } } }, { s: `cd + cd*n -> cd*(1+n)`, assuming: { multiply: { commutative: false } } }, o2, { s: `(-n)*n1 -> -(n*n1)`, assuming: { subtract: { total: true } } }, { s: `n1*(-n) -> -(n1*n)`, assuming: { subtract: { total: true }, multiply: { commutative: false } } }, { s: `ce+ve -> ve+ce`, assuming: { add: { commutative: true } }, imposeContext: { add: { commutative: false } } }, { s: `vd*cd -> cd*vd`, assuming: { multiply: { commutative: true } }, imposeContext: { multiply: { commutative: false } } }, { l: `n+-n1`, r: `n-n1` }, { l: `n+-(n1)`, r: `n-(n1)` }, { s: `n*(n1^-1) -> n/n1`, assuming: { multiply: { commutative: true } } }, { s: `n*n1^-n2 -> n/n1^n2`, assuming: { multiply: { commutative: true } } }, { s: `n^-1 -> 1/n`, assuming: { multiply: { commutative: true } } }, { l: `n^1`, r: `n` }, { s: `n*(n1/n2) -> (n*n1)/n2`, assuming: { multiply: { associative: true } } }, { s: `n-(n1+n2) -> n-n1-n2`, assuming: { addition: { associative: true, commutative: true } } }, { l: `1*n`, r: `n`, imposeContext: { multiply: { commutative: true } } }, { s: `n1/(n2/n3) -> (n1*n3)/n2`, assuming: { multiply: { associative: true } } }, { l: `n1/(-n2)`, r: `-n1/n2` }];
  function z(t5, n3) {
    var i3 = {};
    if (t5.s) {
      var a3 = t5.s.split(`->`);
      if (a3.length === 2) i3.l = a3[0], i3.r = a3[1];
      else throw SyntaxError(`Could not parse rule: ` + t5.s);
    } else i3.l = t5.l, i3.r = t5.r;
    i3.l = L(r2(i3.l)), i3.r = L(r2(i3.r));
    for (var o3 of [`imposeContext`, `repeat`, `assuming`]) o3 in t5 && (i3[o3] = t5[o3]);
    if (t5.evaluate && (i3.evaluate = r2(t5.evaluate)), S2(i3.l, n3)) {
      var s3 = !x2(i3.l, n3), c3;
      s3 && (c3 = U());
      var l3 = j2(i3.l), u3 = U();
      i3.expanded = {}, i3.expanded.l = l3([i3.l, u3]), E(i3.expanded.l, n3), D(i3.expanded.l, n3), i3.expanded.r = l3([i3.r, u3]), s3 && (i3.expandedNC1 = {}, i3.expandedNC1.l = l3([c3, i3.l]), i3.expandedNC1.r = l3([c3, i3.r]), i3.expandedNC2 = {}, i3.expandedNC2.l = l3([c3, i3.expanded.l]), i3.expandedNC2.r = l3([c3, i3.expanded.r]));
    }
    return i3;
  }
  function V(t5, n3) {
    for (var r3 = [], i3 = 0; i3 < t5.length; i3++) {
      var a3 = t5[i3], o3 = void 0, s3 = typeof a3;
      switch (s3) {
        case `string`:
          a3 = { s: a3 };
        case `object`:
          o3 = z(a3, n3);
          break;
        case `function`:
          o3 = a3;
          break;
        default:
          throw TypeError(`Unsupported type of rule: ` + s3);
      }
      r3.push(o3);
    }
    return r3;
  }
  var H2 = 0;
  function U() {
    return new v2(`_p` + H2++);
  }
  function W(t5, n3) {
    var r3 = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : createEmptyMap(), i3 = arguments.length > 3 && arguments[3] !== void 0 ? arguments[3] : {}, o3 = i3.consoleDebug;
    n3 = V(n3 || I.rules, i3.context);
    var s3 = a2(t5, r3);
    s3 = L(s3);
    for (var c3 = {}, l3 = s3.toString({ parenthesis: `all` }); !c3[l3]; ) {
      c3[l3] = true, H2 = 0;
      var u3 = l3;
      o3 && console.log(`Working on: `, l3);
      for (var d3 = 0; d3 < n3.length; d3++) {
        var p3 = ``;
        if (typeof n3[d3] == `function` ? (s3 = n3[d3](s3, i3), o3 && (p3 = n3[d3].name)) : (E(s3, i3.context), s3 = K(s3, n3[d3], i3.context), o3 && (p3 = `${n3[d3].l.toString()} -> ${n3[d3].r.toString()}`)), o3) {
          var m3 = s3.toString({ parenthesis: `all` });
          m3 !== u3 && (console.log(`Applying`, p3, `produced`, m3), u3 = m3);
        }
        O(s3, i3.context);
      }
      l3 = s3.toString({ parenthesis: `all` });
    }
    return s3;
  }
  function G(t5, n3, r3) {
    var i3 = t5;
    if (t5) for (var a3 = 0; a3 < t5.length; ++a3) {
      var o3 = K(t5[a3], n3, r3);
      o3 !== t5[a3] && (i3 === t5 && (i3 = t5.slice()), i3[a3] = o3);
    }
    return i3;
  }
  function K(t5, n3, r3) {
    if (n3.assuming) {
      for (var i3 in n3.assuming) for (var a3 in n3.assuming[i3]) if (b2(i3, a3, r3) !== n3.assuming[i3][a3]) return t5;
    }
    var o3 = w2(n3.imposeContext, r3), s3 = t5;
    if (s3 instanceof h2 || s3 instanceof d2) {
      var u3 = G(s3.args, n3, r3);
      u3 !== s3.args && (s3 = s3.clone(), s3.args = u3);
    } else if (s3 instanceof g2) {
      if (s3.content) {
        var v3 = K(s3.content, n3, r3);
        v3 !== s3.content && (s3 = new g2(v3));
      }
    } else if (s3 instanceof l2) {
      var y3 = G(s3.items, n3, r3);
      y3 !== s3.items && (s3 = new l2(y3));
    } else if (s3 instanceof c2) {
      var x3 = s3.object;
      s3.object && (x3 = K(s3.object, n3, r3));
      var S3 = s3.index;
      s3.index && (S3 = K(s3.index, n3, r3)), (x3 !== s3.object || S3 !== s3.index) && (s3 = new c2(x3, S3));
    } else if (s3 instanceof p2) {
      var E2 = G(s3.dimensions, n3, r3);
      E2 !== s3.dimensions && (s3 = new p2(E2));
    } else if (s3 instanceof m2) {
      var D2 = false, O2 = {};
      for (var j3 in s3.properties) O2[j3] = K(s3.properties[j3], n3, r3), O2[j3] !== s3.properties[j3] && (D2 = true);
      D2 && (s3 = new m2(O2));
    }
    var M3 = n3.r, N3 = te(n3.l, s3, o3)[0];
    if (!N3 && n3.expanded && (M3 = n3.expanded.r, N3 = te(n3.expanded.l, s3, o3)[0]), !N3 && n3.expandedNC1 && (M3 = n3.expandedNC1.r, N3 = te(n3.expandedNC1.l, s3, o3)[0], N3 ||= (M3 = n3.expandedNC2.r, te(n3.expandedNC2.l, s3, o3)[0])), N3) {
      var F2 = s3.implicit;
      s3 = M3.clone(), F2 && `implicit` in M3 && (s3.implicit = true), s3 = s3.transform(function(t6) {
        return t6.isSymbolNode && hasOwnProperty$1(N3.placeholders, t6.name) ? N3.placeholders[t6.name].clone() : t6;
      });
    }
    return n3.repeat && s3 !== t5 && (s3 = K(s3, n3, r3)), s3;
  }
  function q(t5, n3) {
    var r3 = [], i3, a3, o3 = j2(t5);
    if (x2(t5, n3)) for (var s3 = 0; s3 < t5.args.length; s3++) a3 = t5.args.slice(0), a3.splice(s3, 1), i3 = a3.length === 1 ? a3[0] : o3(a3), r3.push(o3([t5.args[s3], i3]));
    else for (var c3 = 1; c3 < t5.args.length; c3++) {
      var l3 = t5.args[0];
      c3 > 1 && (l3 = o3(t5.args.slice(0, c3))), a3 = t5.args.slice(c3), i3 = a3.length === 1 ? a3[0] : o3(a3), r3.push(o3([l3, i3]));
    }
    return r3;
  }
  function J(t5, n3) {
    var r3 = { placeholders: {} };
    if (!t5.placeholders && !n3.placeholders) return r3;
    if (!t5.placeholders) return n3;
    if (!n3.placeholders) return t5;
    for (var i3 in t5.placeholders) if (hasOwnProperty$1(t5.placeholders, i3) && (r3.placeholders[i3] = t5.placeholders[i3], hasOwnProperty$1(n3.placeholders, i3) && !ne(t5.placeholders[i3], n3.placeholders[i3]))) return null;
    for (var a3 in n3.placeholders) hasOwnProperty$1(n3.placeholders, a3) && (r3.placeholders[a3] = n3.placeholders[a3]);
    return r3;
  }
  function ee(t5, n3) {
    var r3 = [];
    if (t5.length === 0 || n3.length === 0) return r3;
    for (var i3, a3 = 0; a3 < t5.length; a3++) for (var o3 = 0; o3 < n3.length; o3++) i3 = J(t5[a3], n3[o3]), i3 && r3.push(i3);
    return r3;
  }
  function Y(t5) {
    if (t5.length === 0) return t5;
    for (var n3 = t5.reduce(ee), r3 = [], i3 = {}, a3 = 0; a3 < n3.length; a3++) {
      var o3 = JSON.stringify(n3[a3], y2);
      i3[o3] || (i3[o3] = true, r3.push(n3[a3]));
    }
    return r3;
  }
  function te(t5, n3, r3, a3) {
    var o3 = [{ placeholders: {} }];
    if (t5 instanceof h2 && n3 instanceof h2 || t5 instanceof d2 && n3 instanceof d2) {
      if (t5 instanceof h2) {
        if (t5.op !== n3.op || t5.fn !== n3.fn) return [];
      } else if (t5 instanceof d2 && t5.name !== n3.name) return [];
      if (n3.args.length === 1 && t5.args.length === 1 || !S2(n3, r3) && n3.args.length === t5.args.length || a3) {
        for (var s3 = [], c3 = 0; c3 < t5.args.length; c3++) {
          var l3 = te(t5.args[c3], n3.args[c3], r3);
          if (l3.length === 0) break;
          s3.push(l3);
        }
        if (s3.length !== t5.args.length) {
          if (!x2(n3, r3) || t5.args.length === 1) return [];
          if (t5.args.length > 2) throw Error(`permuting >2 commutative non-associative rule arguments not yet implemented`);
          var p3 = te(t5.args[0], n3.args[1], r3);
          if (p3.length === 0) return [];
          var m3 = te(t5.args[1], n3.args[0], r3);
          if (m3.length === 0) return [];
          s3 = [p3, m3];
        }
        o3 = Y(s3);
      } else if (n3.args.length >= 2 && t5.args.length === 2) {
        for (var g3 = q(n3, r3), y3 = [], b3 = 0; b3 < g3.length; b3++) {
          var w3 = te(t5, g3[b3], r3, true);
          y3 = y3.concat(w3);
        }
        return y3;
      } else if (t5.args.length > 2) throw Error(`Unexpected non-binary associative function: ` + t5.toString());
      else return [];
    } else if (t5 instanceof v2) {
      if (t5.name.length === 0) throw Error(`Symbol in rule has 0 length...!?`);
      if (R[t5.name]) {
        if (t5.name !== n3.name) return [];
      } else switch (t5.name[1] >= `a` && t5.name[1] <= `z` ? t5.name.substring(0, 2) : t5.name[0]) {
        case `n`:
        case `_p`:
          o3[0].placeholders[t5.name] = n3;
          break;
        case `c`:
        case `cl`:
          if (isConstantNode(n3)) o3[0].placeholders[t5.name] = n3;
          else return [];
          break;
        case `v`:
          if (!isConstantNode(n3)) o3[0].placeholders[t5.name] = n3;
          else return [];
          break;
        case `vl`:
          if (isSymbolNode(n3)) o3[0].placeholders[t5.name] = n3;
          else return [];
          break;
        case `cd`:
          if (isNumericNode(n3)) o3[0].placeholders[t5.name] = n3;
          else return [];
          break;
        case `vd`:
          if (!isNumericNode(n3)) o3[0].placeholders[t5.name] = n3;
          else return [];
          break;
        case `ce`:
          if (isConstantExpression(n3)) o3[0].placeholders[t5.name] = n3;
          else return [];
          break;
        case `ve`:
          if (!isConstantExpression(n3)) o3[0].placeholders[t5.name] = n3;
          else return [];
          break;
        default:
          throw Error(`Invalid symbol in rule: ` + t5.name);
      }
    } else if (t5 instanceof u2) {
      if (!i2(t5.value, n3.value)) return [];
    } else return [];
    return o3;
  }
  function ne(t5, n3) {
    if (t5 instanceof u2 && n3 instanceof u2) {
      if (!i2(t5.value, n3.value)) return false;
    } else if (t5 instanceof v2 && n3 instanceof v2) {
      if (t5.name !== n3.name) return false;
    } else if (t5 instanceof h2 && n3 instanceof h2 || t5 instanceof d2 && n3 instanceof d2) {
      if (t5 instanceof h2) {
        if (t5.op !== n3.op || t5.fn !== n3.fn) return false;
      } else if (t5 instanceof d2 && t5.name !== n3.name) return false;
      if (t5.args.length !== n3.args.length) return false;
      for (var r3 = 0; r3 < t5.args.length; r3++) if (!ne(t5.args[r3], n3.args[r3])) return false;
    } else return false;
    return true;
  }
  return I;
}), name$37 = `simplifyConstant`, dependencies$37 = [`typed`, `config`, `mathWithTransform`, `matrix`, `isBounded`, `?fraction`, `?bignumber`, `AccessorNode`, `ArrayNode`, `ConstantNode`, `FunctionNode`, `IndexNode`, `ObjectNode`, `OperatorNode`, `SymbolNode`], createSimplifyConstant = factory(name$37, dependencies$37, (t4) => {
  var { typed: n2, config: r2, mathWithTransform: i2, matrix: a2, isBounded: o2, fraction: s2, bignumber: c2, AccessorNode: l2, ArrayNode: u2, ConstantNode: d2, FunctionNode: p2, IndexNode: m2, ObjectNode: h2, OperatorNode: g2, SymbolNode: v2 } = t4, { isCommutative: y2, isAssociative: b2, allChildren: x2, createMakeNodeFunction: S2 } = createUtil({ FunctionNode: p2, OperatorNode: g2, SymbolNode: v2 }), w2 = n2(`simplifyConstant`, { Node: (t5) => j2(z(t5, {})), "Node, Object": function(t5, n3) {
    return j2(z(t5, n3));
  } });
  function E(t5) {
    return isFraction(t5) ? t5.valueOf() : t5 instanceof Array ? t5.map(E) : isMatrix(t5) ? a2(E(t5.valueOf())) : t5;
  }
  function D(t5, n3, r3) {
    try {
      return i2[t5].apply(null, n3);
    } catch {
      return n3 = n3.map(E), N2(i2[t5].apply(null, n3), r3);
    }
  }
  var O = n2({ Fraction: I, number: function(t5) {
    return t5 < 0 ? F(new d2(-t5)) : new d2(t5);
  }, BigNumber: function(t5) {
    return t5 < 0 ? F(new d2(-t5)) : new d2(t5);
  }, bigint: function(t5) {
    return t5 < 0n ? F(new d2(-t5)) : new d2(t5);
  }, Complex: function(t5) {
    throw Error(`Cannot convert Complex number to Node`);
  }, string: function(t5) {
    return new d2(t5);
  }, Matrix: function(t5) {
    return new u2(t5.valueOf().map((t6) => O(t6)));
  } });
  function j2(t5) {
    return isNode(t5) ? t5 : O(t5);
  }
  function M2(t5, n3) {
    if (n3 && n3.exactFractions !== false && o2(t5) && s2) {
      var r3 = s2(t5), i3 = n3 && typeof n3.fractionsLimit == `number` ? n3.fractionsLimit : 1 / 0;
      if (r3.valueOf() === t5 && r3.n < i3 && r3.d < i3) return r3;
    }
    return t5;
  }
  var N2 = n2({ "string, Object": function(t5, n3) {
    var i3 = safeNumberType(t5, r2);
    return i3 === `BigNumber` ? (c2 === void 0 && noBignumber(), c2(t5)) : i3 === `bigint` ? BigInt(t5) : i3 === `Fraction` ? (s2 === void 0 && noFraction(), s2(t5)) : M2(parseFloat(t5), n3);
  }, "Fraction, Object": function(t5, n3) {
    return t5;
  }, "BigNumber, Object": function(t5, n3) {
    return t5;
  }, "number, Object": function(t5, n3) {
    return M2(t5, n3);
  }, "bigint, Object": function(t5, n3) {
    return t5;
  }, "Complex, Object": function(t5, n3) {
    return t5.im === 0 ? M2(t5.re, n3) : t5;
  }, "Matrix, Object": function(t5, n3) {
    return a2(M2(t5.valueOf()));
  }, "Array, Object": function(t5, n3) {
    return t5.map(M2);
  } });
  function F(t5) {
    return new g2(`-`, `unaryMinus`, [t5]);
  }
  function I(t5) {
    var n3 = (t6) => r2.number === `BigNumber` && c2 ? c2(t6) : Number(t6), i3 = t5.s * t5.n, a3 = i3 < 0n ? new g2(`-`, `unaryMinus`, [new d2(-n3(i3))]) : new d2(n3(i3));
    return t5.d === 1n ? a3 : new g2(`/`, `divide`, [a3, new d2(n3(t5.d))]);
  }
  function L(t5, n3, r3) {
    if (!isIndexNode(n3)) return new l2(j2(t5), j2(n3));
    if (isArrayNode(t5) || isMatrix(t5)) {
      for (var i3 = Array.from(n3.dimensions); i3.length > 0; ) if (isConstantNode(i3[0]) && typeof i3[0].value != `string`) {
        var o3 = N2(i3.shift().value, r3);
        isArrayNode(t5) ? t5 = t5.items[o3 - 1] : (t5 = t5.valueOf()[o3 - 1], t5 instanceof Array && (t5 = a2(t5)));
      } else if (i3.length > 1 && isConstantNode(i3[1]) && typeof i3[1].value != `string`) {
        var s3 = N2(i3[1].value, r3), c3 = [], p3 = isArrayNode(t5) ? t5.items : t5.valueOf();
        for (var h3 of p3) if (isArrayNode(h3)) c3.push(h3.items[s3 - 1]);
        else if (isMatrix(t5)) c3.push(h3[s3 - 1]);
        else break;
        if (c3.length === p3.length) t5 = isArrayNode(t5) ? new u2(c3) : a2(c3), i3.splice(1, 1);
        else break;
      } else break;
      return i3.length === n3.dimensions.length ? new l2(j2(t5), n3) : i3.length > 0 ? (n3 = new m2(i3), new l2(j2(t5), n3)) : t5;
    }
    if (isObjectNode(t5) && n3.dimensions.length === 1 && isConstantNode(n3.dimensions[0])) {
      var g3 = n3.dimensions[0].value;
      return g3 in t5.properties ? t5.properties[g3] : new d2();
    }
    return new l2(j2(t5), n3);
  }
  function R(t5, n3, r3, i3) {
    var a3 = n3.shift(), o3 = n3.reduce((n4, a4) => {
      if (!isNode(a4)) {
        var o4 = n4.pop();
        if (isNode(o4)) return [o4, a4];
        try {
          return n4.push(D(t5, [o4, a4], i3)), n4;
        } catch {
          n4.push(o4);
        }
      }
      return n4.push(j2(n4.pop())), [r3([n4.length === 1 ? n4[0] : r3(n4), j2(a4)])];
    }, [a3]);
    return o3.length === 1 ? o3[0] : r3([o3[0], O(o3[1])]);
  }
  function z(t5, n3) {
    switch (t5.type) {
      case `SymbolNode`:
        return t5;
      case `ConstantNode`:
        switch (typeof t5.value) {
          case `number`:
            return N2(t5.value, n3);
          case `bigint`:
            return N2(t5.value, n3);
          case `string`:
            return t5.value;
          default:
            if (!isNaN(t5.value)) return N2(t5.value, n3);
        }
        return t5;
      case `FunctionNode`:
        if (i2[t5.name] && i2[t5.name].rawArgs) return t5;
        if (![`add`, `multiply`].includes(t5.name)) {
          var r3 = t5.args.map((t6) => z(t6, n3));
          if (!r3.some(isNode)) try {
            return D(t5.name, r3, n3);
          } catch {
          }
          if (t5.name === `size` && r3.length === 1 && isArrayNode(r3[0])) {
            for (var o3 = [], s3 = r3[0]; isArrayNode(s3); ) o3.push(s3.items.length), s3 = s3.items[0];
            return a2(o3);
          }
          return new p2(t5.name, r3.map(j2));
        }
      case `OperatorNode`:
        var c3 = t5.fn.toString(), l3, d3, g3 = S2(t5);
        if (isOperatorNode(t5) && t5.isUnary()) l3 = [z(t5.args[0], n3)], d3 = isNode(l3[0]) ? g3(l3) : D(c3, l3, n3);
        else if (b2(t5, n3.context)) if (l3 = x2(t5, n3.context), l3 = l3.map((t6) => z(t6, n3)), y2(c3, n3.context)) {
          for (var v3 = [], E2 = [], O2 = 0; O2 < l3.length; O2++) isNode(l3[O2]) ? E2.push(l3[O2]) : v3.push(l3[O2]);
          v3.length > 1 ? (d3 = R(c3, v3, g3, n3), E2.unshift(d3), d3 = R(c3, E2, g3, n3)) : d3 = R(c3, l3, g3, n3);
        } else d3 = R(c3, l3, g3, n3);
        else l3 = t5.args.map((t6) => z(t6, n3)), d3 = R(c3, l3, g3, n3);
        return d3;
      case `ParenthesisNode`:
        return z(t5.content, n3);
      case `AccessorNode`:
        return L(z(t5.object, n3), z(t5.index, n3), n3);
      case `ArrayNode`:
        var M3 = t5.items.map((t6) => z(t6, n3));
        return M3.some(isNode) ? new u2(M3.map(j2)) : a2(M3);
      case `IndexNode`:
        return new m2(t5.dimensions.map((t6) => w2(t6, n3)));
      case `ObjectNode`:
        var F2 = {};
        for (var I2 in t5.properties) F2[I2] = w2(t5.properties[I2], n3);
        return new h2(F2);
      default:
        throw Error(`Unimplemented node type in simplifyConstant: ${t5.type}`);
    }
  }
  return w2;
}), name$36 = `simplifyCore`, dependencies$36 = [`typed`, `parse`, `equal`, `isZero`, `add`, `subtract`, `multiply`, `divide`, `pow`, `AccessorNode`, `ArrayNode`, `ConstantNode`, `FunctionNode`, `IndexNode`, `ObjectNode`, `OperatorNode`, `ParenthesisNode`, `SymbolNode`], createSimplifyCore = factory(name$36, dependencies$36, (t4) => {
  var { typed: n2, parse: r2, equal: i2, isZero: a2, add: o2, subtract: s2, multiply: c2, divide: l2, pow: u2, AccessorNode: d2, ArrayNode: p2, ConstantNode: m2, FunctionNode: h2, IndexNode: g2, ObjectNode: v2, OperatorNode: y2, ParenthesisNode: b2, SymbolNode: x2 } = t4, S2 = new m2(0), w2 = new m2(1), E = new m2(true), D = new m2(false);
  function O(t5) {
    return isOperatorNode(t5) && [`and`, `not`, `or`].includes(t5.op);
  }
  var { hasProperty: j2, isCommutative: M2 } = createUtil({ FunctionNode: h2, OperatorNode: y2, SymbolNode: x2 });
  function N2(t5) {
    var n3 = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {}, r3 = n3 ? n3.context : void 0;
    if (j2(t5, `trivial`, r3)) {
      if (isFunctionNode(t5) && t5.args.length === 1) return N2(t5.args[0], n3);
      var o3 = false, s3 = 0;
      if (t5.forEach((t6) => {
        ++s3, s3 === 1 && (o3 = N2(t6, n3));
      }), s3 === 1) return o3;
    }
    var c3 = t5;
    if (isFunctionNode(c3)) {
      var l3 = getOperator(c3.name);
      if (l3) {
        if (c3.args.length > 2 && j2(c3, `associative`, r3)) for (; c3.args.length > 2; ) {
          var u3 = c3.args.pop(), m3 = c3.args.pop();
          c3.args.push(new y2(l3, c3.name, [u3, m3]));
        }
        c3 = new y2(l3, c3.name, c3.args);
      } else return new h2(N2(c3.fn), c3.args.map((t6) => N2(t6, n3)));
    }
    if (isOperatorNode(c3) && c3.isUnary()) {
      var b3 = N2(c3.args[0], n3);
      if (c3.op === `~` && isOperatorNode(b3) && b3.isUnary() && b3.op === `~` || c3.op === `not` && isOperatorNode(b3) && b3.isUnary() && b3.op === `not` && O(b3.args[0])) return b3.args[0];
      var x3 = true;
      if (c3.op === `-` && isOperatorNode(b3) && (b3.isBinary() && b3.fn === `subtract` && (c3 = new y2(`-`, `subtract`, [b3.args[1], b3.args[0]]), x3 = false), b3.isUnary() && b3.op === `-`)) return b3.args[0];
      if (x3) return new y2(c3.op, c3.fn, [b3]);
    }
    if (isOperatorNode(c3) && c3.isBinary()) {
      var F = N2(c3.args[0], n3), I = N2(c3.args[1], n3);
      if (c3.op === `+`) {
        if (isConstantNode(F) && a2(F.value)) return I;
        if (isConstantNode(I) && a2(I.value)) return F;
        isOperatorNode(I) && I.isUnary() && I.op === `-` && (I = I.args[0], c3 = new y2(`-`, `subtract`, [F, I]));
      }
      if (c3.op === `-`) return isOperatorNode(I) && I.isUnary() && I.op === `-` ? N2(new y2(`+`, `add`, [F, I.args[0]]), n3) : isConstantNode(F) && a2(F.value) ? N2(new y2(`-`, `unaryMinus`, [I])) : isConstantNode(I) && a2(I.value) ? F : new y2(c3.op, c3.fn, [F, I]);
      if (c3.op === `*`) {
        if (isConstantNode(F)) {
          if (a2(F.value)) return S2;
          if (i2(F.value, 1)) return I;
        }
        if (isConstantNode(I)) {
          if (a2(I.value)) return S2;
          if (i2(I.value, 1)) return F;
          if (M2(c3, r3)) return new y2(c3.op, c3.fn, [I, F], c3.implicit);
        }
        return new y2(c3.op, c3.fn, [F, I], c3.implicit);
      }
      if (c3.op === `/`) return isConstantNode(F) && a2(F.value) ? S2 : isConstantNode(I) && i2(I.value, 1) ? F : new y2(c3.op, c3.fn, [F, I]);
      if (c3.op === `^` && isConstantNode(I)) {
        if (a2(I.value)) return w2;
        if (i2(I.value, 1)) return F;
      }
      if (c3.op === `and`) {
        if (isConstantNode(F)) if (F.value) {
          if (O(I)) return I;
          if (isConstantNode(I)) return I.value ? E : D;
        } else return D;
        if (isConstantNode(I)) if (I.value) {
          if (O(F)) return F;
        } else return D;
      }
      if (c3.op === `or`) {
        if (isConstantNode(F)) {
          if (F.value) return E;
          if (O(I)) return I;
        }
        if (isConstantNode(I)) {
          if (I.value) return E;
          if (O(F)) return F;
        }
      }
      return new y2(c3.op, c3.fn, [F, I]);
    }
    if (isOperatorNode(c3)) return new y2(c3.op, c3.fn, c3.args.map((t6) => N2(t6, n3)));
    if (isArrayNode(c3)) return new p2(c3.items.map((t6) => N2(t6, n3)));
    if (isAccessorNode(c3)) return new d2(N2(c3.object, n3), N2(c3.index, n3));
    if (isIndexNode(c3)) return new g2(c3.dimensions.map((t6) => N2(t6, n3)));
    if (isObjectNode(c3)) {
      var L = {};
      for (var R in c3.properties) L[R] = N2(c3.properties[R], n3);
      return new v2(L);
    }
    return c3;
  }
  return n2(name$36, { Node: N2, "Node,Object": N2 });
}), name$35 = `resolve`, dependencies$35 = [`typed`, `parse`, `ConstantNode`, `FunctionNode`, `OperatorNode`, `ParenthesisNode`], createResolve = factory(name$35, dependencies$35, (t4) => {
  var { typed: n2, parse: r2, ConstantNode: i2, FunctionNode: a2, OperatorNode: o2, ParenthesisNode: s2 } = t4;
  function c2(t5, n3) {
    var l2 = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : /* @__PURE__ */ new Set();
    if (!n3) return t5;
    if (isSymbolNode(t5)) {
      if (l2.has(t5.name)) {
        var u2 = Array.from(l2).join(`, `);
        throw ReferenceError(`recursive loop of variable definitions among {${u2}}`);
      }
      var d2 = n3.get(t5.name);
      if (isNode(d2)) {
        var p2 = new Set(l2);
        return p2.add(t5.name), c2(d2, n3, p2);
      } else if (typeof d2 == `number`) return r2(String(d2));
      else if (d2 !== void 0) return new i2(d2);
      else return t5;
    } else if (isOperatorNode(t5)) {
      var m2 = t5.args.map(function(t6) {
        return c2(t6, n3, l2);
      });
      return new o2(t5.op, t5.fn, m2, t5.implicit);
    } else if (isParenthesisNode(t5)) return new s2(c2(t5.content, n3, l2));
    else if (isFunctionNode(t5)) {
      var h2 = t5.args.map(function(t6) {
        return c2(t6, n3, l2);
      });
      return new a2(t5.name, h2);
    }
    return t5.map((t6) => c2(t6, n3, l2));
  }
  return n2(`resolve`, { Node: c2, "Node, Map | null | undefined": c2, "Node, Object": (t5, n3) => c2(t5, createMap$1(n3)), "Array | Matrix": n2.referToSelf((t5) => (n3) => n3.map((n4) => t5(n4))), "Array | Matrix, null | undefined": n2.referToSelf((t5) => (n3) => n3.map((n4) => t5(n4))), "Array, Object": n2.referTo(`Array,Map`, (t5) => (n3, r3) => t5(n3, createMap$1(r3))), "Matrix, Object": n2.referTo(`Matrix,Map`, (t5) => (n3, r3) => t5(n3, createMap$1(r3))), "Array | Matrix, Map": n2.referToSelf((t5) => (n3, r3) => n3.map((n4) => t5(n4, r3))) });
}), name$34 = `symbolicEqual`, dependencies$34 = [`parse`, `simplify`, `typed`, `OperatorNode`], createSymbolicEqual = factory(name$34, dependencies$34, (t4) => {
  var { parse: n2, simplify: r2, typed: i2, OperatorNode: a2 } = t4;
  function o2(t5, n3) {
    var i3 = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : {}, o3 = r2(new a2(`-`, `subtract`, [t5, n3]), {}, i3);
    return isConstantNode(o3) && !o3.value;
  }
  return i2(name$34, { "Node, Node": o2, "Node, Node, Object": o2 });
}), name$33 = `derivative`, dependencies$33 = [`typed`, `config`, `parse`, `simplify`, `equal`, `isZero`, `numeric`, `ConstantNode`, `FunctionNode`, `OperatorNode`, `ParenthesisNode`, `SymbolNode`], createDerivative = factory(name$33, dependencies$33, (t4) => {
  var { typed: n2, config: r2, parse: i2, simplify: a2, equal: o2, isZero: s2, numeric: c2, ConstantNode: l2, FunctionNode: u2, OperatorNode: d2, ParenthesisNode: p2, SymbolNode: m2 } = t4;
  function h2(t5, n3) {
    var r3 = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : { simplify: true }, i3 = /* @__PURE__ */ new Map(), o3 = n3.name;
    function s3(t6) {
      var n4 = i3.get(t6);
      if (n4 !== void 0) return n4;
      var r4 = b2(s3, t6, o3);
      return i3.set(t6, r4), r4;
    }
    var c3 = x2(t5, s3);
    return r3.simplify ? a2(c3) : c3;
  }
  function g2(t5) {
    var n3 = i2(t5);
    if (!n3.isSymbolNode) throw TypeError(`Invalid variable. Cannot parse ${JSON.stringify(t5)} into a variable in function derivative`);
    return n3;
  }
  var v2 = n2(name$33, { "Node, SymbolNode": h2, "Node, SymbolNode, Object": h2, "Node, string": (t5, n3) => h2(t5, g2(n3)), "Node, string, Object": (t5, n3, r3) => h2(t5, g2(n3), r3) });
  v2._simplify = true, v2.toTex = function(t5) {
    return y2.apply(null, t5.args);
  };
  var y2 = n2(`_derivTex`, { "Node, SymbolNode": function(t5, n3) {
    return isConstantNode(t5) && typeOf(t5.value) === `string` ? y2(i2(t5.value).toString(), n3.toString(), 1) : y2(t5.toTex(), n3.toString(), 1);
  }, "Node, ConstantNode": function(t5, n3) {
    if (typeOf(n3.value) === `string`) return y2(t5, i2(n3.value));
    throw Error(`The second parameter to 'derivative' is a non-string constant`);
  }, "Node, SymbolNode, ConstantNode": function(t5, n3, r3) {
    return y2(t5.toString(), n3.name, r3.value);
  }, "string, string, number": function(t5, n3, r3) {
    return (r3 === 1 ? `{d\\over d` + n3 + `}` : `{d^{` + r3 + `}\\over d` + n3 + `^{` + r3 + `}}`) + `\\left[${t5}\\right]`;
  } }), b2 = n2(`_isConst`, { "function, ConstantNode, string": function() {
    return true;
  }, "function, SymbolNode, string": function(t5, n3, r3) {
    return n3.name !== r3;
  }, "function, ParenthesisNode, string": function(t5, n3, r3) {
    return t5(n3.content, r3);
  }, "function, FunctionAssignmentNode, string": function(t5, n3, r3) {
    return n3.params.includes(r3) ? t5(n3.expr, r3) : true;
  }, "function, FunctionNode | OperatorNode, string": function(t5, n3, r3) {
    return n3.args.every((n4) => t5(n4, r3));
  } }), x2 = n2(`_derivative`, { "ConstantNode, function": function() {
    return S2(0);
  }, "SymbolNode, function": function(t5, n3) {
    return n3(t5) ? S2(0) : S2(1);
  }, "ParenthesisNode, function": function(t5, n3) {
    return new p2(x2(t5.content, n3));
  }, "FunctionAssignmentNode, function": function(t5, n3) {
    return n3(t5) ? S2(0) : x2(t5.expr, n3);
  }, "FunctionNode, function": function(t5, n3) {
    if (n3(t5)) return S2(0);
    var r3 = t5.args[0], i3, a3 = false, o3 = false, s3;
    switch (t5.name) {
      case `cbrt`:
        a3 = true, s3 = new d2(`*`, `multiply`, [S2(3), new d2(`^`, `pow`, [r3, new d2(`/`, `divide`, [S2(2), S2(3)])])]);
        break;
      case `sqrt`:
      case `nthRoot`:
        if (t5.args.length === 1) a3 = true, s3 = new d2(`*`, `multiply`, [S2(2), new u2(`sqrt`, [r3])]);
        else if (t5.args.length === 2) return i3 = new d2(`/`, `divide`, [S2(1), t5.args[1]]), x2(new d2(`^`, `pow`, [r3, i3]), n3);
        break;
      case `log10`:
        i3 = S2(10);
      case `log`:
        if (!i3 && t5.args.length === 1) s3 = r3.clone(), a3 = true;
        else if (t5.args.length === 1 && i3 || t5.args.length === 2 && n3(t5.args[1])) s3 = new d2(`*`, `multiply`, [r3.clone(), new u2(`log`, [i3 || t5.args[1]])]), a3 = true;
        else if (t5.args.length === 2) return x2(new d2(`/`, `divide`, [new u2(`log`, [r3]), new u2(`log`, [t5.args[1]])]), n3);
        break;
      case `pow`:
        if (t5.args.length === 2) return x2(new d2(`^`, `pow`, [r3, t5.args[1]]), n3);
        break;
      case `exp`:
        s3 = new u2(`exp`, [r3.clone()]);
        break;
      case `sin`:
        s3 = new u2(`cos`, [r3.clone()]);
        break;
      case `cos`:
        s3 = new d2(`-`, `unaryMinus`, [new u2(`sin`, [r3.clone()])]);
        break;
      case `tan`:
        s3 = new d2(`^`, `pow`, [new u2(`sec`, [r3.clone()]), S2(2)]);
        break;
      case `sec`:
        s3 = new d2(`*`, `multiply`, [t5, new u2(`tan`, [r3.clone()])]);
        break;
      case `csc`:
        o3 = true, s3 = new d2(`*`, `multiply`, [t5, new u2(`cot`, [r3.clone()])]);
        break;
      case `cot`:
        o3 = true, s3 = new d2(`^`, `pow`, [new u2(`csc`, [r3.clone()]), S2(2)]);
        break;
      case `asin`:
        a3 = true, s3 = new u2(`sqrt`, [new d2(`-`, `subtract`, [S2(1), new d2(`^`, `pow`, [r3.clone(), S2(2)])])]);
        break;
      case `acos`:
        a3 = true, o3 = true, s3 = new u2(`sqrt`, [new d2(`-`, `subtract`, [S2(1), new d2(`^`, `pow`, [r3.clone(), S2(2)])])]);
        break;
      case `atan`:
        a3 = true, s3 = new d2(`+`, `add`, [new d2(`^`, `pow`, [r3.clone(), S2(2)]), S2(1)]);
        break;
      case `asec`:
        a3 = true, s3 = new d2(`*`, `multiply`, [new u2(`abs`, [r3.clone()]), new u2(`sqrt`, [new d2(`-`, `subtract`, [new d2(`^`, `pow`, [r3.clone(), S2(2)]), S2(1)])])]);
        break;
      case `acsc`:
        a3 = true, o3 = true, s3 = new d2(`*`, `multiply`, [new u2(`abs`, [r3.clone()]), new u2(`sqrt`, [new d2(`-`, `subtract`, [new d2(`^`, `pow`, [r3.clone(), S2(2)]), S2(1)])])]);
        break;
      case `acot`:
        a3 = true, o3 = true, s3 = new d2(`+`, `add`, [new d2(`^`, `pow`, [r3.clone(), S2(2)]), S2(1)]);
        break;
      case `sinh`:
        s3 = new u2(`cosh`, [r3.clone()]);
        break;
      case `cosh`:
        s3 = new u2(`sinh`, [r3.clone()]);
        break;
      case `tanh`:
        s3 = new d2(`^`, `pow`, [new u2(`sech`, [r3.clone()]), S2(2)]);
        break;
      case `sech`:
        o3 = true, s3 = new d2(`*`, `multiply`, [t5, new u2(`tanh`, [r3.clone()])]);
        break;
      case `csch`:
        o3 = true, s3 = new d2(`*`, `multiply`, [t5, new u2(`coth`, [r3.clone()])]);
        break;
      case `coth`:
        o3 = true, s3 = new d2(`^`, `pow`, [new u2(`csch`, [r3.clone()]), S2(2)]);
        break;
      case `asinh`:
        a3 = true, s3 = new u2(`sqrt`, [new d2(`+`, `add`, [new d2(`^`, `pow`, [r3.clone(), S2(2)]), S2(1)])]);
        break;
      case `acosh`:
        a3 = true, s3 = new u2(`sqrt`, [new d2(`-`, `subtract`, [new d2(`^`, `pow`, [r3.clone(), S2(2)]), S2(1)])]);
        break;
      case `atanh`:
        a3 = true, s3 = new d2(`-`, `subtract`, [S2(1), new d2(`^`, `pow`, [r3.clone(), S2(2)])]);
        break;
      case `asech`:
        a3 = true, o3 = true, s3 = new d2(`*`, `multiply`, [r3.clone(), new u2(`sqrt`, [new d2(`-`, `subtract`, [S2(1), new d2(`^`, `pow`, [r3.clone(), S2(2)])])])]);
        break;
      case `acsch`:
        a3 = true, o3 = true, s3 = new d2(`*`, `multiply`, [new u2(`abs`, [r3.clone()]), new u2(`sqrt`, [new d2(`+`, `add`, [new d2(`^`, `pow`, [r3.clone(), S2(2)]), S2(1)])])]);
        break;
      case `acoth`:
        a3 = true, o3 = true, s3 = new d2(`-`, `subtract`, [S2(1), new d2(`^`, `pow`, [r3.clone(), S2(2)])]);
        break;
      case `abs`:
        s3 = new d2(`/`, `divide`, [new u2(new m2(`abs`), [r3.clone()]), r3.clone()]);
        break;
      default:
        throw Error(`Cannot process function "` + t5.name + `" in derivative: the function is not supported, undefined, or the number of arguments passed to it are not supported`);
    }
    var c3, l3;
    a3 ? (c3 = `/`, l3 = `divide`) : (c3 = `*`, l3 = `multiply`);
    var p3 = x2(r3, n3);
    return o3 && (p3 = new d2(`-`, `unaryMinus`, [p3])), new d2(c3, l3, [p3, s3]);
  }, "OperatorNode, function": function(t5, n3) {
    if (n3(t5)) return S2(0);
    if (t5.op === `+`) return new d2(t5.op, t5.fn, t5.args.map(function(t6) {
      return x2(t6, n3);
    }));
    if (t5.op === `-`) {
      if (t5.isUnary()) return new d2(t5.op, t5.fn, [x2(t5.args[0], n3)]);
      if (t5.isBinary()) return new d2(t5.op, t5.fn, [x2(t5.args[0], n3), x2(t5.args[1], n3)]);
    }
    if (t5.op === `*`) {
      var r3 = t5.args.filter(function(t6) {
        return n3(t6);
      });
      if (r3.length > 0) {
        var i3 = t5.args.filter(function(t6) {
          return !n3(t6);
        }), a3 = i3.length === 1 ? i3[0] : new d2(`*`, `multiply`, i3);
        return new d2(`*`, `multiply`, r3.concat(x2(a3, n3)));
      }
      return new d2(`+`, `add`, t5.args.map(function(r4) {
        return new d2(`*`, `multiply`, t5.args.map(function(t6) {
          return t6 === r4 ? x2(t6, n3) : t6.clone();
        }));
      }));
    }
    if (t5.op === `/` && t5.isBinary()) {
      var c3 = t5.args[0], l3 = t5.args[1];
      return n3(l3) ? new d2(`/`, `divide`, [x2(c3, n3), l3]) : n3(c3) ? new d2(`*`, `multiply`, [new d2(`-`, `unaryMinus`, [c3]), new d2(`/`, `divide`, [x2(l3, n3), new d2(`^`, `pow`, [l3.clone(), S2(2)])])]) : new d2(`/`, `divide`, [new d2(`-`, `subtract`, [new d2(`*`, `multiply`, [x2(c3, n3), l3.clone()]), new d2(`*`, `multiply`, [c3.clone(), x2(l3, n3)])]), new d2(`^`, `pow`, [l3.clone(), S2(2)])]);
    }
    if (t5.op === `^` && t5.isBinary()) {
      var p3 = t5.args[0], m3 = t5.args[1];
      if (n3(p3)) return isConstantNode(p3) && (s2(p3.value) || o2(p3.value, 1)) ? S2(0) : new d2(`*`, `multiply`, [t5, new d2(`*`, `multiply`, [new u2(`log`, [p3.clone()]), x2(m3.clone(), n3)])]);
      if (n3(m3)) {
        if (isConstantNode(m3)) {
          if (s2(m3.value)) return S2(0);
          if (o2(m3.value, 1)) return x2(p3, n3);
        }
        var h3 = new d2(`^`, `pow`, [p3.clone(), new d2(`-`, `subtract`, [m3, S2(1)])]);
        return new d2(`*`, `multiply`, [m3.clone(), new d2(`*`, `multiply`, [x2(p3, n3), h3])]);
      }
      return new d2(`*`, `multiply`, [new d2(`^`, `pow`, [p3.clone(), m3.clone()]), new d2(`+`, `add`, [new d2(`*`, `multiply`, [x2(p3, n3), new d2(`/`, `divide`, [m3.clone(), p3.clone()])]), new d2(`*`, `multiply`, [x2(m3, n3), new u2(`log`, [p3.clone()])])])]);
    }
    throw Error(`Cannot process operator "` + t5.op + `" in derivative: the operator is not supported, undefined, or the number of arguments passed to it are not supported`);
  } });
  function S2(t5, n3) {
    return new l2(c2(t5, safeNumberType(String(t5), r2)));
  }
  return v2;
}), name$32 = `rationalize`, dependencies$32 = `config.typed.equal.isZero.add.subtract.multiply.divide.pow.parse.simplifyConstant.simplifyCore.simplify.?bignumber.?fraction.mathWithTransform.matrix.AccessorNode.ArrayNode.ConstantNode.FunctionNode.IndexNode.ObjectNode.OperatorNode.SymbolNode.ParenthesisNode`.split(`.`), createRationalize = factory(name$32, dependencies$32, (t4) => {
  var { config: n2, typed: r2, equal: i2, isZero: a2, add: o2, subtract: s2, multiply: c2, divide: l2, pow: u2, parse: d2, simplifyConstant: p2, simplifyCore: m2, simplify: h2, fraction: g2, bignumber: v2, mathWithTransform: y2, matrix: b2, AccessorNode: x2, ArrayNode: S2, ConstantNode: w2, FunctionNode: E, IndexNode: D, ObjectNode: O, OperatorNode: j2, SymbolNode: M2, ParenthesisNode: N2 } = t4;
  function F(t5) {
    var n3 = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {}, r3 = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : false, i3 = L(), a3 = I(t5, n3, true, i3.firstRules), o3 = a3.variables.length, s3 = { exactFractions: false }, c3 = { exactFractions: true };
    if (t5 = a3.expression, o3 >= 1) {
      t5 = R(t5);
      var l3, u3, d3 = true, p3 = false;
      t5 = h2(t5, i3.firstRules, {}, s3);
      for (var m3; u3 = d3 ? i3.distrDivRules : i3.sucDivRules, t5 = h2(t5, u3, {}, c3), d3 = !d3, m3 = t5.toString(), m3 !== l3; ) p3 = true, l3 = m3;
      p3 && (t5 = h2(t5, i3.firstRulesAgain, {}, s3)), t5 = h2(t5, i3.finalRules, {}, s3);
    }
    var g3 = [], v3 = {};
    return t5.type === `OperatorNode` && t5.isBinary() && t5.op === `/` ? (o3 === 1 && (t5.args[0] = z(t5.args[0], g3), t5.args[1] = z(t5.args[1])), r3 && (v3.numerator = t5.args[0], v3.denominator = t5.args[1])) : (o3 === 1 && (t5 = z(t5, g3)), r3 && (v3.numerator = t5, v3.denominator = null)), r3 ? (v3.coefficients = g3, v3.variables = a3.variables, v3.expression = t5, v3) : t5;
  }
  return r2(name$32, { Node: F, "Node, boolean": (t5, n3) => F(t5, {}, n3), "Node, Object": F, "Node, Object, boolean": F });
  function I(t5, n3, r3, i3) {
    var a3 = [], o3 = h2(t5, i3, n3, { exactFractions: false });
    r3 = !!r3;
    var s3 = `+-*` + (r3 ? `/` : ``);
    l3(o3);
    var c3 = {};
    return c3.expression = o3, c3.variables = a3, c3;
    function l3(t6) {
      var n4 = t6.type;
      if (n4 === `FunctionNode`) throw Error(`There is an unsolved function call`);
      if (n4 === `OperatorNode`) if (t6.op === `^`) {
        if (t6.args[1].type !== `ConstantNode` || !isInteger(parseFloat(t6.args[1].value))) throw Error(`There is a non-integer exponent`);
        l3(t6.args[0]);
      } else {
        if (!s3.includes(t6.op)) throw Error(`Operator ` + t6.op + ` invalid in polynomial expression`);
        for (var r4 = 0; r4 < t6.args.length; r4++) l3(t6.args[r4]);
      }
      else if (n4 === `SymbolNode`) {
        var i4 = t6.name;
        a3.indexOf(i4) === -1 && a3.push(i4);
      } else if (n4 === `ParenthesisNode`) l3(t6.content);
      else if (n4 !== `ConstantNode`) throw Error(`type ` + n4 + ` is not allowed in polynomial expression`);
    }
  }
  function L() {
    var t5 = [m2, { l: `n+n`, r: `2*n` }, { l: `n+-n`, r: `0` }, p2, { l: `n*(n1^-1)`, r: `n/n1` }, { l: `n*n1^-n2`, r: `n/n1^n2` }, { l: `n1^-1`, r: `1/n1` }, { l: `n*(n1/n2)`, r: `(n*n1)/n2` }, { l: `1*n`, r: `n` }], n3 = [{ l: `(-n1)/(-n2)`, r: `n1/n2` }, { l: `(-n1)*(-n2)`, r: `n1*n2` }, { l: `n1--n2`, r: `n1+n2` }, { l: `n1-n2`, r: `n1+(-n2)` }, { l: `(n1+n2)*n3`, r: `(n1*n3 + n2*n3)` }, { l: `n1*(n2+n3)`, r: `(n1*n2+n1*n3)` }, { l: `c1*n + c2*n`, r: `(c1+c2)*n` }, { l: `c1*n + n`, r: `(c1+1)*n` }, { l: `c1*n - c2*n`, r: `(c1-c2)*n` }, { l: `c1*n - n`, r: `(c1-1)*n` }, { l: `v/c`, r: `(1/c)*v` }, { l: `v/-c`, r: `-(1/c)*v` }, { l: `-v*-c`, r: `c*v` }, { l: `-v*c`, r: `-c*v` }, { l: `v*-c`, r: `-c*v` }, { l: `v*c`, r: `c*v` }, { l: `-(-n1*n2)`, r: `(n1*n2)` }, { l: `-(n1*n2)`, r: `(-n1*n2)` }, { l: `-(-n1+n2)`, r: `(n1-n2)` }, { l: `-(n1+n2)`, r: `(-n1-n2)` }, { l: `(n1^n2)^n3`, r: `(n1^(n2*n3))` }, { l: `-(-n1/n2)`, r: `(n1/n2)` }, { l: `-(n1/n2)`, r: `(-n1/n2)` }], r3 = [{ l: `(n1/n2 + n3/n4)`, r: `((n1*n4 + n3*n2)/(n2*n4))` }, { l: `(n1/n2 + n3)`, r: `((n1 + n3*n2)/n2)` }, { l: `(n1 + n2/n3)`, r: `((n1*n3 + n2)/n3)` }], i3 = [{ l: `(n1/(n2/n3))`, r: `((n1*n3)/n2)` }, { l: `(n1/n2/n3)`, r: `(n1/(n2*n3))` }], a3 = {};
    return a3.firstRules = t5.concat(n3, i3), a3.distrDivRules = r3, a3.sucDivRules = i3, a3.firstRulesAgain = t5.concat(n3), a3.finalRules = [m2, { l: `n*-n`, r: `-n^2` }, { l: `n*n`, r: `n^2` }, p2, { l: `n*-n^n1`, r: `-n^(n1+1)` }, { l: `n*n^n1`, r: `n^(n1+1)` }, { l: `n^n1*-n^n2`, r: `-n^(n1+n2)` }, { l: `n^n1*n^n2`, r: `n^(n1+n2)` }, { l: `n^n1*-n`, r: `-n^(n1+1)` }, { l: `n^n1*n`, r: `n^(n1+1)` }, { l: `n^n1/-n`, r: `-n^(n1-1)` }, { l: `n^n1/n`, r: `n^(n1-1)` }, { l: `n/-n^n1`, r: `-n^(1-n1)` }, { l: `n/n^n1`, r: `n^(1-n1)` }, { l: `n^n1/-n^n2`, r: `n^(n1-n2)` }, { l: `n^n1/n^n2`, r: `n^(n1-n2)` }, { l: `n1+(-n2*n3)`, r: `n1-n2*n3` }, { l: `v*(-c)`, r: `-c*v` }, { l: `n1+-n2`, r: `n1-n2` }, { l: `v*c`, r: `c*v` }, { l: `(n1^n2)^n3`, r: `(n1^(n2*n3))` }], a3;
  }
  function R(t5, n3, r3) {
    var i3 = t5.type, a3 = arguments.length > 1;
    if (i3 === `OperatorNode` && t5.isBinary()) {
      var o3 = false, s3;
      if (t5.op === `^` && (t5.args[0].type === `ParenthesisNode` || t5.args[0].type === `OperatorNode`) && t5.args[1].type === `ConstantNode` && (s3 = parseFloat(t5.args[1].value), o3 = s3 >= 2 && isInteger(s3)), o3) {
        if (s3 > 2) {
          var c3 = t5.args[0];
          t5 = new j2(`*`, `multiply`, [c3, new j2(`^`, `pow`, [t5.args[0].cloneDeep(), new w2(s3 - 1)])]);
        } else t5 = new j2(`*`, `multiply`, [t5.args[0], t5.args[0].cloneDeep()]);
        a3 && (r3 === `content` ? n3.content = t5 : n3.args[r3] = t5);
      }
    }
    if (i3 === `ParenthesisNode`) R(t5.content, t5, `content`);
    else if (i3 !== `ConstantNode` && i3 !== `SymbolNode`) for (var l3 = 0; l3 < t5.args.length; l3++) R(t5.args[l3], t5, l3);
    if (!a3) return t5;
  }
  function z(t5, n3) {
    n3 === void 0 && (n3 = []), n3[0] = 0;
    var r3 = {};
    r3.cte = 1, r3.oper = `+`, r3.fire = ``;
    var i3 = 0, a3 = ``;
    m3(t5, null, r3), i3 = n3.length - 1;
    for (var o3 = true, s3, c3 = i3; c3 >= 0; c3--) if (n3[c3] !== 0) {
      var l3 = new w2(o3 ? n3[c3] : Math.abs(n3[c3])), u3 = n3[c3] < 0 ? `-` : `+`;
      if (c3 > 0) {
        var d3 = new M2(a3);
        if (c3 > 1) {
          var p3 = new w2(c3);
          d3 = new j2(`^`, `pow`, [d3, p3]);
        }
        l3 = n3[c3] === -1 && o3 ? new j2(`-`, `unaryMinus`, [d3]) : Math.abs(n3[c3]) === 1 ? d3 : new j2(`*`, `multiply`, [l3, d3]);
      }
      s3 = o3 ? l3 : u3 === `+` ? new j2(`+`, `add`, [s3, l3]) : new j2(`-`, `subtract`, [s3, l3]), o3 = false;
    }
    if (o3) return new w2(0);
    return s3;
    function m3(t6, r4, o4) {
      var s4 = t6.type;
      if (s4 === `FunctionNode`) throw Error(`There is an unsolved function call`);
      if (s4 === `OperatorNode`) {
        if (!`+-*^`.includes(t6.op)) throw Error(`Operator ` + t6.op + ` invalid`);
        if (r4 !== null && ((t6.fn === `unaryMinus` || t6.fn === `pow`) && r4.fn !== `add` && r4.fn !== `subtract` && r4.fn !== `multiply` || (t6.fn === `subtract` || t6.fn === `add` || t6.fn === `multiply`) && r4.fn !== `add` && r4.fn !== `subtract` || (t6.fn === `subtract` || t6.fn === `add` || t6.fn === `unaryMinus`) && o4.noFil !== 0)) throw Error(`Invalid ` + t6.op + ` placing`);
        (t6.op === `^` || t6.op === `*`) && (o4.fire = t6.op);
        for (var c4 = 0; c4 < t6.args.length; c4++) t6.fn === `unaryMinus` && (o4.oper = `-`), (t6.op === `+` || t6.fn === `subtract`) && (o4.fire = ``, o4.cte = 1, o4.oper = c4 === 0 ? `+` : t6.op), o4.noFil = c4, m3(t6.args[c4], t6, o4);
      } else if (s4 === `SymbolNode`) {
        if (t6.name !== a3 && a3 !== ``) throw Error(`There is more than one variable`);
        if (a3 = t6.name, r4 === null) {
          n3[1] = 1;
          return;
        }
        if (r4.op === `^` && o4.noFil !== 0) throw Error(`In power the variable should be the first parameter`);
        if (r4.op === `*` && o4.noFil !== 1) throw Error(`In multiply the variable should be the second parameter`);
        (o4.fire === `` || o4.fire === `*`) && (i3 < 1 && (n3[1] = 0), n3[1] += o4.cte * (o4.oper === `+` ? 1 : -1), i3 = Math.max(1, i3));
      } else if (s4 === `ConstantNode`) {
        var l4 = parseFloat(t6.value);
        if (r4 === null) {
          n3[0] = l4;
          return;
        }
        if (r4.op === `^`) {
          if (o4.noFil !== 1) throw Error(`Constant cannot be powered`);
          if (!isInteger(l4) || l4 <= 0) throw Error(`Non-integer exponent is not allowed`);
          for (var u4 = i3 + 1; u4 < l4; u4++) n3[u4] = 0;
          l4 > i3 && (n3[l4] = 0), n3[l4] += o4.cte * (o4.oper === `+` ? 1 : -1), i3 = Math.max(l4, i3);
          return;
        }
        o4.cte = l4, o4.fire === `` && (n3[0] += o4.cte * (o4.oper === `+` ? 1 : -1));
      } else throw Error(`Type ` + s4 + ` is not allowed`);
    }
  }
}), name$31 = `zpk2tf`, dependencies$31 = [`typed`, `add`, `multiply`, `Complex`, `number`], createZpk2tf = factory(name$31, dependencies$31, (t4) => {
  var { typed: n2, add: r2, multiply: i2, Complex: a2, number: o2 } = t4;
  return n2(name$31, { "Array,Array,number": function(t5, n3, r3) {
    return s2(t5, n3, r3);
  }, "Array,Array": function(t5, n3) {
    return s2(t5, n3, 1);
  }, "Matrix,Matrix,number": function(t5, n3, r3) {
    return s2(t5.valueOf(), n3.valueOf(), r3);
  }, "Matrix,Matrix": function(t5, n3) {
    return s2(t5.valueOf(), n3.valueOf(), 1);
  } });
  function s2(t5, n3, r3) {
    t5.some((t6) => t6.type === `BigNumber`) && (t5 = t5.map((t6) => o2(t6))), n3.some((t6) => t6.type === `BigNumber`) && (n3 = n3.map((t6) => o2(t6)));
    for (var s3 = [a2(1, 0)], l2 = [a2(1, 0)], u2 = 0; u2 < t5.length; u2++) {
      var d2 = t5[u2];
      typeof d2 == `number` && (d2 = a2(d2, 0)), s3 = c2(s3, [a2(1, 0), a2(-d2.re, -d2.im)]);
    }
    for (var p2 = 0; p2 < n3.length; p2++) {
      var m2 = n3[p2];
      typeof m2 == `number` && (m2 = a2(m2, 0)), l2 = c2(l2, [a2(1, 0), a2(-m2.re, -m2.im)]);
    }
    for (var h2 = 0; h2 < s3.length; h2++) s3[h2] = i2(s3[h2], r3);
    return [s3, l2];
  }
  function c2(t5, n3) {
    for (var o3 = [], s3 = 0; s3 < t5.length + n3.length - 1; s3++) {
      o3[s3] = a2(0, 0);
      for (var c3 = 0; c3 < t5.length; c3++) s3 - c3 >= 0 && s3 - c3 < n3.length && (o3[s3] = r2(o3[s3], i2(t5[c3], n3[s3 - c3])));
    }
    return o3;
  }
}), name$30 = `freqz`, dependencies$30 = [`typed`, `add`, `multiply`, `Complex`, `divide`, `matrix`], createFreqz = factory(name$30, dependencies$30, (t4) => {
  var { typed: n2, add: r2, multiply: i2, Complex: a2, divide: o2, matrix: s2 } = t4;
  return n2(name$30, { "Array, Array": function(t5, n3) {
    return c2(t5, n3, l2(512));
  }, "Array, Array, Array": function(t5, n3, r3) {
    return c2(t5, n3, r3);
  }, "Array, Array, number": function(t5, n3, r3) {
    if (r3 < 0) throw Error(`w must be a positive number`);
    return c2(t5, n3, l2(r3));
  }, "Matrix, Matrix": function(t5, n3) {
    var r3 = l2(512), { w: i3, h: a3 } = c2(t5.valueOf(), n3.valueOf(), r3);
    return { w: s2(i3), h: s2(a3) };
  }, "Matrix, Matrix, Matrix": function(t5, n3, r3) {
    var { h: i3 } = c2(t5.valueOf(), n3.valueOf(), r3.valueOf());
    return { h: s2(i3), w: s2(r3) };
  }, "Matrix, Matrix, number": function(t5, n3, r3) {
    if (r3 < 0) throw Error(`w must be a positive number`);
    var i3 = l2(r3), { h: a3 } = c2(t5.valueOf(), n3.valueOf(), i3);
    return { h: s2(a3), w: s2(i3) };
  } });
  function c2(t5, n3, s3) {
    for (var c3 = [], l3 = [], u2 = 0; u2 < s3.length; u2++) {
      for (var d2 = a2(0, 0), p2 = a2(0, 0), m2 = 0; m2 < t5.length; m2++) d2 = r2(d2, i2(t5[m2], a2(Math.cos(-m2 * s3[u2]), Math.sin(-m2 * s3[u2]))));
      for (var h2 = 0; h2 < n3.length; h2++) p2 = r2(p2, i2(n3[h2], a2(Math.cos(-h2 * s3[u2]), Math.sin(-h2 * s3[u2]))));
      c3.push(d2), l3.push(p2);
    }
    for (var g2 = [], v2 = 0; v2 < c3.length; v2++) g2.push(o2(c3[v2], l3[v2]));
    return { h: g2, w: s3 };
  }
  function l2(t5) {
    for (var n3 = [], r3 = 0; r3 < t5; r3++) n3.push(r3 / t5 * Math.PI);
    return n3;
  }
}), name$29 = `reviver`, dependencies$29 = [`classes`], createReviver = factory(name$29, dependencies$29, (t4) => {
  var { classes: n2 } = t4;
  return function(t5, r2) {
    var i2 = n2[r2 && r2.mathjs];
    return i2 && typeof i2.fromJSON == `function` ? i2.fromJSON(r2) : r2;
  };
}), name$28 = `replacer`, dependencies$28 = [], createReplacer = factory(name$28, dependencies$28, () => function(t4, n2) {
  return typeof n2 == `number` && (!Number.isFinite(n2) || isNaN(n2)) ? { mathjs: `number`, value: String(n2) } : typeof n2 == `bigint` ? { mathjs: `bigint`, value: String(n2) } : n2;
}), version = `15.2.0`, createTrue = factory(`true`, [], () => true), createFalse = factory(`false`, [], () => false), createNull = factory(`null`, [], () => null), createInfinity = recreateFactory(`Infinity`, [`config`, `?BigNumber`], (t4) => {
  var { config: n2, BigNumber: r2 } = t4;
  return n2.number === `BigNumber` ? new r2(1 / 0) : 1 / 0;
}), createNaN = recreateFactory(`NaN`, [`config`, `?BigNumber`], (t4) => {
  var { config: n2, BigNumber: r2 } = t4;
  return n2.number === `BigNumber` ? new r2(NaN) : NaN;
}), createPi = recreateFactory(`pi`, [`config`, `?BigNumber`], (t4) => {
  var { config: n2, BigNumber: r2 } = t4;
  return n2.number === `BigNumber` ? createBigNumberPi(r2) : pi;
}), createTau = recreateFactory(`tau`, [`config`, `?BigNumber`], (t4) => {
  var { config: n2, BigNumber: r2 } = t4;
  return n2.number === `BigNumber` ? createBigNumberTau(r2) : tau;
}), createE = recreateFactory(`e`, [`config`, `?BigNumber`], (t4) => {
  var { config: n2, BigNumber: r2 } = t4;
  return n2.number === `BigNumber` ? createBigNumberE(r2) : e;
}), createPhi = recreateFactory(`phi`, [`config`, `?BigNumber`], (t4) => {
  var { config: n2, BigNumber: r2 } = t4;
  return n2.number === `BigNumber` ? createBigNumberPhi(r2) : phi;
}), createLN2 = recreateFactory(`LN2`, [`config`, `?BigNumber`], (t4) => {
  var { config: n2, BigNumber: r2 } = t4;
  return n2.number === `BigNumber` ? new r2(2).ln() : Math.LN2;
}), createLN10 = recreateFactory(`LN10`, [`config`, `?BigNumber`], (t4) => {
  var { config: n2, BigNumber: r2 } = t4;
  return n2.number === `BigNumber` ? new r2(10).ln() : Math.LN10;
}), createLOG2E = recreateFactory(`LOG2E`, [`config`, `?BigNumber`], (t4) => {
  var { config: n2, BigNumber: r2 } = t4;
  return n2.number === `BigNumber` ? new r2(1).div(new r2(2).ln()) : Math.LOG2E;
}), createLOG10E = recreateFactory(`LOG10E`, [`config`, `?BigNumber`], (t4) => {
  var { config: n2, BigNumber: r2 } = t4;
  return n2.number === `BigNumber` ? new r2(1).div(new r2(10).ln()) : Math.LOG10E;
}), createSQRT1_2 = recreateFactory(`SQRT1_2`, [`config`, `?BigNumber`], (t4) => {
  var { config: n2, BigNumber: r2 } = t4;
  return n2.number === `BigNumber` ? new r2(`0.5`).sqrt() : Math.SQRT1_2;
}), createSQRT2 = recreateFactory(`SQRT2`, [`config`, `?BigNumber`], (t4) => {
  var { config: n2, BigNumber: r2 } = t4;
  return n2.number === `BigNumber` ? new r2(2).sqrt() : Math.SQRT2;
}), createI = recreateFactory(`i`, [`Complex`], (t4) => {
  var { Complex: n2 } = t4;
  return n2.I;
}), createUppercasePi = factory(`PI`, [`pi`], (t4) => {
  var { pi: n2 } = t4;
  return n2;
}), createUppercaseE = factory(`E`, [`e`], (t4) => {
  var { e: n2 } = t4;
  return n2;
}), createVersion = factory(`version`, [], () => version);
function recreateFactory(t4, n2, r2) {
  return factory(t4, n2, r2, { recreateOnConfigChange: true });
}
var createSpeedOfLight = unitFactory(`speedOfLight`, `299792458`, `m s^-1`), createGravitationConstant = unitFactory(`gravitationConstant`, `6.67430e-11`, `m^3 kg^-1 s^-2`), createPlanckConstant = unitFactory(`planckConstant`, `6.62607015e-34`, `J s`), createReducedPlanckConstant = unitFactory(`reducedPlanckConstant`, `1.0545718176461565e-34`, `J s`), createMagneticConstant = unitFactory(`magneticConstant`, `1.25663706212e-6`, `N A^-2`), createElectricConstant = unitFactory(`electricConstant`, `8.8541878128e-12`, `F m^-1`), createVacuumImpedance = unitFactory(`vacuumImpedance`, `376.730313667`, `ohm`), createCoulomb = unitFactory(`coulomb`, `8.987551792261171e9`, `N m^2 C^-2`), createCoulombConstant = unitFactory(`coulombConstant`, `8.987551792261171e9`, `N m^2 C^-2`), createElementaryCharge = unitFactory(`elementaryCharge`, `1.602176634e-19`, `C`), createBohrMagneton = unitFactory(`bohrMagneton`, `9.2740100783e-24`, `J T^-1`), createConductanceQuantum = unitFactory(`conductanceQuantum`, `7.748091729863649e-5`, `S`), createInverseConductanceQuantum = unitFactory(`inverseConductanceQuantum`, `12906.403729652257`, `ohm`), createMagneticFluxQuantum = unitFactory(`magneticFluxQuantum`, `2.0678338484619295e-15`, `Wb`), createNuclearMagneton = unitFactory(`nuclearMagneton`, `5.0507837461e-27`, `J T^-1`), createKlitzing = unitFactory(`klitzing`, `25812.807459304513`, `ohm`), createBohrRadius = unitFactory(`bohrRadius`, `5.29177210903e-11`, `m`), createClassicalElectronRadius = unitFactory(`classicalElectronRadius`, `2.8179403262e-15`, `m`), createElectronMass = unitFactory(`electronMass`, `9.1093837015e-31`, `kg`), createFermiCoupling = unitFactory(`fermiCoupling`, `1.1663787e-5`, `GeV^-2`), createFineStructure = numberFactory(`fineStructure`, 0.0072973525693), createHartreeEnergy = unitFactory(`hartreeEnergy`, `4.3597447222071e-18`, `J`), createProtonMass = unitFactory(`protonMass`, `1.67262192369e-27`, `kg`), createDeuteronMass = unitFactory(`deuteronMass`, `3.3435830926e-27`, `kg`), createNeutronMass = unitFactory(`neutronMass`, `1.6749271613e-27`, `kg`), createQuantumOfCirculation = unitFactory(`quantumOfCirculation`, `3.6369475516e-4`, `m^2 s^-1`), createRydberg = unitFactory(`rydberg`, `10973731.568160`, `m^-1`), createThomsonCrossSection = unitFactory(`thomsonCrossSection`, `6.6524587321e-29`, `m^2`), createWeakMixingAngle = numberFactory(`weakMixingAngle`, 0.2229), createEfimovFactor = numberFactory(`efimovFactor`, 22.7), createAtomicMass = unitFactory(`atomicMass`, `1.66053906660e-27`, `kg`), createAvogadro = unitFactory(`avogadro`, `6.02214076e23`, `mol^-1`), createBoltzmann = unitFactory(`boltzmann`, `1.380649e-23`, `J K^-1`), createFaraday = unitFactory(`faraday`, `96485.33212331001`, `C mol^-1`), createFirstRadiation = unitFactory(`firstRadiation`, `3.7417718521927573e-16`, `W m^2`), createLoschmidt = unitFactory(`loschmidt`, `2.686780111798444e25`, `m^-3`), createGasConstant = unitFactory(`gasConstant`, `8.31446261815324`, `J K^-1 mol^-1`), createMolarPlanckConstant = unitFactory(`molarPlanckConstant`, `3.990312712893431e-10`, `J s mol^-1`), createMolarVolume = unitFactory(`molarVolume`, `0.022413969545014137`, `m^3 mol^-1`), createSackurTetrode = numberFactory(`sackurTetrode`, -1.16487052358), createSecondRadiation = unitFactory(`secondRadiation`, `0.014387768775039337`, `m K`), createStefanBoltzmann = unitFactory(`stefanBoltzmann`, `5.67037441918443e-8`, `W m^-2 K^-4`), createWienDisplacement = unitFactory(`wienDisplacement`, `2.897771955e-3`, `m K`), createMolarMass = unitFactory(`molarMass`, `0.99999999965e-3`, `kg mol^-1`), createMolarMassC12 = unitFactory(`molarMassC12`, `11.9999999958e-3`, `kg mol^-1`), createGravity = unitFactory(`gravity`, `9.80665`, `m s^-2`), createPlanckLength = unitFactory(`planckLength`, `1.616255e-35`, `m`), createPlanckMass = unitFactory(`planckMass`, `2.176435e-8`, `kg`), createPlanckTime = unitFactory(`planckTime`, `5.391245e-44`, `s`), createPlanckCharge = unitFactory(`planckCharge`, `1.87554603778e-18`, `C`), createPlanckTemperature = unitFactory(`planckTemperature`, `1.416785e+32`, `K`);
function unitFactory(t4, n2, r2) {
  return factory(t4, [`config`, `Unit`, `BigNumber`], (t5) => {
    var { config: i2, Unit: a2, BigNumber: o2 } = t5, s2 = new a2(i2.number === `BigNumber` ? new o2(n2) : parseFloat(n2), r2);
    return s2.fixPrefix = true, s2;
  });
}
function numberFactory(t4, n2) {
  return factory(t4, [`config`, `BigNumber`], (t5) => {
    var { config: r2, BigNumber: i2 } = t5;
    return r2.number === `BigNumber` ? new i2(n2) : n2;
  });
}
function ownKeys(t4, n2) {
  var r2 = Object.keys(t4);
  if (Object.getOwnPropertySymbols) {
    var i2 = Object.getOwnPropertySymbols(t4);
    n2 && (i2 = i2.filter(function(n3) {
      return Object.getOwnPropertyDescriptor(t4, n3).enumerable;
    })), r2.push.apply(r2, i2);
  }
  return r2;
}
function _objectSpread(t4) {
  for (var n2 = 1; n2 < arguments.length; n2++) {
    var r2 = arguments[n2] == null ? {} : arguments[n2];
    n2 % 2 ? ownKeys(Object(r2), true).forEach(function(n3) {
      _defineProperty(t4, n3, r2[n3]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(t4, Object.getOwnPropertyDescriptors(r2)) : ownKeys(Object(r2)).forEach(function(n3) {
      Object.defineProperty(t4, n3, Object.getOwnPropertyDescriptor(r2, n3));
    });
  }
  return t4;
}
var name$27 = `mapSlices`, dependencies$27 = [`typed`, `isInteger`], createMapSlicesTransform = factory(name$27, dependencies$27, (t4) => {
  var { typed: n2, isInteger: r2 } = t4, i2 = createMapSlices({ typed: n2, isInteger: r2 });
  return n2(`mapSlices`, { "...any": function(t5) {
    var n3 = t5[1];
    isNumber(n3) ? t5[1] = n3 - 1 : isBigNumber(n3) && (t5[1] = n3.minus(1));
    try {
      return i2.apply(null, t5);
    } catch (t6) {
      throw errorTransform(t6);
    }
  } });
}, _objectSpread({ isTransformFunction: true }, createMapSlices.meta)), name$26 = `column`, dependencies$26 = [`typed`, `Index`, `matrix`, `range`], createColumnTransform = factory(name$26, dependencies$26, (t4) => {
  var { typed: n2, Index: r2, matrix: i2, range: a2 } = t4, o2 = createColumn({ typed: n2, Index: r2, matrix: i2, range: a2 });
  return n2(`column`, { "...any": function(t5) {
    var n3 = t5.length - 1, r3 = t5[n3];
    isNumber(r3) && (t5[n3] = r3 - 1);
    try {
      return o2.apply(null, t5);
    } catch (t6) {
      throw errorTransform(t6);
    }
  } });
}, { isTransformFunction: true });
function compileInlineExpression(t4, n2, r2) {
  var i2 = t4.filter(function(t5) {
    return isSymbolNode(t5) && !(t5.name in n2) && !r2.has(t5.name);
  })[0];
  if (!i2) throw Error(`No undefined variable found in inline expression "` + t4 + `"`);
  var a2 = i2.name, o2 = /* @__PURE__ */ new Map(), s2 = new PartitionedMap(r2, o2, /* @__PURE__ */ new Set([a2])), c2 = t4.compile();
  return function(t5) {
    return o2.set(a2, t5), c2.evaluate(s2);
  };
}
var name$25 = `transformCallback`, dependencies$25 = [`typed`], createTransformCallback = factory(name$25, dependencies$25, (t4) => {
  var { typed: n2 } = t4;
  return function(t5, i2) {
    return n2.isTypedFunction(t5) ? r2(t5, i2) : _transformCallbackFunction(t5, t5.length, i2);
  };
  function r2(t5, i2) {
    var a2 = Object.fromEntries(Object.entries(t5.signatures).map((t6) => {
      var [a3, o2] = t6, s2 = a3.split(`,`).length;
      return n2.isTypedFunction(o2) ? [a3, r2(o2, i2)] : [a3, _transformCallbackFunction(o2, s2, i2)];
    }));
    return typeof t5.name == `string` ? n2(t5.name, a2) : n2(a2);
  }
});
function _transformCallbackFunction(t4, n2, r2) {
  return n2 === r2 ? t4 : n2 === r2 + 1 ? function() {
    var n3 = [...arguments], i2 = n3.slice(0, r2), a2 = _transformDims(n3[r2]);
    return t4(...i2, a2);
  } : n2 > r2 + 1 ? function() {
    var n3 = [...arguments], i2 = n3.slice(0, r2), a2 = _transformDims(n3[r2]), o2 = n3.slice(r2 + 1);
    return t4(...i2, a2, ...o2);
  } : t4;
}
function _transformDims(t4) {
  return t4.map((t5) => t5 + 1);
}
var name$24 = `filter`, dependencies$24 = [`typed`], createFilterTransform = factory(name$24, dependencies$24, (t4) => {
  var { typed: n2 } = t4;
  function r2(t5, r3, a2) {
    var o2 = createFilter({ typed: n2 }), s2 = createTransformCallback({ typed: n2 });
    if (t5.length === 0) return o2();
    var c2 = t5[0];
    if (t5.length === 1) return o2(c2);
    var l2 = t5.length - 1, u2 = t5[l2];
    return c2 &&= i2(c2, a2), u2 &&= isSymbolNode(u2) || isFunctionAssignmentNode(u2) ? i2(u2, a2) : compileInlineExpression(u2, r3, a2), o2(c2, s2(u2, l2));
  }
  r2.rawArgs = true;
  function i2(t5, n3) {
    return t5.compile().evaluate(n3);
  }
  return r2;
}, { isTransformFunction: true }), name$23 = `forEach`, dependencies$23 = [`typed`], createForEachTransform = factory(name$23, dependencies$23, (t4) => {
  var { typed: n2 } = t4, r2 = createForEach({ typed: n2 }), i2 = createTransformCallback({ typed: n2 });
  function a2(t5, n3, a3) {
    if (t5.length === 0) return r2();
    var s2 = t5[0];
    if (t5.length === 1) return r2(s2);
    var c2 = t5.length - 1, l2 = t5[c2];
    return s2 &&= o2(s2, a3), l2 &&= isSymbolNode(l2) || isFunctionAssignmentNode(l2) ? o2(l2, a3) : compileInlineExpression(l2, n3, a3), r2(s2, i2(l2, c2));
  }
  a2.rawArgs = true;
  function o2(t5, n3) {
    return t5.compile().evaluate(n3);
  }
  return a2;
}, { isTransformFunction: true }), name$22 = `index`, dependencies$22 = [`Index`, `getMatrixDataType`], createIndexTransform = factory(name$22, dependencies$22, (t4) => {
  var { Index: n2, getMatrixDataType: r2 } = t4;
  return function() {
    for (var t5 = [], i2 = 0, a2 = arguments.length; i2 < a2; i2++) {
      var o2 = arguments[i2];
      if (isRange(o2)) o2.start--, o2.end -= o2.step > 0 ? 0 : 2;
      else if (o2 && o2.isSet === true) o2 = o2.map(function(t6) {
        return t6 - 1;
      });
      else if (isArray(o2) || isMatrix(o2)) r2(o2) !== `boolean` && (o2 = o2.map(function(t6) {
        return t6 - 1;
      }));
      else if (isNumber(o2) || isBigInt(o2)) o2--;
      else if (isBigNumber(o2)) o2 = o2.toNumber() - 1;
      else if (typeof o2 != `string`) throw TypeError(`Dimension must be an Array, Matrix, number, bigint, string, or Range`);
      t5[i2] = o2;
    }
    var s2 = new n2();
    return n2.apply(s2, t5), s2;
  };
}, { isTransformFunction: true }), name$21 = `map`, dependencies$21 = [`typed`], createMapTransform = factory(name$21, dependencies$21, (t4) => {
  var { typed: n2 } = t4, r2 = createMap({ typed: n2 }), i2 = createTransformCallback({ typed: n2 });
  function a2(t5, n3, a3) {
    if (t5.length === 0) return r2();
    if (t5.length === 1) return r2(t5[0]);
    var o2 = t5.length - 1, s2 = t5.slice(0, o2), c2 = t5[o2];
    return s2 = s2.map((t6) => l2(t6, a3)), c2 &&= isSymbolNode(c2) || isFunctionAssignmentNode(c2) ? l2(c2, a3) : compileInlineExpression(c2, n3, a3), r2(...s2, i2(c2, o2));
    function l2(t6, n4) {
      return t6.compile().evaluate(n4);
    }
  }
  return a2.rawArgs = true, a2;
}, { isTransformFunction: true });
function dimToZeroBase(t4) {
  return isNumber(t4) ? t4 - 1 : isBigNumber(t4) ? t4.minus(1) : t4;
}
function isNumberOrBigNumber(t4) {
  return isNumber(t4) || isBigNumber(t4);
}
function lastDimToZeroBase(t4) {
  if (t4.length === 2 && isCollection(t4[0])) {
    t4 = t4.slice();
    var n2 = t4[1];
    isNumberOrBigNumber(n2) && (t4[1] = dimToZeroBase(n2));
  }
  return t4;
}
var name$20 = `max`, dependencies$20 = [`typed`, `config`, `numeric`, `larger`, `isNaN`], createMaxTransform = factory(name$20, dependencies$20, (t4) => {
  var { typed: n2, config: r2, numeric: i2, larger: a2, isNaN: o2 } = t4, s2 = createMax({ typed: n2, config: r2, numeric: i2, larger: a2, isNaN: o2 });
  return n2(`max`, { "...any": function(t5) {
    t5 = lastDimToZeroBase(t5);
    try {
      return s2.apply(null, t5);
    } catch (t6) {
      throw errorTransform(t6);
    }
  } });
}, { isTransformFunction: true }), name$19 = `mean`, dependencies$19 = [`typed`, `add`, `divide`], createMeanTransform = factory(name$19, dependencies$19, (t4) => {
  var { typed: n2, add: r2, divide: i2 } = t4, a2 = createMean({ typed: n2, add: r2, divide: i2 });
  return n2(`mean`, { "...any": function(t5) {
    t5 = lastDimToZeroBase(t5);
    try {
      return a2.apply(null, t5);
    } catch (t6) {
      throw errorTransform(t6);
    }
  } });
}, { isTransformFunction: true }), name$18 = `min`, dependencies$18 = [`typed`, `config`, `numeric`, `smaller`, `isNaN`], createMinTransform = factory(name$18, dependencies$18, (t4) => {
  var { typed: n2, config: r2, numeric: i2, smaller: a2, isNaN: o2 } = t4, s2 = createMin({ typed: n2, config: r2, numeric: i2, smaller: a2, isNaN: o2 });
  return n2(`min`, { "...any": function(t5) {
    t5 = lastDimToZeroBase(t5);
    try {
      return s2.apply(null, t5);
    } catch (t6) {
      throw errorTransform(t6);
    }
  } });
}, { isTransformFunction: true }), name$17 = `range`, dependencies$17 = [`typed`, `config`, `?matrix`, `?bignumber`, `equal`, `smaller`, `smallerEq`, `larger`, `largerEq`, `add`, `isZero`, `isPositive`], createRangeTransform = factory(name$17, dependencies$17, (t4) => {
  var { typed: n2, config: r2, matrix: i2, bignumber: a2, equal: o2, smaller: s2, smallerEq: c2, larger: l2, largerEq: u2, add: d2, isZero: p2, isPositive: m2 } = t4, h2 = createRange({ typed: n2, config: r2, matrix: i2, bignumber: a2, equal: o2, smaller: s2, smallerEq: c2, larger: l2, largerEq: u2, add: d2, isZero: p2, isPositive: m2 });
  return n2(`range`, { "...any": function(t5) {
    return typeof t5[t5.length - 1] != `boolean` && t5.push(true), h2.apply(null, t5);
  } });
}, { isTransformFunction: true }), name$16 = `row`, dependencies$16 = [`typed`, `Index`, `matrix`, `range`], createRowTransform = factory(name$16, dependencies$16, (t4) => {
  var { typed: n2, Index: r2, matrix: i2, range: a2 } = t4, o2 = createRow({ typed: n2, Index: r2, matrix: i2, range: a2 });
  return n2(`row`, { "...any": function(t5) {
    var n3 = t5.length - 1, r3 = t5[n3];
    isNumber(r3) && (t5[n3] = r3 - 1);
    try {
      return o2.apply(null, t5);
    } catch (t6) {
      throw errorTransform(t6);
    }
  } });
}, { isTransformFunction: true }), name$15 = `subset`, dependencies$15 = [`typed`, `matrix`, `zeros`, `add`], createSubsetTransform = factory(name$15, dependencies$15, (t4) => {
  var { typed: n2, matrix: r2, zeros: i2, add: a2 } = t4, o2 = createSubset({ typed: n2, matrix: r2, zeros: i2, add: a2 });
  return n2(`subset`, { "...any": function(t5) {
    try {
      return o2.apply(null, t5);
    } catch (t6) {
      throw errorTransform(t6);
    }
  } });
}, { isTransformFunction: true }), name$14 = `concat`, dependencies$14 = [`typed`, `matrix`, `isInteger`], createConcatTransform = factory(name$14, dependencies$14, (t4) => {
  var { typed: n2, matrix: r2, isInteger: i2 } = t4, a2 = createConcat({ typed: n2, matrix: r2, isInteger: i2 });
  return n2(`concat`, { "...any": function(t5) {
    var n3 = t5.length - 1, r3 = t5[n3];
    isNumber(r3) ? t5[n3] = r3 - 1 : isBigNumber(r3) && (t5[n3] = r3.minus(1));
    try {
      return a2.apply(null, t5);
    } catch (t6) {
      throw errorTransform(t6);
    }
  } });
}, { isTransformFunction: true }), name$13 = `diff`, dependencies$13 = [`typed`, `matrix`, `subtract`, `number`, `bignumber`], createDiffTransform = factory(name$13, dependencies$13, (t4) => {
  var { typed: n2, matrix: r2, subtract: i2, number: a2, bignumber: o2 } = t4, s2 = createDiff({ typed: n2, matrix: r2, subtract: i2, number: a2, bignumber: o2 });
  return n2(name$13, { "...any": function(t5) {
    t5 = lastDimToZeroBase(t5);
    try {
      return s2.apply(null, t5);
    } catch (t6) {
      throw errorTransform(t6);
    }
  } });
}, { isTransformFunction: true }), name$12 = `std`, dependencies$12 = [`typed`, `map`, `sqrt`, `variance`], createStdTransform = factory(name$12, dependencies$12, (t4) => {
  var { typed: n2, map: r2, sqrt: i2, variance: a2 } = t4, o2 = createStd({ typed: n2, map: r2, sqrt: i2, variance: a2 });
  return n2(`std`, { "...any": function(t5) {
    t5 = lastDimToZeroBase(t5);
    try {
      return o2.apply(null, t5);
    } catch (t6) {
      throw errorTransform(t6);
    }
  } });
}, { isTransformFunction: true }), name$11 = `sum`, dependencies$11 = [`typed`, `config`, `add`, `numeric`], createSumTransform = factory(name$11, dependencies$11, (t4) => {
  var { typed: n2, config: r2, add: i2, numeric: a2 } = t4, o2 = createSum({ typed: n2, config: r2, add: i2, numeric: a2 });
  return n2(name$11, { "...any": function(t5) {
    t5 = lastDimToZeroBase(t5);
    try {
      return o2.apply(null, t5);
    } catch (t6) {
      throw errorTransform(t6);
    }
  } });
}, { isTransformFunction: true }), name$10 = `quantileSeq`, dependencies$10 = [`typed`, `bignumber`, `add`, `subtract`, `divide`, `multiply`, `partitionSelect`, `compare`, `isInteger`, `smaller`, `smallerEq`, `larger`, `mapSlices`], createQuantileSeqTransform = factory(name$10, dependencies$10, (t4) => {
  var { typed: n2, bignumber: r2, add: i2, subtract: a2, divide: o2, multiply: s2, partitionSelect: c2, compare: l2, isInteger: u2, smaller: d2, smallerEq: p2, larger: m2, mapSlices: h2 } = t4, g2 = createQuantileSeq({ typed: n2, bignumber: r2, add: i2, subtract: a2, divide: o2, multiply: s2, partitionSelect: c2, compare: l2, isInteger: u2, smaller: d2, smallerEq: p2, larger: m2, mapSlices: h2 });
  return n2(`quantileSeq`, { "Array | Matrix, number | BigNumber": g2, "Array | Matrix, number | BigNumber, number": (t5, n3, r3) => g2(t5, n3, v2(r3)), "Array | Matrix, number | BigNumber, boolean": g2, "Array | Matrix, number | BigNumber, boolean, number": (t5, n3, r3, i3) => g2(t5, n3, r3, v2(i3)), "Array | Matrix, Array | Matrix": g2, "Array | Matrix, Array | Matrix, number": (t5, n3, r3) => g2(t5, n3, v2(r3)), "Array | Matrix, Array | Matrix, boolean": g2, "Array | Matrix, Array | Matrix, boolean, number": (t5, n3, r3, i3) => g2(t5, n3, r3, v2(i3)) });
  function v2(t5) {
    return lastDimToZeroBase([[], t5])[1];
  }
}, { isTransformFunction: true }), name$9 = `cumsum`, dependencies$9 = [`typed`, `add`, `unaryPlus`], createCumSumTransform = factory(name$9, dependencies$9, (t4) => {
  var { typed: n2, add: r2, unaryPlus: i2 } = t4, a2 = createCumSum({ typed: n2, add: r2, unaryPlus: i2 });
  return n2(name$9, { "...any": function(t5) {
    if (t5.length === 2 && isCollection(t5[0])) {
      var n3 = t5[1];
      isNumber(n3) ? t5[1] = n3 - 1 : isBigNumber(n3) && (t5[1] = n3.minus(1));
    }
    try {
      return a2.apply(null, t5);
    } catch (t6) {
      throw errorTransform(t6);
    }
  } });
}, { isTransformFunction: true }), name$8 = `variance`, dependencies$8 = [`typed`, `add`, `subtract`, `multiply`, `divide`, `mapSlices`, `isNaN`], createVarianceTransform = factory(name$8, dependencies$8, (t4) => {
  var { typed: n2, add: r2, subtract: i2, multiply: a2, divide: o2, mapSlices: s2, isNaN: c2 } = t4, l2 = createVariance({ typed: n2, add: r2, subtract: i2, multiply: a2, divide: o2, mapSlices: s2, isNaN: c2 });
  return n2(name$8, { "...any": function(t5) {
    t5 = lastDimToZeroBase(t5);
    try {
      return l2.apply(null, t5);
    } catch (t6) {
      throw errorTransform(t6);
    }
  } });
}, { isTransformFunction: true }), name$7 = `print`, dependencies$7 = [`typed`, `matrix`, `zeros`, `add`], createPrintTransform = factory(name$7, dependencies$7, (t4) => {
  var { typed: n2, matrix: r2, zeros: i2, add: a2 } = t4, o2 = createPrint({ typed: n2, matrix: r2, zeros: i2, add: a2 });
  return n2(name$7, { "string, Object | Array": function(t5, n3) {
    return o2(s2(t5), n3);
  }, "string, Object | Array, number | Object": function(t5, n3, r3) {
    return o2(s2(t5), n3, r3);
  } });
  function s2(t5) {
    return t5.replace(printTemplate, (t6) => `$` + t6.slice(1).split(`.`).map(function(t7) {
      return !isNaN(t7) && t7.length > 0 ? parseInt(t7) - 1 : t7;
    }).join(`.`));
  }
}, { isTransformFunction: true }), name$6 = `and`, dependencies$6 = [`typed`, `matrix`, `zeros`, `add`, `equalScalar`, `not`, `concat`], createAndTransform = factory(name$6, dependencies$6, (t4) => {
  var { typed: n2, matrix: r2, equalScalar: i2, zeros: a2, not: o2, concat: s2 } = t4, c2 = createAnd({ typed: n2, matrix: r2, equalScalar: i2, zeros: a2, not: o2, concat: s2 });
  function l2(t5, n3, r3) {
    var i3 = t5[0].compile().evaluate(r3);
    return !isCollection(i3) && !c2(i3, true) ? false : c2(i3, t5[1].compile().evaluate(r3));
  }
  return l2.rawArgs = true, l2;
}, { isTransformFunction: true }), name$5 = `or`, dependencies$5 = [`typed`, `matrix`, `equalScalar`, `DenseMatrix`, `concat`], createOrTransform = factory(name$5, dependencies$5, (t4) => {
  var { typed: n2, matrix: r2, equalScalar: i2, DenseMatrix: a2, concat: o2 } = t4, s2 = createOr({ typed: n2, matrix: r2, equalScalar: i2, DenseMatrix: a2, concat: o2 });
  function c2(t5, n3, r3) {
    var i3 = t5[0].compile().evaluate(r3);
    return !isCollection(i3) && s2(i3, false) ? true : s2(i3, t5[1].compile().evaluate(r3));
  }
  return c2.rawArgs = true, c2;
}, { isTransformFunction: true }), name$4 = `nullish`, dependencies$4 = [`typed`, `matrix`, `size`, `flatten`, `deepEqual`], createNullishTransform = factory(name$4, dependencies$4, (t4) => {
  var { typed: n2, matrix: r2, size: i2, flatten: a2, deepEqual: o2 } = t4, s2 = createNullish({ typed: n2, matrix: r2, size: i2, flatten: a2, deepEqual: o2 });
  function c2(t5, n3, r3) {
    var i3 = t5[0].compile().evaluate(r3);
    return !isCollection(i3) && i3 != null && i3 !== void 0 ? i3 : s2(i3, t5[1].compile().evaluate(r3));
  }
  return c2.rawArgs = true, c2;
}, { isTransformFunction: true }), name$3 = `bitAnd`, dependencies$3 = [`typed`, `matrix`, `zeros`, `add`, `equalScalar`, `not`, `concat`], createBitAndTransform = factory(name$3, dependencies$3, (t4) => {
  var { typed: n2, matrix: r2, equalScalar: i2, zeros: a2, not: o2, concat: s2 } = t4, c2 = createBitAnd({ typed: n2, matrix: r2, equalScalar: i2, zeros: a2, not: o2, concat: s2 });
  function l2(t5, n3, r3) {
    var i3 = t5[0].compile().evaluate(r3);
    if (!isCollection(i3)) {
      if (isNaN(i3)) return NaN;
      if (i3 === 0 || i3 === false) return 0;
    }
    return c2(i3, t5[1].compile().evaluate(r3));
  }
  return l2.rawArgs = true, l2;
}, { isTransformFunction: true }), name$2 = `bitOr`, dependencies$2 = [`typed`, `matrix`, `equalScalar`, `DenseMatrix`, `concat`], createBitOrTransform = factory(name$2, dependencies$2, (t4) => {
  var { typed: n2, matrix: r2, equalScalar: i2, DenseMatrix: a2, concat: o2 } = t4, s2 = createBitOr({ typed: n2, matrix: r2, equalScalar: i2, DenseMatrix: a2, concat: o2 });
  function c2(t5, n3, r3) {
    var i3 = t5[0].compile().evaluate(r3);
    if (!isCollection(i3)) {
      if (isNaN(i3)) return NaN;
      if (i3 === -1) return -1;
      if (i3 === true) return 1;
    }
    return s2(i3, t5[1].compile().evaluate(r3));
  }
  return c2.rawArgs = true, c2;
}, { isTransformFunction: true }), name$1 = `num`, dependencies$1 = [`typed`, `fraction`], createNum = factory(name$1, dependencies$1, (t4) => {
  var { typed: n2, fraction: r2 } = t4;
  return n2(name$1, { Fraction: (t5) => t5.s * t5.n, BigNumber: (t5) => {
    var n3 = r2(t5);
    return n3.s * n3.n;
  }, "Array | Matrix": n2.referToSelf((t5) => (n3) => deepMap(n3, t5)) });
}), name = `den`, dependencies = [`typed`, `fraction`], createDen = factory(name, dependencies, (t4) => {
  var { typed: n2, fraction: r2 } = t4;
  return n2(name, { Fraction: (t5) => t5.d, BigNumber: (t5) => r2(t5).d, "Array | Matrix": n2.referToSelf((t5) => (n3) => deepMap(n3, t5)) });
}), factoriesAny_exports = __exportAll({ createAbs: () => createAbs, createAccessorNode: () => createAccessorNode, createAcos: () => createAcos, createAcosh: () => createAcosh, createAcot: () => createAcot, createAcoth: () => createAcoth, createAcsc: () => createAcsc, createAcsch: () => createAcsch, createAdd: () => createAdd, createAddScalar: () => createAddScalar, createAnd: () => createAnd, createAndTransform: () => createAndTransform, createArg: () => createArg, createArrayNode: () => createArrayNode, createAsec: () => createAsec, createAsech: () => createAsech, createAsin: () => createAsin, createAsinh: () => createAsinh, createAssignmentNode: () => createAssignmentNode, createAtan: () => createAtan, createAtan2: () => createAtan2, createAtanh: () => createAtanh, createAtomicMass: () => createAtomicMass, createAvogadro: () => createAvogadro, createBellNumbers: () => createBellNumbers, createBernoulli: () => createBernoulli, createBigNumberClass: () => createBigNumberClass, createBigint: () => createBigint, createBignumber: () => createBignumber, createBin: () => createBin, createBitAnd: () => createBitAnd, createBitAndTransform: () => createBitAndTransform, createBitNot: () => createBitNot, createBitOr: () => createBitOr, createBitOrTransform: () => createBitOrTransform, createBitXor: () => createBitXor, createBlockNode: () => createBlockNode, createBohrMagneton: () => createBohrMagneton, createBohrRadius: () => createBohrRadius, createBoltzmann: () => createBoltzmann, createBoolean: () => createBoolean, createCatalan: () => createCatalan, createCbrt: () => createCbrt, createCeil: () => createCeil, createChain: () => createChain, createChainClass: () => createChainClass, createClassicalElectronRadius: () => createClassicalElectronRadius, createClone: () => createClone, createColumn: () => createColumn, createColumnTransform: () => createColumnTransform, createCombinations: () => createCombinations, createCombinationsWithRep: () => createCombinationsWithRep, createCompare: () => createCompare, createCompareNatural: () => createCompareNatural, createCompareText: () => createCompareText, createCompile: () => createCompile, createComplex: () => createComplex, createComplexClass: () => createComplexClass, createComposition: () => createComposition, createConcat: () => createConcat, createConcatTransform: () => createConcatTransform, createConditionalNode: () => createConditionalNode, createConductanceQuantum: () => createConductanceQuantum, createConj: () => createConj, createConstantNode: () => createConstantNode, createCorr: () => createCorr, createCos: () => createCos, createCosh: () => createCosh, createCot: () => createCot, createCoth: () => createCoth, createCoulomb: () => createCoulomb, createCoulombConstant: () => createCoulombConstant, createCount: () => createCount, createCreateUnit: () => createCreateUnit, createCross: () => createCross, createCsc: () => createCsc, createCsch: () => createCsch, createCtranspose: () => createCtranspose, createCube: () => createCube, createCumSum: () => createCumSum, createCumSumTransform: () => createCumSumTransform, createDeepEqual: () => createDeepEqual, createDen: () => createDen, createDenseMatrixClass: () => createDenseMatrixClass, createDerivative: () => createDerivative, createDet: () => createDet, createDeuteronMass: () => createDeuteronMass, createDiag: () => createDiag, createDiff: () => createDiff, createDiffTransform: () => createDiffTransform, createDistance: () => createDistance, createDivide: () => createDivide, createDivideScalar: () => createDivideScalar, createDot: () => createDot, createDotDivide: () => createDotDivide, createDotMultiply: () => createDotMultiply, createDotPow: () => createDotPow, createE: () => createE, createEfimovFactor: () => createEfimovFactor, createEigs: () => createEigs, createElectricConstant: () => createElectricConstant, createElectronMass: () => createElectronMass, createElementaryCharge: () => createElementaryCharge, createEqual: () => createEqual, createEqualScalar: () => createEqualScalar, createEqualText: () => createEqualText, createErf: () => createErf, createEvaluate: () => createEvaluate, createExp: () => createExp, createExpm: () => createExpm, createExpm1: () => createExpm1, createFactorial: () => createFactorial, createFalse: () => createFalse, createFaraday: () => createFaraday, createFermiCoupling: () => createFermiCoupling, createFft: () => createFft, createFibonacciHeapClass: () => createFibonacciHeapClass, createFilter: () => createFilter, createFilterTransform: () => createFilterTransform, createFineStructure: () => createFineStructure, createFirstRadiation: () => createFirstRadiation, createFix: () => createFix, createFlatten: () => createFlatten, createFloor: () => createFloor, createForEach: () => createForEach, createForEachTransform: () => createForEachTransform, createFormat: () => createFormat, createFraction: () => createFraction, createFractionClass: () => createFractionClass, createFreqz: () => createFreqz, createFunctionAssignmentNode: () => createFunctionAssignmentNode, createFunctionNode: () => createFunctionNode, createGamma: () => createGamma, createGasConstant: () => createGasConstant, createGcd: () => createGcd, createGetMatrixDataType: () => createGetMatrixDataType, createGravitationConstant: () => createGravitationConstant, createGravity: () => createGravity, createHartreeEnergy: () => createHartreeEnergy, createHasNumericValue: () => createHasNumericValue, createHelp: () => createHelp, createHelpClass: () => createHelpClass, createHex: () => createHex, createHypot: () => createHypot, createI: () => createI, createIdentity: () => createIdentity, createIfft: () => createIfft, createIm: () => createIm, createImmutableDenseMatrixClass: () => createImmutableDenseMatrixClass, createIndex: () => createIndex, createIndexClass: () => createIndexClass, createIndexNode: () => createIndexNode, createIndexTransform: () => createIndexTransform, createInfinity: () => createInfinity, createIntersect: () => createIntersect, createInv: () => createInv, createInverseConductanceQuantum: () => createInverseConductanceQuantum, createInvmod: () => createInvmod, createIsBounded: () => createIsBounded, createIsFinite: () => createIsFinite, createIsInteger: () => createIsInteger, createIsNaN: () => createIsNaN, createIsNegative: () => createIsNegative, createIsNumeric: () => createIsNumeric, createIsPositive: () => createIsPositive, createIsPrime: () => createIsPrime, createIsZero: () => createIsZero, createKldivergence: () => createKldivergence, createKlitzing: () => createKlitzing, createKron: () => createKron, createLN10: () => createLN10, createLN2: () => createLN2, createLOG10E: () => createLOG10E, createLOG2E: () => createLOG2E, createLarger: () => createLarger, createLargerEq: () => createLargerEq, createLcm: () => createLcm, createLeafCount: () => createLeafCount, createLeftShift: () => createLeftShift, createLgamma: () => createLgamma, createLog: () => createLog, createLog10: () => createLog10, createLog1p: () => createLog1p, createLog2: () => createLog2, createLoschmidt: () => createLoschmidt, createLsolve: () => createLsolve, createLsolveAll: () => createLsolveAll, createLup: () => createLup, createLusolve: () => createLusolve, createLyap: () => createLyap, createMad: () => createMad, createMagneticConstant: () => createMagneticConstant, createMagneticFluxQuantum: () => createMagneticFluxQuantum, createMap: () => createMap, createMapSlices: () => createMapSlices, createMapSlicesTransform: () => createMapSlicesTransform, createMapTransform: () => createMapTransform, createMatrix: () => createMatrix, createMatrixClass: () => createMatrixClass, createMatrixFromColumns: () => createMatrixFromColumns, createMatrixFromFunction: () => createMatrixFromFunction, createMatrixFromRows: () => createMatrixFromRows, createMax: () => createMax, createMaxTransform: () => createMaxTransform, createMean: () => createMean, createMeanTransform: () => createMeanTransform, createMedian: () => createMedian, createMin: () => createMin, createMinTransform: () => createMinTransform, createMod: () => createMod, createMode: () => createMode, createMolarMass: () => createMolarMass, createMolarMassC12: () => createMolarMassC12, createMolarPlanckConstant: () => createMolarPlanckConstant, createMolarVolume: () => createMolarVolume, createMultinomial: () => createMultinomial, createMultiply: () => createMultiply, createMultiplyScalar: () => createMultiplyScalar, createNaN: () => createNaN, createNeutronMass: () => createNeutronMass, createNode: () => createNode, createNorm: () => createNorm, createNot: () => createNot, createNthRoot: () => createNthRoot, createNthRoots: () => createNthRoots, createNuclearMagneton: () => createNuclearMagneton, createNull: () => createNull, createNullish: () => createNullish, createNullishTransform: () => createNullishTransform, createNum: () => createNum, createNumber: () => createNumber, createNumeric: () => createNumeric, createObjectNode: () => createObjectNode, createOct: () => createOct, createOnes: () => createOnes, createOperatorNode: () => createOperatorNode, createOr: () => createOr, createOrTransform: () => createOrTransform, createParenthesisNode: () => createParenthesisNode, createParse: () => createParse, createParser: () => createParser, createParserClass: () => createParserClass, createPartitionSelect: () => createPartitionSelect, createPermutations: () => createPermutations, createPhi: () => createPhi, createPi: () => createPi, createPickRandom: () => createPickRandom, createPinv: () => createPinv, createPlanckCharge: () => createPlanckCharge, createPlanckConstant: () => createPlanckConstant, createPlanckLength: () => createPlanckLength, createPlanckMass: () => createPlanckMass, createPlanckTemperature: () => createPlanckTemperature, createPlanckTime: () => createPlanckTime, createPolynomialRoot: () => createPolynomialRoot, createPow: () => createPow, createPrint: () => createPrint, createPrintTransform: () => createPrintTransform, createProd: () => createProd, createProtonMass: () => createProtonMass, createQr: () => createQr, createQuantileSeq: () => createQuantileSeq, createQuantileSeqTransform: () => createQuantileSeqTransform, createQuantumOfCirculation: () => createQuantumOfCirculation, createRandom: () => createRandom, createRandomInt: () => createRandomInt, createRange: () => createRange, createRangeClass: () => createRangeClass, createRangeNode: () => createRangeNode, createRangeTransform: () => createRangeTransform, createRationalize: () => createRationalize, createRe: () => createRe, createReducedPlanckConstant: () => createReducedPlanckConstant, createRelationalNode: () => createRelationalNode, createReplacer: () => createReplacer, createReshape: () => createReshape, createResize: () => createResize, createResolve: () => createResolve, createResultSet: () => createResultSet, createReviver: () => createReviver, createRightArithShift: () => createRightArithShift, createRightLogShift: () => createRightLogShift, createRotate: () => createRotate, createRotationMatrix: () => createRotationMatrix, createRound: () => createRound, createRow: () => createRow, createRowTransform: () => createRowTransform, createRydberg: () => createRydberg, createSQRT1_2: () => createSQRT1_2, createSQRT2: () => createSQRT2, createSackurTetrode: () => createSackurTetrode, createSchur: () => createSchur, createSec: () => createSec, createSech: () => createSech, createSecondRadiation: () => createSecondRadiation, createSetCartesian: () => createSetCartesian, createSetDifference: () => createSetDifference, createSetDistinct: () => createSetDistinct, createSetIntersect: () => createSetIntersect, createSetIsSubset: () => createSetIsSubset, createSetMultiplicity: () => createSetMultiplicity, createSetPowerset: () => createSetPowerset, createSetSize: () => createSetSize, createSetSymDifference: () => createSetSymDifference, createSetUnion: () => createSetUnion, createSign: () => createSign, createSimplify: () => createSimplify, createSimplifyConstant: () => createSimplifyConstant, createSimplifyCore: () => createSimplifyCore, createSin: () => createSin, createSinh: () => createSinh, createSize: () => createSize, createSlu: () => createSlu, createSmaller: () => createSmaller, createSmallerEq: () => createSmallerEq, createSolveODE: () => createSolveODE, createSort: () => createSort, createSpaClass: () => createSpaClass, createSparse: () => createSparse, createSparseMatrixClass: () => createSparseMatrixClass, createSpeedOfLight: () => createSpeedOfLight, createSplitUnit: () => createSplitUnit, createSqrt: () => createSqrt, createSqrtm: () => createSqrtm, createSquare: () => createSquare, createSqueeze: () => createSqueeze, createStd: () => createStd, createStdTransform: () => createStdTransform, createStefanBoltzmann: () => createStefanBoltzmann, createStirlingS2: () => createStirlingS2, createString: () => createString, createSubset: () => createSubset, createSubsetTransform: () => createSubsetTransform, createSubtract: () => createSubtract, createSubtractScalar: () => createSubtractScalar, createSum: () => createSum, createSumTransform: () => createSumTransform, createSylvester: () => createSylvester, createSymbolNode: () => createSymbolNode, createSymbolicEqual: () => createSymbolicEqual, createTan: () => createTan, createTanh: () => createTanh, createTau: () => createTau, createThomsonCrossSection: () => createThomsonCrossSection, createTo: () => createTo, createToBest: () => createToBest, createTrace: () => createTrace, createTranspose: () => createTranspose, createTrue: () => createTrue, createTypeOf: () => createTypeOf, createTyped: () => createTyped, createUnaryMinus: () => createUnaryMinus, createUnaryPlus: () => createUnaryPlus, createUnequal: () => createUnequal, createUnitClass: () => createUnitClass, createUnitFunction: () => createUnitFunction, createUppercaseE: () => createUppercaseE, createUppercasePi: () => createUppercasePi, createUsolve: () => createUsolve, createUsolveAll: () => createUsolveAll, createVacuumImpedance: () => createVacuumImpedance, createVariance: () => createVariance, createVarianceTransform: () => createVarianceTransform, createVersion: () => createVersion, createWeakMixingAngle: () => createWeakMixingAngle, createWienDisplacement: () => createWienDisplacement, createXgcd: () => createXgcd, createXor: () => createXor, createZeros: () => createZeros, createZeta: () => createZeta, createZpk2tf: () => createZpk2tf }), all = factoriesAny_exports, require_tiny_emitter = __commonJSMin(((t4, n2) => {
  function r2() {
  }
  r2.prototype = { on: function(t5, n3, r3) {
    var i2 = this.e ||= {};
    return (i2[t5] || (i2[t5] = [])).push({ fn: n3, ctx: r3 }), this;
  }, once: function(t5, n3, r3) {
    var i2 = this;
    function a2() {
      i2.off(t5, a2), n3.apply(r3, arguments);
    }
    return a2._ = n3, this.on(t5, a2, r3);
  }, emit: function(t5) {
    for (var n3 = [].slice.call(arguments, 1), r3 = ((this.e ||= {})[t5] || []).slice(), i2 = 0, a2 = r3.length; i2 < a2; i2++) r3[i2].fn.apply(r3[i2].ctx, n3);
    return this;
  }, off: function(t5, n3) {
    var r3 = this.e ||= {}, i2 = r3[t5], a2 = [];
    if (i2 && n3) for (var o2 = 0, s2 = i2.length; o2 < s2; o2++) i2[o2].fn !== n3 && i2[o2].fn._ !== n3 && a2.push(i2[o2]);
    return a2.length ? r3[t5] = a2 : delete r3[t5], this;
  } }, n2.exports = r2, n2.exports.TinyEmitter = r2;
})), import_tiny_emitter = __toESM(require_tiny_emitter());
function mixin$1(t4) {
  var n2 = new import_tiny_emitter.default();
  return t4.on = n2.on.bind(n2), t4.off = n2.off.bind(n2), t4.once = n2.once.bind(n2), t4.emit = n2.emit.bind(n2), t4;
}
function importFactory(t4, n2, r2, i2) {
  function a2(t5, n3) {
    var r3 = arguments.length;
    if (r3 !== 1 && r3 !== 2) throw new ArgumentsError(`import`, r3, 1, 2);
    n3 ||= {};
    function i3(t6, r4, a4) {
      if (Array.isArray(r4)) r4.forEach((n4) => i3(t6, n4));
      else if (isObject$1(r4) || p2(r4)) for (var o3 in r4) hasOwnProperty$1(r4, o3) && i3(t6, r4[o3], o3);
      else if (isFactory(r4) || a4 !== void 0) {
        var s4 = isFactory(r4) ? v2(r4) ? r4.fn + `.transform` : r4.fn : a4;
        if (hasOwnProperty$1(t6, s4) && t6[s4] !== r4 && !n3.silent) throw Error(`Cannot import "` + s4 + `" twice`);
        t6[s4] = r4;
      } else if (!n3.silent) throw TypeError(`Factory, Object, or Array expected`);
    }
    var a3 = {};
    for (var s3 in i3(a3, t5), a3) if (hasOwnProperty$1(a3, s3)) {
      var c3 = a3[s3];
      if (isFactory(c3)) u2(c3, n3);
      else if (d2(c3)) o2(s3, c3, n3);
      else if (!n3.silent) throw TypeError(`Factory, Object, or Array expected`);
    }
  }
  function o2(n3, a3, o3) {
    if (o3.wrap && typeof a3 == `function` && (a3 = l2(a3)), m2(a3) && (a3 = t4(n3, { [a3.signature]: a3 })), t4.isTypedFunction(r2[n3]) && t4.isTypedFunction(a3)) {
      a3 = o3.override ? t4(n3, a3.signatures) : t4(r2[n3], a3), r2[n3] = a3, delete i2[n3], s2(n3, a3), r2.emit(`import`, n3, function() {
        return a3;
      });
      return;
    }
    var u3 = r2[n3] !== void 0, d3 = r2.Unit?.isValuelessUnit(n3);
    if (!u3 && !d3 || o3.override) {
      r2[n3] = a3, delete i2[n3], s2(n3, a3), r2.emit(`import`, n3, function() {
        return a3;
      });
      return;
    }
    if (!o3.silent) throw Error(`Cannot import "` + n3 + `": already exists`);
  }
  function s2(t5, n3) {
    n3 && typeof n3.transform == `function` ? (r2.expression.transform[t5] = n3.transform, h2(t5) && (r2.expression.mathWithTransform[t5] = n3.transform)) : (delete r2.expression.transform[t5], h2(t5) && (r2.expression.mathWithTransform[t5] = n3));
  }
  function c2(t5) {
    delete r2.expression.transform[t5], h2(t5) ? r2.expression.mathWithTransform[t5] = r2[t5] : delete r2.expression.mathWithTransform[t5];
  }
  function l2(t5) {
    var n3 = function() {
      for (var n4 = [], i3 = 0, a3 = arguments.length; i3 < a3; i3++) {
        var o3 = arguments[i3];
        n4[i3] = o3 && o3.valueOf();
      }
      return t5.apply(r2, n4);
    };
    return t5.transform && (n3.transform = t5.transform), n3;
  }
  function u2(n3, a3) {
    var l3 = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : n3.fn;
    if (l3.includes(`.`)) throw Error(`Factory name should not contain a nested path. Name: ` + JSON.stringify(l3));
    var u3 = v2(n3) ? r2.expression.transform : r2, d3 = l3 in r2.expression.transform, p3 = hasOwnProperty$1(u3, l3) ? u3[l3] : void 0, m3 = function() {
      var i3 = {};
      n3.dependencies.map(stripOptionalNotation).forEach((t5) => {
        if (t5.includes(`.`)) throw Error(`Factory dependency should not contain a nested path. Name: ` + JSON.stringify(t5));
        t5 === `math` ? i3.math = r2 : t5 === `mathWithTransform` ? i3.mathWithTransform = r2.expression.mathWithTransform : t5 === `classes` ? i3.classes = r2 : i3[t5] = r2[t5];
      });
      var o3 = n3(i3);
      if (o3 && typeof o3.transform == `function`) throw Error(`Transforms cannot be attached to factory functions. Please create a separate function for it with export const path = "expression.transform"`);
      if (p3 === void 0 || a3.override) return o3;
      if (t4.isTypedFunction(p3) && t4.isTypedFunction(o3)) return t4(p3, o3);
      if (a3.silent) return p3;
      throw Error(`Cannot import "` + l3 + `": already exists`);
    }, h3 = n3.meta?.formerly ?? ``, y3 = v2(n3) || g2(n3), b2 = r2.expression.mathWithTransform;
    !n3.meta || n3.meta.lazy !== false ? (lazy(u3, l3, m3), h3 && lazy(u3, h3, m3), p3 && d3 ? (c2(l3), h3 && c2(h3)) : y3 && (lazy(b2, l3, () => u3[l3]), h3 && lazy(b2, h3, () => u3[l3]))) : (u3[l3] = m3(), h3 && (u3[h3] = u3[l3]), p3 && d3 ? (c2(l3), h3 && c2(h3)) : y3 && (lazy(b2, l3, () => u3[l3]), h3 && lazy(b2, h3, () => u3[l3]))), i2[l3] = n3, r2.emit(`import`, l3, m3);
  }
  function d2(t5) {
    return typeof t5 == `function` || typeof t5 == `number` || typeof t5 == `string` || typeof t5 == `boolean` || t5 === null || isUnit(t5) || isComplex(t5) || isBigNumber(t5) || isFraction(t5) || isMatrix(t5) || Array.isArray(t5);
  }
  function p2(t5) {
    return typeof t5 == `object` && t5[Symbol.toStringTag] === `Module`;
  }
  function m2(t5) {
    return typeof t5 == `function` && typeof t5.signature == `string`;
  }
  function h2(t5) {
    return !hasOwnProperty$1(y2, t5);
  }
  function g2(t5) {
    return !t5.fn.includes(`.`) && !hasOwnProperty$1(y2, t5.fn) && (!t5.meta || !t5.meta.isClass);
  }
  function v2(t5) {
    return t5 !== void 0 && t5.meta !== void 0 && t5.meta.isTransformFunction === true || false;
  }
  var y2 = { expression: true, type: true, docs: true, error: true, json: true, chain: true };
  return a2;
}
function create$1(t4, n2) {
  var r2 = _extends({}, DEFAULT_CONFIG, n2);
  if (typeof Object.create != `function`) throw Error(`ES5 not supported by this JavaScript engine. Please load the es5-shim and es5-sham library for compatibility.`);
  var i2 = mixin$1({ isNumber, isComplex, isBigNumber, isBigInt, isFraction, isUnit, isString, isArray, isMatrix, isCollection, isDenseMatrix, isSparseMatrix, isRange, isIndex, isBoolean: isBoolean$1, isResultSet, isHelp, isFunction: isFunction$1, isDate, isRegExp, isObject: isObject$1, isMap, isPartitionedMap, isObjectWrappingMap, isNull: isNull$1, isUndefined: isUndefined$1, isAccessorNode, isArrayNode, isAssignmentNode, isBlockNode, isConditionalNode, isConstantNode, isFunctionAssignmentNode, isFunctionNode, isIndexNode, isNode, isObjectNode, isOperatorNode, isParenthesisNode, isRangeNode, isRelationalNode, isSymbolNode, isChain });
  i2.config = configFactory(r2, i2.emit), i2.expression = { transform: {}, mathWithTransform: { config: i2.config } };
  var a2 = [], o2 = [];
  function s2(t5) {
    if (isFactory(t5)) return t5(i2);
    var n3 = t5[Object.keys(t5)[0]];
    if (isFactory(n3)) return n3(i2);
    if (!isLegacyFactory(t5)) throw console.warn("Factory object with properties `type`, `name`, and `factory` expected", t5), Error("Factory object with properties `type`, `name`, and `factory` expected");
    var c3 = a2.indexOf(t5), l3;
    return c3 === -1 ? (l3 = t5.math === true ? t5.factory(i2.type, r2, s2, i2.typed, i2) : t5.factory(i2.type, r2, s2, i2.typed), a2.push(t5), o2.push(l3)) : l3 = o2[c3], l3;
  }
  var c2 = {};
  function l2() {
    var t5 = [...arguments];
    return i2.typed.apply(i2.typed, t5);
  }
  l2.isTypedFunction = import_typed_function.default.isTypedFunction;
  var u2 = importFactory(l2, s2, i2, c2);
  return i2.import = u2, i2.on(`config`, () => {
    Object.values(c2).forEach((t5) => {
      t5 && t5.meta && t5.meta.recreateOnConfigChange && u2(t5, { override: true });
    });
  }), i2.create = create$1.bind(null, t4), i2.factory = factory, i2.import(Object.values(deepFlatten(t4))), i2.ArgumentsError = ArgumentsError, i2.DimensionError = DimensionError, i2.IndexError = IndexError, i2;
}
var require_numeric_1_2_6 = __commonJSMin(((exports) => {
  var numeric = exports === void 0 ? (function() {
  }) : exports;
  typeof global < `u` && (global.numeric = numeric), numeric.version = `1.2.6`, numeric.bench = function(t4, n2) {
    var r2, i2, a2, o2;
    for (n2 === void 0 && (n2 = 15), a2 = 0.5, r2 = /* @__PURE__ */ new Date(); ; ) {
      for (a2 *= 2, o2 = a2; o2 > 3; o2 -= 4) t4(), t4(), t4(), t4();
      for (; o2 > 0; ) t4(), o2--;
      if (i2 = /* @__PURE__ */ new Date(), i2 - r2 > n2) break;
    }
    for (o2 = a2; o2 > 3; o2 -= 4) t4(), t4(), t4(), t4();
    for (; o2 > 0; ) t4(), o2--;
    return i2 = /* @__PURE__ */ new Date(), 1e3 * (3 * a2 - 1) / (i2 - r2);
  }, numeric._myIndexOf = (function(t4) {
    var n2 = this.length, r2;
    for (r2 = 0; r2 < n2; ++r2) if (this[r2] === t4) return r2;
    return -1;
  }), numeric.myIndexOf = Array.prototype.indexOf ? Array.prototype.indexOf : numeric._myIndexOf, numeric.Function = Function, numeric.precision = 4, numeric.largeArray = 50, numeric.prettyPrint = function(t4) {
    function n2(t5) {
      if (t5 === 0) return `0`;
      if (isNaN(t5)) return `NaN`;
      if (t5 < 0) return `-` + n2(-t5);
      if (isFinite(t5)) {
        var r3 = Math.floor(Math.log(t5) / Math.log(10)), i3 = t5 / 10 ** r3, a2 = i3.toPrecision(numeric.precision);
        return parseFloat(a2) === 10 && (r3++, i3 = 1, a2 = i3.toPrecision(numeric.precision)), parseFloat(a2).toString() + `e` + r3.toString();
      }
      return `Infinity`;
    }
    var r2 = [];
    function i2(t5) {
      var a2;
      if (t5 === void 0) return r2.push(Array(numeric.precision + 8).join(` `)), false;
      if (typeof t5 == `string`) return r2.push(`"` + t5 + `"`), false;
      if (typeof t5 == `boolean`) return r2.push(t5.toString()), false;
      if (typeof t5 == `number`) {
        var o2 = n2(t5), s2 = t5.toPrecision(numeric.precision), c2 = parseFloat(t5.toString()).toString(), l2 = [o2, s2, c2, parseFloat(s2).toString(), parseFloat(c2).toString()];
        for (a2 = 1; a2 < l2.length; a2++) l2[a2].length < o2.length && (o2 = l2[a2]);
        return r2.push(Array(numeric.precision + 8 - o2.length).join(` `) + o2), false;
      }
      if (t5 === null) return r2.push(`null`), false;
      if (typeof t5 == `function`) {
        r2.push(t5.toString());
        var u2 = false;
        for (a2 in t5) t5.hasOwnProperty(a2) && (u2 ? r2.push(`,
`) : r2.push(`
{`), u2 = true, r2.push(a2), r2.push(`: 
`), i2(t5[a2]));
        return u2 && r2.push(`}
`), true;
      }
      if (t5 instanceof Array) {
        if (t5.length > numeric.largeArray) return r2.push(`...Large Array...`), true;
        var u2 = false;
        for (r2.push(`[`), a2 = 0; a2 < t5.length; a2++) a2 > 0 && (r2.push(`,`), u2 && r2.push(`
 `)), u2 = i2(t5[a2]);
        return r2.push(`]`), true;
      }
      r2.push(`{`);
      var u2 = false;
      for (a2 in t5) t5.hasOwnProperty(a2) && (u2 && r2.push(`,
`), u2 = true, r2.push(a2), r2.push(`: 
`), i2(t5[a2]));
      return r2.push(`}`), true;
    }
    return i2(t4), r2.join(``);
  }, numeric.parseDate = function(t4) {
    function n2(t5) {
      if (typeof t5 == `string`) return Date.parse(t5.replace(/-/g, `/`));
      if (!(t5 instanceof Array)) throw Error(`parseDate: parameter must be arrays of strings`);
      var r2 = [], i2;
      for (i2 = 0; i2 < t5.length; i2++) r2[i2] = n2(t5[i2]);
      return r2;
    }
    return n2(t4);
  }, numeric.parseFloat = function(t4) {
    function n2(t5) {
      if (typeof t5 == `string`) return parseFloat(t5);
      if (!(t5 instanceof Array)) throw Error(`parseFloat: parameter must be arrays of strings`);
      var r2 = [], i2;
      for (i2 = 0; i2 < t5.length; i2++) r2[i2] = n2(t5[i2]);
      return r2;
    }
    return n2(t4);
  }, numeric.parseCSV = function(t4) {
    var n2 = t4.split(`
`), r2, i2, a2 = [], o2 = /(([^'",]*)|('[^']*')|("[^"]*")),/g, s2 = /^\s*(([+-]?[0-9]+(\.[0-9]*)?(e[+-]?[0-9]+)?)|([+-]?[0-9]*(\.[0-9]+)?(e[+-]?[0-9]+)?))\s*$/, c2 = function(t5) {
      return t5.substr(0, t5.length - 1);
    }, l2 = 0;
    for (i2 = 0; i2 < n2.length; i2++) {
      var u2 = (n2[i2] + `,`).match(o2), d2;
      if (u2.length > 0) {
        for (a2[l2] = [], r2 = 0; r2 < u2.length; r2++) d2 = c2(u2[r2]), s2.test(d2) ? a2[l2][r2] = parseFloat(d2) : a2[l2][r2] = d2;
        l2++;
      }
    }
    return a2;
  }, numeric.toCSV = function(t4) {
    var n2 = numeric.dim(t4), r2, i2, a2 = n2[0], s2, c2;
    for (n2[1], c2 = [], r2 = 0; r2 < a2; r2++) {
      for (s2 = [], i2 = 0; i2 < a2; i2++) s2[i2] = t4[r2][i2].toString();
      c2[r2] = s2.join(`, `);
    }
    return c2.join(`
`) + `
`;
  }, numeric.getURL = function(t4) {
    var n2 = new XMLHttpRequest();
    return n2.open(`GET`, t4, false), n2.send(), n2;
  }, numeric.imageURL = function(t4) {
    function n2(t5) {
      var n3 = t5.length, r3, i3, a3, o3, s3, c2, l3, u3, d3 = `ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=`, p3 = ``;
      for (r3 = 0; r3 < n3; r3 += 3) i3 = t5[r3], a3 = t5[r3 + 1], o3 = t5[r3 + 2], s3 = i3 >> 2, c2 = ((i3 & 3) << 4) + (a3 >> 4), l3 = ((a3 & 15) << 2) + (o3 >> 6), u3 = o3 & 63, r3 + 1 >= n3 ? l3 = u3 = 64 : r3 + 2 >= n3 && (u3 = 64), p3 += d3.charAt(s3) + d3.charAt(c2) + d3.charAt(l3) + d3.charAt(u3);
      return p3;
    }
    function r2(t5, n3, r3) {
      n3 === void 0 && (n3 = 0), r3 === void 0 && (r3 = t5.length);
      var i3 = [0, 1996959894, 3993919788, 2567524794, 124634137, 1886057615, 3915621685, 2657392035, 249268274, 2044508324, 3772115230, 2547177864, 162941995, 2125561021, 3887607047, 2428444049, 498536548, 1789927666, 4089016648, 2227061214, 450548861, 1843258603, 4107580753, 2211677639, 325883990, 1684777152, 4251122042, 2321926636, 335633487, 1661365465, 4195302755, 2366115317, 997073096, 1281953886, 3579855332, 2724688242, 1006888145, 1258607687, 3524101629, 2768942443, 901097722, 1119000684, 3686517206, 2898065728, 853044451, 1172266101, 3705015759, 2882616665, 651767980, 1373503546, 3369554304, 3218104598, 565507253, 1454621731, 3485111705, 3099436303, 671266974, 1594198024, 3322730930, 2970347812, 795835527, 1483230225, 3244367275, 3060149565, 1994146192, 31158534, 2563907772, 4023717930, 1907459465, 112637215, 2680153253, 3904427059, 2013776290, 251722036, 2517215374, 3775830040, 2137656763, 141376813, 2439277719, 3865271297, 1802195444, 476864866, 2238001368, 4066508878, 1812370925, 453092731, 2181625025, 4111451223, 1706088902, 314042704, 2344532202, 4240017532, 1658658271, 366619977, 2362670323, 4224994405, 1303535960, 984961486, 2747007092, 3569037538, 1256170817, 1037604311, 2765210733, 3554079995, 1131014506, 879679996, 2909243462, 3663771856, 1141124467, 855842277, 2852801631, 3708648649, 1342533948, 654459306, 3188396048, 3373015174, 1466479909, 544179635, 3110523913, 3462522015, 1591671054, 702138776, 2966460450, 3352799412, 1504918807, 783551873, 3082640443, 3233442989, 3988292384, 2596254646, 62317068, 1957810842, 3939845945, 2647816111, 81470997, 1943803523, 3814918930, 2489596804, 225274430, 2053790376, 3826175755, 2466906013, 167816743, 2097651377, 4027552580, 2265490386, 503444072, 1762050814, 4150417245, 2154129355, 426522225, 1852507879, 4275313526, 2312317920, 282753626, 1742555852, 4189708143, 2394877945, 397917763, 1622183637, 3604390888, 2714866558, 953729732, 1340076626, 3518719985, 2797360999, 1068828381, 1219638859, 3624741850, 2936675148, 906185462, 1090812512, 3747672003, 2825379669, 829329135, 1181335161, 3412177804, 3160834842, 628085408, 1382605366, 3423369109, 3138078467, 570562233, 1426400815, 3317316542, 2998733608, 733239954, 1555261956, 3268935591, 3050360625, 752459403, 1541320221, 2607071920, 3965973030, 1969922972, 40735498, 2617837225, 3943577151, 1913087877, 83908371, 2512341634, 3803740692, 2075208622, 213261112, 2463272603, 3855990285, 2094854071, 198958881, 2262029012, 4057260610, 1759359992, 534414190, 2176718541, 4139329115, 1873836001, 414664567, 2282248934, 4279200368, 1711684554, 285281116, 2405801727, 4167216745, 1634467795, 376229701, 2685067896, 3608007406, 1308918612, 956543938, 2808555105, 3495958263, 1231636301, 1047427035, 2932959818, 3654703836, 1088359270, 936918e3, 2847714899, 3736837829, 1202900863, 817233897, 3183342108, 3401237130, 1404277552, 615818150, 3134207493, 3453421203, 1423857449, 601450431, 3009837614, 3294710456, 1567103746, 711928724, 3020668471, 3272380065, 1510334235, 755167117], a3 = -1, o3 = 0;
      t5.length;
      var c2;
      for (c2 = n3; c2 < r3; c2++) o3 = (a3 ^ t5[c2]) & 255, a3 = a3 >>> 8 ^ i3[o3];
      return a3 ^ -1;
    }
    var i2 = t4[0].length, a2 = t4[0][0].length, o2, s2, l2, u2, d2, p2, m2, h2, g2, v2, y2 = [137, 80, 78, 71, 13, 10, 26, 10, 0, 0, 0, 13, 73, 72, 68, 82, a2 >> 24 & 255, a2 >> 16 & 255, a2 >> 8 & 255, a2 & 255, i2 >> 24 & 255, i2 >> 16 & 255, i2 >> 8 & 255, i2 & 255, 8, 2, 0, 0, 0, -1, -2, -3, -4, -5, -6, -7, -8, 73, 68, 65, 84, 8, 29];
    for (v2 = r2(y2, 12, 29), y2[29] = v2 >> 24 & 255, y2[30] = v2 >> 16 & 255, y2[31] = v2 >> 8 & 255, y2[32] = v2 & 255, o2 = 1, s2 = 0, m2 = 0; m2 < i2; m2++) {
      for (m2 < i2 - 1 ? y2.push(0) : y2.push(1), d2 = 3 * a2 + 1 + (m2 === 0) & 255, p2 = 3 * a2 + 1 + (m2 === 0) >> 8 & 255, y2.push(d2), y2.push(p2), y2.push(~d2 & 255), y2.push(~p2 & 255), m2 === 0 && y2.push(0), h2 = 0; h2 < a2; h2++) for (l2 = 0; l2 < 3; l2++) d2 = t4[l2][m2][h2], d2 = d2 > 255 ? 255 : d2 < 0 ? 0 : Math.round(d2), o2 = (o2 + d2) % 65521, s2 = (s2 + o2) % 65521, y2.push(d2);
      y2.push(0);
    }
    return g2 = (s2 << 16) + o2, y2.push(g2 >> 24 & 255), y2.push(g2 >> 16 & 255), y2.push(g2 >> 8 & 255), y2.push(g2 & 255), u2 = y2.length - 41, y2[33] = u2 >> 24 & 255, y2[34] = u2 >> 16 & 255, y2[35] = u2 >> 8 & 255, y2[36] = u2 & 255, v2 = r2(y2, 37), y2.push(v2 >> 24 & 255), y2.push(v2 >> 16 & 255), y2.push(v2 >> 8 & 255), y2.push(v2 & 255), y2.push(0), y2.push(0), y2.push(0), y2.push(0), y2.push(73), y2.push(69), y2.push(78), y2.push(68), y2.push(174), y2.push(66), y2.push(96), y2.push(130), `data:image/png;base64,` + n2(y2);
  }, numeric._dim = function(t4) {
    for (var n2 = []; typeof t4 == `object`; ) n2.push(t4.length), t4 = t4[0];
    return n2;
  }, numeric.dim = function(t4) {
    var n2, r2;
    return typeof t4 == `object` ? (n2 = t4[0], typeof n2 == `object` ? (r2 = n2[0], typeof r2 == `object` ? numeric._dim(t4) : [t4.length, n2.length]) : [t4.length]) : [];
  }, numeric.mapreduce = function(t4, n2) {
    return Function(`x`, `accum`, `_s`, `_k`, `if(typeof accum === "undefined") accum = ` + n2 + `;
if(typeof x === "number") { var xi = x; ` + t4 + `; return accum; }
if(typeof _s === "undefined") _s = numeric.dim(x);
if(typeof _k === "undefined") _k = 0;
var _n = _s[_k];
var i,xi;
if(_k < _s.length-1) {
    for(i=_n-1;i>=0;i--) {
        accum = arguments.callee(x[i],accum,_s,_k+1);
    }    return accum;
}
for(i=_n-1;i>=1;i-=2) { 
    xi = x[i];
    ` + t4 + `;
    xi = x[i-1];
    ` + t4 + `;
}
if(i === 0) {
    xi = x[i];
    ` + t4 + `
}
return accum;`);
  }, numeric.mapreduce2 = function(t4, n2) {
    return Function(`x`, `var n = x.length;
var i,xi;
` + n2 + `;
for(i=n-1;i!==-1;--i) { 
    xi = x[i];
    ` + t4 + `;
}
return accum;`);
  }, numeric.same = function t4(n2, r2) {
    var i2, a2;
    if (!(n2 instanceof Array) || !(r2 instanceof Array) || (a2 = n2.length, a2 !== r2.length)) return false;
    for (i2 = 0; i2 < a2; i2++) if (n2[i2] !== r2[i2]) if (typeof n2[i2] == `object`) {
      if (!t4(n2[i2], r2[i2])) return false;
    } else return false;
    return true;
  }, numeric.rep = function(t4, n2, r2) {
    r2 === void 0 && (r2 = 0);
    var i2 = t4[r2], a2 = Array(i2), o2;
    if (r2 === t4.length - 1) {
      for (o2 = i2 - 2; o2 >= 0; o2 -= 2) a2[o2 + 1] = n2, a2[o2] = n2;
      return o2 === -1 && (a2[0] = n2), a2;
    }
    for (o2 = i2 - 1; o2 >= 0; o2--) a2[o2] = numeric.rep(t4, n2, r2 + 1);
    return a2;
  }, numeric.dotMMsmall = function(t4, n2) {
    var r2, i2, a2, o2 = t4.length, s2 = n2.length, c2 = n2[0].length, l2 = Array(o2), u2, d2, p2, m2;
    for (r2 = o2 - 1; r2 >= 0; r2--) {
      for (u2 = Array(c2), d2 = t4[r2], a2 = c2 - 1; a2 >= 0; a2--) {
        for (p2 = d2[s2 - 1] * n2[s2 - 1][a2], i2 = s2 - 2; i2 >= 1; i2 -= 2) m2 = i2 - 1, p2 += d2[i2] * n2[i2][a2] + d2[m2] * n2[m2][a2];
        i2 === 0 && (p2 += d2[0] * n2[0][a2]), u2[a2] = p2;
      }
      l2[r2] = u2;
    }
    return l2;
  }, numeric._getCol = function(t4, n2, r2) {
    var i2 = t4.length, a2;
    for (a2 = i2 - 1; a2 > 0; --a2) r2[a2] = t4[a2][n2], --a2, r2[a2] = t4[a2][n2];
    a2 === 0 && (r2[0] = t4[0][n2]);
  }, numeric.dotMMbig = function(t4, n2) {
    var r2 = numeric._getCol, i2 = n2.length, a2 = Array(i2), o2 = t4.length, s2 = n2[0].length, c2 = Array(o2), l2, u2 = numeric.dotVV, d2, p2;
    for (--i2, --o2, d2 = o2; d2 !== -1; --d2) c2[d2] = Array(s2);
    for (--s2, d2 = s2; d2 !== -1; --d2) for (r2(n2, d2, a2), p2 = o2; p2 !== -1; --p2) l2 = t4[p2], c2[p2][d2] = u2(l2, a2);
    return c2;
  }, numeric.dotMV = function(t4, n2) {
    var r2 = t4.length;
    n2.length;
    var a2, o2 = Array(r2), s2 = numeric.dotVV;
    for (a2 = r2 - 1; a2 >= 0; a2--) o2[a2] = s2(t4[a2], n2);
    return o2;
  }, numeric.dotVM = function(t4, n2) {
    var i2, a2, o2 = t4.length, s2 = n2[0].length, l2 = Array(s2), p2, m2;
    for (a2 = s2 - 1; a2 >= 0; a2--) {
      for (p2 = t4[o2 - 1] * n2[o2 - 1][a2], i2 = o2 - 2; i2 >= 1; i2 -= 2) m2 = i2 - 1, p2 += t4[i2] * n2[i2][a2] + t4[m2] * n2[m2][a2];
      i2 === 0 && (p2 += t4[0] * n2[0][a2]), l2[a2] = p2;
    }
    return l2;
  }, numeric.dotVV = function(t4, n2) {
    var r2, i2 = t4.length, a2, o2 = t4[i2 - 1] * n2[i2 - 1];
    for (r2 = i2 - 2; r2 >= 1; r2 -= 2) a2 = r2 - 1, o2 += t4[r2] * n2[r2] + t4[a2] * n2[a2];
    return r2 === 0 && (o2 += t4[0] * n2[0]), o2;
  }, numeric.dot = function(t4, n2) {
    var r2 = numeric.dim;
    switch (r2(t4).length * 1e3 + r2(n2).length) {
      case 2002:
        return n2.length < 10 ? numeric.dotMMsmall(t4, n2) : numeric.dotMMbig(t4, n2);
      case 2001:
        return numeric.dotMV(t4, n2);
      case 1002:
        return numeric.dotVM(t4, n2);
      case 1001:
        return numeric.dotVV(t4, n2);
      case 1e3:
        return numeric.mulVS(t4, n2);
      case 1:
        return numeric.mulSV(t4, n2);
      case 0:
        return t4 * n2;
      default:
        throw Error(`numeric.dot only works on vectors and matrices`);
    }
  }, numeric.diag = function(t4) {
    var n2, r2, i2, a2 = t4.length, o2 = Array(a2), s2;
    for (n2 = a2 - 1; n2 >= 0; n2--) {
      for (s2 = Array(a2), r2 = n2 + 2, i2 = a2 - 1; i2 >= r2; i2 -= 2) s2[i2] = 0, s2[i2 - 1] = 0;
      for (i2 > n2 && (s2[i2] = 0), s2[n2] = t4[n2], i2 = n2 - 1; i2 >= 1; i2 -= 2) s2[i2] = 0, s2[i2 - 1] = 0;
      i2 === 0 && (s2[0] = 0), o2[n2] = s2;
    }
    return o2;
  }, numeric.getDiag = function(t4) {
    var n2 = Math.min(t4.length, t4[0].length), r2, i2 = Array(n2);
    for (r2 = n2 - 1; r2 >= 1; --r2) i2[r2] = t4[r2][r2], --r2, i2[r2] = t4[r2][r2];
    return r2 === 0 && (i2[0] = t4[0][0]), i2;
  }, numeric.identity = function(t4) {
    return numeric.diag(numeric.rep([t4], 1));
  }, numeric.pointwise = function(t4, n2, r2) {
    r2 === void 0 && (r2 = ``);
    var i2 = [], a2, o2 = /\[i\]$/, s2, c2 = ``, l2 = false;
    for (a2 = 0; a2 < t4.length; a2++) o2.test(t4[a2]) ? (s2 = t4[a2].substring(0, t4[a2].length - 3), c2 = s2) : s2 = t4[a2], s2 === `ret` && (l2 = true), i2.push(s2);
    return i2[t4.length] = `_s`, i2[t4.length + 1] = `_k`, i2[t4.length + 2] = `if(typeof _s === "undefined") _s = numeric.dim(` + c2 + `);
if(typeof _k === "undefined") _k = 0;
var _n = _s[_k];
var i` + (l2 ? `` : `, ret = Array(_n)`) + `;
if(_k < _s.length-1) {
    for(i=_n-1;i>=0;i--) ret[i] = arguments.callee(` + t4.join(`,`) + `,_s,_k+1);
    return ret;
}
` + r2 + `
for(i=_n-1;i!==-1;--i) {
    ` + n2 + `
}
return ret;`, Function.apply(null, i2);
  }, numeric.pointwise2 = function(t4, n2, r2) {
    r2 === void 0 && (r2 = ``);
    var i2 = [], a2, o2 = /\[i\]$/, s2, c2 = ``, l2 = false;
    for (a2 = 0; a2 < t4.length; a2++) o2.test(t4[a2]) ? (s2 = t4[a2].substring(0, t4[a2].length - 3), c2 = s2) : s2 = t4[a2], s2 === `ret` && (l2 = true), i2.push(s2);
    return i2[t4.length] = `var _n = ` + c2 + `.length;
var i` + (l2 ? `` : `, ret = Array(_n)`) + `;
` + r2 + `
for(i=_n-1;i!==-1;--i) {
` + n2 + `
}
return ret;`, Function.apply(null, i2);
  }, numeric._biforeach = (function t4(n2, r2, i2, a2, o2) {
    if (a2 === i2.length - 1) {
      o2(n2, r2);
      return;
    }
    var s2;
    for (s2 = i2[a2] - 1; s2 >= 0; s2--) t4(typeof n2 == `object` ? n2[s2] : n2, typeof r2 == `object` ? r2[s2] : r2, i2, a2 + 1, o2);
  }), numeric._biforeach2 = (function t4(n2, r2, i2, a2, o2) {
    if (a2 === i2.length - 1) return o2(n2, r2);
    var s2, c2 = i2[a2], l2 = Array(c2);
    for (s2 = c2 - 1; s2 >= 0; --s2) l2[s2] = t4(typeof n2 == `object` ? n2[s2] : n2, typeof r2 == `object` ? r2[s2] : r2, i2, a2 + 1, o2);
    return l2;
  }), numeric._foreach = (function t4(n2, r2, i2, a2) {
    if (i2 === r2.length - 1) {
      a2(n2);
      return;
    }
    var o2;
    for (o2 = r2[i2] - 1; o2 >= 0; o2--) t4(n2[o2], r2, i2 + 1, a2);
  }), numeric._foreach2 = (function t4(n2, r2, i2, a2) {
    if (i2 === r2.length - 1) return a2(n2);
    var o2, s2 = r2[i2], c2 = Array(s2);
    for (o2 = s2 - 1; o2 >= 0; o2--) c2[o2] = t4(n2[o2], r2, i2 + 1, a2);
    return c2;
  }), numeric.ops2 = { add: `+`, sub: `-`, mul: `*`, div: `/`, mod: `%`, and: `&&`, or: `||`, eq: `===`, neq: `!==`, lt: `<`, gt: `>`, leq: `<=`, geq: `>=`, band: `&`, bor: `|`, bxor: `^`, lshift: `<<`, rshift: `>>`, rrshift: `>>>` }, numeric.opseq = { addeq: `+=`, subeq: `-=`, muleq: `*=`, diveq: `/=`, modeq: `%=`, lshifteq: `<<=`, rshifteq: `>>=`, rrshifteq: `>>>=`, bandeq: `&=`, boreq: `|=`, bxoreq: `^=` }, numeric.mathfuns = [`abs`, `acos`, `asin`, `atan`, `ceil`, `cos`, `exp`, `floor`, `log`, `round`, `sin`, `sqrt`, `tan`, `isNaN`, `isFinite`], numeric.mathfuns2 = [`atan2`, `pow`, `max`, `min`], numeric.ops1 = { neg: `-`, not: `!`, bnot: `~`, clone: `` }, numeric.mapreducers = { any: [`if(xi) return true;`, `var accum = false;`], all: [`if(!xi) return false;`, `var accum = true;`], sum: [`accum += xi;`, `var accum = 0;`], prod: [`accum *= xi;`, `var accum = 1;`], norm2Squared: [`accum += xi*xi;`, `var accum = 0;`], norminf: [`accum = max(accum,abs(xi));`, `var accum = 0, max = Math.max, abs = Math.abs;`], norm1: [`accum += abs(xi)`, `var accum = 0, abs = Math.abs;`], sup: [`accum = max(accum,xi);`, `var accum = -Infinity, max = Math.max;`], inf: [`accum = min(accum,xi);`, `var accum = Infinity, min = Math.min;`] }, (function() {
    var t4, n2;
    for (t4 = 0; t4 < numeric.mathfuns2.length; ++t4) n2 = numeric.mathfuns2[t4], numeric.ops2[n2] = n2;
    for (t4 in numeric.ops2) if (numeric.ops2.hasOwnProperty(t4)) {
      n2 = numeric.ops2[t4];
      var r2, i2, a2 = ``;
      numeric.myIndexOf.call(numeric.mathfuns2, t4) === -1 ? (r2 = function(t5, r3, i3) {
        return t5 + ` = ` + r3 + ` ` + n2 + ` ` + i3;
      }, i2 = numeric.opseq.hasOwnProperty(t4 + `eq`) ? function(t5, r3) {
        return t5 + ` ` + n2 + `= ` + r3;
      } : function(t5, r3) {
        return t5 + ` = ` + t5 + ` ` + n2 + ` ` + r3;
      }) : (a2 = `var ` + n2 + ` = Math.` + n2 + `;
`, r2 = function(t5, r3, i3) {
        return t5 + ` = ` + n2 + `(` + r3 + `,` + i3 + `)`;
      }, i2 = function(t5, r3) {
        return t5 + ` = ` + n2 + `(` + t5 + `,` + r3 + `)`;
      }), numeric[t4 + `VV`] = numeric.pointwise2([`x[i]`, `y[i]`], r2(`ret[i]`, `x[i]`, `y[i]`), a2), numeric[t4 + `SV`] = numeric.pointwise2([`x`, `y[i]`], r2(`ret[i]`, `x`, `y[i]`), a2), numeric[t4 + `VS`] = numeric.pointwise2([`x[i]`, `y`], r2(`ret[i]`, `x[i]`, `y`), a2), numeric[t4] = Function(`var n = arguments.length, i, x = arguments[0], y;
var VV = numeric.` + t4 + `VV, VS = numeric.` + t4 + `VS, SV = numeric.` + t4 + `SV;
var dim = numeric.dim;
for(i=1;i!==n;++i) { 
  y = arguments[i];
  if(typeof x === "object") {
      if(typeof y === "object") x = numeric._biforeach2(x,y,dim(x),0,VV);
      else x = numeric._biforeach2(x,y,dim(x),0,VS);
  } else if(typeof y === "object") x = numeric._biforeach2(x,y,dim(y),0,SV);
  else ` + i2(`x`, `y`) + `
}
return x;
`), numeric[n2] = numeric[t4], numeric[t4 + `eqV`] = numeric.pointwise2([`ret[i]`, `x[i]`], i2(`ret[i]`, `x[i]`), a2), numeric[t4 + `eqS`] = numeric.pointwise2([`ret[i]`, `x`], i2(`ret[i]`, `x`), a2), numeric[t4 + `eq`] = Function(`var n = arguments.length, i, x = arguments[0], y;
var V = numeric.` + t4 + `eqV, S = numeric.` + t4 + `eqS
var s = numeric.dim(x);
for(i=1;i!==n;++i) { 
  y = arguments[i];
  if(typeof y === "object") numeric._biforeach(x,y,s,0,V);
  else numeric._biforeach(x,y,s,0,S);
}
return x;
`);
    }
    for (t4 = 0; t4 < numeric.mathfuns2.length; ++t4) n2 = numeric.mathfuns2[t4], delete numeric.ops2[n2];
    for (t4 = 0; t4 < numeric.mathfuns.length; ++t4) n2 = numeric.mathfuns[t4], numeric.ops1[n2] = n2;
    for (t4 in numeric.ops1) numeric.ops1.hasOwnProperty(t4) && (a2 = ``, n2 = numeric.ops1[t4], numeric.myIndexOf.call(numeric.mathfuns, t4) !== -1 && Math.hasOwnProperty(n2) && (a2 = `var ` + n2 + ` = Math.` + n2 + `;
`), numeric[t4 + `eqV`] = numeric.pointwise2([`ret[i]`], `ret[i] = ` + n2 + `(ret[i]);`, a2), numeric[t4 + `eq`] = Function(`x`, `if(typeof x !== "object") return ` + n2 + `x
var i;
var V = numeric.` + t4 + `eqV;
var s = numeric.dim(x);
numeric._foreach(x,s,0,V);
return x;
`), numeric[t4 + `V`] = numeric.pointwise2([`x[i]`], `ret[i] = ` + n2 + `(x[i]);`, a2), numeric[t4] = Function(`x`, `if(typeof x !== "object") return ` + n2 + `(x)
var i;
var V = numeric.` + t4 + `V;
var s = numeric.dim(x);
return numeric._foreach2(x,s,0,V);
`));
    for (t4 = 0; t4 < numeric.mathfuns.length; ++t4) n2 = numeric.mathfuns[t4], delete numeric.ops1[n2];
    for (t4 in numeric.mapreducers) numeric.mapreducers.hasOwnProperty(t4) && (n2 = numeric.mapreducers[t4], numeric[t4 + `V`] = numeric.mapreduce2(n2[0], n2[1]), numeric[t4] = Function(`x`, `s`, `k`, n2[1] + `if(typeof x !== "object") {    xi = x;
` + n2[0] + `;
    return accum;
}if(typeof s === "undefined") s = numeric.dim(x);
if(typeof k === "undefined") k = 0;
if(k === s.length-1) return numeric.` + t4 + `V(x);
var xi;
var n = x.length, i;
for(i=n-1;i!==-1;--i) {
   xi = arguments.callee(x[i]);
` + n2[0] + `;
}
return accum;
`));
  })(), numeric.truncVV = numeric.pointwise([`x[i]`, `y[i]`], `ret[i] = round(x[i]/y[i])*y[i];`, `var round = Math.round;`), numeric.truncVS = numeric.pointwise([`x[i]`, `y`], `ret[i] = round(x[i]/y)*y;`, `var round = Math.round;`), numeric.truncSV = numeric.pointwise([`x`, `y[i]`], `ret[i] = round(x/y[i])*y[i];`, `var round = Math.round;`), numeric.trunc = function(t4, n2) {
    return typeof t4 == `object` ? typeof n2 == `object` ? numeric.truncVV(t4, n2) : numeric.truncVS(t4, n2) : typeof n2 == `object` ? numeric.truncSV(t4, n2) : Math.round(t4 / n2) * n2;
  }, numeric.inv = function(t4) {
    var n2 = numeric.dim(t4), r2 = Math.abs, i2 = n2[0], a2 = n2[1], o2 = numeric.clone(t4), s2, c2, l2 = numeric.identity(i2), u2, d2, p2, m2, h2, t4;
    for (m2 = 0; m2 < a2; ++m2) {
      var g2 = -1, v2 = -1;
      for (p2 = m2; p2 !== i2; ++p2) h2 = r2(o2[p2][m2]), h2 > v2 && (g2 = p2, v2 = h2);
      for (c2 = o2[g2], o2[g2] = o2[m2], o2[m2] = c2, d2 = l2[g2], l2[g2] = l2[m2], l2[m2] = d2, t4 = c2[m2], h2 = m2; h2 !== a2; ++h2) c2[h2] /= t4;
      for (h2 = a2 - 1; h2 !== -1; --h2) d2[h2] /= t4;
      for (p2 = i2 - 1; p2 !== -1; --p2) if (p2 !== m2) {
        for (s2 = o2[p2], u2 = l2[p2], t4 = s2[m2], h2 = m2 + 1; h2 !== a2; ++h2) s2[h2] -= c2[h2] * t4;
        for (h2 = a2 - 1; h2 > 0; --h2) u2[h2] -= d2[h2] * t4, --h2, u2[h2] -= d2[h2] * t4;
        h2 === 0 && (u2[0] -= d2[0] * t4);
      }
    }
    return l2;
  }, numeric.det = function(t4) {
    var n2 = numeric.dim(t4);
    if (n2.length !== 2 || n2[0] !== n2[1]) throw Error(`numeric: det() only works on square matrices`);
    var r2 = n2[0], i2 = 1, a2, o2, s2, c2 = numeric.clone(t4), l2, u2, d2, p2, m2;
    for (o2 = 0; o2 < r2 - 1; o2++) {
      for (s2 = o2, a2 = o2 + 1; a2 < r2; a2++) Math.abs(c2[a2][o2]) > Math.abs(c2[s2][o2]) && (s2 = a2);
      for (s2 !== o2 && (p2 = c2[s2], c2[s2] = c2[o2], c2[o2] = p2, i2 *= -1), l2 = c2[o2], a2 = o2 + 1; a2 < r2; a2++) {
        for (u2 = c2[a2], d2 = u2[o2] / l2[o2], s2 = o2 + 1; s2 < r2 - 1; s2 += 2) m2 = s2 + 1, u2[s2] -= l2[s2] * d2, u2[m2] -= l2[m2] * d2;
        s2 !== r2 && (u2[s2] -= l2[s2] * d2);
      }
      if (l2[o2] === 0) return 0;
      i2 *= l2[o2];
    }
    return i2 * c2[o2][o2];
  }, numeric.transpose = function(t4) {
    var n2, r2, i2 = t4.length, a2 = t4[0].length, o2 = Array(a2), s2, c2, l2;
    for (r2 = 0; r2 < a2; r2++) o2[r2] = Array(i2);
    for (n2 = i2 - 1; n2 >= 1; n2 -= 2) {
      for (c2 = t4[n2], s2 = t4[n2 - 1], r2 = a2 - 1; r2 >= 1; --r2) l2 = o2[r2], l2[n2] = c2[r2], l2[n2 - 1] = s2[r2], --r2, l2 = o2[r2], l2[n2] = c2[r2], l2[n2 - 1] = s2[r2];
      r2 === 0 && (l2 = o2[0], l2[n2] = c2[0], l2[n2 - 1] = s2[0]);
    }
    if (n2 === 0) {
      for (s2 = t4[0], r2 = a2 - 1; r2 >= 1; --r2) o2[r2][0] = s2[r2], --r2, o2[r2][0] = s2[r2];
      r2 === 0 && (o2[0][0] = s2[0]);
    }
    return o2;
  }, numeric.negtranspose = function(t4) {
    var n2, r2, i2 = t4.length, a2 = t4[0].length, o2 = Array(a2), s2, c2, l2;
    for (r2 = 0; r2 < a2; r2++) o2[r2] = Array(i2);
    for (n2 = i2 - 1; n2 >= 1; n2 -= 2) {
      for (c2 = t4[n2], s2 = t4[n2 - 1], r2 = a2 - 1; r2 >= 1; --r2) l2 = o2[r2], l2[n2] = -c2[r2], l2[n2 - 1] = -s2[r2], --r2, l2 = o2[r2], l2[n2] = -c2[r2], l2[n2 - 1] = -s2[r2];
      r2 === 0 && (l2 = o2[0], l2[n2] = -c2[0], l2[n2 - 1] = -s2[0]);
    }
    if (n2 === 0) {
      for (s2 = t4[0], r2 = a2 - 1; r2 >= 1; --r2) o2[r2][0] = -s2[r2], --r2, o2[r2][0] = -s2[r2];
      r2 === 0 && (o2[0][0] = -s2[0]);
    }
    return o2;
  }, numeric._random = function t4(n2, r2) {
    var i2, a2 = n2[r2], o2 = Array(a2), s2;
    if (r2 === n2.length - 1) {
      for (s2 = Math.random, i2 = a2 - 1; i2 >= 1; i2 -= 2) o2[i2] = s2(), o2[i2 - 1] = s2();
      return i2 === 0 && (o2[0] = s2()), o2;
    }
    for (i2 = a2 - 1; i2 >= 0; i2--) o2[i2] = t4(n2, r2 + 1);
    return o2;
  }, numeric.random = function(t4) {
    return numeric._random(t4, 0);
  }, numeric.norm2 = function(t4) {
    return Math.sqrt(numeric.norm2Squared(t4));
  }, numeric.linspace = function(t4, n2, r2) {
    if (r2 === void 0 && (r2 = Math.max(Math.round(n2 - t4) + 1, 1)), r2 < 2) return r2 === 1 ? [t4] : [];
    var i2, a2 = Array(r2);
    for (r2--, i2 = r2; i2 >= 0; i2--) a2[i2] = (i2 * n2 + (r2 - i2) * t4) / r2;
    return a2;
  }, numeric.getBlock = function(t4, n2, r2) {
    var i2 = numeric.dim(t4);
    function a2(t5, o2) {
      var s2, c2 = n2[o2], l2 = r2[o2] - c2, u2 = Array(l2);
      if (o2 === i2.length - 1) {
        for (s2 = l2; s2 >= 0; s2--) u2[s2] = t5[s2 + c2];
        return u2;
      }
      for (s2 = l2; s2 >= 0; s2--) u2[s2] = a2(t5[s2 + c2], o2 + 1);
      return u2;
    }
    return a2(t4, 0);
  }, numeric.setBlock = function(t4, n2, r2, i2) {
    var a2 = numeric.dim(t4);
    function o2(t5, i3, s2) {
      var c2, l2 = n2[s2], u2 = r2[s2] - l2;
      if (s2 === a2.length - 1) for (c2 = u2; c2 >= 0; c2--) t5[c2 + l2] = i3[c2];
      for (c2 = u2; c2 >= 0; c2--) o2(t5[c2 + l2], i3[c2], s2 + 1);
    }
    return o2(t4, i2, 0), t4;
  }, numeric.getRange = function(t4, n2, r2) {
    var i2 = n2.length, a2 = r2.length, o2, s2, c2 = Array(i2), l2, u2;
    for (o2 = i2 - 1; o2 !== -1; --o2) for (c2[o2] = Array(a2), l2 = c2[o2], u2 = t4[n2[o2]], s2 = a2 - 1; s2 !== -1; --s2) l2[s2] = u2[r2[s2]];
    return c2;
  }, numeric.blockMatrix = function(t4) {
    var n2 = numeric.dim(t4);
    if (n2.length < 4) return numeric.blockMatrix([t4]);
    var r2 = n2[0], i2 = n2[1], a2 = 0, o2 = 0, s2, c2, l2;
    for (s2 = 0; s2 < r2; ++s2) a2 += t4[s2][0].length;
    for (c2 = 0; c2 < i2; ++c2) o2 += t4[0][c2][0].length;
    var u2 = Array(a2);
    for (s2 = 0; s2 < a2; ++s2) u2[s2] = Array(o2);
    var d2 = 0, p2, m2, h2, g2, v2;
    for (s2 = 0; s2 < r2; ++s2) {
      for (p2 = o2, c2 = i2 - 1; c2 !== -1; --c2) for (l2 = t4[s2][c2], p2 -= l2[0].length, h2 = l2.length - 1; h2 !== -1; --h2) for (v2 = l2[h2], m2 = u2[d2 + h2], g2 = v2.length - 1; g2 !== -1; --g2) m2[p2 + g2] = v2[g2];
      d2 += t4[s2][0].length;
    }
    return u2;
  }, numeric.tensor = function(t4, n2) {
    if (typeof t4 == `number` || typeof n2 == `number`) return numeric.mul(t4, n2);
    var r2 = numeric.dim(t4), i2 = numeric.dim(n2);
    if (r2.length !== 1 || i2.length !== 1) throw Error(`numeric: tensor product is only defined for vectors`);
    var a2 = r2[0], o2 = i2[0], s2 = Array(a2), c2, l2, u2, d2;
    for (l2 = a2 - 1; l2 >= 0; l2--) {
      for (c2 = Array(o2), d2 = t4[l2], u2 = o2 - 1; u2 >= 3; --u2) c2[u2] = d2 * n2[u2], --u2, c2[u2] = d2 * n2[u2], --u2, c2[u2] = d2 * n2[u2], --u2, c2[u2] = d2 * n2[u2];
      for (; u2 >= 0; ) c2[u2] = d2 * n2[u2], --u2;
      s2[l2] = c2;
    }
    return s2;
  }, numeric.T = function(t4, n2) {
    this.x = t4, this.y = n2;
  }, numeric.t = function(t4, n2) {
    return new numeric.T(t4, n2);
  }, numeric.Tbinop = function(t4, n2, r2, i2, a2) {
    numeric.indexOf;
    if (typeof a2 != `string`) for (var s2 in a2 = ``, numeric) numeric.hasOwnProperty(s2) && (t4.indexOf(s2) >= 0 || n2.indexOf(s2) >= 0 || r2.indexOf(s2) >= 0 || i2.indexOf(s2) >= 0) && s2.length > 1 && (a2 += `var ` + s2 + ` = numeric.` + s2 + `;
`);
    return Function([`y`], `var x = this;
if(!(y instanceof numeric.T)) { y = new numeric.T(y); }
` + a2 + `
if(x.y) {  if(y.y) {    return new numeric.T(` + i2 + `);
  }
  return new numeric.T(` + r2 + `);
}
if(y.y) {
  return new numeric.T(` + n2 + `);
}
return new numeric.T(` + t4 + `);
`);
  }, numeric.T.prototype.add = numeric.Tbinop(`add(x.x,y.x)`, `add(x.x,y.x),y.y`, `add(x.x,y.x),x.y`, `add(x.x,y.x),add(x.y,y.y)`), numeric.T.prototype.sub = numeric.Tbinop(`sub(x.x,y.x)`, `sub(x.x,y.x),neg(y.y)`, `sub(x.x,y.x),x.y`, `sub(x.x,y.x),sub(x.y,y.y)`), numeric.T.prototype.mul = numeric.Tbinop(`mul(x.x,y.x)`, `mul(x.x,y.x),mul(x.x,y.y)`, `mul(x.x,y.x),mul(x.y,y.x)`, `sub(mul(x.x,y.x),mul(x.y,y.y)),add(mul(x.x,y.y),mul(x.y,y.x))`), numeric.T.prototype.reciprocal = function() {
    var t4 = numeric.mul, n2 = numeric.div;
    if (this.y) {
      var r2 = numeric.add(t4(this.x, this.x), t4(this.y, this.y));
      return new numeric.T(n2(this.x, r2), n2(numeric.neg(this.y), r2));
    }
    return new T(n2(1, this.x));
  }, numeric.T.prototype.div = function(t4) {
    if (t4 instanceof numeric.T || (t4 = new numeric.T(t4)), t4.y) return this.mul(t4.reciprocal());
    var n2 = numeric.div;
    return this.y ? new numeric.T(n2(this.x, t4.x), n2(this.y, t4.x)) : new numeric.T(n2(this.x, t4.x));
  }, numeric.T.prototype.dot = numeric.Tbinop(`dot(x.x,y.x)`, `dot(x.x,y.x),dot(x.x,y.y)`, `dot(x.x,y.x),dot(x.y,y.x)`, `sub(dot(x.x,y.x),dot(x.y,y.y)),add(dot(x.x,y.y),dot(x.y,y.x))`), numeric.T.prototype.transpose = function() {
    var t4 = numeric.transpose, n2 = this.x, r2 = this.y;
    return r2 ? new numeric.T(t4(n2), t4(r2)) : new numeric.T(t4(n2));
  }, numeric.T.prototype.transjugate = function() {
    var t4 = numeric.transpose, n2 = this.x, r2 = this.y;
    return r2 ? new numeric.T(t4(n2), numeric.negtranspose(r2)) : new numeric.T(t4(n2));
  }, numeric.Tunop = function(t4, n2, r2) {
    return typeof r2 != `string` && (r2 = ``), Function(`var x = this;
` + r2 + `
if(x.y) {  ` + n2 + `;
}
` + t4 + `;
`);
  }, numeric.T.prototype.exp = numeric.Tunop(`return new numeric.T(ex)`, `return new numeric.T(mul(cos(x.y),ex),mul(sin(x.y),ex))`, `var ex = numeric.exp(x.x), cos = numeric.cos, sin = numeric.sin, mul = numeric.mul;`), numeric.T.prototype.conj = numeric.Tunop(`return new numeric.T(x.x);`, `return new numeric.T(x.x,numeric.neg(x.y));`), numeric.T.prototype.neg = numeric.Tunop(`return new numeric.T(neg(x.x));`, `return new numeric.T(neg(x.x),neg(x.y));`, `var neg = numeric.neg;`), numeric.T.prototype.sin = numeric.Tunop(`return new numeric.T(numeric.sin(x.x))`, `return x.exp().sub(x.neg().exp()).div(new numeric.T(0,2));`), numeric.T.prototype.cos = numeric.Tunop(`return new numeric.T(numeric.cos(x.x))`, `return x.exp().add(x.neg().exp()).div(2);`), numeric.T.prototype.abs = numeric.Tunop(`return new numeric.T(numeric.abs(x.x));`, `return new numeric.T(numeric.sqrt(numeric.add(mul(x.x,x.x),mul(x.y,x.y))));`, `var mul = numeric.mul;`), numeric.T.prototype.log = numeric.Tunop(`return new numeric.T(numeric.log(x.x));`, `var theta = new numeric.T(numeric.atan2(x.y,x.x)), r = x.abs();
return new numeric.T(numeric.log(r.x),theta.x);`), numeric.T.prototype.norm2 = numeric.Tunop(`return numeric.norm2(x.x);`, `var f = numeric.norm2Squared;
return Math.sqrt(f(x.x)+f(x.y));`), numeric.T.prototype.inv = function() {
    var t4 = this;
    if (t4.y === void 0) return new numeric.T(numeric.inv(t4.x));
    var n2 = t4.x.length, r2, i2, a2, o2 = numeric.identity(n2), s2 = numeric.rep([n2, n2], 0), c2 = numeric.clone(t4.x), l2 = numeric.clone(t4.y), u2, d2, p2, m2, h2, g2, v2, y2, r2, i2, a2, b2, x2, S2, w2, E, D, O;
    for (r2 = 0; r2 < n2; r2++) {
      for (S2 = c2[r2][r2], w2 = l2[r2][r2], b2 = S2 * S2 + w2 * w2, a2 = r2, i2 = r2 + 1; i2 < n2; i2++) S2 = c2[i2][r2], w2 = l2[i2][r2], x2 = S2 * S2 + w2 * w2, x2 > b2 && (a2 = i2, b2 = x2);
      for (a2 !== r2 && (O = c2[r2], c2[r2] = c2[a2], c2[a2] = O, O = l2[r2], l2[r2] = l2[a2], l2[a2] = O, O = o2[r2], o2[r2] = o2[a2], o2[a2] = O, O = s2[r2], s2[r2] = s2[a2], s2[a2] = O), u2 = c2[r2], d2 = l2[r2], h2 = o2[r2], g2 = s2[r2], S2 = u2[r2], w2 = d2[r2], i2 = r2 + 1; i2 < n2; i2++) E = u2[i2], D = d2[i2], u2[i2] = (E * S2 + D * w2) / b2, d2[i2] = (D * S2 - E * w2) / b2;
      for (i2 = 0; i2 < n2; i2++) E = h2[i2], D = g2[i2], h2[i2] = (E * S2 + D * w2) / b2, g2[i2] = (D * S2 - E * w2) / b2;
      for (i2 = r2 + 1; i2 < n2; i2++) {
        for (p2 = c2[i2], m2 = l2[i2], v2 = o2[i2], y2 = s2[i2], S2 = p2[r2], w2 = m2[r2], a2 = r2 + 1; a2 < n2; a2++) E = u2[a2], D = d2[a2], p2[a2] -= E * S2 - D * w2, m2[a2] -= D * S2 + E * w2;
        for (a2 = 0; a2 < n2; a2++) E = h2[a2], D = g2[a2], v2[a2] -= E * S2 - D * w2, y2[a2] -= D * S2 + E * w2;
      }
    }
    for (r2 = n2 - 1; r2 > 0; r2--) for (h2 = o2[r2], g2 = s2[r2], i2 = r2 - 1; i2 >= 0; i2--) for (v2 = o2[i2], y2 = s2[i2], S2 = c2[i2][r2], w2 = l2[i2][r2], a2 = n2 - 1; a2 >= 0; a2--) E = h2[a2], D = g2[a2], v2[a2] -= S2 * E - w2 * D, y2[a2] -= S2 * D + w2 * E;
    return new numeric.T(o2, s2);
  }, numeric.T.prototype.get = function(t4) {
    var n2 = this.x, r2 = this.y, i2 = 0, a2, o2 = t4.length;
    if (r2) {
      for (; i2 < o2; ) a2 = t4[i2], n2 = n2[a2], r2 = r2[a2], i2++;
      return new numeric.T(n2, r2);
    }
    for (; i2 < o2; ) a2 = t4[i2], n2 = n2[a2], i2++;
    return new numeric.T(n2);
  }, numeric.T.prototype.set = function(t4, n2) {
    var r2 = this.x, i2 = this.y, a2 = 0, o2, s2 = t4.length, c2 = n2.x, l2 = n2.y;
    if (s2 === 0) return l2 ? this.y = l2 : i2 && (this.y = void 0), this.x = r2, this;
    if (l2) {
      for (i2 || (i2 = numeric.rep(numeric.dim(r2), 0), this.y = i2); a2 < s2 - 1; ) o2 = t4[a2], r2 = r2[o2], i2 = i2[o2], a2++;
      return o2 = t4[a2], r2[o2] = c2, i2[o2] = l2, this;
    }
    if (i2) {
      for (; a2 < s2 - 1; ) o2 = t4[a2], r2 = r2[o2], i2 = i2[o2], a2++;
      return o2 = t4[a2], r2[o2] = c2, c2 instanceof Array ? i2[o2] = numeric.rep(numeric.dim(c2), 0) : i2[o2] = 0, this;
    }
    for (; a2 < s2 - 1; ) o2 = t4[a2], r2 = r2[o2], a2++;
    return o2 = t4[a2], r2[o2] = c2, this;
  }, numeric.T.prototype.getRows = function(t4, n2) {
    var r2 = n2 - t4 + 1, i2, a2 = Array(r2), o2, s2 = this.x, c2 = this.y;
    for (i2 = t4; i2 <= n2; i2++) a2[i2 - t4] = s2[i2];
    if (c2) {
      for (o2 = Array(r2), i2 = t4; i2 <= n2; i2++) o2[i2 - t4] = c2[i2];
      return new numeric.T(a2, o2);
    }
    return new numeric.T(a2);
  }, numeric.T.prototype.setRows = function(t4, n2, r2) {
    var i2, a2 = this.x, o2 = this.y, s2 = r2.x, c2 = r2.y;
    for (i2 = t4; i2 <= n2; i2++) a2[i2] = s2[i2 - t4];
    if (c2) for (o2 || (o2 = numeric.rep(numeric.dim(a2), 0), this.y = o2), i2 = t4; i2 <= n2; i2++) o2[i2] = c2[i2 - t4];
    else if (o2) for (i2 = t4; i2 <= n2; i2++) o2[i2] = numeric.rep([s2[i2 - t4].length], 0);
    return this;
  }, numeric.T.prototype.getRow = function(t4) {
    var n2 = this.x, r2 = this.y;
    return r2 ? new numeric.T(n2[t4], r2[t4]) : new numeric.T(n2[t4]);
  }, numeric.T.prototype.setRow = function(t4, n2) {
    var r2 = this.x, i2 = this.y, a2 = n2.x, o2 = n2.y;
    return r2[t4] = a2, o2 ? (i2 || (i2 = numeric.rep(numeric.dim(r2), 0), this.y = i2), i2[t4] = o2) : i2 &&= numeric.rep([a2.length], 0), this;
  }, numeric.T.prototype.getBlock = function(t4, n2) {
    var r2 = this.x, i2 = this.y, a2 = numeric.getBlock;
    return i2 ? new numeric.T(a2(r2, t4, n2), a2(i2, t4, n2)) : new numeric.T(a2(r2, t4, n2));
  }, numeric.T.prototype.setBlock = function(t4, n2, r2) {
    r2 instanceof numeric.T || (r2 = new numeric.T(r2));
    var i2 = this.x, a2 = this.y, o2 = numeric.setBlock, s2 = r2.x, c2 = r2.y;
    if (c2) return a2 ||= (this.y = numeric.rep(numeric.dim(this), 0), this.y), o2(i2, t4, n2, s2), o2(a2, t4, n2, c2), this;
    o2(i2, t4, n2, s2), a2 && o2(a2, t4, n2, numeric.rep(numeric.dim(s2), 0));
  }, numeric.T.rep = function(t4, n2) {
    var r2 = numeric.T;
    n2 instanceof r2 || (n2 = new r2(n2));
    var i2 = n2.x, a2 = n2.y, o2 = numeric.rep;
    return a2 ? new r2(o2(t4, i2), o2(t4, a2)) : new r2(o2(t4, i2));
  }, numeric.T.diag = function(t4) {
    t4 instanceof numeric.T || (t4 = new numeric.T(t4));
    var n2 = t4.x, r2 = t4.y, i2 = numeric.diag;
    return r2 ? new numeric.T(i2(n2), i2(r2)) : new numeric.T(i2(n2));
  }, numeric.T.eig = function() {
    if (this.y) throw Error(`eig: not implemented for complex matrices.`);
    return numeric.eig(this.x);
  }, numeric.T.identity = function(t4) {
    return new numeric.T(numeric.identity(t4));
  }, numeric.T.prototype.getDiag = function() {
    var t4 = numeric, n2 = this.x, r2 = this.y;
    return r2 ? new t4.T(t4.getDiag(n2), t4.getDiag(r2)) : new t4.T(t4.getDiag(n2));
  }, numeric.house = function(t4) {
    var n2 = numeric.clone(t4), r2 = (t4[0] >= 0 ? 1 : -1) * numeric.norm2(t4);
    n2[0] += r2;
    var i2 = numeric.norm2(n2);
    if (i2 === 0) throw Error(`eig: internal error`);
    return numeric.div(n2, i2);
  }, numeric.toUpperHessenberg = function(t4) {
    var n2 = numeric.dim(t4);
    if (n2.length !== 2 || n2[0] !== n2[1]) throw Error(`numeric: toUpperHessenberg() only works on square matrices`);
    var r2 = n2[0], i2, a2, o2, s2, c2, l2 = numeric.clone(t4), u2, d2, p2, m2, h2 = numeric.identity(r2), g2;
    for (a2 = 0; a2 < r2 - 2; a2++) {
      for (s2 = Array(r2 - a2 - 1), i2 = a2 + 1; i2 < r2; i2++) s2[i2 - a2 - 1] = l2[i2][a2];
      if (numeric.norm2(s2) > 0) {
        for (c2 = numeric.house(s2), u2 = numeric.getBlock(l2, [a2 + 1, a2], [r2 - 1, r2 - 1]), d2 = numeric.tensor(c2, numeric.dot(c2, u2)), i2 = a2 + 1; i2 < r2; i2++) for (p2 = l2[i2], m2 = d2[i2 - a2 - 1], o2 = a2; o2 < r2; o2++) p2[o2] -= 2 * m2[o2 - a2];
        for (u2 = numeric.getBlock(l2, [0, a2 + 1], [r2 - 1, r2 - 1]), d2 = numeric.tensor(numeric.dot(u2, c2), c2), i2 = 0; i2 < r2; i2++) for (p2 = l2[i2], m2 = d2[i2], o2 = a2 + 1; o2 < r2; o2++) p2[o2] -= 2 * m2[o2 - a2 - 1];
        for (u2 = Array(r2 - a2 - 1), i2 = a2 + 1; i2 < r2; i2++) u2[i2 - a2 - 1] = h2[i2];
        for (d2 = numeric.tensor(c2, numeric.dot(c2, u2)), i2 = a2 + 1; i2 < r2; i2++) for (g2 = h2[i2], m2 = d2[i2 - a2 - 1], o2 = 0; o2 < r2; o2++) g2[o2] -= 2 * m2[o2];
      }
    }
    return { H: l2, Q: h2 };
  }, numeric.epsilon = 2220446049250313e-31, numeric.QRFrancis = function(t4, n2) {
    n2 === void 0 && (n2 = 1e4), t4 = numeric.clone(t4);
    numeric.clone(t4);
    var i2 = numeric.dim(t4)[0], a2, o2, s2, c2, l2, u2, d2, p2, m2, h2 = numeric.identity(i2), g2, v2, y2, b2, x2, S2, w2, E, D;
    if (i2 < 3) return { Q: h2, B: [[0, i2 - 1]] };
    var O = numeric.epsilon;
    for (D = 0; D < n2; D++) {
      for (w2 = 0; w2 < i2 - 1; w2++) if (Math.abs(t4[w2 + 1][w2]) < O * (Math.abs(t4[w2][w2]) + Math.abs(t4[w2 + 1][w2 + 1]))) {
        var j2 = numeric.QRFrancis(numeric.getBlock(t4, [0, 0], [w2, w2]), n2), M2 = numeric.QRFrancis(numeric.getBlock(t4, [w2 + 1, w2 + 1], [i2 - 1, i2 - 1]), n2);
        for (y2 = Array(w2 + 1), S2 = 0; S2 <= w2; S2++) y2[S2] = h2[S2];
        for (b2 = numeric.dot(j2.Q, y2), S2 = 0; S2 <= w2; S2++) h2[S2] = b2[S2];
        for (y2 = Array(i2 - w2 - 1), S2 = w2 + 1; S2 < i2; S2++) y2[S2 - w2 - 1] = h2[S2];
        for (b2 = numeric.dot(M2.Q, y2), S2 = w2 + 1; S2 < i2; S2++) h2[S2] = b2[S2 - w2 - 1];
        return { Q: h2, B: j2.B.concat(numeric.add(M2.B, w2 + 1)) };
      }
      if (s2 = t4[i2 - 2][i2 - 2], c2 = t4[i2 - 2][i2 - 1], l2 = t4[i2 - 1][i2 - 2], u2 = t4[i2 - 1][i2 - 1], p2 = s2 + u2, d2 = s2 * u2 - c2 * l2, m2 = numeric.getBlock(t4, [0, 0], [2, 2]), p2 * p2 >= 4 * d2) {
        var N2 = 0.5 * (p2 + Math.sqrt(p2 * p2 - 4 * d2)), F = 0.5 * (p2 - Math.sqrt(p2 * p2 - 4 * d2));
        m2 = numeric.add(numeric.sub(numeric.dot(m2, m2), numeric.mul(m2, N2 + F)), numeric.diag(numeric.rep([3], N2 * F)));
      } else m2 = numeric.add(numeric.sub(numeric.dot(m2, m2), numeric.mul(m2, p2)), numeric.diag(numeric.rep([3], d2)));
      for (a2 = [m2[0][0], m2[1][0], m2[2][0]], o2 = numeric.house(a2), y2 = [t4[0], t4[1], t4[2]], b2 = numeric.tensor(o2, numeric.dot(o2, y2)), S2 = 0; S2 < 3; S2++) for (v2 = t4[S2], x2 = b2[S2], E = 0; E < i2; E++) v2[E] -= 2 * x2[E];
      for (y2 = numeric.getBlock(t4, [0, 0], [i2 - 1, 2]), b2 = numeric.tensor(numeric.dot(y2, o2), o2), S2 = 0; S2 < i2; S2++) for (v2 = t4[S2], x2 = b2[S2], E = 0; E < 3; E++) v2[E] -= 2 * x2[E];
      for (y2 = [h2[0], h2[1], h2[2]], b2 = numeric.tensor(o2, numeric.dot(o2, y2)), S2 = 0; S2 < 3; S2++) for (g2 = h2[S2], x2 = b2[S2], E = 0; E < i2; E++) g2[E] -= 2 * x2[E];
      var I;
      for (w2 = 0; w2 < i2 - 2; w2++) {
        for (E = w2; E <= w2 + 1; E++) if (Math.abs(t4[E + 1][E]) < O * (Math.abs(t4[E][E]) + Math.abs(t4[E + 1][E + 1]))) {
          var j2 = numeric.QRFrancis(numeric.getBlock(t4, [0, 0], [E, E]), n2), M2 = numeric.QRFrancis(numeric.getBlock(t4, [E + 1, E + 1], [i2 - 1, i2 - 1]), n2);
          for (y2 = Array(E + 1), S2 = 0; S2 <= E; S2++) y2[S2] = h2[S2];
          for (b2 = numeric.dot(j2.Q, y2), S2 = 0; S2 <= E; S2++) h2[S2] = b2[S2];
          for (y2 = Array(i2 - E - 1), S2 = E + 1; S2 < i2; S2++) y2[S2 - E - 1] = h2[S2];
          for (b2 = numeric.dot(M2.Q, y2), S2 = E + 1; S2 < i2; S2++) h2[S2] = b2[S2 - E - 1];
          return { Q: h2, B: j2.B.concat(numeric.add(M2.B, E + 1)) };
        }
        for (I = Math.min(i2 - 1, w2 + 3), a2 = Array(I - w2), S2 = w2 + 1; S2 <= I; S2++) a2[S2 - w2 - 1] = t4[S2][w2];
        for (o2 = numeric.house(a2), y2 = numeric.getBlock(t4, [w2 + 1, w2], [I, i2 - 1]), b2 = numeric.tensor(o2, numeric.dot(o2, y2)), S2 = w2 + 1; S2 <= I; S2++) for (v2 = t4[S2], x2 = b2[S2 - w2 - 1], E = w2; E < i2; E++) v2[E] -= 2 * x2[E - w2];
        for (y2 = numeric.getBlock(t4, [0, w2 + 1], [i2 - 1, I]), b2 = numeric.tensor(numeric.dot(y2, o2), o2), S2 = 0; S2 < i2; S2++) for (v2 = t4[S2], x2 = b2[S2], E = w2 + 1; E <= I; E++) v2[E] -= 2 * x2[E - w2 - 1];
        for (y2 = Array(I - w2), S2 = w2 + 1; S2 <= I; S2++) y2[S2 - w2 - 1] = h2[S2];
        for (b2 = numeric.tensor(o2, numeric.dot(o2, y2)), S2 = w2 + 1; S2 <= I; S2++) for (g2 = h2[S2], x2 = b2[S2 - w2 - 1], E = 0; E < i2; E++) g2[E] -= 2 * x2[E];
      }
    }
    throw Error(`numeric: eigenvalue iteration does not converge -- increase maxiter?`);
  }, numeric.eig = function(t4, n2) {
    var r2 = numeric.toUpperHessenberg(t4), i2 = numeric.QRFrancis(r2.H, n2), a2 = numeric.T, o2 = t4.length, s2, c2, u2 = i2.B, d2 = numeric.dot(i2.Q, numeric.dot(r2.H, numeric.transpose(i2.Q))), p2 = new a2(numeric.dot(i2.Q, r2.Q)), m2, h2 = u2.length, g2, v2, y2, b2, x2, S2, w2, E, D, O, j2, M2, N2, F, I = Math.sqrt;
    for (c2 = 0; c2 < h2; c2++) if (s2 = u2[c2][0], s2 !== u2[c2][1]) {
      if (g2 = s2 + 1, v2 = d2[s2][s2], y2 = d2[s2][g2], b2 = d2[g2][s2], x2 = d2[g2][g2], y2 === 0 && b2 === 0) continue;
      S2 = -v2 - x2, w2 = v2 * x2 - y2 * b2, E = S2 * S2 - 4 * w2, E >= 0 ? (D = S2 < 0 ? -0.5 * (S2 - I(E)) : -0.5 * (S2 + I(E)), N2 = (v2 - D) * (v2 - D) + y2 * y2, F = b2 * b2 + (x2 - D) * (x2 - D), N2 > F ? (N2 = I(N2), j2 = (v2 - D) / N2, M2 = y2 / N2) : (F = I(F), j2 = b2 / F, M2 = (x2 - D) / F), m2 = new a2([[M2, -j2], [j2, M2]]), p2.setRows(s2, g2, m2.dot(p2.getRows(s2, g2)))) : (D = -0.5 * S2, O = 0.5 * I(-E), N2 = (v2 - D) * (v2 - D) + y2 * y2, F = b2 * b2 + (x2 - D) * (x2 - D), N2 > F ? (N2 = I(N2 + O * O), j2 = (v2 - D) / N2, M2 = y2 / N2, D = 0, O /= N2) : (F = I(F + O * O), j2 = b2 / F, M2 = (x2 - D) / F, D = O / F, O = 0), m2 = new a2([[M2, -j2], [j2, M2]], [[D, O], [O, -D]]), p2.setRows(s2, g2, m2.dot(p2.getRows(s2, g2))));
    }
    var L = p2.dot(t4).dot(p2.transjugate()), o2 = t4.length, R = numeric.T.identity(o2);
    for (g2 = 0; g2 < o2; g2++) if (g2 > 0) for (c2 = g2 - 1; c2 >= 0; c2--) {
      var z = L.get([c2, c2]), V = L.get([g2, g2]);
      if (numeric.neq(z.x, V.x) || numeric.neq(z.y, V.y)) D = L.getRow(c2).getBlock([c2], [g2 - 1]), O = R.getRow(g2).getBlock([c2], [g2 - 1]), R.set([g2, c2], L.get([c2, g2]).neg().sub(D.dot(O)).div(z.sub(V)));
      else {
        R.setRow(g2, R.getRow(c2));
        continue;
      }
    }
    for (g2 = 0; g2 < o2; g2++) D = R.getRow(g2), R.setRow(g2, D.div(D.norm2()));
    return R = R.transpose(), R = p2.transjugate().dot(R), { lambda: L.getDiag(), E: R };
  }, numeric.ccsSparse = function(t4) {
    var n2 = t4.length, r2, i2, a2, o2, s2 = [];
    for (a2 = n2 - 1; a2 !== -1; --a2) for (o2 in i2 = t4[a2], i2) {
      for (o2 = parseInt(o2); o2 >= s2.length; ) s2[s2.length] = 0;
      i2[o2] !== 0 && s2[o2]++;
    }
    var r2 = s2.length, c2 = Array(r2 + 1);
    for (c2[0] = 0, a2 = 0; a2 < r2; ++a2) c2[a2 + 1] = c2[a2] + s2[a2];
    var l2 = Array(c2[r2]), u2 = Array(c2[r2]);
    for (a2 = n2 - 1; a2 !== -1; --a2) for (o2 in i2 = t4[a2], i2) i2[o2] !== 0 && (s2[o2]--, l2[c2[o2] + s2[o2]] = a2, u2[c2[o2] + s2[o2]] = i2[o2]);
    return [c2, l2, u2];
  }, numeric.ccsFull = function(t4) {
    var n2 = t4[0], r2 = t4[1], i2 = t4[2], a2 = numeric.ccsDim(t4), o2 = a2[0], s2 = a2[1], c2, l2, u2, d2, m2 = numeric.rep([o2, s2], 0);
    for (c2 = 0; c2 < s2; c2++) for (u2 = n2[c2], d2 = n2[c2 + 1], l2 = u2; l2 < d2; ++l2) m2[r2[l2]][c2] = i2[l2];
    return m2;
  }, numeric.ccsTSolve = function(t4, n2, r2, i2, a2) {
    var o2 = t4[0], s2 = t4[1], c2 = t4[2], l2 = o2.length - 1, u2 = Math.max, d2 = 0;
    i2 === void 0 && (r2 = numeric.rep([l2], 0)), i2 === void 0 && (i2 = numeric.linspace(0, r2.length - 1)), a2 === void 0 && (a2 = []);
    function p2(t5) {
      var n3;
      if (r2[t5] === 0) {
        for (r2[t5] = 1, n3 = o2[t5]; n3 < o2[t5 + 1]; ++n3) p2(s2[n3]);
        a2[d2] = t5, ++d2;
      }
    }
    var m2, h2, g2, v2, y2, b2, w2;
    for (m2 = i2.length - 1; m2 !== -1; --m2) p2(i2[m2]);
    for (a2.length = d2, m2 = a2.length - 1; m2 !== -1; --m2) r2[a2[m2]] = 0;
    for (m2 = i2.length - 1; m2 !== -1; --m2) h2 = i2[m2], r2[h2] = n2[h2];
    for (m2 = a2.length - 1; m2 !== -1; --m2) {
      for (h2 = a2[m2], g2 = o2[h2], v2 = u2(o2[h2 + 1], g2), y2 = g2; y2 !== v2; ++y2) if (s2[y2] === h2) {
        r2[h2] /= c2[y2];
        break;
      }
      for (w2 = r2[h2], y2 = g2; y2 !== v2; ++y2) b2 = s2[y2], b2 !== h2 && (r2[b2] -= w2 * c2[y2]);
    }
    return r2;
  }, numeric.ccsDFS = function(t4) {
    this.k = Array(t4), this.k1 = Array(t4), this.j = Array(t4);
  }, numeric.ccsDFS.prototype.dfs = function(t4, n2, r2, i2, a2, o2) {
    var s2 = 0, c2, l2 = a2.length, u2 = this.k, d2 = this.k1, p2 = this.j, m2, h2;
    if (i2[t4] === 0) for (i2[t4] = 1, p2[0] = t4, u2[0] = m2 = n2[t4], d2[0] = h2 = n2[t4 + 1]; ; ) if (m2 >= h2) {
      if (a2[l2] = p2[s2], s2 === 0) return;
      ++l2, --s2, m2 = u2[s2], h2 = d2[s2];
    } else c2 = o2[r2[m2]], i2[c2] === 0 ? (i2[c2] = 1, u2[s2] = m2, ++s2, p2[s2] = c2, m2 = n2[c2], d2[s2] = h2 = n2[c2 + 1]) : ++m2;
  }, numeric.ccsLPSolve = function(t4, n2, r2, i2, a2, o2, s2) {
    var c2 = t4[0], l2 = t4[1], u2 = t4[2];
    c2.length - 1;
    var m2 = n2[0], h2 = n2[1], g2 = n2[2], v2, y2 = m2[a2], b2 = m2[a2 + 1], x2, w2, E, D, O, N2;
    for (i2.length = 0, v2 = y2; v2 < b2; ++v2) s2.dfs(o2[h2[v2]], c2, l2, r2, i2, o2);
    for (v2 = i2.length - 1; v2 !== -1; --v2) r2[i2[v2]] = 0;
    for (v2 = y2; v2 !== b2; ++v2) x2 = o2[h2[v2]], r2[x2] = g2[v2];
    for (v2 = i2.length - 1; v2 !== -1; --v2) {
      for (x2 = i2[v2], w2 = c2[x2], E = c2[x2 + 1], D = w2; D < E; ++D) if (o2[l2[D]] === x2) {
        r2[x2] /= u2[D];
        break;
      }
      for (N2 = r2[x2], D = w2; D < E; ++D) O = o2[l2[D]], O !== x2 && (r2[O] -= N2 * u2[D]);
    }
    return r2;
  }, numeric.ccsLUP1 = function(t4, n2) {
    var r2 = t4[0].length - 1, i2 = [numeric.rep([r2 + 1], 0), [], []], a2 = [numeric.rep([r2 + 1], 0), [], []], o2 = i2[0], s2 = i2[1], c2 = i2[2], l2 = a2[0], u2 = a2[1], d2 = a2[2], p2 = numeric.rep([r2], 0), m2 = numeric.rep([r2], 0), h2, g2, v2, x2, S2, w2, E, O = numeric.ccsLPSolve, M2 = Math.abs, N2 = numeric.linspace(0, r2 - 1), F = numeric.linspace(0, r2 - 1), I = new numeric.ccsDFS(r2);
    for (n2 === void 0 && (n2 = 1), h2 = 0; h2 < r2; ++h2) {
      for (O(i2, t4, p2, m2, h2, F, I), x2 = -1, S2 = -1, g2 = m2.length - 1; g2 !== -1; --g2) v2 = m2[g2], !(v2 <= h2) && (w2 = M2(p2[v2]), w2 > x2 && (S2 = v2, x2 = w2));
      for (M2(p2[h2]) < n2 * x2 && (g2 = N2[h2], x2 = N2[S2], N2[h2] = x2, F[x2] = h2, N2[S2] = g2, F[g2] = S2, x2 = p2[h2], p2[h2] = p2[S2], p2[S2] = x2), x2 = o2[h2], S2 = l2[h2], E = p2[h2], s2[x2] = N2[h2], c2[x2] = 1, ++x2, g2 = m2.length - 1; g2 !== -1; --g2) v2 = m2[g2], w2 = p2[v2], m2[g2] = 0, p2[v2] = 0, v2 <= h2 ? (u2[S2] = v2, d2[S2] = w2, ++S2) : (s2[x2] = N2[v2], c2[x2] = w2 / E, ++x2);
      o2[h2 + 1] = x2, l2[h2 + 1] = S2;
    }
    for (g2 = s2.length - 1; g2 !== -1; --g2) s2[g2] = F[s2[g2]];
    return { L: i2, U: a2, P: N2, Pinv: F };
  }, numeric.ccsDFS0 = function(t4) {
    this.k = Array(t4), this.k1 = Array(t4), this.j = Array(t4);
  }, numeric.ccsDFS0.prototype.dfs = function(t4, n2, r2, i2, a2, o2, s2) {
    var c2 = 0, l2, u2 = a2.length, d2 = this.k, p2 = this.k1, m2 = this.j, h2, g2;
    if (i2[t4] === 0) for (i2[t4] = 1, m2[0] = t4, d2[0] = h2 = n2[o2[t4]], p2[0] = g2 = n2[o2[t4] + 1]; ; ) {
      if (isNaN(h2)) throw Error(`Ow!`);
      if (h2 >= g2) {
        if (a2[u2] = o2[m2[c2]], c2 === 0) return;
        ++u2, --c2, h2 = d2[c2], g2 = p2[c2];
      } else l2 = r2[h2], i2[l2] === 0 ? (i2[l2] = 1, d2[c2] = h2, ++c2, m2[c2] = l2, l2 = o2[l2], h2 = n2[l2], p2[c2] = g2 = n2[l2 + 1]) : ++h2;
    }
  }, numeric.ccsLPSolve0 = function(t4, n2, r2, i2, a2, o2, s2, c2) {
    var l2 = t4[0], u2 = t4[1], d2 = t4[2];
    l2.length - 1;
    var h2 = n2[0], g2 = n2[1], v2 = n2[2], y2, b2 = h2[a2], x2 = h2[a2 + 1], S2, E, D, O, j2, F;
    for (i2.length = 0, y2 = b2; y2 < x2; ++y2) c2.dfs(g2[y2], l2, u2, r2, i2, o2, s2);
    for (y2 = i2.length - 1; y2 !== -1; --y2) S2 = i2[y2], r2[s2[S2]] = 0;
    for (y2 = b2; y2 !== x2; ++y2) S2 = g2[y2], r2[S2] = v2[y2];
    for (y2 = i2.length - 1; y2 !== -1; --y2) {
      for (S2 = i2[y2], j2 = s2[S2], E = l2[S2], D = l2[S2 + 1], O = E; O < D; ++O) if (u2[O] === j2) {
        r2[j2] /= d2[O];
        break;
      }
      for (F = r2[j2], O = E; O < D; ++O) r2[u2[O]] -= F * d2[O];
      r2[j2] = F;
    }
  }, numeric.ccsLUP0 = function(t4, n2) {
    var r2 = t4[0].length - 1, i2 = [numeric.rep([r2 + 1], 0), [], []], a2 = [numeric.rep([r2 + 1], 0), [], []], o2 = i2[0], s2 = i2[1], c2 = i2[2], l2 = a2[0], u2 = a2[1], d2 = a2[2], p2 = numeric.rep([r2], 0), m2 = numeric.rep([r2], 0), h2, g2, v2, x2, S2, w2, E, O = numeric.ccsLPSolve0, M2 = Math.abs, N2 = numeric.linspace(0, r2 - 1), F = numeric.linspace(0, r2 - 1), I = new numeric.ccsDFS0(r2);
    for (n2 === void 0 && (n2 = 1), h2 = 0; h2 < r2; ++h2) {
      for (O(i2, t4, p2, m2, h2, F, N2, I), x2 = -1, S2 = -1, g2 = m2.length - 1; g2 !== -1; --g2) v2 = m2[g2], !(v2 <= h2) && (w2 = M2(p2[N2[v2]]), w2 > x2 && (S2 = v2, x2 = w2));
      for (M2(p2[N2[h2]]) < n2 * x2 && (g2 = N2[h2], x2 = N2[S2], N2[h2] = x2, F[x2] = h2, N2[S2] = g2, F[g2] = S2), x2 = o2[h2], S2 = l2[h2], E = p2[N2[h2]], s2[x2] = N2[h2], c2[x2] = 1, ++x2, g2 = m2.length - 1; g2 !== -1; --g2) v2 = m2[g2], w2 = p2[N2[v2]], m2[g2] = 0, p2[N2[v2]] = 0, v2 <= h2 ? (u2[S2] = v2, d2[S2] = w2, ++S2) : (s2[x2] = N2[v2], c2[x2] = w2 / E, ++x2);
      o2[h2 + 1] = x2, l2[h2 + 1] = S2;
    }
    for (g2 = s2.length - 1; g2 !== -1; --g2) s2[g2] = F[s2[g2]];
    return { L: i2, U: a2, P: N2, Pinv: F };
  }, numeric.ccsLUP = numeric.ccsLUP0, numeric.ccsDim = function(t4) {
    return [numeric.sup(t4[1]) + 1, t4[0].length - 1];
  }, numeric.ccsGetBlock = function(t4, n2, r2) {
    var i2 = numeric.ccsDim(t4), a2 = i2[0], o2 = i2[1];
    n2 === void 0 ? n2 = numeric.linspace(0, a2 - 1) : typeof n2 == `number` && (n2 = [n2]), r2 === void 0 ? r2 = numeric.linspace(0, o2 - 1) : typeof r2 == `number` && (r2 = [r2]);
    var s2, u2 = n2.length, d2, p2 = r2.length, m2, h2, g2, v2 = numeric.rep([o2], 0), y2 = [], b2 = [], x2 = [v2, y2, b2], S2 = t4[0], w2 = t4[1], E = t4[2], D = numeric.rep([a2], 0), O = 0, j2 = numeric.rep([a2], 0);
    for (d2 = 0; d2 < p2; ++d2) {
      h2 = r2[d2];
      var M2 = S2[h2], N2 = S2[h2 + 1];
      for (s2 = M2; s2 < N2; ++s2) m2 = w2[s2], j2[m2] = 1, D[m2] = E[s2];
      for (s2 = 0; s2 < u2; ++s2) g2 = n2[s2], j2[g2] && (y2[O] = s2, b2[O] = D[n2[s2]], ++O);
      for (s2 = M2; s2 < N2; ++s2) m2 = w2[s2], j2[m2] = 0;
      v2[d2 + 1] = O;
    }
    return x2;
  }, numeric.ccsDot = function(t4, n2) {
    var r2 = t4[0], i2 = t4[1], a2 = t4[2], o2 = n2[0], s2 = n2[1], c2 = n2[2], l2 = numeric.ccsDim(t4), u2 = numeric.ccsDim(n2), d2 = l2[0];
    l2[1];
    var m2 = u2[1], h2 = numeric.rep([d2], 0), g2 = numeric.rep([d2], 0), v2 = Array(d2), y2 = numeric.rep([m2], 0), b2 = [], x2 = [], S2 = [y2, b2, x2], w2, E, D, O, j2, M2, N2, F, I, L, R;
    for (D = 0; D !== m2; ++D) {
      for (O = o2[D], j2 = o2[D + 1], I = 0, E = O; E < j2; ++E) for (L = s2[E], R = c2[E], M2 = r2[L], N2 = r2[L + 1], w2 = M2; w2 < N2; ++w2) F = i2[w2], g2[F] === 0 && (v2[I] = F, g2[F] = 1, I += 1), h2[F] = h2[F] + a2[w2] * R;
      for (O = y2[D], j2 = O + I, y2[D + 1] = j2, E = I - 1; E !== -1; --E) R = O + E, w2 = v2[E], b2[R] = w2, x2[R] = h2[w2], g2[w2] = 0, h2[w2] = 0;
      y2[D + 1] = y2[D] + I;
    }
    return S2;
  }, numeric.ccsLUPSolve = function(t4, n2) {
    var r2 = t4.L, i2 = t4.U;
    t4.P;
    var o2 = n2[0], s2 = false;
    typeof o2 != `object` && (n2 = [[0, n2.length], numeric.linspace(0, n2.length - 1), n2], o2 = n2[0], s2 = true);
    var c2 = n2[1], l2 = n2[2], u2 = r2[0].length - 1, d2 = o2.length - 1, p2 = numeric.rep([u2], 0), m2 = Array(u2), h2 = numeric.rep([u2], 0), g2 = Array(u2), v2 = numeric.rep([d2 + 1], 0), y2 = [], b2 = [], x2 = numeric.ccsTSolve, S2, w2, E, D, O, j2, M2 = 0;
    for (S2 = 0; S2 < d2; ++S2) {
      for (O = 0, E = o2[S2], D = o2[S2 + 1], w2 = E; w2 < D; ++w2) j2 = t4.Pinv[c2[w2]], g2[O] = j2, h2[j2] = l2[w2], ++O;
      for (g2.length = O, x2(r2, h2, p2, g2, m2), w2 = g2.length - 1; w2 !== -1; --w2) h2[g2[w2]] = 0;
      if (x2(i2, p2, h2, m2, g2), s2) return h2;
      for (w2 = m2.length - 1; w2 !== -1; --w2) p2[m2[w2]] = 0;
      for (w2 = g2.length - 1; w2 !== -1; --w2) j2 = g2[w2], y2[M2] = j2, b2[M2] = h2[j2], h2[j2] = 0, ++M2;
      v2[S2 + 1] = M2;
    }
    return [v2, y2, b2];
  }, numeric.ccsbinop = function(t4, n2) {
    return n2 === void 0 && (n2 = ``), Function(`X`, `Y`, `var Xi = X[0], Xj = X[1], Xv = X[2];
var Yi = Y[0], Yj = Y[1], Yv = Y[2];
var n = Xi.length-1,m = Math.max(numeric.sup(Xj),numeric.sup(Yj))+1;
var Zi = numeric.rep([n+1],0), Zj = [], Zv = [];
var x = numeric.rep([m],0),y = numeric.rep([m],0);
var xk,yk,zk;
var i,j,j0,j1,k,p=0;
` + n2 + `for(i=0;i<n;++i) {
  j0 = Xi[i]; j1 = Xi[i+1];
  for(j=j0;j!==j1;++j) {
    k = Xj[j];
    x[k] = 1;
    Zj[p] = k;
    ++p;
  }
  j0 = Yi[i]; j1 = Yi[i+1];
  for(j=j0;j!==j1;++j) {
    k = Yj[j];
    y[k] = Yv[j];
    if(x[k] === 0) {
      Zj[p] = k;
      ++p;
    }
  }
  Zi[i+1] = p;
  j0 = Xi[i]; j1 = Xi[i+1];
  for(j=j0;j!==j1;++j) x[Xj[j]] = Xv[j];
  j0 = Zi[i]; j1 = Zi[i+1];
  for(j=j0;j!==j1;++j) {
    k = Zj[j];
    xk = x[k];
    yk = y[k];
` + t4 + `
    Zv[j] = zk;
  }
  j0 = Xi[i]; j1 = Xi[i+1];
  for(j=j0;j!==j1;++j) x[Xj[j]] = 0;
  j0 = Yi[i]; j1 = Yi[i+1];
  for(j=j0;j!==j1;++j) y[Yj[j]] = 0;
}
return [Zi,Zj,Zv];`);
  }, (function() {
    var k, A, B, C;
    for (k in numeric.ops2) A = isFinite(eval(`1` + numeric.ops2[k] + `0`)) ? `[Y[0],Y[1],numeric.` + k + `(X,Y[2])]` : `NaN`, B = isFinite(eval(`0` + numeric.ops2[k] + `1`)) ? `[X[0],X[1],numeric.` + k + `(X[2],Y)]` : `NaN`, C = isFinite(eval(`1` + numeric.ops2[k] + `0`)) && isFinite(eval(`0` + numeric.ops2[k] + `1`)) ? `numeric.ccs` + k + `MM(X,Y)` : `NaN`, numeric[`ccs` + k + `MM`] = numeric.ccsbinop(`zk = xk ` + numeric.ops2[k] + `yk;`), numeric[`ccs` + k] = Function(`X`, `Y`, `if(typeof X === "number") return ` + A + `;
if(typeof Y === "number") return ` + B + `;
return ` + C + `;
`);
  })(), numeric.ccsScatter = function(t4) {
    var n2 = t4[0], r2 = t4[1], i2 = t4[2], a2 = numeric.sup(r2) + 1, o2 = n2.length, s2 = numeric.rep([a2], 0), c2 = Array(o2), l2 = Array(o2), u2 = numeric.rep([a2], 0), d2;
    for (d2 = 0; d2 < o2; ++d2) u2[r2[d2]]++;
    for (d2 = 0; d2 < a2; ++d2) s2[d2 + 1] = s2[d2] + u2[d2];
    var p2 = s2.slice(0), m2, h2;
    for (d2 = 0; d2 < o2; ++d2) h2 = r2[d2], m2 = p2[h2], c2[m2] = n2[d2], l2[m2] = i2[d2], p2[h2] = p2[h2] + 1;
    return [s2, c2, l2];
  }, numeric.ccsGather = function(t4) {
    var n2 = t4[0], r2 = t4[1], i2 = t4[2], a2 = n2.length - 1, o2 = r2.length, s2 = Array(o2), c2 = Array(o2), l2 = Array(o2), u2, d2, p2, m2, h2 = 0;
    for (u2 = 0; u2 < a2; ++u2) for (p2 = n2[u2], m2 = n2[u2 + 1], d2 = p2; d2 !== m2; ++d2) c2[h2] = u2, s2[h2] = r2[d2], l2[h2] = i2[d2], ++h2;
    return [s2, c2, l2];
  }, numeric.sdim = function t4(n2, r2, i2) {
    if (r2 === void 0 && (r2 = []), typeof n2 != `object`) return r2;
    for (var a2 in i2 === void 0 && (i2 = 0), i2 in r2 || (r2[i2] = 0), n2.length > r2[i2] && (r2[i2] = n2.length), n2) n2.hasOwnProperty(a2) && t4(n2[a2], r2, i2 + 1);
    return r2;
  }, numeric.sclone = function t4(n2, r2, i2) {
    r2 === void 0 && (r2 = 0), i2 === void 0 && (i2 = numeric.sdim(n2).length);
    var a2, o2 = Array(n2.length);
    if (r2 === i2 - 1) {
      for (a2 in n2) n2.hasOwnProperty(a2) && (o2[a2] = n2[a2]);
      return o2;
    }
    for (a2 in n2) n2.hasOwnProperty(a2) && (o2[a2] = t4(n2[a2], r2 + 1, i2));
    return o2;
  }, numeric.sdiag = function(t4) {
    var n2 = t4.length, r2, i2 = Array(n2), a2;
    for (r2 = n2 - 1; r2 >= 1; r2 -= 2) a2 = r2 - 1, i2[r2] = [], i2[r2][r2] = t4[r2], i2[a2] = [], i2[a2][a2] = t4[a2];
    return r2 === 0 && (i2[0] = [], i2[0][0] = t4[r2]), i2;
  }, numeric.sidentity = function(t4) {
    return numeric.sdiag(numeric.rep([t4], 1));
  }, numeric.stranspose = function(t4) {
    var n2 = [];
    t4.length;
    var i2, a2, o2;
    for (i2 in t4) if (t4.hasOwnProperty(i2)) for (a2 in o2 = t4[i2], o2) o2.hasOwnProperty(a2) && (typeof n2[a2] != `object` && (n2[a2] = []), n2[a2][i2] = o2[a2]);
    return n2;
  }, numeric.sLUP = function(t4, n2) {
    throw Error(`The function numeric.sLUP had a bug in it and has been removed. Please use the new numeric.ccsLUP function instead.`);
  }, numeric.sdotMM = function(t4, n2) {
    var r2 = t4.length;
    n2.length;
    var a2 = numeric.stranspose(n2), o2 = a2.length, s2, c2, l2, u2, d2, p2, m2 = Array(r2), h2;
    for (l2 = r2 - 1; l2 >= 0; l2--) {
      for (h2 = [], s2 = t4[l2], d2 = o2 - 1; d2 >= 0; d2--) {
        for (u2 in p2 = 0, c2 = a2[d2], s2) s2.hasOwnProperty(u2) && u2 in c2 && (p2 += s2[u2] * c2[u2]);
        p2 && (h2[d2] = p2);
      }
      m2[l2] = h2;
    }
    return m2;
  }, numeric.sdotMV = function(t4, n2) {
    var r2 = t4.length, i2, a2, o2, s2 = Array(r2), c2;
    for (a2 = r2 - 1; a2 >= 0; a2--) {
      for (o2 in i2 = t4[a2], c2 = 0, i2) i2.hasOwnProperty(o2) && n2[o2] && (c2 += i2[o2] * n2[o2]);
      c2 && (s2[a2] = c2);
    }
    return s2;
  }, numeric.sdotVM = function(t4, n2) {
    var r2, i2, a2, o2, s2 = [];
    for (r2 in t4) if (t4.hasOwnProperty(r2)) for (i2 in a2 = n2[r2], o2 = t4[r2], a2) a2.hasOwnProperty(i2) && (s2[i2] || (s2[i2] = 0), s2[i2] += o2 * a2[i2]);
    return s2;
  }, numeric.sdotVV = function(t4, n2) {
    var r2, i2 = 0;
    for (r2 in t4) t4[r2] && n2[r2] && (i2 += t4[r2] * n2[r2]);
    return i2;
  }, numeric.sdot = function(t4, n2) {
    var r2 = numeric.sdim(t4).length, i2 = numeric.sdim(n2).length;
    switch (r2 * 1e3 + i2) {
      case 0:
        return t4 * n2;
      case 1001:
        return numeric.sdotVV(t4, n2);
      case 2001:
        return numeric.sdotMV(t4, n2);
      case 1002:
        return numeric.sdotVM(t4, n2);
      case 2002:
        return numeric.sdotMM(t4, n2);
      default:
        throw Error(`numeric.sdot not implemented for tensors of order ` + r2 + ` and ` + i2);
    }
  }, numeric.sscatter = function(t4) {
    var n2 = t4[0].length, r2, i2, a2, o2 = t4.length, s2 = [], c2;
    for (i2 = n2 - 1; i2 >= 0; --i2) if (t4[o2 - 1][i2]) {
      for (c2 = s2, a2 = 0; a2 < o2 - 2; a2++) r2 = t4[a2][i2], c2[r2] || (c2[r2] = []), c2 = c2[r2];
      c2[t4[a2][i2]] = t4[a2 + 1][i2];
    }
    return s2;
  }, numeric.sgather = function t4(n2, r2, i2) {
    r2 === void 0 && (r2 = []), i2 === void 0 && (i2 = []);
    var a2 = i2.length, o2, s2;
    for (o2 in n2) if (n2.hasOwnProperty(o2)) if (i2[a2] = parseInt(o2), s2 = n2[o2], typeof s2 == `number`) {
      if (s2) {
        if (r2.length === 0) for (o2 = a2 + 1; o2 >= 0; --o2) r2[o2] = [];
        for (o2 = a2; o2 >= 0; --o2) r2[o2].push(i2[o2]);
        r2[a2 + 1].push(s2);
      }
    } else t4(s2, r2, i2);
    return i2.length > a2 && i2.pop(), r2;
  }, numeric.cLU = function(t4) {
    var n2 = t4[0], r2 = t4[1], i2 = t4[2], a2 = n2.length, o2 = 0, s2, c2, l2, u2, d2, p2;
    for (s2 = 0; s2 < a2; s2++) n2[s2] > o2 && (o2 = n2[s2]);
    o2++;
    var m2 = Array(o2), h2 = Array(o2), g2 = numeric.rep([o2], 1 / 0), v2 = numeric.rep([o2], -1 / 0), y2, b2, x2;
    for (l2 = 0; l2 < a2; l2++) s2 = n2[l2], c2 = r2[l2], c2 < g2[s2] && (g2[s2] = c2), c2 > v2[s2] && (v2[s2] = c2);
    for (s2 = 0; s2 < o2 - 1; s2++) v2[s2] > v2[s2 + 1] && (v2[s2 + 1] = v2[s2]);
    for (s2 = o2 - 1; s2 >= 1; s2--) g2[s2] < g2[s2 - 1] && (g2[s2 - 1] = g2[s2]);
    var S2 = 0, w2 = 0;
    for (s2 = 0; s2 < o2; s2++) h2[s2] = numeric.rep([v2[s2] - g2[s2] + 1], 0), m2[s2] = numeric.rep([s2 - g2[s2]], 0), S2 += s2 - g2[s2] + 1, w2 += v2[s2] - s2 + 1;
    for (l2 = 0; l2 < a2; l2++) s2 = n2[l2], h2[s2][r2[l2] - g2[s2]] = i2[l2];
    for (s2 = 0; s2 < o2 - 1; s2++) for (u2 = s2 - g2[s2], y2 = h2[s2], c2 = s2 + 1; g2[c2] <= s2 && c2 < o2; c2++) if (d2 = s2 - g2[c2], p2 = v2[s2] - s2, b2 = h2[c2], x2 = b2[d2] / y2[u2], x2) {
      for (l2 = 1; l2 <= p2; l2++) b2[l2 + d2] -= x2 * y2[l2 + u2];
      m2[c2][s2 - g2[c2]] = x2;
    }
    var y2 = [], b2 = [], E = [], D = [], O = [], j2 = [], a2 = 0, M2 = 0, N2;
    for (s2 = 0; s2 < o2; s2++) {
      for (u2 = g2[s2], d2 = v2[s2], N2 = h2[s2], c2 = s2; c2 <= d2; c2++) N2[c2 - u2] && (y2[a2] = s2, b2[a2] = c2, E[a2] = N2[c2 - u2], a2++);
      for (N2 = m2[s2], c2 = u2; c2 < s2; c2++) N2[c2 - u2] && (D[M2] = s2, O[M2] = c2, j2[M2] = N2[c2 - u2], M2++);
      D[M2] = s2, O[M2] = s2, j2[M2] = 1, M2++;
    }
    return { U: [y2, b2, E], L: [D, O, j2] };
  }, numeric.cLUsolve = function(t4, n2) {
    var r2 = t4.L, i2 = t4.U, a2 = numeric.clone(n2), o2 = r2[0], s2 = r2[1], c2 = r2[2], l2 = i2[0], u2 = i2[1], d2 = i2[2], p2 = l2.length;
    o2.length;
    var h2 = a2.length, g2, y2 = 0;
    for (g2 = 0; g2 < h2; g2++) {
      for (; s2[y2] < g2; ) a2[g2] -= c2[y2] * a2[s2[y2]], y2++;
      y2++;
    }
    for (y2 = p2 - 1, g2 = h2 - 1; g2 >= 0; g2--) {
      for (; u2[y2] > g2; ) a2[g2] -= d2[y2] * a2[u2[y2]], y2--;
      a2[g2] /= d2[y2], y2--;
    }
    return a2;
  }, numeric.cgrid = function(t4, n2) {
    typeof t4 == `number` && (t4 = [t4, t4]);
    var r2 = numeric.rep(t4, -1), i2, a2, o2;
    if (typeof n2 != `function`) switch (n2) {
      case `L`:
        n2 = function(n3, r3) {
          return n3 >= t4[0] / 2 || r3 < t4[1] / 2;
        };
        break;
      default:
        n2 = function(t5, n3) {
          return true;
        };
        break;
    }
    for (o2 = 0, i2 = 1; i2 < t4[0] - 1; i2++) for (a2 = 1; a2 < t4[1] - 1; a2++) n2(i2, a2) && (r2[i2][a2] = o2, o2++);
    return r2;
  }, numeric.cdelsq = function(t4) {
    var n2 = [[-1, 0], [0, -1], [0, 1], [1, 0]], r2 = numeric.dim(t4), i2 = r2[0], a2 = r2[1], o2, s2, c2, l2, u2, d2 = [], p2 = [], m2 = [];
    for (o2 = 1; o2 < i2 - 1; o2++) for (s2 = 1; s2 < a2 - 1; s2++) if (!(t4[o2][s2] < 0)) {
      for (c2 = 0; c2 < 4; c2++) l2 = o2 + n2[c2][0], u2 = s2 + n2[c2][1], !(t4[l2][u2] < 0) && (d2.push(t4[o2][s2]), p2.push(t4[l2][u2]), m2.push(-1));
      d2.push(t4[o2][s2]), p2.push(t4[o2][s2]), m2.push(4);
    }
    return [d2, p2, m2];
  }, numeric.cdotMV = function(t4, n2) {
    var r2, i2 = t4[0], a2 = t4[1], o2 = t4[2], s2, c2 = i2.length, l2 = 0;
    for (s2 = 0; s2 < c2; s2++) i2[s2] > l2 && (l2 = i2[s2]);
    for (l2++, r2 = numeric.rep([l2], 0), s2 = 0; s2 < c2; s2++) r2[i2[s2]] += o2[s2] * n2[a2[s2]];
    return r2;
  }, numeric.Spline = function(t4, n2, r2, i2, a2) {
    this.x = t4, this.yl = n2, this.yr = r2, this.kl = i2, this.kr = a2;
  }, numeric.Spline.prototype._at = function(t4, n2) {
    var r2 = this.x, i2 = this.yl, a2 = this.yr, o2 = this.kl, s2 = this.kr, t4, c2, l2, u2, d2 = numeric.add, p2 = numeric.sub, m2 = numeric.mul;
    c2 = p2(m2(o2[n2], r2[n2 + 1] - r2[n2]), p2(a2[n2 + 1], i2[n2])), l2 = d2(m2(s2[n2 + 1], r2[n2] - r2[n2 + 1]), p2(a2[n2 + 1], i2[n2])), u2 = (t4 - r2[n2]) / (r2[n2 + 1] - r2[n2]);
    var h2 = u2 * (1 - u2);
    return d2(d2(d2(m2(1 - u2, i2[n2]), m2(u2, a2[n2 + 1])), m2(c2, h2 * (1 - u2))), m2(l2, h2 * u2));
  }, numeric.Spline.prototype.at = function(t4) {
    if (typeof t4 == `number`) {
      var n2 = this.x, r2 = n2.length, i2, a2, o2, s2 = Math.floor;
      for (i2 = 0, a2 = r2 - 1; a2 - i2 > 1; ) o2 = s2((i2 + a2) / 2), n2[o2] <= t4 ? i2 = o2 : a2 = o2;
      return this._at(t4, i2);
    }
    var r2 = t4.length, d2, p2 = Array(r2);
    for (d2 = r2 - 1; d2 !== -1; --d2) p2[d2] = this.at(t4[d2]);
    return p2;
  }, numeric.Spline.prototype.diff = function() {
    var t4 = this.x, n2 = this.yl, r2 = this.yr, i2 = this.kl, a2 = this.kr, o2 = n2.length, s2, c2, l2, u2 = i2, d2 = a2, p2 = Array(o2), m2 = Array(o2), h2 = numeric.add, g2 = numeric.mul, v2 = numeric.div, y2 = numeric.sub;
    for (s2 = o2 - 1; s2 !== -1; --s2) c2 = t4[s2 + 1] - t4[s2], l2 = y2(r2[s2 + 1], n2[s2]), p2[s2] = v2(h2(g2(l2, 6), g2(i2[s2], -4 * c2), g2(a2[s2 + 1], -2 * c2)), c2 * c2), m2[s2 + 1] = v2(h2(g2(l2, -6), g2(i2[s2], 2 * c2), g2(a2[s2 + 1], 4 * c2)), c2 * c2);
    return new numeric.Spline(t4, u2, d2, p2, m2);
  }, numeric.Spline.prototype.roots = function() {
    function t4(t5) {
      return t5 * t5;
    }
    var n2 = [], r2 = this.x, i2 = this.yl, a2 = this.yr, o2 = this.kl, s2 = this.kr;
    typeof i2[0] == `number` && (i2 = [i2], a2 = [a2], o2 = [o2], s2 = [s2]);
    var c2 = i2.length, l2 = r2.length - 1, u2, d2, p2, v2, y2, b2, x2, n2 = Array(c2), S2, w2, E, D, O, j2, M2, N2, F, L, R, z, V, H2, U, W, G = Math.sqrt;
    for (u2 = 0; u2 !== c2; ++u2) {
      for (v2 = i2[u2], y2 = a2[u2], b2 = o2[u2], x2 = s2[u2], S2 = [], d2 = 0; d2 !== l2; d2++) {
        for (d2 > 0 && y2[d2] * v2[d2] < 0 && S2.push(r2[d2]), F = r2[d2 + 1] - r2[d2], r2[d2], D = v2[d2], O = y2[d2 + 1], w2 = b2[d2] / F, E = x2[d2 + 1] / F, N2 = t4(w2 - E + 3 * (D - O)) + 12 * E * D, j2 = E + 3 * D + 2 * w2 - 3 * O, M2 = 3 * (E + w2 + 2 * (D - O)), N2 <= 0 ? (R = j2 / M2, L = R > r2[d2] && R < r2[d2 + 1] ? [r2[d2], R, r2[d2 + 1]] : [r2[d2], r2[d2 + 1]]) : (R = (j2 - G(N2)) / M2, z = (j2 + G(N2)) / M2, L = [r2[d2]], R > r2[d2] && R < r2[d2 + 1] && L.push(R), z > r2[d2] && z < r2[d2 + 1] && L.push(z), L.push(r2[d2 + 1])), H2 = L[0], R = this._at(H2, d2), p2 = 0; p2 < L.length - 1; p2++) {
          if (U = L[p2 + 1], z = this._at(U, d2), R === 0) {
            S2.push(H2), H2 = U, R = z;
            continue;
          }
          if (z === 0 || R * z > 0) {
            H2 = U, R = z;
            continue;
          }
          for (var K = 0; W = (R * U - z * H2) / (R - z), !(W <= H2 || W >= U); ) if (V = this._at(W, d2), V * z > 0) U = W, z = V, K === -1 && (R *= 0.5), K = -1;
          else if (V * R > 0) H2 = W, R = V, K === 1 && (z *= 0.5), K = 1;
          else break;
          S2.push(W), H2 = L[p2 + 1], R = this._at(H2, d2);
        }
        z === 0 && S2.push(U);
      }
      n2[u2] = S2;
    }
    return typeof this.yl[0] == `number` ? n2[0] : n2;
  }, numeric.spline = function(t4, n2, r2, i2) {
    var a2 = t4.length, o2 = [], s2 = [], c2 = [], l2, u2 = numeric.sub, d2 = numeric.mul, p2 = numeric.add;
    for (l2 = a2 - 2; l2 >= 0; l2--) s2[l2] = t4[l2 + 1] - t4[l2], c2[l2] = u2(n2[l2 + 1], n2[l2]);
    (typeof r2 == `string` || typeof i2 == `string`) && (r2 = i2 = `periodic`);
    var m2 = [[], [], []];
    switch (typeof r2) {
      case `undefined`:
        o2[0] = d2(3 / (s2[0] * s2[0]), c2[0]), m2[0].push(0, 0), m2[1].push(0, 1), m2[2].push(2 / s2[0], 1 / s2[0]);
        break;
      case `string`:
        o2[0] = p2(d2(3 / (s2[a2 - 2] * s2[a2 - 2]), c2[a2 - 2]), d2(3 / (s2[0] * s2[0]), c2[0])), m2[0].push(0, 0, 0), m2[1].push(a2 - 2, 0, 1), m2[2].push(1 / s2[a2 - 2], 2 / s2[a2 - 2] + 2 / s2[0], 1 / s2[0]);
        break;
      default:
        o2[0] = r2, m2[0].push(0), m2[1].push(0), m2[2].push(1);
        break;
    }
    for (l2 = 1; l2 < a2 - 1; l2++) o2[l2] = p2(d2(3 / (s2[l2 - 1] * s2[l2 - 1]), c2[l2 - 1]), d2(3 / (s2[l2] * s2[l2]), c2[l2])), m2[0].push(l2, l2, l2), m2[1].push(l2 - 1, l2, l2 + 1), m2[2].push(1 / s2[l2 - 1], 2 / s2[l2 - 1] + 2 / s2[l2], 1 / s2[l2]);
    switch (typeof i2) {
      case `undefined`:
        o2[a2 - 1] = d2(3 / (s2[a2 - 2] * s2[a2 - 2]), c2[a2 - 2]), m2[0].push(a2 - 1, a2 - 1), m2[1].push(a2 - 2, a2 - 1), m2[2].push(1 / s2[a2 - 2], 2 / s2[a2 - 2]);
        break;
      case `string`:
        m2[1][m2[1].length - 1] = 0;
        break;
      default:
        o2[a2 - 1] = i2, m2[0].push(a2 - 1), m2[1].push(a2 - 1), m2[2].push(1);
        break;
    }
    o2 = typeof o2[0] == `number` ? [o2] : numeric.transpose(o2);
    var h2 = Array(o2.length);
    if (typeof r2 == `string`) for (l2 = h2.length - 1; l2 !== -1; --l2) h2[l2] = numeric.ccsLUPSolve(numeric.ccsLUP(numeric.ccsScatter(m2)), o2[l2]), h2[l2][a2 - 1] = h2[l2][0];
    else for (l2 = h2.length - 1; l2 !== -1; --l2) h2[l2] = numeric.cLUsolve(numeric.cLU(m2), o2[l2]);
    return h2 = typeof n2[0] == `number` ? h2[0] : numeric.transpose(h2), new numeric.Spline(t4, n2, n2, h2, h2);
  }, numeric.fftpow2 = function t4(n2, r2) {
    var i2 = n2.length;
    if (i2 !== 1) {
      var a2 = Math.cos, o2 = Math.sin, s2, c2, l2 = Array(i2 / 2), u2 = Array(i2 / 2), d2 = Array(i2 / 2), p2 = Array(i2 / 2);
      for (c2 = i2 / 2, s2 = i2 - 1; s2 !== -1; --s2) --c2, d2[c2] = n2[s2], p2[c2] = r2[s2], --s2, l2[c2] = n2[s2], u2[c2] = r2[s2];
      t4(l2, u2), t4(d2, p2), c2 = i2 / 2;
      var m2, h2 = -6.283185307179586 / i2, g2, v2;
      for (s2 = i2 - 1; s2 !== -1; --s2) --c2, c2 === -1 && (c2 = i2 / 2 - 1), m2 = h2 * s2, g2 = a2(m2), v2 = o2(m2), n2[s2] = l2[c2] + g2 * d2[c2] - v2 * p2[c2], r2[s2] = u2[c2] + g2 * p2[c2] + v2 * d2[c2];
    }
  }, numeric._ifftpow2 = function t4(n2, r2) {
    var i2 = n2.length;
    if (i2 !== 1) {
      var a2 = Math.cos, o2 = Math.sin, s2, c2, l2 = Array(i2 / 2), u2 = Array(i2 / 2), d2 = Array(i2 / 2), p2 = Array(i2 / 2);
      for (c2 = i2 / 2, s2 = i2 - 1; s2 !== -1; --s2) --c2, d2[c2] = n2[s2], p2[c2] = r2[s2], --s2, l2[c2] = n2[s2], u2[c2] = r2[s2];
      t4(l2, u2), t4(d2, p2), c2 = i2 / 2;
      var m2, h2 = 6.283185307179586 / i2, g2, v2;
      for (s2 = i2 - 1; s2 !== -1; --s2) --c2, c2 === -1 && (c2 = i2 / 2 - 1), m2 = h2 * s2, g2 = a2(m2), v2 = o2(m2), n2[s2] = l2[c2] + g2 * d2[c2] - v2 * p2[c2], r2[s2] = u2[c2] + g2 * p2[c2] + v2 * d2[c2];
    }
  }, numeric.ifftpow2 = function(t4, n2) {
    numeric._ifftpow2(t4, n2), numeric.diveq(t4, t4.length), numeric.diveq(n2, n2.length);
  }, numeric.convpow2 = function(t4, n2, r2, i2) {
    numeric.fftpow2(t4, n2), numeric.fftpow2(r2, i2);
    var a2, o2 = t4.length, s2, c2, l2, u2;
    for (a2 = o2 - 1; a2 !== -1; --a2) s2 = t4[a2], l2 = n2[a2], c2 = r2[a2], u2 = i2[a2], t4[a2] = s2 * c2 - l2 * u2, n2[a2] = s2 * u2 + l2 * c2;
    numeric.ifftpow2(t4, n2);
  }, numeric.T.prototype.fft = function() {
    var t4 = this.x, n2 = this.y, r2 = t4.length, i2 = Math.log, a2 = i2(2), o2 = 2 ** Math.ceil(i2(2 * r2 - 1) / a2), s2 = numeric.rep([o2], 0), c2 = numeric.rep([o2], 0), l2 = Math.cos, u2 = Math.sin, d2, p2 = -3.141592653589793 / r2, m2, h2 = numeric.rep([o2], 0), g2 = numeric.rep([o2], 0);
    for (d2 = 0; d2 < r2; d2++) h2[d2] = t4[d2];
    if (n2 !== void 0) for (d2 = 0; d2 < r2; d2++) g2[d2] = n2[d2];
    for (s2[0] = 1, d2 = 1; d2 <= o2 / 2; d2++) m2 = p2 * d2 * d2, s2[d2] = l2(m2), c2[d2] = u2(m2), s2[o2 - d2] = l2(m2), c2[o2 - d2] = u2(m2);
    var y2 = new numeric.T(h2, g2), b2 = new numeric.T(s2, c2);
    return y2 = y2.mul(b2), numeric.convpow2(y2.x, y2.y, numeric.clone(b2.x), numeric.neg(b2.y)), y2 = y2.mul(b2), y2.x.length = r2, y2.y.length = r2, y2;
  }, numeric.T.prototype.ifft = function() {
    var t4 = this.x, n2 = this.y, r2 = t4.length, i2 = Math.log, a2 = i2(2), o2 = 2 ** Math.ceil(i2(2 * r2 - 1) / a2), s2 = numeric.rep([o2], 0), c2 = numeric.rep([o2], 0), l2 = Math.cos, u2 = Math.sin, d2, p2 = 3.141592653589793 / r2, m2, h2 = numeric.rep([o2], 0), g2 = numeric.rep([o2], 0);
    for (d2 = 0; d2 < r2; d2++) h2[d2] = t4[d2];
    if (n2 !== void 0) for (d2 = 0; d2 < r2; d2++) g2[d2] = n2[d2];
    for (s2[0] = 1, d2 = 1; d2 <= o2 / 2; d2++) m2 = p2 * d2 * d2, s2[d2] = l2(m2), c2[d2] = u2(m2), s2[o2 - d2] = l2(m2), c2[o2 - d2] = u2(m2);
    var y2 = new numeric.T(h2, g2), b2 = new numeric.T(s2, c2);
    return y2 = y2.mul(b2), numeric.convpow2(y2.x, y2.y, numeric.clone(b2.x), numeric.neg(b2.y)), y2 = y2.mul(b2), y2.x.length = r2, y2.y.length = r2, y2.div(r2);
  }, numeric.gradient = function(t4, n2) {
    var r2 = n2.length, i2 = t4(n2);
    if (isNaN(i2)) throw Error(`gradient: f(x) is a NaN!`);
    var a2 = Math.max, o2, s2 = numeric.clone(n2), c2, l2, u2 = Array(r2);
    numeric.div;
    numeric.sub;
    var m2, a2 = Math.max, g2 = 1e-3, v2 = Math.abs, y2 = Math.min, b2, x2, S2, w2 = 0, E, D, O;
    for (o2 = 0; o2 < r2; o2++) for (var j2 = a2(1e-6 * i2, 1e-8); ; ) {
      if (++w2, w2 > 20) throw Error(`Numerical gradient fails`);
      if (s2[o2] = n2[o2] + j2, c2 = t4(s2), s2[o2] = n2[o2] - j2, l2 = t4(s2), s2[o2] = n2[o2], isNaN(c2) || isNaN(l2)) {
        j2 /= 16;
        continue;
      }
      if (u2[o2] = (c2 - l2) / (2 * j2), b2 = n2[o2] - j2, x2 = n2[o2], S2 = n2[o2] + j2, E = (c2 - i2) / j2, D = (i2 - l2) / j2, O = a2(v2(u2[o2]), v2(i2), v2(c2), v2(l2), v2(b2), v2(x2), v2(S2), 1e-8), m2 = y2(a2(v2(E - u2[o2]), v2(D - u2[o2]), v2(E - D)) / O, j2 / O), m2 > g2) j2 /= 16;
      else break;
    }
    return u2;
  }, numeric.uncmin = function(t4, n2, r2, i2, a2, o2, s2) {
    var c2 = numeric.gradient;
    s2 === void 0 && (s2 = {}), r2 === void 0 && (r2 = 1e-8), i2 === void 0 && (i2 = function(n3) {
      return c2(t4, n3);
    }), a2 === void 0 && (a2 = 1e3), n2 = numeric.clone(n2);
    var l2 = n2.length, u2 = t4(n2), d2, p2;
    if (isNaN(u2)) throw Error(`uncmin: f(x0) is a NaN!`);
    var m2 = Math.max, h2 = numeric.norm2;
    r2 = m2(r2, numeric.epsilon);
    var g2, v2, y2, b2 = s2.Hinv || numeric.identity(l2), x2 = numeric.dot;
    numeric.inv;
    var w2 = numeric.sub, E = numeric.add, D = numeric.tensor, O = numeric.div, j2 = numeric.mul, M2 = numeric.all, N2 = numeric.isFinite, F = numeric.neg, I = 0, R, z, V, H2, W, K, q, Y = ``;
    for (v2 = i2(n2); I < a2; ) {
      if (typeof o2 == `function` && o2(I, n2, u2, v2, b2)) {
        Y = `Callback returned true`;
        break;
      }
      if (!M2(N2(v2))) {
        Y = `Gradient has Infinity or NaN`;
        break;
      }
      if (g2 = F(x2(b2, v2)), !M2(N2(g2))) {
        Y = `Search direction has Infinity or NaN`;
        break;
      }
      if (q = h2(g2), q < r2) {
        Y = `Newton step smaller than tol`;
        break;
      }
      for (K = 1, p2 = x2(v2, g2), z = n2; I < a2 && !(K * q < r2); ) {
        if (R = j2(g2, K), z = E(n2, R), d2 = t4(z), d2 - u2 >= 0.1 * K * p2 || isNaN(d2)) {
          K *= 0.5, ++I;
          continue;
        }
        break;
      }
      if (K * q < r2) {
        Y = `Line search step size smaller than tol`;
        break;
      }
      if (I === a2) {
        Y = `maxit reached during line search`;
        break;
      }
      y2 = i2(z), V = w2(y2, v2), W = x2(V, R), H2 = x2(b2, V), b2 = w2(E(b2, j2((W + x2(V, H2)) / (W * W), D(R, R))), O(E(D(H2, R), D(R, H2)), W)), n2 = z, u2 = d2, v2 = y2, ++I;
    }
    return { solution: n2, f: u2, gradient: v2, invHessian: b2, iterations: I, message: Y };
  }, numeric.Dopri = function(t4, n2, r2, i2, a2, o2, s2) {
    this.x = t4, this.y = n2, this.f = r2, this.ymid = i2, this.iterations = a2, this.events = s2, this.message = o2;
  }, numeric.Dopri.prototype._at = function(t4, n2) {
    function r2(t5) {
      return t5 * t5;
    }
    var i2 = this, a2 = i2.x, o2 = i2.y, s2 = i2.f, c2 = i2.ymid;
    a2.length;
    var u2, d2, p2, m2, h2, g2, t4, y2, b2 = 0.5, x2 = numeric.add, S2 = numeric.mul, w2 = numeric.sub, E, D, O;
    return u2 = a2[n2], d2 = a2[n2 + 1], m2 = o2[n2], h2 = o2[n2 + 1], y2 = d2 - u2, p2 = u2 + b2 * y2, g2 = c2[n2], E = w2(s2[n2], S2(m2, 1 / (u2 - p2) + 2 / (u2 - d2))), D = w2(s2[n2 + 1], S2(h2, 1 / (d2 - p2) + 2 / (d2 - u2))), O = [r2(t4 - d2) * (t4 - p2) / r2(u2 - d2) / (u2 - p2), r2(t4 - u2) * r2(t4 - d2) / r2(u2 - p2) / r2(d2 - p2), r2(t4 - u2) * (t4 - p2) / r2(d2 - u2) / (d2 - p2), (t4 - u2) * r2(t4 - d2) * (t4 - p2) / r2(u2 - d2) / (u2 - p2), (t4 - d2) * r2(t4 - u2) * (t4 - p2) / r2(u2 - d2) / (d2 - p2)], x2(x2(x2(x2(S2(m2, O[0]), S2(g2, O[1])), S2(h2, O[2])), S2(E, O[3])), S2(D, O[4]));
  }, numeric.Dopri.prototype.at = function(t4) {
    var n2, r2, i2, a2 = Math.floor;
    if (typeof t4 != `number`) {
      var o2 = t4.length, s2 = Array(o2);
      for (n2 = o2 - 1; n2 !== -1; --n2) s2[n2] = this.at(t4[n2]);
      return s2;
    }
    var c2 = this.x;
    for (n2 = 0, r2 = c2.length - 1; r2 - n2 > 1; ) i2 = a2(0.5 * (n2 + r2)), c2[i2] <= t4 ? n2 = i2 : r2 = i2;
    return this._at(t4, n2);
  }, numeric.dopri = function(t4, n2, r2, i2, a2, o2, s2) {
    a2 === void 0 && (a2 = 1e-6), o2 === void 0 && (o2 = 1e3);
    var c2 = [t4], l2 = [r2], u2 = [i2(t4, r2)], d2, p2, m2, h2, g2, v2, y2 = [], b2 = 1 / 5, x2 = [3 / 40, 9 / 40], S2 = [44 / 45, -56 / 15, 32 / 9], w2 = [19372 / 6561, -25360 / 2187, 64448 / 6561, -212 / 729], E = [9017 / 3168, -355 / 33, 46732 / 5247, 49 / 176, -5103 / 18656], D = [35 / 384, 0, 500 / 1113, 125 / 192, -2187 / 6784, 11 / 84], O = [0.5 * 6025192743 / 30085553152, 0, 0.5 * 51252292925 / 65400821598, 0.5 * -2691868925 / 45128329728, 0.5 * 187940372067 / 1594534317056, 0.5 * -1776094331 / 19743644256, 0.5 * 11237099 / 235043384], j2 = [1 / 5, 3 / 10, 4 / 5, 8 / 9, 1, 1], M2 = [-71 / 57600, 0, 71 / 16695, -71 / 1920, 17253 / 339200, -22 / 525, 1 / 40], N2 = 0, F, I, L = (n2 - t4) / 10, R = 0, z = numeric.add, V = numeric.mul, H2, U, G = Math.min, K = Math.abs, q = numeric.norminf, J = Math.pow, ee = numeric.any, Y = numeric.lt, te = numeric.and;
    numeric.sub;
    var X, ie, ae, Z = new numeric.Dopri(c2, l2, u2, y2, -1, ``);
    for (typeof s2 == `function` && (X = s2(t4, r2)); t4 < n2 && R < o2; ) {
      if (++R, t4 + L > n2 && (L = n2 - t4), d2 = i2(t4 + j2[0] * L, z(r2, V(b2 * L, u2[N2]))), p2 = i2(t4 + j2[1] * L, z(z(r2, V(x2[0] * L, u2[N2])), V(x2[1] * L, d2))), m2 = i2(t4 + j2[2] * L, z(z(z(r2, V(S2[0] * L, u2[N2])), V(S2[1] * L, d2)), V(S2[2] * L, p2))), h2 = i2(t4 + j2[3] * L, z(z(z(z(r2, V(w2[0] * L, u2[N2])), V(w2[1] * L, d2)), V(w2[2] * L, p2)), V(w2[3] * L, m2))), g2 = i2(t4 + j2[4] * L, z(z(z(z(z(r2, V(E[0] * L, u2[N2])), V(E[1] * L, d2)), V(E[2] * L, p2)), V(E[3] * L, m2)), V(E[4] * L, h2))), H2 = z(z(z(z(z(r2, V(u2[N2], L * D[0])), V(p2, L * D[2])), V(m2, L * D[3])), V(h2, L * D[4])), V(g2, L * D[5])), v2 = i2(t4 + L, H2), F = z(z(z(z(z(V(u2[N2], L * M2[0]), V(p2, L * M2[2])), V(m2, L * M2[3])), V(h2, L * M2[4])), V(g2, L * M2[5])), V(v2, L * M2[6])), U = typeof F == `number` ? K(F) : q(F), U > a2) {
        if (L = 0.2 * L * J(a2 / U, 0.25), t4 + L === t4) {
          Z.msg = `Step size became too small`;
          break;
        }
        continue;
      }
      if (y2[N2] = z(z(z(z(z(z(r2, V(u2[N2], L * O[0])), V(p2, L * O[2])), V(m2, L * O[3])), V(h2, L * O[4])), V(g2, L * O[5])), V(v2, L * O[6])), ++N2, c2[N2] = t4 + L, l2[N2] = H2, u2[N2] = v2, typeof s2 == `function`) {
        var oe, $2 = t4, se = t4 + 0.5 * L, ce;
        if (ie = s2(se, y2[N2 - 1]), ae = te(Y(X, 0), Y(0, ie)), ee(ae) || ($2 = se, se = t4 + L, X = ie, ie = s2(se, H2), ae = te(Y(X, 0), Y(0, ie))), ee(ae)) {
          for (var le, ue, de, fe, pe = 0, me = 1, he = 1; ; ) {
            if (typeof X == `number`) ce = (he * ie * $2 - me * X * se) / (he * ie - me * X);
            else for (ce = se, I = X.length - 1; I !== -1; --I) X[I] < 0 && ie[I] > 0 && (ce = G(ce, (he * ie[I] * $2 - me * X[I] * se) / (he * ie[I] - me * X[I])));
            if (ce <= $2 || ce >= se) break;
            oe = Z._at(ce, N2 - 1), fe = s2(ce, oe), de = te(Y(X, 0), Y(0, fe)), ee(de) ? (se = ce, ie = fe, ae = de, he = 1, pe === -1 ? me *= 0.5 : me = 1, pe = -1) : ($2 = ce, X = fe, me = 1, pe === 1 ? he *= 0.5 : he = 1, pe = 1);
          }
          return H2 = Z._at(0.5 * (t4 + ce), N2 - 1), Z.f[N2] = i2(ce, oe), Z.x[N2] = ce, Z.y[N2] = oe, Z.ymid[N2 - 1] = H2, Z.events = ae, Z.iterations = R, Z;
        }
      }
      t4 += L, r2 = H2, X = ie, L = G(0.8 * L * J(a2 / U, 0.25), 4 * L);
    }
    return Z.iterations = R, Z;
  }, numeric.LU = function(t4, n2) {
    n2 ||= false;
    var r2 = Math.abs, i2, a2, o2, s2, c2, l2, u2, d2, p2, m2 = t4.length, h2 = m2 - 1, g2 = Array(m2);
    for (n2 || (t4 = numeric.clone(t4)), o2 = 0; o2 < m2; ++o2) {
      for (u2 = o2, l2 = t4[o2], p2 = r2(l2[o2]), a2 = o2 + 1; a2 < m2; ++a2) s2 = r2(t4[a2][o2]), p2 < s2 && (p2 = s2, u2 = a2);
      for (g2[o2] = u2, u2 != o2 && (t4[o2] = t4[u2], t4[u2] = l2, l2 = t4[o2]), c2 = l2[o2], i2 = o2 + 1; i2 < m2; ++i2) t4[i2][o2] /= c2;
      for (i2 = o2 + 1; i2 < m2; ++i2) {
        for (d2 = t4[i2], a2 = o2 + 1; a2 < h2; ++a2) d2[a2] -= d2[o2] * l2[a2], ++a2, d2[a2] -= d2[o2] * l2[a2];
        a2 === h2 && (d2[a2] -= d2[o2] * l2[a2]);
      }
    }
    return { LU: t4, P: g2 };
  }, numeric.LUsolve = function(t4, n2) {
    var r2, i2, a2 = t4.LU, o2 = a2.length, s2 = numeric.clone(n2), c2 = t4.P, l2, u2, p2;
    for (r2 = o2 - 1; r2 !== -1; --r2) s2[r2] = n2[r2];
    for (r2 = 0; r2 < o2; ++r2) for (l2 = c2[r2], c2[r2] !== r2 && (p2 = s2[r2], s2[r2] = s2[l2], s2[l2] = p2), u2 = a2[r2], i2 = 0; i2 < r2; ++i2) s2[r2] -= s2[i2] * u2[i2];
    for (r2 = o2 - 1; r2 >= 0; --r2) {
      for (u2 = a2[r2], i2 = r2 + 1; i2 < o2; ++i2) s2[r2] -= s2[i2] * u2[i2];
      s2[r2] /= u2[r2];
    }
    return s2;
  }, numeric.solve = function(t4, n2, r2) {
    return numeric.LUsolve(numeric.LU(t4, r2), n2);
  }, numeric.echelonize = function(t4) {
    var n2 = numeric.dim(t4), r2 = n2[0], i2 = n2[1], a2 = numeric.identity(r2), o2 = Array(r2), s2, c2, l2, u2, d2, p2, m2, h2, g2 = Math.abs, v2 = numeric.diveq;
    for (t4 = numeric.clone(t4), s2 = 0; s2 < r2; ++s2) {
      for (l2 = 0, d2 = t4[s2], p2 = a2[s2], c2 = 1; c2 < i2; ++c2) g2(d2[l2]) < g2(d2[c2]) && (l2 = c2);
      for (o2[s2] = l2, v2(p2, d2[l2]), v2(d2, d2[l2]), c2 = 0; c2 < r2; ++c2) if (c2 !== s2) {
        for (m2 = t4[c2], h2 = m2[l2], u2 = i2 - 1; u2 !== -1; --u2) m2[u2] -= d2[u2] * h2;
        for (m2 = a2[c2], u2 = r2 - 1; u2 !== -1; --u2) m2[u2] -= p2[u2] * h2;
      }
    }
    return { I: a2, A: t4, P: o2 };
  }, numeric.__solveLP = function(t4, n2, r2, i2, a2, o2, s2) {
    var c2 = numeric.sum;
    numeric.log;
    var u2 = numeric.mul, d2 = numeric.sub, p2 = numeric.dot, m2 = numeric.div, h2 = numeric.add, g2 = t4.length, v2 = r2.length, y2, b2 = false, S2 = 0, w2 = 1;
    numeric.transpose(n2);
    numeric.svd;
    var M2 = numeric.transpose;
    numeric.leq;
    var F = Math.sqrt, I = Math.abs;
    numeric.muleq;
    numeric.norminf;
    numeric.any;
    var V = Math.min, H2 = numeric.all, U = numeric.gt, W = Array(g2), G = Array(v2);
    numeric.rep([v2], 1);
    var q, J = numeric.solve, ee = d2(r2, p2(n2, o2)), Y, te = p2(t4, t4), ne;
    for (Y = S2; Y < a2; ++Y) {
      var X, ae;
      for (X = v2 - 1; X !== -1; --X) G[X] = m2(n2[X], ee[X]);
      var Z = M2(G);
      for (X = g2 - 1; X !== -1; --X) W[X] = c2(Z[X]);
      w2 = 0.25 * I(te / p2(t4, W));
      var oe = 100 * F(te / p2(W, W));
      for ((!isFinite(w2) || w2 > oe) && (w2 = oe), ne = h2(t4, u2(w2, W)), q = p2(Z, G), X = g2 - 1; X !== -1; --X) q[X][X] += 1;
      ae = J(q, m2(ne, w2), true);
      var $2 = m2(ee, p2(n2, ae)), se = 1;
      for (X = v2 - 1; X !== -1; --X) $2[X] < 0 && (se = V(se, -0.999 * $2[X]));
      if (y2 = d2(o2, u2(ae, se)), ee = d2(r2, p2(n2, y2)), !H2(U(ee, 0))) return { solution: o2, message: ``, iterations: Y };
      if (o2 = y2, w2 < i2) return { solution: y2, message: ``, iterations: Y };
      if (s2) {
        var ce = p2(t4, ne), le = p2(n2, ne);
        for (b2 = true, X = v2 - 1; X !== -1; --X) if (ce * le[X] < 0) {
          b2 = false;
          break;
        }
      } else b2 = !(o2[g2 - 1] >= 0);
      if (b2) return { solution: y2, message: `Unbounded`, iterations: Y };
    }
    return { solution: o2, message: `maximum iteration count exceeded`, iterations: Y };
  }, numeric._solveLP = function(t4, n2, r2, i2, a2) {
    var o2 = t4.length, s2 = r2.length, c2;
    numeric.sum;
    numeric.log;
    numeric.mul;
    var p2 = numeric.sub, m2 = numeric.dot;
    numeric.div;
    numeric.add;
    var v2 = numeric.rep([o2], 0).concat([1]), y2 = numeric.rep([s2, 1], -1), b2 = numeric.blockMatrix([[n2, y2]]), x2 = r2, c2 = numeric.rep([o2], 0).concat(Math.max(0, numeric.sup(numeric.neg(r2))) + 1), S2 = numeric.__solveLP(v2, b2, x2, i2, a2, c2, false), w2 = numeric.clone(S2.solution);
    if (w2.length = o2, numeric.inf(p2(r2, m2(n2, w2))) < 0) return { solution: NaN, message: `Infeasible`, iterations: S2.iterations };
    var E = numeric.__solveLP(t4, n2, r2, i2, a2 - S2.iterations, w2, true);
    return E.iterations += S2.iterations, E;
  }, numeric.solveLP = function(t4, n2, r2, i2, a2, o2, s2) {
    if (s2 === void 0 && (s2 = 1e3), o2 === void 0 && (o2 = numeric.epsilon), i2 === void 0) return numeric._solveLP(t4, n2, r2, o2, s2);
    var c2 = i2.length, l2 = i2[0].length, u2 = n2.length, d2 = numeric.echelonize(i2), p2 = numeric.rep([l2], 0), m2 = d2.P, h2 = [], g2;
    for (g2 = m2.length - 1; g2 !== -1; --g2) p2[m2[g2]] = 1;
    for (g2 = l2 - 1; g2 !== -1; --g2) p2[g2] === 0 && h2.push(g2);
    var v2 = numeric.getRange, y2 = numeric.linspace(0, c2 - 1), b2 = numeric.linspace(0, u2 - 1), x2 = v2(i2, y2, h2), S2 = v2(n2, b2, m2), w2 = v2(n2, b2, h2), E = numeric.dot, D = numeric.sub, O = E(S2, d2.I), j2 = D(w2, E(O, x2)), M2 = D(r2, E(O, a2)), N2 = Array(m2.length), F = Array(h2.length);
    for (g2 = m2.length - 1; g2 !== -1; --g2) N2[g2] = t4[m2[g2]];
    for (g2 = h2.length - 1; g2 !== -1; --g2) F[g2] = t4[h2[g2]];
    var I = D(F, E(N2, E(d2.I, x2))), L = numeric._solveLP(I, j2, M2, o2, s2), R = L.solution;
    if (R !== R) return L;
    var z = E(d2.I, D(a2, E(x2, R))), V = Array(t4.length);
    for (g2 = m2.length - 1; g2 !== -1; --g2) V[m2[g2]] = z[g2];
    for (g2 = h2.length - 1; g2 !== -1; --g2) V[h2[g2]] = R[g2];
    return { solution: V, message: L.message, iterations: L.iterations };
  }, numeric.MPStoLP = function(t4) {
    t4 instanceof String && t4.split(`
`);
    var n2 = 0, r2 = [`Initial state`, `NAME`, `ROWS`, `COLUMNS`, `RHS`, `BOUNDS`, `ENDATA`], i2 = t4.length, a2, o2, s2, c2 = 0, l2 = {}, u2 = [], d2 = 0, p2 = {}, m2 = 0, h2, g2 = [], v2 = [], y2 = [];
    function b2(i3) {
      throw Error(`MPStoLP: ` + i3 + `
Line ` + a2 + `: ` + t4[a2] + `
Current state: ` + r2[n2] + `
`);
    }
    for (a2 = 0; a2 < i2; ++a2) {
      s2 = t4[a2];
      var x2 = s2.match(/\S*/g), S2 = [];
      for (o2 = 0; o2 < x2.length; ++o2) x2[o2] !== `` && S2.push(x2[o2]);
      if (S2.length !== 0) {
        for (o2 = 0; o2 < r2.length && s2.substr(0, r2[o2].length) !== r2[o2]; ++o2) ;
        if (o2 < r2.length) {
          if (n2 = o2, o2 === 1 && (h2 = S2[1]), o2 === 6) return { name: h2, c: g2, A: numeric.transpose(v2), b: y2, rows: l2, vars: p2 };
          continue;
        }
        switch (n2) {
          case 0:
          case 1:
            b2(`Unexpected line`);
          case 2:
            switch (S2[0]) {
              case `N`:
                c2 === 0 ? c2 = S2[1] : b2(`Two or more N rows`);
                break;
              case `L`:
                l2[S2[1]] = d2, u2[d2] = 1, y2[d2] = 0, ++d2;
                break;
              case `G`:
                l2[S2[1]] = d2, u2[d2] = -1, y2[d2] = 0, ++d2;
                break;
              case `E`:
                l2[S2[1]] = d2, u2[d2] = 0, y2[d2] = 0, ++d2;
                break;
              default:
                b2(`Parse error ` + numeric.prettyPrint(S2));
            }
            break;
          case 3:
            p2.hasOwnProperty(S2[0]) || (p2[S2[0]] = m2, g2[m2] = 0, v2[m2] = numeric.rep([d2], 0), ++m2);
            var w2 = p2[S2[0]];
            for (o2 = 1; o2 < S2.length; o2 += 2) {
              if (S2[o2] === c2) {
                g2[w2] = parseFloat(S2[o2 + 1]);
                continue;
              }
              var E = l2[S2[o2]];
              v2[w2][E] = (u2[E] < 0 ? -1 : 1) * parseFloat(S2[o2 + 1]);
            }
            break;
          case 4:
            for (o2 = 1; o2 < S2.length; o2 += 2) y2[l2[S2[o2]]] = (u2[l2[S2[o2]]] < 0 ? -1 : 1) * parseFloat(S2[o2 + 1]);
            break;
          case 5:
            break;
          case 6:
            b2(`Internal error`);
        }
      }
    }
    b2(`Reached end of file without ENDATA`);
  }, numeric.seedrandom = { pow: Math.pow, random: Math.random }, (function(t4, n2, r2, i2, a2, o2, s2) {
    n2.seedrandom = function(d3, p2) {
      var m2 = [], h2;
      return d3 = u2(l2(p2 ? [d3, t4] : arguments.length ? d3 : [(/* @__PURE__ */ new Date()).getTime(), t4, window], 3), m2), h2 = new c2(m2), u2(h2.S, t4), n2.random = function() {
        for (var t5 = h2.g(i2), n3 = s2, c3 = 0; t5 < a2; ) t5 = (t5 + c3) * r2, n3 *= r2, c3 = h2.g(1);
        for (; t5 >= o2; ) t5 /= 2, n3 /= 2, c3 >>>= 1;
        return (t5 + c3) / n3;
      }, d3;
    };
    function c2(t5) {
      var n3, i3, a3 = this, o3 = t5.length, s3 = 0, c3 = a3.i = a3.j = a3.m = 0;
      for (a3.S = [], a3.c = [], o3 || (t5 = [o3++]); s3 < r2; ) a3.S[s3] = s3++;
      for (s3 = 0; s3 < r2; s3++) n3 = a3.S[s3], c3 = d2(c3 + n3 + t5[s3 % o3]), i3 = a3.S[c3], a3.S[s3] = i3, a3.S[c3] = n3;
      a3.g = function(t6) {
        var n4 = a3.S, i4 = d2(a3.i + 1), o4 = n4[i4], s4 = d2(a3.j + o4), c4 = n4[s4];
        n4[i4] = c4, n4[s4] = o4;
        for (var l3 = n4[d2(o4 + c4)]; --t6; ) i4 = d2(i4 + 1), o4 = n4[i4], s4 = d2(s4 + o4), c4 = n4[s4], n4[i4] = c4, n4[s4] = o4, l3 = l3 * r2 + n4[d2(o4 + c4)];
        return a3.i = i4, a3.j = s4, l3;
      }, a3.g(r2);
    }
    function l2(t5, n3, r3, i3, a3) {
      if (r3 = [], a3 = typeof t5, n3 && a3 == `object`) {
        for (i3 in t5) if (i3.indexOf(`S`) < 5) try {
          r3.push(l2(t5[i3], n3 - 1));
        } catch {
        }
      }
      return r3.length ? r3 : t5 + (a3 == `string` ? `` : `\0`);
    }
    function u2(t5, n3, r3, i3) {
      for (t5 += ``, r3 = 0, i3 = 0; i3 < t5.length; i3++) n3[d2(i3)] = d2((r3 ^= n3[d2(i3)] * 19) + t5.charCodeAt(i3));
      for (i3 in t5 = ``, n3) t5 += String.fromCharCode(n3[i3]);
      return t5;
    }
    function d2(t5) {
      return t5 & r2 - 1;
    }
    s2 = n2.pow(r2, i2), a2 = n2.pow(2, a2), o2 = a2 * 2, u2(n2.random(), t4);
  })([], numeric.seedrandom, 256, 6, 52), (function(t4) {
    function n2(t5) {
      if (typeof t5 != `object`) return t5;
      var r3 = [], i3, a3 = t5.length;
      for (i3 = 0; i3 < a3; i3++) r3[i3 + 1] = n2(t5[i3]);
      return r3;
    }
    function r2(t5) {
      if (typeof t5 != `object`) return t5;
      var n3 = [], i3, a3 = t5.length;
      for (i3 = 1; i3 < a3; i3++) n3[i3 - 1] = r2(t5[i3]);
      return n3;
    }
    function i2(t5, n3, r3) {
      var i3, a3, o3, s3, c3;
      for (o3 = 1; o3 <= r3; o3 += 1) {
        for (t5[o3][o3] = 1 / t5[o3][o3], c3 = -t5[o3][o3], i3 = 1; i3 < o3; i3 += 1) t5[i3][o3] = c3 * t5[i3][o3];
        if (s3 = o3 + 1, r3 < s3) break;
        for (a3 = s3; a3 <= r3; a3 += 1) for (c3 = t5[o3][a3], t5[o3][a3] = 0, i3 = 1; i3 <= o3; i3 += 1) t5[i3][a3] = t5[i3][a3] + c3 * t5[i3][o3];
      }
    }
    function a2(t5, n3, r3, i3) {
      var a3, o3, s3, c3;
      for (o3 = 1; o3 <= r3; o3 += 1) {
        for (c3 = 0, a3 = 1; a3 < o3; a3 += 1) c3 += t5[a3][o3] * i3[a3];
        i3[o3] = (i3[o3] - c3) / t5[o3][o3];
      }
      for (s3 = 1; s3 <= r3; s3 += 1) for (o3 = r3 + 1 - s3, i3[o3] = i3[o3] / t5[o3][o3], c3 = -i3[o3], a3 = 1; a3 < o3; a3 += 1) i3[a3] = i3[a3] + c3 * t5[a3][o3];
    }
    function o2(t5, n3, r3, i3) {
      var a3, o3, s3, c3, l2, u2;
      for (o3 = 1; o3 <= r3; o3 += 1) {
        if (i3[1] = o3, u2 = 0, s3 = o3 - 1, s3 < 1) {
          if (u2 = t5[o3][o3] - u2, u2 <= 0) break;
          t5[o3][o3] = Math.sqrt(u2);
        } else {
          for (c3 = 1; c3 <= s3; c3 += 1) {
            for (l2 = t5[c3][o3], a3 = 1; a3 < c3; a3 += 1) l2 -= t5[a3][o3] * t5[a3][c3];
            l2 /= t5[c3][c3], t5[c3][o3] = l2, u2 += l2 * l2;
          }
          if (u2 = t5[o3][o3] - u2, u2 <= 0) break;
          t5[o3][o3] = Math.sqrt(u2);
        }
        i3[1] = 0;
      }
    }
    function s2(t5, n3, r3, s3, c3, l2, u2, d2, p2, m2, h2, g2, v2, y2, b2, x2) {
      var S2, w2, E, D, O, j2, M2, N2, F, I, L, R, z = Math.min(s3, m2), V, H2, U, W, G, K, q, J, ee, Y, te, ne, X, ie;
      E = 2 * s3 + z * (z + 5) / 2 + 2 * m2 + 1, te = 1e-60;
      do
        te += te, ne = 1 + 0.1 * te, X = 1 + 0.2 * te;
      while (ne <= 1 || X <= 1);
      for (S2 = 1; S2 <= s3; S2 += 1) b2[S2] = n3[S2];
      for (S2 = s3 + 1; S2 <= E; S2 += 1) b2[S2] = 0;
      for (S2 = 1; S2 <= m2; S2 += 1) g2[S2] = 0;
      if (O = [], x2[1] === 0) {
        if (o2(t5, r3, s3, O), O[1] !== 0) {
          x2[1] = 2;
          return;
        }
        a2(t5, r3, s3, n3), i2(t5, r3, s3);
      } else {
        for (w2 = 1; w2 <= s3; w2 += 1) for (c3[w2] = 0, S2 = 1; S2 <= w2; S2 += 1) c3[w2] = c3[w2] + t5[S2][w2] * n3[S2];
        for (w2 = 1; w2 <= s3; w2 += 1) for (n3[w2] = 0, S2 = w2; S2 <= s3; S2 += 1) n3[w2] = n3[w2] + t5[w2][S2] * c3[S2];
      }
      for (l2[1] = 0, w2 = 1; w2 <= s3; w2 += 1) for (c3[w2] = n3[w2], l2[1] += b2[w2] * c3[w2], b2[w2] = 0, S2 = w2 + 1; S2 <= s3; S2 += 1) t5[S2][w2] = 0;
      for (l2[1] = -l2[1] / 2, x2[1] = 0, M2 = s3, N2 = M2 + s3, L = N2 + z, F = L + z + 1, I = F + z * (z + 1) / 2, V = I + m2, S2 = 1; S2 <= m2; S2 += 1) {
        for (U = 0, w2 = 1; w2 <= s3; w2 += 1) U += u2[w2][S2] * u2[w2][S2];
        b2[V + S2] = Math.sqrt(U);
      }
      v2 = 0, y2[1] = 0, y2[2] = 0;
      function ae() {
        for (y2[1] += 1, E = I, S2 = 1; S2 <= m2; S2 += 1) {
          for (E += 1, U = -d2[S2], w2 = 1; w2 <= s3; w2 += 1) U += u2[w2][S2] * c3[w2];
          if (Math.abs(U) < te && (U = 0), S2 > h2) b2[E] = U;
          else if (b2[E] = -Math.abs(U), U > 0) {
            for (w2 = 1; w2 <= s3; w2 += 1) u2[w2][S2] = -u2[w2][S2];
            d2[S2] = -d2[S2];
          }
        }
        for (S2 = 1; S2 <= v2; S2 += 1) b2[I + g2[S2]] = 0;
        for (R = 0, H2 = 0, S2 = 1; S2 <= m2; S2 += 1) b2[I + S2] < H2 * b2[V + S2] && (R = S2, H2 = b2[I + S2] / b2[V + S2]);
        return R === 0 ? 999 : 0;
      }
      function Z() {
        for (S2 = 1; S2 <= s3; S2 += 1) {
          for (U = 0, w2 = 1; w2 <= s3; w2 += 1) U += t5[w2][S2] * u2[w2][R];
          b2[S2] = U;
        }
        for (D = M2, S2 = 1; S2 <= s3; S2 += 1) b2[D + S2] = 0;
        for (w2 = v2 + 1; w2 <= s3; w2 += 1) for (S2 = 1; S2 <= s3; S2 += 1) b2[D + S2] = b2[D + S2] + t5[S2][w2] * b2[w2];
        for (ee = true, S2 = v2; S2 >= 1; --S2) {
          for (U = b2[S2], E = F + S2 * (S2 + 3) / 2, D = E - S2, w2 = S2 + 1; w2 <= v2; w2 += 1) U -= b2[E] * b2[N2 + w2], E += w2;
          if (U /= b2[D], b2[N2 + S2] = U, g2[S2] < h2 || U < 0) break;
          ee = false, j2 = S2;
        }
        if (!ee) for (W = b2[L + j2] / b2[N2 + j2], S2 = 1; S2 <= v2 && !(g2[S2] < h2 || b2[N2 + S2] < 0); S2 += 1) H2 = b2[L + S2] / b2[N2 + S2], H2 < W && (W = H2, j2 = S2);
        for (U = 0, S2 = M2 + 1; S2 <= M2 + s3; S2 += 1) U += b2[S2] * b2[S2];
        if (Math.abs(U) <= te) {
          if (ee) return x2[1] = 1, 999;
          for (S2 = 1; S2 <= v2; S2 += 1) b2[L + S2] = b2[L + S2] - W * b2[N2 + S2];
          return b2[L + v2 + 1] = b2[L + v2 + 1] + W, 700;
        } else {
          for (U = 0, S2 = 1; S2 <= s3; S2 += 1) U += b2[M2 + S2] * u2[S2][R];
          for (G = -b2[I + R] / U, Y = true, ee || W < G && (G = W, Y = false), S2 = 1; S2 <= s3; S2 += 1) c3[S2] = c3[S2] + G * b2[M2 + S2], Math.abs(c3[S2]) < te && (c3[S2] = 0);
          for (l2[1] += G * U * (G / 2 + b2[L + v2 + 1]), S2 = 1; S2 <= v2; S2 += 1) b2[L + S2] = b2[L + S2] - G * b2[N2 + S2];
          if (b2[L + v2 + 1] = b2[L + v2 + 1] + G, Y) {
            for (v2 += 1, g2[v2] = R, E = F + (v2 - 1) * v2 / 2 + 1, S2 = 1; S2 <= v2 - 1; S2 += 1) b2[E] = b2[S2], E += 1;
            if (v2 === s3) b2[E] = b2[s3];
            else {
              for (S2 = s3; S2 >= v2 + 1 && !(b2[S2] === 0 || (K = Math.max(Math.abs(b2[S2 - 1]), Math.abs(b2[S2])), q = Math.min(Math.abs(b2[S2 - 1]), Math.abs(b2[S2])), H2 = b2[S2 - 1] >= 0 ? Math.abs(K * Math.sqrt(1 + q * q / (K * K))) : -Math.abs(K * Math.sqrt(1 + q * q / (K * K))), K = b2[S2 - 1] / H2, q = b2[S2] / H2, K === 1)); --S2) if (K === 0) for (b2[S2 - 1] = q * H2, w2 = 1; w2 <= s3; w2 += 1) H2 = t5[w2][S2 - 1], t5[w2][S2 - 1] = t5[w2][S2], t5[w2][S2] = H2;
              else for (b2[S2 - 1] = H2, J = q / (1 + K), w2 = 1; w2 <= s3; w2 += 1) H2 = K * t5[w2][S2 - 1] + q * t5[w2][S2], t5[w2][S2] = J * (t5[w2][S2 - 1] + H2) - t5[w2][S2], t5[w2][S2 - 1] = H2;
              b2[E] = b2[v2];
            }
          } else {
            for (U = -d2[R], w2 = 1; w2 <= s3; w2 += 1) U += c3[w2] * u2[w2][R];
            if (R > h2) b2[I + R] = U;
            else if (b2[I + R] = -Math.abs(U), U > 0) {
              for (w2 = 1; w2 <= s3; w2 += 1) u2[w2][R] = -u2[w2][R];
              d2[R] = -d2[R];
            }
            return 700;
          }
        }
        return 0;
      }
      function oe() {
        if (E = F + j2 * (j2 + 1) / 2 + 1, D = E + j2, b2[D] === 0 || (K = Math.max(Math.abs(b2[D - 1]), Math.abs(b2[D])), q = Math.min(Math.abs(b2[D - 1]), Math.abs(b2[D])), H2 = b2[D - 1] >= 0 ? Math.abs(K * Math.sqrt(1 + q * q / (K * K))) : -Math.abs(K * Math.sqrt(1 + q * q / (K * K))), K = b2[D - 1] / H2, q = b2[D] / H2, K === 1)) return 798;
        if (K === 0) {
          for (S2 = j2 + 1; S2 <= v2; S2 += 1) H2 = b2[D - 1], b2[D - 1] = b2[D], b2[D] = H2, D += S2;
          for (S2 = 1; S2 <= s3; S2 += 1) H2 = t5[S2][j2], t5[S2][j2] = t5[S2][j2 + 1], t5[S2][j2 + 1] = H2;
        } else {
          for (J = q / (1 + K), S2 = j2 + 1; S2 <= v2; S2 += 1) H2 = K * b2[D - 1] + q * b2[D], b2[D] = J * (b2[D - 1] + H2) - b2[D], b2[D - 1] = H2, D += S2;
          for (S2 = 1; S2 <= s3; S2 += 1) H2 = K * t5[S2][j2] + q * t5[S2][j2 + 1], t5[S2][j2 + 1] = J * (t5[S2][j2] + H2) - t5[S2][j2 + 1], t5[S2][j2] = H2;
        }
        return 0;
      }
      function $2() {
        for (D = E - j2, S2 = 1; S2 <= j2; S2 += 1) b2[D] = b2[E], E += 1, D += 1;
        return b2[L + j2] = b2[L + j2 + 1], g2[j2] = g2[j2 + 1], j2 += 1, j2 < v2 ? 797 : 0;
      }
      function se() {
        return b2[L + v2] = b2[L + v2 + 1], b2[L + v2 + 1] = 0, g2[v2] = 0, --v2, y2[2] += 1, 0;
      }
      for (ie = 0; ; ) {
        if (ie = ae(), ie === 999) return;
        for (; ie = Z(), ie !== 0; ) {
          if (ie === 999) return;
          if (ie === 700) if (j2 === v2) se();
          else {
            for (; oe(), ie = $2(), ie === 797; ) ;
            se();
          }
        }
      }
    }
    function c2(t5, i3, a3, o3, c3, l2) {
      t5 = n2(t5), i3 = n2(i3), a3 = n2(a3);
      var u2, d2, p2, m2, h2, g2 = [], v2 = [], y2 = [], b2 = [], x2 = [], S2;
      if (c3 ||= 0, l2 = l2 ? n2(l2) : [void 0, 0], o3 = o3 ? n2(o3) : [], d2 = t5.length - 1, p2 = a3[1].length - 1, !o3) for (u2 = 1; u2 <= p2; u2 += 1) o3[u2] = 0;
      for (u2 = 1; u2 <= p2; u2 += 1) v2[u2] = 0;
      for (m2 = 0, h2 = Math.min(d2, p2), u2 = 1; u2 <= d2; u2 += 1) y2[u2] = 0;
      for (g2[1] = 0, u2 = 1; u2 <= 2 * d2 + h2 * (h2 + 5) / 2 + 2 * p2 + 1; u2 += 1) b2[u2] = 0;
      for (u2 = 1; u2 <= 2; u2 += 1) x2[u2] = 0;
      return s2(t5, i3, d2, d2, y2, g2, a3, o3, d2, p2, c3, v2, m2, x2, b2, l2), S2 = ``, l2[1] === 1 && (S2 = `constraints are inconsistent, no solution!`), l2[1] === 2 && (S2 = `matrix D in quadratic function is not positive definite!`), { solution: r2(y2), value: r2(g2), unconstrained_solution: r2(i3), iterations: r2(x2), iact: r2(v2), message: S2 };
    }
    t4.solveQP = c2;
  })(numeric), numeric.svd = function(t4) {
    var n2, r2 = numeric.epsilon, i2 = 1e-64 / r2, a2 = 50, o2 = 0, s2 = 0, c2 = 0, l2 = 0, u2 = 0, d2 = numeric.clone(t4), p2 = d2.length, m2 = d2[0].length;
    if (p2 < m2) throw `Need more rows than columns`;
    var h2 = Array(m2), g2 = Array(m2);
    for (s2 = 0; s2 < m2; s2++) h2[s2] = g2[s2] = 0;
    var v2 = numeric.rep([m2, m2], 0);
    function y2(t5, n3) {
      return t5 = Math.abs(t5), n3 = Math.abs(n3), t5 > n3 ? t5 * Math.sqrt(1 + n3 * n3 / t5 / t5) : n3 == 0 ? t5 : n3 * Math.sqrt(1 + t5 * t5 / n3 / n3);
    }
    var b2 = 0, x2 = 0, S2 = 0, w2 = 0, E = 0, D = 0, O = 0;
    for (s2 = 0; s2 < m2; s2++) {
      for (h2[s2] = x2, O = 0, u2 = s2 + 1, c2 = s2; c2 < p2; c2++) O += d2[c2][s2] * d2[c2][s2];
      if (O <= i2) x2 = 0;
      else for (b2 = d2[s2][s2], x2 = Math.sqrt(O), b2 >= 0 && (x2 = -x2), S2 = b2 * x2 - O, d2[s2][s2] = b2 - x2, c2 = u2; c2 < m2; c2++) {
        for (O = 0, l2 = s2; l2 < p2; l2++) O += d2[l2][s2] * d2[l2][c2];
        for (b2 = O / S2, l2 = s2; l2 < p2; l2++) d2[l2][c2] += b2 * d2[l2][s2];
      }
      for (g2[s2] = x2, O = 0, c2 = u2; c2 < m2; c2++) O += d2[s2][c2] * d2[s2][c2];
      if (O <= i2) x2 = 0;
      else {
        for (b2 = d2[s2][s2 + 1], x2 = Math.sqrt(O), b2 >= 0 && (x2 = -x2), S2 = b2 * x2 - O, d2[s2][s2 + 1] = b2 - x2, c2 = u2; c2 < m2; c2++) h2[c2] = d2[s2][c2] / S2;
        for (c2 = u2; c2 < p2; c2++) {
          for (O = 0, l2 = u2; l2 < m2; l2++) O += d2[c2][l2] * d2[s2][l2];
          for (l2 = u2; l2 < m2; l2++) d2[c2][l2] += O * h2[l2];
        }
      }
      E = Math.abs(g2[s2]) + Math.abs(h2[s2]), E > w2 && (w2 = E);
    }
    for (s2 = m2 - 1; s2 != -1; s2 += -1) {
      if (x2 != 0) {
        for (S2 = x2 * d2[s2][s2 + 1], c2 = u2; c2 < m2; c2++) v2[c2][s2] = d2[s2][c2] / S2;
        for (c2 = u2; c2 < m2; c2++) {
          for (O = 0, l2 = u2; l2 < m2; l2++) O += d2[s2][l2] * v2[l2][c2];
          for (l2 = u2; l2 < m2; l2++) v2[l2][c2] += O * v2[l2][s2];
        }
      }
      for (c2 = u2; c2 < m2; c2++) v2[s2][c2] = 0, v2[c2][s2] = 0;
      v2[s2][s2] = 1, x2 = h2[s2], u2 = s2;
    }
    for (s2 = m2 - 1; s2 != -1; s2 += -1) {
      for (u2 = s2 + 1, x2 = g2[s2], c2 = u2; c2 < m2; c2++) d2[s2][c2] = 0;
      if (x2 != 0) {
        for (S2 = d2[s2][s2] * x2, c2 = u2; c2 < m2; c2++) {
          for (O = 0, l2 = u2; l2 < p2; l2++) O += d2[l2][s2] * d2[l2][c2];
          for (b2 = O / S2, l2 = s2; l2 < p2; l2++) d2[l2][c2] += b2 * d2[l2][s2];
        }
        for (c2 = s2; c2 < p2; c2++) d2[c2][s2] = d2[c2][s2] / x2;
      } else for (c2 = s2; c2 < p2; c2++) d2[c2][s2] = 0;
      d2[s2][s2] += 1;
    }
    for (r2 *= w2, l2 = m2 - 1; l2 != -1; l2 += -1) for (var j2 = 0; j2 < a2; j2++) {
      var M2 = false;
      for (u2 = l2; u2 != -1; u2 += -1) {
        if (Math.abs(h2[u2]) <= r2) {
          M2 = true;
          break;
        }
        if (Math.abs(g2[u2 - 1]) <= r2) break;
      }
      if (!M2) {
        o2 = 0, O = 1;
        var N2 = u2 - 1;
        for (s2 = u2; s2 < l2 + 1 && (b2 = O * h2[s2], h2[s2] = o2 * h2[s2], !(Math.abs(b2) <= r2)); s2++) for (x2 = g2[s2], S2 = y2(b2, x2), g2[s2] = S2, o2 = x2 / S2, O = -b2 / S2, c2 = 0; c2 < p2; c2++) E = d2[c2][N2], D = d2[c2][s2], d2[c2][N2] = E * o2 + D * O, d2[c2][s2] = -E * O + D * o2;
      }
      if (D = g2[l2], u2 == l2) {
        if (D < 0) for (g2[l2] = -D, c2 = 0; c2 < m2; c2++) v2[c2][l2] = -v2[c2][l2];
        break;
      }
      if (j2 >= a2 - 1) throw `Error: no convergence.`;
      for (w2 = g2[u2], E = g2[l2 - 1], x2 = h2[l2 - 1], S2 = h2[l2], b2 = ((E - D) * (E + D) + (x2 - S2) * (x2 + S2)) / (2 * S2 * E), x2 = y2(b2, 1), b2 = b2 < 0 ? ((w2 - D) * (w2 + D) + S2 * (E / (b2 - x2) - S2)) / w2 : ((w2 - D) * (w2 + D) + S2 * (E / (b2 + x2) - S2)) / w2, o2 = 1, O = 1, s2 = u2 + 1; s2 < l2 + 1; s2++) {
        for (x2 = h2[s2], E = g2[s2], S2 = O * x2, x2 = o2 * x2, D = y2(b2, S2), h2[s2 - 1] = D, o2 = b2 / D, O = S2 / D, b2 = w2 * o2 + x2 * O, x2 = -w2 * O + x2 * o2, S2 = E * O, E *= o2, c2 = 0; c2 < m2; c2++) w2 = v2[c2][s2 - 1], D = v2[c2][s2], v2[c2][s2 - 1] = w2 * o2 + D * O, v2[c2][s2] = -w2 * O + D * o2;
        for (D = y2(b2, S2), g2[s2 - 1] = D, o2 = b2 / D, O = S2 / D, b2 = o2 * x2 + O * E, w2 = -O * x2 + o2 * E, c2 = 0; c2 < p2; c2++) E = d2[c2][s2 - 1], D = d2[c2][s2], d2[c2][s2 - 1] = E * o2 + D * O, d2[c2][s2] = -E * O + D * o2;
      }
      h2[u2] = 0, h2[l2] = b2, g2[l2] = w2;
    }
    for (s2 = 0; s2 < g2.length; s2++) g2[s2] < r2 && (g2[s2] = 0);
    for (s2 = 0; s2 < m2; s2++) for (c2 = s2 - 1; c2 >= 0; c2--) if (g2[c2] < g2[s2]) {
      for (o2 = g2[c2], g2[c2] = g2[s2], g2[s2] = o2, l2 = 0; l2 < d2.length; l2++) n2 = d2[l2][s2], d2[l2][s2] = d2[l2][c2], d2[l2][c2] = n2;
      for (l2 = 0; l2 < v2.length; l2++) n2 = v2[l2][s2], v2[l2][s2] = v2[l2][c2], v2[l2][c2] = n2;
      s2 = c2;
    }
    return { U: d2, S: g2, V: v2 };
  };
})), import_numeric_1_2_6 = __toESM(require_numeric_1_2_6());
globalThis.numeric = import_numeric_1_2_6.default;
function createInstance({ define_e: t4 = true, define_pi: n2 = true, define_i: r2 = true, pow_strict: i2 = true } = {}) {
  let a2 = { ...all };
  delete a2.createTau, delete a2.createPhi, t4 || delete a2.createE, n2 || delete a2.createPi, r2 || delete a2.createI;
  let o2 = create$1(a2);
  o2.import(import_numeric_1_2_6.default, { wrap: true, silent: true });
  var s2 = o2.pow;
  function c2(t5, n3) {
    return n3 === 0 && typeof t5 == `number` && (t5 === 0 || !Number.isFinite(t5)) ? NaN : s2(t5, n3);
  }
  return i2 ? o2.import({ pow: c2 }, { override: true }) : o2.import({ pow: s2 }, { override: true }), o2.define_e = t4, o2.define_pi = n2, o2.define_i = r2, o2.pow_strict = i2, o2;
}
var mathjs_default = createInstance(), function_normalizations = { ln: `log`, arccos: `acos`, arccosh: `acosh`, arcsin: `asin`, arcsinh: `asinh`, arctan: `atan`, arctanh: `atanh`, arcsec: `asec`, arcsech: `asech`, arccsc: `acsc`, arccsch: `acsch`, arccot: `acot`, arccoth: `acoth`, cosec: `csc` }, create_trig_inverses_for = [`cos`, `cosh`, `sin`, `sinh`, `tan`, `tanh`, `sec`, `sech`, `csc`, `csch`, `cot`, `coth`], move_exponents_outside_for = [`cos`, `cosh`, `sin`, `sinh`, `tan`, `tanh`, `sec`, `sech`, `csc`, `csch`, `cot`, `coth`, `log`, `ln`];
function normalize_function_names(t4) {
  var n2 = get_tree(t4);
  if (!Array.isArray(n2)) return n2;
  var r2 = n2[0], i2 = n2.slice(1);
  if (r2 === `apply`) {
    if (i2[0] === `sqrt`) return [`^`, normalize_function_names(i2[1]), 0.5];
    if (i2[0] === `cbrt`) return [`^`, normalize_function_names(i2[1]), [`/`, 1, 3]];
    if (i2[0] === `nthroot`) return i2.length !== 2 || i2[1][0] !== `tuple` || i2[1].length !== 3 ? [`^`, normalize_function_names(i2[1]), 0.5] : [`^`, normalize_function_names(i2[1][1]), [`/`, 1, normalize_function_names(i2[1][2])]];
    var a2 = normalize_function_names_sub(i2[0]);
    a2 = [`apply`, a2];
    var o2 = i2.slice(1).map(function(t5) {
      return normalize_function_names(t5);
    });
    return o2 = o2.length > 1 ? [`tuple`].concat(o2) : o2[0], a2.push(o2), a2;
  }
  return r2 === `^` && i2[0] === `e` && mathjs_default.define_e ? [`apply`, `exp`, normalize_function_names(i2[1])] : r2 === `binom` ? [`apply`, `nCr`, [`tuple`, normalize_function_names(i2[0]), normalize_function_names(i2[1])]] : [r2].concat(i2.map(function(t5) {
    return normalize_function_names(t5);
  }));
}
function normalize_function_names_sub(t4) {
  if (typeof t4 == `string`) return t4 in function_normalizations ? function_normalizations[t4] : t4;
  if (!Array.isArray(t4)) return t4;
  var n2 = t4[0], r2 = t4.slice(1);
  if (n2 === `^` && r2.length === 2 && r2[1] === -1) {
    let t5 = normalize_function_names_sub(r2[0]);
    if (create_trig_inverses_for.includes(t5)) return `a` + t5;
  }
  return [n2].concat(r2.map(function(t5) {
    return normalize_function_names_sub(t5);
  }));
}
function normalize_applied_functions(t4) {
  var n2 = get_tree(t4);
  if (!Array.isArray(n2)) return n2;
  var r2 = n2[0], i2 = n2.slice(1);
  if (r2 === `apply`) {
    let t5 = i2[0];
    if (t5[0] === `^`) {
      let n4 = t5[1], r4 = t5[2];
      return r4 !== -1 && move_exponents_outside_for.includes(n4) ? [`^`, [`apply`, n4, normalize_applied_functions(i2[1])], r4] : [`apply`, t5, normalize_applied_functions(i2[1])];
    }
    let n3 = 0;
    for (; t5[0] === `prime`; ) n3 += 1, t5 = t5[1];
    let r3 = [`apply`, t5, normalize_applied_functions(i2[1])];
    for (let t6 = 0; t6 < n3; t6++) r3 = [`prime`, r3];
    return r3;
  }
  return [r2].concat(i2.map(function(t5, n3) {
    return normalize_applied_functions(t5);
  }));
}
function log_subscript_to_two_arg_log(t4) {
  var n2 = get_tree(t4);
  if (!Array.isArray(n2)) return n2;
  let r2 = n2[0], i2 = n2.slice(1);
  if (r2 === `apply` && i2.length === 2) {
    let t5 = i2[0];
    if (Array.isArray(t5) && t5.length === 3 && t5[0] === `_` && t5[1] === `log`) return [`apply`, `log`, [`tuple`, i2[1], t5[2]]];
  }
  return [r2, ...i2.map(log_subscript_to_two_arg_log)];
}
function substitute_abs(t4) {
  var n2 = get_tree(t4);
  if (!Array.isArray(n2)) return n2;
  var r2 = n2[0], i2 = n2.slice(1);
  return r2 === `apply` && i2[0] === `abs` ? [`^`, [`^`, substitute_abs(i2[1]), 2], 0.5] : [r2].concat(i2.map(function(t5) {
    return substitute_abs(t5);
  }));
}
function constants_to_floats(t4) {
  var n2 = get_tree(t4);
  if (!(mathjs_default.define_e || mathjs_default.define_pi)) return n2;
  if (typeof n2 == `string`) {
    if (n2 === `e`) {
      if (mathjs_default.define_e) return mathjs_default.e;
    } else if (n2 === `pi` && mathjs_default.define_pi) return mathjs_default.pi;
    return n2;
  }
  if (!Array.isArray(n2)) return n2;
  let r2 = n2[0], i2 = n2.slice(1);
  return r2 === `^` && i2[0] === `e` ? [`^`, `e`, constants_to_floats(i2[1])] : [r2, ...i2.map(constants_to_floats)];
}
function normalize_angle_linesegment_arg_order(t4) {
  var n2 = get_tree(t4);
  if (!Array.isArray(n2)) return n2;
  let r2 = n2[0], i2 = n2.slice(1);
  return r2 === `angle` && i2.length === 3 && compare_function(i2[0], i2[2]) === 1 || r2 === `linesegment` && i2.length === 2 && compare_function(i2[0], i2[1]) === 1 ? (i2.reverse(), [r2, ...i2]) : [r2, ...i2.map(normalize_angle_linesegment_arg_order)];
}
function normalize_negative_numbers(t4) {
  var n2 = get_tree(t4);
  function r2(t5) {
    if (typeof t5 == `number` && t5 >= 0) return [-t5, true];
    if (!Array.isArray(t5)) return [t5, false];
    if (t5[0] === `*` && typeof t5[1] == `number` && t5[1] >= 0) return [[`*`, -t5[1], ...t5.slice(2).map(normalize_negative_numbers)], true];
    if (t5[0] === `/` && t5.length === 3) {
      let [n3, i3] = r2(t5[1]);
      if (i3) return [[`/`, n3, normalize_negative_numbers(t5[2])], true];
    }
    return [t5, false];
  }
  if (!Array.isArray(n2)) return n2;
  let i2 = n2[0], a2 = n2.slice(1);
  if (i2 === `-` && a2.length === 1) {
    if (typeof a2[0] == `number` && a2[0] >= 0) return -a2[0];
    if (Array.isArray(a2[0]) && a2[0][0] === `*`) {
      let [t5, n3] = r2(a2[0][1]);
      if (n3) return [`*`, t5, ...a2[0].slice(2).map(normalize_negative_numbers)];
    }
    if (Array.isArray(a2[0]) && a2[0][0] === `/` && a2[0].length === 3) {
      let [t5, n3] = r2(a2[0][1]);
      if (n3) return [`/`, t5, normalize_negative_numbers(a2[0][2])];
    }
  }
  return [i2, ...a2.map(normalize_negative_numbers)];
}
function tuples_to_vectors(t4) {
  var n2 = get_tree(t4);
  if (!Array.isArray(n2)) return n2;
  var r2 = n2[0], i2 = n2.slice(1);
  if (r2 === `tuple`) return [`vector`].concat(i2.map(function(t5, n3) {
    return tuples_to_vectors(t5);
  }));
  if (r2 === `apply`) {
    if (i2[1][0] === `tuple`) {
      let t5 = tuples_to_vectors(i2[0]), n3 = i2[1].slice(1);
      return [`apply`, t5, [`tuple`].concat(n3.map(function(t6, n4) {
        return tuples_to_vectors(t6);
      }))];
    }
  } else if (r2 === `gts` || r2 === `lts` || r2 === `interval`) {
    let t5 = i2[0], n3 = i2[1];
    if (t5[0] !== `tuple` || n3[0] !== `tuple`) throw Error(`Badly formed ast`);
    return [r2, [`tuple`].concat(t5.slice(1).map(function(t6, n4) {
      return tuples_to_vectors(t6);
    })), n3];
  } else if (r2 === `matrix`) {
    let t5 = i2[0], n3 = i2[1];
    return t5 = [`tuple`].concat(t5.slice(1).map(tuples_to_vectors)), n3 = [`tuple`].concat(n3.slice(1).map((t6) => [`tuple`].concat(t6.slice(1).map(tuples_to_vectors)))), [`matrix`, t5, n3];
  }
  return [r2].concat(i2.map(function(t5, n3) {
    return tuples_to_vectors(t5);
  }));
}
function to_intervals(t4) {
  var n2 = get_tree(t4);
  if (!Array.isArray(n2)) return n2;
  var r2 = n2[0], i2 = n2.slice(1);
  if (r2 === `tuple` && i2.length === 2) {
    let t5 = [`tuple`].concat(i2.map(function(t6, n3) {
      return to_intervals(t6);
    }));
    return t5 = [`interval`, t5, [`tuple`, false, false]], t5;
  }
  if (r2 === `array` && i2.length === 2) {
    let t5 = [`tuple`].concat(i2.map(function(t6, n3) {
      return to_intervals(t6);
    }));
    return t5 = [`interval`, t5, [`tuple`, true, true]], t5;
  }
  if (r2 === `apply`) {
    if (i2[1][0] === `tuple`) {
      let t5 = to_intervals(i2[0]), n3 = i2[1].slice(1);
      return [`apply`, t5, [`tuple`].concat(n3.map(function(t6, n4) {
        return to_intervals(t6);
      }))];
    }
  } else if (r2 === `gts` || r2 === `lts` || r2 === `interval`) {
    let t5 = i2[0], n3 = i2[1];
    if (t5[0] !== `tuple` || n3[0] !== `tuple`) throw Error(`Badly formed ast`);
    return [r2, [`tuple`].concat(t5.slice(1).map(function(t6, n4) {
      return to_intervals(t6);
    })), n3];
  }
  return [r2].concat(i2.map(function(t5, n3) {
    return to_intervals(t5);
  }));
}
function altvectors_to_vectors(t4) {
  var n2 = get_tree(t4);
  if (!Array.isArray(n2)) return n2;
  var r2 = n2[0], i2 = n2.slice(1);
  return r2 === `altvector` ? [`vector`].concat(i2.map(function(t5, n3) {
    return altvectors_to_vectors(t5);
  })) : [r2].concat(i2.map(function(t5, n3) {
    return altvectors_to_vectors(t5);
  }));
}
function padNumberStringToDigits(t4, n2) {
  let r2 = Math.floor(n2);
  if (r2 > 0) {
    if (t4.includes(`.`)) {
      if (t4[0] === `0` ? r2 += t4.match(/^0\.0*/)[0].length : r2++, t4.length < r2) {
        let n3 = r2 - t4.length;
        t4 += `0`.repeat(n3);
      }
    } else if (t4.length < r2) {
      let n3 = r2 - t4.length;
      t4 += `.` + `0`.repeat(n3);
    }
  }
  return t4;
}
function padNumberStringToDecimals(t4, n2) {
  let r2 = Math.floor(n2);
  if (r2 > 0) if (t4.includes(`.`)) {
    let n3 = t4.match(/\.\d*$/)[0].length - 1;
    if (n3 < r2) {
      let i2 = r2 - n3;
      t4 += `0`.repeat(i2);
    }
  } else t4 += `.` + `0`.repeat(r2);
  return t4;
}
function padNumberStringToDigitsAndDecimals(t4, n2, r2) {
  let i2 = Math.floor(n2);
  if (!(i2 > 0)) return padNumberStringToDecimals(t4, r2);
  let a2 = Math.floor(r2);
  if (!(a2 > 0)) return padNumberStringToDigits(t4, n2);
  if (t4.includes(`.`)) {
    t4[0] === `0` ? i2 += t4.match(/^0\.0*/)[0].length : i2++;
    let n3 = 0;
    t4.length < i2 && (n3 = i2 - t4.length);
    let r3 = t4.match(/\.\d*$/)[0].length - 1;
    r3 < a2 && (n3 = Math.max(n3, a2 - r3)), n3 > 0 && (t4 += `0`.repeat(n3));
  } else {
    let n3 = 0;
    t4.length < i2 && (n3 = i2 - t4.length), n3 = Math.max(n3, a2), t4 += `.` + `0`.repeat(n3);
  }
  return t4;
}
function remove_leading_negative_factor(t4) {
  if (typeof t4 == `number` && t4 < 0) return [-t4, true];
  if (!Array.isArray(t4)) return [t4, false];
  if (t4[0] === `-` && t4.length === 2) return [t4[1], true];
  if (t4[0] === `*` && t4.length >= 2) {
    let n2 = t4[1];
    if (typeof n2 == `number` && n2 < 0) return [[`*`, -n2, ...t4.slice(2)], true];
    if (Array.isArray(n2) && n2[0] === `-` && n2.length === 2) return [[`*`, n2[1], ...t4.slice(2)], true];
  }
  return [t4, false];
}
function normalize_display_negative_fractions_sub(t4, { inside_unary_minus: n2 = false } = {}) {
  if (!Array.isArray(t4)) return t4;
  let r2 = t4[0], i2 = t4.slice(1);
  if (r2 === `-` && i2.length === 1) return [`-`, normalize_display_negative_fractions_sub(i2[0], { inside_unary_minus: true })];
  if (r2 === `+`) return [`+`, ...i2.map((t5) => normalize_display_negative_fractions_sub(t5, { inside_unary_minus: false }))];
  if (r2 === `*`) {
    if (i2.length === 0) return t4;
    let n3 = [normalize_display_negative_fractions_sub(i2[0], { inside_unary_minus: false })];
    for (let t5 = 1; t5 < i2.length; t5 += 1) n3.push(normalize_display_negative_fractions_sub(i2[t5], { inside_unary_minus: false }));
    return [`*`, ...n3];
  }
  if (r2 === `/` && i2.length === 2) {
    let t5 = i2[0], r3 = i2[1];
    if (!n2) {
      let [n3, i3] = remove_leading_negative_factor(t5);
      if (i3) return [`-`, [`/`, normalize_display_negative_fractions_sub(n3, { inside_unary_minus: false }), normalize_display_negative_fractions_sub(r3, { inside_unary_minus: false })]];
    }
    return [`/`, normalize_display_negative_fractions_sub(t5, { inside_unary_minus: false }), normalize_display_negative_fractions_sub(r3, { inside_unary_minus: false })];
  }
  return [r2, ...i2.map((t5) => normalize_display_negative_fractions_sub(t5, { inside_unary_minus: false }))];
}
function normalize_display_negative_fractions(t4) {
  return normalize_display_negative_fractions_sub(t4, { inside_unary_minus: false });
}
function expandScientificNotation(t4) {
  let n2 = t4.indexOf(`e`);
  if (n2 === -1) return t4;
  let r2 = t4.substring(0, n2), i2 = Number(t4.substring(n2 + 1)), a2 = ``;
  (r2[0] === `-` || r2[0] === `+`) && (a2 = r2[0] === `-` ? `-` : ``, r2 = r2.substring(1));
  let o2 = r2.indexOf(`.`);
  o2 === -1 && (o2 = r2.length);
  let s2 = r2.replace(`.`, ``), c2 = o2 + i2;
  return c2 <= 0 ? a2 + `0.` + `0`.repeat(-c2) + s2 : c2 >= s2.length ? a2 + s2 + `0`.repeat(c2 - s2.length) : a2 + s2.substring(0, c2) + `.` + s2.substring(c2);
}
var unicode_operators = { "+": function(t4) {
  return t4.join(` `);
}, "-": function(t4) {
  return `-` + t4[0];
}, pm: function(t4) {
  return `± ` + t4[0];
}, "*": function(t4) {
  return t4.join(` `);
}, "/": function(t4) {
  return t4[0] + `/` + t4[1];
}, _: function(t4) {
  return t4[0] + `_` + t4[1];
}, "^": function(t4) {
  return t4[0] + `^` + t4[1];
}, prime: function(t4) {
  return t4[0] + `'`;
}, tuple: function(t4) {
  return `( ` + t4.join(`, `) + ` )`;
}, array: function(t4) {
  return `[ ` + t4.join(`, `) + ` ]`;
}, list: function(t4) {
  return t4.join(`, `);
}, set: function(t4) {
  return `{ ` + t4.join(`, `) + ` }`;
}, vector: function(t4) {
  return `( ` + t4.join(`, `) + ` )`;
}, altvector: function(t4) {
  return `⟨ ` + t4.join(`, `) + ` ⟩`;
}, interval: function(t4) {
  return `( ` + t4.join(`, `) + ` )`;
}, matrix: function(t4) {
  return `( ` + t4.join(`, `) + ` )`;
}, and: function(t4) {
  return t4.join(` and `);
}, or: function(t4) {
  return t4.join(` or `);
}, not: function(t4) {
  return `not ` + t4[0];
}, "=": function(t4) {
  return t4.join(` = `);
}, "<": function(t4) {
  return t4.join(` < `);
}, ">": function(t4) {
  return t4.join(` > `);
}, lts: function(t4) {
  return t4.join(` < `);
}, gts: function(t4) {
  return t4.join(` > `);
}, le: function(t4) {
  return t4.join(` ≤ `);
}, ge: function(t4) {
  return t4.join(` ≥ `);
}, ne: function(t4) {
  return t4.join(` ≠ `);
}, forall: function(t4) {
  return `∀ ` + t4[0];
}, exists: function(t4) {
  return `∃ ` + t4[0];
}, in: function(t4) {
  return t4[0] + ` ∈ ` + t4[1];
}, notin: function(t4) {
  return t4[0] + ` ∉ ` + t4[1];
}, ni: function(t4) {
  return t4[0] + ` ∋ ` + t4[1];
}, notni: function(t4) {
  return t4[0] + ` ∌ ` + t4[1];
}, subset: function(t4) {
  return t4[0] + ` ⊂ ` + t4[1];
}, subseteq: function(t4) {
  return t4[0] + ` ⊆ ` + t4[1];
}, notsubset: function(t4) {
  return t4[0] + ` ⊄ ` + t4[1];
}, notsubseteq: function(t4) {
  return t4[0] + ` ⊈ ` + t4[1];
}, superset: function(t4) {
  return t4[0] + ` ⊃ ` + t4[1];
}, superseteq: function(t4) {
  return t4[0] + ` ⊇ ` + t4[1];
}, notsuperset: function(t4) {
  return t4[0] + ` ⊅ ` + t4[1];
}, notsuperseteq: function(t4) {
  return t4[0] + ` ⊉ ` + t4[1];
}, union: function(t4) {
  return t4.join(` ∪ `);
}, intersect: function(t4) {
  return t4.join(` ∩ `);
}, rightarrow: function(t4) {
  return t4.join(` → `);
}, leftarrow: function(t4) {
  return t4.join(` ← `);
}, leftrightarrow: function(t4) {
  return t4.join(` ↔ `);
}, implies: function(t4) {
  return t4.join(` ⟹ `);
}, impliedby: function(t4) {
  return t4.join(` ⟸ `);
}, iff: function(t4) {
  return t4.join(` ⟺ `);
}, perp: function(t4) {
  return t4.join(` ⟂ `);
}, parallel: function(t4) {
  return t4.join(` ∥ `);
}, derivative_leibniz: function(t4) {
  return `d` + t4[0] + `/d` + t4[1];
}, partial_derivative_leibniz: function(t4) {
  return `∂` + t4[0] + `/∂` + t4[1];
}, "|": function(t4) {
  return t4[0] + ` | ` + t4[1];
}, ":": function(t4) {
  return t4[0] + ` : ` + t4[1];
}, binom: function(t4) {
  return `binom( ` + t4[0] + `, ` + t4[1] + ` )`;
}, vec: function(t4) {
  return `vec(` + t4[0] + `)`;
}, linesegment: function(t4) {
  return `linesegment( ` + t4.join(`, `) + ` )`;
}, angle: function(t4, n2) {
  return n2 ? `∠` + t4.join(``) : `∠( ` + t4.join(`, `) + ` )`;
}, unit: function(t4) {
  return t4[0] + ` ` + t4[1];
} }, nonunicode_operators = { "+": function(t4) {
  return t4.join(` `);
}, "-": function(t4) {
  return `-` + t4[0];
}, pm: function(t4) {
  return `plusminus ` + t4[0];
}, "*": function(t4) {
  return t4.join(` `);
}, "/": function(t4) {
  return t4[0] + `/` + t4[1];
}, _: function(t4) {
  return t4[0] + `_` + t4[1];
}, "^": function(t4) {
  return t4[0] + `^` + t4[1];
}, prime: function(t4) {
  return t4[0] + `'`;
}, tuple: function(t4) {
  return `( ` + t4.join(`, `) + ` )`;
}, array: function(t4) {
  return `[ ` + t4.join(`, `) + ` ]`;
}, list: function(t4) {
  return t4.join(`, `);
}, set: function(t4) {
  return `{ ` + t4.join(`, `) + ` }`;
}, vector: function(t4) {
  return `( ` + t4.join(`, `) + ` )`;
}, altvector: function(t4) {
  return `( ` + t4.join(`, `) + ` )`;
}, interval: function(t4) {
  return `( ` + t4.join(`, `) + ` )`;
}, matrix: function(t4) {
  return `( ` + t4.join(`, `) + ` )`;
}, and: function(t4) {
  return t4.join(` and `);
}, or: function(t4) {
  return t4.join(` or `);
}, not: function(t4) {
  return `not ` + t4[0];
}, "=": function(t4) {
  return t4.join(` = `);
}, "<": function(t4) {
  return t4.join(` < `);
}, ">": function(t4) {
  return t4.join(` > `);
}, lts: function(t4) {
  return t4.join(` < `);
}, gts: function(t4) {
  return t4.join(` > `);
}, le: function(t4) {
  return t4.join(` <= `);
}, ge: function(t4) {
  return t4.join(` >= `);
}, ne: function(t4) {
  return t4.join(` ne `);
}, forall: function(t4) {
  return `forall ` + t4[0];
}, exists: function(t4) {
  return `exists ` + t4[0];
}, in: function(t4) {
  return t4[0] + ` elementof ` + t4[1];
}, notin: function(t4) {
  return t4[0] + ` notelementof ` + t4[1];
}, ni: function(t4) {
  return t4[0] + ` containselement ` + t4[1];
}, notni: function(t4) {
  return t4[0] + ` notcontainselement ` + t4[1];
}, subset: function(t4) {
  return t4[0] + ` subset ` + t4[1];
}, subseteq: function(t4) {
  return t4[0] + ` subseteq ` + t4[1];
}, notsubset: function(t4) {
  return t4[0] + ` notsubset ` + t4[1];
}, notsubseteq: function(t4) {
  return t4[0] + ` notsubseteq ` + t4[1];
}, superset: function(t4) {
  return t4[0] + ` superset ` + t4[1];
}, superseteq: function(t4) {
  return t4[0] + ` superseteq ` + t4[1];
}, notsuperset: function(t4) {
  return t4[0] + ` notsuperset ` + t4[1];
}, notsuperseteq: function(t4) {
  return t4[0] + ` notsuperseteq ` + t4[1];
}, union: function(t4) {
  return t4.join(` union `);
}, intersect: function(t4) {
  return t4.join(` intersect `);
}, rightarrow: function(t4) {
  return t4.join(` rightarrow `);
}, leftarrow: function(t4) {
  return t4.join(` leftarrow `);
}, leftrightarrow: function(t4) {
  return t4.join(` leftrightarrow `);
}, implies: function(t4) {
  return t4.join(` implies `);
}, impliedby: function(t4) {
  return t4.join(` impliedby `);
}, iff: function(t4) {
  return t4.join(` iff `);
}, perp: function(t4) {
  return t4.join(` perp `);
}, parallel: function(t4) {
  return t4.join(` parallel `);
}, derivative_leibniz: function(t4) {
  return `d` + t4[0] + `/d` + t4[1];
}, partial_derivative_leibniz: function(t4) {
  return `∂` + t4[0] + `/∂` + t4[1];
}, "|": function(t4) {
  return t4[0] + ` | ` + t4[1];
}, ":": function(t4) {
  return t4[0] + ` : ` + t4[1];
}, binom: function(t4) {
  return `binom( ` + t4[0] + `, ` + t4[1] + ` )`;
}, vec: function(t4) {
  return `vec(` + t4[0] + `)`;
}, linesegment: function(t4) {
  return `linesegment( ` + t4.join(`, `) + ` )`;
}, angle: function(t4, n2) {
  return n2 ? `angle ` + t4.join(``) : `angle( ` + t4.join(`, `) + ` )`;
}, unit: function(t4) {
  return t4[0] + ` ` + t4[1];
} }, output_unicodeDefault = true, astToText$2 = class {
  constructor({ output_unicode: t4 = output_unicodeDefault, padToDigits: n2 = null, padToDecimals: r2 = null, avoidScientificNotation: i2 = false, showBlanks: a2 = true, explicitMultiplicationSymbols: o2 = false } = {}) {
    this.output_unicode = t4, this.operators = unicode_operators, t4 || (this.operators = nonunicode_operators), this.padToDigits = n2, this.padToDecimals = r2, this.avoidScientificNotation = i2, this.showBlanks = a2, this.explicitMultiplicationSymbols = o2;
  }
  convert(t4) {
    return this.statement(normalize_display_negative_fractions(t4));
  }
  statement(t4) {
    if (!Array.isArray(t4)) return this.single_statement(t4);
    let n2 = t4[0], r2 = t4.slice(1);
    if (n2 === `ldots`) return `...`;
    if (!(n2 in this.operators) && n2 !== `apply`) throw Error(`Badly formed ast: operator ` + n2 + ` not recognized.`);
    return [`implies`, `impliedby`, `iff`, `rightarrow`, `leftarrow`, `leftrightarrow`].includes(n2) ? this.operators[n2](r2.map((t5) => this.statement(t5))) : n2 === `and` || n2 === `or` ? this.operators[n2](r2.map((function(t5, n3) {
      let r3 = this.single_statement(t5);
      return r3.match(/ /) && !r3.match(/^\(.*\)$/) ? `(` + r3 + `)` : r3;
    }).bind(this))) : this.single_statement(t4);
  }
  single_statement(t4) {
    if (!Array.isArray(t4)) return this.expression(t4);
    let n2 = t4[0], r2 = t4.slice(1);
    if (n2 === `not`) return this.operators[n2](r2.map((function(t5, n3) {
      let r3 = this.single_statement(t5);
      return r3.match(/ /) && !r3.match(/^\(.*\)$/) ? `(` + r3 + `)` : r3;
    }).bind(this)));
    if (n2 === `exists` || n2 === `forall`) return this.operators[n2]([this.single_statement(r2[0])]);
    if (n2 === `=` || n2 === `ne` || n2 === `<` || n2 === `>` || n2 === `le` || n2 === `ge` || n2 === `in` || n2 === `notin` || n2 === `ni` || n2 === `notni` || n2 === `subset` || n2 === `notsubset` || n2 === `subseteq` || n2 === `notsubseteq` || n2 === `superset` || n2 === `notsuperset` || n2 === `superseteq` || n2 === `notsuperseteq`) return this.operators[n2](r2.map((function(t5, n3) {
      return this.expression(t5);
    }).bind(this)));
    if (n2 === `lts` || n2 === `gts`) {
      let t5 = r2[0], i2 = r2[1];
      if (t5[0] !== `tuple` || i2[0] !== `tuple`) throw Error(`Badly formed ast`);
      let a2 = this.expression(t5[1]);
      for (let r3 = 1; r3 < t5.length - 1; r3++) i2[r3] ? n2 === `lts` ? a2 += ` < ` : a2 += ` > ` : n2 === `lts` ? this.output_unicode ? a2 += ` ≤ ` : a2 += ` <= ` : this.output_unicode ? a2 += ` ≥ ` : a2 += ` >= `, a2 += this.expression(t5[r3 + 1]);
      return a2;
    }
    return this.expression(t4);
  }
  expression(t4) {
    if (!Array.isArray(t4)) return this.term(t4);
    let n2 = t4[0], r2 = t4.slice(1);
    return n2 === `+` ? r2.length === 1 ? `+` + this.term(r2[0]) : this.operators[n2](r2.map((function(t5, n3) {
      return n3 > 0 ? this.termWithPlusIfNotNegated(t5) : this.term(t5);
    }).bind(this))) : [`union`, `intersect`, `perp`, `parallel`].includes(n2) ? this.operators[n2](r2.map((function(t5, n3) {
      return this.term(t5);
    }).bind(this))) : this.term(t4);
  }
  term(t4) {
    if (!Array.isArray(t4)) return this.factor(t4);
    let n2 = t4[0], r2 = t4.slice(1);
    if (n2 === `-` || n2 === `pm`) return this.operators[n2](r2.map((function(t5, n3) {
      return this.term(t5);
    }).bind(this)));
    if (n2 === `*`) {
      let t5 = r2.map((function(t6, n3) {
        let i2;
        return n3 > 0 ? (i2 = this.factorWithParenthesesIfNegatedOrExplicitPlus(t6), !this.explicitMultiplicationSymbols && i2.match(/^[0-9]/) ? `* ` + i2 : t6[0] === `angle` && r2.length > 1 ? `( ` + i2 + ` )` : i2) : (i2 = this.factor(t6), t6[0] === `angle` && r2.length > 1 ? `( ` + i2 + ` )` : i2);
      }).bind(this));
      return this.explicitMultiplicationSymbols ? t5.join(`*`) : this.operators[n2](t5);
    }
    if (n2 === `/`) {
      let t5 = this.factor(r2[0]), i2 = this.factor(r2[1]);
      return this.simple_factor_or_function_or_parens(r2[0]) || (t5 = `(` + t5 + `)`), this.simple_factor_or_function_or_parens(r2[1]) || (i2 = `(` + i2 + `)`), this.operators[n2]([t5, i2]);
    }
    return n2 === `unit` ? this.operators[n2](r2.map((function(t5, n3) {
      return this.expression(t5);
    }).bind(this))) : this.factor(t4);
  }
  symbolConvert(t4) {
    let n2 = { alpha: `α`, beta: `β`, Gamma: `Γ`, gamma: `γ`, Delta: `Δ`, delta: `δ`, epsilon: `ε`, zeta: `ζ`, eta: `η`, Theta: `ϴ`, theta: `θ`, iota: `ι`, kappa: `κ`, Lambda: `Λ`, lambda: `λ`, mu: `μ`, nu: `ν`, Xi: `Ξ`, xi: `ξ`, Pi: `Π`, pi: `π`, rho: `ρ`, Sigma: `Σ`, sigma: `σ`, tau: `τ`, Upsilon: `Υ`, upsilon: `υ`, Phi: `Φ`, phi: `ϕ`, Psi: `Ψ`, psi: `ψ`, Omega: `Ω`, omega: `ω`, perp: `⟂`, int: `∫`, emptyset: `∅` };
    return this.output_unicode && t4 in n2 ? n2[t4] : !this.showBlanks && t4 === `＿` ? `` : t4;
  }
  simple_factor_or_function_or_parens(t4) {
    let n2 = this.factor(t4);
    return n2.length <= 1 || typeof t4 == `string` && t4.match(/^\w+$/) || Array.isArray(t4) && t4[0] === `apply` || n2.match(/^\(.*\)$/) || n2.match(/^⟨.*⟩$/) ? true : typeof t4 == `number` ? !!(t4 >= 0 && (this.avoidScientificNotation || !t4.toString().includes(`e`))) : false;
  }
  factor(t4) {
    if (typeof t4 == `string`) return this.symbolConvert(t4);
    if (typeof t4 == `number`) {
      if (t4 === 1 / 0) return this.output_unicode ? `∞` : `infinity`;
      if (t4 === -1 / 0) return this.output_unicode ? `-∞` : `-infinity`;
      if (Number.isNaN(t4)) return `NaN`;
      {
        let n3 = t4.toString(), r3 = n3.indexOf(`e`);
        if (r3 === -1 || this.avoidScientificNotation) return r3 !== -1 && this.avoidScientificNotation && (n3 = expandScientificNotation(n3)), (this.padToDigits !== null || this.padToDecimals !== null) && (n3 = padNumberStringToDigitsAndDecimals(n3, this.padToDigits, this.padToDecimals)), n3;
        let i2 = n3.substring(0, r3), a2 = n3.substring(r3 + 1);
        if (this.padToDigits !== null || this.padToDecimals !== null) {
          let r4 = Number(a2);
          if (r4 > 0 && this.padToDecimals !== null) return n3 = t4.toLocaleString(`fullwide`, { useGrouping: false }), padNumberStringToDigitsAndDecimals(n3, this.padToDigits, this.padToDecimals);
          {
            let t5 = null;
            this.padToDecimals !== null && (t5 = this.padToDecimals + r4), i2 = padNumberStringToDigitsAndDecimals(i2, this.padToDigits, t5);
          }
        }
        return a2[0] === `+` ? i2 + ` * 10^` + a2.substring(1) : i2 + ` * 10^(` + a2 + `)`;
      }
    }
    if (!Array.isArray(t4)) return ``;
    let n2 = t4[0], r2 = t4.slice(1);
    if (n2 === `^`) {
      if (Number.isInteger(r2[0]) && Number.isInteger(r2[1])) {
        let t6;
        return t6 = r2[0] < 0 ? `(` + r2[0].toString() + `)` : r2[0].toString(), r2[1] < 0 ? t6 + `^(` + r2[1].toString() + `)` : t6 + `^` + r2[1].toString();
      }
      let t5 = this.factor(r2[0]), n3 = r2[0];
      for (; n3[0] === `prime`; ) n3 = n3[1];
      this.simple_factor_or_function_or_parens(n3) || n3[0] === `_` && typeof n3[1] == `string` || (t5 = `(` + t5.toString() + `)`);
      let i2 = this.factor(r2[1]);
      return this.simple_factor_or_function_or_parens(r2[1]) || (i2 = `(` + i2.toString() + `)`), t5 + `^` + i2;
    } else if (n2 === `_`) return this.operators[n2](r2.map((function(t5, n3) {
      let r3 = this.factor(t5);
      return this.simple_factor_or_function_or_parens(t5) ? r3 : `(` + r3 + `)`;
    }).bind(this)));
    else if (n2 === `prime`) {
      let t5 = r2[0], n3 = 1;
      for (; t5[0] === `prime`; ) n3 += 1, t5 = t5[1];
      let i2 = this.factor(t5);
      this.simple_factor_or_function_or_parens(t5) || t5[0] === `_` && typeof t5[1] == `string` || (i2 = `(` + i2 + `)`);
      for (let t6 = 0; t6 < n3; t6++) i2 += `'`;
      return i2;
    } else if (n2 === `-`) return this.operators[n2](r2.map((function(t5, n3) {
      return this.term(t5);
    }).bind(this)));
    else if (n2 === `tuple` || n2 === `array` || n2 === `list` || n2 === `set` || n2 === `vector` || n2 === `altvector` || n2 === `|` || n2 === `:` || n2 === `binom` || n2 === `vec` || n2 === `linesegment`) return this.operators[n2](r2.map((function(t5, n3) {
      return this.statement(t5);
    }).bind(this)));
    else if (n2 === `interval`) {
      let t5 = r2[0], n3 = r2[1];
      if (t5[0] !== `tuple` || n3[0] !== `tuple`) throw Error(`Badly formed ast`);
      let i2 = this.statement(t5[1]) + `, ` + this.statement(t5[2]);
      return i2 = n3[1] ? `[ ` + i2 : `( ` + i2, n3[2] ? i2 += ` ]` : i2 += ` )`, i2;
    } else if (n2 === `matrix`) {
      let t5 = r2[0], n3 = r2[1], i2 = `[ `;
      for (let r3 = 0; r3 < t5[1]; r3 += 1) {
        i2 += `[ `;
        for (let a2 = 0; a2 < t5[2]; a2 += 1) i2 += this.statement(n3[r3 + 1][a2 + 1]), a2 < t5[2] - 1 && (i2 += `,`), i2 += ` `;
        i2 += `]`, r3 < t5[1] - 1 && (i2 += `,`), i2 += ` `;
      }
      return i2 += `]`, i2;
    } else if (n2 === `derivative_leibniz` || n2 === `partial_derivative_leibniz`) {
      let t5 = `d`;
      n2 === `partial_derivative_leibniz` && (t5 = `∂`);
      let i2 = r2[0], a2 = r2[1], o2 = 1, s2 = ``;
      Array.isArray(i2) ? (s2 = i2[1], o2 = i2[2]) : s2 = i2;
      let c2 = t5;
      o2 > 1 && (c2 = c2 + `^` + o2), c2 = c2 + this.symbolConvert(s2) + `/`;
      let l2 = 1;
      Array.isArray(a2) && (l2 = a2.length - 1);
      for (let n3 = 1; n3 <= l2; n3++) {
        let r3 = a2[n3], i3 = 1, o3 = ``;
        Array.isArray(r3) ? (o3 = r3[1], i3 = r3[2]) : o3 = r3, c2 = c2 + t5 + this.symbolConvert(o3), i3 > 1 && (c2 = c2 + `^` + i3);
      }
      return c2;
    } else if (n2 === `apply`) {
      if (r2[0] === `abs`) return `|` + this.statement(r2[1]) + `|`;
      if (r2[0] === `factorial`) {
        let t6 = this.factor(r2[1]);
        return this.simple_factor_or_function_or_parens(r2[1]) || r2[1][0] === `_` && typeof r2[1][1] == `string` ? t6 + `!` : `(` + t6 + `)!`;
      }
      let t5 = r2[0];
      if (t5[0] === `^` && (t5 = t5[1]), t5[0] === `_` && (t5 = t5[1]), t5 === `int`) {
        let t6 = this.factor(r2[0]), n4 = r2[1], i3;
        if (Array.isArray(n4) && n4[0] === `*`) {
          let t7 = [], r3 = [`*`];
          for (let i4 = 1; i4 < n4.length; i4++) {
            let a2 = n4[i4];
            Array.isArray(a2) && a2[0] === `d` ? t7.push(a2) : r3.push(a2);
          }
          i3 = this.term(r3), t7.length > 0 && (i3 += ` ` + t7.map((t8) => `d` + this.factor(t8[1])).join(` `));
        }
        return i3 ||= this.term(r2[1]), t6 + ` ` + i3;
      }
      let n3 = this.factor(r2[0]), i2 = this.statement(r2[1]);
      return r2[1][0] !== `tuple` && (i2 = `(` + i2 + `)`), n3 + i2;
    } else if (n2 === `angle`) {
      let t5 = r2.every((t6) => {
        if (typeof t6 == `string` && t6.length === 1) return true;
        if (!Array.isArray(t6)) return false;
        let n3 = t6[0];
        return n3 === `_` || n3 === `^` || n3 === `prime`;
      });
      return this.operators[n2](r2.map((function(t6, n3) {
        return this.statement(t6);
      }).bind(this)), t5);
    } else if (n2 === `+` && t4.length === 2) return `+ ` + this.factor(t4[1]);
    else return `(` + this.statement(t4) + `)`;
  }
  factorWithParenthesesIfNegatedOrExplicitPlus(t4) {
    var n2 = this.factor(t4);
    return n2.match(/^-/) || n2.match(/^\+/) ? `(` + n2 + `)` : n2;
  }
  termWithPlusIfNotNegated(t4) {
    let n2 = this.term(t4);
    return n2.match(/^± /) || n2.match(/^plusminus /) ? n2 : n2.match(/^-/) ? n2.match(/^-[^ ]/) ? `- ` + n2.slice(1) : n2 : `+ ` + n2;
  }
}, astToText$1 = new astToText$2();
function subscripts_to_strings(t4, n2 = false) {
  var r2 = get_tree(t4);
  if (!Array.isArray(r2)) return r2;
  let i2 = r2[0], a2 = r2.slice(1);
  return i2 === `_` && (n2 || a2.every((t5) => [`number`, `string`].includes(typeof t5))) ? astToText$1.convert(r2) : [i2].concat(a2.map((t5) => subscripts_to_strings(t5, n2)));
}
function strings_to_subscripts(t4) {
  var n2 = get_tree(t4);
  if (typeof n2 == `string`) {
    let t5 = n2.match(/^([0-9a-zA-Z]+)_([a-zA-Z]+|[0-9]+)$/);
    if (t5) {
      let n3 = Number(t5[1]);
      isNaN(n3) && (n3 = t5[1]);
      let r3 = Number(t5[2]);
      return isNaN(r3) && (r3 = t5[2]), [`_`, n3, r3];
    } else return n2;
  }
  if (!Array.isArray(n2)) return n2;
  let r2 = n2[0], i2 = n2.slice(1);
  return [r2].concat(i2.map(strings_to_subscripts));
}
var normalization_exports = __exportAll({ altvectors_to_vectors: () => altvectors_to_vectors, constants_to_floats: () => constants_to_floats, default_order: () => default_order, log_subscript_to_two_arg_log: () => log_subscript_to_two_arg_log, normalize_angle_linesegment_arg_order: () => normalize_angle_linesegment_arg_order, normalize_applied_functions: () => normalize_applied_functions, normalize_function_names: () => normalize_function_names, normalize_negative_numbers: () => normalize_negative_numbers, strings_to_subscripts: () => strings_to_subscripts, subscripts_to_strings: () => subscripts_to_strings, substitute_abs: () => substitute_abs, to_intervals: () => to_intervals, tuples_to_vectors: () => tuples_to_vectors });
function get_all_units() {
  return { "%": { scale: (t4) => [`/`, t4, 100], only_scales: true }, $: { prefix: true, scale: (t4) => t4 }, deg: { scale: (t4) => [`/`, [`*`, t4, `pi`], 180], only_scales: true } };
}
var all_units$1 = get_all_units();
function get_unit_of_tree(t4) {
  if (t4[0] === `unit`) {
    for (let n2 in all_units$1) if (all_units$1[n2].prefix) {
      if (t4[1] === n2) return n2;
    } else if (t4[2] === n2) return n2;
  }
  return null;
}
function get_unit_value_of_tree(t4) {
  if (t4[0] === `unit`) {
    for (let n2 in all_units$1) if (all_units$1[n2].prefix) {
      if (t4[1] === n2) return [n2, t4[2]];
    } else if (t4[2] === n2) return [n2, t4[1]];
  }
  return null;
}
function remove_duplicate_negatives(t4) {
  if (!Array.isArray(t4)) return t4;
  var n2 = t4[0], r2 = t4.slice(1);
  return n2 === `-` && Array.isArray(r2[0]) && r2[0][0] === `-` ? remove_duplicate_negatives(r2[0][1]) : n2 === `-` && typeof r2[0] == `number` && r2[0] < 0 ? -r2[0] : (r2 = r2.map(remove_duplicate_negatives), [n2].concat(r2));
}
function normalize_negatives_in_factors(t4) {
  if (!Array.isArray(t4)) return t4;
  var n2 = t4[0], r2 = t4.slice(1);
  if (r2 = r2.map(normalize_negatives_in_factors), n2 !== `*` && n2 !== `/`) return [n2].concat(r2);
  for (var i2 = 1, a2 = [], o2 = 0; o2 < r2.length; o2++) Array.isArray(r2[o2]) && r2[o2][0] === `-` ? (i2 *= -1, a2.push(r2[o2][1])) : typeof r2[o2] == `number` && r2[o2] < 0 ? (i2 *= -1, a2.push(-r2[o2])) : a2.push(r2[o2]);
  var s2 = [n2].concat(a2);
  return i2 === -1 && (s2 = [`-`, s2]), s2;
}
function normalize_negatives(t4) {
  var n2 = get_tree(t4);
  return n2 = remove_duplicate_negatives(n2), n2 = normalize_negatives_in_factors(n2), n2 = remove_duplicate_negatives(n2), n2 = normalize_negative_numbers(n2), n2;
}
function sort_key(t4, n2 = {}) {
  if (typeof t4 == `number`) return n2.ignore_negatives ? [0, `number`, Math.abs(t4)] : [0, `number`, t4];
  if (typeof t4 == `string`) return t4 === `-` || t4 === `+` ? [8, `plus_minus_string`, t4] : [1, `symbol`, t4];
  if (typeof t4 == `boolean`) return [1, `boolean`, t4];
  if (!Array.isArray(t4)) return [-1, `unknown`, t4];
  var r2 = t4[0], i2 = t4.slice(1);
  if (r2 === `^` && i2.length === 2) return [...sort_key(i2[0], n2), `power`, ...sort_key(i2[1], n2)];
  if (r2 === `apply`) {
    var a2 = [2, `function`, i2[0]];
    (i2[0] === `sqrt` || i2[0] === `cbrt` || i2[0] === `nthroot`) && (a2 = [5, `root`, i2[0] === `sqrt` ? 2 : i2[0] === `cbrt` ? 3 : i2[1][2] ?? 2]);
    var o2 = i2[1], s2 = 1, c2 = [];
    return Array.isArray(o2) ? (o2 = o2.slice(1), s2 = o2.length, c2 = o2.map((t5) => sort_key(t5, n2))) : c2 = [sort_key(o2, n2)], a2.push([s2, c2]), a2;
  } else if (r2 === `unit`) {
    let [r3, i3] = get_unit_value_of_tree(t4);
    if (r3) {
      let t5 = sort_key(i3, n2);
      return t5[1] += `_` + r3, t5;
    }
  }
  var l2 = i2.length, u2 = i2.map(sort_key, n2);
  return r2 === `*` ? [4, `product`, l2, u2] : r2 === `/` ? [4, `quotient`, l2, u2] : r2 === `+` ? [5, `sum`, l2, u2] : r2 === `-` ? n2.ignore_negatives ? u2[0] : [6, `minus`, l2, u2] : r2 === `pm` ? n2.ignore_negatives ? u2[0] : [6, `pm`, l2, u2] : [`tuple`, `vector`, `altvector`].includes(r2) ? [7, l2, u2] : r2 === `interval` ? i2[1][1] === false ? i2[1][2] === false ? [7, ...u2[0].slice(1)] : [8, l2, u2] : i2[1][2] === false ? [8, l2, u2] : [9, ...u2[0].slice(1)] : r2 === `array` ? [9, l2, u2] : [10, r2, l2, u2];
}
function arrayCompare(t4, n2) {
  if (Array.isArray(t4)) if (Array.isArray(n2)) {
    let r2 = Math.min(t4.length, n2.length);
    for (let i2 = 0; i2 < r2; i2++) {
      let r3 = arrayCompare(t4[i2], n2[i2]);
      if (r3 !== 0) return r3;
    }
    return t4.length < n2.length ? -1 : +(t4.length > n2.length);
  } else return 1;
  else if (Array.isArray(n2)) return -1;
  else return t4 < n2 ? -1 : +(t4 > n2);
}
function compare_function(t4, n2, r2 = {}) {
  return arrayCompare(sort_key(t4, r2), sort_key(n2, r2));
}
function coeff_factors_from_term(t4, n2) {
  if (typeof t4 == `string`) {
    let r2 = n2.indexOf(t4);
    r2 === -1 && (n2.push(t4), r2 = n2.length - 1);
    let i2 = [];
    return i2[r2] = 1, { factor_contains: i2, coeff: 1 };
  } else if (Array.isArray(t4)) {
    let r2 = t4[0], i2 = t4.slice(1);
    if (r2 === `*`) {
      let t5 = [], r3 = [];
      for (let a2 of i2) {
        if (typeof a2 == `string`) {
          let t6 = n2.indexOf(a2);
          t6 === -1 && (n2.push(a2), t6 = n2.length - 1), r3[t6] === void 0 && (r3[t6] = 0), r3[t6]++;
          continue;
        } else if (Array.isArray(a2) && (a2[0] === `^` || a2[0] === `-`)) {
          let i3 = coeff_factors_from_term(a2, n2);
          for (let t6 in i3.factor_contains) r3[t6] === void 0 && (r3[t6] = 0), r3[t6] += i3.factor_contains[t6];
          i3.coeff !== 1 && t5.push(i3.coeff);
          continue;
        }
        t5.push(a2);
      }
      return t5 = t5.length === 0 ? 1 : t5.length === 1 ? t5[0] : [`*`, ...t5], { factor_contains: r3, coeff: t5 };
    } else if (r2 === `^`) {
      let t5 = i2[0], r3 = i2[1], a2 = [];
      if (typeof t5 == `string` && Number.isFinite(r3)) {
        let i3 = n2.indexOf(t5);
        return i3 === -1 && (n2.push(t5), i3 = n2.length - 1), a2[i3] = r3, { factor_contains: a2, coeff: 1 };
      }
    } else if (r2 === `-`) {
      let t5 = coeff_factors_from_term(i2[0], n2), r3 = -1;
      return typeof t5.coeff == `number` ? r3 *= t5.coeff : r3 = [`-`, t5.coeff], { factor_contains: t5.factor_contains, coeff: r3 };
    } else if (r2 === `/`) {
      let t5 = coeff_factors_from_term(i2[0], n2), r3 = [`/`, t5.coeff, i2[1]];
      return { factor_contains: t5.factor_contains, coeff: r3 };
    }
  }
  return { factor_contains: [], coeff: t4 };
}
function default_order(t4, n2) {
  n2 === void 0 && (n2 = {});
  var r2 = get_tree(t4);
  r2 = flatten$5(r2), r2 = normalize_negatives(r2);
  function i2(t5) {
    if (!Array.isArray(t5)) return t5;
    var r3 = t5[0], a2 = t5.slice(1);
    if (a2 = a2.map(i2), r3 === `+`) {
      let t6 = [], r4 = [], i3 = [];
      for (let n3 of a2) {
        let a3 = coeff_factors_from_term(n3, t6);
        r4.push(a3.factor_contains), i3.push(a3.coeff);
      }
      let o3 = [];
      for (let [n3, i4] of t6.entries()) {
        let t7 = { varname: i4, exponents_in_term: [] };
        for (let i5 = 0; i5 < r4.length; i5++) t7.exponents_in_term.push(r4[i5][n3] || 0);
        o3.push(t7);
      }
      o3.sort((t7, n3) => t7.varname < n3.varname ? -1 : 1);
      let s3 = [];
      for (let t7 = 0; t7 < i3.length; t7++) {
        let r5 = o3.reduce((n3, r6) => [...n3, -r6.exponents_in_term[t7]], []);
        r5.push(sort_key(i3[t7], n2)), s3.push(r5);
      }
      let c2 = [];
      for (let [t7, n3] of a2.entries()) c2.push({ term: n3, sort_key: s3[t7] });
      c2.sort((t7, n3) => arrayCompare(t7.sort_key, n3.sort_key)), a2 = c2.map((t7) => t7.term);
    } else if (r3 === `*`) {
      let t6 = [], r4 = [];
      for (let n3 of a2) [`tuple`, `list`, `vector`, `altvector`, `interval`, `set`, `array`, `matrix`].includes(n3[0]) ? r4.push(n3) : t6.push(n3);
      t6.sort((t7, r5) => compare_function(t7, r5, n2)), a2 = [...t6, ...r4];
    } else if (r3 === `=` || r3 === `and` || r3 === `or` || r3 === `ne` || r3 === `union` || r3 === `intersect`) a2.sort((t6, r4) => compare_function(t6, r4, n2));
    else if (r3 === `>` || r3 === `ge`) a2 = a2.reverse(), r3 = r3 === `>` ? `<` : `le`;
    else if (r3 === `gts`) {
      var o2 = a2[0], s2 = a2[1];
      if (o2[0] !== `tuple` || s2[0] !== `tuple`) throw Error(`Badly formed ast`);
      o2 = [`tuple`].concat(o2.slice(1).reverse()), s2 = [`tuple`].concat(s2.slice(1).reverse()), r3 = `lts`, a2 = [o2, s2];
    } else if (r3 === `ni` || r3 === `notni` || r3 === `superset` || r3 === `notsuperset` || r3 === `superseteq` || r3 === `notsuperseteq`) a2 = a2.reverse(), r3 === `ni` ? r3 = `in` : r3 === `notni` ? r3 = `notin` : r3 === `superset` ? r3 = `subset` : r3 === `notsuperset` ? r3 = `notsubset` : r3 === `superseteq` ? r3 = `subseteq` : r3 === `notsuperseteq` && (r3 = `notsubseteq`);
    else if (r3 === `-` && a2[0][0] === `*`) return a2[0][1] = [`-`, a2[0][1]], a2[0];
    return [r3].concat(a2);
  }
  return normalize_negatives(i2(r2));
}
var variables_exports = __exportAll({ functions: () => functions$2, operators: () => operators$3, variables: () => variables });
function leaves(t4, n2) {
  if (!Array.isArray(t4)) return [t4];
  var r2 = t4[0], i2 = t4.slice(1);
  return n2 && r2 === `_` && typeof i2[0] == `string` && (typeof i2[1] == `string` || typeof i2[1] == `number`) ? [i2[0] + `_` + i2[1]] : (r2 === `apply` && (i2 = t4.slice(2)), i2.length === 0 ? [] : i2.map(function(t5, r3) {
    return leaves(t5, n2);
  }).reduce(function(t5, n3) {
    return t5.concat(n3);
  }));
}
function variables(t4, n2 = false) {
  var r2 = leaves(get_tree(t4), n2);
  return r2 = r2.filter(function(t5, n3) {
    return typeof t5 == `string` && (mathjs_default.define_e || t5 !== `e`) && (mathjs_default.define_pi || t5 !== `pi`) && (mathjs_default.define_i || t5 !== `i`);
  }), r2 = r2.filter(function(t5, n3, i2) {
    return n3 === r2.indexOf(t5);
  }), r2;
}
function operators_list(t4) {
  if (!Array.isArray(t4)) return [];
  var n2 = t4[0], r2 = t4.slice(1);
  return n2 === `apply` && (r2 = t4.slice(2)), r2.length === 0 ? [n2] : [n2].concat(r2.map(function(t5, n3) {
    return operators_list(t5);
  }).reduce(function(t5, n3) {
    return t5.concat(n3);
  }));
}
function operators$3(t4) {
  var n2 = operators_list(get_tree(t4));
  return n2 = n2.filter(function(t5, n3) {
    return t5 !== `apply`;
  }), n2 = n2.filter(function(t5, r2, i2) {
    return r2 === n2.indexOf(t5);
  }), n2;
}
function functions_list(t4) {
  if (!Array.isArray(t4)) return [];
  var n2 = t4[0], r2 = t4.slice(1), i2 = [];
  return n2 === `apply` && (i2 = [r2[0]], r2 = t4.slice(2)), i2.concat(r2.map(function(t5, n3) {
    return functions_list(t5);
  }).reduce(function(t5, n3) {
    return t5.concat(n3);
  }, []));
}
function functions$2(t4) {
  var n2 = functions_list(get_tree(t4));
  return n2 = n2.filter(function(t5, r2, i2) {
    return r2 === n2.indexOf(t5);
  }), n2;
}
var VERSION, root, ArrayProto, ObjProto, SymbolProto, push, slice, toString$1, hasOwnProperty, supportsArrayBuffer, supportsDataView, nativeIsArray, nativeKeys, nativeCreate, nativeIsView, _isNaN, _isFinite, hasEnumBug, nonEnumerableProps, MAX_ARRAY_INDEX, init__setup = __esmMin((() => {
  VERSION = `1.13.6`, root = typeof self == `object` && self.self === self && self || typeof global == `object` && global.global === global && global || Function(`return this`)() || {}, ArrayProto = Array.prototype, ObjProto = Object.prototype, SymbolProto = typeof Symbol < `u` ? Symbol.prototype : null, push = ArrayProto.push, slice = ArrayProto.slice, toString$1 = ObjProto.toString, hasOwnProperty = ObjProto.hasOwnProperty, supportsArrayBuffer = typeof ArrayBuffer < `u`, supportsDataView = typeof DataView < `u`, nativeIsArray = Array.isArray, nativeKeys = Object.keys, nativeCreate = Object.create, nativeIsView = supportsArrayBuffer && ArrayBuffer.isView, _isNaN = isNaN, _isFinite = isFinite, hasEnumBug = !{ toString: null }.propertyIsEnumerable(`toString`), nonEnumerableProps = [`valueOf`, `isPrototypeOf`, `toString`, `propertyIsEnumerable`, `hasOwnProperty`, `toLocaleString`], MAX_ARRAY_INDEX = 2 ** 53 - 1;
}));
function restArguments(t4, n2) {
  return n2 = n2 == null ? t4.length - 1 : +n2, function() {
    for (var r2 = Math.max(arguments.length - n2, 0), i2 = Array(r2), a2 = 0; a2 < r2; a2++) i2[a2] = arguments[a2 + n2];
    switch (n2) {
      case 0:
        return t4.call(this, i2);
      case 1:
        return t4.call(this, arguments[0], i2);
      case 2:
        return t4.call(this, arguments[0], arguments[1], i2);
    }
    var o2 = Array(n2 + 1);
    for (a2 = 0; a2 < n2; a2++) o2[a2] = arguments[a2];
    return o2[n2] = i2, t4.apply(this, o2);
  };
}
var init_restArguments = __esmMin((() => {
}));
function isObject(t4) {
  var n2 = typeof t4;
  return n2 === `function` || n2 === `object` && !!t4;
}
var init_isObject = __esmMin((() => {
}));
function isNull(t4) {
  return t4 === null;
}
var init_isNull = __esmMin((() => {
}));
function isUndefined(t4) {
  return t4 === void 0;
}
var init_isUndefined = __esmMin((() => {
}));
function isBoolean(t4) {
  return t4 === true || t4 === false || toString$1.call(t4) === `[object Boolean]`;
}
var init_isBoolean = __esmMin((() => {
  init__setup();
}));
function isElement(t4) {
  return !!(t4 && t4.nodeType === 1);
}
var init_isElement = __esmMin((() => {
}));
function tagTester(t4) {
  var n2 = `[object ` + t4 + `]`;
  return function(t5) {
    return toString$1.call(t5) === n2;
  };
}
var init__tagTester = __esmMin((() => {
  init__setup();
})), isString_default, init_isString = __esmMin((() => {
  init__tagTester(), isString_default = tagTester(`String`);
})), isNumber_default, init_isNumber = __esmMin((() => {
  init__tagTester(), isNumber_default = tagTester(`Number`);
})), isDate_default, init_isDate = __esmMin((() => {
  init__tagTester(), isDate_default = tagTester(`Date`);
})), isRegExp_default, init_isRegExp = __esmMin((() => {
  init__tagTester(), isRegExp_default = tagTester(`RegExp`);
})), isError_default, init_isError = __esmMin((() => {
  init__tagTester(), isError_default = tagTester(`Error`);
})), isSymbol_default, init_isSymbol = __esmMin((() => {
  init__tagTester(), isSymbol_default = tagTester(`Symbol`);
})), isArrayBuffer_default, init_isArrayBuffer = __esmMin((() => {
  init__tagTester(), isArrayBuffer_default = tagTester(`ArrayBuffer`);
})), isFunction, nodelist, isFunction_default, init_isFunction = __esmMin((() => {
  init__tagTester(), init__setup(), isFunction = tagTester(`Function`), nodelist = root.document && root.document.childNodes, typeof /./ != `function` && typeof Int8Array != `object` && typeof nodelist != `function` && (isFunction = function(t4) {
    return typeof t4 == `function` || false;
  }), isFunction_default = isFunction;
})), _hasObjectTag_default, init__hasObjectTag = __esmMin((() => {
  init__tagTester(), _hasObjectTag_default = tagTester(`Object`);
})), hasStringTagBug, isIE11, init__stringTagBug = __esmMin((() => {
  init__setup(), init__hasObjectTag(), hasStringTagBug = supportsDataView && _hasObjectTag_default(new DataView(new ArrayBuffer(8))), isIE11 = typeof Map < `u` && _hasObjectTag_default(/* @__PURE__ */ new Map());
}));
function ie10IsDataView(t4) {
  return t4 != null && isFunction_default(t4.getInt8) && isArrayBuffer_default(t4.buffer);
}
var isDataView, isDataView_default, init_isDataView = __esmMin((() => {
  init__tagTester(), init_isFunction(), init_isArrayBuffer(), init__stringTagBug(), isDataView = tagTester(`DataView`), isDataView_default = hasStringTagBug ? ie10IsDataView : isDataView;
})), isArray_default, init_isArray = __esmMin((() => {
  init__setup(), init__tagTester(), isArray_default = nativeIsArray || tagTester(`Array`);
}));
function has$1(t4, n2) {
  return t4 != null && hasOwnProperty.call(t4, n2);
}
var init__has = __esmMin((() => {
  init__setup();
})), isArguments, isArguments_default, init_isArguments = __esmMin((() => {
  init__tagTester(), init__has(), isArguments = tagTester(`Arguments`), (function() {
    isArguments(arguments) || (isArguments = function(t4) {
      return has$1(t4, `callee`);
    });
  })(), isArguments_default = isArguments;
}));
function isFinite$1(t4) {
  return !isSymbol_default(t4) && _isFinite(t4) && !isNaN(parseFloat(t4));
}
var init_isFinite = __esmMin((() => {
  init__setup(), init_isSymbol();
}));
function isNaN$1(t4) {
  return isNumber_default(t4) && _isNaN(t4);
}
var init_isNaN = __esmMin((() => {
  init__setup(), init_isNumber();
}));
function constant(t4) {
  return function() {
    return t4;
  };
}
var init_constant = __esmMin((() => {
}));
function createSizePropertyCheck(t4) {
  return function(n2) {
    var r2 = t4(n2);
    return typeof r2 == `number` && r2 >= 0 && r2 <= MAX_ARRAY_INDEX;
  };
}
var init__createSizePropertyCheck = __esmMin((() => {
  init__setup();
}));
function shallowProperty(t4) {
  return function(n2) {
    return n2?.[t4];
  };
}
var init__shallowProperty = __esmMin((() => {
})), _getByteLength_default, init__getByteLength = __esmMin((() => {
  init__shallowProperty(), _getByteLength_default = shallowProperty(`byteLength`);
})), _isBufferLike_default, init__isBufferLike = __esmMin((() => {
  init__createSizePropertyCheck(), init__getByteLength(), _isBufferLike_default = createSizePropertyCheck(_getByteLength_default);
}));
function isTypedArray(t4) {
  return nativeIsView ? nativeIsView(t4) && !isDataView_default(t4) : _isBufferLike_default(t4) && typedArrayPattern.test(toString$1.call(t4));
}
var typedArrayPattern, isTypedArray_default, init_isTypedArray = __esmMin((() => {
  init__setup(), init_isDataView(), init_constant(), init__isBufferLike(), typedArrayPattern = /\[object ((I|Ui)nt(8|16|32)|Float(32|64)|Uint8Clamped|Big(I|Ui)nt64)Array\]/, isTypedArray_default = supportsArrayBuffer ? isTypedArray : constant(false);
})), _getLength_default, init__getLength = __esmMin((() => {
  init__shallowProperty(), _getLength_default = shallowProperty(`length`);
}));
function emulatedSet(t4) {
  for (var n2 = {}, r2 = t4.length, i2 = 0; i2 < r2; ++i2) n2[t4[i2]] = true;
  return { contains: function(t5) {
    return n2[t5] === true;
  }, push: function(r3) {
    return n2[r3] = true, t4.push(r3);
  } };
}
function collectNonEnumProps(t4, n2) {
  n2 = emulatedSet(n2);
  var r2 = nonEnumerableProps.length, i2 = t4.constructor, a2 = isFunction_default(i2) && i2.prototype || ObjProto, o2 = `constructor`;
  for (has$1(t4, o2) && !n2.contains(o2) && n2.push(o2); r2--; ) o2 = nonEnumerableProps[r2], o2 in t4 && t4[o2] !== a2[o2] && !n2.contains(o2) && n2.push(o2);
}
var init__collectNonEnumProps = __esmMin((() => {
  init__setup(), init_isFunction(), init__has();
}));
function keys(t4) {
  if (!isObject(t4)) return [];
  if (nativeKeys) return nativeKeys(t4);
  var n2 = [];
  for (var r2 in t4) has$1(t4, r2) && n2.push(r2);
  return hasEnumBug && collectNonEnumProps(t4, n2), n2;
}
var init_keys = __esmMin((() => {
  init_isObject(), init__setup(), init__has(), init__collectNonEnumProps();
}));
function isEmpty(t4) {
  if (t4 == null) return true;
  var n2 = _getLength_default(t4);
  return typeof n2 == `number` && (isArray_default(t4) || isString_default(t4) || isArguments_default(t4)) ? n2 === 0 : _getLength_default(keys(t4)) === 0;
}
var init_isEmpty = __esmMin((() => {
  init__getLength(), init_isArray(), init_isString(), init_isArguments(), init_keys();
}));
function isMatch(t4, n2) {
  var r2 = keys(n2), i2 = r2.length;
  if (t4 == null) return !i2;
  for (var a2 = Object(t4), o2 = 0; o2 < i2; o2++) {
    var s2 = r2[o2];
    if (n2[s2] !== a2[s2] || !(s2 in a2)) return false;
  }
  return true;
}
var init_isMatch = __esmMin((() => {
  init_keys();
}));
function _$1(t4) {
  if (t4 instanceof _$1) return t4;
  if (!(this instanceof _$1)) return new _$1(t4);
  this._wrapped = t4;
}
var init_underscore = __esmMin((() => {
  init__setup(), _$1.VERSION = VERSION, _$1.prototype.value = function() {
    return this._wrapped;
  }, _$1.prototype.valueOf = _$1.prototype.toJSON = _$1.prototype.value, _$1.prototype.toString = function() {
    return String(this._wrapped);
  };
}));
function toBufferView(t4) {
  return new Uint8Array(t4.buffer || t4, t4.byteOffset || 0, _getByteLength_default(t4));
}
var init__toBufferView = __esmMin((() => {
  init__getByteLength();
}));
function eq(t4, n2, r2, i2) {
  if (t4 === n2) return t4 !== 0 || 1 / t4 == 1 / n2;
  if (t4 == null || n2 == null) return false;
  if (t4 !== t4) return n2 !== n2;
  var a2 = typeof t4;
  return a2 !== `function` && a2 !== `object` && typeof n2 != `object` ? false : deepEq(t4, n2, r2, i2);
}
function deepEq(t4, n2, r2, i2) {
  t4 instanceof _$1 && (t4 = t4._wrapped), n2 instanceof _$1 && (n2 = n2._wrapped);
  var a2 = toString$1.call(t4);
  if (a2 !== toString$1.call(n2)) return false;
  if (hasStringTagBug && a2 == `[object Object]` && isDataView_default(t4)) {
    if (!isDataView_default(n2)) return false;
    a2 = tagDataView;
  }
  switch (a2) {
    case `[object RegExp]`:
    case `[object String]`:
      return `` + t4 == `` + n2;
    case `[object Number]`:
      return +t4 == +t4 ? +t4 == 0 ? 1 / t4 == 1 / n2 : +t4 == +n2 : +n2 != +n2;
    case `[object Date]`:
    case `[object Boolean]`:
      return +t4 == +n2;
    case `[object Symbol]`:
      return SymbolProto.valueOf.call(t4) === SymbolProto.valueOf.call(n2);
    case `[object ArrayBuffer]`:
    case tagDataView:
      return deepEq(toBufferView(t4), toBufferView(n2), r2, i2);
  }
  var o2 = a2 === `[object Array]`;
  if (!o2 && isTypedArray_default(t4)) {
    if (_getByteLength_default(t4) !== _getByteLength_default(n2)) return false;
    if (t4.buffer === n2.buffer && t4.byteOffset === n2.byteOffset) return true;
    o2 = true;
  }
  if (!o2) {
    if (typeof t4 != `object` || typeof n2 != `object`) return false;
    var s2 = t4.constructor, c2 = n2.constructor;
    if (s2 !== c2 && !(isFunction_default(s2) && s2 instanceof s2 && isFunction_default(c2) && c2 instanceof c2) && `constructor` in t4 && `constructor` in n2) return false;
  }
  r2 ||= [], i2 ||= [];
  for (var l2 = r2.length; l2--; ) if (r2[l2] === t4) return i2[l2] === n2;
  if (r2.push(t4), i2.push(n2), o2) {
    if (l2 = t4.length, l2 !== n2.length) return false;
    for (; l2--; ) if (!eq(t4[l2], n2[l2], r2, i2)) return false;
  } else {
    var u2 = keys(t4), d2;
    if (l2 = u2.length, keys(n2).length !== l2) return false;
    for (; l2--; ) if (d2 = u2[l2], !(has$1(n2, d2) && eq(t4[d2], n2[d2], r2, i2))) return false;
  }
  return r2.pop(), i2.pop(), true;
}
function isEqual(t4, n2) {
  return eq(t4, n2);
}
var tagDataView, init_isEqual = __esmMin((() => {
  init_underscore(), init__setup(), init__getByteLength(), init_isTypedArray(), init_isFunction(), init__stringTagBug(), init_isDataView(), init_keys(), init__has(), init__toBufferView(), tagDataView = `[object DataView]`;
}));
function allKeys(t4) {
  if (!isObject(t4)) return [];
  var n2 = [];
  for (var r2 in t4) n2.push(r2);
  return hasEnumBug && collectNonEnumProps(t4, n2), n2;
}
var init_allKeys = __esmMin((() => {
  init_isObject(), init__setup(), init__collectNonEnumProps();
}));
function ie11fingerprint(t4) {
  var n2 = _getLength_default(t4);
  return function(r2) {
    if (r2 == null || _getLength_default(allKeys(r2))) return false;
    for (var i2 = 0; i2 < n2; i2++) if (!isFunction_default(r2[t4[i2]])) return false;
    return t4 !== weakMapMethods || !isFunction_default(r2[forEachName]);
  };
}
var forEachName, hasName, commonInit, mapTail, mapMethods, weakMapMethods, setMethods, init__methodFingerprint = __esmMin((() => {
  init__getLength(), init_isFunction(), init_allKeys(), forEachName = `forEach`, hasName = `has`, commonInit = [`clear`, `delete`], mapTail = [`get`, hasName, `set`], mapMethods = commonInit.concat(forEachName, mapTail), weakMapMethods = commonInit.concat(mapTail), setMethods = [`add`].concat(commonInit, forEachName, hasName);
})), isMap_default, init_isMap = __esmMin((() => {
  init__tagTester(), init__stringTagBug(), init__methodFingerprint(), isMap_default = isIE11 ? ie11fingerprint(mapMethods) : tagTester(`Map`);
})), isWeakMap_default, init_isWeakMap = __esmMin((() => {
  init__tagTester(), init__stringTagBug(), init__methodFingerprint(), isWeakMap_default = isIE11 ? ie11fingerprint(weakMapMethods) : tagTester(`WeakMap`);
})), isSet_default, init_isSet = __esmMin((() => {
  init__tagTester(), init__stringTagBug(), init__methodFingerprint(), isSet_default = isIE11 ? ie11fingerprint(setMethods) : tagTester(`Set`);
})), isWeakSet_default, init_isWeakSet = __esmMin((() => {
  init__tagTester(), isWeakSet_default = tagTester(`WeakSet`);
}));
function values(t4) {
  for (var n2 = keys(t4), r2 = n2.length, i2 = Array(r2), a2 = 0; a2 < r2; a2++) i2[a2] = t4[n2[a2]];
  return i2;
}
var init_values = __esmMin((() => {
  init_keys();
}));
function pairs(t4) {
  for (var n2 = keys(t4), r2 = n2.length, i2 = Array(r2), a2 = 0; a2 < r2; a2++) i2[a2] = [n2[a2], t4[n2[a2]]];
  return i2;
}
var init_pairs = __esmMin((() => {
  init_keys();
}));
function invert(t4) {
  for (var n2 = {}, r2 = keys(t4), i2 = 0, a2 = r2.length; i2 < a2; i2++) n2[t4[r2[i2]]] = r2[i2];
  return n2;
}
var init_invert = __esmMin((() => {
  init_keys();
}));
function functions$1(t4) {
  var n2 = [];
  for (var r2 in t4) isFunction_default(t4[r2]) && n2.push(r2);
  return n2.sort();
}
var init_functions = __esmMin((() => {
  init_isFunction();
}));
function createAssigner(t4, n2) {
  return function(r2) {
    var i2 = arguments.length;
    if (n2 && (r2 = Object(r2)), i2 < 2 || r2 == null) return r2;
    for (var a2 = 1; a2 < i2; a2++) for (var o2 = arguments[a2], s2 = t4(o2), c2 = s2.length, l2 = 0; l2 < c2; l2++) {
      var u2 = s2[l2];
      (!n2 || r2[u2] === void 0) && (r2[u2] = o2[u2]);
    }
    return r2;
  };
}
var init__createAssigner = __esmMin((() => {
})), extend_default, init_extend = __esmMin((() => {
  init__createAssigner(), init_allKeys(), extend_default = createAssigner(allKeys);
})), extendOwn_default, init_extendOwn = __esmMin((() => {
  init__createAssigner(), init_keys(), extendOwn_default = createAssigner(keys);
})), defaults_default, init_defaults = __esmMin((() => {
  init__createAssigner(), init_allKeys(), defaults_default = createAssigner(allKeys, true);
}));
function ctor() {
  return function() {
  };
}
function baseCreate(t4) {
  if (!isObject(t4)) return {};
  if (nativeCreate) return nativeCreate(t4);
  var n2 = ctor();
  n2.prototype = t4;
  var r2 = new n2();
  return n2.prototype = null, r2;
}
var init__baseCreate = __esmMin((() => {
  init_isObject(), init__setup();
}));
function create(t4, n2) {
  var r2 = baseCreate(t4);
  return n2 && extendOwn_default(r2, n2), r2;
}
var init_create = __esmMin((() => {
  init__baseCreate(), init_extendOwn();
}));
function clone(t4) {
  return isObject(t4) ? isArray_default(t4) ? t4.slice() : extend_default({}, t4) : t4;
}
var init_clone = __esmMin((() => {
  init_isObject(), init_isArray(), init_extend();
}));
function tap(t4, n2) {
  return n2(t4), t4;
}
var init_tap = __esmMin((() => {
}));
function toPath$1(t4) {
  return isArray_default(t4) ? t4 : [t4];
}
var init_toPath = __esmMin((() => {
  init_underscore(), init_isArray(), _$1.toPath = toPath$1;
}));
function toPath(t4) {
  return _$1.toPath(t4);
}
var init__toPath = __esmMin((() => {
  init_underscore(), init_toPath();
}));
function deepGet(t4, n2) {
  for (var r2 = n2.length, i2 = 0; i2 < r2; i2++) {
    if (t4 == null) return;
    t4 = t4[n2[i2]];
  }
  return r2 ? t4 : void 0;
}
var init__deepGet = __esmMin((() => {
}));
function get(t4, n2, r2) {
  var i2 = deepGet(t4, toPath(n2));
  return isUndefined(i2) ? r2 : i2;
}
var init_get = __esmMin((() => {
  init__toPath(), init__deepGet(), init_isUndefined();
}));
function has(t4, n2) {
  n2 = toPath(n2);
  for (var r2 = n2.length, i2 = 0; i2 < r2; i2++) {
    var a2 = n2[i2];
    if (!has$1(t4, a2)) return false;
    t4 = t4[a2];
  }
  return !!r2;
}
var init_has = __esmMin((() => {
  init__has(), init__toPath();
}));
function identity(t4) {
  return t4;
}
var init_identity = __esmMin((() => {
}));
function matcher(t4) {
  return t4 = extendOwn_default({}, t4), function(n2) {
    return isMatch(n2, t4);
  };
}
var init_matcher = __esmMin((() => {
  init_extendOwn(), init_isMatch();
}));
function property(t4) {
  return t4 = toPath(t4), function(n2) {
    return deepGet(n2, t4);
  };
}
var init_property = __esmMin((() => {
  init__deepGet(), init__toPath();
}));
function optimizeCb(t4, n2, r2) {
  if (n2 === void 0) return t4;
  switch (r2 ?? 3) {
    case 1:
      return function(r3) {
        return t4.call(n2, r3);
      };
    case 3:
      return function(r3, i2, a2) {
        return t4.call(n2, r3, i2, a2);
      };
    case 4:
      return function(r3, i2, a2, o2) {
        return t4.call(n2, r3, i2, a2, o2);
      };
  }
  return function() {
    return t4.apply(n2, arguments);
  };
}
var init__optimizeCb = __esmMin((() => {
}));
function baseIteratee(t4, n2, r2) {
  return t4 == null ? identity : isFunction_default(t4) ? optimizeCb(t4, n2, r2) : isObject(t4) && !isArray_default(t4) ? matcher(t4) : property(t4);
}
var init__baseIteratee = __esmMin((() => {
  init_identity(), init_isFunction(), init_isObject(), init_isArray(), init_matcher(), init_property(), init__optimizeCb();
}));
function iteratee(t4, n2) {
  return baseIteratee(t4, n2, 1 / 0);
}
var init_iteratee = __esmMin((() => {
  init_underscore(), init__baseIteratee(), _$1.iteratee = iteratee;
}));
function cb(t4, n2, r2) {
  return _$1.iteratee === iteratee ? baseIteratee(t4, n2, r2) : _$1.iteratee(t4, n2);
}
var init__cb = __esmMin((() => {
  init_underscore(), init__baseIteratee(), init_iteratee();
}));
function mapObject(t4, n2, r2) {
  n2 = cb(n2, r2);
  for (var i2 = keys(t4), a2 = i2.length, o2 = {}, s2 = 0; s2 < a2; s2++) {
    var c2 = i2[s2];
    o2[c2] = n2(t4[c2], c2, t4);
  }
  return o2;
}
var init_mapObject = __esmMin((() => {
  init__cb(), init_keys();
}));
function noop() {
}
var init_noop = __esmMin((() => {
}));
function propertyOf(t4) {
  return t4 == null ? noop : function(n2) {
    return get(t4, n2);
  };
}
var init_propertyOf = __esmMin((() => {
  init_noop(), init_get();
}));
function times(t4, n2, r2) {
  var i2 = Array(Math.max(0, t4));
  n2 = optimizeCb(n2, r2, 1);
  for (var a2 = 0; a2 < t4; a2++) i2[a2] = n2(a2);
  return i2;
}
var init_times = __esmMin((() => {
  init__optimizeCb();
}));
function random(t4, n2) {
  return n2 ?? (n2 = t4, t4 = 0), t4 + Math.floor(Math.random() * (n2 - t4 + 1));
}
var init_random = __esmMin((() => {
})), now_default, init_now = __esmMin((() => {
  now_default = Date.now || function() {
    return (/* @__PURE__ */ new Date()).getTime();
  };
}));
function createEscaper(t4) {
  var n2 = function(n3) {
    return t4[n3];
  }, r2 = `(?:` + keys(t4).join(`|`) + `)`, i2 = RegExp(r2), a2 = RegExp(r2, `g`);
  return function(t5) {
    return t5 = t5 == null ? `` : `` + t5, i2.test(t5) ? t5.replace(a2, n2) : t5;
  };
}
var init__createEscaper = __esmMin((() => {
  init_keys();
})), _escapeMap_default, init__escapeMap = __esmMin((() => {
  _escapeMap_default = { "&": `&amp;`, "<": `&lt;`, ">": `&gt;`, '"': `&quot;`, "'": `&#x27;`, "`": `&#x60;` };
})), escape_default, init_escape = __esmMin((() => {
  init__createEscaper(), init__escapeMap(), escape_default = createEscaper(_escapeMap_default);
})), _unescapeMap_default, init__unescapeMap = __esmMin((() => {
  init_invert(), init__escapeMap(), _unescapeMap_default = invert(_escapeMap_default);
})), unescape_default, init_unescape = __esmMin((() => {
  init__createEscaper(), init__unescapeMap(), unescape_default = createEscaper(_unescapeMap_default);
})), templateSettings_default, init_templateSettings = __esmMin((() => {
  init_underscore(), templateSettings_default = _$1.templateSettings = { evaluate: /<%([\s\S]+?)%>/g, interpolate: /<%=([\s\S]+?)%>/g, escape: /<%-([\s\S]+?)%>/g };
}));
function escapeChar(t4) {
  return `\\` + escapes[t4];
}
function template(t4, n2, r2) {
  !n2 && r2 && (n2 = r2), n2 = defaults_default({}, n2, _$1.templateSettings);
  var i2 = RegExp([(n2.escape || noMatch).source, (n2.interpolate || noMatch).source, (n2.evaluate || noMatch).source].join(`|`) + `|$`, `g`), a2 = 0, o2 = `__p+='`;
  t4.replace(i2, function(n3, r3, i3, s3, c3) {
    return o2 += t4.slice(a2, c3).replace(escapeRegExp, escapeChar), a2 = c3 + n3.length, r3 ? o2 += `'+
((__t=(` + r3 + `))==null?'':_.escape(__t))+
'` : i3 ? o2 += `'+
((__t=(` + i3 + `))==null?'':__t)+
'` : s3 && (o2 += `';
` + s3 + `
__p+='`), n3;
  }), o2 += `';
`;
  var s2 = n2.variable;
  if (s2) {
    if (!bareIdentifier.test(s2)) throw Error(`variable is not a bare identifier: ` + s2);
  } else o2 = `with(obj||{}){
` + o2 + `}
`, s2 = `obj`;
  o2 = `var __t,__p='',__j=Array.prototype.join,print=function(){__p+=__j.call(arguments,'');};
` + o2 + `return __p;
`;
  var c2;
  try {
    c2 = Function(s2, `_`, o2);
  } catch (t5) {
    throw t5.source = o2, t5;
  }
  var l2 = function(t5) {
    return c2.call(this, t5, _$1);
  };
  return l2.source = `function(` + s2 + `){
` + o2 + `}`, l2;
}
var noMatch, escapes, escapeRegExp, bareIdentifier, init_template = __esmMin((() => {
  init_defaults(), init_underscore(), init_templateSettings(), noMatch = /(.)^/, escapes = { "'": `'`, "\\": `\\`, "\r": `r`, "\n": `n`, "\u2028": `u2028`, "\u2029": `u2029` }, escapeRegExp = /\\|'|\r|\n|\u2028|\u2029/g, bareIdentifier = /^\s*(\w|\$)+\s*$/;
}));
function result(t4, n2, r2) {
  n2 = toPath(n2);
  var i2 = n2.length;
  if (!i2) return isFunction_default(r2) ? r2.call(t4) : r2;
  for (var a2 = 0; a2 < i2; a2++) {
    var o2 = t4?.[n2[a2]];
    o2 === void 0 && (o2 = r2, a2 = i2), t4 = isFunction_default(o2) ? o2.call(t4) : o2;
  }
  return t4;
}
var init_result = __esmMin((() => {
  init_isFunction(), init__toPath();
}));
function uniqueId(t4) {
  var n2 = ++idCounter + ``;
  return t4 ? t4 + n2 : n2;
}
var idCounter, init_uniqueId = __esmMin((() => {
  idCounter = 0;
}));
function chain(t4) {
  var n2 = _$1(t4);
  return n2._chain = true, n2;
}
var init_chain = __esmMin((() => {
  init_underscore();
}));
function executeBound(t4, n2, r2, i2, a2) {
  if (!(i2 instanceof n2)) return t4.apply(r2, a2);
  var o2 = baseCreate(t4.prototype), s2 = t4.apply(o2, a2);
  return isObject(s2) ? s2 : o2;
}
var init__executeBound = __esmMin((() => {
  init__baseCreate(), init_isObject();
})), partial, init_partial = __esmMin((() => {
  init_restArguments(), init__executeBound(), init_underscore(), partial = restArguments(function(t4, n2) {
    var r2 = partial.placeholder, i2 = function() {
      for (var a2 = 0, o2 = n2.length, s2 = Array(o2), c2 = 0; c2 < o2; c2++) s2[c2] = n2[c2] === r2 ? arguments[a2++] : n2[c2];
      for (; a2 < arguments.length; ) s2.push(arguments[a2++]);
      return executeBound(t4, i2, this, this, s2);
    };
    return i2;
  }), partial.placeholder = _$1;
})), bind_default, init_bind = __esmMin((() => {
  init_restArguments(), init_isFunction(), init__executeBound(), bind_default = restArguments(function(t4, n2, r2) {
    if (!isFunction_default(t4)) throw TypeError(`Bind must be called on a function`);
    var i2 = restArguments(function(a2) {
      return executeBound(t4, i2, n2, this, r2.concat(a2));
    });
    return i2;
  });
})), _isArrayLike_default, init__isArrayLike = __esmMin((() => {
  init__createSizePropertyCheck(), init__getLength(), _isArrayLike_default = createSizePropertyCheck(_getLength_default);
}));
function flatten$3(t4, n2, r2, i2) {
  if (i2 ||= [], !n2 && n2 !== 0) n2 = 1 / 0;
  else if (n2 <= 0) return i2.concat(t4);
  for (var a2 = i2.length, o2 = 0, s2 = _getLength_default(t4); o2 < s2; o2++) {
    var c2 = t4[o2];
    if (_isArrayLike_default(c2) && (isArray_default(c2) || isArguments_default(c2))) if (n2 > 1) flatten$3(c2, n2 - 1, r2, i2), a2 = i2.length;
    else for (var l2 = 0, u2 = c2.length; l2 < u2; ) i2[a2++] = c2[l2++];
    else r2 || (i2[a2++] = c2);
  }
  return i2;
}
var init__flatten = __esmMin((() => {
  init__getLength(), init__isArrayLike(), init_isArray(), init_isArguments();
})), bindAll_default, init_bindAll = __esmMin((() => {
  init_restArguments(), init__flatten(), init_bind(), bindAll_default = restArguments(function(t4, n2) {
    n2 = flatten$3(n2, false, false);
    var r2 = n2.length;
    if (r2 < 1) throw Error(`bindAll must be passed function names`);
    for (; r2--; ) {
      var i2 = n2[r2];
      t4[i2] = bind_default(t4[i2], t4);
    }
    return t4;
  });
}));
function memoize(t4, n2) {
  var r2 = function(i2) {
    var a2 = r2.cache, o2 = `` + (n2 ? n2.apply(this, arguments) : i2);
    return has$1(a2, o2) || (a2[o2] = t4.apply(this, arguments)), a2[o2];
  };
  return r2.cache = {}, r2;
}
var init_memoize = __esmMin((() => {
  init__has();
})), delay_default, init_delay = __esmMin((() => {
  init_restArguments(), delay_default = restArguments(function(t4, n2, r2) {
    return setTimeout(function() {
      return t4.apply(null, r2);
    }, n2);
  });
})), defer_default, init_defer = __esmMin((() => {
  init_partial(), init_delay(), init_underscore(), defer_default = partial(delay_default, _$1, 1);
}));
function throttle(t4, n2, r2) {
  var i2, a2, o2, s2, c2 = 0;
  r2 ||= {};
  var l2 = function() {
    c2 = r2.leading === false ? 0 : now_default(), i2 = null, s2 = t4.apply(a2, o2), i2 || (a2 = o2 = null);
  }, u2 = function() {
    var u3 = now_default();
    !c2 && r2.leading === false && (c2 = u3);
    var d2 = n2 - (u3 - c2);
    return a2 = this, o2 = arguments, d2 <= 0 || d2 > n2 ? (i2 &&= (clearTimeout(i2), null), c2 = u3, s2 = t4.apply(a2, o2), i2 || (a2 = o2 = null)) : !i2 && r2.trailing !== false && (i2 = setTimeout(l2, d2)), s2;
  };
  return u2.cancel = function() {
    clearTimeout(i2), c2 = 0, i2 = a2 = o2 = null;
  }, u2;
}
var init_throttle = __esmMin((() => {
  init_now();
}));
function debounce(t4, n2, r2) {
  var i2, a2, o2, s2, c2, l2 = function() {
    var u3 = now_default() - a2;
    n2 > u3 ? i2 = setTimeout(l2, n2 - u3) : (i2 = null, r2 || (s2 = t4.apply(c2, o2)), i2 || (o2 = c2 = null));
  }, u2 = restArguments(function(u3) {
    return c2 = this, o2 = u3, a2 = now_default(), i2 || (i2 = setTimeout(l2, n2), r2 && (s2 = t4.apply(c2, o2))), s2;
  });
  return u2.cancel = function() {
    clearTimeout(i2), i2 = o2 = c2 = null;
  }, u2;
}
var init_debounce = __esmMin((() => {
  init_restArguments(), init_now();
}));
function wrap(t4, n2) {
  return partial(n2, t4);
}
var init_wrap = __esmMin((() => {
  init_partial();
}));
function negate(t4) {
  return function() {
    return !t4.apply(this, arguments);
  };
}
var init_negate = __esmMin((() => {
}));
function compose() {
  var t4 = arguments, n2 = t4.length - 1;
  return function() {
    for (var r2 = n2, i2 = t4[n2].apply(this, arguments); r2--; ) i2 = t4[r2].call(this, i2);
    return i2;
  };
}
var init_compose = __esmMin((() => {
}));
function after(t4, n2) {
  return function() {
    if (--t4 < 1) return n2.apply(this, arguments);
  };
}
var init_after = __esmMin((() => {
}));
function before(t4, n2) {
  var r2;
  return function() {
    return --t4 > 0 && (r2 = n2.apply(this, arguments)), t4 <= 1 && (n2 = null), r2;
  };
}
var init_before = __esmMin((() => {
})), once_default, init_once = __esmMin((() => {
  init_partial(), init_before(), once_default = partial(before, 2);
}));
function findKey(t4, n2, r2) {
  n2 = cb(n2, r2);
  for (var i2 = keys(t4), a2, o2 = 0, s2 = i2.length; o2 < s2; o2++) if (a2 = i2[o2], n2(t4[a2], a2, t4)) return a2;
}
var init_findKey = __esmMin((() => {
  init__cb(), init_keys();
}));
function createPredicateIndexFinder(t4) {
  return function(n2, r2, i2) {
    r2 = cb(r2, i2);
    for (var a2 = _getLength_default(n2), o2 = t4 > 0 ? 0 : a2 - 1; o2 >= 0 && o2 < a2; o2 += t4) if (r2(n2[o2], o2, n2)) return o2;
    return -1;
  };
}
var init__createPredicateIndexFinder = __esmMin((() => {
  init__cb(), init__getLength();
})), findIndex_default, init_findIndex = __esmMin((() => {
  init__createPredicateIndexFinder(), findIndex_default = createPredicateIndexFinder(1);
})), findLastIndex_default, init_findLastIndex = __esmMin((() => {
  init__createPredicateIndexFinder(), findLastIndex_default = createPredicateIndexFinder(-1);
}));
function sortedIndex(t4, n2, r2, i2) {
  r2 = cb(r2, i2, 1);
  for (var a2 = r2(n2), o2 = 0, s2 = _getLength_default(t4); o2 < s2; ) {
    var c2 = Math.floor((o2 + s2) / 2);
    r2(t4[c2]) < a2 ? o2 = c2 + 1 : s2 = c2;
  }
  return o2;
}
var init_sortedIndex = __esmMin((() => {
  init__cb(), init__getLength();
}));
function createIndexFinder(t4, n2, r2) {
  return function(i2, a2, o2) {
    var s2 = 0, c2 = _getLength_default(i2);
    if (typeof o2 == `number`) t4 > 0 ? s2 = o2 >= 0 ? o2 : Math.max(o2 + c2, s2) : c2 = o2 >= 0 ? Math.min(o2 + 1, c2) : o2 + c2 + 1;
    else if (r2 && o2 && c2) return o2 = r2(i2, a2), i2[o2] === a2 ? o2 : -1;
    if (a2 !== a2) return o2 = n2(slice.call(i2, s2, c2), isNaN$1), o2 >= 0 ? o2 + s2 : -1;
    for (o2 = t4 > 0 ? s2 : c2 - 1; o2 >= 0 && o2 < c2; o2 += t4) if (i2[o2] === a2) return o2;
    return -1;
  };
}
var init__createIndexFinder = __esmMin((() => {
  init__getLength(), init__setup(), init_isNaN();
})), indexOf_default, init_indexOf = __esmMin((() => {
  init_sortedIndex(), init_findIndex(), init__createIndexFinder(), indexOf_default = createIndexFinder(1, findIndex_default, sortedIndex);
})), lastIndexOf_default, init_lastIndexOf = __esmMin((() => {
  init_findLastIndex(), init__createIndexFinder(), lastIndexOf_default = createIndexFinder(-1, findLastIndex_default);
}));
function find(t4, n2, r2) {
  var i2 = (_isArrayLike_default(t4) ? findIndex_default : findKey)(t4, n2, r2);
  if (i2 !== void 0 && i2 !== -1) return t4[i2];
}
var init_find = __esmMin((() => {
  init__isArrayLike(), init_findIndex(), init_findKey();
}));
function findWhere(t4, n2) {
  return find(t4, matcher(n2));
}
var init_findWhere = __esmMin((() => {
  init_find(), init_matcher();
}));
function each(t4, n2, r2) {
  n2 = optimizeCb(n2, r2);
  var i2, a2;
  if (_isArrayLike_default(t4)) for (i2 = 0, a2 = t4.length; i2 < a2; i2++) n2(t4[i2], i2, t4);
  else {
    var o2 = keys(t4);
    for (i2 = 0, a2 = o2.length; i2 < a2; i2++) n2(t4[o2[i2]], o2[i2], t4);
  }
  return t4;
}
var init_each = __esmMin((() => {
  init__optimizeCb(), init__isArrayLike(), init_keys();
}));
function map(t4, n2, r2) {
  n2 = cb(n2, r2);
  for (var i2 = !_isArrayLike_default(t4) && keys(t4), a2 = (i2 || t4).length, o2 = Array(a2), s2 = 0; s2 < a2; s2++) {
    var c2 = i2 ? i2[s2] : s2;
    o2[s2] = n2(t4[c2], c2, t4);
  }
  return o2;
}
var init_map = __esmMin((() => {
  init__cb(), init__isArrayLike(), init_keys();
}));
function createReduce(t4) {
  var n2 = function(n3, r2, i2, a2) {
    var o2 = !_isArrayLike_default(n3) && keys(n3), s2 = (o2 || n3).length, c2 = t4 > 0 ? 0 : s2 - 1;
    for (a2 || (i2 = n3[o2 ? o2[c2] : c2], c2 += t4); c2 >= 0 && c2 < s2; c2 += t4) {
      var l2 = o2 ? o2[c2] : c2;
      i2 = r2(i2, n3[l2], l2, n3);
    }
    return i2;
  };
  return function(t5, r2, i2, a2) {
    var o2 = arguments.length >= 3;
    return n2(t5, optimizeCb(r2, a2, 4), i2, o2);
  };
}
var init__createReduce = __esmMin((() => {
  init__isArrayLike(), init_keys(), init__optimizeCb();
})), reduce_default, init_reduce = __esmMin((() => {
  init__createReduce(), reduce_default = createReduce(1);
})), reduceRight_default, init_reduceRight = __esmMin((() => {
  init__createReduce(), reduceRight_default = createReduce(-1);
}));
function filter(t4, n2, r2) {
  var i2 = [];
  return n2 = cb(n2, r2), each(t4, function(t5, r3, a2) {
    n2(t5, r3, a2) && i2.push(t5);
  }), i2;
}
var init_filter = __esmMin((() => {
  init__cb(), init_each();
}));
function reject(t4, n2, r2) {
  return filter(t4, negate(cb(n2)), r2);
}
var init_reject = __esmMin((() => {
  init_filter(), init_negate(), init__cb();
}));
function every(t4, n2, r2) {
  n2 = cb(n2, r2);
  for (var i2 = !_isArrayLike_default(t4) && keys(t4), a2 = (i2 || t4).length, o2 = 0; o2 < a2; o2++) {
    var s2 = i2 ? i2[o2] : o2;
    if (!n2(t4[s2], s2, t4)) return false;
  }
  return true;
}
var init_every = __esmMin((() => {
  init__cb(), init__isArrayLike(), init_keys();
}));
function some(t4, n2, r2) {
  n2 = cb(n2, r2);
  for (var i2 = !_isArrayLike_default(t4) && keys(t4), a2 = (i2 || t4).length, o2 = 0; o2 < a2; o2++) {
    var s2 = i2 ? i2[o2] : o2;
    if (n2(t4[s2], s2, t4)) return true;
  }
  return false;
}
var init_some = __esmMin((() => {
  init__cb(), init__isArrayLike(), init_keys();
}));
function contains(t4, n2, r2, i2) {
  return _isArrayLike_default(t4) || (t4 = values(t4)), (typeof r2 != `number` || i2) && (r2 = 0), indexOf_default(t4, n2, r2) >= 0;
}
var init_contains = __esmMin((() => {
  init__isArrayLike(), init_values(), init_indexOf();
})), invoke_default, init_invoke = __esmMin((() => {
  init_restArguments(), init_isFunction(), init_map(), init__deepGet(), init__toPath(), invoke_default = restArguments(function(t4, n2, r2) {
    var i2, a2;
    return isFunction_default(n2) ? a2 = n2 : (n2 = toPath(n2), i2 = n2.slice(0, -1), n2 = n2[n2.length - 1]), map(t4, function(t5) {
      var o2 = a2;
      if (!o2) {
        if (i2 && i2.length && (t5 = deepGet(t5, i2)), t5 == null) return;
        o2 = t5[n2];
      }
      return o2 == null ? o2 : o2.apply(t5, r2);
    });
  });
}));
function pluck(t4, n2) {
  return map(t4, property(n2));
}
var init_pluck = __esmMin((() => {
  init_map(), init_property();
}));
function where(t4, n2) {
  return filter(t4, matcher(n2));
}
var init_where = __esmMin((() => {
  init_filter(), init_matcher();
}));
function max(t4, n2, r2) {
  var i2 = -1 / 0, a2 = -1 / 0, o2, s2;
  if (n2 == null || typeof n2 == `number` && typeof t4[0] != `object` && t4 != null) {
    t4 = _isArrayLike_default(t4) ? t4 : values(t4);
    for (var c2 = 0, l2 = t4.length; c2 < l2; c2++) o2 = t4[c2], o2 != null && o2 > i2 && (i2 = o2);
  } else n2 = cb(n2, r2), each(t4, function(t5, r3, o3) {
    s2 = n2(t5, r3, o3), (s2 > a2 || s2 === -1 / 0 && i2 === -1 / 0) && (i2 = t5, a2 = s2);
  });
  return i2;
}
var init_max = __esmMin((() => {
  init__isArrayLike(), init_values(), init__cb(), init_each();
}));
function min(t4, n2, r2) {
  var i2 = 1 / 0, a2 = 1 / 0, o2, s2;
  if (n2 == null || typeof n2 == `number` && typeof t4[0] != `object` && t4 != null) {
    t4 = _isArrayLike_default(t4) ? t4 : values(t4);
    for (var c2 = 0, l2 = t4.length; c2 < l2; c2++) o2 = t4[c2], o2 != null && o2 < i2 && (i2 = o2);
  } else n2 = cb(n2, r2), each(t4, function(t5, r3, o3) {
    s2 = n2(t5, r3, o3), (s2 < a2 || s2 === 1 / 0 && i2 === 1 / 0) && (i2 = t5, a2 = s2);
  });
  return i2;
}
var init_min = __esmMin((() => {
  init__isArrayLike(), init_values(), init__cb(), init_each();
}));
function toArray(t4) {
  return t4 ? isArray_default(t4) ? slice.call(t4) : isString_default(t4) ? t4.match(reStrSymbol) : _isArrayLike_default(t4) ? map(t4, identity) : values(t4) : [];
}
var reStrSymbol, init_toArray = __esmMin((() => {
  init_isArray(), init__setup(), init_isString(), init__isArrayLike(), init_map(), init_identity(), init_values(), reStrSymbol = /[^\ud800-\udfff]|[\ud800-\udbff][\udc00-\udfff]|[\ud800-\udfff]/g;
}));
function sample(t4, n2, r2) {
  if (n2 == null || r2) return _isArrayLike_default(t4) || (t4 = values(t4)), t4[random(t4.length - 1)];
  var i2 = toArray(t4), a2 = _getLength_default(i2);
  n2 = Math.max(Math.min(n2, a2), 0);
  for (var o2 = a2 - 1, s2 = 0; s2 < n2; s2++) {
    var c2 = random(s2, o2), l2 = i2[s2];
    i2[s2] = i2[c2], i2[c2] = l2;
  }
  return i2.slice(0, n2);
}
var init_sample = __esmMin((() => {
  init__isArrayLike(), init_values(), init__getLength(), init_random(), init_toArray();
}));
function shuffle(t4) {
  return sample(t4, 1 / 0);
}
var init_shuffle = __esmMin((() => {
  init_sample();
}));
function sortBy(t4, n2, r2) {
  var i2 = 0;
  return n2 = cb(n2, r2), pluck(map(t4, function(t5, r3, a2) {
    return { value: t5, index: i2++, criteria: n2(t5, r3, a2) };
  }).sort(function(t5, n3) {
    var r3 = t5.criteria, i3 = n3.criteria;
    if (r3 !== i3) {
      if (r3 > i3 || r3 === void 0) return 1;
      if (r3 < i3 || i3 === void 0) return -1;
    }
    return t5.index - n3.index;
  }), `value`);
}
var init_sortBy = __esmMin((() => {
  init__cb(), init_pluck(), init_map();
}));
function group(t4, n2) {
  return function(r2, i2, a2) {
    var o2 = n2 ? [[], []] : {};
    return i2 = cb(i2, a2), each(r2, function(n3, a3) {
      t4(o2, n3, i2(n3, a3, r2));
    }), o2;
  };
}
var init__group = __esmMin((() => {
  init__cb(), init_each();
})), groupBy_default, init_groupBy = __esmMin((() => {
  init__group(), init__has(), groupBy_default = group(function(t4, n2, r2) {
    has$1(t4, r2) ? t4[r2].push(n2) : t4[r2] = [n2];
  });
})), indexBy_default, init_indexBy = __esmMin((() => {
  init__group(), indexBy_default = group(function(t4, n2, r2) {
    t4[r2] = n2;
  });
})), countBy_default, init_countBy = __esmMin((() => {
  init__group(), init__has(), countBy_default = group(function(t4, n2, r2) {
    has$1(t4, r2) ? t4[r2]++ : t4[r2] = 1;
  });
})), partition_default, init_partition = __esmMin((() => {
  init__group(), partition_default = group(function(t4, n2, r2) {
    t4[+!r2].push(n2);
  }, true);
}));
function size(t4) {
  return t4 == null ? 0 : _isArrayLike_default(t4) ? t4.length : keys(t4).length;
}
var init_size = __esmMin((() => {
  init__isArrayLike(), init_keys();
}));
function keyInObj(t4, n2, r2) {
  return n2 in r2;
}
var init__keyInObj = __esmMin((() => {
})), pick_default, init_pick = __esmMin((() => {
  init_restArguments(), init_isFunction(), init__optimizeCb(), init_allKeys(), init__keyInObj(), init__flatten(), pick_default = restArguments(function(t4, n2) {
    var r2 = {}, i2 = n2[0];
    if (t4 == null) return r2;
    isFunction_default(i2) ? (n2.length > 1 && (i2 = optimizeCb(i2, n2[1])), n2 = allKeys(t4)) : (i2 = keyInObj, n2 = flatten$3(n2, false, false), t4 = Object(t4));
    for (var a2 = 0, o2 = n2.length; a2 < o2; a2++) {
      var s2 = n2[a2], c2 = t4[s2];
      i2(c2, s2, t4) && (r2[s2] = c2);
    }
    return r2;
  });
})), omit_default, init_omit = __esmMin((() => {
  init_restArguments(), init_isFunction(), init_negate(), init_map(), init__flatten(), init_contains(), init_pick(), omit_default = restArguments(function(t4, n2) {
    var r2 = n2[0], i2;
    return isFunction_default(r2) ? (r2 = negate(r2), n2.length > 1 && (i2 = n2[1])) : (n2 = map(flatten$3(n2, false, false), String), r2 = function(t5, r3) {
      return !contains(n2, r3);
    }), pick_default(t4, r2, i2);
  });
}));
function initial(t4, n2, r2) {
  return slice.call(t4, 0, Math.max(0, t4.length - (n2 == null || r2 ? 1 : n2)));
}
var init_initial = __esmMin((() => {
  init__setup();
}));
function first(t4, n2, r2) {
  return t4 == null || t4.length < 1 ? n2 == null || r2 ? void 0 : [] : n2 == null || r2 ? t4[0] : initial(t4, t4.length - n2);
}
var init_first = __esmMin((() => {
  init_initial();
}));
function rest(t4, n2, r2) {
  return slice.call(t4, n2 == null || r2 ? 1 : n2);
}
var init_rest = __esmMin((() => {
  init__setup();
}));
function last(t4, n2, r2) {
  return t4 == null || t4.length < 1 ? n2 == null || r2 ? void 0 : [] : n2 == null || r2 ? t4[t4.length - 1] : rest(t4, Math.max(0, t4.length - n2));
}
var init_last = __esmMin((() => {
  init_rest();
}));
function compact(t4) {
  return filter(t4, Boolean);
}
var init_compact = __esmMin((() => {
  init_filter();
}));
function flatten$2(t4, n2) {
  return flatten$3(t4, n2, false);
}
var init_flatten = __esmMin((() => {
  init__flatten();
})), difference_default, init_difference = __esmMin((() => {
  init_restArguments(), init__flatten(), init_filter(), init_contains(), difference_default = restArguments(function(t4, n2) {
    return n2 = flatten$3(n2, true, true), filter(t4, function(t5) {
      return !contains(n2, t5);
    });
  });
})), without_default, init_without = __esmMin((() => {
  init_restArguments(), init_difference(), without_default = restArguments(function(t4, n2) {
    return difference_default(t4, n2);
  });
}));
function uniq(t4, n2, r2, i2) {
  isBoolean(n2) || (i2 = r2, r2 = n2, n2 = false), r2 != null && (r2 = cb(r2, i2));
  for (var a2 = [], o2 = [], s2 = 0, c2 = _getLength_default(t4); s2 < c2; s2++) {
    var l2 = t4[s2], u2 = r2 ? r2(l2, s2, t4) : l2;
    n2 && !r2 ? ((!s2 || o2 !== u2) && a2.push(l2), o2 = u2) : r2 ? contains(o2, u2) || (o2.push(u2), a2.push(l2)) : contains(a2, l2) || a2.push(l2);
  }
  return a2;
}
var init_uniq = __esmMin((() => {
  init_isBoolean(), init__cb(), init__getLength(), init_contains();
})), union_default, init_union = __esmMin((() => {
  init_restArguments(), init_uniq(), init__flatten(), union_default = restArguments(function(t4) {
    return uniq(flatten$3(t4, true, true));
  });
}));
function intersection(t4) {
  for (var n2 = [], r2 = arguments.length, i2 = 0, a2 = _getLength_default(t4); i2 < a2; i2++) {
    var o2 = t4[i2];
    if (!contains(n2, o2)) {
      var s2;
      for (s2 = 1; s2 < r2 && contains(arguments[s2], o2); s2++) ;
      s2 === r2 && n2.push(o2);
    }
  }
  return n2;
}
var init_intersection = __esmMin((() => {
  init__getLength(), init_contains();
}));
function unzip(t4) {
  for (var n2 = t4 && max(t4, _getLength_default).length || 0, r2 = Array(n2), i2 = 0; i2 < n2; i2++) r2[i2] = pluck(t4, i2);
  return r2;
}
var init_unzip = __esmMin((() => {
  init_max(), init__getLength(), init_pluck();
})), zip_default, init_zip = __esmMin((() => {
  init_restArguments(), init_unzip(), zip_default = restArguments(unzip);
}));
function object(t4, n2) {
  for (var r2 = {}, i2 = 0, a2 = _getLength_default(t4); i2 < a2; i2++) n2 ? r2[t4[i2]] = n2[i2] : r2[t4[i2][0]] = t4[i2][1];
  return r2;
}
var init_object = __esmMin((() => {
  init__getLength();
}));
function range(t4, n2, r2) {
  n2 ?? (n2 = t4 || 0, t4 = 0), r2 ||= n2 < t4 ? -1 : 1;
  for (var i2 = Math.max(Math.ceil((n2 - t4) / r2), 0), a2 = Array(i2), o2 = 0; o2 < i2; o2++, t4 += r2) a2[o2] = t4;
  return a2;
}
var init_range = __esmMin((() => {
}));
function chunk(t4, n2) {
  if (n2 == null || n2 < 1) return [];
  for (var r2 = [], i2 = 0, a2 = t4.length; i2 < a2; ) r2.push(slice.call(t4, i2, i2 += n2));
  return r2;
}
var init_chunk = __esmMin((() => {
  init__setup();
}));
function chainResult(t4, n2) {
  return t4._chain ? _$1(n2).chain() : n2;
}
var init__chainResult = __esmMin((() => {
  init_underscore();
}));
function mixin(t4) {
  return each(functions$1(t4), function(n2) {
    var r2 = _$1[n2] = t4[n2];
    _$1.prototype[n2] = function() {
      var t5 = [this._wrapped];
      return push.apply(t5, arguments), chainResult(this, r2.apply(_$1, t5));
    };
  }), _$1;
}
var init_mixin = __esmMin((() => {
  init_underscore(), init_each(), init_functions(), init__setup(), init__chainResult();
})), underscore_array_methods_default, init_underscore_array_methods = __esmMin((() => {
  init_underscore(), init_each(), init__setup(), init__chainResult(), each([`pop`, `push`, `reverse`, `shift`, `sort`, `splice`, `unshift`], function(t4) {
    var n2 = ArrayProto[t4];
    _$1.prototype[t4] = function() {
      var r2 = this._wrapped;
      return r2 != null && (n2.apply(r2, arguments), (t4 === `shift` || t4 === `splice`) && r2.length === 0 && delete r2[0]), chainResult(this, r2);
    };
  }), each([`concat`, `join`, `slice`], function(t4) {
    var n2 = ArrayProto[t4];
    _$1.prototype[t4] = function() {
      var t5 = this._wrapped;
      return t5 != null && (t5 = n2.apply(t5, arguments)), chainResult(this, t5);
    };
  }), underscore_array_methods_default = _$1;
})), modules_exports = __exportAll({ VERSION: () => VERSION, after: () => after, all: () => every, allKeys: () => allKeys, any: () => some, assign: () => extendOwn_default, before: () => before, bind: () => bind_default, bindAll: () => bindAll_default, chain: () => chain, chunk: () => chunk, clone: () => clone, collect: () => map, compact: () => compact, compose: () => compose, constant: () => constant, contains: () => contains, countBy: () => countBy_default, create: () => create, debounce: () => debounce, default: () => underscore_array_methods_default, defaults: () => defaults_default, defer: () => defer_default, delay: () => delay_default, detect: () => find, difference: () => difference_default, drop: () => rest, each: () => each, escape: () => escape_default, every: () => every, extend: () => extend_default, extendOwn: () => extendOwn_default, filter: () => filter, find: () => find, findIndex: () => findIndex_default, findKey: () => findKey, findLastIndex: () => findLastIndex_default, findWhere: () => findWhere, first: () => first, flatten: () => flatten$2, foldl: () => reduce_default, foldr: () => reduceRight_default, forEach: () => each, functions: () => functions$1, get: () => get, groupBy: () => groupBy_default, has: () => has, head: () => first, identity: () => identity, include: () => contains, includes: () => contains, indexBy: () => indexBy_default, indexOf: () => indexOf_default, initial: () => initial, inject: () => reduce_default, intersection: () => intersection, invert: () => invert, invoke: () => invoke_default, isArguments: () => isArguments_default, isArray: () => isArray_default, isArrayBuffer: () => isArrayBuffer_default, isBoolean: () => isBoolean, isDataView: () => isDataView_default, isDate: () => isDate_default, isElement: () => isElement, isEmpty: () => isEmpty, isEqual: () => isEqual, isError: () => isError_default, isFinite: () => isFinite$1, isFunction: () => isFunction_default, isMap: () => isMap_default, isMatch: () => isMatch, isNaN: () => isNaN$1, isNull: () => isNull, isNumber: () => isNumber_default, isObject: () => isObject, isRegExp: () => isRegExp_default, isSet: () => isSet_default, isString: () => isString_default, isSymbol: () => isSymbol_default, isTypedArray: () => isTypedArray_default, isUndefined: () => isUndefined, isWeakMap: () => isWeakMap_default, isWeakSet: () => isWeakSet_default, iteratee: () => iteratee, keys: () => keys, last: () => last, lastIndexOf: () => lastIndexOf_default, map: () => map, mapObject: () => mapObject, matcher: () => matcher, matches: () => matcher, max: () => max, memoize: () => memoize, methods: () => functions$1, min: () => min, mixin: () => mixin, negate: () => negate, noop: () => noop, now: () => now_default, object: () => object, omit: () => omit_default, once: () => once_default, pairs: () => pairs, partial: () => partial, partition: () => partition_default, pick: () => pick_default, pluck: () => pluck, property: () => property, propertyOf: () => propertyOf, random: () => random, range: () => range, reduce: () => reduce_default, reduceRight: () => reduceRight_default, reject: () => reject, rest: () => rest, restArguments: () => restArguments, result: () => result, sample: () => sample, select: () => filter, shuffle: () => shuffle, size: () => size, some: () => some, sortBy: () => sortBy, sortedIndex: () => sortedIndex, tail: () => rest, take: () => first, tap: () => tap, template: () => template, templateSettings: () => templateSettings_default, throttle: () => throttle, times: () => times, toArray: () => toArray, toPath: () => toPath$1, transpose: () => unzip, unescape: () => unescape_default, union: () => union_default, uniq: () => uniq, unique: () => uniq, uniqueId: () => uniqueId, unzip: () => unzip, values: () => values, where: () => where, without: () => without_default, wrap: () => wrap, zip: () => zip_default }), init_modules = __esmMin((() => {
  init__setup(), init_restArguments(), init_isObject(), init_isNull(), init_isUndefined(), init_isBoolean(), init_isElement(), init_isString(), init_isNumber(), init_isDate(), init_isRegExp(), init_isError(), init_isSymbol(), init_isArrayBuffer(), init_isDataView(), init_isArray(), init_isFunction(), init_isArguments(), init_isFinite(), init_isNaN(), init_isTypedArray(), init_isEmpty(), init_isMatch(), init_isEqual(), init_isMap(), init_isWeakMap(), init_isSet(), init_isWeakSet(), init_keys(), init_allKeys(), init_values(), init_pairs(), init_invert(), init_functions(), init_extend(), init_extendOwn(), init_defaults(), init_create(), init_clone(), init_tap(), init_get(), init_has(), init_mapObject(), init_identity(), init_constant(), init_noop(), init_toPath(), init_property(), init_propertyOf(), init_matcher(), init_times(), init_random(), init_now(), init_escape(), init_unescape(), init_templateSettings(), init_template(), init_result(), init_uniqueId(), init_chain(), init_iteratee(), init_partial(), init_bind(), init_bindAll(), init_memoize(), init_delay(), init_defer(), init_throttle(), init_debounce(), init_wrap(), init_negate(), init_compose(), init_after(), init_before(), init_once(), init_findKey(), init_findIndex(), init_findLastIndex(), init_sortedIndex(), init_indexOf(), init_lastIndexOf(), init_find(), init_findWhere(), init_each(), init_map(), init_reduce(), init_reduceRight(), init_filter(), init_reject(), init_every(), init_some(), init_contains(), init_invoke(), init_pluck(), init_where(), init_max(), init_min(), init_shuffle(), init_sample(), init_sortBy(), init_groupBy(), init_indexBy(), init_countBy(), init_partition(), init_toArray(), init_size(), init_pick(), init_omit(), init_first(), init_initial(), init_last(), init_rest(), init_compact(), init_flatten(), init_without(), init_uniq(), init_union(), init_intersection(), init_difference(), init_unzip(), init_zip(), init_object(), init_range(), init_chunk(), init_mixin(), init_underscore_array_methods();
})), _, init_index_default = __esmMin((() => {
  init_modules(), _ = mixin(modules_exports), _._ = _;
})), index_all_exports = __exportAll({ VERSION: () => VERSION, after: () => after, all: () => every, allKeys: () => allKeys, any: () => some, assign: () => extendOwn_default, before: () => before, bind: () => bind_default, bindAll: () => bindAll_default, chain: () => chain, chunk: () => chunk, clone: () => clone, collect: () => map, compact: () => compact, compose: () => compose, constant: () => constant, contains: () => contains, countBy: () => countBy_default, create: () => create, debounce: () => debounce, default: () => _, defaults: () => defaults_default, defer: () => defer_default, delay: () => delay_default, detect: () => find, difference: () => difference_default, drop: () => rest, each: () => each, escape: () => escape_default, every: () => every, extend: () => extend_default, extendOwn: () => extendOwn_default, filter: () => filter, find: () => find, findIndex: () => findIndex_default, findKey: () => findKey, findLastIndex: () => findLastIndex_default, findWhere: () => findWhere, first: () => first, flatten: () => flatten$2, foldl: () => reduce_default, foldr: () => reduceRight_default, forEach: () => each, functions: () => functions$1, get: () => get, groupBy: () => groupBy_default, has: () => has, head: () => first, identity: () => identity, include: () => contains, includes: () => contains, indexBy: () => indexBy_default, indexOf: () => indexOf_default, initial: () => initial, inject: () => reduce_default, intersection: () => intersection, invert: () => invert, invoke: () => invoke_default, isArguments: () => isArguments_default, isArray: () => isArray_default, isArrayBuffer: () => isArrayBuffer_default, isBoolean: () => isBoolean, isDataView: () => isDataView_default, isDate: () => isDate_default, isElement: () => isElement, isEmpty: () => isEmpty, isEqual: () => isEqual, isError: () => isError_default, isFinite: () => isFinite$1, isFunction: () => isFunction_default, isMap: () => isMap_default, isMatch: () => isMatch, isNaN: () => isNaN$1, isNull: () => isNull, isNumber: () => isNumber_default, isObject: () => isObject, isRegExp: () => isRegExp_default, isSet: () => isSet_default, isString: () => isString_default, isSymbol: () => isSymbol_default, isTypedArray: () => isTypedArray_default, isUndefined: () => isUndefined, isWeakMap: () => isWeakMap_default, isWeakSet: () => isWeakSet_default, iteratee: () => iteratee, keys: () => keys, last: () => last, lastIndexOf: () => lastIndexOf_default, map: () => map, mapObject: () => mapObject, matcher: () => matcher, matches: () => matcher, max: () => max, memoize: () => memoize, methods: () => functions$1, min: () => min, mixin: () => mixin, negate: () => negate, noop: () => noop, now: () => now_default, object: () => object, omit: () => omit_default, once: () => once_default, pairs: () => pairs, partial: () => partial, partition: () => partition_default, pick: () => pick_default, pluck: () => pluck, property: () => property, propertyOf: () => propertyOf, random: () => random, range: () => range, reduce: () => reduce_default, reduceRight: () => reduceRight_default, reject: () => reject, rest: () => rest, restArguments: () => restArguments, result: () => result, sample: () => sample, select: () => filter, shuffle: () => shuffle, size: () => size, some: () => some, sortBy: () => sortBy, sortedIndex: () => sortedIndex, tail: () => rest, take: () => first, tap: () => tap, template: () => template, templateSettings: () => templateSettings_default, throttle: () => throttle, times: () => times, toArray: () => toArray, toPath: () => toPath$1, transpose: () => unzip, unescape: () => unescape_default, union: () => union_default, uniq: () => uniq, unique: () => uniq, uniqueId: () => uniqueId, unzip: () => unzip, values: () => values, where: () => where, without: () => without_default, wrap: () => wrap, zip: () => zip_default }), init_index_all = __esmMin((() => {
  init_index_default(), init_modules();
}));
init_index_all();
var nonTupleVectorOperators = [`vector`, `altvector`];
function coerce_tuple_array_vectors({ operator1: t4, operator2: n2, operands1: r2, operands2: i2, coerce_tuples_arrays: a2, coerce_vectors: o2 }) {
  let s2 = t4 === n2;
  if (!s2) {
    if (a2) {
      if (t4 === `tuple`) {
        if (nonTupleVectorOperators.includes(n2)) t4 = n2, s2 = true;
        else if (n2 === `interval` && r2.length === 2) {
          let n3 = i2[1];
          n3[1] || n3[2] || (s2 = true, t4 = `interval`, r2 = [[`tuple`, ...r2], [`tuple`, false, false]]);
        }
      } else if (n2 === `tuple`) {
        if (nonTupleVectorOperators.includes(t4)) n2 = t4, s2 = true;
        else if (t4 === `interval` && i2.length === 2) {
          let t5 = r2[1];
          t5[1] || t5[2] || (s2 = true, n2 = `interval`, i2 = [[`tuple`, ...i2], [`tuple`, false, false]]);
        }
      }
      if (!s2) {
        if (t4 === `array` && r2.length === 2) {
          if (n2 === `interval`) {
            let n3 = i2[1];
            n3[1] && n3[2] && (s2 = true, t4 = `interval`, r2 = [[`tuple`, ...r2], [`tuple`, true, true]]);
          }
        } else if (n2 === `array` && i2.length === 2 && t4 === `interval`) {
          let t5 = r2[1];
          t5[1] && t5[2] && (s2 = true, n2 = `interval`, i2 = [[`tuple`, ...i2], [`tuple`, true, true]]);
        }
      }
    }
    !s2 && o2 && nonTupleVectorOperators.includes(t4) && nonTupleVectorOperators.includes(n2) && (s2 = true, t4 = n2 = `vector`);
  }
  return { operator1: t4, operator2: n2, operands1: r2, operands2: i2 };
}
function handleNaNInfinityStringify(t4, n2) {
  return n2 === n2 ? n2 === 1 / 0 ? `1/0` : n2 === -1 / 0 ? `-1/0` : n2 : `0/0`;
}
function handleNaNInfinityParse(t4, n2) {
  return n2 === `0/0` ? NaN : n2 === `1/0` ? 1 / 0 : n2 === `-1/0` ? -1 / 0 : n2;
}
function deepClone(t4) {
  return JSON.parse(JSON.stringify(t4, handleNaNInfinityStringify), handleNaNInfinityParse);
}
var equal = function(t4, n2, { allowed_error_in_numbers: r2 = 0, include_error_in_number_exponents: i2 = false, allowed_error_is_absolute: a2 = false, coerce_tuples_arrays: o2 = true, coerce_vectors: s2 = true } = {}) {
  if (!(Array.isArray(t4) && Array.isArray(n2))) {
    if (typeof t4 != typeof n2) return false;
    if (typeof t4 == `number` && Number.isFinite(t4)) {
      let i3 = 1e-14, o3 = Math.min(Math.abs(t4), Math.abs(n2));
      return a2 ? (i3 *= o3, r2 > i3 && (i3 = r2)) : (r2 > i3 && (i3 = r2), i3 *= o3), Math.abs(t4 - n2) <= i3;
    }
    return t4 === n2;
  }
  var c2 = t4[0], l2 = t4.slice(1), u2 = n2[0], d2 = n2.slice(1);
  return c2 !== u2 && ({ operator1: c2, operator2: u2, operands1: l2, operands2: d2 } = coerce_tuple_array_vectors({ operator1: c2, operator2: u2, operands1: l2, operands2: d2, coerce_tuples_arrays: o2, coerce_vectors: s2 })), c2 !== u2 || l2.length !== d2.length ? false : r2 > 0 && !i2 && c2 === `^` ? equal(l2[0], d2[0], { allowed_error_in_numbers: r2, include_error_in_number_exponents: i2, allowed_error_is_absolute: a2, coerce_tuples_arrays: o2, coerce_vectors: s2 }) ? equal(l2[1], d2[1], { coerce_tuples_arrays: o2, coerce_vectors: s2 }) : false : _.every(_.zip(l2, d2), function(t5) {
    return equal(t5[0], t5[1], { allowed_error_in_numbers: r2, include_error_in_number_exponents: i2, allowed_error_is_absolute: a2, coerce_tuples_arrays: o2, coerce_vectors: s2 });
  });
}, match$1 = function(t4, n2, r2) {
  var i2 = false;
  r2 === void 0 ? r2 = {} : r2.allow_extended_match && (i2 = true, r2 = Object.assign({}, r2), delete r2.allow_extended_match);
  var a2 = r2.variables;
  if (a2 === void 0) {
    a2 = {};
    let t5 = variables(n2);
    for (let n3 = 0; n3 < t5.length; n3++) a2[t5[n3]] = true;
    r2 = Object.assign({}, r2), r2.variables = a2;
  }
  if (n2 in a2) {
    let r3 = a2[n2];
    if (r3 !== true) if (r3 instanceof RegExp) {
      if (typeof t4 == `string`) {
        if (!t4.match(r3)) return false;
      } else if (!JSON.stringify(t4).match(r3)) return false;
    } else if (typeof a2[n2] == `function`) {
      if (!a2[n2](t4)) return false;
    } else return false;
    let i3 = {};
    return i3[n2] = t4, i3;
  }
  if (r2.allow_permutations && (t4 = default_order(t4), n2 = default_order(n2)), !Array.isArray(n2)) return t4 === n2 ? {} : false;
  var o2 = allChildren(t4), s2 = n2[0], c2 = n2.slice(1);
  if (!Array.isArray(t4) || t4[0] !== s2 || o2.length < c2.length) {
    if (Array.isArray(r2.allow_implicit_identities)) {
      let i3 = matchImplicitIdentity(t4, n2, r2);
      if (i3) return i3;
    }
    if (s2 === `*` && Array.isArray(t4) && t4[0] === `-` && Array.isArray(t4[1]) && t4[1][0] === `*`) o2 = allChildren(t4[1]), o2[0] = [`-`, o2[0]];
    else return false;
  }
  return matchOperands(s2, o2, c2, r2, i2) || (Array.isArray(r2.allow_implicit_identities) ? matchImplicitIdentity([s2].concat(o2), n2, r2) : false);
};
function matchOperands(t4, n2, r2, i2, a2) {
  var o2 = r2.map((t5) => ({})), s2 = r2.length, c2 = false;
  i2.allow_permutations && (t4 === `*` ? c2 = ![...n2, ...r2].some((t5) => [`tuple`, `list`, `vector`, `altvector`, `interval`, `set`, `array`, `matrix`].includes(t5[0])) : (t4 === `+` || t4 === `=` || t4 === `and` || t4 === `or` || t4 === `ne` || t4 === `union` || t4 === `intersect`) && (c2 = true));
  function l2(u3, d3, p2) {
    let m2 = 1, h2 = 1;
    (is_associative$1[t4] || [`tuple`, `vector`, `altvector`].includes(t4)) && (m2 = u3.length - (s2 - d3 - 1), h2 = u3.length), i2.max_group !== void 0 && (m2 = i2.max_group < m2 ? i2.max_group : m2), i2.max_last_group !== void 0 && (h2 = i2.max_last_group < h2 ? i2.max_last_group : h2);
    let g2;
    if (!a2 && d3 === s2 - 1) if (u3.length <= h2) g2 = [u3];
    else return false;
    else if (c2) g2 = subsets(u3, m2);
    else {
      g2 = [];
      for (let t5 = 1; t5 <= m2; t5++) g2.push(u3.slice(0, t5));
    }
    for (let a3 of g2) {
      let c3 = o2[d3][a3];
      if (c3 === void 0) {
        let s3 = a3.reduce(function(t5, r3) {
          return t5.concat([n2[r3]]);
        }, []);
        s3 = s3.length > 1 ? [t4].concat(s3) : s3[0], c3 = match$1(s3, r2[d3], i2), o2[d3][a3] = c3;
      }
      if (!c3 || !_.every(_.intersection(Object.keys(p2), Object.keys(c3)), function(t5) {
        return equal(p2[t5], c3[t5]);
      })) continue;
      let m3 = Object.assign({}, c3);
      Object.assign(m3, p2);
      let h3 = u3.filter((t5) => !a3.includes(t5));
      if (d3 === s2 - 1) return { matches: m3, skipped: h3.reduce(function(t5, r3) {
        return t5.concat([n2[r3]]);
      }, []) };
      let g3 = l2(h3, d3 + 1, m3);
      if (g3) return g3;
    }
    return false;
  }
  var u2 = {}, d2 = [...Array(n2.length).keys()];
  if (c2) {
    let t5 = l2(d2, 0, {});
    return t5 ? (u2 = t5.matches, t5.skipped.length > 0 && (u2._skipped = t5.skipped), u2) : false;
  } else {
    let t5 = a2 ? n2.length - s2 : 0, r3 = [], i3;
    for (let a3 = 0; a3 <= t5 && (i3 = l2(d2, 0, {}), !i3); a3++) d2 = d2.slice(1), r3.push(n2[a3]);
    return i3 ? (u2 = i3.matches, i3.skipped.length > 0 && (u2._skipped = i3.skipped), r3.length > 0 && (u2._skipped_before = r3), u2) : false;
  }
}
function matchImplicitIdentity(t4, n2, r2) {
  var i2 = n2[0], a2 = n2.slice(1);
  if (!(i2 === `+` || i2 === `*` || i2 === `^`)) return false;
  var o2 = [];
  for (let t5 = 0; t5 < a2.length; t5++) {
    let n3 = a2[t5];
    typeof n3 == `string` && r2.allow_implicit_identities.includes(n3) && o2.push(n3);
  }
  if (o2.length === 0) return false;
  for (let i3 of o2) {
    let a3 = match_one_implicit_identity(t4, n2, r2, i3);
    if (a3) return a3;
  }
  return false;
}
function match_one_implicit_identity(t4, n2, r2, i2) {
  var a2 = n2[0], o2 = n2.slice(1), s2 = {};
  a2 === `+` ? s2[i2] = 0 : s2[i2] = 1, a2 === `*` && o2.includes(i2) && Array.isArray(t4) && t4[0] === `-` && (s2[i2] = -1, t4 = t4[1]);
  var c2 = o2.indexOf(i2);
  if (o2.splice(c2, 1), a2 === `^` && c2 === 0) return false;
  n2 = o2.length === 1 ? o2[0] : [a2].concat(o2);
  var l2 = match$1(t4, n2, r2);
  if (l2) {
    if (i2 in l2 && !equal(l2[i2], s2[i2])) return false;
    Object.assign(s2, l2);
  } else return false;
  return s2;
}
var substitute$1 = function(t4, n2) {
  return typeof t4 == `number` || typeof t4 == `boolean` ? t4 : typeof t4 == `string` ? n2[t4] === void 0 ? t4 : deepClone(n2[t4]) : Array.isArray(t4) ? [t4[0]].concat(t4.slice(1).map(function(t5) {
    return substitute$1(t5, n2);
  })) : [];
}, transform = function(t4, n2) {
  if (Array.isArray(t4)) {
    let r2 = [t4[0]];
    for (let i2 = 1; i2 < t4.length; i2++) r2.push(transform(t4[i2], n2));
    return n2(r2);
  }
  return n2(t4);
}, applyAllTransformations = function(t4, n2, r2) {
  r2 === void 0 && (r2 = 5);
  for (var i2 = t4, a2; r2 > 0; r2--) {
    a2 = i2;
    for (let t5 = 0; t5 < n2.length; t5++) {
      let r3 = n2[t5][0], a3 = n2[t5][1], o2 = n2[t5][2];
      o2 === void 0 && (o2 = {}), i2 = transform(i2, function(t6) {
        let n3 = match$1(t6, r3, o2);
        if (n3) {
          let t7 = substitute$1(a3, n3), i3 = o2.assumptions ? substitute$1(o2.assumptions, n3) : void 0;
          o2.evaluate_numbers && (t7 = evaluate_numbers(t7, { max_digits: o2.max_digits, evaluate_functions: o2.evaluate_functions, assumptions: i3 }));
          let s2 = [], c2 = [];
          return n3._skipped && (s2 = n3._skipped), n3._skipped_before && (c2 = n3._skipped_before), (c2.length > 0 || s2.length > 0) && (Array.isArray(t7) && (t7 = t7[0] === r3[0] ? t7.slice(1) : [t7]), t7 = [r3[0]].concat(c2, t7, s2)), o2.evaluate_numbers && (t7 = evaluate_numbers(t7, { max_digits: o2.max_digits, evaluate_functions: o2.evaluate_functions, assumptions: i3 })), t7;
        } else return t6;
      });
    }
    if (equal(a2, i2)) return i2;
  }
  return i2;
}, math = mathjs_default, operators$2 = { "+": function(t4) {
  return t4.length === 1 ? t4[0] : new math.OperatorNode(`+`, `add`, t4);
}, "*": function(t4) {
  return new math.OperatorNode(`*`, `multiply`, t4);
}, "/": function(t4) {
  return new math.OperatorNode(`/`, `divide`, t4);
}, "-": function(t4) {
  return new math.OperatorNode(`-`, `unaryMinus`, [t4[0]]);
}, "^": function(t4) {
  return new math.OperatorNode(`^`, `pow`, t4);
}, vector: function(t4) {
  return new math.ArrayNode(t4);
}, altvector: function(t4) {
  return new math.ArrayNode(t4);
}, and: function(t4) {
  return new math.OperatorNode(`and`, `and`, t4);
}, or: function(t4) {
  return new math.OperatorNode(`or`, `or`, t4);
}, not: function(t4) {
  return new math.OperatorNode(`not`, `not`, [t4[0]]);
}, "<": function(t4) {
  return new math.OperatorNode(`<`, `smaller`, t4);
}, ">": function(t4) {
  return new math.OperatorNode(`>`, `larger`, t4);
}, le: function(t4) {
  return new math.OperatorNode(`<=`, `smallerEq`, t4);
}, ge: function(t4) {
  return new math.OperatorNode(`>=`, `largerEq`, t4);
}, ne: function(t4) {
  return new math.OperatorNode(`!=`, `unequal`, t4);
}, binom: function(t4) {
  let n2 = new math.SymbolNode(`combinations`);
  return new math.FunctionNode(n2, t4);
} }, functionConversions = { nCr: `combinations`, nPr: `permutations`, binom: `combinations` }, astToMathjs$2 = class {
  constructor({ mathjs: t4 = null } = {}) {
    t4 && (math = t4);
  }
  convert(t4) {
    if (typeof t4 == `number`) return Number.isFinite(t4) ? new math.ConstantNode(t4) : Number.isNaN(t4) ? new math.SymbolNode(`NaN`) : t4 < 0 ? operators$2[`-`]([new math.SymbolNode(`Infinity`)]) : new math.SymbolNode(`Infinity`);
    if (typeof t4 == `string`) return new math.SymbolNode(t4);
    if (typeof t4 == `boolean`) throw Error(`no support for boolean`);
    if (!Array.isArray(t4)) throw Error(`Invalid ast`);
    let n2 = t4[0], r2 = t4.slice(1);
    if (n2 === `apply`) {
      if (typeof r2[0] != `string`) throw Error(`Non string functions not implemented for conversion to mathjs`);
      if (r2[0] === `factorial`) return new math.OperatorNode(`!`, `factorial`, [this.convert(r2[1])]);
      let t5 = functionConversions[r2[0]];
      t5 ||= r2[0];
      let n3 = new math.SymbolNode(t5), i2 = r2[1], a2;
      return a2 = i2[0] === `tuple` ? i2.slice(1).map((function(t6, n4) {
        return this.convert(t6);
      }).bind(this)) : [this.convert(i2)], r2[0] === `count` && (a2.length > 2 || !(a2[0] instanceof math.ArrayNode)) && (a2 = [new math.ArrayNode(a2)]), new math.FunctionNode(n3, a2);
    }
    if (n2 === `lts` || n2 === `gts`) {
      let t5 = r2[0], i2 = r2[1];
      if (t5[0] !== `tuple` || i2[0] !== `tuple`) throw Error(`Badly formed ast`);
      let a2 = t5.slice(1).map((function(t6, n3) {
        return this.convert(t6);
      }).bind(this)), o2 = [];
      for (let r3 = 1; r3 < t5.length - 1; r3++) i2[r3] ? n2 === `lts` ? o2.push(new math.OperatorNode(`<`, `smaller`, a2.slice(r3 - 1, r3 + 1))) : o2.push(new math.OperatorNode(`>`, `larger`, a2.slice(r3 - 1, r3 + 1))) : n2 === `lts` ? o2.push(new math.OperatorNode(`<=`, `smallerEq`, a2.slice(r3 - 1, r3 + 1))) : o2.push(new math.OperatorNode(`>=`, `largerEq`, a2.slice(r3 - 1, r3 + 1)));
      let s2 = new math.OperatorNode(`and`, `and`, o2.slice(0, 2));
      for (let t6 = 2; t6 < o2.length; t6++) s2 = new math.OperatorNode(`and`, `and`, [s2, o2[t6]]);
      return s2;
    }
    if (n2 === `=`) {
      let t5 = r2.map((function(t6, n4) {
        return this.convert(t6);
      }).bind(this)), n3 = [];
      for (let r3 = 1; r3 < t5.length; r3++) n3.push(new math.OperatorNode(`==`, `equal`, t5.slice(r3 - 1, r3 + 1)));
      if (n3.length === 1) return n3[0];
      let i2 = new math.OperatorNode(`and`, `and`, n3.slice(0, 2));
      for (let t6 = 2; t6 < n3.length; t6++) i2 = new math.OperatorNode(`and`, `and`, [i2, n3[t6]]);
      return i2;
    }
    if (n2 === `in` || n2 === `notin` || n2 === `ni` || n2 === `notni`) {
      let t5, i2;
      if (n2 === `in` || n2 === `notin` ? (t5 = r2[0], i2 = r2[1]) : (t5 = r2[1], i2 = r2[0]), typeof t5 != `number` && typeof t5 != `string`) throw Error(`Set membership non-string variables not implemented for conversion to mathjs`);
      if (t5 = this.convert(t5), i2[0] !== `interval`) throw Error(`Set membership in non-intervals not implemented for conversion to mathjs`);
      let a2 = i2[1], o2 = i2[2];
      if (a2[0] !== `tuple` || o2[0] !== `tuple`) throw Error(`Badly formed ast`);
      let s2 = this.convert(a2[1]), c2 = this.convert(a2[2]), l2 = [];
      o2[1] ? l2.push(new math.OperatorNode(`>=`, `largerEq`, [t5, s2])) : l2.push(new math.OperatorNode(`>`, `larger`, [t5, s2])), o2[2] ? l2.push(new math.OperatorNode(`<=`, `smallerEq`, [t5, c2])) : l2.push(new math.OperatorNode(`<`, `smaller`, [t5, c2]));
      let u2 = new math.OperatorNode(`and`, `and`, l2);
      return (n2 === `notin` || n2 === `notni`) && (u2 = new math.OperatorNode(`not`, `not`, [u2])), u2;
    }
    if (n2 === `subset` || n2 === `notsubset` || n2 === `superset` || n2 === `notsuperset`) {
      let t5, i2;
      if (n2 === `subset` || n2 === `notsubset` ? (i2 = r2[0], t5 = r2[1]) : (i2 = r2[1], t5 = r2[0]), i2[0] !== `interval` || t5[0] !== `interval`) throw Error(`Set containment of non-intervals not implemented for conversion to mathjs`);
      let a2 = i2[1], o2 = i2[2], s2 = t5[1], c2 = t5[2];
      if (a2[0] !== `tuple` || o2[0] !== `tuple` || s2[0] !== `tuple` || c2[0] !== `tuple`) throw Error(`Badly formed ast`);
      let l2 = this.convert(a2[1]), u2 = this.convert(a2[2]), d2 = this.convert(s2[1]), p2 = this.convert(s2[2]), m2 = [];
      o2[1] && !c2[1] ? m2.push(new math.OperatorNode(`>`, `larger`, [l2, d2])) : m2.push(new math.OperatorNode(`>=`, `largerEq`, [l2, d2])), o2[2] && !c2[2] ? m2.push(new math.OperatorNode(`<`, `smaller`, [u2, p2])) : m2.push(new math.OperatorNode(`<=`, `smallerEq`, [u2, p2]));
      let h2 = new math.OperatorNode(`and`, `and`, m2);
      return (n2 === `notsubset` || n2 === `notsuperset`) && (h2 = new math.OperatorNode(`not`, `not`, [h2])), h2;
    }
    if (n2 === `matrix`) {
      let t5 = r2[0], n3 = t5[1], i2 = t5[2], a2 = r2[1];
      if (!Number.isInteger(n3) || !Number.isInteger(i2)) throw Error(`Matrix must have integer dimensions`);
      let o2 = [];
      for (let t6 = 1; t6 <= n3; t6++) {
        let n4 = [];
        for (let r3 = 1; r3 <= i2; r3++) n4.push(this.convert(a2[t6][r3]));
        o2.push(new math.ArrayNode(n4));
      }
      return new math.ArrayNode(o2);
    }
    if (n2 === `array`) {
      let t5 = r2.map((t6) => this.convert(t6));
      return new math.ArrayNode(t5);
    }
    if (n2 in operators$2) return operators$2[n2](r2.map((function(t5, n3) {
      return this.convert(t5);
    }).bind(this)));
    throw Error(`Operator ` + n2 + ` not implemented for conversion to mathjs`);
  }
}, require_sieve = __commonJSMin(((t4, n2) => {
  n2.exports = function(t5) {
    for (var n3 = Array(t5), r2 = 0; r2 < t5; r2++) n3[r2] = true;
    for (var r2 = 2; r2 < Math.sqrt(t5); r2++) for (var i2 = r2 * r2; i2 < t5; i2 += r2) n3[i2] = false;
    for (var a2 = [], r2 = 2; r2 < t5; r2++) n3[r2] && a2.push(r2);
    return a2;
  };
})), require_factor = __commonJSMin(((t4, n2) => {
  var r2 = require_sieve(), i2 = r2(1e5);
  n2.exports = function(t5) {
    (!i2 || i2[i2.length - 1] < t5) && (i2 = r2(t5));
    for (var n3 = [], a2 = 0; a2 < i2.length && t5 > 1; a2++) {
      var o2 = i2[a2];
      if (t5 % o2 === 0) {
        for (var s2 = { prime: o2, power: 0 }; t5 % o2 === 0; ) s2.power++, t5 /= o2;
        n3.push(s2);
      }
    }
    return t5 > 1 && n3.push({ prime: t5, power: 1 }), n3;
  };
})), require_inc_mixed = __commonJSMin(((t4, n2) => {
  n2.exports = function(t5, n3) {
    var r2 = t5.map(function(t6) {
      return t6;
    });
    r2[0]++;
    for (var i2 = 0; i2 < t5.length && !(r2[i2] <= n3[i2]); i2++) i2 === t5.length - 1 ? r2[i2] = 0 : (r2[i2] = 0, r2[i2 + 1]++);
    return r2;
  };
})), require_divisors = __commonJSMin(((t4, n2) => {
  var r2 = require_factor(), i2 = require_inc_mixed();
  n2.exports = function(t5) {
    for (var n3 = r2(t5), a2 = n3.map(function(t6) {
      return 0;
    }), o2 = n3.map(function(t6) {
      return t6.power;
    }), s2 = [1]; ; ) {
      a2 = i2(a2, o2);
      var c2 = a2.map(function(t6, r3) {
        return n3[r3].prime ** +t6;
      }).reduce(function(t6, n4) {
        return t6 * n4;
      }, 1);
      if (c2 === 1) break;
      s2.push(c2);
    }
    return s2.sort(function(t6, n4) {
      return parseInt(t6) - parseInt(n4);
    }), s2;
  };
})), require_prime_factors = __commonJSMin(((t4, n2) => {
  var r2 = require_factor();
  n2.exports = function(t5) {
    return r2(t5).map(function(t6) {
      return t6.prime;
    });
  };
})), require_euler_phi = __commonJSMin(((t4, n2) => {
  var r2 = require_prime_factors();
  n2.exports = function(t5) {
    var n3 = function(t6) {
      return t6.reduce(function(t7, n4) {
        return t7 * n4;
      }, 1);
    }, i2 = r2(t5), a2 = n3(i2.map(function(t6) {
      return t6 - 1;
    })), o2 = n3(i2);
    return t5 * a2 / o2;
  };
})), require_gcd = __commonJSMin(((t4, n2) => {
  n2.exports = function(t5, n3) {
    for (t5 < 0 && (t5 = -t5), n3 < 0 && (n3 = -n3); ; ) {
      if (n3 === 0) return t5;
      if (t5 %= n3, t5 === 0) return n3;
      n3 %= t5;
    }
  };
})), require_find_divisor = __commonJSMin(((t4, n2) => {
  var r2 = require_gcd();
  n2.exports = function(t5) {
    for (var n3 = 2 * Math.floor(Math.sqrt(Math.sqrt(t5))), i2 = 2, a2 = i2, o2, s2 = 1; s2 < n3; s2++) if (i2 = (i2 * i2 + 1) % t5, a2 = (a2 * a2 + 1) % t5, a2 = (a2 * a2 + 1) % t5, o2 = r2(a2 - i2, t5), o2 != 1) return o2;
    return 1;
  };
})), require_inverse_mod = __commonJSMin(((t4, n2) => {
  n2.exports = function(t5, n3) {
    t5 < 0 && (t5 = t5 % n3 + n3);
    for (var r2 = 0, i2 = 1, a2 = n3, o2 = t5; o2 !== 0; ) {
      var s2 = Math.floor(a2 / o2), c2 = r2;
      r2 = i2, i2 = c2 - s2 * i2;
      var l2 = a2;
      a2 = o2, o2 = l2 - s2 * o2;
    }
    return a2 > 1 ? NaN : r2 > 0 ? r2 : r2 + n3;
  };
})), require_is_abundant = __commonJSMin(((t4, n2) => {
  var r2 = require_divisors();
  n2.exports = function(t5) {
    if (t5 === 1) return false;
    var n3 = r2(t5);
    return n3.pop(), t5 < n3.reduce(function(t6, n4) {
      return t6 + n4;
    });
  };
})), require_is_deficient = __commonJSMin(((t4, n2) => {
  var r2 = require_divisors();
  n2.exports = function(t5) {
    if (t5 === 1) return true;
    var n3 = r2(t5);
    return n3.pop(), t5 > n3.reduce(function(t6, n4) {
      return t6 + n4;
    });
  };
})), require_is_heptagonal = __commonJSMin(((t4, n2) => {
  n2.exports = function(t5) {
    return (Math.sqrt(40 * t5 + 9) + 3) / 10 % 1 == 0;
  };
})), require_is_hexagonal = __commonJSMin(((t4, n2) => {
  n2.exports = function(t5) {
    return (Math.sqrt(8 * t5 + 1) + 1) / 4 % 1 == 0;
  };
})), require_is_octagonal = __commonJSMin(((t4, n2) => {
  n2.exports = function(t5) {
    return (Math.sqrt(3 * t5 + 1) + 1) / 3 % 1 == 0;
  };
})), require_is_pentagonal = __commonJSMin(((t4, n2) => {
  n2.exports = function(t5) {
    return (Math.sqrt(24 * t5 + 1) + 1) / 6 % 1 == 0;
  };
})), require_is_perfect = __commonJSMin(((t4, n2) => {
  var r2 = require_divisors();
  n2.exports = function(t5) {
    if (t5 === 1) return false;
    var n3 = r2(t5);
    return n3.pop(), t5 === n3.reduce(function(t6, n4) {
      return t6 + n4;
    });
  };
})), require_is_prime = __commonJSMin(((t4, n2) => {
  var r2 = require_factor();
  n2.exports = function(t5) {
    var n3 = r2(t5);
    return n3.length == 1 ? n3[0].power === 1 : false;
  };
})), require_multiply_mod = __commonJSMin(((t4, n2) => {
  n2.exports = function(t5, n3, r2) {
    if (t5 < 94906265 && n3 < 94906265) return t5 * n3 % r2;
    var i2 = 0, a2 = r2 / 2;
    t5 >= r2 && (t5 %= r2), n3 >= r2 && (n3 %= r2);
    for (var o2 = 0; o2 < 53; o2++) i2 = i2 >= a2 ? 2 * i2 - r2 : 2 * i2, t5 >= 4503599627370496 && (i2 += n3, t5 -= 4503599627370495), i2 > r2 && (i2 -= r2), t5 *= 2;
    return i2;
  };
})), require_power_mod = __commonJSMin(((t4, n2) => {
  var r2 = require_multiply_mod(), i2 = require_inverse_mod();
  n2.exports = function t5(n3, a2, o2) {
    if (a2 < 0) return i2(t5(n3, -a2, o2), o2);
    var s2 = 1;
    for (n3 %= o2; a2 > 0; ) a2 % 2 == 1 && (s2 = r2(s2, n3, o2), --a2), a2 /= 2, n3 = r2(n3, n3, o2);
    return s2;
  };
})), require_miller = __commonJSMin(((t4, n2) => {
  var r2 = require_multiply_mod(), i2 = require_power_mod();
  n2.exports = function(t5) {
    if (t5 < 2) return false;
    if (t5 == 2 || t5 == 3) return true;
    if (!(t5 & 1) || t5 % 3 == 0) return false;
    for (var n3 = t5 - 1, a2 = 0; n3 % 2 == 0; ) n3 /= 2, a2 += 1;
    for (var o2 = t5 < 1373653 ? [2, 3] : t5 < 9080191 ? [31, 73] : t5 < 4759123141 ? [2, 7, 61] : t5 < 1122004669633 ? [2, 13, 23, 1662803] : t5 < 2152302898747 ? [2, 3, 5, 7, 11] : t5 < 3474749660383 ? [2, 3, 5, 7, 11, 13] : [2, 3, 5, 7, 11, 13, 17], s2 = 0; s2 < o2.length; s2++) {
      for (var c2 = o2[s2], l2 = i2(c2, n3, t5), u2 = 0, d2 = a2; d2 > 0; ) {
        if (u2 = r2(l2, l2, t5), u2 === 1 && l2 !== 1 && l2 !== t5 - 1) return false;
        l2 = u2, --d2;
      }
      if (u2 !== 1) return false;
    }
    return true;
  };
})), require_is_square = __commonJSMin(((t4, n2) => {
  n2.exports = function(t5) {
    return Math.sqrt(t5) % 1 == 0;
  };
})), require_is_triangular = __commonJSMin(((t4, n2) => {
  n2.exports = function(t5) {
    return Math.sqrt(8 * t5 + 1) % 1 == 0;
  };
})), require_jacobi_symbol = __commonJSMin(((t4, n2) => {
  n2.exports = function(t5, n3) {
    if (n3 % 2 == 0 || n3 < 0) return NaN;
    t5 < 0 && (t5 = t5 % n3 + n3);
    for (var r2 = 0; ; ) {
      if (t5 %= n3, t5 === 0) return 0;
      for (; t5 % 2 == 0; ) r2 ^= (n3 % 8 * (n3 % 8) - 1) / 8, t5 /= 2;
      if (t5 == 1) return r2 & 1 ? -1 : 1;
      r2 ^= (t5 % 4 - 1) * (n3 % 4 - 1) / 4;
      var i2 = t5;
      t5 = n3, n3 = i2;
    }
    return NaN;
  };
})), require_log_mod = __commonJSMin(((t4, n2) => {
  var r2 = require_power_mod(), i2 = require_multiply_mod(), a2 = {};
  n2.exports = function(t5, n3, o2) {
    t5 = (t5 % o2 + o2) % o2;
    var s2 = Math.ceil(Math.sqrt(o2)), c2 = {};
    if (a2[o2] === void 0 && (a2[o2] = {}), a2[o2][n3] === void 0) {
      a2[o2][n3] = {}, c2 = a2[o2][n3];
      for (var l2 = 0; l2 < s2; l2++) c2[r2(n3, l2, o2)] = l2;
    } else c2 = a2[o2][n3];
    for (var u2 = r2(n3, -s2, o2), d2 = t5, p2 = 0; p2 < s2; p2++) if (c2[d2] !== void 0) return (i2(p2, s2, o2) + c2[d2]) % o2;
    else d2 = i2(d2, u2, o2);
    return NaN;
  };
})), require_primitive_root = __commonJSMin(((t4, n2) => {
  var r2 = (init_index_all(), __toCommonJS(index_all_exports)), i2 = require_euler_phi(), a2 = require_prime_factors(), o2 = require_power_mod();
  n2.exports = function(t5) {
    for (var n3 = i2(t5), s2 = a2(n3), c2 = 2; c2 < t5; c2++) if (r2.every(s2, function(r3) {
      return o2(c2, n3 / r3, t5) != 1;
    })) return c2;
    return NaN;
  };
})), require_quadratic_nonresidue = __commonJSMin(((t4, n2) => {
  var r2 = require_jacobi_symbol();
  n2.exports = function(t5) {
    for (var n3 = 2; n3 < t5; n3++) if (r2(n3, t5) == -1) return n3;
    return NaN;
  };
})), require_random_primitive_root = __commonJSMin(((t4, n2) => {
  var r2 = require_primitive_root(), i2 = require_euler_phi(), a2 = require_gcd(), o2 = require_power_mod();
  n2.exports = function(t5) {
    for (var n3 = r2(t5), s2 = i2(t5), c2 = 0; c2 < 100; c2++) {
      var l2 = Math.floor(Math.random() * s2);
      if (a2(l2, s2) == 1) return o2(n3, l2, t5);
    }
    return n3;
  };
})), require_square_root_mod_prime = __commonJSMin(((t4, n2) => {
  var r2 = require_jacobi_symbol(), i2 = require_power_mod(), a2 = require_quadratic_nonresidue();
  n2.exports = function(t5, n3) {
    if (r2(t5, n3) != 1) return NaN;
    for (var o2 = n3 - 1, s2 = 0; o2 % 2 == 0; ) o2 /= 2, s2++;
    if (n3 % 4 == 3) return i2(t5, (n3 + 1) / 4, n3);
    for (var c2 = i2(a2(n3), o2, n3), l2 = i2(t5, (o2 + 1) / 2, n3), u2 = i2(t5, o2, n3), d2 = s2; ; ) {
      if (u2 % n3 == 1) return l2;
      for (var p2 = u2, m2 = 1; m2 < d2 && (p2 = p2 * p2 % n3, p2 != 1); m2++) ;
      var h2 = m2;
      m2++;
      for (var g2 = c2; m2 < d2; ) g2 = g2 * g2 % n3, m2++;
      d2 = h2, l2 = l2 * g2 % n3, u2 = u2 * g2 * g2 % n3, c2 = g2 * g2 % n3;
    }
    return NaN;
  };
})), require_square_root_mod = __commonJSMin(((t4, n2) => {
  var r2 = (init_index_all(), __toCommonJS(index_all_exports)), i2 = require_factor(), a2 = require_square_root_mod_prime(), o2 = require_jacobi_symbol(), s2 = require_inverse_mod(), c2 = require_multiply_mod();
  n2.exports = function(t5, n3) {
    var l2 = 1, u2 = [0];
    return i2(n3).forEach(function(n4) {
      var i3 = n4.prime, d2 = n4.power, p2 = a2(t5, i3), m2 = [];
      if (o2(t5, i3) != 1) return [];
      u2.forEach(function(t6) {
        m2.unshift(t6 * i3 * s2(i3, l2) + p2 * l2 * s2(l2, i3)), m2.unshift(t6 * i3 * s2(i3, l2) - p2 * l2 * s2(l2, i3));
      }), m2.sort(), u2 = r2.unique(m2), l2 *= i3;
      var h2 = 1;
      for (d2--; d2 > 0; ) {
        var g2 = i3 ** +Math.min(h2, d2);
        d2 -= Math.min(h2, d2), u2 = u2.map(function(n5) {
          var r3 = -((n5 * n5 - t5) / l2), i4 = s2(2 * n5, g2);
          return n5 + l2 * c2(r3, i4, g2);
        }), l2 *= g2;
      }
    }), u2.map(function(t6) {
      return (t6 % n3 + n3) % n3;
    });
  };
})), require_number_theory = __commonJSMin(((t4, n2) => {
  n2.exports = { divisors: require_divisors(), eulerPhi: require_euler_phi(), factor: require_factor(), findDivisor: require_find_divisor(), gcd: require_gcd(), incMixed: require_inc_mixed(), inverseMod: require_inverse_mod(), isAbundant: require_is_abundant(), isDeficient: require_is_deficient(), isHeptagonal: require_is_heptagonal(), isHexagonal: require_is_hexagonal(), isOctagonal: require_is_octagonal(), isPentagonal: require_is_pentagonal(), isPerfect: require_is_perfect(), isPrime: require_is_prime(), isProbablyPrime: require_miller(), isSquare: require_is_square(), isTriangular: require_is_triangular(), jacobiSymbol: require_jacobi_symbol(), logMod: require_log_mod(), miller: require_miller(), multiplyMod: require_multiply_mod(), powerMod: require_power_mod(), primeFactors: require_prime_factors(), primitiveRoot: require_primitive_root(), quadraticNonresidue: require_quadratic_nonresidue(), randomPrimitiveRoot: require_random_primitive_root(), sieve: require_sieve(), squareRootMod: require_square_root_mod(), squareRootModPrime: require_square_root_mod_prime(), totient: require_euler_phi() };
})), import_number_theory = __toESM(require_number_theory());
function gcd$2(t4, n2) {
  return Number.isNaN(t4) || Number.isNaN(n2) ? NaN : import_number_theory.default.gcd(t4, n2);
}
function flatten$1(t4) {
  return t4.reduce(function(t5, n2) {
    return t5.concat(n2);
  }, []);
}
function positiveModulo(t4, n2) {
  return (t4 % n2 + n2) % n2;
}
var ZmodN = class t2 {
  constructor(t4, n2, r2 = false) {
    this.values = t4.map(function(t5) {
      return positiveModulo(t5, n2);
    }), this.modulus = n2, this.approximate = r2;
  }
  apply(n2, r2, i2) {
    if (i2 == null || Number.isNaN(i2)) {
      if (Number.isNaN(this.modulus) || Number.isNaN(n2.modulus)) return new t2([NaN], NaN, this.approximate || n2.approximate);
      i2 = gcd$2(this.modulus, n2.modulus);
    }
    return new t2(flatten$1(this.values.map(function(t4) {
      return n2.values.map(function(n3) {
        return Number.isNaN(t4) || Number.isNaN(n3) ? NaN : (i2 + r2(t4, n3)) % i2;
      });
    })), i2, this.approximate || n2.approximate);
  }
  add(t4) {
    return this.apply(t4, function(t5, n2) {
      return t5 + n2;
    });
  }
  power(n2) {
    var r2 = this.modulus;
    return n2.modulus == import_number_theory.default.eulerPhi(r2) ? this.apply(n2, function(t4, n3) {
      return import_number_theory.default.powerMod(t4, n3, r2);
    }, r2) : new t2([NaN], NaN, this.approximate || n2.approximate);
  }
  isNaN() {
    for (var t4 of this.values) if (isNaN(t4)) return true;
    return false;
  }
  subtract(t4) {
    return this.apply(t4, function(t5, n2) {
      return t5 - n2;
    });
  }
  multiply(t4) {
    var n2 = gcd$2(this.modulus, t4.modulus);
    return this.apply(t4, function(t5, r2) {
      return import_number_theory.default.multiplyMod(t5, r2, n2);
    });
  }
  inverse() {
    var n2 = this.modulus;
    return new t2(this.values.map(function(t4) {
      return import_number_theory.default.inverseMod(t4, n2);
    }), this.modulus, this.approximate);
  }
  negate() {
    var n2 = this.modulus;
    return new t2(this.values.map(function(t4) {
      return n2 - t4;
    }), this.modulus, this.approximate);
  }
  divide(n2) {
    var r2 = gcd$2(this.modulus, n2.modulus);
    return new t2(flatten$1(flatten$1(this.values.map(function(t4) {
      return n2.values.map(function(n3) {
        if (t4 % n3 == 0) return [t4 / n3];
        var i2 = gcd$2(n3, r2);
        if (t4 % i2 != 0) return [];
        var a2 = n3 / i2, o2 = t4 / i2, s2 = import_number_theory.default.multiplyMod(o2, import_number_theory.default.inverseMod(a2, r2), r2), c2 = [], l2;
        for (l2 = 0; l2 < i2; l2++) c2.unshift(s2 + import_number_theory.default.multiplyMod(l2, r2 / i2, r2));
        return c2;
      });
    }))), r2, this.approximate || n2.approximate);
  }
  sqrt() {
    var n2 = this.modulus;
    return new t2(flatten$1(this.values.map(function(t4) {
      return import_number_theory.default.squareRootMod(t4, n2);
    })), this.modulus, this.approximate);
  }
  toString = function() {
    return `{` + this.values.sort().toString() + `}/` + this.modulus.toString();
  };
  equals(t4) {
    return this.toString() == t4.toString();
  }
}, PRIME = 10739999, levelFunctions = { "+": function(t4) {
  return t4.reduce(function(t5, n2) {
    return t5.add(n2);
  });
}, "*": function(t4) {
  return t4.reduce(function(t5, n2) {
    return t5.multiply(n2);
  });
}, "/": function(t4) {
  return t4.reduce(function(t5, n2) {
    return t5.divide(n2);
  });
}, "-": function(t4) {
  return t4[0].negate();
}, "~": function(t4) {
  return t4.reduce(function(t5, n2) {
    return t5.subtract(n2);
  }, new ZmodN([0], t4[0].modulus));
}, factorial: function(t4) {
  return ZmodN([NaN], 1);
}, gamma: function(t4) {
  return ZmodN([NaN], 1);
} };
function rationalApproximation(t4, n2 = 1e15) {
  let r2 = [[1, 0], [0, 1]], i2;
  for (; r2[1][0] * (i2 = Math.floor(t4)) + r2[1][1] <= n2; ) {
    let n3;
    if (n3 = r2[0][0] * i2 + r2[0][1], r2[0][1] = r2[0][0], r2[0][0] = n3, n3 = r2[1][0] * i2 + r2[1][1], r2[1][1] = r2[1][0], r2[1][0] = n3, t4 == i2 || (t4 = 1 / (t4 - i2), t4 > 2147483647)) break;
  }
  let a2 = r2[1][0] * i2 + r2[1][1] > n2;
  return { numerator: r2[0][0], denominator: r2[1][0], approximate: a2 };
}
function finite_field_evaluate_ast(t4, n2, r2, i2) {
  if (typeof t4 == `string`) return t4 === `e` ? new ZmodN([import_number_theory.default.primitiveRoot(r2)], r2) : t4 === `pi` ? r2 % 2 == 0 ? new ZmodN([r2 / 2], r2) : new ZmodN([NaN], r2) : t4 === `i` ? new ZmodN([0], 1) : t4 in n2 ? new ZmodN([n2[t4]], r2) : t4;
  if (typeof t4 == `number`) {
    if (Number.isInteger(t4)) return new ZmodN([t4], r2);
    {
      let n3 = rationalApproximation(t4), i3 = new ZmodN([n3.numerator], r2, n3.approximate), a3 = new ZmodN([n3.denominator], r2, n3.approximate);
      return i3.divide(a3);
    }
  }
  var a2 = t4[0], o2 = t4.slice(1);
  if (a2 === `apply`) {
    if (typeof o2[0] != `string`) throw Error(`Non string functions not implemented for finite fields`);
    if (o2[0] === `exp`) {
      var s2 = finite_field_evaluate_ast(`e`, {}, r2), c2 = finite_field_evaluate_ast(o2[1], n2, import_number_theory.default.eulerPhi(r2));
      return s2.power(c2);
    }
    if (o2[0] === `sec`) return finite_field_evaluate_ast([`/`, 1, [`apply`, `cos`, o2[1]]], n2, r2);
    if (o2[0] === `csc`) return finite_field_evaluate_ast([`/`, 1, [`apply`, `sin`, o2[1]]], n2, r2);
    if (o2[0] === `cot`) return finite_field_evaluate_ast([`/`, 1, [`apply`, `tan`, o2[1]]], n2, r2);
    if (o2[0] === `tan`) {
      var l2 = import_number_theory.default.eulerPhi(r2);
      return finite_field_evaluate_ast([`/`, [`apply`, `sin`, o2[1]], [`apply`, `cos`, o2[1]]], n2, r2);
    }
    if (o2[0] === `sin`) {
      var u2 = import_number_theory.default.primitiveRoot(r2), d2 = new ZmodN([u2], r2), l2 = import_number_theory.default.eulerPhi(r2);
      if (l2 % 4 != 0) return new ZmodN([NaN], r2);
      var p2 = new ZmodN([import_number_theory.default.powerMod(u2, l2 / 4, r2)], r2), m2 = finite_field_evaluate_ast(o2[1], n2, l2);
      return d2.power(m2).subtract(d2.power(m2.negate())).divide(p2.add(p2));
    }
    if (o2[0] === `cos`) {
      var d2 = new ZmodN([import_number_theory.default.primitiveRoot(r2)], r2), m2 = finite_field_evaluate_ast(o2[1], n2, import_number_theory.default.eulerPhi(r2));
      return d2.power(m2).add(d2.power(m2.negate())).divide(new ZmodN([2], r2));
    }
    if (o2[0] === `log`) return new ZmodN([NaN], NaN);
    if (o2[0] === `sqrt`) return finite_field_evaluate_ast(o2[1], n2, r2).sqrt();
    if (o2[0] === `abs`) {
      let t5 = finite_field_evaluate_ast(o2[1], n2, r2);
      return t5.multiply(t5).sqrt();
    }
  }
  if (a2 == `^`) {
    var s2 = finite_field_evaluate_ast(o2[0], n2, r2), c2 = finite_field_evaluate_ast(o2[1], n2, import_number_theory.default.eulerPhi(r2));
    return s2.power(c2);
  }
  return a2 in levelFunctions ? levelFunctions[a2](o2.map(function(t5, a3) {
    return finite_field_evaluate_ast(t5, n2, r2);
  })) : new ZmodN([NaN], r2);
}
var astToFiniteField$1 = class {
  constructor() {
  }
  convert(t4, n2, r2 = PRIME) {
    return finite_field_evaluate_ast(t4, n2, r2);
  }
}, evaluation_exports = __exportAll({ evaluate: () => evaluate, evaluate_to_constant: () => evaluate_to_constant, f: () => f, finite_field_evaluate: () => finite_field_evaluate }), astToMathjs$1 = new astToMathjs$2({ mathjs: mathjs_default }), astToFiniteField = new astToFiniteField$1(), f = function(t4) {
  var n2 = get_tree(t4), r2 = factorial_to_gamma_function(astToMathjs$1.convert(log_subscript_to_two_arg_log(normalize_function_names(normalize_applied_functions(n2)))));
  return r2.evaluate.bind(r2);
}, evaluate = function(t4, n2) {
  return f(t4)(n2);
}, finite_field_evaluate = function(t4, n2, r2) {
  return astToFiniteField.convert(t4.tree, n2, r2);
}, evaluate_to_constant = function(t4, { remove_units_first: n2 = true, scale_based_on_unit: r2 = true, nan_for_non_numeric: i2 = true } = {}) {
  var a2 = get_tree(t4);
  if (n2 && (a2 = remove_units(a2, r2)), typeof a2 == `number`) return a2;
  if (typeof a2 == `string`) return a2 === `pi` && mathjs_default.define_pi ? Math.PI : a2 === `e` && mathjs_default.define_e ? Math.E : a2 === `i` && mathjs_default.define_i ? { re: 0, im: 1 } : i2 ? NaN : null;
  if (!Array.isArray(a2)) return i2 ? NaN : null;
  var o2 = [];
  o2.push([[`^`, `x`, [`/`, `m`, `n`]], [`^`, [`apply`, `nthRoot`, [`tuple`, `x`, `n`]], `m`], { variables: { x: (t5) => t5 < 0, n: (t5) => Number.isInteger(t5) && Number.isInteger((t5 - 1) / 2), m: (t5) => Number.isInteger(t5) } }]), o2.push([[`^`, `x`, [`-`, [`/`, `m`, `n`]]], [`^`, [`apply`, `nthRoot`, [`tuple`, `x`, `n`]], [`-`, `m`]], { variables: { x: (t5) => t5 < 0, n: (t5) => Number.isInteger(t5) && Number.isInteger((t5 - 1) / 2), m: (t5) => Number.isInteger(t5) } }]), a2 = applyAllTransformations(a2, o2, 40);
  var s2 = i2 ? NaN : null;
  try {
    var c2 = f(a2);
    s2 = c2();
  } catch {
  }
  if (!Number.isNaN(s2) && (typeof s2 == `number` || typeof s2?.re == `number` && typeof s2?.im == `number`)) {
    if (s2.re === 1 / 0 || s2.re === -1 / 0 || s2.im === 1 / 0 || s2.im === -1 / 0) {
      let t5;
      if (typeof s2?.re == `number` && typeof s2?.im == `number`) if (s2.im === 0) t5 = s2.re;
      else {
        let n3;
        n3 = s2.im === 1 ? `i` : s2.im === -1 ? [`-`, `i`] : [`*`, s2.im, `i`], t5 = s2.re === 0 ? n3 : [`+`, s2.re, n3];
      }
      else t5 = s2;
      s2 = i2 ? NaN : null;
      try {
        var c2 = f(t5);
        s2 = c2();
      } catch {
      }
    } else s2.im === 0 && (s2 = s2.re);
    return s2;
  }
  return typeof s2 == `object` && (s2 = i2 ? NaN : null), s2;
};
function factorial_to_gamma_function(t4) {
  return t4.transform(function(t5, n2, r2) {
    if (t5.isOperatorNode && t5.op === `!` && t5.fn === `factorial`) {
      var i2 = [new mathjs_default.OperatorNode(`+`, `add`, [t5.args[0], new mathjs_default.ConstantNode(1)])];
      return new mathjs_default.FunctionNode(new mathjs_default.SymbolNode(`gamma`), i2);
    } else return t5;
  });
}
var functions = { C: {}, R: {}, nonzeroC: {}, nonneg: {}, pos: {} };
functions.C.nonneg = [`abs`], functions.C.nonzero = [`exp`], functions.C.R = [`abs`, `arg`], functions.C.C = [`abs`, `arg`, `exp`, `sign`, `cos`, `cosh`, `sin`, `sinh`, `erf`, `sqrt`, `log`, `ln`, `log10`], functions.R.pos = [`exp`], functions.R.nonneg = [`abs`, `exp`, `arg`], functions.R.R = [`abs`, `arg`, `exp`, `sign`, `cos`, `cosh`, `sin`, `sinh`, `erf`], functions.R.Z = [`sign`], functions.nonzeroC.pos = [`abs`], functions.nonneg.nonneg = [`abs`, `exp`, `arg`, `sqrt`, `erf`], functions.nonzeroC.nonzero = [`abs`], functions.nonneg.R = [...new Set(functions.R.R.concat(functions.nonneg.nonneg))], functions.pos.pos = [`abs`, `exp`, `sqrt`, `erf`], functions.pos.nonneg = functions.pos.pos, functions.pos.nonzero = [`abs`, `exp`, `sqrt`, `erf`], functions.pos.R = functions.nonneg.R.concat([`log`, `ln`, `log10`]);
function negate_adjust(t4, n2) {
  if (t4) return !n2;
  if (t4 === false) return n2;
}
function narrow_assumptions(t4, n2) {
  if (!Array.isArray(n2)) return [];
  var r2 = n2[0], i2 = n2.slice(1);
  if (r2 !== `and`) return [];
  for (var a2 = [], o2 = 0; o2 < i2.length; o2++) equal(i2[o2], t4) || a2.push(i2[o2]);
  return a2.length === 0 ? [] : a2.legnth === 1 ? a2[0] : [r2].concat(a2);
}
function simple_assumption_combination(t4, n2, r2) {
  return t4 === `and` ? n2 === void 0 ? r2 : r2 === void 0 ? n2 : n2 || r2 : n2 === void 0 || r2 === void 0 || n2 !== r2 ? void 0 : n2;
}
function is_integer_ast(t4, n2, r2) {
  if (typeof n2 != `object` && (n2 = []), r2 === void 0 && (r2 = n2), typeof t4 == `number`) return Number.isInteger(t4);
  var i2 = evaluate_to_constant(t4, { remove_units_first: false, nan_for_non_numeric: false });
  if (i2 !== null) return Number.isInteger(i2);
  if (typeof t4 == `string`) {
    let i3;
    if (i3 = Array.isArray(n2) ? n2 : n2.get_assumptions(t4), !Array.isArray(i3)) return;
    let a2 = i3, o2 = i3[0], s2 = i3.slice(1), c2 = false;
    for (; o2 === `not`; ) {
      if (c2 = !c2, i3 = s2[0], !Array.isArray(i3)) return;
      o2 = i3[0], s2 = i3.slice(1);
    }
    if (o2 === `in` && s2[0] === t4 && s2[1] === `Z`) return negate_adjust(true, c2);
    if (o2 === `notin` && s2[0] === t4 && s2[1] === `Z`) return negate_adjust(false, c2);
    if (c2 === false) {
      if (o2 === `notin` && (s2[0] === t4 && s2[1] === `R` || s2[0] === t4 && s2[1] === `C`)) return false;
    } else if (o2 === `in` && (s2[0] === t4 && s2[1] === `R` || s2[0] === t4 && s2[1] === `Z`)) return false;
    if (o2 === `=` && !c2 || o2 === `ne` && c2) {
      let n3 = narrow_assumptions(a2, r2);
      if (s2[0] === t4) return is_integer_ast(s2[1], n3);
      if (s2[1] === t4) return is_integer_ast(s2[0], n3);
    }
    if (o2 !== `and` && o2 !== `or` || c2) return;
    s2.length > 2 && (i3 = unflattenRight(i3), s2 = i3.slice(1));
    let l2 = is_integer_ast(t4, s2[0], r2), u2 = is_integer_ast(t4, s2[1], r2);
    return simple_assumption_combination(o2, l2, u2);
  }
  if (Array.isArray(t4)) {
    let i3;
    i3 = Array.isArray(n2) ? n2 : n2.get_assumptions([variables(t4)]);
    let a2 = t4[0], o2 = t4.slice(1);
    if (a2 === `-`) return is_integer_ast(o2[0], i3, r2);
    if (a2 === `*`) return o2.every(function(t5) {
      return is_integer_ast(t5, i3, r2);
    }) ? true : void 0;
    if (a2 === `^`) {
      let t5 = is_nonzero_ast(o2[0], i3, r2);
      if (!t5) {
        if (Number.isNaN(o2[0])) return false;
        if (is_positive_ast(o2[1], i3, true, r2)) {
          if (t5 === false) return true;
        } else return;
      } else if (is_nonzero_ast(o2[1], i3, r2) === false) return !(o2[0] === 1 / 0 || o2[0] === -1 / 0);
      let n3 = is_integer_ast(o2[0], i3, r2), a3 = is_integer_ast(o2[1], i3, r2);
      return n3 && (a3 && is_positive_ast(o2[1], i3, false, r2) ? true : void 0);
    }
    if (a2 === `+`) {
      let t5 = 0;
      for (let n3 = 0; n3 < o2.length; n3++) {
        let a3 = is_integer_ast(o2[n3], i3, r2);
        if (a3 === false) {
          if (t5 > 0) return;
          t5 += 1;
        }
        if (a3 === void 0) return;
      }
      return t5 === 0;
    }
    return a2 === `apply` ? functions.C.Z && functions.C.Z.includes(o2[0]) && is_complex_ast(o2[1], i3, r2) || functions.R.Z && functions.R.Z.includes(o2[0]) && is_real_ast(o2[1], i3, r2) || functions.nonzeroC.Z && functions.nonzeroC.Z.includes(o2[0]) && is_nonzero_ast(o2[1], i3, r2) && is_complex_ast(o2[1], i3, r2) || functions.nonneg.Z && functions.nonneg.Z.includes(o2[0]) && is_positive_ast(o2[1], i3, false, r2) || functions.pos.Z && functions.pos.Z.includes(o2[0]) && is_positive_ast(o2[1], i3, true, r2) ? true : void 0 : a2 === `/` || a2 === `prime` ? void 0 : false;
  }
  return false;
}
function is_real_ast(t4, n2, r2) {
  if (typeof n2 != `object` && (n2 = []), r2 === void 0 && (r2 = n2), typeof t4 == `number`) return Number.isFinite(t4);
  var i2 = evaluate_to_constant(t4, { remove_units_first: false, nan_for_non_numeric: false });
  if (i2 !== null) return typeof i2 == `number` ? Number.isFinite(i2) : false;
  if (typeof t4 == `string`) {
    let i3;
    if (i3 = Array.isArray(n2) ? n2 : n2.get_assumptions(t4), !Array.isArray(i3)) return;
    let a2 = i3, o2 = i3[0], s2 = i3.slice(1), c2 = false;
    for (; o2 === `not`; ) {
      if (c2 = !c2, i3 = s2[0], !Array.isArray(i3)) return;
      o2 = i3[0], s2 = i3.slice(1);
    }
    if (o2 === `in` && s2[0] === t4 && s2[1] === `R`) return negate_adjust(true, c2);
    if (o2 === `notin` && s2[0] === t4 && s2[1] === `R`) return negate_adjust(false, c2);
    if (c2 === false) {
      if (o2 === `in` && s2[0] === t4 && s2[1] === `Z`) return true;
    } else if (o2 === `notin` && s2[0] === t4 && s2[1] === `Z`) return true;
    if (o2 === `<` || o2 === `le`) {
      let n3 = variables(i3), r3 = functions$2(i3);
      if (n3.indexOf(t4) !== -1 && r3.length === 0) return true;
    }
    if (o2 === `=` && !c2 || o2 === `ne` && c2) {
      let n3 = narrow_assumptions(a2, r2);
      if (s2[0] === t4) return is_real_ast(s2[1], n3);
      if (s2[1] === t4) return is_real_ast(s2[0], n3);
    }
    if (o2 !== `and` && o2 !== `or` || c2) return;
    s2.length > 2 && (i3 = unflattenRight(i3), s2 = i3.slice(1));
    let l2 = is_real_ast(t4, s2[0], r2), u2 = is_real_ast(t4, s2[1], r2);
    return simple_assumption_combination(o2, l2, u2);
  }
  if (Array.isArray(t4)) {
    let i3;
    i3 = Array.isArray(n2) ? n2 : n2.get_assumptions([variables(t4)]);
    let a2 = t4[0], o2 = t4.slice(1);
    if (a2 === `-`) return is_real_ast(o2[0], i3, r2);
    if (a2 === `*` || a2 === `+`) {
      if (o2.length > 2 && (t4 = unflattenRight(t4), o2 = t4.slice(1)), a2 === `*` && (is_nonzero_ast(o2[0], i3, r2) === false || is_nonzero_ast(o2[1], i3, r2) === false)) return true;
      let n3 = is_real_ast(o2[0], i3, r2), s2 = is_real_ast(o2[1], i3, r2);
      return n3 && s2 ? true : n3 && s2 === false || s2 && n3 === false ? false : void 0;
    }
    if (a2 === `^`) {
      let t5 = is_nonzero_ast(o2[0], i3, r2), n3 = is_positive_ast(o2[1], i3, true, r2);
      if (!t5) {
        if (Number.isNaN(o2[0])) return false;
        if (n3) {
          if (t5 === false) return true;
        } else return;
      } else if (is_nonzero_ast(o2[1], i3, r2) === false) return !(o2[0] === 1 / 0 || o2[0] === -1 / 0);
      let a3 = is_real_ast(o2[0], i3, r2), s2 = is_real_ast(o2[1], i3, r2);
      return a3 && s2 ? is_positive_ast(o2[0], i3, false, r2) ? is_positive_ast(o2[0], i3, true, r2) || n3 ? true : void 0 : is_integer_ast(o2[1], i3, r2) ? true : void 0 : void 0;
    }
    return a2 === `/` ? is_nonzero_ast(o2[1], i3, r2) && (is_nonzero_ast(o2[0], i3, r2) === false || is_real_ast(o2[0], i3, r2) && is_real_ast(o2[1], i3, r2)) ? true : void 0 : a2 === `apply` ? functions.C.R && functions.C.R.includes(o2[0]) && is_complex_ast(o2[1], i3, r2) || functions.R.R && functions.R.R.includes(o2[0]) && is_real_ast(o2[1], i3, r2) || functions.nonzeroC.R && functions.nonzeroC.R.includes(o2[0]) && is_nonzero_ast(o2[1], i3, r2) && is_complex_ast(o2[1], i3, r2) || functions.nonneg.R && functions.nonneg.R.includes(o2[0]) && is_positive_ast(o2[1], i3, false, r2) || functions.pos.R && functions.pos.R.includes(o2[0]) && is_positive_ast(o2[1], i3, true, r2) ? true : void 0 : a2 === `prime` ? void 0 : false;
  }
  return false;
}
function is_complex_ast(t4, n2, r2) {
  if (typeof n2 != `object` && (n2 = []), r2 === void 0 && (r2 = n2), typeof t4 == `number`) return Number.isFinite(t4);
  var i2 = evaluate_to_constant(t4, { remove_units_first: false, nan_for_non_numeric: false });
  if (i2 !== null) return typeof i2 == `number` ? Number.isFinite(i2) : !!(i2.re !== void 0 && Number.isFinite(i2.re) && i2.im !== void 0 && Number.isFinite(i2.im));
  if (typeof t4 == `string`) {
    let i3;
    if (i3 = Array.isArray(n2) ? n2 : n2.get_assumptions(t4), !Array.isArray(i3)) return;
    let a2 = i3, o2 = i3[0], s2 = i3.slice(1), c2 = false;
    for (; o2 === `not`; ) {
      if (c2 = !c2, i3 = s2[0], !Array.isArray(i3)) return;
      o2 = i3[0], s2 = i3.slice(1);
    }
    if (o2 === `in` && s2[0] === t4 && s2[1] === `C`) return negate_adjust(true, c2);
    if (o2 === `notin` && s2[0] === t4 && s2[1] === `C`) return negate_adjust(false, c2);
    if (c2 === false) {
      if (o2 === `in` && (s2[0] === t4 && s2[1] === `Z` || s2[0] === t4 && s2[1] === `R`)) return true;
    } else if (o2 === `notin` && (s2[0] === t4 && s2[1] === `Z` || s2[0] === t4 && s2[1] === `R`)) return true;
    if (o2 === `<` || o2 === `le`) {
      let n3 = variables(i3), r3 = functions$2(i3);
      if (n3.indexOf(t4) !== -1 && r3.length === 0) return true;
    }
    if (o2 === `=` && !c2 || o2 === `ne` && c2) {
      let n3 = narrow_assumptions(a2, r2);
      if (s2[0] === t4) return is_complex_ast(s2[1], n3);
      if (s2[1] === t4) return is_complex_ast(s2[0], n3);
    }
    if (o2 !== `and` && o2 !== `or` || c2) return;
    s2.length > 2 && (i3 = unflattenRight(i3), s2 = i3.slice(1));
    let l2 = is_complex_ast(t4, s2[0], r2), u2 = is_complex_ast(t4, s2[1], r2);
    return simple_assumption_combination(o2, l2, u2);
  }
  if (Array.isArray(t4)) {
    let i3;
    i3 = Array.isArray(n2) ? n2 : n2.get_assumptions([variables(t4)]);
    let a2 = t4[0], o2 = t4.slice(1);
    if (a2 === `-`) return is_complex_ast(o2[0], i3, r2);
    if (a2 === `*` || a2 === `+`) return a2 === `*` && !o2.every((t5) => is_nonzero_ast(t5, i3, r2) !== false) || o2.every((t5) => is_complex_ast(t5, i3, r2)) ? true : void 0;
    if (a2 === `^`) {
      let t5 = is_nonzero_ast(o2[0], i3, r2), n3 = is_positive_ast(o2[1], i3, true, r2);
      if (!t5) {
        if (Number.isNaN(o2[0])) return false;
        if (n3) {
          if (t5 === false) return true;
        } else return;
      } else if (is_nonzero_ast(o2[1], i3, r2) === false) return !(o2[0] === 1 / 0 || o2[0] === -1 / 0);
      let a3 = is_complex_ast(o2[0], i3, r2), s2 = is_complex_ast(o2[1], i3, r2);
      return a3 && s2 ? true : void 0;
    }
    return a2 === `/` ? is_nonzero_ast(o2[1], i3, r2) && (is_nonzero_ast(o2[0], i3, r2) === false || is_complex_ast(o2[0], i3, r2) && is_complex_ast(o2[1], i3, r2)) ? true : void 0 : a2 === `apply` ? functions.C.C && functions.C.C.includes(o2[0]) && is_complex_ast(o2[1], i3, r2) || functions.R.C && functions.R.C.includes(o2[0]) && is_real_ast(o2[1], i3, r2) || functions.nonzeroC.C && functions.nonzeroC.C.includes(o2[0]) && is_nonzero_ast(o2[1], i3, r2) && is_complex_ast(o2[1], i3, r2) || functions.nonneg.C && functions.nonneg.C.includes(o2[0]) && is_positive_ast(o2[1], i3, false, r2) || functions.pos.C && functions.pos.C.includes(o2[0]) && is_positive_ast(o2[1], i3, true, r2) ? true : void 0 : a2 === `prime` ? void 0 : false;
  }
  return false;
}
function is_nonzero_ast(t4, n2, r2) {
  if (typeof n2 != `object` && (n2 = []), r2 === void 0 && (r2 = n2), typeof t4 == `number`) return Number.isFinite(t4) ? t4 !== 0 : Number.isNaN(t4) ? void 0 : true;
  var i2 = evaluate_to_constant(t4, { remove_units_first: false, nan_for_non_numeric: false });
  if (i2 !== null) return typeof i2 == `number` ? Number.isFinite(i2) ? i2 !== 0 : Number.isNaN(i2) ? void 0 : true : i2.re !== void 0 && i2.im !== void 0 && (i2.re !== 0 || i2.im !== 0) ? true : void 0;
  if (typeof t4 == `string`) {
    let i3;
    if (i3 = Array.isArray(n2) ? n2 : n2.get_assumptions(t4), !Array.isArray(i3)) return;
    let a2 = i3, o2 = i3[0], s2 = i3.slice(1), c2 = false;
    for (; o2 === `not`; ) {
      if (c2 = !c2, i3 = s2[0], !Array.isArray(i3)) return;
      o2 = i3[0], s2 = i3.slice(1);
    }
    let l2 = narrow_assumptions(a2, r2);
    if (o2 === `=` && !c2 || o2 === `ne` && c2) {
      if (s2[0] === t4) return is_nonzero_ast(s2[1], l2);
      if (s2[1] === t4) return is_nonzero_ast(s2[0], l2);
    }
    if ((o2 === `ne` && !c2 || o2 === `=` && c2) && (s2[0] === t4 && is_nonzero_ast(s2[1], l2) === false || s2[1] === t4 && is_nonzero_ast(s2[0], l2) === false)) return true;
    if (o2 === `<`) {
      if (!c2) {
        if (s2[0] === t4 && is_negative_ast(s2[1], l2, false) || s2[1] === t4 && is_positive_ast(s2[0], l2, false)) return true;
      } else if (s2[0] === t4 && is_positive_ast(s2[1], l2, true) || s2[1] === t4 && is_negative_ast(s2[0], l2, true)) return true;
    }
    if (o2 === `le`) {
      if (!c2) {
        if (s2[0] === t4 && is_negative_ast(s2[1], l2, true) || s2[1] === t4 && is_positive_ast(s2[0], l2, true)) return true;
      } else if (s2[0] === t4 && is_positive_ast(s2[1], l2, false) || s2[1] === t4 && is_negative_ast(s2[0], l2, false)) return true;
    }
    if (o2 !== `and` && o2 !== `or` || c2) return;
    s2.length > 2 && (i3 = unflattenRight(i3), s2 = i3.slice(1));
    let u2 = is_nonzero_ast(t4, s2[0], r2), d2 = is_nonzero_ast(t4, s2[1], r2);
    return simple_assumption_combination(o2, u2, d2);
  }
  if (Array.isArray(t4)) {
    let i3;
    i3 = Array.isArray(n2) ? n2 : n2.get_assumptions([variables(t4)]);
    let a2 = t4[0], o2 = t4.slice(1);
    if (a2 === `-`) return is_nonzero_ast(o2[0], i3, r2);
    if (a2 === `+`) {
      if (o2.length > 2 && (t4 = unflattenRight(t4), o2 = t4.slice(1)), equal(o2[0], simplify([`-`, o2[1]], r2))) return false;
      let n3 = is_nonzero_ast(o2[0], i3, r2), a3 = is_nonzero_ast(o2[1], i3, r2);
      if (n3 === false) return a3;
      if (a3 === false) return n3;
      let s2 = is_real_ast(o2[0], i3, r2), c2 = is_real_ast(o2[1], i3, r2);
      if (!s2 || !c2) return s2 === true ? c2 === false ? true : void 0 : c2 === true && s2 === false ? true : void 0;
      let l2 = is_positive_ast(o2[0], i3, false, r2), u2 = is_positive_ast(o2[1], i3, false, r2), d2 = is_positive_ast(o2[0], i3, true, r2), p2 = is_positive_ast(o2[1], i3, true, r2);
      return l2 && p2 || d2 && u2 || l2 === false && p2 === false || d2 === false && u2 === false ? true : void 0;
    }
    if (a2 === `*`) {
      let t5 = true;
      for (let n3 = 0; n3 < o2.length; n3++) {
        let a3 = is_nonzero_ast(o2[n3], i3, r2);
        if (a3 === false) return false;
        a3 === void 0 && (t5 = false);
      }
      return t5 ? true : void 0;
    }
    if (a2 === `/`) {
      let t5 = is_nonzero_ast(o2[0], i3, r2);
      return is_nonzero_ast(o2[1], i3, r2) ? t5 : void 0;
    }
    if (a2 === `^`) {
      let t5 = is_nonzero_ast(o2[0], i3, r2);
      return t5 ? (o2[0] === 1 / 0 || o2[0] === -1 / 0) && is_nonzero_ast(o2[1], i3, r2) === false ? void 0 : true : is_positive_ast(o2[1], i3, true, r2) && t5 === false ? false : void 0;
    }
    return a2 === `apply` ? functions.C.nonzero && functions.C.nonzero.includes(o2[0]) && is_complex_ast(o2[1], i3, r2) || functions.R.nonzero && functions.R.nonzero.includes(o2[0]) && is_real_ast(o2[1], i3, r2) || functions.nonzeroC.nonzero && functions.nonzeroC.nonzero.includes(o2[0]) && is_nonzero_ast(o2[1], i3, r2) || functions.nonneg.nonzero && functions.nonneg.nonzero.includes(o2[0]) && is_positive_ast(o2[1], i3, false, r2) || functions.pos.nonzero && functions.pos.nonzero.includes(o2[0]) && is_positive_ast(o2[1], i3, true, r2) ? true : void 0 : a2 === `prime` ? void 0 : false;
  }
  return false;
}
function is_positive_ast(t4, n2, r2, i2) {
  if (typeof n2 != `object` && (n2 = []), i2 === void 0 && (i2 = n2), r2 === void 0 && (r2 = true), typeof t4 == `number`) return Number.isFinite(t4) ? r2 ? t4 > 0 : t4 >= 0 : false;
  var a2 = is_real_ast(t4, n2, i2);
  if (!a2) return a2;
  var o2 = evaluate_to_constant(t4, { remove_units_first: false, nan_for_non_numeric: false });
  if (o2 !== null) return typeof o2 == `number` && Number.isFinite(o2) ? r2 ? o2 > 0 : o2 >= 0 : false;
  if (typeof t4 == `string`) {
    let a3;
    if (a3 = Array.isArray(n2) ? n2 : n2.get_assumptions(t4), !Array.isArray(a3)) return;
    let o3 = a3, s2 = a3[0], c2 = a3.slice(1), l2 = false;
    for (; s2 === `not`; ) {
      if (l2 = !l2, a3 = c2[0], !Array.isArray(a3)) return;
      s2 = a3[0], c2 = a3.slice(1);
    }
    let u2 = narrow_assumptions(o3, i2);
    if (s2 === `=` && !l2 || s2 === `ne` && l2) {
      if (c2[0] === t4) return is_positive_ast(c2[1], u2, r2);
      if (c2[1] === t4) return is_positive_ast(c2[0], u2, r2);
    }
    if (s2 === `<`) if (l2) {
      if (c2[0] === t4 && is_positive_ast(c2[1], u2, r2)) return true;
      if (c2[1] === t4 && is_negative_ast(c2[0], u2, !r2)) return false;
    } else {
      if (c2[0] === t4 && is_negative_ast(c2[1], u2, false)) return false;
      if (c2[1] === t4 && is_positive_ast(c2[0], u2, false)) return true;
    }
    if (s2 === `le`) if (l2) {
      if (c2[0] === t4 && is_positive_ast(c2[1], u2, false)) return true;
      if (c2[1] === t4 && is_negative_ast(c2[0], u2, false)) return false;
    } else {
      if (c2[0] === t4 && is_negative_ast(c2[1], u2, !r2)) return false;
      if (c2[1] === t4 && is_positive_ast(c2[0], u2, r2)) return true;
    }
    if (s2 !== `and` && s2 !== `or` || l2) return;
    c2.length > 2 && (a3 = unflattenRight(a3), c2 = a3.slice(1));
    let d2 = is_positive_ast(t4, c2[0], r2, i2), p2 = is_positive_ast(t4, c2[1], r2, i2);
    return simple_assumption_combination(s2, d2, p2);
  }
  if (Array.isArray(t4)) {
    let a3;
    a3 = Array.isArray(n2) ? n2 : n2.get_assumptions([variables(t4)]);
    let o3 = t4[0], s2 = t4.slice(1);
    if (o3 === `-`) return is_negative_ast(s2[0], a3, r2, i2);
    if (o3 === `+`) {
      s2.length > 2 && (t4 = unflattenRight(t4), s2 = t4.slice(1));
      let n3 = is_positive_ast(s2[0], a3, false, i2), o4 = is_positive_ast(s2[1], a3, false, i2), c2 = is_positive_ast(s2[0], a3, true, i2), l2 = is_positive_ast(s2[1], a3, true, i2);
      if (r2) {
        if (n3 && l2 || c2 && o4) return true;
      } else if (n3 && o4) return true;
      if (r2) {
        if (c2 === false && l2 === false) return false;
      } else if (n3 === false && l2 === false || c2 === false && o4 === false) return false;
      return;
    }
    if (o3 === `*`) {
      if (!s2.every((t5) => is_nonzero_ast(t5, a3, i2) !== false)) return !r2;
      s2.length > 2 && (t4 = unflattenRight(t4), s2 = t4.slice(1));
      let n3 = is_real_ast(s2[0], a3, i2), o4 = is_real_ast(s2[1], a3, i2);
      if (n3 === void 0 || o4 === void 0) return;
      if (n3 === false) return o4 === false ? void 0 : false;
      if (o4 === false) return false;
      let c2 = is_positive_ast(s2[0], a3, false, i2), l2 = is_positive_ast(s2[1], a3, false, i2), u2 = is_positive_ast(s2[0], a3, true, i2), d2 = is_positive_ast(s2[1], a3, true, i2);
      if (r2) {
        if (u2 && d2 || c2 === false && l2 === false) return true;
        if (u2 === false && l2 || c2 && d2 === false) return false;
      } else {
        if (c2 && l2 || u2 === false && d2 === false) return true;
        if (u2 && l2 === false || c2 === false && d2) return false;
      }
      return;
    }
    if (o3 === `/`) {
      if (!is_nonzero_ast(s2[1], a3, i2)) return;
      if (is_nonzero_ast(s2[0], a3, i2) === false) return !r2;
      let t5 = is_positive_ast(s2[1], a3, true, i2);
      if (t5 === void 0) return;
      let n3 = t5 ? r2 : !r2, o4 = is_positive_ast(s2[0], a3, n3, i2);
      return o4 === void 0 ? void 0 : o4 === true ? t5 === true : t5 !== true;
    }
    if (o3 === `^`) {
      let t5 = is_nonzero_ast(s2[0], a3, i2);
      if (!t5) if (is_positive_ast(s2[1], a3, true, i2)) {
        if (t5 === false) return !r2;
        if (r2) return;
      } else return;
      else if (is_nonzero_ast(s2[1], a3, i2) === false) return s2[0] === 1 / 0 || s2[0] === -1 / 0 ? void 0 : true;
      return is_real_ast(s2[0], a3, i2) === true ? is_positive_ast(s2[0], a3, r2, i2) ? is_real_ast(s2[1], a3, i2) ? true : void 0 : is_integer_ast(simplify([`/`, s2[1], 2], i2), a3, i2) ? true : void 0 : void 0;
    }
    return o3 === `apply` && !r2 ? functions.C.nonneg && functions.C.nonneg.includes(s2[0]) && is_complex_ast(s2[1], a3, i2) || functions.R.nonneg && functions.R.nonneg.includes(s2[0]) && is_real_ast(s2[1], a3, i2) || functions.nonzeroC.nonneg && functions.nonzeroC.nonneg.includes(s2[0]) && is_nonzero_ast(s2[1], a3, i2) && is_complex_ast(s2[1], a3, i2) || functions.nonneg.nonneg && functions.nonneg.nonneg.includes(s2[0]) && is_positive_ast(s2[1], a3, false, i2) || functions.pos.nonneg && functions.pos.nonneg.includes(s2[0]) && is_positive_ast(s2[1], a3, true, i2) ? true : void 0 : o3 === `apply` && r2 ? functions.C.pos && functions.C.pos.includes(s2[0]) && is_complex_ast(s2[1], a3, i2) || functions.R.pos && functions.R.pos.includes(s2[0]) && is_real_ast(s2[1], a3, i2) || functions.nonzeroC.pos && functions.nonzeroC.pos.includes(s2[0]) && is_nonzero_ast(s2[1], a3, i2) && is_complex_ast(s2[1], a3, i2) || functions.nonneg.pos && functions.nonneg.pos.includes(s2[0]) && is_positive_ast(s2[1], a3, false, i2) || functions.pos.pos && functions.pos.pos.includes(s2[0]) && is_positive_ast(s2[1], a3, true, i2) ? true : void 0 : o3 === `prime` ? void 0 : false;
  }
  return false;
}
function is_negative_ast(t4, n2, r2, i2) {
  r2 === void 0 && (r2 = true);
  var a2 = is_real_ast(t4, n2, i2);
  if (a2 === true) {
    let a3 = is_positive_ast(t4, n2, !r2, i2);
    return a3 === false ? true : a3 === true ? false : void 0;
  }
  return a2;
}
function ParseError(t4, n2) {
  this.name = `ParseError`, this.message = t4 || `Error parsing input`, this.stack = Error().stack, this.location = n2;
}
ParseError.prototype = Object.create(Error.prototype), ParseError.prototype.constructor = ParseError;
var lexer = class {
  constructor(t4, n2 = `\\s`) {
    this.input = ``, this.location = 0, this.token_rules = [], this.initial_whitespace = RegExp(`^(` + n2 + `)+`);
    for (let n3 of t4) this.token_rules.push([RegExp(`^` + n3[0])].concat(n3.slice(1)));
  }
  set_input(t4) {
    if (typeof t4 != `string`) throw Error(`Input must be a string`);
    this.input = t4, this.location = 0;
  }
  return_state() {
    return { input: this.input, location: this.location };
  }
  set_state({ input: t4 = null, location: n2 = 0 } = {}) {
    t4 !== null && (this.input = t4, this.location = n2);
  }
  advance({ remove_initial_space: t4 = true } = {}) {
    let n2 = this.initial_whitespace.exec(this.input);
    if (n2) {
      let r3 = n2[0].length;
      if (this.input = this.input.slice(r3), this.location += r3, !t4) return { token_type: `SPACE`, token_text: n2[0], original_text: n2[0] };
    }
    if (this.input.length === 0) return { token_type: `EOF`, token_text: ``, original_text: `` };
    n2 = null;
    for (var r2 of this.token_rules) if (n2 = r2[0].exec(this.input), n2) {
      let t5 = n2[0].length;
      this.input = this.input.slice(t5), this.location += t5;
      break;
    }
    return n2 === null ? { token_type: `INVALID`, token_text: this.input[0], original_text: this.input[0] } : r2.length > 2 ? { token_type: r2[1], token_text: r2[2], original_text: n2[0] } : { token_type: r2[1], token_text: n2[0], original_text: n2[0] };
  }
  unput(t4) {
    if (typeof t4 != `string`) throw Error(`Input must be a string`);
    this.location -= t4.length, this.input = t4 + this.input;
  }
}, is_associative = { "+": true, "*": true, and: true, or: true, union: true, intersect: true };
function flatten(t4) {
  if (!Array.isArray(t4)) return t4;
  var n2 = t4[0], r2 = t4.slice(1);
  if (r2 = r2.map(function(t5, n3) {
    return flatten(t5);
  }), is_associative[n2]) {
    for (var i2 = [], a2 = 0; a2 < r2.length; a2++) Array.isArray(r2[a2]) && r2[a2][0] === n2 && r2[a2].length > 2 ? i2 = i2.concat(r2[a2].slice(1)) : i2.push(r2[a2]);
    r2 = i2;
  }
  return [n2].concat(r2);
}
var number_rules_sci$1 = [[`[0-9]+(\\.[0-9]*)?(E[+\\-]?[0-9]+\\s*($|(?=\\,|\\||\\)|\\}|\\])))?`, `NUMBER`], [`\\.[0-9]+(E[+\\-]?[0-9]+\\s*($|(?=\\,|\\||\\)|\\}|\\])))?`, `NUMBER`]], number_rules_non_sci$1 = [[`[0-9]+(\\.[0-9]*)?`, `NUMBER`], [`\\.[0-9]+`, `NUMBER`]], base_text_rules = [[`\\*\\*`, `^`], [`\\*`, `*`], [`\\xB7`, `*`], [`·`, `*`], [`•`, `*`], [`⋅`, `*`], [`×`, `*`], [`/`, `/`], [`-`, `-`], [`֊`, `-`], [`־`, `-`], [`᠆`, `-`], [`‐`, `-`], [`‑`, `-`], [`‒`, `-`], [`–`, `-`], [`—`, `-`], [`―`, `-`], [`⁻`, `-`], [`₋`, `-`], [`−`, `-`], [`⸺`, `-`], [`⸻`, `-`], [`﹘`, `-`], [`﹣`, `-`], [`－`, `-`], [`\\+`, `+`], [`plusminus(?![a-zA-Z0-9])`, `PM`], [`±`, `PM`], [`\\^`, `^`], [`‸`, `^`], [`ʌ`, `^`], [`′`, `'`], [`\\|`, `|`], [`\\(`, `(`], [`\\)`, `)`], [`\\[`, `[`], [`\\]`, `]`], [`\\{`, `{`], [`\\}`, `}`], [`⟨`, `LANGLE`], [`⟩`, `RANGLE`], [`〈`, `LANGLE`], [`〉`, `RANGLE`], [`,`, `,`], [`:`, `:`], [`α`, `VARMULTICHAR`, `alpha`], [`β`, `VARMULTICHAR`, `beta`], [`ϐ`, `VARMULTICHAR`, `beta`], [`Γ`, `VARMULTICHAR`, `Gamma`], [`γ`, `VARMULTICHAR`, `gamma`], [`Δ`, `VARMULTICHAR`, `Delta`], [`δ`, `VARMULTICHAR`, `delta`], [`ε`, `VARMULTICHAR`, `epsilon`], [`ϵ`, `VARMULTICHAR`, `epsilon`], [`ζ`, `VARMULTICHAR`, `zeta`], [`η`, `VARMULTICHAR`, `eta`], [`Θ`, `VARMULTICHAR`, `Theta`], [`ϴ`, `VARMULTICHAR`, `Theta`], [`θ`, `VARMULTICHAR`, `theta`], [`ᶿ`, `VARMULTICHAR`, `theta`], [`ϑ`, `VARMULTICHAR`, `theta`], [`ι`, `VARMULTICHAR`, `iota`], [`κ`, `VARMULTICHAR`, `kappa`], [`Λ`, `VARMULTICHAR`, `Lambda`], [`λ`, `VARMULTICHAR`, `lambda`], [`μ`, `VARMULTICHAR`, `mu`], [`µ`, `VARMULTICHAR`, `mu`], [`ν`, `VARMULTICHAR`, `nu`], [`Ξ`, `VARMULTICHAR`, `Xi`], [`ξ`, `VARMULTICHAR`, `xi`], [`Π`, `VARMULTICHAR`, `Pi`], [`π`, `VARMULTICHAR`, `pi`], [`ϖ`, `VARMULTICHAR`, `pi`], [`ρ`, `VARMULTICHAR`, `rho`], [`ϱ`, `VARMULTICHAR`, `rho`], [`Σ`, `VARMULTICHAR`, `Sigma`], [`σ`, `VARMULTICHAR`, `sigma`], [`ς`, `VARMULTICHAR`, `sigma`], [`τ`, `VARMULTICHAR`, `tau`], [`Υ`, `VARMULTICHAR`, `Upsilon`], [`υ`, `VARMULTICHAR`, `upsilon`], [`Φ`, `VARMULTICHAR`, `Phi`], [`φ`, `VARMULTICHAR`, `phi`], [`ϕ`, `VARMULTICHAR`, `phi`], [`Ψ`, `VARMULTICHAR`, `Psi`], [`ψ`, `VARMULTICHAR`, `psi`], [`Ω`, `VARMULTICHAR`, `Omega`], [`ω`, `VARMULTICHAR`, `omega`], [`∅`, `VARMULTICHAR`, `emptyset`], [`oo(?![a-zA-Z0-9])`, `INFINITY`], [`OO(?![a-zA-Z0-9])`, `INFINITY`], [`infty(?![a-zA-Z0-9])`, `INFINITY`], [`infinity(?![a-zA-Z0-9])`, `INFINITY`], [`Infinity(?![a-zA-Z0-9])`, `INFINITY`], [`∞`, `INFINITY`], [`ℯ`, `VAR`, `e`], [`♠`, `VARMULTICHAR`, `spade`], [`♡`, `VARMULTICHAR`, `heart`], [`♢`, `VARMULTICHAR`, `diamond`], [`♣`, `VARMULTICHAR`, `club`], [`★`, `VARMULTICHAR`, `bigstar`], [`◯`, `VARMULTICHAR`, `bigcirc`], [`◊`, `VARMULTICHAR`, `lozenge`], [`△`, `VARMULTICHAR`, `bigtriangleup`], [`▽`, `VARMULTICHAR`, `bigtriangledown`], [`⧫`, `VARMULTICHAR`, `blacklozenge`], [`■`, `VARMULTICHAR`, `blacksquare`], [`▲`, `VARMULTICHAR`, `blacktriangle`], [`▼`, `VARMULTICHAR`, `blacktriangledown`], [`◀`, `VARMULTICHAR`, `blacktriangleleft`], [`▶`, `VARMULTICHAR`, `blacktriangleright`], [`□`, `VARMULTICHAR`, `Box`], [`∘`, `VARMULTICHAR`, `circ`], [`⋆`, `VARMULTICHAR`, `star`], [`and(?![a-zA-Z0-9])`, `AND`], [`\\&\\&?`, `AND`], [`∧`, `AND`], [`or(?![a-zA-Z0-9])`, `OR`], [`∨`, `OR`], [`not(?![a-zA-Z0-9])`, `NOT`], [`¬`, `NOT`], [`=`, `=`], [`᐀`, `=`], [`゠`, `=`], [`!=`, `NE`], [`≠`, `NE`], [`<=`, `LE`], [`≤`, `LE`], [`>=`, `GE`], [`≥`, `GE`], [`<`, `<`], [`>`, `>`], [`forall(?![a-zA-Z0-9])`, `FORALL`], [`∀`, `FORALL`], [`exists(?![a-zA-Z0-9])`, `EXISTS`], [`∃`, `EXISTS`], [`elementof(?![a-zA-Z0-9])`, `IN`], [`∈`, `IN`], [`notelementof(?![a-zA-Z0-9])`, `NOTIN`], [`∉`, `NOTIN`], [`containselement(?![a-zA-Z0-9])`, `NI`], [`∋`, `NI`], [`notcontainselement(?![a-zA-Z0-9])`, `NOTNI`], [`∌`, `NOTNI`], [`subset(?![a-zA-Z0-9])`, `SUBSET`], [`⊂`, `SUBSET`], [`subseteq(?![a-zA-Z0-9])`, `SUBSETEQ`], [`⊆`, `SUBSETEQ`], [`notsubset(?![a-zA-Z0-9])`, `NOTSUBSET`], [`⊄`, `NOTSUBSET`], [`notsubseteq(?![a-zA-Z0-9])`, `NOTSUBSETEQ`], [`⊈`, `NOTSUBSETEQ`], [`superset(?![a-zA-Z0-9])`, `SUPERSET`], [`⊃`, `SUPERSET`], [`superseteq(?![a-zA-Z0-9])`, `SUPERSETEQ`], [`⊇`, `SUPERSETEQ`], [`notsuperset(?![a-zA-Z0-9])`, `NOTSUPERSET`], [`⊅`, `NOTSUPERSET`], [`notsuperseteq(?![a-zA-Z0-9])`, `NOTSUPERSETEQ`], [`⊉`, `NOTSUPERSETEQ`], [`union(?![a-zA-Z0-9])`, `UNION`], [`∪`, `UNION`], [`intersect(?![a-zA-Z0-9])`, `INTERSECT`], [`∩`, `INTERSECT`], [`rightarrow(?![a-zA-Z0-9])`, `RIGHTARROW`], [`→`, `RIGHTARROW`], [`⟶`, `RIGHTARROW`], [`leftarrow(?![a-zA-Z0-9])`, `LEFTARROW`], [`←`, `LEFTARROW`], [`⟵`, `LEFTARROW`], [`leftrightarrow(?![a-zA-Z0-9])`, `LEFTRIGHTARROW`], [`↔`, `LEFTRIGHTARROW`], [`⟷`, `LEFTRIGHTARROW`], [`implies(?![a-zA-Z0-9])`, `IMPLIES`], [`⇒`, `IMPLIES`], [`⟹`, `IMPLIES`], [`impliedby(?![a-zA-Z0-9])`, `IMPLIEDBY`], [`⇐`, `IMPLIEDBY`], [`⟸`, `IMPLIEDBY`], [`iff(?![a-zA-Z0-9])`, `IFF`], [`⇔`, `IFF`], [`⟺`, `IFF`], [`perp(?![a-zA-Z0-9])`, `PERP`], [`⟂`, `PERP`], [`parallel(?![a-zA-Z0-9])`, `PARALLEL`], [`∥`, `PARALLEL`], [`angle(?![a-zA-Z0-9])`, `ANGLE`], [`∠`, `ANGLE`], [`int(?![a-zA-Z0-9])`, `INT`], [`∫`, `INT`], [`!`, `!`], [`'`, `'`], [`_`, `_`], [`\\.\\.\\.`, `LDOTS`], [`[a-zA-Z∂][a-zA-Z∂0-9]*`, `VAR`], [`[＿$%]`, `VAR`]], allowSimplifiedFunctionApplicationDefault$1 = true, splitSymbolsDefault = true, unsplitSymbolsDefault = `alpha.beta.gamma.Gamma.delta.Delta.epsilon.zeta.eta.theta.Theta.iota.kappa.lambda.Lambda.mu.nu.xi.Xi.pi.Pi.rho.sigma.Sigma.tau.Tau.upsilon.Upsilon.phi.Phi.chi.psi.Psi.omega.Omega.angle.deg.emptyset`.split(`.`), appliedFunctionSymbolsDefault$1 = `abs.exp.log.ln.log10.sign.sqrt.cbrt.nthroot.mod.erf.cos.cosh.acos.acosh.arccos.arccosh.cot.coth.acot.acoth.arccot.arccoth.csc.csch.acsc.acsch.arccsc.arccsch.sec.sech.asec.asech.arcsec.arcsech.sin.sinh.asin.asinh.arcsin.arcsinh.tan.tanh.atan.atan2.atanh.arctan.arctanh.arg.conj.re.im.det.trace.nPr.nCr.floor.ceil.round`.split(`.`), functionSymbolsDefault$1 = [`f`, `g`], operatorSymbolsDefault$1 = [`binom`, `vec`, `linesegment`], unitsDefault$1 = get_all_units(), parseLeibnizNotationDefault$1 = true, textToAst$4 = class {
  constructor({ allowSimplifiedFunctionApplication: t4 = allowSimplifiedFunctionApplicationDefault$1, splitSymbols: n2 = splitSymbolsDefault, unsplitSymbols: r2 = unsplitSymbolsDefault, appliedFunctionSymbols: i2 = appliedFunctionSymbolsDefault$1, functionSymbols: a2 = functionSymbolsDefault$1, operatorSymbols: o2 = operatorSymbolsDefault$1, units: s2 = unitsDefault$1, parseLeibnizNotation: c2 = parseLeibnizNotationDefault$1, parseScientificNotation: l2 = true } = {}) {
    this.allowSimplifiedFunctionApplication = t4, this.splitSymbols = n2, this.unsplitSymbols = r2, this.appliedFunctionSymbols = i2, this.functionSymbols = a2, this.operatorSymbols = o2, this.units = s2, this.parseLeibnizNotation = c2;
    let u2 = base_text_rules;
    u2 = l2 ? [...number_rules_sci$1, ...u2] : [...number_rules_non_sci$1, ...u2], this.lexer = new lexer(u2);
  }
  advance(t4) {
    if (this.token = this.lexer.advance(t4), this.token.token_type === `INVALID`) throw new ParseError(`Invalid symbol '` + this.token.original_text + `'`, this.lexer.location);
  }
  return_state() {
    return { lexer_state: this.lexer.return_state(), token: Object.assign({}, this.token) };
  }
  set_state(t4) {
    this.lexer.set_state(t4.lexer_state), this.token = Object.assign({}, t4.token);
  }
  convert(t4) {
    this.lexer.set_input(t4), this.advance();
    var n2 = this.statement_list();
    if (this.token.token_type !== `EOF`) throw new ParseError(`Invalid location of '` + this.token.original_text + `'`, this.lexer.location);
    return flatten(n2);
  }
  statement_list() {
    for (var t4 = [this.statement()]; this.token.token_type === `,`; ) this.advance(), t4.push(this.statement());
    return t4 = t4.length > 1 ? [`list`].concat(t4) : t4[0], t4;
  }
  statement({ inside_absolute_value: t4 = 0 } = {}) {
    if (this.token.token_type === `LDOTS`) return this.advance(), [`ldots`];
    var n2;
    try {
      n2 = this.return_state();
      let r2 = this.statement_a({ inside_absolute_value: t4 });
      return this.token.token_type === `:` ? (this.advance(), [`:`, r2, this.statement_a()]) : r2;
    } catch (t5) {
      try {
        this.set_state(n2);
        let r2 = this.statement_a({ parse_absolute_value: false });
        if (this.token.token_type !== `|`) throw t5;
        return this.advance(), [`|`, r2, this.statement_a({ parse_absolute_value: false })];
      } catch {
        throw t5;
      }
    }
  }
  statement_a({ inside_absolute_value: t4 = 0, parse_absolute_value: n2 = true } = {}) {
    for (var r2 = this.statement_b({ inside_absolute_value: t4, parse_absolute_value: n2 }); [`IMPLIES`, `IMPLIEDBY`, `IFF`, `LEFTARROW`, `RIGHTARROW`, `LEFTRIGHTARROW`].includes(this.token.token_type); ) {
      let i2 = this.token.token_type.toLowerCase();
      this.advance();
      let a2 = this.statement_b({ inside_absolute_value: t4, parse_absolute_value: n2 });
      r2 = [i2, r2, a2];
    }
    return r2;
  }
  statement_b({ inside_absolute_value: t4 = 0, parse_absolute_value: n2 = true } = {}) {
    for (var r2 = this.statement_c({ inside_absolute_value: t4, parse_absolute_value: n2 }); this.token.token_type === `OR`; ) {
      let i2 = this.token.token_type.toLowerCase();
      this.advance();
      let a2 = this.statement_c({ inside_absolute_value: t4, parse_absolute_value: n2 });
      r2 = [i2, r2, a2];
    }
    return r2;
  }
  statement_c(t4) {
    for (var n2 = this.relation(t4); this.token.token_type === `AND`; ) {
      let r2 = this.token.token_type.toLowerCase();
      this.advance();
      let i2 = this.relation(t4);
      n2 = [r2, n2, i2];
    }
    return n2;
  }
  relation(t4) {
    if (this.token.token_type === `NOT` || this.token.token_type === `!`) return this.advance(), [`not`, this.relation(t4)];
    if (this.token.token_type === `FORALL` || this.token.token_type === `EXISTS`) {
      let n3 = this.token.token_type.toLowerCase();
      return this.advance(), [n3, this.relation(t4)];
    }
    for (var n2 = this.expression(t4); this.token.token_type === `=` || this.token.token_type === `NE` || this.token.token_type === `<` || this.token.token_type === `>` || this.token.token_type === `LE` || this.token.token_type === `GE` || this.token.token_type === `IN` || this.token.token_type === `NOTIN` || this.token.token_type === `NI` || this.token.token_type === `NOTNI` || this.token.token_type === `SUBSET` || this.token.token_type === `NOTSUBSET` || this.token.token_type === `SUBSETEQ` || this.token.token_type === `NOTSUBSETEQ` || this.token.token_type === `SUPERSET` || this.token.token_type === `NOTSUPERSET` || this.token.token_type === `SUPERSETEQ` || this.token.token_type === `NOTSUPERSETEQ`; ) {
      let r2 = this.token.token_type.toLowerCase(), i2 = 0;
      this.token.token_type === `<` || this.token.token_type === `LE` ? i2 = -1 : (this.token.token_type === `>` || this.token.token_type === `GE`) && (i2 = 1), this.advance();
      let a2 = this.expression(t4);
      if (i2 === -1) if (this.token.token_type === `<` || this.token.token_type === `LE`) {
        let i3 = [`tuple`];
        r2 === `<` ? i3.push(true) : i3.push(false);
        let o2 = [`tuple`, n2, a2];
        for (; this.token.token_type === `<` || this.token.token_type === `LE`; ) this.token.token_type === `<` ? i3.push(true) : i3.push(false), this.advance(), o2.push(this.expression(t4));
        n2 = [`lts`, o2, i3];
      } else n2 = [r2, n2, a2];
      else if (i2 === 1) if (this.token.token_type === `>` || this.token.token_type === `GE`) {
        let i3 = [`tuple`];
        r2 === `>` ? i3.push(true) : i3.push(false);
        let o2 = [`tuple`, n2, a2];
        for (; this.token.token_type === `>` || this.token.token_type === `GE`; ) this.token.token_type === `>` ? i3.push(true) : i3.push(false), this.advance(), o2.push(this.expression(t4));
        n2 = [`gts`, o2, i3];
      } else n2 = [r2, n2, a2];
      else if (r2 === `=`) for (n2 = [`=`, n2, a2]; this.token.token_type === `=`; ) this.advance(), n2.push(this.expression(t4));
      else n2 = [r2, n2, a2];
    }
    return n2;
  }
  expression(t4) {
    if (this.token.token_type === `NOT` || this.token.token_type === `!`) return this.advance(), [`not`, this.expression(t4)];
    let n2 = false;
    this.token.token_type === `+` && (n2 = true, this.advance());
    let r2 = false;
    this.token.token_type === `-` && (r2 = true, this.advance());
    let i2 = false;
    this.token.token_type === `PM` && (i2 = true, this.advance());
    var a2 = this.term(t4);
    if (r2 || n2) {
      if (a2 === false) return (n2 ? `+` : ``) + (r2 ? `-` : ``);
      if (typeof a2 == `string` && [...a2].every((t5) => [`+`, `-`].includes(t5))) return (n2 ? `+` : ``) + (r2 ? `-` : ``) + a2;
    }
    for (a2 === false && (a2 = `＿`), r2 && (a2 = a2 > 0 ? -a2 : [`-`, a2]), i2 && (a2 = [`pm`, a2]), n2 && (a2 = [`+`, a2]); [`+`, `-`, `PM`, `UNION`, `INTERSECT`, `PERP`, `PARALLEL`].includes(this.token.token_type); ) {
      let n3 = this.token.token_type.toLowerCase(), r3 = false, i3 = false, o2 = false;
      this.token.token_type === `-` ? (n3 = `+`, r3 = true, this.advance()) : this.token.token_type === `PM` ? (n3 = `+`, i3 = true, this.advance()) : (this.advance(), n3 === `+` && (this.token.token_type === `-` ? (r3 = true, o2 = true, this.advance()) : this.token.token_type === `PM` && (i3 = true, this.advance())));
      let s2 = this.term(t4);
      if (n3 === `+`) {
        if (s2 === false && (typeof a2 == `number` || typeof a2 == `string`)) {
          if (o2) return a2 + `+-`;
          if (r3) return a2 + `-`;
          if (!i3) return a2 + `+`;
        } else if (typeof s2 == `string` && [...s2].every((t5) => [`+`, `-`].includes(t5)) && (typeof a2 == `number` || typeof a2 == `string`)) {
          if (o2) return a2 + `+-` + s2;
          if (r3) return a2 + `-` + s2;
          if (!i3) return a2 + `+` + s2;
        }
      }
      s2 === false && (s2 = `＿`), r3 && (s2 = s2 > 0 ? -s2 : [`-`, s2]), i3 && (s2 = [`pm`, s2]), a2 = [n3, a2, s2];
    }
    return a2;
  }
  term(t4) {
    var n2 = this.factor(t4), r2 = false;
    do
      if (r2 = false, this.token.token_type === `*`) {
        this.advance(), n2 === false && (n2 = `＿`);
        let i2 = this.factor(t4);
        i2 === false && (i2 = `＿`), n2 = [`*`, n2, i2], r2 = true;
      } else if (this.token.token_type === `/`) {
        this.advance(), n2 === false && (n2 = `＿`);
        let i2 = this.factor(t4);
        i2 === false && (i2 = `＿`), n2 = [`/`, n2, i2], r2 = true;
      } else {
        let i2 = Object.assign({}, t4);
        i2.allow_absolute_value_closing = true;
        let a2 = this.nonMinusFactor(i2);
        a2 !== false && (n2 === false && (n2 = `＿`), n2 = [`*`, n2, a2], r2 = true);
      }
    while (r2);
    return this.convert_units_in_term(flatten(n2));
  }
  convert_units_in_term(t4) {
    if (!Array.isArray(t4)) return t4;
    let n2 = t4[0], r2 = t4.slice(1);
    if (n2 === `*`) {
      let n3 = r2.length;
      for (let [t5, i2] of r2.entries()) {
        let a2 = this.units[i2];
        if (a2) {
          if (a2.prefix && t5 < n3 - 1) {
            let o2;
            o2 = t5 === n3 - 2 ? r2[n3 - 1] : this.convert_units_in_term([`*`, ...r2.slice(t5 + 1)]);
            let s2 = i2;
            a2.substitute && (s2 = a2.substitute);
            let c2 = [`unit`, s2, o2];
            return t5 === 0 ? c2 : [`*`, ...r2.slice(0, t5), c2];
          } else if (!a2.prefix && t5 > 0) {
            let o2 = i2;
            a2.substitute && (o2 = a2.substitute);
            let s2;
            return s2 = t5 === 1 ? [`unit`, r2[0], o2] : [`unit`, [`*`, ...r2.slice(0, t5)], o2], t5 === n3 - 1 ? s2 : this.convert_units_in_term([`*`, s2, ...r2.slice(t5 + 1)]);
          }
        }
      }
      return t4;
    } else if (n2 === `/`) return [`/`, this.convert_units_in_term(r2[0]), this.convert_units_in_term(r2[1])];
    return t4;
  }
  factor(t4) {
    if (this.token.token_text === `+`) {
      this.advance();
      let n3 = this.factor(t4);
      return n3 === false ? `+` : typeof n3 == `string` && [...n3].every((t5) => [`+`, `-`].includes(t5)) ? `+` + n3 : [`+`, n3];
    }
    if (this.token.token_type === `-`) {
      this.advance();
      let n3 = this.factor(t4);
      return n3 > 0 ? -n3 : n3 === false ? `-` : typeof n3 == `string` && [...n3].every((t5) => [`+`, `-`].includes(t5)) ? `-` + n3 : [`-`, n3];
    }
    let n2 = this.nonMinusFactor(t4);
    return n2 === false && this.token.token_type === `PERP` && (n2 = `perp`, this.advance()), n2;
  }
  nonMinusFactor(t4) {
    for (var n2 = this.baseFactor(t4); this.token.token_type === `^` || this.token.token_type === `!` || this.token.token_type === `'`; ) if (n2 === false && (n2 = `＿`), this.token.token_type === `^`) {
      this.advance();
      let r2 = this.get_subsuperscript(t4);
      n2 = [`^`, n2, r2];
    } else this.token.token_type === `!` ? (n2 = [`apply`, `factorial`, n2], this.advance()) : (n2 = [`prime`, n2], this.advance());
    return n2;
  }
  get_subsuperscript({ parse_absolute_value: t4 }) {
    if ([`+`, `-`, `PERP`].includes(this.token.token_type)) {
      let t5 = this.token.token_type.toLowerCase();
      return this.advance(), t5;
    } else {
      let n2 = this.baseFactor({ parse_absolute_value: t4, in_subsuperscript_with_no_delimiters: true });
      return n2 === false && (n2 = `＿`), n2;
    }
  }
  baseFactor({ inside_absolute_value: t4 = 0, parse_absolute_value: n2 = true, allow_absolute_value_closing: r2 = false, in_subsuperscript_with_no_delimiters: i2 = false } = {}) {
    var a2 = false;
    if (this.token.token_type === `NUMBER`) a2 = parseFloat(this.token.token_text), this.advance();
    else if (this.token.token_type === `INFINITY`) a2 = 1 / 0, this.advance();
    else if (this.token.token_type === `VAR` || this.token.token_type === `VARMULTICHAR`) if (a2 = this.token.token_text, this.appliedFunctionSymbols.includes(a2) || this.functionSymbols.includes(a2)) {
      let t5 = false;
      if (this.appliedFunctionSymbols.includes(a2) && (t5 = true), this.advance(), this.token.token_type === `_`) {
        this.advance();
        let t6 = this.get_subsuperscript({ parse_absolute_value: n2 });
        a2 = a2 === `log` && t6 === 10 ? `log10` : [`_`, a2, t6];
      }
      if (i2) t5 && (a2 = [`apply`, a2, `＿`]);
      else {
        for (; this.token.token_type === `'`; ) a2 = [`prime`, a2], this.advance();
        for (; this.token.token_type === `^`; ) {
          this.advance();
          let t6 = this.get_subsuperscript({ parse_absolute_value: n2 });
          a2 = [`^`, a2, t6];
        }
        if (this.token.token_type === `(`) {
          this.advance();
          let t6 = this.statement_list();
          if (this.token.token_type !== `)`) throw new ParseError(`Expecting )`, this.lexer.location);
          this.advance(), t6[0] === `list` && (t6[0] = `tuple`), a2 = [`apply`, a2, t6];
        } else if (t5) {
          if (!this.allowSimplifiedFunctionApplication) throw new ParseError(`Expecting ( after function`, this.lexer.location);
          let t6 = this.factor({ parse_absolute_value: n2 });
          t6 === false && (t6 = `＿`), a2 = [`apply`, a2, t6];
        }
      }
      return a2;
    } else if (this.operatorSymbols.includes(a2)) if (this.advance(), this.token.token_type === `(`) {
      this.advance();
      let t5 = this.statement_list();
      if (this.token.token_type !== `)`) throw new ParseError(`Expecting )`, this.lexer.location);
      this.advance(), a2 = t5[0] === `list` ? [a2, ...t5.slice(1)] : [a2, t5];
    } else {
      let t5 = this.factor({ parse_absolute_value: n2 });
      t5 === false && (t5 = `＿`), a2 = [a2, t5];
    }
    else {
      if (this.parseLeibnizNotation) {
        let t5 = this.return_state(), n3 = this.leibniz_notation();
        if (n3) return n3;
        this.set_state(t5);
      }
      let i3 = this.splitSymbols;
      if (i3 && (this.token.token_type === `VARMULTICHAR` || this.unsplitSymbols.includes(a2) || a2.length === 1 || a2.match(/[\d]/g)) && (i3 = false), i3) {
        for (let t5 = a2.length - 1; t5 >= 0; t5--) this.lexer.unput(` `), this.lexer.unput(a2[t5]);
        return this.advance(), this.baseFactor({ inside_absolute_value: t4, parse_absolute_value: n2, allow_absolute_value_closing: r2 });
      } else this.advance();
    }
    else if (this.token.token_type === `(` || this.token.token_type === `[` || this.token.token_type === `{` || this.token.token_type === `LANGLE`) {
      let t5 = this.token.token_type, n3, r3;
      this.token.token_type === `(` ? (n3 = `)`, r3 = `]`) : this.token.token_type === `[` ? (n3 = `]`, r3 = `)`) : this.token.token_type === `{` ? (n3 = `}`, r3 = null) : (n3 = `RANGLE`, r3 = null), this.advance(), a2 = this.statement_list();
      let i3 = 1;
      if (a2[0] === `list` && (i3 = a2.length - 1), this.token.token_type !== n3) {
        if (i3 !== 2 || r3 === null) throw new ParseError(`Expecting ` + n3, this.lexer.location);
        if (this.token.token_type !== r3) throw new ParseError(`Expecting ) or ]`, this.lexer.location);
        a2[0] = `tuple`, a2 = [`interval`, a2];
        let o2;
        o2 = t5 === `(` ? [`tuple`, false, true] : [`tuple`, true, false], a2.push(o2);
      } else i3 >= 2 ? t5 === `(` ? a2[0] = `tuple` : t5 === `[` ? a2[0] = `array` : t5 === `{` ? a2[0] = `set` : a2[0] = `altvector` : t5 === `{` && (a2 = (a2[0] === `|` || a2[0], [`set`, a2]));
      this.advance();
    } else if (this.token.token_type === `|` && n2 && (t4 === 0 || !r2)) {
      if (t4 += 1, this.advance(), a2 = this.statement({ inside_absolute_value: t4 }), a2 = [`apply`, `abs`, a2], this.token.token_type !== `|`) throw new ParseError(`Expecting |`, this.lexer.location);
      this.advance();
    } else if (this.token.token_type === `ANGLE`) if (this.advance(), this.token.token_type === `(`) {
      this.advance();
      let t5 = this.statement_list();
      if (this.token.token_type !== `)`) throw new ParseError(`Expecting )`, this.lexer.location);
      this.advance(), t5[0] === `list` ? a2 = [`angle`, ...t5.slice(1)] : t5[0] === `*` && (a2 = [`angle`, t5]);
    } else {
      let t5 = [], r3 = this.nonMinusFactor({ parse_absolute_value: n2 });
      for (; r3 !== false; ) t5.push(r3), r3 = this.nonMinusFactor({ parse_absolute_value: n2 });
      a2 = t5.length === 0 ? `angle` : [`angle`, ...t5];
    }
    else if (this.token.token_type === `INT`) {
      this.advance();
      let t5 = `int`;
      if (this.token.token_type === `_`) {
        this.advance();
        let r4 = this.get_subsuperscript({ parse_absolute_value: n2 });
        t5 = [`_`, t5, r4];
      }
      if (this.token.token_type === `^`) {
        this.advance();
        let r4 = this.get_subsuperscript({ parse_absolute_value: n2 });
        t5 = [`^`, t5, r4];
      }
      let r3 = flatten(this.term({ parse_absolute_value: n2 }));
      if (Array.isArray(r3) && r3[0] === `*`) {
        let t6 = [];
        for (let n3 = 0; n3 < r3.length - 1; n3++) if (r3[n3] === `d`) {
          let i3 = r3[n3 + 1];
          r3.splice(n3, 2), t6.push([`d`, i3]), n3--;
        }
        r3.push(...t6);
      }
      return [`apply`, t5, r3];
    }
    if (this.token.token_type === `_`) {
      a2 === false && (a2 = `＿`), this.advance();
      let t5 = this.get_subsuperscript({ parse_absolute_value: n2 });
      a2 = [`_`, a2, t5];
    }
    return a2;
  }
  leibniz_notation() {
    var t4 = this.token.token_text;
    if (!(this.token.token_type === `VAR` && (t4[0] === `d` || t4[0] === `∂`) && (t4.length === 1 || t4.length === 2 && /[a-zA-Z]/.exec(t4[1])))) return false;
    let n2 = t4[0], r2 = 1, i2 = ``, a2 = [], o2 = [];
    if (t4.length === 2) i2 = t4[1];
    else if (this.advance(), this.token.token_type === `VARMULTICHAR` || this.token.token_type === `VAR` && !this.token.token_text.includes(`∂`)) i2 = this.token.token_text;
    else {
      if (this.token.token_type !== `^` || (this.advance(), this.token.token_type !== `NUMBER`) || (r2 = parseFloat(this.token.token_text), !Number.isInteger(r2))) return false;
      if (this.advance(), this.token.token_type === `VAR` && !this.token.token_text.includes(`∂`) || this.token.token_type === `VARMULTICHAR`) i2 = this.token.token_text;
      else return false;
    }
    if (this.advance(), this.token.token_type !== `/`) return false;
    let s2 = 0;
    for (this.advance(); ; ) {
      if (this.token.token_type !== `VAR` || this.token.token_text[0] !== n2) return false;
      this.token.token_text.length > 2 && (this.lexer.unput(this.token.token_text.slice(2)), this.token.token_text = this.token.token_text.slice(0, 2));
      let c2 = this.token.token_text;
      if (c2.length === 2) if (/[a-zA-Z]/.exec(c2[1])) a2.push(c2[1]);
      else return false;
      else {
        if (this.advance(), !(this.token.token_type === `VAR` && !this.token.token_text.includes(`∂`) || this.token.token_type === `VARMULTICHAR`)) return false;
        a2.push(this.token.token_text);
      }
      let l2 = 1, u2 = false;
      if (this.advance({ remove_initial_space: false }), this.token.token_type === `SPACE` && (u2 = true, this.advance()), this.token.token_type === `^`) {
        if (this.advance(), this.token.token_type !== `NUMBER` || (l2 = parseFloat(this.token.token_text), !Number.isInteger(l2))) return false;
        u2 = false, this.advance({ remove_initial_space: false }), this.token.token_type === `SPACE` && (u2 = true, this.advance());
      }
      if (o2.push(l2), s2 += l2, s2 > r2) return false;
      if (s2 === r2) {
        if (!u2 && (this.token.token_type === `VAR` || this.token.token_type === `VARMULTICHAR`)) return false;
        this.token.token_type === `SPACE` && this.advance();
        let s3 = `derivative_leibniz`;
        n2 === `∂` && (s3 = `partial_` + s3), t4 = [s3], r2 === 1 ? t4.push(i2) : t4.push([`tuple`, i2, r2]);
        let c3 = [];
        for (let t5 = 0; t5 < a2.length; t5 += 1) o2[t5] === 1 ? c3.push(a2[t5]) : c3.push([`tuple`, a2[t5], o2[t5]]);
        return c3 = [`tuple`].concat(c3), t4.push(c3), t4;
      }
    }
  }
}, MAX_PM_COUNT = 10, MAX_PM_EXPANSIONS = 1 << MAX_PM_COUNT;
function contains_pm(t4) {
  if (!Array.isArray(t4)) return false;
  if (t4[0] === `pm`) return true;
  for (let n2 = 1; n2 < t4.length; n2++) if (contains_pm(t4[n2])) return true;
  return false;
}
function count_pm(t4) {
  if (!Array.isArray(t4)) return 0;
  let n2 = +(t4[0] === `pm`);
  for (let r2 = 1; r2 < t4.length; r2++) n2 += count_pm(t4[r2]);
  return n2;
}
function expand_pm_signs(t4) {
  let n2 = count_pm(t4);
  if (n2 === 0) return [t4];
  if (n2 > MAX_PM_COUNT) throw Error(`pm: cannot expand ${n2} plus-minus operators (limit is ${MAX_PM_COUNT} → ${MAX_PM_EXPANSIONS} combinations)`);
  let r2 = 1 << n2, i2 = [];
  for (let n3 = 0; n3 < r2; n3++) i2.push(replace_pm(t4, n3, { idx: 0 }));
  return i2;
}
function replace_pm(t4, n2, r2) {
  if (!Array.isArray(t4)) return t4;
  if (t4[0] === `pm`) {
    let i3 = n2 >> r2.idx & 1;
    r2.idx += 1;
    let a2 = replace_pm(t4[1], n2, r2);
    return i3 === 0 ? a2 : [`-`, a2];
  }
  let i2 = [t4[0]];
  for (let a2 = 1; a2 < t4.length; a2++) i2.push(replace_pm(t4[a2], n2, r2));
  return i2;
}
var transformation_exports = __exportAll({ expand: () => expand, expand_relations: () => expand_relations, get_component: () => get_component, perform_matrix_multiplications: () => perform_matrix_multiplications, perform_matrix_scalar_multiplications: () => perform_matrix_scalar_multiplications, perform_vector_scalar_multiplications: () => perform_vector_scalar_multiplications, substitute: () => substitute, substitute_component: () => substitute_component }), textToAst$3 = new textToAst$4();
function expand(t4, n2) {
  var r2 = get_tree(t4);
  r2 = perform_matrix_multiplications(r2), r2 = perform_vector_matrix_additions_scalar_multiplications(r2);
  let i2 = (t5) => !Array.isArray(t5) || ![`d`, `*`].includes(t5[0]) || t5[0] === `*` && t5.slice(1).every((t6) => !Array.isArray(t6) || t6[0] !== `d`), a2 = (t5) => i2(t5) && !contains_pm(t5), o2 = (t5) => !contains_pm(t5);
  var s2 = [];
  return s2.push([textToAst$3.convert(`a*(b+c)`), textToAst$3.convert(`a*b+a*c`), { variables: { a: a2, b: true, c: true }, allow_extended_match: true }]), s2.push([textToAst$3.convert(`(a+b)*c`), textToAst$3.convert(`a*c+b*c`), { variables: { a: true, b: true, c: a2 }, allow_extended_match: true }]), n2 || s2.push([textToAst$3.convert(`(a+b)/c`), textToAst$3.convert(`a/c+b/c`), { variables: { a: true, b: true, c: o2 } }]), s2.push([textToAst$3.convert(`-(a+b)`), textToAst$3.convert(`-a-b`)]), s2.push([textToAst$3.convert(`a(-b)`), textToAst$3.convert(`-ab`)]), s2.push([textToAst$3.convert(`(a+b)^2`), textToAst$3.convert(`a^2+2ab+b^2`), { variables: { a: o2, b: o2 } }]), s2.push([textToAst$3.convert(`(a+b)^3`), textToAst$3.convert(`a^3+3a^2b+3ab^2+b^3`), { variables: { a: o2, b: o2 } }]), s2.push([textToAst$3.convert(`(a+b)^4`), textToAst$3.convert(`a^4+4a^3b+6a^2b^2+4ab^3+b^4`), { variables: { a: o2, b: o2 } }]), s2.push([textToAst$3.convert(`a*(b+c)*d`), textToAst$3.convert(`(a*b+a*c)*d`), { variables: { a: o2, b: true, c: true, d: true } }]), r2 = applyAllTransformations(r2, s2, 20), r2 = flatten$5(r2), r2 = evaluate_numbers(r2), r2 = collect_like_terms_factors(r2), r2 = normalize_negatives(r2), r2;
}
function perform_matrix_multiplications(t4, n2 = true, r2 = true) {
  var i2 = get_tree(t4);
  function a2(t5, n3) {
    let r3 = t5[1][1], i3 = t5[1][2], a3 = n3[1][2];
    if (n3[1][1] !== i3) return { success: false };
    let o3 = t5[2], s3 = n3[2], c3 = [`tuple`];
    for (let t6 = 0; t6 < r3; t6++) {
      let n4 = o3[t6 + 1].slice(1), r4 = [`tuple`];
      for (let t7 = 0; t7 < a3; t7++) {
        let a4 = s3.slice(1).map((n5) => n5[t7 + 1]);
        if (n4.length !== i3 || a4.length !== i3) return subtree;
        let o4 = [`+`, ...n4.map((t8, n5) => [`*`, t8, a4[n5]])];
        r4.push(o4);
      }
      c3.push(r4);
    }
    return { success: true, newMatrix: [`matrix`, [`tuple`, r3, a3], c3] };
  }
  function o2(t5, n3) {
    if (!Number.isInteger(n3) || n3 < 1) return { success: false };
    if (n3 === 1) return { success: true, newMatrix: t5 };
    let r3 = t5[1][1];
    if (t5[1][2] !== r3) return { success: false };
    let i3 = t5[2], a3 = JSON.parse(JSON.stringify(i3));
    for (let t6 = 1; t6 < n3; t6++) {
      let t7 = a3;
      a3 = [`tuple`];
      for (let n4 = 0; n4 < r3; n4++) {
        let o3 = t7[n4 + 1].slice(1), s3 = [`tuple`];
        for (let t8 = 0; t8 < r3; t8++) {
          let n5 = i3.slice(1).map((n6) => n6[t8 + 1]);
          if (o3.length !== r3 || n5.length !== r3) return subtree;
          let a4 = [`+`, ...o3.map((t9, r4) => [`*`, t9, n5[r4]])];
          s3.push(a4);
        }
        a3.push(s3);
      }
    }
    return { success: true, newMatrix: [`matrix`, [`tuple`, r3, r3], a3] };
  }
  function s2(t5, n3) {
    let r3 = t5[1][1], i3 = t5[1][2], a3 = t5[2], o3 = [`tuple`];
    for (let t6 = 0; t6 < r3; t6++) {
      let r4 = [`tuple`], s3 = a3[t6 + 1];
      for (let t7 = 0; t7 < i3; t7++) r4.push([`*`, n3, s3[t7 + 1]]);
      o3.push(r4);
    }
    return [`matrix`, [`tuple`, r3, i3], o3];
  }
  function c2() {
    let t5 = [`^`, [`matrix`, `size`, `matrixData`], `p`], n3 = { variables: { p: (t6) => Number.isInteger(t6) && t6 > 0, size: true, matrixData: true } };
    i2 = transform(i2, function(r3) {
      let i3 = match$1(r3, t5, n3);
      if (i3) {
        let t6 = o2([`matrix`, i3.size, i3.matrixData], i3.p);
        return t6.success ? t6.newMatrix : r3;
      } else return r3;
    });
  }
  function l2() {
    let t5 = [`*`, `a`, [`matrix`, `size`, `matrixData`], `b`], n3 = { variables: { a: true, b: true, size: true, matrixData: true }, allow_implicit_identities: [`a`, `b`] };
    i2 = transform(i2, function(r3) {
      let i3 = match$1(r3, t5, n3);
      if (i3) {
        let t6 = [`matrix`, i3.size, i3.matrixData], n4 = [], r4 = [];
        Array.isArray(i3.a) && i3.a[0] === `*` ? n4.push(...i3.a.slice(1)) : n4.push(i3.a), Array.isArray(i3.b) && i3.b[0] === `*` ? r4.push(...i3.b.slice(1)) : r4.push(i3.b);
        let o3 = n4.length;
        for (let r5 = o3 - 1; r5 >= 0; r5--) {
          let i4 = n4[r5][0];
          if (i4 === `matrix`) {
            let i5 = a2(n4[r5], t6);
            if (i5.success) t6 = i5.newMatrix, n4.pop();
            else break;
          } else if ([`tuple`, `list`, `vector`, `altvector`, `interval`, `set`, `array`].includes(i4)) break;
          else n4[r5] !== 1 && (t6 = s2(t6, n4[r5])), n4.pop();
        }
        for (; r4.length > 0; ) {
          let n5 = r4[0][0];
          if (n5 === `matrix`) {
            let n6 = a2(t6, r4[0]);
            if (n6.success) t6 = n6.newMatrix, r4.splice(0, 1);
            else break;
          } else if ([`tuple`, `list`, `vector`, `altvector`, `interval`, `set`, `array`].includes(n5)) break;
          else r4[0] !== 1 && (t6 = s2(t6, r4[0])), r4.splice(0, 1);
        }
        return n4.length > 0 || r4.length > 0 ? [`*`, ...n4, t6, ...r4] : t6;
      } else return r3;
    });
  }
  function u2() {
    let t5 = [`*`, [`matrix`, [`tuple`, `m`, `n`], `matrixData`], `b`], a3 = { variables: { n: Number.isInteger, m: Number.isInteger, matrixData: true, b: true } };
    i2 = transform(i2, function(i3) {
      let o3 = match$1(i3, t5, a3);
      if (o3) {
        let t6 = o3.m, a4 = o3.n, s3 = o3.b, c3 = o3.matrixData, l3 = [];
        n2 && (l3.push(`vector`), l3.push(`altvector`)), r2 && l3.push(`tuple`);
        let u3, d2 = [];
        if (l3.includes(s3[0])) u3 = s3;
        else if (Array.isArray(s3) && s3[0] === `*` && l3.includes(s3[1][0])) u3 = s3[1], d2 = s3.slice(2);
        else return i3;
        let p2 = u3.slice(1);
        if (p2.length !== a4) return i3;
        let m2 = [u3[0]];
        for (let n3 = 0; n3 < t6; n3++) {
          let t7 = [`+`, ...c3[n3 + 1].slice(1).map((t8, n4) => [`*`, t8, p2[n4]])];
          m2.push(t7);
        }
        return d2.length > 0 ? [`*`, m2, ...d2] : m2;
      } else return i3;
    });
  }
  return c2(), l2(), (n2 || r2) && u2(), i2;
}
function perform_vector_scalar_multiplications(t4, n2 = true) {
  var r2 = get_tree(t4);
  let i2 = function(t5, n3) {
    let r3 = [`*`, `a`, [n3, `vectorData`], `b`], i3 = { allow_implicit_identities: [`a`, `b`] };
    return t5 = transform(t5, function(t6) {
      let a2 = match$1(t6, r3, i3);
      if (a2) {
        if (a2.a === 1 && a2.b === 1) return t6;
        let r4 = a2.vectorData;
        r4 = Array.isArray(r4) && r4[0] === n3 ? r4.slice(1) : [r4];
        let i4 = [], o2 = [];
        Array.isArray(a2.a) && a2.a[0] === `*` ? i4.push(...a2.a.slice(1)) : i4.push(a2.a), Array.isArray(a2.b) && a2.b[0] === `*` ? o2.push(...a2.b.slice(1)) : o2.push(a2.b);
        let s2 = i4.length;
        for (let t7 = s2 - 1; t7 >= 0; t7--) {
          let n4 = i4[t7][0];
          if ([`tuple`, `list`, `vector`, `altvector`, `interval`, `set`, `array`, `matrix`].includes(n4)) break;
          i4[t7] !== 1 && (r4 = r4.map((n5) => [`*`, n5, i4[t7]])), i4.pop();
        }
        for (; o2.length > 0; ) {
          let t7 = o2[0][0];
          if ([`tuple`, `list`, `vector`, `altvector`, `interval`, `set`, `array`, `matrix`].includes(t7)) break;
          o2[0] !== 1 && (r4 = r4.map((t8) => [`*`, t8, o2[0]])), o2.splice(0, 1);
        }
        let c2 = [n3, ...r4];
        return i4.length > 0 || o2.length > 0 ? [`*`, ...i4, c2, ...o2] : c2;
      } else return t6;
    }), t5;
  };
  return r2 = i2(r2, `vector`), r2 = i2(r2, `altvector`), n2 && (r2 = i2(r2, `tuple`)), r2;
}
function perform_matrix_scalar_multiplications(t4) {
  var n2 = get_tree(t4);
  let r2 = [`*`, `a`, [`matrix`, `size`, `matrixData`], `b`], i2 = { allow_implicit_identities: [`a`, `b`] };
  return n2 = transform(n2, function(t5) {
    let n3 = match$1(t5, r2, i2);
    if (n3) {
      if (n3.a === 1 && n3.b === 1) return t5;
      let r3 = n3.matrixData, i3 = [], a2 = [];
      Array.isArray(n3.a) && n3.a[0] === `*` ? i3.push(...n3.a.slice(1)) : i3.push(n3.a), Array.isArray(n3.b) && n3.b[0] === `*` ? a2.push(...n3.b.slice(1)) : a2.push(n3.b);
      let o2 = i3.length;
      for (let t6 = o2 - 1; t6 >= 0; t6--) {
        let n4 = i3[t6][0];
        if ([`tuple`, `list`, `vector`, `altvector`, `interval`, `set`, `array`, `matrix`].includes(n4)) break;
        i3[t6] !== 1 && (r3 = r3.map((n5) => n5 === `tuple` ? n5 : n5.map((n6) => n6 === `tuple` ? n6 : [`*`, n6, i3[t6]]))), i3.pop();
      }
      for (; a2.length > 0; ) {
        let t6 = a2[0][0];
        if ([`tuple`, `list`, `vector`, `altvector`, `interval`, `set`, `array`, `matrix`].includes(t6)) break;
        a2[0] !== 1 && (r3 = r3.map((t7) => t7 === `tuple` ? t7 : t7.map((t8) => t8 === `tuple` ? t8 : [`*`, t8, a2[0]]))), a2.splice(0, 1);
      }
      let s2 = [`matrix`, n3.size, r3];
      return i3.length > 0 || a2.length > 0 ? [`*`, ...i3, s2, ...a2] : s2;
    } else return t5;
  }), n2;
}
function expand_relations(t4) {
  return transform(get_tree(t4), expand_relations_transform);
}
function expand_relations_transform(t4) {
  if (!Array.isArray(t4)) return t4;
  var n2 = t4[0], r2 = t4.slice(1);
  if (n2 === `=`) {
    if (r2.length <= 2) return t4;
    let n3 = [`and`];
    for (let t5 = 0; t5 < r2.length - 1; t5++) n3.push([`=`, r2[t5], r2[t5 + 1]]);
    return n3;
  }
  if (n2 === `gts` || n2 === `lts`) {
    let t5 = r2[0], i2 = r2[1];
    if (t5[0] !== `tuple` || i2[0] !== `tuple`) throw Error(`Badly formed ast`);
    let a2 = [];
    for (let r3 = 1; r3 < t5.length - 1; r3++) {
      let o3;
      o3 = i2[r3] ? n2 === `lts` ? `<` : `>` : n2 === `lts` ? `le` : `ge`, a2.push([o3, t5[r3], t5[r3 + 1]]);
    }
    let o2 = [`and`, a2[0], a2[1]];
    for (let t6 = 2; t6 < a2.length; t6++) o2 = [`and`, o2, a2[t6]];
    return o2;
  }
  if (n2 === `in` || n2 === `notin` || n2 === `ni` || n2 === `notni`) {
    let i2 = false;
    (n2 === `notin` || n2 === `notni`) && (i2 = true);
    let a2, o2;
    if (n2 === `in` || n2 === `notin` ? (a2 = r2[0], o2 = r2[1]) : (a2 = r2[1], o2 = r2[0]), o2 = to_intervals(o2), o2[0] !== `interval`) return t4;
    let s2 = o2[1], c2 = o2[2];
    if (s2[0] !== `tuple` || c2[0] !== `tuple`) throw Error(`Badly formed ast`);
    let l2 = s2[1], u2 = s2[2], d2 = [];
    c2[1] ? i2 ? d2.push([`<`, a2, l2]) : d2.push([`ge`, a2, l2]) : i2 ? d2.push([`le`, a2, l2]) : d2.push([`>`, a2, l2]), c2[2] ? i2 ? d2.push([`>`, a2, u2]) : d2.push([`le`, a2, u2]) : i2 ? d2.push([`ge`, a2, u2]) : d2.push([`<`, a2, u2]);
    let p2;
    return p2 = i2 ? [`or`].concat(d2) : [`and`].concat(d2), p2;
  }
  if (n2 === `subset` || n2 === `notsubset` || n2 === `superset` || n2 === `notsuperset`) {
    let i2 = false;
    (n2 === `notsubset` || n2 === `notsuperset`) && (i2 = true);
    let a2, o2;
    if (n2 === `subset` || n2 === `notsubset` ? (a2 = r2[0], o2 = r2[1]) : (a2 = r2[1], o2 = r2[0]), a2 = to_intervals(a2), o2 = to_intervals(o2), a2[0] !== `interval` || o2[0] !== `interval`) return t4;
    let s2 = a2[1], c2 = a2[2], l2 = o2[1], u2 = o2[2];
    if (s2[0] !== `tuple` || c2[0] !== `tuple` || l2[0] !== `tuple` || u2[0] !== `tuple`) throw Error(`Badly formed ast`);
    let d2 = s2[1], p2 = s2[2], m2 = l2[1], h2 = l2[2], g2 = [];
    c2[1] && !u2[1] ? i2 ? g2.push([`le`, d2, m2]) : g2.push([`>`, d2, m2]) : i2 ? g2.push([`<`, d2, m2]) : g2.push([`ge`, d2, m2]), c2[2] && !u2[2] ? i2 ? g2.push([`ge`, p2, h2]) : g2.push([`<`, p2, h2]) : i2 ? g2.push([`>`, p2, h2]) : g2.push([`le`, p2, h2]);
    let v2;
    return v2 = i2 ? [`or`].concat(g2) : [`and`].concat(g2), v2;
  }
  return t4;
}
function substitute(t4, n2) {
  var r2 = get_tree(t4), i2 = {};
  for (let t5 in n2) i2[t5] = get_tree(n2[t5]);
  return substitute$1(r2, i2);
}
function substitute_component(t4, n2, r2) {
  let i2 = get_tree(t4);
  get_tree(r2);
  if (typeof n2 == `number`) n2 = [n2];
  else if (!Array.isArray(n2)) throw Error(`Invalid substitute_component: ` + n2);
  let o2 = [`list`, `tuple`, `vector`, `altvector`, `array`];
  return s2(i2, n2);
  function s2(t5, n3, i3) {
    if (n3.length === 0) return r2;
    if (!Array.isArray(t5)) throw Error(`Invalid substitute_component: expected list, tuple, vector, or array`);
    let a2 = t5[0], c2 = t5.slice(1);
    if (!o2.includes(a2)) throw Error(`Invalid substitute_component: expected list, tuple, vector, or array`);
    let l2 = n3[0];
    if (l2 < 0 || l2 > c2.length - 1) throw Error(`Invalid substitute_component: component out of range`);
    let u2 = n3.slice(1), d2 = s2(c2[l2], u2);
    return [a2, ...c2.slice(0, l2), d2, ...c2.slice(l2 + 1)];
  }
}
function get_component(t4, n2) {
  let r2 = get_tree(t4);
  if (typeof n2 == `number`) n2 = [n2];
  else if (!Array.isArray(n2)) throw Error(`Invalid get_component: ` + n2);
  let i2 = [`list`, `tuple`, `vector`, `altvector`, `array`];
  return a2(r2, n2);
  function a2(t5, n3) {
    if (n3.length === 0) return t5;
    if (!Array.isArray(t5)) throw Error(`Invalid get_component: expected list, tuple, vector, or array`);
    let r3 = t5[0], o2 = t5.slice(1);
    if (!i2.includes(r3)) throw Error(`Invalid get_component: expected list, tuple, vector, or array`);
    let s2 = n3[0];
    if (s2 < 0 || s2 > o2.length - 1) throw Error(`Invalid get_component: component out of range`);
    let c2 = n3.slice(1);
    return a2(o2[s2], c2);
  }
}
function apply_root(t4, n2) {
  return n2 === 2 ? [`apply`, `sqrt`, t4] : n2 === 3 ? [`apply`, `cbrt`, t4] : [`apply`, `nthroot`, [`tuple`, t4, n2]];
}
function pull_number_outside_root(t4, n2 = 2) {
  let r2 = t4, i2 = 1, a2 = 2, o2 = mathjs_default.pow(a2, n2);
  for (; o2 <= Math.abs(r2); ) r2 % o2 === 0 ? (r2 /= o2, i2 *= a2) : (a2++, o2 = mathjs_default.pow(a2, n2));
  return { n_outside: i2, n_inside: r2 };
}
function make_power(t4, n2) {
  return n2 === 0 ? 1 : n2 === 1 ? t4 : [`^`, t4, n2];
}
function collect_radicand_factors(t4, n2) {
  let r2 = 1, i2 = (t5) => {
    if (Array.isArray(t5) && t5[0] === `*`) for (let n3 of t5.slice(1)) i2(n3);
    else Array.isArray(t5) && t5[0] === `-` ? (r2 = -r2, i2(t5[1])) : typeof t5 == `number` && t5 < 0 ? (r2 = -r2, n2.push(-t5)) : n2.push(t5);
  };
  return i2(t4), r2;
}
function combine_factors(t4, n2) {
  let r2 = 1, i2 = /* @__PURE__ */ new Map(), a2 = /* @__PURE__ */ new Map(), o2 = [], s2 = [];
  for (let c2 of t4) {
    if (typeof c2 == `number`) {
      r2 *= c2;
      continue;
    }
    let t5, l2;
    if (typeof c2 == `string`) t5 = c2, l2 = 1;
    else if (Array.isArray(c2) && c2[0] === `^` && typeof c2[1] == `string` && Number.isInteger(c2[2])) t5 = c2[1], l2 = c2[2];
    else {
      s2.push(c2);
      continue;
    }
    if (a2.has(t5) || a2.set(t5, is_nonzero_ast(t5, n2) === true), l2 < 1 && !a2.get(t5)) {
      s2.push(c2);
      continue;
    }
    i2.has(t5) ? i2.set(t5, i2.get(t5) + l2) : (o2.push(t5), i2.set(t5, l2));
  }
  return { numberFactor: r2, expByVar: i2, varOrder: o2, others: s2 };
}
function pull_factor_into_root(t4, n2, r2, i2, a2, o2) {
  let s2 = n2 >= 1 ? Math.floor(n2 / r2) : 0;
  if (s2 === 0) {
    o2.push(make_power(t4, n2));
    return;
  }
  let c2;
  if (is_positive_ast(t4, i2, false)) c2 = t4;
  else if (r2 % 2 == 1 && is_real_ast(t4, i2)) c2 = t4;
  else if (r2 % 2 == 0 && is_real_ast(t4, i2)) c2 = s2 % 2 == 0 ? t4 : [`apply`, `abs`, t4];
  else {
    o2.push(make_power(t4, n2));
    return;
  }
  a2.push(make_power(c2, s2)), o2.push(make_power(t4, n2 % r2));
}
function simplify_powers_in_roots(t4, n2) {
  if (!Array.isArray(t4)) return t4;
  var r2 = t4[0], i2 = t4.slice(1);
  if (r2 === `*`) {
    let t5 = [];
    for (let r4 of i2) {
      let i3 = simplify_powers_in_roots(r4, n2);
      Array.isArray(i3) && i3[0] === `*` ? t5.push(...i3.slice(1)) : t5.push(i3);
    }
    let { numberFactor: r3, expByVar: a2, varOrder: o2, others: s2 } = combine_factors(t5, n2), c2 = [];
    r3 !== 1 && c2.push(r3);
    for (let t6 of o2) {
      let n3 = make_power(t6, a2.get(t6));
      n3 !== 1 && c2.push(n3);
    }
    return c2.push(...s2), c2.length === 0 ? 1 : c2.length === 1 ? c2[0] : [`*`, ...c2];
  }
  if (r2 === `apply` && [`sqrt`, `cbrt`, `nthroot`].includes(i2[0])) {
    let t5 = i2[0], r3 = 2, a2 = i2[1];
    if (t5 === `cbrt`) r3 = 3;
    else if (t5 === `nthroot`) if (Array.isArray(i2[1]) && i2[1][0] === `tuple`) if (Number.isInteger(i2[1][2]) && i2[1][2] > 0) r3 = i2[1][2], a2 = i2[1][1];
    else return [`apply`, `nthroot`, ...i2.slice(1).map((t6) => simplify_powers_in_roots(t6, n2))];
    else r3 = 2;
    a2 = simplify_powers_in_roots(a2, n2);
    let o2 = [], s2 = collect_radicand_factors(a2, o2), { numberFactor: c2, expByVar: l2, varOrder: u2, others: d2 } = combine_factors(o2, n2);
    if (c2 === 0) return 0;
    let p2 = [], m2 = [];
    if (Number.isInteger(c2)) {
      let { n_outside: t6, n_inside: n3 } = pull_number_outside_root(c2, r3);
      t6 !== 1 && p2.push(t6), n3 !== 1 && m2.push(n3);
    } else c2 !== 1 && m2.push(c2);
    for (let t6 of u2) pull_factor_into_root(t6, l2.get(t6), r3, n2, p2, m2);
    for (let t6 of d2) Array.isArray(t6) && t6[0] === `^` && Number.isInteger(t6[2]) ? pull_factor_into_root(t6[1], t6[2], r3, n2, p2, m2) : m2.push(t6);
    let h2 = m2.filter((t6) => t6 !== 1), g2;
    if (h2.length === 0) g2 = s2 < 0 ? -1 : 1;
    else {
      let t6 = h2.length === 1 ? h2[0] : [`*`, ...h2];
      g2 = s2 < 0 ? [`-`, t6] : t6;
    }
    let v2 = p2.filter((t6) => t6 !== 1);
    return g2 !== 1 && v2.push(apply_root(g2, r3)), v2.length === 0 ? 1 : v2.length === 1 ? v2[0] : [`*`, ...v2];
  }
  return [r2, ...i2.map((t5) => simplify_powers_in_roots(t5, n2))];
}
var simplify_exports = __exportAll({ add_unit: () => add_unit, clean: () => clean, collapse_unary_minus: () => collapse_unary_minus, collect_like_terms_factors: () => collect_like_terms_factors, default_order: () => default_order, evaluate_numbers: () => evaluate_numbers, perform_vector_matrix_additions_scalar_multiplications: () => perform_vector_matrix_additions_scalar_multiplications, remove_scaling_units: () => remove_scaling_units, remove_units: () => remove_units, set_small_zero: () => set_small_zero, simplify: () => simplify, simplify_logical: () => simplify_logical, simplify_ratios: () => simplify_ratios });
function clean(t4) {
  return flatten$5(get_tree(t4));
}
function evalf(t4, n2) {
  return parseFloat(mathjs_default.format(t4, { notation: `exponential`, precision: n2 }));
}
function collapse_unary_minus(t4) {
  var n2 = get_tree(t4);
  if (!Array.isArray(n2)) return n2;
  var r2 = n2[0], i2 = n2.slice(1);
  if (i2 = i2.map((t5) => collapse_unary_minus(t5)), r2 === `-`) {
    if (typeof i2[0] == `number`) return -i2[0];
    if (Array.isArray(i2[0]) && i2[0][0] === `*` && typeof i2[0][1] == `number`) return [`*`, -i2[0][1]].concat(i2[0].slice(2));
    if (Array.isArray(i2[0]) && i2[0][0] === `/`) {
      if (typeof i2[0][1] == `number`) return [`/`, -i2[0][1], i2[0][2]];
      if (Array.isArray(i2[0][1]) && i2[0][1][0] === `*` && typeof i2[0][1][1] == `number`) return [`/`, [`*`, -i2[0][1][1]].concat(i2[0][1].slice(2)), i2[0][2]];
    }
  }
  return [r2].concat(i2);
}
function simplify(t4, n2, r2) {
  var i2 = get_tree(t4);
  return contains_blank(i2) || (n2 === void 0 && t4.context !== void 0 && t4.context.get_assumptions !== void 0 && (n2 = t4.context.get_assumptions([t4.variables()])), i2 = evaluate_numbers(i2, { assumptions: n2, max_digits: r2, evaluate_functions: true }), !Array.isArray(i2)) ? i2 : (i2 = simplify_logical(i2, n2), i2 = perform_vector_matrix_additions_scalar_multiplications(i2), i2 = simplify_integers_in_roots(i2), i2 = collect_like_terms_factors(i2, n2, r2), i2);
}
function simplify_logical(t4, n2) {
  var r2 = get_tree(t4);
  if (contains_blank(r2)) return r2;
  n2 === void 0 && t4.context !== void 0 && t4.context.get_assumptions !== void 0 && (n2 = t4.context.get_assumptions([t4.variables()])), r2 = evaluate_numbers(r2, { assumptions: n2 }), r2 = unflattenRight(r2);
  var i2 = [];
  return i2.push([[`not`, [`not`, `a`]], `a`]), i2.push([[`not`, [`and`, `a`, `b`]], [`or`, [`not`, `a`], [`not`, `b`]]]), i2.push([[`not`, [`or`, `a`, `b`]], [`and`, [`not`, `a`], [`not`, `b`]]]), i2.push([[`not`, [`=`, `a`, `b`]], [`ne`, `a`, `b`]]), i2.push([[`not`, [`ne`, `a`, `b`]], [`=`, `a`, `b`]]), i2.push([[`not`, [`<`, `a`, `b`]], [`le`, `b`, `a`]]), i2.push([[`not`, [`le`, `a`, `b`]], [`not`, [`le`, `a`, `b`]]]), i2.push([[`not`, [`in`, `a`, `b`]], [`notin`, `a`, `b`]]), i2.push([[`not`, [`subset`, `a`, `b`]], [`notsubset`, `a`, `b`]]), r2 = applyAllTransformations(r2, i2, 20), r2 = flatten$5(r2), r2;
}
function simplify_integers_in_roots(t4) {
  if (!Array.isArray(t4)) return t4;
  var n2 = t4[0], r2 = t4.slice(1);
  if (n2 === `*`) {
    let t5 = 1, n3 = [];
    for (let i2 of r2) if (typeof i2 == `number`) t5 *= i2;
    else {
      let r3 = simplify_integers_in_roots(i2);
      Array.isArray(r3) && r3[0] === `*` ? typeof r3[1] == `number` ? (t5 *= r3[1], n3.push(...r3.slice(2))) : n3.push(...r3.slice(1)) : n3.push(r3);
    }
    return n3.length === 0 ? t5 : (t5 !== 1 && n3.unshift(t5), [`*`, ...n3]);
  }
  if (n2 === `apply` && [`sqrt`, `cbrt`, `nthroot`].includes(r2[0])) {
    let t5 = r2[0], n3 = 2, i2 = r2[1];
    if (t5 === `cbrt`) n3 = 3;
    else if (t5 === `nthroot`) if (Array.isArray(r2[1]) && r2[1][0] === `tuple`) if (Number.isInteger(r2[1][2]) && r2[1][2] > 0) n3 = r2[1][2], i2 = r2[1][1];
    else return [`apply`, `nthroot`, ...r2.slice(1).map(simplify_integers_in_roots)];
    else n3 = 2;
    if (i2 = simplify_integers_in_roots(i2), Number.isInteger(i2)) {
      let { n_outside: t6, n_inside: r3 } = pull_number_outside_root(i2, n3);
      if (t6 !== 1) return [`*`, t6, apply_root(r3, n3)];
    } else if (Array.isArray(i2) && i2[0] === `*` && typeof i2[1] == `number`) {
      let { n_outside: t6, n_inside: r3 } = pull_number_outside_root(i2[1], n3);
      if (t6 !== 1) return [`*`, t6, apply_root([`*`, r3, ...i2.slice(2)], n3)];
    }
    return apply_root(i2, n3);
  }
  return [n2, ...r2.map(simplify_integers_in_roots)];
}
function perform_vector_matrix_additions_scalar_multiplications(t4, n2 = true) {
  var r2 = get_tree(t4);
  r2 = perform_matrix_scalar_multiplications(r2), r2 = perform_vector_scalar_multiplications(r2, n2);
  let i2 = [`+`, `a`, `b`];
  return r2 = transform(r2, function(t5) {
    let r3 = match$1(t5, i2, { allow_permutations: true });
    if (r3) {
      let i3 = [`vector`, `altvector`];
      n2 && i3.push(`tuple`);
      let a2 = {}, o2 = {}, s2 = [];
      if (i3.includes(r3.a[0])) {
        let t6 = r3.a.length - 1;
        a2[t6] || (a2[t6] = []), a2[t6].push(r3.a);
      } else if (r3.a[0] === `matrix`) {
        let t6 = r3.a[1].slice(1).toString();
        o2[t6] || (o2[t6] = []), o2[t6].push(r3.a);
      } else s2.push(r3.a);
      if (i3.includes(r3.b[0])) {
        let t6 = r3.b.length - 1;
        a2[t6] || (a2[t6] = []), a2[t6].push(r3.b);
      } else if (r3.b[0] === `matrix`) {
        let t6 = r3.b[1].slice(1).toString();
        o2[t6] || (o2[t6] = []), o2[t6].push(r3.b);
      } else if (r3.b[0] === `+`) for (let t6 of r3.b.slice(1)) if (i3.includes(t6[0])) {
        let n3 = t6.length - 1;
        a2[n3] || (a2[n3] = []), a2[n3].push(t6);
      } else if (t6[0] === `matrix`) {
        let n3 = t6[1].slice(1).toString();
        o2[n3] || (o2[n3] = []), o2[n3].push(t6);
      } else s2.push(t6);
      else s2.push(r3.b);
      if (Object.values(a2).every((t6) => t6.length < 2) && Object.values(o2).every((t6) => t6.length < 2)) return t5;
      let c2 = s2;
      for (let t6 in a2) if (a2[t6].length < 2) c2.push(...a2[t6]);
      else {
        let n3 = a2[t6].some((t7) => t7[0] === `vector`), r4 = a2[t6].some((t7) => t7[0] === `tuple`), i4 = a2[t6].some((t7) => t7[0] === `altvector`), o3;
        o3 = n3 || r4 && i4 ? [`vector`] : i4 ? [`altvector`] : [`tuple`];
        for (let n4 = 0; n4 < t6; n4++) o3.push([`+`, ...a2[t6].map((t7) => t7[n4 + 1])]);
        c2.push(o3);
      }
      for (let t6 in o2) if (o2[t6].length < 2) c2.push(...o2[t6]);
      else {
        let [n3, r4] = t6.split(`,`).map(Number), i4 = [`tuple`];
        for (let a4 = 0; a4 < n3; a4++) {
          let n4 = [`tuple`];
          for (let i5 = 0; i5 < r4; i5++) n4.push([`+`, ...o2[t6].map((t7) => t7[2][a4 + 1][i5 + 1])]);
          i4.push(n4);
        }
        let a3 = [`matrix`, [`tuple`, n3, r4], i4];
        c2.push(a3);
      }
      return c2.length === 1 ? c2[0] : [`+`, ...c2];
    } else return t5;
  }), r2;
}
function contains_decimal_number(t4) {
  return typeof t4 == `string` ? false : typeof t4 == `number` ? !!(Number.isFinite(t4) && !Number.isInteger(t4)) : Array.isArray(t4) ? t4.slice(1).some((t5) => contains_decimal_number(t5)) : false;
}
function contains_only_numbers(t4, { include_number_symbols: n2 = false, evaluate_functions: r2 = false } = {}) {
  return typeof t4 == `string` ? !!(n2 && (t4 === `e` && mathjs_default.define_e || t4 === `pi` && mathjs_default.define_pi) || t4 === `i` && mathjs_default.define_i) : typeof t4 == `number` ? true : Array.isArray(t4) ? r2 && t4[0] === `apply` ? t4.slice(2).every((t5) => contains_only_numbers(t5, { include_number_symbols: n2 })) : t4.slice(1).every((t5) => contains_only_numbers(t5, { include_number_symbols: n2 })) : false;
}
function try_evaluate_sum_of_numbers(t4, n2, r2) {
  if (typeof t4 == `number`) {
    if (t4 === 0) return n2;
    if (typeof n2 == `number`) return t4 + n2;
    if (Array.isArray(n2) && n2[0] === `+` && typeof n2[1] == `number`) return [`+`, t4 + n2[1], n2[2]];
    if (!r2 && Array.isArray(n2) && n2[0] === `+` && typeof n2[2] == `number`) return [`+`, t4 + n2[2], n2[1]];
  }
  return typeof n2 == `number` && n2 === 0 ? t4 : null;
}
function try_evaluate_negate_number(t4) {
  if (typeof t4 == `number`) return -t4;
  if (Array.isArray(t4) && t4[0] === `*` && typeof t4[1] == `number`) return [`*`, -t4[1]].concat(t4.slice(2));
  if (Array.isArray(t4) && t4[0] === `/`) {
    if (typeof t4[1] == `number`) return [`/`, -t4[1], t4[2]];
    if (Array.isArray(t4[1]) && t4[1][0] === `*` && typeof t4[1][1] == `number`) return [`/`, [`*`, -t4[1][1]].concat(t4[1].slice(2)), t4[2]];
  }
  return null;
}
function try_evaluate_product_of_numbers(t4, n2) {
  if (typeof t4 == `number`) {
    if (isNaN(t4)) return NaN;
    if (typeof n2 == `number`) return t4 * n2;
    if (!isFinite(t4)) return t4 === 1 / 0 && is_negative_ast(n2) || t4 === -1 / 0 && is_positive_ast(n2) ? -1 / 0 : is_nonzero_ast(n2) === false ? NaN : 1 / 0;
    if (t4 === 0) return 0;
    if (t4 === 1) return n2;
    if (t4 === -1) return [`-`, n2];
    if (Array.isArray(n2) && n2[0] === `*` && typeof n2[1] == `number`) return t4 *= n2[1], n2 = n2[2], t4 === 1 ? n2 : t4 === -1 ? [`-`, n2] : [`*`, t4, n2];
  }
  if (typeof n2 == `number`) {
    if (isNaN(n2)) return NaN;
    if (!isFinite(n2)) return n2 === 1 / 0 && is_negative_ast(t4) || n2 === -1 / 0 && is_positive_ast(t4) ? -1 / 0 : is_nonzero_ast(t4) === false ? NaN : 1 / 0;
    if (n2 === 0) return 0;
    if (n2 === 1) return t4;
    if (n2 === -1) return [`-`, t4];
    if (Array.isArray(t4) && t4[0] === `*` && typeof t4[1] == `number`) return n2 *= t4[1], t4 = t4[2], n2 === 1 ? t4 : n2 === -1 ? [`-`, t4] : [`*`, t4, n2];
  }
  if (mathjs_default.define_i && t4 === `i`) {
    if (n2 === `i`) return -1;
    if (Array.isArray(n2) && n2[0] === `*` && n2[1] === `i`) return [`-`, n2[2]];
  } else if (mathjs_default.define_i && n2 === `i` && Array.isArray(t4) && t4[0] === `*` && t4[1] === `i`) return [`-`, t4[2]];
  return null;
}
function try_evaluate_quotient_of_numbers(t4, n2, r2, i2) {
  if (typeof t4 == `number`) {
    if (t4 === 0) {
      let t5 = is_nonzero_ast(n2, r2);
      if (t5) return 0;
      if (t5 === false) return NaN;
    }
    if (typeof n2 == `number`) {
      let r3 = t4 / n2;
      if (i2 === 1 / 0 || mathjs_default.round(r3, i2) === r3) return r3;
      if (n2 < 0) return [`/`, -t4, -n2];
    }
    if (Array.isArray(n2) && n2[0] === `*` && typeof n2[1] == `number`) {
      let r3 = t4 / n2[1];
      if (i2 === 1 / 0 || mathjs_default.round(r3, i2) === r3) return [`/`, r3, n2[2]];
    }
  } else if (typeof n2 == `number`) {
    if (Array.isArray(t4) && t4[0] === `*` && typeof t4[1] == `number`) {
      let r4 = t4[1] / n2;
      if (i2 === 1 / 0 || mathjs_default.round(r4, i2) === r4) return r4 === 1 ? t4[2] : [`*`, r4, t4[2]];
      if (n2 < 0) return [`/`, [`*`, -t4[1], t4[2]], -n2];
    }
    let r3 = 1 / n2;
    if (i2 === 1 / 0 || mathjs_default.round(r3, i2) === r3) return [`*`, r3, t4];
    if (n2 < 0) return Array.isArray(t4) && t4[0] === `-` ? [`/`, t4[1], -n2] : [`-`, [`/`, t4, -n2]];
  }
  return null;
}
function set_small_zero(t4, n2 = 1e-14) {
  var r2 = get_tree(t4);
  return typeof r2 == `number` ? mathjs_default.abs(r2) < n2 ? 0 : r2 : Array.isArray(r2) ? [r2[0], ...r2.slice(1).map((t5) => set_small_zero(t5, n2))] : r2;
}
function evaluate_numbers_sub(t4, n2, r2, i2, a2, o2) {
  if (t4 === void 0) return t4;
  if (typeof t4 == `number`) return o2 > 0 && mathjs_default.abs(t4) < o2 ? 0 : t4;
  if (contains_only_numbers(t4, { include_number_symbols: true, evaluate_functions: a2 })) {
    let n3 = function(n4) {
      if (o2 > 0 && mathjs_default.abs(n4) < o2) return 0;
      if (r2 === 1 / 0 || Number.isInteger(n4)) return n4;
      let i3 = evalf(n4, 14), s3 = evalf(n4, r2);
      if (r2 === 0 && (s3 = mathjs_default.round(n4)), s3 === i3 || contains_decimal_number(t4) && contains_only_numbers(t4, { include_number_symbols: true, evaluate_functions: a2 })) return n4;
      let c3 = mathjs_default.fraction(n4), l3 = { n: mathjs_default.number(c3.n), d: mathjs_default.number(c3.d), s: mathjs_default.number(c3.s) }, u2 = evalf(l3.d, 3);
      return (l3.n < 1e4 || u2 === l3.d) && evalf(l3.s * l3.n / l3.d, 14) === i3 ? l3.d === 1 ? l3.s * l3.n : [`/`, l3.s * l3.n, l3.d] : null;
    };
    var s2 = evaluate_to_constant(t4, { remove_units_first: false });
    if (!Number.isNaN(s2)) {
      if (typeof s2 == `number`) {
        if (Number.isFinite(s2)) {
          let t5 = n3(s2);
          if (t5 !== null) return t5;
        }
      } else if (s2 && Number.isFinite(s2.re) && Number.isFinite(s2.im)) {
        let t5 = n3(s2.re), r3 = n3(s2.im);
        if (t5 !== null && r3 !== null) {
          if (r3 === 0) return t5;
          {
            let n4;
            return n4 = r3 === 1 ? `i` : r3 === -1 ? [`-`, `i`] : [`*`, r3, `i`], t5 === 0 ? n4 : [`+`, t5, n4];
          }
        }
      }
    }
  }
  if (!Array.isArray(t4)) return t4;
  var c2 = t4[0], l2 = t4.slice(1).map((t5) => evaluate_numbers_sub(t5, n2, r2, i2, a2, o2));
  if (c2 === `+`) {
    let t5 = l2[0], n3 = l2[1];
    if (n3 === void 0) return t5;
    let r3 = try_evaluate_sum_of_numbers(t5, n3, i2);
    if (r3 !== null) return r3;
    if (t5[0] === `unit` && n3[0] === `unit`) {
      let r4 = get_unit_of_tree(t5), a3 = get_unit_of_tree(n3);
      if (r4 && r4 === a3) {
        let a4 = try_evaluate_sum_of_numbers(remove_units(t5, false), remove_units(n3, false), i2);
        if (a4 !== null) return add_unit(a4, r4);
      }
    }
    return [c2].concat(l2);
  }
  if (c2 === `-`) {
    let t5 = try_evaluate_negate_number(l2[0]);
    if (t5 !== null) return t5;
    if (l2[0][0] === `unit`) {
      let t6 = get_unit_of_tree(l2[0]);
      if (t6) {
        let n3 = try_evaluate_negate_number(remove_units(l2[0], false));
        if (n3 !== null) return add_unit(n3, t6);
      }
    }
    return [c2].concat(l2);
  }
  if (c2 === `*`) {
    let t5 = l2[0], n3 = l2[1];
    if (n3 === void 0) return t5;
    let r3 = try_evaluate_product_of_numbers(t5, n3);
    if (r3 !== null) return r3;
    if (t5[0] === `unit` && n3[0] !== `unit`) {
      let r4 = get_unit_of_tree(t5);
      if (r4) {
        let i3 = try_evaluate_product_of_numbers(remove_units(t5, false), n3);
        if (i3 !== null) return add_unit(i3, r4);
      }
    }
    if (t5[0] !== `unit` && n3[0] === `unit`) {
      let r4 = get_unit_of_tree(n3);
      if (r4) {
        let i3 = try_evaluate_product_of_numbers(t5, remove_units(n3, false));
        if (i3 !== null) return add_unit(i3, r4);
      }
    }
    return [c2].concat(l2);
  }
  if (c2 === `/`) {
    let t5 = l2[0], i3 = l2[1], a3 = try_evaluate_quotient_of_numbers(t5, i3, n2, r2);
    if (a3 !== null) return a3;
    if (t5[0] === `unit` && i3[0] !== `unit`) {
      let a4 = get_unit_of_tree(t5);
      if (a4) {
        let o3 = try_evaluate_quotient_of_numbers(remove_units(t5, false), i3, n2, r2);
        if (o3 !== null) return add_unit(o3, a4);
      }
    }
    return [c2].concat(l2);
  }
  if (c2 === `^`) {
    let t5 = l2[0], i3 = l2[1];
    if (typeof i3 == `number`) {
      if (i3 === 0) {
        if (!mathjs_default.pow_strict) return 1;
        let r3 = is_nonzero_ast(t5, n2);
        if (r3 && t5 !== 1 / 0 && t5 !== -1 / 0) return 1;
        if (r3 === false) return NaN;
      } else if (i3 === 1) return t5;
      else if (typeof t5 == `number`) {
        let n3 = mathjs_default.pow(t5, i3);
        if (r2 === 1 / 0 || mathjs_default.round(n3, r2) === n3) return n3;
      }
    } else if (t5 === 1) return 1;
    return [c2].concat(l2);
  }
  return [c2].concat(l2);
}
function evaluate_numbers(t4, { assumptions: n2, max_digits: r2, skip_ordering: i2 = false, evaluate_functions: a2 = false, set_small_zero: o2 = 0 } = {}) {
  (r2 === void 0 || !(Number.isInteger(r2) || r2 === 1 / 0)) && (r2 = 0), o2 === true && (o2 = 1e-14);
  var s2 = get_tree(t4);
  if (contains_blank(s2)) return s2;
  n2 === void 0 && t4.context !== void 0 && t4.context.get_assumptions !== void 0 && (n2 = t4.context.get_assumptions([t4.variables()]));
  var c2;
  return i2 ? (s2 = unflattenRight(flatten$5(s2)), c2 = evaluate_numbers_sub(s2, n2, r2, i2, a2, o2)) : (s2 = move_units_outside_products(s2), s2 = unflattenRight(default_order(flatten$5(s2))), c2 = default_order(evaluate_numbers_sub(s2, n2, r2, i2, a2, o2)), c2 = default_order(evaluate_numbers_sub(unflattenRight(c2), n2, r2, i2, a2, o2))), c2 = set_negative_zeros_to_zero(c2), flatten$5(c2);
}
function set_negative_zeros_to_zero(t4) {
  return t4 === 0 ? 0 : Array.isArray(t4) ? [t4[0], ...t4.slice(1).map(set_negative_zeros_to_zero)] : t4;
}
function collect_like_terms_factors(t4, n2, r2) {
  function i2(t5) {
    return !!(typeof t5 == `number` || Array.isArray(t5) && t5[0] === `-` && typeof t5[1] == `number`);
  }
  function a2(t5) {
    return !!(typeof t5 == `number` && t5 < 0 || Array.isArray(t5) && t5[0] === `-` && typeof t5[1] == `number` && t5 > 0);
  }
  function o2(t5) {
    if (typeof t5 == `number` || Array.isArray(t5) && t5[0] === `-` && typeof t5[1] == `number`) return true;
    var n3 = evaluate_to_constant(t5, { remove_units_first: false });
    return !!Number.isFinite(n3);
  }
  let s2 = (t5) => !contains_pm(t5);
  var c2 = get_tree(t4);
  if (contains_blank(c2)) return c2;
  n2 === void 0 && t4.context !== void 0 && t4.context.get_assumptions !== void 0 && (n2 = t4.context.get_assumptions([t4.variables()])), c2 = evaluate_numbers(c2, { assumptions: n2, max_digits: r2, evaluate_functions: true });
  var l2 = [];
  l2.push([[`/`, `x`, [`^`, `y`, `a`]], [`*`, `x`, [`^`, `y`, [`-`, `a`]]], { evaluate_numbers: true, max_digits: r2 }]), l2.push([[`/`, `x`, [`apply`, `exp`, `a`]], [`*`, `x`, [`apply`, `exp`, [`-`, `a`]]], { evaluate_numbers: true, max_digits: r2, variables: { x: true, a: true } }]), l2.push([[`/`, `x`, `y`], [`*`, `x`, [`^`, `y`, [`-`, 1]]], { evaluate_numbers: true, max_digits: r2 }]), c2 = applyAllTransformations(c2, l2, 40), l2 = [];
  for (let t5 in all_units) all_units[t5].prefix ? (l2.push([[`+`, [`unit`, t5, `x`], [`unit`, t5, `y`]], [`unit`, t5, [`+`, `x`, `y`]], { variables: { x: true, y: true }, evaluate_numbers: true, max_digits: r2, allow_extended_match: true, allow_permutations: true, max_group: 1 }]), l2.push([[`+`, [`unit`, t5, `x`], [`-`, [`unit`, t5, `y`]]], [`unit`, t5, [`+`, `x`, [`-`, `y`]]], { variables: { x: true, y: true }, evaluate_numbers: true, max_digits: r2, allow_extended_match: true, allow_permutations: true, max_group: 1 }])) : (l2.push([[`+`, [`unit`, `x`, t5], [`unit`, `y`, t5]], [`unit`, [`+`, `x`, `y`], t5], { variables: { x: true, y: true }, evaluate_numbers: true, max_digits: r2, allow_extended_match: true, allow_permutations: true, max_group: 1 }]), l2.push([[`+`, [`unit`, `x`, t5], [`-`, [`unit`, `y`, t5]]], [`unit`, [`+`, `x`, [`-`, `y`]], t5], { variables: { x: true, y: true }, evaluate_numbers: true, max_digits: r2, allow_extended_match: true, allow_permutations: true, max_group: 1 }]));
  return l2.push([[`*`, [`^`, `x`, `n`], [`^`, `x`, `m`]], [`^`, `x`, [`+`, `n`, `m`]], { variables: { x: (t5) => is_nonzero_ast(t5, n2) && s2(t5), n: i2, m: i2 }, evaluate_numbers: true, max_digits: r2, allow_implicit_identities: [`m`, `n`], allow_extended_match: true, allow_permutations: true, max_group: 1 }]), l2.push([[`*`, [`^`, `x`, `n`], [`^`, `x`, `m`]], [`^`, `x`, [`+`, `n`, `m`]], { variables: { x: s2, n: (t5) => i2(t5) && is_positive_ast(t5, n2), m: (t5) => i2(t5) && is_positive_ast(t5, n2) }, evaluate_numbers: true, max_digits: r2, allow_implicit_identities: [`m`, `n`], allow_extended_match: true, allow_permutations: true, max_group: 1 }]), l2.push([[`^`, [`-`, `x`], `n`], [`^`, `x`, `n`], { variables: { x: true, n: (t5) => Number.isInteger(t5) && Number.isInteger(t5 / 2) }, evaluate_numbers: true, max_digits: r2, allow_extended_match: true, max_group: 1 }]), l2.push([[`^`, [`-`, `x`], `n`], [`-`, [`^`, `x`, `n`]], { variables: { x: true, n: (t5) => Number.isInteger(t5) && !Number.isInteger(t5 / 2) }, evaluate_numbers: true, max_digits: r2, allow_extended_match: true, max_group: 1 }]), l2.push([[`*`, [`^`, `x`, `n`], [`^`, `x`, `m`]], [`^`, `x`, [`+`, `n`, `m`]], { variables: { x: s2, n: (t5) => i2(t5) && is_negative_ast(t5, n2), m: (t5) => i2(t5) && is_negative_ast(t5, n2) }, evaluate_numbers: true, max_digits: r2, allow_extended_match: true, allow_permutations: true, max_group: 1 }]), l2.push([[`*`, [`apply`, `exp`, `n`], [`apply`, `exp`, `m`]], [`apply`, `exp`, [`+`, `n`, `m`]], { variables: { n: i2, m: i2 }, evaluate_numbers: true, max_digits: r2, allow_implicit_identities: [`m`, `n`], allow_extended_match: true, allow_permutations: true, max_group: 1 }]), l2.push([[`+`, [`*`, `n`, `x`], [`*`, `m`, `x`]], [`*`, [`+`, `n`, `m`], `x`], { variables: { x: s2, n: i2, m: i2 }, evaluate_numbers: true, max_digits: r2, allow_implicit_identities: [`m`, `n`], allow_extended_match: true, allow_permutations: true, max_group: 1 }]), l2.push([[`+`, [`*`, `n1`, `x`], [`*`, [`/`, `n2`, `m2`], `x`]], [`*`, [`+`, `n1`, [`/`, `n2`, `m2`]], `x`], { variables: { x: s2, n1: i2, n2: i2, m2: i2 }, evaluate_numbers: true, max_digits: r2, allow_implicit_identities: [`m2`, `n1`, `n2`], allow_extended_match: true, allow_permutations: true, max_group: 1 }]), l2.push([[`+`, [`*`, `n1`, `x`], [`*`, [`-`, [`/`, `n2`, `m2`]], `x`]], [`*`, [`+`, `n1`, [`-`, [`/`, `n2`, `m2`]]], `x`], { variables: { x: s2, n1: i2, n2: i2, m2: i2 }, evaluate_numbers: true, max_digits: r2, allow_implicit_identities: [`m2`, `n1`, `n2`], allow_extended_match: true, allow_permutations: true, max_group: 1 }]), l2.push([[`+`, [`*`, [`/`, `n1`, `m1`], `x`], [`*`, [`/`, `n2`, `m2`], `x`]], [`*`, [`+`, [`/`, `n1`, `m1`], [`/`, `n2`, `m2`]], `x`], { variables: { x: s2, n1: i2, m1: i2, n2: i2, m2: i2 }, evaluate_numbers: true, max_digits: r2, allow_implicit_identities: [`m1`, `m2`, `n1`, `n2`], allow_extended_match: true, allow_permutations: true, max_group: 1 }]), l2.push([[`+`, [`*`, [`/`, `n1`, `m1`], `x`], [`*`, [`-`, [`/`, `n2`, `m2`]], `x`]], [`*`, [`+`, [`/`, `n1`, `m1`], [`-`, [`/`, `n2`, `m2`]]], `x`], { variables: { x: s2, n1: i2, m1: i2, n2: i2, m2: i2 }, evaluate_numbers: true, max_digits: r2, allow_implicit_identities: [`m1`, `m2`, `n1`, `n2`], allow_extended_match: true, allow_permutations: true, max_group: 1 }]), l2.push([[`+`, [`*`, `n`, `x`], [`-`, [`*`, `m`, `x`]]], [`*`, [`+`, `n`, [`-`, `m`]], `x`], { variables: { x: s2, n: i2, m: i2 }, evaluate_numbers: true, max_digits: r2, allow_implicit_identities: [`m`, `n`], allow_extended_match: true, allow_permutations: true, max_group: 1 }]), l2.push([[`-`, [`pm`, `x`]], [`pm`, `x`], { variables: { x: true } }]), l2.push([[`*`, `c`, [`pm`, `x`]], [`pm`, [`*`, `c`, `x`]], { variables: { c: (t5) => !contains_pm(t5), x: true }, allow_extended_match: true, allow_permutations: true, max_group: 1 }]), l2.push([[`^`, [`*`, `x`, `y`], `a`], [`*`, [`^`, `x`, `a`], [`^`, `y`, `a`]], { allow_permutations: true }]), l2.push([[`^`, [`^`, `x`, `n`], `m`], [`^`, `x`, [`*`, `n`, `m`]], { variables: { x: true, n: i2, m: i2 }, evaluate_numbers: true, max_digits: r2, allow_permutations: true }]), l2.push([[`-`, [`+`, `a`, `b`]], [`+`, [`-`, `a`], [`-`, `b`]]]), l2.push([[`apply`, `cbrt`, [`*`, `a`, `x`]], [`-`, [`apply`, `cbrt`, [`-`, [`*`, `a`, `x`]]]], { variables: { a: (t5) => is_negative_ast(t5, n2), x: true }, evaluate_numbers: true, max_digits: r2, allow_implicit_identities: [`x`] }]), l2.push([[`apply`, `cbrt`, [`-`, `x`]], [`-`, [`apply`, `cbrt`, `x`]], { variables: { x: true }, evaluate_numbers: true, max_digits: r2, allow_implicit_identities: [`x`] }]), l2.push([[`apply`, `nthroot`, [`tuple`, [`*`, `a`, `x`], `n`]], [`-`, [`apply`, `nthroot`, [`tuple`, [`-`, [`*`, `a`, `x`]], `n`]]], { variables: { a: (t5) => is_negative_ast(t5, n2), x: true, n: (t5) => Number.isInteger(t5) && !Number.isInteger(t5 / 2) && t5 > 1 }, evaluate_numbers: true, max_digits: r2, allow_implicit_identities: [`x`] }]), l2.push([[`apply`, `nthroot`, [`tuple`, [`-`, `x`], `n`]], [`-`, [`apply`, `nthroot`, [`tuple`, `x`, `n`]]], { variables: { x: true, n: (t5) => Number.isInteger(t5) && !Number.isInteger(t5 / 2) && t5 > 1 }, evaluate_numbers: true, max_digits: r2, allow_implicit_identities: [`x`] }]), l2.push([[`^`, [`apply`, `abs`, `x`], `n`], [`^`, `x`, `n`], { variables: { x: true, n: (t5) => Number.isInteger(t5) && Number.isInteger(t5 / 2) }, evaluate_numbers: true, max_digits: r2 }]), l2.push([[`*`, `x`, `y`], [`*`, `x`, `y`], { variables: { x: o2, y: o2 }, evaluate_numbers: true, max_digits: r2, allow_extended_match: true, allow_permutations: true, max_group: 1 }]), c2 = applyAllTransformations(c2, l2, 40), c2 = simplify_powers_in_roots(c2, n2), l2 = [], l2.push([[`*`, `x`, [`^`, `y`, [`-`, `a`]]], [`/`, `x`, [`^`, `y`, `a`]], { allow_extended_match: true, allow_permutations: true, evaluate_numbers: true, max_digits: r2, max_group: 1 }]), l2.push([[`*`, `x`, [`apply`, `exp`, [`-`, `a`]]], [`/`, `x`, [`apply`, `exp`, `a`]], { allow_extended_match: true, allow_permutations: true, evaluate_numbers: true, max_digits: r2, max_group: 1, variables: { x: true, a: true } }]), l2.push([[`*`, `x`, [`^`, `y`, `n`]], [`/`, `x`, [`^`, `y`, [`-`, `n`]]], { variables: { x: true, y: true, n: a2 }, evaluate_numbers: true, max_digits: r2, allow_extended_match: true, allow_permutations: true, max_group: 1 }]), l2.push([[`*`, `x`, [`apply`, `exp`, `n`]], [`/`, `x`, [`apply`, `exp`, [`-`, `n`]]], { variables: { x: true, n: a2 }, evaluate_numbers: true, max_digits: r2, allow_extended_match: true, allow_permutations: true, max_group: 1 }]), c2 = applyAllTransformations(c2, l2, 40), l2 = [], l2.push([[`^`, `y`, `n`], [`/`, 1, [`^`, `y`, [`-`, `n`]]], { variables: { y: true, n: a2 }, evaluate_numbers: true, max_digits: r2 }]), l2.push([[`apply`, `exp`, `n`], [`/`, 1, [`apply`, `exp`, [`-`, `n`]]], { variables: { n: a2 }, evaluate_numbers: true, max_digits: r2 }]), c2 = applyAllTransformations(c2, l2, 40), l2 = [], l2.push([[`/`, [`/`, `x`, `y`], `z`], [`/`, `x`, [`*`, `y`, `z`]], { allow_extended_match: true, allow_permutations: true }]), l2.push([[`/`, `x`, [`/`, `y`, `z`]], [`/`, [`*`, `x`, `z`], `y`], { allow_extended_match: true, allow_permutations: true }]), c2 = applyAllTransformations(c2, l2, 40), l2 = [], l2.push([[`*`, `x`, [`/`, `y`, `z`]], [`/`, [`*`, `x`, `y`], `z`], { allow_extended_match: true, allow_permutations: true, max_group: 1 }]), l2.push([[`/`, [`/`, `x`, `y`], `z`], [`/`, `x`, [`*`, `y`, `z`]], { allow_extended_match: true, allow_permutations: true }]), l2.push([[`/`, `x`, [`/`, `y`, `z`]], [`/`, [`*`, `x`, `z`], `y`], { allow_extended_match: true, allow_permutations: true }]), c2 = applyAllTransformations(c2, l2, 40), c2 = evaluate_numbers(c2, { assumptions: n2, max_digits: r2, evaluate_functions: true }), c2;
}
function simplify_ratios(t4, n2) {
  function r2(t5) {
    var n3 = 1;
    return t5 = t5.map(function(t6) {
      if (typeof t6 == `number`) return t6 < 0 ? (n3 *= -1, -t6) : t6;
      if (!Array.isArray(t6)) return t6;
      if (t6[0] === `-`) return n3 *= -1, t6[1];
      if (t6[0] !== `+`) return t6;
      var r3 = false;
      if ((typeof t6[1] == `number` && t6[1] < 0 || Array.isArray(t6[1]) && t6[1][0] === `-` || Array.isArray(t6[1]) && t6[1][0] === `*` && Number(t6[1][1]) < 0) && (r3 = true), r3) {
        n3 *= -1;
        var i3 = t6.slice(1).map((t7) => [`-`, t7]);
        return evaluate_numbers([`+`].concat(i3));
      } else return t6;
    }), { factors: t5, sign_change: n3 };
  }
  function i2(t5, n3) {
    if (!Array.isArray(t5)) return n3 ? [`-`, t5] : t5;
    var a3 = t5[0];
    if (a3 === `-`) return i2(t5[1], n3 = true);
    var o2 = t5.slice(1).map((t6) => i2(t6));
    if (a3 !== `/`) return n3 ? [`-`, [a3, ...o2]] : [a3, ...o2];
    var s2 = o2[0], c2 = o2[1];
    s2 = default_order(s2, { ignore_negatives: true });
    var l2 = Array.isArray(s2) && s2[0] === `*` ? s2.slice(1) : [s2], u2 = r2(l2);
    l2 = u2.factors, n3 && (u2.sign_change *= -1), c2 = default_order(c2, { ignore_negatives: true });
    var d2 = Array.isArray(c2) && c2[0] === `*` ? c2.slice(1) : [c2], p2 = r2(d2);
    return d2 = p2.factors, u2.sign_change * p2.sign_change < 0 && (l2[0] = [`-`, l2[0]]), s2 = l2.length === 1 ? l2[0] : [`*`].concat(l2), c2 = d2.length === 1 ? d2[0] : [`*`].concat(d2), [`/`, s2, c2];
  }
  var a2 = get_tree(t4);
  return contains_blank(a2) ? a2 : (n2 === void 0 && t4.context !== void 0 && t4.context.get_assumptions !== void 0 && (n2 = t4.context.get_assumptions([t4.variables()])), i2(a2));
}
function contains_blank(t4) {
  return t4 === `＿` ? true : Array.isArray(t4) ? t4.some(contains_blank) : false;
}
var all_units = get_all_units();
function remove_units(t4, n2 = true) {
  var r2 = get_tree(t4);
  if (!Array.isArray(r2)) return r2;
  let i2 = r2[0], a2 = r2.slice(1);
  if (i2 === `unit`) {
    let t5 = all_units[get_unit_of_tree(r2)];
    if (!t5) return r2;
    let i3 = remove_units(t5.prefix ? a2[1] : a2[0], n2);
    return n2 ? t5.scale(i3) : i3;
  } else return [i2, ...a2.map((t5) => remove_units(t5, n2))];
}
function remove_scaling_units(t4) {
  var n2 = get_tree(t4);
  if (!Array.isArray(n2)) return n2;
  let r2 = n2[0], i2 = n2.slice(1);
  if (r2 === `unit`) {
    let t5 = all_units[get_unit_of_tree(n2)];
    if (!(t5 && t5.only_scales)) return n2;
    let r3 = remove_scaling_units(t5.prefix ? i2[1] : i2[0]);
    return t5.scale(r3);
  } else return [r2, ...i2.map((t5) => remove_scaling_units(t5))];
}
function add_unit(t4, n2) {
  var r2 = get_tree(t4);
  let i2 = all_units[n2];
  return i2 ? i2.prefix ? [`unit`, n2, r2] : [`unit`, r2, n2] : r2;
}
function move_units_outside_products(t4) {
  if (!Array.isArray(t4)) return t4;
  let n2 = t4[0], r2 = t4.slice(1);
  if (n2 === `*`) {
    let t5 = [], n3 = [];
    for (let i3 of r2) {
      let r3 = get_unit_value_of_tree(i3);
      r3 ? (t5.push(r3[0]), n3.push(r3[1])) : n3.push(i3);
    }
    let i2 = [`*`, ...n3];
    for (let n4 of t5) i2 = add_unit(i2, n4);
    return i2;
  } else return [n2, ...r2.map(move_units_outside_products)];
}
var solve_exports = __exportAll({ solve_linear: () => solve_linear });
function solve_linear(t4, n2, r2) {
  if (typeof n2 == `string`) {
    r2 === void 0 && t4.context !== void 0 && t4.context.get_assumptions !== void 0 && (r2 = t4.context.get_assumptions([t4.variables()]));
    var i2 = simplify(get_tree(t4), r2);
    if (Array.isArray(i2)) {
      var a2 = i2[0], o2 = i2.slice(1);
      if (a2 === `=` || a2 === `ne` || a2 === `<` || a2 === `le` || a2 === `>` || a2 === `ge`) {
        var s2 = simplify([`+`, o2[0], [`-`, o2[1]]], r2), c2 = (t5) => !variables(t5).includes(n2);
        if (s2 = simplify(applyAllTransformations(s2, [[[`+`, [`*`, `_a`, n2], [`*`, `_b`, n2]], [`*`, [`+`, `_a`, `_b`], n2], { variables: { _a: c2, _b: c2 }, allow_permutations: true, allow_extended_match: true, allow_implicit_identities: [`_a`, `_b`], evaluate_numbers: true }]], 20)), variables(s2).includes(n2)) {
          var l2 = match$1(s2, [`+`, [`*`, `_a`, n2], `_b`], { variables: { _a: c2, _b: c2 }, allow_permutations: true, allow_implicit_identities: [`_a`, `_b`] });
          if (l2) {
            var u2 = simplify(l2._a), d2 = simplify(l2._b);
            if (is_nonzero_ast(u2, r2)) {
              if (a2 === `=` || a2 === `ne` || is_positive_ast(u2, r2)) {
                let t5 = simplify([`/`, [`-`, d2], u2]);
                return [a2, n2, t5];
              }
              if (is_negative_ast(u2, r2)) {
                var p2 = simplify([`/`, [`-`, d2], u2]);
                return a2 = a2 === `<` ? `>` : a2 === `le` ? `ge` : a2 === `>` ? `<` : `le`, [a2, n2, p2];
              }
            }
          }
        }
      }
    }
  }
}
function clean_assumptions(t4, n2) {
  if (!Array.isArray(t4) || t4.length === 0) return t4;
  t4 = flatten$5(default_order(simplify_logical(expand_relations(t4))));
  var r2 = t4[0], i2 = t4.slice(1);
  if (r2 === `and` || r2 === `or`) {
    if (i2 = i2.reduce(function(t5, n3) {
      return t5.every(function(t6) {
        return !equal(t6, n3);
      }) && t5.push(n3), t5;
    }, []), r2 === `and` && n2 && Array.isArray(n2)) {
      let t5;
      t5 = n2[0] === `and` ? n2.slice(1) : [n2], i2 = i2.filter((n3) => t5.every((t6) => !equal(t6, n3)));
    }
    t4 = i2.length === 1 ? i2[0] : [r2].concat(i2);
  }
  if (r2 !== `and` && n2 && Array.isArray(n2)) {
    let r3;
    if (r3 = n2[0] === `and` ? n2.slice(1) : [n2], !r3.every((n3) => !equal(n3, t4))) return;
  }
  return t4;
}
function calculate_derived_assumptions(t4, n2) {
  if (n2 === void 0) {
    n2 = [];
    for (let r3 in t4.byvar) {
      let i3 = t4.byvar[r3];
      i3.length > 0 && n2.push(i3);
    }
    if (n2.length === 0) return {};
    n2 = n2.length === 1 ? n2[0] : [`and`].concat(n2), n2 = clean_assumptions(n2);
  }
  if (!Array.isArray(n2) || n2.length === 0) return {};
  var r2 = n2[0], i2 = n2.slice(1);
  if (r2 === `and` || r2 === `or`) {
    let n3 = i2.map(function(n4) {
      return calculate_derived_assumptions(t4, n4);
    }), a3 = [...new Set(n3.reduce((t5, n4) => [...t5, ...Object.keys(n4)], []))], o3 = {};
    for (let i3 of a3) {
      let a4 = n3.reduce(function(t5, n4) {
        return n4[i3] !== void 0 && t5.push(n4[i3]), t5;
      }, []);
      if (r2 === `and` || a4.length === n3.length) {
        let n4 = o3[i3];
        n4 = n4 === void 0 ? a4.length > 1 ? [r2].concat(a4) : a4[0] : a4.length > 1 ? [`and`, n4, [r2].concat(a4)] : [`and`, n4, a4[0]], o3[i3] = clean_assumptions(n4, get_assumptions(t4, i3, { omit_derived: true }));
      }
    }
    return o3;
  }
  let a2 = {};
  if (r2 === `=` || r2 === `ne` || r2 === `<` || r2 === `le` || r2 === `in` || r2 === `subset` || r2 === `notin` || r2 === `notsubset`) {
    var o2 = false;
    for (let n3 = 0; n3 < 2; n3++) {
      let c3 = i2[n3], l2 = i2[1 - n3], u2 = variables(l2);
      if (typeof c3 != `string` || u2.length === 0 || u2.includes(c3)) continue;
      o2 = true;
      var s2 = r2;
      n3 === 1 && (r2 === `<` ? s2 = `>` : r2 === `le` ? s2 = `ge` : r2 === `in` ? s2 = `ni` : r2 === `subset` ? s2 = `superset` : r2 === `notin` ? s2 = `notni` : r2 === `notsubset` && (s2 = `notsuperset`));
      let d2 = get_assumptions_for_expr(t4, l2, [c3]);
      if (d2 = combine_assumptions(c3, s2, l2, d2), d2 !== void 0) {
        let n4 = a2[c3];
        n4 = n4 === void 0 ? d2 : [`and`, n4, d2], a2[c3] = clean_assumptions(n4, get_assumptions(t4, c3, { omit_derived: true }));
      }
    }
    if (o2) return a2;
  }
  let c2 = [];
  for (let n3 of i2) {
    let r3 = get_assumptions_for_expr(t4, n3, []);
    r3 !== void 0 && c2.push(r3);
  }
  if (c2.length === 0) return {};
  c2 = c2.length === 1 ? c2[0] : [`and`].concat(c2);
  for (let r3 of variables(n2)) a2[r3] = clean_assumptions(c2, get_assumptions(t4, r3, { omit_derived: true }));
  return a2;
}
function get_assumptions_for_expr(t4, n2, r2) {
  let i2 = variables(n2);
  if (i2 = i2.filter((t5) => !r2.includes(t5)), i2.length === 0) return;
  function a2(t5) {
    return !!(typeof t5 == `number` || Array.isArray(t5) && t5[0] === `-` && typeof t5[1] == `number`);
  }
  var o2 = [`_b`], s2 = [`_b`], c2 = { _b: a2 }, l2 = {};
  for (let t5 = 0; t5 < i2.length; t5++) {
    let n3 = `_a` + t5;
    l2[i2[t5]] = n3, o2.push([`*`, n3, i2[t5]]), s2.push(n3), c2[n3] = a2;
  }
  o2 = [`+`].concat(o2);
  var u2 = match$1(n2, o2, { variables: c2, allow_permutations: true, allow_extended_match: false, allow_implicit_identities: s2, max_group: 1 });
  if (!u2) {
    let i3 = [];
    for (let a3 of variables(n2)) {
      let n3 = get_assumptions_for_expr(t4, a3, r2);
      n3 !== void 0 && i3.push(n3);
    }
    return i3.length === 0 ? void 0 : i3.length === 1 ? i3[0] : [`and`].concat(i3);
  }
  for (let t5 of i2) l2[t5] = u2[l2[t5]];
  let d2 = false;
  u2._b === 0 && u2._a0 === 1 && i2.length === 1 && (d2 = true);
  var p2 = get_assumptions(t4, [i2], { exclude_variables: r2 });
  if (p2 === void 0) return;
  return clean_assumptions(m2(p2));
  function m2(a3) {
    if (!Array.isArray(a3)) return;
    var o3 = a3[0], s3 = a3.slice(1);
    if (o3 === `and` || o3 === `or`) {
      let t5 = s3.map(m2).filter((t6) => t6 !== void 0);
      return t5.length === 0 ? void 0 : o3 === `or` ? t5.length === s3.length ? [`or`].concat(t5) : void 0 : t5.length === 1 ? t5[0] : [`and`].concat(t5);
    }
    if (!([`=`, `ne`, `<`, `le`].includes(o3) || [`in`, `notin`, `subset`, `notsubset`].includes(o3) && d2)) {
      let i3 = r2.concat(variables(n2)), o4 = [];
      for (let n3 of variables(a3)) {
        if (i3.includes(n3)) continue;
        let r3 = get_assumptions_for_expr(t4, n3, i3);
        r3 !== void 0 && o4.push(r3);
      }
      return o4.length === 0 ? a3 : o4.length === 1 ? [`and`, a3, o4[0]] : [`and`, a3].concat(o4);
    }
    let c3 = [];
    for (let a4 = 0; a4 <= 1; a4++) {
      let d3 = s3[a4], m3 = s3[1 - a4];
      if (typeof d3 == `string` && i2.includes(d3)) {
        var u3 = {};
        u3[d3] = m3;
        var p3 = simplify(substitute$1(n2, u3)), h2 = false, g2 = o3;
        (a4 === 1 && l2[d3] > 0 || a4 === 0 && l2[d3] < 0) && (o3 === `<` ? (h2 = true, g2 = `>`) : o3 === `le` ? (h2 = true, g2 = `ge`) : o3 === `in` ? (h2 = true, g2 = `ni`) : o3 === `subset` ? (h2 = true, g2 = `superset`) : o3 === `notin` ? (h2 = true, g2 = `notni`) : o3 === `notsubset` && (h2 = true, g2 = `notsuperset`)), h2 ? c3.push([o3, p3, n2]) : c3.push([o3, n2, p3]);
        let i3 = get_assumptions_for_expr(t4, p3, r2.concat([d3]));
        i3 = combine_assumptions(n2, g2, p3, i3), i3 !== void 0 && c3.push(i3);
      }
    }
    if (c3.length === 1) return c3[0];
    if (c3.length > 1) return [`and`].concat(c3);
    let v2 = r2.concat(variables(n2));
    c3 = [];
    for (let n3 of variables(a3)) {
      if (v2.includes(n3)) continue;
      let r3 = get_assumptions_for_expr(t4, n3, v2);
      r3 !== void 0 && c3.push(r3);
    }
    return c3.length === 0 ? a3 : c3.length === 1 ? [`and`, a3, c3[0]] : [`and`, a3].concat(c3);
  }
}
function combine_assumptions(t4, n2, r2, i2) {
  if (![`=`, `ne`, `<`, `le`, `>`, `ge`, `in`, `notin`, `ni`, `notni`, `subset`, `notsubset`, `superset`, `notsuperset`].includes(n2)) return i2;
  if (Array.isArray(i2)) {
    var a2 = i2[0], o2 = i2.slice(1);
    if (a2 === `and` || a2 === `or`) {
      let i3 = o2.map((i4) => combine_assumptions(t4, n2, r2, i4)).filter((t5) => t5 !== void 0);
      return i3.length === 0 ? void 0 : a2 === `or` ? i3.length === o2.length ? [[`or`].concat(i3)] : void 0 : i3.length === 1 ? i3[0] : [`and`].concat(i3);
    }
    if (![`=`, `ne`, `<`, `le`, `in`, `notin`, `subset`, `notsubset`].includes(a2)) return i2;
    var s2 = a2, c2;
    if (equal(o2[0], r2)) c2 = o2[1];
    else if (equal(o2[1], r2)) c2 = o2[0], a2 === `<` ? s2 = `>` : a2 === `le` ? s2 = `ge` : a2 === `in` ? s2 = `ni` : a2 === `notin` ? s2 = `notni` : a2 === `subset` ? s2 = `superset` : a2 === `notsubset` && (s2 = `notsuperset`);
    else return i2;
    var l2;
    if (n2 === `=`) l2 = s2;
    else if (s2 === `=`) l2 = n2;
    else if (n2 === `<`) if (s2 === `<` || s2 === `le`) l2 = `<`;
    else if (s2 === `in` || s2 === `notin`) return i2;
    else return;
    else if (n2 === `le`) if (s2 === `<`) l2 = `<`;
    else if (s2 === `le`) l2 = `le`;
    else if (s2 === `in` || s2 === `notin`) return i2;
    else return;
    else if (n2 === `>`) if (s2 === `>` || s2 === `ge`) l2 = `>`;
    else if (s2 === `in` || s2 === `notin`) return i2;
    else return;
    else if (n2 === `ge`) if (s2 === `>`) l2 = `>`;
    else if (s2 === `ge`) l2 = `ge`;
    else if (s2 === `in` || s2 === `notin`) return i2;
    else return;
    else if (n2 === `in`) if (s2 === `subset`) l2 = `in`;
    else return;
    else if (n2 === `notin`) if (s2 === `superset`) l2 = `notin`;
    else return;
    else if (n2 === `ni`) if (s2 === `notin`) l2 = `notsubset`;
    else return;
    else if (n2 === `notni`) if (s2 === `in`) l2 = `notsuperset`;
    else return;
    else if (n2 === `subset`) if (s2 === `subset`) l2 = `subset`;
    else if (s2 === `notni`) l2 = `notni`;
    else if (s2 === `notsuperset`) l2 = `notsuperset`;
    else return;
    else if (n2 === `notsubset`) if (s2 === `superset`) l2 = `notsubset`;
    else return;
    else if (n2 === `superset`) if (s2 === `superset`) l2 = `superset`;
    else if (s2 === `ni`) l2 = `ni`;
    else if (s2 === `notsubset`) l2 = `notsubset`;
    else return;
    else if (n2 === `notsuperset`) if (s2 === `subset`) l2 = `notsuperset`;
    else return;
    else return;
    return l2 === `>` ? [`<`, c2, t4] : l2 === `ge` ? [`le`, c2, t4] : l2 === `ni` ? [`in`, c2, t4] : l2 === `notni` ? [`notin`, c2, t4] : l2 === `superset` ? [`subset`, c2, t4] : l2 === `notsuperset` ? [`notsubset`, c2, t4] : [l2, t4, c2];
  }
}
function filter_assumptions_from_tree(t4, n2) {
  if (!(!Array.isArray(t4) || t4.length === 0)) {
    Array.isArray(n2) || (n2 = [n2]);
    var r2 = t4[0], i2 = t4.slice(1);
    if (r2 === `and`) {
      var a2 = i2.map(function(t5) {
        return filter_assumptions_from_tree(t5, n2);
      });
      return a2 = a2.filter((t5) => t5 !== void 0), a2.length === 0 ? void 0 : a2.length === 1 ? a2[0] : [`and`].concat(a2);
    }
    var o2 = variables(t4);
    if (!(n2.filter((t5) => o2.includes(t5)).length > 0)) return t4;
  }
}
function get_assumptions_sub(t4, n2, r2, i2) {
  Array.isArray(n2) || (n2 = [n2]);
  var a2 = [];
  if (n2.forEach(function(n3) {
    if (t4.byvar[n3] || t4.derived[n3]) {
      if (t4.byvar[n3] && t4.byvar[n3].length > 0) {
        var o2 = filter_assumptions_from_tree(t4.byvar[n3], r2);
        o2 !== void 0 && a2.push(o2);
      }
      if (t4.derived[n3] && t4.derived[n3].length > 0 && !i2) {
        var s2 = filter_assumptions_from_tree(t4.derived[n3], r2);
        s2 !== void 0 && a2.push(s2);
      }
    } else t4.generic.length > 0 && (n3 === `x` || !variables(t4.generic).includes(n3)) && a2.push(substitute$1(t4.generic, { x: n3 }));
  }), a2.length === 1 ? a2 = a2[0] : a2.length > 1 && (a2 = [`and`].concat(a2)), a2.length > 0) return clean_assumptions(a2);
}
function get_assumptions(t4, n2, r2) {
  r2 === void 0 && (r2 = {});
  var i2 = r2.exclude_variables;
  i2 === void 0 ? i2 = [] : Array.isArray(i2) || (i2 = [i2]);
  var a2, o2 = get_tree(n2);
  if (typeof o2 == `string`) a2 = [o2];
  else if (Array.isArray(o2)) Array.isArray(o2[0]) && (a2 = o2[0]);
  else return;
  return a2 ? get_assumptions_sub(t4, a2, i2, r2.omit_derived) : get_assumptions_for_expr(t4, o2, i2);
}
function add_assumption(t4, n2, r2) {
  var i2 = get_tree(n2);
  if (!Array.isArray(i2)) return 0;
  i2 = clean_assumptions(simplify(i2, t4));
  var a2 = add_assumption_sub(t4, i2, r2);
  return a2 && (t4.derived = calculate_derived_assumptions(t4)), a2;
}
function add_assumption_sub(t4, n2, r2) {
  if (n2[0] === `and`) return n2.slice(1).map((n3) => add_assumption_sub(t4, n3, r2)).reduce(function(t5, n3) {
    return t5 + n3;
  });
  var i2 = variables(n2);
  if (i2.length === 0) return 0;
  let a2 = 0;
  !r2 && t4.generic.length > 0 && i2.forEach(function(n3) {
    t4.byvar[n3] === void 0 && (n3 === `x` || !variables(t4.generic).includes(n3)) && (add_assumption_sub(t4, substitute$1(t4.generic, { x: n3 }), true), a2 += 1);
  });
  for (let r3 of i2) {
    let i3 = solve_linear(n2, r3, t4), o2 = n2;
    i3 && (o2 = i3);
    let s2 = t4.byvar[r3];
    s2 !== void 0 && s2.length !== 0 && (o2 = [`and`, s2, o2]), o2 = clean_assumptions(o2), equal(o2, s2) || (t4.byvar[r3] = o2, a2 += 1);
  }
  return a2;
}
function add_generic_assumption(t4, n2) {
  var r2 = get_tree(n2);
  if (!Array.isArray(r2)) return 0;
  r2 = clean_assumptions(simplify(r2, t4));
  var i2 = add_generic_assumption_sub(t4, r2);
  return i2 && (t4.derived = calculate_derived_assumptions(t4)), i2;
}
function add_generic_assumption_sub(t4, n2) {
  if (n2[0] === `and`) return n2.slice(1).map((n3) => add_generic_assumption_sub(t4, n3)).reduce(function(t5, n3) {
    return t5 + n3;
  });
  if (!variables(n2).includes(`x`)) return 0;
  let r2 = solve_linear(n2, `x`, t4), i2 = n2;
  r2 && (i2 = r2);
  let a2 = t4.generic;
  return a2.length !== 0 && (i2 = [`and`, a2, i2]), i2 = clean_assumptions(i2), equal(i2, a2) ? 0 : (t4.generic = i2, 1);
}
function remove_assumption(t4, n2) {
  var r2 = get_tree(n2);
  if (!Array.isArray(r2)) return 0;
  r2 = clean_assumptions(simplify(r2, t4));
  var i2 = remove_assumption_sub(t4, r2);
  return i2 && (t4.derived = calculate_derived_assumptions(t4)), i2;
}
function remove_assumption_sub(t4, n2) {
  if (n2[0] === `and`) return n2.slice(1).map((n3) => remove_assumption_sub(t4, n3)).reduce(function(t5, n3) {
    return t5 + n3;
  });
  var r2 = variables(n2);
  if (r2.length === 0) return 0;
  var i2 = 0;
  for (let a2 of r2) {
    let r3 = solve_linear(n2, a2, t4), o2 = t4.byvar[a2];
    if (!o2 || o2.length === 0) continue;
    let s2 = o2[0], c2 = o2.slice(1), l2 = c2.length, u2;
    if (s2 === `and`) if (c2 = c2.filter((t5) => !(equal(t5, n2) || equal(t5, r3))), c2.length === 0) u2 = [];
    else if (c2.length === 1) u2 = c2[0];
    else if (c2.length < l2) u2 = [s2].concat(c2);
    else continue;
    else if (equal(o2, n2) || equal(o2, r3)) u2 = [];
    else continue;
    i2 += 1, t4.byvar[a2] = u2;
  }
  return i2;
}
function remove_generic_assumption(t4, n2) {
  var r2 = get_tree(n2);
  if (!Array.isArray(r2)) return 0;
  r2 = clean_assumptions(simplify(r2, t4));
  var i2 = remove_generic_assumption_sub(t4, r2);
  return i2 && (t4.derived = calculate_derived_assumptions(t4)), i2;
}
function remove_generic_assumption_sub(t4, n2) {
  if (n2[0] === `and`) return n2.slice(1).map((n3) => remove_generic_assumption_sub(t4, n3)).reduce(function(t5, n3) {
    return t5 + n3;
  });
  if (!variables(n2).includes(`x`)) return 0;
  var r2 = t4.generic;
  if (r2.length === 0) return 0;
  let i2 = solve_linear(n2, `x`, t4);
  var a2 = r2[0], o2 = r2.slice(1), s2 = o2.length, c2;
  if (a2 === `and`) if (o2 = o2.filter((t5) => !(equal(t5, n2) || equal(t5, i2))), o2.length === 0) c2 = [];
  else if (o2.length === 1) c2 = o2[0];
  else if (o2.length < s2) c2 = [a2].concat(o2);
  else return 0;
  else if (equal(r2, n2) || equal(r2, i2)) c2 = [];
  else return 0;
  return t4.generic = c2, 1;
}
function initialize_assumptions() {
  var t4 = {};
  return t4.byvar = {}, t4.derived = {}, t4.generic = [], t4.not_commutative = [], t4.get_assumptions = function(n2, r2) {
    return get_assumptions(t4, n2, r2);
  }, t4.add_assumption = function(n2, r2) {
    return add_assumption(t4, n2, r2);
  }, t4.add_generic_assumption = function(n2) {
    return add_generic_assumption(t4, n2);
  }, t4.remove_assumption = function(n2) {
    return remove_assumption(t4, n2);
  }, t4.remove_generic_assumption = function(n2) {
    return remove_generic_assumption(t4, n2);
  }, t4;
}
var operators$1 = { "+": function(t4) {
  return t4.join(` `);
}, "-": function(t4) {
  return `-` + t4[0];
}, pm: function(t4) {
  return `\\pm ` + t4[0];
}, "*": function(t4) {
  return t4.join(` `);
}, "/": function(t4) {
  return `\\frac{` + t4[0] + `}{` + t4[1] + `}`;
}, _: function(t4) {
  return t4[0] + `_{` + t4[1] + `}`;
}, "^": function(t4) {
  return t4[0] + `^{` + t4[1] + `}`;
}, prime: function(t4) {
  return t4[0] + `'`;
}, tuple: function(t4) {
  return `\\left( ` + t4.join(`, `) + ` \\right)`;
}, array: function(t4) {
  return `\\left[ ` + t4.join(`, `) + ` \\right]`;
}, list: function(t4) {
  return t4.join(`, `);
}, set: function(t4) {
  return `\\left\\{ ` + t4.join(`, `) + ` \\right\\}`;
}, vector: function(t4) {
  return `\\left( ` + t4.join(`, `) + ` \\right)`;
}, altvector: function(t4) {
  return `\\left\\langle ` + t4.join(`, `) + ` \\right\\rangle`;
}, interval: function(t4) {
  return `\\left( ` + t4.join(`, `) + ` \\right)`;
}, matrix: function(t4) {
  return `\\left( ` + t4.join(`, `) + ` \\right)`;
}, and: function(t4) {
  return t4.join(` \\land `);
}, or: function(t4) {
  return t4.join(` \\lor `);
}, not: function(t4) {
  return `\\lnot ` + t4[0];
}, "=": function(t4) {
  return t4.join(` = `);
}, "<": function(t4) {
  return t4.join(` < `);
}, ">": function(t4) {
  return t4.join(` > `);
}, lts: function(t4) {
  return t4.join(` < `);
}, gts: function(t4) {
  return t4.join(` > `);
}, le: function(t4) {
  return t4.join(` \\le `);
}, ge: function(t4) {
  return t4.join(` \\ge `);
}, ne: function(t4) {
  return t4.join(` \\ne `);
}, forall: function(t4) {
  return `\\forall ` + t4[0];
}, exists: function(t4) {
  return `\\exists ` + t4[0];
}, in: function(t4) {
  return t4[0] + ` \\in ` + t4[1];
}, notin: function(t4) {
  return t4[0] + ` \\notin ` + t4[1];
}, ni: function(t4) {
  return t4[0] + ` \\ni ` + t4[1];
}, notni: function(t4) {
  return t4[0] + ` \\not\\ni ` + t4[1];
}, subset: function(t4) {
  return t4[0] + ` \\subset ` + t4[1];
}, subseteq: function(t4) {
  return t4[0] + ` \\subseteq ` + t4[1];
}, notsubset: function(t4) {
  return t4[0] + ` \\not\\subset ` + t4[1];
}, notsubseteq: function(t4) {
  return t4[0] + ` \\not\\subseteq ` + t4[1];
}, superset: function(t4) {
  return t4[0] + ` \\supset ` + t4[1];
}, superseteq: function(t4) {
  return t4[0] + ` \\supseteq ` + t4[1];
}, notsuperset: function(t4) {
  return t4[0] + ` \\not\\supset ` + t4[1];
}, notsuperseteq: function(t4) {
  return t4[0] + ` \\not\\supseteq ` + t4[1];
}, union: function(t4) {
  return t4.join(` \\cup `);
}, intersect: function(t4) {
  return t4.join(` \\cap `);
}, implies: function(t4) {
  return t4.join(` \\implies `);
}, impliedby: function(t4) {
  return t4.join(` \\impliedby `);
}, iff: function(t4) {
  return t4.join(` \\iff `);
}, leftarrow: function(t4) {
  return t4.join(` \\leftarrow `);
}, rightarrow: function(t4) {
  return t4.join(` \\rightarrow `);
}, leftrightarrow: function(t4) {
  return t4.join(` \\leftrightarrow `);
}, perp: function(t4) {
  return t4.join(` \\perp `);
}, parallel: function(t4) {
  return t4.join(` \\parallel `);
}, derivative_leibniz: function(t4) {
  return `\\frac{d` + t4[0] + `}{d` + t4[1] + `}`;
}, partial_derivative_leibniz: function(t4) {
  return `\\frac{d` + t4[0] + `}{d` + t4[1] + `}`;
}, "|": function(t4) {
  return t4[0] + ` \\mid ` + t4[1];
}, ":": function(t4) {
  return t4[0] + ` : ` + t4[1];
}, binom: function(t4) {
  return `\\binom{` + t4[0] + `}{` + t4[1] + `}`;
}, vec: function(t4) {
  return `\\vec{` + t4[0] + `}`;
}, linesegment: function(t4) {
  return `\\overline{` + t4.join(` `) + `}`;
}, angle: function(t4, n2) {
  return n2 ? `\\angle ` + t4.join(``) : `\\angle\\left( ` + t4.join(`, `) + ` \\right)`;
}, unit: function(t4) {
  return t4[1] === `\\circ` ? t4[0] + `^{\\circ}` : t4[0] + ` ` + t4[1];
} }, allowedLatexSymbolsDefault$1 = `alpha.beta.gamma.Gamma.delta.Delta.epsilon.zeta.eta.theta.Theta.iota.kappa.lambda.Lambda.mu.nu.xi.Xi.pi.Pi.rho.sigma.Sigma.tau.Tau.upsilon.Upsilon.phi.Phi.chi.psi.Psi.omega.Omega.partial.abs.exp.log.ln.log10.sign.sqrt.erf.cos.cosh.cot.coth.csc.csch.sec.sech.sin.sinh.tan.tanh.arcsin.arccos.arctan.arccsc.arcsec.arccot.arg.Re.Im.det.angle.perp.circ.%.$.int.varnothing`.split(`.`), convertLatexSymbolsDefault = { acos: `arccos`, acosh: `arccosh`, acot: `arccot`, acoth: `arccoth`, acsc: `arccsc`, acsch: `arccsch`, asec: `arcsec`, asech: `arcsech`, asin: `arcsin`, asinh: `arcsinh`, atan: `arctan`, atanh: `arctanh`, deg: `circ`, emptyset: `varnothing` }, matrixEnvironmentDefault = `bmatrix`, astToLatex$1 = class {
  constructor({ allowedLatexSymbols: t4 = allowedLatexSymbolsDefault$1, convertLatexSymbols: n2 = convertLatexSymbolsDefault, matrixEnvironment: r2 = matrixEnvironmentDefault, padToDigits: i2 = null, padToDecimals: a2 = null, avoidScientificNotation: o2 = false, showBlanks: s2 = true } = {}) {
    this.allowedLatexSymbols = t4, this.convertLatexSymbols = n2, this.matrixEnvironment = r2, this.padToDigits = i2, this.padToDecimals = a2, this.avoidScientificNotation = o2, this.showBlanks = s2;
  }
  convert(t4) {
    return this.statement(normalize_display_negative_fractions(t4));
  }
  statement(t4) {
    if (!Array.isArray(t4)) return this.single_statement(t4);
    var n2 = t4[0], r2 = t4.slice(1);
    if (n2 === `ldots`) return `\\ldots`;
    if (!(n2 in operators$1) && n2 !== `apply`) throw Error(`Badly formed ast: operator ` + n2 + ` not recognized.`);
    return [`implies`, `impliedby`, `iff`, `rightarrow`, `leftarrow`, `leftrightarrow`].includes(n2) ? operators$1[n2](r2.map((t5) => this.statement(t5))) : n2 === `and` || n2 === `or` ? operators$1[n2](r2.map((function(t5, n3) {
      let r3 = this.single_statement(t5);
      return r3.match(/ /) && !r3.match(/^\\left\(.*\\right\)$/) ? `\\left(` + r3 + `\\right)` : r3;
    }).bind(this))) : this.single_statement(t4);
  }
  single_statement(t4) {
    if (!Array.isArray(t4)) return this.expression(t4);
    var n2 = t4[0], r2 = t4.slice(1);
    if (n2 === `not`) return operators$1[n2](r2.map((function(t5, n3) {
      let r3 = this.single_statement(t5);
      return r3.match(/ /) && !r3.match(/^\\left\(.*\\right\)$/) ? `\\left(` + r3 + `\\right)` : r3;
    }).bind(this)));
    if (n2 === `exists` || n2 === `forall`) return operators$1[n2]([this.single_statement(r2[0])]);
    if (n2 === `=` || n2 === `ne` || n2 === `<` || n2 === `>` || n2 === `le` || n2 === `ge` || n2 === `in` || n2 === `notin` || n2 === `ni` || n2 === `notni` || n2 === `subset` || n2 === `notsubset` || n2 === `subseteq` || n2 === `notsubseteq` || n2 === `superset` || n2 === `notsuperset` || n2 === `superseteq` || n2 === `notsuperseteq`) return operators$1[n2](r2.map((function(t5, n3) {
      return this.expression(t5);
    }).bind(this)));
    if (n2 === `lts` || n2 === `gts`) {
      let t5 = r2[0], i2 = r2[1];
      if (t5[0] !== `tuple` || i2[0] !== `tuple`) throw Error(`Badly formed ast`);
      let a2 = this.expression(t5[1]);
      for (let r3 = 1; r3 < t5.length - 1; r3++) i2[r3] ? n2 === `lts` ? a2 += ` < ` : a2 += ` > ` : n2 === `lts` ? a2 += ` \\le ` : a2 += ` \\ge `, a2 += this.expression(t5[r3 + 1]);
      return a2;
    }
    return this.expression(t4);
  }
  expression(t4) {
    if (!Array.isArray(t4)) return this.term(t4);
    var n2 = t4[0], r2 = t4.slice(1);
    return n2 === `+` ? r2.length === 1 ? `+` + this.term(r2[0]) : operators$1[n2](r2.map((function(t5, n3) {
      return n3 > 0 ? this.termWithPlusIfNotNegated(t5) : this.term(t5);
    }).bind(this))) : [`union`, `intersect`, `perp`, `parallel`].includes(n2) ? operators$1[n2](r2.map((function(t5, n3) {
      return this.term(t5);
    }).bind(this))) : this.term(t4);
  }
  term(t4) {
    if (!Array.isArray(t4)) return this.factor(t4);
    var n2 = t4[0], r2 = t4.slice(1);
    return n2 === `-` || n2 === `pm` ? operators$1[n2](r2.map((function(t5, n3) {
      return this.term(t5);
    }).bind(this))) : n2 === `*` ? operators$1[n2](r2.map((function(t5, n3) {
      let i2;
      return n3 > 0 ? (i2 = this.factorWithParenthesesIfNegatedExplicitPlusOrFraction(t5), i2.match(/^[0-9]/) ? `\\cdot ` + i2 : t5[0] === `angle` && r2.length > 1 ? `\\left( ` + i2 + ` \\right)` : i2) : (i2 = this.factor(t5), t5[0] === `angle` && r2.length > 1 ? `\\left( ` + i2 + ` \\right)` : i2);
    }).bind(this))) : n2 === `/` || n2 === `unit` ? operators$1[n2](r2.map((function(t5, n3) {
      return this.expression(t5);
    }).bind(this))) : this.factor(t4);
  }
  simple_factor_or_function_or_parens(t4) {
    var n2 = this.factor(t4);
    return n2.length <= 1 || typeof t4 == `string` && t4.match(/^\w+$/) || Array.isArray(t4) && t4[0] === `apply` && ![`sqrt`, `cbrt`, `nthroot`].includes(t4[1]) || n2.match(/^\\left\(.*\\right\)$/) || n2.match(/^\\left\\langle.*\\right\\rangle$/) ? true : typeof t4 == `number` ? !!(t4 >= 0 && (this.avoidScientificNotation || !t4.toString().includes(`e`))) : false;
  }
  stringConvert(t4) {
    return t4.length > 1 ? (this.convertLatexSymbols[t4] && (t4 = this.convertLatexSymbols[t4]), this.allowedLatexSymbols.includes(t4) ? `\\` + t4 : `\\operatorname{` + t4 + `}`) : this.allowedLatexSymbols.includes(t4) ? `\\` + t4 : t4 === `＿` && !this.showBlanks ? `` : t4;
  }
  factor(t4) {
    if (typeof t4 == `string`) return this.stringConvert(t4);
    if (typeof t4 == `number`) {
      if (t4 === 1 / 0) return `\\infty`;
      if (t4 === -1 / 0) return `-\\infty`;
      if (Number.isNaN(t4)) return `NaN`;
      {
        let n3 = t4.toString(), r3 = n3.indexOf(`e`);
        if (r3 === -1 || this.avoidScientificNotation) return r3 !== -1 && this.avoidScientificNotation && (n3 = expandScientificNotation(n3)), (this.padToDigits !== null || this.padToDecimals !== null) && (n3 = padNumberStringToDigitsAndDecimals(n3, this.padToDigits, this.padToDecimals)), n3;
        let i2 = n3.substring(0, r3), a2 = n3.substring(r3 + 1);
        if (a2[0] === `+` && (a2 = a2.slice(1)), this.padToDigits !== null || this.padToDecimals !== null) {
          let r4 = Number(a2);
          if (r4 > 0 && this.padToDecimals !== null) return n3 = t4.toLocaleString(`fullwide`, { useGrouping: false }), padNumberStringToDigitsAndDecimals(n3, this.padToDigits, this.padToDecimals);
          {
            let t5 = null;
            this.padToDecimals !== null && (t5 = this.padToDecimals + r4), i2 = padNumberStringToDigitsAndDecimals(i2, this.padToDigits, t5);
          }
        }
        return i2 + ` \\cdot 10^{` + a2 + `}`;
      }
    }
    if (!Array.isArray(t4)) return ``;
    var n2 = t4[0], r2 = t4.slice(1);
    if (n2 === `^`) {
      if (Number.isInteger(r2[0]) && Number.isInteger(r2[1])) return r2[0] < 0 ? `\\left(` + r2[0].toString() + `\\right)^{` + r2[1].toString() + `}` : r2[0].toString() + `^{` + r2[1].toString() + `}`;
      let t5 = this.factor(r2[0]), n3 = r2[0];
      for (; n3[0] === `prime`; ) n3 = n3[1];
      return this.simple_factor_or_function_or_parens(n3) || n3[0] === `_` && typeof n3[1] == `string` || (t5 = `\\left(` + t5.toString() + `\\right)`), t5 + `^{` + this.statement(r2[1]) + `}`;
    } else if (n2 === `_`) {
      let t5 = this.factor(r2[0]);
      return this.simple_factor_or_function_or_parens(r2[0]) || (t5 = `\\left(` + t5.toString() + `\\right)`), t5 + `_{` + this.statement(r2[1]) + `}`;
    } else if (n2 === `prime`) {
      let t5 = r2[0], n3 = 1;
      for (; t5[0] === `prime`; ) n3 += 1, t5 = t5[1];
      let i2 = this.factor(t5);
      this.simple_factor_or_function_or_parens(t5) || t5[0] === `_` && typeof t5[1] == `string` || (i2 = `\\left(` + i2 + `\\right)`);
      for (let t6 = 0; t6 < n3; t6++) i2 += `'`;
      return i2;
    } else if (n2 === `/`) return this.term(t4);
    else if (n2 === `-`) return operators$1[n2](r2.map((function(t5, n3) {
      return this.factor(t5);
    }).bind(this)));
    else if (n2 === `tuple` || n2 === `array` || n2 === `list` || n2 === `set` || n2 === `vector` || n2 === `altvector` || n2 === `|` || n2 === `:` || n2 === `binom` || n2 === `vec` || n2 === `linesegment`) return operators$1[n2](r2.map((function(t5, n3) {
      return this.statement(t5);
    }).bind(this)));
    else if (n2 === `interval`) {
      let t5 = r2[0], n3 = r2[1];
      if (t5[0] !== `tuple` || n3[0] !== `tuple`) throw Error(`Badly formed ast`);
      let i2 = this.statement(t5[1]) + `, ` + this.statement(t5[2]);
      return i2 = n3[1] ? `\\left[ ` + i2 : `\\left( ` + i2, n3[2] ? i2 += ` \\right]` : i2 += ` \\right)`, i2;
    } else if (n2 === `matrix`) {
      let t5 = r2[0], n3 = r2[1], i2 = `\\begin{` + this.matrixEnvironment + `} `;
      for (let r3 = 0; r3 < t5[1]; r3 += 1) {
        for (let a2 = 0; a2 < t5[2]; a2 += 1) i2 += this.statement(n3[r3 + 1][a2 + 1]), a2 < t5[2] - 1 && (i2 += ` & `);
        r3 < t5[1] - 1 && (i2 += ` \\\\ `);
      }
      return i2 = i2 + ` \\end{` + this.matrixEnvironment + `}`, i2;
    } else if (n2 === `derivative_leibniz` || n2 === `partial_derivative_leibniz`) {
      let t5 = `d`;
      n2 === `partial_derivative_leibniz` && (t5 = `\\partial `);
      let i2 = r2[0], a2 = r2[1], o2 = 1, s2 = ``;
      Array.isArray(i2) ? (s2 = i2[1], o2 = i2[2]) : s2 = i2;
      let c2 = t5;
      o2 > 1 ? c2 = c2.trimRight() + `^{` + o2 + `}` + this.stringConvert(s2) : c2 += this.stringConvert(s2), c2 = `\\frac{ ` + c2 + ` }{ `;
      let l2 = 1;
      Array.isArray(a2) && (l2 = a2.length - 1);
      for (let n3 = 1; n3 <= l2; n3++) {
        let r3 = a2[n3], i3 = 1, o3 = ``;
        Array.isArray(r3) ? (o3 = r3[1], i3 = r3[2]) : o3 = r3, c2 = c2 + t5 + this.stringConvert(o3), i3 > 1 && (c2 = c2 + `^{` + i3 + `}`), c2 += ` `;
      }
      return c2 += `}`, c2;
    } else if (n2 === `apply`) {
      if (r2[0] === `abs`) return `\\left|` + this.statement(r2[1]) + `\\right|`;
      if (r2[0] === `floor`) return `\\left\\lfloor ` + this.statement(r2[1]) + ` \\right\\rfloor`;
      if (r2[0] === `ceil`) return `\\left\\lceil ` + this.statement(r2[1]) + ` \\right\\rceil`;
      if (r2[0] === `factorial`) {
        let t6 = this.factor(r2[1]);
        return this.simple_factor_or_function_or_parens(r2[1]) || r2[1][0] === `_` && typeof r2[1][1] == `string` ? t6 + `!` : `\\left(` + t6 + `\\right)!`;
      } else if (r2[0] === `sqrt`) return `\\sqrt{` + this.statement(r2[1]) + `}`;
      else if (r2[0] === `cbrt`) return `\\sqrt[3]{` + this.statement(r2[1]) + `}`;
      else if (r2[0] === `nthroot`) return Array.isArray(r2[1]) && r2[1][0] === `tuple` && r2[1].length === 3 ? `\\sqrt[` + this.statement(r2[1][2]) + `]{` + this.statement(r2[1][1]) + `}` : `\\sqrt{` + this.statement(r2[1]) + `}`;
      let t5 = r2[0];
      if (t5[0] === `^` && (t5 = t5[1]), t5[0] === `_` && (t5 = t5[1]), t5 === `int`) {
        let t6 = this.factor(r2[0]), n4 = r2[1], i3;
        if (Array.isArray(n4) && n4[0] === `*`) {
          let t7 = [], r3 = [`*`];
          for (let i4 = 1; i4 < n4.length; i4++) {
            let a2 = n4[i4];
            Array.isArray(a2) && a2[0] === `d` ? t7.push(a2) : r3.push(a2);
          }
          i3 = this.term(r3), t7.length > 0 && (i3[i3.length - 1].match(/[a-zA-Z]/) && (i3 += `\\,`), i3 += t7.map((t8) => `d` + this.factor(t8[1])).join(`\\,`));
        }
        return i3 ||= this.term(r2[1]), t6 + ` ` + i3;
      }
      let n3;
      n3 = r2[0] === `log10` ? `\\log_{10}` : r2[0] === `re` ? `\\Re` : r2[0] === `im` ? `\\Im` : this.factor(r2[0]);
      let i2 = this.statement(r2[1]);
      return r2[1][0] !== `tuple` && (i2 = `\\left(` + i2 + `\\right)`), n3 + i2;
    } else if (n2 === `angle`) {
      let t5 = r2.every((t6) => {
        if (typeof t6 == `string` && t6.length === 1) return true;
        if (!Array.isArray(t6)) return false;
        let n3 = t6[0];
        return n3 === `_` || n3 === `^` || n3 === `prime`;
      });
      return operators$1[n2](r2.map((function(t6, n3) {
        return this.statement(t6);
      }).bind(this)), t5);
    } else if (n2 === `+` && t4.length === 2) return `+ ` + this.factor(t4[1]);
    else return `\\left(` + this.statement(t4) + `\\right)`;
  }
  factorWithParenthesesIfNegatedExplicitPlusOrFraction(t4) {
    var n2 = this.factor(t4);
    return n2.match(/^-/) || n2.match(/^\+/) || t4[0] === `/` ? `\\left(` + n2 + `\\right)` : n2;
  }
  termWithPlusIfNotNegated(t4) {
    var n2 = this.term(t4);
    return n2.match(/^\\pm /) ? n2 : n2.match(/^-/) ? n2.match(/^-[^ ]/) ? `- ` + n2.slice(1) : n2 : `+ ` + n2;
  }
}, operators = { "+,add": function(t4) {
  return [`+`].concat(t4);
}, "*,multiply": function(t4) {
  return [`*`].concat(t4);
}, "/,divide": function(t4) {
  return [`/`, t4[0], t4[1]];
}, "-,unaryMinus": function(t4) {
  return [`-`, t4[0]];
}, "-,subtract": function(t4) {
  return [`+`, t4[0], [`-`, t4[1]]];
}, "^,pow": function(t4) {
  return [`^`, t4[0], t4[1]];
}, "and,and": function(t4) {
  return [`and`].concat(t4);
}, "or,or": function(t4) {
  return [`or`].concat(t4);
}, "not,not": function(t4) {
  return [`not`, t4[0]];
}, "==,equal": function(t4) {
  return [`=`].concat(t4);
}, "<,smaller": function(t4) {
  return [`<`, t4[0], t4[1]];
}, ">,larger": function(t4) {
  return [`>`, t4[0], t4[1]];
}, "<=,smallerEq": function(t4) {
  return [`le`, t4[0], t4[1]];
}, ">=,largerEq": function(t4) {
  return [`ge`, t4[0], t4[1]];
}, "!=,unequal": function(t4) {
  return [`ne`, t4[0], t4[1]];
}, "!,factorial": function(t4) {
  return [`apply`, `factorial`, t4[0]];
} }, mathjsToAst$1 = class {
  convert(t4) {
    if (t4.isConstantNode) return t4.value;
    if (t4.isSymbolNode) return t4.name;
    if (t4.isOperatorNode) {
      var n2 = [t4.op, t4.fn].join(`,`);
      if (n2 in operators) return operators[n2](t4.args.map((function(t5, n3) {
        return this.convert(t5);
      }).bind(this)));
      throw Error(`Unsupported operator: ` + t4.op + `, ` + t4.fn);
    }
    if (t4.isFunctionNode) {
      var r2 = t4.args.map((function(t5, n3) {
        return this.convert(t5);
      }).bind(this));
      r2 = r2.length > 1 ? [`tuple`].concat(r2) : r2[0];
      var i2 = [`apply`, t4.name];
      return i2.push(r2), i2;
    }
    if (t4.isArrayNode) return [`vector`].concat(t4.args.map((function(t5, n3) {
      return this.convert(t5);
    }).bind(this)));
    if (t4.isParenthesisNode) return this.convert(t4.content);
    throw Error(`Unsupported node type: ` + t4.type);
  }
}, differentiation_exports = __exportAll({ derivative: () => derivative, derivativeStory: () => derivativeStory, derivative_story: () => derivative_story, derivative_with_story: () => derivative_with_story }), astToMathjs = new astToMathjs$2({ mathjs: mathjs_default }), mathjsToAst = new mathjsToAst$1(), textToAst$2 = new textToAst$4(), astToLatex = new astToLatex$1(), derivatives = { sin: textToAst$2.convert(`cos x`), cos: textToAst$2.convert(`-(sin x)`), tan: textToAst$2.convert(`(sec x)^2`), cot: textToAst$2.convert(`-((csc x)^2)`), sec: textToAst$2.convert(`(sec x)*(tan x)`), csc: textToAst$2.convert(`-(csc x)*(cot x)`), sqrt: textToAst$2.convert(`1/(2*sqrt(x))`), cbrt: textToAst$2.convert(`1/(3*x^(2/3))`), log: textToAst$2.convert(`1/x`), ln: textToAst$2.convert(`1/x`), exp: textToAst$2.convert(`exp(x)`), arcsin: textToAst$2.convert(`1/sqrt(1 - x^2)`), arccos: textToAst$2.convert(`-1/sqrt(1 - x^2)`), arctan: textToAst$2.convert(`1/(1 + x^2)`), arccsc: textToAst$2.convert(`-1/(sqrt(-1/x^2 + 1)*x^2)`), arcsec: textToAst$2.convert(`1/(sqrt(-1/x^2 + 1)*x^2)`), arccot: textToAst$2.convert(`-1/(1 + x^2)`), asin: textToAst$2.convert(`1/sqrt(1 - x^2)`), acos: textToAst$2.convert(`-1/sqrt(1 - x^2)`), atan: textToAst$2.convert(`1/(1 + x^2)`), acsc: textToAst$2.convert(`-1/(sqrt(-1/x^2 + 1)*x^2)`), asec: textToAst$2.convert(`1/(sqrt(-1/x^2 + 1)*x^2)`), acot: textToAst$2.convert(`-1/(1 + x^2)`), abs: textToAst$2.convert(`abs(x)/x`) };
function derivative_with_story(t4, n2, r2 = []) {
  var i2 = get_tree(t4), a2 = `\\frac{d}{d` + n2 + `} `;
  if (typeof i2 == `number` || variables(i2).indexOf(n2) < 0) return r2.push(`The derivative of a constant is zero, that is, \\(` + a2 + astToLatex.convert(i2) + ` = 0\\).`), 0;
  if (typeof i2 == `string`) return n2 === i2 ? (r2.push(`We know the derivative of the identity function is one, that is, \\(` + a2 + astToLatex.convert(i2) + ` = 1\\).`), 1) : (r2.push(`As far as \\(` + astToLatex.convert(n2) + `\\) is concerned, \\(` + astToLatex.convert(i2) + `\\) is constant, so ` + a2 + astToLatex.convert(i2) + ` = 0\\).`), 0);
  if (!Array.isArray(i2)) return null;
  var o2 = i2[0], s2 = i2.slice(1);
  if (o2 === `apply` && s2[0] === `nthroot`) if (s2[1].length !== 3 || s2[1][0] !== `tuple`) s2[0] = `sqrt`;
  else {
    let t5 = s2[1][2];
    o2 = `^`, s2 = [s2[1][1], [`/`, 1, t5]];
  }
  if (o2 === `+` || o2 === `-` || o2 === `~`) {
    r2.push(`Using the sum rule, \\(` + a2 + astToLatex.convert(i2) + ` = ` + s2.map(function(t6, n3) {
      return a2 + astToLatex.convert(t6);
    }).join(` + `) + `\\).`);
    let t5 = [o2].concat(s2.map(function(t6, i3) {
      return derivative_with_story(t6, n2, r2);
    }));
    return t5 = simplify(t5), r2.push(`So using the sum rule, \\(` + a2 + astToLatex.convert(i2) + ` = ` + astToLatex.convert(t5) + `\\).`), t5;
  }
  if (o2 === `*`) {
    let t5 = [], o3 = [];
    for (let r3 = 0; r3 < s2.length; r3++) typeof s2[r3] == `number` || variables(s2[r3]).indexOf(n2) < 0 ? o3.push(s2[r3]) : t5.push(s2[r3]);
    if (o3.length > 0) {
      if (t5.length === 0) return r2.push(`Since the derivative of a constant is zero, \\(` + a2 + astToLatex.convert(i2) + ` = 0.\\)`), 0;
      let s3 = [`*`].concat(t5);
      if (t5.length === 1 && (s3 = t5[0]), s3 === n2) {
        r2.push(`By the constant multiple rule, \\(` + a2 + astToLatex.convert(i2) + ` = ` + o3.map(function(t7, n3) {
          return astToLatex.convert(t7);
        }).join(` \\cdot `) + `\\).`);
        let t6 = [`*`].concat(o3);
        return t6 = simplify(t6), t6;
      }
      r2.push(`By the constant multiple rule, \\(` + a2 + astToLatex.convert(i2) + ` = ` + o3.map(function(t6, n3) {
        return astToLatex.convert(t6);
      }).join(` \\cdot `) + ` \\cdot ` + a2 + `\\left(` + astToLatex.convert(s3) + `\\right)\\).`);
      let c3 = derivative_with_story(s3, n2, r2), l3 = [`*`].concat(o3.concat([c3]));
      return l3 = simplify(l3), r2.push(`And so \\(` + a2 + astToLatex.convert(i2) + ` = ` + astToLatex.convert(l3) + `\\).`), l3;
    }
    r2.push(`Using the product rule, \\(` + a2 + astToLatex.convert(i2) + ` = ` + s2.map(function(t6, n3) {
      return s2.map(function(r3, i3) {
        return n3 === i3 ? a2 + `\\left(` + astToLatex.convert(t6) + `\\right)` : astToLatex.convert(r3);
      }).join(` \\cdot `);
    }).join(` + `) + `\\).`);
    let c2 = s2.slice(), l2 = [`+`].concat(s2.map(function(t6, i3) {
      return [`*`].concat(c2.map(function(t7, a3) {
        if (i3 === a3) {
          let i4 = derivative_with_story(t7, n2, r2);
          return i4 === 1 ? null : i4;
        } else return t7;
      }).filter(function(t7) {
        return t7 != null;
      }));
    }));
    return l2 = simplify(l2), r2.push(`So using the product rule, \\(` + a2 + astToLatex.convert(i2) + ` = ` + astToLatex.convert(l2) + `\\).`), l2;
  }
  if (o2 === `/`) {
    let t5 = s2[0], o3 = s2[1];
    if (variables(o3).indexOf(n2) < 0) {
      r2.push(`By the constant multiple rule, \\(` + a2 + astToLatex.convert(i2) + ` = ` + astToLatex.convert([`/`, 1, o3]) + ` \\cdot ` + a2 + `\\left(` + astToLatex.convert(t5) + `\\right)\\).`);
      let s3 = derivative_with_story(t5, n2, r2), c3 = substitute$1(textToAst$2.convert(`(1/g)*d`), { d: s3, g: o3 });
      return c3 = simplify(c3), r2.push(`So \\(` + a2 + astToLatex.convert(i2) + ` = ` + astToLatex.convert(c3) + `\\).`), c3;
    }
    if (variables(t5).indexOf(n2) < 0) {
      t5 !== 1 && r2.push(`By the constant multiple rule, \\(` + a2 + astToLatex.convert(i2) + ` = ` + astToLatex.convert(t5) + ` \\cdot ` + a2 + `\\left(` + astToLatex.convert([`/`, 1, o3]) + `\\right)\\).`), r2.push(`Since \\(\\frac{d}{du} \\frac{1}{u}\\) is \\(\\frac{-1}{u^2}\\), the chain rule gives \\(` + a2 + astToLatex.convert(i2) + ` = ` + astToLatex.convert(t5) + `\\cdot \\frac{-1}{ ` + astToLatex.convert(o3) + `^2} \\cdot ` + a2 + astToLatex.convert(o3) + `\\).`);
      let s3 = derivative_with_story(o3, n2, r2), c3 = substitute$1(textToAst$2.convert(`f * (-a/(g^2))`), { f: t5, a: s3, g: o3 });
      return c3 = simplify(c3), r2.push(`So since \\(\\frac{d}{du} \\frac{1}{u}\\) is \\(\\frac{-1}{u^2}\\), the chain rule gives \\(` + a2 + astToLatex.convert(i2) + ` = ` + astToLatex.convert(c3) + `\\).`), c3;
    }
    r2.push(`Using the quotient rule, \\(` + a2 + astToLatex.convert(i2) + ` = \\frac{` + a2 + `\\left(` + astToLatex.convert(t5) + `\\right) \\cdot ` + astToLatex.convert(o3) + ` - ` + astToLatex.convert(t5) + `\\cdot ` + a2 + `\\left(` + astToLatex.convert(o3) + `\\right)}{ \\left( ` + astToLatex.convert(o3) + ` \\right)^2} \\).`);
    let c2 = derivative_with_story(t5, n2, r2), l2 = derivative_with_story(o3, n2, r2), u2 = substitute$1(textToAst$2.convert(`(a * g - f * b)/(g^2)`), { a: c2, b: l2, f: t5, g: o3 });
    return u2 = simplify(u2), r2.push(`So using the quotient rule, \\(` + a2 + astToLatex.convert(i2) + ` = ` + astToLatex.convert(u2) + `\\).`), u2;
  }
  if (o2 === `^`) {
    let t5 = s2[0], o3 = s2[1];
    if (variables(o3).indexOf(n2) < 0) {
      if (typeof t5 == `string` && t5 === `x`) {
        if (typeof o3 == `number`) {
          let n4 = substitute$1(textToAst$2.convert(`n * (f^m)`), { n: o3, m: o3 - 1, f: t5 });
          return n4 = simplify(n4), r2.push(`By the power rule, \\(` + a2 + astToLatex.convert(i2) + ` = ` + astToLatex.convert(o3) + ` \\cdot \\left(` + astToLatex.convert(t5) + `\\right)^{` + astToLatex.convert([`-`, o3, 1]) + `}\\).`), n4;
        }
        let n3 = substitute$1(textToAst$2.convert(`n * (f^(n-1))`), { n: o3, f: t5 });
        return n3 = simplify(n3), r2.push(`By the power rule, \\(` + a2 + astToLatex.convert(i2) + ` = ` + astToLatex.convert(o3) + ` \\cdot \\left(` + astToLatex.convert(t5) + `\\right)^{` + astToLatex.convert([`-`, o3, 1]) + `}\\).`), n3;
      }
      o3 !== 1 && r2.push(`By the power rule and the chain rule, \\(` + a2 + astToLatex.convert(i2) + ` = ` + astToLatex.convert(o3) + ` \\cdot \\left(` + astToLatex.convert(t5) + `\\right)^{` + astToLatex.convert([`-`, o3, 1]) + `} \\cdot ` + a2 + astToLatex.convert(t5) + `\\).`);
      let s3 = derivative_with_story(t5, n2, r2);
      if (o3 === 1) return s3;
      if (typeof o3 == `number`) {
        let n3 = substitute$1(textToAst$2.convert(`n * (f^m) * a`), { n: o3, m: o3 - 1, f: t5, a: s3 });
        return n3 = simplify(n3), r2.push(`So by the power rule and the chain rule, \\(` + a2 + astToLatex.convert(i2) + ` = ` + astToLatex.convert(n3) + `\\).`), n3;
      }
      let c3 = substitute$1(textToAst$2.convert(`n * (f^(n-1)) * a`), { n: o3, f: t5, a: s3 });
      return c3 = simplify(c3), r2.push(`So by the power rule and the chain rule, \\(` + a2 + astToLatex.convert(i2) + ` = ` + astToLatex.convert(c3) + `\\).`), c3;
    }
    if (t5 === `e` && mathjs_default.define_e) {
      if (typeof o3 == `string` && o3 === n2) {
        let t7 = substitute$1(textToAst$2.convert(`e^(f)`), { f: o3 });
        return t7 = simplify(t7), r2.push(`The derivative of \\(e^` + astToLatex.convert(n2) + `\\) is itself, that is, \\(` + a2 + astToLatex.convert(i2) + ` = ` + astToLatex.convert(i2) + `\\).`), t7;
      }
      r2.push(`Using the rule for \\(e^x\\) and the chain rule, we know \\(` + a2 + astToLatex.convert(i2) + ` = ` + astToLatex.convert(i2) + ` \\cdot ` + a2 + astToLatex.convert(o3) + `\\).`);
      let t6 = substitute$1(textToAst$2.convert(`e^(f)*d`), { f: o3, d: derivative_with_story(o3, n2, r2) });
      return t6 = simplify(t6), r2.push(`So using the rule for \\(e^x\\) and the chain rule, \\(` + a2 + astToLatex.convert(i2) + ` = ` + astToLatex.convert(t6) + `\\).`), t6;
    }
    if (typeof t5 == `number`) {
      if (typeof o3 == `string` && o3 === n2) {
        let s4 = substitute$1(textToAst$2.convert(`a^(f) * log(a)`), { a: t5, f: o3 });
        return s4 = simplify(s4), r2.push(`The derivative of \\(a^` + astToLatex.convert(n2) + `\\) is \\(a^{` + astToLatex.convert(n2) + `} \\, \\log a\\), that is, \\(` + a2 + astToLatex.convert(i2) + ` = ` + astToLatex.convert(s4) + `\\).`), s4;
      }
      let s3 = substitute$1(textToAst$2.convert(`a^(f) * log(a)`), { a: t5, f: o3 });
      r2.push(`Using the rule for \\(a^x\\) and the chain rule, we know \\(` + a2 + astToLatex.convert(i2) + ` = ` + astToLatex.convert(s3) + ` \\cdot ` + a2 + astToLatex.convert(o3) + `\\).`);
      let c3 = substitute$1(textToAst$2.convert(`a^(b)*log(a)*d`), { a: t5, b: o3, d: derivative_with_story(o3, n2, r2) });
      return c3 = simplify(c3), r2.push(`So using the rule for \\(a^x\\) and the chain rule, \\(` + a2 + astToLatex.convert(i2) + ` = ` + astToLatex.convert(c3) + `\\).`), c3;
    }
    let c2 = t5, l2 = o3;
    r2.push(`Recall the general rule for exponents, namely that \\(\\frac{d}{dx} u(x)^{v(x)} = u(x)^{v(x)} \\cdot \\left( v'(x) \\cdot \\log u(x) + \\frac{v(x) \\cdot u'(x)}{u(x)} \\right)\\).  In this case, \\(u(x) = ` + astToLatex.convert(c2) + `\\) and \\(v(x) = ` + astToLatex.convert(l2) + `\\).`);
    let u2 = derivative_with_story(c2, n2, r2), d2 = derivative_with_story(l2, n2, r2), p2 = substitute$1(textToAst$2.convert(`(f^g)*(b * log(f) + (g * a)/f)`), { a: u2, b: d2, f: c2, g: l2 });
    return p2 = simplify(p2), r2.push(`So by the general rule for exponents, \\(` + a2 + astToLatex.convert(i2) + ` = ` + astToLatex.convert(p2) + `\\).`), p2;
  }
  if (o2 === `apply` && !(s2[0] in derivatives)) {
    let t5 = s2[1];
    r2.push(`By the chain rule, \\(` + a2 + astToLatex.convert(i2) + ` = ` + astToLatex.convert(substitute$1([`apply`, s2[0] + `'`, `x`], { x: t5 })) + ` \\cdot ` + a2 + astToLatex.convert(t5) + `\\).`);
    let o3 = [`*`, substitute$1([`apply`, s2[0] + `'`, `x`], { x: t5 }), derivative_with_story(t5, n2, r2)];
    return o3 = simplify(o3), r2.push(`So by the chain rule, \\(` + a2 + astToLatex.convert(i2) + ` = ` + astToLatex.convert(o3) + `\\).`), o3;
  }
  if (o2 === `apply` && s2[0] in derivatives || o2 in derivatives) {
    let t5 = false;
    o2 === `apply` && (o2 = s2[0], s2 = s2.slice(1), t5 = true);
    let c2 = s2[0];
    if (typeof c2 == `number`) {
      return r2.push(`The derivative of a constant is zero so \\(` + a2 + astToLatex.convert(i2) + ` = ` + astToLatex.convert(0) + `\\).`), 0;
    } else if (typeof c2 == `string` && c2 === n2) {
      let t6 = [`*`, substitute$1(derivatives[o2], { x: c2 })];
      return t6 = simplify(t6), r2.push(`It is the case that \\(` + a2 + astToLatex.convert(i2) + ` = ` + astToLatex.convert(t6) + `\\).`), t6;
    } else if (typeof c2 == `string` && c2 !== n2) {
      return r2.push(`Since the derivative of a constant is zero, \\(` + a2 + astToLatex.convert(i2) + ` = ` + astToLatex.convert(0) + `\\).`), 0;
    } else {
      let s3 = [o2, `u`];
      t5 && (s3 = [`apply`].concat(s3)), r2.push(`Recall \\(\\frac{d}{du}` + astToLatex.convert(s3) + ` = ` + astToLatex.convert(derivative_with_story(s3, `u`, [])) + `\\).`), r2.push(`By the chain rule, \\(` + a2 + astToLatex.convert(i2) + ` = ` + astToLatex.convert(substitute$1(derivatives[o2], { x: c2 })) + ` \\cdot ` + a2 + astToLatex.convert(c2) + `\\).`);
      let l2 = [`*`, substitute$1(derivatives[o2], { x: c2 }), derivative_with_story(c2, n2, r2)];
      return l2 = simplify(l2), r2.push(`So by the chain rule, \\(` + a2 + astToLatex.convert(i2) + ` = ` + astToLatex.convert(l2) + `\\).`), l2;
    }
  }
  return 0;
}
function lowercaseFirstLetter(t4) {
  return t4.charAt(0).toLowerCase() + t4.slice(1);
}
function simplify_story(t4) {
  for (let n2 = t4.length - 1; n2 >= 1; n2--) t4[n2] === t4[n2 - 1] && t4.splice(n2, 1);
  for (let n2 = 0; n2 < t4.length; n2++) for (let r2 = n2 + 1; r2 < t4.length; r2++) t4[n2] === t4[r2] && (t4[r2] = `Again, ` + lowercaseFirstLetter(t4[r2]));
  return t4;
}
function derivative_story(t4, n2) {
  var r2 = [];
  return derivative_with_story(t4, n2, r2), r2 = simplify_story(r2), r2;
}
var derivativeStory = derivative_story;
function derivative(t4, n2) {
  var r2 = get_tree(t4);
  let i2 = astToMathjs.convert(log_subscript_to_two_arg_log(normalize_function_names(normalize_applied_functions(r2)))), a2 = mathjs_default.derivative(i2, n2);
  return mathjsToAst.convert(a2);
}
function pm_equals_numerical({ expr: t4, other: n2, randomBindings: r2, expr_context: i2, other_context: a2, relative_tolerance: o2, absolute_tolerance: s2, tolerance_for_zero: c2, allowed_error_in_numbers: l2 = 0, include_error_in_number_exponents: u2 = false, allowed_error_is_absolute: d2 = false, rng: p2 }) {
  let m2, h2, g2, v2, y2;
  try {
    ({ variables: m2, integer_variables: h2, functions: g2, expr: v2, other: y2 } = prepare_exprs_numerical_eval(t4, n2, i2, a2));
  } catch {
    return false;
  }
  let b2, x2;
  try {
    b2 = expand_pm_signs(v2.tree), x2 = expand_pm_signs(y2.tree);
  } catch {
    return false;
  }
  let S2, w2;
  try {
    S2 = b2.map((t5) => i2.fromAst(t5).f()), w2 = x2.map((t5) => a2.fromAst(t5).f());
  } catch {
    return false;
  }
  let E, D;
  if (l2 > 0 && b2.length > 0) try {
    let t5 = replace_numbers_with_parameters({ expr: v2, variables: m2, include_exponents: u2 });
    E = t5.parameters;
    let n3 = Object.keys(E);
    if (n3.length > 0) {
      let r3;
      try {
        r3 = expand_pm_signs(t5.expr_with_params);
      } catch {
        r3 = null;
      }
      r3 && r3.length === b2.length && (D = r3.map((t6) => {
        try {
          let r4 = i2.fromAst(t6), a3 = r4.derivative(n3[0]);
          d2 || (a3 = a3.multiply(E[n3[0]]));
          for (let t7 of n3.slice(1)) {
            let n4 = r4.derivative(t7);
            d2 || (n4 = n4.multiply(E[t7])), a3 = a3.add(n4);
          }
          return a3.multiply(l2).f();
        } catch {
          return;
        }
      }));
    }
  } catch {
  }
  let M2 = Number.MAX_VALUE * 1e-20, N2 = [10, 1, 100, 0.1, 1e3, 0.01], F = 0;
  for (let t5 = 0; t5 < 1e3; t5++) {
    let n3 = N2[Math.floor(t5 / 20) % N2.length], i3;
    try {
      i3 = r2(p2, m2, n3);
    } catch {
      continue;
    }
    for (let t6 = 0; t6 < h2.length; t6++) i3[h2[t6]] = generate_random_integer(-10, 10, p2);
    for (let t6 = 0; t6 < g2.length; t6++) i3[g2[t6]] = random_cubic(p2);
    let a3;
    if (D) {
      let t6 = Object.assign({}, i3, E);
      a3 = Array(D.length).fill(0);
      let n4 = false;
      for (let r3 = 0; r3 < D.length; r3++) {
        let i4 = D[r3];
        if (!i4) continue;
        let o3;
        try {
          o3 = mathjs_default.abs(i4(t6));
        } catch {
          n4 = true;
          break;
        }
        if (!Number.isFinite(o3)) {
          n4 = true;
          break;
        }
        a3[r3] = o3;
      }
      if (n4) continue;
    }
    let l3, u3;
    try {
      l3 = S2.map((t6) => t6(i3)), u3 = w2.map((t6) => t6(i3));
    } catch {
      continue;
    }
    if (!(!l3.every((t6) => typeof t6 != `boolean` && Number.isFinite(mathjs_default.abs(t6)) && mathjs_default.abs(t6) < M2) || !u3.every((t6) => typeof t6 != `boolean` && Number.isFinite(mathjs_default.abs(t6)) && mathjs_default.abs(t6) < M2))) {
      if (!value_multisets_match(l3, u3, o2, s2, c2, a3)) return false;
      if (F += 1, F >= 10) return true;
    }
  }
  return false;
}
function value_multisets_match(t4, n2, r2, i2, a2, o2) {
  let s2 = (t5) => o2 && o2[t5] !== void 0 ? o2[t5] : 0, c2 = (o3, c3) => values_close(t4[o3], n2[c3], r2, i2, a2, s2(o3));
  if (t4.length !== n2.length) return t4.every((t5, r3) => n2.some((t6, n3) => c2(r3, n3))) && n2.every((n3, r3) => t4.some((t5, n4) => c2(n4, r3)));
  let l2 = t4.length, u2 = Array(l2).fill(-1), d2 = (t5, n3) => {
    for (let r3 = 0; r3 < l2; r3++) if (!(n3[r3] || !c2(t5, r3)) && (n3[r3] = true, u2[r3] === -1 || d2(u2[r3], n3))) return u2[r3] = t5, true;
    return false;
  };
  for (let t5 = 0; t5 < l2; t5++) if (!d2(t5, Array(l2).fill(false))) return false;
  return true;
}
function values_close(t4, n2, r2, i2, a2, o2 = 0) {
  let s2 = mathjs_default.abs(t4), c2 = mathjs_default.abs(n2), l2 = Math.min(s2, c2), u2 = Math.max(s2, c2), d2 = o2;
  return d2 += l2 * r2, d2 = Math.min(d2, 0.1 * l2), d2 === 0 && (t4 === 0 || n2 === 0) ? d2 += a2 : d2 += i2, u2 === 0 || mathjs_default.abs(mathjs_default.subtract(t4, n2)) < d2;
}
var astToText = new astToText$2();
function generate_random_integer(t4, n2, r2) {
  return t4 = mathjs_default.ceil(t4), n2 = mathjs_default.floor(n2), mathjs_default.floor(r2() * (n2 - t4 + 1)) + t4;
}
function random_cubic(t4) {
  let n2 = (t4() - 0.5) * 1, r2 = (t4() - 0.5) * 2, i2 = (t4() - 0.5) * 6, a2 = (t4() - 0.5) * 24;
  return function(t5) {
    return mathjs_default.add(mathjs_default.multiply(mathjs_default.add(mathjs_default.multiply(mathjs_default.add(mathjs_default.multiply(n2, t5), r2), t5), i2), t5), a2);
  };
}
var equals$6 = function({ expr: t4, other: n2, randomBindings: r2, expr_context: i2, other_context: a2, relative_tolerance: o2 = 1e-12, absolute_tolerance: s2 = 0, tolerance_for_zero: c2 = 1e-15, allowed_error_in_numbers: l2 = 0, include_error_in_number_exponents: u2 = false, allowed_error_is_absolute: d2 = false, coerce_tuples_arrays: p2 = true, coerce_vectors: m2 = true, rng: h2 }) {
  if (i2 ||= t4.context, a2 ||= n2.context, Array.isArray(t4.tree) && Array.isArray(n2.tree)) {
    let g2 = t4.tree[0], v2 = t4.tree.slice(1), y2 = n2.tree[0], b2 = n2.tree.slice(1);
    if (g2 === `tuple` || g2 === `vector` || g2 === `altvector` || g2 === `list` || g2 === `array` || g2 === `matrix` || g2 === `interval`) {
      if (y2 !== g2 && ({ operator1: g2, operator2: y2, operands1: v2, operands2: b2 } = coerce_tuple_array_vectors({ operator1: g2, operator2: y2, operands1: v2, operands2: b2, coerce_tuples_arrays: p2, coerce_vectors: m2 })), y2 !== g2 || b2.length !== v2.length) return false;
      for (let t5 = 0; t5 < v2.length; t5++) if (!equals$6({ expr: i2.fromAst(v2[t5]), other: a2.fromAst(b2[t5]), randomBindings: r2, expr_context: i2, other_context: a2, relative_tolerance: o2, absolute_tolerance: s2, tolerance_for_zero: c2, allowed_error_in_numbers: l2, include_error_in_number_exponents: u2, allowed_error_is_absolute: d2, coerce_tuples_arrays: p2, coerce_vectors: m2, rng: h2 })) return false;
      return true;
    }
    if (v2.length === 2 && [`=`, `>`, `<`, `ge`, `le`].includes(g2)) {
      if (b2.length !== 2 || (g2 === `>` ? (g2 = `<`, v2 = [v2[1], v2[0]]) : g2 === `ge` && (g2 = `le`, v2 = [v2[1], v2[0]]), y2 === `>` ? (y2 = `<`, b2 = [b2[1], b2[0]]) : y2 === `ge` && (y2 = `le`, b2 = [b2[1], b2[0]]), g2 !== y2)) return false;
      let t5 = [`+`, v2[0], [`-`, v2[1]]], n3 = [`+`, b2[0], [`-`, b2[1]]], x2 = g2 !== `=`;
      if (contains_pm(t5) || contains_pm(n3)) {
        if (g2 === `=`) {
          let g3 = (t6, n4) => {
            let r3 = expand_pm_signs(t6), i3 = [];
            for (let t7 of r3) {
              let r4 = n4.fromAst(t7).simplify().tree;
              i3.some((t8) => equal(t8, r4)) || i3.push(r4);
            }
            return i3.length === 1 ? i3[0] : [`*`, ...i3];
          }, v3, y3;
          try {
            v3 = g3(t5, i2), y3 = g3(n3, a2);
          } catch {
            return false;
          }
          return component_equals$1({ expr: i2.fromAst(v3), other: a2.fromAst(y3), randomBindings: r2, expr_context: i2, other_context: a2, allow_proportional: true, require_positive_proportion: false, relative_tolerance: o2, absolute_tolerance: s2, tolerance_for_zero: c2, allowed_error_in_numbers: l2, include_error_in_number_exponents: u2, allowed_error_is_absolute: d2, coerce_tuples_arrays: p2, coerce_vectors: m2, rng: h2 });
        }
        return pm_equals_numerical({ expr: i2.fromAst(t5), other: a2.fromAst(n3), randomBindings: r2, expr_context: i2, other_context: a2, relative_tolerance: o2, absolute_tolerance: s2, tolerance_for_zero: c2, allowed_error_in_numbers: l2, include_error_in_number_exponents: u2, allowed_error_is_absolute: d2, rng: h2 });
      }
      return component_equals$1({ expr: i2.fromAst(t5), other: a2.fromAst(n3), randomBindings: r2, expr_context: i2, other_context: a2, allow_proportional: true, require_positive_proportion: x2, relative_tolerance: o2, absolute_tolerance: s2, tolerance_for_zero: c2, allowed_error_in_numbers: l2, include_error_in_number_exponents: u2, allowed_error_is_absolute: d2, coerce_tuples_arrays: p2, coerce_vectors: m2, rng: h2 });
    }
  }
  return contains_pm(t4.tree) || contains_pm(n2.tree) ? pm_equals_numerical({ expr: t4, other: n2, randomBindings: r2, expr_context: i2, other_context: a2, relative_tolerance: o2, absolute_tolerance: s2, tolerance_for_zero: c2, allowed_error_in_numbers: l2, include_error_in_number_exponents: u2, allowed_error_is_absolute: d2, rng: h2 }) : component_equals$1({ expr: t4, other: n2, randomBindings: r2, expr_context: i2, other_context: a2, relative_tolerance: o2, absolute_tolerance: s2, tolerance_for_zero: c2, allowed_error_in_numbers: l2, include_error_in_number_exponents: u2, allowed_error_is_absolute: d2, coerce_tuples_arrays: p2, coerce_vectors: m2, rng: h2 });
}, component_equals$1 = function({ expr: t4, other: n2, randomBindings: r2, expr_context: i2, other_context: a2, allow_proportional: o2 = false, require_positive_proportion: s2 = false, relative_tolerance: c2, absolute_tolerance: l2, tolerance_for_zero: u2, allowed_error_in_numbers: d2, include_error_in_number_exponents: p2, allowed_error_is_absolute: m2, coerce_tuples_arrays: h2, coerce_vectors: g2, rng: v2 }) {
  var y2 = Number.MAX_VALUE * 1e-20, b2 = 0, x2 = 10, S2 = 100, w2, E, D;
  ({ variables: w2, integer_variables: E, functions: D, expr: t4, other: n2 } = prepare_exprs_numerical_eval(t4, n2, i2, a2));
  try {
    var O = t4.f(), j2 = n2.f();
  } catch {
    if (t4.equalsViaSyntax(n2, { allow_blanks: true, allowed_error_in_numbers: d2, include_error_in_number_exponents: p2, allowed_error_is_absolute: m2, coerce_tuples_arrays: h2, coerce_vectors: g2 })) return true;
    if (!(Array.isArray(t4.tree) && Array.isArray(n2.tree)) || t4.tree[0] !== n2.tree[0]) return false;
    let o3 = t4.tree.slice(1), s3 = n2.tree.slice(1);
    for (let t5 = 0; t5 < o3.length; t5++) if (!equals$6({ expr: i2.fromAst(o3[t5]), other: a2.fromAst(s3[t5]), randomBindings: r2, expr_context: i2, other_context: a2, relative_tolerance: c2, absolute_tolerance: l2, tolerance_for_zero: u2, allowed_error_in_numbers: d2, include_error_in_number_exponents: p2, allowed_error_is_absolute: m2, coerce_tuples_arrays: h2, coerce_vectors: g2, rng: v2 })) return false;
    return true;
  }
  let M2, N2, F;
  if (d2 > 0) {
    let n3 = replace_numbers_with_parameters({ expr: t4, variables: w2, include_exponents: p2 });
    M2 = i2.fromAst(n3.expr_with_params), N2 = n3.parameters;
    let r3 = Object.keys(N2);
    if (r3.length > 0) {
      let t5 = M2.derivative(r3[0]);
      if (m2 || (t5 = t5.multiply(N2[r3[0]])), r3.length > 1) for (let n5 of r3.slice(1)) {
        let r4 = M2.derivative(n5);
        m2 || (r4 = r4.multiply(N2[n5])), t5 = t5.add(r4);
      }
      let n4 = t5.multiply(d2);
      try {
        F = n4.f();
      } catch {
      }
    }
  }
  var I = 1, L = [10, 1, 100, 0.1, 1e3, 0.01], R = 0;
  let z = 0, V = true, H2 = 0;
  for (let t5 = 0; t5 < 10 * S2; t5++) {
    try {
      var U = W(L[R], v2);
    } catch {
      continue;
    }
    if (U.always_zero === false && (V = false), !U.equal && !U.out_of_bounds && !U.always_zero && U.sufficient_finite_values !== false && (H2++, H2 > S2)) return false;
    if (U.equal) if (U.always_zero) {
      if (!V) continue;
      if (z += 1, z > 5 && (R += 1, z = 0), R >= L.length) return true;
      continue;
    } else return true;
  }
  return false;
  function W(t5, n3) {
    var i3 = r2(n3, w2, t5);
    for (let t6 = 0; t6 < E.length; t6++) i3[E[t6]] = generate_random_integer(-10, 10, n3);
    for (let t6 = 0; t6 < D.length; t6++) i3[D[t6]] = random_cubic(n3);
    var a3;
    F && (a3 = Object.assign({}, i3, N2));
    var d3 = O(i3), p3 = j2(i3);
    if (typeof d3 == `boolean` || typeof p3 == `boolean`) return { out_of_bounds: true, always_zero: false };
    var m3 = mathjs_default.abs(d3), h3 = mathjs_default.abs(p3);
    if (!(m3 < y2 && h3 < y2) || !((m3 === 0 || m3 > b2) && (h3 === 0 || h3 > b2))) return { out_of_bounds: true, always_zero: false };
    var g3 = Math.min(m3, h3), v3 = Math.max(m3, h3), S3 = 1;
    let M3 = 0;
    if (F) {
      try {
        M3 = mathjs_default.abs(F(a3));
      } catch {
        return { equal_at_start: false, always_zero: false };
      }
      if (!Number.isFinite(M3)) return { equal_at_start: false, always_zero: false };
    }
    if (M3 += g3 * c2, M3 = Math.min(M3, 0.1 * g3), M3 === 0 && (d3 === 0 || p3 === 0) ? M3 += u2 : M3 += l2, !(v3 === 0 || mathjs_default.abs(mathjs_default.subtract(d3, p3)) < M3) && (!o2 || m3 === 0 || h3 === 0 || (S3 = mathjs_default.divide(d3, p3), s2 && !(S3 > 0)))) return { equal_at_start: false, always_zero: false };
    var L2 = v3 === 0, R2 = 0;
    for (let t6 = 0; t6 < 100; t6++) {
      var z2 = r2(n3, w2, I / 100, i3);
      for (let t7 = 0; t7 < E.length; t7++) z2[E[t7]] = generate_random_integer(-10, 10, n3);
      for (let t7 = 0; t7 < D.length; t7++) z2[D[t7]] = random_cubic(n3);
      var V2;
      F && (V2 = Object.assign({}, z2, N2));
      try {
        d3 = O(z2), p3 = mathjs_default.multiply(j2(z2), S3);
      } catch {
        continue;
      }
      if (m3 = mathjs_default.abs(d3), h3 = mathjs_default.abs(p3), m3 < y2 && h3 < y2) {
        g3 = Math.min(m3, h3), v3 = Math.max(m3, h3), R2++;
        let t7 = 0;
        if (F) {
          try {
            t7 = mathjs_default.abs(F(V2));
          } catch {
            continue;
          }
          if (!Number.isFinite(t7)) continue;
        }
        if (t7 += g3 * c2, t7 = Math.min(t7, 0.1 * g3), t7 === 0 && (d3 === 0 || p3 === 0) ? t7 += u2 : t7 += l2, !(v3 === 0 || mathjs_default.abs(mathjs_default.subtract(d3, p3)) < t7)) return { equality_in_middle: false, always_zero: false };
        if (L2 &&= v3 === 0, R2 >= x2) return { equal: true, always_zero: L2 };
      }
    }
    return { sufficient_finite_values: false, always_zero: L2 };
  }
};
function prepare_exprs_numerical_eval(t4, n2, r2, i2) {
  r2 ||= t4.context, i2 ||= n2.context, { expr: t4, other: n2 } = normalize_for_equality_checking(t4, n2, r2, i2);
  var a2 = [.../* @__PURE__ */ new Set([...t4.variables(), ...n2.variables()])];
  mathjs_default.define_pi && (a2 = a2.filter(function(t5) {
    return t5 !== `pi`;
  })), mathjs_default.define_i && (a2 = a2.filter(function(t5) {
    return t5 !== `i`;
  })), mathjs_default.define_e && (a2 = a2.filter(function(t5) {
    return t5 !== `e`;
  }));
  var o2 = [];
  for (let t5 = 0; t5 < a2.length; t5++) (is_integer_ast(a2[t5], r2.assumptions) || is_integer_ast(a2[t5], i2.assumptions)) && o2.push(a2[t5]);
  var s2 = [.../* @__PURE__ */ new Set([...t4.functions(), ...n2.functions()])].filter((t5) => t5.length === 1 || t5.includes(`_`) || t5.includes(`^`)), c2 = s2.filter((t5) => a2.includes(t5));
  if (s2 = s2.filter((t5) => !a2.includes(t5)), c2.length > 0) {
    let o3 = t4.tree, l2 = n2.tree, u2 = `x1`;
    for (; [...s2, ...a2].includes(u2); ) u2 += `x1`;
    let d2 = `x2`;
    for (; [...s2, ...a2].includes(d2); ) d2 += `x2`;
    for (let t5 of c2) {
      let n3 = t5 + t5;
      for (; [...s2, ...a2].includes(n3); ) n3 += t5;
      let r3 = [[[`apply`, t5, u2], [`apply`, n3, u2], { variables: { [u2]: true } }]];
      o3 = applyAllTransformations(o3, r3), l2 = applyAllTransformations(l2, r3), s2.push(n3);
    }
    t4 = r2.fromAst(o3), n2 = i2.fromAst(l2);
  }
  return { variables: a2, integer_variables: o2, functions: s2, expr: t4, other: n2 };
}
function normalize_for_equality_checking(t4, n2, r2, i2) {
  r2 ||= t4.context, i2 ||= n2.context, t4 = t4.normalize_function_names(), n2 = n2.normalize_function_names(), t4 = t4.log_subscript_to_two_arg_log(), n2 = n2.log_subscript_to_two_arg_log(), t4 = t4.subscripts_to_strings(), n2 = n2.subscripts_to_strings(), t4 = t4.normalize_applied_functions(), n2 = n2.normalize_applied_functions();
  let a2 = rename_functions_with_exponents(t4.tree), o2 = rename_functions_with_exponents(n2.tree);
  return t4 = r2.fromAst(rename_vecs(a2)), n2 = i2.fromAst(rename_vecs(o2)), { expr: t4, other: n2 };
}
function replace_numbers_with_parameters({ expr: t4, variables: n2, include_exponents: r2 = false }) {
  let i2 = {}, a2 = 0;
  function o2() {
    a2++;
    let t5 = `par` + a2;
    for (; n2.includes(t5); ) a2++, t5 = `par` + a2;
    return t5;
  }
  function s2(t5) {
    if (typeof t5 == `number`) {
      if (t5 === 0) return t5;
      {
        let n4 = o2();
        return i2[n4] = t5, n4;
      }
    }
    if (typeof t5 == `string`) {
      if (t5 === `pi`) {
        if (mathjs_default.define_pi) {
          let t6 = o2();
          return i2[t6] = mathjs_default.PI, t6;
        }
      } else if (t5 === `e` && mathjs_default.define_e) {
        let t6 = o2();
        return i2[t6] = mathjs_default.e, t6;
      }
      return t5;
    }
    if (!Array.isArray(t5)) return t5;
    let n3 = t5[0], a3 = t5.slice(1);
    return n3 === `^` && !r2 ? [n3, s2(a3[0]), a3[1]] : [n3, ...a3.map(s2)];
  }
  return t4 = t4.evaluate_numbers({ max_digits: 1 / 0 }), { expr_with_params: s2(t4.tree), parameters: i2 };
}
function rename_functions_with_exponents(t4) {
  if (!Array.isArray(t4)) return t4;
  let n2 = t4[0], r2 = t4.slice(1);
  if (n2 === `apply`) {
    let t5 = r2[0];
    return Array.isArray(t5) && t5[0] === `^` && t5.slice(1).every((t6) => [`number`, `string`].includes(typeof t6)) && (t5 = astToText.convert(t5)), [`apply`, t5, r2[1]];
  }
  return [n2, ...r2.map(rename_functions_with_exponents)];
}
function rename_vecs(t4) {
  if (!Array.isArray(t4)) return t4;
  let n2 = t4[0], r2 = t4.slice(1);
  return n2 === `vec` && r2.length === 1 ? `__vec_` + astToText.convert(r2[0]) : [n2, ...r2.map(rename_vecs)];
}
function randomComplexBindings(t4, n2, r2, i2) {
  var a2 = {};
  return i2 === void 0 ? n2.forEach(function(n3) {
    a2[n3] = mathjs_default.complex(t4() * 2 * r2 - r2, t4() * 2 * r2 - r2);
  }) : n2.forEach(function(n3) {
    a2[n3] = mathjs_default.complex(i2[n3].re + t4() * 2 * r2 - r2, i2[n3].im + t4() * 2 * r2 - r2);
  }), a2;
}
var equals$5 = function(t4, n2, { relative_tolerance: r2 = 1e-12, absolute_tolerance: i2 = 0, tolerance_for_zero: a2 = 1e-15, allowed_error_in_numbers: o2 = 0, include_error_in_number_exponents: s2 = false, allowed_error_is_absolute: c2 = false, allow_blanks: l2 = false, coerce_tuples_arrays: u2 = true, coerce_vectors: d2 = true } = {}) {
  if (!l2 && (t4.variables().includes(`＿`) || n2.variables().includes(`＿`))) return false;
  let p2 = (0, import_seedrandom.default)(`complex_seed`);
  return t4 = t4.remove_scaling_units(), n2 = n2.remove_scaling_units(), equals$6({ expr: t4, other: n2, randomBindings: randomComplexBindings, expr_context: t4.context, other_context: n2.context, relative_tolerance: r2, absolute_tolerance: i2, tolerance_for_zero: a2, allowed_error_in_numbers: o2, include_error_in_number_exponents: s2, allowed_error_is_absolute: c2, coerce_tuples_arrays: u2, coerce_vectors: d2, rng: p2 });
};
function randomRealBindings(t4, n2, r2, i2) {
  var a2 = {};
  return i2 === void 0 ? n2.forEach(function(n3) {
    a2[n3] = t4() * 2 * r2 - r2;
  }) : n2.forEach(function(n3) {
    a2[n3] = i2[n3] + t4() * 2 * r2 - r2;
  }), a2;
}
var equals$4 = function(t4, n2, { relative_tolerance: r2 = 1e-12, absolute_tolerance: i2 = 0, tolerance_for_zero: a2 = 1e-15, allowed_error_in_numbers: o2 = 0, include_error_in_number_exponents: s2 = false, allowed_error_is_absolute: c2 = false } = {}) {
  if (!t4.isAnalytic() || !n2.isAnalytic()) return false;
  let l2 = seedrandom(`real_seed`);
  return equals$6({ expr: t4, other: n2, randomBindings: randomRealBindings, expr_context: t4.context, other_content: n2.context, relative_tolerance: r2, absolute_tolerance: i2, tolerance_for_zero: a2, allowed_error_in_numbers: o2, include_error_in_number_exponents: s2, allowed_error_is_absolute: c2, rng: l2 });
}, equals$3 = function(t4, n2, { allowed_error_in_numbers: r2 = 0, include_error_in_number_exponents: i2 = false, allowed_error_is_absolute: a2 = false, allow_blanks: o2 = false, coerce_tuples_arrays: s2 = true, coerce_vectors: c2 = true } = {}) {
  if (!o2 && (t4.variables().includes(`＿`) || n2.variables().includes(`＿`))) return false;
  let l2 = t4.normalize_function_names().normalize_applied_functions().normalize_negative_numbers().normalize_angle_linesegment_arg_order(), u2 = n2.normalize_function_names().normalize_applied_functions().normalize_negative_numbers().normalize_angle_linesegment_arg_order();
  return equal(l2.tree, u2.tree, { allowed_error_in_numbers: r2, include_error_in_number_exponents: i2, allowed_error_is_absolute: a2, coerce_tuples_arrays: s2, coerce_vectors: c2 });
};
function randomFiniteFieldBindings(t4, n2, r2) {
  var i2 = {};
  return n2.forEach(function(n3) {
    i2[n3] = Math.floor(t4() * r2);
  }), i2;
}
var equals$2 = function(t4, n2, { allow_blanks: r2 = false, coerce_tuples_arrays: i2 = true, coerce_vectors: a2 = true } = {}) {
  if (!r2 && (t4.variables().includes(`＿`) || n2.variables().includes(`＿`))) return false;
  let o2 = (0, import_seedrandom.default)(`finite_field_seed`);
  if (Array.isArray(t4.tree) && Array.isArray(n2.tree)) {
    let r3 = t4.tree[0], p2 = t4.tree.slice(1), m2 = n2.tree[0], h2 = n2.tree.slice(1);
    if (r3 === `tuple` || r3 === `vector` || r3 === `altvector` || r3 === `list` || r3 === `array` || r3 === `matrix` || r3 === `interval`) {
      if (m2 !== r3 && ({ operator1: r3, operator2: m2, operands1: p2, operands2: h2 } = coerce_tuple_array_vectors({ operator1: r3, operator2: m2, operands1: p2, operands2: h2, coerce_tuples_arrays: i2, coerce_vectors: a2 })), m2 !== r3 || h2.length !== p2.length) return false;
      for (let r4 = 0; r4 < p2.length; r4++) if (equals$2(t4.context.fromAst(p2[r4]), n2.context.fromAst(h2[r4]), o2) === false) return false;
      return;
    }
    if (p2.length === 2 && [`=`, `>`, `<`, `ge`, `le`].includes(r3)) {
      if (h2.length !== 2 || (r3 === `>` ? (r3 = `<`, p2 = [p2[1], p2[0]]) : r3 === `ge` && (r3 = `le`, p2 = [p2[1], p2[0]]), m2 === `>` ? (m2 = `<`, h2 = [h2[1], h2[0]]) : m2 === `ge` && (m2 = `le`, h2 = [h2[1], h2[0]]), r3 !== m2)) return false;
      let i3 = [`+`, p2[0], [`-`, p2[1]]], a3 = [`+`, h2[0], [`-`, h2[1]]], g2 = r3 !== `=`, v2 = t4.context.fromAst(i3), y2 = n2.context.fromAst(a3);
      try {
        let t5 = prepare_exprs_numerical_eval(v2, y2);
        if (t5.functions.length === 0) {
          var s2 = t5.expr.f(), c2 = t5.other.f();
          let r4 = Number.MAX_VALUE * 1e-20;
          for (let i4 = 0; i4 < 10; i4++) {
            var l2 = {};
            t5.variables.forEach(function(t6) {
              l2[t6] = o2() * 2 - 1;
            });
            for (let n3 = 0; n3 < t5.integer_variables.length; n3++) l2[t5.integer_variables[n3]] = mathjs_default.floor(o2() * 21) - 10;
            let i5 = s2(l2), p3 = c2(l2);
            var u2 = mathjs_default.abs(i5), d2 = mathjs_default.abs(p3);
            if (!(u2 < r4 && d2 < r4) || u2 == 0 || d2 === 0) continue;
            let m3 = mathjs_default.divide(i5, p3);
            if (g2 && !(m3 > 0)) return false;
            if (m3 !== 1) {
              let t6 = rationalApproximation(m3);
              y2 = n2.context.fromAst([`*`, [`/`, t6.numerator, t6.denominator], a3]);
            }
            break;
          }
        }
      } catch {
        return;
      }
      return component_equals(v2, y2, o2);
    }
  }
  return component_equals(t4, n2, o2);
}, component_equals = function(t4, n2, r2) {
  ({ expr: t4, other: n2 } = normalize_for_equality_checking(t4, n2));
  let i2 = [1181, 1187, 1193, 1201, 1213, 1217, 1223, 1229, 1231];
  var a2 = [.../* @__PURE__ */ new Set([...t4.variables(), ...n2.variables()])];
  for (let o2 of i2) {
    let i3 = randomFiniteFieldBindings(r2, a2, o2);
    try {
      let r3 = t4.finite_field_evaluate(i3, o2), a3 = n2.finite_field_evaluate(i3, o2);
      if (!r3.approximate && !a3.approximate && !r3.isNaN() && !a3.isNaN() && r3.values.length > 0 && a3.values.length > 0 && !r3.equals(a3)) return false;
    } catch {
      return;
    }
  }
}, equals$1 = function(t4, n2, { min_elements_match: r2 = 3, match_partial: i2 = false } = {}) {
  if (!is_discrete_infinite_set(t4)) return false;
  if (is_discrete_infinite_set(n2)) {
    var a2 = [];
    let r3 = t4.context.get_assumptions(t4);
    if (r3 !== void 0 && a2.push(r3), r3 = n2.context.get_assumptions(n2), r3 !== void 0 && a2.push(r3), a2 = a2.length === 0 ? void 0 : a2.length === 1 ? a2[0] : clean_assumptions([`and`].concat(a2)), i2) {
      let r4 = contained_in(t4.tree, n2.tree, a2, i2);
      if (r4 === false) return 0;
      let o2 = contained_in(n2.tree, t4.tree, a2, i2);
      return o2 === false ? 0 : r4 === true ? o2 === true ? 1 : o2 : o2 === true ? r4 : Math.min(r4, o2);
    } else return contained_in(t4.tree, n2.tree, a2, i2) && contained_in(n2.tree, t4.tree, a2, i2);
  } else {
    let i3 = n2.tree;
    if (i3[0] !== `list`) return false;
    let a3 = i3.length - 2;
    if (i3[a3 + 1][0] !== `ldots` || a3 < r2) return false;
    let o2 = i3.slice(0, a3 + 1), s2 = sequence_from_discrete_infinite(t4, a3);
    return s2 ? (s2 = [`list`].concat(s2), equals(t4.context.from(s2), n2.context.from(o2))) : false;
  }
};
function is_discrete_infinite_set(t4) {
  var n2 = t4.tree;
  if (!Array.isArray(n2) || n2[0] !== `discrete_infinite_set`) return false;
  for (var r2 of n2.slice(1)) if (!Array.isArray(r2) || r2[0] !== `tuple` || r2.length !== 5) return false;
  return true;
}
function contained_in(t4, n2, r2, i2) {
  if (t4[0] === `discrete_infinite_set`) if (i2) {
    let a3 = 0;
    for (let o4 of t4.slice(1)) {
      let t5 = contained_in(o4, n2, r2, i2);
      t5 === true ? a3++ : t5 !== false && (a3 += t5);
    }
    let o3 = t4.length - 1;
    return a3 === o3 ? true : a3 === 0 ? false : a3 / o3;
  } else return t4.slice(1).every((t5) => contained_in(t5, n2, r2));
  var a2 = t4[1], o2 = t4[2], s2 = evaluate_numbers(t4[3], r2), c2 = t4[4];
  if (s2 !== -1 / 0 || c2 !== 1 / 0) return false;
  a2 = simplify([`/`, a2, o2], r2, 1 / 0);
  var l2 = n2.slice(1), u2 = [];
  for (let t5 = 0; t5 < l2.length; t5++) {
    let n3 = evaluate_numbers(l2[t5][3]), i3 = l2[t5][4];
    if (n3 !== -1 / 0 || i3 !== 1 / 0) return false;
    let a3 = simplify([`/`, l2[t5][1], o2], r2, 1 / 0), s3 = simplify([`/`, l2[t5][2], o2], r2, 1 / 0);
    if (typeof s3 != `number`) return false;
    let c3 = mathjs_default.fraction(s3), d3 = mathjs_default.number(c3.n), p3 = mathjs_default.number(c3.d);
    u2.push([d3, p3, a3, s3]);
  }
  for (u2.sort(); u2[0][0] === 1; ) {
    let t5 = u2[0][2], n3 = u2[0][3], i3 = simplify(expand([`+`, t5, [`-`, a2]]), r2, 1 / 0);
    if (Number.isFinite(i3) && Number.isFinite(n3) && (i3 = mathjs_default.mod(i3, n3), mathjs_default.min(i3, n3 - i3) < 1e-10 * n3)) return true;
    if (u2.splice(0, 1), u2.length === 0) return false;
  }
  var d2 = [...new Set(u2.map((t5) => t5[0]))];
  let p2 = 0;
  for (let t5 of d2) {
    let n3 = u2.map(function(n4, r3) {
      if (mathjs_default.mod(t5, n4[0]) === 0) {
        let i3 = t5 / n4[0];
        return [n4[0], i3, r3];
      } else return;
    }).filter((t6) => t6), o3 = [];
    for (let i3 of n3) {
      let n4 = i3[0], s3 = i3[1], c3 = i3[2], l3 = u2[c3][2], d3 = u2[c3][3];
      for (let i4 = 0; i4 < n4; i4++) {
        let c4 = simplify(expand([`+`, l3, [`-`, [`+`, a2, i4]]]), r2, 1 / 0);
        if (Number.isFinite(c4) && Number.isFinite(d3) && (c4 = mathjs_default.mod(c4, d3), mathjs_default.min(c4, d3 - c4) < 1e-10 * d3)) {
          for (let t6 = 0; t6 < s3; t6++) o3[i4 + t6 * n4] = true;
          let r3 = true;
          for (let n5 = 0; n5 < t5; n5++) if (!o3[n5]) {
            r3 = false;
            break;
          }
          if (r3) return true;
          break;
        }
      }
    }
    if (i2) {
      let n4 = 0;
      for (let r3 = 0; r3 < t5; r3++) o3[r3] && n4++;
      n4 /= t5, n4 > p2 && (p2 = n4);
    }
  }
  return i2 && p2 > 0 ? p2 : false;
}
function sequence_from_discrete_infinite(t4, n2) {
  var r2 = t4.tree.slice(1);
  if (r2.length > 1) return;
  let i2 = r2[0][1], a2 = r2[0][2], o2 = evaluate_numbers(r2[0][3]), s2 = r2[0][4];
  if (!Number.isInteger(o2) || s2 !== 1 / 0) return;
  let c2 = [];
  for (let t5 = 0; t5 < n2; t5++) c2.push(evaluate_numbers([`+`, [`*`, a2, o2 + t5], i2]));
  return c2;
}
var equality_exports = __exportAll({ equals: () => equals, equalsDiscreteInfinite: () => equals$1, equalsViaComplex: () => equals$5, equalsViaFiniteField: () => equals$2, equalsViaReal: () => equals$4, equalsViaSyntax: () => equals$3 }), equals = function(t4, n2, { relative_tolerance: r2 = 1e-12, absolute_tolerance: i2 = 0, tolerance_for_zero: a2 = 1e-15, allowed_error_in_numbers: o2 = 0, include_error_in_number_exponents: s2 = false, allowed_error_is_absolute: c2 = false, allow_blanks: l2 = false, coerce_tuples_arrays: u2 = true, coerce_vectors: d2 = true } = {}) {
  if (!l2 && (t4.variables().includes(`＿`) || n2.variables().includes(`＿`))) return false;
  let p2 = t4.evaluate_numbers({ max_digits: 1 / 0 }).normalize_function_names().normalize_applied_functions().normalize_negative_numbers().normalize_angle_linesegment_arg_order().remove_scaling_units().simplify(), m2 = n2.evaluate_numbers({ max_digits: 1 / 0 }).normalize_function_names().normalize_applied_functions().normalize_negative_numbers().normalize_angle_linesegment_arg_order().remove_scaling_units().simplify();
  return p2.equalsViaSyntax(m2, { allowed_error_in_numbers: o2, include_error_in_number_exponents: s2, allowed_error_is_absolute: c2, allow_blanks: true, coerce_tuples_arrays: u2, coerce_vectors: d2 }) ? true : o2 === 0 && t4.equalsViaFiniteField(n2, { allow_blanks: true, coerce_tuples_arrays: u2, coerce_vectors: d2 }) == 0 ? false : t4.equalsViaComplex(n2, { relative_tolerance: r2, absolute_tolerance: i2, tolerance_for_zero: a2, allowed_error_in_numbers: o2, include_error_in_number_exponents: s2, allowed_error_is_absolute: c2, allow_blanks: true, coerce_tuples_arrays: u2, coerce_vectors: d2 }) ? true : !!equals$1(t4, n2);
}, sign_error_exports = __exportAll({ equalSpecifiedSignErrors: () => equalSpecifiedSignErrors, equalWithSignErrors: () => equalWithSignErrors }), equalWithSignErrors = function(t4, n2, { equalityFunction: r2 = equals, max_sign_errors: i2 = 1 } = {}) {
  if (r2(t4, n2)) return { matched: true, n_sign_errors: 0 };
  for (let a2 = 1; a2 <= i2; a2++) if (equalSpecifiedSignErrors(t4, n2, { equalityFunction: r2, n_sign_errors: a2 })) return { matched: true, n_sign_errors: a2 };
  return { matched: false };
}, equalSpecifiedSignErrors = function(t4, n2, { equalityFunction: r2 = equals, n_sign_errors: i2 = 1 } = {}) {
  if (i2 === 0) return r2(t4, n2);
  if (!(Number.isInteger(i2) && i2 > 0)) throw Error(`Have not implemented equality check with ${i2} sign errors.`);
  if (i2 > 1) {
    let t5 = r2;
    r2 = function(n3, r3) {
      return equalSpecifiedSignErrors(n3, r3, { equalityFunction: t5, n_sign_errors: i2 - 1 });
    };
  }
  var a2 = t4.tree, o2 = [[a2]], s2 = 0, c2, l2;
  t4.tree = [`-`, a2];
  var u2 = r2(t4, n2);
  if (t4.tree = a2, u2) return true;
  for (; c2 = o2[s2++]; ) if (c2 = c2[0], Array.isArray(c2)) {
    for (l2 = 1; l2 < c2.length; l2++) if (o2.push([c2[l2]]), c2[l2] = [`-`, c2[l2]], u2 = r2(t4, n2), c2[l2] = c2[l2][1], u2) return true;
  }
  return false;
}, arithmetic_exports = __exportAll({ add: () => add, copy: () => copy, divide: () => divide, mod: () => mod$2, multiply: () => multiply, pow: () => pow, subtract: () => subtract });
function add(t4, n2) {
  return clean([`+`, get_tree(t4), get_tree(n2)]);
}
function subtract(t4, n2) {
  return clean([`+`, get_tree(t4), [`-`, get_tree(n2)]]);
}
function multiply(t4, n2) {
  return clean([`*`, get_tree(t4), get_tree(n2)]);
}
function divide(t4, n2) {
  return clean([`/`, get_tree(t4), get_tree(n2)]);
}
function pow(t4, n2) {
  return clean([`^`, get_tree(t4), get_tree(n2)]);
}
function mod$2(t4, n2) {
  return clean([`apply`, `mod`, [`tuple`, get_tree(t4), get_tree(n2)]]);
}
function copy(t4) {
  return get_tree(t4);
}
var analytic_exports = __exportAll({ isAnalytic: () => isAnalytic }), analytic_operators = [`+`, `-`, `*`, `/`, `^`, `tuple`, `vector`, `altvector`, `list`, `array`, `matrix`, `interval`, `vec`], non_analytic_functions = [`abs`, `sign`, `arg`], relation_operators = [`=`, `le`, `ge`, `<`, `>`];
function isAnalytic(t4, { allow_abs: n2 = false, allow_arg: r2 = false, allow_relation: i2 = false } = {}) {
  var a2 = normalize_applied_functions(normalize_function_names(t4));
  a2 = subscripts_to_strings(a2);
  var o2 = operators$3(a2);
  for (let t5 = 0; t5 < o2.length; t5++) {
    let n3 = o2[t5];
    if (analytic_operators.indexOf(n3) === -1) if (i2) {
      if (relation_operators.indexOf(n3) === -1) return false;
    } else return false;
  }
  var s2 = functions$2(a2);
  for (let t5 = 0; t5 < s2.length; t5++) {
    let i3 = s2[t5];
    if (non_analytic_functions.includes(i3) && !(n2 && i3 == `abs` || r2 && i3 == `arg`)) return false;
  }
  return true;
}
var sets_exports = __exportAll({ create_discrete_infinite_set: () => create_discrete_infinite_set });
function create_discrete_infinite_set({ offsets: t4, periods: n2, min_index: r2 = [`-`, 1 / 0], max_index: i2 = 1 / 0 } = {}) {
  if (t4 = get_tree(t4), n2 = get_tree(n2), r2 = get_tree(r2), i2 = get_tree(i2), t4 === void 0 || n2 === void 0) return;
  let a2 = [];
  if (t4[0] === `list`) if (n2[0] === `list`) {
    if (t4.length !== n2.length || t4.length === 1) return;
    for (let o2 = 1; o2 < t4.length; o2++) a2.push([`tuple`, t4[o2], n2[o2], r2, i2]);
  } else for (let o2 = 1; o2 < t4.length; o2++) a2.push([`tuple`, t4[o2], n2, r2, i2]);
  else a2.push([`tuple`, t4, n2, r2, i2]);
  return [`discrete_infinite_set`].concat(a2);
}
var matrix_exports = __exportAll({ cross_prod: () => cross_prod, dot_prod: () => dot_prod, matrix: () => matrix, scalar_mul: () => scalar_mul, vector_add: () => vector_add, vector_sub: () => vector_sub });
function tuple(t4) {
  var n2 = [];
  n2.push(`tuple`);
  var r2 = t4.length;
  for (let i2 = 0; i2 < r2; i2++) n2.push(t4[i2]);
  return n2;
}
function matrix(t4) {
  var n2 = [];
  n2.push(`matrix`);
  var r2 = t4.length, i2 = t4[0].length;
  for (let n3 = 1; n3 < r2; n3++) if (t4[n3].length !== i2) throw Error(`Matrix dimensions mismatch`);
  n2.push(tuple([r2, i2]));
  let a2 = [];
  for (let n3 = 0; n3 < r2; n3++) a2.push(tuple(t4[n3].map(function(t5) {
    return t5.tree;
  })));
  return n2.push(tuple(a2)), n2;
}
function vector_add(t4, n2) {
  if (t4 = get_tree(t4), n2 = get_tree(n2), t4.length !== n2.length || t4[0] !== `tuple` && t4[0] !== `vector` && t4[0] !== `altvector` || n2[0] !== `tuple` && n2[0] !== `vector` && n2[0] !== `altvector`) throw Error(`Can't add. Those aren't vectors, or the dimensions don't match`);
  var r2 = t4[0] === `altvector` && n2[0] === `altvector` ? [`altvector`] : [`vector`], i2 = t4.length;
  for (let a2 = 1; a2 < i2; a2 += 1) r2.push([`+`, t4[a2], n2[a2]]);
  return simplify(r2);
}
function scalar_mul(t4, n2) {
  if (n2 = get_tree(n2), n2[0] !== `tuple` && n2[0] !== `vector` && n2[0] !== `altvector`) throw Error(`Can't scalar multiply. Isn't a vector`);
  var r2 = n2[0] === `altvector` ? [`altvector`] : [`vector`], i2 = n2.length;
  for (let a2 = 1; a2 < i2; a2 += 1) r2.push([`*`, n2[a2], t4]);
  return simplify(r2);
}
function vector_sub(t4, n2) {
  return vector_add(t4, scalar_mul(-1, n2));
}
function dot_prod(t4, n2) {
  if (t4 = get_tree(t4), n2 = get_tree(n2), t4.length !== n2.length || t4[0] !== `tuple` && t4[0] !== `vector` && t4[0] !== `altvector` || n2[0] !== `tuple` && n2[0] !== `vector` && n2[0] !== `altvector`) throw Error(`Can't take dot product. Those aren't vectors, or the dimensions don't match`);
  var r2 = 0, i2 = 0, a2 = t4.length;
  for (let o2 = 1; o2 < a2; o2 += 1) i2 = [`*`, t4[o2], n2[o2]], r2 = [`+`, r2, i2];
  return simplify(r2);
}
function cross_prod(t4, n2) {
  if (t4 = get_tree(t4), n2 = get_tree(n2), t4[0] !== `tuple` && t4[0] !== `vector` && t4[0] !== `altvector` || n2[0] !== `tuple` && n2[0] !== `vector` && n2[0] !== `altvector`) throw Error(`Can't take cross product. Those aren't vectors`);
  if (t4.length === 3 && n2.length === 3) return simplify([`+`, [`*`, t4[1], n2[2]], [`-`, [`*`, t4[2], n2[1]]]]);
  if (t4.length === 4 && n2.length === 4) {
    var r2 = [`+`, [`*`, t4[2], n2[3]], [`-`, [`*`, t4[3], n2[2]]]], i2 = [`+`, [`*`, t4[3], n2[1]], [`-`, [`*`, t4[1], n2[3]]]], a2 = [`+`, [`*`, t4[1], n2[2]], [`-`, [`*`, t4[2], n2[1]]]];
    return simplify([t4[0] === `altvector` && n2[0] === `altvector` ? `altvector` : `vector`, r2, i2, a2]);
  }
  throw Error(`Can't take cross product. The dimensions aren't both 2 or 3.`);
}
var astToGuppy$1 = class t3 {
  constructor() {
    this.operators = { "+": function(t4) {
      return t4.join(`<e>+</e>`);
    }, "-": function(t4) {
      return `<e>-` + t4.join(`-`) + `</e>`;
    }, "*": function(t4) {
      return t4.join(`<f type="*" group="operations" c="yes"><b p="latex">\\cdot</b><b p="text">*</b></f>`);
    }, "/": function(n2) {
      return t3.dfrac(n2[0], n2[1]);
    }, "^": function(n2) {
      return t3.power(n2[0], n2[1]);
    }, sin: function(n2) {
      return t3.trig(`sin`, n2[0]);
    }, cos: function(n2) {
      return t3.trig(`cos`, n2[0]);
    }, tan: function(n2) {
      return t3.trig(`tan`, n2[0]);
    }, arcsin: function(n2) {
      return t3.trig(`arcsin`, n2[0]);
    }, arccos: function(n2) {
      return t3.trig(`arccos`, n2[0]);
    }, arctan: function(n2) {
      return t3.trig(`arctan`, n2[0]);
    }, arccsc: function(n2) {
      return t3.trig(`arccsc`, n2[0]);
    }, arcsec: function(n2) {
      return t3.trig(`arcsec`, n2[0]);
    }, arccot: function(n2) {
      return t3.trig(`arccot`, n2[0]);
    }, csc: function(n2) {
      return t3.trig(`csc`, n2[0]);
    }, sec: function(n2) {
      return t3.trig(`sec`, n2[0]);
    }, cot: function(n2) {
      return t3.trig(`cot`, n2[0]);
    }, log: function(n2) {
      return t3.trig(`log`, n2[0]);
    }, exp: function(n2) {
      return t3.trig(`exp`, n2[0]);
    }, ln: function(n2) {
      return t3.trig(`ln`, n2[0]);
    }, sqrt: function(n2) {
      return t3.sqrt(n2[0]);
    }, abs: function(n2) {
      return t3.abs(n2[0]);
    } };
  }
  static dfrac(t4, n2) {
    return `<f type="fraction" group="functions"><b p="latex">\\dfrac{<r ref="1"/>}{<r ref="2"/>}</b><b p="small_latex">\\frac{<r ref="1"/>}{<r ref="2"/>}</b><b p="text">(<r ref="1"/>)/(<r ref="2"/>)</b><c up="1" down="2" name="numerator"><e></e>` + t4 + `<e></e></c><c up="1" down="2" name="denominator"><e></e>` + n2 + `<e></e></c></f>`;
  }
  static trig(t4, n2) {
    return `<f type="` + t4 + `" group="functions"><b p="latex">\\` + t4 + `\\left(<r ref="1"/>\\right)</b><b p="text"> ` + t4 + `(<r ref="1"/>)</b><c delete="1"><e></e>` + n2 + `<e></e></c></f>`;
  }
  static sqrt(t4) {
    return `<f type="square_root" group="functions"><b p="latex">\\sqrt{<r ref="1"/>}</b><b p="text">sqrt(<r ref="1"/>)</b><c delete="1"><e></e>` + t4 + `<e></e></c></f>`;
  }
  static power(t4, n2) {
    return `<f type="exponential" group="functions"><b p="latex">{<r ref="1"/>}^{<r ref="2"/>}</b><b p="text">(<r ref="1"/>)^(<r ref="2"/>)</b><c up="2" bracket="yes" delete="1" name="base"><e></e>` + t4 + `<e></e></c><c down="1" delete="1" name="exponent" small="yes"><e></e>` + n2 + `<e></e></c></f>`;
  }
  static abs(t4) {
    return `<f type="absolute_value" group="functions"><b p="latex">\\left|<r ref="1"/>\\right|</b><b p="text">abs(<r ref="1"/>)</b><c delete="1"><e></e>` + t4 + `<e></e></c></f>`;
  }
  static paren(t4) {
    return `<f type="bracket" group="functions"><b p="latex">\\left(<r ref="1"/>\\right)</b><b p="text">(<r ref="1"/>)</b><c delete="1" is_bracket="yes"><e></e>` + t4 + `<e></e></c></f>`;
  }
  static isFunctionSymbol(t4) {
    return [`sin`, `cos`, `tan`, `csc`, `sec`, `cot`, `arcsin`, `arccos`, `arctan`, `arccsc`, `arcsec`, `arccot`, `log`, `ln`, `exp`, `sqrt`, `abs`, `this.factorial`].indexOf(t4) !== -1;
  }
  static isGreekLetterSymbol(t4) {
    return `pi.theta.theta.Theta.alpha.nu.beta.xi.Xi.gamma.Gamma.delta.Delta.pi.Pi.epsilon.epsilon.rho.rho.zeta.sigma.Sigma.eta.tau.upsilon.Upsilon.iota.phi.phi.Phi.kappa.chi.lambda.Lambda.psi.Psi.omega.Omega`.split(`.`).indexOf(t4) !== -1;
  }
  factorWithParenthesesIfNegated(n2) {
    var r2 = this.factor(n2);
    return r2.toString().match(/^<e>-/) ? t3.paren(r2.toString()) : r2;
  }
  factor(n2) {
    if (typeof n2 == `string`) return t3.isGreekLetterSymbol(n2) ? `<f type="` + n2 + `" group="greek" c="yes"><b p="latex">\\` + n2 + `</b><b p="text"> $` + n2 + `</b></f>` : `<e>` + n2 + `</e>`;
    if (typeof n2 == `number`) return `<e>` + n2 + `</e>`;
    if (!Array.isArray(n2)) return `<e></e>`;
    var r2 = n2[0], i2 = n2.slice(1);
    return r2 === `apply` && (r2 = n2[1], i2 = n2.slice(2)), r2 === `abs` ? this.operators[r2](i2.map((function(t4, n3) {
      return this.expression(t4);
    }).bind(this))) : t3.isFunctionSymbol(r2) ? r2 === `this.factorial` && (i2[0].toString().length === 1 || i2[0].toString().match(/^[0-9]*$/)) ? this.operators[r2](i2) : this.operators[r2](i2.map((function(t4, n3) {
      return this.factor(t4);
    }).bind(this))) : r2 === `^` || r2 === `~` ? this.operators[r2](i2.map((function(t4, n3) {
      return this.factor(t4);
    }).bind(this))) : t3.paren(this.expression(n2));
  }
  term(t4) {
    if (!Array.isArray(t4)) return this.factor(t4);
    var n2 = t4[0], r2 = t4.slice(1);
    return n2 === `*` ? this.operators[n2](r2.map((function(t5, n3) {
      var r3 = this.factorWithParenthesesIfNegated(t5);
      return r3.toString().match(/^[0-9]/) && n3 > 0 ? ` * ` + r3 : r3;
    }).bind(this))) : n2 === `/` ? this.operators[n2](r2.map((function(t5, n3) {
      return this.factor(t5);
    }).bind(this))) : this.factor(t4);
  }
  expression(t4) {
    if (!Array.isArray(t4)) return this.term(t4);
    var n2 = t4[0], r2 = t4.slice(1);
    return n2 === `+` || n2 === `-` ? this.operators[n2](r2.map((function(t5, n3) {
      return this.factorWithParenthesesIfNegated(t5);
    }).bind(this))) : this.term(t4);
  }
  convert(t4) {
    return (`<m><e></e>` + this.expression(t4) + `<e></e></m>`).replace(/<\/e><e>/g, ``);
  }
}, glslOperators = { "+": function(t4) {
  var n2 = t4[0];
  return t4.slice(1).forEach(function(t5) {
    n2 = n2 + `+` + t5;
  }), n2;
}, "-": function(t4) {
  var n2 = `-` + t4[0];
  return t4.slice(1).forEach(function(t5) {
    n2 = n2 + `-` + t5;
  }), n2;
}, "~": function(t4) {
  var n2 = `vec2(0.0,0.0)`;
  return t4.forEach(function(t5) {
    n2 = n2 + `-` + t5;
  }), n2;
}, "*": function(t4) {
  var n2 = t4[0];
  return t4.slice(1).forEach(function(t5) {
    n2 = `cmul(` + n2 + `,` + t5 + `)`;
  }), n2;
}, "/": function(t4) {
  var n2 = t4[0];
  return t4.slice(1).forEach(function(t5) {
    n2 = `cdiv(` + n2 + `,` + t5 + `)`;
  }), n2;
}, sin: function(t4) {
  return `csin(` + t4[0] + `)`;
}, cos: function(t4) {
  return `ccos(` + t4[0] + `)`;
}, tan: function(t4) {
  return `ctan(` + t4[0] + `)`;
}, sinh: function(t4) {
  return `csinh(` + t4[0] + `)`;
}, cosh: function(t4) {
  return `ccosh(` + t4[0] + `)`;
}, arcsin: function(t4) {
  return `carcsin(` + t4[0] + `)`;
}, arccos: function(t4) {
  return `carccos(` + t4[0] + `)`;
}, arctan: function(t4) {
  return `carctan(` + t4[0] + `)`;
}, arccsc: function(t4) {
  return `carcsin(cdiv(vec2(1.0,0),` + t4[0] + `))`;
}, arcsec: function(t4) {
  return `carccos(cdiv(vec2(1.0,0),` + t4[0] + `))`;
}, arccot: function(t4) {
  return `carctan(cdiv(vec2(1.0,0),` + t4[0] + `))`;
}, csc: function(t4) {
  return `ccsc(` + t4[0] + `)`;
}, sec: function(t4) {
  return `csec(` + t4[0] + `)`;
}, cot: function(t4) {
  return `ccot(` + t4[0] + `)`;
}, exp: function(t4) {
  return `cexp(` + t4[0] + `)`;
}, conj: function(t4) {
  return `conjugate(` + t4[0] + `)`;
}, sqrt: function(t4) {
  return `cpower(` + t4[0] + `,vec2(0.5,0.0))`;
}, log: function(t4) {
  return `clog(` + t4[0] + `)`;
}, ln: function(t4) {
  return `clog(` + t4[0] + `)`;
}, "^": function(t4) {
  return `cpower(` + t4[0] + `,` + t4[1] + `)`;
}, abs: function(t4) {
  return `cabs(` + t4[0] + `)`;
}, apply: function(t4) {
  return `vec2(NaN,NaN)`;
} }, astToGLSL$1 = class {
  constructor() {
  }
  convert(t4) {
    if (typeof t4 == `boolean`) throw Error(`no support for boolean`);
    if (typeof t4 == `string`) return t4 === `e` ? `vec2(2.71828182845905,0.0)` : t4 === `pi` ? `vec2(3.14159265358979,0.0)` : t4 === `i` ? `vec2(0.0,1.0)` : String(t4);
    if (typeof t4 == `number`) return `vec2(` + String(t4) + `,0.0)`;
    if (`real` in t4 && `imaginary` in t4) return t4;
    if (!Array.isArray(t4)) throw Error(`Invalid ast`);
    var n2 = t4[0], r2 = t4.slice(1);
    if (n2 === `apply`) {
      if (typeof r2[0] != `string`) throw Error(`Non string functions not implemented for conversion to GLSL`);
      var n2 = r2[0], r2 = r2.slice(1);
      return glslOperators[n2](r2.map((function(t5, n3) {
        return this.convert(t5);
      }).bind(this)));
    }
    if (n2 in glslOperators) return glslOperators[n2](r2.map((function(t5, n3) {
      return this.convert(t5);
    }).bind(this)));
    throw Error(`Operator ` + n2 + ` not implemented for conversion to mathjs`);
  }
}, printing_exports = __exportAll({ tex: () => tex, toGLSL: () => toGLSL, toLatex: () => toLatex, toString: () => toString, toXML: () => toXML }), astToGuppy = new astToGuppy$1(), astToGLSL = new astToGLSL$1(), tex = function(t4, n2) {
  return new astToLatex$1(n2).convert(t4.tree);
}, toLatex = tex, toString = function(t4, n2) {
  return new astToText$2(n2).convert(t4.tree);
}, toGLSL = function(t4) {
  return astToGLSL.convert(t4.tree);
}, toXML = function(t4) {
  return astToGuppy.convert(t4.tree);
}, integration_exports = __exportAll({ integrateNumerically: () => integrateNumerically }), integrateNumerically = function(t4, n2, r2, i2) {
  for (var a2 = 100, o2 = 0, s2 = {}, c2 = 0; c2 < a2; c2++) s2[n2] = r2 + (i2 - r2) * (c2 + 0.5) / a2, o2 += t4.evaluate(s2);
  return o2 * (i2 - r2) / a2;
};
init_index_all(), init_index_all();
function expression_to_polynomial(t4) {
  var n2 = get_tree(t4);
  if (typeof n2 == `string`) return n2 === `pi` && mathjs_default.define_pi || n2 === `i` && mathjs_default.define_i || n2 === `e` && mathjs_default.define_e ? n2 : [`polynomial`, n2, [[1, 1]]];
  if (typeof n2 == `number`) return n2;
  var r2 = evaluate_to_constant(n2, { remove_units_first: false });
  if (Number.isFinite(r2)) return simplify(n2);
  if (!Array.isArray(n2) || !operators$3(n2).every((t5) => [`+`, `-`, `*`, `^`, `/`, `_`, `prime`].includes(t5))) return false;
  var i2 = n2[0], a2 = n2.slice(1);
  if (i2 === `+`) {
    let t5 = a2.map(expression_to_polynomial);
    return t5.every((t6) => t6 !== false) ? t5.reduce((t6, n3) => polynomial_add(t6, n3)) : false;
  } else if (i2 === `-`) {
    let t5 = expression_to_polynomial(a2[0]);
    return t5 ? polynomial_neg(t5) : false;
  } else if (i2 === `*`) {
    let t5 = a2.map(expression_to_polynomial);
    return t5.every((t6) => t6 !== false) ? t5.reduce((t6, n3) => polynomial_mul(t6, n3)) : false;
  } else if (i2 === `^`) {
    let t5 = a2[0], r3 = expression_to_polynomial(t5);
    if (r3 === false) return false;
    let i3 = simplify(a2[1]);
    if (typeof i3 != `number` || i3 < 0 || !Number.isInteger(i3)) {
      let r4 = evaluate_to_constant(i3, { remove_units_first: false });
      if (Number.isFinite(r4)) {
        let n3 = mathjs_default.fraction(r4), i4 = { n: mathjs_default.number(n3.n), d: mathjs_default.number(n3.d), s: mathjs_default.number(n3.s) };
        if (i4.d <= 100) {
          t5 = i4.s < 0 ? [`^`, t5, [`/`, -1, i4.d]] : [`^`, t5, [`/`, 1, i4.d]];
          var o2 = [`polynomial`, simplify(t5), []];
          return o2[2].push([i4.n, 1]), o2;
        }
      }
      return [`polynomial`, n2, [[1, 1]]];
    }
    return i3 === 0 ? 1 : i3 === 1 ? r3 : polynomial_pow(r3, i3);
  } else if (i2 === `/`) {
    var s2 = a2[1], c2 = evaluate_to_constant(s2, { remove_units_first: false });
    return Number.isFinite(c2) ? polynomial_mul(expression_to_polynomial(a2[0]), [`/`, 1, c2]) : [`polynomial`, n2, [[1, 1]]];
  } else return [`polynomial`, n2, [[1, 1]]];
}
function polynomials_in_same_leading_variable(t4, n2) {
  return t4[1] !== n2[1] && (compare_function(t4[1], n2[1]) < 0 ? n2 = [`polynomial`, t4[1], [[0, n2]]] : t4 = [`polynomial`, n2[1], [[0, t4]]]), [t4, n2];
}
function polynomial_add(t4, n2) {
  if (t4[0] !== `polynomial`) {
    if (n2[0] !== `polynomial`) return simplify([`+`, t4, n2]);
    t4 = [`polynomial`, n2[1], [[0, t4]]];
  } else if (n2[0] !== `polynomial`) n2 = [`polynomial`, t4[1], [[0, n2]]];
  else {
    let r3 = polynomials_in_same_leading_variable(t4, n2);
    t4 = r3[0], n2 = r3[1];
  }
  let r2 = [`polynomial`, t4[1], []], i2 = t4[2], a2 = n2[2], o2 = r2[2], s2 = i2.length, c2 = a2.length, l2 = 0, u2 = 0;
  for (; l2 < s2 || u2 < c2; ) if (l2 === s2) a2[u2][1] && o2.push(a2[u2]), u2 += 1;
  else if (u2 === c2) i2[l2][1] && o2.push(i2[l2]), l2 += 1;
  else if (i2[l2][0] === a2[u2][0]) {
    let t5 = polynomial_add(i2[l2][1], a2[u2][1]);
    t5 && o2.push([i2[l2][0], t5]), l2 += 1, u2 += 1;
  } else i2[l2][0] < a2[u2][0] ? (i2[l2][1] && o2.push(i2[l2]), l2 += 1) : (a2[u2][1] && o2.push(a2[u2]), u2 += 1);
  return o2.length === 0 ? 0 : o2.length === 1 && o2[0][0] === 0 ? o2[0][1] : r2;
}
function polynomial_neg(t4) {
  if (t4[0] !== `polynomial`) return simplify([`-`, t4]);
  let n2 = [`polynomial`, t4[1], []], r2 = t4[2], i2 = n2[2], a2 = r2.length;
  for (var o2 = 0; o2 < a2; o2 += 1) i2.push([r2[o2][0], polynomial_neg(r2[o2][1])]);
  return n2;
}
function polynomial_sub(t4, n2) {
  return polynomial_add(t4, polynomial_neg(n2));
}
function polynomial_mul(t4, n2) {
  if (t4[0] !== `polynomial`) {
    if (n2[0] !== `polynomial`) return simplify([`*`, t4, n2]);
    if (t4) {
      let r3 = [`polynomial`, n2[1], []], i3 = n2[2], a3 = r3[2];
      for (let n3 of i3) n3[1] && a3.push([n3[0], polynomial_mul(t4, n3[1])]);
      return r3;
    }
  } else if (n2 && n2[0] !== `polynomial`) {
    let r3 = [`polynomial`, t4[1], []], i3 = t4[2], a3 = r3[2];
    for (let t5 of i3) t5[1] && a3.push([t5[0], polynomial_mul(t5[1], n2)]);
    return r3;
  }
  let r2 = polynomials_in_same_leading_variable(t4, n2);
  t4 = r2[0], n2 = r2[1];
  let i2 = t4[2], a2 = n2[2], o2 = [`polynomial`, t4[1], []], s2 = o2[2], c2 = i2.length, l2 = a2.length, u2 = [];
  for (let t5 of i2) for (let n3 of a2) {
    let r3 = false, i3 = t5[0] + n3[0];
    for (let t6 of u2) if (i3 === t6) {
      r3 = true;
      break;
    }
    r3 || u2.push(i3);
  }
  u2.sort(function(t5, n3) {
    return t5 - n3;
  });
  for (let t5 of u2) {
    let n3 = 0, r3 = 0;
    for (; r3 < c2 && i2[r3][0] <= t5; ) {
      let o3 = 0;
      for (; o3 < l2 && a2[o3][0] <= t5; ) {
        if (i2[r3][0] + a2[o3][0] === t5) {
          n3 = polynomial_add(n3, polynomial_mul(i2[r3][1], a2[o3][1]));
          break;
        }
        o3 += 1;
      }
      r3 += 1;
    }
    n3 && s2.push([t5, n3]);
  }
  return o2;
}
function polynomial_pow(t4, n2) {
  if (isNaN(n2) || n2 < 0 || !Number.isInteger(n2)) return;
  let r2 = 1;
  for (; n2 > 0; ) n2 & 1 && (r2 = polynomial_mul(r2, t4)), t4 = polynomial_mul(t4, t4), n2 >>= 1;
  return r2;
}
function polynomial_to_expression(t4) {
  if (!Array.isArray(t4) || t4[0] !== `polynomial`) return t4;
  let n2 = t4[1], r2 = t4[2], i2 = [], a2 = r2.length;
  for (var o2 = 0; o2 < a2; o2 += 1) r2[o2][1] && (r2[o2][0] === 0 ? i2.push(polynomial_to_expression(r2[o2][1])) : r2[o2][0] === 1 ? i2.push([`*`, polynomial_to_expression(r2[o2][1]), n2]) : i2.push([`*`, polynomial_to_expression(r2[o2][1]), [`^`, n2, r2[o2][0]]]));
  return i2.length === 0 ? 0 : (i2.length === 1 ? i2 = i2[0] : i2.unshift(`+`), simplify(i2));
}
function initial_term(t4) {
  if (!Array.isArray(t4) || t4[0] !== `polynomial`) return t4;
  let n2 = [];
  for (; Array.isArray(t4) && t4[0] === `polynomial`; ) {
    let r2 = t4[1], i2 = t4[2], a2 = i2[i2.length - 1][0];
    t4 = i2[i2.length - 1][1], n2.push([r2, a2]);
  }
  return [`monomial`, t4, n2];
}
function mono_less_than(t4, n2) {
  if (!Array.isArray(n2) || n2[0] !== `monomial`) return false;
  if (!Array.isArray(t4) || t4[0] !== `monomial`) return true;
  let r2 = t4[2], i2 = n2[2], a2 = r2.length, o2 = i2.length;
  for (var s2 = a2 < o2 ? a2 : o2, c2 = 0; c2 < s2; c2++) {
    if (r2[c2][0] !== i2[c2][0]) return !(compare_function(r2[c2][0], i2[c2][0]) < 0);
    if (r2[c2][1] < i2[c2][1]) return true;
    if (r2[c2][1] > i2[c2][1]) return false;
  }
  return !(a2 === o2 || s2 === o2);
}
function mono_gcd(t4, n2) {
  if (!Array.isArray(t4) || !Array.isArray(n2) || t4[0] !== `monomial` || n2[0] !== `monomial`) return 1;
  let r2 = t4[2], i2 = n2[2], a2 = [], o2 = r2.length, s2 = i2.length, c2 = 0, l2 = 0;
  for (; c2 < o2 && l2 < s2; ) r2[c2][0] === i2[l2][0] ? (r2[c2][1] < i2[l2][1] ? a2.push(r2[c2]) : a2.push(i2[l2]), c2 += 1, l2 += 1) : compare_function(r2[c2][0], i2[l2][0]) < 0 ? c2 += 1 : compare_function(i2[l2][0], r2[c2][0]) < 0 && (l2 += 1);
  return a2.length === 0 ? 1 : [`monomial`, 1, a2];
}
function mono_div(t4, n2) {
  if (!Array.isArray(n2) || n2[0] !== `monomial`) return n2 === 1 ? t4 : !Array.isArray(t4) || t4[0] !== `monomial` ? evaluate_numbers([`/`, t4, n2]) : [t4[0], evaluate_numbers([`/`, t4[1], n2]), t4[2]];
  if (!Array.isArray(t4) || t4[0] !== `monomial`) return;
  let r2 = t4[2], i2 = n2[2], a2 = [], o2 = r2.length, s2 = i2.length, c2 = 0, l2 = 0;
  for (; c2 < o2 && l2 < s2; ) if (r2[c2][0] === i2[l2][0]) {
    if (r2[c2][1] < i2[l2][1]) return;
    {
      let t5 = r2[c2][1] - i2[l2][1];
      t5 !== 0 && a2.push([r2[c2][0], t5]);
    }
    c2 += 1, l2 += 1;
  } else if (compare_function(r2[c2][0], i2[l2][0]) < 0) a2.push(r2[c2]), c2 += 1;
  else if (compare_function(i2[l2][0], r2[c2][0]) < 0) return;
  if (!(l2 < s2)) {
    for (; c2 < o2; ) a2.push(r2[c2]), c2 += 1;
    return a2.length === 0 ? n2[1] === 1 ? t4[1] : evaluate_numbers([`/`, t4[1], n2[1]]) : n2[1] === 1 ? [`monomial`, t4[1], a2] : [`monomial`, evaluate_numbers([`/`, t4[1], n2[1]]), a2];
  }
}
function mono_is_div(t4, n2) {
  if (n2 === 0) return false;
  if (!Array.isArray(n2) || n2[0] !== `monomial`) return true;
  if (!Array.isArray(t4) || t4[0] !== `monomial`) return false;
  let r2 = t4[2], i2 = n2[2], a2 = [], o2 = r2.length, s2 = i2.length, c2 = 0, l2 = 0;
  for (; c2 < o2 && l2 < s2; ) if (r2[c2][0] === i2[l2][0]) {
    if (r2[c2][1] < i2[l2][1]) return false;
    {
      let t5 = r2[c2][1] - i2[l2][1];
      t5 !== 0 && a2.push([r2[c2][0], t5]);
    }
    c2 += 1, l2 += 1;
  } else if (compare_function(r2[c2][0], i2[l2][0]) < 0) a2.push(r2[c2]), c2 += 1;
  else if (compare_function(i2[l2][0], r2[c2][0]) < 0) return false;
  return !(l2 < s2);
}
function mono_to_poly(t4) {
  if (!Array.isArray(t4) || t4[0] !== `monomial`) return t4;
  let n2 = t4[2].length - 1, r2 = t4[1];
  for (; n2 >= 0; ) {
    var i2 = [];
    i2.push([t4[2][n2][1], r2]), r2 = [`polynomial`, t4[2][n2][0], i2], --n2;
  }
  return r2;
}
function max_div_init(t4, n2) {
  if (t4 === 0) return 0;
  let r2 = t4, i2 = [];
  for (; Array.isArray(r2) && r2[0] === `polynomial`; ) {
    let t5 = r2[1], n3 = r2[2], a3 = n3[n3.length - 1][0];
    r2 = n3[n3.length - 1][1], i2.push([t5, a3]);
  }
  let a2 = [`monomial`, r2, i2], o2 = n2.length;
  for (var s2 = 0; s2 < o2; s2 += 1) if (mono_is_div(a2, n2[s2])) return [a2, s2];
  return max_div_init(polynomial_sub(t4, mono_to_poly(a2)), n2);
}
function poly_div(t4, n2) {
  let r2 = [], i2 = [], a2 = [], o2 = [], s2 = t4;
  for (var c2 of n2) r2.push(initial_term(c2));
  let l2 = max_div_init(s2, r2);
  for (; l2 !== 0; ) a2 = l2[1], o2 = mono_div(l2[0], r2[a2]), i2.push([a2, o2]), s2 = polynomial_sub(s2, polynomial_mul(mono_to_poly(o2), n2[a2])), l2 = max_div_init(s2, r2);
  return [i2, s2];
}
function prereduce(t4) {
  let n2 = t4.length, r2 = [];
  for (var i2 = 0; i2 < n2; i2++) {
    if (t4[i2] !== 0 && (!Array.isArray(t4[i2]) || t4[i2][0] !== `polynomial`)) return [1];
    t4[i2] !== 0 && r2.push(t4[i2]);
  }
  return r2.length === 0 ? [0] : r2;
}
function reduce_ith(t4, n2) {
  let r2 = [], a2 = [], o2 = [], s2 = n2[t4], c2 = n2.length;
  for (var l2 = 0; l2 < c2; l2 += 1) l2 === t4 ? r2.push(0) : r2.push(initial_term(n2[l2]));
  let u2 = max_div_init(s2, r2);
  for (; u2 !== 0; ) a2 = u2[1], o2 = mono_div(u2[0], r2[a2]), s2 = polynomial_sub(s2, polynomial_mul(mono_to_poly(o2), n2[a2])), u2 = max_div_init(s2, r2);
  return s2;
}
function reduce(t4) {
  let n2 = 0, r2 = [], i2 = prereduce(t4), a2 = i2.length;
  if (a2 === 1) return i2;
  let o2 = true;
  for (; o2; ) for (o2 = false, i2 = prereduce(i2), a2 = i2.length, n2 = 0; n2 < a2; ) r2 = reduce_ith(n2, i2), _.isEqual(r2, i2[n2]) || (i2[n2] = r2, o2 = true), n2 += 1;
  n2 = 0;
  let s2 = [], c2 = 0;
  for (; n2 < a2; ) s2 = initial_term(i2[n2]), !Array.isArray(s2) || s2[0] !== `monomial` ? i2[n2] = 1 : (c2 = s2[1], c2 !== 1 && (i2[n2] = polynomial_mul(i2[n2], [`/`, 1, c2]))), n2 += 1;
  return i2;
}
function hij(t4, n2, r2) {
  let i2 = initial_term(r2[t4]), a2 = initial_term(r2[n2]), o2 = mono_gcd(i2, a2), s2 = mono_to_poly(mono_div(i2, o2));
  return poly_div(polynomial_sub(polynomial_mul(mono_to_poly(mono_div(a2, o2)), r2[t4]), polynomial_mul(s2, r2[n2])), r2)[1];
}
function build_hij_table(t4, n2) {
  let r2 = n2.length, i2 = 0, a2 = 1, o2 = [];
  for (; a2 < r2; ) {
    for (; i2 < a2; ) t4[[i2, a2]] = hij(i2, a2, n2), t4[[i2, a2]] !== 0 && o2.push(t4[[i2, a2]]), i2 += 1;
    i2 = 0, a2 += 1;
  }
  return o2.length > 0 ? choice_lowest_degree(o2) : 0;
}
function update_hij_table(t4, n2, r2) {
  let i2 = n2.length;
  n2.push(r2);
  let a2 = 0, o2 = 1, s2 = [];
  for (; o2 < i2; ) {
    for (; a2 < o2; ) t4[[a2, o2]] !== 0 && (t4[[a2, o2]] = hij(a2, o2, n2), t4[[a2, o2]] !== 0 && s2.push(t4[[a2, o2]])), a2 += 1;
    a2 = 0, o2 += 1;
  }
  for (; a2 < i2; ) t4[[a2, i2]] = hij(a2, i2, n2), t4[[a2, i2]] !== 0 && s2.push(t4[[a2, i2]]), a2 += 1;
  return s2.length > 0 ? choice_lowest_degree(s2) : 0;
}
function choice_lowest_degree(t4) {
  let n2 = [], r2 = t4.length;
  for (a2 = 0; a2 < r2; a2 += 1) n2.push(initial_term(t4[a2]));
  let i2 = 0;
  for (var a2 = 1; a2 < r2; a2 += 1) {
    if (n2[a2] === 1) return 1;
    mono_less_than(n2[a2], n2[i2]) && (i2 = a2);
  }
  return t4[i2];
}
function reduced_grobner(t4) {
  let n2 = reduce(t4), r2 = {}, i2 = build_hij_table(r2, n2);
  for (; i2 !== 0; ) i2 = update_hij_table(r2, n2, i2);
  return n2 = reduce(n2), n2;
}
function poly_lcm(t4, n2) {
  let r2 = reduced_grobner([polynomial_mul([`polynomial`, `_t`, [[1, 1]]], t4), polynomial_mul([`polynomial`, `_t`, [[0, 1], [1, -1]]], n2)]), i2 = r2.length;
  for (var a2 = 0; a2 < i2; a2 += 1) {
    if (!Array.isArray(r2[a2]) || r2[a2][0] !== `polynomial`) return 1;
    if (r2[a2][1] !== `_t`) return r2[a2];
  }
}
function poly_gcd(t4, n2) {
  let r2 = poly_lcm(t4, n2), i2 = poly_div(polynomial_mul(t4, n2), [r2]), a2 = 0, o2 = i2[0].length;
  for (var s2 = 0; s2 < o2; s2 += 1) a2 = polynomial_add(a2, mono_to_poly(i2[0][s2][1]));
  let c2 = initial_term(a2);
  if (!Array.isArray(c2) || c2[0] !== `monomial`) a2 = 1;
  else {
    let t5 = c2[1];
    t5 !== 1 && (a2 = polynomial_mul(a2, [`/`, 1, t5]));
  }
  return a2;
}
function poly_by_divisor(t4, n2) {
  let r2 = poly_div(t4, [n2]), i2 = 0, a2 = r2[0].length;
  for (var o2 = 0; o2 < a2; o2 += 1) i2 = polynomial_add(i2, mono_to_poly(r2[0][o2][1]));
  return i2;
}
function reduce_rational_expression(t4, n2) {
  let r2 = stringify_vars(t4), i2 = stringify_vars(n2);
  t4 = r2[0], n2 = i2[0];
  let a2 = poly_gcd(t4, n2), o2 = initial_term(n2)[1], s2 = polynomial_mul(a2, o2), c2 = poly_by_divisor(t4, s2), l2 = poly_by_divisor(n2, s2);
  return [destringify_vars(destringify_vars(c2, r2[1]), i2[1]), destringify_vars(destringify_vars(l2, r2[1]), i2[1])];
}
function stringify_vars(t4) {
  if (!Array.isArray(t4) || t4[0] !== `polynomial`) return [t4, {}];
  let n2 = {}, r2 = `z` + JSON.stringify(t4[1]);
  n2[r2] = t4[1], t4[1] = r2;
  let i2 = t4[2], a2 = [];
  for (let t5 of i2) {
    a2 = stringify_vars(t5[1]), t5[1] = a2[0];
    for (let t6 in a2[1]) n2[t6] = a2[1][t6];
  }
  return [t4, n2];
}
function destringify_vars(t4, n2) {
  if (!Array.isArray(t4) || t4[0] !== `polynomial`) return t4;
  n2[t4[1]] && (t4[1] = n2[t4[1]]);
  let r2 = t4[2];
  for (let t5 of r2) t5[1] = destringify_vars(t5[1], n2);
  return t4;
}
var rational_exports = __exportAll({ common_denominator: () => common_denominator, reduce_rational: () => reduce_rational }), textToAst$1 = new textToAst$4();
function common_denominator(t4, n2) {
  t4 = simplify(t4, n2);
  var r2 = [];
  return r2.push([textToAst$1.convert(`a/c+b/c`), textToAst$1.convert(`(a+b)/c`), { evaluate_numbers: true, allow_extended_match: true, allow_permutations: true, max_group: 1 }]), r2.push([textToAst$1.convert(`a/c-b/c`), textToAst$1.convert(`(a-b)/c`), { evaluate_numbers: true, allow_extended_match: true, allow_permutations: true, max_group: 1 }]), r2.push([textToAst$1.convert(`a+b/c`), textToAst$1.convert(`(ac+b)/c`), { evaluate_numbers: true, allow_extended_match: true, allow_permutations: true, max_group: 1 }]), r2.push([textToAst$1.convert(`a-b/c`), textToAst$1.convert(`(ac-b)/c`), { evaluate_numbers: true, allow_extended_match: true, allow_permutations: true, max_group: 1 }]), r2.push([textToAst$1.convert(`a/d+b/c`), textToAst$1.convert(`(ac+bd)/(cd)`), { evaluate_numbers: true, allow_extended_match: true, allow_permutations: true, max_group: 1 }]), r2.push([textToAst$1.convert(`a/d-b/c`), textToAst$1.convert(`(ac-bd)/(cd)`), { evaluate_numbers: true, allow_extended_match: true, allow_permutations: true, max_group: 1 }]), r2.push([textToAst$1.convert(`x*(y/z)`), textToAst$1.convert(`(x*y)/z`), { allow_extended_match: true, allow_permutations: true, max_group: 1 }]), r2.push([textToAst$1.convert(`(x/y)/z`), textToAst$1.convert(`x/(y*z)`), { allow_extended_match: true, allow_permutations: true }]), r2.push([textToAst$1.convert(`x/(y/z)`), textToAst$1.convert(`xz/y`), { allow_extended_match: true, allow_permutations: true }]), t4 = applyAllTransformations(t4, r2, 40), t4 = expand(t4, true), t4;
}
function as_num_denom(t4) {
  var n2 = get_tree(t4);
  return n2 = common_denominator(n2), n2[0] === `/` ? [n2[1], n2[2]] : [n2, 1];
}
function reduce_rational(t4) {
  var n2 = get_tree(t4), r2 = as_num_denom(n2);
  if (r2[1] === 1) return n2;
  var i2 = reduce_rational_expression(expression_to_polynomial(r2[0]), expression_to_polynomial(r2[1])), a2 = polynomial_to_expression(i2[0]), o2 = polynomial_to_expression(i2[1]);
  return o2 === 1 ? a2 : [`/`, a2, o2];
}
var round_exports = __exportAll({ round_numbers_to_decimals: () => round_numbers_to_decimals, round_numbers_to_precision: () => round_numbers_to_precision, round_numbers_to_precision_plus_decimals: () => round_numbers_to_precision_plus_decimals });
function toFixed(t4, n2) {
  return mathjs_default.format(t4, { notation: `fixed`, precision: n2 });
}
function round_numbers_to_precision(t4, n2 = 14) {
  var r2 = get_tree(t4);
  if (n2 < 1) throw Error(`For round_numbers_to_precision, digits must be positive`);
  if (!Number.isFinite(n2)) throw Error(`For round_numbers_to_precision, digits must be a number`);
  return n2 > 15 ? r2 : (n2 = Math.round(n2), round_numbers_to_precision_sub(r2, n2));
}
var round_numbers_to_precision_sub = function(t4, n2 = 14) {
  if (typeof t4 == `number` && Number.isFinite(t4) && t4 !== 0) {
    let r2 = n2 - mathjs_default.floor(mathjs_default.log10(mathjs_default.abs(t4))) - 1;
    return parseFloat(r2 < 0 ? toFixed(t4, r2) + `0`.repeat(mathjs_default.abs(r2)) : toFixed(t4, r2));
  }
  return Array.isArray(t4) ? [t4[0], ...t4.slice(1).map((t5) => round_numbers_to_precision_sub(t5, n2))] : t4;
};
function round_numbers_to_decimals(t4, n2 = 14) {
  var r2 = get_tree(t4);
  if (!Number.isFinite(n2)) throw Error(`For round_numbers_to_decimals, ndecimals must be a number`);
  return n2 = Math.round(n2), n2 = Math.max(-330, Math.min(330, n2)), round_numbers_to_decimals_sub(r2, n2);
}
var round_numbers_to_decimals_sub = function(t4, n2 = 0) {
  return typeof t4 == `number` ? parseFloat(n2 < 0 ? toFixed(t4, n2) + `0`.repeat(mathjs_default.abs(n2)) : toFixed(t4, n2)) : Array.isArray(t4) ? [t4[0], ...t4.slice(1).map((t5) => round_numbers_to_decimals_sub(t5, n2))] : t4;
};
function round_numbers_to_precision_plus_decimals(t4, n2 = 4, r2 = 2) {
  var i2 = get_tree(t4);
  let a2 = true, o2 = true;
  if (n2 >= 1) {
    if (n2 = Math.round(n2), n2 > 15) return i2;
  } else a2 = false;
  return Number.isFinite(r2) ? (r2 = Math.round(r2), r2 = Math.max(-330, Math.min(330, r2))) : o2 = false, a2 ? o2 ? round_numbers_to_precision_plus_decimals_sub(i2, n2, r2) : round_numbers_to_precision_sub(i2, n2) : o2 ? round_numbers_to_decimals_sub(i2, r2) : i2;
}
var round_numbers_to_precision_plus_decimals_sub = function(t4, n2 = 4, r2 = 2) {
  if (typeof t4 == `number` && Number.isFinite(t4) && t4 !== 0) {
    let i2 = mathjs_default.floor(mathjs_default.log10(mathjs_default.abs(t4))), a2 = Math.max(n2 - i2 - 1, r2);
    return parseFloat(a2 < 0 ? toFixed(t4, a2) + `0`.repeat(mathjs_default.abs(a2)) : toFixed(t4, a2));
  }
  return Array.isArray(t4) ? [t4[0], ...t4.slice(1).map((t5) => round_numbers_to_precision_plus_decimals_sub(t5, n2, r2))] : t4;
}, match_exports = __exportAll({ match: () => match }), match = function(t4, n2, r2) {
  return match$1(get_tree(t4), get_tree(n2), r2);
}, expression_to_tree$1 = [simplify_exports, differentiation_exports, normalization_exports, arithmetic_exports, transformation_exports, solve_exports, sets_exports, matrix_exports, rational_exports, round_exports], expression_to_other = [variables_exports, printing_exports, equality_exports, integration_exports, evaluation_exports, analytic_exports, sign_error_exports, match_exports], standard_exports = __exportAll({ abs: () => abs, acos: () => acos, acosh: () => acosh, acot: () => acot, acoth: () => acoth, acsc: () => acsc, acsch: () => acsch, asec: () => asec, asech: () => asech, asin: () => asin, asinh: () => asinh, atan: () => atan, atan2: () => atan2, atanh: () => atanh, conj: () => conj, cos: () => cos, cosh: () => cosh, cot: () => cot, coth: () => coth, csc: () => csc, csch: () => csch, erf: () => erf, exp: () => exp, factorial: () => factorial, gamma: () => gamma, im: () => im, log: () => log, log10: () => log10, re: () => re, sec: () => sec, sech: () => sech, sign: () => sign, sin: () => sin, sinh: () => sinh, sqrt: () => sqrt, tan: () => tan, tanh: () => tanh });
function abs(t4) {
  return [`apply`, `abs`, get_tree(t4)];
}
function exp(t4) {
  return [`apply`, `exp`, get_tree(t4)];
}
function log(t4) {
  return [`apply`, `log`, get_tree(t4)];
}
function log10(t4) {
  return [`apply`, `log10`, get_tree(t4)];
}
function sign(t4) {
  return [`apply`, `sign`, get_tree(t4)];
}
function sqrt(t4) {
  return [`apply`, `sqrt`, get_tree(t4)];
}
function conj(t4) {
  return [`apply`, `conj`, get_tree(t4)];
}
function im(t4) {
  return [`apply`, `im`, get_tree(t4)];
}
function re(t4) {
  return [`apply`, `re`, get_tree(t4)];
}
function factorial(t4) {
  return [`apply`, `factorial`, get_tree(t4)];
}
function gamma(t4) {
  return [`apply`, `gamma`, get_tree(t4)];
}
function erf(t4) {
  return [`apply`, `erf`, get_tree(t4)];
}
function acos(t4) {
  return [`apply`, `acos`, get_tree(t4)];
}
function acosh(t4) {
  return [`apply`, `acosh`, get_tree(t4)];
}
function acot(t4) {
  return [`apply`, `acot`, get_tree(t4)];
}
function acoth(t4) {
  return [`apply`, `acoth`, get_tree(t4)];
}
function acsc(t4) {
  return [`apply`, `acsc`, get_tree(t4)];
}
function acsch(t4) {
  return [`apply`, `acsch`, get_tree(t4)];
}
function asec(t4) {
  return [`apply`, `asec`, get_tree(t4)];
}
function asech(t4) {
  return [`apply`, `asech`, get_tree(t4)];
}
function asin(t4) {
  return [`apply`, `asin`, get_tree(t4)];
}
function asinh(t4) {
  return [`apply`, `asinh`, get_tree(t4)];
}
function atan(t4) {
  return [`apply`, `atan`, get_tree(t4)];
}
function atanh(t4) {
  return [`apply`, `atanh`, get_tree(t4)];
}
function cos(t4) {
  return [`apply`, `cos`, get_tree(t4)];
}
function cosh(t4) {
  return [`apply`, `cosh`, get_tree(t4)];
}
function cot(t4) {
  return [`apply`, `cot`, get_tree(t4)];
}
function coth(t4) {
  return [`apply`, `coth`, get_tree(t4)];
}
function csc(t4) {
  return [`apply`, `csc`, get_tree(t4)];
}
function csch(t4) {
  return [`apply`, `csch`, get_tree(t4)];
}
function sec(t4) {
  return [`apply`, `sec`, get_tree(t4)];
}
function sech(t4) {
  return [`apply`, `sech`, get_tree(t4)];
}
function sin(t4) {
  return [`apply`, `sin`, get_tree(t4)];
}
function sinh(t4) {
  return [`apply`, `sinh`, get_tree(t4)];
}
function tan(t4) {
  return [`apply`, `tan`, get_tree(t4)];
}
function tanh(t4) {
  return [`apply`, `tanh`, get_tree(t4)];
}
function atan2(t4, n2) {
  return [`apply`, `atan2`, [`tuple`, get_tree(t4), get_tree(n2)]];
}
var expression_to_tree = [standard_exports], whitespace_rule = `\\s|\\\\,|\\\\!|\\\\ |\\\\>|\\\\;|\\\\:|\\\\quad\\b|\\\\qquad\\b`, number_rules_sci = [[`[0-9]+(\\.[0-9]*)?(E[+\\-]?[0-9]+\\s*($|(?=\\,|&|\\||\\\\\\||\\)|\\}|\\\\}|\\]|\\\\\\\\|\\\\end)))?`, `NUMBER`], [`\\.[0-9]+(E[+\\-]?[0-9]+\\s*($|(?=\\,|&|\\||\\\\\\||\\)|\\}|\\\\}|\\]|\\\\\\\\|\\\\end)))?`, `NUMBER`]], number_rules_non_sci = [[`[0-9]+(\\.[0-9]*)?`, `NUMBER`], [`\\.[0-9]+`, `NUMBER`]], base_latex_rules = [[`\\*`, `*`], [`\\/`, `/`], [`-`, `-`], [`\\+`, `+`], [`\\\\pm(?![a-zA-Z])`, `PM`], [`\\^`, `^`], [`\\(`, `(`], [`\\\\left\\s*\\(`, `(`], [`\\\\bigl\\s*\\(`, `(`], [`\\\\Bigl\\s*\\(`, `(`], [`\\\\biggl\\s*\\(`, `(`], [`\\\\Biggl\\s*\\(`, `(`], [`\\)`, `)`], [`\\\\right\\s*\\)`, `)`], [`\\\\bigr\\s*\\)`, `)`], [`\\\\Bigr\\s*\\)`, `)`], [`\\\\biggr\\s*\\)`, `)`], [`\\\\Biggr\\s*\\)`, `)`], [`\\[`, `[`], [`\\\\left\\s*\\[`, `[`], [`\\\\bigl\\s*\\[`, `[`], [`\\\\Bigl\\s*\\[`, `[`], [`\\\\biggl\\s*\\[`, `[`], [`\\\\Biggl\\s*\\[`, `[`], [`\\]`, `]`], [`\\\\right\\s*\\]`, `]`], [`\\\\bigr\\s*\\]`, `]`], [`\\\\Bigr\\s*\\]`, `]`], [`\\\\biggr\\s*\\]`, `]`], [`\\\\Biggr\\s*\\]`, `]`], [`\\|`, `|`], [`\\\\left\\s*\\|`, `|L`], [`\\\\bigl\\s*\\|`, `|L`], [`\\\\Bigl\\s*\\|`, `|L`], [`\\\\biggl\\s*\\|`, `|L`], [`\\\\Biggl\\s*\\|`, `|L`], [`\\\\right\\s*\\|`, `|`], [`\\\\bigr\\s*\\|`, `|`], [`\\\\Bigr\\s*\\|`, `|`], [`\\\\biggr\\s*\\|`, `|`], [`\\\\Biggr\\s*\\|`, `|`], [`\\\\big\\s*\\|`, `|`], [`\\\\Big\\s*\\|`, `|`], [`\\\\bigg\\s*\\|`, `|`], [`\\\\Bigg\\s*\\|`, `|`], [`{`, `{`], [`}`, `}`], [`\\\\{`, `LBRACE`], [`\\\\left\\s*\\\\{`, `LBRACE`], [`\\\\bigl\\s*\\\\{`, `LBRACE`], [`\\\\Bigl\\s*\\\\{`, `LBRACE`], [`\\\\biggl\\s*\\\\{`, `LBRACE`], [`\\\\Biggl\\s*\\\\{`, `LBRACE`], [`\\\\}`, `RBRACE`], [`\\\\right\\s*\\\\}`, `RBRACE`], [`\\\\bigr\\s*\\\\}`, `RBRACE`], [`\\\\Bigr\\s*\\\\}`, `RBRACE`], [`\\\\biggr\\s*\\\\}`, `RBRACE`], [`\\\\Biggr\\s*\\\\}`, `RBRACE`], [`\\\\lfloor(?![a-zA-Z])`, `LFLOOR`], [`\\\\left\\s*\\\\lfloor(?![a-zA-Z])`, `LFLOOR`], [`\\\\bigl\\s*\\\\lfloor(?![a-zA-Z])`, `LFLOOR`], [`\\\\Bigl\\s*\\\\lfloor(?![a-zA-Z])`, `LFLOOR`], [`\\\\biggl\\s*\\\\lfloor(?![a-zA-Z])`, `LFLOOR`], [`\\\\Biggl\\s*\\\\lfloor(?![a-zA-Z])`, `LFLOOR`], [`\\\\rfloor(?![a-zA-Z])`, `RFLOOR`], [`\\\\right\\s*\\\\rfloor(?![a-zA-Z])`, `RFLOOR`], [`\\\\bigr\\s*\\\\rfloor(?![a-zA-Z])`, `RFLOOR`], [`\\\\Bigr\\s*\\\\rfloor(?![a-zA-Z])`, `RFLOOR`], [`\\\\biggr\\s*\\\\rfloor(?![a-zA-Z])`, `RFLOOR`], [`\\\\Biggr\\s*\\\\rfloor(?![a-zA-Z])`, `RFLOOR`], [`\\\\lceil(?![a-zA-Z])`, `LCEIL`], [`\\\\left\\s*\\\\lceil(?![a-zA-Z])`, `LCEIL`], [`\\\\bigl\\s*\\\\lceil(?![a-zA-Z])`, `LCEIL`], [`\\\\Bigl\\s*\\\\lceil(?![a-zA-Z])`, `LCEIL`], [`\\\\biggl\\s*\\\\lceil(?![a-zA-Z])`, `LCEIL`], [`\\\\Biggl\\s*\\\\lceil(?![a-zA-Z])`, `LCEIL`], [`\\\\rceil(?![a-zA-Z])`, `RCEIL`], [`\\\\right\\s*\\\\rceil(?![a-zA-Z])`, `RCEIL`], [`\\\\bigr\\s*\\\\rceil(?![a-zA-Z])`, `RCEIL`], [`\\\\Bigr\\s*\\\\rceil(?![a-zA-Z])`, `RCEIL`], [`\\\\biggr\\s*\\\\rceil(?![a-zA-Z])`, `RCEIL`], [`\\\\Biggr\\s*\\\\rceil(?![a-zA-Z])`, `RCEIL`], [`\\\\langle(?![a-zA-Z])`, `LANGLE`], [`\\\\left\\s*\\\\langle(?![a-zA-Z])`, `LANGLE`], [`\\\\bigl\\s*\\\\langle(?![a-zA-Z])`, `LANGLE`], [`\\\\Bigl\\s*\\\\langle(?![a-zA-Z])`, `LANGLE`], [`\\\\biggl\\s*\\\\langle(?![a-zA-Z])`, `LANGLE`], [`\\\\Biggl\\s*\\\\langle(?![a-zA-Z])`, `LANGLE`], [`\\\\rangle(?![a-zA-Z])`, `RANGLE`], [`\\\\right\\s*\\\\rangle(?![a-zA-Z])`, `RANGLE`], [`\\\\bigr\\s*\\\\rangle(?![a-zA-Z])`, `RANGLE`], [`\\\\Bigr\\s*\\\\rangle(?![a-zA-Z])`, `RANGLE`], [`\\\\biggr\\s*\\\\rangle(?![a-zA-Z])`, `RANGLE`], [`\\\\Biggr\\s*\\\\rangle(?![a-zA-Z])`, `RANGLE`], [`\\\\cdot(?![a-zA-Z])`, `*`], [`\\\\div(?![a-zA-Z])`, `/`], [`\\\\times(?![a-zA-Z])`, `*`], [`,`, `,`], [`:`, `:`], [`\\\\mid(?![a-zA-Z])`, `MID`], [`\\\\varnothing(?![a-zA-Z])`, `LATEXCOMMAND`, `\\emptyset`], [`\\\\vartheta(?![a-zA-Z])`, `LATEXCOMMAND`, `\\theta`], [`\\\\varepsilon(?![a-zA-Z])`, `LATEXCOMMAND`, `\\epsilon`], [`\\\\varrho(?![a-zA-Z])`, `LATEXCOMMAND`, `\\rho`], [`\\\\varphi(?![a-zA-Z])`, `LATEXCOMMAND`, `\\phi`], [`\\\\infty(?![a-zA-Z])`, `INFINITY`], [`\\\\asin(?![a-zA-Z])`, `LATEXCOMMAND`, `\\arcsin`], [`\\\\acos(?![a-zA-Z])`, `LATEXCOMMAND`, `\\arccos`], [`\\\\atan(?![a-zA-Z])`, `LATEXCOMMAND`, `\\arctan`], [`\\\\sqrt(?![a-zA-Z])`, `SQRT`], [`\\\\land(?![a-zA-Z])`, `AND`], [`\\\\wedge(?![a-zA-Z])`, `AND`], [`\\\\lor(?![a-zA-Z])`, `OR`], [`\\\\vee(?![a-zA-Z])`, `OR`], [`\\\\lnot(?![a-zA-Z])`, `NOT`], [`\\\\neg(?![a-zA-Z])`, `NOT`], [`=`, `=`], [`\\\\neq(?![a-zA-Z])`, `NE`], [`\\\\ne(?![a-zA-Z])`, `NE`], [`\\\\not\\s*=`, `NE`], [`\\\\leq(?![a-zA-Z])`, `LE`], [`\\\\le(?![a-zA-Z])`, `LE`], [`\\\\geq(?![a-zA-Z])`, `GE`], [`\\\\ge(?![a-zA-Z])`, `GE`], [`<`, `<`], [`\\\\lt(?![a-zA-Z])`, `<`], [`>`, `>`], [`\\\\gt(?![a-zA-Z])`, `>`], [`\\\\forall(?![a-zA-Z])`, `FORALL`], [`\\\\exists(?![a-zA-Z])`, `EXISTS`], [`\\\\in(?![a-zA-Z])`, `IN`], [`\\\\notin(?![a-zA-Z])`, `NOTIN`], [`\\\\not\\s*\\\\in(?![a-zA-Z])`, `NOTIN`], [`\\\\ni(?![a-zA-Z])`, `NI`], [`\\\\not\\s*\\\\ni(?![a-zA-Z])`, `NOTNI`], [`\\\\subset(?![a-zA-Z])`, `SUBSET`], [`\\\\subseteq(?![a-zA-Z])`, `SUBSETEQ`], [`\\\\not\\s*\\\\subset(?![a-zA-Z])`, `NOTSUBSET`], [`\\\\not\\s*\\\\subseteq(?![a-zA-Z])`, `NOTSUBSETEQ`], [`\\\\supset(?![a-zA-Z])`, `SUPERSET`], [`\\\\supseteq(?![a-zA-Z])`, `SUPERSETEQ`], [`\\\\not\\s*\\\\supset(?![a-zA-Z])`, `NOTSUPERSET`], [`\\\\not\\s*\\\\supseteq(?![a-zA-Z])`, `NOTSUPERSETEQ`], [`\\\\cup(?![a-zA-Z])`, `UNION`], [`\\\\cap(?![a-zA-Z])`, `INTERSECT`], [`\\\\to(?![a-zA-Z])`, `RIGHTARROW`], [`\\\\rightarrow(?![a-zA-Z])`, `RIGHTARROW`], [`\\\\longrightarrow(?![a-zA-Z])`, `RIGHTARROW`], [`\\\\gets(?![a-zA-Z])`, `LEFTARROW`], [`\\\\leftarrow(?![a-zA-Z])`, `LEFTARROW`], [`\\\\longleftarrow(?![a-zA-Z])`, `LEFTARROW`], [`\\\\leftrightarrow(?![a-zA-Z])`, `LEFTRIGHTARROW`], [`\\\\longleftrightarrow(?![a-zA-Z])`, `LEFTRIGHTARROW`], [`\\\\implies(?![a-zA-Z])`, `IMPLIES`], [`\\\\Longrightarrow(?![a-zA-Z])`, `IMPLIES`], [`\\\\Rightarrow(?![a-zA-Z])`, `IMPLIES`], [`\\\\impliedby(?![a-zA-Z])`, `IMPLIEDBY`], [`\\\\Longleftarrow(?![a-zA-Z])`, `IMPLIEDBY`], [`\\\\Leftarrow(?![a-zA-Z])`, `IMPLIEDBY`], [`\\\\iff(?![a-zA-Z])`, `IFF`], [`\\\\Longleftrightarrow(?![a-zA-Z])`, `IFF`], [`\\\\Leftrightarrow(?![a-zA-Z])`, `IFF`], [`\\\\perp(?![a-zA-Z])`, `PERP`], [`\\\\bot(?![a-zA-Z])`, `PERP`], [`\\\\parallel(?![a-zA-Z])`, `PARALLEL`], [`\\\\\\|`, `PARALLEL`], [`\\\\angle(?![a-zA-Z])`, `ANGLE`], [`\\\\int(?![a-zA-Z])`, `INT`], [`!`, `!`], [`'`, `'`], [`_`, `_`], [`&`, `&`], [`\\\\ldots(?![a-zA-Z])`, `LDOTS`], [`\\\\\\\\`, `LINEBREAK`], [`\\\\begin\\s*{\\s*[a-zA-Z0-9]+\\s*}`, `BEGINENVIRONMENT`], [`\\\\end\\s*{\\s*[a-zA-Z0-9]+\\s*}`, `ENDENVIRONMENT`], [`\\\\operatorname\\s*{\\s*[a-zA-Z0-9\\+\\-]+\\s*}`, `VARMULTICHAR`], [`\\\\[a-zA-Z]+(?![a-zA-Z])`, `LATEXCOMMAND`], [`\\\\[$%]`, `LATEXCOMMAND`], [`[a-zA-Z＿$%]`, `VAR`]], allowSimplifiedFunctionApplicationDefault = true, allowedLatexSymbolsDefault = `alpha.beta.gamma.Gamma.delta.Delta.epsilon.zeta.eta.theta.Theta.iota.kappa.lambda.Lambda.mu.nu.xi.Xi.pi.Pi.rho.sigma.Sigma.tau.Tau.upsilon.Upsilon.phi.Phi.chi.psi.Psi.omega.Omega.partial.angle.circ.%.$.emptyset`.split(`.`), appliedFunctionSymbolsDefault = `abs.exp.log.ln.log10.sign.sqrt.erf.cos.cosh.acos.acosh.arccos.arccosh.cot.coth.acot.acoth.arccot.arccoth.csc.csch.acsc.acsch.arccsc.arccsch.sec.sech.asec.asech.arcsec.arcsech.sin.sinh.asin.asinh.arcsin.arcsinh.tan.tanh.atan.atan2.atanh.arctan.arctanh.arg.conj.Re.Im.det.trace.nPr.nCr.floor.ceil.round`.split(`.`), functionSymbolsDefault = [`f`, `g`], operatorSymbolsDefault = { frac: { nargs: 2, substitute: `/` }, binom: { nargs: 2 }, vec: { nargs: 1 }, overline: { nargs: 1, substitute: `linesegment`, remove_products: true } }, unitsDefault = get_all_units();
unitsDefault.circ = unitsDefault.deg, unitsDefault.circ.substitute = `deg`, unitsDefault.circ.isExponent = true, delete unitsDefault.deg;
var parseLeibnizNotationDefault = true, latexToAst$1 = class {
  constructor({ allowSimplifiedFunctionApplication: t4 = allowSimplifiedFunctionApplicationDefault, allowedLatexSymbols: n2 = allowedLatexSymbolsDefault, appliedFunctionSymbols: r2 = appliedFunctionSymbolsDefault, functionSymbols: i2 = functionSymbolsDefault, operatorSymbols: a2 = operatorSymbolsDefault, units: o2 = unitsDefault, parseLeibnizNotation: s2 = parseLeibnizNotationDefault, parseScientificNotation: c2 = true } = {}) {
    this.allowSimplifiedFunctionApplication = t4, this.allowedLatexSymbols = n2, this.appliedFunctionSymbols = r2, this.functionSymbols = i2, this.operatorSymbols = a2, this.units = o2, this.parseLeibnizNotation = s2;
    let l2 = base_latex_rules;
    l2 = c2 ? [...number_rules_sci, ...l2] : [...number_rules_non_sci, ...l2], this.lexer = new lexer(l2, whitespace_rule);
  }
  advance(t4) {
    if (this.token = this.lexer.advance(t4), this.token.token_type === `INVALID`) throw new ParseError(`Invalid symbol '` + this.token.original_text + `'`, this.lexer.location);
  }
  return_state() {
    return { lexer_state: this.lexer.return_state(), token: Object.assign({}, this.token) };
  }
  set_state(t4) {
    this.lexer.set_state(t4.lexer_state), this.token = Object.assign({}, t4.token);
  }
  convert(t4) {
    this.lexer.set_input(t4), this.advance();
    var n2 = this.statement_list();
    if (this.token.token_type !== `EOF`) throw new ParseError(`Invalid location of '` + this.token.original_text + `'`, this.lexer.location);
    return flatten(n2);
  }
  statement_list() {
    for (var t4 = [this.statement()]; this.token.token_type === `,`; ) this.advance(), t4.push(this.statement());
    return t4 = t4.length > 1 ? [`list`].concat(t4) : t4[0], t4;
  }
  statement({ inside_absolute_value: t4 = 0 } = {}) {
    if (this.token.token_type === `LDOTS`) return this.advance(), [`ldots`];
    var n2;
    try {
      n2 = this.return_state();
      let r2 = this.statement_a({ inside_absolute_value: t4 });
      if (this.token.token_type !== `:` && this.token.token_type !== `MID`) return r2;
      let i2 = this.token.token_type === `:` ? `:` : `|`;
      return this.advance(), [i2, r2, this.statement_a()];
    } catch (t5) {
      try {
        this.set_state(n2);
        let r2 = this.statement_a({ parse_absolute_value: false });
        if (this.token.token_type[0] !== `|`) throw t5;
        return this.advance(), [`|`, r2, this.statement_a({ parse_absolute_value: false })];
      } catch {
        throw t5;
      }
    }
  }
  statement_a({ inside_absolute_value: t4 = 0, parse_absolute_value: n2 = true } = {}) {
    for (var r2 = this.statement_b({ inside_absolute_value: t4, parse_absolute_value: n2 }); [`IMPLIES`, `IMPLIEDBY`, `IFF`, `LEFTARROW`, `RIGHTARROW`, `LEFTRIGHTARROW`].includes(this.token.token_type); ) {
      let i2 = this.token.token_type.toLowerCase();
      this.advance();
      let a2 = this.statement_b({ inside_absolute_value: t4, parse_absolute_value: n2 });
      r2 = [i2, r2, a2];
    }
    return r2;
  }
  statement_b({ inside_absolute_value: t4 = 0, parse_absolute_value: n2 = true } = {}) {
    for (var r2 = this.statement_c({ inside_absolute_value: t4, parse_absolute_value: n2 }); this.token.token_type === `OR`; ) {
      let i2 = this.token.token_type.toLowerCase();
      this.advance();
      let a2 = this.statement_c({ inside_absolute_value: t4, parse_absolute_value: n2 });
      r2 = [i2, r2, a2];
    }
    return r2;
  }
  statement_c(t4) {
    for (var n2 = this.relation(t4); this.token.token_type === `AND`; ) {
      let r2 = this.token.token_type.toLowerCase();
      this.advance();
      let i2 = this.relation(t4);
      n2 = [r2, n2, i2];
    }
    return n2;
  }
  relation(t4) {
    if (this.token.token_type === `NOT` || this.token.token_type === `!`) return this.advance(), [`not`, this.relation(t4)];
    if (this.token.token_type === `FORALL` || this.token.token_type === `EXISTS`) {
      let n3 = this.token.token_type.toLowerCase();
      return this.advance(), [n3, this.relation(t4)];
    }
    for (var n2 = this.expression(t4); this.token.token_type === `=` || this.token.token_type === `NE` || this.token.token_type === `<` || this.token.token_type === `>` || this.token.token_type === `LE` || this.token.token_type === `GE` || this.token.token_type === `IN` || this.token.token_type === `NOTIN` || this.token.token_type === `NI` || this.token.token_type === `NOTNI` || this.token.token_type === `SUBSET` || this.token.token_type === `NOTSUBSET` || this.token.token_type === `SUBSETEQ` || this.token.token_type === `NOTSUBSETEQ` || this.token.token_type === `SUPERSET` || this.token.token_type === `NOTSUPERSET` || this.token.token_type === `SUPERSETEQ` || this.token.token_type === `NOTSUPERSETEQ`; ) {
      let r2 = this.token.token_type.toLowerCase(), i2 = 0;
      this.token.token_type === `<` || this.token.token_type === `LE` ? i2 = -1 : (this.token.token_type === `>` || this.token.token_type === `GE`) && (i2 = 1), this.advance();
      let a2 = this.expression(t4);
      if (i2 === -1) if (this.token.token_type === `<` || this.token.token_type === `LE`) {
        let i3 = [`tuple`];
        r2 === `<` ? i3.push(true) : i3.push(false);
        let o2 = [`tuple`, n2, a2];
        for (; this.token.token_type === `<` || this.token.token_type === `LE`; ) this.token.token_type === `<` ? i3.push(true) : i3.push(false), this.advance(), o2.push(this.expression(t4));
        n2 = [`lts`, o2, i3];
      } else n2 = [r2, n2, a2];
      else if (i2 === 1) if (this.token.token_type === `>` || this.token.token_type === `GE`) {
        let i3 = [`tuple`];
        r2 === `>` ? i3.push(true) : i3.push(false);
        let o2 = [`tuple`, n2, a2];
        for (; this.token.token_type === `>` || this.token.token_type === `GE`; ) this.token.token_type === `>` ? i3.push(true) : i3.push(false), this.advance(), o2.push(this.expression(t4));
        n2 = [`gts`, o2, i3];
      } else n2 = [r2, n2, a2];
      else if (r2 === `=`) for (n2 = [`=`, n2, a2]; this.token.token_type === `=`; ) this.advance(), n2.push(this.expression(t4));
      else n2 = [r2, n2, a2];
    }
    return n2;
  }
  expression(t4) {
    if (this.token.token_type === `NOT` || this.token.token_type === `!`) return this.advance(), [`not`, this.expression(t4)];
    let n2 = false;
    this.token.token_type === `+` && (n2 = true, this.advance());
    let r2 = false;
    this.token.token_type === `-` && (r2 = true, this.advance());
    let i2 = false;
    this.token.token_type === `PM` && (i2 = true, this.advance());
    var a2 = this.term(t4);
    if (r2 || n2) {
      if (a2 === false) return (n2 ? `+` : ``) + (r2 ? `-` : ``);
      if (typeof a2 == `string` && [...a2].every((t5) => [`+`, `-`].includes(t5))) return (n2 ? `+` : ``) + (r2 ? `-` : ``) + a2;
    }
    for (a2 === false && (a2 = `＿`), r2 && (a2 = a2 > 0 ? -a2 : [`-`, a2]), i2 && (a2 = [`pm`, a2]), n2 && (a2 = [`+`, a2]); [`+`, `-`, `PM`, `UNION`, `INTERSECT`, `PERP`, `PARALLEL`].includes(this.token.token_type); ) {
      let n3 = this.token.token_type.toLowerCase(), r3 = false, i3 = false, o2 = false;
      this.token.token_type === `-` ? (n3 = `+`, r3 = true, this.advance()) : this.token.token_type === `PM` ? (n3 = `+`, i3 = true, this.advance()) : (this.advance(), n3 === `+` && (this.token.token_type === `-` ? (r3 = true, o2 = true, this.advance()) : this.token.token_type === `PM` && (i3 = true, this.advance())));
      let s2 = this.term(t4);
      if (n3 === `+`) {
        if (s2 === false && (typeof a2 == `number` || typeof a2 == `string`)) {
          if (o2) return a2 + `+-`;
          if (r3) return a2 + `-`;
          if (!i3) return a2 + `+`;
        } else if (typeof s2 == `string` && [...s2].every((t5) => [`+`, `-`].includes(t5)) && (typeof a2 == `number` || typeof a2 == `string`)) {
          if (o2) return a2 + `+-` + s2;
          if (r3) return a2 + `-` + s2;
          if (!i3) return a2 + `+` + s2;
        }
      }
      s2 === false && (s2 = `＿`), r3 && (s2 = s2 > 0 ? -s2 : [`-`, s2]), i3 && (s2 = [`pm`, s2]), a2 = [n3, a2, s2];
    }
    return a2;
  }
  term(t4) {
    var n2 = this.factor(t4), r2 = false;
    do
      if (r2 = false, this.token.token_type === `*`) {
        this.advance(), n2 === false && (n2 = `＿`);
        let i2 = this.factor(t4);
        i2 === false && (i2 = `＿`), n2 = [`*`, n2, i2], r2 = true;
      } else if (this.token.token_type === `/`) {
        this.advance(), n2 === false && (n2 = `＿`);
        let i2 = this.factor(t4);
        i2 === false && (i2 = `＿`), n2 = [`/`, n2, i2], r2 = true;
      } else {
        let i2 = Object.assign({}, t4);
        i2.allow_absolute_value_closing = true;
        let a2 = this.nonMinusFactor(i2);
        a2 !== false && (n2 === false && (n2 = `＿`), n2 = [`*`, n2, a2], r2 = true);
      }
    while (r2);
    return this.convert_units_in_term(flatten(n2));
  }
  convert_units_in_term(t4) {
    if (!Array.isArray(t4)) return t4;
    let n2 = t4[0], r2 = t4.slice(1);
    if (n2 === `*`) {
      let t5 = r2.length;
      for (let [n3, i2] of r2.entries()) {
        let a2 = this.units[i2];
        if (a2) {
          if (a2.prefix && n3 < t5 - 1) {
            let o2;
            o2 = n3 === t5 - 2 ? r2[t5 - 1] : this.convert_units_in_term([`*`, ...r2.slice(n3 + 1)]);
            let s2 = i2;
            a2.substitute && (s2 = a2.substitute);
            let c2 = [`unit`, s2, o2];
            return n3 === 0 ? c2 : [`*`, ...r2.slice(0, n3).map(this.convert_units_in_term.bind(this)), c2];
          } else if (!a2.prefix && n3 > 0) {
            let o2 = i2;
            a2.substitute && (o2 = a2.substitute);
            let s2;
            return s2 = n3 === 1 ? [`unit`, r2[0], o2] : [`unit`, [`*`, ...r2.slice(0, n3).map(this.convert_units_in_term.bind(this))], o2], n3 === t5 - 1 ? s2 : this.convert_units_in_term([`*`, s2, ...r2.slice(n3 + 1)]);
          }
        }
      }
      return [n2, ...r2.map(this.convert_units_in_term.bind(this))];
    } else if (n2 === `/`) return [`/`, this.convert_units_in_term(r2[0]), this.convert_units_in_term(r2[1])];
    else if (n2 === `^`) {
      let t5 = this.units[r2[1]];
      if (t5 && t5.isExponent) {
        let n3 = r2[1];
        return t5.substitute && (n3 = t5.substitute), [`unit`, r2[0], n3];
      }
      return [n2, ...r2.map(this.convert_units_in_term.bind(this))];
    }
    return [n2, ...r2.map(this.convert_units_in_term.bind(this))];
  }
  factor(t4) {
    if (this.token.token_text === `+`) {
      this.advance();
      let n3 = this.factor(t4);
      return n3 === false ? `+` : typeof n3 == `string` && [...n3].every((t5) => [`+`, `-`].includes(t5)) ? `+` + n3 : [`+`, n3];
    }
    if (this.token.token_type === `-`) {
      this.advance();
      let n3 = this.factor(t4);
      return n3 > 0 ? -n3 : n3 === false ? `-` : typeof n3 == `string` && [...n3].every((t5) => [`+`, `-`].includes(t5)) ? `-` + n3 : [`-`, n3];
    }
    let n2 = this.nonMinusFactor(t4);
    return n2 === false && this.token.token_type === `PERP` && (n2 = `perp`, this.advance()), n2;
  }
  nonMinusFactor(t4) {
    for (var n2 = this.baseFactor(t4); this.token.token_type === `^` || this.token.token_type === `!` || this.token.token_type === `'`; ) if (n2 === false && (n2 = `＿`), this.token.token_type === `^`) {
      this.advance();
      let r2 = this.get_subsuperscript(t4);
      n2 = [`^`, n2, r2];
    } else this.token.token_type === `!` ? (n2 = [`apply`, `factorial`, n2], this.advance()) : (n2 = [`prime`, n2], this.advance());
    return n2;
  }
  get_single_digit_as_number() {
    if (this.token.token_type === `NUMBER` && this.token.token_text[0] !== `.`) {
      let t4 = Number(this.token.token_text[0]);
      return this.token.token_text.length > 1 && this.lexer.unput(this.token.token_text.slice(1)), this.advance(), t4;
    }
    return null;
  }
  get_subsuperscript({ parse_absolute_value: t4 }) {
    let n2 = this.get_single_digit_as_number();
    if (n2 !== null) return n2;
    if ([`+`, `-`, `PERP`].includes(this.token.token_type)) {
      let t5 = this.token.token_type.toLowerCase();
      return this.advance(), t5;
    } else {
      let n3 = this.baseFactor({ parse_absolute_value: t4, in_subsuperscript_with_no_delimiters: true });
      return n3 === false && (n3 = `＿`), n3;
    }
  }
  baseFactor({ inside_absolute_value: t4 = 0, parse_absolute_value: n2 = true, allow_absolute_value_closing: r2 = false, in_subsuperscript_with_no_delimiters: i2 = false } = {}) {
    var a2 = false;
    if (this.token.token_type === `BEGINENVIRONMENT`) {
      let t5 = /\\begin\s*{\s*([a-zA-Z0-9]+)\s*}/.exec(this.token.token_text)[1];
      if ([`matrix`, `pmatrix`, `bmatrix`].includes(t5)) {
        let r3 = 0, i3 = 0, o2 = [], s2 = [], c2 = 0, l2 = this.token.token_type;
        for (this.advance(); this.token.token_type !== `ENDENVIRONMENT`; ) this.token.token_type === `&` ? ((l2 === `&` || l2 === `LINEBREAK`) && (s2.push(0), c2 += 1), l2 = this.token.token_type, this.advance()) : this.token.token_type === `LINEBREAK` ? ((l2 === `&` || l2 === `LINEBREAK`) && (s2.push(0), c2 += 1), o2.push(s2), c2 > i3 && (i3 = c2), r3 += 1, c2 = 0, s2 = [], l2 = this.token.token_type, this.advance()) : (s2.push(this.statement({ parse_absolute_value: n2 })), c2 += 1, l2 = ` `);
        if (/\\end\s*{\s*([a-zA-Z0-9]+)\s*}/.exec(this.token.token_text)[1] !== t5) throw new ParseError(`Expecting \\end{` + t5 + `}`, this.lexer.location);
        l2 === `&` && (s2.push(0), c2 += 1), o2.push(s2), c2 > i3 && (i3 = c2), r3 += 1, this.advance(), a2 = [`matrix`, [`tuple`, r3, i3]];
        let u2 = [`tuple`];
        for (let t6 of o2) {
          let n3 = [`tuple`].concat(t6);
          for (let r4 = t6.length; r4 < i3; r4 += 1) n3.push(0);
          u2.push(n3);
        }
        return a2.push(u2), a2;
      } else throw new ParseError(`Unrecognized environment ` + t5, this.lexer.location);
    }
    if (this.token.token_type === `NUMBER`) a2 = parseFloat(this.token.token_text), this.advance();
    else if (this.token.token_type === `INFINITY`) a2 = 1 / 0, this.advance();
    else if (this.token.token_type === `SQRT`) {
      this.advance();
      let t5 = 2;
      if (this.token.token_type === `[`) {
        this.advance();
        let r4 = this.statement({ parse_absolute_value: n2 });
        if (this.token.token_type !== `]`) throw new ParseError(`Expecting ]`, this.lexer.location);
        this.advance(), t5 = r4;
      }
      if (this.token.token_type !== `{`) throw new ParseError(`Expecting {`, this.lexer.location);
      this.advance();
      let r3 = this.statement({ parse_absolute_value: n2 });
      if (this.token.token_type !== `}`) throw new ParseError(`Expecting }`, this.lexer.location);
      this.advance(), a2 = t5 === 2 ? [`apply`, `sqrt`, r3] : t5 === 3 ? [`apply`, `cbrt`, r3] : [`apply`, `nthroot`, [`tuple`, r3, t5]];
    } else if (this.token.token_type === `VAR` || this.token.token_type === `LATEXCOMMAND` || this.token.token_type === `VARMULTICHAR`) {
      if (a2 = this.token.token_text, this.token.token_type === `LATEXCOMMAND`) {
        if (a2 = a2.slice(1), !(this.appliedFunctionSymbols.includes(a2) || this.functionSymbols.includes(a2) || this.allowedLatexSymbols.includes(a2) || a2 in this.operatorSymbols)) throw new ParseError(`Unrecognized latex command ` + this.token.original_text, this.lexer.location);
      } else this.token.token_type === `VARMULTICHAR` && (a2 = /\\operatorname\s*\{\s*([a-zA-Z0-9\+\-]+)\s*\}/.exec(a2)[1]);
      if (this.appliedFunctionSymbols.includes(a2) || this.functionSymbols.includes(a2)) {
        let t5 = false;
        if (this.appliedFunctionSymbols.includes(a2) && (t5 = true), [`Re`, `Im`].includes(a2) && (a2 = a2.toLowerCase()), this.advance(), this.token.token_type === `_`) {
          this.advance();
          let t6 = this.get_subsuperscript({ parse_absolute_value: n2 });
          a2 = a2 === `log` && t6 === 10 ? `log10` : [`_`, a2, t6];
        }
        if (i2) t5 && (a2 = [`apply`, a2, `＿`]);
        else {
          for (; this.token.token_type === `'`; ) a2 = [`prime`, a2], this.advance();
          for (; this.token.token_type === `^`; ) {
            this.advance();
            let t6 = this.get_subsuperscript({ parse_absolute_value: n2 });
            a2 = [`^`, a2, t6];
          }
          if (this.token.token_type === `{` || this.token.token_type === `(`) {
            let t6;
            t6 = this.token.token_type === `{` ? `}` : `)`, this.advance();
            let n3 = this.statement_list();
            if (this.token.token_type !== t6) throw new ParseError(`Expecting ` + t6, this.lexer.location);
            this.advance(), n3[0] === `list` && (n3[0] = `tuple`), a2 = [`apply`, a2, n3];
          } else if (t5) {
            if (!this.allowSimplifiedFunctionApplication) throw new ParseError(`Expecting ( after function`, this.lexer.location);
            let t6 = this.factor({ parse_absolute_value: n2 });
            t6 === false && (t6 = `＿`), a2 = [`apply`, a2, t6];
          }
        }
        return a2;
      } else if (a2 in this.operatorSymbols) {
        if (this.advance(), a2 === `frac` && this.parseLeibnizNotation) {
          let t6 = this.return_state(), n3 = this.leibniz_notation();
          if (n3) return n3;
          this.set_state(t6);
        }
        let t5 = a2;
        this.operatorSymbols[a2].substitute && (t5 = this.operatorSymbols[a2].substitute);
        let r3 = this.operatorSymbols[a2].nargs, i3 = this.operatorSymbols[a2].remove_products, o2 = [];
        for (let t6 = 0; t6 < r3; t6++) if (this.token.token_type === `{`) {
          this.advance();
          let t7 = this.statement({ parse_absolute_value: n2 });
          if (i3 && Array.isArray(t7) && t7[0] === `*` ? o2.push(...t7.slice(1)) : o2.push(t7), this.token.token_type !== `}`) throw new ParseError(`Expecting }`, this.lexer.location);
          this.advance();
        } else {
          let t7 = this.get_single_digit_as_number();
          if (t7 === null) if (this.token.token_type === `VAR`) t7 = this.token.token_text, this.advance();
          else throw new ParseError(`Expecting {`, this.lexer.location);
          o2.push(t7);
        }
        a2 = [t5, ...o2];
      } else this.advance();
    } else if (this.token.token_type === `(` || this.token.token_type === `[` || this.token.token_type === `{` || this.token.token_type === `LBRACE` || this.token.token_type === `LANGLE`) {
      let t5 = this.token.token_type, n3, r3;
      this.token.token_type === `(` ? (n3 = `)`, r3 = `]`) : this.token.token_type === `[` ? (n3 = `]`, r3 = `)`) : this.token.token_type === `{` ? (n3 = `}`, r3 = null) : this.token.token_type === `LBRACE` ? (n3 = `RBRACE`, r3 = null) : (n3 = `RANGLE`, r3 = null), this.advance(), a2 = this.statement_list();
      let i3 = 1;
      if (a2[0] === `list` && (i3 = a2.length - 1), this.token.token_type !== n3) {
        if (i3 !== 2 || r3 === null) throw new ParseError(`Expecting ` + n3, this.lexer.location);
        if (this.token.token_type !== r3) throw new ParseError(`Expecting ) or ]`, this.lexer.location);
        a2[0] = `tuple`, a2 = [`interval`, a2];
        let o2;
        o2 = t5 === `(` ? [`tuple`, false, true] : [`tuple`, true, false], a2.push(o2);
      } else i3 >= 2 ? t5 === `(` || t5 === `{` ? a2[0] = `tuple` : t5 === `[` ? a2[0] = `array` : t5 === `LBRACE` ? a2[0] = `set` : a2[0] = `altvector` : t5 === `LBRACE` && (a2 = (a2[0] === `|` || a2[0], [`set`, a2]));
      this.advance();
    } else if (this.token.token_type[0] === `|` && n2 && (t4 === 0 || !r2 || this.token.token_type[1] === `L`)) {
      if (t4 += 1, this.advance(), a2 = this.statement({ inside_absolute_value: t4 }), a2 = [`apply`, `abs`, a2], this.token.token_type !== `|`) throw new ParseError(`Expecting |`, this.lexer.location);
      this.advance();
    } else if (this.token.token_type === `LFLOOR` || this.token.token_type === `LCEIL`) {
      let t5, n3;
      if (this.token.token_type === `LFLOOR` ? (t5 = `RFLOOR`, n3 = `floor`) : this.token.token_type === `LCEIL` && (t5 = `RCEIL`, n3 = `ceil`), this.advance(), a2 = this.statement(), a2 = [`apply`, n3, a2], this.token.token_type !== t5) throw new ParseError(`Expecting ` + t5, this.lexer.location);
      this.advance();
    } else if (this.token.token_type === `ANGLE`) if (this.advance(), this.token.token_type === `{` || this.token.token_type === `(`) {
      let t5;
      t5 = this.token.token_type === `{` ? `}` : `)`, this.advance();
      let n3 = this.statement_list();
      if (this.token.token_type !== t5) throw new ParseError(`Expecting ` + t5, this.lexer.location);
      this.advance(), n3[0] === `list` ? a2 = [`angle`, ...n3.slice(1)] : n3[0] === `*` && (a2 = [`angle`, n3]);
    } else {
      let t5 = [], r3 = this.nonMinusFactor({ parse_absolute_value: n2 });
      for (; r3 !== false; ) t5.push(r3), r3 = this.nonMinusFactor({ parse_absolute_value: n2 });
      a2 = t5.length === 0 ? `angle` : [`angle`, ...t5];
    }
    else if (this.token.token_type === `INT`) {
      this.advance();
      let t5 = `int`;
      if (this.token.token_type === `_`) {
        this.advance();
        let r4 = this.get_subsuperscript({ parse_absolute_value: n2 });
        t5 = [`_`, t5, r4];
      }
      if (this.token.token_type === `^`) {
        this.advance();
        let r4 = this.get_subsuperscript({ parse_absolute_value: n2 });
        t5 = [`^`, t5, r4];
      }
      let r3 = flatten(this.term({ parse_absolute_value: n2 }));
      if (Array.isArray(r3) && r3[0] === `*`) {
        let t6 = [];
        for (let n3 = 0; n3 < r3.length - 1; n3++) if (r3[n3] === `d`) {
          let i3 = r3[n3 + 1];
          r3.splice(n3, 2), t6.push([`d`, i3]), n3--;
        }
        r3.push(...t6);
      }
      return [`apply`, t5, r3];
    }
    if (this.token.token_type === `_`) {
      a2 === false && (a2 = `＿`), this.advance();
      let t5 = this.get_subsuperscript({ parse_absolute_value: n2 });
      a2 = [`_`, a2, t5];
    }
    return a2;
  }
  leibniz_notation() {
    if (this.token.token_type !== `{`) return false;
    this.advance();
    var t4 = this.token.token_text;
    let n2 = ``, r2 = 1, i2 = ``, a2 = [], o2 = [];
    if (this.token.token_type === `LATEXCOMMAND` && t4.slice(1) === `partial`) n2 = `∂`;
    else if (this.token.token_type === `VAR` && t4 === `d`) n2 = `d`;
    else return false;
    if (this.advance(), this.token.token_type === `^`) {
      this.advance();
      let t5 = false;
      if (this.token.token_type === `{` && (t5 = true, this.advance()), this.token.token_type !== `NUMBER` || (r2 = parseFloat(this.token.token_text), !Number.isInteger(r2)) || t5 && (this.advance(), this.token.token_type !== `}`)) return false;
      this.advance();
    }
    if (this.token.token_type === `VAR`) i2 = this.token.token_text;
    else if (this.token.token_type === `VARMULTICHAR`) i2 = /\\operatorname\s*\{\s*([a-zA-Z0-9\+\-]+)\s*\}/.exec(this.token.token_text)[1];
    else if (this.token.token_type === `LATEXCOMMAND`) if (t4 = this.token.token_text.slice(1), this.allowedLatexSymbols.includes(t4)) i2 = t4;
    else return false;
    if (this.advance(), this.token.token_type !== `}` || (this.advance(), this.token.token_type !== `{`)) return false;
    this.advance();
    let s2 = 0;
    for (; ; ) {
      if (!(n2 === `d` && this.token.token_type === `VAR` && this.token.token_text === `d` || n2 === `∂` && this.token.token_type === `LATEXCOMMAND` && this.token.token_text.slice(1) === `partial`)) return false;
      if (this.advance(), this.token.token_type === `VAR`) a2.push(this.token.token_text);
      else if (this.token.token_type === `VARMULTICHAR`) a2.push(/\\operatorname\s*\{\s*([a-zA-Z0-9\+\-]+)\s*\}/.exec(this.token.token_text)[1]);
      else if (this.token.token_type === `LATEXCOMMAND`) {
        let t5 = this.token.token_text.slice(1);
        if (this.allowedLatexSymbols.includes(t5)) a2.push(t5);
        else return false;
      } else return false;
      let c2 = 1;
      if (this.advance(), this.token.token_type === `^`) {
        this.advance();
        let t5 = false;
        if (this.token.token_type === `{` && (t5 = true, this.advance()), this.token.token_type !== `NUMBER` || (c2 = parseFloat(this.token.token_text), !Number.isInteger(c2)) || t5 && (this.advance(), this.token.token_type !== `}`)) return false;
        this.advance();
      }
      if (o2.push(c2), s2 += c2, s2 > r2) return false;
      if (s2 === r2) {
        if (this.token.token_type !== `}`) return false;
        this.advance();
        let s3 = `derivative_leibniz`;
        n2 === `∂` && (s3 = `partial_` + s3), t4 = [s3], r2 === 1 ? t4.push(i2) : t4.push([`tuple`, i2, r2]);
        let c3 = [];
        for (let t5 = 0; t5 < a2.length; t5 += 1) o2[t5] === 1 ? c3.push(a2[t5]) : c3.push([`tuple`, a2[t5], o2[t5]]);
        return c3 = [`tuple`].concat(c3), t4.push(c3), t4;
      }
    }
  }
}, latexToGuppy = class {
  constructor() {
    this.latexToAst = new latexToAst$1(), this.astToGuppy = new astToGuppy$1();
  }
  convert(t4) {
    return this.astToGuppy.convert(this.latexToAst.convert(t4));
  }
}, latexToMathjs = class {
  constructor() {
    this.latexToAst = new latexToAst$1(), this.astToMathjs = new astToMathjs$2();
  }
  convert(t4) {
    return this.astToMathjs.convert(this.latexToAst.convert(t4));
  }
}, latexToText = class {
  constructor() {
    this.latexToAst = new latexToAst$1(), this.astToText = new astToText$2();
  }
  convert(t4) {
    return this.astToText.convert(this.latexToAst.convert(t4));
  }
}, mathjsToGuppy = class {
  constructor() {
    this.mathjsToAst = new mathjsToAst$1(), this.astToGuppy = new astToGuppy$1();
  }
  convert(t4) {
    return this.astToGuppy.convert(this.mathjsToAst.convert(t4));
  }
}, mathjsToLatex = class {
  constructor() {
    this.mathjsToAst = new mathjsToAst$1(), this.astToLatex = new astToLatex$1();
  }
  convert(t4) {
    return this.astToLatex.convert(this.mathjsToAst.convert(t4));
  }
}, mathjsToText = class {
  constructor() {
    this.mathjsToAst = new mathjsToAst$1(), this.astToText = new astToText$2();
  }
  convert(t4) {
    return this.astToText.convert(this.mathjsToAst.convert(t4));
  }
}, require_ms = __commonJSMin(((t4, n2) => {
  var r2 = 1e3, i2 = r2 * 60, a2 = i2 * 60, o2 = a2 * 24, s2 = o2 * 365.25;
  n2.exports = function(t5, n3) {
    n3 ||= {};
    var r3 = typeof t5;
    if (r3 === `string` && t5.length > 0) return c2(t5);
    if (r3 === `number` && isNaN(t5) === false) return n3.long ? u2(t5) : l2(t5);
    throw Error(`val is not a non-empty string or a valid number. val=` + JSON.stringify(t5));
  };
  function c2(t5) {
    if (t5 = String(t5), !(t5.length > 100)) {
      var n3 = /^((?:\d+)?\.?\d+) *(milliseconds?|msecs?|ms|seconds?|secs?|s|minutes?|mins?|m|hours?|hrs?|h|days?|d|years?|yrs?|y)?$/i.exec(t5);
      if (n3) {
        var c3 = parseFloat(n3[1]);
        switch ((n3[2] || `ms`).toLowerCase()) {
          case `years`:
          case `year`:
          case `yrs`:
          case `yr`:
          case `y`:
            return c3 * s2;
          case `days`:
          case `day`:
          case `d`:
            return c3 * o2;
          case `hours`:
          case `hour`:
          case `hrs`:
          case `hr`:
          case `h`:
            return c3 * a2;
          case `minutes`:
          case `minute`:
          case `mins`:
          case `min`:
          case `m`:
            return c3 * i2;
          case `seconds`:
          case `second`:
          case `secs`:
          case `sec`:
          case `s`:
            return c3 * r2;
          case `milliseconds`:
          case `millisecond`:
          case `msecs`:
          case `msec`:
          case `ms`:
            return c3;
          default:
            return;
        }
      }
    }
  }
  function l2(t5) {
    return t5 >= o2 ? Math.round(t5 / o2) + `d` : t5 >= a2 ? Math.round(t5 / a2) + `h` : t5 >= i2 ? Math.round(t5 / i2) + `m` : t5 >= r2 ? Math.round(t5 / r2) + `s` : t5 + `ms`;
  }
  function u2(t5) {
    return d2(t5, o2, `day`) || d2(t5, a2, `hour`) || d2(t5, i2, `minute`) || d2(t5, r2, `second`) || t5 + ` ms`;
  }
  function d2(t5, n3, r3) {
    if (!(t5 < n3)) return t5 < n3 * 1.5 ? Math.floor(t5 / n3) + ` ` + r3 : Math.ceil(t5 / n3) + ` ` + r3 + `s`;
  }
})), require_debug = __commonJSMin(((t4, n2) => {
  t4 = n2.exports = a2.debug = a2.default = a2, t4.coerce = l2, t4.disable = s2, t4.enable = o2, t4.enabled = c2, t4.humanize = require_ms(), t4.names = [], t4.skips = [], t4.formatters = {};
  var r2;
  function i2(n3) {
    var r3 = 0, i3;
    for (i3 in n3) r3 = (r3 << 5) - r3 + n3.charCodeAt(i3), r3 |= 0;
    return t4.colors[Math.abs(r3) % t4.colors.length];
  }
  function a2(n3) {
    function a3() {
      if (a3.enabled) {
        var n4 = a3, i3 = +/* @__PURE__ */ new Date();
        n4.diff = i3 - (r2 || i3), n4.prev = r2, n4.curr = i3, r2 = i3;
        for (var o3 = Array(arguments.length), s3 = 0; s3 < o3.length; s3++) o3[s3] = arguments[s3];
        o3[0] = t4.coerce(o3[0]), typeof o3[0] != `string` && o3.unshift(`%O`);
        var c3 = 0;
        o3[0] = o3[0].replace(/%([a-zA-Z%])/g, function(r3, i4) {
          if (r3 === `%%`) return r3;
          c3++;
          var a4 = t4.formatters[i4];
          if (typeof a4 == `function`) {
            var s4 = o3[c3];
            r3 = a4.call(n4, s4), o3.splice(c3, 1), c3--;
          }
          return r3;
        }), t4.formatArgs.call(n4, o3), (a3.log || t4.log || console.log.bind(console)).apply(n4, o3);
      }
    }
    return a3.namespace = n3, a3.enabled = t4.enabled(n3), a3.useColors = t4.useColors(), a3.color = i2(n3), typeof t4.init == `function` && t4.init(a3), a3;
  }
  function o2(n3) {
    t4.save(n3), t4.names = [], t4.skips = [];
    for (var r3 = (typeof n3 == `string` ? n3 : ``).split(/[\s,]+/), i3 = r3.length, a3 = 0; a3 < i3; a3++) r3[a3] && (n3 = r3[a3].replace(/\*/g, `.*?`), n3[0] === `-` ? t4.skips.push(RegExp(`^` + n3.substr(1) + `$`)) : t4.names.push(RegExp(`^` + n3 + `$`)));
  }
  function s2() {
    t4.enable(``);
  }
  function c2(n3) {
    var r3, i3;
    for (r3 = 0, i3 = t4.skips.length; r3 < i3; r3++) if (t4.skips[r3].test(n3)) return false;
    for (r3 = 0, i3 = t4.names.length; r3 < i3; r3++) if (t4.names[r3].test(n3)) return true;
    return false;
  }
  function l2(t5) {
    return t5 instanceof Error ? t5.stack || t5.message : t5;
  }
})), require_browser = __commonJSMin(((t4, n2) => {
  t4 = n2.exports = require_debug(), t4.log = a2, t4.formatArgs = i2, t4.save = o2, t4.load = s2, t4.useColors = r2, t4.storage = typeof chrome < `u` && chrome.storage !== void 0 ? chrome.storage.local : c2(), t4.colors = [`lightseagreen`, `forestgreen`, `goldenrod`, `dodgerblue`, `darkorchid`, `crimson`];
  function r2() {
    return typeof window < `u` && window.process && window.process.type === `renderer` ? true : typeof document < `u` && document.documentElement && document.documentElement.style && document.documentElement.style.WebkitAppearance || typeof window < `u` && window.console && (window.console.firebug || window.console.exception && window.console.table) || typeof navigator < `u` && navigator.userAgent && navigator.userAgent.toLowerCase().match(/firefox\/(\d+)/) && parseInt(RegExp.$1, 10) >= 31 || typeof navigator < `u` && navigator.userAgent && navigator.userAgent.toLowerCase().match(/applewebkit\/(\d+)/);
  }
  t4.formatters.j = function(t5) {
    try {
      return JSON.stringify(t5);
    } catch (t6) {
      return `[UnexpectedJSONParseError]: ` + t6.message;
    }
  };
  function i2(n3) {
    var r3 = this.useColors;
    if (n3[0] = (r3 ? `%c` : ``) + this.namespace + (r3 ? ` %c` : ` `) + n3[0] + (r3 ? `%c ` : ` `) + `+` + t4.humanize(this.diff), r3) {
      var i3 = `color: ` + this.color;
      n3.splice(1, 0, i3, `color: inherit`);
      var a3 = 0, o3 = 0;
      n3[0].replace(/%[a-zA-Z%]/g, function(t5) {
        t5 !== `%%` && (a3++, t5 === `%c` && (o3 = a3));
      }), n3.splice(o3, 0, i3);
    }
  }
  function a2() {
    return typeof console == `object` && console.log && Function.prototype.apply.call(console.log, console, arguments);
  }
  function o2(n3) {
    try {
      n3 == null ? t4.storage.removeItem(`debug`) : t4.storage.debug = n3;
    } catch {
    }
  }
  function s2() {
    var n3;
    try {
      n3 = t4.storage.debug;
    } catch {
    }
    return !n3 && typeof process < `u` && `env` in process && (n3 = process.env.DEBUG), n3;
  }
  t4.enable(s2());
  function c2() {
    try {
      return window.localStorage;
    } catch {
    }
  }
})), require_xml_parser = __commonJSMin(((t4, n2) => {
  var r2 = require_browser()(`xml-parser`);
  n2.exports = i2;
  function i2(t5) {
    return t5 = t5.trim(), t5 = t5.replace(/<!--[\s\S]*?-->/g, ``), n3();
    function n3() {
      return { declaration: i3(), root: a2() };
    }
    function i3() {
      if (l2(/^<\?xml\s*/)) {
        for (var t6 = { attributes: {} }; !(u2() || d2(`?>`)); ) {
          var n4 = s2();
          if (!n4) return t6;
          t6.attributes[n4.name] = n4.value;
        }
        return l2(/\?>\s*/), t6;
      }
    }
    function a2() {
      r2(`tag %j`, t5);
      var n4 = l2(/^<([\w-:.]+)\s*/);
      if (n4) {
        for (var i4 = { name: n4[1], attributes: {}, children: [] }; !(u2() || d2(`>`) || d2(`?>`) || d2(`/>`)); ) {
          var c3 = s2();
          if (!c3) return i4;
          i4.attributes[c3.name] = c3.value;
        }
        if (l2(/^\s*\/>\s*/)) return i4;
        l2(/\??>\s*/), i4.content = o2();
        for (var p2; p2 = a2(); ) i4.children.push(p2);
        return l2(/^<\/[\w-:.]+>\s*/), i4;
      }
    }
    function o2() {
      r2(`content %j`, t5);
      var n4 = l2(/^([^<]*)/);
      return n4 ? n4[1] : ``;
    }
    function s2() {
      r2(`attribute %j`, t5);
      var n4 = l2(/([\w:-]+)\s*=\s*("[^"]*"|'[^']*'|\w+)\s*/);
      if (n4) return { name: n4[1], value: c2(n4[2]) };
    }
    function c2(t6) {
      return t6.replace(/^['"]|['"]$/g, ``);
    }
    function l2(n4) {
      var r3 = t5.match(n4);
      if (r3) return t5 = t5.slice(r3[0].length), r3;
    }
    function u2() {
      return t5.length == 0;
    }
    function d2(n4) {
      return t5.indexOf(n4) == 0;
    }
  }
})), import_xml_parser = __toESM(require_xml_parser()), entities = { "&#913;": `\\Alpha`, "&Alpha;": `\\Alpha`, "&#x0391;": `\\Alpha`, "\\u0391;": `\\Alpha`, "&#914;": `\\Beta`, "&Beta;": `\\Beta`, "&#x0392;": `\\Beta`, "\\u0392;": `\\Beta`, "&#915;": `\\Gamma`, "&Gamma;": `\\Gamma`, "&#x0393;": `\\Gamma`, "\\u0393;": `\\Gamma`, "&#916;": `\\Delta`, "&Delta;": `\\Delta`, "&#x0394;": `\\Delta`, "\\u0394;": `\\Delta`, "&#917;": `\\Epsilon`, "&Epsilon;": `\\Epsilon`, "&#x0395;": `\\Epsilon`, "\\u0395;": `\\Epsilon`, "&#918;": `\\Zeta`, "&Zeta;": `\\Zeta`, "&#x0396;": `\\Zeta`, "\\u0396;": `\\Zeta`, "&#919;": `\\Eta`, "&Eta;": `\\Eta`, "&#x0397;": `\\Eta`, "\\u0397;": `\\Eta`, "&#920;": `\\Theta`, "&Theta;": `\\Theta`, "&#x0398;": `\\Theta`, "\\u0398;": `\\Theta`, "&#921;": `\\Iota`, "&Iota;": `\\Iota`, "&#x0399;": `\\Iota`, "\\u0399;": `\\Iota`, "&#922;": `\\Kappa`, "&Kappa;": `\\Kappa`, "&#x039A;": `\\Kappa`, "\\u039A;": `\\Kappa`, "&#923;": `\\Lambda`, "&Lambda;": `\\Lambda`, "&#x039B;": `\\Lambda`, "\\u039B;": `\\Lambda`, "&#924;": `\\Mu`, "&Mu;": `\\Mu`, "&#x039C;": `\\Mu`, "\\u039C;": `\\Mu`, "&#925;": `\\Nu`, "&Nu;": `\\Nu`, "&#x039D;": `\\Nu`, "\\u039D;": `\\Nu`, "&#926;": `\\Xi`, "&Xi;": `\\Xi`, "&#x039E;": `\\Xi`, "\\u039E;": `\\Xi`, "&#927;": `\\Omicron`, "&Omicron;": `\\Omicron`, "&#x039F;": `\\Omicron`, "\\u039F;": `\\Omicron`, "&#928;": `\\Pi`, "&Pi;": `\\Pi`, "&#x03A0;": `\\Pi`, "\\u03A0;": `\\Pi`, "&#929;": `\\Rho`, "&Rho;": `\\Rho`, "&#x03A1;": `\\Rho`, "\\u03A1;": `\\Rho`, "&#931;": `\\Sigma`, "&Sigma;": `\\Sigma`, "&#x03A3;": `\\Sigma`, "\\u03A3;": `\\Sigma`, "&#932;": `\\Tau`, "&Tau;": `\\Tau`, "&#x03A4;": `\\Tau`, "\\u03A4;": `\\Tau`, "&#933;": `\\Upsilon`, "&Upsilon;": `\\Upsilon`, "&#x03A5;": `\\Upsilon`, "\\u03A5;": `\\Upsilon`, "&#934;": `\\Phi`, "&Phi;": `\\Phi`, "&#x03A6;": `\\Phi`, "\\u03A6;": `\\Phi`, "&#935;": `\\Chi`, "&Chi;": `\\Chi`, "&#x03A7;": `\\Chi`, "\\u03A7;": `\\Chi`, "&#936;": `\\Psi`, "&Psi;": `\\Psi`, "&#x03A8;": `\\Psi`, "\\u03A8;": `\\Psi`, "&#937;": `\\Omega`, "&Omega;": `\\Omega`, "&#x03A9;": `\\Omega`, "\\u03A9;": `\\Omega`, "&#945;": `\\alpha`, "&alpha;": `\\alpha`, "&#x03B1;": `\\alpha`, "\\u03B1;": `\\alpha`, "&#946;": `\\beta`, "&beta;": `\\beta`, "&#x03B2;": `\\beta`, "\\u03B2;": `\\beta`, "&#947;": `\\gamma`, "&gamma;": `\\gamma`, "&#x03B3;": `\\gamma`, "\\u03B3;": `\\gamma`, "&#948;": `\\delta`, "&delta;": `\\delta`, "&#x03B4;": `\\delta`, "\\u03B4;": `\\delta`, "&#949;": `\\epsilon`, "&epsilon;": `\\epsilon`, "&#x03B5;": `\\epsilon`, "\\u03B5;": `\\epsilon`, "&#950;": `\\zeta`, "&zeta;": `\\zeta`, "&#x03B6;": `\\zeta`, "\\u03B6;": `\\zeta`, "&#951;": `\\eta`, "&eta;": `\\eta`, "&#x03B7;": `\\eta`, "\\u03B7;": `\\eta`, "&#952;": `\\theta`, "&theta;": `\\theta`, "&#x03B8;": `\\theta`, "\\u03B8;": `\\theta`, "&#953;": `\\iota`, "&iota;": `\\iota`, "&#x03B9;": `\\iota`, "\\u03B9;": `\\iota`, "&#954;": `\\kappa`, "&kappa;": `\\kappa`, "&#x03BA;": `\\kappa`, "\\u03BA;": `\\kappa`, "&#955;": `\\lambda`, "&lambda;": `\\lambda`, "&#x03BB;": `\\lambda`, "\\u03BB;": `\\lambda`, "&#956;": `\\mu`, "&mu;": `\\mu`, "&#x03BC;": `\\mu`, "\\u03BC;": `\\mu`, "&#957;": `\\nu`, "&nu;": `\\nu`, "&#x03BD;": `\\nu`, "\\u03BD;": `\\nu`, "&#958;": `\\xi`, "&xi;": `\\xi`, "&#x03BE;": `\\xi`, "\\u03BE;": `\\xi`, "&#959;": `\\omicron`, "&omicron;": `\\omicron`, "&#x03BF;": `\\omicron`, "\\u03BF;": `\\omicron`, "&#960;": `\\pi`, "&pi;": `\\pi`, "&#x03C0;": `\\pi`, "\\u03C0;": `\\pi`, "&#961;": `\\rho`, "&rho;": `\\rho`, "&#x03C1;": `\\rho`, "\\u03C1;": `\\rho`, "&#962;": `\\sigma`, ";": `\\sigma`, "&#x03C2;": `\\sigma`, "\\u03C2;": `\\sigma`, "&#963;": `\\sigma`, "&sigma;": `\\sigma`, "&#x03C3;": `\\sigma`, "\\u03C3;": `\\sigma`, "&#964;": `\\tau`, "&tau;": `\\tau`, "&#x03C4;": `\\tau`, "\\u03C4;": `\\tau`, "&#965;": `\\upsilon`, "&upsilon;": `\\upsilon`, "&#x03C5;": `\\upsilon`, "\\u03C5;": `\\upsilon`, "&#966;": `\\phi`, "&phi;": `\\phi`, "&#x03C6;": `\\phi`, "\\u03C6;": `\\phi`, "&#967;": `\\chi`, "&chi;": `\\chi`, "&#x03C7;": `\\chi`, "\\u03C7;": `\\chi`, "&#968;": `\\psi`, "&psi;": `\\psi`, "&#x03C8;": `\\psi`, "\\u03C8;": `\\psi`, "&#969;": `\\omega`, "&omega;": `\\omega`, "&#x03C9;": `\\omega`, "\\u03C9;": `\\omega`, "&#x2212;": `-`, "&minus;": `-`, "&#x221E;": `\\infty`, "&#8734;": `\\infty`, "&infin;": `\\infty`, "&sdot;": `\\cdot`, "&#x22C5;": `\\cdot`, "&#8901;": `\\cdot`, "&times;": `\\times`, "&#x00D7;": `\\times`, "&#215;": `\\times` }, mmlToLatex = class {
  parse(t4) {
    if (t4.name === `mi`) return entities[t4.content] ? entities[t4.content] : t4.content.length > 1 ? `\\` + t4.content : t4.content;
    if (t4.name === `mn`) return t4.content;
    if (t4.name === `msup`) return this.parse(t4.children[0]) + `^{` + this.parse(t4.children[1]) + `}`;
    if (t4.name === `mroot`) return `\\sqrt[` + this.parse(t4.children[1]) + `]{` + this.parse(t4.children[1]) + `}`;
    if (t4.name === `mfrac`) return `\\frac{` + this.parse(t4.children[0]) + `}{` + this.parse(t4.children[1]) + `}`;
    if (t4.name === `msqrt`) return `\\sqrt{` + t4.children.map((function(t5, n2) {
      return this.parse(t5);
    }).bind(this)).join(` `) + `}`;
    if (t4.name === `mo`) return entities[t4.content] ? entities[t4.content] : t4.content === `&#x2061;` ? ` ` : t4.content;
    if (t4.name === `mrow` && t4.attributes.class === `MJX-TeXAtom-ORD`) return t4.children.map((function(t5, n2) {
      return this.parse(t5);
    }).bind(this)).join(` `);
    if (t4.name === `math` || t4.name === `mrow`) return `(` + t4.children.map((function(t5, n2) {
      return this.parse(t5);
    }).bind(this)).join(` `) + `)`;
  }
  convert(t4) {
    return this.parse((0, import_xml_parser.default)(t4).root);
  }
}, mmlToAst$1 = class {
  constructor() {
    this.mmlToLatex = new mmlToLatex(), this.latexToAst = new latexToAst$1();
  }
  convert(t4) {
    return this.latexToAst.convert(this.mmlToLatex.convert(t4));
  }
}, mmlToGuppy = class {
  constructor() {
    this.mmlToLatex = new mmlToLatex(), this.latexToAst = new latexToAst$1(), this.astToGuppy = new astToGuppy$1();
  }
  convert(t4) {
    return this.astToGuppy.convert(this.latexToAst.convert(this.mmlToLatex.convert(t4)));
  }
}, mmlToMathjs = class {
  constructor() {
    this.mmlToLatex = new mmlToLatex(), this.latexToAst = new latexToAst$1(), this.astToMathjs = new astToMathjs$2();
  }
  convert(t4) {
    return this.astToMathjs.convert(this.latexToAst.convert(this.mmlToLatex.convert(t4)));
  }
}, mmlToText = class {
  constructor() {
    this.mmlToLatex = new mmlToLatex(), this.latexToAst = new latexToAst$1(), this.astToText = new astToText$2();
  }
  convert(t4) {
    return this.astToText.convert(this.latexToAst.convert(this.mmlToLatex.convert(t4)));
  }
}, textToGuppy = class {
  constructor() {
    this.textToAst = new textToAst$4(), this.astToGuppy = new astToGuppy$1();
  }
  convert(t4) {
    return this.astToGuppy.convert(this.textToAst.convert(t4));
  }
}, textToLatex = class {
  constructor() {
    this.textToAst = new textToAst$4(), this.astToLatex = new astToLatex$1();
  }
  convert(t4) {
    return this.astToLatex.convert(this.textToAst.convert(t4));
  }
}, textToMathjs = class {
  constructor() {
    this.textToAst = new textToAst$4(), this.astToMathjs = new astToMathjs$2();
  }
  convert(t4) {
    return this.astToMathjs.convert(this.textToAst.convert(t4));
  }
}, converters_exports = __exportAll({ astToFiniteFieldObj: () => astToFiniteField$1, astToGLSL: () => astToGLSL$1, astToGuppyObj: () => astToGuppy$1, astToLatexObj: () => astToLatex$1, astToMathjsObj: () => astToMathjs$2, astToTextObj: () => astToText$2, latexToAstObj: () => latexToAst$1, latexToGuppyObj: () => latexToGuppy, latexToMathjsObj: () => latexToMathjs, latexToTextObj: () => latexToText, mathjsToAstObj: () => mathjsToAst$1, mathjsToGuppyObj: () => mathjsToGuppy, mathjsToLatexObj: () => mathjsToLatex, mathjsToTextObj: () => mathjsToText, mmlToAstObj: () => mmlToAst$1, mmlToGuppyObj: () => mmlToGuppy, mmlToLatexObj: () => mmlToLatex, mmlToMathjsObj: () => mmlToMathjs, mmlToTextObj: () => mmlToText, textToAstObj: () => textToAst$4, textToGuppyObj: () => textToGuppy, textToLatexObj: () => textToLatex, textToMathjsObj: () => textToMathjs }), textToAst = new textToAst$4(), latexToAst = new latexToAst$1(), mmlToAst = new mmlToAst$1(), utils = { match: match$1, flatten: flatten$5, unflattenLeft, unflattenRight };
function Expression(t4, n2) {
  this.tree = flatten$5(t4), this.context = n2, this.toJSON = function() {
    let t5 = { objectType: `math-expression`, tree: this.tree }, n3 = {};
    for (let t6 in this.context.assumptions) Object.keys(this.context.assumptions[t6]).length > 0 && (n3[t6] = this.context.assumptions[t6]);
    return Object.keys(n3).length > 0 && (t5.assumptions = n3), t5;
  };
}
function extend(t4, n2) {
  return flatten_array(Array.prototype.slice.call(arguments, 2)).forEach(function(r2) {
    if (r2) for (var i2 in r2) n2 ? (function() {
      var n3 = i2;
      t4[n3] = function() {
        return this.fromAst(r2[n3].apply(null, arguments));
      };
    })() : t4[i2] = r2[i2];
  }), t4;
}
function extend_prototype(t4, n2) {
  return flatten_array(Array.prototype.slice.call(arguments, 2)).forEach(function(r2) {
    if (r2) for (var i2 in r2) (function() {
      var a2 = i2;
      t4[a2] = function() {
        var t5 = [this].concat(Array.prototype.slice.call(arguments));
        return n2 ? this.context.fromAst(r2[a2].apply(null, t5)) : r2[a2].apply(null, t5);
      };
    })();
  }), t4;
}
function create_from_multiple(t4, n2) {
  if (Array.isArray(t4) || typeof t4 == `number`) return new Expression(t4, Context);
  if (typeof t4 == `string`) try {
    return new Expression(textToAst.convert(t4), Context);
  } catch (n3) {
    try {
      return new Expression(latexToAst.convert(t4), Context);
    } catch (r2) {
      try {
        return new Expression(mmlToAst.convert(t4), Context);
      } catch (i2) {
        throw t4.indexOf(`\\`) === -1 ? t4.indexOf(`</`) === -1 ? n3 : i2 : r2;
      }
    }
  }
}
function parseText(t4, n2) {
  return new Expression(textToAst.convert(t4), Context);
}
function parseLatex(t4, n2) {
  return new Expression(latexToAst.convert(t4), Context);
}
function parseMml(t4, n2) {
  return new Expression(mmlToAst.convert(t4), Context);
}
var Context = { ZmodN, assumptions: initialize_assumptions(), parser_parameters: {}, from: create_from_multiple, fromText: parseText, parse: parseText, fromLaTeX: parseLatex, fromLatex: parseLatex, fromTeX: parseLatex, fromTex: parseLatex, fromMml: parseMml, parse_tex: parseLatex, converters: converters_exports, utils, fromAst: function(t4) {
  return new Expression(t4, this);
}, set_to_default: function() {
  this.assumptions = initialize_assumptions();
}, get_assumptions: function(t4, n2) {
  return get_assumptions(this.assumptions, t4, n2);
}, add_assumption: function(t4, n2) {
  return add_assumption(this.assumptions, t4, n2);
}, add_generic_assumption: function(t4) {
  return add_generic_assumption(this.assumptions, t4);
}, remove_assumption: function(t4) {
  return remove_assumption(this.assumptions, t4);
}, remove_generic_assumption: function(t4) {
  return remove_generic_assumption(this.assumptions, t4);
}, clear_assumptions: function() {
  this.assumptions = initialize_assumptions();
}, math: mathjs_default, reviver: function(t4, n2) {
  if (n2 && n2.objectType === `math-expression` && n2.tree !== void 0) {
    let t5 = Context.fromAst(n2.tree);
    return n2.assumptions !== void 0 && (t5.assumptions = n2.assumptions), t5;
  }
  return n2;
}, class: Expression };
Context.set_to_default(), extend(Context, true, expression_to_tree$1, expression_to_tree), extend(Context, false, expression_to_other), extend_prototype(Expression.prototype, true, expression_to_tree$1), extend_prototype(Expression.prototype, false, expression_to_other);
function flatten_array(t4, n2) {
  n2 = n2 === void 0 ? [] : n2;
  for (var r2 = 0; r2 < t4.length; r2++) Array.isArray(t4[r2]) ? flatten_array(t4[r2], n2) : n2.push(t4[r2]);
  return n2;
}
const COARSE_POINTER_QUERY = "(pointer: coarse)";
function getCoarsePointerQueryList() {
  const matchMedia = globalThis.matchMedia;
  if (typeof matchMedia !== "function") {
    return null;
  }
  return matchMedia(COARSE_POINTER_QUERY);
}
function hasCoarsePrimaryPointer() {
  return getCoarsePointerQueryList()?.matches ?? false;
}
var base32$1 = { exports: {} };
var hasRequiredBase32;
function requireBase32() {
  if (hasRequiredBase32) return base32$1.exports;
  hasRequiredBase32 = 1;
  (function(module) {
    (function() {
      var root2 = typeof window === "object" ? window : {};
      var NODE_JS = !root2.HI_BASE32_NO_NODE_JS && typeof process === "object" && process.versions && process.versions.node;
      if (NODE_JS) {
        root2 = commonjsGlobal;
      }
      var COMMON_JS = !root2.HI_BASE32_NO_COMMON_JS && true && module.exports;
      var BASE32_ENCODE_CHAR = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567".split("");
      var BASE32_DECODE_CHAR = {
        "A": 0,
        "B": 1,
        "C": 2,
        "D": 3,
        "E": 4,
        "F": 5,
        "G": 6,
        "H": 7,
        "I": 8,
        "J": 9,
        "K": 10,
        "L": 11,
        "M": 12,
        "N": 13,
        "O": 14,
        "P": 15,
        "Q": 16,
        "R": 17,
        "S": 18,
        "T": 19,
        "U": 20,
        "V": 21,
        "W": 22,
        "X": 23,
        "Y": 24,
        "Z": 25,
        "2": 26,
        "3": 27,
        "4": 28,
        "5": 29,
        "6": 30,
        "7": 31
      };
      var blocks = [0, 0, 0, 0, 0, 0, 0, 0];
      var throwInvalidUtf8 = function(position, partial2) {
        if (partial2.length > 10) {
          partial2 = "..." + partial2.substr(-10);
        }
        var err = new Error("Decoded data is not valid UTF-8. Maybe try base32.decode.asBytes()? Partial data after reading " + position + " bytes: " + partial2 + " <-");
        err.position = position;
        throw err;
      };
      var toUtf8String = function(bytes) {
        var str = "", length = bytes.length, i3 = 0, followingChars = 0, b2, c2;
        while (i3 < length) {
          b2 = bytes[i3++];
          if (b2 <= 127) {
            str += String.fromCharCode(b2);
            continue;
          } else if (b2 > 191 && b2 <= 223) {
            c2 = b2 & 31;
            followingChars = 1;
          } else if (b2 <= 239) {
            c2 = b2 & 15;
            followingChars = 2;
          } else if (b2 <= 247) {
            c2 = b2 & 7;
            followingChars = 3;
          } else {
            throwInvalidUtf8(i3, str);
          }
          for (var j2 = 0; j2 < followingChars; ++j2) {
            b2 = bytes[i3++];
            if (b2 < 128 || b2 > 191) {
              throwInvalidUtf8(i3, str);
            }
            c2 <<= 6;
            c2 += b2 & 63;
          }
          if (c2 >= 55296 && c2 <= 57343) {
            throwInvalidUtf8(i3, str);
          }
          if (c2 > 1114111) {
            throwInvalidUtf8(i3, str);
          }
          if (c2 <= 65535) {
            str += String.fromCharCode(c2);
          } else {
            c2 -= 65536;
            str += String.fromCharCode((c2 >> 10) + 55296);
            str += String.fromCharCode((c2 & 1023) + 56320);
          }
        }
        return str;
      };
      var decodeAsBytes = function(base32Str) {
        if (base32Str === "") {
          return [];
        } else if (!/^[A-Z2-7=]+$/.test(base32Str)) {
          throw new Error("Invalid base32 characters");
        }
        base32Str = base32Str.replace(/=/g, "");
        var v1, v2, v3, v4, v5, v6, v7, v8, bytes = [], index = 0, length = base32Str.length;
        for (var i3 = 0, count = length >> 3 << 3; i3 < count; ) {
          v1 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v2 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v3 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v4 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v5 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v6 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v7 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v8 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          bytes[index++] = (v1 << 3 | v2 >>> 2) & 255;
          bytes[index++] = (v2 << 6 | v3 << 1 | v4 >>> 4) & 255;
          bytes[index++] = (v4 << 4 | v5 >>> 1) & 255;
          bytes[index++] = (v5 << 7 | v6 << 2 | v7 >>> 3) & 255;
          bytes[index++] = (v7 << 5 | v8) & 255;
        }
        var remain = length - count;
        if (remain === 2) {
          v1 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v2 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          bytes[index++] = (v1 << 3 | v2 >>> 2) & 255;
        } else if (remain === 4) {
          v1 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v2 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v3 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v4 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          bytes[index++] = (v1 << 3 | v2 >>> 2) & 255;
          bytes[index++] = (v2 << 6 | v3 << 1 | v4 >>> 4) & 255;
        } else if (remain === 5) {
          v1 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v2 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v3 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v4 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v5 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          bytes[index++] = (v1 << 3 | v2 >>> 2) & 255;
          bytes[index++] = (v2 << 6 | v3 << 1 | v4 >>> 4) & 255;
          bytes[index++] = (v4 << 4 | v5 >>> 1) & 255;
        } else if (remain === 7) {
          v1 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v2 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v3 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v4 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v5 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v6 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v7 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          bytes[index++] = (v1 << 3 | v2 >>> 2) & 255;
          bytes[index++] = (v2 << 6 | v3 << 1 | v4 >>> 4) & 255;
          bytes[index++] = (v4 << 4 | v5 >>> 1) & 255;
          bytes[index++] = (v5 << 7 | v6 << 2 | v7 >>> 3) & 255;
        }
        return bytes;
      };
      var encodeAscii = function(str) {
        var v1, v2, v3, v4, v5, base32Str = "", length = str.length;
        for (var i3 = 0, count = parseInt(length / 5) * 5; i3 < count; ) {
          v1 = str.charCodeAt(i3++);
          v2 = str.charCodeAt(i3++);
          v3 = str.charCodeAt(i3++);
          v4 = str.charCodeAt(i3++);
          v5 = str.charCodeAt(i3++);
          base32Str += BASE32_ENCODE_CHAR[v1 >>> 3] + BASE32_ENCODE_CHAR[(v1 << 2 | v2 >>> 6) & 31] + BASE32_ENCODE_CHAR[v2 >>> 1 & 31] + BASE32_ENCODE_CHAR[(v2 << 4 | v3 >>> 4) & 31] + BASE32_ENCODE_CHAR[(v3 << 1 | v4 >>> 7) & 31] + BASE32_ENCODE_CHAR[v4 >>> 2 & 31] + BASE32_ENCODE_CHAR[(v4 << 3 | v5 >>> 5) & 31] + BASE32_ENCODE_CHAR[v5 & 31];
        }
        var remain = length - count;
        if (remain === 1) {
          v1 = str.charCodeAt(i3);
          base32Str += BASE32_ENCODE_CHAR[v1 >>> 3] + BASE32_ENCODE_CHAR[v1 << 2 & 31] + "======";
        } else if (remain === 2) {
          v1 = str.charCodeAt(i3++);
          v2 = str.charCodeAt(i3);
          base32Str += BASE32_ENCODE_CHAR[v1 >>> 3] + BASE32_ENCODE_CHAR[(v1 << 2 | v2 >>> 6) & 31] + BASE32_ENCODE_CHAR[v2 >>> 1 & 31] + BASE32_ENCODE_CHAR[v2 << 4 & 31] + "====";
        } else if (remain === 3) {
          v1 = str.charCodeAt(i3++);
          v2 = str.charCodeAt(i3++);
          v3 = str.charCodeAt(i3);
          base32Str += BASE32_ENCODE_CHAR[v1 >>> 3] + BASE32_ENCODE_CHAR[(v1 << 2 | v2 >>> 6) & 31] + BASE32_ENCODE_CHAR[v2 >>> 1 & 31] + BASE32_ENCODE_CHAR[(v2 << 4 | v3 >>> 4) & 31] + BASE32_ENCODE_CHAR[v3 << 1 & 31] + "===";
        } else if (remain === 4) {
          v1 = str.charCodeAt(i3++);
          v2 = str.charCodeAt(i3++);
          v3 = str.charCodeAt(i3++);
          v4 = str.charCodeAt(i3);
          base32Str += BASE32_ENCODE_CHAR[v1 >>> 3] + BASE32_ENCODE_CHAR[(v1 << 2 | v2 >>> 6) & 31] + BASE32_ENCODE_CHAR[v2 >>> 1 & 31] + BASE32_ENCODE_CHAR[(v2 << 4 | v3 >>> 4) & 31] + BASE32_ENCODE_CHAR[(v3 << 1 | v4 >>> 7) & 31] + BASE32_ENCODE_CHAR[v4 >>> 2 & 31] + BASE32_ENCODE_CHAR[v4 << 3 & 31] + "=";
        }
        return base32Str;
      };
      var encodeUtf8 = function(str) {
        var v1, v2, v3, v4, v5, code, end = false, base32Str = "", index = 0, i3, start = 0, length = str.length;
        if (str === "") {
          return base32Str;
        }
        do {
          blocks[0] = blocks[5];
          blocks[1] = blocks[6];
          blocks[2] = blocks[7];
          for (i3 = start; index < length && i3 < 5; ++index) {
            code = str.charCodeAt(index);
            if (code < 128) {
              blocks[i3++] = code;
            } else if (code < 2048) {
              blocks[i3++] = 192 | code >> 6;
              blocks[i3++] = 128 | code & 63;
            } else if (code < 55296 || code >= 57344) {
              blocks[i3++] = 224 | code >> 12;
              blocks[i3++] = 128 | code >> 6 & 63;
              blocks[i3++] = 128 | code & 63;
            } else {
              code = 65536 + ((code & 1023) << 10 | str.charCodeAt(++index) & 1023);
              blocks[i3++] = 240 | code >> 18;
              blocks[i3++] = 128 | code >> 12 & 63;
              blocks[i3++] = 128 | code >> 6 & 63;
              blocks[i3++] = 128 | code & 63;
            }
          }
          start = i3 - 5;
          if (index === length) {
            ++index;
          }
          if (index > length && i3 < 6) {
            end = true;
          }
          v1 = blocks[0];
          if (i3 > 4) {
            v2 = blocks[1];
            v3 = blocks[2];
            v4 = blocks[3];
            v5 = blocks[4];
            base32Str += BASE32_ENCODE_CHAR[v1 >>> 3] + BASE32_ENCODE_CHAR[(v1 << 2 | v2 >>> 6) & 31] + BASE32_ENCODE_CHAR[v2 >>> 1 & 31] + BASE32_ENCODE_CHAR[(v2 << 4 | v3 >>> 4) & 31] + BASE32_ENCODE_CHAR[(v3 << 1 | v4 >>> 7) & 31] + BASE32_ENCODE_CHAR[v4 >>> 2 & 31] + BASE32_ENCODE_CHAR[(v4 << 3 | v5 >>> 5) & 31] + BASE32_ENCODE_CHAR[v5 & 31];
          } else if (i3 === 1) {
            base32Str += BASE32_ENCODE_CHAR[v1 >>> 3] + BASE32_ENCODE_CHAR[v1 << 2 & 31] + "======";
          } else if (i3 === 2) {
            v2 = blocks[1];
            base32Str += BASE32_ENCODE_CHAR[v1 >>> 3] + BASE32_ENCODE_CHAR[(v1 << 2 | v2 >>> 6) & 31] + BASE32_ENCODE_CHAR[v2 >>> 1 & 31] + BASE32_ENCODE_CHAR[v2 << 4 & 31] + "====";
          } else if (i3 === 3) {
            v2 = blocks[1];
            v3 = blocks[2];
            base32Str += BASE32_ENCODE_CHAR[v1 >>> 3] + BASE32_ENCODE_CHAR[(v1 << 2 | v2 >>> 6) & 31] + BASE32_ENCODE_CHAR[v2 >>> 1 & 31] + BASE32_ENCODE_CHAR[(v2 << 4 | v3 >>> 4) & 31] + BASE32_ENCODE_CHAR[v3 << 1 & 31] + "===";
          } else {
            v2 = blocks[1];
            v3 = blocks[2];
            v4 = blocks[3];
            base32Str += BASE32_ENCODE_CHAR[v1 >>> 3] + BASE32_ENCODE_CHAR[(v1 << 2 | v2 >>> 6) & 31] + BASE32_ENCODE_CHAR[v2 >>> 1 & 31] + BASE32_ENCODE_CHAR[(v2 << 4 | v3 >>> 4) & 31] + BASE32_ENCODE_CHAR[(v3 << 1 | v4 >>> 7) & 31] + BASE32_ENCODE_CHAR[v4 >>> 2 & 31] + BASE32_ENCODE_CHAR[v4 << 3 & 31] + "=";
          }
        } while (!end);
        return base32Str;
      };
      var encodeBytes = function(bytes) {
        var v1, v2, v3, v4, v5, base32Str = "", length = bytes.length;
        for (var i3 = 0, count = parseInt(length / 5) * 5; i3 < count; ) {
          v1 = bytes[i3++];
          v2 = bytes[i3++];
          v3 = bytes[i3++];
          v4 = bytes[i3++];
          v5 = bytes[i3++];
          base32Str += BASE32_ENCODE_CHAR[v1 >>> 3] + BASE32_ENCODE_CHAR[(v1 << 2 | v2 >>> 6) & 31] + BASE32_ENCODE_CHAR[v2 >>> 1 & 31] + BASE32_ENCODE_CHAR[(v2 << 4 | v3 >>> 4) & 31] + BASE32_ENCODE_CHAR[(v3 << 1 | v4 >>> 7) & 31] + BASE32_ENCODE_CHAR[v4 >>> 2 & 31] + BASE32_ENCODE_CHAR[(v4 << 3 | v5 >>> 5) & 31] + BASE32_ENCODE_CHAR[v5 & 31];
        }
        var remain = length - count;
        if (remain === 1) {
          v1 = bytes[i3];
          base32Str += BASE32_ENCODE_CHAR[v1 >>> 3] + BASE32_ENCODE_CHAR[v1 << 2 & 31] + "======";
        } else if (remain === 2) {
          v1 = bytes[i3++];
          v2 = bytes[i3];
          base32Str += BASE32_ENCODE_CHAR[v1 >>> 3] + BASE32_ENCODE_CHAR[(v1 << 2 | v2 >>> 6) & 31] + BASE32_ENCODE_CHAR[v2 >>> 1 & 31] + BASE32_ENCODE_CHAR[v2 << 4 & 31] + "====";
        } else if (remain === 3) {
          v1 = bytes[i3++];
          v2 = bytes[i3++];
          v3 = bytes[i3];
          base32Str += BASE32_ENCODE_CHAR[v1 >>> 3] + BASE32_ENCODE_CHAR[(v1 << 2 | v2 >>> 6) & 31] + BASE32_ENCODE_CHAR[v2 >>> 1 & 31] + BASE32_ENCODE_CHAR[(v2 << 4 | v3 >>> 4) & 31] + BASE32_ENCODE_CHAR[v3 << 1 & 31] + "===";
        } else if (remain === 4) {
          v1 = bytes[i3++];
          v2 = bytes[i3++];
          v3 = bytes[i3++];
          v4 = bytes[i3];
          base32Str += BASE32_ENCODE_CHAR[v1 >>> 3] + BASE32_ENCODE_CHAR[(v1 << 2 | v2 >>> 6) & 31] + BASE32_ENCODE_CHAR[v2 >>> 1 & 31] + BASE32_ENCODE_CHAR[(v2 << 4 | v3 >>> 4) & 31] + BASE32_ENCODE_CHAR[(v3 << 1 | v4 >>> 7) & 31] + BASE32_ENCODE_CHAR[v4 >>> 2 & 31] + BASE32_ENCODE_CHAR[v4 << 3 & 31] + "=";
        }
        return base32Str;
      };
      var encode = function(input, asciiOnly) {
        var notString = typeof input !== "string";
        if (notString && input.constructor === ArrayBuffer) {
          input = new Uint8Array(input);
        }
        if (notString) {
          return encodeBytes(input);
        } else if (asciiOnly) {
          return encodeAscii(input);
        } else {
          return encodeUtf8(input);
        }
      };
      var decode = function(base32Str, asciiOnly) {
        if (!asciiOnly) {
          return toUtf8String(decodeAsBytes(base32Str));
        }
        if (base32Str === "") {
          return "";
        } else if (!/^[A-Z2-7=]+$/.test(base32Str)) {
          throw new Error("Invalid base32 characters");
        }
        var v1, v2, v3, v4, v5, v6, v7, v8, str = "", length = base32Str.indexOf("=");
        if (length === -1) {
          length = base32Str.length;
        }
        for (var i3 = 0, count = length >> 3 << 3; i3 < count; ) {
          v1 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v2 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v3 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v4 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v5 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v6 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v7 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v8 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          str += String.fromCharCode((v1 << 3 | v2 >>> 2) & 255) + String.fromCharCode((v2 << 6 | v3 << 1 | v4 >>> 4) & 255) + String.fromCharCode((v4 << 4 | v5 >>> 1) & 255) + String.fromCharCode((v5 << 7 | v6 << 2 | v7 >>> 3) & 255) + String.fromCharCode((v7 << 5 | v8) & 255);
        }
        var remain = length - count;
        if (remain === 2) {
          v1 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v2 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          str += String.fromCharCode((v1 << 3 | v2 >>> 2) & 255);
        } else if (remain === 4) {
          v1 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v2 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v3 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v4 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          str += String.fromCharCode((v1 << 3 | v2 >>> 2) & 255) + String.fromCharCode((v2 << 6 | v3 << 1 | v4 >>> 4) & 255);
        } else if (remain === 5) {
          v1 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v2 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v3 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v4 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v5 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          str += String.fromCharCode((v1 << 3 | v2 >>> 2) & 255) + String.fromCharCode((v2 << 6 | v3 << 1 | v4 >>> 4) & 255) + String.fromCharCode((v4 << 4 | v5 >>> 1) & 255);
        } else if (remain === 7) {
          v1 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v2 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v3 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v4 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v5 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v6 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v7 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          str += String.fromCharCode((v1 << 3 | v2 >>> 2) & 255) + String.fromCharCode((v2 << 6 | v3 << 1 | v4 >>> 4) & 255) + String.fromCharCode((v4 << 4 | v5 >>> 1) & 255) + String.fromCharCode((v5 << 7 | v6 << 2 | v7 >>> 3) & 255);
        }
        return str;
      };
      var exports2 = {
        encode,
        decode
      };
      decode.asBytes = decodeAsBytes;
      if (COMMON_JS) {
        module.exports = exports2;
      } else {
        root2.base32 = exports2;
      }
    })();
  })(base32$1);
  return base32$1.exports;
}
requireBase32();
const { gcd, lcm } = Context.math;
const { mod } = Context.math;
const { dopri } = Context.math;
const defaultCreativeCommonsVersion = "4.0";
function creativeCommonsLicense(code, name2, urlSlug, description) {
  return {
    code,
    name: `Creative Commons ${name2}`,
    description,
    kind: "creative-commons",
    url: (version2 = defaultCreativeCommonsVersion) => `https://creativecommons.org/licenses/${urlSlug}/${version2}/`
  };
}
function fixedUrlLicense(code, name2, url, description, kind) {
  return {
    code,
    name: name2,
    description,
    kind,
    url: () => url
  };
}
const mediaLicenses = [
  creativeCommonsLicense(
    "CC-BY",
    "Attribution",
    "by",
    "Creative Commons Attribution: reuse with credit, including commercially."
  ),
  creativeCommonsLicense(
    "CC-BY-SA",
    "Attribution-ShareAlike",
    "by-sa",
    "Creative Commons Attribution-ShareAlike: reuse with credit; adaptations keep the same license."
  ),
  creativeCommonsLicense(
    "CC-BY-ND",
    "Attribution-NoDerivatives",
    "by-nd",
    "Creative Commons Attribution-NoDerivatives: reuse unchanged, with credit."
  ),
  creativeCommonsLicense(
    "CC-BY-NC",
    "Attribution-NonCommercial",
    "by-nc",
    "Creative Commons Attribution-NonCommercial: reuse with credit for noncommercial purposes."
  ),
  creativeCommonsLicense(
    "CC-BY-NC-SA",
    "Attribution-NonCommercial-ShareAlike",
    "by-nc-sa",
    "Creative Commons Attribution-NonCommercial-ShareAlike: noncommercial reuse with credit; adaptations keep the same license."
  ),
  creativeCommonsLicense(
    "CC-BY-NC-ND",
    "Attribution-NonCommercial-NoDerivatives",
    "by-nc-nd",
    "Creative Commons Attribution-NonCommercial-NoDerivatives: reuse unchanged for noncommercial purposes, with credit."
  ),
  fixedUrlLicense(
    "CC0",
    "CC0 1.0 Public Domain Dedication",
    "https://creativecommons.org/publicdomain/zero/1.0/",
    "Creative Commons Zero: the creator has dedicated the work to the public domain.",
    "public-domain"
  ),
  fixedUrlLicense(
    "PDM",
    "Public Domain Mark 1.0",
    "https://creativecommons.org/publicdomain/mark/1.0/",
    "Public Domain Mark: the work is free of known copyright restrictions.",
    "public-domain"
  ),
  fixedUrlLicense(
    "GFDL",
    "GNU Free Documentation License",
    "https://www.gnu.org/licenses/fdl-1.3.html",
    "GNU Free Documentation License: copyleft license common on Wikimedia.",
    "license"
  ),
  fixedUrlLicense(
    "FAL",
    "Free Art License 1.3",
    "https://artlibre.org/licence/lal/en/",
    "Free Art License: copyleft license for artistic works.",
    "license"
  ),
  fixedUrlLicense(
    "OGL",
    "Open Government Licence v3.0 (UK)",
    "https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/",
    "UK Open Government Licence: reuse government material with attribution.",
    "license"
  ),
  fixedUrlLicense(
    "MIT",
    "MIT License",
    "https://opensource.org/license/mit",
    "MIT License: permissive license common for icon and illustration sets.",
    "license"
  ),
  fixedUrlLicense(
    "APACHE-2.0",
    "Apache License 2.0",
    "https://www.apache.org/licenses/LICENSE-2.0",
    "Apache License 2.0: permissive license common for icon and illustration sets.",
    "license"
  )
];
Object.fromEntries(
  mediaLicenses.map((info) => [info.code.toLowerCase(), info])
);
const mathjaxConfig = {
  tex: {
    tags: "ams",
    macros: {
      lt: "<",
      gt: ">",
      amp: "&",
      var: ["\\mathrm{#1}", 1],
      csch: "\\operatorname{csch}",
      sech: "\\operatorname{sech}",
      erf: "\\operatorname{erf}"
    },
    displayMath: [["\\[", "\\]"]]
  },
  output: {
    displayOverflow: "linebreak"
  }
};
var cssesc_1;
var hasRequiredCssesc;
function requireCssesc() {
  if (hasRequiredCssesc) return cssesc_1;
  hasRequiredCssesc = 1;
  var object2 = {};
  var hasOwnProperty2 = object2.hasOwnProperty;
  var merge = function merge2(options, defaults) {
    if (!options) {
      return defaults;
    }
    var result2 = {};
    for (var key in defaults) {
      result2[key] = hasOwnProperty2.call(options, key) ? options[key] : defaults[key];
    }
    return result2;
  };
  var regexAnySingleEscape = /[ -,\.\/:-@\[-\^`\{-~]/;
  var regexSingleEscape = /[ -,\.\/:-@\[\]\^`\{-~]/;
  var regexExcessiveSpaces = /(^|\\+)?(\\[A-F0-9]{1,6})\x20(?![a-fA-F0-9\x20])/g;
  var cssesc2 = function cssesc3(string, options) {
    options = merge(options, cssesc3.options);
    if (options.quotes != "single" && options.quotes != "double") {
      options.quotes = "single";
    }
    var quote = options.quotes == "double" ? '"' : "'";
    var isIdentifier = options.isIdentifier;
    var firstChar = string.charAt(0);
    var output = "";
    var counter = 0;
    var length = string.length;
    while (counter < length) {
      var character = string.charAt(counter++);
      var codePoint = character.charCodeAt();
      var value = void 0;
      if (codePoint < 32 || codePoint > 126) {
        if (codePoint >= 55296 && codePoint <= 56319 && counter < length) {
          var extra = string.charCodeAt(counter++);
          if ((extra & 64512) == 56320) {
            codePoint = ((codePoint & 1023) << 10) + (extra & 1023) + 65536;
          } else {
            counter--;
          }
        }
        value = "\\" + codePoint.toString(16).toUpperCase() + " ";
      } else {
        if (options.escapeEverything) {
          if (regexAnySingleEscape.test(character)) {
            value = "\\" + character;
          } else {
            value = "\\" + codePoint.toString(16).toUpperCase() + " ";
          }
        } else if (/[\t\n\f\r\x0B]/.test(character)) {
          value = "\\" + codePoint.toString(16).toUpperCase() + " ";
        } else if (character == "\\" || !isIdentifier && (character == '"' && quote == character || character == "'" && quote == character) || isIdentifier && regexSingleEscape.test(character)) {
          value = "\\" + character;
        } else {
          value = character;
        }
      }
      output += value;
    }
    if (isIdentifier) {
      if (/^-[-\d]/.test(output)) {
        output = "\\-" + output.slice(1);
      } else if (/\d/.test(firstChar)) {
        output = "\\3" + firstChar + " " + output.slice(1);
      }
    }
    output = output.replace(regexExcessiveSpaces, function($0, $1, $2) {
      if ($1 && $1.length % 2) {
        return $0;
      }
      return ($1 || "") + $2;
    });
    if (!isIdentifier && options.wrap) {
      return quote + output + quote;
    }
    return output;
  };
  cssesc2.options = {
    "escapeEverything": false,
    "isIdentifier": false,
    "quotes": "single",
    "wrap": false
  };
  cssesc2.version = "3.0.0";
  cssesc_1 = cssesc2;
  return cssesc_1;
}
requireCssesc();
const globalThis$1 = Function("return this")() || {};
const virtualKeyboardState = globalThis$1?.virtualKeyboardState || {
  count: 0,
  open: false,
  userClosedTray: false,
  openListeners: /* @__PURE__ */ new Set(),
  focusedMathInputSources: /* @__PURE__ */ new Set(),
  keyboardDomNode: null,
  keyboardReactRoot: null,
  registrations: [],
  lastActiveRegistrationId: null,
  nextRegistrationId: 0
};
globalThis$1.virtualKeyboardState = virtualKeyboardState;
function getRegistrationById(id) {
  if (id === null) {
    return null;
  }
  return virtualKeyboardState.registrations.find(
    (registration) => registration.id === id
  ) ?? null;
}
function getActiveRegistration() {
  const activeElement = document.activeElement;
  for (let i2 = virtualKeyboardState.registrations.length - 1; i2 >= 0; i2 -= 1) {
    const registration = virtualKeyboardState.registrations[i2];
    const ownerElement = registration.ownerRef?.current;
    if (ownerElement && activeElement instanceof Node && (ownerElement === activeElement || ownerElement.contains(activeElement))) {
      virtualKeyboardState.lastActiveRegistrationId = registration.id;
      return registration;
    }
  }
  if (isInVirtualKeyboardTray(activeElement)) {
    return getRegistrationById(
      virtualKeyboardState.lastActiveRegistrationId
    );
  }
  return null;
}
function getTrayRegistration() {
  const activeRegistration = getActiveRegistration();
  if (activeRegistration) {
    return activeRegistration;
  }
  const lastActiveRegistration = getRegistrationById(
    virtualKeyboardState.lastActiveRegistrationId
  );
  if (lastActiveRegistration) {
    return lastActiveRegistration;
  }
  const registrations = virtualKeyboardState.registrations;
  return registrations[registrations.length - 1];
}
function broadcastKeyboardChoice(open) {
  const choice = [
    { type: "keyboardChoice", command: open ? "virtual" : "system" }
  ];
  for (const registration of virtualKeyboardState.registrations) {
    registration.onClick(choice);
  }
}
function getTrayOpen() {
  return virtualKeyboardState.open;
}
function subscribeToTrayOpen(listener) {
  virtualKeyboardState.openListeners.add(listener);
  return () => {
    virtualKeyboardState.openListeners.delete(listener);
  };
}
function setTrayOpen(open, { byUser }) {
  if (byUser) {
    virtualKeyboardState.userClosedTray = !open;
    broadcastKeyboardChoice(open);
  }
  if (virtualKeyboardState.open === open) {
    return;
  }
  virtualKeyboardState.open = open;
  for (const listener of virtualKeyboardState.openListeners) {
    listener();
  }
}
function reportMathInputFocus(source, mathInputFocused) {
  if (!hasCoarsePrimaryPointer()) {
    return;
  }
  const focusedSources = virtualKeyboardState.focusedMathInputSources;
  if (mathInputFocused) {
    focusedSources.add(source);
    if (!virtualKeyboardState.userClosedTray) {
      setTrayOpen(true, { byUser: false });
    }
    return;
  }
  if (isInVirtualKeyboardTray(document.activeElement)) {
    return;
  }
  focusedSources.delete(source);
  if (focusedSources.size > 0) {
    return;
  }
  setTrayOpen(false, { byUser: false });
}
function rerenderTray() {
  const registration = getTrayRegistration();
  const theme = registration?.theme;
  const translate = registration?.translate;
  const direction = registration?.direction;
  const trayEl = getVirtualKeyboardTrayElement();
  if (trayEl) {
    const currentTheme = trayEl.getAttribute("data-theme") ?? void 0;
    const currentDirection = trayEl.getAttribute("dir") ?? void 0;
    if (currentTheme === theme && currentDirection === direction && virtualKeyboardState.lastRenderedTranslate === translate) {
      return;
    }
  }
  virtualKeyboardState.lastRenderedTranslate = translate;
  virtualKeyboardState.keyboardReactRoot?.render(
    renderTray(theme, translate, direction)
  );
}
function TrayWithSharedOpenState({
  theme,
  translate,
  direction
}) {
  const open = React__default.useSyncExternalStore(
    subscribeToTrayOpen,
    getTrayOpen,
    getTrayOpen
  );
  return /* @__PURE__ */ React__default.createElement(
    KeyboardTray,
    {
      theme,
      translate,
      direction,
      open,
      onOpenChange: (nextOpen) => setTrayOpen(nextOpen, { byUser: true }),
      onClick: (e2) => {
        getActiveRegistration()?.onClick(e2);
      }
    }
  );
}
function renderTray(theme, translate, direction) {
  return /* @__PURE__ */ React__default.createElement(MathJaxContext, { config: mathjaxConfig, version: 4 }, /* @__PURE__ */ React__default.createElement(
    TrayWithSharedOpenState,
    {
      theme,
      translate,
      direction
    }
  ));
}
function UniqueKeyboardTray({
  onClick,
  theme,
  direction,
  translate,
  ownerRef
}) {
  const [id] = React__default.useState(() => {
    const next = virtualKeyboardState.nextRegistrationId;
    virtualKeyboardState.nextRegistrationId += 1;
    return next;
  });
  React__default.useEffect(() => {
    if (virtualKeyboardState.count === 0) {
      const keyboardDomNode = document.createElement("footer");
      keyboardDomNode.id = "virtual-keyboard-dummy";
      document.body.appendChild(keyboardDomNode);
      virtualKeyboardState.keyboardDomNode = keyboardDomNode;
      const root2 = createRoot(keyboardDomNode);
      virtualKeyboardState.keyboardReactRoot = root2;
      virtualKeyboardState.handleFocusChange = () => {
        rerenderTray();
      };
      document.addEventListener(
        "focusin",
        virtualKeyboardState.handleFocusChange
      );
    }
    virtualKeyboardState.count += 1;
    virtualKeyboardState.registrations.push({
      id,
      onClick,
      theme,
      translate,
      direction,
      ownerRef
    });
    rerenderTray();
    return () => {
      virtualKeyboardState.registrations = virtualKeyboardState.registrations.filter(
        (registration) => registration.id !== id
      );
      if (virtualKeyboardState.lastActiveRegistrationId === id) {
        const remaining = virtualKeyboardState.registrations;
        virtualKeyboardState.lastActiveRegistrationId = remaining[remaining.length - 1]?.id ?? null;
      }
      virtualKeyboardState.count -= 1;
      if (virtualKeyboardState.count === 0) {
        if (virtualKeyboardState.handleFocusChange) {
          document.removeEventListener(
            "focusin",
            virtualKeyboardState.handleFocusChange
          );
          virtualKeyboardState.handleFocusChange = void 0;
        }
        if (virtualKeyboardState.keyboardReactRoot) {
          const root2 = virtualKeyboardState.keyboardReactRoot;
          setTimeout(() => {
            root2.unmount();
          }, 0);
          virtualKeyboardState.keyboardReactRoot = null;
        }
        if (virtualKeyboardState.keyboardDomNode) {
          document.body.removeChild(
            virtualKeyboardState.keyboardDomNode
          );
          virtualKeyboardState.keyboardDomNode = null;
        }
        virtualKeyboardState.open = false;
        virtualKeyboardState.userClosedTray = false;
        virtualKeyboardState.focusedMathInputSources.clear();
      } else {
        rerenderTray();
      }
    };
  }, []);
  React__default.useEffect(() => {
    const registration = getRegistrationById(id);
    if (registration) {
      registration.onClick = onClick;
      registration.theme = theme;
      registration.translate = translate;
      registration.direction = direction;
      registration.ownerRef = ownerRef;
      rerenderTray();
    }
  }, [onClick, ownerRef, theme, translate, direction]);
  return null;
}
function ExternalAwareVirtualKeyboard({
  externalVirtualKeyboardProvided = false,
  onClick = () => {
  },
  theme,
  translate,
  direction,
  ownerRef
}) {
  React__default.useEffect(() => {
    if (externalVirtualKeyboardProvided) {
      const listener = (event) => {
        if (event.source !== window.parent || event.data?.subject !== "keyboard") {
          return;
        }
        onClick(event.data.keyCommands);
      };
      window.addEventListener("message", listener);
      return () => {
        window.removeEventListener("message", listener);
      };
    }
  }, [externalVirtualKeyboardProvided, onClick]);
  React__default.useEffect(() => {
    let lastReported = false;
    let pending;
    function send(mathInputFocused) {
      if (externalVirtualKeyboardProvided) {
        window.parent.postMessage(
          {
            subject: "keyboard-focus",
            mathInputFocused
          },
          "*"
        );
      } else {
        reportMathInputFocus(ownerRef, mathInputFocused);
      }
    }
    function report() {
      const activeElement = document.activeElement;
      const owner = ownerRef.current;
      const mathInputFocused = owner !== null && activeElement instanceof Node && owner.contains(activeElement);
      if (mathInputFocused === lastReported) {
        return;
      }
      lastReported = mathInputFocused;
      send(mathInputFocused);
    }
    function scheduleReport() {
      clearTimeout(pending);
      pending = setTimeout(report, 0);
    }
    document.addEventListener("focusin", scheduleReport);
    document.addEventListener("focusout", scheduleReport);
    return () => {
      clearTimeout(pending);
      document.removeEventListener("focusin", scheduleReport);
      document.removeEventListener("focusout", scheduleReport);
      if (lastReported) {
        send(false);
      }
    };
  }, [externalVirtualKeyboardProvided, ownerRef]);
  return externalVirtualKeyboardProvided ? null : /* @__PURE__ */ React__default.createElement(
    UniqueKeyboardTray,
    {
      onClick,
      ownerRef,
      theme,
      translate,
      direction
    }
  );
}
function ExternalVirtualKeyboard({
  theme,
  ownerRef
}) {
  React__default.useEffect(() => {
    let reportedFocused = false;
    function listener(event) {
      if (event.source !== ownerRef.current?.contentWindow || event.data?.subject !== "keyboard-focus") {
        return;
      }
      reportedFocused = event.data.mathInputFocused;
      reportMathInputFocus(ownerRef, reportedFocused);
    }
    window.addEventListener("message", listener);
    return () => {
      window.removeEventListener("message", listener);
      if (reportedFocused) {
        reportMathInputFocus(ownerRef, false);
      }
    };
  }, [ownerRef]);
  return /* @__PURE__ */ React__default.createElement(
    UniqueKeyboardTray,
    {
      ownerRef,
      theme,
      onClick: (events) => {
        if (!ownerRef.current) {
          return;
        }
        ownerRef.current.contentWindow?.postMessage(
          {
            keyCommands: events,
            subject: "keyboard"
          },
          // Use "*" rather than window.location.origin: targetOrigin
          // must match the *iframe document's* origin, not the parent's.
          // The iframe may be cross-origin, so "*" is the only safe
          // value here. The message is low-sensitivity (key commands
          // only) and the recipient already validates event.source.
          "*"
        );
      }
    }
  );
}
export {
  ExternalAwareVirtualKeyboard,
  ExternalVirtualKeyboard,
  FUNCTION_KEYS,
  KEYS,
  Keyboard,
  KeyboardTray,
  ManagedKeyboard,
  OPERATOR_KEYS,
  OTHER_SYMBOLS,
  SPECIAL_KEYS,
  SYMBOL_KEYS,
  TEX_NAMES_LOWER,
  TEX_NAMES_UPPER,
  TRIG_KEYS,
  UniqueKeyboardTray,
  VAR_KEYS,
  VIRTUAL_KEYBOARD_TRAY_ID,
  ExternalAwareVirtualKeyboard as VirtualKeyboard,
  getVirtualKeyboardTrayElement,
  isInVirtualKeyboardTray,
  reportMathInputFocus
};
//# sourceMappingURL=index.js.map
